export type AIAssistantAccGrade = {
  id: number
  quarter: number | null
  aim: number | null
  aim_str: string | null
  semester: number | null
  year: number | null
  student_aim_for_course: number
  comment: string
  comment_author: string | null
  /** @format date-time */
  comment_created: string | null
  comment_last_modified_by: string | null
  /** @format uuid */
  course: string
  grade: string | null
  displayed_grade: string | null
  target_grade_status: TargetGradeStatusEnum
  weighted_percents: number | null
  course_str: string
  student_str: string
  /** @format uuid */
  student: string
  target_grade: string | null
  target_percents: number | null
  teacher_str: string | null
  /** @format uuid */
  teacher_id: string | null
  custom_grade: string
  course_drop_id: number | null
  end_of_quarter_exams: AIAssistantEndOfQuarterExam[]
}

export type AIAssistantAccessArrangement = {
  value: Value6A8Enum
  label: string
}

export type AIAssistantAssessmentAssignment = {
  /** @format uuid */
  id: string
  name: string
  description: string
  course: SimpleCourse
  quarter: number
  type: number
  /** @format date */
  due_date: string
  type_str: string
  moderated_grades_allowed: boolean
}

export type AIAssistantAssessmentResult = {
  /** @format uuid */
  id: string
  /** @format uuid */
  student: string
  student_str: string
  assignment: AIAssistantAssessmentAssignment
  grade_in_percent: number | null
  grade_in_ib: string
  custom_grade: TECustomGradeEnum | BlankEnum
  score_definitions: AIAssistantAssessmentScoreDefinition[]
  is_submitted: boolean
  is_marked_as_sent_by_student: boolean
  is_incomplete: boolean
  is_late: boolean
  is_exempt: boolean
  moderated_grade_status: ModeratedGradeStatusEnum | NullEnum | null
  course_drop_id: number | null
}

export type AIAssistantAssessmentScoreDefinition = {
  /** @format uuid */
  id: string
  subtopic_name: string | null
  /** @format double */
  max_points: number | null
  /** @format double */
  achieved_points: number | null
  is_skipped: boolean
}

export type AIAssistantAssignment = {
  /** @format uuid */
  id: string
  name: string
  description: string
  course: SimpleCourse
  type: number
  /** @format date */
  due_date: string
  type_str: string
  moderated_grades_allowed: boolean
}

export type AIAssistantAssignmentItem = {
  /** @format uuid */
  id: string
  /** @format date-time */
  created: string
  grade_in_percent: number | null
  custom_grade: TECustomGradeEnum | BlankEnum
  average_grade: number | null
  grade_in_ib: string
  is_submitted: boolean
  is_incomplete: boolean
  is_late: boolean
  is_exempt: boolean
  /** @format uuid */
  student: string
  assignment: AIAssistantAssignment
  score_definitions: AIAssistantAssignmentScoreDefinition[]
  /** Moderated grade in percents. */
  moderated_grade: number | null
  moderated_grade_status: ModeratedGradeStatusEnum | NullEnum | null
  grade_moderated_by: SimpleUser | null
}

export type AIAssistantAssignmentScoreDefinition = {
  /** @format uuid */
  id: string
  /** @format double */
  max_points: number | null
  /** @format uuid */
  section_plan_item_id: string
  /** @format uuid */
  assignment_id: string
  subtopic_name: string | null
  students_scores: AIAssistantStudentAssignmentScore[]
  /** Indicates if scores from this definition should be skipped in grade calculation process */
  is_skipped: boolean
}

export type AIAssistantAttendanceCourse = {
  /** @format uuid */
  id: string
  name: string
}

export type AIAssistantAttendanceLesson = {
  /** @format uuid */
  id: string
  /** @format date */
  date: string
  start: string
  end: string
  course: AIAssistantAttendanceCourse
  teacher: AIAssistantAttendanceTeacher
}

export type AIAssistantAttendanceRecord = {
  /** @format uuid */
  id: string
  student: AIAssistantAttendanceRecordStudent
  lesson: AIAssistantAttendanceLesson
  status: AIAssistantAttendanceRecordStatusEnum
  is_marked_automatically: boolean
}

/**
 * * `present` - present
 * * `late` - late
 * * `absent_excused` - absent_excused
 * * `absent_unexcused` - absent_unexcused
 * * `sick` - sick
 * * `unmarked` - unmarked
 */
export type AIAssistantAttendanceRecordStatusEnum =
  | 'present'
  | 'late'
  | 'absent_excused'
  | 'absent_unexcused'
  | 'sick'
  | 'unmarked'

export type AIAssistantAttendanceRecordStudent = {
  /** @format uuid */
  id: string
  full_name: string
}

export type AIAssistantAttendanceSummary = {
  marked_attendances_count: number
  present: number
  late: number
  absent_excused: number
  absent_unexcused: number
  sick: number
  missed_lessons_count: number
  /** @format double */
  missed_lessons_percentage: number | null
  missed_minutes: number
  student: AIAssistantMissionControlStudent
  courses: AIAssistantCourseAttendanceSummary[] | null
}

export type AIAssistantAttendanceTeacher = {
  /** @format uuid */
  id: string
  full_name: string
}

export type AIAssistantCockpitArtisan = {
  /** @format uuid */
  id: string
  full_name: string
}

export type AIAssistantCockpitCourse = {
  /** @format uuid */
  id: string
  name: string
  code: string
  version: number
}

export type AIAssistantCockpitCoursePlan = {
  id: number
  course: AIAssistantCockpitCourse
  school_year: AIAssistantCockpitSchoolYear
  /** @default 0 */
  group_number: number
  artisans: AIAssistantCockpitArtisan[]
  co_artisans: AIAssistantCockpitArtisan[]
  plan_status: SectionGroupPlanStatusEnum
  plan_status_display: string
  /** @format date-time */
  submitted_for_review_at: string | null
  quarters: AIAssistantCockpitCoursePlanQuarter[]
}

export type AIAssistantCockpitCoursePlanQuarter = {
  quarter: AIAssistantCockpitQuarter
  section_id: number
  plan_status: SectionPlanStatusEnum
  plan_status_display: string
  subtopics: AIAssistantCockpitSubtopicCounts
  planned_lessons: number
  realized_lessons: number
  taught_lessons: number
  unlinked_lessons: number | null
  /** @format date */
  last_linked_lesson_on: string | null
  /** @format date */
  first_unlinked_lesson_on: string | null
}

export type AIAssistantCockpitDomain = {
  /** @format uuid */
  id: string
  name: string
}

export type AIAssistantCockpitQuarter = {
  id: number
  name: string
  short_name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
}

export type AIAssistantCockpitSchoolYear = {
  id: number
  name: string
}

export type AIAssistantCockpitSubtopic = {
  /** @format uuid */
  id: string
  course: AIAssistantCockpitCourse
  course_plan: AIAssistantCockpitSubtopicCoursePlan
  school_year: AIAssistantCockpitSchoolYear
  quarter: AIAssistantCockpitQuarter
  section_id: number
  artisans: AIAssistantCockpitArtisan[]
  co_artisans: AIAssistantCockpitArtisan[]
  plan_status: SectionPlanStatusEnum
  plan_status_display: string
  /** Indicates the order of items in the section plan */
  ordering: number
  /** @format uuid */
  merged_into_id: string | null
  domain: AIAssistantCockpitDomain
  topic: AIAssistantCockpitTopic
  subtopic: AIAssistantCockpitSubtopicDetails
  is_revision: boolean
  is_custom: boolean
  planned_lessons: number
  scheduled_lessons: number
  realized_lessons: number
  status: LessonSectionPlanItemStatusEnum
  status_display: string
  /** Indicates item was finished manually, not including the lessons */
  is_finished_before_planned_time: boolean
  taught_on: string[]
}

export type AIAssistantCockpitSubtopicCounts = {
  to_do: number
  planned: number
  in_progress: number
  completed: number
}

export type AIAssistantCockpitSubtopicCoursePlan = {
  id: number
  group_number: number
}

export type AIAssistantCockpitSubtopicDetails = {
  /** @format uuid */
  id: string
  name: string
  code: string | null
  description: string | null
}

export type AIAssistantCockpitTopic = {
  /** @format uuid */
  id: string
  name: string
}

export type AIAssistantCourseAttendanceSummary = {
  marked_attendances_count: number
  present: number
  late: number
  absent_excused: number
  absent_unexcused: number
  sick: number
  missed_lessons_count: number
  /** @format double */
  missed_lessons_percentage: number | null
  missed_minutes: number
  course: AIAssistantAttendanceCourse
  is_dropped: boolean
  /** @format date-time */
  dropped_at: string | null
}

export type AIAssistantCreditHistoryYear = {
  source: AIAssistantCreditHistoryYearSourceEnum
  imported_history_id: number | null
  idp_plan_id: string | null
  school_year: AIAssistantGPASchoolYear
  grade_level_id: number
  grade_level_name: string
  school: string
  /** @format double */
  credit_count: number | null
  department_credits: DepartmentCredits[]
}

/**
 * * `imported` - imported
 * * `idp` - idp
 */
export type AIAssistantCreditHistoryYearSourceEnum = 'imported' | 'idp'

export type AIAssistantCurrentAim = {
  name: string
  school_year: string | null
  is_school_default: boolean
}

export type AIAssistantCurrentPeriods = {
  year: number | null
  semester: number | null
  quarter: number | null
}

export type AIAssistantDegreeAim = {
  id: number
  name: string
}

export type AIAssistantDegreeGoal = {
  priority: number
  degree_aim_id: number | null
  degree_aim: string | null
  destination: string | null
  destination_str: string | null
}

export type AIAssistantEndOfQuarterExam = {
  name: string
  /** @format date */
  date: string
  percent: number | null
  grade: string | null
}

export type AIAssistantEstimatedCompletion = {
  /** @format date */
  date: string
  display: string
  school_year: string | null
  /** @format double */
  years_remaining: number
  is_manually_set: boolean
}

export type AIAssistantExamAccessArrangements = {
  student: AIAssistantMissionControlStudent
  saa_student: boolean
  is_statemented: boolean
  exam_extra_time: ExamExtraTimeEnum | NullEnum | null
  access_arrangements: AIAssistantAccessArrangement[]
}

export type AIAssistantExamSitting = {
  id: number
  /** @format uuid */
  student_id: string
  student_full_name: string
  exam_id: string
  exam_name: string
  official_name: string
  /** @format uuid */
  course_id: string
  course_name: string
  exam_session_id: string | null
  exam_session_name: string | null
  exam_type: ExamTypeEnum
  school_year_id: number
  quarter_id: number
  /** @format date */
  date: string | null
  start_time: string | null
  end_time: string | null
  time_zone: string
  is_published: boolean
  is_scheduled: boolean
  status: Status128Enum
}

export type AIAssistantExternalExam = {
  id: number
  student: AIAssistantMissionControlStudent
  type: ExternalExamTypeEnum
  type_str: string
  subtype: string | null
  subject: string | null
  /** @format date */
  date: string | null
  calendar_year: number | null
  /** @format double */
  score: number | null
  /** @format double */
  min_score: number | null
  meets_minimum: boolean | null
  status: AIAssistantExternalExamStatusEnum
}

/**
 * * `upcoming` - Upcoming
 * * `taken` - Taken
 * * `unknown` - Unknown
 */
export type AIAssistantExternalExamStatusEnum = 'upcoming' | 'taken' | 'unknown'

export type AIAssistantGPASchoolYear = {
  id: number
  name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
}

export type AIAssistantGradeLevel = {
  id: number
  name: string
  stage_label: string | null
}

export type AIAssistantMe = {
  /** @format uuid */
  id: string
  full_name: string
  roles: string[]
  active_roles: string[]
  departments: AIAssistantMeDepartment[]
  house_team: AIAssistantMeHouse[]
}

export type AIAssistantMeDepartment = {
  id: number
  name: string
  label: string
}

export type AIAssistantMeHouse = {
  id: number
  name: string
}

export type AIAssistantMissionControlStudent = {
  /** @format uuid */
  id: string
  full_name: string
  current_grade_level_id: number | null
  current_grade_level_str: string | null
}

export type AIAssistantPeriodCatalog = {
  current: AIAssistantCurrentPeriods
  years: AIAssistantPeriodYear[]
}

export type AIAssistantPeriodQuarter = {
  id: number
  name: string
  short_name: QuarterShortNameEnum
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
}

export type AIAssistantPeriodSemester = {
  id: number
  name: string
  short_name: ShortNameD69Enum
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  quarters: AIAssistantPeriodQuarter[]
}

export type AIAssistantPeriodYear = {
  id: number
  name: string
  short_name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  semesters: AIAssistantPeriodSemester[]
}

export type AIAssistantRecordedHighSchoolGPA = {
  id: number
  /**
   * @format decimal
   * @pattern ^-?\d{0,6}(?:\.\d{0,4})?$
   */
  unweighted_value: string
  /**
   * @format decimal
   * @pattern ^-?\d{0,6}(?:\.\d{0,4})?$
   */
  weighted_value: string
  /** @format date-time */
  recorded_modified_at: string
}

export type AIAssistantRecordedPeriodGPA = {
  id: number
  grade_level_id: number
  grade_level_name: string
  period: GPAPeriod
  /**
   * @format decimal
   * @pattern ^-?\d{0,6}(?:\.\d{0,4})?$
   */
  unweighted_value: string
  /**
   * @format decimal
   * @pattern ^-?\d{0,6}(?:\.\d{0,4})?$
   */
  weighted_value: string
  /** @format date-time */
  recorded_modified_at: string
}

export type AIAssistantStudent = {
  /** @format uuid */
  id: string
  full_name: string
  current_grade_level_id: number | null
  current_grade_level_str: string | null
  /** @format date */
  diploma_date?: string | null
  diploma_type?: DiplomaTypeEnum | BlankEnum | NullEnum | null
}

export type AIAssistantStudentAssignmentScore = {
  /** @format uuid */
  id: string
  /** @format uuid */
  student_id: string
  /** @format double */
  achieved_points: number | null
  student_full_name: string
}

export type AIAssistantStudentCreditHistory = {
  count: number
  page_size: number
  current: number
  /** @format uri */
  next: string | null
  /** @format uri */
  previous: string | null
  /** @format uuid */
  student_id: string
  current_school_year: AIAssistantGPASchoolYear | null
  summary: CreditSummary[]
  results: AIAssistantCreditHistoryYear[]
}

export type AIAssistantStudentDetail = {
  /** @format uuid */
  id: string
  full_name: string
  current_grade_level_id: number | null
  current_grade_level_str: string | null
  /** @format date */
  date_of_birth?: string | null
  age: number | null
  gender?: ForGenderEnum | BlankEnum
  /** @maxItems 250 */
  nationalities: (NationalityEnum | BlankEnum)[]
  nationalities_str: string[]
  native_language: string | null
  native_language_str: string | null
  has_allergy: boolean
  is_at_home?: boolean
}

export type AIAssistantStudentGPA = {
  student: AIAssistantMissionControlStudent
  periods: AIAssistantRecordedPeriodGPA[]
  high_school_gpa: AIAssistantRecordedHighSchoolGPA | null
}

export type AIAssistantStudentGoals = {
  student: AIAssistantMissionControlStudent
  current_aim: AIAssistantCurrentAim
  degree_goals: AIAssistantDegreeGoal[]
  career_goal: string | null
  estimated_completion: AIAssistantEstimatedCompletion | null
}

export type AIAssistantStudentWhereabouts = {
  /** @format uuid */
  id: string
  full_name: string
  house: AIAssistantStudentWhereaboutsHouse | null
  campus_status: CampusStatusEnum
  campus_status_reason: CampusStatusReasonEnum | NullEnum | null
  campus_location: string
  boarding_status: BoardingStatusEnum
  sick_at_home: boolean
}

export type AIAssistantStudentWhereaboutsHouse = {
  id: number
  name: string
}

export type AIAssistantTeacher = {
  /** @format uuid */
  id: string
  full_name: string
  email: string
  departments: AIAssistantTeacherDepartment[]
}

export type AIAssistantTeacherDepartment = {
  id: number
  name: string
}

export type AIAssistantTeachingCalendar = {
  years: AIAssistantTeachingSummary[]
  semesters: AIAssistantTeachingSummary[]
  quarters: AIAssistantTeachingSummary[]
  breaks: AIAssistantTeachingCalendarBreak[]
}

export type AIAssistantTeachingCalendarBreak = {
  id: number
  name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
}

export type AIAssistantTeachingSummary = {
  id: number
  /** @format date */
  first_lesson_date: string | null
  /** @format date */
  latest_lesson_date: string | null
  /** @min 0 */
  teaching_days_so_far: number
}

export type AIAssistantWeeklyCategory = {
  /** @format uuid */
  id: string
  student: AIAssistantWeeklyCategoryStudent
  report: AIAssistantWeeklyCategoryReport
  assigned_category: AssignedCategory92AEnum
  assigned_category_display: string
  is_changed_by_downgrade: boolean
  is_upgraded_by_reward: boolean
  is_completed: boolean
}

export type AIAssistantWeeklyCategoryHouse = {
  id: number
  name: string
}

export type AIAssistantWeeklyCategoryReport = {
  /** @format uuid */
  id: string
  house: AIAssistantWeeklyCategoryHouse
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  status: BoardingAttendanceReportStatusEnum
  status_display: string
}

export type AIAssistantWeeklyCategoryStudent = {
  /** @format uuid */
  id: string
  full_name: string
}

export type AbacusInvoiceProduct = {
  id: number
  /**
   * @min -2147483648
   * @max 2147483647
   */
  abacus_id: number
  name: string
}

export type AbacusInvoiceProductRequest = {
  /**
   * @min -2147483648
   * @max 2147483647
   */
  abacus_id: number
  /** @minLength 1 */
  name: string
}

export type Absence = {
  /** @format uuid */
  id: string
  /** @format uuid */
  student: string
  student_str: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date?: string
  comment?: string
  /**
   * * `late` - Late
   * * `absent_excused` - Absent excused
   * * `absent_unexcused` - Absent unexcused
   * * `sick` - Sick
   */
  absence_type: AbsenceTypeEnum
  sick_report: string
  /**
   * * `regular` - Regular
   * * `sport` - Sport
   */
  class_type?: ClassTypeEnum
  added_by?: string
  last_modified_by?: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  source: SourceAaeEnum
  source_display: string
}

export type AbsenceAvailableFiltersResponse = {
  student: AvailableStringFilter
  absence_type: AvailableStringFilter
  school_year: AvailableStringFilter
}

export type AbsenceRequest = {
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date?: string
  comment?: string
  /**
   * * `late` - Late
   * * `absent_excused` - Absent excused
   * * `absent_unexcused` - Absent unexcused
   * * `sick` - Sick
   */
  absence_type: AbsenceTypeEnum
  /**
   * * `regular` - Regular
   * * `sport` - Sport
   */
  class_type?: ClassTypeEnum
  added_by?: string
  last_modified_by?: string
  students: string[]
}

/**
 * * `late` - Late
 * * `absent_excused` - Absent excused
 * * `absent_unexcused` - Absent unexcused
 * * `sick` - Sick
 */
export type AbsenceTypeEnum =
  | 'late'
  | 'absent_excused'
  | 'absent_unexcused'
  | 'sick'

export type AbsenceUpdate = {
  /** @format uuid */
  id: string
  /** @format uuid */
  student: string
  student_str: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date?: string
  comment?: string
  /**
   * * `late` - Late
   * * `absent_excused` - Absent excused
   * * `absent_unexcused` - Absent unexcused
   * * `sick` - Sick
   */
  absence_type: AbsenceTypeEnum
  sick_report: string
  /**
   * * `regular` - Regular
   * * `sport` - Sport
   */
  class_type?: ClassTypeEnum
  added_by?: string
  last_modified_by?: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  source: SourceAaeEnum
  source_display: string
}

export type AbsenceUpdateRequest = {
  /** @format uuid */
  student: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date?: string
  comment?: string
  /**
   * * `late` - Late
   * * `absent_excused` - Absent excused
   * * `absent_unexcused` - Absent unexcused
   * * `sick` - Sick
   */
  absence_type: AbsenceTypeEnum
  /**
   * * `regular` - Regular
   * * `sport` - Sport
   */
  class_type?: ClassTypeEnum
  added_by?: string
  last_modified_by?: string
}

export type AcademicAttendance = {
  /** @format uuid */
  id: string
  /** @format uuid */
  lesson: string
  student: StudentInAttendance
  is_present?: boolean
  is_late?: boolean
  is_absent_excused?: boolean
  is_absent_unexcused?: boolean
  is_sick?: boolean
  is_marked_automatically: boolean
  /** @format date-time */
  modified: string
  /** @format date-time */
  marked_at?: string | null
  course_drop: number | null
}

export type AcademicAttendanceAlert = {
  /** @format uuid */
  id: string
  teacher: string
  course: string
  /** @format date */
  lesson_date: string
  lesson_time: string
  /**
   * * `unresolved` - Unresolved
   * * `resolved` - Resolved
   */
  status?: AcademicAttendanceAlertStatusEnum
  is_read?: boolean
  is_notification_sent?: boolean
}

export type AcademicAttendanceAlertRequest = {
  /** @format date */
  lesson_date: string
  /** @minLength 1 */
  lesson_time: string
  /**
   * * `unresolved` - Unresolved
   * * `resolved` - Resolved
   */
  status?: AcademicAttendanceAlertStatusEnum
  is_read?: boolean
  is_notification_sent?: boolean
}

/**
 * * `unresolved` - Unresolved
 * * `resolved` - Resolved
 */
export type AcademicAttendanceAlertStatusEnum = 'unresolved' | 'resolved'

export type AcademicAttendanceRequest = {
  is_present?: boolean
  is_late?: boolean
  is_absent_excused?: boolean
  is_absent_unexcused?: boolean
  is_sick?: boolean
  unlock_edit?: boolean
  /** @format date-time */
  marked_at?: string | null
}

/**
 * * `5` - Not started
 * * `10` - Offered
 * * `15` - Waitlist offered
 * * `20` - Accepted
 * * `25` - Waitlist accepted
 * * `30` - No reply
 * * `35` - Rejected
 * * `40` - Lost
 */
export type AcademicOfferStatusEnum = 5 | 10 | 15 | 20 | 25 | 30 | 35 | 40

/**
 * * `5` - Not started
 * * `10` - Documents requested
 * * `15` - Assessment scheduled
 * * `20` - Final review
 * * `25` - Accepted
 * * `30` - Waitlist
 * * `35` - On hold
 * * `40` - Rejected
 */
export type AcademicReviewStatusEnum = 5 | 10 | 15 | 20 | 25 | 30 | 35 | 40

/**
 * * `15` - Enrolled
 * * `30` - Active
 * * `45` - Left
 * * `60` - Alumni
 */
export type AcademicStatusEnum = 15 | 30 | 45 | 60

export type AccGrade = {
  id: number
  quarter?: number | null
  aim: number | null
  semester?: number | null
  year?: number | null
  student_aim_for_course: number
  /** @maxLength 2000 */
  comment?: string
  comment_author: string
  comment_created: string
  comment_last_modified_by: string
  can_manage_comment: boolean
  /** @format uuid */
  course: string
  grade: string | null
  target_grade_status: TargetGradeStatusEnum
  weighted_percents: number | null
  course_str: string
  student_str: string
  /** @format uuid */
  student: string
  target_grade: string | null
  teacher_str: string
  teacher_id: string
  custom_grade?: AccGradeCustomGradeEnum | BlankEnum
  course_drop_id: number | null
}

/**
 * * `EI` - Grade Exempt: Illness, Credit Awarded
 * * `EF` - Grade Exempt: Force-Majeure, Credit Awarded
 * * `EO` - Grade Exempt: Other, Credit Awarded
 * * `IC` - Incomplete
 * * `SC` - Successfully Completed
 * * `CA` - Successfully Completed & Credit Awarded
 */
export type AccGradeCustomGradeEnum = 'EI' | 'EF' | 'EO' | 'IC' | 'SC' | 'CA'

export type AccGradeRequest = {
  quarter?: number | null
  semester?: number | null
  year?: number | null
  /** @maxLength 2000 */
  comment?: string
  /** @format uuid */
  course: string
  /** @format uuid */
  student: string
  custom_grade?: AccGradeCustomGradeEnum | BlankEnum
}

/**
 * * `green_paper` - Green paper
 * * `pink_paper` - Pink paper
 * * `breaks` - Breaks
 * * `prompt` - Prompt
 * * `use_laptop` - Use of laptop
 * * `reader_pen` - Reader
 * * `own_room` - Own room
 * * `scribe` - Scribe
 */
export type AccessArrangementsEnum =
  | 'green_paper'
  | 'pink_paper'
  | 'breaks'
  | 'prompt'
  | 'use_laptop'
  | 'reader_pen'
  | 'own_room'
  | 'scribe'

export type Accommodation = {
  id: number
  /** @format uuid */
  student: string
  student_str: string
  room: number
  /** @format date */
  start_date: string
  /** @format date */
  end_date?: string | null
  is_temporary_movement?: boolean
  /**
   * * `isolation` - Isolation
   * * `quarantine` - Quarantine
   * * `sleepover` - Sleepover
   */
  temporary_movement_type: TemporaryMovementTypeEnum
  temporary_movement_type_display: string
  house_str: string
  house: number
  room_str: string
  room_code: string
  room_name: string
  /** @format date-time */
  deleted: string | null
  is_room_only_for_student?: boolean
}

export type AccommodationRequest = {
  /** @format uuid */
  student: string
  room: number
  /** @format date */
  start_date: string
  /** @format date */
  end_date?: string | null
  is_temporary_movement?: boolean
  /**
   * * `isolation` - Isolation
   * * `quarantine` - Quarantine
   * * `sleepover` - Sleepover
   */
  temporary_movement_type: TemporaryMovementTypeEnum
  is_room_only_for_student?: boolean
  added_by?: string
  last_modified_by?: string
}

export type AccountOverdueEnum =
  | 'settled'
  | 'first_reminder'
  | 'second_reminder'
  | 'app_locked'

export type Achievement = {
  id: number
  /** @maxLength 256 */
  name: string
}

export type AchievementAndEffortProgressComment = {
  /** @format uuid */
  id: string
  positive_comments?: string[]
  to_be_improved_comments?: string[]
}

export type AchievementAndEffortProgressCommentRequest = {
  positive_comments?: string[]
  to_be_improved_comments?: string[]
}

export type AchievementHolder = {
  id: number
  /** @format uuid */
  student: string
  student_str: string
  achievement: number
  achievement_str: string
  /** @format date */
  date: string
}

/**
 * * `generate` - Generate Comment
 * * `improve` - Improve Comment
 */
export type ActionType421Enum = 'generate' | 'improve'

export type ActivityMove = {
  /** @format uuid */
  activity: string
  hour: number
  hour_display: string
  day_display: string
  day: string
  duration: string
  artisans_off_work: SimpleUser[]
}

/**
 * * `5` - Preferred postal address
 * * `10` - Billing address
 * * `15` - Additional address
 */
export type AddressTypeEnum = 5 | 10 | 15

export type AdjacentBoardingAttendanceReport = {
  id: number
  /** @format date-time */
  created: string
}

export type AdjacentNightAttendanceReport = {
  id: number
  /** @format date-time */
  created: string
}

export type AdminAbsenceRecovery = {
  id: string
  polymorphic_ctype: number | null
  /** @format date-time */
  datetime_from: string
  /** @format date-time */
  datetime_to: string
  school_programme: SchoolProgrammeRecovery
  programme_attendance_report: ProgrammeAttendanceReportRecovery
  assigned_to_str: string
  /** @format uuid */
  assigned_to?: string | null
  description?: string
  /** via annotate */
  location: string
  is_editable: boolean
  /**
   * * `AbsenceRecovery` - AbsenceRecovery
   * * `LunchtimeRecovery` - LunchtimeRecovery
   * * `HighSchoolLunchtimeRecovery` - HighSchoolLunchtimeRecovery
   */
  resourcetype: RecoveryResourcetypeEnum
}

export type AdminAbsenceRecoveryRequest = {
  /** @format uuid */
  assigned_to?: string | null
  description?: string
  /**
   * * `AbsenceRecovery` - AbsenceRecovery
   * * `LunchtimeRecovery` - LunchtimeRecovery
   * * `HighSchoolLunchtimeRecovery` - HighSchoolLunchtimeRecovery
   */
  resourcetype: RecoveryResourcetypeEnum
}

export type AdminAdditionalAusgang = {
  id: string
  /**
   * * `pending` - Pending
   * * `pending_attendees` - Pending attendees
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `done` - Done
   * * `cancelled` - Cancelled
   * * `expired` - Expired
   */
  status?: MeritRewardStatusEnum
  student: string
  /** @format date-time */
  created: string
  /** @format date-time */
  date_from: string
  /** @format date-time */
  date_to: string
  leave_details: string
  /** @maxLength 128 */
  absence_name: string
  participants: MeritRewardParticipant[]
  decision_comment?: string
  /** @format uuid */
  campus_absence?: string | null
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type AdminAlertLesson = {
  /** @format uuid */
  id: string
  /** @format date */
  date: string
  display_hour: string
  course: string
  teacher: string
}

export type AdminAlertLessonRequest = {
  /** @format date */
  date: string
}

export type AdminAlertSchoolBreak = {
  id: number
  /** @maxLength 100 */
  name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
}

export type AdminAlertSchoolBreakRequest = {
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
}

export type AdminApproveIdpPlan = {
  /** @format date */
  parent_approval_deadline?: string | null
  parent_approval_needed?: boolean
}

export type AdminApproveIdpPlanRequest = {
  /** @format date */
  parent_approval_deadline?: string | null
  parent_approval_needed?: boolean
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type AdminBoard = {
  id: string
  pinned_in_apps?: AppTypeEnum[]
  pin_order: number
  created_by?: string
  widget: AdminWidget
  show_in_idp_app: boolean
  show_in_parents_app: boolean
  app_visibility: AppVisibilityEnum
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 128 */
  name: string
  status: WidgetStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  display_from: string
  /** @format date-time */
  display_to?: string | null
  publish_on_infoboards?: boolean
  widgets_str: string[]
  widget_str: string
  /**
   * * `square` - Square
   * * `framed` - Framed
   * * `fullscreen` - Fullscreen
   */
  layout?: LayoutEnum
  /**
   * @min -2147483648
   * @max 2147483647
   */
  display_time?: number
  /**
   * @min -2147483648
   * @max 2147483647
   */
  sort_value?: number
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type AdminBoardDraft = {
  id: string
  /** @maxLength 128 */
  name?: string
  created_by?: string
  widget: AdminWidget
  /** @format date-time */
  display_from?: string | null
  /** @format date-time */
  display_to?: string | null
  publish_on_infoboards?: boolean
  /**
   * * `square` - Square
   * * `framed` - Framed
   * * `fullscreen` - Fullscreen
   */
  layout?: LayoutEnum
  /**
   * @min -2147483648
   * @max 2147483647
   */
  display_time?: number
  status: WidgetStatusEnum
  widget_str: string
  show_in_idp_app: boolean
  show_in_parents_app: boolean
  pinned_in_apps?: AppTypeEnum[]
  pin_order: number
}

export type AdminBoardDraftList = {
  id: string
  /** @maxLength 128 */
  name: string
  /** @maxLength 128 */
  widget_str: string
  /** @format date-time */
  created: string
  created_by: string
  /**
   * * `unpublished` - Unpublished
   * * `published` - Published
   * * `scheduled` - Scheduled
   * * `draft` - Draft
   */
  status?: WidgetStatusEnum
  show_in_idp_app: boolean
  show_in_parents_app: boolean
  publish_on_infoboards?: boolean
  pinned_in_apps: any[]
  pin_order: number
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type AdminBoardDraftRequest = {
  /** @maxLength 128 */
  name?: string
  created_by?: string
  widget: AdminWidgetRequest
  /** @format date-time */
  display_from?: string | null
  /** @format date-time */
  display_to?: string | null
  publish_on_infoboards?: boolean
  /**
   * * `square` - Square
   * * `framed` - Framed
   * * `fullscreen` - Fullscreen
   */
  layout?: LayoutEnum
  /**
   * @min -2147483648
   * @max 2147483647
   */
  display_time?: number
  pinned_in_apps?: AppTypeEnum[]
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type AdminBoardRequest = {
  pinned_in_apps?: AppTypeEnum[]
  created_by?: string
  widget: AdminWidgetRequest
  /**
   * @minLength 1
   * @maxLength 128
   */
  name: string
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  display_from: string
  /** @format date-time */
  display_to?: string | null
  publish_on_infoboards?: boolean
  /**
   * * `square` - Square
   * * `framed` - Framed
   * * `fullscreen` - Fullscreen
   */
  layout?: LayoutEnum
  /**
   * @min -2147483648
   * @max 2147483647
   */
  display_time?: number
  /**
   * @min -2147483648
   * @max 2147483647
   */
  sort_value?: number
}

export type AdminBoardingAnnouncementWidget = {
  id: string
  /** @default "#FD8F5E" */
  bg_color: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  status: WidgetStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  pinned_at: string | null
  pinned_in_apps: string[]
  /**
   * @min -2147483648
   * @max 2147483647
   */
  pin_order: number | null
  from_automation?: boolean
  /** @maxLength 90 */
  text: string
  polymorphic_ctype: number | null
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminBoardingAnnouncementWidgetRequest = {
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * @minLength 1
   * @maxLength 90
   */
  text: string
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminBookTheCafe = {
  id: string
  /**
   * * `pending` - Pending
   * * `pending_attendees` - Pending attendees
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `done` - Done
   * * `cancelled` - Cancelled
   * * `expired` - Expired
   */
  status?: MeritRewardStatusEnum
  student: string
  /** @format date-time */
  created: string
  /** @format date */
  date: string
  /** @maxLength 128 */
  movie: string
  participants: MeritRewardParticipant[]
  decision_comment?: string
  /** @format uuid */
  campus_absence?: string | null
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type AdminCasualAnyday = {
  id: string
  /**
   * * `pending` - Pending
   * * `pending_attendees` - Pending attendees
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `done` - Done
   * * `cancelled` - Cancelled
   * * `expired` - Expired
   */
  status?: MeritRewardStatusEnum
  student: string
  /** @format date-time */
  created: string
  /** @format date */
  date: string
  decision_comment?: string
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type AdminCategoryUpgrade = {
  id: string
  /**
   * * `pending` - Pending
   * * `pending_attendees` - Pending attendees
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `done` - Done
   * * `cancelled` - Cancelled
   * * `expired` - Expired
   */
  status?: MeritRewardStatusEnum
  student: string
  /** @format date-time */
  created: string
  decision_comment?: string
  category_before_upgrade: string
  category_after_upgrade: string
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type AdminCelebrationWidget = {
  id: string
  /** @default "#FF607F" */
  bg_color: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  status: WidgetStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  pinned_at: string | null
  pinned_in_apps: string[]
  /**
   * @min -2147483648
   * @max 2147483647
   */
  pin_order: number | null
  from_automation?: boolean
  polymorphic_ctype: number | null
  students: string[]
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminCelebrationWidgetRequest = {
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminCeleryTaskStatus = {
  id: number
  /** @format uuid */
  uuid: string
  timetable: number | null
  /** @format date-time */
  started_at: string
  /** @format date-time */
  ended_at: string | null
  execution_time: string
  state: StateEnum
  history: AdminCeleryTaskStatusHistory[]
  task_arguments: AdminCeleryTaskStatusTaskArguments | null
}

export type AdminCeleryTaskStatusHistory = {
  id: number
  state: StateEnum
  done_in: number
  /**
   * time when state was changed
   * @format date-time
   */
  timestamp: string
  /**
   * in seconds
   * @format decimal
   * @pattern ^-?\d{0,9}(?:\.\d{0,2})?$
   */
  execution_time: string
}

export type AdminCeleryTaskStatusList = {
  id: number
  /** @format uuid */
  uuid: string
  timetable: number | null
  /** @format date-time */
  started_at: string
  /** @format date-time */
  ended_at: string | null
  execution_time: string
  state: StateEnum
  /** via annotate */
  latest_state: string | null
}

export type AdminCeleryTaskStatusTaskArguments = {
  /** @format date */
  start_date: string
  /** @format date */
  today: string
  weeks_to_generate: number
  /**
   * * `ImportSource.MANUAL` - ImportSource.MANUAL
   * * `ImportSource.AUTOMATIC` - ImportSource.AUTOMATIC
   */
  import_source: ImportSourceEnum
  user: SimpleUser | null
}

export type AdminCityBrunchZurich = {
  id: string
  /**
   * * `pending` - Pending
   * * `pending_attendees` - Pending attendees
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `done` - Done
   * * `cancelled` - Cancelled
   * * `expired` - Expired
   */
  status?: MeritRewardStatusEnum
  student: string
  /** @format date-time */
  created: string
  /** @format date */
  date: string
  participants: MeritRewardParticipant[]
  decision_comment?: string
  /** @format uuid */
  campus_absence?: string | null
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type AdminClockWidget = {
  id: string
  /** @default "#000000" */
  bg_color: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  status: WidgetStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  pinned_at: string | null
  pinned_in_apps: string[]
  /**
   * @min -2147483648
   * @max 2147483647
   */
  pin_order: number | null
  from_automation?: boolean
  polymorphic_ctype: number | null
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminClockWidgetRequest = {
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminCongratulationsWidget = {
  id: string
  /** @default "#FFFFFF" */
  bg_color: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  status: WidgetStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  pinned_at: string | null
  pinned_in_apps: string[]
  /**
   * @min -2147483648
   * @max 2147483647
   */
  pin_order: number | null
  from_automation?: boolean
  polymorphic_ctype: number | null
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminCongratulationsWidgetRequest = {
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminDashboardUser = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  first_name?: string
  /** @maxLength 128 */
  last_name?: string
  /**
   * Email address
   * @format email
   * @maxLength 254
   */
  email: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AdminEventAgendaWidget = {
  id: string
  /** @default "#5965EF" */
  bg_color: string
  agenda_items: AgendaItem[]
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  status: WidgetStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  pinned_at: string | null
  pinned_in_apps: string[]
  /**
   * @min -2147483648
   * @max 2147483647
   */
  pin_order: number | null
  from_automation?: boolean
  /** @maxLength 90 */
  header: string
  /** @format date */
  date_from: string
  /** @format date */
  date_to?: string | null
  /** @format time */
  time_from: string
  /** @format time */
  time_to: string
  polymorphic_ctype: number | null
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AdminEventAgendaWidgetRequest = {
  agenda_items: AgendaItemRequest[]
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * @minLength 1
   * @maxLength 90
   */
  header: string
  /** @format date */
  date_from: string
  /** @format date */
  date_to?: string | null
  /** @format time */
  time_from: string
  /** @format time */
  time_to: string
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AdminEventInfoWidget = {
  id: string
  bg_color: string
  link?: AdminLinkWithQRCode | null
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  status: WidgetStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  pinned_at: string | null
  pinned_in_apps: string[]
  /**
   * @min -2147483648
   * @max 2147483647
   */
  pin_order: number | null
  from_automation?: boolean
  /** @maxLength 90 */
  header: string
  /** @maxLength 400 */
  message?: string
  /** @maxLength 64 */
  classroom_shortcut?: string
  /** @maxLength 64 */
  location?: string
  /** @maxLength 64 */
  course_name?: string
  show_qr_code?: boolean
  /** @maxLength 64 */
  qr_code_text?: string
  polymorphic_ctype: number | null
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AdminEventInfoWidgetRequest = {
  /** @minLength 1 */
  bg_color: string
  link?: AdminLinkWithQRCodeRequest | null
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * @minLength 1
   * @maxLength 90
   */
  header: string
  /** @maxLength 400 */
  message?: string
  /** @maxLength 64 */
  classroom_shortcut?: string
  /** @maxLength 64 */
  location?: string
  /** @maxLength 64 */
  course_name?: string
  show_qr_code?: boolean
  /** @maxLength 64 */
  qr_code_text?: string
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

/** Shared text and QR validation for event media widgets. */
export type AdminEventInfoWithPhotoWidget = {
  id: string
  bg_color: string
  link?: AdminLinkWithQRCode | null
  photo_data: Image
  photo_in_my_rosenberg_idp_data: Image
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  status: WidgetStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  pinned_at: string | null
  pinned_in_apps: string[]
  /**
   * @min -2147483648
   * @max 2147483647
   */
  pin_order: number | null
  from_automation?: boolean
  /** @maxLength 100 */
  header: string
  include_message?: boolean
  /** @maxLength 400 */
  message?: string
  crop_my_rosenberg_idp_photo_separately?: boolean
  /** @maxLength 64 */
  location?: string
  /** @maxLength 64 */
  course_name?: string
  show_qr_code?: boolean
  /** @maxLength 64 */
  qr_code_text?: string
  polymorphic_ctype: number | null
  photo: string
  photo_in_my_rosenberg_idp?: string | null
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

/** Shared text and QR validation for event media widgets. */
export type AdminEventInfoWithPhotoWidgetRequest = {
  /** @minLength 1 */
  bg_color: string
  link?: AdminLinkWithQRCodeRequest | null
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * @minLength 1
   * @maxLength 100
   */
  header: string
  include_message?: boolean
  /** @maxLength 400 */
  message?: string
  crop_my_rosenberg_idp_photo_separately?: boolean
  /** @maxLength 64 */
  location?: string
  /** @maxLength 64 */
  course_name?: string
  show_qr_code?: boolean
  /** @maxLength 64 */
  qr_code_text?: string
  /** @minLength 1 */
  photo: string
  /** @minLength 1 */
  photo_in_my_rosenberg_idp?: string | null
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminFeed = {
  id: string
  /** @format date-time */
  created: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at: string
  /** @format date-time */
  display_from: string
  /**
   * * `unpublished` - Unpublished
   * * `published` - Published
   * * `scheduled` - Scheduled
   * * `draft` - Draft
   */
  status?: WidgetStatusEnum
  name: string
  board_id: string
  board_name: string
  board_status: string
  content_type: FeedContentTypeEnum
  student_name: string
  pinned_in_apps: any[]
  pin_order: number
}

export type AdminFeedOptions = {
  model_names: StringOption[]
  statuses: StringOption[]
}

export type AdminHighSchoolLunchtimeRecovery = {
  id: string
  polymorphic_ctype: number | null
  /** @format date-time */
  datetime_from: string
  /** @format date-time */
  datetime_to: string
  school_programme: SchoolProgrammeRecovery
  programme_attendance_report: ProgrammeAttendanceReportRecovery
  assigned_to_str: string
  /** @format uuid */
  assigned_to: string | null
  description?: string
  /** via annotate */
  location: string
  is_editable: boolean
  /**
   * * `AbsenceRecovery` - AbsenceRecovery
   * * `LunchtimeRecovery` - LunchtimeRecovery
   * * `HighSchoolLunchtimeRecovery` - HighSchoolLunchtimeRecovery
   */
  resourcetype: RecoveryResourcetypeEnum
}

export type AdminHighSchoolLunchtimeRecoveryRequest = {
  description?: string
  /**
   * * `AbsenceRecovery` - AbsenceRecovery
   * * `LunchtimeRecovery` - LunchtimeRecovery
   * * `HighSchoolLunchtimeRecovery` - HighSchoolLunchtimeRecovery
   */
  resourcetype: RecoveryResourcetypeEnum
}

export type AdminImportRequest = {
  /** @min 1 */
  num_of_weeks_to_generate: number
  /** @format date */
  start_date: string
  timetable: number
  /** @default false */
  temporary_school_break_schedule?: boolean
}

export type AdminImportResponse = {
  task_id: string
}

/** Shared text and QR validation for event media widgets. */
export type AdminInfoWithVideoWidget = {
  id: string
  bg_color: string
  link?: AdminLinkWithQRCode | null
  video?: string | null
  video_data: Video | null
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  status: WidgetStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  pinned_at: string | null
  pinned_in_apps: string[]
  /**
   * @min -2147483648
   * @max 2147483647
   */
  pin_order: number | null
  from_automation?: boolean
  /** @maxLength 100 */
  header: string
  include_message?: boolean
  /** @maxLength 400 */
  message?: string
  /** @maxLength 64 */
  location?: string
  /** @maxLength 64 */
  course_name?: string
  show_qr_code?: boolean
  /** @maxLength 64 */
  qr_code_text?: string
  polymorphic_ctype: number | null
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

/** Shared text and QR validation for event media widgets. */
export type AdminInfoWithVideoWidgetRequest = {
  /** @minLength 1 */
  bg_color: string
  link?: AdminLinkWithQRCodeRequest | null
  /** @minLength 1 */
  video?: string | null
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * @minLength 1
   * @maxLength 100
   */
  header: string
  include_message?: boolean
  /** @maxLength 400 */
  message?: string
  /** @maxLength 64 */
  location?: string
  /** @maxLength 64 */
  course_name?: string
  show_qr_code?: boolean
  /** @maxLength 64 */
  qr_code_text?: string
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminInfoboardsSettings = {
  id: number
  /** Generate automated alerts about teacher's covers */
  generate_teacher_cover_alerts?: boolean
  /** Generate automated alerts about school breaks (1 week before start date) */
  generate_school_break_alerts?: boolean
  /** Show 2 most recent articles */
  show_recent_articles?: boolean
}

export type AdminInfoboardsSettingsRequest = {
  /** Generate automated alerts about teacher's covers */
  generate_teacher_cover_alerts?: boolean
  /** Generate automated alerts about school breaks (1 week before start date) */
  generate_school_break_alerts?: boolean
  /** Show 2 most recent articles */
  show_recent_articles?: boolean
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AdminJournalItem = {
  id: string
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  article_url: AdminLinkWithQRCode
  /**
   * @min 0
   * @max 2147483647
   */
  order?: number
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AdminJournalItemRequest = {
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  article_url: AdminLinkWithQRCodeRequest
  /**
   * @min 0
   * @max 2147483647
   */
  order?: number
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AdminJournalWidget = {
  id: string
  /** @default "#EBCFCF" */
  bg_color: string
  journal_items: AdminJournalItem[]
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  status: WidgetStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  pinned_at: string | null
  pinned_in_apps: string[]
  /**
   * @min -2147483648
   * @max 2147483647
   */
  pin_order: number | null
  from_automation?: boolean
  polymorphic_ctype: number | null
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AdminJournalWidgetRequest = {
  journal_items: AdminJournalItemRequest[]
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type AdminLinkWithQRCode = {
  id: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /**
   * @format uri
   * @maxLength 200
   */
  url: string
  /** @format uri */
  qr_code: string
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type AdminLinkWithQRCodeRequest = {
  /**
   * @format uri
   * @minLength 1
   * @maxLength 200
   */
  url: string
}

export type AdminLunchtimeRecovery = {
  id: string
  polymorphic_ctype: number | null
  /** @format date-time */
  datetime_from: string
  /** @format date-time */
  datetime_to: string
  school_programme: SchoolProgrammeRecovery
  programme_attendance_report: ProgrammeAttendanceReportRecovery
  assigned_to_str: string
  /** @format uuid */
  assigned_to?: string | null
  description?: string
  /** via annotate */
  location: string
  is_editable: boolean
  /**
   * * `AbsenceRecovery` - AbsenceRecovery
   * * `LunchtimeRecovery` - LunchtimeRecovery
   * * `HighSchoolLunchtimeRecovery` - HighSchoolLunchtimeRecovery
   */
  resourcetype: RecoveryResourcetypeEnum
}

export type AdminLunchtimeRecoveryRequest = {
  /** @format uuid */
  assigned_to?: string | null
  description?: string
  /**
   * * `AbsenceRecovery` - AbsenceRecovery
   * * `LunchtimeRecovery` - LunchtimeRecovery
   * * `HighSchoolLunchtimeRecovery` - HighSchoolLunchtimeRecovery
   */
  resourcetype: RecoveryResourcetypeEnum
}

export type AdminMakeAnImpact = {
  id: string
  /**
   * * `pending` - Pending
   * * `pending_attendees` - Pending attendees
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `done` - Done
   * * `cancelled` - Cancelled
   * * `expired` - Expired
   */
  status?: MeritRewardStatusEnum
  student: string
  /** @format date-time */
  created: string
  decision_comment?: string
  /**
   * @min 1
   * @max 2147483647
   */
  points_donated: number
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type AdminMenuWidget = {
  id: string
  /** @default "#00AC69" */
  bg_color: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  status: WidgetStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  pinned_at: string | null
  pinned_in_apps: string[]
  /**
   * @min -2147483648
   * @max 2147483647
   */
  pin_order: number | null
  from_automation?: boolean
  polymorphic_ctype: number | null
  menu?: number | null
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminMenuWidgetRequest = {
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  menu?: number | null
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminMeritReward =
  | AdminCasualAnyday
  | AdminRoomService
  | AdminBookTheCafe
  | AdminCityBrunchZurich
  | AdminAdditionalAusgang
  | AdminCategoryUpgrade
  | AdminStopTheClock
  | AdminMakeAnImpact
  | AdminOnCampusAusgang

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type AdminMessage = {
  id: number
  /**
   * * `alert` - Alert
   * * `notice` - Notice
   */
  type: MessageTypeEnum
  name: string
  content: string
  /** @format date */
  display_to: string
  is_created_manually?: boolean
  author?: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  lesson: AdminAlertLesson
  school_break: AdminAlertSchoolBreak
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type AdminMessageRequest = {
  /**
   * * `alert` - Alert
   * * `notice` - Notice
   */
  type: MessageTypeEnum
  /** @minLength 1 */
  name: string
  /** @minLength 1 */
  content: string
  /** @format date */
  display_to: string
  is_created_manually?: boolean
  author?: string
}

export type AdminMysteryLinkWidget = {
  id: string
  /** @default "#9561E8" */
  bg_color: string
  approved_mystery_links: AdminStudentMysteryLink[]
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  status: WidgetStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  pinned_at: string | null
  pinned_in_apps: string[]
  /**
   * @min -2147483648
   * @max 2147483647
   */
  pin_order: number | null
  from_automation?: boolean
  polymorphic_ctype: number | null
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminMysteryLinkWidgetRequest = {
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminOnCampusAusgang = {
  id: string
  /**
   * * `pending` - Pending
   * * `pending_attendees` - Pending attendees
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `done` - Done
   * * `cancelled` - Cancelled
   * * `expired` - Expired
   */
  status?: MeritRewardStatusEnum
  student: string
  /** @format date-time */
  created: string
  /** @format date-time */
  date_from: string
  /** @format date-time */
  date_to: string
  leave_details: string
  /** @maxLength 128 */
  absence_name: string
  participants: MeritRewardParticipant[]
  decision_comment?: string
  /** @format uuid */
  campus_absence?: string | null
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type AdminParentApproveIdpPlanRequest = {
  /** @minLength 1 */
  parent_approve_comment: string
}

export type AdminPosterWidget = {
  id: string
  /** @default "#808080" */
  bg_color: string
  image_data: Image
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  status: WidgetStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  pinned_at: string | null
  pinned_in_apps: string[]
  /**
   * @min -2147483648
   * @max 2147483647
   */
  pin_order: number | null
  from_automation?: boolean
  polymorphic_ctype: number | null
  image: string
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminPosterWidgetRequest = {
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /** @minLength 1 */
  image: string
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminQuarter = {
  id: number
  /** @maxLength 32 */
  name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  semester?: number
}

export type AdminQuarterRequest = {
  /**
   * @minLength 1
   * @maxLength 32
   */
  name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  semester?: number
}

export type AdminRecovery =
  | AdminAbsenceRecovery
  | AdminLunchtimeRecovery
  | AdminHighSchoolLunchtimeRecovery

export type AdminRecoveryRequest =
  | AdminAbsenceRecoveryRequest
  | AdminLunchtimeRecoveryRequest
  | AdminHighSchoolLunchtimeRecoveryRequest

export type AdminRoomService = {
  id: string
  /**
   * * `pending` - Pending
   * * `pending_attendees` - Pending attendees
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `done` - Done
   * * `cancelled` - Cancelled
   * * `expired` - Expired
   */
  status?: MeritRewardStatusEnum
  student: string
  /** @format date-time */
  created: string
  /** @format date */
  date: string
  house: string
  room: string
  decision_comment?: string
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AdminSchoolYear = {
  id: number
  /** @maxLength 32 */
  name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  semesters: AdminSemester[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AdminSchoolYearRequest = {
  /**
   * @minLength 1
   * @maxLength 32
   */
  name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  semesters: AdminSemesterRequest[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AdminSemester = {
  id: number
  /** @maxLength 32 */
  name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  year?: number | null
  quarters: AdminQuarter[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AdminSemesterRequest = {
  /**
   * @minLength 1
   * @maxLength 32
   */
  name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  year?: number | null
  quarters: AdminQuarterRequest[]
}

export type AdminSiteSettings = {
  id: number
  /** Allow teachers to set target grades */
  target_grades_set_allowed?: boolean
  /**
   * Setting target grades starts at
   * @format date-time
   */
  target_grades_start?: string | null
  /**
   * Setting target grades ends at
   * @format date-time
   */
  target_grades_end?: string | null
  /** Edit values, or set from pending only */
  target_grades_set_all?: boolean
  /**
   * Disable generating tasks from datetime
   * @format date-time
   */
  disable_generating_tasks_from?: string | null
  /**
   * Disable generating tasks to datetime
   * @format date-time
   */
  disable_generating_tasks_to?: string | null
  /** Disable generating take student to destination task */
  disable_take_student_to_destination_task?: boolean
}

export type AdminSiteSettingsRequest = {
  /** Allow teachers to set target grades */
  target_grades_set_allowed?: boolean
  /**
   * Setting target grades starts at
   * @format date-time
   */
  target_grades_start?: string | null
  /**
   * Setting target grades ends at
   * @format date-time
   */
  target_grades_end?: string | null
  /** Edit values, or set from pending only */
  target_grades_set_all?: boolean
  /**
   * Disable generating tasks from datetime
   * @format date-time
   */
  disable_generating_tasks_from?: string | null
  /**
   * Disable generating tasks to datetime
   * @format date-time
   */
  disable_generating_tasks_to?: string | null
  /** Disable generating take student to destination task */
  disable_take_student_to_destination_task?: boolean
}

export type AdminStopTheClock = {
  id: string
  /**
   * * `pending` - Pending
   * * `pending_attendees` - Pending attendees
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `done` - Done
   * * `cancelled` - Cancelled
   * * `expired` - Expired
   */
  status?: MeritRewardStatusEnum
  student: string
  /** @format date-time */
  created: string
  decision_comment?: string
  original_date: string
  prolonged_date: string
  /** @format uuid */
  campus_absence?: string | null
  participants: SimpleCampusAbsenceStudent[]
  leave_details: string
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type AdminStudentAnnouncement = {
  id: string
  /** @format uuid */
  student: string
  student_str: string
  /**
   * * `pending` - Pending
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `deleted` - Deleted
   * * `archived` - Archived
   */
  status?: StudentContentStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  display_to?: string | null
  emojis?: string[]
  /** @maxLength 100 */
  text: string
  /** @maxLength 7 */
  bg_color?: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  verbose_name: string
  /**
   * * `StudentMysteryLink` - StudentMysteryLink
   * * `StudentAnnouncement` - StudentAnnouncement
   * * `StudentPic` - StudentPic
   */
  resourcetype: StudentContentResourcetypeEnum
}

export type AdminStudentAnnouncementWidget = {
  id: string
  /** @default "#FFFFFF" */
  bg_color: string
  approved_student_announcements: AdminStudentAnnouncement[]
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  status: WidgetStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  pinned_at: string | null
  pinned_in_apps: string[]
  /**
   * @min -2147483648
   * @max 2147483647
   */
  pin_order: number | null
  from_automation?: boolean
  polymorphic_ctype: number | null
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminStudentAnnouncementWidgetRequest = {
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminStudentApplicationMedication = {
  id: number
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  medicine_name: string
  dosage: string
  reason_and_diagnosis: string
  /**
   * * `new` - New
   * * `added` - Added
   * * `ignored` - Ignored
   */
  status?: AdminStudentApplicationMedicationStatusEnum
  /** @format date-time */
  processed_at?: string | null
  /** @format uuid */
  student?: string | null
  medicine_plan?: string | null
  /** @format uuid */
  processed_by?: string | null
}

export type AdminStudentApplicationMedicationRequest = {
  /** @minLength 1 */
  medicine_name: string
  /** @minLength 1 */
  dosage: string
  /** @minLength 1 */
  reason_and_diagnosis: string
  /**
   * * `new` - New
   * * `added` - Added
   * * `ignored` - Ignored
   */
  status?: AdminStudentApplicationMedicationStatusEnum
  /** @format date-time */
  processed_at?: string | null
  /** @format uuid */
  student?: string | null
  /** @minLength 1 */
  medicine_plan?: string | null
  /** @format uuid */
  processed_by?: string | null
}

/**
 * * `new` - New
 * * `added` - Added
 * * `ignored` - Ignored
 */
export type AdminStudentApplicationMedicationStatusEnum =
  | 'new'
  | 'added'
  | 'ignored'

export type AdminStudentArt = {
  id: number
  image: string
  image_data: Image
  /** @maxLength 255 */
  name: string
  /** @maxLength 255 */
  author?: string
  /**
   * * `student` - Student
   * * `author` - Author
   */
  author_type: AuthorTypeEnum
  /**
   * @min 0
   * @max 2147483647
   */
  order?: number
  /** @format uuid */
  student?: string | null
  student_data: SimpleUserOptimized
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AdminStudentArtGalleryWidget = {
  id: string
  /** @default "#808080" */
  bg_color: string
  arts: AdminStudentArt[]
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  status: WidgetStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  pinned_at: string | null
  pinned_in_apps: string[]
  /**
   * @min -2147483648
   * @max 2147483647
   */
  pin_order: number | null
  from_automation?: boolean
  polymorphic_ctype: number | null
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AdminStudentArtGalleryWidgetRequest = {
  arts: AdminStudentArtRequest[]
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminStudentArtRequest = {
  /** @minLength 1 */
  image: string
  /**
   * @minLength 1
   * @maxLength 255
   */
  name: string
  /** @maxLength 255 */
  author?: string
  /**
   * * `student` - Student
   * * `author` - Author
   */
  author_type: AuthorTypeEnum
  /**
   * @min 0
   * @max 2147483647
   */
  order?: number
  /** @format uuid */
  student?: string | null
}

export type AdminStudentContent =
  | AdminStudentMysteryLink
  | AdminStudentAnnouncement
  | AdminStudentPic

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type AdminStudentMysteryLink = {
  id: string
  /** @format uuid */
  student: string
  student_str: string
  /**
   * * `pending` - Pending
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `deleted` - Deleted
   * * `archived` - Archived
   */
  status?: StudentContentStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  display_to?: string | null
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  link: AdminLinkWithQRCode
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  verbose_name: string
  /**
   * * `StudentMysteryLink` - StudentMysteryLink
   * * `StudentAnnouncement` - StudentAnnouncement
   * * `StudentPic` - StudentPic
   */
  resourcetype: StudentContentResourcetypeEnum
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type AdminStudentPic = {
  id: string
  /** @format uuid */
  student: string
  student_str: string
  /**
   * * `pending` - Pending
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `deleted` - Deleted
   * * `archived` - Archived
   */
  status?: StudentContentStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  display_to?: string | null
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  image: Image
  /** @maxLength 50 */
  title?: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  verbose_name: string
  /**
   * * `StudentMysteryLink` - StudentMysteryLink
   * * `StudentAnnouncement` - StudentAnnouncement
   * * `StudentPic` - StudentPic
   */
  resourcetype: StudentContentResourcetypeEnum
}

export type AdminStudentsPicWidget = {
  id: string
  /** @default "#FFFFFF" */
  bg_color: string
  approved_student_pics: AdminStudentPic[]
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  status: WidgetStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  pinned_at: string | null
  pinned_in_apps: string[]
  /**
   * @min -2147483648
   * @max 2147483647
   */
  pin_order: number | null
  from_automation?: boolean
  polymorphic_ctype: number | null
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminStudentsPicWidgetRequest = {
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type AdminTeacher = {
  id: string
  username: string
  /** @format email */
  email: string
  first_name: string
  last_name: string
  full_name: string
  /** @maxLength 16 */
  acronym: string
  phone_number?: string
  departments?: number[]
  departments_str: string[]
  preferred_rooms?: number[]
  preferred_rooms_str: string[]
  two_factor_auth?: boolean
  /** Teacher hidden in timetable generator */
  is_hidden_in_generator?: boolean
  custom_schedule?: any
  is_active: boolean
  is_placeholder?: boolean
  is_mentor?: boolean
  mentor_for_grades?: number[]
  mentor_for_grades_str: string[]
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type AdminTeacherRequest = {
  /**
   * @format email
   * @minLength 1
   */
  email: string
  /** @minLength 1 */
  first_name: string
  /** @minLength 1 */
  last_name: string
  /**
   * @minLength 1
   * @maxLength 16
   */
  acronym: string
  /** @minLength 1 */
  phone_number?: string
  departments?: number[]
  preferred_rooms?: number[]
  two_factor_auth?: boolean
  /** Teacher hidden in timetable generator */
  is_hidden_in_generator?: boolean
  custom_schedule?: any
  is_active: boolean
  is_placeholder?: boolean
  is_mentor?: boolean
  mentor_for_grades?: number[]
}

export type AdminTimetable =
  | TimetableExam
  | TimetableLesson
  | TimetableSchoolProgramme
  | TimetableRotaActivity

export type AdminTimetableCourse = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  name: string
  type: string
  level: string
  departments: string[]
}

export type AdminTimetableLesson = {
  /** @format uuid */
  id: string
  /** @format date */
  date: string
  start: string
  end: string
  display_hour: string
  course: AdminTimetableCourse
  room: AdminTimetableRoom
  is_archived?: boolean
  /** @format uuid */
  teacher: string
  /** @format uuid */
  teacher_cover?: string | null
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
  teacher_details: SimpleUser
  teacher_cover_details?: SimpleUser | null
  co_teacher_1_details: SimpleUser
  co_teacher_2_details: SimpleUser
}

export type AdminTimetableLessonRoomAssignment = {
  room?: number | null
}

export type AdminTimetableLessonRoomAssignmentRequest = {
  room?: number | null
}

export type AdminTimetableLessonUpdate = {
  /** @format uuid */
  id: string
  /** @format date */
  date: string
  start: string
  end: string
  is_archived: boolean
  /** @format uuid */
  teacher: string
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2: string | null
  /** @format uuid */
  teacher_cover?: string | null
}

export type AdminTimetableLessonUpdateRequest = {
  /** @format uuid */
  teacher_cover?: string | null
}

/**
 * * `Exam` - Exam
 * * `Lesson` - Lesson
 * * `SchoolProgramme` - SchoolProgramme
 * * `Rota` - Rota
 */
export type AdminTimetableResourcetypeEnum =
  | 'Exam'
  | 'Lesson'
  | 'SchoolProgramme'
  | 'Rota'

export type AdminTimetableRoom = {
  id: number
  /** @maxLength 128 */
  name: string
  /**
   * @min 0
   * @max 2147483647
   */
  capacity?: number | null
}

export type AdminTypes = {
  label: string
  resourcetype: ResourcetypeEnum
  polymorphic_ctype: number
  initial_data: AdminWidget
  /** @default "" */
  require_module: string
  thumbnail: string | null
  allowed_layouts: string
  use_default_bg_color: string
}

export type AdminUser = {
  /** @format uuid */
  id: string
  academichod: UserAcademicHODProfile | null
  academicadmin: UserAcademicAdminProfile | null
  idpadmin: UserIdpAdminProfile | null
  admin: UserAdminProfile | null
  teacher: UserTeacherProfile | null
  boardingdirector: UserBoardingDirectorProfile | null
  healthcoordinator: UserHealthCoordinatorProfile | null
  headofhouse: HeadOfHouseProfile | null
  housetutor: HouseTutorProfile | null
  nightguard: NightGuardProfile | null
  viewer: ViewerProfile | null
  campusheadteam: CampusHeadTeamProfile | null
  propertymanager: PropertyManagerProfile | null
  infoboardeditor: InfoboardEditorProfile | null
  behaviourmanagement: BehaviourManagementProfile | null
  securityteam: SecurityTeamProfile | null
  /** Required. 150 characters or fewer. Letters, digits and @/./+/-/_ only. */
  username: string
  first_name: string
  last_name: string
  /** @format email */
  email: string
  phone_number: string
  two_factor_auth?: boolean
  full_name: string
  /** @format date-time */
  last_login?: string | null
  /**
   * Superuser status
   * Designates that this user has all permissions without explicitly assigning them.
   */
  is_superuser?: boolean
  /**
   * Staff status
   * Designates whether the user can log into this admin site.
   */
  is_staff?: boolean
  /**
   * Active
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  /** @format date-time */
  date_joined?: string
  search?: string | null
  searchable_content?: string
  profiles: string[]
  /** @maxLength 256 */
  name?: string
  /**  full name */
  _full_name: string
  gender?: ForGenderEnum | BlankEnum
  google_subject: string | null
  /**
   * @format int64
   * @min 0
   * @max 9223372036854776000
   */
  abacus_person_number?: number | null
  disable_access_to_idp?: boolean
  staff_type: StaffTypeEnum | BlankEnum
  can_handle_generator?: boolean
  /** legacy field, kept for historical data */
  is_vaccinated: boolean
  allow_push_notifications?: boolean
  allow_email_notifications?: boolean
  accept_campus_notifications?: boolean
  accept_education_notifications?: boolean
  accept_reminders_notifications?: boolean
  accept_dashboard_notifications?: boolean
  cannot_use_sms_login?: boolean
  can_login_as_student: boolean
  can_report_bug: boolean
  can_generate_transcripts_of_grades: boolean
  can_edit_official_transcripts_of_grades: boolean
  is_teacher: boolean
  is_student: boolean
  is_parent: boolean
  is_company: boolean
  is_laundry_user: boolean
  /** @format date-time */
  last_update: string
  is_deleted_due_to_duplicate?: boolean
  billing_additional_email?: number | null
  /** @format uuid */
  last_updated_by?: string | null
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type AdminUserAvailability = {
  id: number
  user: AdminUserAvailabilityUser
  monday: AdminUserAvailabilitySlot[]
  tuesday: AdminUserAvailabilitySlot[]
  wednesday: AdminUserAvailabilitySlot[]
  thursday: AdminUserAvailabilitySlot[]
  friday: AdminUserAvailabilitySlot[]
  saturday: AdminUserAvailabilitySlot[]
  sunday: AdminUserAvailabilitySlot[]
}

export type AdminUserAvailabilitySlot = {
  id: number
  /** @format time */
  start_time: string
  /** @format time */
  end_time: string
}

export type AdminUserAvailabilityUpdateRequest = {
  monday: AdminUserAvailabilityUpdateSlotRequest[]
  tuesday: AdminUserAvailabilityUpdateSlotRequest[]
  wednesday: AdminUserAvailabilityUpdateSlotRequest[]
  thursday: AdminUserAvailabilityUpdateSlotRequest[]
  friday: AdminUserAvailabilityUpdateSlotRequest[]
  saturday: AdminUserAvailabilityUpdateSlotRequest[]
  sunday: AdminUserAvailabilityUpdateSlotRequest[]
}

export type AdminUserAvailabilityUpdateSlotRequest = {
  /** @format time */
  start_time: string
  /** @format time */
  end_time: string
}

export type AdminUserAvailabilityUser = {
  /** @format uuid */
  id: string
  /** @maxLength 256 */
  name?: string
  /** @maxLength 128 */
  first_name?: string
  /** @maxLength 128 */
  last_name?: string
}

export type AdminUserChangeRequest = {
  academichod?: UserAcademicHODProfileRequest | null
  academicadmin?: UserAcademicAdminProfileRequest | null
  idpadmin?: UserIdpAdminProfileRequest | null
  admin?: UserAdminProfileRequest | null
  teacher?: UserTeacherProfileRequest | null
  boardingdirector?: UserBoardingDirectorProfileRequest | null
  healthcoordinator?: UserHealthCoordinatorProfileRequest | null
  headofhouse?: HeadOfHouseProfileRequest | null
  housetutor?: HouseTutorProfileRequest | null
  nightguard?: NightGuardProfileRequest | null
  viewer?: ViewerProfileRequest | null
  campusheadteam?: CampusHeadTeamProfileRequest | null
  propertymanager?: PropertyManagerProfileRequest | null
  infoboardeditor?: InfoboardEditorProfileRequest | null
  behaviourmanagement?: BehaviourManagementProfileRequest | null
  securityteam?: SecurityTeamProfileRequest | null
  /** @minLength 1 */
  password1?: string
  /** @minLength 1 */
  password2?: string
  /** @minLength 1 */
  first_name: string
  /** @minLength 1 */
  last_name: string
  /**
   * @format email
   * @minLength 1
   */
  email: string
  /** @minLength 1 */
  phone_number: string
  two_factor_auth?: boolean
  /** @format date-time */
  last_login?: string | null
  /**
   * Superuser status
   * Designates that this user has all permissions without explicitly assigning them.
   */
  is_superuser?: boolean
  /**
   * Staff status
   * Designates whether the user can log into this admin site.
   */
  is_staff?: boolean
  /**
   * Active
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  /** @format date-time */
  date_joined?: string
  search?: string | null
  searchable_content?: string
  /** @maxLength 256 */
  name?: string
  gender?: ForGenderEnum | BlankEnum
  /**
   * @format int64
   * @min 0
   * @max 9223372036854776000
   */
  abacus_person_number?: number | null
  disable_access_to_idp?: boolean
  staff_type: StaffTypeEnum | BlankEnum
  can_handle_generator?: boolean
  allow_push_notifications?: boolean
  allow_email_notifications?: boolean
  accept_campus_notifications?: boolean
  accept_education_notifications?: boolean
  accept_reminders_notifications?: boolean
  accept_dashboard_notifications?: boolean
  cannot_use_sms_login?: boolean
  is_deleted_due_to_duplicate?: boolean
  billing_additional_email?: number | null
  /** @format uuid */
  last_updated_by?: string | null
}

export type AdminUserList = {
  /** @format uuid */
  id: string
  full_name: string
  /**
   * Email address
   * @format email
   * @maxLength 254
   */
  email: string
  /**
   * Superuser status
   * Designates that this user has all permissions without explicitly assigning them.
   */
  is_superuser?: boolean
  /**
   * Staff status
   * Designates whether the user can log into this admin site.
   */
  is_staff?: boolean
  /** @maxLength 128 */
  first_name?: string
  /** @maxLength 128 */
  last_name?: string
  /** The groups this user belongs to. A user will get all permissions granted to each of their groups. */
  groups?: number[]
  /**
   * Active
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  /** @format date-time */
  date_joined?: string
  /** @format date-time */
  last_login?: string | null
  staff_type?: StaffTypeEnum | BlankEnum
}

export type AdminUserPasswordChange = {
  /** @maxLength 128 */
  new_password1: string
  /** @maxLength 128 */
  new_password2: string
}

export type AdminUserPasswordChangeRequest = {
  /**
   * @minLength 1
   * @maxLength 128
   */
  new_password1: string
  /**
   * @minLength 1
   * @maxLength 128
   */
  new_password2: string
}

export type AdminVariantManualCongratulations = {
  id: number
  /**
   * * `wreath` - Wreath
   * * `cap` - Cap
   */
  variant: VariantEnum
  images: string[]
  images_data: Image[]
  /** @format uuid */
  student: string
  student_str: string
  /** @maxLength 400 */
  message: string
  /** @maxLength 128 */
  course_name?: string
  /**
   * @min 0
   * @max 2147483647
   */
  order?: number
}

export type AdminVariantManualCongratulationsRequest = {
  /**
   * * `wreath` - Wreath
   * * `cap` - Cap
   */
  variant: VariantEnum
  images: string[]
  /** @format uuid */
  student: string
  /**
   * @minLength 1
   * @maxLength 400
   */
  message: string
  /** @maxLength 128 */
  course_name?: string
  /**
   * @min 0
   * @max 2147483647
   */
  order?: number
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AdminVariantManualCongratulationsWidget = {
  id: string
  /** @default "#808080" */
  bg_color: string
  items: AdminVariantManualCongratulations[]
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  status: WidgetStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  pinned_at: string | null
  pinned_in_apps: string[]
  /**
   * @min -2147483648
   * @max 2147483647
   */
  pin_order: number | null
  from_automation?: boolean
  polymorphic_ctype: number | null
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AdminVariantManualCongratulationsWidgetRequest = {
  items: AdminVariantManualCongratulationsRequest[]
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type AdminWidget =
  | AdminBoardingAnnouncementWidget
  | AdminCelebrationWidget
  | AdminEventAgendaWidget
  | AdminCongratulationsWidget
  | AdminPosterWidget
  | AdminStudentArtGalleryWidget
  | AdminEventInfoWidget
  | AdminEventInfoWithPhotoWidget
  | AdminInfoWithVideoWidget
  | AdminJournalWidget
  | AdminMysteryLinkWidget
  | AdminMenuWidget
  | AdminStudentAnnouncementWidget
  | AdminStudentsPicWidget
  | AdminVariantManualCongratulationsWidget
  | AdminClockWidget

export type AdminWidgetRequest =
  | AdminBoardingAnnouncementWidgetRequest
  | AdminCelebrationWidgetRequest
  | AdminEventAgendaWidgetRequest
  | AdminCongratulationsWidgetRequest
  | AdminPosterWidgetRequest
  | AdminStudentArtGalleryWidgetRequest
  | AdminEventInfoWidgetRequest
  | AdminEventInfoWithPhotoWidgetRequest
  | AdminInfoWithVideoWidgetRequest
  | AdminJournalWidgetRequest
  | AdminMysteryLinkWidgetRequest
  | AdminMenuWidgetRequest
  | AdminStudentAnnouncementWidgetRequest
  | AdminStudentsPicWidgetRequest
  | AdminVariantManualCongratulationsWidgetRequest
  | AdminClockWidgetRequest

export type AgendaItem = {
  id: number
  /** @format time */
  time_from?: string | null
  /** @format time */
  time_to?: string | null
  activity: string
  location?: string
}

export type AgendaItemRequest = {
  /** @format time */
  time_from?: string | null
  /** @format time */
  time_to?: string | null
  /** @minLength 1 */
  activity: string
  location?: string
  /** Indicates the position of item on the list. If omitted the index of item in the list value will be passed as default. */
  ordering?: number
}

export type Aim = {
  id: number
  /** @maxLength 64 */
  name: string
  /** @pattern ^[-a-zA-Z0-9_]+$ */
  slug: string
  is_default?: boolean
}

export type AimScorings = {
  min: number
  max: number
  grade: string
}

export type AllergenKindOption = {
  label: string
  value: string
  allergens: StringOption[]
}

export type Allergy = {
  id: number
  /** @format date-time */
  created: string
  type: string
  subtypes?: string[]
}

export type AllergyChoicesResponse = {
  reactions: AllergyReactionOption[]
  allergen_kinds: AllergenKindOption[]
  medicines: StringOption[]
  medicine_locations: StringOption[]
  immediate_responses: StringOption[]
}

export type AllergyReactionOption = {
  label: string
  value: string
  mild_reactions: StringOption[]
  severe_reactions: StringOption[]
}

export type AllergyRequest = {
  /** @minLength 1 */
  type: string
  subtypes?: string[]
}

export type AlternativeTeacherQuarterChange = {
  quarter: string
  main_teacher_updated: boolean
  alternative_teachers_removed: boolean
}

/** Serializes a section with its generator-selected teacher. */
export type AlternativeTeacherSection = {
  section_id: number
  course_name: string
  group_number: number
  main_teacher: SimpleUser
  alternative_teachers: SimpleUser[]
  selected_teacher: SimpleUser
  quarter_changes: AlternativeTeacherQuarterChange[]
}

/**
 * * `reserved` - Reserved
 * * `social_room` - Social room
 * * `duty_room` - Duty room
 * * `artisan_rest_room` - Artisan rest room
 * * `storage` - Storage
 * * `artisan_private_room` - Artisan private room
 * * `student_accommodation` - Student accommodation
 * * `not_assigned` - Not assigned
 */
export type AnnotatedHouseRoomStatusChoices =
  | 'reserved'
  | 'social_room'
  | 'duty_room'
  | 'artisan_rest_room'
  | 'storage'
  | 'artisan_private_room'
  | 'student_accommodation'
  | 'not_assigned'

/**
 * * `rosenberg_parent` - Rosenberg Parent
 * * `my_rosenberg_idp` - My Rosenberg IDP
 */
export type AppAccessEnum = 'rosenberg_parent' | 'my_rosenberg_idp'

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AppMysteryLink = {
  id: string
  student_data: StudentWithAvatar
  link: LinkWithQRCode
  status: StudentContentStatusEnum
  /** @format date-time */
  created: string
  /** @format date-time */
  published_at: string | null
  is_pinned: string
  /** via annotate (feed) */
  reaction_count: number
  /** via annotate (feed) */
  reacted_by_user: boolean
  /** @default "student_content" */
  content_type: ContentTypeCf1Enum
  /**
   * * `StudentMysteryLink` - StudentMysteryLink
   * * `StudentAnnouncement` - StudentAnnouncement
   * * `StudentPic` - StudentPic
   */
  resourcetype: StudentContentResourcetypeEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AppMysteryLinkRequest = {
  student?: string
  link: LinkWithQRCodeRequest
  /**
   * * `StudentMysteryLink` - StudentMysteryLink
   * * `StudentAnnouncement` - StudentAnnouncement
   * * `StudentPic` - StudentPic
   */
  resourcetype: StudentContentResourcetypeEnum
}

export type AppStatusVersion = {
  ios_minimum_required_version?: string
  ios_minimum_recommended_version?: string
  android_minimum_required_version?: string
  android_minimum_recommended_version?: string
  parent_ios_minimum_required_version?: string
  parent_ios_minimum_recommended_version?: string
  parent_android_minimum_required_version?: string
  parent_android_minimum_recommended_version?: string
}

export type AppStudentAnnouncement = {
  id: string
  student_data: StudentWithAvatar
  emojis: string[]
  /** @maxLength 100 */
  text: string
  /** @maxLength 7 */
  bg_color?: string
  status: StudentContentStatusEnum
  /** @format date-time */
  created: string
  /** @format date-time */
  published_at: string | null
  is_pinned: string
  /** via annotate (feed) */
  reaction_count: number
  /** via annotate (feed) */
  reacted_by_user: boolean
  /** @default "student_content" */
  content_type: ContentTypeCf1Enum
  /**
   * * `StudentMysteryLink` - StudentMysteryLink
   * * `StudentAnnouncement` - StudentAnnouncement
   * * `StudentPic` - StudentPic
   */
  resourcetype: StudentContentResourcetypeEnum
}

export type AppStudentAnnouncementRequest = {
  student?: string
  emojis: string[]
  /**
   * @minLength 1
   * @maxLength 100
   */
  text: string
  /** @maxLength 7 */
  bg_color?: string
  /**
   * * `StudentMysteryLink` - StudentMysteryLink
   * * `StudentAnnouncement` - StudentAnnouncement
   * * `StudentPic` - StudentPic
   */
  resourcetype: StudentContentResourcetypeEnum
}

export type AppStudentPic = {
  id: string
  student_data: StudentWithAvatar
  image: string
  image_details: PublicImage
  /** @maxLength 50 */
  title?: string
  status: StudentContentStatusEnum
  /** @format date-time */
  created: string
  /** @format date-time */
  published_at: string | null
  is_pinned: string
  /** via annotate (feed) */
  reaction_count: number
  /** via annotate (feed) */
  reacted_by_user: boolean
  /** @default "student_content" */
  content_type: ContentTypeCf1Enum
  /**
   * * `StudentMysteryLink` - StudentMysteryLink
   * * `StudentAnnouncement` - StudentAnnouncement
   * * `StudentPic` - StudentPic
   */
  resourcetype: StudentContentResourcetypeEnum
}

export type AppStudentPicRequest = {
  student?: string
  /** @minLength 1 */
  image: string
  /** @maxLength 50 */
  title?: string
  /**
   * * `StudentMysteryLink` - StudentMysteryLink
   * * `StudentAnnouncement` - StudentAnnouncement
   * * `StudentPic` - StudentPic
   */
  resourcetype: StudentContentResourcetypeEnum
}

/**
 * * `student` - Student
 * * `parent` - Parent
 */
export type AppTypeEnum = 'student' | 'parent'

/**
 * * `both` - Both
 * * `idp_app` - Student
 * * `parent_app` - Parent
 * * `not_visible` - Not visible
 */
export type AppVisibilityEnum =
  | 'both'
  | 'idp_app'
  | 'parent_app'
  | 'not_visible'

export type ApplicationSummerSubjectAssign = {
  /**
   * * `fine-arts` - Fine Arts
   * * `performing-arts` - Performing Arts
   * * `leadership` - Diplomacy & Activism
   * * `robotics` - Robotics & Engineering
   * * `environmental-science` - Environmental Science & Sustainability
   * * `fashion` - Fashion & Textile Design
   * * `informatics` - Informatics & Coding
   * * `intensive-english` - Intensive English
   * * `intensive-german` - Intensive German
   * * `succession-management` - Succession Management Essentials
   * * `wealth-and-investment` - Wealth Creation & Investment Essentials
   * * `ai-agentic` - AI Agentic Enterprise Essentials
   * * `rosenberg-x-crimson-university-admission` - Rosenberg x Crimson University Admission Focus Course
   * * `english` - English
   * * `german` - German
   * * `tennis` - Tennis
   * * `personal-training` - Personal Training
   * * `horse-riding` - Horse Riding
   * * `golf` - Golf
   */
  subject_name: SubjectNameEnum
  /**
   * * `lesson` - Lesson
   * * `training` - Training
   * * `focus_course` - Focus Course
   */
  subject_type: SubjectTypeEnum
  /**
   * @min 0
   * @max 2147483647
   */
  amount?: number
  level?: LevelEnum | BlankEnum
}

export type ApplicationSummerSubjectAssignRequest = {
  /**
   * * `fine-arts` - Fine Arts
   * * `performing-arts` - Performing Arts
   * * `leadership` - Diplomacy & Activism
   * * `robotics` - Robotics & Engineering
   * * `environmental-science` - Environmental Science & Sustainability
   * * `fashion` - Fashion & Textile Design
   * * `informatics` - Informatics & Coding
   * * `intensive-english` - Intensive English
   * * `intensive-german` - Intensive German
   * * `succession-management` - Succession Management Essentials
   * * `wealth-and-investment` - Wealth Creation & Investment Essentials
   * * `ai-agentic` - AI Agentic Enterprise Essentials
   * * `rosenberg-x-crimson-university-admission` - Rosenberg x Crimson University Admission Focus Course
   * * `english` - English
   * * `german` - German
   * * `tennis` - Tennis
   * * `personal-training` - Personal Training
   * * `horse-riding` - Horse Riding
   * * `golf` - Golf
   */
  subject_name: SubjectNameEnum
  /**
   * * `lesson` - Lesson
   * * `training` - Training
   * * `focus_course` - Focus Course
   */
  subject_type: SubjectTypeEnum
  /**
   * @min 0
   * @max 2147483647
   */
  amount?: number
  level?: LevelEnum | BlankEnum
}

export type ApplyChanges = {
  no_of_applied_items: number
}

export type Appointment = {
  id: number
  /** @format date-time */
  created: string
  /**
   * * `hospital` - Hospital
   * * `doctor` - Doctor
   */
  appointment_type?: AppointmentTypeEnum
  appointment_type_label: string
  doctor?: number | null
  doctor_name?: string
  doctor_str: string
  hospital?: number | null
  hospital_name?: string
  hospital_str: string
  /** @format date-time */
  date_and_time: string
  report: string
  diagnosis: string
  recommendations: string
  status: AppointmentStatusEnum
  duration_in_minutes: number
  /** @format date-time */
  end_of_visit: string
  cancellation_reason: string
}

export type AppointmentCancellation = {
  cancellation_reason?: string
}

export type AppointmentCancellationRequest = {
  /** @minLength 1 */
  cancellation_reason?: string
}

export type AppointmentRequest = {
  /**
   * * `hospital` - Hospital
   * * `doctor` - Doctor
   */
  appointment_type?: AppointmentTypeEnum
  doctor?: number | null
  doctor_name?: string
  hospital?: number | null
  hospital_name?: string
  /** @format date-time */
  date_and_time: string
  /** @minLength 1 */
  report: string
  /** @format date-time */
  end_of_visit: string
}

/**
 * * `new` - New
 * * `cancelled` - Cancelled
 * * `after_the_visit` - After the visit
 */
export type AppointmentStatusEnum = 'new' | 'cancelled' | 'after_the_visit'

/**
 * * `hospital` - Hospital
 * * `doctor` - Doctor
 */
export type AppointmentTypeEnum = 'hospital' | 'doctor'

export type ApproveStudentContent = {
  /** @format date */
  display_to: string | null
}

export type ApproveStudentRequest = {
  increase?: IncreaseEnum | NullEnum | null
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type ArrivalInfo = {
  /** @format date */
  arrival?: string | null
  airport_service?: boolean
  /** @maxLength 256 */
  airport_origin?: string
  /** @maxLength 256 */
  flight_number?: string
  /** @maxLength 256 */
  train_number?: string
  /** @format date */
  requested_arrival_date?: string | null
  /** @format time */
  requested_arrival_time?: string | null
  /** @format uuid */
  contact_person?: string | null
  contact_person_str: string | null
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type ArrivalInfoRequest = {
  /** @format date */
  arrival?: string | null
  airport_service?: boolean
  /** @maxLength 256 */
  airport_origin?: string
  /** @maxLength 256 */
  flight_number?: string
  /** @maxLength 256 */
  train_number?: string
  /** @format date */
  requested_arrival_date?: string | null
  /** @format time */
  requested_arrival_time?: string | null
  /** @format uuid */
  contact_person?: string | null
}

export type AssignRoomsRequest = {
  /** @default true */
  unassigned_only?: boolean
}

export type AssignStudentToSectionRequest = {
  target_group: number
  idp_section_enrollments: number[]
}

/**
 * * `4` - S
 * * `3` - E
 * * `2` - A
 * * `1` - C
 * * `0` - not assigned
 */
export type AssignedCategory92AEnum = 4 | 3 | 2 | 1 | 0

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type Assignment = {
  /** @format uuid */
  id: string
  /** @maxLength 255 */
  name: string
  description: string
  type: number
  /** @format date */
  due_date: string
  /** @format date-time */
  created: string
  /** @format uuid */
  lesson?: string | null
  /** @format uuid */
  course: string
  course_str: string
  teacher_str: string
  /** @format uuid */
  teacher: string | null
  student_items: StudentAssignmentItem[]
  is_homework?: boolean
  quarter?: number
  moderated_grades_allowed: boolean
  week: StringOption | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AssignmentGrade = {
  /** @format uuid */
  id: string
  /** @format date-time */
  created: string
  /**
   * @min 0
   * @max 100
   */
  grade_in_percent?: number | null
  /**
   * Custom grades are eligible only for assignments related with CCU courses
   *
   * * `SC` - Successfully Completed
   * * `CA` - Successfully Completed & Credit Awarded
   */
  custom_grade?: TECustomGradeEnum | BlankEnum
  average_grade: number | null
  /** @maxLength 32 */
  grade_in_ib?: string
  is_submitted?: boolean
  is_incomplete?: boolean
  is_late?: boolean
  is_exempt?: boolean
  /** @format uuid */
  student: string
  assignment: AssignmentInStudentAssignmentItem
  score_definitions: BaseAssignmentScoreDefinition[]
  /**
   * Moderated grade in percents.
   * @min 0
   * @max 100
   */
  moderated_grade?: number | null
  moderated_grade_status: ModeratedGradeStatusEnum | NullEnum | null
  grade_moderated_by: SimpleUser | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AssignmentGradeCreate = {
  /** @format uuid */
  id: string
  /** @format date-time */
  created: string
  grade_in_percent: number | null
  /**
   * Custom grades are eligible only for assignments related with CCU courses
   *
   * * `SC` - Successfully Completed
   * * `CA` - Successfully Completed & Credit Awarded
   */
  custom_grade?: TECustomGradeEnum | BlankEnum
  average_grade: number | null
  /** @maxLength 32 */
  grade_in_ib?: string
  is_submitted?: boolean
  is_incomplete?: boolean
  is_late?: boolean
  is_exempt?: boolean
  /** @format uuid */
  student: string
  assignment: AssignmentInStudentAssignmentItemCreate
  score_definitions: BaseAssignmentScoreDefinition[]
  /**
   * Moderated grade in percents.
   * @min 0
   * @max 100
   */
  moderated_grade?: number | null
  moderated_grade_status: ModeratedGradeStatusEnum | NullEnum | null
  grade_moderated_by: SimpleUser | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AssignmentGradeCreateRequest = {
  /**
   * Custom grades are eligible only for assignments related with CCU courses
   *
   * * `SC` - Successfully Completed
   * * `CA` - Successfully Completed & Credit Awarded
   */
  custom_grade?: TECustomGradeEnum | BlankEnum
  /** @maxLength 32 */
  grade_in_ib?: string
  is_submitted?: boolean
  is_incomplete?: boolean
  is_late?: boolean
  is_exempt?: boolean
  /** @format uuid */
  student: string
  assignment: AssignmentInStudentAssignmentItemCreateRequest
  /**
   * Moderated grade in percents.
   * @min 0
   * @max 100
   */
  moderated_grade?: number | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AssignmentGradeRequest = {
  /**
   * @min 0
   * @max 100
   */
  grade_in_percent?: number | null
  /**
   * Custom grades are eligible only for assignments related with CCU courses
   *
   * * `SC` - Successfully Completed
   * * `CA` - Successfully Completed & Credit Awarded
   */
  custom_grade?: TECustomGradeEnum | BlankEnum
  /** @maxLength 32 */
  grade_in_ib?: string
  is_submitted?: boolean
  is_incomplete?: boolean
  is_late?: boolean
  is_exempt?: boolean
  /** @format uuid */
  student: string
  assignment: AssignmentInStudentAssignmentItemRequest
  score_definitions: BaseAssignmentScoreDefinitionRequest[]
  /**
   * Moderated grade in percents.
   * @min 0
   * @max 100
   */
  moderated_grade?: number | null
}

export type AssignmentInStudentAssignmentItem = {
  /** @format uuid */
  id: string
  /** @maxLength 255 */
  name: string
  description: string
  course: SimpleCourse
  type: number
  /** @format date */
  due_date: string
  type_str: string
  moderated_grades_allowed: boolean
}

export type AssignmentInStudentAssignmentItemCreate = {
  /** @format uuid */
  id: string
  name: string
  description: string
  course: SimpleCourse
  type: number
  /** @format date */
  due_date: string
  type_str: string
  moderated_grades_allowed: boolean
}

export type AssignmentInStudentAssignmentItemCreateRequest = {
  /** @format uuid */
  id: string
}

export type AssignmentInStudentAssignmentItemRequest = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name: string
  /** @minLength 1 */
  description: string
  type: number
  /** @format date */
  due_date: string
}

export type AssignmentModeratedGradeApproveRequest = {
  /** @minLength 1 */
  feedback?: string
}

export type AssignmentModeratedGradeList = {
  /** @format uuid */
  id: string
  student: SimpleUser
  artisan: SimpleUser
  aim_str: string
  assignment_name: string
  /**
   * @min 0
   * @max 100
   */
  average_grade?: number | null
  /**
   * The date of grade moderation request.
   * @format date-time
   */
  grade_moderated_at: string | null
  /**
   * @min 0
   * @max 100
   */
  grade_in_percent?: number | null
  /**
   * Moderated grade in percents.
   * @min 0
   * @max 100
   */
  moderated_grade?: number | null
  moderated_grade_status?: ModeratedGradeStatusEnum | NullEnum | null
}

export type AssignmentModeratedGradeRejectRequest = {
  /** @minLength 1 */
  feedback?: string
  /**
   * * `external_grade_boundaries` - Not reflective of external grade boundaries
   * * `other` - Other
   */
  rejection_reason: RejectionReasonEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AssignmentRequest = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name: string
  /** @minLength 1 */
  description: string
  type: number
  /** @format date */
  due_date: string
  /** @format uuid */
  lesson?: string | null
  /** @format uuid */
  course: string
  student_items: StudentAssignmentItemRequest[]
  is_homework?: boolean
  quarter?: number
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AssignmentRetrieve = {
  /** @format uuid */
  id: string
  /** @maxLength 255 */
  name: string
  description: string
  type: number
  /** @format date */
  due_date: string
  /** @format date-time */
  created: string
  /** @format uuid */
  lesson?: string | null
  /** @format uuid */
  course: string
  course_str: string
  teacher_str: string
  /** @format uuid */
  teacher?: string | null
  student_items: StudentAssignmentItemWithGradeLevel[]
  is_homework?: boolean
  quarter?: number
  moderated_grades_allowed: boolean
  week: StringOption | null
  score_definitions: AssignmentScoreDefinition[]
  course_group: string | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AssignmentScoreDefinition = {
  /** @format uuid */
  id: string
  /**
   * @format double
   * @min 1
   */
  max_points?: number | null
  /** @format uuid */
  section_plan_item_id: string
  /** @format uuid */
  assignment_id: string
  subtopic_name: string
  students_scores: StudentAssignmentScore[]
  /** Indicates if scores from this definition should be skipped in grade calculation process */
  is_skipped?: boolean
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AssignmentScoreDefinitionRequest = {
  /** @format uuid */
  id: string
  /**
   * @format double
   * @min 1
   */
  max_points?: number | null
  students_scores: StudentAssignmentScoreRequest[]
  /** Indicates if scores from this definition should be skipped in grade calculation process */
  is_skipped?: boolean
}

export type AssignmentType = {
  id: number
  /** @maxLength 64 */
  name: string
  /** @pattern ^[-a-zA-Z0-9_]+$ */
  slug: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AssignmentUpdate = {
  /** @format uuid */
  id: string
  /** @maxLength 255 */
  name: string
  description: string
  type: number
  /** @format date */
  due_date: string
  /** @format date-time */
  created: string
  /** @format uuid */
  lesson?: string | null
  /** @format uuid */
  course: string
  course_str: string
  teacher_str: string
  /** @format uuid */
  teacher?: string | null
  student_items: StudentAssignmentItem[]
  is_homework?: boolean
  quarter?: number
  moderated_grades_allowed: boolean
  week: StringOption | null
  score_definitions: AssignmentScoreDefinition[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AssignmentUpdateRequest = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name: string
  /** @minLength 1 */
  description: string
  type: number
  /** @format date */
  due_date: string
  /** @format uuid */
  lesson?: string | null
  /** @format uuid */
  course: string
  /** @format uuid */
  teacher?: string | null
  student_items: StudentAssignmentItemRequest[]
  is_homework?: boolean
  quarter?: number
  score_definitions: AssignmentScoreDefinitionRequest[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type AssignmentWithSubtopicNamesList = {
  /** @format uuid */
  id: string
  /** @maxLength 255 */
  name: string
  description: string
  type: number
  /** @format date */
  due_date: string
  /** @format date-time */
  created: string
  /** @format uuid */
  lesson?: string | null
  /** @format uuid */
  course: string
  course_str: string
  teacher_str: string
  /** @format uuid */
  teacher: string | null
  student_items: StudentAssignmentItem[]
  is_homework?: boolean
  quarter?: number
  moderated_grades_allowed: boolean
  week: StringOption | null
  subtopic_names: string[]
}

/**
 * * `met` - Met
 * * `over_limit` - Over limit
 * * `not_enough` - Not enough
 */
export type AthRulesEnum = 'met' | 'over_limit' | 'not_enough'

export type Attachment = {
  id: string
  /** @format uri */
  file: string
  user?: string
  file_name: string
}

export type AttachmentRequest = {
  /** @format binary */
  file: File
  user?: string
}

export type AttendanceAlertsAvailableFiltersResponse = {
  teacher: AvailableStringFilter
  course: AvailableStringFilter
  status: AvailableStringFilter
}

/** Ausgang week & year */
export type AusgangsPerWeekResponse = Record<
  string,
  Record<
    string,
    {
      afternoon_ausgang?: number
      evening_ausgang?: number
      evening_campus_ausgang?: number
      birthday_ausgang?: number
      on_board?: number
      other?: number
      prolonged_ausgang?: number
    }
  >
>

/**
 * * `student` - Student
 * * `author` - Author
 */
export type AuthorTypeEnum = 'student' | 'author'

export type AutomatedWidgetsStatus = {
  widget: string
  status: string | null
}

/**
 * * `free` - Free
 * * `in_use` - In use
 */
export type AvailabilityEnum = 'free' | 'in_use'

export type AvailableBooleanFilter = {
  query_param: string
  label: string
  choices: BooleanOption[]
}

export type AvailableExam = {
  exam_id: string
  /** @format uuid */
  course_id: string
  course_name: string
  grade_level_ids: number[]
  /**
   * @min 0
   * @max 2147483647
   */
  default_duration: number | null
  available_grade_level_ids: string
}

export type AvailableExternalExamCourses = {
  exam_id: string | null
  /** @format uuid */
  course_id: string | null
  course_name: string | null
  grade_level_ids: number[]
  available_grade_level_ids: number[]
  default_duration: number | null
}

export type AvailableGradeLevelsChoices = {
  query_param: string
  label: string
  choices: GradeLevel[]
}

export type AvailableIntegerFilter = {
  query_param: string
  label: string
  choices: IntegerOption[]
}

export type AvailableSectionDetails = {
  course: SimpleCourse
  students: SimpleEnrolledStudent[]
}

export type AvailableStringFilter = {
  query_param: string
  label: string
  choices: StringOption[]
}

export type AvailableStringFilterHealthCheckStudent = {
  query_param: string
  label: string
  choices: HealthCheckStudentOption[]
}

export type AvailableStringFilterHouseRoomBookedFor = {
  query_param: string
  label: string
  choices: HouseRoomBookedForOption[]
}

export type AvailableStudent = {
  /** @format uuid */
  id: string
  full_name: string
  student_grade_level: string
  student_house: string
  student_category: string
  student_category_label: string
}

export type AvailableStudentForSchoolProgrammeAvailableFilters = {
  houses: AvailableIntegerFilter
  grade_levels: AvailableIntegerFilter
  ages: AvailableIntegerFilter
  categories: AvailableIntegerFilter
  sections: SectionsAvailableIntegerFilter
}

export type AvailableStudents = {
  value: string
  label: string
}

export type AvailableUsersResponse = {
  available: SimpleUser[]
  unavailable: SimpleUser[]
  busy: BusyUsers
}

export type BaseAssignmentScoreDefinition = {
  /** @format uuid */
  id: string
  /**
   * @format double
   * @min 1
   */
  max_points?: number | null
  /** @format uuid */
  section_plan_item_id: string
  /** @format uuid */
  assignment_id: string
  subtopic_name: string
  students_scores: StudentAssignmentScore[]
  /** Indicates if scores from this definition should be skipped in grade calculation process */
  is_skipped?: boolean
}

export type BaseAssignmentScoreDefinitionRequest = {
  /** @format uuid */
  id: string
  /**
   * @format double
   * @min 1
   */
  max_points?: number | null
  students_scores: StudentAssignmentScoreRequest[]
  /** Indicates if scores from this definition should be skipped in grade calculation process */
  is_skipped?: boolean
}

export type BaseCSVFileRequest = {
  file: string
}

export type BaseExamSessionChanges = {
  id: string
  /** @format date */
  date: string
  course_name: string
  /** @format time */
  start_time: string
  /** @format time */
  end_time: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type Basket = {
  id: number
  courses: CourseInBasket[]
  /**
   * @min 0
   * @max 2147483647
   */
  order?: number
  /** Basket type */
  choice_type: BasketChoiceTypeEnum
  name?: string
  key: string
  is_ignored?: boolean
  /** @format double */
  credit_count: number
  include_extra_basket?: boolean
  idp_plan?: string | null
}

/**
 * * `default` - Default
 * * `private` - Private
 * * `addon` - Addon
 * * `free_choice` - Free Choice
 * * `exam` - Exam
 * * `higher_level` - Higher Level
 */
export type BasketChoiceTypeEnum =
  | 'default'
  | 'private'
  | 'addon'
  | 'free_choice'
  | 'exam'
  | 'higher_level'

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type BasketRequest = {
  courses: CourseInBasketRequest[]
  /**
   * @min 0
   * @max 2147483647
   */
  order?: number
  /** Basket type */
  choice_type: BasketChoiceTypeEnum
  name?: string
  /** @minLength 1 */
  key: string
  is_ignored?: boolean
  include_extra_basket?: boolean
  /** @minLength 1 */
  idp_plan?: string | null
}

export type BehaviourAndAttitudeProgressComment = {
  /** @format uuid */
  id: string
  positive_comments?: string[]
  to_be_improved_comments?: string[]
}

export type BehaviourAndAttitudeProgressCommentRequest = {
  positive_comments?: string[]
  to_be_improved_comments?: string[]
}

/** Collects and raises validation errors in bulk */
export type BehaviourManagementProfile = {
  id: string
  /** @format uuid */
  user?: string | null
}

/** Collects and raises validation errors in bulk */
export type BehaviourManagementProfileRequest = {
  /** @format uuid */
  user?: string | null
}

export type BillingRecipient = {
  /**
   * * `student` - Student
   * * `contact` - Contact
   * * `company` - Company
   */
  type: BillingRecipientTypeEnum
  /** @format uuid */
  id: string
}

export type BillingRecipientRequest = {
  /**
   * * `student` - Student
   * * `contact` - Contact
   * * `company` - Company
   */
  type: BillingRecipientTypeEnum
  /** @format uuid */
  id: string
}

/**
 * * `student` - Student
 * * `contact` - Contact
 * * `company` - Company
 */
export type BillingRecipientTypeEnum = 'student' | 'contact' | 'company'

export type BillingReport = {
  /** @format date-time */
  created: string
  /** @maxLength 120 */
  name: string
  timeframe_str: string
  author_str: string
  id: string
}

export type BillingReportCreate = {
  /** @maxLength 120 */
  name: string
  all_students: boolean
  students: string[]
  /**
   * * `quarter` - Quarter
   * * `school_year` - School Year
   */
  timeframe_type: TimeframeTypeEnum
  product_types: ProductTypesEnum[]
  quarter?: number | null
  school_year?: number | null
  grade_levels?: number[]
  author?: string
  items?: BillingReportItem[] | null
  id: string
}

export type BillingReportCreateRequest = {
  /**
   * @minLength 1
   * @maxLength 120
   */
  name: string
  all_students: boolean
  students: string[]
  /**
   * * `quarter` - Quarter
   * * `school_year` - School Year
   */
  timeframe_type: TimeframeTypeEnum
  product_types: ProductTypesEnum[]
  quarter?: number | null
  school_year?: number | null
  grade_levels?: number[]
  author?: string
  items?: BillingReportItemRequest[] | null
}

export type BillingReportItem = {
  /** @format uuid */
  student: string
  student_str: string
  description?: string
  show_description_in_excel?: boolean
  /**
   * * `quarter` - Quarter
   * * `week` - Week
   * * `year` - Year
   * * `course` - Course
   * * `lesson` - Lesson
   * * `order` - Order
   */
  quantity_unit: QuantityUnitEnum
  /**
   * @format decimal
   * @pattern ^-?\d{0,6}(?:\.\d{0,2})?$
   */
  total_price: string
  total_price_currency: string
  /**
   * @format decimal
   * @pattern ^-?\d{0,6}(?:\.\d{0,2})?$
   */
  unit_price: string
  unit_price_currency: string
  /**
   * @min -2147483648
   * @max 2147483647
   */
  quantity: number
  /**
   * * `academic_individual` - Academic Individual
   * * `sports` - Sports
   * * `co_curricular` - Co-Curricular
   * * `graduation_fee` - Graduation Fee
   * * `art_materials` - Art Materials
   * * `a_level_program` - A-level Programme
   * * `ib_programme` - IB Programme
   * * `ap_programme` - AP Programme
   * * `ielts_preparation_course` - IELTS Preparation Course
   * * `sat_preparation_course` - SAT/ACT
   * * `private_lessons` - Private Lessons
   * * `accommodation_single` - Accommodation - Single room
   * * `accommodation_double` - Accommodation - Double room
   * * `laundry` - Laundry
   * * `make_up_test` - Make-Up Tests
   * * `special_access_arrangements` - Special Access Arrangements
   */
  product_type: ProductTypeEnum
  product_type_display: string
  product_subtype?: string
  details?: any
  plural_quantity_unit: string
  abacus_invoice_product?: number | null
  abacus_invoice_product_name?: string
}

export type BillingReportItemRequest = {
  /** @format uuid */
  student: string
  description?: string
  show_description_in_excel?: boolean
  /**
   * * `quarter` - Quarter
   * * `week` - Week
   * * `year` - Year
   * * `course` - Course
   * * `lesson` - Lesson
   * * `order` - Order
   */
  quantity_unit: QuantityUnitEnum
  /**
   * @format decimal
   * @pattern ^-?\d{0,6}(?:\.\d{0,2})?$
   */
  total_price: string
  /**
   * @format decimal
   * @pattern ^-?\d{0,6}(?:\.\d{0,2})?$
   */
  unit_price: string
  /**
   * @min -2147483648
   * @max 2147483647
   */
  quantity: number
  /**
   * * `academic_individual` - Academic Individual
   * * `sports` - Sports
   * * `co_curricular` - Co-Curricular
   * * `graduation_fee` - Graduation Fee
   * * `art_materials` - Art Materials
   * * `a_level_program` - A-level Programme
   * * `ib_programme` - IB Programme
   * * `ap_programme` - AP Programme
   * * `ielts_preparation_course` - IELTS Preparation Course
   * * `sat_preparation_course` - SAT/ACT
   * * `private_lessons` - Private Lessons
   * * `accommodation_single` - Accommodation - Single room
   * * `accommodation_double` - Accommodation - Double room
   * * `laundry` - Laundry
   * * `make_up_test` - Make-Up Tests
   * * `special_access_arrangements` - Special Access Arrangements
   */
  product_type: ProductTypeEnum
  product_subtype?: string
  details?: any
  abacus_invoice_product?: number | null
  abacus_invoice_product_name?: string
}

export type BillingReportRequest = {
  /**
   * @minLength 1
   * @maxLength 120
   */
  name: string
}

export type BillingReportsItems = {
  /** @maxLength 120 */
  name: string
  all_students: boolean
  students: string[]
  /**
   * * `quarter` - Quarter
   * * `school_year` - School Year
   */
  timeframe_type: TimeframeTypeEnum
  product_types: ProductTypesEnum[]
  quarter?: number | null
  school_year?: number | null
  grade_levels?: number[]
  author?: string
  items?: BillingReportItem[] | null
  id: string
  students_with_no_data: StudentWithNoData[]
}

export type BillingReportsOptions = {
  product_types: StringOption[]
}

export type BillingReportsPreview = {
  items: BillingReportItem[]
  students_with_no_data: StudentWithNoData[]
}

export type BlankEnum = ''

/**
 * * `0-` - 0-
 * * `0+` - 0+
 * * `a-` - A-
 * * `a+` - A+
 * * `b-` - B-
 * * `b+` - B+
 * * `ab-` - AB-
 * * `ab+` - AB+
 * * `unknown` - Unknown
 */
export type BloodTypeEnum =
  | '0-'
  | '0+'
  | 'a-'
  | 'a+'
  | 'b-'
  | 'b+'
  | 'ab-'
  | 'ab+'
  | 'unknown'

/**
 * * `boarding_director` - Boarding Director
 * * `campus_head` - Campus Head
 * * `deputy_campus_head_main` - Deputy Campus Head (Main)
 * * `deputy_campus_head_senior` - Deputy Campus Head (Sen)
 * * `head_of_house` - Head of House
 * * `house_tutor` - House Tutor
 */
export type BoardingArtisanRoleEnum =
  | 'boarding_director'
  | 'campus_head'
  | 'deputy_campus_head_main'
  | 'deputy_campus_head_senior'
  | 'head_of_house'
  | 'house_tutor'

export type BoardingAttendance = {
  /** @format uuid */
  id: string
  mark?: BoardingAttendanceMarkEnum | BlankEnum
  student_str: string
  /** @format uuid */
  student: string
  is_at_home?: boolean
  is_at_class?: boolean
  is_at_exam?: boolean
  is_at_school_programme?: boolean
  has_meal_skip?: boolean
  boarding_status?: string
  category: number
  category_week_ago: string
  category_str: string
  category_week_ago_str: string
}

/**
 * * `present` - Present
 * * `late` - Late
 * * `sick` - Sick
 * * `absent_excused` - Absent exc.
 * * `absent_unexcused` - Absent unexc.
 */
export type BoardingAttendanceMarkEnum =
  | 'present'
  | 'late'
  | 'sick'
  | 'absent_excused'
  | 'absent_unexcused'

export type BoardingAttendanceReport = {
  id: number
  /**
   * * `new` - New
   * * `incomplete` - Incomplete
   * * `finished` - Finished
   */
  status?: BoardingAttendanceReportStatusEnum
  /** @format uuid */
  tutor?: string | null
  tutor_str: string
  status_display: string
  house?: number | null
  house_str: string
  /** @format date-time */
  created: string
  next_report: AdjacentBoardingAttendanceReport | null
  previous_report: AdjacentBoardingAttendanceReport | null
  for_senior_campus?: boolean
  for_senior_campus_type?: ForGenderEnum | NullEnum | null
  is_automatic: boolean
  allow_mark_absent_excused: boolean
  report_type: ReportTypeEnum
}

export type BoardingAttendanceReportAvailableFilters = {
  status: AvailableStringFilter
  tutor: AvailableStringFilter
  house: AvailableIntegerFilter
  for_senior_campus: AvailableBooleanFilter
  for_senior_campus_type: AvailableStringFilter
}

export type BoardingAttendanceReportList = {
  id: number
  /**
   * * `new` - New
   * * `incomplete` - Incomplete
   * * `finished` - Finished
   */
  status?: BoardingAttendanceReportStatusEnum
  /** @format uuid */
  tutor?: string | null
  tutor_str: string
  status_display: string
  /** @format date-time */
  created: string
  house?: number | null
  house_str: string
  report_type: ReportTypeEnum
}

export type BoardingAttendanceReportRequest = {
  /**
   * * `new` - New
   * * `incomplete` - Incomplete
   * * `finished` - Finished
   */
  status?: BoardingAttendanceReportStatusEnum
  /** @format uuid */
  tutor?: string | null
  house?: number | null
  for_senior_campus?: boolean
  for_senior_campus_type?: ForGenderEnum | NullEnum | null
}

/**
 * * `new` - New
 * * `incomplete` - Incomplete
 * * `finished` - Finished
 */
export type BoardingAttendanceReportStatusEnum =
  | 'new'
  | 'incomplete'
  | 'finished'

export type BoardingAttendanceRequest = {
  mark?: BoardingAttendanceMarkEnum | BlankEnum
  /** @format uuid */
  student: string
  is_at_home?: boolean
  is_at_class?: boolean
  is_at_exam?: boolean
  is_at_school_programme?: boolean
  has_meal_skip?: boolean
  /** @minLength 1 */
  boarding_status?: string
}

export type BoardingComment = {
  /** @format uuid */
  id: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  added_by?: string
  /** @format uuid */
  added_by_id: string | null
  last_modified_by?: string
  /**
   * @min 0
   * @max 32767
   */
  semester?: BoardingCommentSemesterEnum | NullEnum | null
  /** @format uuid */
  student: string
  school_year: number
  current_quarter: number
  content: string
}

export type BoardingCommentRequest = {
  added_by?: string
  last_modified_by?: string
  /**
   * @min 0
   * @max 32767
   */
  semester?: BoardingCommentSemesterEnum | NullEnum | null
  /** @format uuid */
  student: string
  school_year: number
  current_quarter: number
  /** @minLength 1 */
  content: string
}

/**
 * * `1` - Semester 1
 * * `2` - Semester 2
 */
export type BoardingCommentSemesterEnum = 1 | 2

export type BoardingCommentSimple = {
  /**
   * @min 0
   * @max 32767
   */
  semester?: BoardingCommentSemesterEnum | NullEnum | null
}

/**
 * * `at home` - at home
 * * `regular on board` - regular on board
 * * `temporary movement` - temporary movement
 * * `day` - day
 * * `campus absence` - campus absence
 * * `appointment` - appointment
 */
export type BoardingStatusEnum =
  | 'at home'
  | 'regular on board'
  | 'temporary movement'
  | 'day'
  | 'campus absence'
  | 'appointment'

export type BooleanOption = {
  label: string
  value: boolean
}

export type BusyInSlotRequestRequest = {
  /**
   * * `0` - Monday
   * * `1` - Tuesday
   * * `2` - Wednesday
   * * `3` - Thursday
   * * `4` - Friday
   * * `5` - Saturday
   * * `6` - Sunday
   */
  day: RotaDayEnum
  /**
   * * `0` - 8:00-8:40
   * * `1` - 8:45-9:25
   * * `2` - 9:30-10:10
   * * `3` - 10:30-11:10
   * * `4` - 11:15-11:55
   * * `5` - 13:00-13:40
   * * `6` - 13:45-14:25
   * * `7` - 14:30-15:10
   * * `8` - 15:15-15:55
   * * `9` - 16:00-16:40
   * * `10` - 16:45-17:25
   * * `11` - 17:30-18:10
   * * `12` - 18:15-18:55
   * * `13` - 19:00-19:40
   * * `14` - 19:40-20:30
   * * `15` - 20:30-21:00
   */
  hour?: HourEnum
  /** @min 1 */
  duration?: number
  /** @format time */
  custom_start?: string
  /** @format time */
  custom_end?: string
}

export type BusyInSlotResponse = {
  busy_teachers_ids: string[]
  busy_rooms_ids: number[]
  off_schedule_room_ids: number[]
}

export type BusyRoomReason = {
  /** @format uuid */
  id: string
  course: string
  /** @format date */
  date: string
  /** @format time */
  start_time: string
  /** @format time */
  end_time: string
}

export type BusyUsers = {
  exams: UserExams[]
  lessons: UserLessons[]
  school_programmes: UserSchoolProgrammes[]
}

export type BusyUsersRequestRequest = {
  /**
   * * `0` - Monday
   * * `1` - Tuesday
   * * `2` - Wednesday
   * * `3` - Thursday
   * * `4` - Friday
   * * `5` - Saturday
   * * `6` - Sunday
   */
  day: RotaDayEnum
  /** @format time */
  start_hour?: string
  /** @format time */
  end_hour?: string
  /**
   * * `regular` - Duty
   * * `break` - Break
   * * `meeting` - Leaders Meeting
   * * `night_presence` - Night Presence
   * * `ski_trip` - Ski trip
   * * `sudden_time_off` - Sudden Time Off
   */
  rota_type?: RotaTypeEnum
  /** @format date */
  date: string
}

export type BusyUsersResponse = {
  users: string[]
  rooms: number[]
}

export type CRMStatusCount = {
  /**
   * * `open` - Open
   * * `started` - In progress
   * * `submitted` - Submitted
   * * `payment_pending` - Submitted | Payment pending
   * * `payment_received` - Submitted | Payment received
   * * `payment_unsuccessful` - Submitted | Payment unsuccessful
   * * `decision_pending` - Decision pending
   * * `assessment` - Approved
   * * `unsuccessful` - Unsuccessful
   */
  status: CRMStatusCountStatusEnum
  cnt: number
}

/**
 * * `open` - Open
 * * `started` - In progress
 * * `submitted` - Submitted
 * * `payment_pending` - Submitted | Payment pending
 * * `payment_received` - Submitted | Payment received
 * * `payment_unsuccessful` - Submitted | Payment unsuccessful
 * * `decision_pending` - Decision pending
 * * `assessment` - Approved
 * * `unsuccessful` - Unsuccessful
 */
export type CRMStatusCountStatusEnum =
  | 'open'
  | 'started'
  | 'submitted'
  | 'payment_pending'
  | 'payment_received'
  | 'payment_unsuccessful'
  | 'decision_pending'
  | 'assessment'
  | 'unsuccessful'

export type CRMStatusSummary = {
  admin_url: string
  status_summary: CRMStatusCount[]
}

export type CalculateCreditsResponse = {
  credits_count: number
}

export type CalculateItemDataResponse = {
  /** @format double */
  credits: number
  grade: string | null
  average: number | null
}

export type CalendarEvent = {
  id: number
  /**
   * For backward compatibility, can be deleted in future release
   * @format date
   */
  date: string
  /** @format date */
  date_start: string
  /** @format date */
  date_end: string
  /**
   * * `community_weekend` - Community weekend
   * * `academic_tests` - Academic tests
   */
  event_type: CalendarEventEventTypeEnum
  event_type_display: string
}

/**
 * * `community_weekend` - Community weekend
 * * `academic_tests` - Academic tests
 */
export type CalendarEventEventTypeEnum = 'community_weekend' | 'academic_tests'

export type CalendarEventRequest = {
  /** @format date */
  date_start: string
  /** @format date */
  date_end: string
  /**
   * * `community_weekend` - Community weekend
   * * `academic_tests` - Academic tests
   */
  event_type: CalendarEventEventTypeEnum
}

export type Callback = {
  /**
   * * `success` - Success
   * * `infeasible` - Infeasible
   * * `error` - Error
   * * `optimizing` - Optimizing
   */
  status: CallbackStatusEnum
  timetable_path?: string
  external_id: string
  is_optimal?: boolean | null
  infeasible_section_ids?: string[] | null
}

export type CallbackRequest = {
  /**
   * * `success` - Success
   * * `infeasible` - Infeasible
   * * `error` - Error
   * * `optimizing` - Optimizing
   */
  status: CallbackStatusEnum
  timetable_path?: string
  /** @minLength 1 */
  external_id: string
  is_optimal?: boolean | null
  infeasible_section_ids?: string[] | null
}

/**
 * * `success` - Success
 * * `infeasible` - Infeasible
 * * `error` - Error
 * * `optimizing` - Optimizing
 */
export type CallbackStatusEnum =
  | 'success'
  | 'infeasible'
  | 'error'
  | 'optimizing'

/** Adds nested create feature */
export type CampusAbsence = {
  /** @format uuid */
  id: string
  created_by?: string
  last_modified_by?: string
  campus_absence_students?: CampusAbsenceStudent[]
  quarter: number
  status: CampusAbsenceStatusEnum
  status_label: string
  /**
   * * `ausgang` - Ausgang
   * * `external_exam` - External exam
   * * `official` - Official
   * * `school_programme` - School Programme
   * * `extra_ausgang` - Extra Ausgang
   */
  campus_absence_type: CampusAbsenceTypeEnum
  campus_absence_type_label: string
  /**
   * * `afternoon_ausgang` - Afternoon Ausgang
   * * `evening_ausgang` - Evening Ausgang
   * * `evening_campus_ausgang` - Evening - Campus Ausgang
   * * `birthday_ausgang` - Birthday Ausgang
   * * `family_visit` - Family visit
   * * `on_board` - On board Ausgang
   * * `prolonged_ausgang` - Prolonged Ausgang
   * * `campus` - Campus
   * * `offsite` - Offsite
   * * `offsite_overnight` - Offsite, Overnight
   * * `official_appointment` - Official appointment
   * * `specialist_doctor_appointment` - Specialist Doctor Appointment
   * * `programme` - Programme
   * * `house_evening` - House evening
   * * `term_break` - Term Break
   * * `school_trip` - School Trip
   * * `weekend_programme` - Weekend Programme
   * * `other` - Other
   * * `book_the_cafe` - Book the Cafe
   * * `city_brunch_zurich` - City Brunch Zurich
   * * `additional_ausgang` - Additional Ausgang
   * * `on_campus_ausgang` - On Campus Ausgang
   */
  campus_absence_subtype: CampusAbsenceSubTypeEnum
  campus_absence_subtype_label: string
  leave_details: string
  /** @maxLength 256 */
  contact_person_name?: string
  /** @maxLength 128 */
  contact_person_phone?: string
  /** @format date-time */
  date_from: string
  /** @format date-time */
  date_to: string
  check_in: boolean
  /** @format date-time */
  check_in_time: string | null
  check_out: boolean
  /** @format date-time */
  check_out_time: string | null
  disable_students_length_validation?: boolean
  supervisors?: (string | null)[]
  house?: number | null
  has_comment: string
  merit_reward: string
  prolonged_by: string
  school_programme: number | null
  school_programme_title: string | null
}

export type CampusAbsenceChangeStatus = {
  /**
   * * `requested` - Pending
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `canceled_by_student` - Canceled by student
   * * `done` - Done
   * * `cancelled` - Cancelled
   */
  status: CampusAbsenceStatusEnum
}

export type CampusAbsenceChangeStatusRequest = {
  /**
   * * `requested` - Pending
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `canceled_by_student` - Canceled by student
   * * `done` - Done
   * * `cancelled` - Cancelled
   */
  status: CampusAbsenceStatusEnum
}

export type CampusAbsenceCheckInOut = {
  check_in: boolean
  check_out: boolean
}

export type CampusAbsenceCheckInOutRequest = {
  check_in: boolean
  check_out: boolean
}

export type CampusAbsenceCollisionsResponse = Record<
  string,
  {
    campus_absence_id?: string
    /** @format date-time */
    campus_absence_date_from?: string
    /** @format date-time */
    campus_absence_date_to?: string
    campus_absence_status_label?:
      | 'Pending'
      | 'Approved'
      | 'Rejected'
      | 'Canceled by student'
      | 'Done'
      | 'Cancelled'
    campus_absence_type_label?:
      | 'Ausgang'
      | 'External exam'
      | 'Official'
      | 'School Programme'
      | 'Extra Ausgang'
    campus_absence_subtype?:
      | 'Afternoon Ausgang'
      | 'Evening Ausgang'
      | 'Evening - Campus Ausgang'
      | 'Birthday Ausgang'
      | 'Family visit'
      | 'On board Ausgang'
      | 'Prolonged Ausgang'
      | 'Campus'
      | 'Offsite'
      | 'Offsite, Overnight'
      | 'Official appointment'
      | 'Specialist Doctor Appointment'
      | 'Programme'
      | 'House evening'
      | 'Term Break'
      | 'School Trip'
      | 'Weekend Programme'
      | 'Other'
      | 'Book the Cafe'
      | 'City Brunch Zurich'
      | 'Additional Ausgang'
      | 'On Campus Ausgang'
    student_name?: string
  }[]
>

export type CampusAbsenceComment = {
  /** @format uuid */
  id: string
  author?: string
  content: string
  /** @format date-time */
  modified: string
  can_edit: boolean
}

export type CampusAbsenceCommentRequest = {
  author?: string
  /** @minLength 1 */
  content: string
  reject?: boolean
}

export type CampusAbsenceList = {
  /** @format uuid */
  id: string
  campus_absence_students?: CampusAbsenceStudentList[]
  /**
   * * `requested` - Pending
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `canceled_by_student` - Canceled by student
   * * `done` - Done
   * * `cancelled` - Cancelled
   */
  status?: CampusAbsenceStatusEnum
  status_label: string
  /** @format date-time */
  date_from: string
  /** @format date-time */
  date_to: string
  check_in?: boolean
  /** @format date-time */
  check_in_time?: string | null
  check_out?: boolean
  /** @format date-time */
  check_out_time?: string | null
  campus_absence_type_label: string
  /** @maxLength 256 */
  contact_person_name?: string
  /** @maxLength 128 */
  contact_person_phone?: string
  campus_absence_subtype_label: string
  has_comment: boolean
  school_programme?: number | null
}

export type CampusAbsenceLog = {
  id: number
  action: string
  description: string
  /** @format date-time */
  timestamp: string
}

/** Adds nested create feature */
export type CampusAbsenceRequest = {
  created_by?: string
  last_modified_by?: string
  campus_absence_students?: CampusAbsenceStudentRequest[]
  quarter: number
  /**
   * * `ausgang` - Ausgang
   * * `external_exam` - External exam
   * * `official` - Official
   * * `school_programme` - School Programme
   * * `extra_ausgang` - Extra Ausgang
   */
  campus_absence_type: CampusAbsenceTypeEnum
  /**
   * * `afternoon_ausgang` - Afternoon Ausgang
   * * `evening_ausgang` - Evening Ausgang
   * * `evening_campus_ausgang` - Evening - Campus Ausgang
   * * `birthday_ausgang` - Birthday Ausgang
   * * `family_visit` - Family visit
   * * `on_board` - On board Ausgang
   * * `prolonged_ausgang` - Prolonged Ausgang
   * * `campus` - Campus
   * * `offsite` - Offsite
   * * `offsite_overnight` - Offsite, Overnight
   * * `official_appointment` - Official appointment
   * * `specialist_doctor_appointment` - Specialist Doctor Appointment
   * * `programme` - Programme
   * * `house_evening` - House evening
   * * `term_break` - Term Break
   * * `school_trip` - School Trip
   * * `weekend_programme` - Weekend Programme
   * * `other` - Other
   * * `book_the_cafe` - Book the Cafe
   * * `city_brunch_zurich` - City Brunch Zurich
   * * `additional_ausgang` - Additional Ausgang
   * * `on_campus_ausgang` - On Campus Ausgang
   */
  campus_absence_subtype: CampusAbsenceSubTypeEnum
  /** @minLength 1 */
  leave_details: string
  /** @maxLength 256 */
  contact_person_name?: string
  /** @maxLength 128 */
  contact_person_phone?: string
  /** @format date-time */
  date_from: string
  /** @format date-time */
  date_to: string
  disable_students_length_validation?: boolean
  supervisors?: (string | null)[]
  house?: number | null
}

/**
 * * `requested` - Pending
 * * `approved` - Approved
 * * `rejected` - Rejected
 * * `canceled_by_student` - Canceled by student
 * * `done` - Done
 * * `cancelled` - Cancelled
 */
export type CampusAbsenceStatusEnum =
  | 'requested'
  | 'approved'
  | 'rejected'
  | 'canceled_by_student'
  | 'done'
  | 'cancelled'

export type CampusAbsenceStudent = {
  /** @format uuid */
  id: string
  /** @format uuid */
  student: string
  student_data: StudentSimple
  saved_category: number | null
  saved_category_week_ago: number | null
  saved_homework_status: string | null
  saved_current_week_ausgangs_count: any
  homework_status: string
  category: number
  category_week_ago: number
  ausgangs_this_week: Record<string, number | string>
  ausgangs_per_week: Record<string, Record<string, number>>
}

export type CampusAbsenceStudentList = {
  /** @format uuid */
  id: string
  student_data: StudentSimple
  /** @format uuid */
  student: string
}

export type CampusAbsenceStudentRequest = {
  /** @format uuid */
  student: string
}

/**
 * * `afternoon_ausgang` - Afternoon Ausgang
 * * `evening_ausgang` - Evening Ausgang
 * * `evening_campus_ausgang` - Evening - Campus Ausgang
 * * `birthday_ausgang` - Birthday Ausgang
 * * `family_visit` - Family visit
 * * `on_board` - On board Ausgang
 * * `prolonged_ausgang` - Prolonged Ausgang
 * * `campus` - Campus
 * * `offsite` - Offsite
 * * `offsite_overnight` - Offsite, Overnight
 * * `official_appointment` - Official appointment
 * * `specialist_doctor_appointment` - Specialist Doctor Appointment
 * * `programme` - Programme
 * * `house_evening` - House evening
 * * `term_break` - Term Break
 * * `school_trip` - School Trip
 * * `weekend_programme` - Weekend Programme
 * * `other` - Other
 * * `book_the_cafe` - Book the Cafe
 * * `city_brunch_zurich` - City Brunch Zurich
 * * `additional_ausgang` - Additional Ausgang
 * * `on_campus_ausgang` - On Campus Ausgang
 */
export type CampusAbsenceSubTypeEnum =
  | 'afternoon_ausgang'
  | 'evening_ausgang'
  | 'evening_campus_ausgang'
  | 'birthday_ausgang'
  | 'family_visit'
  | 'on_board'
  | 'prolonged_ausgang'
  | 'campus'
  | 'offsite'
  | 'offsite_overnight'
  | 'official_appointment'
  | 'specialist_doctor_appointment'
  | 'programme'
  | 'house_evening'
  | 'term_break'
  | 'school_trip'
  | 'weekend_programme'
  | 'other'
  | 'book_the_cafe'
  | 'city_brunch_zurich'
  | 'additional_ausgang'
  | 'on_campus_ausgang'

/**
 * * `ausgang` - Ausgang
 * * `external_exam` - External exam
 * * `official` - Official
 * * `school_programme` - School Programme
 * * `extra_ausgang` - Extra Ausgang
 */
export type CampusAbsenceTypeEnum =
  | 'ausgang'
  | 'external_exam'
  | 'official'
  | 'school_programme'
  | 'extra_ausgang'

export type CampusAbsenceWarningRequest = {
  /** @format date-time */
  date_from: string
  /** @format date-time */
  date_to: string
  students: string[]
  /** @format uuid */
  excluded_campus_absence?: string | null
}

export type CampusAbsencesChoices = {
  statuses: StringOption[]
  types: StringOption[]
  subtypes: StringOption[]
  valid_subtypes_for_type: Record<string, string[]>
}

/** Collects and raises validation errors in bulk */
export type CampusHeadTeamProfile = {
  id: string
  /** @format uuid */
  user?: string | null
}

/** Collects and raises validation errors in bulk */
export type CampusHeadTeamProfileRequest = {
  /** @format uuid */
  user?: string | null
}

/**
 * * `on campus` - on campus
 * * `off campus` - off campus
 */
export type CampusStatusEnum = 'on campus' | 'off campus'

/**
 * * `leave request` - leave request
 * * `school programme` - school programme
 * * `campus absence` - campus absence
 * * `absent excused` - absent excused
 * * `absent unexcused` - absent unexcused
 * * `sick` - sick
 */
export type CampusStatusReasonEnum =
  | 'leave request'
  | 'school programme'
  | 'campus absence'
  | 'absent excused'
  | 'absent unexcused'
  | 'sick'

/**
 * * `no_cancellation` - Don't cancel classes
 * * `msl_hsl_enrolled` - Cancel all the MSL and HSL classes for the students enrolled in this exam session.
 * * `msl_hsl_enrolled_and_not_enrolled` - Cancel all the MSL and HSL classes for all the students from selected years (enrolled and not enrolled in this exam session).
 * * `enrolled` - Cancel all the classes for the students enrolled in this exam session.
 * * `enrolled_and_not_enrolled` - Cancel all the classes for all the students from selected years (enrolled and not enrolled in this exam session).
 */
export type CancelClassTypeEnum =
  | 'no_cancellation'
  | 'msl_hsl_enrolled'
  | 'msl_hsl_enrolled_and_not_enrolled'
  | 'enrolled'
  | 'enrolled_and_not_enrolled'

export type CancelLesson = {
  id: string
  lesson: CancelledLesson
  reasons: string
}

export type CancelLessonsAvailableFiltersResponse = {
  teacher: AvailableStringFilter
  cancellation_reason: AvailableStringFilter
  quarter: AvailableIntegerFilter
}

export type CancelledLesson = {
  /** @format uuid */
  id: string
  /** @format date */
  date: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  course_str: string
  teacher: SimpleUser
  teacher_cover: SimpleUser | null
}

export type CandidateAcademicOffer = {
  id: number
  /**
   * * `5` - Not started
   * * `10` - Offered
   * * `15` - Waitlist offered
   * * `20` - Accepted
   * * `25` - Waitlist accepted
   * * `30` - No reply
   * * `35` - Rejected
   * * `40` - Lost
   */
  offer_status: AcademicOfferStatusEnum
  program_type?: ProgramTypeEnum | NullEnum | null
  /** @format date */
  offer_deadline?: string | null
  /**
   * @min 1
   * @max 15
   */
  offer_length?: number | null
  /** @format date */
  admission_payment_date: string | null
  /**
   * @min 0
   * @max 32767
   */
  invoice_status: InvoiceStatusEnum
}

export type CandidateAcademicOfferList = {
  /**
   * * `5` - Not started
   * * `10` - Offered
   * * `15` - Waitlist offered
   * * `20` - Accepted
   * * `25` - Waitlist accepted
   * * `30` - No reply
   * * `35` - Rejected
   * * `40` - Lost
   */
  offer_status: AcademicOfferStatusEnum
  invoice_status: InvoiceStatusEnum
}

export type CandidateAcademicOfferRequest = {
  /**
   * * `5` - Not started
   * * `10` - Offered
   * * `15` - Waitlist offered
   * * `20` - Accepted
   * * `25` - Waitlist accepted
   * * `30` - No reply
   * * `35` - Rejected
   * * `40` - Lost
   */
  offer_status: AcademicOfferStatusEnum
  program_type?: ProgramTypeEnum | NullEnum | null
  /** @format date */
  offer_deadline?: string | null
  /**
   * @min 1
   * @max 15
   */
  offer_length?: number | null
  /** @format date */
  admission_payment_date: string | null
  /**
   * @min 0
   * @max 32767
   */
  invoice_status: InvoiceStatusEnum
}

export type CandidateProfileAvailableFilters = {
  age: AvailableIntegerFilter
  candidate: AvailableStringFilter
  status: AvailableIntegerFilter
  academic_review_status: AvailableStringFilter
  student__year_of_rosenberg_entry: AvailableIntegerFilter
  academic_offer__offer_status: AvailableStringFilter
  academic_offer__invoice_status: AvailableStringFilter
  academic_offer__program_type: AvailableStringFilter
  nationalities: AvailableStringFilter
  career_goal: AvailableIntegerFilter
  fitness_for_sports: AvailableStringFilter
  is_swimmer: AvailableBooleanFilter
  is_athlete: AvailableBooleanFilter
  is_resident_entrepreneur: AvailableBooleanFilter
  insurance_type: AvailableStringFilter
  student__gender: AvailableStringFilter
  allergen_kinds: AvailableStringFilter
  chronic_condition_types: AvailableStringFilter
  meal_preference: AvailableStringFilter
  current_grade_level: AvailableIntegerFilter
  hide_enrolled: AvailableBooleanFilter
}

export type CandidateProfileDetail = {
  /** Student */
  pk: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  age: number | null
  status: CandidateProfileStatusEnum
  /**
   * @min 0
   * @max 32767
   */
  academic_review_status: AcademicReviewStatusEnum
  /**
   * @min 1
   * @max 15
   */
  year_until_high_school_graduation?: number | null
  student: StudentCandidateProfileDetail
  academic_offer: CandidateAcademicOffer
}

export type CandidateProfileDetailRequest = {
  /**
   * @min 0
   * @max 32767
   */
  academic_review_status: AcademicReviewStatusEnum
  /**
   * @min 1
   * @max 15
   */
  year_until_high_school_graduation?: number | null
  student: StudentCandidateProfileDetailRequest
  academic_offer: CandidateAcademicOfferRequest
}

export type CandidateProfileList = {
  /** Student */
  pk: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  age: number | null
  current_grade_level: number | null
  student: StudentCandidateProfileList
  /**
   * * `1` - Completed
   * * `2` - In progress
   * * `3` - Waitlist
   * * `4` - Rejected
   * * `5` - Applied
   * * `7` - Lost
   */
  status: CandidateProfileStatusEnum
  academic_review_status: AcademicReviewStatusEnum
  academic_offer: CandidateAcademicOfferList
  is_athlete: boolean | null
}

/**
 * * `1` - Completed
 * * `2` - In progress
 * * `3` - Waitlist
 * * `4` - Rejected
 * * `5` - Applied
 * * `7` - Lost
 */
export type CandidateProfileStatusEnum = 1 | 2 | 3 | 4 | 5 | 7

export type CandidateProfileSummaryResponse = {
  status?: Record<string, number>
  academic_review_status?: Record<string, number>
  offer_status?: Record<string, number>
  invoice_status?: Record<string, number>
  enrolled_status?: Record<string, number>
}

/**
 * * `0` - U
 * * `1` - S
 * * `2` - D
 * * `3` - T
 */
export type CapacityEnum = 0 | 1 | 2 | 3

export type Catalog = {
  id: number
  name: string
}

export type CatalogRequest = {
  /** @minLength 1 */
  name: string
}

/**
 * * `4` - S
 * * `3` - E
 * * `2` - A
 * * `1` - C
 * * `0` - not assigned
 */
export type CategoriesEnum = 4 | 3 | 2 | 1 | 0

/**
 * * `tops` - Tops
 * * `bottoms` - Bottoms
 * * `dresses` - Dresses
 * * `outerwear` - Outerwear
 * * `formal_clothing` - Formal clothing
 * * `accessories` - Accessories
 * * `lingerie` - Lingerie
 * * `bathroom_textiles` - Bathroom textiles
 * * `private_bed_linen` - Private bed linen
 * * `sport_clothing` - Sport clothing
 */
export type CategoryEnum =
  | 'tops'
  | 'bottoms'
  | 'dresses'
  | 'outerwear'
  | 'formal_clothing'
  | 'accessories'
  | 'lingerie'
  | 'bathroom_textiles'
  | 'private_bed_linen'
  | 'sport_clothing'

/**
 * * `tops` - Tops
 * * `bottoms` - Bottoms
 * * `dresses` - Dresses
 * * `outerwear` - Outerwear
 * * `formal_clothing` - Formal clothing
 * * `accessories` - Accessories
 * * `lingerie` - Lingerie
 * * `bathroom_textiles` - Bathroom textiles
 * * `private_bed_linen` - Private bed linen
 * * `sport_clothing` - Sport clothing
 */
export type CategoryKeyEnum =
  | 'tops'
  | 'bottoms'
  | 'dresses'
  | 'outerwear'
  | 'formal_clothing'
  | 'accessories'
  | 'lingerie'
  | 'bathroom_textiles'
  | 'private_bed_linen'
  | 'sport_clothing'

/**
 * * `met` - Met
 * * `over_limit` - Over limit
 * * `not_enough` - Not enough
 */
export type CcuRulesEnum = 'met' | 'over_limit' | 'not_enough'

export type ChangeAvatar = {
  /** @format uri */
  current_avatar: string
}

export type ChangeAvatarRequest = {
  avatar: string | null
}

export type ChangeCoTeacher1InSectionRequest = {
  /** @format uuid */
  co_teacher_1: string | null
  target_group: number
  apply_in_quarters: QuarterShortNameEnum[]
}

export type ChangeCoTeacher2InSectionRequest = {
  /** @format uuid */
  co_teacher_2: string | null
  target_group: number
  apply_in_quarters: QuarterShortNameEnum[]
}

export type ChangeEndDateMedicineInPlan = {
  /** @format date-time */
  end_date: string
}

export type ChangeEndDateMedicineInPlanRequest = {
  /** @format date-time */
  end_date: string
}

export type ChangeOrderRequest = {
  /** @minLength 1 */
  post_id: string
  /** @minLength 1 */
  after_post_id: string | null
  /** @minLength 1 */
  before_post_id: string | null
}

/**
 * * `requested_by_parent` - Requested by parent (e-mail)
 * * `requested_by_doctor` - Requested by doctor
 * * `wrong_entry` - Wrong entry (mistake)
 */
export type ChangeReasonEnum =
  | 'requested_by_parent'
  | 'requested_by_doctor'
  | 'wrong_entry'

export type ChangeTeacherInSectionRequest = {
  /** @format uuid */
  teacher: string
  target_group: number
  apply_in_quarters: QuarterShortNameEnum[]
}

export type ChangesInSectionsRequest = {
  enrolments: AssignStudentToSectionRequest[]
  teachers: ChangeTeacherInSectionRequest[]
  co_teachers_1: ChangeCoTeacher1InSectionRequest[]
  co_teachers_2: ChangeCoTeacher2InSectionRequest[]
}

export type CheckClashesRequest = {
  /** @format date-time */
  date_from: string
  /** @format date-time */
  date_to: string
}

export type ChronicCondition = {
  id: number
  /** @format date-time */
  created: string
  type: string
  subtypes?: string[]
}

export type ChronicConditionChoicesResponse = {
  condition_types: ConditionTypeOption[]
  impacts_on_daily_routine: StringOption[]
  watch_out_for: StringOption[]
  emergency_medicine_applications: StringOption[]
  emergency_medicine_locations: StringOption[]
  iep_contacts: StringOption[]
  immediate_responses: StringOption[]
}

export type ChronicConditionRequest = {
  /** @minLength 1 */
  type: string
  subtypes?: string[]
}

export type ClashWithExamEC = {
  id: string
  course_name: string
  /** @format date */
  date?: string | null
  /** @format time */
  start_time?: string | null
  /** @format time */
  end_time?: string | null
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
}

/**
 * * `regular` - Regular
 * * `sport` - Sport
 */
export type ClassTypeEnum = 'regular' | 'sport'

export type CloseEnrolmentChoiceRequest = {
  /**
   * * `close_for_recurring` - close_for_recurring
   * * `close_for_all` - close_for_all
   * * `withdraw_enrolment` - withdraw_enrolment
   */
  close_type: CloseTypeEnum
}

export type CloseEnrolmentChoiceV2Request = {
  /**
   * * `close_for_recurring` - close_for_recurring
   * * `close_for_all` - close_for_all
   * * `withdraw_enrolment` - withdraw_enrolment
   */
  close_type: CloseTypeEnum
}

/**
 * * `mistake` - Sick report made by mistake
 * * `student_not_sick` - Student not sick
 * * `student_recovered` - Student recovered
 */
export type CloseReasonEnum =
  | 'mistake'
  | 'student_not_sick'
  | 'student_recovered'

export type CloseSickReport = {
  id: string
  /**
   * * `mistake` - Sick report made by mistake
   * * `student_not_sick` - Student not sick
   * * `student_recovered` - Student recovered
   */
  close_reason: CloseReasonEnum
}

export type CloseSickReportRequest = {
  /**
   * * `mistake` - Sick report made by mistake
   * * `student_not_sick` - Student not sick
   * * `student_recovered` - Student recovered
   */
  close_reason: CloseReasonEnum
}

/**
 * * `close_for_recurring` - close_for_recurring
 * * `close_for_all` - close_for_all
 * * `withdraw_enrolment` - withdraw_enrolment
 */
export type CloseTypeEnum =
  | 'close_for_recurring'
  | 'close_for_all'
  | 'withdraw_enrolment'

/**
 * * `date` - date
 * * `category` - category
 * * `day_student` - day_student
 * * `no_available_meals` - no_available_meals
 * * `already_sent` - already_sent
 * * `disabled` - disabled
 */
export type ClosedReasonEnum =
  | 'date'
  | 'category'
  | 'day_student'
  | 'no_available_meals'
  | 'already_sent'
  | 'disabled'

export type CockpitDraftAssignment = {
  /** @format uuid */
  id: string
  /** @maxLength 255 */
  name?: string | null
  type: SimpleAssignmentType
  students: StudentFullName[]
  /** @format date */
  due_date?: string | null
  week: StringOption | null
  /**
   * Indicates the order of assignments in the section plan item
   * @min 0
   * @max 2147483647
   */
  ordering?: number
}

export type CockpitDraftAssignmentUpdateRequest = {
  /** @format uuid */
  id?: string
  /** @maxLength 255 */
  name?: string | null
  type?: number
  students?: string[]
  /** @format date */
  due_date?: string | null
  /** @minLength 1 */
  week?: string | null
  /**
   * Indicates the order of assignments in the section plan item
   * @min 0
   * @max 2147483647
   */
  ordering?: number
}

export type CockpitEditingPeriod = {
  /** @format uuid */
  id: string
  scopes: ScopesEnum[]
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
}

export type CockpitEditingPeriodRequest = {
  scopes: ScopesEnum[]
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
}

export type CockpitSectionGroup = {
  id: number
  /**
   * * `to_do` - To do
   * * `in_planning` - In planning
   * * `ready_for_review` - Ready for review
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `partially_rejected` - Partially rejected
   */
  section_group_plan_status: SectionGroupPlanStatusEnum
  teachers: SimpleUser[]
  section_group_plan_id: string | null
  school_year: SimpleSchoolYear
  has_ready_global_standard: boolean
  was_rejected: boolean
  section_ids: number[]
  students: string[]
}

export type CockpitSectionGroupPlanStatusStats = {
  /** @default 0 */
  to_do?: number
  /** @default 0 */
  in_planning?: number
  /** @default 0 */
  ready_for_review?: number
  /** @default 0 */
  approved?: number
  /** @default 0 */
  rejected?: number
}

export type CockpitSectionGroupsAvailableFiltersResponse = {
  course: AvailableStringFilter
  teachers: AvailableStringFilter
  school_year: AvailableIntegerFilter
  section_group_plan_status: AvailableStringFilter
}

export type CockpitSectionGroupsStats = {
  section_group_plan_status: CockpitSectionGroupPlanStatusStats
}

export type CodeRequestResponse = {
  /**
   * Masked email address, e.g. pa***@example.com.
   * @format email
   */
  email: string
  cannot_use_sms_login: boolean
  /** @format uuid */
  challenge_id: string
}

export type Comment = {
  related_id: string
  related_author: string
  author_first_name: string
  author_last_name: string
  author_profiles: string[]
  /** @format uuid */
  student_id: string
  student_first_name: string
  student_last_name: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  comment_type: string
  is_weekly: boolean
  related_course: string
  course_name: string
  content: string
}

/**
 * * `weekly` - Weekly Comment
 * * `quarter` - Quarter Comment
 * * `semester` - Semester Comment
 */
export type CommentGenerationContextCommentTypeEnum =
  | 'weekly'
  | 'quarter'
  | 'semester'

export type CommentGenerationContextRequest = {
  acc_grade?: number
  /** @format uuid */
  course?: string
  /**
   * * `weekly` - Weekly Comment
   * * `quarter` - Quarter Comment
   * * `semester` - Semester Comment
   */
  comment_type: CommentGenerationContextCommentTypeEnum
}

export type CommentGenerationRequest = {
  /** @format uuid */
  student: string
  /**
   * * `generate` - Generate Comment
   * * `improve` - Improve Comment
   */
  action_type: ActionType421Enum
  /** @minLength 1 */
  draft_comment?: string
  /** @minLength 1 */
  user_instructions?: string
  context?: CommentGenerationContextRequest
  history?: CommentHistoryMessageRequest[]
}

export type CommentGenerationResponse = {
  result: string
  /**
   * * `generate` - Generate Comment
   * * `improve` - Improve Comment
   */
  action_type: ActionType421Enum
}

export type CommentHistoryMessageRequest = {
  /**
   * * `user` - user
   * * `assistant` - assistant
   */
  role: CommentHistoryMessageRoleEnum
  /** @minLength 1 */
  content: string
}

/**
 * * `user` - user
 * * `assistant` - assistant
 */
export type CommentHistoryMessageRoleEnum = 'user' | 'assistant'

/**
 * * `offer` - Offer
 * * `choice` - Choice
 * * `results` - Results
 */
export type CommentType3CfEnum = 'offer' | 'choice' | 'results'

export type Company = {
  /** @format uuid */
  id: string
  name: string
}

export type CompanyAvailableFilters = {
  student: AvailableStringFilter
  candidate: AvailableStringFilter
  parent: AvailableStringFilter
}

export type CompanyCreate = {
  name: string
  /**
   * @format email
   * @maxLength 254
   */
  company_email: string
  billing_email_address: string
  billing_additional_email_id?: number | null
  /** @maxLength 128 */
  company_phone_number: string
  crucial_information?: string
  languages?: NativeLanguageEnum[]
}

export type CompanyCreateRequest = {
  /** @minLength 1 */
  name: string
  /**
   * @format email
   * @minLength 1
   * @maxLength 254
   */
  company_email: string
  billing_additional_email_id?: number | null
  /**
   * @minLength 1
   * @maxLength 128
   */
  company_phone_number: string
  crucial_information?: string
  languages?: NativeLanguageEnum[]
}

export type CompanyDetail = {
  /** @format uuid */
  id: string
  name: string
  /** @format email */
  company_email: string
  billing_email_address: string
  billing_additional_email_id?: number | null
  company_phone_number: string
  /** @format date-time */
  date_joined: string
  /** @format date-time */
  last_update: string
  crucial_information: string
  languages: NativeLanguageEnum[]
}

export type CompanyList = {
  /** @format uuid */
  id: string
  name: string
  /** @format email */
  company_email: string
  company_phone_number: string
  /** @format date-time */
  date_joined: string
  /** @format date-time */
  last_update: string
  students: SimpleUser[]
  candidates: SimpleUser[]
  parents: SimpleUser[]
}

export type CompanyUpdate = {
  name: string
  /**
   * @format email
   * @maxLength 254
   */
  company_email: string
  billing_email_address: string
  billing_additional_email_id?: number | null
  /** @maxLength 128 */
  company_phone_number: string
  crucial_information?: string
  languages?: NativeLanguageEnum[]
}

export type CompanyUpdateRequest = {
  /** @minLength 1 */
  name: string
  /**
   * @format email
   * @minLength 1
   * @maxLength 254
   */
  company_email: string
  billing_additional_email_id?: number | null
  /**
   * @minLength 1
   * @maxLength 128
   */
  company_phone_number: string
  crucial_information?: string
  languages?: NativeLanguageEnum[]
}

export type ComparisonTableRow = Record<
  string,
  {
    updated: boolean
    hidden: boolean
    old: string | null
    new: string | null
    errors: string[]
  }
>

export type ComparisonTableRowRequest = Record<
  string,
  {
    updated: boolean
    hidden: boolean
    /** @minLength 1 */
    old: string | null
    /** @minLength 1 */
    new: string | null
    errors: string[]
  }
>

export type Competency = {
  id: number
  /** @maxLength 256 */
  name: string
}

export type CompetencyRequest = {
  /**
   * @minLength 1
   * @maxLength 256
   */
  name: string
}

export type Condition = {
  id: number
  /** @format uuid */
  health_check: string
  /**
   * * `unchanged` - Unchanged
   * * `better` - Better
   * * `worse` - Worse
   */
  status: ConditionStatusEnum
  new_symptoms?: NewSymptomsEnum[]
  new_symptoms_appeared?: boolean
}

export type ConditionRequest = {
  /** @format uuid */
  health_check: string
  /**
   * * `unchanged` - Unchanged
   * * `better` - Better
   * * `worse` - Worse
   */
  status: ConditionStatusEnum
  new_symptoms?: NewSymptomsEnum[]
  new_symptoms_appeared?: boolean
}

/**
 * * `unchanged` - Unchanged
 * * `better` - Better
 * * `worse` - Worse
 */
export type ConditionStatusEnum = 'unchanged' | 'better' | 'worse'

export type ConditionTypeOption = {
  label: string
  value: string
  subtypes: StringOption[]
}

export type ContactData = {
  /** @format uuid */
  id: string
  full_name: string
  salutation: string | null
  title: string | null
  /**
   * Email address
   * @format email
   */
  email: string
  phone_number: string
  user_type: UserTypeResponse
  status: StatusResponse | null
  has_parent_app_access: boolean
  academic_details: ParentAcademicSummary | null
}

/**
 * * `academic` - Academic
 * * `accounting` - Accounting
 * * `app_helpdesk` - App Helpdesk
 * * `boarding` - Boarding
 * * `co_curricular` - Co-Curricular
 * * `health` - Health
 * * `idp` - IDP (R) - Individual Development Plan
 * * `leave_request` - Leave request
 * * `meal_plans` - Meal Plans
 * * `other` - Other topics
 * * `rooming` - Rooming
 * * `sport` - Sport
 * * `talent_and_enrichment` - Talent & Enrichment
 * * `university_application` - University Application
 */
export type ContactMessageSubjectEnum =
  | 'academic'
  | 'accounting'
  | 'app_helpdesk'
  | 'boarding'
  | 'co_curricular'
  | 'health'
  | 'idp'
  | 'leave_request'
  | 'meal_plans'
  | 'other'
  | 'rooming'
  | 'sport'
  | 'talent_and_enrichment'
  | 'university_application'

/**
 * * `first_parent` - First Parent
 * * `second_parent` - Second Parent
 * * `relative` - Relative
 * * `other` - Other
 */
export type ContactPersonEnum =
  | 'first_parent'
  | 'second_parent'
  | 'relative'
  | 'other'

/**
 * * `parent` - Parent
 * * `legal_guardian` - Legal Guardian
 * * `in_legal_guardian_absence` - In Legal Guardian Absence
 * * `arrival_contact` - Arrival Contact
 * * `educational_consultant` - Educational Consultant
 * * `main_invoice_recipient` - Main Invoice Recipient
 * * `invoice_recipient` - Invoice Recipient
 * * `contact_leave` - Contact Leave
 * * `invoice_copy_recipient` - Invoice Copy Recipient
 * * `assistant` - Assistant
 * * `relative` - Relative
 * * `other` - Other
 */
export type ContactTypeEnum =
  | 'parent'
  | 'legal_guardian'
  | 'in_legal_guardian_absence'
  | 'arrival_contact'
  | 'educational_consultant'
  | 'main_invoice_recipient'
  | 'invoice_recipient'
  | 'contact_leave'
  | 'invoice_copy_recipient'
  | 'assistant'
  | 'relative'
  | 'other'

export type ContactUserTypeEnum = 1 | 2 | 3

/** * `widget` - widget */
export type ContentType649Enum = 'widget'

/** * `student_content` - student_content */
export type ContentTypeCf1Enum = 'student_content'

/** * `message` - message */
export type ContentTypeF8CEnum = 'message'

export type CopyRotaGenerationRequest = {
  source_timetable: number
  target_timetable: number
}

export type CopySectionsConflictResponse = {
  message: string
  sections_summary: CopySectionsSummary
}

export type CopySectionsDifference = {
  course_code: string
  course_names: string[]
  /**
   * Reason why the course could not be matched by its case-sensitive course code and unordered set of published grade IDs. `grades_mismatch`: the code exists in both quarters, but the grade sets do not match; `missing_in_destination`: the code exists only in the source quarter; `missing_in_source`: the code exists only in the destination quarter.
   *
   * * `grades_mismatch` - Grades mismatch
   * * `missing_in_destination` - Missing in destination
   * * `missing_in_source` - Missing in source
   */
  reason: CopySectionsDifferenceReasonEnum
  source: CopySectionsDifferenceSide
  destination: CopySectionsDifferenceSide
}

/**
 * * `grades_mismatch` - Grades mismatch
 * * `missing_in_destination` - Missing in destination
 * * `missing_in_source` - Missing in source
 */
export type CopySectionsDifferenceReasonEnum =
  | 'grades_mismatch'
  | 'missing_in_destination'
  | 'missing_in_source'

export type CopySectionsDifferenceSide = {
  course_enrolment_ids: number[]
  grades: CopySectionsGrade[]
  sections_count: number
  students: CopySectionsStudent[]
}

export type CopySectionsDifferencesResponse = {
  error_type: string
  message: string
  differences: CopySectionsDifference[]
  sections_summary: CopySectionsSummary
  query_param_to_force?: string
}

export type CopySectionsGrade = {
  id: number
  name: string
}

export type CopySectionsOperationResult = {
  source_quarter: number
  destination_quarter: number
  copied_sections_count: number | null
  copied_enrolments_count: number | null
  differences: CopySectionsDifference[]
  sections_summary?: CopySectionsSummary
}

export type CopySectionsStudent = {
  /** @format uuid */
  id: string
  full_name: string
}

export type CopySectionsSummary = {
  source_sections_count: number
  copyable_sections_count: number
  copied_sections_count: number | null
  destination_sections_to_replace_count: number
}

export type CopyTalentEnrichment = {
  source_timetable: number
  target_timetable: number
}

export type CopyTalentEnrichmentRequest = {
  source_timetable: number
  target_timetable: number
}

export type Country = {
  /**
   * * `AF` - Afghanistan
   * * `AX` - Åland Islands
   * * `AL` - Albania
   * * `DZ` - Algeria
   * * `AS` - American Samoa
   * * `AD` - Andorra
   * * `AO` - Angola
   * * `AI` - Anguilla
   * * `AQ` - Antarctica
   * * `AG` - Antigua and Barbuda
   * * `AR` - Argentina
   * * `AM` - Armenia
   * * `AW` - Aruba
   * * `AU` - Australia
   * * `AT` - Austria
   * * `AZ` - Azerbaijan
   * * `BS` - Bahamas
   * * `BH` - Bahrain
   * * `BD` - Bangladesh
   * * `BB` - Barbados
   * * `BY` - Belarus
   * * `BE` - Belgium
   * * `BZ` - Belize
   * * `BJ` - Benin
   * * `BM` - Bermuda
   * * `BT` - Bhutan
   * * `BO` - Bolivia
   * * `BQ` - Bonaire, Sint Eustatius and Saba
   * * `BA` - Bosnia and Herzegovina
   * * `BW` - Botswana
   * * `BV` - Bouvet Island
   * * `BR` - Brazil
   * * `IO` - British Indian Ocean Territory
   * * `BN` - Brunei
   * * `BG` - Bulgaria
   * * `BF` - Burkina Faso
   * * `BI` - Burundi
   * * `CV` - Cabo Verde
   * * `KH` - Cambodia
   * * `CM` - Cameroon
   * * `CA` - Canada
   * * `KY` - Cayman Islands
   * * `CF` - Central African Republic
   * * `TD` - Chad
   * * `CL` - Chile
   * * `CN` - China
   * * `CX` - Christmas Island
   * * `CC` - Cocos (Keeling) Islands
   * * `CO` - Colombia
   * * `KM` - Comoros
   * * `CG` - Congo
   * * `CD` - Congo (the Democratic Republic of the)
   * * `CK` - Cook Islands
   * * `CR` - Costa Rica
   * * `CI` - Côte d'Ivoire
   * * `HR` - Croatia
   * * `CU` - Cuba
   * * `CW` - Curaçao
   * * `CY` - Cyprus
   * * `CZ` - Czechia
   * * `DK` - Denmark
   * * `DJ` - Djibouti
   * * `DM` - Dominica
   * * `DO` - Dominican Republic
   * * `EC` - Ecuador
   * * `EG` - Egypt
   * * `SV` - El Salvador
   * * `GQ` - Equatorial Guinea
   * * `ER` - Eritrea
   * * `EE` - Estonia
   * * `SZ` - Eswatini
   * * `ET` - Ethiopia
   * * `FK` - Falkland Islands (Malvinas)
   * * `FO` - Faroe Islands
   * * `FJ` - Fiji
   * * `FI` - Finland
   * * `FR` - France
   * * `GF` - French Guiana
   * * `PF` - French Polynesia
   * * `TF` - French Southern Territories
   * * `GA` - Gabon
   * * `GM` - Gambia
   * * `GE` - Georgia
   * * `DE` - Germany
   * * `GH` - Ghana
   * * `GI` - Gibraltar
   * * `GR` - Greece
   * * `GL` - Greenland
   * * `GD` - Grenada
   * * `GP` - Guadeloupe
   * * `GU` - Guam
   * * `GT` - Guatemala
   * * `GG` - Guernsey
   * * `GN` - Guinea
   * * `GW` - Guinea-Bissau
   * * `GY` - Guyana
   * * `HT` - Haiti
   * * `HM` - Heard Island and McDonald Islands
   * * `VA` - Holy See
   * * `HN` - Honduras
   * * `HK` - Hong Kong
   * * `HU` - Hungary
   * * `IS` - Iceland
   * * `IN` - India
   * * `ID` - Indonesia
   * * `IR` - Iran
   * * `IQ` - Iraq
   * * `IE` - Ireland
   * * `IM` - Isle of Man
   * * `IL` - Israel
   * * `IT` - Italy
   * * `JM` - Jamaica
   * * `JP` - Japan
   * * `JE` - Jersey
   * * `JO` - Jordan
   * * `KZ` - Kazakhstan
   * * `KE` - Kenya
   * * `KI` - Kiribati
   * * `XK` - Kosovo
   * * `KW` - Kuwait
   * * `KG` - Kyrgyzstan
   * * `LA` - Laos
   * * `LV` - Latvia
   * * `LB` - Lebanon
   * * `LS` - Lesotho
   * * `LR` - Liberia
   * * `LY` - Libya
   * * `LI` - Liechtenstein
   * * `LT` - Lithuania
   * * `LU` - Luxembourg
   * * `MO` - Macao
   * * `MG` - Madagascar
   * * `MW` - Malawi
   * * `MY` - Malaysia
   * * `MV` - Maldives
   * * `ML` - Mali
   * * `MT` - Malta
   * * `MH` - Marshall Islands
   * * `MQ` - Martinique
   * * `MR` - Mauritania
   * * `MU` - Mauritius
   * * `YT` - Mayotte
   * * `MX` - Mexico
   * * `FM` - Micronesia
   * * `MD` - Moldova
   * * `MC` - Monaco
   * * `MN` - Mongolia
   * * `ME` - Montenegro
   * * `MS` - Montserrat
   * * `MA` - Morocco
   * * `MZ` - Mozambique
   * * `MM` - Myanmar
   * * `NA` - Namibia
   * * `NR` - Nauru
   * * `NP` - Nepal
   * * `NL` - Netherlands
   * * `NC` - New Caledonia
   * * `NZ` - New Zealand
   * * `NI` - Nicaragua
   * * `NE` - Niger
   * * `NG` - Nigeria
   * * `NU` - Niue
   * * `NF` - Norfolk Island
   * * `KP` - North Korea
   * * `MK` - North Macedonia
   * * `MP` - Northern Mariana Islands
   * * `NO` - Norway
   * * `OM` - Oman
   * * `PK` - Pakistan
   * * `PW` - Palau
   * * `PS` - Palestine, State of
   * * `PA` - Panama
   * * `PG` - Papua New Guinea
   * * `PY` - Paraguay
   * * `PE` - Peru
   * * `PH` - Philippines
   * * `PN` - Pitcairn
   * * `PL` - Poland
   * * `PT` - Portugal
   * * `PR` - Puerto Rico
   * * `QA` - Qatar
   * * `RE` - Réunion
   * * `RO` - Romania
   * * `RU` - Russia
   * * `RW` - Rwanda
   * * `BL` - Saint Barthélemy
   * * `SH` - Saint Helena, Ascension and Tristan da Cunha
   * * `KN` - Saint Kitts and Nevis
   * * `LC` - Saint Lucia
   * * `MF` - Saint Martin (French part)
   * * `PM` - Saint Pierre and Miquelon
   * * `VC` - Saint Vincent and the Grenadines
   * * `WS` - Samoa
   * * `SM` - San Marino
   * * `ST` - Sao Tome and Principe
   * * `SA` - Saudi Arabia
   * * `SN` - Senegal
   * * `RS` - Serbia
   * * `SC` - Seychelles
   * * `SL` - Sierra Leone
   * * `SG` - Singapore
   * * `SX` - Sint Maarten (Dutch part)
   * * `SK` - Slovakia
   * * `SI` - Slovenia
   * * `SB` - Solomon Islands
   * * `SO` - Somalia
   * * `ZA` - South Africa
   * * `GS` - South Georgia and the South Sandwich Islands
   * * `KR` - South Korea
   * * `SS` - South Sudan
   * * `ES` - Spain
   * * `LK` - Sri Lanka
   * * `SD` - Sudan
   * * `SR` - Suriname
   * * `SJ` - Svalbard and Jan Mayen
   * * `SE` - Sweden
   * * `CH` - Switzerland
   * * `SY` - Syria
   * * `TW` - Taiwan
   * * `TJ` - Tajikistan
   * * `TZ` - Tanzania
   * * `TH` - Thailand
   * * `TL` - Timor-Leste
   * * `TG` - Togo
   * * `TK` - Tokelau
   * * `TO` - Tonga
   * * `TT` - Trinidad and Tobago
   * * `TN` - Tunisia
   * * `TR` - Türkiye
   * * `TM` - Turkmenistan
   * * `TC` - Turks and Caicos Islands
   * * `TV` - Tuvalu
   * * `UG` - Uganda
   * * `UA` - Ukraine
   * * `AE` - United Arab Emirates
   * * `GB` - United Kingdom
   * * `UM` - United States Minor Outlying Islands
   * * `US` - United States of America
   * * `UY` - Uruguay
   * * `UZ` - Uzbekistan
   * * `VU` - Vanuatu
   * * `VE` - Venezuela
   * * `VN` - Vietnam
   * * `VG` - Virgin Islands (British)
   * * `VI` - Virgin Islands (U.S.)
   * * `WF` - Wallis and Futuna
   * * `EH` - Western Sahara
   * * `YE` - Yemen
   * * `ZM` - Zambia
   * * `ZW` - Zimbabwe
   */
  code: NationalityEnum
  name: string
}

export type CourseAllowedSlots = {
  id: number
  /**
   * @min 0
   * @max 2147483647
   */
  day: WeekDayEnum
  /** @format time */
  start_time: string
  /** @format time */
  end_time: string
}

export type CourseAllowedSlotsRequest = {
  /**
   * @min 0
   * @max 2147483647
   */
  day: WeekDayEnum
  /** @format time */
  start_time: string
  /** @format time */
  end_time: string
}

export type CourseAssignmentType = {
  id: number
  /** @maxLength 64 */
  name: string
}

export type CourseAvailableToEnrollOn = {
  course_enrolment_id: number
  course: TAECourse
  grades: SimpleGradeLevel[]
  maximum_participants: number
  free_spots: number
}

export type CourseDepartment = {
  id: number
  /** @maxLength 32 */
  name: string
}

export type CourseDepartmentRequest = {
  /**
   * @minLength 1
   * @maxLength 32
   */
  name: string
}

export type CourseDescription = {
  description?: string
}

export type CourseDetail = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  name: string
  /** @maxLength 128 */
  code: string
  types?: CourseType[]
  preparation_for_types?: CourseType[]
  levels?: CourseLevel[]
  /** @format double */
  credits: number | null
  departments?: CourseDepartment[]
  rooms?: CourseRoom[]
  teachers: string
  /**
   * @format decimal
   * @pattern ^-?\d{0,1}(?:\.\d{0,2})?$
   */
  humanix_factor?: string
  difficulty: string
  previous_courses: number[]
  next_courses: number[]
  group?: CourseGroup
  families?: CourseFamily[]
  intelligences?: CourseIntelligence[]
  description?: string
  has_academic_exam?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  exam_duration?: number | null
  has_external_exam?: boolean
  external_exam_type?: ExternalExamTypeEnum | BlankEnum
  /** RPS-6527 */
  is_payable?: boolean
  /** @format uri */
  outline_file?: string | null
  outline_file_name: string | null
  aim: SimpleAim | null
  grade_levels: SimpleGradeLevel[]
}

export type CourseDropData = {
  section: number
}

export type CourseDropDataRequest = {
  section: number
}

export type CourseEnrollment = {
  /** @format uuid */
  id: string
  name: string
  teacher_str: string
  teacher: string
  is_talent_and_enrichment: string
  enrollment_id: number
  student: EnrolledStudent
  quarter: number
  course: EnrolledCourse
  course_drop_id: number | null
  /** @format date-time */
  course_drop_created: string | null
}

export type CourseFamily = {
  /** @maxLength 64 */
  id: string
  /** @maxLength 64 */
  name?: string
}

export type CourseFamilyRequest = {
  /**
   * @minLength 1
   * @maxLength 64
   */
  id: string
  /** @maxLength 64 */
  name?: string
}

export type CourseGradesIdpViewResponse = {
  course_group: string | null
  students_grades: StudentIdpViewGrades[]
  former_students_grades: FormerStudentIdpViewGrades[]
}

export type CourseGradesQuarterViewResponse = {
  course_group: string | null
  periods: StudentCourseGradesPeriods
  students_grades: StudentQuarterViewGrades[]
  former_students_grades: FormerStudentQuarterViewGrades[]
}

export type CourseGroup = {
  /** @maxLength 64 */
  id: string
  /** @maxLength 64 */
  name?: string
}

/**
 * * `CCU` - CCU
 * * `ATH` - ATH
 */
export type CourseGroupEnum = 'CCU' | 'ATH'

export type CourseGroupRequest = {
  /**
   * @minLength 1
   * @maxLength 64
   */
  id: string
  /** @maxLength 64 */
  name?: string
}

/**
 * * `CCU` - CCU
 * * `ATH` - ATH
 */
export type CourseGroupsEnum = 'CCU' | 'ATH'

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type CourseInBasket = {
  id: number
  requested_course?: RequestedCourse | null
  /** @format uuid */
  course?: string | null
  course_name: string
  course_code: string
  quarters: IdpSectionEnrollment[]
  basket?: number
  apply_for_all_quarters?: boolean
  mission_control_department?: MissionControlDepartmentEnum | BlankEnum
  changed_to_after_drop?: boolean
  swapped?: boolean
  swap_department?: string | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type CourseInBasketRequest = {
  requested_course?: RequestedCourseRequest | null
  /** @format uuid */
  course?: string | null
  quarters: IdpSectionEnrollmentRequest[]
  basket?: number
  apply_for_all_quarters?: boolean
  mission_control_department?: MissionControlDepartmentEnum | BlankEnum
  changed_to_after_drop?: boolean
  swapped?: boolean
  /** @minLength 1 */
  swap_department?: string | null
}

export type CourseInBasketUpdateDepartment = {
  id: number
  requested_course: number | null
  /** @format uuid */
  course: string | null
  quarters: number[]
  basket: number
  apply_for_all_quarters: boolean
  mission_control_department?: MissionControlDepartmentEnum | BlankEnum
}

export type CourseInBasketUpdateDepartmentRequest = {
  mission_control_department?: MissionControlDepartmentEnum | BlankEnum
}

export type CourseInExamSession = {
  exam_id: string
  date: string
  start_time: string
  end_time: string
  course_id: string
  course_name: string
}

export type CourseInIdp = {
  /** @format uuid */
  id: string
  name: string
  code: string
  departments: string[]
  departments_for_mission_control: string[]
  types: string[]
  levels: string[]
  families: string[]
  group: string | null
  subject: string | null
  number_of_lessons: number | null
  is_talent_and_enrichment: string
  next_course_ids: string[]
  performed_in_quarters: QuarterShortNameEnum[]
}

export type CourseIntelligence = {
  /** @maxLength 64 */
  id: string
  /** @maxLength 64 */
  name?: string
}

export type CourseIntelligenceRequest = {
  /**
   * @minLength 1
   * @maxLength 64
   */
  id: string
  /** @maxLength 64 */
  name?: string
}

export type CourseLatestVersionResponse = {
  is_latest_version: boolean
}

export type CourseLevel = {
  /** @maxLength 64 */
  id: string
  /** @maxLength 64 */
  name?: string
}

export type CourseLevelRequest = {
  /**
   * @minLength 1
   * @maxLength 64
   */
  id: string
  /** @maxLength 64 */
  name?: string
}

export type CourseList = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  name: string
  types: string[]
  preparation_for_types?: string[]
  department_names: string[]
  /** @maxLength 128 */
  code: string
  version: number
  departments: number[]
  is_latest_version?: boolean
  levels: CourseLevel[]
  readable_filter_option: string
  /** RPS-6527 */
  is_payable?: boolean
}

export type CourseMissionControlDepartment = {
  /** @format uuid */
  course: string
  /**
   * * `ENG` - ENG
   * * `MAT` - MAT
   * * `SCI` - SCI
   * * `MLA` - MLA
   * * `SSD` - SSD
   * * `ART` - ART
   */
  mission_control_department: MissionControlDepartmentEnum
}

export type CourseMissionControlDepartmentRequest = {
  /** @format uuid */
  course: string
  /**
   * * `ENG` - ENG
   * * `MAT` - MAT
   * * `SCI` - SCI
   * * `MLA` - MLA
   * * `SSD` - SSD
   * * `ART` - ART
   */
  mission_control_department: MissionControlDepartmentEnum
}

export type CourseOverview = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  name: string
  types: string[]
  preparation_for_types?: string[]
  department_names: string[]
  /** @maxLength 128 */
  code: string
  version: number
  departments: number[]
  is_latest_version?: boolean
  levels: CourseLevel[]
  readable_filter_option: string
  /** RPS-6527 */
  is_payable?: boolean
  section_groups_count: number
  approved_section_groups_count: number
  global_standard_status: string
  global_standard_ready: boolean
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type CoursePlanner = {
  /** @format uuid */
  id: string
  number_of_lessons?: number | null
  outline_file?: string | null
  allowed_slots?: CourseAllowedSlots[] | null
  outline_file_name: string | null
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @format date-time */
  deleted: string | null
  deleted_by_cascade: boolean
  /** @maxLength 128 */
  name: string
  code: string
  /** @default 1 */
  version: number
  is_latest_version?: boolean
  /** @format double */
  credits: number | null
  /**
   * @min 0
   * @max 2147483647
   */
  external_id?: number | null
  difficulty: string
  number: string
  /**
   * @format decimal
   * @pattern ^-?\d{0,1}(?:\.\d{0,2})?$
   */
  humanix_factor?: string
  /** @maxItems 4 */
  performed_in_quarters?: QuarterShortNameEnum[]
  description?: string
  has_academic_exam?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  exam_duration?: number | null
  has_external_exam?: boolean
  external_exam_type?: ExternalExamTypeEnum | BlankEnum
  old_is_exam_course_flag: boolean
  exam_course_name?: string
  /** RPS-6527 */
  is_payable?: boolean
  /** Used in summer camps */
  is_camps_focus?: boolean
  /** Used in summer camps */
  is_camps_private?: boolean
  /** Used in billing reports for sport package++ */
  is_included_in_sport_package?: boolean
  /** @default false */
  is_tae_private?: boolean
  /** Used in T&E generator */
  is_preferred_until_time?: boolean
  /** Exclude course from transcript of grades */
  exclude_from_tog?: boolean
  /** Marks the course as non-credit — it does not grant any credits. */
  is_non_credit_course?: boolean
  custom_constraints?: boolean
  override_allowed_slots?: boolean
  /**
   * @min 5
   * @max 240
   */
  lesson_duration?: number | null
  override_preparation_time?: boolean
  /**
   * @min 0
   * @max 60
   */
  preparation_time_before?: number | null
  /**
   * @min 0
   * @max 60
   */
  preparation_time_after?: number | null
  legacy_department?: number | null
  group: string
  subject: string
  departments: number[]
  families: string[]
  intelligences: string[]
  types: string[]
  preparation_for_types?: string[]
  levels: string[]
  rooms?: number[]
}

export type CoursePlannerList = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  code: string
  /** @maxLength 128 */
  name: string
  is_latest_version?: boolean
  version: number
  /** @maxItems 4 */
  performed_in_quarters?: QuarterShortNameEnum[]
  custom_constraints?: boolean
  group?: string | null
  departments: CourseDepartment[]
  is_tae_private?: boolean
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type CoursePlannerRequest = {
  number_of_lessons?: number | null
  next_courses?: string[]
  related_courses?: string[]
  course_request?: number | null
  outline_file?: string | null
  allowed_slots?: CourseAllowedSlotsRequest[] | null
  /**
   * @minLength 1
   * @maxLength 128
   */
  name: string
  is_latest_version?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  external_id?: number | null
  /** @minLength 1 */
  difficulty: string
  /** @minLength 1 */
  number: string
  /**
   * @format decimal
   * @pattern ^-?\d{0,1}(?:\.\d{0,2})?$
   */
  humanix_factor?: string
  /** @maxItems 4 */
  performed_in_quarters?: QuarterShortNameEnum[]
  description?: string
  has_academic_exam?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  exam_duration?: number | null
  has_external_exam?: boolean
  external_exam_type?: ExternalExamTypeEnum | BlankEnum
  exam_course_name?: string
  /** RPS-6527 */
  is_payable?: boolean
  /** Used in summer camps */
  is_camps_focus?: boolean
  /** Used in summer camps */
  is_camps_private?: boolean
  /** Used in billing reports for sport package++ */
  is_included_in_sport_package?: boolean
  /** @default false */
  is_tae_private?: boolean
  /** Used in T&E generator */
  is_preferred_until_time?: boolean
  /** Exclude course from transcript of grades */
  exclude_from_tog?: boolean
  /** Marks the course as non-credit — it does not grant any credits. */
  is_non_credit_course?: boolean
  custom_constraints?: boolean
  override_allowed_slots?: boolean
  /**
   * @min 5
   * @max 240
   */
  lesson_duration?: number | null
  override_preparation_time?: boolean
  /**
   * @min 0
   * @max 60
   */
  preparation_time_before?: number | null
  /**
   * @min 0
   * @max 60
   */
  preparation_time_after?: number | null
  legacy_department?: number | null
  /** @minLength 1 */
  group: string
  /** @minLength 1 */
  subject: string
  departments: number[]
  families: string[]
  intelligences: string[]
  types: string[]
  preparation_for_types?: string[]
  levels: string[]
  rooms?: number[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type CoursePlannerResponse = {
  /** @format uuid */
  id: string
  number_of_lessons?: number | null
  next_courses: string[]
  outline_file?: string | null
  allowed_slots?: CourseAllowedSlots[] | null
  outline_file_name: string | null
  previous_courses: string[]
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @format date-time */
  deleted: string | null
  deleted_by_cascade: boolean
  /** @maxLength 128 */
  name: string
  code: string
  /** @default 1 */
  version: number
  is_latest_version?: boolean
  /** @format double */
  credits: number | null
  /**
   * @min 0
   * @max 2147483647
   */
  external_id?: number | null
  difficulty: string
  number: string
  /**
   * @format decimal
   * @pattern ^-?\d{0,1}(?:\.\d{0,2})?$
   */
  humanix_factor?: string
  /** @maxItems 4 */
  performed_in_quarters?: QuarterShortNameEnum[]
  description?: string
  has_academic_exam?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  exam_duration?: number | null
  has_external_exam?: boolean
  external_exam_type?: ExternalExamTypeEnum | BlankEnum
  old_is_exam_course_flag: boolean
  exam_course_name?: string
  /** RPS-6527 */
  is_payable?: boolean
  /** Used in summer camps */
  is_camps_focus?: boolean
  /** Used in summer camps */
  is_camps_private?: boolean
  /** Used in billing reports for sport package++ */
  is_included_in_sport_package?: boolean
  /** @default false */
  is_tae_private?: boolean
  /** Used in T&E generator */
  is_preferred_until_time?: boolean
  /** Exclude course from transcript of grades */
  exclude_from_tog?: boolean
  /** Marks the course as non-credit — it does not grant any credits. */
  is_non_credit_course?: boolean
  custom_constraints?: boolean
  override_allowed_slots?: boolean
  /**
   * @min 5
   * @max 240
   */
  lesson_duration?: number | null
  override_preparation_time?: boolean
  /**
   * @min 0
   * @max 60
   */
  preparation_time_before?: number | null
  /**
   * @min 0
   * @max 60
   */
  preparation_time_after?: number | null
  legacy_department?: number | null
  group: string
  subject: string
  departments: number[]
  families: string[]
  intelligences: string[]
  types: string[]
  preparation_for_types?: string[]
  levels: string[]
  rooms?: number[]
}

export type CoursePotentialGradesViewResponse = {
  course_group: string | null
  students_grades: StudentPotentialGradesView[]
  former_students_grades: FormerStudentPotentialGradesView[]
}

export type CourseRoom = {
  id: number
  /** @maxLength 128 */
  name: string
  /**
   * @min 0
   * @max 2147483647
   */
  capacity?: number | null
}

export type CourseSubject = {
  /** @maxLength 64 */
  id: string
  /** @maxLength 64 */
  name?: string
}

export type CourseSubjectRequest = {
  /**
   * @minLength 1
   * @maxLength 64
   */
  id: string
  /** @maxLength 64 */
  name?: string
}

export type CourseType = {
  /** @maxLength 64 */
  id: string
  /** @maxLength 64 */
  name?: string
}

export type CourseTypeRequest = {
  /**
   * @minLength 1
   * @maxLength 64
   */
  id: string
  /** @maxLength 64 */
  name?: string
}

export type CourseWithDepartments = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  name: string
  departments: CourseDepartment[]
}

export type CoursesCapacityOrMaxSectionsIncreased = {
  id: number
  /** @format uuid */
  course_id: string
  course_name: string
  /**
   * @min 0
   * @max 32767
   */
  capacity_per_section: number
  /**
   * @min 0
   * @max 32767
   */
  max_sections: number
}

export type Cover = {
  covering: string[]
  covered?: string[]
}

export type CoverConflictRequest = {
  /** @format date */
  cover_date: string
  cover: CoverRequest
  rota_timetable: number
  /** @format time */
  start_hour?: string
  /** @format time */
  end_hour?: string
}

export type CoverRequest = {
  covering: string[]
  covered?: string[]
}

export type CreateDraftActivitySuggesterTaskRequest = {
  /** @format uuid */
  activity_id: string
  years_to_rearrange: number[]
  /**
   * @min 0
   * @max 2147483647
   */
  to_day?: RotaDayEnum | NullEnum | null
  /**
   * @min 0
   * @max 2147483647
   */
  to_hour?: HourEnum | NullEnum | null
  ignore_rooms_availability?: boolean
  /** @format uuid */
  edit_session_id: string | null
}

export type CreateEditSessionRequest = {
  /** @format uuid */
  edit_session_id?: string | null
}

export type CreateSuggesterTaskResponse = {
  task_id: number
}

export type CreditSummary = {
  department: DepartmentEnum
  /** @format double */
  credits_achieved: number
  /** @format double */
  credits_target: number
}

export type CumulativeItem = {
  course: string
  is_present: number
  is_late: number
  is_absent_excused: number
  is_absent_unexcused: number
  is_sick: number
  marked_attendances_count: number
  missed_lessons_count: number
  /** @format double */
  missed_lessons_percentage: number
  is_course_dropped: boolean
  /** @format date-time */
  course_dropped_at_datetime: string | null
}

export type CumulativeResponse = {
  summary: CumulativeSummary
  items: CumulativeItem[]
}

export type CumulativeSummary = {
  /** @default "General" */
  course: string
  is_present: number
  is_late: number
  is_absent_excused: number
  is_absent_unexcused: number
  is_sick: number
  marked_attendances_count: number
  missed_lessons_count: number
  /** @format double */
  missed_lessons_percentage: number
}

/**
 * * `4` - S
 * * `3` - E
 * * `2` - A
 * * `1` - C
 * * `0` - not assigned
 */
export type CurrentCategoryEnum = 4 | 3 | 2 | 1 | 0

export type CurrentEditor = {
  id: string
  full_name: string
}

export type CurrentLesson = {
  /** @format uuid */
  id: string
  students: any[]
  course: string
}

export type CurrentLocation = {
  /** @format uuid */
  id: string
  current_location: string
  boarding_status: string
  campus_status: string
  campus_status_reason: string | null
  campus_location: string
}

export type CurrentUser = {
  /** @format uuid */
  id: string
  /**
   * Required. 150 characters or fewer. Letters, digits and @/./+/-/_ only.
   * @maxLength 150
   * @pattern ^[\w.@+-]+$
   */
  username: string
  /**
   * Email address
   * @format email
   * @maxLength 254
   */
  email: string
  /** @maxLength 128 */
  first_name?: string
  /** @maxLength 128 */
  last_name?: string
  full_name: string
  /** @maxLength 256 */
  name?: string
  permissions: string
  /**
   * Staff status
   * Designates whether the user can log into this admin site.
   */
  is_staff?: boolean
  /**
   * Superuser status
   * Designates that this user has all permissions without explicitly assigning them.
   */
  is_superuser?: boolean
  profiles: string[]
  staff_type?: StaffTypeEnum | BlankEnum
  is_laundry_user: boolean
  /** @format uri */
  avatar: string | null
  has_perms_to_own_objects_only: boolean
  can_report_bug: boolean
  can_generate_transcripts_of_grades: boolean
  can_edit_official_transcripts_of_grades: boolean
  departments: SimpleDepartment[]
}

/**
 * * `import_timetable` - Import timetable
 * * `archive_activities` - Archive activities
 * * `generate_lessons` - Generate lessons
 * * `access_student_document` - Access student document
 * * `publish_menu` - Publish menu
 * * `unpublish_menu` - Unublish menu
 * * `publish_exams` - Publish exams
 * * `unpublish_exams` - Unpublish exams
 * * `complete_old_health_checks` - Complete old health checks
 * * `create_health_checks_from_sick_report` - Create health checks from sick report
 * * `enable_google_only_login` - Enable Google-only login
 * * `disable_google_only_login` - Disable Google-only login
 * * `merge_user_profiles` - Merge user profiles
 * * `update_transcript_grades` - Update transcript grades
 */
export type CustomActionEnum =
  | 'import_timetable'
  | 'archive_activities'
  | 'generate_lessons'
  | 'access_student_document'
  | 'publish_menu'
  | 'unpublish_menu'
  | 'publish_exams'
  | 'unpublish_exams'
  | 'complete_old_health_checks'
  | 'create_health_checks_from_sick_report'
  | 'enable_google_only_login'
  | 'disable_google_only_login'
  | 'merge_user_profiles'
  | 'update_transcript_grades'

export type CustomGradeType = {
  value: string
  label: string
  is_tae: boolean
}

export type CustomSubtopicCreateRequest = {
  /** @format uuid */
  topic_id: string
  /**
   * @minLength 1
   * @maxLength 255
   */
  name: string
  /** @maxLength 20 */
  code?: string | null
  /**
   * Planned number of lessons for completing the subtopic
   * @min 0
   * @max 2147483647
   */
  duration: number
  /** Indicates if subtopic is revision. */
  is_revision?: boolean
}

export type DayHour = {
  /**
   * * `0` - 0
   * * `1` - 1
   * * `2` - 2
   * * `3` - 3
   * * `4` - 4
   * * `5` - 5
   * * `6` - 6
   * * `7` - 7
   * * `8` - 8
   * * `9` - 9
   * * `10` - 10
   * * `11` - 11
   * * `12` - 12
   * * `13` - 13
   * * `14` - 14
   * * `15` - 15
   */
  value: DayHourValueEnum
  /**
   * * `8:00-8:40` - 8:00-8:40
   * * `8:45-9:25` - 8:45-9:25
   * * `9:30-10:10` - 9:30-10:10
   * * `10:30-11:10` - 10:30-11:10
   * * `11:15-11:55` - 11:15-11:55
   * * `13:00-13:40` - 13:00-13:40
   * * `13:45-14:25` - 13:45-14:25
   * * `14:30-15:10` - 14:30-15:10
   * * `15:15-15:55` - 15:15-15:55
   * * `16:00-16:40` - 16:00-16:40
   * * `16:45-17:25` - 16:45-17:25
   * * `17:30-18:10` - 17:30-18:10
   * * `18:15-18:55` - 18:15-18:55
   * * `19:00-19:40` - 19:00-19:40
   * * `19:40-20:30` - 19:40-20:30
   * * `20:30-21:00` - 20:30-21:00
   */
  label: DayHourLabelEnum
}

/**
 * * `8:00-8:40` - 8:00-8:40
 * * `8:45-9:25` - 8:45-9:25
 * * `9:30-10:10` - 9:30-10:10
 * * `10:30-11:10` - 10:30-11:10
 * * `11:15-11:55` - 11:15-11:55
 * * `13:00-13:40` - 13:00-13:40
 * * `13:45-14:25` - 13:45-14:25
 * * `14:30-15:10` - 14:30-15:10
 * * `15:15-15:55` - 15:15-15:55
 * * `16:00-16:40` - 16:00-16:40
 * * `16:45-17:25` - 16:45-17:25
 * * `17:30-18:10` - 17:30-18:10
 * * `18:15-18:55` - 18:15-18:55
 * * `19:00-19:40` - 19:00-19:40
 * * `19:40-20:30` - 19:40-20:30
 * * `20:30-21:00` - 20:30-21:00
 */
export type DayHourLabelEnum =
  | '8:00-8:40'
  | '8:45-9:25'
  | '9:30-10:10'
  | '10:30-11:10'
  | '11:15-11:55'
  | '13:00-13:40'
  | '13:45-14:25'
  | '14:30-15:10'
  | '15:15-15:55'
  | '16:00-16:40'
  | '16:45-17:25'
  | '17:30-18:10'
  | '18:15-18:55'
  | '19:00-19:40'
  | '19:40-20:30'
  | '20:30-21:00'

/**
 * * `0` - 0
 * * `1` - 1
 * * `2` - 2
 * * `3` - 3
 * * `4` - 4
 * * `5` - 5
 * * `6` - 6
 * * `7` - 7
 * * `8` - 8
 * * `9` - 9
 * * `10` - 10
 * * `11` - 11
 * * `12` - 12
 * * `13` - 13
 * * `14` - 14
 * * `15` - 15
 */
export type DayHourValueEnum =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type DayMenu = {
  id: number
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @format date */
  date: string
  is_filled?: boolean
  is_skipped?: boolean
  is_published: boolean
  /** @maxLength 120 */
  lunch_starter?: string
  /** @maxLength 120 */
  lunch_soup?: string
  /** @maxLength 120 */
  lunch_main_course?: string
  /** @maxLength 120 */
  lunch_dessert?: string
  /** @maxLength 120 */
  lunch_starter_vege?: string
  /** @maxLength 120 */
  lunch_soup_vege?: string
  /** @maxLength 120 */
  lunch_main_course_vege?: string
  /** @maxLength 120 */
  lunch_dessert_vege?: string
  /** @maxLength 120 */
  dinner_starter?: string
  /** @maxLength 120 */
  dinner_soup?: string
  /** @maxLength 120 */
  dinner_main_course?: string
  /** @maxLength 120 */
  dinner_dessert?: string
  /** @maxLength 120 */
  dinner_starter_vege?: string
  /** @maxLength 120 */
  dinner_soup_vege?: string
  /** @maxLength 120 */
  dinner_main_course_vege?: string
  /** @maxLength 120 */
  dinner_dessert_vege?: string
  /** @maxLength 120 */
  breakfast_special?: string
  /** @maxLength 120 */
  breakfast_special_vege?: string
}

export type DayMenuIsPublished = {
  is_published: boolean
  can_publish: boolean
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type DayMenuRequest = {
  /** @format date */
  date: string
  is_filled?: boolean
  is_skipped?: boolean
  /** @maxLength 120 */
  lunch_starter?: string
  /** @maxLength 120 */
  lunch_soup?: string
  /** @maxLength 120 */
  lunch_main_course?: string
  /** @maxLength 120 */
  lunch_dessert?: string
  /** @maxLength 120 */
  lunch_starter_vege?: string
  /** @maxLength 120 */
  lunch_soup_vege?: string
  /** @maxLength 120 */
  lunch_main_course_vege?: string
  /** @maxLength 120 */
  lunch_dessert_vege?: string
  /** @maxLength 120 */
  dinner_starter?: string
  /** @maxLength 120 */
  dinner_soup?: string
  /** @maxLength 120 */
  dinner_main_course?: string
  /** @maxLength 120 */
  dinner_dessert?: string
  /** @maxLength 120 */
  dinner_starter_vege?: string
  /** @maxLength 120 */
  dinner_soup_vege?: string
  /** @maxLength 120 */
  dinner_main_course_vege?: string
  /** @maxLength 120 */
  dinner_dessert_vege?: string
  /** @maxLength 120 */
  breakfast_special?: string
  /** @maxLength 120 */
  breakfast_special_vege?: string
}

export type DeadlineChaserStudent = {
  /** @format email */
  email: string
  name: string
  artisan_email: string
}

export type DeadlineChaserToken = {
  access_token: string
  token_type: string
  expires_in: number
  base_url: string
}

export type DefaultCustomConstraints = {
  lesson_duration: number
  preparation_time_before: number
  preparation_time_after: number
  default_schedule: Period[]
}

export type DegreeAim = {
  id: number
  can_be_modified: boolean
  order: number
  /** @maxLength 256 */
  name: string
}

export type DegreeAimRequest = {
  /**
   * @minLength 1
   * @maxLength 256
   */
  name: string
}

export type DegreeAimStudents = {
  degree_aim_1: SimpleStudentWithGradeLevel[]
  degree_aim_2: SimpleStudentWithGradeLevel[]
}

export type Department = {
  id: number
  /** @maxLength 32 */
  name: string
  /** @maxLength 32 */
  label?: string
  is_talent_and_enrichment?: boolean
  /** Flag indicating if department should be excluded from cockpit apps */
  is_excluded_from_cockpit?: boolean
}

export type DepartmentCredits = {
  department: DepartmentEnum
  /** @format double */
  credits: number
}

/**
 * * `ENG` - ENG
 * * `MAT` - MAT
 * * `SCI` - SCI
 * * `MLA` - MLA
 * * `SSD` - SSD
 * * `ART` - ART
 * * `Electives` - Electives
 */
export type DepartmentEnum =
  | 'ENG'
  | 'MAT'
  | 'SCI'
  | 'MLA'
  | 'SSD'
  | 'ART'
  | 'Electives'

export type DepartmentRequest = {
  /**
   * @minLength 1
   * @maxLength 32
   */
  name: string
  /** @maxLength 32 */
  label?: string
  is_talent_and_enrichment?: boolean
  /** Flag indicating if department should be excluded from cockpit apps */
  is_excluded_from_cockpit?: boolean
}

export type DetailResponseSchema = {
  detail: string
}

/**
 * * `l1` - L1, Regular and vegetarian. Students can choose themselves.
 * * `l2` - L2, Specific, individual requirements. Kitchen adapts the existing menu to specific needs.
 * * `l3` - L3, Specific, very individual requirements. Kitchen creates new menu to specific needs, almost every meal has to be cooked individually.
 * * `l4` - L4, Highly specific. Severe allergic reaction to traces of certain food. Meals need to be ordered from outside supplier.
 */
export type DietTypeEnum = 'l1' | 'l2' | 'l3' | 'l4'

export type DinningAndSickMealAttendance = {
  dining: MealPreferenceCount
  sick: MealPreferenceCount
}

/**
 * * `abitur` - Abitur
 * * `aufnahmeprufung_uni_bern` - Aufnahmeprüfung Uni Bern
 * * `british_gce_a_levels` - British GCE A Levels
 * * `diploma` - Diploma
 * * `esame_di_stato` - Esame di Stato
 * * `esame_die_stato_linguistico` - Esame die Stato Linguistico
 * * `esame_die_stato_scientifico` - Esame die Stato Scientifico
 * * `graduation` - Graduation
 * * `handelsdiplom` - Handelsdiplom
 * * `high_school_certificate` - High School Certificate
 * * `high_school_diploma` - High School Diploma
 * * `high_school_diploma_ap` - High School Diploma & AP
 * * `international_bacalaureate_ib` - International Bacalaureate (IB)
 * * `matura` - Matura
 * * `zurcher_matura` - Zürcher Matura
 */
export type DiplomaTypeEnum =
  | 'abitur'
  | 'aufnahmeprufung_uni_bern'
  | 'british_gce_a_levels'
  | 'diploma'
  | 'esame_di_stato'
  | 'esame_die_stato_linguistico'
  | 'esame_die_stato_scientifico'
  | 'graduation'
  | 'handelsdiplom'
  | 'high_school_certificate'
  | 'high_school_diploma'
  | 'high_school_diploma_ap'
  | 'international_bacalaureate_ib'
  | 'matura'
  | 'zurcher_matura'

export type DisabledAutoimportTimetable = {
  timetable_id: number
  rota_timetable_id: number | null
}

export type DisabledAutoimportTimetableRequest = {
  timetable_id: number
  rota_timetable_id: number | null
}

/**
 * * `idp_app` - IDP app
 * * `parents_app` - Parents app
 */
export type DisplayInEnum = 'idp_app' | 'parents_app'

export type Doctor = {
  id: number
  /** @maxLength 256 */
  name: string
  /** @format date-time */
  created: string
  modified: string
  appointment_count: string
}

export type DoctorDiagnosis = {
  id: number
  diagnosis: string
  recommendations: string
  /**
   * * `new` - New
   * * `cancelled` - Cancelled
   * * `after_the_visit` - After the visit
   */
  status?: AppointmentStatusEnum
}

export type DoctorDiagnosisRequest = {
  /** @minLength 1 */
  diagnosis: string
  /** @minLength 1 */
  recommendations: string
  /**
   * * `new` - New
   * * `cancelled` - Cancelled
   * * `after_the_visit` - After the visit
   */
  status?: AppointmentStatusEnum
}

export type DoctorRequest = {
  /**
   * @minLength 1
   * @maxLength 256
   */
  name: string
}

/**
 * * `1` - Draft
 * * `5` - Academic
 * * `7` - Admission
 * * `10` - Administrative
 * * `13` - Boarding
 * * `15` - Medical
 * * `20` - Other
 * * `25` - Archived
 */
export type DocumentTypeEnum = 1 | 5 | 7 | 10 | 13 | 15 | 20 | 25

export type Domain = {
  /** @format uuid */
  id: string
  /** @maxLength 255 */
  name: string
  departments: SimpleDepartment[]
  has_subtopics: boolean
}

export type DomainCreateRequest = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name: string
  departments: number[]
  description?: string | null
}

export type DomainDetails = {
  /** @format uuid */
  id: string
  /** @maxLength 255 */
  name: string
  departments: SimpleDepartment[]
  description?: string | null
  topics: SimpleTopic[]
}

/**
 * * `capsule` - capsule
 * * `drop` - drop
 * * `puff` - puff
 * * `sachet` - sachet
 * * `table_spoon` - table spoon
 * * `tablet` - tablet
 * * `tea_spoon` - tea spoon
 * * `finger_tip` - finger tip unit (cream)
 * * `milliliters` - milliliters
 */
export type DosageFormEnum =
  | 'capsule'
  | 'drop'
  | 'puff'
  | 'sachet'
  | 'table_spoon'
  | 'tablet'
  | 'tea_spoon'
  | 'finger_tip'
  | 'milliliters'

export type DraftActivity = {
  /** @format uuid */
  id: string
  teacher_display: string
  co_teacher_1_display: string
  co_teacher_2_display: string
  room_display: string
  /**
   * @min 0
   * @max 2147483647
   */
  day: RotaDayEnum
  /**
   * @min 0
   * @max 15
   */
  hour?: HourEnum | NullEnum | null
  /** @format time */
  custom_start?: string | null
  /** @format time */
  custom_end?: string | null
  /** @format double */
  duration?: number
  is_archived?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  external_id?: number | null
  is_private: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  fet_id?: number | null
  is_moved: boolean
  /** attendances */
  co_teacher_1_perm_to_manage_lesson?: boolean
  /** attendances */
  co_teacher_2_perm_to_manage_lesson?: boolean
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
  /** @format uuid */
  course: string
  /**
   * Artisan
   * @format uuid
   */
  teacher: string
  room?: number | null
  timetable: number
  section: number
  from_optimization?: number | null
  /** @format uuid */
  separated_from?: string | null
  students: string[]
}

export type DraftActivityAvailableFilters = {
  students: AvailableStringFilter
  course: AvailableStringFilter
  room: AvailableStringFilter
  teacher: AvailableStringFilter
}

export type DraftActivityCallArgs = {
  /** @format uuid */
  id: string
  section_id: number
  course: SimpleCourse
  day: RotaDayEnum | NullEnum | null
  /**
   * * `0` - 8:00-8:40
   * * `1` - 8:45-9:25
   * * `2` - 9:30-10:10
   * * `3` - 10:30-11:10
   * * `4` - 11:15-11:55
   * * `5` - 13:00-13:40
   * * `6` - 13:45-14:25
   * * `7` - 14:30-15:10
   * * `8` - 15:15-15:55
   * * `9` - 16:00-16:40
   * * `10` - 16:45-17:25
   * * `11` - 17:30-18:10
   * * `12` - 18:15-18:55
   * * `13` - 19:00-19:40
   * * `14` - 19:40-20:30
   * * `15` - 20:30-21:00
   */
  hour: HourEnum
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1_id: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2_id: string | null
  /**
   * Artisan
   * @format uuid
   */
  teacher_id: string
  room_id: number | null
  student_ids: string[]
  timetable_id: number
}

export type DraftActivityCreate = {
  /** @format uuid */
  id: string
  teacher_display: string
  co_teacher_1_display: string
  co_teacher_2_display: string
  room_display: string
  /**
   * @min 0
   * @max 2147483647
   */
  day: RotaDayEnum
  /**
   * @min 0
   * @max 15
   */
  hour?: HourEnum | NullEnum | null
  /** @format time */
  custom_start?: string | null
  /** @format time */
  custom_end?: string | null
  /** @format double */
  duration?: number
  is_archived?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  external_id?: number | null
  is_private: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  fet_id?: number | null
  is_moved: boolean
  /** attendances */
  co_teacher_1_perm_to_manage_lesson?: boolean
  /** attendances */
  co_teacher_2_perm_to_manage_lesson?: boolean
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
  /** @format uuid */
  course: string
  /**
   * Artisan
   * @format uuid
   */
  teacher: string
  room?: number | null
  timetable: number
  section?: number | null
  from_optimization?: number | null
  /** @format uuid */
  separated_from?: string | null
  students: string[]
}

export type DraftActivityCreateRequest = {
  create_new_section?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  day: RotaDayEnum
  /**
   * @min 0
   * @max 15
   */
  hour?: HourEnum | NullEnum | null
  /** @format time */
  custom_start?: string | null
  /** @format time */
  custom_end?: string | null
  /** @format double */
  duration?: number
  is_archived?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  external_id?: number | null
  /**
   * @min 0
   * @max 2147483647
   */
  fet_id?: number | null
  /** attendances */
  co_teacher_1_perm_to_manage_lesson?: boolean
  /** attendances */
  co_teacher_2_perm_to_manage_lesson?: boolean
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
  /** @format uuid */
  course: string
  /**
   * Artisan
   * @format uuid
   */
  teacher: string
  room?: number | null
  timetable: number
  section?: number | null
  from_optimization?: number | null
  /** @format uuid */
  separated_from?: string | null
  students: string[]
}

export type DraftActivityEditor = {
  /** @format uuid */
  id: string
  full_name: string
  /**
   * * `active` - ACTIVE
   * * `inactive` - INACTIVE
   */
  status: DraftActivityEditorStatusEnum
  is_edit_session_locked: boolean
  /**
   * * `user` - USER
   * * `suggester_generation` - SUGGESTER_GENERATION
   * * `suggester_preview` - SUGGESTER_PREVIEW
   * * `tae_generator` - TAE_GENERATOR
   */
  edit_session_source: EditSessionSourceEnum
}

/**
 * * `active` - ACTIVE
 * * `inactive` - INACTIVE
 */
export type DraftActivityEditorStatusEnum = 'active' | 'inactive'

export type DraftActivityInProgressSuggesterTask = {
  id: number
  /**
   * * `pending` - Pending
   * * `running` - Running
   * * `succeeded` - Succeeded
   * * `failed` - Failed
   * * `cancelled` - Cancelled
   * * `completed` - Completed
   */
  status?: Status7B2Enum
  author: SimpleUser
  /**
   * @min 0
   * @max 2147483647
   */
  to_hour?: HourEnum | NullEnum | null
  /**
   * @min 0
   * @max 2147483647
   */
  to_day?: RotaDayEnum | NullEnum | null
  years_to_rearrange: number[]
  /** @format date-time */
  started_at?: string | null
  draft_activity: SimpleDraftActivity
}

export type DraftActivityList = {
  /** @format uuid */
  id: string
  date: string
  start: string
  end: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /** @format uuid */
  course: string
  course_str: string
  users: SimpleUser[]
  /**
   *  type
   * @default "lesson"
   */
  _type: string
  display_hour: string
  room?: number | null
  room_str: string
  teacher: SimpleUser
  co_teacher_1?: SimpleUser | null
  co_teacher_2?: SimpleUser | null
  current_editor: DraftActivityEditor | null
}

export type DraftActivityListRequest = {
  room?: number | null
}

export type DraftActivitySuggesterTaskCallArgs = {
  draft_activity: DraftActivityCallArgs
  to_day: RotaDayEnum | NullEnum | null
  /**
   * * `0` - 8:00-8:40
   * * `1` - 8:45-9:25
   * * `2` - 9:30-10:10
   * * `3` - 10:30-11:10
   * * `4` - 11:15-11:55
   * * `5` - 13:00-13:40
   * * `6` - 13:45-14:25
   * * `7` - 14:30-15:10
   * * `8` - 15:15-15:55
   * * `9` - 16:00-16:40
   * * `10` - 16:45-17:25
   * * `11` - 17:30-18:10
   * * `12` - 18:15-18:55
   * * `13` - 19:00-19:40
   * * `14` - 19:40-20:30
   * * `15` - 20:30-21:00
   */
  to_hour: HourEnum
  years_to_rearrange: number[]
  ignore_rooms_availability: boolean
}

export type DraftActivitySuggestion = {
  id: number
  task_id: number
  moves: DraftActivitySuggestionMove[]
}

export type DraftActivitySuggestionMove = {
  /** @format uuid */
  activity_id: string
  course: SimpleCourse
  artisans: SimpleUser[]
  students: SimpleStudentWithGradeLevel[]
  old_room: SimpleRoom | null
  new_room: SimpleRoom | null
  room: SimpleRoom | null
  /**
   * @min 0
   * @max 2147483647
   */
  from_day: RotaDayEnum
  from_time_slot: string
  /**
   * @min 0
   * @max 2147483647
   */
  to_day: RotaDayEnum
  to_time_slot: string
}

export type DraftActivitySuggestionPreview = {
  /** @format uuid */
  id: string
  date: string
  start: string
  end: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /** @format uuid */
  course: string
  course_str: string
  /**
   *  type
   * @default "lesson"
   */
  _type: string
  display_hour: string
  room?: number | null
  room_str: string
  teacher: SimpleUser
  /** @default false */
  is_suggested_move?: boolean
  /** @default false */
  is_duplicate?: boolean
}

export type DraftRotaActivity = {
  id: string
  /** @format uuid */
  user?: string | null
  users?: string[]
  /**
   * * `regular` - Duty
   * * `break` - Break
   * * `meeting` - Leaders Meeting
   * * `night_presence` - Night Presence
   * * `ski_trip` - Ski trip
   * * `sudden_time_off` - Sudden Time Off
   */
  rota_type?: RotaTypeEnum
  rota_timetable: number
  /**
   * @min 0
   * @max 2147483647
   */
  day: RotaDayEnum
  /** @format time */
  start_hour?: string
  /** @format time */
  end_hour?: string
  /** @format uuid */
  team?: string | null
  role?: BoardingArtisanRoleEnum | BlankEnum
  room?: number | null
  cover?: Cover | null
  /** @format date */
  cover_date?: string
  /** @format date */
  date?: string
}

export type DraftRotaActivityConflictCheck = {
  id: string
  /** @format uuid */
  user?: string | null
  users?: string[]
  /**
   * * `regular` - Duty
   * * `break` - Break
   * * `meeting` - Leaders Meeting
   * * `night_presence` - Night Presence
   * * `ski_trip` - Ski trip
   * * `sudden_time_off` - Sudden Time Off
   */
  rota_type?: RotaTypeEnum
  rota_timetable: number
  /**
   * @min 0
   * @max 2147483647
   */
  day: RotaDayEnum
  /** @format time */
  start_hour?: string
  /** @format time */
  end_hour?: string
  /** @format uuid */
  team?: string | null
  role?: BoardingArtisanRoleEnum | BlankEnum
  room?: number | null
  /** @format date */
  date?: string
}

export type DraftRotaActivityConflictCheckRequest = {
  /** @format uuid */
  user?: string | null
  users?: string[]
  /**
   * * `regular` - Duty
   * * `break` - Break
   * * `meeting` - Leaders Meeting
   * * `night_presence` - Night Presence
   * * `ski_trip` - Ski trip
   * * `sudden_time_off` - Sudden Time Off
   */
  rota_type?: RotaTypeEnum
  rota_timetable: number
  /**
   * @min 0
   * @max 2147483647
   */
  day: RotaDayEnum
  /** @format time */
  start_hour?: string
  /** @format time */
  end_hour?: string
  /** @format uuid */
  team?: string | null
  role?: BoardingArtisanRoleEnum | BlankEnum
  room?: number | null
  /** @format date */
  date?: string
}

export type DraftRotaActivityConstraintCheck = {
  id: string
  /**
   * @min 0
   * @max 2147483647
   */
  day: RotaDayEnum
  /** @format time */
  start_hour?: string
  /** @format time */
  end_hour?: string
  /**
   * * `regular` - Duty
   * * `break` - Break
   * * `meeting` - Leaders Meeting
   * * `night_presence` - Night Presence
   * * `ski_trip` - Ski trip
   * * `sudden_time_off` - Sudden Time Off
   */
  rota_type?: RotaTypeEnum
  rota_timetable: number
  /** @format uuid */
  user?: string | null
  users?: string[]
  /** @format uuid */
  team?: string | null
  cover?: Cover | null
  /** @format date */
  cover_date?: string
  /** @format date */
  date?: string
}

export type DraftRotaActivityConstraintCheckRequest = {
  /**
   * @min 0
   * @max 2147483647
   */
  day: RotaDayEnum
  /** @format time */
  start_hour?: string
  /** @format time */
  end_hour?: string
  /**
   * * `regular` - Duty
   * * `break` - Break
   * * `meeting` - Leaders Meeting
   * * `night_presence` - Night Presence
   * * `ski_trip` - Ski trip
   * * `sudden_time_off` - Sudden Time Off
   */
  rota_type?: RotaTypeEnum
  rota_timetable: number
  /** @format uuid */
  user?: string | null
  users?: string[]
  /** @format uuid */
  team?: string | null
  cover?: CoverRequest | null
  /** @format date */
  cover_date?: string
  /** @format date */
  date?: string
}

export type DraftRotaActivityRequest = {
  /** @format uuid */
  user?: string | null
  users?: string[]
  /**
   * * `regular` - Duty
   * * `break` - Break
   * * `meeting` - Leaders Meeting
   * * `night_presence` - Night Presence
   * * `ski_trip` - Ski trip
   * * `sudden_time_off` - Sudden Time Off
   */
  rota_type?: RotaTypeEnum
  rota_timetable: number
  /**
   * @min 0
   * @max 2147483647
   */
  day: RotaDayEnum
  /** @format time */
  start_hour?: string
  /** @format time */
  end_hour?: string
  /** @format uuid */
  team?: string | null
  role?: BoardingArtisanRoleEnum | BlankEnum
  room?: number | null
  cover?: CoverRequest | null
  /** @format date */
  cover_date?: string
  /** @format date */
  date?: string
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type DraftRotaActivityResponse = {
  id: string
  /** @format uuid */
  user: string | null
  user_str: string
  quarter: number
  rota_type: RotaTypeEnum
  rota_timetable: number
  /**
   *  type
   * @default "rota"
   */
  _type: string
  /** @format date */
  date: string
  start: string
  end: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  role: string
  role_str: string
  /** @default "" */
  house_name: string
  /** @format uuid */
  team: string | null
  team_str: string
  users: string[]
  users_str: string
  room: number | null
  covered_by: SimpleUser[]
  covering: SimpleUser[]
  /** @format uuid */
  display_for_user: string
}

export type DraftSectionsCourseValidationGroups = {
  ccu: DraftSectionsCourseValidationItem[]
  ath: DraftSectionsCourseValidationItem[]
}

export type DraftSectionsCourseValidationItem = {
  enrolment_id: number
  course_enrolment_id: number
  course: DraftSectionsValidationCourse
}

export type DraftSectionsGenerationAvailability = {
  can_generate: boolean
  has_draft_sections: boolean
}

export type DraftSectionsValidationCourse = {
  /** @format uuid */
  id: string
  code: string
  name: string
}

export type DraftSectionsValidationError = {
  missing_number_of_lessons: DraftSectionsCourseValidationGroups
  invalid_capacity_per_section: DraftSectionsCourseValidationGroups
}

export type DrinkGive = {
  id: number
  was_given: boolean
  /** @format uuid */
  health_check: string
  /** @format date-time */
  created: string
}

export type DrinkGiveRequest = {
  was_given: boolean
  /** @format uuid */
  health_check: string
}

export type DuplicateRecord = {
  id: number
  /** @format date-time */
  created: string
  user_1: DuplicateRecordDetailUser
  user_2: DuplicateRecordDetailUser
  status: StatusEd6Enum
  /**
   * Type of users involved in this duplicate pair
   *
   * * `10` - Student - Student
   * * `20` - Parent - Parent
   * * `30` - Student - Candidate
   * * `40` - Candidate - Candidate
   */
  duplicate_type: DuplicateTypeEnum
}

export type DuplicateRecordDetailUser =
  | DuplicateRecordStudentUser
  | DuplicateRecordParentUser

export type DuplicateRecordList = {
  id: number
  /** @format date-time */
  created: string
  user_1: DuplicateRecordListUser
  user_2: DuplicateRecordListUser
  status: StatusEd6Enum
  /**
   * Type of users involved in this duplicate pair
   *
   * * `10` - Student - Student
   * * `20` - Parent - Parent
   * * `30` - Student - Candidate
   * * `40` - Candidate - Candidate
   */
  duplicate_type: DuplicateTypeEnum
}

export type DuplicateRecordListUser =
  | DuplicateRecordStudentUserList
  | DuplicateRecordParentUserList

export type DuplicateRecordParentUser = {
  /** @format uuid */
  id: string
  user_type: UserTypeEnum
  first_name: string
  last_name: string
  title: string
  /**
   * Email address
   * @format email
   */
  email: string
  phone_number: string
  /** @format date-time */
  date_joined: string
  /**
   * Active
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active: boolean
  nationalities: string[]
  native_languages: string[]
  gender: ForGenderEnum
  user_addresses: UserAddress[]
  user_additional_phone_numbers: UserAdditionalPhoneNumber[]
  user_additional_emails: UserAdditionalEmail[]
  maiden_name: string | null
  salutation: string | null
  occupation: string | null
}

export type DuplicateRecordParentUserList = {
  /** @format uuid */
  id: string
  user_type: UserTypeEnum
  /** @maxLength 128 */
  first_name?: string
  /** @maxLength 128 */
  last_name?: string
}

export type DuplicateRecordStudentUser = {
  /** @format uuid */
  id: string
  user_type: UserTypeEnum
  first_name: string
  last_name: string
  title: string
  /**
   * Email address
   * @format email
   */
  email: string
  phone_number: string
  /** @format date-time */
  date_joined: string
  /**
   * Active
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active: boolean
  nationalities: string[]
  native_languages: string[]
  gender: ForGenderEnum
  user_addresses: UserAddress[]
  user_additional_phone_numbers: UserAdditionalPhoneNumber[]
  user_additional_emails: UserAdditionalEmail[]
  /** @format date */
  date_of_birth: string | null
  birthplace: string | null
  academic_status: AcademicStatusEnum | NullEnum | null
  candidate_status: CandidateProfileStatusEnum | NullEnum | null
}

export type DuplicateRecordStudentUserList = {
  /** @format uuid */
  id: string
  user_type: UserTypeEnum
  /** @maxLength 128 */
  first_name?: string
  /** @maxLength 128 */
  last_name?: string
}

/**
 * * `10` - Student - Student
 * * `20` - Parent - Parent
 * * `30` - Student - Candidate
 * * `40` - Candidate - Candidate
 */
export type DuplicateTypeEnum = 10 | 20 | 30 | 40

export type EditSessionConflictResponse = {
  message: string
  current_editor: CurrentEditor | null
}

export type EditSessionCreatedResponse = {
  message: string
  /** @format uuid */
  edit_session_id: string
}

/**
 * * `user` - USER
 * * `suggester_generation` - SUGGESTER_GENERATION
 * * `suggester_preview` - SUGGESTER_PREVIEW
 * * `tae_generator` - TAE_GENERATOR
 */
export type EditSessionSourceEnum =
  | 'user'
  | 'suggester_generation'
  | 'suggester_preview'
  | 'tae_generator'

/**
 * * `topical` - Topical (Skin)
 * * `oral` - Oral
 * * `nasal` - Nasal
 * * `injection` - Injection
 */
export type EmergencyMedicineApplicationsEnum =
  | 'topical'
  | 'oral'
  | 'nasal'
  | 'injection'

/**
 * * `student` - Student (at all times)
 * * `house` - House
 * * `BDT` - BDT
 */
export type EmergencyMedicineLocationEnum = 'student' | 'house' | 'BDT'

export type EnrollOnCoursesConflict = {
  enrolments_capacity_reached: EnrollmentCapacityReached[]
}

export type EnrollOnCoursesRequest = {
  course_enrolments: number[]
  increase?: IncreaseEnum | NullEnum | null
}

export type EnrollStudentsRequest = {
  students: string[]
  increase?: IncreaseEnum | NullEnum | null
}

export type EnrolledByEnum = 'Student' | 'Admin'

export type EnrolledCourse = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  name: string
  difficulty: string
}

export type EnrolledStudent = {
  /** @format uuid */
  id: string
  full_name: string
}

export type Enrollment = {
  id: number
  student: string
  student_str: string
  student_grade_level_in_stage: string | null
  student_aim: string | null
  /** @default true */
  is_active?: boolean
  number_of_lessons: number
  /** @default false */
  is_private?: boolean
  quarter: string
}

export type EnrollmentCapacityReached = {
  id: number
  /** @format uuid */
  course_id: string
  course_name: string
  total_capacity: string
  /**
   * @min 0
   * @max 32767
   */
  capacity_per_section: number
  /**
   * @min 0
   * @max 32767
   */
  max_sections: number
}

export type EnrollmentRequest = {
  id: number
  /** @default true */
  is_active?: boolean
  number_of_lessons: number
  /** @default false */
  is_private?: boolean
}

/**
 * * `pending` - Pending
 * * `requested` - Requested
 * * `rejected` - Rejected
 * * `approved` - Approved
 * * `leave_absent` - Leave - Absent
 */
export type EnrolmentStatusEnum =
  | 'pending'
  | 'requested'
  | 'rejected'
  | 'approved'
  | 'leave_absent'

/**
 * * `week` - Weekly
 * * `quarter` - Quarterly
 * * `semester` - Semesterly
 * * `year` - Yearly
 */
export type EscalationExpireAfterEnum = 'week' | 'quarter' | 'semester' | 'year'

export type Exam = {
  id: string
  official_name?: string
  /** @format uuid */
  course: string
  course_str: string
  /** @format date */
  date?: string | null
  /**
   * @min 0
   * @max 2147483647
   */
  duration?: number | null
  /** @format time */
  start_time?: string | null
  /** @format time */
  end_time?: string | null
  location?: number | null
  location_str: string
  is_published: boolean
  is_make_up_test: boolean
  test_invigilators?: string[]
  test_invigilators_str: string
  saa_invigilators?: string[]
  saa_invigilators_str: string
  all_invigilators: string[]
  all_invigilators_str: string
  users: string
  teachers?: string[]
  teachers_str: string
  grade_levels?: number[]
  students: ExamStudent[]
  has_any_data: string
  has_all_required_data: string
  is_read_only: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  exam_session_id: string
  exam_session_name: string
  has_saa_students: boolean
  status: Status128Enum
}

export type ExamAvailableFilters = {
  course: AvailableStringFilter
  students: AvailableStringFilter
  test_invigilators: AvailableStringFilter
  saa_invigilators: AvailableStringFilter
  all_invigilators: AvailableStringFilter
  teachers: AvailableStringFilter
  grade_levels: AvailableIntegerFilter
  departments: AvailableIntegerFilter
  location: AvailableIntegerFilter
  status: AvailableStringFilter
  has_clashes: AvailableBooleanFilter
  is_published: AvailableBooleanFilter
}

export type ExamClash = {
  id: number
  group: ExamClashGroupEnum
  exam_id: string
  /** @format uuid */
  user_id: string | null
  /** @format uuid */
  student_id: string | null
  location_id: number | null
  user_details: SimpleUser
  student_details: SimpleUser
  location_details: LocationEC
  clash_with_exam_details: ClashWithExamEC
}

/**
 * * `student` - Student
 * * `location` - Location
 * * `teacher` - Teacher
 * * `saa_invigilator` - Saa Invigilator
 * * `test_invigilator` - Test Invigilator
 */
export type ExamClashGroupEnum =
  | 'student'
  | 'location'
  | 'teacher'
  | 'saa_invigilator'
  | 'test_invigilator'

export type ExamClashPreviewRequest = {
  for_exam_id?: string | null
  /** @format date */
  date: string
  /** @format time */
  start_time: string
  /** @format time */
  end_time: string
  location_id: number
  test_invigilators_ids?: string[]
  saa_invigilators_ids?: string[]
  students_ids?: string[]
  exam_ids_to_ignore?: string[]
}

export type ExamClashes = {
  id: string
  course: string
  /** @format date */
  date?: string | null
  /** @format time */
  start_hour: string
  /** @format time */
  end_hour: string
  /** @default "exams" */
  clash_type: string
}

export type ExamEnrolledStudent = {
  id: number
  is_marked_automatically: boolean
  is_marked: boolean
  /** @format date-time */
  marked_at: string | null
  is_present?: boolean
  /** is exempt */
  is_absent_excused?: boolean
  is_absent_unexcused?: boolean
  is_sick?: boolean
  is_academic_honesty?: boolean
  is_disturbance?: boolean
  disturbance_merit_note?: string
  academic_honesty_merit_note?: string
  student: ExamStudent
  grade_level: number
  exam: string
  note_content: string
}

export type ExamEnrolledStudentRequest = {
  is_present?: boolean
  /** is exempt */
  is_absent_excused?: boolean
  is_absent_unexcused?: boolean
  is_sick?: boolean
  is_academic_honesty?: boolean
  is_disturbance?: boolean
  disturbance_merit_note?: string
  academic_honesty_merit_note?: string
}

/**
 * * `25` - 25% extra time
 * * `50` - 50% extra time
 * * `100` - 100% extra time
 */
export type ExamExtraTimeEnum = 25 | 50 | 100

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type ExamForm = {
  id: string
  official_name?: string
  /** @format date */
  date?: string | null
  /**
   * @min 0
   * @max 2147483647
   */
  duration?: number | null
  /** @format time */
  start_time?: string | null
  /** @format time */
  end_time?: string | null
  location?: number | null
  test_invigilators?: string[]
  saa_invigilators?: string[]
  teachers?: string[]
  is_published: boolean
  is_make_up_test?: boolean
  keep_published?: boolean
}

export type ExamFormCourses = {
  id: string | null
  /** @format uuid */
  course: string
}

export type ExamFormCoursesRequest = {
  /** @minLength 1 */
  id: string | null
  /** @format uuid */
  course: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type ExamFormRequest = {
  official_name?: string
  /** @format date */
  date?: string | null
  /**
   * @min 0
   * @max 2147483647
   */
  duration?: number | null
  /** @format time */
  start_time?: string | null
  /** @format time */
  end_time?: string | null
  location?: number | null
  test_invigilators?: string[]
  saa_invigilators?: string[]
  teachers?: string[]
  is_make_up_test?: boolean
  exam_courses: ExamFormCoursesRequest[]
  exams_students: ExamsStudentsRequest[]
  keep_published?: boolean
}

export type ExamFormStudent = {
  /** @format uuid */
  student?: string
  grade_level?: number
  id?: number | null
  access_arrangements?: string | null
  /** @format uuid */
  course?: string | null
  exam_extra_time?: number | null
  is_removed?: boolean | null
  is_statemented?: boolean | null
  saa_student?: boolean | null
  student_str?: string | null
  note?: ExamFormStudentNote | null
}

export type ExamFormStudentNote = {
  id: string
  content: string
}

export type ExamFormStudentNoteRequest = {
  content: string
}

export type ExamFormStudentRequest = {
  /** @format uuid */
  student?: string
  grade_level?: number
  id?: number | null
  access_arrangements?: string | null
  /** @format uuid */
  course?: string | null
  exam_extra_time?: number | null
  is_removed?: boolean | null
  is_statemented?: boolean | null
  saa_student?: boolean | null
  student_str?: string | null
  note?: ExamFormStudentNoteRequest | null
}

export type ExamGenerationApplyRequest = {
  /**
   * @minLength 1
   * @maxLength 64
   */
  generation_id: string
}

export type ExamGenerationDay = {
  /** @format date */
  date: string
  slots: ExamGenerationTimeSlot[]
}

export type ExamGenerationDayRequest = {
  /** @format date */
  date: string
  slots: ExamGenerationTimeSlotRequest[]
}

export type ExamGenerationProcessState = {
  id: string
  exam_session_id: string
  /** @format date-time */
  created_at: string
  /**
   * Time when Panel commissioned the generation.
   * @format date-time
   */
  started_at: string
  /**
   * * `pending` - Pending
   * * `running` - Running
   * * `failed` - Failed
   * * `succeeded` - Succeeded
   */
  status: ExamGenerationProcessStatusEnum
  failure_reason: string | null
  time_left_seconds: number
  progress: ExamGenerationProgress | null
  assign_rooms: boolean
  assign_invigilators: boolean
  edit_scheduled_unpublished_exams: boolean
  /**
   * Deprecated: use edit_scheduled_unpublished_exams.
   * @deprecated
   */
  edit_unscheduled_exams: boolean
  override_time_slots: ExamGenerationDay[] | null
  unassigned_exams: ExamGenerationUnassignedExam[] | null
  exams_count: number
  succeeded_exams_count: number | null
  changed_exams_count: number | null
  saa_exams_count: number
  succeeded_saa_exams_count: number | null
  lessons_to_cancel_count: number | null
}

/**
 * * `pending` - Pending
 * * `running` - Running
 * * `failed` - Failed
 * * `succeeded` - Succeeded
 */
export type ExamGenerationProcessStatusEnum =
  | 'pending'
  | 'running'
  | 'failed'
  | 'succeeded'

export type ExamGenerationProgress = {
  exams_to_plan: number
  exams_assigned: number
}

export type ExamGenerationStarted = {
  /** @maxLength 64 */
  id: string
}

export type ExamGenerationTimeSlot = {
  /** @format time */
  time_from: string
  /** @format time */
  time_to: string
}

export type ExamGenerationTimeSlotRequest = {
  /** @format time */
  time_from: string
  /** @format time */
  time_to: string
}

export type ExamGenerationUnassignedExam = {
  id: string
  name: string
}

export type ExamPlannerTimetableExam = {
  id: string
  start: string
  end: string
  start_date: string
  end_date: string
  /**
   *  type
   * @default "exam"
   */
  _type: Type4CaEnum
  /** @format date */
  date: string | null
  /** @format uuid */
  course: string
  course_str: string
  official_name: string
  users: string
  location_str: string
  test_invigilators: SimpleUserWithAvatar[]
  saa_invigilators: SimpleUserWithAvatar[]
  teachers: SimpleUserWithAvatar[]
  exam_session_id: string
  is_allowed: string
  /** via annotate */
  school_year_id: number
  has_clashes: boolean
  is_published: boolean
}

export type ExamScore = {
  id: number
  idp_plan?: string | null
  /**
   * * `SAT_MAT` - SAT MAT
   * * `SAT_ENG` - SAT ENG
   * * `IELTS` - IELTS
   * * `WIDA` - WIDA
   * * `MAP` - MAP
   */
  name: NameEnum
  /**
   * @min 0
   * @max 100
   */
  score: number
}

export type ExamScoreRequest = {
  /** @minLength 1 */
  idp_plan?: string | null
  /**
   * * `SAT_MAT` - SAT MAT
   * * `SAT_ENG` - SAT ENG
   * * `IELTS` - IELTS
   * * `WIDA` - WIDA
   * * `MAP` - MAP
   */
  name: NameEnum
  /**
   * @min 0
   * @max 100
   */
  score: number
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type ExamSession = {
  id: string
  name: string
  course_types?: string[]
  /**
   * * `quarter` - Quarter exams
   * * `academic` - Academic
   * * `external` - External exams
   */
  exam_type: ExamTypeEnum
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  quarter: number
  departments: number[]
  grade_levels: number[]
  cancellation_dates: ExamSessionCancellationDate[]
  /** @default 0 */
  exams_count: string
  /** @default 0 */
  published_exams_count: string
  has_exam_ready_to_publish: string
  exams_ready_to_publish_count: string
  next_session: ExamSessionBasicData | null
  previous_session: ExamSessionBasicData | null
}

export type ExamSessionBasicData = {
  id: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
}

export type ExamSessionCancellationDate = {
  id: number
  /**
   * * `no_cancellation` - Don't cancel classes
   * * `msl_hsl_enrolled` - Cancel all the MSL and HSL classes for the students enrolled in this exam session.
   * * `msl_hsl_enrolled_and_not_enrolled` - Cancel all the MSL and HSL classes for all the students from selected years (enrolled and not enrolled in this exam session).
   * * `enrolled` - Cancel all the classes for the students enrolled in this exam session.
   * * `enrolled_and_not_enrolled` - Cancel all the classes for all the students from selected years (enrolled and not enrolled in this exam session).
   */
  cancel_class_type?: CancelClassTypeEnum
  /** @format date */
  date: string
  /** @format time */
  time_from?: string | null
  /** @format time */
  time_to?: string | null
  cancel_for_years: number[]
}

export type ExamSessionCancellationDateRequest = {
  /**
   * * `no_cancellation` - Don't cancel classes
   * * `msl_hsl_enrolled` - Cancel all the MSL and HSL classes for the students enrolled in this exam session.
   * * `msl_hsl_enrolled_and_not_enrolled` - Cancel all the MSL and HSL classes for all the students from selected years (enrolled and not enrolled in this exam session).
   * * `enrolled` - Cancel all the classes for the students enrolled in this exam session.
   * * `enrolled_and_not_enrolled` - Cancel all the classes for all the students from selected years (enrolled and not enrolled in this exam session).
   */
  cancel_class_type?: CancelClassTypeEnum
  /** @format date */
  date: string
  /** @format time */
  time_from?: string | null
  /** @format time */
  time_to?: string | null
  cancel_for_years: number[]
}

export type ExamSessionChangesPreviewResponse = {
  remove: ExamSessionRemoveChangesPreview
  clear: ExamSessionClearChangesPreview
}

export type ExamSessionClearChangesPreview = {
  date: BaseExamSessionChanges[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type ExamSessionCreate = {
  id: string
  name: string
  course_types: string[]
  /**
   * * `quarter` - Quarter exams
   * * `academic` - Academic
   * * `external` - External exams
   */
  exam_type: ExamTypeEnum
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  quarter: number
  departments: number[]
  grade_levels: number[]
  cancellation_dates: ExamSessionCancellationDate[]
  /** @default 0 */
  exams_count: string
  /** @default 0 */
  published_exams_count: string
  has_exam_ready_to_publish: string
  exams_ready_to_publish_count: string
  next_session: ExamSessionBasicData | null
  previous_session: ExamSessionBasicData | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type ExamSessionCreateRequest = {
  /** @minLength 1 */
  name: string
  course_types: string[]
  /**
   * * `quarter` - Quarter exams
   * * `academic` - Academic
   * * `external` - External exams
   */
  exam_type: ExamTypeEnum
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  quarter: number
  departments: number[]
  grade_levels: number[]
  cancellation_dates: ExamSessionCancellationDateRequest[]
}

export type ExamSessionDepartmentChanges = {
  id: string
  /** @format date */
  date: string
  course_name: string
  /** @format time */
  start_time: string
  /** @format time */
  end_time: string
  first_removed_department_name: string
}

export type ExamSessionGenerateRequest = {
  assign_rooms: boolean
  assign_invigilators: boolean
  /** Allow re-planning unpublished exams with a date and start time. Required unless edit_unscheduled_exams is provided. */
  edit_scheduled_unpublished_exams?: boolean
  /**
   * Deprecated: use edit_scheduled_unpublished_exams, which takes precedence when both fields are provided.
   * @deprecated
   */
  edit_unscheduled_exams?: boolean
  override_time_slots: ExamGenerationDayRequest[] | null
}

export type ExamSessionGradeLevelChanges = {
  id: string
  /** @format date */
  date: string
  course_name: string
  /** @format time */
  start_time: string
  /** @format time */
  end_time: string
  removed_grade_level_ids: number[]
}

export type ExamSessionList = {
  id: string
  name: string
  course_types?: string[]
  /**
   * * `quarter` - Quarter exams
   * * `academic` - Academic
   * * `external` - External exams
   */
  exam_type: ExamTypeEnum
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  quarter: number
  /** @default 0 */
  exams_count: string
  /** @default 0 */
  published_exams_count: string
  has_read_only_exam: string
  has_exam_ready_to_publish: string
  exams_ready_to_publish_count: string
}

export type ExamSessionQuarterChanges = {
  id: string
  /** @format date */
  date: string
  course_name: string
  /** @format time */
  start_time: string
  /** @format time */
  end_time: string
  quarter_id: number
}

export type ExamSessionRemoveChangesPreview = {
  quarter: ExamSessionQuarterChanges[]
  department: ExamSessionDepartmentChanges[]
  grade_level: ExamSessionGradeLevelChanges[]
  course_type: BaseExamSessionChanges[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type ExamSessionRequest = {
  /** @minLength 1 */
  name: string
  course_types?: string[]
  /**
   * * `quarter` - Quarter exams
   * * `academic` - Academic
   * * `external` - External exams
   */
  exam_type: ExamTypeEnum
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  quarter: number
  departments: number[]
  grade_levels: number[]
  cancellation_dates: ExamSessionCancellationDateRequest[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type ExamSessionRetrieve = {
  id: string
  name: string
  course_types?: string[]
  /**
   * * `quarter` - Quarter exams
   * * `academic` - Academic
   * * `external` - External exams
   */
  exam_type: ExamTypeEnum
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  quarter: number
  departments: number[]
  grade_levels: number[]
  cancellation_dates: ExamSessionCancellationDate[]
  /** @default 0 */
  exams_count: string
  /** @default 0 */
  published_exams_count: string
  has_exam_ready_to_publish: string
  exams_ready_to_publish_count: string
  next_session: ExamSessionBasicData | null
  previous_session: ExamSessionBasicData | null
  /** @default 0 */
  cancelled_lessons_count: number
  scheduled_unpublished_exams_count: number
}

export type ExamSessionSimpleList = {
  id: string
  name: string
}

export type ExamSessionSummary = {
  name: string
  course_types?: string[]
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  departments: number[]
  grade_levels: number[]
  exams_count: number
  saa_exams_count: number
  cancelled_lessons_count: number
}

export type ExamStudent = {
  /** @format uuid */
  id: string
  full_name: string
  is_at_home?: boolean
  saa_student?: boolean
  is_statemented?: boolean
  access_arrangements_labels: string[]
}

export type ExamStudentRequest = {
  is_at_home?: boolean
  saa_student?: boolean
  is_statemented?: boolean
}

export type ExamTimetableAvailableFilters = {
  course: AvailableStringFilter
  students: AvailableStringFilter
  teachers: AvailableStringFilter
  all_invigilators: AvailableStringFilter
  grade_levels: AvailableIntegerFilter
  location: AvailableIntegerFilter
  status: AvailableStringFilter
  has_clashes: AvailableBooleanFilter
  is_published: AvailableBooleanFilter
}

/**
 * * `quarter` - Quarter exams
 * * `academic` - Academic
 * * `external` - External exams
 */
export type ExamTypeEnum = 'quarter' | 'academic' | 'external'

export type ExamsStudents = {
  /** @format uuid */
  course?: string | null
  exam?: string | null
  students: ExamFormStudent[]
}

export type ExamsStudentsRequest = {
  /** @format uuid */
  course?: string | null
  /** @minLength 1 */
  exam?: string | null
  students: ExamFormStudentRequest[]
}

/**
 * * `private` - private
 * * `divided` - divided
 * * `insufficient_sections` - insufficient_sections
 * * `related_student_groups_mismatch` - related_student_groups_mismatch
 */
export type ExclusionReasonCodesEnum =
  | 'private'
  | 'divided'
  | 'insufficient_sections'
  | 'related_student_groups_mismatch'

/**
 * * `scheduled` - scheduled
 * * `open` - open
 */
export type ExistingEnrolmentStatusEnum = 'scheduled' | 'open'

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type ExternalExamForm = {
  id: string
  official_name?: string
  /** @format date */
  date?: string | null
  /**
   * @min 0
   * @max 2147483647
   */
  duration?: number | null
  /** @format time */
  start_time?: string | null
  /** @format time */
  end_time?: string | null
  location?: number | null
  test_invigilators?: string[]
  saa_invigilators?: string[]
  teachers?: string[]
  is_published: boolean
  is_make_up_test?: boolean
  keep_published?: boolean
  exam_session_id?: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type ExternalExamFormRequest = {
  official_name?: string
  /** @format date */
  date?: string | null
  /**
   * @min 0
   * @max 2147483647
   */
  duration?: number | null
  /** @format time */
  start_time?: string | null
  /** @format time */
  end_time?: string | null
  location?: number | null
  test_invigilators?: string[]
  saa_invigilators?: string[]
  teachers?: string[]
  is_make_up_test?: boolean
  exam_courses: ExamFormCoursesRequest[]
  exams_students?: ExamsStudentsRequest[]
  keep_published?: boolean
  /** @minLength 1 */
  exam_session_id?: string
  exam_type: ExamTypeEnum
  quarter: number
}

export type ExternalExamType = {
  label: string
  /**
   * * `ACT` - ACT
   * * `A_LEVEL` - A Level (GCE), iGCSE
   * * `AP` - Advanced Placement
   * * `CAMBRIDGE` - CAMBRIDGE
   * * `DELE` - DELE
   * * `DELF` - DELF
   * * `GCSE` - GCSE
   * * `GMAT` - GMAT
   * * `GOETHE` - GOETHE
   * * `IAL` - International A Level
   * * `IB` - International Baccalaureate
   * * `IELTS` - IELTS
   * * `LAMDA` - LAMDA
   * * `MAP` - MAP
   * * `SAT_ENG` - SAT ENG
   * * `SAT_MAT` - SAT MAT
   * * `WIDA` - WIDA
   */
  value: ExternalExamTypeEnum
  subtypes: string[]
}

/**
 * * `ACT` - ACT
 * * `A_LEVEL` - A Level (GCE), iGCSE
 * * `AP` - Advanced Placement
 * * `CAMBRIDGE` - CAMBRIDGE
 * * `DELE` - DELE
 * * `DELF` - DELF
 * * `GCSE` - GCSE
 * * `GMAT` - GMAT
 * * `GOETHE` - GOETHE
 * * `IAL` - International A Level
 * * `IB` - International Baccalaureate
 * * `IELTS` - IELTS
 * * `LAMDA` - LAMDA
 * * `MAP` - MAP
 * * `SAT_ENG` - SAT ENG
 * * `SAT_MAT` - SAT MAT
 * * `WIDA` - WIDA
 */
export type ExternalExamTypeEnum =
  | 'ACT'
  | 'A_LEVEL'
  | 'AP'
  | 'CAMBRIDGE'
  | 'DELE'
  | 'DELF'
  | 'GCSE'
  | 'GMAT'
  | 'GOETHE'
  | 'IAL'
  | 'IB'
  | 'IELTS'
  | 'LAMDA'
  | 'MAP'
  | 'SAT_ENG'
  | 'SAT_MAT'
  | 'WIDA'

export type ExternalJournal = {
  article_id: string
  article_slug: string
}

export type ExternalJournalRequest = {
  /** @minLength 1 */
  article_id: string
  /** @minLength 1 */
  article_slug: string
}

export type FavouriteViewSetting = {
  id: number
  user?: string
  /**
   * @min 0
   * @max 32767
   */
  module: ModuleEnum
  /** @maxLength 128 */
  name: string
  data: any
}

export type FavouriteViewSettingRequest = {
  user?: string
  /**
   * @min 0
   * @max 32767
   */
  module: ModuleEnum
  /**
   * @minLength 1
   * @maxLength 128
   */
  name: string
  data: any
  share_with?: string[]
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type FavouriteWidget = {
  type: string
  img_path: string
}

export type Feed = PublicWidget | StudentContent

export type FeedClick = {
  id: number
  /**
   * * `post_click` - Post Click
   * * `link_click` - Link Click
   * * `slider_click` - Slider Click
   * * `expand` - Expand
   */
  click_type?: FeedClickTypeEnum
  user?: string
  student_content?: string | null
  widget?: string | null
}

export type FeedClickRequest = {
  /**
   * * `post_click` - Post Click
   * * `link_click` - Link Click
   * * `slider_click` - Slider Click
   * * `expand` - Expand
   */
  click_type?: FeedClickTypeEnum
  user?: string
  /** @minLength 1 */
  student_content?: string | null
  /** @minLength 1 */
  widget?: string | null
}

/**
 * * `post_click` - Post Click
 * * `link_click` - Link Click
 * * `slider_click` - Slider Click
 * * `expand` - Expand
 */
export type FeedClickTypeEnum =
  | 'post_click'
  | 'link_click'
  | 'slider_click'
  | 'expand'

/**
 * * `widget` - Widget
 * * `student_content` - Student Content
 * * `message` - Message
 * * `celebration_item` - Celebration Item
 * * `congratulations_item` - Result
 * * `student_art` - Student Art
 * * `variant_manual_congratulations` - Congratulations
 */
export type FeedContentTypeEnum =
  | 'widget'
  | 'student_content'
  | 'message'
  | 'celebration_item'
  | 'congratulations_item'
  | 'student_art'
  | 'variant_manual_congratulations'

export type FeedSurfTime = {
  id: number
  user?: string
  /** @format date-time */
  start: string
  /** @format date-time */
  end: string
}

export type FeedSurfTimeRequest = {
  user?: string
  /** @format date-time */
  start: string
  /** @format date-time */
  end: string
}

/**
 * * `approval` - Approval
 * * `rejection` - Rejection
 */
export type FeedbackTypeEnum = 'approval' | 'rejection'

export type FileForStudent = {
  id: number
  /** @maxLength 100 */
  name: string
  author?: string
  author_str: string
  file?: string
  /** @format date-time */
  created: string
  str: string
  filename: string
  /**
   * @format uri
   * @maxLength 200
   */
  link?: string
  display_in: DisplayInEnum[]
}

export type FileForStudentChoices = {
  display_in: StringOption[]
}

export type FileForStudentSwaggerRequest = {
  /** @maxLength 100 */
  name: string
  author?: string
  /** @minLength 1 */
  file?: string
  /**
   * @format uri
   * @minLength 1
   * @maxLength 200
   */
  link?: string
  display_in: DisplayInEnum[]
}

export type FilterRepresentationResponseSchema = {
  resourcetype: AvailableStringFilter
  student: AvailableStringFilter
  status: AvailableStringFilter
  house: AvailableIntegerFilter
}

export type FirstContact = {
  id: number
  symptoms: SymptomsFf5Enum[]
  details: string
  has_covid_symptoms: boolean
  symptoms_display: string[]
  /**
   * @format decimal
   * @pattern ^-?\d{0,2}(?:\.\d{0,1})?$
   */
  temperature?: string | null
}

export type FirstContactRequest = {
  symptoms: SymptomsFf5Enum[]
  /** @minLength 1 */
  details: string
  /**
   * @format decimal
   * @pattern ^-?\d{0,2}(?:\.\d{0,1})?$
   */
  temperature?: string | null
}

export type FoodGive = {
  id: number
  was_given: boolean
  /** @format uuid */
  health_check: string
  /** @format date-time */
  created: string
}

export type FoodGiveRequest = {
  was_given: boolean
  /** @format uuid */
  health_check: string
}

/**
 * * `male` - Male
 * * `female` - Female
 */
export type ForGenderEnum = 'male' | 'female'

export type FormQRCodeResolveData = {
  qr_code_id: number
}

export type FormerStudentIdpViewGrades = {
  student: SimpleUser
  aim: SimpleAim
  artisan: SimpleUser | null
  grades: IdpViewGrades
  is_currently_enrolled: boolean
  /** @format date */
  course_drop_created: string
}

export type FormerStudentPotentialGradesView = {
  student: StudentWithGradeLevel
  aim: SimpleAim
  course_aim: SetGradeAim
  artisan: SimpleUser | null
  grades: PotentailGrades
  latest_grades: StudentAssignmentGrade[]
  /** @format date */
  course_drop_created: string
}

export type FormerStudentQuarterViewGrades = {
  student: StudentWithGradeLevel
  aim: SimpleAim
  course_aim: SetGradeAim
  artisan: SimpleUser | null
  grades: QuarterViewGrades
  latest_grades: StudentAssignmentGrade[]
  /** @format date */
  course_drop_created: string
}

/**
 * * `45` - Left
 * * `60` - Alumni
 */
export type FormerStudentStatusEnum = 45 | 60

export type FreeSlot = {
  /**
   * @min 0
   * @max 2147483647
   */
  day: RotaDayEnum
  /**
   * @min 0
   * @max 15
   */
  hour?: HourEnum | NullEnum | null
  display_hour: string
  start: string
  end: string
  course: string
}

export type FreeSpotsList = {
  id: number
  free_spots: number
}

export type GPAForCourse = {
  id: number
  /**
   * @format decimal
   * @pattern ^-?\d{0,6}(?:\.\d{0,4})?$
   */
  unweighted_value: string
  /**
   * @format decimal
   * @pattern ^-?\d{0,6}(?:\.\d{0,4})?$
   */
  weighted_value: string
  grade: string | null
  /** @format uuid */
  course: string
  course_str: string
  teacher: string
  teacher_str: string
  difficulty: string
}

export type GPAPeriod = {
  type: GPAPeriodTypeEnum
  id: number
  name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
}

/**
 * * `quarter` - quarter
 * * `semester` - semester
 * * `year` - year
 */
export type GPAPeriodTypeEnum = 'quarter' | 'semester' | 'year'

export type GenerateSummerCertificateRequest = {
  course_ids: string[]
}

export type GenerateZippedCertificatesRequest = {
  students_ids: string[]
  ceremony_dates: string[]
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type GenerationTask = {
  id: number
  /**
   * Timeout in seconds
   * @min 1
   * @max 86400
   */
  timeout?: number
  use_main_teacher_only?: boolean
  /**
   * Generate students above grade
   * @min 0
   * @max 2147483647
   */
  min_grade?: number | null
  status: GenerationTaskStatusEnum
  stop_request_sent?: boolean
  best_timetable: number | null
  optimize_only_late_slots?: boolean
  status_display: string
  /** @format date-time */
  created: string
  assign_rooms?: boolean
  quarter: number
  /**
   * @min 0
   * @max 15
   */
  late_hour?: HourEnum
  late_hour_display: string
  is_finished?: boolean
  students?: string[]
  fail_reason_display: string
  timetables: string
  generate_blank_timetable?: boolean
  shuffle_students_in_sections?: boolean
  sections_to_shuffle: any
  sections_excluded_from_shuffle?: any
  /**
   * @format double
   * @min 0
   * @max 1
   */
  shuffle_balance_tolerance?: number
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type GenerationTaskRequest = {
  /**
   * Timeout in seconds
   * @min 1
   * @max 86400
   */
  timeout?: number
  use_main_teacher_only?: boolean
  /**
   * Generate students above grade
   * @min 0
   * @max 2147483647
   */
  min_grade?: number | null
  stop_request_sent?: boolean
  optimize_only_late_slots?: boolean
  assign_rooms?: boolean
  quarter: number
  /**
   * @min 0
   * @max 15
   */
  late_hour?: HourEnum
  is_finished?: boolean
  students?: string[]
  generate_blank_timetable?: boolean
  shuffle_students_in_sections?: boolean
  sections_excluded_from_shuffle?: any
  /**
   * @format double
   * @min 0
   * @max 1
   */
  shuffle_balance_tolerance?: number
}

/**
 * * `new` - New
 * * `server_obtained` - Server has been obtained
 * * `generation_in_progress` - Generation in progress
 * * `failed` - Generation has failed
 * * `success` - Success
 */
export type GenerationTaskStatusEnum =
  | 'new'
  | 'server_obtained'
  | 'generation_in_progress'
  | 'failed'
  | 'success'

export type GeneratorTaskCosts = {
  currency: string
  /** @format double */
  cost_per_hour: number
}

export type GeneratorUpdateDraftActivity = {
  /** @format uuid */
  id: string
  course_name: string
  teacher: SimpleUser
  /**
   * @min 0
   * @max 2147483647
   */
  day: RotaDayEnum
  start_hour: string
  end_hour: string
  room_id: number | null
}

export type GeneratorUpdateNewDraftActivity = {
  /** @format uuid */
  id: string
  course_name: string
  teacher: SimpleUser
  /**
   * @min 0
   * @max 2147483647
   */
  day: RotaDayEnum
  start_hour: string
  end_hour: string
  room_id: number | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type GlobalStandard = {
  /** @format uuid */
  id: string
  course: GlobalStandardCourse
  school_year: SimpleSchoolYear
  /**
   * * `to_do` - To Do
   * * `in_progress` - In progress
   * * `updating` - Updating
   * * `sent_to_artisans` - Sent to artisans
   */
  status?: GlobalStandardStatusEnum
  created_by?: string
  /** @format date-time */
  created: string
  items?: GlobalStandardItem[]
}

export type GlobalStandardCourse = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  name: string
  departments: CourseDepartment[]
  aim: SimpleAim | null
  grade_levels: SimpleGradeLevel[]
}

export type GlobalStandardCourseRequest = {
  /**
   * @minLength 1
   * @maxLength 128
   */
  name: string
  departments: CourseDepartmentRequest[]
  aim: SimpleAimRequest | null
}

export type GlobalStandardId = {
  /** @format uuid */
  id: string
}

/** Collects and raises validation errors in bulk */
export type GlobalStandardItem = {
  /** @format uuid */
  id?: string
  /** @format uuid */
  domain_id: string
  domain_name: string
  /** @format uuid */
  topic_id: string
  topic_name: string
  /** @format uuid */
  subtopic_id: string
  subtopic_name: string
  subtopic_duration: number
}

/** Collects and raises validation errors in bulk */
export type GlobalStandardItemDetail = {
  /** @format uuid */
  id?: string
  /** @format uuid */
  domain_id: string
  domain_name: string
  /** @format uuid */
  topic_id: string
  topic_name: string
  /** @format uuid */
  subtopic_id: string
  subtopic_name: string
  subtopic_duration: number
  is_used_in_section_plan: boolean
}

/** Collects and raises validation errors in bulk */
export type GlobalStandardItemDetailRequest = {
  /** @format uuid */
  id?: string
  /** @format uuid */
  subtopic_id: string
  created_by?: string
}

/** Collects and raises validation errors in bulk */
export type GlobalStandardItemRequest = {
  /** @format uuid */
  id?: string
  /** @format uuid */
  subtopic_id: string
  created_by?: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type GlobalStandardList = {
  /** @format uuid */
  id: string
  course: CourseWithDepartments
  school_year: SimpleSchoolYear
  /**
   * * `to_do` - To Do
   * * `in_progress` - In progress
   * * `updating` - Updating
   * * `sent_to_artisans` - Sent to artisans
   */
  status?: GlobalStandardStatusEnum
  created_by?: string
  /** @format date-time */
  created: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type GlobalStandardRequest = {
  /** @format uuid */
  course_id: string
  school_year_id: number
  /**
   * * `to_do` - To Do
   * * `in_progress` - In progress
   * * `updating` - Updating
   * * `sent_to_artisans` - Sent to artisans
   */
  status?: GlobalStandardStatusEnum
  created_by?: string
  items?: GlobalStandardItemRequest[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type GlobalStandardRetrieve = {
  /** @format uuid */
  id: string
  course: GlobalStandardCourse
  school_year: SimpleSchoolYear
  status: GlobalStandardStatusEnum
  created_by?: string
  /** @format date-time */
  created: string
  items: GlobalStandardItemDetail[]
}

/**
 * * `to_do` - To Do
 * * `in_progress` - In progress
 * * `updating` - Updating
 * * `sent_to_artisans` - Sent to artisans
 */
export type GlobalStandardStatusEnum =
  | 'to_do'
  | 'in_progress'
  | 'updating'
  | 'sent_to_artisans'

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type GlobalStandardUpdate = {
  /** @format uuid */
  id: string
  course: GlobalStandardCourse
  school_year: SimpleSchoolYear
  /**
   * * `to_do` - To Do
   * * `in_progress` - In progress
   * * `updating` - Updating
   * * `sent_to_artisans` - Sent to artisans
   */
  status?: GlobalStandardStatusEnum
  created_by?: string
  /** @format date-time */
  created: string
  items?: GlobalStandardItemDetail[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type GlobalStandardUpdateRequest = {
  /** @format uuid */
  course_id: string
  school_year_id: number
  /**
   * * `to_do` - To Do
   * * `in_progress` - In progress
   * * `updating` - Updating
   * * `sent_to_artisans` - Sent to artisans
   */
  status?: GlobalStandardStatusEnum
  created_by?: string
  items?: GlobalStandardItemDetailRequest[]
}

export type GoogleLogin =
  | TwoFactorLoginResponse
  | TwoFactorLoginCodeResponse
  | TwoFactorLoginUserResponse

export type GoogleLoginRequestRequest = {
  /**
   * @minLength 1
   * @maxLength 16384
   */
  id_token: string
}

export type GoogleLoginServiceUnavailableResponse = {
  /** Google login is temporarily unavailable. */
  detail: string
}

/**
 * * `pending` - Pending
 * * `much_above` - Much Above
 * * `above` - Above
 * * `below` - Below
 * * `much_below` - Much Below
 */
export type GradeChangeEnum =
  | 'pending'
  | 'much_above'
  | 'above'
  | 'below'
  | 'much_below'

export type GradeLevel = {
  id: number
  /** @maxLength 64 */
  name: string
  /** @pattern ^[-a-zA-Z0-9_]+$ */
  slug: string
  /**
   * @min -2147483648
   * @max 2147483647
   */
  stage?: StageEnum | NullEnum | null
  stage_label: string
}

/**
 * * `CCU` - CCU
 * * `ATH` - ATH
 */
export type Group702Enum = 'CCU' | 'ATH'

export type GroupedExamList = {
  /** @default false */
  is_unassigned?: boolean
  /** @format uuid */
  exam_session_id: string | null
  exam_session_name: string | null
  /** @format date */
  start_date: string | null
  /** @format date */
  end_date: string | null
  exams_count: number
  published_exams_count: number
  exams: Exam[]
}

export type HaveStaffUsersClashes = {
  have_clashes: boolean
}

export type HaveStudentsClashes = {
  have_clashes: boolean
}

/** Collects and raises validation errors in bulk */
export type HeadOfHouseProfile = {
  id: string
  /** @format uuid */
  user?: string | null
}

/** Collects and raises validation errors in bulk */
export type HeadOfHouseProfileRequest = {
  /** @format uuid */
  user?: string | null
}

export type HealthCheck = {
  /** @format uuid */
  id: string
  /** @format uuid */
  student: string
  student_str: string
  /** @format date-time */
  slot_start: string
  /** @format date-time */
  slot_end: string
  comment: string
  is_completed?: boolean
  added_after_time?: boolean
  check_type_str: string
  /**
   * * `room` - room check
   * * `campus` - campus check
   */
  check_type?: HealthCheckTypeEnum
  sick_report: SickReportInHealthCheck
  has_medicine: string
  has_fever: boolean
  current_location: string
}

export type HealthCheckAvailableFilters = {
  student: AvailableStringFilterHealthCheckStudent
  check_type: AvailableStringFilter
  house: AvailableStringFilter
}

export type HealthCheckComment = {
  id: number
  /** @format uuid */
  health_check: string
  content: string
  /** @format date-time */
  created: string
}

export type HealthCheckCommentRequest = {
  /** @format uuid */
  health_check: string
  /** @minLength 1 */
  content: string
}

export type HealthCheckList = {
  /** @format uuid */
  id: string
  sick_report_id: string
  /** @format uuid */
  student: string
  student_str: string
  /** @format date-time */
  slot_start: string
  /** @format date-time */
  slot_end: string
  comment: string
  is_completed?: boolean
  added_after_time?: boolean
  check_type_str: string
  /**
   * * `room` - room check
   * * `campus` - campus check
   */
  check_type?: HealthCheckTypeEnum
  has_medicine: string
  current_location: string
}

export type HealthCheckLog = {
  id: number
  str: string
  set_after_time?: boolean
  /** @format uuid */
  added_by?: string | null
  added_by_str: string
  /** @format date-time */
  created: string
  /** @format date-time */
  slot_start: string
  /** @format date-time */
  slot_end: string
}

export type HealthCheckMinAndMaxDate = {
  /** @format date */
  min_date: string | null
  /** @format date */
  max_date: string | null
}

export type HealthCheckSlot = {
  slot_start: string
  slot_end: string
}

export type HealthCheckStudentOption = {
  label: string
  value: string
  searchable_content: string
}

/**
 * * `room` - room check
 * * `campus` - campus check
 */
export type HealthCheckTypeEnum = 'room' | 'campus'

export type HighSchoolGPA = {
  /**
   * @format decimal
   * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
   */
  unweighted_value: string
  /**
   * @format decimal
   * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
   */
  weighted_value: string
}

export type Hospital = {
  id: number
  /** @maxLength 256 */
  name: string
}

export type HospitalRequest = {
  /**
   * @minLength 1
   * @maxLength 256
   */
  name: string
}

/**
 * * `0` - 8:00-8:40
 * * `1` - 8:45-9:25
 * * `2` - 9:30-10:10
 * * `3` - 10:30-11:10
 * * `4` - 11:15-11:55
 * * `5` - 13:00-13:40
 * * `6` - 13:45-14:25
 * * `7` - 14:30-15:10
 * * `8` - 15:15-15:55
 * * `9` - 16:00-16:40
 * * `10` - 16:45-17:25
 * * `11` - 17:30-18:10
 * * `12` - 18:15-18:55
 * * `13` - 19:00-19:40
 * * `14` - 19:40-20:30
 * * `15` - 20:30-21:00
 */
export type HourEnum =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15

export type House = {
  id: number
  /** @maxLength 128 */
  name: string
  tutors: string[]
  generate_reports?: boolean
  generate_reports_at_study_time?: boolean
  generate_reports_at_the_weekend?: boolean
  generate_night_attendance_reports?: boolean
  is_senior_campus?: boolean
  is_junior_campus?: boolean
  is_female_campus?: boolean
  is_male_campus?: boolean
}

export type HouseList = {
  id: number
  /** @maxLength 128 */
  name: string
  tutors_data: SimpleUser[]
}

export type HouseRequest = {
  /**
   * @minLength 1
   * @maxLength 128
   */
  name: string
  tutors: string[]
  generate_reports?: boolean
  generate_reports_at_study_time?: boolean
  generate_reports_at_the_weekend?: boolean
  generate_night_attendance_reports?: boolean
  is_senior_campus?: boolean
  is_junior_campus?: boolean
  is_female_campus?: boolean
  is_male_campus?: boolean
}

export type HouseRoom = {
  id: number
  str: string
  code: string
  house: number
  name: string
  can_handle_isolation: boolean
  house_str: string
  reserved_slots: string
  available_slots: string
  is_reserved_now_for_student_only: boolean
  student_which_reserves_now_info: string
  status: AnnotatedHouseRoomStatusChoices
  /**
   * * `S` - Small
   * * `L` - Large
   */
  size?: SizeEnum
  /**
   * @min 0
   * @max 32767
   */
  capacity?: CapacityEnum
  /**
   * @min 0
   * @max 32767
   */
  people_per_bathroom?: number
}

export type HouseRoomAvailableFilters = {
  booked_for: AvailableStringFilterHouseRoomBookedFor
  house: AvailableStringFilter
  id: AvailableStringFilter
  code: AvailableStringFilter
  status: AvailableStringFilter
}

export type HouseRoomBookedForOption = {
  label: string
  value: string
  is_student: boolean
  searchable_content: string
}

export type HouseRoomList = {
  id: number
  str: string
  code: string
  house: number
  name: string
  can_handle_isolation: boolean
  house_str: string
  reserved_slots: string
  available_slots: string
  is_reserved_now_for_student_only: boolean
  student_which_reserves_now_info: string
  status: AnnotatedHouseRoomStatusChoices
  /**
   * * `S` - Small
   * * `L` - Large
   */
  size?: SizeEnum
  /**
   * @min 0
   * @max 32767
   */
  capacity?: CapacityEnum
  /**
   * @min 0
   * @max 32767
   */
  people_per_bathroom?: number
  is_currently_booked: boolean
  prebooked_date: string
  is_taken_by_booking: string
  places_taken: number
  booked_by: string
  occupied_places_by_accommodations: HouseRoomOccupiedPlacesByAccommodations[]
  occupied_places_by_bookings: HouseRoomOccupiedPlacesByBookings[]
}

export type HouseRoomOccupiedPlacesByAccommodations = {
  id: number
  /** @format uuid */
  occupier_id: string
  occupier_str: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date?: string | null
  is_temporary_movement?: boolean
  is_room_only_for_student?: boolean
}

export type HouseRoomOccupiedPlacesByBookings = {
  id: number
  /** @format uuid */
  occupier_id: string | null
  occupier_str: string
  /** @format date */
  start_date: string
  /**
   * * `reserved` - Reserved
   * * `social_room` - Social room
   * * `duty_room` - Duty room
   * * `artisan_rest_room` - Artisan rest room
   * * `storage` - Storage
   * * `artisan_private_room` - Artisan private room
   */
  purpose?: PurposeEnum
  is_occupier_student: boolean
}

export type HouseRoomRequest = {
  /**
   * * `S` - Small
   * * `L` - Large
   */
  size?: SizeEnum
  /**
   * @min 0
   * @max 32767
   */
  capacity?: CapacityEnum
  /**
   * @min 0
   * @max 32767
   */
  people_per_bathroom?: number
}

/** Collects and raises validation errors in bulk */
export type HouseTutorProfile = {
  id: string
  /** @format uuid */
  user?: string | null
}

/** Collects and raises validation errors in bulk */
export type HouseTutorProfileRequest = {
  /** @format uuid */
  user?: string | null
}

export type IdName = {
  id: string
  name: string
}

export type IdTokenOAuth2InputRequest = {
  /** @minLength 1 */
  provider?: string
  /** @minLength 1 */
  redirect_uri?: string
  /** @minLength 1 */
  id_token: string
}

export type IdpEnrollmentUsageInSection = {
  id: number
  idp_plan_id: string
  /** @format uuid */
  student: string
  student_str: string
  idp_plan_version: string
}

export type IdpPlanChangesAddedAction = {
  /** * `added` - added */
  action: IdpPlanChangesAddedActionActionEnum
  course_id: string
  course_name: string
  quarters: string[]
  is_private: boolean
}

/** * `added` - added */
export type IdpPlanChangesAddedActionActionEnum = 'added'

export type IdpPlanChangesCourseDroppedAction = {
  /** * `course_dropped` - course_dropped */
  action: IdpPlanChangesCourseDroppedActionActionEnum
  course_id: string
  course_name: string
  quarters: string[]
  to_dropped: boolean
}

/** * `course_dropped` - course_dropped */
export type IdpPlanChangesCourseDroppedActionActionEnum = 'course_dropped'

export type IdpPlanChangesDeletedAction = {
  /** * `deleted` - deleted */
  action: IdpPlanChangesDeletedActionActionEnum
  course_id: string
  course_name: string
  quarters: string[]
}

/** * `deleted` - deleted */
export type IdpPlanChangesDeletedActionActionEnum = 'deleted'

export type IdpPlanChangesLessonsNumberChangedAction = {
  /** * `lessons_number_changed` - lessons_number_changed */
  action: IdpPlanChangesLessonsNumberChangedActionActionEnum
  course_id: string
  course_name: string
  quarters: string[]
  from_hours: number
  to_hours: number
}

/** * `lessons_number_changed` - lessons_number_changed */
export type IdpPlanChangesLessonsNumberChangedActionActionEnum =
  'lessons_number_changed'

export type IdpPlanChangesPrivacyChangedAction = {
  /** * `privacy_changed` - privacy_changed */
  action: IdpPlanChangesPrivacyChangedActionActionEnum
  course_id: string
  course_name: string
  quarters: string[]
  to_private: boolean
}

/** * `privacy_changed` - privacy_changed */
export type IdpPlanChangesPrivacyChangedActionActionEnum = 'privacy_changed'

/**
 * * `new` - New
 * * `draft` - Draft
 * * `rejected` - Rejected
 * * `waits_for_admin_approval` - In admin approval
 * * `in_parental_approval` - In parental approval
 * * `is_discussion` - In discussion
 * * `approved` - Approved
 * * `automatically_approved` - Automatically approved
 * * `updated` - Updated
 * * `archived` - Archived
 */
export type IdpPlanStatusEnum =
  | 'new'
  | 'draft'
  | 'rejected'
  | 'waits_for_admin_approval'
  | 'in_parental_approval'
  | 'is_discussion'
  | 'approved'
  | 'automatically_approved'
  | 'updated'
  | 'archived'

export type IdpPlanStudent = {
  /** @format uuid */
  id: string
  full_name: string
  /** @format date */
  date_of_entry?: string | null
  entry_quarter: string
  is_new: boolean
}

export type IdpSectionEnroll = {
  id: number
  section?: number | null
  /**
   * @min -2147483648
   * @max 2147483647
   */
  number_of_lessons?: number
}

export type IdpSectionEnrollRequest = {
  section?: number | null
  /** @format uuid */
  course?: string | null
  /**
   * @min -2147483648
   * @max 2147483647
   */
  number_of_lessons?: number
}

export type IdpSectionEnrollment = {
  id: number
  /** @format uuid */
  student: string
  student_str: string
  quarter: number
  section?: number | null
  course_drop_student_idp_plan_id: string | null
  /**
   * @min -2147483648
   * @max 2147483647
   */
  number_of_lessons: number
  is_private?: boolean
  /** @format date-time */
  section_deleted_at?: string | null
  course_in_basket?: number | null
  /** @format uuid */
  section_deleted_by?: string | null
  course_drop?: number | null
}

export type IdpSectionEnrollmentRequest = {
  /** @format uuid */
  student: string
  quarter: number
  section?: number | null
  course_drop_data?: CourseDropDataRequest | null
  /**
   * @min -2147483648
   * @max 2147483647
   */
  number_of_lessons: number
  is_private?: boolean
  /** @format date-time */
  section_deleted_at?: string | null
  course_in_basket?: number | null
  /** @format uuid */
  section_deleted_by?: string | null
  course_drop?: number | null
}

export type IdpTableCourseGrades = {
  id: number
  quarter_id: number | null
  semester_id: number | null
  grade: string
  /** @format double */
  weighted_percents: number | null
  comment: string
  comments_allowed: boolean
  aim_id: number
  period_short_name: string
  course_drop_id: number | null
}

export type IdpTableStudentCourses = {
  /** @format uuid */
  course: string
  grades: Record<string, IdpTableCourseGrades>
}

export type IdpTableStudentCoursesGrades = {
  /** @format uuid */
  student: string
  courses: IdpTableStudentCourses[]
}

export type IdpViewBasePeriodGrade = {
  grade_id: number | null
  quarter_id: number | null
  semester_id: number | null
  school_year_id: number | null
  grade: string | null
  target_grade_status: TargetGradeStatusEnum | NullEnum | null
  has_comment: boolean | null
  /** @default false */
  can_manage_comment?: boolean
}

export type IdpViewGrades = {
  Q1?: IdpViewPeriodGrade | null
  Q2?: IdpViewPeriodGrade | null
  Q3?: IdpViewPeriodGrade | null
  Q4?: IdpViewPeriodGrade | null
  S1?: IdpViewPeriodGrade | null
  S2?: IdpViewPeriodGrade | null
  FY?: IdpViewPeriodGrade | null
  P1?: IdpViewBasePeriodGrade | null
  P2?: IdpViewBasePeriodGrade | null
  P3?: IdpViewBasePeriodGrade | null
  P4?: IdpViewBasePeriodGrade | null
}

export type IdpViewPeriodGrade = {
  grade_id: number | null
  quarter_id: number | null
  semester_id: number | null
  school_year_id: number | null
  grade: string | null
  target_grade_status: TargetGradeStatusEnum | NullEnum | null
  has_comment: boolean | null
  /** @default false */
  can_manage_comment?: boolean
  is_custom_grade_set_manually: boolean | null
}

export type IdpViewStudentGrades = {
  grades_in_courses: IdpViewStudentGradesInCourses[]
  grades_in_dropped_courses: IdpViewStudentGradesInDroppedCourses[]
}

export type IdpViewStudentGradesInCourses = {
  course: SimpleCourseWithGroup
  artisan: SimpleUser
  aim: SimpleAim
  grades: IdpViewGrades
  is_currently_enrolled: boolean
}

export type IdpViewStudentGradesInDroppedCourses = {
  course: SimpleCourseWithGroup
  artisan: SimpleUser
  aim: SimpleAim
  grades: IdpViewGrades
  is_currently_enrolled: boolean
  /** @format date */
  course_drop_created: string
}

/**
 * * `teacher` - Teacher
 * * `admin` - Admin
 */
export type IepContactEnum = 'teacher' | 'admin'

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type Image = {
  id: string
  ppoi?: string
  thumbnail: string
  catalogs?: number[]
  /** @format date-time */
  created: string
  /** @format date-time */
  created_at: string
  /** @maxLength 7 */
  dominant_color?: string
  /** @maxLength 128 */
  title?: string
  /** @maxLength 128 */
  alt?: string
  file_url: string
  /** @format uri */
  file?: string
  filename: string
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type ImageRequest = {
  /** @minLength 1 */
  ppoi?: string
  catalogs?: number[]
  /**
   * @minLength 1
   * @maxLength 7
   */
  dominant_color?: string
  /** @maxLength 128 */
  title?: string
  /** @maxLength 128 */
  alt?: string
  /** @format binary */
  file?: File
}

/**
 * * `4` - S
 * * `3` - E
 * * `2` - A
 * * `1` - C
 * * `0` - not assigned
 */
export type ImmediateDowngradeEnum = 4 | 3 | 2 | 1 | 0

/**
 * * `avoid mild physical exercise` - Avoid Mild Physical Exercise
 * * `avoid intensive physical exercise` - Avoid Intensive Physical Exercise
 * * `no impact` - No Impact
 * * `concentration` - Concentration
 * * `fatigue` - Fatigue
 * * `headache` - Headache
 * * `body ache or pain` - Body Ache or Pain
 * * `cognitive issues` - Cognitive issues
 * * `flashing lights (incl. movies & video games)` - Flashing Lights (incl. Movies & Video Games)
 */
export type ImpactEnum =
  | 'avoid mild physical exercise'
  | 'avoid intensive physical exercise'
  | 'no impact'
  | 'concentration'
  | 'fatigue'
  | 'headache'
  | 'body ache or pain'
  | 'cognitive issues'
  | 'flashing lights (incl. movies & video games)'

/**
 * * `ImportSource.MANUAL` - ImportSource.MANUAL
 * * `ImportSource.AUTOMATIC` - ImportSource.AUTOMATIC
 */
export type ImportSourceEnum = 'ImportSource.MANUAL' | 'ImportSource.AUTOMATIC'

export type ImportStatusCheckResponse = {
  /**
   * * `Exam(s)` - Exams
   * * `Timetable & Boarding Rota` - Tt
   */
  type?: ImportStatusCheckResponseTypeEnum
  in_progress: boolean
}

/**
 * * `Exam(s)` - Exams
 * * `Timetable & Boarding Rota` - Tt
 */
export type ImportStatusCheckResponseTypeEnum =
  | 'Exam(s)'
  | 'Timetable & Boarding Rota'

export type IncompleteAssignmentCount = {
  me: number
  my_teams: number
}

/** Used only for correct schema display in TaskViewSet.count_daily_incomplete() */
export type IncompleteDailyTaskCount = {
  daily: number
}

/** Used only for correct schema display in TaskViewSet.count_incomplete() */
export type IncompleteTaskCount = {
  daily: number
  assignment: IncompleteAssignmentCount
  teams: IncompleteTaskPerTeamCount
  status: IncompleteTaskPerStatusCount
}

export type IncompleteTaskPerStatusCount = {
  overdue: number
  close_deadline: number
  unscheduled: number
  expired: number
}

export type IncompleteTaskPerTeamCount = {
  '2f53d039-567a-43a7-8d3e-9c7a16bd1ae7': number
}

/**
 * * `max_sections` - Max sections
 * * `capacity_per_section` - Capacity per section
 */
export type IncreaseEnum = 'max_sections' | 'capacity_per_section'

/** Collects and raises validation errors in bulk */
export type InfoboardEditorProfile = {
  id: string
  /** @format uuid */
  user?: string | null
}

/** Collects and raises validation errors in bulk */
export type InfoboardEditorProfileRequest = {
  /** @format uuid */
  user?: string | null
}

/**
 * * `standard` - Standard
 * * `private` - Private
 * * `semi_private` - Semi-private
 */
export type InsuranceTypeEnum = 'standard' | 'private' | 'semi_private'

export type IntegerOption = {
  label: string
  value: number
}

export type Interest = {
  id: number
  /** @maxLength 256 */
  name: string
}

export type InterestRequest = {
  /**
   * @minLength 1
   * @maxLength 256
   */
  name: string
}

export type Internship = {
  id: number
  /** @maxLength 256 */
  name: string
  is_hidden?: boolean
}

export type InternshipAttachment = {
  id: number
  /** @format uri */
  file: string
  file_name: string
}

export type InterventionProgressComment = {
  /** @format uuid */
  id: string
  comment: string | null
}

export type InterventionProgressCommentRequest = {
  /** @minLength 1 */
  comment: string | null
}

export type InvigilatorsFromRoomResponse = {
  auto_assigned_invigilators: string[]
}

/**
 * * `5` - Not started
 * * `10` - Invoice sent
 * * `15` - Waitlist sent
 * * `17` - Deposit
 * * `20` - Paid
 * * `22` - Waitlist deposit
 * * `25` - Waitlist paid
 * * `30` - Failed
 */
export type InvoiceStatusEnum = 5 | 10 | 15 | 17 | 20 | 22 | 25 | 30

/**
 * * `lesson` - Lesson
 * * `exam` - Exam
 * * `programme` - Programme
 * * `duty` - Duty
 */
export type KindEnum = 'lesson' | 'exam' | 'programme' | 'duty'

export type Knox = {
  token: string
}

/**
 * * `a1` - A1
 * * `a2` - A2
 * * `b1` - B1
 * * `b2` - B2
 * * `c1` - C1
 * * `c2` - C2
 * * `other` - Other
 */
export type LanguageLevelEnum =
  | 'a1'
  | 'a2'
  | 'b1'
  | 'b2'
  | 'c1'
  | 'c2'
  | 'other'

/**
 * * `german` - German
 * * `french` - French
 * * `english` - English
 * * `latin` - Latin
 * * `italian` - Italian
 * * `spanish` - Spanish
 * * `other` - Other
 */
export type LanguageNameEnum =
  | 'german'
  | 'french'
  | 'english'
  | 'latin'
  | 'italian'
  | 'spanish'
  | 'other'

export type LaundryCatalog = {
  categories: LaundryCatalogCategory[]
}

export type LaundryCatalogCategory = {
  /**
   * * `tops` - Tops
   * * `bottoms` - Bottoms
   * * `dresses` - Dresses
   * * `outerwear` - Outerwear
   * * `formal_clothing` - Formal clothing
   * * `accessories` - Accessories
   * * `lingerie` - Lingerie
   * * `bathroom_textiles` - Bathroom textiles
   * * `private_bed_linen` - Private bed linen
   * * `sport_clothing` - Sport clothing
   */
  category_key: CategoryKeyEnum
  category_name: string
  items: LaundryCatalogItem[]
}

export type LaundryCatalogItem = {
  id: number
  /** @maxLength 128 */
  name: string
  /**
   * @format double
   * @min -10000
   * @exclusiveMin true
   * @max 10000
   * @exclusiveMax true
   */
  unit_price: number
  /**
   * * `tops` - Tops
   * * `bottoms` - Bottoms
   * * `dresses` - Dresses
   * * `outerwear` - Outerwear
   * * `formal_clothing` - Formal clothing
   * * `accessories` - Accessories
   * * `lingerie` - Lingerie
   * * `bathroom_textiles` - Bathroom textiles
   * * `private_bed_linen` - Private bed linen
   * * `sport_clothing` - Sport clothing
   */
  category: CategoryEnum
  /**
   * * `normal` - Normal
   * * `delicate` - Delicate
   * * `dry` - Dry
   */
  laundry_type: LaundryTypeEnum
  /**
   * @min 0
   * @max 2147483647
   */
  ordering?: number
}

export type LaundryCatalogPricing = {
  categories: LaundryCatalogPricingCategory[]
}

export type LaundryCatalogPricingCategory = {
  /**
   * * `tops` - Tops
   * * `bottoms` - Bottoms
   * * `dresses` - Dresses
   * * `outerwear` - Outerwear
   * * `formal_clothing` - Formal clothing
   * * `accessories` - Accessories
   * * `lingerie` - Lingerie
   * * `bathroom_textiles` - Bathroom textiles
   * * `private_bed_linen` - Private bed linen
   * * `sport_clothing` - Sport clothing
   */
  category_key: CategoryKeyEnum
  category_name: string
  items: LaundryCatalogPricingItem[]
}

export type LaundryCatalogPricingCategoryRequest = {
  /**
   * * `tops` - Tops
   * * `bottoms` - Bottoms
   * * `dresses` - Dresses
   * * `outerwear` - Outerwear
   * * `formal_clothing` - Formal clothing
   * * `accessories` - Accessories
   * * `lingerie` - Lingerie
   * * `bathroom_textiles` - Bathroom textiles
   * * `private_bed_linen` - Private bed linen
   * * `sport_clothing` - Sport clothing
   */
  category_key: CategoryKeyEnum
  items: LaundryCatalogPricingItemRequest[]
}

export type LaundryCatalogPricingItem = {
  /** @maxLength 128 */
  name: string
  /** @min 1 */
  ordering: number
  types: LaundryCatalogPricingItemTypes
}

export type LaundryCatalogPricingItemRequest = {
  /**
   * @minLength 1
   * @maxLength 128
   */
  name: string
  /** @min 1 */
  ordering: number
  types: LaundryCatalogPricingItemTypesRequest
}

export type LaundryCatalogPricingItemType = {
  id: number | null
  /**
   * @format double
   * @min 0
   * @max 10000
   * @exclusiveMax true
   */
  unit_price: number | null
}

export type LaundryCatalogPricingItemTypeRequest = {
  /**
   * @format double
   * @min 0
   * @max 10000
   * @exclusiveMax true
   */
  unit_price: number | null
}

export type LaundryCatalogPricingItemTypes = {
  normal: LaundryCatalogPricingItemType
  dry: LaundryCatalogPricingItemType
  delicate: LaundryCatalogPricingItemType
}

export type LaundryCatalogPricingItemTypesRequest = {
  normal: LaundryCatalogPricingItemTypeRequest
  dry: LaundryCatalogPricingItemTypeRequest
  delicate: LaundryCatalogPricingItemTypeRequest
}

export type LaundryHouse = {
  id: number
  /** @maxLength 128 */
  name: string
  students: LaundryHouseStudent[]
}

export type LaundryHouseAvailableQRCode = {
  id: number
  code: string | null
}

export type LaundryHouseStudent = {
  /** @format uuid */
  id: string
  full_name: string
  room_name: string
  available_qr_codes: LaundryHouseAvailableQRCode[]
}

export type LaundryOrderAvailableFilters = {
  is_current: AvailableBooleanFilter
  house: AvailableIntegerFilter
  student: AvailableStringFilter
  status: AvailableStringFilter
  is_claim_submitted: AvailableBooleanFilter
  is_laundry_edited: AvailableBooleanFilter
}

export type LaundryOrderClaimRejectRequest = {
  /** @maxLength 500 */
  hotel_services_note?: string
}

export type LaundryOrderClaimRequest = {
  /** @maxLength 500 */
  hotel_services_note?: string
  claimed_items: LaundryOrderClaimedItemRequest[]
}

/**
 * * `student` - Student
 * * `panel` - Panel
 */
export type LaundryOrderClaimSubmittedByEnum = 'student' | 'panel'

export type LaundryOrderClaimedItemRequest = {
  /** @min 1 */
  catalog_item_id?: number | null
  /** @min 1 */
  version_item_id?: number | null
  /** @min 1 */
  quantity: number
}

export type LaundryOrderCounter = {
  /** @min 0 */
  pickup: number
  /** @min 0 */
  in_laundry: number
  /** @min 0 */
  completed: number
  /** @min 0 */
  rejected: number
  /** @min 0 */
  claim: number
}

export type LaundryOrderCreateRequest = {
  order_items: LaundryOrderVersionItemCreateRequest[]
  /** @format uuid */
  student_id: string
  qr_code_id: number
  /**
   * * `normal` - Normal
   * * `delicate` - Delicate
   * * `dry` - Dry
   */
  laundry_type: LaundryTypeEnum
}

export type LaundryOrderDetail = {
  id: number
  code: string | null
  /** @deprecated */
  is_claim_submitted: boolean
  claim_submitted_by: LaundryOrderClaimSubmittedByEnum | NullEnum | null
  student_name: string
  house_name: string | null
  room_name: string | null
  /**
   * * `normal` - Normal
   * * `delicate` - Delicate
   * * `dry` - Dry
   */
  laundry_type?: LaundryTypeEnum
  /**
   * * `pickup` - Pickup
   * * `in_laundry` - In laundry
   * * `completed` - Completed
   * * `rejected` - Rejected
   * * `claim` - Claim
   * * `closed` - Closed
   * * `claim_closed` - Claim closed
   */
  status?: StudentLaundryOrderStatusEnum
  /** @format date-time */
  pickup_date: string
  /** @format date */
  delivery_date: string | null
  /** @format date-time */
  closed_at?: string | null
  /** @maxLength 500 */
  laundry_note?: string
  claim_details: string | null
  hotel_services_note: string | null
  /** @maxLength 500 */
  rejection_reason?: string
  is_laundry_edited: boolean
  order_items: LaundryOrderDetailCategory[]
  /**
   * @format double
   * @min -100000000
   * @exclusiveMin true
   * @max 100000000
   * @exclusiveMax true
   */
  total_price: number
  number_of_items: number
  /**
   * @format double
   * @min -100000000
   * @exclusiveMin true
   * @max 100000000
   * @exclusiveMax true
   */
  old_total_price: number | null
  old_number_of_items: number | null
}

export type LaundryOrderDetailCategory = {
  category_key: string
  category_name: string
  items: LaundryOrderDetailItem[]
}

export type LaundryOrderDetailItem = {
  catalog_item_id: number | null
  version_item_id: number | null
  name: string
  /**
   * @format double
   * @min -10000
   * @exclusiveMin true
   * @max 10000
   * @exclusiveMax true
   */
  unit_price: number
  quantity: number
  old_quantity: number | null
  is_new: boolean | null
  is_removed: boolean | null
  is_edited: boolean | null
  is_claimed: boolean
  claimed_quantity: number | null
}

export type LaundryOrderList = {
  id: number
  student_name: string
  house_name: string | null
  /** @format date-time */
  pickup_date: string
  /** @format date */
  delivery_date: string | null
  /** @format date-time */
  closed_at?: string | null
  /**
   * * `pickup` - Pickup
   * * `in_laundry` - In laundry
   * * `completed` - Completed
   * * `rejected` - Rejected
   * * `claim` - Claim
   * * `closed` - Closed
   * * `claim_closed` - Claim closed
   */
  status?: StudentLaundryOrderStatusEnum
  is_laundry_edited: boolean
  /** @deprecated */
  is_claim_submitted: boolean
  claim_submitted_by: LaundryOrderClaimSubmittedByEnum | NullEnum | null
}

export type LaundryOrderVersionItemCreateRequest = {
  catalog_item_id: number | null
  /**
   * @minLength 1
   * @maxLength 128
   */
  name?: string
  /**
   * @min 1
   * @max 2147483647
   */
  quantity?: number
}

export type LaundryQRCodeActiveOrderLookupResponse = {
  active_order_id: number | null
}

export type LaundryQRCodeAvailableFilters = {
  house: AvailableIntegerFilter
  room: AvailableIntegerFilter
  availability: AvailableStringFilter
}

export type LaundryQRCodeBulkCreateRequest = {
  house: number
  /**
   * @min 1
   * @max 20
   */
  quantity: number
  rooms?: number[]
}

export type LaundryQRCodeList = {
  id: number
  code: string | null
  room_id: number
  room_name: string
  house_name: string
  /**
   * * `free` - Free
   * * `in_use` - In use
   */
  availability: AvailabilityEnum
  /** @format date-time */
  created: string
  order_id: number | null
  order_status: StudentLaundryOrderStatusEnum | NullEnum | null
  /** @default false */
  is_new?: boolean
}

export type LaundryStaffOrderDetail = {
  id: number
  /**
   * * `normal` - Normal
   * * `delicate` - Delicate
   * * `dry` - Dry
   */
  laundry_type?: LaundryTypeEnum
  /**
   * * `pickup` - Pickup
   * * `in_laundry` - In laundry
   * * `completed` - Completed
   * * `rejected` - Rejected
   * * `claim` - Claim
   * * `closed` - Closed
   * * `claim_closed` - Claim closed
   */
  status?: StudentLaundryOrderStatusEnum
  /** @maxLength 500 */
  rejection_reason?: string
  is_laundry_edited?: boolean
  order_items: LaundryStaffOrderDetailCategory[]
}

export type LaundryStaffOrderDetailCategory = {
  category_key: string
  category_name: string
  items: LaundryStaffOrderDetailItem[]
}

export type LaundryStaffOrderDetailItem = {
  id: number
  catalog_item_id: number | null
  name: string
  /**
   * @format double
   * @min -10000
   * @exclusiveMin true
   * @max 10000
   * @exclusiveMax true
   */
  unit_price: number
  quantity: number
  is_student_item: boolean
}

export type LaundryStaffOrderFinish = {
  id: number
}

export type LaundryStaffOrderFinishRequest = {
  /** @maxLength 500 */
  laundry_note?: string
}

export type LaundryStaffOrderReject = {
  id: number
}

export type LaundryStaffOrderRejectRequest = {
  /**
   * @minLength 1
   * @maxLength 500
   */
  rejection_reason: string
}

export type LaundryStaffOrderVersionCreate = {
  order_items: LaundryStaffOrderVersionItemCreate[]
}

export type LaundryStaffOrderVersionCreateRequest = {
  order_items: LaundryStaffOrderVersionItemCreateRequest[]
}

export type LaundryStaffOrderVersionItemCreate = {
  catalog_item_id: number | null
  /** @maxLength 128 */
  name?: string
  /**
   * @min 0
   * @max 2147483647
   */
  quantity?: number
  /**
   * @format double
   * @min 0.01
   * @max 10000
   * @exclusiveMax true
   */
  unit_price?: number
  student_version_item_id?: number | null
}

export type LaundryStaffOrderVersionItemCreateRequest = {
  catalog_item_id: number | null
  /**
   * @minLength 1
   * @maxLength 128
   */
  name?: string
  /**
   * @min 0
   * @max 2147483647
   */
  quantity?: number
  /**
   * @format double
   * @min 0.01
   * @max 10000
   * @exclusiveMax true
   */
  unit_price?: number
  student_version_item_id?: number | null
}

export type LaundryStaffOrderVersionUpdate = {
  order_items: LaundryStaffOrderVersionItemCreate[]
}

export type LaundryStaffOrderVersionUpdateRequest = {
  order_items: LaundryStaffOrderVersionItemCreateRequest[]
}

/**
 * * `normal` - Normal
 * * `delicate` - Delicate
 * * `dry` - Dry
 */
export type LaundryTypeEnum = 'normal' | 'delicate' | 'dry'

/**
 * * `square` - Square
 * * `framed` - Framed
 * * `fullscreen` - Fullscreen
 */
export type LayoutEnum = 'square' | 'framed' | 'fullscreen'

export type LeaveCheck = {
  /** @format uuid */
  id: string
  /** @maxLength 1024 */
  destination: string
  /** @maxLength 256 */
  train?: string
  /** @maxLength 256 */
  plane?: string
  /** @maxLength 256 */
  pick_up_location?: string
  /** @format time */
  pick_up_time?: string | null
  comment?: string
  /**
   * * `in` - Check-in
   * * `out` - Check-out
   */
  check_type: LeaveCheckTypeEnum
  /** @maxLength 64 */
  contact_person: string
  /** @maxLength 128 */
  contact_person_phone: string
  is_completed?: boolean
  is_driver_needed?: boolean
  /** @format date-time */
  date: string
  /** @format uuid */
  leave_request: string
}

export type LeaveCheckComplete = {
  /** @format date-time */
  date: string
}

export type LeaveCheckCompleteRequest = {
  /** @format date-time */
  date: string
}

export type LeaveCheckRequest = {
  /**
   * @minLength 1
   * @maxLength 1024
   */
  destination: string
  /** @maxLength 256 */
  train?: string
  /** @maxLength 256 */
  plane?: string
  /** @maxLength 256 */
  pick_up_location?: string
  /** @format time */
  pick_up_time?: string | null
  comment?: string
  /**
   * * `in` - Check-in
   * * `out` - Check-out
   */
  check_type: LeaveCheckTypeEnum
  /**
   * @minLength 1
   * @maxLength 64
   */
  contact_person: string
  /**
   * @minLength 1
   * @maxLength 128
   */
  contact_person_phone: string
  is_completed?: boolean
  is_driver_needed?: boolean
  /** @format date-time */
  date: string
  /** @format uuid */
  leave_request: string
}

/**
 * * `in` - Check-in
 * * `out` - Check-out
 */
export type LeaveCheckTypeEnum = 'in' | 'out'

export type LeaveRequest = {
  /** @format uuid */
  id: string
  check_out?: RelatedLeaveCheck
  check_in?: RelatedLeaveCheck
  /** @format uuid */
  student: string
  student_str: string
  /**
   * @min -2147483648
   * @max 2147483647
   */
  leave_type: LeaveRequestTypeEnum
  get_leave_type_display: string
  /**
   * * `manual` - Manual
   * * `from idp` - From IDP
   * * `by_student` - Requested by student
   */
  request_type?: RequestTypeEnum
  get_request_type_display: string
  /**
   * * `tentative` - Pending guardian
   * * `confirmed_contact_person` - Pending academic
   * * `confirmed_academic` - Pending boarding
   * * `changed` - Changed
   * * `confirmed` - Approved
   * * `declined` - Rejected
   * * `declined_contact_person` - Rejected by guardian
   * * `declined_academic` - Rejected by academic
   * * `canceled` - Canceled by student
   * * `expired` - Expired
   */
  status?: LeaveRequestStatusEnum
  get_status_display: string
  history_items: LeaveRequestHistoryItem[]
  check_out_is_completed: string
  check_in_is_completed: string
  has_siblings_not_completed: string
  student_message?: string
  has_clashes?: boolean
  clashes_resolved?: boolean
  approve_url: string
}

export type LeaveRequestAvailableFilters = {
  student: AvailableStringFilter
}

export type LeaveRequestClashes = {
  /** @format uuid */
  id: string
  /**
   * * `manual` - Manual
   * * `from idp` - From IDP
   * * `by_student` - Requested by student
   */
  request_type?: RequestTypeEnum
  request_type_label: string
  /**
   * @min -2147483648
   * @max 2147483647
   */
  leave_type?: LeaveRequestTypeEnum
  leave_type_label: string
  /** @format date-time */
  check_out_date: string
  /** @format date-time */
  check_in_date: string | null
  /** @default "leave_requests" */
  clash_type: string
}

export type LeaveRequestHistoryItem = {
  /** @format uuid */
  id: string
  /**
   * * `tentative` - Pending guardian
   * * `confirmed_contact_person` - Pending academic
   * * `confirmed_academic` - Pending boarding
   * * `changed` - Changed
   * * `confirmed` - Approved
   * * `declined` - Rejected
   * * `declined_contact_person` - Rejected by guardian
   * * `declined_academic` - Rejected by academic
   * * `canceled` - Canceled by student
   * * `expired` - Expired
   */
  status?: LeaveRequestStatusEnum
  /** @format date-time */
  created: string
  /**
   * * `create` - Created
   * * `change` - Changed
   * * `email_send` - E-mail to parent
   * * `contact_person_approve` - Approved by guardian
   * * `contact_person_reject` - Rejected by guardian
   * * `academic_approve` - Approved by academic
   * * `academic_reject` - Rejected by academic
   * * `academic_requested` - Moved to requested by academic
   * * `bdt_approve` - Approved by boarding
   * * `bdt_reject` - Rejected by boarding
   * * `bdt_requested` - Moved to requested by boarding
   * * `student_cancel` - Canceled by student
   * * `expired` - Expired
   * * `check_out_completed` - Check-out checked
   * * `check_out_uncompleted` - Check-out unchecked
   * * `check_in_completed` - Check-in checked
   * * `check_in_uncompleted` - Check-in unchecked
   */
  log_type: LeaveRequestHistoryItemLogTypeEnum
  get_log_type_display: string
  description: string
  comment?: string
  added_by: SimpleUser | null
}

/**
 * * `create` - Created
 * * `change` - Changed
 * * `email_send` - E-mail to parent
 * * `contact_person_approve` - Approved by guardian
 * * `contact_person_reject` - Rejected by guardian
 * * `academic_approve` - Approved by academic
 * * `academic_reject` - Rejected by academic
 * * `academic_requested` - Moved to requested by academic
 * * `bdt_approve` - Approved by boarding
 * * `bdt_reject` - Rejected by boarding
 * * `bdt_requested` - Moved to requested by boarding
 * * `student_cancel` - Canceled by student
 * * `expired` - Expired
 * * `check_out_completed` - Check-out checked
 * * `check_out_uncompleted` - Check-out unchecked
 * * `check_in_completed` - Check-in checked
 * * `check_in_uncompleted` - Check-in unchecked
 */
export type LeaveRequestHistoryItemLogTypeEnum =
  | 'create'
  | 'change'
  | 'email_send'
  | 'contact_person_approve'
  | 'contact_person_reject'
  | 'academic_approve'
  | 'academic_reject'
  | 'academic_requested'
  | 'bdt_approve'
  | 'bdt_reject'
  | 'bdt_requested'
  | 'student_cancel'
  | 'expired'
  | 'check_out_completed'
  | 'check_out_uncompleted'
  | 'check_in_completed'
  | 'check_in_uncompleted'

export type LeaveRequestHistoryItemRequest = {
  /**
   * * `tentative` - Pending guardian
   * * `confirmed_contact_person` - Pending academic
   * * `confirmed_academic` - Pending boarding
   * * `changed` - Changed
   * * `confirmed` - Approved
   * * `declined` - Rejected
   * * `declined_contact_person` - Rejected by guardian
   * * `declined_academic` - Rejected by academic
   * * `canceled` - Canceled by student
   * * `expired` - Expired
   */
  status?: LeaveRequestStatusEnum
  /**
   * * `create` - Created
   * * `change` - Changed
   * * `email_send` - E-mail to parent
   * * `contact_person_approve` - Approved by guardian
   * * `contact_person_reject` - Rejected by guardian
   * * `academic_approve` - Approved by academic
   * * `academic_reject` - Rejected by academic
   * * `academic_requested` - Moved to requested by academic
   * * `bdt_approve` - Approved by boarding
   * * `bdt_reject` - Rejected by boarding
   * * `bdt_requested` - Moved to requested by boarding
   * * `student_cancel` - Canceled by student
   * * `expired` - Expired
   * * `check_out_completed` - Check-out checked
   * * `check_out_uncompleted` - Check-out unchecked
   * * `check_in_completed` - Check-in checked
   * * `check_in_uncompleted` - Check-in unchecked
   */
  log_type: LeaveRequestHistoryItemLogTypeEnum
  /** @minLength 1 */
  description: string
  comment?: string
}

export type LeaveRequestList = {
  /** @format uuid */
  id: string
  check_out?: RelatedSimpleLeaveCheck
  check_in?: RelatedSimpleLeaveCheck
  /** @format uuid */
  student: string
  student_str: string
  /**
   * * `tentative` - Pending guardian
   * * `confirmed_contact_person` - Pending academic
   * * `confirmed_academic` - Pending boarding
   * * `changed` - Changed
   * * `confirmed` - Approved
   * * `declined` - Rejected
   * * `declined_contact_person` - Rejected by guardian
   * * `declined_academic` - Rejected by academic
   * * `canceled` - Canceled by student
   * * `expired` - Expired
   */
  status?: LeaveRequestStatusEnum
  get_status_display: string
  get_request_type_display: string
  has_clashes?: boolean
  clashes_resolved?: boolean
  check_out_is_completed: string
  check_in_is_completed: string
  has_siblings_not_completed: string
}

export type LeaveRequestRequest = {
  check_out?: RelatedLeaveCheckRequest
  check_in?: RelatedLeaveCheckRequest
  /** @format uuid */
  student: string
  /**
   * @min -2147483648
   * @max 2147483647
   */
  leave_type: LeaveRequestTypeEnum
  /**
   * * `manual` - Manual
   * * `from idp` - From IDP
   * * `by_student` - Requested by student
   */
  request_type?: RequestTypeEnum
  /**
   * * `tentative` - Pending guardian
   * * `confirmed_contact_person` - Pending academic
   * * `confirmed_academic` - Pending boarding
   * * `changed` - Changed
   * * `confirmed` - Approved
   * * `declined` - Rejected
   * * `declined_contact_person` - Rejected by guardian
   * * `declined_academic` - Rejected by academic
   * * `canceled` - Canceled by student
   * * `expired` - Expired
   */
  status?: LeaveRequestStatusEnum
  student_message?: string
  has_clashes?: boolean
  clashes_resolved?: boolean
}

/**
 * * `approve` - Approve
 * * `reject` - Reject
 * * `requested` - Requested
 */
export type LeaveRequestReviewActionEnum = 'approve' | 'reject' | 'requested'

/**
 * * `tentative` - Pending guardian
 * * `confirmed_contact_person` - Pending academic
 * * `confirmed_academic` - Pending boarding
 * * `changed` - Changed
 * * `confirmed` - Approved
 * * `declined` - Rejected
 * * `declined_contact_person` - Rejected by guardian
 * * `declined_academic` - Rejected by academic
 * * `canceled` - Canceled by student
 * * `expired` - Expired
 */
export type LeaveRequestStatusEnum =
  | 'tentative'
  | 'confirmed_contact_person'
  | 'confirmed_academic'
  | 'changed'
  | 'confirmed'
  | 'declined'
  | 'declined_contact_person'
  | 'declined_academic'
  | 'canceled'
  | 'expired'

/**
 * * `0` - Weekend
 * * `1` - Holiday
 * * `2` - Other
 * * `3` - Arrival
 */
export type LeaveRequestTypeEnum = 0 | 1 | 2 | 3

/**
 * * `family_member_attended` - A family member attended Rosenberg.
 * * `friend_attended` - My friends attended Rosenberg and recommended me.
 * * `family_friend_told_me` - A family friend told me about Rosenberg.
 * * `educational_consultant` - Educational consultant.
 * * `other` - Other
 */
export type LegacyTypeEnum =
  | 'family_member_attended'
  | 'friend_attended'
  | 'family_friend_told_me'
  | 'educational_consultant'
  | 'other'

/**
 * * `first_parent` - First Parent
 * * `second_parent` - Second Parent
 * * `both_parents` - Both Parents
 * * `relative` - Relative
 * * `other` - Other
 */
export type LegalGuardianRelationEnum =
  | 'first_parent'
  | 'second_parent'
  | 'both_parents'
  | 'relative'
  | 'other'

export type LessonAvailableSectionPlanItem = {
  /** @format uuid */
  id: string
  /** @format uuid */
  domain_id: string
  domain_name: string
  /** @format uuid */
  topic_id: string
  topic_name: string
  /** @format uuid */
  subtopic_id: string
  subtopic_name: string
  subtopic_description: string
  duration: number
  realized_lessons: number
  /**
   * * `to_do` - To do
   * * `planned` - Planned
   * * `in_progress` - In progress
   * * `completed` - Completed
   */
  status: LessonSectionPlanItemStatusEnum
}

export type LessonAvailableSectionPlanItemsQuarter = {
  id: number
  short_name: string
  section_plan_items: LessonAvailableSectionPlanItem[]
}

export type LessonClash = {
  /** @format uuid */
  id: string
  date: string
  course_name: string
  hour: string
  /** @format time */
  custom_start: string | null
  /** @format time */
  custom_end: string | null
}

export type LessonClashes = {
  /** @format uuid */
  id: string
  course: string
  /** @format date */
  date: string
  start_hour: string
  end_hour: string
  /** @default "lessons" */
  clash_type: string
}

export type LessonSectionPlanItem = {
  /** @format uuid */
  id: string
  /** @format uuid */
  domain_id: string
  domain_name: string
  /** @format uuid */
  topic_id: string
  topic_name: string
  /** @format uuid */
  subtopic_id: string
  subtopic_name: string
  subtopic_description: string
  duration: number
  /**
   * * `to_do` - To do
   * * `planned` - Planned
   * * `in_progress` - In progress
   * * `completed` - Completed
   */
  status: LessonSectionPlanItemStatusEnum
  realized_lessons: number
}

/**
 * * `to_do` - To do
 * * `planned` - Planned
 * * `in_progress` - In progress
 * * `completed` - Completed
 */
export type LessonSectionPlanItemStatusEnum =
  | 'to_do'
  | 'planned'
  | 'in_progress'
  | 'completed'

export type LessonSuggestedSectionPlanItemsResponse = {
  suggested: LessonSectionPlanItem | null
  previously_completed: LessonSectionPlanItem | null
}

/**
 * * `beginner` - Beginner
 * * `intermediate` - Intermediate
 * * `advanced` - Advanced
 * * `` - Not provided
 */
export type LevelEnum = 'beginner' | 'intermediate' | 'advanced'

export type LinkWithQRCode = {
  id: string
  /**
   * @format uri
   * @maxLength 200
   */
  url: string
}

export type LinkWithQRCodeRequest = {
  /**
   * @format uri
   * @minLength 1
   * @maxLength 200
   */
  url: string
}

export type Location = {
  id: number
  /** @maxLength 128 */
  name: string
  is_academic_location?: boolean
  house: SimpleHouse
}

export type LocationEC = {
  id: number
  /** @maxLength 128 */
  name: string
  /**
   * @min 0
   * @max 2147483647
   */
  capacity_for_exam?: number | null
}

export type LogEntry = {
  id: number
  action: string
  request_id: string
  /** @format uuid */
  actor?: string | null
  /** Actor representation */
  actor_repr: string
  str: string
  object_id: string
  /** Object representation */
  object_repr: string
  additional_data?: any
  /** Remote address */
  remote_addr?: string | null
  /** @format date-time */
  timestamp: string
  changes: string
}

export type LogEntryHistory = {
  id: number
  model_verbose_name: string | null
  action: LogEntryHistoryActionEnum | NullEnum | null
  custom_action: CustomActionEnum | NullEnum | null
  custom_action_label: string
  /** @format uuid */
  actor?: string | null
  /** Actor representation */
  actor_repr: string
  object_id: string
  object_repr: string | null
  additional_data: Record<string, string | number>
  /** @format date-time */
  timestamp: string
  changes: Record<string, string>
}

/**
 * * `0` - create
 * * `1` - update
 * * `2` - delete
 */
export type LogEntryHistoryActionEnum = 0 | 1 | 2

export type LoginLinkResponse = {
  token: string
  expiry: string
  user: LoginLinkUser
}

export type LoginLinkUser = {
  /**
   * Email address
   * @format email
   * @maxLength 254
   */
  email: string
  /** @maxLength 128 */
  phone_number: string
}

export type LoginResponse = {
  token: string
  expiry: string
}

export type LoginUserResponse = {
  phone_number: string
  can_login_as_student: boolean
}

export type ManyEnrolmentsToApprovedRequest = {
  enrolments: number[]
}

export type ManyEnrolmentsToRejectedRequest = {
  enrolments: number[]
  /** @minLength 1 */
  rejection_comment: string
}

export type ManyEnrolmentsToRequestedRequest = {
  enrolments: number[]
}

export type ManyStaffUsersClashes = Record<
  string,
  {
    lessons: LessonClashes[]
    exams: ExamClashes[]
    programmes: ProgrammeClashes[]
  }
>

export type ManyStudentsClashes = Record<
  string,
  {
    lessons: LessonClashes[]
    exams: ExamClashes[]
    programmes: ProgrammeClashes[]
    leave_requests: LeaveRequestClashes[]
  }
>

export type MarkHomeworkAsSentStudent = {
  is_marked_as_sent_by_student?: boolean
}

export type MarkHomeworkAsSentStudentRequest = {
  is_marked_as_sent_by_student?: boolean
}

export type MaxSaldoStatusEnum = 5 | 10 | 15 | 17 | 20

export type MealAttendanceResponse = {
  breakfast: DinningAndSickMealAttendance
  lunch: DinningAndSickMealAttendance
  dinner: DinningAndSickMealAttendance
  is_academic_saturday: boolean
  /** @format date-time */
  last_update: string
}

export type MealAttendanceStudent = {
  name: string
  classification: DietTypeEnum
  /** @default "student" */
  role: string
}

/**
 * * `Brunch` - Brunch
 * * `Dinner` - Dinner
 */
export type MealLabelEnum = 'Brunch' | 'Dinner'

export type MealPreferenceChoice = {
  /**
   * * `l1` - L1, Regular and vegetarian. Students can choose themselves.
   * * `l2` - L2, Specific, individual requirements. Kitchen adapts the existing menu to specific needs.
   * * `l3` - L3, Specific, very individual requirements. Kitchen creates new menu to specific needs, almost every meal has to be cooked individually.
   * * `l4` - L4, Highly specific. Severe allergic reaction to traces of certain food. Meals need to be ordered from outside supplier.
   */
  value: DietTypeEnum
  label: string
  description: string
  tooltip: string
  price_info: string
}

export type MealPreferenceCount = {
  /** @min 0 */
  l1: number
  /** @min 0 */
  l2: number
  /** @min 0 */
  l3: number
  /** @min 0 */
  l4: number
  users: MealAttendanceStudent[]
}

/**
 * * `breakfast` - Brunch
 * * `dinner` - Dinner
 */
export type MealTypeEnum = 'breakfast' | 'dinner'

export type MedicalReportAttachment = {
  /** @min 1 */
  id: number
  /** @format uri */
  file: string
  file_name: string
}

export type MedicalReportAttachmentRequest = {
  /** @min 1 */
  id: number
}

export type Medicine = {
  id: number
  created_by?: string
  /** @maxLength 255 */
  name: string
  prescription?: boolean
  tags: string
}

export type MedicineGiven = {
  /** @format uuid */
  id: string
  /** @format uuid */
  health_check: string
  /** @format uuid */
  scheduled_medicine?: string | null
  optional_medicine?: string | null
}

export type MedicineGivenRequest = {
  /** @format uuid */
  health_check: string
  /** @format uuid */
  scheduled_medicine?: string | null
  /** @minLength 1 */
  optional_medicine?: string | null
}

/** Adds nested create feature */
export type MedicineInPlan = {
  id: string
  medicine: number
  medicine_name: string
  medicine_prescription: boolean
  /** @maxLength 255 */
  medicine_strength: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /**
   * * `daily` - Daily
   * * `every_x_days` - Every "X" days
   * * `monthly` - Monthly
   * * `weekly` - Weekly
   */
  repeat_form: RepeatFormEnum
  /**
   * @min 0
   * @max 2147483647
   */
  repeat_every: number
  comment?: string
  intakes?: MedicineIntakePlan[]
  is_optional?: boolean
  repeat_form_label: string
  created_by?: string
  /** @format uuid */
  student: string
  medicine_plan?: string
  /** Is medicine only sick report */
  is_med_only_sick_report: boolean
  is_archived: string
  student_str: string
  is_temporary: boolean
  sick_report_id: string | null
  changelog: MedicineInPlanChangelog[]
}

export type MedicineInPlanAvailableFilters = {
  is_archived: AvailableStringFilter
  is_temporary: AvailableStringFilter
  student: AvailableStringFilter
  house: AvailableStringFilter
}

export type MedicineInPlanChangelog = {
  id: number
  /** @format date-time */
  created: string
  changed_by?: string
  reason_name: string
  reason_label: string
}

export type MedicineInPlanChangelogRequest = {
  changed_by?: string
  /** @minLength 1 */
  reason_name: string
  /** @minLength 1 */
  reason_label: string
}

/** Adds nested create feature */
export type MedicineInPlanRequest = {
  medicine: number
  /**
   * @minLength 1
   * @maxLength 255
   */
  medicine_strength: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /**
   * * `daily` - Daily
   * * `every_x_days` - Every "X" days
   * * `monthly` - Monthly
   * * `weekly` - Weekly
   */
  repeat_form: RepeatFormEnum
  /**
   * @min 0
   * @max 2147483647
   */
  repeat_every: number
  comment?: string
  intakes?: MedicineIntakePlanRequest[]
  is_optional?: boolean
  created_by?: string
  /** @format uuid */
  student: string
  /** @minLength 1 */
  medicine_plan?: string
}

/** Adds update nested feature */
export type MedicineInPlanUpdate = {
  id: string
  medicine: number
  medicine_name: string
  medicine_prescription: boolean
  /** @maxLength 255 */
  medicine_strength: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /**
   * * `daily` - Daily
   * * `every_x_days` - Every "X" days
   * * `monthly` - Monthly
   * * `weekly` - Weekly
   */
  repeat_form: RepeatFormEnum
  /**
   * @min 0
   * @max 2147483647
   */
  repeat_every: number
  comment?: string
  intakes?: MedicineIntakePlan[]
  is_optional?: boolean
}

/** Adds update nested feature */
export type MedicineInPlanUpdateRequest = {
  medicine: number
  /**
   * @minLength 1
   * @maxLength 255
   */
  medicine_strength: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /**
   * * `daily` - Daily
   * * `every_x_days` - Every "X" days
   * * `monthly` - Monthly
   * * `weekly` - Weekly
   */
  repeat_form: RepeatFormEnum
  /**
   * @min 0
   * @max 2147483647
   */
  repeat_every: number
  comment?: string
  intakes?: MedicineIntakePlanRequest[]
  is_optional?: boolean
  change_reason: ChangeReasonEnum
}

export type MedicineIntakePlan = {
  id: string
  medicine_in_plan?: string
  /** @format time */
  intake_time: string
  formatted_intake_time: string
  /**
   * @format decimal
   * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
   */
  dose: string
  /**
   * * `capsule` - capsule
   * * `drop` - drop
   * * `puff` - puff
   * * `sachet` - sachet
   * * `table_spoon` - table spoon
   * * `tablet` - tablet
   * * `tea_spoon` - tea spoon
   * * `finger_tip` - finger tip unit (cream)
   * * `milliliters` - milliliters
   */
  dosage_form: DosageFormEnum
  dosage_form_label: string
}

export type MedicineIntakePlanRequest = {
  /** @minLength 1 */
  medicine_in_plan?: string
  /** @format time */
  intake_time: string
  /**
   * @format decimal
   * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
   */
  dose: string
  /**
   * * `capsule` - capsule
   * * `drop` - drop
   * * `puff` - puff
   * * `sachet` - sachet
   * * `table_spoon` - table spoon
   * * `tablet` - tablet
   * * `tea_spoon` - tea spoon
   * * `finger_tip` - finger tip unit (cream)
   * * `milliliters` - milliliters
   */
  dosage_form: DosageFormEnum
}

/**
 * * `student` - Student (at all times)
 * * `house` - House
 * * `BDT` - BDT
 */
export type MedicineLocationEnum = 'student' | 'house' | 'BDT'

/** Adds nested create feature */
export type MedicinePlan = {
  id: string
  /** @format date-time */
  created: string
  created_by?: string
  /** @format uuid */
  student: string
  medicines: MedicineInPlan[]
  sick_report?: string | null
  is_temporary?: boolean
  /** Is medicine only sick report */
  is_med_only_sick_report?: boolean
}

/** Adds nested create feature */
export type MedicinePlanRequest = {
  created_by?: string
  /** @format uuid */
  student: string
  medicines: MedicineInPlanRequest[]
  /** @minLength 1 */
  sick_report?: string | null
  is_temporary?: boolean
  student_application_medication?: number | null
  /** Is medicine only sick report */
  is_med_only_sick_report?: boolean
}

export type MedicineRequest = {
  created_by?: string
  /**
   * @minLength 1
   * @maxLength 255
   */
  name: string
  prescription?: boolean
}

export type MedicinesInHealthCheck = {
  /** @format uuid */
  id: string
  /** @format date-time */
  should_be_given_at: string
  was_given?: boolean
  /** @format date-time */
  given_at?: string | null
  /** @format uuid */
  given_by?: string | null
  medicine_name: string
  medicine_strength: string
  was_skipped?: boolean
  skip_reason?: SkipReasonEnum | BlankEnum
}

export type MergeErrorResponse = {
  /**
   * * `MERGE_ERROR` - Merge error
   * * `STUDENT_INVALID_STATUS` - Student has invalid status
   * * `UNSUPPORTED_DUPLICATE_TYPE` - Unsupported duplicate type
   */
  error_code: MergeErrorResponseErrorCodeEnum
}

/**
 * * `MERGE_ERROR` - Merge error
 * * `STUDENT_INVALID_STATUS` - Student has invalid status
 * * `UNSUPPORTED_DUPLICATE_TYPE` - Unsupported duplicate type
 */
export type MergeErrorResponseErrorCodeEnum =
  | 'MERGE_ERROR'
  | 'STUDENT_INVALID_STATUS'
  | 'UNSUPPORTED_DUPLICATE_TYPE'

export type MergeRequestRequest = {
  /** @default true */
  commit?: boolean
}

export type MergeResponse = {
  /** @format uuid */
  target: string
  /** @format uuid */
  source: string
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type Merit = {
  id: number
  /** @format uuid */
  student: string
  /** @format uuid */
  course?: string | null
  note: string
  merit_item: number
  merit_item_details: MeritItem
  /** @format uuid */
  author: string | null
  author_str: string
  /** @format date-time */
  created: string
  course_str: string
  lesson_date_str: string
  is_academic_absence_or_lateness_demerit: boolean
  student_str: string
  status: string
  status_display: string
  /**
   * * `merit` - Merit
   * * `demerit` - Demerit
   */
  merit_type: MeritTypeEnum
  /** @format double */
  alcohol_level?: number | null
  /** @format double */
  value?: number
  merit_type_display: string
  reviewed_by_str: string
  escalated_merit_item: MeritItem
  sanction_message: string
  escalated_sanction: NextLastSanctionEnum
  escalated_sanction_display: string
  /** @format date-time */
  reviewed_at: string | null
  house_room?: number | null
  house_room_str: string | null
  source_object_id: string
  is_for_exam_attendance: string
  added_on: string
  rejection_reason: string
  electronics_involved: boolean
  week: number
  iso_year: number
}

export type MeritAvailableFilters = {
  status: AvailableStringFilter
  merit_type: AvailableStringFilter
  escalated_sanction: AvailableStringFilter
  course: AvailableStringFilter
  author: AvailableStringFilter
  student: AvailableStringFilter
  merit_item: MeritItemAvailableStringFilter
  house: AvailableStringFilter
}

export type MeritConfirm = {
  id: number
  sanction_message?: string
  /** @format date */
  suspension_date_from?: string | null
  /** @format date */
  suspension_date_to?: string | null
}

export type MeritConfirmRequest = {
  sanction_message?: string
  /** @format date */
  suspension_date_from?: string | null
  /** @format date */
  suspension_date_to?: string | null
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type MeritItem = {
  id: number
  /**
   * * `respect` - Respect
   * * `community` - Community
   * * `safety` - Safety
   * * `health` - Health
   */
  type: MeritItemTypeEnum
  name: string
  /**
   * * `attitude` - Attitude
   * * `kindness` - Kindness
   * * `service` - Service
   * * `language` - Language
   * * `electronics` - Electronics
   * * `dress` - Dress
   * * `late` - Late
   * * `night` - Night
   * * `room` - Room
   * * `absence` - Absence
   * * `leave_request` - Leave request
   * * `ausgang` - Ausgang
   * * `deception` - Deception
   * * `transport` - Transport
   * * `private_home` - Private Home
   * * `deliveries` - Deliveries
   * * `safety` - Safety
   * * `facilities` - Facilities
   * * `aggression` - Aggression
   * * `recklessness` - Recklessness
   * * `covid` - Covid
   * * `smoking` - Smoking
   * * `alcohol` - Alcohol
   * * `drugs` - Drugs
   * * `possession` - Possession
   * * `reputation` - Reputation
   * * `refusal` - Refusal
   * * `abusive_behaviour` - Abusive Behaviour
   */
  subtype: SubtypeEnum
  subtype_display: string
  /**
   * * `merit` - Merit
   * * `verbal_commendation` - Verbal commendation
   * * `written_commendation` - Written commendation
   * * `certificate` - Certificate
   * * `verbal_warning` - Verbal warning
   * * `written_warning` - Written warning
   * * `ultimatum` - Ultimatum
   * * `suspension` - Suspension
   * * `exclusion` - Exclusion
   */
  sanction: NextLastSanctionEnum
  sanction_display: string
  /** @format double */
  merit_factor?: number
  /**
   * @min -2147483648
   * @max 2147483647
   */
  immediate_downgrade?: ImmediateDowngradeEnum | NullEnum | null
  /**
   * * `week` - Weekly
   * * `quarter` - Quarterly
   * * `semester` - Semesterly
   * * `year` - Yearly
   */
  escalation_expire_after?: EscalationExpireAfterEnum
  immediate_downgrade_display: string
  escalation_expire_after_display: string
  /**
   * * `merit` - Merit
   * * `demerit` - Demerit
   */
  merit_type: MeritTypeEnum
  profiles_that_can_review?: string[]
  profiles_that_can_add?: string[]
  system_generated: boolean
  allow_to_select?: boolean
  all_profiles_can_add?: boolean
  is_automatic_approve?: boolean
  next_merit_item?: number | null
  next_last_sanction?: NextLastSanctionEnum | BlankEnum
  has_grade_based_escalation_factor?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  escalation_factor?: number | null
  /**
   * @min 0
   * @max 2147483647
   */
  pri_escalation_factor?: number | null
  /**
   * @min 0
   * @max 2147483647
   */
  msl_escalation_factor?: number | null
  /**
   * @min 0
   * @max 2147483647
   */
  hsl_escalation_factor?: number | null
  /** @format date */
  due_date: string
}

export type MeritItemAvailableFilters = {
  escalation_expire_after: AvailableStringFilter
  sanction: AvailableStringFilter
}

export type MeritItemAvailableStringFilter = {
  query_param: string
  label: string
  choices: MeritItemStringOption[]
}

export type MeritItemBehaviour = {
  id: number
  name: string
  type: MeritItemTypeEnum
  subtype: SubtypeEnum
  merit_type: MeritTypeEnum
  sanction: NextLastSanctionEnum
  require_alcohol_level: boolean
  profiles_that_can_review: string[]
  profiles_that_can_add: string[]
  system_generated: boolean
  is_automatic_approve: boolean
}

export type MeritItemOptions = {
  types: StringOption[]
  subtypes: StringOption[]
  merit_types: StringOption[]
  profiles: StringOption[]
  sanctions: SanctionsOption
  escalation_expire_after_options: StringOption[]
  categories: IntegerOption[]
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type MeritItemPreview = {
  id: number
  type: MeritItemTypeEnum
  name: string
  subtype: SubtypeEnum
  subtype_display: string
  sanction: NextLastSanctionEnum
  sanction_display: string
  /** @format double */
  merit_factor: number
  immediate_downgrade: ImmediateDowngradeEnum | NullEnum | null
  escalation_expire_after: EscalationExpireAfterEnum
  immediate_downgrade_display: string
  escalation_expire_after_display: string
  merit_type: MeritTypeEnum
  profiles_that_can_review: string[]
  profiles_that_can_add: string[]
  system_generated: boolean
  allow_to_select: boolean
  all_profiles_can_add: boolean
  is_automatic_approve: boolean
  next_merit_item: number | null
  next_last_sanction: NextLastSanctionEnum
  has_grade_based_escalation_factor: boolean
  escalation_factor: number | null
  pri_escalation_factor: number | null
  msl_escalation_factor: number | null
  hsl_escalation_factor: number | null
  /** @format date */
  due_date: string
  /** @format date-time */
  downgrade_due_date?: string
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type MeritItemRequest = {
  /**
   * * `respect` - Respect
   * * `community` - Community
   * * `safety` - Safety
   * * `health` - Health
   */
  type: MeritItemTypeEnum
  /** @minLength 1 */
  name: string
  /**
   * * `attitude` - Attitude
   * * `kindness` - Kindness
   * * `service` - Service
   * * `language` - Language
   * * `electronics` - Electronics
   * * `dress` - Dress
   * * `late` - Late
   * * `night` - Night
   * * `room` - Room
   * * `absence` - Absence
   * * `leave_request` - Leave request
   * * `ausgang` - Ausgang
   * * `deception` - Deception
   * * `transport` - Transport
   * * `private_home` - Private Home
   * * `deliveries` - Deliveries
   * * `safety` - Safety
   * * `facilities` - Facilities
   * * `aggression` - Aggression
   * * `recklessness` - Recklessness
   * * `covid` - Covid
   * * `smoking` - Smoking
   * * `alcohol` - Alcohol
   * * `drugs` - Drugs
   * * `possession` - Possession
   * * `reputation` - Reputation
   * * `refusal` - Refusal
   * * `abusive_behaviour` - Abusive Behaviour
   */
  subtype: SubtypeEnum
  /**
   * * `merit` - Merit
   * * `verbal_commendation` - Verbal commendation
   * * `written_commendation` - Written commendation
   * * `certificate` - Certificate
   * * `verbal_warning` - Verbal warning
   * * `written_warning` - Written warning
   * * `ultimatum` - Ultimatum
   * * `suspension` - Suspension
   * * `exclusion` - Exclusion
   */
  sanction: NextLastSanctionEnum
  /** @format double */
  merit_factor?: number
  /**
   * @min -2147483648
   * @max 2147483647
   */
  immediate_downgrade?: ImmediateDowngradeEnum | NullEnum | null
  /**
   * * `week` - Weekly
   * * `quarter` - Quarterly
   * * `semester` - Semesterly
   * * `year` - Yearly
   */
  escalation_expire_after?: EscalationExpireAfterEnum
  /**
   * * `merit` - Merit
   * * `demerit` - Demerit
   */
  merit_type: MeritTypeEnum
  profiles_that_can_review?: string[]
  profiles_that_can_add?: string[]
  allow_to_select?: boolean
  all_profiles_can_add?: boolean
  is_automatic_approve?: boolean
  next_merit_item?: number | null
  next_last_sanction?: NextLastSanctionEnum | BlankEnum
  has_grade_based_escalation_factor?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  escalation_factor?: number | null
  /**
   * @min 0
   * @max 2147483647
   */
  pri_escalation_factor?: number | null
  /**
   * @min 0
   * @max 2147483647
   */
  msl_escalation_factor?: number | null
  /**
   * @min 0
   * @max 2147483647
   */
  hsl_escalation_factor?: number | null
}

export type MeritItemStringOption = {
  label: string
  value: string
  type: string
}

/**
 * * `respect` - Respect
 * * `community` - Community
 * * `safety` - Safety
 * * `health` - Health
 */
export type MeritItemTypeEnum = 'respect' | 'community' | 'safety' | 'health'

export type MeritLastSanctionPreview = {
  sanction: string
  sanction_display: string
  profiles_that_can_review: string[]
  profiles_that_can_add: string[]
}

export type MeritReject = {
  rejection_reason: string
}

export type MeritRejectRequest = {
  /** @minLength 1 */
  rejection_reason: string
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type MeritRequest = {
  /** @format uuid */
  student: string
  /** @format uuid */
  course?: string | null
  /** @minLength 1 */
  note: string
  merit_item: number
  /**
   * * `merit` - Merit
   * * `demerit` - Demerit
   */
  merit_type: MeritTypeEnum
  /** @format double */
  alcohol_level?: number | null
  /** @format double */
  value?: number
  house_room?: number | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type MeritRewardApprove = {
  /** Adds nested create feature */
  campus_absence?: CampusAbsence
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type MeritRewardApproveRequest = {
  /** Adds nested create feature */
  campus_absence?: CampusAbsenceRequest
}

export type MeritRewardParticipant = {
  id: number
  /** @format uuid */
  student: string
  student_data: StudentSimple
  homework_status: string
  category: string
  category_week_ago: string
  ausgangs_this_week: string
}

export type MeritRewardParticipantUpdate = {
  /**
   * * `rejected` - rejected
   * * `accepted` - accepted
   */
  status: MeritRewardParticipantUpdateStatusEnum
  cost: number
  /** @format double */
  merit_points: number
}

export type MeritRewardParticipantUpdateRequest = {
  /**
   * * `rejected` - rejected
   * * `accepted` - accepted
   */
  status: MeritRewardParticipantUpdateStatusEnum
}

/**
 * * `rejected` - rejected
 * * `accepted` - accepted
 */
export type MeritRewardParticipantUpdateStatusEnum = 'rejected' | 'accepted'

export type MeritRewardReject = {
  decision_comment: string
}

export type MeritRewardRejectRequest = {
  /** @minLength 1 */
  decision_comment: string
}

/**
 * * `CasualAnyday` - CasualAnyday
 * * `RoomService` - RoomService
 * * `BookTheCafe` - BookTheCafe
 * * `CityBrunchZurich` - CityBrunchZurich
 * * `AdditionalAusgang` - AdditionalAusgang
 * * `CategoryUpgrade` - CategoryUpgrade
 * * `StopTheClock` - StopTheClock
 * * `MakeAnImpact` - MakeAnImpact
 * * `OnCampusAusgang` - OnCampusAusgang
 */
export type MeritRewardResourcetypeEnum =
  | 'CasualAnyday'
  | 'RoomService'
  | 'BookTheCafe'
  | 'CityBrunchZurich'
  | 'AdditionalAusgang'
  | 'CategoryUpgrade'
  | 'StopTheClock'
  | 'MakeAnImpact'
  | 'OnCampusAusgang'

export type MeritRewardStatusCounts = {
  all: number
  pending: number
  pending_attendees: number
  approved: number
  rejected: number
  done: number
  cancelled: number
  expired: number
}

/**
 * * `pending` - Pending
 * * `pending_attendees` - Pending attendees
 * * `approved` - Approved
 * * `rejected` - Rejected
 * * `done` - Done
 * * `cancelled` - Cancelled
 * * `expired` - Expired
 */
export type MeritRewardStatusEnum =
  | 'pending'
  | 'pending_attendees'
  | 'approved'
  | 'rejected'
  | 'done'
  | 'cancelled'
  | 'expired'

export type MeritSanctionPreview = MeritItemPreview | MeritLastSanctionPreview

export type MeritStatistics = {
  merits_count: number
  demerits_count: number
  /**
   * * `4` - S
   * * `3` - E
   * * `2` - A
   * * `1` - C
   * * `0` - not assigned
   */
  current_category?: CurrentCategoryEnum
  /**
   * * `4` - S
   * * `3` - E
   * * `2` - A
   * * `1` - C
   * * `0` - not assigned
   */
  potential_category?: PotentialCategoryEnum
}

/**
 * * `merit` - Merit
 * * `demerit` - Demerit
 */
export type MeritTypeEnum = 'merit' | 'demerit'

export type MeritsExchangeAusgangsThisWeek = {
  ausgangs: Record<string, number>
  count: number
}

export type MeritsExchangeAvailableStudents = {
  /** @format uuid */
  value: string
  label: string
  homework_status: string | null
  category: number | null
  category_week_ago: number | null
  ausgangs_this_week: MeritsExchangeAusgangsThisWeek
}

export type MeritsRewardMakeAnImpactSummary = {
  donated_merits: number
  /** @format double */
  donation_total: number
}

export type MessageResponseSchema = {
  message: string
}

/**
 * * `alert` - Alert
 * * `notice` - Notice
 */
export type MessageTypeEnum = 'alert' | 'notice'

export type MissingNumberOfLessonsInCourse = {
  id: string
  name: string
  code: string
}

export type MissingNumberOfLessonsResponseSchema = {
  ccu: MissingNumberOfLessonsInCourse[]
  ath: MissingNumberOfLessonsInCourse[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type MissionControl = {
  id: number
  current_aim_str: string
  level_str: string
  degree_aim_1: DegreeAim
  degree_aim_1_id?: number | null
  degree_destination_1: NationalityEnum | BlankEnum
  degree_aim_2: DegreeAim
  degree_aim_2_id?: number | null
  degree_destination_2: NationalityEnum | BlankEnum
  estimated_completion_display: string
  years_remaining: string
  /** Deprecated, replaced with career_aim */
  career_goal: string
  interests_objs: Interest[]
  interests: number[]
  skills_objs: Skill[]
  skills: number[]
  competencies_objs: Competency[]
  competencies: number[]
  saa_student: string
  is_statemented: string
  exam_extra_time: string
  access_arrangements: string
  age: string
  /** @format date */
  estimated_completion?: string | null
  is_estimated_completion_edited: boolean
  /** Career Aim (Goal) */
  career_aim?: string
  recommendations?: string
  /** @format uuid */
  student: string
}

/**
 * * `ENG` - ENG
 * * `MAT` - MAT
 * * `SCI` - SCI
 * * `MLA` - MLA
 * * `SSD` - SSD
 * * `ART` - ART
 */
export type MissionControlDepartmentEnum =
  | 'ENG'
  | 'MAT'
  | 'SCI'
  | 'MLA'
  | 'SSD'
  | 'ART'

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type MissionControlRequest = {
  degree_aim_1_id?: number | null
  degree_destination_1: NationalityEnum | BlankEnum
  degree_aim_2_id?: number | null
  degree_destination_2: NationalityEnum | BlankEnum
  interests: number[]
  skills: number[]
  competencies: number[]
  /** @format date */
  estimated_completion?: string | null
  /** Career Aim (Goal) */
  career_aim?: string
  recommendations?: string
  /** @format uuid */
  student: string
}

export type ModeratedGradeFeedback = {
  rejection_reason: string
  feedback?: string | null
  /**
   * * `approval` - Approval
   * * `rejection` - Rejection
   */
  feedback_type: FeedbackTypeEnum
  author: SimpleUser
  /** @format date-time */
  created: string
  student: SimpleUser
  moderated_grade: number
}

/**
 * * `awaiting_approval` - Awaiting approval
 * * `approved` - Approved
 * * `rejected` - Rejected
 */
export type ModeratedGradeStatusEnum =
  | 'awaiting_approval'
  | 'approved'
  | 'rejected'

/**
 * * `1` - students
 * * `2` - crm
 * * `3` - contacts_crm
 * * `4` - contacts_students
 * * `5` - companies
 */
export type ModuleEnum = 1 | 2 | 3 | 4 | 5

export type MultipleExam = {
  ids: string[]
}

export type MultipleExamRequest = {
  ids: string[]
}

/**
 * * `SAT_MAT` - SAT MAT
 * * `SAT_ENG` - SAT ENG
 * * `IELTS` - IELTS
 * * `WIDA` - WIDA
 * * `MAP` - MAP
 */
export type NameEnum = 'SAT_MAT' | 'SAT_ENG' | 'IELTS' | 'WIDA' | 'MAP'

/**
 * * `AF` - Afghanistan
 * * `AX` - Åland Islands
 * * `AL` - Albania
 * * `DZ` - Algeria
 * * `AS` - American Samoa
 * * `AD` - Andorra
 * * `AO` - Angola
 * * `AI` - Anguilla
 * * `AQ` - Antarctica
 * * `AG` - Antigua and Barbuda
 * * `AR` - Argentina
 * * `AM` - Armenia
 * * `AW` - Aruba
 * * `AU` - Australia
 * * `AT` - Austria
 * * `AZ` - Azerbaijan
 * * `BS` - Bahamas
 * * `BH` - Bahrain
 * * `BD` - Bangladesh
 * * `BB` - Barbados
 * * `BY` - Belarus
 * * `BE` - Belgium
 * * `BZ` - Belize
 * * `BJ` - Benin
 * * `BM` - Bermuda
 * * `BT` - Bhutan
 * * `BO` - Bolivia
 * * `BQ` - Bonaire, Sint Eustatius and Saba
 * * `BA` - Bosnia and Herzegovina
 * * `BW` - Botswana
 * * `BV` - Bouvet Island
 * * `BR` - Brazil
 * * `IO` - British Indian Ocean Territory
 * * `BN` - Brunei
 * * `BG` - Bulgaria
 * * `BF` - Burkina Faso
 * * `BI` - Burundi
 * * `CV` - Cabo Verde
 * * `KH` - Cambodia
 * * `CM` - Cameroon
 * * `CA` - Canada
 * * `KY` - Cayman Islands
 * * `CF` - Central African Republic
 * * `TD` - Chad
 * * `CL` - Chile
 * * `CN` - China
 * * `CX` - Christmas Island
 * * `CC` - Cocos (Keeling) Islands
 * * `CO` - Colombia
 * * `KM` - Comoros
 * * `CG` - Congo
 * * `CD` - Congo (the Democratic Republic of the)
 * * `CK` - Cook Islands
 * * `CR` - Costa Rica
 * * `CI` - Côte d'Ivoire
 * * `HR` - Croatia
 * * `CU` - Cuba
 * * `CW` - Curaçao
 * * `CY` - Cyprus
 * * `CZ` - Czechia
 * * `DK` - Denmark
 * * `DJ` - Djibouti
 * * `DM` - Dominica
 * * `DO` - Dominican Republic
 * * `EC` - Ecuador
 * * `EG` - Egypt
 * * `SV` - El Salvador
 * * `GQ` - Equatorial Guinea
 * * `ER` - Eritrea
 * * `EE` - Estonia
 * * `SZ` - Eswatini
 * * `ET` - Ethiopia
 * * `FK` - Falkland Islands (Malvinas)
 * * `FO` - Faroe Islands
 * * `FJ` - Fiji
 * * `FI` - Finland
 * * `FR` - France
 * * `GF` - French Guiana
 * * `PF` - French Polynesia
 * * `TF` - French Southern Territories
 * * `GA` - Gabon
 * * `GM` - Gambia
 * * `GE` - Georgia
 * * `DE` - Germany
 * * `GH` - Ghana
 * * `GI` - Gibraltar
 * * `GR` - Greece
 * * `GL` - Greenland
 * * `GD` - Grenada
 * * `GP` - Guadeloupe
 * * `GU` - Guam
 * * `GT` - Guatemala
 * * `GG` - Guernsey
 * * `GN` - Guinea
 * * `GW` - Guinea-Bissau
 * * `GY` - Guyana
 * * `HT` - Haiti
 * * `HM` - Heard Island and McDonald Islands
 * * `VA` - Holy See
 * * `HN` - Honduras
 * * `HK` - Hong Kong
 * * `HU` - Hungary
 * * `IS` - Iceland
 * * `IN` - India
 * * `ID` - Indonesia
 * * `IR` - Iran
 * * `IQ` - Iraq
 * * `IE` - Ireland
 * * `IM` - Isle of Man
 * * `IL` - Israel
 * * `IT` - Italy
 * * `JM` - Jamaica
 * * `JP` - Japan
 * * `JE` - Jersey
 * * `JO` - Jordan
 * * `KZ` - Kazakhstan
 * * `KE` - Kenya
 * * `KI` - Kiribati
 * * `XK` - Kosovo
 * * `KW` - Kuwait
 * * `KG` - Kyrgyzstan
 * * `LA` - Laos
 * * `LV` - Latvia
 * * `LB` - Lebanon
 * * `LS` - Lesotho
 * * `LR` - Liberia
 * * `LY` - Libya
 * * `LI` - Liechtenstein
 * * `LT` - Lithuania
 * * `LU` - Luxembourg
 * * `MO` - Macao
 * * `MG` - Madagascar
 * * `MW` - Malawi
 * * `MY` - Malaysia
 * * `MV` - Maldives
 * * `ML` - Mali
 * * `MT` - Malta
 * * `MH` - Marshall Islands
 * * `MQ` - Martinique
 * * `MR` - Mauritania
 * * `MU` - Mauritius
 * * `YT` - Mayotte
 * * `MX` - Mexico
 * * `FM` - Micronesia
 * * `MD` - Moldova
 * * `MC` - Monaco
 * * `MN` - Mongolia
 * * `ME` - Montenegro
 * * `MS` - Montserrat
 * * `MA` - Morocco
 * * `MZ` - Mozambique
 * * `MM` - Myanmar
 * * `NA` - Namibia
 * * `NR` - Nauru
 * * `NP` - Nepal
 * * `NL` - Netherlands
 * * `NC` - New Caledonia
 * * `NZ` - New Zealand
 * * `NI` - Nicaragua
 * * `NE` - Niger
 * * `NG` - Nigeria
 * * `NU` - Niue
 * * `NF` - Norfolk Island
 * * `KP` - North Korea
 * * `MK` - North Macedonia
 * * `MP` - Northern Mariana Islands
 * * `NO` - Norway
 * * `OM` - Oman
 * * `PK` - Pakistan
 * * `PW` - Palau
 * * `PS` - Palestine, State of
 * * `PA` - Panama
 * * `PG` - Papua New Guinea
 * * `PY` - Paraguay
 * * `PE` - Peru
 * * `PH` - Philippines
 * * `PN` - Pitcairn
 * * `PL` - Poland
 * * `PT` - Portugal
 * * `PR` - Puerto Rico
 * * `QA` - Qatar
 * * `RE` - Réunion
 * * `RO` - Romania
 * * `RU` - Russia
 * * `RW` - Rwanda
 * * `BL` - Saint Barthélemy
 * * `SH` - Saint Helena, Ascension and Tristan da Cunha
 * * `KN` - Saint Kitts and Nevis
 * * `LC` - Saint Lucia
 * * `MF` - Saint Martin (French part)
 * * `PM` - Saint Pierre and Miquelon
 * * `VC` - Saint Vincent and the Grenadines
 * * `WS` - Samoa
 * * `SM` - San Marino
 * * `ST` - Sao Tome and Principe
 * * `SA` - Saudi Arabia
 * * `SN` - Senegal
 * * `RS` - Serbia
 * * `SC` - Seychelles
 * * `SL` - Sierra Leone
 * * `SG` - Singapore
 * * `SX` - Sint Maarten (Dutch part)
 * * `SK` - Slovakia
 * * `SI` - Slovenia
 * * `SB` - Solomon Islands
 * * `SO` - Somalia
 * * `ZA` - South Africa
 * * `GS` - South Georgia and the South Sandwich Islands
 * * `KR` - South Korea
 * * `SS` - South Sudan
 * * `ES` - Spain
 * * `LK` - Sri Lanka
 * * `SD` - Sudan
 * * `SR` - Suriname
 * * `SJ` - Svalbard and Jan Mayen
 * * `SE` - Sweden
 * * `CH` - Switzerland
 * * `SY` - Syria
 * * `TW` - Taiwan
 * * `TJ` - Tajikistan
 * * `TZ` - Tanzania
 * * `TH` - Thailand
 * * `TL` - Timor-Leste
 * * `TG` - Togo
 * * `TK` - Tokelau
 * * `TO` - Tonga
 * * `TT` - Trinidad and Tobago
 * * `TN` - Tunisia
 * * `TR` - Türkiye
 * * `TM` - Turkmenistan
 * * `TC` - Turks and Caicos Islands
 * * `TV` - Tuvalu
 * * `UG` - Uganda
 * * `UA` - Ukraine
 * * `AE` - United Arab Emirates
 * * `GB` - United Kingdom
 * * `UM` - United States Minor Outlying Islands
 * * `US` - United States of America
 * * `UY` - Uruguay
 * * `UZ` - Uzbekistan
 * * `VU` - Vanuatu
 * * `VE` - Venezuela
 * * `VN` - Vietnam
 * * `VG` - Virgin Islands (British)
 * * `VI` - Virgin Islands (U.S.)
 * * `WF` - Wallis and Futuna
 * * `EH` - Western Sahara
 * * `YE` - Yemen
 * * `ZM` - Zambia
 * * `ZW` - Zimbabwe
 */
export type NationalityEnum =
  | 'AF'
  | 'AX'
  | 'AL'
  | 'DZ'
  | 'AS'
  | 'AD'
  | 'AO'
  | 'AI'
  | 'AQ'
  | 'AG'
  | 'AR'
  | 'AM'
  | 'AW'
  | 'AU'
  | 'AT'
  | 'AZ'
  | 'BS'
  | 'BH'
  | 'BD'
  | 'BB'
  | 'BY'
  | 'BE'
  | 'BZ'
  | 'BJ'
  | 'BM'
  | 'BT'
  | 'BO'
  | 'BQ'
  | 'BA'
  | 'BW'
  | 'BV'
  | 'BR'
  | 'IO'
  | 'BN'
  | 'BG'
  | 'BF'
  | 'BI'
  | 'CV'
  | 'KH'
  | 'CM'
  | 'CA'
  | 'KY'
  | 'CF'
  | 'TD'
  | 'CL'
  | 'CN'
  | 'CX'
  | 'CC'
  | 'CO'
  | 'KM'
  | 'CG'
  | 'CD'
  | 'CK'
  | 'CR'
  | 'CI'
  | 'HR'
  | 'CU'
  | 'CW'
  | 'CY'
  | 'CZ'
  | 'DK'
  | 'DJ'
  | 'DM'
  | 'DO'
  | 'EC'
  | 'EG'
  | 'SV'
  | 'GQ'
  | 'ER'
  | 'EE'
  | 'SZ'
  | 'ET'
  | 'FK'
  | 'FO'
  | 'FJ'
  | 'FI'
  | 'FR'
  | 'GF'
  | 'PF'
  | 'TF'
  | 'GA'
  | 'GM'
  | 'GE'
  | 'DE'
  | 'GH'
  | 'GI'
  | 'GR'
  | 'GL'
  | 'GD'
  | 'GP'
  | 'GU'
  | 'GT'
  | 'GG'
  | 'GN'
  | 'GW'
  | 'GY'
  | 'HT'
  | 'HM'
  | 'VA'
  | 'HN'
  | 'HK'
  | 'HU'
  | 'IS'
  | 'IN'
  | 'ID'
  | 'IR'
  | 'IQ'
  | 'IE'
  | 'IM'
  | 'IL'
  | 'IT'
  | 'JM'
  | 'JP'
  | 'JE'
  | 'JO'
  | 'KZ'
  | 'KE'
  | 'KI'
  | 'XK'
  | 'KW'
  | 'KG'
  | 'LA'
  | 'LV'
  | 'LB'
  | 'LS'
  | 'LR'
  | 'LY'
  | 'LI'
  | 'LT'
  | 'LU'
  | 'MO'
  | 'MG'
  | 'MW'
  | 'MY'
  | 'MV'
  | 'ML'
  | 'MT'
  | 'MH'
  | 'MQ'
  | 'MR'
  | 'MU'
  | 'YT'
  | 'MX'
  | 'FM'
  | 'MD'
  | 'MC'
  | 'MN'
  | 'ME'
  | 'MS'
  | 'MA'
  | 'MZ'
  | 'MM'
  | 'NA'
  | 'NR'
  | 'NP'
  | 'NL'
  | 'NC'
  | 'NZ'
  | 'NI'
  | 'NE'
  | 'NG'
  | 'NU'
  | 'NF'
  | 'KP'
  | 'MK'
  | 'MP'
  | 'NO'
  | 'OM'
  | 'PK'
  | 'PW'
  | 'PS'
  | 'PA'
  | 'PG'
  | 'PY'
  | 'PE'
  | 'PH'
  | 'PN'
  | 'PL'
  | 'PT'
  | 'PR'
  | 'QA'
  | 'RE'
  | 'RO'
  | 'RU'
  | 'RW'
  | 'BL'
  | 'SH'
  | 'KN'
  | 'LC'
  | 'MF'
  | 'PM'
  | 'VC'
  | 'WS'
  | 'SM'
  | 'ST'
  | 'SA'
  | 'SN'
  | 'RS'
  | 'SC'
  | 'SL'
  | 'SG'
  | 'SX'
  | 'SK'
  | 'SI'
  | 'SB'
  | 'SO'
  | 'ZA'
  | 'GS'
  | 'KR'
  | 'SS'
  | 'ES'
  | 'LK'
  | 'SD'
  | 'SR'
  | 'SJ'
  | 'SE'
  | 'CH'
  | 'SY'
  | 'TW'
  | 'TJ'
  | 'TZ'
  | 'TH'
  | 'TL'
  | 'TG'
  | 'TK'
  | 'TO'
  | 'TT'
  | 'TN'
  | 'TR'
  | 'TM'
  | 'TC'
  | 'TV'
  | 'UG'
  | 'UA'
  | 'AE'
  | 'GB'
  | 'UM'
  | 'US'
  | 'UY'
  | 'UZ'
  | 'VU'
  | 'VE'
  | 'VN'
  | 'VG'
  | 'VI'
  | 'WF'
  | 'EH'
  | 'YE'
  | 'ZM'
  | 'ZW'

/**
 * * `aa` - Afar
 * * `ab` - Abkhaz
 * * `ae` - Avestan
 * * `af` - Afrikaans
 * * `ak` - Akan
 * * `am` - Amharic
 * * `an` - Aragonese
 * * `ar` - Arabic
 * * `as` - Assamese
 * * `av` - Avaric
 * * `ay` - Aymara
 * * `az` - Azerbaijani
 * * `ba` - Bashkir
 * * `be` - Belarusian
 * * `bg` - Bulgarian
 * * `bi` - Bislama
 * * `bm` - Bambara
 * * `bn` - Bengali
 * * `bo` - Tibetan
 * * `br` - Breton
 * * `bs` - Bosnian
 * * `ca` - Catalan
 * * `ce` - Chechen
 * * `ch` - Chamorro
 * * `co` - Corsican
 * * `cr` - Cree
 * * `cs` - Czech
 * * `cu` - Old Church Slavonic
 * * `cv` - Chuvash
 * * `cy` - Welsh
 * * `da` - Danish
 * * `de` - German
 * * `dv` - Divehi
 * * `dz` - Dzongkha
 * * `ee` - Ewe
 * * `el` - Greek
 * * `en` - English
 * * `eo` - Esperanto
 * * `es` - Spanish
 * * `et` - Estonian
 * * `eu` - Basque
 * * `fa` - Persian
 * * `ff` - Fula
 * * `fi` - Finnish
 * * `fj` - Fijian
 * * `fo` - Faroese
 * * `fr` - French
 * * `fy` - Western Frisian
 * * `ga` - Irish
 * * `gd` - Scottish Gaelic
 * * `gl` - Galician
 * * `gn` - Guaraní
 * * `gu` - Gujarati
 * * `gv` - Manx
 * * `ha` - Hausa
 * * `he` - Hebrew
 * * `hi` - Hindi
 * * `ho` - Hiri Motu
 * * `hr` - Croatian
 * * `ht` - Haitian
 * * `hu` - Hungarian
 * * `hy` - Armenian
 * * `hz` - Herero
 * * `ia` - Interlingua
 * * `id` - Indonesian
 * * `ie` - Interlingue
 * * `ig` - Igbo
 * * `ii` - Nuosu
 * * `ik` - Inupiaq
 * * `io` - Ido
 * * `is` - Icelandic
 * * `it` - Italian
 * * `iu` - Inuktitut
 * * `ja` - Japanese
 * * `jv` - Javanese
 * * `ka` - Georgian
 * * `kg` - Kongo
 * * `ki` - Kikuyu
 * * `kj` - Kwanyama
 * * `kk` - Kazakh
 * * `kl` - Kalaallisut
 * * `km` - Khmer
 * * `kn` - Kannada
 * * `ko` - Korean
 * * `kr` - Kanuri
 * * `ks` - Kashmiri
 * * `ku` - Kurdish
 * * `kv` - Komi
 * * `kw` - Cornish
 * * `ky` - Kyrgyz
 * * `la` - Latin
 * * `lb` - Luxembourgish
 * * `lg` - Ganda
 * * `li` - Limburgish
 * * `ln` - Lingala
 * * `lo` - Lao
 * * `lt` - Lithuanian
 * * `lu` - Luba-Katanga
 * * `lv` - Latvian
 * * `mg` - Malagasy
 * * `mh` - Marshallese
 * * `mi` - Māori
 * * `mk` - Macedonian
 * * `ml` - Malayalam
 * * `mn` - Mongolian
 * * `mr` - Marathi
 * * `ms` - Malay
 * * `mt` - Maltese
 * * `my` - Burmese
 * * `na` - Nauru
 * * `nb` - Norwegian Bokmål
 * * `nd` - Northern Ndebele
 * * `ne` - Nepali
 * * `ng` - Ndonga
 * * `nl` - Dutch
 * * `nn` - Norwegian Nynorsk
 * * `no` - Norwegian
 * * `nr` - Southern Ndebele
 * * `nv` - Navajo
 * * `ny` - Chichewa
 * * `oc` - Occitan
 * * `oj` - Ojibwe
 * * `om` - Oromo
 * * `or` - Oriya
 * * `os` - Ossetian
 * * `pa` - Panjabi
 * * `pi` - Pāli
 * * `pl` - Polish
 * * `ps` - Pashto
 * * `pt` - Portuguese
 * * `qu` - Quechua
 * * `rm` - Romansh
 * * `rn` - Kirundi
 * * `ro` - Romanian
 * * `ru` - Russian
 * * `rw` - Kinyarwanda
 * * `sa` - Sanskrit
 * * `sc` - Sardinian
 * * `sd` - Sindhi
 * * `se` - Northern Sami
 * * `sg` - Sango
 * * `si` - Sinhala
 * * `sk` - Slovak
 * * `sl` - Slovenian
 * * `sm` - Samoan
 * * `sn` - Shona
 * * `so` - Somali
 * * `sq` - Albanian
 * * `sr` - Serbian
 * * `ss` - Swati
 * * `st` - Southern Sotho
 * * `su` - Sundanese
 * * `sv` - Swedish
 * * `sw` - Swahili
 * * `ta` - Tamil
 * * `te` - Telugu
 * * `tg` - Tajik
 * * `th` - Thai
 * * `ti` - Tigrinya
 * * `tk` - Turkmen
 * * `tl` - Tagalog
 * * `tn` - Tswana
 * * `to` - Tonga
 * * `tr` - Turkish
 * * `ts` - Tsonga
 * * `tt` - Tatar
 * * `tw` - Twi
 * * `ty` - Tahitian
 * * `ug` - Uyghur
 * * `uk` - Ukrainian
 * * `ur` - Urdu
 * * `uz` - Uzbek
 * * `ve` - Venda
 * * `vi` - Vietnamese
 * * `vo` - Volapük
 * * `wa` - Walloon
 * * `wo` - Wolof
 * * `xh` - Xhosa
 * * `yi` - Yiddish
 * * `yo` - Yoruba
 * * `za` - Zhuang
 * * `zh` - Chinese
 * * `zu` - Zulu
 */
export type NativeLanguageEnum =
  | 'aa'
  | 'ab'
  | 'ae'
  | 'af'
  | 'ak'
  | 'am'
  | 'an'
  | 'ar'
  | 'as'
  | 'av'
  | 'ay'
  | 'az'
  | 'ba'
  | 'be'
  | 'bg'
  | 'bi'
  | 'bm'
  | 'bn'
  | 'bo'
  | 'br'
  | 'bs'
  | 'ca'
  | 'ce'
  | 'ch'
  | 'co'
  | 'cr'
  | 'cs'
  | 'cu'
  | 'cv'
  | 'cy'
  | 'da'
  | 'de'
  | 'dv'
  | 'dz'
  | 'ee'
  | 'el'
  | 'en'
  | 'eo'
  | 'es'
  | 'et'
  | 'eu'
  | 'fa'
  | 'ff'
  | 'fi'
  | 'fj'
  | 'fo'
  | 'fr'
  | 'fy'
  | 'ga'
  | 'gd'
  | 'gl'
  | 'gn'
  | 'gu'
  | 'gv'
  | 'ha'
  | 'he'
  | 'hi'
  | 'ho'
  | 'hr'
  | 'ht'
  | 'hu'
  | 'hy'
  | 'hz'
  | 'ia'
  | 'id'
  | 'ie'
  | 'ig'
  | 'ii'
  | 'ik'
  | 'io'
  | 'is'
  | 'it'
  | 'iu'
  | 'ja'
  | 'jv'
  | 'ka'
  | 'kg'
  | 'ki'
  | 'kj'
  | 'kk'
  | 'kl'
  | 'km'
  | 'kn'
  | 'ko'
  | 'kr'
  | 'ks'
  | 'ku'
  | 'kv'
  | 'kw'
  | 'ky'
  | 'la'
  | 'lb'
  | 'lg'
  | 'li'
  | 'ln'
  | 'lo'
  | 'lt'
  | 'lu'
  | 'lv'
  | 'mg'
  | 'mh'
  | 'mi'
  | 'mk'
  | 'ml'
  | 'mn'
  | 'mr'
  | 'ms'
  | 'mt'
  | 'my'
  | 'na'
  | 'nb'
  | 'nd'
  | 'ne'
  | 'ng'
  | 'nl'
  | 'nn'
  | 'no'
  | 'nr'
  | 'nv'
  | 'ny'
  | 'oc'
  | 'oj'
  | 'om'
  | 'or'
  | 'os'
  | 'pa'
  | 'pi'
  | 'pl'
  | 'ps'
  | 'pt'
  | 'qu'
  | 'rm'
  | 'rn'
  | 'ro'
  | 'ru'
  | 'rw'
  | 'sa'
  | 'sc'
  | 'sd'
  | 'se'
  | 'sg'
  | 'si'
  | 'sk'
  | 'sl'
  | 'sm'
  | 'sn'
  | 'so'
  | 'sq'
  | 'sr'
  | 'ss'
  | 'st'
  | 'su'
  | 'sv'
  | 'sw'
  | 'ta'
  | 'te'
  | 'tg'
  | 'th'
  | 'ti'
  | 'tk'
  | 'tl'
  | 'tn'
  | 'to'
  | 'tr'
  | 'ts'
  | 'tt'
  | 'tw'
  | 'ty'
  | 'ug'
  | 'uk'
  | 'ur'
  | 'uz'
  | 've'
  | 'vi'
  | 'vo'
  | 'wa'
  | 'wo'
  | 'xh'
  | 'yi'
  | 'yo'
  | 'za'
  | 'zh'
  | 'zu'

export type NewLaundryFormQRCodeResolveResponse = {
  /** * `new_laundry_form` - new_laundry_form */
  view_type: ViewTypeEnum
  data: FormQRCodeResolveData
}

export type NewStudentsBannerResponse = {
  /** @format date */
  date_of_entry: string
  is_imported: boolean
  students: string[]
}

export type NewStudentsCountResponse = {
  new_students_count: number
}

/**
 * * `cold` - Cold
 * * `flu` - Flu
 * * `stomach` - Stomach
 * * `allergy` - Allergy
 * * `teeth` - Teeth
 * * `headache` - Headache
 * * `fatigue` - Fatigue
 * * `nausea` - Nausea
 * * `menstruation_pain` - Menstruation pain
 * * `fever` - Fever
 * * `covid_isolation` - Covid - isolation
 * * `covid_quarantine` - Covid - quarantine
 * * `other` - Other
 */
export type NewSymptomsEnum =
  | 'cold'
  | 'flu'
  | 'stomach'
  | 'allergy'
  | 'teeth'
  | 'headache'
  | 'fatigue'
  | 'nausea'
  | 'menstruation_pain'
  | 'fever'
  | 'covid_isolation'
  | 'covid_quarantine'
  | 'other'

/**
 * * `merit` - Merit
 * * `verbal_commendation` - Verbal commendation
 * * `written_commendation` - Written commendation
 * * `certificate` - Certificate
 * * `verbal_warning` - Verbal warning
 * * `written_warning` - Written warning
 * * `ultimatum` - Ultimatum
 * * `suspension` - Suspension
 * * `exclusion` - Exclusion
 */
export type NextLastSanctionEnum =
  | 'merit'
  | 'verbal_commendation'
  | 'written_commendation'
  | 'certificate'
  | 'verbal_warning'
  | 'written_warning'
  | 'ultimatum'
  | 'suspension'
  | 'exclusion'

export type NightAttendance = {
  /** @format uuid */
  id: string
  mark?: NightAttendanceMarkEnum | BlankEnum
  student_str: string
  /** @format uuid */
  student: string
  house_name: string
  room_id: string
  room_name: string
  has_comment: string
  is_at_home?: boolean
  is_at_campus_absence?: boolean
  /** @format time */
  bedtime?: string | null
  merit_note: string
  merit_electronics_involved: string
  is_marking_disabled: string
  boarding_status?: string
  is_room_replacement: string
}

export type NightAttendanceComment = {
  /** @format uuid */
  id: string
  author?: string
  author_str: string
  content: string
  /** @format date-time */
  modified: string
}

export type NightAttendanceCommentRequest = {
  author?: string
  /** @minLength 1 */
  content: string
  mark_disturbance?: boolean
}

/**
 * * `present` - Present
 * * `disturbance` - Disturbance
 * * `absent` - Absent
 */
export type NightAttendanceMarkEnum = 'present' | 'disturbance' | 'absent'

export type NightAttendanceReport = {
  id: number
  /** @format date-time */
  created: string
  status: BoardingAttendanceReportStatusEnum
  night_guard?: string
  night_guard_str: string
  /**
   * * `male` - Male
   * * `female` - Female
   */
  for_gender: ForGenderEnum
  for_gender_display: string
  status_display: string
  has_disruption?: boolean
  next_report: AdjacentNightAttendanceReport | null
  previous_report: AdjacentNightAttendanceReport | null
}

export type NightAttendanceReportList = {
  id: number
  /** @format date-time */
  created: string
  status: BoardingAttendanceReportStatusEnum
  night_guard_str: string
  /**
   * * `male` - Male
   * * `female` - Female
   */
  for_gender?: ForGenderEnum
  for_gender_display: string
  status_display: string
  has_disruption?: boolean
}

export type NightAttendanceReportRequest = {
  night_guard?: string
  /**
   * * `male` - Male
   * * `female` - Female
   */
  for_gender: ForGenderEnum
  has_disruption?: boolean
}

export type NightAttendanceRequest = {
  mark?: NightAttendanceMarkEnum | BlankEnum
  /** @format uuid */
  student: string
  /** @minLength 1 */
  disturbance_note: string
  electronics_involved: boolean
  is_at_home?: boolean
  is_at_campus_absence?: boolean
  /** @format time */
  bedtime?: string | null
  /** @minLength 1 */
  boarding_status?: string
}

/** Collects and raises validation errors in bulk */
export type NightGuardProfile = {
  id: string
  /** @format uuid */
  user?: string | null
}

/** Collects and raises validation errors in bulk */
export type NightGuardProfileRequest = {
  /** @format uuid */
  user?: string | null
}

export type NoCommonCoursesForCopyError = {
  code: string
  detail: string
  differences: CopySectionsDifference[]
  sections_summary: CopySectionsSummary
}

export type NoDraftSectionsForQuarterError = {
  code: string
  detail: string
}

export type NoEligibleEnrolmentsError = {
  code: string
  detail: string
}

/**
 * * `academic` - Academic
 * * `administrative` - Administrative
 * * `admission` - Admission
 * * `archived` - Archived
 * * `boarding` - Boarding
 * * `medical` - Medical
 * * `other` - Other
 */
export type NoteTypeEnum =
  | 'academic'
  | 'administrative'
  | 'admission'
  | 'archived'
  | 'boarding'
  | 'medical'
  | 'other'

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type Notification = {
  /** @format uuid */
  id: string
  body: string
  was_read: boolean
  sender: string
  notification_type: NotificationTypeEnum
  notification_type_display: string
  related_model: string | null
  related_object_id: string | null
  notification_class_name: NotificationClassNameEnum
  sent_at: string
  /** @format date-time */
  created: string
  extra_data: any
}

export type NotificationClassName = {
  /** @maxItems 20 */
  notification_class_name: NotificationClassNameEnum[]
}

/**
 * * `NewMeritNotification` - NewMeritNotification
 * * `NewDemeritNotification` - NewDemeritNotification
 * * `MeritDeescalatedNotification` - MeritDeescalatedNotification
 * * `DemeritDeescalatedNotification` - DemeritDeescalatedNotification
 * * `CategoryDecreasedNotification` - CategoryDecreasedNotification
 * * `CategoryIncreasedNotification` - CategoryIncreasedNotification
 * * `CampusAbsenceAcceptedNotification` - CampusAbsenceAcceptedNotification
 * * `CampusAbsenceRejectedNotification` - CampusAbsenceRejectedNotification
 * * `CampusAbsenceRejectedByRecoveryNotification` - CampusAbsenceRejectedByRecoveryNotification
 * * `LeaveRequestCreatedByTheSchoolNotification` - LeaveRequestCreatedByTheSchoolNotification
 * * `LeaveRequestParentAcceptNotification` - LeaveRequestParentAcceptNotification
 * * `LeaveRequestCampusAcceptNotification` - LeaveRequestCampusAcceptNotification
 * * `LeaveRequestParentRejectedNotification` - LeaveRequestParentRejectedNotification
 * * `LeaveRequestCampusRejectedNotification` - LeaveRequestCampusRejectedNotification
 * * `LeaveRequestExpiredNotification` - LeaveRequestExpiredNotification
 * * `ParentNewLeaveRequestNotification` - ParentNewLeaveRequestNotification
 * * `ParentLeaveRequestRejectedNotification` - ParentLeaveRequestRejectedNotification
 * * `ParentLeaveRequestApprovedNotification` - ParentLeaveRequestApprovedNotification
 * * `ParentLeaveRequestCancelledNotification` - ParentLeaveRequestCancelledNotification
 * * `ParentLeaveRequestEditedNotification` - ParentLeaveRequestEditedNotification
 * * `ParentLeaveRequestExpiredNotification` - ParentLeaveRequestExpiredNotification
 * * `SchoolBreakNotification` - SchoolBreakNotification
 * * `ProgrammeEnrolmentApprovedNotification` - ProgrammeEnrolmentApprovedNotification
 * * `ProgrammeEnrolmentApprovedInCompulsoryProgrammeNotification` - ProgrammeEnrolmentApprovedInCompulsoryProgrammeNotification
 * * `ProgrammeEnrolmentRequestedNotification` - ProgrammeEnrolmentRequestedNotification
 * * `ProgrammeEnrolmentRejectedNotification` - ProgrammeEnrolmentRejectedNotification
 * * `OptionalSchoolProgrammeApprovedNotification` - OptionalSchoolProgrammeApprovedNotification
 * * `SchoolProgrammeRejectedNotification` - SchoolProgrammeRejectedNotification
 * * `RecoveryEnrolmentNotification` - RecoveryEnrolmentNotification
 * * `StopTheClockApprovedNotification` - StopTheClockApprovedNotification
 * * `StopTheClockRejectedNotification` - StopTheClockRejectedNotification
 * * `StopTheClockRequestedNotification` - StopTheClockRequestedNotification
 * * `CasualAnydayApprovedNotification` - CasualAnydayApprovedNotification
 * * `CasualAnydayRejectedNotification` - CasualAnydayRejectedNotification
 * * `RoomServiceApprovedNotification` - RoomServiceApprovedNotification
 * * `RoomServiceRejectedNotification` - RoomServiceRejectedNotification
 * * `BookTheCafeApprovedNotification` - BookTheCafeApprovedNotification
 * * `BookTheCafeRejectedNotification` - BookTheCafeRejectedNotification
 * * `CityBrunchZurichApprovedNotification` - CityBrunchZurichApprovedNotification
 * * `CityBrunchZurichRejectedNotification` - CityBrunchZurichRejectedNotification
 * * `AdditionalAusgangApprovedNotification` - AdditionalAusgangApprovedNotification
 * * `AdditionalAusgangRejectedNotification` - AdditionalAusgangRejectedNotification
 * * `OnCampusAusgangApprovedNotification` - OnCampusAusgangApprovedNotification
 * * `OnCampusAusgangRejectedNotification` - OnCampusAusgangRejectedNotification
 * * `MeritRewardInvitationNotification` - MeritRewardInvitationNotification
 * * `MeritRewardInvitationReminderNotification` - MeritRewardInvitationReminderNotification
 * * `MeritRewardInvitationAcceptedNotification` - MeritRewardInvitationAcceptedNotification
 * * `MeritRewardInvitationRejectedNotification` - MeritRewardInvitationRejectedNotification
 * * `MeritRewardReviewNotification` - MeritRewardReviewNotification
 * * `MeritRewardNotEnoughParticipantsNotification` - MeritRewardNotEnoughParticipantsNotification
 * * `MeritRewardCancelledNotification` - MeritRewardCancelledNotification
 * * `StudentAddedToCampusAbsenceNotification` - StudentAddedToCampusAbsenceNotification
 * * `StudentRemovedFromCampusAbsenceNotification` - StudentRemovedFromCampusAbsenceNotification
 * * `CampusAbsenceEditedNotification` - CampusAbsenceEditedNotification
 * * `DoctorsAppointmentScheduledNotification` - DoctorsAppointmentScheduledNotification
 * * `DoctorsAppointmentCancelledNotification` - DoctorsAppointmentCancelledNotification
 * * `DoctorsAppointmentReScheduledNotification` - DoctorsAppointmentReScheduledNotification
 * * `StudentLaundryEditedNotification` - StudentLaundryEditedNotification
 * * `StudentContentApprovedNotification` - StudentContentApprovedNotification
 * * `StudentContentRejectedNotification` - StudentContentRejectedNotification
 * * `StudentContentRemovedNotification` - StudentContentRemovedNotification
 * * `NewHomeworkNotification` - NewHomeworkNotification
 * * `HomeworkReviewedNotification` - HomeworkReviewedNotification
 * * `AssessmentReviewedNotification` - AssessmentReviewedNotification
 * * `TeacherCoverNotification` - TeacherCoverNotification
 * * `NewTimetableNotification` - NewTimetableNotification
 * * `ExamSessionNewExamsNotification` - ExamSessionNewExamsNotification
 * * `ExamSessionClassCancelledNotification` - ExamSessionClassCancelledNotification
 * * `ExamSessionExamsChangedNotification` - ExamSessionExamsChangedNotification
 * * `ParentNewIdpPlanNotification` - ParentNewIdpPlanNotification
 * * `ParentIdpPlanApprovedNotification` - ParentIdpPlanApprovedNotification
 * * `ParentNewReportCardNotification` - ParentNewReportCardNotification
 * * `CloseHomeworkDeadlineNotification` - CloseHomeworkDeadlineNotification
 * * `ParentLeaveRequestReminderNotification` - ParentLeaveRequestReminderNotification
 * * `ParentTAEStudentEnrolmentPreviewAvailableNotification` - ParentTAEStudentEnrolmentPreviewAvailableNotification
 * * `ParentTAEStudentEnrolmentSelectionsConfirmedNotification` - ParentTAEStudentEnrolmentSelectionsConfirmedNotification
 * * `ParentTAEStudentEnrolmentSelectionsUpdatedNotification` - ParentTAEStudentEnrolmentSelectionsUpdatedNotification
 * * `ParentTAEStudentEnrolmentEditedNotification` - ParentTAEStudentEnrolmentEditedNotification
 * * `TAEEnrolmentAvailableNotification` - TAEEnrolmentAvailableNotification
 * * `TAEEnrolmentSignUpAvailableNotification` - TAEEnrolmentSignUpAvailableNotification
 * * `ParentTAEEnrolmentAvailableNotification` - ParentTAEEnrolmentAvailableNotification
 * * `ParentTAEEnrolmentSignUpAvailableNotification` - ParentTAEEnrolmentSignUpAvailableNotification
 * * `TAEEnrolmentOneWeekToDeadlineNotification` - TAEEnrolmentOneWeekToDeadlineNotification
 * * `TAEEnrolmentTodayIsDeadlineNotification` - TAEEnrolmentTodayIsDeadlineNotification
 * * `TAEEnrolmentSummaryAvailableNotification` - TAEEnrolmentSummaryAvailableNotification
 * * `ParentTAEEnrolmentSummaryAvailableNotification` - ParentTAEEnrolmentSummaryAvailableNotification
 * * `TAEEnrolmentReopenedNotification` - TAEEnrolmentReopenedNotification
 * * `TAEEnrolmentCourseDeletedNotification` - TAEEnrolmentCourseDeletedNotification
 * * `TAEEnrolmentReopenClosedNotification` - TAEEnrolmentReopenClosedNotification
 * * `TAEStudentEnrolmentReopenedNotification` - TAEStudentEnrolmentReopenedNotification
 * * `TAEStudentEnrolmentReopenClosedNotification` - TAEStudentEnrolmentReopenClosedNotification
 * * `TAEEnrolmentDeadlineExtendedNotification` - TAEEnrolmentDeadlineExtendedNotification
 * * `TAEEnrolmentSignUpOneWeekToDeadlineNotification` - TAEEnrolmentSignUpOneWeekToDeadlineNotification
 * * `TAEEnrolmentSignUpTodayIsDeadlineNotification` - TAEEnrolmentSignUpTodayIsDeadlineNotification
 * * `TAEEnrolmentSignUpDeadlineExtendedNotification` - TAEEnrolmentSignUpDeadlineExtendedNotification
 * * `TAEStudentEnrolmentReplacementRequiredNotification` - TAEStudentEnrolmentReplacementRequiredNotification
 * * `TAEStudentEnrolmentSummaryAvailableNotification` - TAEStudentEnrolmentSummaryAvailableNotification
 * * `ParentTAEStudentEnrolmentSummaryAvailableNotification` - ParentTAEStudentEnrolmentSummaryAvailableNotification
 * * `StudentMealAttendanceFormNotification` - StudentMealAttendanceFormNotification
 * * `StudentMealAttendanceFormReminderNotification` - StudentMealAttendanceFormReminderNotification
 * * `StudentNewDocumentNotification` - StudentNewDocumentNotification
 * * `ParentNewDocumentNotification` - ParentNewDocumentNotification
 * * `RecurringProgrammeSessionApprovedNotification` - RecurringProgrammeSessionApprovedNotification
 * * `RecurringProgrammeSessionRejectedNotification` - RecurringProgrammeSessionRejectedNotification
 * * `RecurringProgrammeMultipleSessionsApprovedNotification` - RecurringProgrammeMultipleSessionsApprovedNotification
 * * `RecurringProgrammeMultipleSessionsRejectedNotification` - RecurringProgrammeMultipleSessionsRejectedNotification
 * * `RecurringProgrammeAllSessionsApprovedNotification` - RecurringProgrammeAllSessionsApprovedNotification
 * * `RecurringProgrammeAllSessionsRejectedNotification` - RecurringProgrammeAllSessionsRejectedNotification
 * * `OptionalRecurringProgrammeApprovedNotification` - OptionalRecurringProgrammeApprovedNotification
 * * `CompulsoryRecurringProgrammeApprovedNotification` - CompulsoryRecurringProgrammeApprovedNotification
 * * `SchoolProgrammeCancelledNotification` - SchoolProgrammeCancelledNotification
 */
export type NotificationClassNameEnum =
  | 'NewMeritNotification'
  | 'NewDemeritNotification'
  | 'MeritDeescalatedNotification'
  | 'DemeritDeescalatedNotification'
  | 'CategoryDecreasedNotification'
  | 'CategoryIncreasedNotification'
  | 'CampusAbsenceAcceptedNotification'
  | 'CampusAbsenceRejectedNotification'
  | 'CampusAbsenceRejectedByRecoveryNotification'
  | 'LeaveRequestCreatedByTheSchoolNotification'
  | 'LeaveRequestParentAcceptNotification'
  | 'LeaveRequestCampusAcceptNotification'
  | 'LeaveRequestParentRejectedNotification'
  | 'LeaveRequestCampusRejectedNotification'
  | 'LeaveRequestExpiredNotification'
  | 'ParentNewLeaveRequestNotification'
  | 'ParentLeaveRequestRejectedNotification'
  | 'ParentLeaveRequestApprovedNotification'
  | 'ParentLeaveRequestCancelledNotification'
  | 'ParentLeaveRequestEditedNotification'
  | 'ParentLeaveRequestExpiredNotification'
  | 'SchoolBreakNotification'
  | 'ProgrammeEnrolmentApprovedNotification'
  | 'ProgrammeEnrolmentApprovedInCompulsoryProgrammeNotification'
  | 'ProgrammeEnrolmentRequestedNotification'
  | 'ProgrammeEnrolmentRejectedNotification'
  | 'OptionalSchoolProgrammeApprovedNotification'
  | 'SchoolProgrammeRejectedNotification'
  | 'RecoveryEnrolmentNotification'
  | 'StopTheClockApprovedNotification'
  | 'StopTheClockRejectedNotification'
  | 'StopTheClockRequestedNotification'
  | 'CasualAnydayApprovedNotification'
  | 'CasualAnydayRejectedNotification'
  | 'RoomServiceApprovedNotification'
  | 'RoomServiceRejectedNotification'
  | 'BookTheCafeApprovedNotification'
  | 'BookTheCafeRejectedNotification'
  | 'CityBrunchZurichApprovedNotification'
  | 'CityBrunchZurichRejectedNotification'
  | 'AdditionalAusgangApprovedNotification'
  | 'AdditionalAusgangRejectedNotification'
  | 'OnCampusAusgangApprovedNotification'
  | 'OnCampusAusgangRejectedNotification'
  | 'MeritRewardInvitationNotification'
  | 'MeritRewardInvitationReminderNotification'
  | 'MeritRewardInvitationAcceptedNotification'
  | 'MeritRewardInvitationRejectedNotification'
  | 'MeritRewardReviewNotification'
  | 'MeritRewardNotEnoughParticipantsNotification'
  | 'MeritRewardCancelledNotification'
  | 'StudentAddedToCampusAbsenceNotification'
  | 'StudentRemovedFromCampusAbsenceNotification'
  | 'CampusAbsenceEditedNotification'
  | 'DoctorsAppointmentScheduledNotification'
  | 'DoctorsAppointmentCancelledNotification'
  | 'DoctorsAppointmentReScheduledNotification'
  | 'StudentLaundryEditedNotification'
  | 'StudentContentApprovedNotification'
  | 'StudentContentRejectedNotification'
  | 'StudentContentRemovedNotification'
  | 'NewHomeworkNotification'
  | 'HomeworkReviewedNotification'
  | 'AssessmentReviewedNotification'
  | 'TeacherCoverNotification'
  | 'NewTimetableNotification'
  | 'ExamSessionNewExamsNotification'
  | 'ExamSessionClassCancelledNotification'
  | 'ExamSessionExamsChangedNotification'
  | 'ParentNewIdpPlanNotification'
  | 'ParentIdpPlanApprovedNotification'
  | 'ParentNewReportCardNotification'
  | 'CloseHomeworkDeadlineNotification'
  | 'ParentLeaveRequestReminderNotification'
  | 'ParentTAEStudentEnrolmentPreviewAvailableNotification'
  | 'ParentTAEStudentEnrolmentSelectionsConfirmedNotification'
  | 'ParentTAEStudentEnrolmentSelectionsUpdatedNotification'
  | 'ParentTAEStudentEnrolmentEditedNotification'
  | 'TAEEnrolmentAvailableNotification'
  | 'TAEEnrolmentSignUpAvailableNotification'
  | 'ParentTAEEnrolmentAvailableNotification'
  | 'ParentTAEEnrolmentSignUpAvailableNotification'
  | 'TAEEnrolmentOneWeekToDeadlineNotification'
  | 'TAEEnrolmentTodayIsDeadlineNotification'
  | 'TAEEnrolmentSummaryAvailableNotification'
  | 'ParentTAEEnrolmentSummaryAvailableNotification'
  | 'TAEEnrolmentReopenedNotification'
  | 'TAEEnrolmentCourseDeletedNotification'
  | 'TAEEnrolmentReopenClosedNotification'
  | 'TAEStudentEnrolmentReopenedNotification'
  | 'TAEStudentEnrolmentReopenClosedNotification'
  | 'TAEEnrolmentDeadlineExtendedNotification'
  | 'TAEEnrolmentSignUpOneWeekToDeadlineNotification'
  | 'TAEEnrolmentSignUpTodayIsDeadlineNotification'
  | 'TAEEnrolmentSignUpDeadlineExtendedNotification'
  | 'TAEStudentEnrolmentReplacementRequiredNotification'
  | 'TAEStudentEnrolmentSummaryAvailableNotification'
  | 'ParentTAEStudentEnrolmentSummaryAvailableNotification'
  | 'StudentMealAttendanceFormNotification'
  | 'StudentMealAttendanceFormReminderNotification'
  | 'StudentNewDocumentNotification'
  | 'ParentNewDocumentNotification'
  | 'RecurringProgrammeSessionApprovedNotification'
  | 'RecurringProgrammeSessionRejectedNotification'
  | 'RecurringProgrammeMultipleSessionsApprovedNotification'
  | 'RecurringProgrammeMultipleSessionsRejectedNotification'
  | 'RecurringProgrammeAllSessionsApprovedNotification'
  | 'RecurringProgrammeAllSessionsRejectedNotification'
  | 'OptionalRecurringProgrammeApprovedNotification'
  | 'CompulsoryRecurringProgrammeApprovedNotification'
  | 'SchoolProgrammeCancelledNotification'

export type NotificationClassNameRequest = {
  /** @maxItems 20 */
  notification_class_name: NotificationClassNameEnum[]
}

export type NotificationSettings = {
  accept_campus_notifications?: boolean
  accept_education_notifications?: boolean
  accept_reminders_notifications?: boolean
  accept_dashboard_notifications?: boolean
}

export type NotificationSettingsRequest = {
  accept_campus_notifications?: boolean
  accept_education_notifications?: boolean
  accept_reminders_notifications?: boolean
  accept_dashboard_notifications?: boolean
}

/**
 * * `campus` - Campus
 * * `academic` - Academic
 * * `education` - Education
 * * `reminders` - Reminders
 * * `dashboard` - Dashboard
 */
export type NotificationTypeEnum =
  | 'campus'
  | 'academic'
  | 'education'
  | 'reminders'
  | 'dashboard'

export type NullEnum = null

export type ObjectTypeEnum = 'leave_request' | 'campus_absence'

/**
 * * `open_enrolment` - Open enrolment
 * * `last_chance` - Last chance
 * * `course_deleted` - Course deleted
 */
export type OpenStateEnum = 'open_enrolment' | 'last_chance' | 'course_deleted'

export type OptionalMedicinesInHealthCheck = {
  id: string
  medicine_name: string
  /** @maxLength 255 */
  medicine_strength: string
  medicine?: number | null
  medicine_plan: string
}

export type PackageEnrolmentSource = {
  id: number
  tae_enrolment: number
  hf_membership_count: number
}

export type PaginatedAIAssistantAccGradeList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AIAssistantAccGrade[]
  page_size: number
  current: number
}

export type PaginatedAIAssistantAssessmentResultList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AIAssistantAssessmentResult[]
  page_size: number
  current: number
}

export type PaginatedAIAssistantAssignmentItemList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AIAssistantAssignmentItem[]
  page_size: number
  current: number
}

export type PaginatedAIAssistantAttendanceRecordList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AIAssistantAttendanceRecord[]
  page_size: number
  current: number
}

export type PaginatedAIAssistantAttendanceSummaryList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AIAssistantAttendanceSummary[]
  page_size: number
  current: number
  /** @format date */
  lesson_date_after: string
  /** @format date */
  lesson_date_before: string
  unavailable_students: string[]
}

export type PaginatedAIAssistantCockpitCoursePlanList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AIAssistantCockpitCoursePlan[]
  page_size: number
  current: number
}

export type PaginatedAIAssistantCockpitSubtopicList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AIAssistantCockpitSubtopic[]
  page_size: number
  current: number
}

export type PaginatedAIAssistantDegreeAimList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AIAssistantDegreeAim[]
  page_size: number
  current: number
}

export type PaginatedAIAssistantExamAccessArrangementsList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AIAssistantExamAccessArrangements[]
  page_size: number
  current: number
}

export type PaginatedAIAssistantExamSittingList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AIAssistantExamSitting[]
  page_size: number
  current: number
}

export type PaginatedAIAssistantExternalExamList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AIAssistantExternalExam[]
  page_size: number
  current: number
}

export type PaginatedAIAssistantStudentDetailList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AIAssistantStudentDetail[]
  page_size: number
  current: number
}

export type PaginatedAIAssistantStudentGPAList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AIAssistantStudentGPA[]
  page_size: number
  current: number
  school_year: AIAssistantGPASchoolYear | null
}

export type PaginatedAIAssistantStudentGoalsList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AIAssistantStudentGoals[]
  page_size: number
  current: number
}

export type PaginatedAIAssistantStudentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AIAssistantStudent[]
  page_size: number
  current: number
}

export type PaginatedAIAssistantStudentWhereaboutsList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AIAssistantStudentWhereabouts[]
  page_size: number
  current: number
  counts: {
    /** Students per value; values with no students are omitted. */
    campus_status: {
      'on campus'?: number
      'off campus'?: number
    }
    /** Students per value; values with no students are omitted. */
    campus_status_reason: {
      'leave request'?: number
      'school programme'?: number
      'campus absence'?: number
      'absent excused'?: number
      'absent unexcused'?: number
      sick?: number
    }
    /** Students per value; values with no students are omitted. */
    boarding_status: {
      'at home'?: number
      'regular on board'?: number
      'temporary movement'?: number
      day?: number
      'campus absence'?: number
      appointment?: number
    }
    sick_at_home: number
  }
  /** False when the user sees only some active students, so counts are not school-wide. */
  covers_all_students: boolean
}

export type PaginatedAIAssistantTeacherList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AIAssistantTeacher[]
  page_size: number
  current: number
}

export type PaginatedAIAssistantWeeklyCategoryList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AIAssistantWeeklyCategory[]
  page_size: number
  current: number
}

export type PaginatedAbacusInvoiceProductList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AbacusInvoiceProduct[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAbsenceList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Absence[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAcademicAttendanceAlertList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AcademicAttendanceAlert[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAcademicAttendanceList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AcademicAttendance[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAccGradeList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AccGrade[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAccommodationList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Accommodation[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAchievementHolderList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AchievementHolder[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAchievementList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Achievement[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAdminBoardDraftListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AdminBoardDraftList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAdminBoardList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AdminBoard[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAdminCeleryTaskStatusListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AdminCeleryTaskStatusList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAdminFeedList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AdminFeed[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAdminMeritRewardList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AdminMeritReward[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAdminMessageList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AdminMessage[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAdminRecoveryList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AdminRecovery[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAdminSchoolYearList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AdminSchoolYear[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAdminStudentContentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AdminStudentContent[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAdminTeacherList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AdminTeacher[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAdminTimetableLessonList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AdminTimetableLesson[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAdminUserListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AdminUserList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAdminWidgetList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AdminWidget[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAimList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Aim[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAllergyList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Allergy[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAssignmentGradeList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AssignmentGrade[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAssignmentModeratedGradeListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AssignmentModeratedGradeList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAssignmentWithSubtopicNamesListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AssignmentWithSubtopicNamesList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAttachmentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Attachment[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedAvailableStudentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: AvailableStudent[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedBillingReportList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: BillingReport[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedBoardingAttendanceReportListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: BoardingAttendanceReportList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedBoardingCommentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: BoardingComment[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCalendarEventList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: CalendarEvent[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCampusAbsenceListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: CampusAbsenceList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCancelLessonList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: CancelLesson[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCandidateProfileListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: CandidateProfileList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCatalogList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Catalog[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedChronicConditionList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: ChronicCondition[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCockpitSectionGroupList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: CockpitSectionGroup[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCommentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Comment[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCompanyList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Company[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCompanyListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: CompanyList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCompetencyList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Competency[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCourseFamilyList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: CourseFamily[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCourseGroupList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: CourseGroup[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCourseIntelligenceList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: CourseIntelligence[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCourseLevelList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: CourseLevel[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCourseListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: CourseList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCourseOverviewList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: CourseOverview[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCoursePlannerListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: CoursePlannerList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCourseSubjectList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: CourseSubject[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedCourseTypeList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: CourseType[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedDayMenuList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: DayMenu[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedDegreeAimList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: DegreeAim[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedDepartmentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Department[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedDoctorList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Doctor[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedDomainList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Domain[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedDraftActivityListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: DraftActivityList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedDraftActivitySuggestionPreviewList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: DraftActivitySuggestionPreview[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedDraftRotaActivityResponseList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: DraftRotaActivityResponse[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedDuplicateRecordListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: DuplicateRecordList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedExamEnrolledStudentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: ExamEnrolledStudent[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedExamList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Exam[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedFavouriteWidgetList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: FavouriteWidget[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedFeedList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Feed[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedFileForStudentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: FileForStudent[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedGenerationTaskList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: GenerationTask[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedGlobalStandardListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: GlobalStandardList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedGradeLevelList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: GradeLevel[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedGroupedExamListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: GroupedExamList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedHealthCheckListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: HealthCheckList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedHospitalList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Hospital[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedHouseListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: HouseList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedHouseRoomListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: HouseRoomList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedImageList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Image[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedInterestList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Interest[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedInternshipList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Internship[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedLaundryOrderListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: LaundryOrderList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedLaundryQRCodeListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: LaundryQRCodeList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedLeaveRequestListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: LeaveRequestList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedLocationList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Location[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedLogEntryHistoryList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: LogEntryHistory[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedLogEntryList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: LogEntry[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedMedicineInPlanList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: MedicineInPlan[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedMedicineList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Medicine[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedMedicinePlanList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: MedicinePlan[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedMeritItemList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: MeritItem[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedMeritList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Merit[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedMissionControlList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: MissionControl[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedNightAttendanceReportListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: NightAttendanceReportList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedNotificationList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Notification[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedParentCoursesList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: ParentCourses[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedParentIdpPlanStatusHistoryList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: ParentIdpPlanStatusHistory[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedParentLeaveRequestList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: ParentLeaveRequest[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedParentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Parent[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedParentMessageList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: ParentMessage[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedParentReportCardList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: ParentReportCard[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedParentTAEParentCommentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: ParentTAEParentComment[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedParentTAEParentCommentV2List = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: ParentTAEParentCommentV2[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedParentsFileList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: ParentsFile[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedPotentialOptimizationList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: PotentialOptimization[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedProgrammeAttendanceReportList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: ProgrammeAttendanceReport[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedProgrammeEnrolmentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: ProgrammeEnrolment[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedProgrammeStudentAttendanceList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: ProgrammeStudentAttendance[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedProgressCommentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: ProgressComment[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedPublicNextLessonList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: PublicNextLesson[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedQuarterList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Quarter[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
  current_period?: Quarter
}

export type PaginatedReportCardList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: ReportCard[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedRequestedCourseList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: RequestedCourse[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedRoomList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Room[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedRotaGenerationListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: RotaGenerationList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedSchoolBreakList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: SchoolBreak[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedSchoolProgrammeListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: SchoolProgrammeList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedSchoolYearList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: SchoolYear[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
  current_period?: SchoolYear
}

export type PaginatedSectionListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: SectionList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedSemesterList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Semester[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
  current_period?: Semester
}

export type PaginatedSickReportListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: SickReportList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedSimpleSectionList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: SimpleSection[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedSkillList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Skill[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStringOptionList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StringOption[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentAcademicAttendanceList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentAcademicAttendance[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentAchievementList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentAchievement[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentCampusAbsenceList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentCampusAbsence[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentCategoryReportListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentCategoryReportList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentContentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentContent[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentCourseRequirementList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentCourseRequirement[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentCoursesList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentCourses[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentDayMenuList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentDayMenu[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentDocumentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentDocument[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentExternalExamList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentExternalExam[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentHomeworkList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentHomework[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentIdpPlanListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentIdpPlanList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentInternshipList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentInternship[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentLaundryOrderListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentLaundryOrderList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentLeaveRequestSwaggerList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentLeaveRequestSwagger[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentMeritListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentMeritList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentMeritRewardList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentMeritReward[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentMessageList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentMessage[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentRecurringProgrammeSessionList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentRecurringProgrammeSession[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentSchoolProgrammeListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentSchoolProgrammeList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentTAEExperienceList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentTAEExperience[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentTAEProjectList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentTAEProject[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentTranscriptList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentTranscript[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedStudentsFileList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: StudentsFile[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTAEAvailableCourseList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TAEAvailableCourse[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTAECourseEnrolmentInDraftSectionsList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TAECourseEnrolmentInDraftSections[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTAECourseEnrolmentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TAECourseEnrolment[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTAEEnrolmentListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TAEEnrolmentList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTAEEnrolmentStudentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TAEEnrolmentStudent[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTAEEnrolmentV2ListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TAEEnrolmentV2List[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTAEExperienceList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TAEExperience[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTAEGeneratorDraftActivityUpdateList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TAEGeneratorDraftActivityUpdate[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
  /** @example 100 */
  total_count: number
}

export type PaginatedTAEPlanList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TAEPlan[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTAEPlanStudentToReviewList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TAEPlanStudentToReview[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTAEStudentEnrolmentListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TAEStudentEnrolmentList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTalentEnrichmentEnrolmentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TalentEnrichmentEnrolment[]
  page_size: number
  current: number
}

export type PaginatedTalentEnrichmentOfferingList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TalentEnrichmentOffering[]
  page_size: number
  current: number
}

export type PaginatedTalentEnrichmentSignupList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TalentEnrichmentSignup[]
  page_size: number
  current: number
}

export type PaginatedTaskList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Task[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTeacherAcademicActivitiesReportPreviewList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TeacherAcademicActivitiesReportPreview[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTeacherBoardingActivitiesReportPreviewList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TeacherBoardingActivitiesReportPreview[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTeacherTimetableListLessonList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TeacherTimetableListLesson[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTeamList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Team[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTimetableList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Timetable[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTimetableLogList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TimetableLog[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTimetableMentoringList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TimetableMentoring[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTopicForGlobalStandardList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TopicForGlobalStandard[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTopicListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: TopicList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedTutorList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: Tutor[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedUserContactsList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: UserContacts[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedUserContactsSimpleListList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: UserContactsSimpleList[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedUserNoteList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: UserNote[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

export type PaginatedWeeklyCommentList = {
  /** @example 123 */
  count: number
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=4"
   */
  next: string | null
  /**
   * @format uri
   * @example "http://api.example.org/accounts/?page=2"
   */
  previous: string | null
  results: WeeklyComment[]
  /** @example 15 */
  page_size: number
  /** @example 1 */
  current: number
}

/**
 * * `no` - No
 * * `current` - Current
 * * `future` - Future
 */
export type PanelStudentEnum = 'no' | 'current' | 'future'

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type Parent = {
  /** @format uuid */
  id: string
  /** @format date-time */
  date_joined: string
  /** @format date-time */
  last_update: string
  /** @maxLength 128 */
  first_name: string
  /** @maxLength 128 */
  last_name: string
  /** @maxLength 128 */
  maiden_name?: string
  full_name: string
  /**
   * Email address
   * @format email
   * @maxLength 254
   */
  email: string
  /**
   * @format email
   * @maxLength 254
   */
  secondary_email?: string
  /** @maxLength 128 */
  phone_number: string
  salutation: string
  /** @maxLength 128 */
  title?: string
  occupation?: string
  current_company?: ParentCompanyInput | null
  current_company_str: string
  /** @maxItems 250 */
  nationalities?: (NationalityEnum | BlankEnum)[]
  native_languages?: NativeLanguageEnum[]
  /**
   * * `parent` - Parent
   * * `legal_guardian` - Legal Guardian
   * * `in_legal_guardian_absence` - In Legal Guardian Absence
   * * `arrival_contact` - Arrival Contact
   * * `educational_consultant` - Educational Consultant
   * * `main_invoice_recipient` - Main Invoice Recipient
   * * `invoice_recipient` - Invoice Recipient
   * * `contact_leave` - Contact Leave
   * * `invoice_copy_recipient` - Invoice Copy Recipient
   * * `assistant` - Assistant
   * * `relative` - Relative
   * * `other` - Other
   */
  contact_type?: ContactTypeEnum
  contact_leave_request?: boolean
  is_allowed_to_visit?: boolean
  is_app_locked: boolean
  /**
   * Active
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  academic_details: ParentAcademicSummary
  contacts: ParentStudentContact[]
  /** @maxLength 2000 */
  crucial_information?: string
}

export type ParentAcademicDetails = {
  is_former_student?: boolean
  status?: FormerStudentStatusEnum | NullEnum | null
  grade_level?: number | null
  grade_level_str: string | null
  /** @format date */
  date_of_entry?: string | null
  /** @format date */
  date_of_depart?: string | null
  /** @format date */
  diploma_date?: string | null
  diploma_type?: DiplomaTypeEnum | NullEnum | null
  member_of_alumni_association?: boolean
  collabs_with_alumni_app?: boolean
}

export type ParentAcademicDetailsRequest = {
  is_former_student?: boolean
  status?: FormerStudentStatusEnum | NullEnum | null
  grade_level?: number | null
  /** @format date */
  date_of_entry?: string | null
  /** @format date */
  date_of_depart?: string | null
  /** @format date */
  diploma_date?: string | null
  diploma_type?: DiplomaTypeEnum | NullEnum | null
  member_of_alumni_association?: boolean
  collabs_with_alumni_app?: boolean
}

export type ParentAcademicSummary = {
  is_former_student: boolean
  status: FormerStudentStatusEnum | NullEnum | null
}

export type ParentAvailableFilters = {
  id: AvailableStringFilter
  student: AvailableStringFilter
  nationalities: AvailableStringFilter
  has_app_access: AvailableBooleanFilter
  has_active_students: AvailableBooleanFilter
  roles: AvailableStringFilter
  current_company: AvailableStringFilter
  former_student_status: AvailableStringFilter
  candidate: AvailableStringFilter
  year_of_rosenberg_entry: AvailableIntegerFilter
}

export type ParentBasket = {
  id: number
  name: string
  courses: ParentCourseInBasket[]
}

export type ParentCompanyInput = {
  /** @format uuid */
  id?: string
  name?: string
}

export type ParentCompanyInputRequest = {
  /** @format uuid */
  id?: string
  /** @minLength 1 */
  name?: string
}

export type ParentContactMessage = {
  author?: string
  /** @format uuid */
  student: string
  message: string
  /**
   * * `academic` - Academic
   * * `accounting` - Accounting
   * * `app_helpdesk` - App Helpdesk
   * * `boarding` - Boarding
   * * `co_curricular` - Co-Curricular
   * * `health` - Health
   * * `idp` - IDP (R) - Individual Development Plan
   * * `leave_request` - Leave request
   * * `meal_plans` - Meal Plans
   * * `other` - Other topics
   * * `rooming` - Rooming
   * * `sport` - Sport
   * * `talent_and_enrichment` - Talent & Enrichment
   * * `university_application` - University Application
   */
  subject: ContactMessageSubjectEnum
}

export type ParentContactMessageRequest = {
  author?: string
  /** @format uuid */
  student: string
  /** @minLength 1 */
  message: string
  /**
   * * `academic` - Academic
   * * `accounting` - Accounting
   * * `app_helpdesk` - App Helpdesk
   * * `boarding` - Boarding
   * * `co_curricular` - Co-Curricular
   * * `health` - Health
   * * `idp` - IDP (R) - Individual Development Plan
   * * `leave_request` - Leave request
   * * `meal_plans` - Meal Plans
   * * `other` - Other topics
   * * `rooming` - Rooming
   * * `sport` - Sport
   * * `talent_and_enrichment` - Talent & Enrichment
   * * `university_application` - University Application
   */
  subject: ContactMessageSubjectEnum
}

export type ParentContactMessageSubjects = {
  label: string
  /**
   * * `academic` - Academic
   * * `accounting` - Accounting
   * * `app_helpdesk` - App Helpdesk
   * * `boarding` - Boarding
   * * `co_curricular` - Co-Curricular
   * * `health` - Health
   * * `idp` - IDP (R) - Individual Development Plan
   * * `leave_request` - Leave request
   * * `meal_plans` - Meal Plans
   * * `other` - Other topics
   * * `rooming` - Rooming
   * * `sport` - Sport
   * * `talent_and_enrichment` - Talent & Enrichment
   * * `university_application` - University Application
   */
  value: ContactMessageSubjectEnum
}

export type ParentCourse = {
  /** @format uuid */
  id: string
  name: string
  code: string
}

export type ParentCourseInBasket = {
  id: number
  course: ParentCourse
  quarters: ParentIdpSectionEnrollment[]
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type ParentCourses = {
  id: string
  name: string
  teacher: UserInCourse
  grades: ParentGrade[]
  current_grade: ParentGrade
  is_private: boolean
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type ParentCreateRequest = {
  /** @maxLength 128 */
  first_name: string
  /** @maxLength 128 */
  last_name: string
  /** @maxLength 128 */
  maiden_name?: string
  /**
   * Email address
   * @format email
   * @minLength 1
   * @maxLength 254
   */
  email: string
  /**
   * @format email
   * @maxLength 254
   */
  secondary_email?: string
  /**
   * @minLength 1
   * @maxLength 128
   */
  phone_number: string
  /** @minLength 1 */
  salutation: string
  /** @maxLength 128 */
  title?: string
  occupation?: string
  current_company?: ParentCompanyInputRequest | null
  /** @maxItems 250 */
  nationalities?: (NationalityEnum | BlankEnum)[]
  native_languages?: NativeLanguageEnum[]
  /**
   * * `parent` - Parent
   * * `legal_guardian` - Legal Guardian
   * * `in_legal_guardian_absence` - In Legal Guardian Absence
   * * `arrival_contact` - Arrival Contact
   * * `educational_consultant` - Educational Consultant
   * * `main_invoice_recipient` - Main Invoice Recipient
   * * `invoice_recipient` - Invoice Recipient
   * * `contact_leave` - Contact Leave
   * * `invoice_copy_recipient` - Invoice Copy Recipient
   * * `assistant` - Assistant
   * * `relative` - Relative
   * * `other` - Other
   */
  contact_type?: ContactTypeEnum
  contact_leave_request?: boolean
  is_allowed_to_visit?: boolean
  /**
   * Active
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  academic_details?: ParentAcademicDetailsRequest
  contacts?: ParentCreateUserRelationRequest[]
  /** @maxLength 2000 */
  crucial_information?: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type ParentCreateResponse = {
  /** @format uuid */
  id: string
  /** @format date-time */
  date_joined: string
  /** @format date-time */
  last_update: string
  /** @maxLength 128 */
  first_name: string
  /** @maxLength 128 */
  last_name: string
  /** @maxLength 128 */
  maiden_name?: string
  full_name: string
  /**
   * Email address
   * @format email
   * @maxLength 254
   */
  email: string
  /**
   * @format email
   * @maxLength 254
   */
  secondary_email?: string
  /** @maxLength 128 */
  phone_number: string
  salutation: string
  /** @maxLength 128 */
  title?: string
  occupation?: string
  current_company?: ParentCompanyInput | null
  current_company_str: string
  /** @maxItems 250 */
  nationalities?: (NationalityEnum | BlankEnum)[]
  native_languages?: NativeLanguageEnum[]
  /**
   * * `parent` - Parent
   * * `legal_guardian` - Legal Guardian
   * * `in_legal_guardian_absence` - In Legal Guardian Absence
   * * `arrival_contact` - Arrival Contact
   * * `educational_consultant` - Educational Consultant
   * * `main_invoice_recipient` - Main Invoice Recipient
   * * `invoice_recipient` - Invoice Recipient
   * * `contact_leave` - Contact Leave
   * * `invoice_copy_recipient` - Invoice Copy Recipient
   * * `assistant` - Assistant
   * * `relative` - Relative
   * * `other` - Other
   */
  contact_type?: ContactTypeEnum
  contact_leave_request?: boolean
  is_allowed_to_visit?: boolean
  is_app_locked: boolean
  /**
   * Active
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  academic_details: ParentAcademicDetails
  contacts: ParentStudentContact[]
  /** @maxLength 2000 */
  crucial_information?: string
}

export type ParentCreateUserRelationRequest = {
  /** @format uuid */
  related_user: string
  /**
   * * `5` - Mother/Father
   * * `10` - Stepmother/Stepfather
   * * `15` - Grandmother/Grandfather
   * * `20` - Aunt/Uncle
   * * `25` - Sister/Brother
   * * `30` - Stepsister/Stepbrother
   * * `35` - Half sister/Half brother
   * * `40` - Daughter/Son
   * * `45` - Stepdaughter/Stepson
   * * `50` - Granddaughter/Grandson
   * * `55` - Niece/Nephew
   * * `60` - Cousin
   * * `65` - Friend
   * * `70` - Other
   */
  relation_type: RelationTypeEnum
  /** Roles for this relation (synced with ParentStudentConnection) */
  roles: ContactTypeEnum[]
  has_app_access?: boolean
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type ParentDetail = {
  /** @format uuid */
  id: string
  /** @format date-time */
  date_joined: string
  /** @format date-time */
  last_update: string
  /** @maxLength 128 */
  first_name: string
  /** @maxLength 128 */
  last_name: string
  /** @maxLength 128 */
  maiden_name?: string
  full_name: string
  /**
   * Email address
   * @format email
   * @maxLength 254
   */
  email: string
  /**
   * @format email
   * @maxLength 254
   */
  secondary_email?: string
  /** @maxLength 128 */
  phone_number: string
  salutation: string
  /** @maxLength 128 */
  title?: string
  occupation?: string
  current_company?: ParentCompanyInput | null
  current_company_str: string
  /** @maxItems 250 */
  nationalities?: (NationalityEnum | BlankEnum)[]
  native_languages?: NativeLanguageEnum[]
  /**
   * * `parent` - Parent
   * * `legal_guardian` - Legal Guardian
   * * `in_legal_guardian_absence` - In Legal Guardian Absence
   * * `arrival_contact` - Arrival Contact
   * * `educational_consultant` - Educational Consultant
   * * `main_invoice_recipient` - Main Invoice Recipient
   * * `invoice_recipient` - Invoice Recipient
   * * `contact_leave` - Contact Leave
   * * `invoice_copy_recipient` - Invoice Copy Recipient
   * * `assistant` - Assistant
   * * `relative` - Relative
   * * `other` - Other
   */
  contact_type?: ContactTypeEnum
  contact_leave_request?: boolean
  is_allowed_to_visit?: boolean
  is_app_locked: boolean
  /**
   * Active
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  academic_details?: ParentAcademicDetails
  contacts: ParentStudentContact[]
  /** @maxLength 2000 */
  crucial_information?: string
  billing_email_address: string
  billing_additional_email_id?: number | null
  inactive_note: UserNote
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type ParentDetailRequest = {
  /** @maxLength 128 */
  first_name: string
  /** @maxLength 128 */
  last_name: string
  /** @maxLength 128 */
  maiden_name?: string
  /**
   * Email address
   * @format email
   * @minLength 1
   * @maxLength 254
   */
  email: string
  /**
   * @format email
   * @maxLength 254
   */
  secondary_email?: string
  /**
   * @minLength 1
   * @maxLength 128
   */
  phone_number: string
  /** @minLength 1 */
  salutation: string
  /** @maxLength 128 */
  title?: string
  occupation?: string
  current_company?: ParentCompanyInputRequest | null
  /** @maxItems 250 */
  nationalities?: (NationalityEnum | BlankEnum)[]
  native_languages?: NativeLanguageEnum[]
  /**
   * * `parent` - Parent
   * * `legal_guardian` - Legal Guardian
   * * `in_legal_guardian_absence` - In Legal Guardian Absence
   * * `arrival_contact` - Arrival Contact
   * * `educational_consultant` - Educational Consultant
   * * `main_invoice_recipient` - Main Invoice Recipient
   * * `invoice_recipient` - Invoice Recipient
   * * `contact_leave` - Contact Leave
   * * `invoice_copy_recipient` - Invoice Copy Recipient
   * * `assistant` - Assistant
   * * `relative` - Relative
   * * `other` - Other
   */
  contact_type?: ContactTypeEnum
  contact_leave_request?: boolean
  is_allowed_to_visit?: boolean
  /**
   * Active
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  academic_details?: ParentAcademicDetailsRequest
  /** @maxLength 2000 */
  crucial_information?: string
  billing_additional_email_id?: number | null
  inactive_note_content?: string
}

export type ParentDiscussIdpPlanRequest = {
  /** @minLength 1 */
  message: string
}

export type ParentGrade = {
  period_short_name: string
  grade: string
  weighted_percents: number | null
  target_grade: string | null
  target_percents: number | null
  /**
   * * `pending` - Pending
   * * `much_above` - Much Above
   * * `above` - Above
   * * `below` - Below
   * * `much_below` - Much Below
   */
  target_grade_status?: TargetGradeStatusEnum
}

export type ParentIdpPlan = {
  id: string
  /** @format uuid */
  student: string
  status: IdpPlanStatusEnum
  status_display: string
  version: number
  /** Last approved version */
  is_active: boolean
  year: SchoolYear
  baskets?: ParentBasket[]
  /** @format date-time */
  parent_approved_at: string | null
  /** @format uuid */
  parent_approve_author: string | null
  parent_approve_author_str: string
  /** @format date */
  parent_approval_deadline: string | null
  /** @format uuid */
  parent_message_author: string | null
  parent_message_author_str: string
  /** @format date-time */
  parent_message_created_at: string | null
  notifications_mark_as_read: number | null
}

export type ParentIdpPlanChanges =
  | IdpPlanChangesAddedAction
  | IdpPlanChangesDeletedAction
  | IdpPlanChangesPrivacyChangedAction
  | IdpPlanChangesLessonsNumberChangedAction
  | IdpPlanChangesCourseDroppedAction

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type ParentIdpPlanStatusHistory = {
  id: number
  status_display: string
  /**
   * * `new` - New
   * * `draft` - Draft
   * * `rejected` - Rejected
   * * `waits_for_admin_approval` - In admin approval
   * * `in_parental_approval` - In parental approval
   * * `is_discussion` - In discussion
   * * `approved` - Approved
   * * `automatically_approved` - Automatically approved
   * * `updated` - Updated
   * * `archived` - Archived
   */
  target_status: IdpPlanStatusEnum
  version: number
  /** @format date-time */
  created: string
  parent_approve_author: string
  parent_approve_author_str: string
  parent_message_author: string
  parent_message_author_str: string
}

export type ParentIdpPlanYears = {
  id: number
  name: string
  has_new_idp_plan: boolean
}

export type ParentIdpSectionEnrollment = {
  id: number
  is_private: boolean
  number_of_lessons: number
  quarter: string
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type ParentLeaveCheck = {
  /** @format uuid */
  id: string
  /** @maxLength 1024 */
  destination: string
  /** @maxLength 256 */
  train?: string
  /** @maxLength 256 */
  plane?: string
  /** @maxLength 256 */
  pick_up_location?: string
  /** @format time */
  pick_up_time?: string | null
  comment?: string
  /** @maxLength 64 */
  contact_person: string
  /** @maxLength 128 */
  contact_person_phone: string
  is_driver_needed?: boolean
  /** @format date-time */
  date: string
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type ParentLeaveRequest = {
  /** @format uuid */
  id: string
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  check_out: ParentLeaveCheck
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  check_in: ParentLeaveCheck
  leave_type: LeaveRequestTypeEnum
  leave_type_label: string
  request_type: RequestTypeEnum
  status: LeaveRequestStatusEnum
  status_label: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /** @format date-time */
  order_date: string
  parent_saw_changes: boolean
  notifications_mark_as_read: number | null
}

export type ParentLeaveRequestStatus = {
  label: string
  /**
   * * `tentative` - Pending guardian
   * * `confirmed_contact_person` - Pending academic
   * * `confirmed_academic` - Pending boarding
   * * `changed` - Changed
   * * `confirmed` - Approved
   * * `declined` - Rejected
   * * `declined_contact_person` - Rejected by guardian
   * * `declined_academic` - Rejected by academic
   * * `canceled` - Canceled by student
   * * `expired` - Expired
   */
  value: LeaveRequestStatusEnum
}

export type ParentLeaveRequestType = {
  label: string
  /**
   * * `0` - Weekend
   * * `1` - Holiday
   * * `2` - Other
   * * `3` - Arrival
   */
  value: LeaveRequestTypeEnum
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type ParentMessage = {
  id: number
  content: string
  /** @format date-time */
  created: string
  /**
   * * `alert` - Alert
   * * `notice` - Notice
   */
  type: MessageTypeEnum
  /** via annotate (feed) */
  reaction_count: number
  /** via annotate (feed) */
  reacted_by_user: boolean
  /** @default "message" */
  content_type: ContentTypeF8CEnum
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type ParentMissionControlResponse = {
  id: number
  degree_aim_1: string
  degree_aim_2: string
  degree_destination_1: string
  degree_destination_2: string
  estimated_completion_display: string
}

export type ParentProfile = {
  /** @format uuid */
  id: string
  first_name: string
  last_name: string
  name: string
  full_name: string
  /**
   * Email address
   * @format email
   */
  email: string
  phone_number: string
  account_overdue: AccountOverdueEnum
  max_saldo_status: MaxSaldoStatusEnum
  favourites?: string[]
}

export type ParentProfileRequest = {
  favourites?: string[]
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type ParentReportCard = {
  id: number
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  quarter: ReportCardQuarter
  /** @format uri */
  file: string
  is_read: boolean
}

export type ParentSendReportCardRequest = {
  quarter: number
  students: string[]
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type ParentStudent = {
  /** @format uuid */
  id: string
  first_name: string
  last_name: string
  full_name: string
  /** @format uri */
  avatar: string | null
  current_grade_level: number
  current_grade_level_name: string | null
  grade_level_in_next_year: number
  house_phone_number: string
  is_day_student: boolean
  account_overdue: string
  saldo_status: SaldoStatusEnum
  has_pending_item: boolean
}

export type ParentStudentContact = {
  id: number
  /** @format uuid */
  parent: string
  /** Student details */
  contact_data: SimpleStudentContact
  /** Field used in summer camp instance */
  arrival_contact_for_student: string
  /** @format uuid */
  student: string
  /** Parent roles for student */
  roles: ContactTypeEnum[]
  has_app_access?: boolean
}

export type ParentStudentContactRequest = {
  /** @format uuid */
  student: string
  /** Parent roles for student */
  roles: ContactTypeEnum[]
  has_app_access?: boolean
}

export type ParentTAEEnrolmentChoiceV2 = {
  id: number
  name: string
  choice_type: TAEManageSignupChoicesChoiceTypeEnum
  private_status: PrivateStatusEnum | NullEnum | null
  number_of_lessons: number | null
  is_private: boolean
}

export type ParentTAEEnrolmentStatus = {
  /**
   * * `open` - Open
   * * `in_review` - In review
   * * `reviewed` - Reviewed
   * * `closed` - Closed
   * * `reopened` - Reopened
   */
  status: StudentTAEEnrolmentStatusEnum
  /** @format date */
  deadline: string | null
  open_state: OpenStateEnum | NullEnum | null
  semester: ParentTAEEnrolmentStatusSemester | null
  student_enrolment_id: number | null
  /** @format date-time */
  status_modified: string | null
  student: ParentTAEEnrolmentStatusStudent
  /** @format uri */
  offer_url: string | null
  reopened_co_curriculars: boolean
  reopened_sports: boolean
  scope: ScopeEnum[] | null
}

export type ParentTAEEnrolmentStatusSemester = {
  id: number
  name: string
  short_name: string
}

export type ParentTAEEnrolmentStatusStudent = {
  id: string
  short_first_name: string
}

export type ParentTAEEnrolmentStatusStudentV2 = {
  /** @format uuid */
  id: string
  short_first_name: string
}

export type ParentTAEEnrolmentStatusV2 = {
  /**
   * * `open` - Open
   * * `in_review` - In review
   * * `reviewed` - Reviewed
   * * `closed` - Closed
   * * `reopened` - Reopened
   */
  status: StudentTAEEnrolmentStatusEnum
  /** @format date-time */
  deadline: string | null
  student_enrolment_id: number | null
  /** @format date-time */
  status_modified: string | null
  open_state: OpenStateEnum | NullEnum | null
  /** @format uri */
  offer_url: string | null
  reopened_co_curriculars: boolean
  reopened_sports: boolean
  enrolment: StudentTAEEnrolmentStatusEnrolment | null
  scope: StudentTAEEnrolmentStatusScope[] | null
  student: ParentTAEEnrolmentStatusStudentV2
}

export type ParentTAEParentComment = {
  id: number
  /** @format date-time */
  created: string
  content: string
}

export type ParentTAEParentCommentCreate = {
  id: number
  /** @format date-time */
  created: string
  content: string
  /**
   * * `offer` - Offer
   * * `choice` - Choice
   * * `results` - Results
   */
  comment_type: CommentType3CfEnum
  /** @format uuid */
  student: string
}

export type ParentTAEParentCommentCreateRequest = {
  /** @minLength 1 */
  content: string
  /**
   * * `offer` - Offer
   * * `choice` - Choice
   * * `results` - Results
   */
  comment_type: CommentType3CfEnum
  /** @format uuid */
  student: string
}

export type ParentTAEParentCommentCreateV2 = {
  id: number
  /** @format date-time */
  created: string
  content: string
  /**
   * * `offer` - Offer
   * * `choice` - Choice
   * * `results` - Results
   */
  comment_type: CommentType3CfEnum
  /** @format uuid */
  student: string
}

export type ParentTAEParentCommentCreateV2Request = {
  /** @minLength 1 */
  content: string
  /**
   * * `offer` - Offer
   * * `choice` - Choice
   * * `results` - Results
   */
  comment_type: CommentType3CfEnum
  /** @format uuid */
  student: string
}

export type ParentTAEParentCommentV2 = {
  id: number
  /** @format date-time */
  created: string
  content: string
}

export type ParentTAEStudentEnrolment = {
  id: number
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_package: SportMembershipOriginalChoiceEnum
  parent_comment: string
  sports: ParentTAEStudentEnrolmentChoice[]
  co_curriculars: ParentTAEStudentEnrolmentChoice[]
}

export type ParentTAEStudentEnrolmentChoice = {
  id: number
  name: string
  choice_type: TAEManageSignupChoicesChoiceTypeEnum
  private_status: PrivateStatusEnum | NullEnum | null
  number_of_lessons: number | null
  is_private: boolean
}

export type ParentTAEStudentEnrolmentUpdate = {
  id: number
  parent_comment?: string
}

export type ParentTAEStudentEnrolmentUpdateRequest = {
  /** @minLength 1 */
  parent_comment?: string
}

export type ParentTAEStudentEnrolmentV2 = {
  id: number
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_package: SportMembershipOriginalChoiceEnum
  sports: ParentTAEEnrolmentChoiceV2[]
  co_curriculars: ParentTAEEnrolmentChoiceV2[]
}

export type ParentWarning = {
  id: number
  idp_plan?: string | null
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /**
   * @min 0
   * @max 2147483647
   */
  order?: number
  content: string
  accepted_by_parent: boolean
  note?: string
}

export type ParentWarningRequest = {
  /** @minLength 1 */
  idp_plan?: string | null
  /**
   * @min 0
   * @max 2147483647
   */
  order?: number
  /** @minLength 1 */
  content: string
  accepted_by_parent: boolean
  note?: string
}

export type ParentsFile = {
  id: number
  name: string
  /** @format uri */
  file: string | null
  filename: string
  /** @format uri */
  link: string
}

export type ParticipantAvailableStringFilter = {
  query_param: string
  label: string
  choices: ParticipantStringOption[]
}

export type ParticipantStringOption = {
  label: string
  value: string
  is_leader: boolean
  is_member: boolean
  is_owner: boolean
}

export type ParticipantStudent = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  first_name?: string
  /** @maxLength 128 */
  last_name?: string
  full_name: string
  /** @format uri */
  avatar?: string | null
  /**
   * mainly student's app usage
   * -> we're considering only first name given
   */
  reduced_full_name: string
}

export type ParticipantStudentRequest = {
  /** @maxLength 128 */
  first_name?: string
  /** @maxLength 128 */
  last_name?: string
  /** @format binary */
  avatar?: File | null
}

export type PasswordLoginRequest = {
  /** @minLength 1 */
  username: string
  /** @minLength 1 */
  password: string
}

export type PatchedAbacusInvoiceProductRequest = {
  /**
   * @min -2147483648
   * @max 2147483647
   */
  abacus_id?: number
  /** @minLength 1 */
  name?: string
}

export type PatchedAbsenceUpdateRequest = {
  /** @format uuid */
  student?: string
  /** @format date-time */
  start_date?: string
  /** @format date-time */
  end_date?: string
  comment?: string
  /**
   * * `late` - Late
   * * `absent_excused` - Absent excused
   * * `absent_unexcused` - Absent unexcused
   * * `sick` - Sick
   */
  absence_type?: AbsenceTypeEnum
  /**
   * * `regular` - Regular
   * * `sport` - Sport
   */
  class_type?: ClassTypeEnum
  added_by?: string
  last_modified_by?: string
}

export type PatchedAcademicAttendanceRequest = {
  is_present?: boolean
  is_late?: boolean
  is_absent_excused?: boolean
  is_absent_unexcused?: boolean
  is_sick?: boolean
  unlock_edit?: boolean
  /** @format date-time */
  marked_at?: string | null
}

export type PatchedAccGradeRequest = {
  quarter?: number | null
  semester?: number | null
  year?: number | null
  /** @maxLength 2000 */
  comment?: string
  /** @format uuid */
  course?: string
  /** @format uuid */
  student?: string
  custom_grade?: AccGradeCustomGradeEnum | BlankEnum
}

export type PatchedAccommodationRequest = {
  /** @format uuid */
  student?: string
  room?: number
  /** @format date */
  start_date?: string
  /** @format date */
  end_date?: string | null
  is_temporary_movement?: boolean
  /**
   * * `isolation` - Isolation
   * * `quarantine` - Quarantine
   * * `sleepover` - Sleepover
   */
  temporary_movement_type?: TemporaryMovementTypeEnum
  is_room_only_for_student?: boolean
  added_by?: string
  last_modified_by?: string
}

export type PatchedAdminAbsenceRecoveryRequest = {
  /** @format uuid */
  assigned_to?: string | null
  description?: string
  /**
   * * `AbsenceRecovery` - AbsenceRecovery
   * * `LunchtimeRecovery` - LunchtimeRecovery
   * * `HighSchoolLunchtimeRecovery` - HighSchoolLunchtimeRecovery
   */
  resourcetype?: RecoveryResourcetypeEnum
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PatchedAdminBoardDraftRequest = {
  /** @maxLength 128 */
  name?: string
  created_by?: string
  widget?: AdminWidgetRequest
  /** @format date-time */
  display_from?: string | null
  /** @format date-time */
  display_to?: string | null
  publish_on_infoboards?: boolean
  /**
   * * `square` - Square
   * * `framed` - Framed
   * * `fullscreen` - Fullscreen
   */
  layout?: LayoutEnum
  /**
   * @min -2147483648
   * @max 2147483647
   */
  display_time?: number
  pinned_in_apps?: AppTypeEnum[]
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PatchedAdminBoardRequest = {
  pinned_in_apps?: AppTypeEnum[]
  created_by?: string
  widget?: AdminWidgetRequest
  /**
   * @minLength 1
   * @maxLength 128
   */
  name?: string
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  display_from?: string
  /** @format date-time */
  display_to?: string | null
  publish_on_infoboards?: boolean
  /**
   * * `square` - Square
   * * `framed` - Framed
   * * `fullscreen` - Fullscreen
   */
  layout?: LayoutEnum
  /**
   * @min -2147483648
   * @max 2147483647
   */
  display_time?: number
  /**
   * @min -2147483648
   * @max 2147483647
   */
  sort_value?: number
}

export type PatchedAdminBoardingAnnouncementWidgetRequest = {
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * @minLength 1
   * @maxLength 90
   */
  text?: string
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype?: WidgetResourcetypeEnum
}

export type PatchedAdminCelebrationWidgetRequest = {
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype?: WidgetResourcetypeEnum
}

export type PatchedAdminClockWidgetRequest = {
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype?: WidgetResourcetypeEnum
}

export type PatchedAdminCongratulationsWidgetRequest = {
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype?: WidgetResourcetypeEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedAdminEventAgendaWidgetRequest = {
  agenda_items?: AgendaItemRequest[]
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * @minLength 1
   * @maxLength 90
   */
  header?: string
  /** @format date */
  date_from?: string
  /** @format date */
  date_to?: string | null
  /** @format time */
  time_from?: string
  /** @format time */
  time_to?: string
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype?: WidgetResourcetypeEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedAdminEventInfoWidgetRequest = {
  /** @minLength 1 */
  bg_color?: string
  link?: AdminLinkWithQRCodeRequest | null
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * @minLength 1
   * @maxLength 90
   */
  header?: string
  /** @maxLength 400 */
  message?: string
  /** @maxLength 64 */
  classroom_shortcut?: string
  /** @maxLength 64 */
  location?: string
  /** @maxLength 64 */
  course_name?: string
  show_qr_code?: boolean
  /** @maxLength 64 */
  qr_code_text?: string
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype?: WidgetResourcetypeEnum
}

/** Shared text and QR validation for event media widgets. */
export type PatchedAdminEventInfoWithPhotoWidgetRequest = {
  /** @minLength 1 */
  bg_color?: string
  link?: AdminLinkWithQRCodeRequest | null
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * @minLength 1
   * @maxLength 100
   */
  header?: string
  include_message?: boolean
  /** @maxLength 400 */
  message?: string
  crop_my_rosenberg_idp_photo_separately?: boolean
  /** @maxLength 64 */
  location?: string
  /** @maxLength 64 */
  course_name?: string
  show_qr_code?: boolean
  /** @maxLength 64 */
  qr_code_text?: string
  /** @minLength 1 */
  photo?: string
  /** @minLength 1 */
  photo_in_my_rosenberg_idp?: string | null
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype?: WidgetResourcetypeEnum
}

export type PatchedAdminHighSchoolLunchtimeRecoveryRequest = {
  description?: string
  /**
   * * `AbsenceRecovery` - AbsenceRecovery
   * * `LunchtimeRecovery` - LunchtimeRecovery
   * * `HighSchoolLunchtimeRecovery` - HighSchoolLunchtimeRecovery
   */
  resourcetype?: RecoveryResourcetypeEnum
}

/** Shared text and QR validation for event media widgets. */
export type PatchedAdminInfoWithVideoWidgetRequest = {
  /** @minLength 1 */
  bg_color?: string
  link?: AdminLinkWithQRCodeRequest | null
  /** @minLength 1 */
  video?: string | null
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * @minLength 1
   * @maxLength 100
   */
  header?: string
  include_message?: boolean
  /** @maxLength 400 */
  message?: string
  /** @maxLength 64 */
  location?: string
  /** @maxLength 64 */
  course_name?: string
  show_qr_code?: boolean
  /** @maxLength 64 */
  qr_code_text?: string
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype?: WidgetResourcetypeEnum
}

export type PatchedAdminInfoboardsSettingsRequest = {
  /** Generate automated alerts about teacher's covers */
  generate_teacher_cover_alerts?: boolean
  /** Generate automated alerts about school breaks (1 week before start date) */
  generate_school_break_alerts?: boolean
  /** Show 2 most recent articles */
  show_recent_articles?: boolean
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedAdminJournalWidgetRequest = {
  journal_items?: AdminJournalItemRequest[]
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype?: WidgetResourcetypeEnum
}

export type PatchedAdminLunchtimeRecoveryRequest = {
  /** @format uuid */
  assigned_to?: string | null
  description?: string
  /**
   * * `AbsenceRecovery` - AbsenceRecovery
   * * `LunchtimeRecovery` - LunchtimeRecovery
   * * `HighSchoolLunchtimeRecovery` - HighSchoolLunchtimeRecovery
   */
  resourcetype?: RecoveryResourcetypeEnum
}

export type PatchedAdminMenuWidgetRequest = {
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  menu?: number | null
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype?: WidgetResourcetypeEnum
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PatchedAdminMessageRequest = {
  /**
   * * `alert` - Alert
   * * `notice` - Notice
   */
  type?: MessageTypeEnum
  /** @minLength 1 */
  name?: string
  /** @minLength 1 */
  content?: string
  /** @format date */
  display_to?: string
  is_created_manually?: boolean
  author?: string
}

export type PatchedAdminMysteryLinkWidgetRequest = {
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype?: WidgetResourcetypeEnum
}

export type PatchedAdminPosterWidgetRequest = {
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /** @minLength 1 */
  image?: string
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype?: WidgetResourcetypeEnum
}

export type PatchedAdminRecoveryRequest =
  | PatchedAdminAbsenceRecoveryRequest
  | PatchedAdminLunchtimeRecoveryRequest
  | PatchedAdminHighSchoolLunchtimeRecoveryRequest

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedAdminSchoolYearRequest = {
  /**
   * @minLength 1
   * @maxLength 32
   */
  name?: string
  /** @format date */
  start_date?: string
  /** @format date */
  end_date?: string
  semesters?: AdminSemesterRequest[]
}

export type PatchedAdminSiteSettingsRequest = {
  /** Allow teachers to set target grades */
  target_grades_set_allowed?: boolean
  /**
   * Setting target grades starts at
   * @format date-time
   */
  target_grades_start?: string | null
  /**
   * Setting target grades ends at
   * @format date-time
   */
  target_grades_end?: string | null
  /** Edit values, or set from pending only */
  target_grades_set_all?: boolean
  /**
   * Disable generating tasks from datetime
   * @format date-time
   */
  disable_generating_tasks_from?: string | null
  /**
   * Disable generating tasks to datetime
   * @format date-time
   */
  disable_generating_tasks_to?: string | null
  /** Disable generating take student to destination task */
  disable_take_student_to_destination_task?: boolean
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PatchedAdminStudentAnnouncementRequest = {
  /** @format uuid */
  student?: string
  /**
   * * `pending` - Pending
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `deleted` - Deleted
   * * `archived` - Archived
   */
  status?: StudentContentStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  display_to?: string | null
  emojis?: string[]
  /**
   * @minLength 1
   * @maxLength 100
   */
  text?: string
  /** @maxLength 7 */
  bg_color?: string
  /**
   * * `StudentMysteryLink` - StudentMysteryLink
   * * `StudentAnnouncement` - StudentAnnouncement
   * * `StudentPic` - StudentPic
   */
  resourcetype?: StudentContentResourcetypeEnum
}

export type PatchedAdminStudentAnnouncementWidgetRequest = {
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype?: WidgetResourcetypeEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedAdminStudentArtGalleryWidgetRequest = {
  arts?: AdminStudentArtRequest[]
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype?: WidgetResourcetypeEnum
}

export type PatchedAdminStudentContentRequest =
  | PatchedAdminStudentMysteryLinkRequest
  | PatchedAdminStudentAnnouncementRequest
  | PatchedAdminStudentPicRequest

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PatchedAdminStudentMysteryLinkRequest = {
  /** @format uuid */
  student?: string
  /**
   * * `pending` - Pending
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `deleted` - Deleted
   * * `archived` - Archived
   */
  status?: StudentContentStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  display_to?: string | null
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  link?: AdminLinkWithQRCodeRequest
  /**
   * * `StudentMysteryLink` - StudentMysteryLink
   * * `StudentAnnouncement` - StudentAnnouncement
   * * `StudentPic` - StudentPic
   */
  resourcetype?: StudentContentResourcetypeEnum
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PatchedAdminStudentPicRequest = {
  /** @format uuid */
  student?: string
  /**
   * * `pending` - Pending
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `deleted` - Deleted
   * * `archived` - Archived
   */
  status?: StudentContentStatusEnum
  /** @format date-time */
  published_at?: string | null
  /** @format date-time */
  display_to?: string | null
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  image?: ImageRequest
  /** @maxLength 50 */
  title?: string
  /**
   * * `StudentMysteryLink` - StudentMysteryLink
   * * `StudentAnnouncement` - StudentAnnouncement
   * * `StudentPic` - StudentPic
   */
  resourcetype?: StudentContentResourcetypeEnum
}

export type PatchedAdminStudentsPicWidgetRequest = {
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype?: WidgetResourcetypeEnum
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PatchedAdminTeacherRequest = {
  /**
   * @format email
   * @minLength 1
   */
  email?: string
  /** @minLength 1 */
  first_name?: string
  /** @minLength 1 */
  last_name?: string
  /**
   * @minLength 1
   * @maxLength 16
   */
  acronym?: string
  /** @minLength 1 */
  phone_number?: string
  departments?: number[]
  preferred_rooms?: number[]
  two_factor_auth?: boolean
  /** Teacher hidden in timetable generator */
  is_hidden_in_generator?: boolean
  custom_schedule?: any
  is_active?: boolean
  is_placeholder?: boolean
  is_mentor?: boolean
  mentor_for_grades?: number[]
}

export type PatchedAdminTimetableLessonUpdateRequest = {
  /** @format uuid */
  teacher_cover?: string | null
}

export type PatchedAdminUserRequest = {
  /** @minLength 1 */
  password1?: string
  /** @minLength 1 */
  password2?: string
  /** @minLength 1 */
  first_name?: string
  /** @minLength 1 */
  last_name?: string
  /**
   * @format email
   * @minLength 1
   */
  email?: string
  /** @minLength 1 */
  phone_number?: string
  two_factor_auth?: boolean
  /** @format date-time */
  last_login?: string | null
  /**
   * Superuser status
   * Designates that this user has all permissions without explicitly assigning them.
   */
  is_superuser?: boolean
  /**
   * Staff status
   * Designates whether the user can log into this admin site.
   */
  is_staff?: boolean
  /**
   * Active
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  /** @format date-time */
  date_joined?: string
  search?: string | null
  searchable_content?: string
  /** @maxLength 256 */
  name?: string
  gender?: ForGenderEnum | BlankEnum
  /**
   * @format int64
   * @min 0
   * @max 9223372036854776000
   */
  abacus_person_number?: number | null
  disable_access_to_idp?: boolean
  staff_type?: StaffTypeEnum | BlankEnum
  can_handle_generator?: boolean
  allow_push_notifications?: boolean
  allow_email_notifications?: boolean
  accept_campus_notifications?: boolean
  accept_education_notifications?: boolean
  accept_reminders_notifications?: boolean
  accept_dashboard_notifications?: boolean
  cannot_use_sms_login?: boolean
  is_deleted_due_to_duplicate?: boolean
  billing_additional_email?: number | null
  /** @format uuid */
  last_updated_by?: string | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedAdminVariantManualCongratulationsWidgetRequest = {
  items?: AdminVariantManualCongratulationsRequest[]
  /** @maxLength 255 */
  working_name?: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  from_automation?: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype?: WidgetResourcetypeEnum
}

export type PatchedAdminWidgetRequest =
  | PatchedAdminBoardingAnnouncementWidgetRequest
  | PatchedAdminCelebrationWidgetRequest
  | PatchedAdminEventAgendaWidgetRequest
  | PatchedAdminCongratulationsWidgetRequest
  | PatchedAdminPosterWidgetRequest
  | PatchedAdminStudentArtGalleryWidgetRequest
  | PatchedAdminEventInfoWidgetRequest
  | PatchedAdminEventInfoWithPhotoWidgetRequest
  | PatchedAdminInfoWithVideoWidgetRequest
  | PatchedAdminJournalWidgetRequest
  | PatchedAdminMysteryLinkWidgetRequest
  | PatchedAdminMenuWidgetRequest
  | PatchedAdminStudentAnnouncementWidgetRequest
  | PatchedAdminStudentsPicWidgetRequest
  | PatchedAdminVariantManualCongratulationsWidgetRequest
  | PatchedAdminClockWidgetRequest

export type PatchedAllergyRequest = {
  /** @minLength 1 */
  type?: string
  subtypes?: string[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedAppMysteryLinkRequest = {
  student?: string
  link?: LinkWithQRCodeRequest
  /**
   * * `StudentMysteryLink` - StudentMysteryLink
   * * `StudentAnnouncement` - StudentAnnouncement
   * * `StudentPic` - StudentPic
   */
  resourcetype?: StudentContentResourcetypeEnum
}

export type PatchedAppStudentAnnouncementRequest = {
  student?: string
  emojis?: string[]
  /**
   * @minLength 1
   * @maxLength 100
   */
  text?: string
  /** @maxLength 7 */
  bg_color?: string
  /**
   * * `StudentMysteryLink` - StudentMysteryLink
   * * `StudentAnnouncement` - StudentAnnouncement
   * * `StudentPic` - StudentPic
   */
  resourcetype?: StudentContentResourcetypeEnum
}

export type PatchedAppStudentPicRequest = {
  student?: string
  /** @minLength 1 */
  image?: string
  /** @maxLength 50 */
  title?: string
  /**
   * * `StudentMysteryLink` - StudentMysteryLink
   * * `StudentAnnouncement` - StudentAnnouncement
   * * `StudentPic` - StudentPic
   */
  resourcetype?: StudentContentResourcetypeEnum
}

export type PatchedAppointmentCancellationRequest = {
  /** @minLength 1 */
  cancellation_reason?: string
}

export type PatchedAppointmentRequest = {
  /**
   * * `hospital` - Hospital
   * * `doctor` - Doctor
   */
  appointment_type?: AppointmentTypeEnum
  doctor?: number | null
  doctor_name?: string
  hospital?: number | null
  hospital_name?: string
  /** @format date-time */
  date_and_time?: string
  /** @minLength 1 */
  report?: string
  /** @format date-time */
  end_of_visit?: string
}

export type PatchedApproveStudentContentRequest = {
  /** @format date */
  display_to?: string | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedAssignmentGradeRequest = {
  /**
   * @min 0
   * @max 100
   */
  grade_in_percent?: number | null
  /**
   * Custom grades are eligible only for assignments related with CCU courses
   *
   * * `SC` - Successfully Completed
   * * `CA` - Successfully Completed & Credit Awarded
   */
  custom_grade?: TECustomGradeEnum | BlankEnum
  /** @maxLength 32 */
  grade_in_ib?: string
  is_submitted?: boolean
  is_incomplete?: boolean
  is_late?: boolean
  is_exempt?: boolean
  /** @format uuid */
  student?: string
  assignment?: AssignmentInStudentAssignmentItemRequest
  score_definitions?: BaseAssignmentScoreDefinitionRequest[]
  /**
   * Moderated grade in percents.
   * @min 0
   * @max 100
   */
  moderated_grade?: number | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedAssignmentUpdateRequest = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name?: string
  /** @minLength 1 */
  description?: string
  type?: number
  /** @format date */
  due_date?: string
  /** @format uuid */
  lesson?: string | null
  /** @format uuid */
  course?: string
  /** @format uuid */
  teacher?: string | null
  student_items?: StudentAssignmentItemRequest[]
  is_homework?: boolean
  quarter?: number
  score_definitions?: AssignmentScoreDefinitionRequest[]
}

export type PatchedBillingReportRequest = {
  /**
   * @minLength 1
   * @maxLength 120
   */
  name?: string
}

export type PatchedBoardingAttendanceReportRequest = {
  /**
   * * `new` - New
   * * `incomplete` - Incomplete
   * * `finished` - Finished
   */
  status?: BoardingAttendanceReportStatusEnum
  /** @format uuid */
  tutor?: string | null
  house?: number | null
  for_senior_campus?: boolean
  for_senior_campus_type?: ForGenderEnum | NullEnum | null
}

export type PatchedBoardingAttendanceRequest = {
  mark?: BoardingAttendanceMarkEnum | BlankEnum
  /** @format uuid */
  student?: string
  is_at_home?: boolean
  is_at_class?: boolean
  is_at_exam?: boolean
  is_at_school_programme?: boolean
  has_meal_skip?: boolean
  /** @minLength 1 */
  boarding_status?: string
}

export type PatchedBoardingCommentRequest = {
  added_by?: string
  last_modified_by?: string
  /**
   * @min 0
   * @max 32767
   */
  semester?: BoardingCommentSemesterEnum | NullEnum | null
  /** @format uuid */
  student?: string
  school_year?: number
  current_quarter?: number
  /** @minLength 1 */
  content?: string
}

export type PatchedCalendarEventRequest = {
  /** @format date */
  date_start?: string
  /** @format date */
  date_end?: string
  /**
   * * `community_weekend` - Community weekend
   * * `academic_tests` - Academic tests
   */
  event_type?: CalendarEventEventTypeEnum
}

export type PatchedCampusAbsenceCommentRequest = {
  author?: string
  /** @minLength 1 */
  content?: string
  reject?: boolean
}

/** Adds nested create feature */
export type PatchedCampusAbsenceRequest = {
  created_by?: string
  last_modified_by?: string
  campus_absence_students?: CampusAbsenceStudentRequest[]
  quarter?: number
  /**
   * * `ausgang` - Ausgang
   * * `external_exam` - External exam
   * * `official` - Official
   * * `school_programme` - School Programme
   * * `extra_ausgang` - Extra Ausgang
   */
  campus_absence_type?: CampusAbsenceTypeEnum
  /**
   * * `afternoon_ausgang` - Afternoon Ausgang
   * * `evening_ausgang` - Evening Ausgang
   * * `evening_campus_ausgang` - Evening - Campus Ausgang
   * * `birthday_ausgang` - Birthday Ausgang
   * * `family_visit` - Family visit
   * * `on_board` - On board Ausgang
   * * `prolonged_ausgang` - Prolonged Ausgang
   * * `campus` - Campus
   * * `offsite` - Offsite
   * * `offsite_overnight` - Offsite, Overnight
   * * `official_appointment` - Official appointment
   * * `specialist_doctor_appointment` - Specialist Doctor Appointment
   * * `programme` - Programme
   * * `house_evening` - House evening
   * * `term_break` - Term Break
   * * `school_trip` - School Trip
   * * `weekend_programme` - Weekend Programme
   * * `other` - Other
   * * `book_the_cafe` - Book the Cafe
   * * `city_brunch_zurich` - City Brunch Zurich
   * * `additional_ausgang` - Additional Ausgang
   * * `on_campus_ausgang` - On Campus Ausgang
   */
  campus_absence_subtype?: CampusAbsenceSubTypeEnum
  /** @minLength 1 */
  leave_details?: string
  /** @maxLength 256 */
  contact_person_name?: string
  /** @maxLength 128 */
  contact_person_phone?: string
  /** @format date-time */
  date_from?: string
  /** @format date-time */
  date_to?: string
  disable_students_length_validation?: boolean
  supervisors?: (string | null)[]
  house?: number | null
}

export type PatchedCandidateProfileDetailRequest = {
  /**
   * @min 0
   * @max 32767
   */
  academic_review_status?: AcademicReviewStatusEnum
  /**
   * @min 1
   * @max 15
   */
  year_until_high_school_graduation?: number | null
  student?: StudentCandidateProfileDetailRequest
  academic_offer?: CandidateAcademicOfferRequest
}

export type PatchedCatalogRequest = {
  /** @minLength 1 */
  name?: string
}

export type PatchedChangeAvatarRequest = {
  avatar?: string | null
}

export type PatchedChronicConditionRequest = {
  /** @minLength 1 */
  type?: string
  subtypes?: string[]
}

export type PatchedCompanyUpdateRequest = {
  /** @minLength 1 */
  name?: string
  /**
   * @format email
   * @minLength 1
   * @maxLength 254
   */
  company_email?: string
  billing_additional_email_id?: number | null
  /**
   * @minLength 1
   * @maxLength 128
   */
  company_phone_number?: string
  crucial_information?: string
  languages?: NativeLanguageEnum[]
}

export type PatchedConditionRequest = {
  /** @format uuid */
  health_check?: string
  /**
   * * `unchanged` - Unchanged
   * * `better` - Better
   * * `worse` - Worse
   */
  status?: ConditionStatusEnum
  new_symptoms?: NewSymptomsEnum[]
  new_symptoms_appeared?: boolean
}

export type PatchedCourseDescriptionRequest = {
  description?: string
}

export type PatchedCourseFamilyRequest = {
  /**
   * @minLength 1
   * @maxLength 64
   */
  id?: string
  /** @maxLength 64 */
  name?: string
}

export type PatchedCourseGroupRequest = {
  /**
   * @minLength 1
   * @maxLength 64
   */
  id?: string
  /** @maxLength 64 */
  name?: string
}

export type PatchedCourseInBasketUpdateDepartmentRequest = {
  mission_control_department?: MissionControlDepartmentEnum | BlankEnum
}

export type PatchedCourseIntelligenceRequest = {
  /**
   * @minLength 1
   * @maxLength 64
   */
  id?: string
  /** @maxLength 64 */
  name?: string
}

export type PatchedCourseLevelRequest = {
  /**
   * @minLength 1
   * @maxLength 64
   */
  id?: string
  /** @maxLength 64 */
  name?: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedCoursePlannerRequest = {
  number_of_lessons?: number | null
  next_courses?: string[]
  related_courses?: string[]
  course_request?: number | null
  outline_file?: string | null
  allowed_slots?: CourseAllowedSlotsRequest[] | null
  /**
   * @minLength 1
   * @maxLength 128
   */
  name?: string
  is_latest_version?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  external_id?: number | null
  /** @minLength 1 */
  difficulty?: string
  /** @minLength 1 */
  number?: string
  /**
   * @format decimal
   * @pattern ^-?\d{0,1}(?:\.\d{0,2})?$
   */
  humanix_factor?: string
  /** @maxItems 4 */
  performed_in_quarters?: QuarterShortNameEnum[]
  description?: string
  has_academic_exam?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  exam_duration?: number | null
  has_external_exam?: boolean
  external_exam_type?: ExternalExamTypeEnum | BlankEnum
  exam_course_name?: string
  /** RPS-6527 */
  is_payable?: boolean
  /** Used in summer camps */
  is_camps_focus?: boolean
  /** Used in summer camps */
  is_camps_private?: boolean
  /** Used in billing reports for sport package++ */
  is_included_in_sport_package?: boolean
  /** @default false */
  is_tae_private?: boolean
  /** Used in T&E generator */
  is_preferred_until_time?: boolean
  /** Exclude course from transcript of grades */
  exclude_from_tog?: boolean
  /** Marks the course as non-credit — it does not grant any credits. */
  is_non_credit_course?: boolean
  custom_constraints?: boolean
  override_allowed_slots?: boolean
  /**
   * @min 5
   * @max 240
   */
  lesson_duration?: number | null
  override_preparation_time?: boolean
  /**
   * @min 0
   * @max 60
   */
  preparation_time_before?: number | null
  /**
   * @min 0
   * @max 60
   */
  preparation_time_after?: number | null
  legacy_department?: number | null
  /** @minLength 1 */
  group?: string
  /** @minLength 1 */
  subject?: string
  departments?: number[]
  families?: string[]
  intelligences?: string[]
  types?: string[]
  preparation_for_types?: string[]
  levels?: string[]
  rooms?: number[]
}

export type PatchedCourseSubjectRequest = {
  /**
   * @minLength 1
   * @maxLength 64
   */
  id?: string
  /** @maxLength 64 */
  name?: string
}

export type PatchedCourseTypeRequest = {
  /**
   * @minLength 1
   * @maxLength 64
   */
  id?: string
  /** @maxLength 64 */
  name?: string
}

export type PatchedCoverConflictRequest = {
  /** @format date */
  cover_date?: string
  cover?: CoverRequest
  rota_timetable?: number
  /** @format time */
  start_hour?: string
  /** @format time */
  end_hour?: string
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PatchedDayMenuRequest = {
  /** @format date */
  date?: string
  is_filled?: boolean
  is_skipped?: boolean
  /** @maxLength 120 */
  lunch_starter?: string
  /** @maxLength 120 */
  lunch_soup?: string
  /** @maxLength 120 */
  lunch_main_course?: string
  /** @maxLength 120 */
  lunch_dessert?: string
  /** @maxLength 120 */
  lunch_starter_vege?: string
  /** @maxLength 120 */
  lunch_soup_vege?: string
  /** @maxLength 120 */
  lunch_main_course_vege?: string
  /** @maxLength 120 */
  lunch_dessert_vege?: string
  /** @maxLength 120 */
  dinner_starter?: string
  /** @maxLength 120 */
  dinner_soup?: string
  /** @maxLength 120 */
  dinner_main_course?: string
  /** @maxLength 120 */
  dinner_dessert?: string
  /** @maxLength 120 */
  dinner_starter_vege?: string
  /** @maxLength 120 */
  dinner_soup_vege?: string
  /** @maxLength 120 */
  dinner_main_course_vege?: string
  /** @maxLength 120 */
  dinner_dessert_vege?: string
  /** @maxLength 120 */
  breakfast_special?: string
  /** @maxLength 120 */
  breakfast_special_vege?: string
}

export type PatchedDegreeAimRequest = {
  /**
   * @minLength 1
   * @maxLength 256
   */
  name?: string
}

export type PatchedDepartmentRequest = {
  /**
   * @minLength 1
   * @maxLength 32
   */
  name?: string
  /** @maxLength 32 */
  label?: string
  is_talent_and_enrichment?: boolean
  /** Flag indicating if department should be excluded from cockpit apps */
  is_excluded_from_cockpit?: boolean
}

export type PatchedDisconnectParentStudentContactRequest = {
  /** @format uuid */
  student?: string
}

export type PatchedDoctorDiagnosisRequest = {
  /** @minLength 1 */
  diagnosis?: string
  /** @minLength 1 */
  recommendations?: string
  /**
   * * `new` - New
   * * `cancelled` - Cancelled
   * * `after_the_visit` - After the visit
   */
  status?: AppointmentStatusEnum
}

export type PatchedDoctorRequest = {
  /**
   * @minLength 1
   * @maxLength 256
   */
  name?: string
}

export type PatchedDraftActivityRequest = {
  /**
   * @min 0
   * @max 2147483647
   */
  day?: RotaDayEnum
  /**
   * @min 0
   * @max 15
   */
  hour?: HourEnum | NullEnum | null
  /** @format time */
  custom_start?: string | null
  /** @format time */
  custom_end?: string | null
  /** @format double */
  duration?: number
  is_archived?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  external_id?: number | null
  /**
   * @min 0
   * @max 2147483647
   */
  fet_id?: number | null
  /** attendances */
  co_teacher_1_perm_to_manage_lesson?: boolean
  /** attendances */
  co_teacher_2_perm_to_manage_lesson?: boolean
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
  /** @format uuid */
  course?: string
  /**
   * Artisan
   * @format uuid
   */
  teacher?: string
  room?: number | null
  from_optimization?: number | null
  /** @format uuid */
  separated_from?: string | null
  students?: string[]
}

export type PatchedDraftRotaActivityConflictCheckRequest = {
  /** @format uuid */
  user?: string | null
  users?: string[]
  /**
   * * `regular` - Duty
   * * `break` - Break
   * * `meeting` - Leaders Meeting
   * * `night_presence` - Night Presence
   * * `ski_trip` - Ski trip
   * * `sudden_time_off` - Sudden Time Off
   */
  rota_type?: RotaTypeEnum
  rota_timetable?: number
  /**
   * @min 0
   * @max 2147483647
   */
  day?: RotaDayEnum
  /** @format time */
  start_hour?: string
  /** @format time */
  end_hour?: string
  /** @format uuid */
  team?: string | null
  role?: BoardingArtisanRoleEnum | BlankEnum
  room?: number | null
  /** @format date */
  date?: string
}

export type PatchedDraftRotaActivityRequest = {
  /** @format uuid */
  user?: string | null
  users?: string[]
  /**
   * * `regular` - Duty
   * * `break` - Break
   * * `meeting` - Leaders Meeting
   * * `night_presence` - Night Presence
   * * `ski_trip` - Ski trip
   * * `sudden_time_off` - Sudden Time Off
   */
  rota_type?: RotaTypeEnum
  rota_timetable?: number
  /**
   * @min 0
   * @max 2147483647
   */
  day?: RotaDayEnum
  /** @format time */
  start_hour?: string
  /** @format time */
  end_hour?: string
  /** @format uuid */
  team?: string | null
  role?: BoardingArtisanRoleEnum | BlankEnum
  room?: number | null
  cover?: CoverRequest | null
  /** @format date */
  cover_date?: string
  /** @format date */
  date?: string
}

export type PatchedDrinkGiveRequest = {
  was_given?: boolean
  /** @format uuid */
  health_check?: string
}

export type PatchedExamEnrolledStudentRequest = {
  is_present?: boolean
  /** is exempt */
  is_absent_excused?: boolean
  is_absent_unexcused?: boolean
  is_sick?: boolean
  is_academic_honesty?: boolean
  is_disturbance?: boolean
  disturbance_merit_note?: string
  academic_honesty_merit_note?: string
}

export type PatchedExamRequest = {
  official_name?: string
  /** @format uuid */
  course?: string
  /** @format date */
  date?: string | null
  /**
   * @min 0
   * @max 2147483647
   */
  duration?: number | null
  /** @format time */
  start_time?: string | null
  /** @format time */
  end_time?: string | null
  location?: number | null
  is_make_up_test?: boolean
  test_invigilators?: string[]
  saa_invigilators?: string[]
  teachers?: string[]
  grade_levels?: number[]
}

export type PatchedFavouriteViewSettingRequest = {
  user?: string
  /**
   * @min 0
   * @max 32767
   */
  module?: ModuleEnum
  /**
   * @minLength 1
   * @maxLength 128
   */
  name?: string
  data?: any
  share_with?: string[]
}

export type PatchedFileForStudentRequest = {
  /** @maxLength 100 */
  name?: string
  author?: string
  file?: string
  /**
   * @format uri
   * @minLength 1
   * @maxLength 200
   */
  link?: string
  display_in?: DisplayInEnum[]
}

export type PatchedFirstContactRequest = {
  symptoms?: SymptomsFf5Enum[]
  /** @minLength 1 */
  details?: string
  /**
   * @format decimal
   * @pattern ^-?\d{0,2}(?:\.\d{0,1})?$
   */
  temperature?: string | null
}

export type PatchedFoodGiveRequest = {
  was_given?: boolean
  /** @format uuid */
  health_check?: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedGlobalStandardUpdateRequest = {
  /** @format uuid */
  course_id?: string
  school_year_id?: number
  /**
   * * `to_do` - To Do
   * * `in_progress` - In progress
   * * `updating` - Updating
   * * `sent_to_artisans` - Sent to artisans
   */
  status?: GlobalStandardStatusEnum
  created_by?: string
  items?: GlobalStandardItemDetailRequest[]
}

export type PatchedHealthCheckCommentRequest = {
  /** @format uuid */
  health_check?: string
  /** @minLength 1 */
  content?: string
}

export type PatchedHospitalRequest = {
  /**
   * @minLength 1
   * @maxLength 256
   */
  name?: string
}

export type PatchedHouseRequest = {
  /**
   * @minLength 1
   * @maxLength 128
   */
  name?: string
  tutors?: string[]
  generate_reports?: boolean
  generate_reports_at_study_time?: boolean
  generate_reports_at_the_weekend?: boolean
  generate_night_attendance_reports?: boolean
  is_senior_campus?: boolean
  is_junior_campus?: boolean
  is_female_campus?: boolean
  is_male_campus?: boolean
}

export type PatchedHouseRoomRequest = {
  /**
   * * `S` - Small
   * * `L` - Large
   */
  size?: SizeEnum
  /**
   * @min 0
   * @max 32767
   */
  capacity?: CapacityEnum
  /**
   * @min 0
   * @max 32767
   */
  people_per_bathroom?: number
}

export type PatchedIdpSectionEnrollRequest = {
  section?: number | null
  /** @format uuid */
  course?: string | null
  /**
   * @min -2147483648
   * @max 2147483647
   */
  number_of_lessons?: number
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PatchedImageRequest = {
  /** @minLength 1 */
  ppoi?: string
  catalogs?: number[]
  /**
   * @minLength 1
   * @maxLength 7
   */
  dominant_color?: string
  /** @maxLength 128 */
  title?: string
  /** @maxLength 128 */
  alt?: string
  /** @format binary */
  file?: File
}

export type PatchedLeaveCheckRequest = {
  /**
   * @minLength 1
   * @maxLength 1024
   */
  destination?: string
  /** @maxLength 256 */
  train?: string
  /** @maxLength 256 */
  plane?: string
  /** @maxLength 256 */
  pick_up_location?: string
  /** @format time */
  pick_up_time?: string | null
  comment?: string
  /**
   * * `in` - Check-in
   * * `out` - Check-out
   */
  check_type?: LeaveCheckTypeEnum
  /**
   * @minLength 1
   * @maxLength 64
   */
  contact_person?: string
  /**
   * @minLength 1
   * @maxLength 128
   */
  contact_person_phone?: string
  is_completed?: boolean
  is_driver_needed?: boolean
  /** @format date-time */
  date?: string
  /** @format uuid */
  leave_request?: string
}

export type PatchedLeaveRequestRequest = {
  check_out?: RelatedLeaveCheckRequest
  check_in?: RelatedLeaveCheckRequest
  /** @format uuid */
  student?: string
  /**
   * @min -2147483648
   * @max 2147483647
   */
  leave_type?: LeaveRequestTypeEnum
  /**
   * * `manual` - Manual
   * * `from idp` - From IDP
   * * `by_student` - Requested by student
   */
  request_type?: RequestTypeEnum
  /**
   * * `tentative` - Pending guardian
   * * `confirmed_contact_person` - Pending academic
   * * `confirmed_academic` - Pending boarding
   * * `changed` - Changed
   * * `confirmed` - Approved
   * * `declined` - Rejected
   * * `declined_contact_person` - Rejected by guardian
   * * `declined_academic` - Rejected by academic
   * * `canceled` - Canceled by student
   * * `expired` - Expired
   */
  status?: LeaveRequestStatusEnum
  student_message?: string
  has_clashes?: boolean
  clashes_resolved?: boolean
}

export type PatchedMarkHomeworkAsSentStudentRequest = {
  is_marked_as_sent_by_student?: boolean
}

export type PatchedMedicineGivenRequest = {
  /** @format uuid */
  health_check?: string
  /** @format uuid */
  scheduled_medicine?: string | null
  /** @minLength 1 */
  optional_medicine?: string | null
}

/** Adds nested create feature */
export type PatchedMedicineInPlanRequest = {
  medicine?: number
  /**
   * @minLength 1
   * @maxLength 255
   */
  medicine_strength?: string
  /** @format date-time */
  start_date?: string
  /** @format date-time */
  end_date?: string
  /**
   * * `daily` - Daily
   * * `every_x_days` - Every "X" days
   * * `monthly` - Monthly
   * * `weekly` - Weekly
   */
  repeat_form?: RepeatFormEnum
  /**
   * @min 0
   * @max 2147483647
   */
  repeat_every?: number
  comment?: string
  intakes?: MedicineIntakePlanRequest[]
  is_optional?: boolean
  created_by?: string
  /** @format uuid */
  student?: string
  /** @minLength 1 */
  medicine_plan?: string
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PatchedMeritItemRequest = {
  /**
   * * `respect` - Respect
   * * `community` - Community
   * * `safety` - Safety
   * * `health` - Health
   */
  type?: MeritItemTypeEnum
  /** @minLength 1 */
  name?: string
  /**
   * * `attitude` - Attitude
   * * `kindness` - Kindness
   * * `service` - Service
   * * `language` - Language
   * * `electronics` - Electronics
   * * `dress` - Dress
   * * `late` - Late
   * * `night` - Night
   * * `room` - Room
   * * `absence` - Absence
   * * `leave_request` - Leave request
   * * `ausgang` - Ausgang
   * * `deception` - Deception
   * * `transport` - Transport
   * * `private_home` - Private Home
   * * `deliveries` - Deliveries
   * * `safety` - Safety
   * * `facilities` - Facilities
   * * `aggression` - Aggression
   * * `recklessness` - Recklessness
   * * `covid` - Covid
   * * `smoking` - Smoking
   * * `alcohol` - Alcohol
   * * `drugs` - Drugs
   * * `possession` - Possession
   * * `reputation` - Reputation
   * * `refusal` - Refusal
   * * `abusive_behaviour` - Abusive Behaviour
   */
  subtype?: SubtypeEnum
  /**
   * * `merit` - Merit
   * * `verbal_commendation` - Verbal commendation
   * * `written_commendation` - Written commendation
   * * `certificate` - Certificate
   * * `verbal_warning` - Verbal warning
   * * `written_warning` - Written warning
   * * `ultimatum` - Ultimatum
   * * `suspension` - Suspension
   * * `exclusion` - Exclusion
   */
  sanction?: NextLastSanctionEnum
  /** @format double */
  merit_factor?: number
  /**
   * @min -2147483648
   * @max 2147483647
   */
  immediate_downgrade?: ImmediateDowngradeEnum | NullEnum | null
  /**
   * * `week` - Weekly
   * * `quarter` - Quarterly
   * * `semester` - Semesterly
   * * `year` - Yearly
   */
  escalation_expire_after?: EscalationExpireAfterEnum
  /**
   * * `merit` - Merit
   * * `demerit` - Demerit
   */
  merit_type?: MeritTypeEnum
  profiles_that_can_review?: string[]
  profiles_that_can_add?: string[]
  allow_to_select?: boolean
  all_profiles_can_add?: boolean
  is_automatic_approve?: boolean
  next_merit_item?: number | null
  next_last_sanction?: NextLastSanctionEnum | BlankEnum
  has_grade_based_escalation_factor?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  escalation_factor?: number | null
  /**
   * @min 0
   * @max 2147483647
   */
  pri_escalation_factor?: number | null
  /**
   * @min 0
   * @max 2147483647
   */
  msl_escalation_factor?: number | null
  /**
   * @min 0
   * @max 2147483647
   */
  hsl_escalation_factor?: number | null
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PatchedMeritRequest = {
  /** @format uuid */
  student?: string
  /** @format uuid */
  course?: string | null
  /** @minLength 1 */
  note?: string
  merit_item?: number
  /**
   * * `merit` - Merit
   * * `demerit` - Demerit
   */
  merit_type?: MeritTypeEnum
  /** @format double */
  alcohol_level?: number | null
  /** @format double */
  value?: number
  house_room?: number | null
}

export type PatchedMeritRewardParticipantUpdateRequest = {
  /**
   * * `rejected` - rejected
   * * `accepted` - accepted
   */
  status?: MeritRewardParticipantUpdateStatusEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedMissionControlRequest = {
  degree_aim_1_id?: number | null
  degree_destination_1?: NationalityEnum | BlankEnum
  degree_aim_2_id?: number | null
  degree_destination_2?: NationalityEnum | BlankEnum
  interests?: number[]
  skills?: number[]
  competencies?: number[]
  /** @format date */
  estimated_completion?: string | null
  /** Career Aim (Goal) */
  career_aim?: string
  recommendations?: string
  /** @format uuid */
  student?: string
}

export type PatchedNightAttendanceCommentRequest = {
  author?: string
  /** @minLength 1 */
  content?: string
  mark_disturbance?: boolean
}

export type PatchedNightAttendanceReportRequest = {
  night_guard?: string
  /**
   * * `male` - Male
   * * `female` - Female
   */
  for_gender?: ForGenderEnum
  has_disruption?: boolean
}

export type PatchedNightAttendanceRequest = {
  mark?: NightAttendanceMarkEnum | BlankEnum
  /** @format uuid */
  student?: string
  /** @minLength 1 */
  disturbance_note?: string
  electronics_involved?: boolean
  is_at_home?: boolean
  is_at_campus_absence?: boolean
  /** @format time */
  bedtime?: string | null
  /** @minLength 1 */
  boarding_status?: string
}

export type PatchedNotificationSettingsRequest = {
  accept_campus_notifications?: boolean
  accept_education_notifications?: boolean
  accept_reminders_notifications?: boolean
  accept_dashboard_notifications?: boolean
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedParentDetailRequest = {
  /** @maxLength 128 */
  first_name?: string
  /** @maxLength 128 */
  last_name?: string
  /** @maxLength 128 */
  maiden_name?: string
  /**
   * Email address
   * @format email
   * @minLength 1
   * @maxLength 254
   */
  email?: string
  /**
   * @format email
   * @maxLength 254
   */
  secondary_email?: string
  /**
   * @minLength 1
   * @maxLength 128
   */
  phone_number?: string
  /** @minLength 1 */
  salutation?: string
  /** @maxLength 128 */
  title?: string
  occupation?: string
  current_company?: ParentCompanyInputRequest | null
  /** @maxItems 250 */
  nationalities?: (NationalityEnum | BlankEnum)[]
  native_languages?: NativeLanguageEnum[]
  /**
   * * `parent` - Parent
   * * `legal_guardian` - Legal Guardian
   * * `in_legal_guardian_absence` - In Legal Guardian Absence
   * * `arrival_contact` - Arrival Contact
   * * `educational_consultant` - Educational Consultant
   * * `main_invoice_recipient` - Main Invoice Recipient
   * * `invoice_recipient` - Invoice Recipient
   * * `contact_leave` - Contact Leave
   * * `invoice_copy_recipient` - Invoice Copy Recipient
   * * `assistant` - Assistant
   * * `relative` - Relative
   * * `other` - Other
   */
  contact_type?: ContactTypeEnum
  contact_leave_request?: boolean
  is_allowed_to_visit?: boolean
  /**
   * Active
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  academic_details?: ParentAcademicDetailsRequest
  /** @maxLength 2000 */
  crucial_information?: string
  billing_additional_email_id?: number | null
  inactive_note_content?: string
}

export type PatchedParentProfileRequest = {
  favourites?: string[]
}

export type PatchedParentStudentContactCreateRequest = {
  /** @format uuid */
  student?: string
  /** Parent roles for student */
  roles?: ContactTypeEnum[]
  has_app_access?: boolean
}

export type PatchedParentStudentContactUpdateRequest = {
  /** Parent roles for student */
  roles?: ContactTypeEnum[]
  has_app_access?: boolean
}

export type PatchedParentTAEStudentEnrolmentUpdateRequest = {
  /** @minLength 1 */
  parent_comment?: string
}

export type PatchedProgrammeAttendanceReportRequest = {
  programme?: number
  /** @maxLength 64 */
  location?: string
  /**
   * * `new` - New
   * * `incomplete` - Incomplete
   * * `finished` - Finished
   */
  status?: BoardingAttendanceReportStatusEnum
  assigned_to?: string
}

export type PatchedProgrammeStudentAttendanceRequest = {
  report?: number
  /** @format date-time */
  marked_at?: string | null
  is_present?: boolean
  is_late?: boolean
  is_absent_unexcused?: boolean
  /** is exempt */
  is_absent_excused?: boolean
  is_sick?: boolean
  is_disturbance?: boolean
  is_marked_automatically?: boolean
  is_marked?: boolean
  disturbance_merit_note?: string
}

/** Adds nested create feature */
export type PatchedProgressCommentDeadlineRequest = {
  /** @format date-time */
  comment_deadline?: string
}

/** Adds nested create feature */
export type PatchedProgressCommentDraftRequest = {
  draft?: any
  /** @format date-time */
  draft_created_at?: string | null
  /** @format date-time */
  draft_modified_at?: string | null
  draft_added_by?: string
  draft_last_modified_by?: string
}

/** Adds nested create feature */
export type PatchedProgressCommentEvaluateRequest = {
  added_by?: string
  last_modified_by?: string
  /**
   * @min 1
   * @max 5
   */
  strategy_efficiency?: number | null
  strategy_efficiency_comment?: string
}

/** Adds nested create feature */
export type PatchedProgressCommentRequest = {
  added_by?: string
  last_modified_by?: string
  /** @format date */
  start_date?: string
  /** @format date */
  end_date?: string
  /** @format date-time */
  comment_deadline?: string
  /** @format uuid */
  student?: string
  /** @format uuid */
  course?: string
  semester?: number
  /** @minLength 1 */
  semester_grade?: string
  quarter?: number
  /** @minLength 1 */
  quarter_grade?: string
  /** @minLength 1 */
  potential_grade?: string
  /**
   * * `pending` - Pending
   * * `much_above` - Much Above
   * * `above` - Above
   * * `below` - Below
   * * `much_below` - Much Below
   */
  target_grade_status?: TargetGradeStatusEnum
  /**
   * @min 1
   * @max 5
   */
  strategy_efficiency?: number | null
  strategy_efficiency_comment?: string
  achievement_and_effort?: AchievementAndEffortProgressCommentRequest
  behaviour_and_attitude?: BehaviourAndAttitudeProgressCommentRequest
  student_target?: StudentTargetProgressCommentRequest
  intervention?: InterventionProgressCommentRequest
}

/** Adds nested create feature */
export type PatchedPublishBoardRequest = {
  widget?: PublishWidgetSimpleRequest
  publish_on_infoboards?: boolean
  /** @format date-time */
  display_from?: string | null
  /** @format date-time */
  display_to?: string | null
  pinned_in_apps?: AppTypeEnum[]
}

export type PatchedRoomBookingUpdateRequest = {
  /** @format date */
  start_date?: string
  /** @format uuid */
  booked_for?: string | null
  /**
   * * `reserved` - Reserved
   * * `social_room` - Social room
   * * `duty_room` - Duty room
   * * `artisan_rest_room` - Artisan rest room
   * * `storage` - Storage
   * * `artisan_private_room` - Artisan private room
   */
  purpose?: PurposeEnum
  last_modified_by?: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedRoomRequest = {
  /**
   * @minLength 1
   * @maxLength 128
   */
  name?: string
  /**
   * @min 0
   * @max 2147483647
   */
  capacity?: number | null
  is_private?: boolean
  is_external?: boolean
  msl_room?: boolean
  pri_room?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  group?: RoomGroupEnum | NullEnum | null
  schedule_automatically?: boolean
  available_for_exam?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  capacity_for_exam?: number | null
  department?: number
  location_id?: number | null
  override_allowed_slots?: boolean
  allowed_slots?: RoomAllowedSlotsRequest[] | null
  allow_multiple_activities?: boolean
  /**
   * @min 2
   * @max 2147483647
   */
  max_activities_number?: number | null
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PatchedSchoolBreakRequest = {
  /**
   * @minLength 1
   * @maxLength 100
   */
  name?: string
  /** @format date */
  start_date?: string
  /** @format date */
  end_date?: string
  disable_lunchtime_recovery_in_the_preceding_week?: boolean
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedSchoolProgrammeRequest = {
  is_compulsory?: boolean
  /**
   * * `academics` - Academics
   * * `house_evening` - House Evening
   * * `term_break` - Term Break
   * * `school_trip` - School Trip
   * * `weekend_programme` - Weekend Programme
   * * `exam_preparation` - Exam Preparation
   * * `other` - Other
   * * `absence_recovery` - Absence Recovery
   * * `lunchtime_recovery` - Lunchtime Recovery
   */
  type?: SchoolProgrammeTypeEnum
  /**
   * @minLength 1
   * @maxLength 128
   */
  title?: string
  description?: string
  parental_approval_required?: boolean
  message_to_parents?: string
  enrol_all_students?: boolean
  categories?: CategoriesEnum[]
  houses?: number[]
  grade_levels?: number[]
  ages?: number[]
  sections?: number[]
  added_by?: string
  duplicate_programme?: number
  last_modified_by?: string
  leaders?: string[]
  members?: string[]
  /** @format date-time */
  start_date?: string
  /** @format date-time */
  end_date?: string
  /** @format date-time */
  enrolment_deadline?: string | null
  room?: number | null
  /**
   * @format decimal
   * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
   */
  cost?: string | null
  students_limit?: number
  has_enrolments?: boolean
  risk_assessment_required?: boolean
  risk_assessment_file?: number | null
  other_files?: number[]
  enrolments?: ProgrammeEnrolmentRequest[]
  rejection_reason?: string
  is_recurring?: boolean
  recurring?: RecurringProgrammeRequest | null
}

export type PatchedSectionGroupPlanUpdateAssignmentsRequest = {
  q1_section_plan?: SectionPlanAssignmentsUpdateRequest
  q2_section_plan?: SectionPlanAssignmentsUpdateRequest
  q3_section_plan?: SectionPlanAssignmentsUpdateRequest
  q4_section_plan?: SectionPlanAssignmentsUpdateRequest
}

export type PatchedSectionGroupPlanUpdateTimelineRequest = {
  section_group_id?: number
  q1_section_plan?: SectionPlanRequest
  q2_section_plan?: SectionPlanRequest
  q3_section_plan?: SectionPlanRequest
  q4_section_plan?: SectionPlanRequest
}

/** Adds nested create feature */
export type PatchedSectionRequest = {
  enrollments?: SectionEnrollmentRequest[]
  create_for_quarters?: number[]
  is_mentoring?: boolean | null
  must_be_consecutive?: boolean
  /** attendances + grades */
  co_teacher_1_perm_to_manage_lesson?: boolean
  /** attendances + grades */
  co_teacher_2_perm_to_manage_lesson?: boolean
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
  quarter?: number
  /** @format uuid */
  course?: string
  /**
   * Main teacher
   * @format uuid
   */
  teacher?: string
  teachers?: string[]
}

export type PatchedSickReportAbsenceDatesRequest = {
  created_by?: string
  last_modified_by?: string
  /** @minLength 1 */
  sick_report?: string
  /** @format date-time */
  absent_from?: string | null
  /** @format date-time */
  absent_to?: string | null
  /**
   * * `regular` - Regular
   * * `sport` - Sport
   */
  class_type?: ClassTypeEnum
  omit_campus_checks?: boolean
}

export type PatchedSickReportCommentRequest = {
  author?: string
  /** @minLength 1 */
  content?: string
}

export type PatchedSickReportRequest = {
  /**
   * * `regular` - Regular
   * * `med_only` - Med-only
   */
  type?: SickReportTypeEnum
  /** @format uuid */
  student?: string
  generate_absences_on_create?: boolean
  close_reason?: CloseReasonEnum | BlankEnum
  /** @format date-time */
  closed_at?: string | null
  created_by?: string
  last_modified_by?: string
}

export type PatchedStudentAchievementRequest = {
  achievement?: number
  /** @format date */
  date?: string
}

export type PatchedStudentAddressRequest = {
  label?: string
  /** @minLength 1 */
  name?: string
  /**
   * @min 0
   * @max 32767
   */
  address_type?: AddressTypeEnum
  /** @minLength 1 */
  address?: string
  /** @minLength 1 */
  address_city?: string
  /** @minLength 1 */
  address_zipcode?: string
  /** @minLength 1 */
  address_country?: string
}

export type PatchedStudentAimForCourseRequest = {
  /** @format uuid */
  student?: string
  /** @format uuid */
  course?: string
  aim?: number | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedStudentAllergyRequest = {
  /** @format uuid */
  student?: string
  /** @minLength 1 */
  allergen_kind?: string
  allergen_subtypes?: string[]
  reactions?: StudentAllergyReactionRequest[]
  medicines?: string[]
  is_emergency_medicine?: boolean
  medicine_location?: MedicineLocationEnum | BlankEnum
  immediate_response?: StudentAllergyImmediateResponseEnum | BlankEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedStudentCampusAbsenceRequest = {
  created_by?: string
  /**
   * * `afternoon_ausgang` - Afternoon Ausgang
   * * `evening_ausgang` - Evening Ausgang
   * * `evening_campus_ausgang` - Evening - Campus Ausgang
   * * `birthday_ausgang` - Birthday Ausgang
   * * `family_visit` - Family visit
   * * `on_board` - On board Ausgang
   * * `prolonged_ausgang` - Prolonged Ausgang
   * * `campus` - Campus
   * * `offsite` - Offsite
   * * `offsite_overnight` - Offsite, Overnight
   * * `official_appointment` - Official appointment
   * * `specialist_doctor_appointment` - Specialist Doctor Appointment
   * * `programme` - Programme
   * * `house_evening` - House evening
   * * `term_break` - Term Break
   * * `school_trip` - School Trip
   * * `weekend_programme` - Weekend Programme
   * * `other` - Other
   * * `book_the_cafe` - Book the Cafe
   * * `city_brunch_zurich` - City Brunch Zurich
   * * `additional_ausgang` - Additional Ausgang
   * * `on_campus_ausgang` - On Campus Ausgang
   */
  campus_absence_subtype?: CampusAbsenceSubTypeEnum
  custom_name?: string
  leave_details?: string
  /** @maxLength 256 */
  contact_person_name?: string
  /** @maxLength 128 */
  contact_person_phone?: string
  /** @format date-time */
  date_from?: string
  /** @format date-time */
  date_to?: string
  campus_absence_students?: StudentCampusAbsenceStudentRequest[]
}

export type PatchedStudentCategoryItemRequest = {
  /** @format uuid */
  student?: string
  /**
   * @min -2147483648
   * @max 2147483647
   */
  assigned_category?: AssignedCategory92AEnum
  is_completed?: boolean
}

export type PatchedStudentChangePhotoRequest = {
  /**
   * @format binary
   * @pattern (?:png|jpg|jpeg)$
   */
  photo?: File
}

export type PatchedStudentChronicConditionRequest = {
  /** @format uuid */
  student?: string
  /** @minLength 1 */
  condition_type?: string
  condition_subtype?: string[]
  other_condition_type?: string
  impact?: ImpactEnum[]
  watch_out_for?: WatchOutForEnum[]
  emergency_medicines?: string[]
  emergency_medicine_applications?: EmergencyMedicineApplicationsEnum[]
  emergency_medicine_location?: EmergencyMedicineLocationEnum[]
  iep_contact?: IepContactEnum[]
  immediate_response?: StudentChronicConditionImmediateResponseEnum | BlankEnum
  comment?: string
  has_medicine_plan?: boolean
  has_emergency_medicine?: boolean
  has_iep?: boolean
}

export type PatchedStudentContentRequest =
  | PatchedAppMysteryLinkRequest
  | PatchedAppStudentAnnouncementRequest
  | PatchedAppStudentPicRequest

export type PatchedStudentCourseRequirementRequest = {
  /** @minLength 1 */
  min_grade?: string
  /** @minLength 1 */
  current_grade?: string
  /** @minLength 1 */
  potential_grade?: string
  /**
   * @min 0
   * @max 100
   */
  min_percents?: number | null
  /**
   * @min 0
   * @max 100
   */
  current_percents?: number | null
  /**
   * @min 0
   * @max 100
   */
  potential_percents?: number | null
  aim?: number | null
  year?: number
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedStudentDetailRequest = {
  /** @maxLength 256 */
  nickname?: string
  /**
   * @minLength 1
   * @maxLength 128
   */
  first_name?: string
  /**
   * @minLength 1
   * @maxLength 128
   */
  last_name?: string
  /** @maxLength 256 */
  name?: string
  /** @maxLength 256 */
  title?: string
  current_grade_level?: number | null
  /**
   * Email address
   * @format email
   * @minLength 1
   * @maxLength 254
   */
  email?: string
  billing_additional_email_id?: number | null
  /** @maxLength 128 */
  phone_number?: string
  /** @maxItems 250 */
  nationalities?: (NationalityEnum | BlankEnum)[]
  native_language?: NativeLanguageEnum | BlankEnum
  education_details?: string
  /** @format date */
  date_of_birth?: string
  birthplace?: string
  /**
   * * `male` - Male
   * * `female` - Female
   */
  gender?: ForGenderEnum
  is_at_home?: boolean
  saa_student?: boolean
  is_statemented?: boolean
  /**
   * @min -2147483648
   * @max 2147483647
   */
  exam_extra_time?: ExamExtraTimeEnum | NullEnum | null
  access_arrangements?: AccessArrangementsEnum[]
  is_draft?: boolean
  external_id_in_invoice_system?: string
  /**
   * @min 0
   * @max 2147483647
   */
  saldo_status?: SaldoStatusEnum
  has_any_dietary_restrictions?: boolean
  dietary_restrictions?: string
  /** @format date */
  date_of_entry?: string | null
  /** @format date */
  date_of_depart?: string | null
  /** @format date */
  diploma_date?: string | null
  diploma_type?: DiplomaTypeEnum | BlankEnum | NullEnum | null
  year_of_rosenberg_entry?: number | null
  is_resident_entrepreneur?: boolean | null
  /** This field is needed for the summer camps instance */
  ever_expelled_from_school?: boolean
  /**
   * @min 0
   * @max 32767
   */
  academic_status?: AcademicStatusEnum | NullEnum | null
  member_of_alumni_association?: boolean
  collabs_with_alumni_app?: boolean
  zemis_no?: string
  /** @format date */
  residence_permit_valid_thru?: string | null
  /** @format date */
  passport_valid_thru?: string | null
}

export type PatchedStudentDietaryRestrictionsRequest = {
  has_any_dietary_restrictions?: boolean
  dietary_restrictions?: string
}

export type PatchedStudentDocumentRequest = {
  /** @format uuid */
  student?: string
  /** @format binary */
  file?: File
  /**
   * @min 0
   * @max 32767
   */
  document_type?: DocumentTypeEnum
  /**
   * @minLength 1
   * @maxLength 256
   */
  document_name?: string
  access_profiles_override?: string[] | null
  added_by?: string
  last_modified_by?: string
}

export type PatchedStudentExternalCreditsRequest = {
  /**
   * @format double
   * @min -10000
   * @exclusiveMin true
   * @max 10000
   * @exclusiveMax true
   */
  external_credits?: number
  include_external_credits_in_transcript?: boolean
}

export type PatchedStudentExternalExamRequest = {
  /**
   * * `ACT` - ACT
   * * `A_LEVEL` - A Level (GCE), iGCSE
   * * `AP` - Advanced Placement
   * * `CAMBRIDGE` - CAMBRIDGE
   * * `DELE` - DELE
   * * `DELF` - DELF
   * * `GCSE` - GCSE
   * * `GMAT` - GMAT
   * * `GOETHE` - GOETHE
   * * `IAL` - International A Level
   * * `IB` - International Baccalaureate
   * * `IELTS` - IELTS
   * * `LAMDA` - LAMDA
   * * `MAP` - MAP
   * * `SAT_ENG` - SAT ENG
   * * `SAT_MAT` - SAT MAT
   * * `WIDA` - WIDA
   */
  type?: ExternalExamTypeEnum
  /** @format date */
  date?: string | null
  /**
   * Field allows to set only calendar year instead of full date
   * @maxLength 4
   */
  calendar_year?: string | null
  /** @format double */
  min_score?: number | null
  /** @format double */
  score?: number | null
  /** @maxLength 16 */
  subtype?: string
  /**
   * The external course name provided by user
   * @maxLength 255
   */
  external_course_name?: string | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedStudentIdpPlanRequest = {
  baskets?: BasketRequest[]
  /** @format uuid */
  student?: string
  note?: string
  grade_level?: number
  /** @minLength 1 */
  aim?: string
  year?: number
  draft_payload?: any
  exams?: ExamScoreRequest[]
  warnings?: ParentWarningRequest[]
  /** @default true */
  commit?: boolean
  /** @format double */
  credit_count?: number | null
  courses_mission_control_departments?: CourseMissionControlDepartmentRequest[]
  parent_message?: string
}

/** Validate ownership before nested replacement deletes existing records. */
export type PatchedStudentInternshipsUpdateRequest = {
  internships?: StudentInternshipUpdateRequest[]
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PatchedStudentLeaveRequestRequest = {
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  check_out?: StudentLeaveCheckRequest
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  check_in?: StudentLeaveCheckRequest
  /**
   * @min -2147483648
   * @max 2147483647
   */
  leave_type?: LeaveRequestTypeEnum
  student_message?: string
}

export type PatchedStudentMedicalInfoRequest = {
  /** @format double */
  height?: number | null
  /** @format double */
  weight?: number | null
  blood_type?: BloodTypeEnum | BlankEnum
  is_student_swimmer?: boolean | null
  is_student_athlete?: boolean | null
  student_fitness_for_sports?: StudentFitnessForSportsEnum | BlankEnum
  insurance_type?: InsuranceTypeEnum | BlankEnum
  /** @maxLength 64 */
  insurance_number?: string
  /** @maxLength 64 */
  insurance_client_number?: string
  /** @maxLength 32 */
  ahv_number?: string
  has_student_contact_with_drugs?: boolean
  is_more_medical_information_available?: boolean
  more_medical_information?: string
}

export type PatchedStudentProfileRequest = {
  allow_push_notifications?: boolean
  allow_email_notifications?: boolean
  favourites?: string[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedStudentPsychologicalCareRequest = {
  has_any_mental_health_issues?: boolean
  has_regular_psychological_care?: boolean
  summer_mental_health_issues?: SummerMentalHealthIssueRequest[]
  medical_reports?: MedicalReportAttachmentRequest[]
}

export type PatchedStudentSaldoStatusRequest = {
  /**
   * @min 0
   * @max 2147483647
   */
  saldo_status?: SaldoStatusEnum
}

export type PatchedStudentSchoolHistoryRequest = {
  /** @format uuid */
  student?: string
  /** @minLength 1 */
  school_name?: string
  /**
   * @min 1
   * @max 12
   */
  school_from_month?: SchoolFromMonthEnum
  /**
   * @min 0
   * @max 2147483647
   */
  school_from_year?: number
  /**
   * @min 1
   * @max 12
   */
  school_until_month?: SchoolUntilMonthEnum
  /**
   * @min 0
   * @max 2147483647
   */
  school_until_year?: number
}

export type PatchedStudentStudiedLanguageRequest = {
  /** @format uuid */
  student?: string
  /**
   * * `german` - German
   * * `french` - French
   * * `english` - English
   * * `latin` - Latin
   * * `italian` - Italian
   * * `spanish` - Spanish
   * * `other` - Other
   */
  language_name?: LanguageNameEnum
  language_name_other?: string
  /**
   * * `a1` - A1
   * * `a2` - A2
   * * `b1` - B1
   * * `b2` - B2
   * * `c1` - C1
   * * `c2` - C2
   * * `other` - Other
   */
  language_level?: LanguageLevelEnum
  language_level_other?: string
  /**
   * @min 0
   * @max 2147483647
   */
  language_year?: number
}

/** Validate ownership before nested replacement deletes existing records. */
export type PatchedStudentTAEExperienceUpdateRequest = {
  tae_experiences?: StudentTAEExperienceRequest[]
}

/** Validate ownership before nested replacement deletes existing records. */
export type PatchedStudentTAEProjectUpdateRequest = {
  tae_projects?: StudentTAEProjectRequest[]
}

export type PatchedStudentTranscriptRequest = {
  grade_level?: number
  /**
   * @min -2147483648
   * @max 2147483647
   */
  semester?: BoardingCommentSemesterEnum
  /** @maxLength 256 */
  school?: string
  status?: StudentTranscriptStatusEnum | BlankEnum
}

/** Validate ownership before nested replacement deletes existing records. */
export type PatchedStudentTranscriptUpdateRequest = {
  transcripts?: StudentTranscriptUpdateItemRequest[]
}

export type PatchedSummerAcademicCommentRequest = {
  author?: string
  /**
   * @minLength 1
   * @maxLength 2000
   */
  content?: string
}

export type PatchedSummerBoardingCommentRequest = {
  author?: string
  /**
   * @minLength 1
   * @maxLength 2000
   */
  content?: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedSummerCampStudentAssignsRequest = {
  /** Course booking additional comment */
  additional_comments?: string
  summer_assigns?: SummerSubjectAssignRequest[]
  /**
   * * `standard` - Standard
   * * `special` - Special
   */
  package?: SummerPackageTypeEnum
  /**
   * @min 1
   * @max 4
   */
  duration?: number | null
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PatchedSummerCampStudentRequest = {
  /**
   * * `standard` - Standard
   * * `special` - Special
   */
  package?: SummerPackageTypeEnum
  has_tetanus_vaccination?: boolean
  /** @format date */
  tetanus_vaccination_date?: string | null
}

export type PatchedSummerCertificateDraftRequest = {
  /** It's a helper field for storing data only for frontend purposes. */
  draft?: any
}

export type PatchedTAECourseEnrolmentRequest = {
  grades?: number[]
  /**
   * @min 0
   * @max 32767
   */
  minimum_participants?: number | null
  /**
   * @min 0
   * @max 32767
   */
  capacity_per_section?: number | null
  /**
   * @min 0
   * @max 32767
   */
  max_sections?: number | null
}

export type PatchedTAEDraftSectionEnrolmentUpdateRequest = {
  /**
   * @min 1
   * @max 25
   */
  number_of_lessons?: number
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedTAEDraftSectionRequest = {
  /** @format uuid */
  teacher?: string
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
  course_enrolment?: number
  quarter?: number
  enrolments?: TAEDraftSectionEnrolmentRequest[]
}

export type PatchedTAEDraftSectionUpdateRequest = {
  /** @format uuid */
  teacher?: string
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
}

export type PatchedTAEEnrollmentStudentChoiceRequest = {
  /**
   * @min 0
   * @max 32767
   */
  number_of_lessons?: number | null
}

export type PatchedTAEEnrolmentV2ListRequest = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name?: string
  /** @format date */
  start?: string | null
  /** @format date-time */
  deadline_recurring?: string | null
  /** @format date-time */
  deadline_new?: string | null
  /**
   * * `DRAFT` - Draft
   * * `SCHEDULED` - Scheduled
   * * `OPEN` - Open
   * * `CLOSED` - Closed
   * * `ARCHIVED` - Archived
   */
  status?: Status8FbEnum
}

export type PatchedTAEPlanRequest = {
  school_year?: number
}

export type PatchedTAERemoveDraftEnrolmentRequest = {
  draft_section_enrolment?: number
  /**
   * RPS-14268
   * @default false
   */
  withdraw_course_approval?: boolean
}

/**
 * Admin edit of the lessons selected for a student's course choice.
 *
 * `number_of_lessons_original_choice` is deliberately absent: it records what
 * the student requested and stays the "Requested" select option forever.
 */
export type PatchedTAEStudentChoiceNumberOfLessonsRequest = {
  /**
   * @min 1
   * @max 25
   */
  number_of_lessons?: number
}

export type PatchedTAEStudentEnrollmentUpdateRequest = {
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_membership?: SportMembershipOriginalChoiceEnum
}

export type PatchedTAEStudentEnrolmentSportMembershipRequest = {
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_membership?: SportMembershipOriginalChoiceEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedTaskRequest = {
  /** @minLength 1 */
  title?: string
  description?: string
  /**
   * @format uri
   * @maxLength 200
   */
  link?: string
  /** @format date-time */
  deadline?: string | null
  created_by?: string
  /** @format uuid */
  assignee?: string | null
  /** @format uuid */
  team?: string | null
  subtasks?: SubtaskRequest[]
  done_comment?: string
  recurring?: RecurringRequest | null
  is_cloned?: boolean
  /** @minLength 1 */
  cloned_from?: string | null
}

export type PatchedTeacherTimetableUpdateLessonRequest = {
  /** @format uuid */
  section_plan_item_id?: string
  /** @format uuid */
  finished_before_planned_time_item_id?: string
  /**
   * * `selected-suggested-subtopic` - Selected Suggested Subtopic
   * * `changed-subtopic` - Changed Subtopic
   */
  action_type?: TeacherTimetableUpdateLessonActionTypeEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedTeamRequest = {
  /** @minLength 1 */
  name?: string
  house?: number | null
  members?: TeamMemberRequest[]
  include_in_boarding_rota?: boolean
  allow_manual_boarding_rota_assignment?: boolean
}

export type PatchedTemperatureCheckRequest = {
  /** @format uuid */
  health_check?: string
  /** @format double */
  temperature?: number
}

export type PatchedTimetableAutoImportConfigUpdateRequest = {
  is_enabled?: boolean
  is_daily?: boolean
  days?: RotaDayEnum[]
  /** @format time */
  time?: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedTimetableMentoringRequest = {
  ignored_students?: string[]
  timetable?: number
  grades_to_assign?: number[]
  /**
   * @min 0
   * @max 2147483647
   */
  students_in_section?: number
  copy_from_previous_quarter?: boolean
  teachers_per_grade?: TeachersPerGradeRequest[]
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PatchedTimetableRequest = {
  /**
   * @format decimal
   * @pattern ^-?\d{0,2}(?:\.\d{0,2})?$
   */
  gaps_per_students?: string | null
  /**
   * * `live` - Live
   * * `active_in_past` - Active in past
   * * `generation_in_progress` - Generation in progress
   * * `draft` - Draft
   * * `infeasible` - Infeasible data. Please increase time, or verify sections
   * * `failed` - Generation process failed
   */
  status?: Status2A1Enum
  /** Enabled preview for Artisans */
  enabled_preview_for_teachers?: boolean
  has_tae_sections_changed?: boolean
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type PatchedTopicRequest = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name?: string
  description?: string | null
  /** @format uuid */
  domain_id?: string
  department_ids?: number[]
  grade_levels_ids?: number[]
  subtopics?: SubtopicRequest[]
  aim_ids?: number[]
}

export type PatchedUserAdditionalEmailRequest = {
  /**
   * @format email
   * @minLength 1
   * @maxLength 254
   */
  email?: string
}

export type PatchedUserAdditionalPhoneNumberRequest = {
  /**
   * @minLength 1
   * @maxLength 128
   */
  phone_number?: string
}

export type PatchedUserAddressRequest = {
  label?: string
  name?: string
  /**
   * @min 0
   * @max 32767
   */
  address_type?: AddressTypeEnum
  address?: string
  address_city?: string
  address_zipcode?: string
  address_country?: string
}

export type PatchedUserNoteRequest = {
  /** @maxLength 255 */
  title?: string
  /**
   * @minLength 1
   * @maxLength 2000
   */
  note?: string
  /**
   * * `academic` - Academic
   * * `administrative` - Administrative
   * * `admission` - Admission
   * * `archived` - Archived
   * * `boarding` - Boarding
   * * `medical` - Medical
   * * `other` - Other
   */
  note_type?: NoteTypeEnum
  access_profiles_override?: string[] | null
  is_type_locked?: boolean
  added_by?: string
  last_modified_by?: string
}

export type PatchedUserRelationUpdateRequest = {
  /**
   * @min 0
   * @max 32767
   */
  relation_type?: RelationTypeEnum
  /** Roles for this relation (synced with ParentStudentConnection) */
  roles?: ContactTypeEnum[] | null
  has_app_access?: boolean
}

export type PatchedUserTaskTitleRequest = {
  /** @minLength 1 */
  title?: string
  added_by?: string
  last_modified_by?: string
}

export type PatchedWeeklyCommentRequest = {
  last_modified_by?: string
  /**
   * @minLength 1
   * @maxLength 2000
   */
  content?: string
  /** @format uuid */
  course?: string
  /** @format uuid */
  student?: string
}

export type PendingMerit = {
  id: number
  /** @format date-time */
  created: string
}

export type PendingMeritsPreview = {
  merits: PendingMerit[]
}

export type PendingStudentContentItems = {
  /** @min 0 */
  StudentAnnouncement: number
  /** @min 0 */
  StudentMysteryLink: number
  /** @min 0 */
  StudentPic: number
}

export type People = {
  teachers: Person[]
  co_teachers: Person[]
  leaders: Person[]
  invigilators: Person[]
  rostered: Person[]
  covered_by: Person[]
  covers_for: Person[]
}

export type Period = {
  /**
   * @min 0
   * @max 5
   */
  day: number
  /** @format time */
  start: string
  /** @format time */
  end: string
}

export type PeriodGPA = {
  id: number
  /**
   * @format decimal
   * @pattern ^-?\d{0,6}(?:\.\d{0,4})?$
   */
  unweighted_value: string
  /**
   * @format decimal
   * @pattern ^-?\d{0,6}(?:\.\d{0,4})?$
   */
  weighted_value: string
  period: string
  gpa_for_courses: GPAForCourse[]
}

export type Person = {
  /** @format uuid */
  id: string
  full_name: string
}

export type PostsInfo = {
  ever_created_post: boolean
}

export type PotentailGrades = {
  Q1: PotentialGradesViewPeriodGrade
  Q2: PotentialGradesViewPeriodGrade | null
  Q3: PotentialGradesViewPeriodGrade | null
  Q4: PotentialGradesViewPeriodGrade | null
}

/**
 * * `4` - S
 * * `3` - E
 * * `2` - A
 * * `1` - C
 * * `0` - not assigned
 */
export type PotentialCategoryEnum = 4 | 3 | 2 | 1 | 0

export type PotentialGradesViewPeriodGrade = {
  grade_id: number | null
  quarter_id: number
  grade: string | null
  target_grade: string | null
  weighted_percents: number | null
}

export type PotentialOptimization = {
  id: number
  activity: string
  conflict_students: string[]
  conflict_free_students: string[]
  no_teacher?: boolean
  private_activities_collisions: string
  /**
   * @min 0
   * @max 2147483647
   */
  cost: number
  /**
   * @min 0
   * @max 2147483647
   */
  profit: number
  /**
   * @min 0
   * @max 2147483647
   */
  balance: number
  /**
   * @min 0
   * @max 2147483647
   */
  day: RotaDayEnum
  day_display: string
  /**
   * @min 0
   * @max 15
   */
  hour?: HourEnum | NullEnum | null
  hour_display: string
}

/**
 * * `private` - Private
 * * `semiprivate` - Semiprivate
 */
export type PrivateStatusEnum = 'private' | 'semiprivate'

/**
 * * `academic_individual` - Academic Individual
 * * `sports` - Sports
 * * `co_curricular` - Co-Curricular
 * * `graduation_fee` - Graduation Fee
 * * `art_materials` - Art Materials
 * * `a_level_program` - A-level Programme
 * * `ib_programme` - IB Programme
 * * `ap_programme` - AP Programme
 * * `ielts_preparation_course` - IELTS Preparation Course
 * * `sat_preparation_course` - SAT/ACT
 * * `private_lessons` - Private Lessons
 * * `accommodation_single` - Accommodation - Single room
 * * `accommodation_double` - Accommodation - Double room
 * * `laundry` - Laundry
 * * `make_up_test` - Make-Up Tests
 * * `special_access_arrangements` - Special Access Arrangements
 */
export type ProductTypeEnum =
  | 'academic_individual'
  | 'sports'
  | 'co_curricular'
  | 'graduation_fee'
  | 'art_materials'
  | 'a_level_program'
  | 'ib_programme'
  | 'ap_programme'
  | 'ielts_preparation_course'
  | 'sat_preparation_course'
  | 'private_lessons'
  | 'accommodation_single'
  | 'accommodation_double'
  | 'laundry'
  | 'make_up_test'
  | 'special_access_arrangements'

/**
 * * `academic_individual` - Academic Individual
 * * `sports` - Sports
 * * `co_curricular` - Co-Curricular
 * * `graduation_fee` - Graduation Fee
 * * `art_materials` - Art Materials
 * * `a_level_program` - A-level Programme
 * * `ib_programme` - IB Programme
 * * `ap_programme` - AP Programme
 * * `ielts_preparation_course` - IELTS Preparation Course
 * * `sat_preparation_course` - SAT/ACT
 * * `private_lessons` - Private Lessons
 * * `accommodation_single` - Accommodation - Single room
 * * `accommodation_double` - Accommodation - Double room
 * * `laundry` - Laundry
 * * `make_up_test` - Make-Up Tests
 * * `special_access_arrangements` - Special Access Arrangements
 */
export type ProductTypesEnum =
  | 'academic_individual'
  | 'sports'
  | 'co_curricular'
  | 'graduation_fee'
  | 'art_materials'
  | 'a_level_program'
  | 'ib_programme'
  | 'ap_programme'
  | 'ielts_preparation_course'
  | 'sat_preparation_course'
  | 'private_lessons'
  | 'accommodation_single'
  | 'accommodation_double'
  | 'laundry'
  | 'make_up_test'
  | 'special_access_arrangements'

/**
 * * `dayschool` - Day School
 * * `boarding` - Boarding
 * * `external` - External
 */
export type ProgramTypeEnum = 'dayschool' | 'boarding' | 'external'

export type ProgrammeAttendanceReport = {
  id: number
  /** @format date-time */
  created: string
  programme: number
  /** @maxLength 64 */
  location?: string
  /**
   * * `new` - New
   * * `incomplete` - Incomplete
   * * `finished` - Finished
   */
  status?: BoardingAttendanceReportStatusEnum
  assigned_to?: string
  status_display: string
  previous_obj_id: number | null
  next_obj_id: number | null
  is_performable_by_requested_user: string
}

export type ProgrammeAttendanceReportRecovery = {
  id: number
  /** @format date-time */
  created: string
  programme: number
  /**
   * * `new` - New
   * * `incomplete` - Incomplete
   * * `finished` - Finished
   */
  status?: BoardingAttendanceReportStatusEnum
  status_display: string
}

export type ProgrammeAttendanceReportRecoveryRequest = {
  programme: number
  /**
   * * `new` - New
   * * `incomplete` - Incomplete
   * * `finished` - Finished
   */
  status?: BoardingAttendanceReportStatusEnum
}

export type ProgrammeAttendanceReportUpdate = {
  /** @maxLength 64 */
  location?: string
}

export type ProgrammeAttendanceReportUpdateRequest = {
  /** @maxLength 64 */
  location?: string
}

export type ProgrammeClashes = {
  id: number
  /** @maxLength 128 */
  title: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /** @default "programmes" */
  clash_type: string
}

export type ProgrammeEnrolment = {
  id: number
  status: EnrolmentStatusEnum
  rejection_comment: string
  from_custom_list?: boolean
  /** @format uuid */
  student: string
  student_data: SimpleEnrolledStudent
  student_house?: number | null
  student_grade_level: number
  /**
   * @min -2147483648
   * @max 2147483647
   */
  student_category?: StudentCategoryEnum | NullEnum | null
  student_category_label: string
  met_requirements: boolean
}

export type ProgrammeEnrolmentHouseName = {
  enrolment_house_name: string
}

export type ProgrammeEnrolmentRequest = {
  from_custom_list?: boolean
  /** @format uuid */
  student: string
  student_house?: number | null
  student_grade_level: number
  /**
   * @min -2147483648
   * @max 2147483647
   */
  student_category?: StudentCategoryEnum | NullEnum | null
}

export type ProgrammeStudentAttendance = {
  id: number
  /** @format date-time */
  created: string
  report: number
  student_str: string
  /** @format date-time */
  marked_at?: string | null
  is_present?: boolean
  is_late?: boolean
  is_absent_unexcused?: boolean
  /** is exempt */
  is_absent_excused?: boolean
  is_sick?: boolean
  is_disturbance?: boolean
  is_marked_automatically?: boolean
  is_marked?: boolean
  disturbance_merit_note?: string
  is_performable_by_requested_user: string
}

export type ProgrammeStudentAttendanceUpdate = {
  id: number
  is_present?: boolean
  is_late?: boolean
  is_absent_unexcused?: boolean
  /** is exempt */
  is_absent_excused?: boolean
  is_sick?: boolean
  is_disturbance?: boolean
  is_marked_automatically?: boolean
  disturbance_merit_note?: string
}

export type ProgrammeStudentAttendanceUpdateRequest = {
  is_present?: boolean
  is_late?: boolean
  is_absent_unexcused?: boolean
  /** is exempt */
  is_absent_excused?: boolean
  is_sick?: boolean
  is_disturbance?: boolean
  is_marked_automatically?: boolean
  disturbance_merit_note?: string
}

/** Adds nested create feature */
export type ProgressComment = {
  /** @format uuid */
  id: string
  /** @format date-time */
  created: string
  added_by?: string
  last_modified_by?: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  /** @format date-time */
  comment_deadline: string
  /** @format uuid */
  student: string
  student_name: string
  /** @format uuid */
  course: string
  course_name: string
  course_enrollment_id: number
  /** @format uuid */
  lead_teacher_id: string | null
  lead_teacher_str: string
  semester: number
  semester_grade: string
  quarter: number
  quarter_grade: string
  potential_grade: string
  /**
   * * `pending` - Pending
   * * `much_above` - Much Above
   * * `above` - Above
   * * `below` - Below
   * * `much_below` - Much Below
   */
  target_grade_status: TargetGradeStatusEnum
  fixed_target_grade_status: string
  /**
   * @min 1
   * @max 5
   */
  strategy_efficiency?: number | null
  strategy_efficiency_comment?: string
  achievement_and_effort: AchievementAndEffortProgressComment
  behaviour_and_attitude: BehaviourAndAttitudeProgressComment
  student_target: StudentTargetProgressComment
  intervention: InterventionProgressComment
  draft: any
  /** @format date-time */
  draft_created_at: string | null
  /** @format date-time */
  draft_modified_at: string | null
}

/** Adds nested create feature */
export type ProgressCommentDeadline = {
  /** @format uuid */
  id: string
  /** @format date-time */
  comment_deadline: string
}

/** Adds nested create feature */
export type ProgressCommentDraft = {
  /** @format uuid */
  id: string
  draft: any
  /** @format date-time */
  draft_created_at?: string | null
  /** @format date-time */
  draft_modified_at?: string | null
  draft_added_by?: string
  draft_last_modified_by?: string
}

/** Adds nested create feature */
export type ProgressCommentEvaluate = {
  /** @format uuid */
  id: string
  added_by?: string
  last_modified_by?: string
  /**
   * @min 1
   * @max 5
   */
  strategy_efficiency?: number | null
  strategy_efficiency_comment?: string
}

/** Adds nested create feature */
export type ProgressCommentRequest = {
  added_by?: string
  last_modified_by?: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  /** @format date-time */
  comment_deadline: string
  /** @format uuid */
  student: string
  /** @format uuid */
  course: string
  semester: number
  /** @minLength 1 */
  semester_grade: string
  quarter: number
  /** @minLength 1 */
  quarter_grade: string
  /** @minLength 1 */
  potential_grade: string
  /**
   * * `pending` - Pending
   * * `much_above` - Much Above
   * * `above` - Above
   * * `below` - Below
   * * `much_below` - Much Below
   */
  target_grade_status: TargetGradeStatusEnum
  /**
   * @min 1
   * @max 5
   */
  strategy_efficiency?: number | null
  strategy_efficiency_comment?: string
  achievement_and_effort: AchievementAndEffortProgressCommentRequest
  behaviour_and_attitude: BehaviourAndAttitudeProgressCommentRequest
  student_target: StudentTargetProgressCommentRequest
  intervention: InterventionProgressCommentRequest
}

/** Collects and raises validation errors in bulk */
export type PropertyManagerProfile = {
  id: string
  /** @format uuid */
  user?: string | null
}

/** Collects and raises validation errors in bulk */
export type PropertyManagerProfileRequest = {
  /** @format uuid */
  user?: string | null
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PublicBoard = {
  id: string
  widgets: PublicWidget[]
  widget: PublicWidget
  /** @format date-time */
  display_to?: string | null
  /**
   * * `square` - Square
   * * `framed` - Framed
   * * `fullscreen` - Fullscreen
   */
  layout?: LayoutEnum
  label_color: string | null
  /**
   * @min -2147483648
   * @max 2147483647
   */
  display_time?: number
}

export type PublicBoardingAnnouncementWidget = {
  id: string
  /** @maxLength 90 */
  text: string
  /** @default "#FD8F5E" */
  bg_color: string
  /** @format date-time */
  published_at?: string | null
  is_pinned: boolean
  /** via annotate (feed) */
  reaction_count: number
  /** via annotate (feed) */
  reacted_by_user: boolean
  /** @default "widget" */
  content_type: ContentType649Enum
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type PublicCelebrationItem = {
  id: number
  student: string
  /** via annotate (feed) */
  reaction_count: number
  /** via annotate (feed) */
  reacted_by_user: boolean
  /** @default "celebration_item" */
  content_type: PublicCelebrationItemContentTypeEnum
}

/** * `celebration_item` - celebration_item */
export type PublicCelebrationItemContentTypeEnum = 'celebration_item'

export type PublicCelebrationWidget = {
  id: string
  /** @default "#FF607F" */
  bg_color: string
  students: string[]
  /** @format date-time */
  published_at?: string | null
  is_pinned: boolean
  items: PublicCelebrationItem[]
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type PublicClockWidget = {
  id: string
  /** @default "#000000" */
  bg_color: string
  /** @format date-time */
  published_at?: string | null
  is_pinned: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type PublicCongratulationsItem = {
  id: number
  /** @maxLength 128 */
  course: string
  /** @maxLength 2 */
  grade: string
  student: string
  /** via annotate (feed) */
  reaction_count: number
  /** via annotate (feed) */
  reacted_by_user: boolean
  /** @default "congratulations_item" */
  content_type: PublicCongratulationsItemContentTypeEnum
}

/** * `congratulations_item` - congratulations_item */
export type PublicCongratulationsItemContentTypeEnum = 'congratulations_item'

export type PublicCongratulationsWidget = {
  id: string
  /** @default "#FFFFFF" */
  bg_color: string
  grades: PublicCongratulationsItem[]
  /** @format date-time */
  published_at?: string | null
  is_pinned: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type PublicDayMenu = {
  id: number
  /** @format date */
  date: string
  is_skipped: boolean
  lunch_starter: string
  lunch_soup: string
  lunch_main_course: string
  lunch_dessert: string
  lunch_starter_vege: string
  lunch_soup_vege: string
  lunch_main_course_vege: string
  lunch_dessert_vege: string
  dinner_starter: string
  dinner_soup: string
  dinner_main_course: string
  dinner_dessert: string
  dinner_starter_vege: string
  dinner_soup_vege: string
  dinner_main_course_vege: string
  dinner_dessert_vege: string
  breakfast_special: string
  breakfast_special_vege: string
}

export type PublicEventAgendaWidget = {
  id: string
  /** @default "#5965EF" */
  bg_color: string
  header: string
  /** @format date */
  date_from: string
  /** @format date */
  date_to?: string | null
  /** @format time */
  time_from?: string | null
  /** @format time */
  time_to?: string | null
  agenda_items: AgendaItem[]
  /** @format date-time */
  published_at?: string | null
  is_pinned: boolean
  /** via annotate (feed) */
  reaction_count: number
  /** via annotate (feed) */
  reacted_by_user: boolean
  /** @default "widget" */
  content_type: ContentType649Enum
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type PublicEventInfoWidget = {
  id: string
  bg_color: string
  /** @maxLength 90 */
  header: string
  /** @maxLength 400 */
  message?: string
  /** @maxLength 64 */
  classroom_shortcut?: string
  /** @maxLength 64 */
  location?: string
  /** @maxLength 64 */
  course_name?: string
  show_qr_code?: boolean
  link: PublicLinkWithQRCode
  /** @maxLength 64 */
  qr_code_text?: string
  /** @format date-time */
  published_at?: string | null
  is_pinned: boolean
  /** via annotate (feed) */
  reaction_count: number
  /** via annotate (feed) */
  reacted_by_user: boolean
  /** @default "widget" */
  content_type: ContentType649Enum
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type PublicEventInfoWithPhotoWidget = {
  id: string
  bg_color: string
  /** @maxLength 100 */
  header: string
  include_message?: boolean
  /** @maxLength 400 */
  message?: string
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  photo: PublicImage
  crop_my_rosenberg_idp_photo_separately?: boolean
  photo_in_my_rosenberg_idp: PublicImage | null
  /** @maxLength 64 */
  location?: string
  /** @maxLength 64 */
  course_name?: string
  show_qr_code?: boolean
  link: PublicLinkWithQRCode
  /** @maxLength 64 */
  qr_code_text?: string
  /** @format date-time */
  published_at?: string | null
  is_pinned: boolean
  /** via annotate (feed) */
  reaction_count: number
  /** via annotate (feed) */
  reacted_by_user: boolean
  /** @default "widget" */
  content_type: ContentType649Enum
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PublicImage = {
  id: string
  images: string
  alt: string
  title: string
  /** @format uri */
  file?: string
  height: number | null
  width: number | null
  ppoi_tuple: string
  /** @maxLength 7 */
  dominant_color?: string
  has_transparency?: boolean
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PublicImageRequest = {
  /** @format binary */
  file?: File
  /**
   * @minLength 1
   * @maxLength 7
   */
  dominant_color?: string
  has_transparency?: boolean
}

export type PublicInfoWithVideoWidget = {
  id: string
  bg_color: string
  /** @maxLength 100 */
  header: string
  include_message?: boolean
  /** @maxLength 400 */
  message?: string
  video: string | null
  /** @maxLength 64 */
  location?: string
  /** @maxLength 64 */
  course_name?: string
  show_qr_code?: boolean
  link: PublicLinkWithQRCode
  /** @maxLength 64 */
  qr_code_text?: string
  /** @format date-time */
  published_at?: string | null
  is_pinned: boolean
  /** via annotate (feed) */
  reaction_count: number
  /** via annotate (feed) */
  reacted_by_user: boolean
  /** @default "widget" */
  content_type: ContentType649Enum
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type PublicJournalItem = {
  id: string
  article_url: PublicLinkWithQRCode
  title: string
  lead: string
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  photo: PublicImage
}

export type PublicJournalWidget = {
  id: string
  /** @default "#EBCFCF" */
  bg_color: string
  journal_items: PublicJournalItem[]
  /** @format date-time */
  published_at: string | null
  is_pinned: boolean
  /** deprecated field */
  article_url: PublicLinkWithQRCode | null
  /** deprecated field */
  title: string | null
  /** deprecated field */
  lead: string | null
  /** deprecated field */
  photo: PublicImage | null
  /** via annotate (feed) */
  reaction_count: number
  /** via annotate (feed) */
  reacted_by_user: boolean
  /** @default "widget" */
  content_type: ContentType649Enum
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type PublicLeaveCheck = {
  /** @format uuid */
  id: string
  /** @maxLength 1024 */
  destination: string
  /** @maxLength 256 */
  train?: string
  /** @maxLength 256 */
  plane?: string
  /** @maxLength 256 */
  pick_up_location?: string
  /** @format time */
  pick_up_time?: string | null
  comment?: string
  /** @maxLength 64 */
  contact_person: string
  /** @maxLength 128 */
  contact_person_phone: string
  is_driver_needed?: boolean
  /** @format date-time */
  date: string
}

export type PublicLeaveRequest = {
  /** @format uuid */
  id: string
  check_out: PublicLeaveCheck
  check_in: PublicLeaveCheck
  /**
   * @min -2147483648
   * @max 2147483647
   */
  leave_type?: LeaveRequestTypeEnum
  /**
   * * `tentative` - Pending guardian
   * * `confirmed_contact_person` - Pending academic
   * * `confirmed_academic` - Pending boarding
   * * `changed` - Changed
   * * `confirmed` - Approved
   * * `declined` - Rejected
   * * `declined_contact_person` - Rejected by guardian
   * * `declined_academic` - Rejected by academic
   * * `canceled` - Canceled by student
   * * `expired` - Expired
   */
  status?: LeaveRequestStatusEnum
  student_first_name: string
}

export type PublicLinkWithQRCode = {
  /**
   * @format uri
   * @maxLength 200
   */
  url: string
  /** @format uri */
  qr_code?: string
}

export type PublicMenuWidget = {
  id: string
  /** @default "#00AC69" */
  bg_color: string
  menu: PublicDayMenu
  /** @format date-time */
  published_at?: string | null
  is_pinned: boolean
  /** via annotate (feed) */
  reaction_count: number
  /** via annotate (feed) */
  reacted_by_user: boolean
  /** @default "widget" */
  content_type: ContentType649Enum
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type PublicMessage = {
  id: number
  content: string
  /** @format date */
  display_to: string
  /**
   * * `alert` - Alert
   * * `notice` - Notice
   */
  type: MessageTypeEnum
}

export type PublicMysteryLinkWidget = {
  id: string
  /** @default "#9561E8" */
  bg_color: string
  links: PublicLinkWithQRCode[]
  is_pinned: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type PublicNextLesson = {
  /** @maxLength 36 */
  course: string
  room: string
}

export type PublicPosterWidget = {
  id: string
  /** @default "#808080" */
  bg_color: string
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  image: PublicImage
  /** @format date-time */
  published_at?: string | null
  is_pinned: boolean
  /** via annotate (feed) */
  reaction_count: number
  /** via annotate (feed) */
  reacted_by_user: boolean
  /** @default "widget" */
  content_type: ContentType649Enum
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type PublicRecentJournalArticle = {
  title: string
  lead: string
}

export type PublicStudentAnnouncementWidget = {
  id: string
  /** @default "#FFFFFF" */
  bg_color: string
  announcements: StudentAnnouncement[]
  is_pinned: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type PublicStudentArt = {
  id: number
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  image: PublicImage
  /** @maxLength 255 */
  name: string
  author: string
  /** via annotate (feed) */
  reaction_count: number
  /** via annotate (feed) */
  reacted_by_user: boolean
  /** @default "student_art" */
  content_type: PublicStudentArtContentTypeEnum
}

/** * `student_art` - student_art */
export type PublicStudentArtContentTypeEnum = 'student_art'

export type PublicStudentArtGalleryWidget = {
  id: string
  /** @default "#808080" */
  bg_color: string
  arts: PublicStudentArt[]
  /** @format date-time */
  published_at?: string | null
  is_pinned: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type PublicStudentsPicWidget = {
  id: string
  /** @default "#FFFFFF" */
  bg_color: string
  pics: StudentPic[]
  is_pinned: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type PublicUpdateStatus = {
  /** @format date-time */
  boards?: string | null
  /** @format date-time */
  alerts?: string | null
  /** @format date-time */
  notices?: string | null
  /** @format date-time */
  frontend?: string | null
}

export type PublicVariantManualCongratulations = {
  id: number
  /**
   * * `wreath` - Wreath
   * * `cap` - Cap
   */
  variant: VariantEnum
  images: PublicImage[]
  student: string
  /** @maxLength 400 */
  message: string
  /** @maxLength 128 */
  course_name?: string
  /** via annotate (feed) */
  reaction_count: number
  /** via annotate (feed) */
  reacted_by_user: boolean
  /** @default "variant_manual_congratulations" */
  content_type: PublicVariantManualCongratulationsContentTypeEnum
}

/** * `variant_manual_congratulations` - variant_manual_congratulations */
export type PublicVariantManualCongratulationsContentTypeEnum =
  'variant_manual_congratulations'

export type PublicVariantManualCongratulationsWidget = {
  id: string
  /** @default "#808080" */
  bg_color: string
  items: PublicVariantManualCongratulations[]
  /** @format date-time */
  published_at?: string | null
  is_pinned: boolean
  /**
   * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
   * * `CelebrationWidget` - CelebrationWidget
   * * `EventAgendaWidget` - EventAgendaWidget
   * * `CongratulationsWidget` - CongratulationsWidget
   * * `PosterWidget` - PosterWidget
   * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
   * * `EventInfoWidget` - EventInfoWidget
   * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
   * * `InfoWithVideoWidget` - InfoWithVideoWidget
   * * `JournalWidget` - JournalWidget
   * * `MysteryLinkWidget` - MysteryLinkWidget
   * * `MenuWidget` - MenuWidget
   * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
   * * `StudentsPicWidget` - StudentsPicWidget
   * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
   * * `ClockWidget` - ClockWidget
   */
  resourcetype: WidgetResourcetypeEnum
}

export type PublicWidget =
  | PublicBoardingAnnouncementWidget
  | PublicCelebrationWidget
  | PublicEventAgendaWidget
  | PublicCongratulationsWidget
  | PublicPosterWidget
  | PublicStudentArtGalleryWidget
  | PublicEventInfoWidget
  | PublicEventInfoWithPhotoWidget
  | PublicInfoWithVideoWidget
  | PublicJournalWidget
  | PublicMysteryLinkWidget
  | PublicMenuWidget
  | PublicStudentAnnouncementWidget
  | PublicStudentsPicWidget
  | PublicVariantManualCongratulationsWidget
  | PublicClockWidget

/** Adds nested create feature */
export type PublishBoard = {
  id: string
  widget: PublishWidgetSimple
  publish_on_infoboards?: boolean
  /** @format date-time */
  display_from?: string | null
  /** @format date-time */
  display_to?: string | null
  status: WidgetStatusEnum
  /** @format date-time */
  published_at: string | null
  pinned_in_apps?: AppTypeEnum[]
  pin_order: number
}

/** Adds nested create feature */
export type PublishBoardRequest = {
  widget: PublishWidgetSimpleRequest
  publish_on_infoboards?: boolean
  /** @format date-time */
  display_from?: string | null
  /** @format date-time */
  display_to?: string | null
  pinned_in_apps?: AppTypeEnum[]
}

export type PublishExamRequestRequest = {
  exams: string[]
}

export type PublishWidgetSimple = {
  id: string
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  status: WidgetStatusEnum
  /** @format date-time */
  published_at?: string | null
  pinned_in_apps?: AppTypeEnum[]
}

export type PublishWidgetSimpleRequest = {
  show_in_idp_app?: boolean
  show_in_parents_app?: boolean
  /** @format date-time */
  published_at?: string | null
  pinned_in_apps?: AppTypeEnum[]
}

/**
 * * `reserved` - Reserved
 * * `social_room` - Social room
 * * `duty_room` - Duty room
 * * `artisan_rest_room` - Artisan rest room
 * * `storage` - Storage
 * * `artisan_private_room` - Artisan private room
 */
export type PurposeEnum =
  | 'reserved'
  | 'social_room'
  | 'duty_room'
  | 'artisan_rest_room'
  | 'storage'
  | 'artisan_private_room'

export type PushDraftSectionsRequest = {
  quarter: number
}

/**
 * * `quarter` - Quarter
 * * `week` - Week
 * * `year` - Year
 * * `course` - Course
 * * `lesson` - Lesson
 * * `order` - Order
 */
export type QuantityUnitEnum =
  | 'quarter'
  | 'week'
  | 'year'
  | 'course'
  | 'lesson'
  | 'order'

export type Quarter = {
  id: number
  name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  semester: number
  short_name: QuarterShortNameEnum
  school_year: number
}

export type QuarterAverage = {
  quarter_id: number
  average: number | null
}

export type QuarterInCourses = {
  courses: string[]
  quarter: QuarterShortNameEnum
}

export type QuarterInCoursesRequest = {
  courses: string[]
  quarter: QuarterShortNameEnum
}

export type QuarterShortNameEnum = 'Q1' | 'Q2' | 'Q3' | 'Q4'

export type QuarterViewGrades = {
  quarter: QuarterViewPeriodGrade
  semester: QuarterViewPeriodGrade
  potential: QuarterViewPeriodGrade
}

export type QuarterViewPeriodGrade = {
  name: string
  percents: number | null
  grade: string | null
}

export type QuarterViewStudentAverageGrade = {
  percents: number
  grade: string
  name: string
}

export type QuarterViewStudentGrades = {
  grades_in_courses: QuarterViewStudentGradesInCourses[]
  grades_in_dropped_courses: QuarterViewStudentGradesInDroppedCourses[]
  periods: StudentCourseGradesPeriods
  student: StudentWithGradeLevel
}

export type QuarterViewStudentGradesInCourses = {
  course: SimpleCourseWithGroup
  artisan: SimpleUser
  aim: SimpleAim
  course_aim: SetGradeAim
  grades: QuarterViewStudentPeriodsGrades
  latest_grades: QuarterViewStudentLatestGrade[]
}

export type QuarterViewStudentGradesInDroppedCourses = {
  course: SimpleCourseWithGroup
  artisan: SimpleUser
  aim: SimpleAim
  course_aim: SetGradeAim
  grades: QuarterViewStudentPeriodsGrades
  latest_grades: QuarterViewStudentLatestGrade[]
  /** @format date */
  course_drop_created: string
}

export type QuarterViewStudentLatestGrade = {
  percents: number
  grade: string
  assignment_type: SimpleAssignmentType
}

export type QuarterViewStudentPeriodsGrades = {
  quarter: QuarterViewStudentAverageGrade
  semester: QuarterViewStudentAverageGrade
  potential: QuarterViewStudentAverageGrade
}

/**
 * * `hours_below_range` - Planned hours do not meet course range
 * * `improve_subtopics_order` - Order of subtopics realization to improve
 * * `other` - Other
 */
export type Reason837Enum =
  | 'hours_below_range'
  | 'improve_subtopics_order'
  | 'other'

/**
 * * `missing_standard_session` - missing_standard_session
 * * `eligible_students_extra_session` - eligible_students_extra_session
 * * `missing_teacher_source` - missing_teacher_source
 * * `no_available_slot` - no_available_slot
 */
export type ReasonD9FEnum =
  | 'missing_standard_session'
  | 'eligible_students_extra_session'
  | 'missing_teacher_source'
  | 'no_available_slot'

export type RecoveryAvailableFilters = {
  school_programme__type: AvailableStringFilter
  student: AvailableStringFilter
  assigned_to: AvailableStringFilter
}

/**
 * * `AbsenceRecovery` - AbsenceRecovery
 * * `LunchtimeRecovery` - LunchtimeRecovery
 * * `HighSchoolLunchtimeRecovery` - HighSchoolLunchtimeRecovery
 */
export type RecoveryResourcetypeEnum =
  | 'AbsenceRecovery'
  | 'LunchtimeRecovery'
  | 'HighSchoolLunchtimeRecovery'

export type Recurring = {
  pk: string
  /**
   * * `daily` - Daily
   * * `weekly` - Weekly
   * * `monthly` - Monthly
   * * `annually` - Annually
   * * `every_weekday` - Every weekday
   * * `every_weekend` - Every weekend
   */
  type: RecurringTypeEnum
  /** @format date */
  end_date?: string | null
}

export type RecurringProgramme = {
  id: number
  /**
   * * `daily` - Daily
   * * `every_weekday` - Every weekday
   * * `weekly` - Weekly
   * * `monthly` - Monthly
   */
  type: Type061Enum
  /** @format date */
  end_date: string
}

export type RecurringProgrammeRequest = {
  /**
   * * `daily` - Daily
   * * `every_weekday` - Every weekday
   * * `weekly` - Weekly
   * * `monthly` - Monthly
   */
  type: Type061Enum
  /** @format date */
  end_date: string
}

export type RecurringRequest = {
  /**
   * * `daily` - Daily
   * * `weekly` - Weekly
   * * `monthly` - Monthly
   * * `annually` - Annually
   * * `every_weekday` - Every weekday
   * * `every_weekend` - Every weekend
   */
  type: RecurringTypeEnum
  /** @format date */
  end_date?: string | null
  update_type?: RecurringUpdateTypeEnum
}

/**
 * * `daily` - Daily
 * * `weekly` - Weekly
 * * `monthly` - Monthly
 * * `annually` - Annually
 * * `every_weekday` - Every weekday
 * * `every_weekend` - Every weekend
 */
export type RecurringTypeEnum =
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'annually'
  | 'every_weekday'
  | 'every_weekend'

/**
 * * `this` - This
 * * `this_and_next` - This and following
 * * `next` - Following
 */
export type RecurringUpdateTypeEnum = 'this' | 'this_and_next' | 'next'

/**
 * * `REGENERATE_ALL` - Regenerate all
 * * `ADD_UNASSIGNED` - Add unassigned students
 * * `COPY_SECTIONS` - Copy sections
 */
export type RegenerateSectionsRequestOperationEnum =
  | 'REGENERATE_ALL'
  | 'ADD_UNASSIGNED'
  | 'COPY_SECTIONS'

export type RegenerateSectionsRequestRequest = {
  /**
   * * `REGENERATE_ALL` - Regenerate all
   * * `ADD_UNASSIGNED` - Add unassigned students
   * * `COPY_SECTIONS` - Copy sections
   */
  operation: RegenerateSectionsRequestOperationEnum
  source_quarter?: number
}

export type RegisterDevice = {
  /** @maxLength 256 */
  device_id: string
  user?: string
  /** @maxLength 12 */
  language?: string
  is_active?: boolean
}

export type RegisterDeviceRequest = {
  /**
   * @minLength 1
   * @maxLength 256
   */
  device_id: string
  user?: string
  /**
   * @minLength 1
   * @maxLength 12
   */
  language?: string
  is_active?: boolean
}

export type RejectEnrolmentRequest = {
  /** @minLength 1 */
  rejection_comment: string
}

export type RejectIdpPlan = {
  note: string
  rejected_by: string
  rejected_at: string
}

export type RejectIdpPlanRequest = {
  /** @minLength 1 */
  note: string
}

/**
 * * `external_grade_boundaries` - Not reflective of external grade boundaries
 * * `other` - Other
 */
export type RejectionReasonEnum = 'external_grade_boundaries' | 'other'

export type RelatedLeaveCheck = {
  /** @format uuid */
  id: string
  /** @maxLength 1024 */
  destination?: string
  /** @maxLength 256 */
  train?: string
  /** @maxLength 256 */
  plane?: string
  /** @maxLength 256 */
  pick_up_location?: string
  /** @format time */
  pick_up_time?: string | null
  comment?: string
  check_type: LeaveCheckTypeEnum
  /** @maxLength 64 */
  contact_person: string
  /** @maxLength 128 */
  contact_person_phone: string
  is_completed?: boolean
  is_driver_needed?: boolean
  /** @format date-time */
  date: string
  /** @format date-time */
  original_date?: string | null
}

export type RelatedLeaveCheckRequest = {
  /** @maxLength 1024 */
  destination?: string
  /** @maxLength 256 */
  train?: string
  /** @maxLength 256 */
  plane?: string
  /** @maxLength 256 */
  pick_up_location?: string
  /** @format time */
  pick_up_time?: string | null
  comment?: string
  /**
   * @minLength 1
   * @maxLength 64
   */
  contact_person: string
  /**
   * @minLength 1
   * @maxLength 128
   */
  contact_person_phone: string
  is_completed?: boolean
  is_driver_needed?: boolean
  /** @format date-time */
  date: string
  /** @format date-time */
  original_date?: string | null
}

export type RelatedSimpleLeaveCheck = {
  /** @format uuid */
  id: string
  /**
   * * `in` - Check-in
   * * `out` - Check-out
   */
  check_type: LeaveCheckTypeEnum
  is_completed?: boolean
  /** @format date-time */
  date: string
  /** @format date-time */
  original_date?: string | null
}

export type RelationSuggestionList = {
  id: number
  suggested_user: SuggestedUser
  /** @format date-time */
  created: string
}

export type RelationSuggestionToggleDismiss = {
  is_dismissed: boolean
}

/**
 * * `5` - Mother/Father
 * * `10` - Stepmother/Stepfather
 * * `15` - Grandmother/Grandfather
 * * `20` - Aunt/Uncle
 * * `25` - Sister/Brother
 * * `30` - Stepsister/Stepbrother
 * * `35` - Half sister/Half brother
 * * `40` - Daughter/Son
 * * `45` - Stepdaughter/Stepson
 * * `50` - Granddaughter/Grandson
 * * `55` - Niece/Nephew
 * * `60` - Cousin
 * * `65` - Friend
 * * `70` - Other
 */
export type RelationTypeEnum =
  | 5
  | 10
  | 15
  | 20
  | 25
  | 30
  | 35
  | 40
  | 45
  | 50
  | 55
  | 60
  | 65
  | 70

/**
 * * `daily` - Daily
 * * `every_x_days` - Every "X" days
 * * `monthly` - Monthly
 * * `weekly` - Weekly
 */
export type RepeatFormEnum = 'daily' | 'every_x_days' | 'monthly' | 'weekly'

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type ReportCard = {
  id: number
  /** @format uuid */
  student: string
  /** @format uri */
  file: string
  quarter: number
  /** @format date-time */
  created: string
  /** @format date-time */
  sent_at: string | null
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type ReportCardQuarter = {
  id: number
  name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  short_name: QuarterShortNameEnum
  school_year: SchoolYear
}

/**
 * * `lunchtime` - lunchtime
 * * `study_time` - study_time
 * * `dinner` - dinner
 * * `weekend` - weekend
 * * `default` - default
 */
export type ReportTypeEnum =
  | 'lunchtime'
  | 'study_time'
  | 'dinner'
  | 'weekend'
  | 'default'

/**
 * * `manual` - Manual
 * * `from idp` - From IDP
 * * `by_student` - Requested by student
 */
export type RequestTypeEnum = 'manual' | 'from idp' | 'by_student'

export type RequestedCourse = {
  id: number
  status_display: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /**
   * * `new` - New
   * * `rejected` - Rejected
   * * `course_created` - Course created
   */
  status?: RequestedCourseStatusEnum
  /** @maxLength 256 */
  name: string
  note: string
  /** @format uuid */
  course: string | null
}

export type RequestedCourseRequest = {
  /**
   * * `new` - New
   * * `rejected` - Rejected
   * * `course_created` - Course created
   */
  status?: RequestedCourseStatusEnum
  /**
   * @minLength 1
   * @maxLength 256
   */
  name: string
  /** @minLength 1 */
  note: string
}

/**
 * * `new` - New
 * * `rejected` - Rejected
 * * `course_created` - Course created
 */
export type RequestedCourseStatusEnum = 'new' | 'rejected' | 'course_created'

/** Serializer for pin code's reset's (first step). */
export type ResetMobilePinCode = {
  device_id: string
}

/** Serializer for pin code's reset's confirmation (second step). */
export type ResetMobilePinCodeConfirm = {
  device_id: string
  hashed_token_and_device_id: string
}

/** Serializer for pin code's reset's confirmation (second step). */
export type ResetMobilePinCodeConfirmRequest = {
  /** @minLength 1 */
  device_id: string
  /** @minLength 1 */
  hashed_token_and_device_id: string
}

/** Serializer for pin code's reset's (first step). */
export type ResetMobilePinCodeRequest = {
  /** @minLength 1 */
  device_id: string
}

export type ResourcetypeEnum =
  | 'BoardingAnnouncementWidget'
  | 'CelebrationWidget'
  | 'EventAgendaWidget'
  | 'CongratulationsWidget'
  | 'PosterWidget'
  | 'StudentArtGalleryWidget'
  | 'EventInfoWidget'
  | 'EventInfoWithPhotoWidget'
  | 'InfoWithVideoWidget'
  | 'JournalWidget'
  | 'MysteryLinkWidget'
  | 'MenuWidget'
  | 'StudentsAnnouncementWidget'
  | 'StudentsPicWidget'
  | 'VariantManualCongratulationsWidget'
  | 'ClockWidget'

/**
 * * `order_found` - order_found
 * * `not_your_order` - not_your_order
 * * `no_order_assigned` - no_order_assigned
 * * `wrong_house_or_room` - wrong_house_or_room
 */
export type ResultTypeEnum =
  | 'order_found'
  | 'not_your_order'
  | 'no_order_assigned'
  | 'wrong_house_or_room'

export type RetrieveDraftActivitySuggesterTask = {
  id: number
  /**
   * * `pending` - Pending
   * * `running` - Running
   * * `succeeded` - Succeeded
   * * `failed` - Failed
   * * `cancelled` - Cancelled
   * * `completed` - Completed
   */
  status?: Status7B2Enum
  call_args: DraftActivitySuggesterTaskCallArgs
  suggestions: DraftActivitySuggestion[] | null
}

export type ReviewLeaveRequest = {
  /**
   * * `approve` - Approve
   * * `reject` - Reject
   * * `requested` - Requested
   */
  action: LeaveRequestReviewActionEnum
  comment?: string
}

export type ReviewLeaveRequestAsParent = {
  /**
   * * `approve_as_parent` - Approve as parent
   * * `reject_as_parent` - Reject as parent
   */
  action: ReviewLeaveRequestAsParentActionEnum
}

/**
 * * `approve_as_parent` - Approve as parent
 * * `reject_as_parent` - Reject as parent
 */
export type ReviewLeaveRequestAsParentActionEnum =
  | 'approve_as_parent'
  | 'reject_as_parent'

export type ReviewLeaveRequestAsParentRequest = {
  /**
   * * `approve_as_parent` - Approve as parent
   * * `reject_as_parent` - Reject as parent
   */
  action: ReviewLeaveRequestAsParentActionEnum
}

export type ReviewLeaveRequestRequest = {
  /**
   * * `approve` - Approve
   * * `reject` - Reject
   * * `requested` - Requested
   */
  action: LeaveRequestReviewActionEnum
  comment?: string
}

export type RevisionWeekAutofillIssue = {
  /**
   * * `missing_standard_session` - missing_standard_session
   * * `eligible_students_extra_session` - eligible_students_extra_session
   * * `missing_teacher_source` - missing_teacher_source
   * * `no_available_slot` - no_available_slot
   */
  reason: ReasonD9FEnum
  course_id: string
  course_name: string
  student_ids: string[]
  teacher_ids: string[]
  test_invigilator_ids: string[]
  location_id: number | null
}

export type RevisionWeekAutofillPlanCreateRequest = {
  /** @minLength 1 */
  exam_session_id: string
  days?: number[]
  time_blocks?: string[]
  /**
   * @min 1
   * @default 2
   */
  default_sessions_per_course?: number
  /**
   * @min 1
   * @default 2
   */
  extra_session_exam_threshold?: number
  /**
   * @min 0
   * @default 1
   */
  extra_sessions_per_eligible_student?: number
  /** @default true */
  preserve_existing_sessions?: boolean
  /**
   * @min 1
   * @default 1
   */
  max_sessions_per_course_per_day?: number
}

export type RevisionWeekAutofillPlanDetail = {
  id: string
  exam_session_id: string
  /**
   * * `generated` - Generated
   * * `applied` - Applied
   * * `stale` - Stale
   */
  status?: StatusE12Enum
  /** @format date-time */
  created: string
  created_by_id: string
  /** @format date-time */
  applied_at?: string | null
  applied_by_id: string | null
  days?: number[]
  time_blocks?: string[]
  /**
   * @min 0
   * @max 32767
   */
  default_sessions_per_course: number
  /**
   * @min 0
   * @max 32767
   */
  extra_session_exam_threshold: number
  /**
   * @min 0
   * @max 32767
   */
  extra_sessions_per_eligible_student: number
  preserve_existing_sessions?: boolean
  /**
   * @min 0
   * @max 32767
   */
  max_sessions_per_course_per_day: number
  /**
   * @min 0
   * @max 2147483647
   */
  planned_count?: number
  /**
   * @min 0
   * @max 2147483647
   */
  create_count?: number
  /**
   * @min 0
   * @max 2147483647
   */
  update_count?: number
  /**
   * @min 0
   * @max 2147483647
   */
  issue_count?: number
  issues: RevisionWeekAutofillIssue[]
  items: RevisionWeekAutofillPlanItem[]
}

export type RevisionWeekAutofillPlanItem = {
  id: string
  /**
   * @min 0
   * @max 32767
   */
  order: number
  /**
   * * `create` - Create
   * * `update` - Update
   */
  action: RevisionWeekAutofillPlanItemActionEnum
  /**
   * * `missing_standard_session` - missing_standard_session
   * * `eligible_students_extra_session` - eligible_students_extra_session
   * * `missing_teacher_source` - missing_teacher_source
   * * `no_available_slot` - no_available_slot
   */
  reason: ReasonD9FEnum
  course_id: string
  course_name: string
  official_name?: string
  source_exam_id: string | null
  applied_exam_id: string | null
  location_id: number | null
  /** @format date */
  date: string
  /** @format time */
  start_time: string
  /** @format time */
  end_time: string
  /**
   * @min 0
   * @max 2147483647
   */
  duration: number
  is_standard?: boolean
  students: RevisionWeekAutofillPlanItemStudent[]
  teachers: RevisionWeekAutofillPlanItemTeacher[]
  invigilators: RevisionWeekAutofillPlanItemInvigilator[]
}

/**
 * * `create` - Create
 * * `update` - Update
 */
export type RevisionWeekAutofillPlanItemActionEnum = 'create' | 'update'

export type RevisionWeekAutofillPlanItemInvigilator = {
  id: number
  user_id: string
}

export type RevisionWeekAutofillPlanItemStudent = {
  id: number
  student_id: string
  grade_level_id: number
}

export type RevisionWeekAutofillPlanItemTeacher = {
  id: number
  teacher_id: string
}

export type RevisionWeekAutofillPlanList = {
  id: string
  exam_session_id: string
  /**
   * * `generated` - Generated
   * * `applied` - Applied
   * * `stale` - Stale
   */
  status?: StatusE12Enum
  /** @format date-time */
  created: string
  created_by_id: string
  /** @format date-time */
  applied_at?: string | null
  applied_by_id: string | null
  /**
   * @min 0
   * @max 2147483647
   */
  planned_count?: number
  /**
   * @min 0
   * @max 2147483647
   */
  create_count?: number
  /**
   * @min 0
   * @max 2147483647
   */
  update_count?: number
  /**
   * @min 0
   * @max 2147483647
   */
  issue_count?: number
}

export type RewardType = {
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
  cost: number
  is_available: boolean
}

export type Role = {
  value: string
  label: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type Room = {
  id: number
  /** @format date-time */
  deleted: string | null
  /** @maxLength 128 */
  name: string
  /**
   * @min 0
   * @max 2147483647
   */
  capacity?: number | null
  is_private?: boolean
  is_external?: boolean
  is_available_in_room_timetable: boolean
  msl_room?: boolean
  pri_room?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  group?: RoomGroupEnum | NullEnum | null
  schedule_automatically?: boolean
  available_for_exam?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  capacity_for_exam?: number | null
  department: number
  location_id?: number | null
  override_allowed_slots?: boolean
  allowed_slots?: RoomAllowedSlots[] | null
  allow_multiple_activities?: boolean
  /**
   * @min 2
   * @max 2147483647
   */
  max_activities_number?: number | null
}

export type RoomAllowedSlots = {
  id: number
  /**
   * @min 0
   * @max 2147483647
   */
  day: WeekDayEnum
  /** @format time */
  start_time: string
  /** @format time */
  end_time: string
}

export type RoomAllowedSlotsRequest = {
  /**
   * @min 0
   * @max 2147483647
   */
  day: WeekDayEnum
  /** @format time */
  start_time: string
  /** @format time */
  end_time: string
}

export type RoomAvailabilityClashingEventsResponse = {
  room_id: number
  pk: string
  label: string
  /**
   * * `exam` - Exam
   * * `lesson` - Lesson
   * * `school_programme` - School Programme
   */
  room_clash_type: RoomClashTypeEnum
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  seats_taken: number
}

export type RoomAvailabilityRequestRequest = {
  /** @format date-time */
  date_from: string
  /** @format date-time */
  date_to: string
  considered_room_ids?: number[] | null
  /** @minLength 1 */
  excluded_lesson_id?: string | null
  /** @minLength 1 */
  excluded_exam_id?: string | null
  /** @minLength 1 */
  excluded_school_programme_id?: string | null
}

export type RoomAvailabilityResponse = {
  id: number
  /** @maxLength 128 */
  name: string
  /**
   * @min 0
   * @max 2147483647
   */
  capacity?: number | null
  is_external?: boolean
  allow_multiple_activities?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  max_activities_number?: number | null
  clashing_events: RoomAvailabilityClashingEventsResponse[]
}

export type RoomBookingBookForOptions = {
  label: string
  /** @format uuid */
  value: string
  is_student: boolean
}

export type RoomBookingCreate = {
  id: number
  room: number
  /** @format date */
  start_date: string
  /** @format uuid */
  booked_for?: string | null
  /**
   * * `reserved` - Reserved
   * * `social_room` - Social room
   * * `duty_room` - Duty room
   * * `artisan_rest_room` - Artisan rest room
   * * `storage` - Storage
   * * `artisan_private_room` - Artisan private room
   */
  purpose?: PurposeEnum
}

export type RoomBookingCreateRequest = {
  room: number
  /** @format date */
  start_date: string
  /** @format uuid */
  booked_for?: string | null
  /**
   * * `reserved` - Reserved
   * * `social_room` - Social room
   * * `duty_room` - Duty room
   * * `artisan_rest_room` - Artisan rest room
   * * `storage` - Storage
   * * `artisan_private_room` - Artisan private room
   */
  purpose?: PurposeEnum
  added_by?: string
}

export type RoomBookingUpdate = {
  id: number
  /** @format date */
  start_date: string
  /** @format uuid */
  booked_for?: string | null
  /**
   * * `reserved` - Reserved
   * * `social_room` - Social room
   * * `duty_room` - Duty room
   * * `artisan_rest_room` - Artisan rest room
   * * `storage` - Storage
   * * `artisan_private_room` - Artisan private room
   */
  purpose?: PurposeEnum
}

export type RoomBookingUpdateRequest = {
  /** @format date */
  start_date: string
  /** @format uuid */
  booked_for?: string | null
  /**
   * * `reserved` - Reserved
   * * `social_room` - Social room
   * * `duty_room` - Duty room
   * * `artisan_rest_room` - Artisan rest room
   * * `storage` - Storage
   * * `artisan_private_room` - Artisan private room
   */
  purpose?: PurposeEnum
  last_modified_by?: string
}

/**
 * * `exam` - Exam
 * * `lesson` - Lesson
 * * `school_programme` - School Programme
 */
export type RoomClashTypeEnum = 'exam' | 'lesson' | 'school_programme'

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type RoomDetail = {
  id: number
  /** @format date-time */
  deleted: string | null
  /** @maxLength 128 */
  name: string
  /**
   * @min 0
   * @max 2147483647
   */
  capacity?: number | null
  is_private?: boolean
  is_external?: boolean
  is_available_in_room_timetable: boolean
  msl_room?: boolean
  pri_room?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  group?: RoomGroupEnum | NullEnum | null
  schedule_automatically?: boolean
  available_for_exam?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  capacity_for_exam?: number | null
  department: number
  location_id?: number | null
  override_allowed_slots?: boolean
  allowed_slots?: RoomAllowedSlots[] | null
  allow_multiple_activities?: boolean
  /**
   * @min 2
   * @max 2147483647
   */
  max_activities_number?: number | null
  location: Location
}

export type RoomForExam = {
  id: number
  name: string
  capacity: number
  seats_taken: number
  is_available: boolean
  exams: BusyRoomReason[]
  lessons: BusyRoomReason[]
}

/**
 * * `1` - 1
 * * `2` - 2
 * * `3` - 3
 * * `4` - 4
 * * `5` - 5
 */
export type RoomGroupEnum = 1 | 2 | 3 | 4 | 5

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type RoomRequest = {
  /**
   * @minLength 1
   * @maxLength 128
   */
  name: string
  /**
   * @min 0
   * @max 2147483647
   */
  capacity?: number | null
  is_private?: boolean
  is_external?: boolean
  msl_room?: boolean
  pri_room?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  group?: RoomGroupEnum | NullEnum | null
  schedule_automatically?: boolean
  available_for_exam?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  capacity_for_exam?: number | null
  department: number
  location_id?: number | null
  override_allowed_slots?: boolean
  allowed_slots?: RoomAllowedSlotsRequest[] | null
  allow_multiple_activities?: boolean
  /**
   * @min 2
   * @max 2147483647
   */
  max_activities_number?: number | null
}

export type RoomTTScheduledActivities =
  | TimetableExam
  | TimetableLesson
  | TimetableSchoolProgramme

export type RoomTimetableAvailableFilters = {
  location: AvailableIntegerFilter
  room: AvailableIntegerFilter
  capacity: AvailableStringFilter
}

export type RoomTimetableFreeRoom = {
  room_str: string
  location_str: string
  /** @format date-time */
  start: string
  /** @format date-time */
  end: string
  /**  type */
  _type: RoomTimetableFreeRoomTypeEnum
}

/** * `free_room` - free_room */
export type RoomTimetableFreeRoomTypeEnum = 'free_room'

export type RoomTimetableFreeRoomsAvailableFilters = {
  location: AvailableIntegerFilter
  capacity: AvailableStringFilter
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type RoomWithOccupation = {
  id: number
  /** @format date-time */
  deleted: string | null
  /** @maxLength 128 */
  name: string
  /**
   * @min 0
   * @max 2147483647
   */
  capacity?: number | null
  is_private?: boolean
  is_external?: boolean
  is_available_in_room_timetable: boolean
  msl_room?: boolean
  pri_room?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  group?: RoomGroupEnum | NullEnum | null
  schedule_automatically?: boolean
  available_for_exam?: boolean
  /**
   * @min 0
   * @max 2147483647
   */
  capacity_for_exam?: number | null
  department: number
  location_id?: number | null
  override_allowed_slots?: boolean
  allowed_slots?: RoomAllowedSlots[] | null
  allow_multiple_activities?: boolean
  /**
   * @min 2
   * @max 2147483647
   */
  max_activities_number?: number | null
  is_occupied: boolean
}

/**
 * * `0` - Monday
 * * `1` - Tuesday
 * * `2` - Wednesday
 * * `3` - Thursday
 * * `4` - Friday
 * * `5` - Saturday
 * * `6` - Sunday
 */
export type RotaDayEnum = 0 | 1 | 2 | 3 | 4 | 5 | 6

export type RotaGenerationCallback = {
  event_data: Record<string, any>
  /**
   * * `task_succeeded` - TASK_SUCCEEDED
   * * `task_failed` - TASK_FAILED
   */
  event_type: RotaGenerationCallbackEventTypeEnum
}

/**
 * * `task_succeeded` - TASK_SUCCEEDED
 * * `task_failed` - TASK_FAILED
 */
export type RotaGenerationCallbackEventTypeEnum =
  | 'task_succeeded'
  | 'task_failed'

export type RotaGenerationCallbackRequest = {
  event_data: Record<string, any>
  /**
   * * `task_succeeded` - TASK_SUCCEEDED
   * * `task_failed` - TASK_FAILED
   */
  event_type: RotaGenerationCallbackEventTypeEnum
}

export type RotaGenerationIssue = {
  teams: SimpleTeam[]
  users: SimpleUser[]
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type RotaGenerationList = {
  id: number
  status: Status241Enum
  timetable: number | null
  /** @format date-time */
  created: string
  quarter: number
  rota_timetable_id: number | null
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type RotaGenerationTask = {
  id: number
  status: Status241Enum
  /** @format date-time */
  created: string
  /** @format date-time */
  finished_at: string | null
  rota_timetable_id: number | null
  teams: RotaTeam[]
  quarter: number
  timetable?: number | null
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type RotaGenerationTaskRequest = {
  timetable?: number | null
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type RotaGenerationTaskRetrieve = {
  id: number
  status: Status241Enum
  /** @format date-time */
  created: string
  /** @format date-time */
  finished_at: string | null
  rota_timetable_id: number | null
  teams: RotaTeamRetrieve[]
  quarter: number
  timetable?: number | null
  issues: RotaGenerationIssue
}

export type RotaRoleChoice = {
  label: string
  /**
   * * `boarding_director` - Boarding Director
   * * `campus_head` - Campus Head
   * * `deputy_campus_head_main` - Deputy Campus Head (Main)
   * * `deputy_campus_head_senior` - Deputy Campus Head (Sen)
   * * `head_of_house` - Head of House
   * * `house_tutor` - House Tutor
   */
  value: BoardingArtisanRoleEnum
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type RotaTeam = {
  id: number
  team_id: string
  members: RotaTeamMember[]
}

export type RotaTeamMember = {
  id: number
  user_id: string
  /**
   * @min 0
   * @max 32767
   */
  team_role?: TeamMemberRoleEnum
  roles?: BoardingArtisanRoleEnum[]
}

export type RotaTeamMemberRequest = {
  /** @minLength 1 */
  user_id: string
  /**
   * @min 0
   * @max 32767
   */
  team_role?: TeamMemberRoleEnum
  roles?: BoardingArtisanRoleEnum[]
}

export type RotaTeamMemberRetrieve = {
  id: number
  user_id: string
  rota_roles: string[]
  user_first_name: string
  user_last_name: string
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type RotaTeamRequest = {
  /** @minLength 1 */
  team_id: string
  members: RotaTeamMemberRequest[]
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type RotaTeamRetrieve = {
  /** @format uuid */
  id: string
  members: RotaTeamMemberRetrieve[]
  house_name: string
  name: string
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type RotaTimetable = {
  id: number
  timetable: number
  status: RotaTimetableStatusEnum
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  rota_generation: number | null
}

/**
 * * `generation_in_progress` - Generation in progress
 * * `selected_for_import` - Selected for import
 * * `draft` - Draft
 */
export type RotaTimetableStatusEnum =
  | 'generation_in_progress'
  | 'selected_for_import'
  | 'draft'

/**
 * * `regular` - Duty
 * * `break` - Break
 * * `meeting` - Leaders Meeting
 * * `night_presence` - Night Presence
 * * `ski_trip` - Ski trip
 * * `sudden_time_off` - Sudden Time Off
 */
export type RotaTypeEnum =
  | 'regular'
  | 'break'
  | 'meeting'
  | 'night_presence'
  | 'ski_trip'
  | 'sudden_time_off'

/**
 * * `5` - Settled
 * * `10` - First reminder
 * * `15` - Second reminder
 * * `17` - Unsettled
 * * `20` - App locked
 */
export type SaldoEnum = 5 | 10 | 15 | 17 | 20

/**
 * * `5` - Settled
 * * `10` - First reminder
 * * `15` - Second reminder
 * * `17` - Unsettled
 * * `20` - App locked
 */
export type SaldoStatusEnum = 5 | 10 | 15 | 17 | 20

export type Salutation = {
  /** @maxLength 128 */
  salutation: string
}

export type SalutationRequest = {
  /**
   * @minLength 1
   * @maxLength 128
   */
  salutation: string
  added_by?: string
  last_modified_by?: string
}

export type SanctionsOption = {
  merit: StringOption[]
  demerit: StringOption[]
}

export type SaveStudentsFilesOrder = {
  files_to_order: number[]
}

export type SaveStudentsFilesOrderRequest = {
  files_to_order: number[]
}

export type ScheduledMedicine = {
  /** @format uuid */
  id: string
  medicine_intake_plan: MedicineIntakePlan
  /** @format uuid */
  student_id: string
  student_name: string
  /** @format date-time */
  should_be_given_at: string
  was_given: boolean
  /** @format date-time */
  given_at: string | null
  /** @format uuid */
  given_by: string | null
  is_optional: boolean
  is_deleted: boolean
  /** @format date-time */
  deleted_at: string | null
  was_skipped: boolean
  skip_reason: SkipReasonEnum
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type SchoolBreak = {
  id: number
  /** @maxLength 100 */
  name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  status: string
  disable_lunchtime_recovery_in_the_preceding_week?: boolean
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type SchoolBreakRequest = {
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  disable_lunchtime_recovery_in_the_preceding_week?: boolean
}

/**
 * * `1` - January
 * * `2` - February
 * * `3` - March
 * * `4` - April
 * * `5` - May
 * * `6` - June
 * * `7` - July
 * * `8` - August
 * * `9` - September
 * * `10` - October
 * * `11` - November
 * * `12` - December
 */
export type SchoolFromMonthEnum =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type SchoolProgramme = {
  id: number
  status: SchoolProgrammeStatusEnum
  status_label: string
  /** @format date-time */
  created: string
  is_compulsory?: boolean
  /**
   * * `academics` - Academics
   * * `house_evening` - House Evening
   * * `term_break` - Term Break
   * * `school_trip` - School Trip
   * * `weekend_programme` - Weekend Programme
   * * `exam_preparation` - Exam Preparation
   * * `other` - Other
   * * `absence_recovery` - Absence Recovery
   * * `lunchtime_recovery` - Lunchtime Recovery
   */
  type: SchoolProgrammeTypeEnum
  type_label: string
  /** @maxLength 128 */
  title: string
  description?: string
  parental_approval_required?: boolean
  message_to_parents?: string
  enrol_all_students?: boolean
  categories?: CategoriesEnum[]
  houses?: number[]
  grade_levels?: number[]
  ages?: number[]
  sections?: number[]
  added_by?: string
  /** @format uuid */
  added_by_id: string
  last_modified_by?: string
  leaders: string[]
  leaders_data: SimpleUser[]
  members: string[]
  members_data: SimpleUser[]
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /** @format date-time */
  enrolment_deadline?: string | null
  room?: number | null
  room_data: SimpleRoom
  /**
   * @format decimal
   * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
   */
  cost?: string | null
  cost_currency: string | null
  students_limit?: number
  has_enrolments: boolean
  risk_assessment_required?: boolean
  risk_assessment_file?: number | null
  risk_assessment_file_details: SchoolProgrammeAttachment | null
  other_files?: number[]
  other_files_details: SchoolProgrammeAttachment[]
  enrolments?: ProgrammeEnrolment[]
  enrolments_count: number
  approved_enrolments_count: number
  rejection_reason?: string
  is_editable: boolean
  is_room_editable: boolean
  is_enrolment_editable: boolean
  is_status_editable: boolean
  can_be_deleted_by_user: boolean
  is_recurring: boolean
  recurring: RecurringProgramme | null
  is_main_recurring_session: boolean
  main_programme_id: number | null
  rejected_sessions_future: number
  rejected_sessions_all: number
}

export type SchoolProgrammeAttachment = {
  id: number
  /** @format uri */
  file: string
  file_name: string
}

export type SchoolProgrammeAttachmentRequest = {
  /** @format binary */
  file: File
}

export type SchoolProgrammeAvailableFilters = {
  student: AvailableStringFilter
  added_by: AvailableStringFilter
  participant: ParticipantAvailableStringFilter
  type: AvailableStringFilter
  is_compulsory: AvailableStringFilter
  is_off_campus: AvailableStringFilter
  status: AvailableStringFilter
  frequency: AvailableStringFilter
}

export type SchoolProgrammeBulkStatusChangeClashes = {
  no_of_clashes: number
}

export type SchoolProgrammeCancelRequest = {
  /** @minLength 1 */
  cancellation_reason: string
}

export type SchoolProgrammeChangeStatusResponse = {
  affected_recurring_sessions: number | null
}

export type SchoolProgrammeClash = {
  id: number
  type: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
}

export type SchoolProgrammeList = {
  id: number
  added_by: SimpleUser
  status: SchoolProgrammeStatusEnum
  status_label: string
  is_compulsory: boolean
  type: SchoolProgrammeTypeEnum
  type_label: string
  title: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /** @format date-time */
  enrolment_deadline: string | null
  students_limit: number
  approved_enrolments_count: number
  requested_enrolments_count: number
  rejection_reason: string
  cancellation_reason: string
  is_status_editable: boolean
  can_be_deleted_by_user: boolean
  has_enrolments: boolean
  is_recurring: boolean
  recurring: RecurringProgramme | null
  is_main_recurring_session: boolean
  room: number | null
  is_off_campus: boolean
  staff_ids: string[]
  students_ids: string[]
}

export type SchoolProgrammeRecovery = {
  id: number
  type: SchoolProgrammeTypeEnum
  type_label: string
  title: string
  description: string
  added_by_str: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
}

export type SchoolProgrammeRejectRequest = {
  /** @minLength 1 */
  rejection_reason: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type SchoolProgrammeRequest = {
  is_compulsory?: boolean
  /**
   * * `academics` - Academics
   * * `house_evening` - House Evening
   * * `term_break` - Term Break
   * * `school_trip` - School Trip
   * * `weekend_programme` - Weekend Programme
   * * `exam_preparation` - Exam Preparation
   * * `other` - Other
   * * `absence_recovery` - Absence Recovery
   * * `lunchtime_recovery` - Lunchtime Recovery
   */
  type: SchoolProgrammeTypeEnum
  /**
   * @minLength 1
   * @maxLength 128
   */
  title: string
  description?: string
  parental_approval_required?: boolean
  message_to_parents?: string
  enrol_all_students?: boolean
  categories?: CategoriesEnum[]
  houses?: number[]
  grade_levels?: number[]
  ages?: number[]
  sections?: number[]
  added_by?: string
  duplicate_programme?: number
  last_modified_by?: string
  leaders: string[]
  members: string[]
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /** @format date-time */
  enrolment_deadline?: string | null
  room?: number | null
  /**
   * @format decimal
   * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
   */
  cost?: string | null
  students_limit?: number
  has_enrolments: boolean
  risk_assessment_required?: boolean
  risk_assessment_file?: number | null
  other_files?: number[]
  enrolments?: ProgrammeEnrolmentRequest[]
  rejection_reason?: string
  is_recurring: boolean
  recurring: RecurringProgrammeRequest | null
}

export type SchoolProgrammeStaffOption = {
  /** @format uuid */
  value: string
  label: string
  teams: SchoolProgrammeStaffTeam[]
}

export type SchoolProgrammeStaffTeam = {
  /** @format uuid */
  id: string
  name: string
  /**
   * * `1` - Member
   * * `2` - Leader
   */
  role: TeamMemberRoleEnum
}

/**
 * * `requested` - Requested
 * * `approved` - Approved
 * * `rejected` - Rejected
 * * `cancelled` - Cancelled
 */
export type SchoolProgrammeStatusEnum =
  | 'requested'
  | 'approved'
  | 'rejected'
  | 'cancelled'

/**
 * * `academics` - Academics
 * * `house_evening` - House Evening
 * * `term_break` - Term Break
 * * `school_trip` - School Trip
 * * `weekend_programme` - Weekend Programme
 * * `exam_preparation` - Exam Preparation
 * * `other` - Other
 * * `absence_recovery` - Absence Recovery
 * * `lunchtime_recovery` - Lunchtime Recovery
 */
export type SchoolProgrammeTypeEnum =
  | 'academics'
  | 'house_evening'
  | 'term_break'
  | 'school_trip'
  | 'weekend_programme'
  | 'exam_preparation'
  | 'other'
  | 'absence_recovery'
  | 'lunchtime_recovery'

export type SchoolProgrammesEnrolmentsAvailableFilters = {
  student: AvailableStringFilter
  year: AvailableIntegerFilter
  status: AvailableStringFilter
}

/**
 * * `1` - January
 * * `2` - February
 * * `3` - March
 * * `4` - April
 * * `5` - May
 * * `6` - June
 * * `7` - July
 * * `8` - August
 * * `9` - September
 * * `10` - October
 * * `11` - November
 * * `12` - December
 */
export type SchoolUntilMonthEnum =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12

export type SchoolYear = {
  id: number
  name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  short_name: string
}

/**
 * * `CCU` - CCU
 * * `ATH` - ATH
 */
export type ScopeEnum = 'CCU' | 'ATH'

/**
 * * `global_standards` - Global standards in Course builder
 * * `course_plans` - Course plans in Course planner
 */
export type ScopesEnum = 'global_standards' | 'course_plans'

export type Scorings = {
  scorings: Record<string, AimScorings[]>
}

/** Adds nested create feature */
export type Section = {
  id: number
  course_name: string
  course_code: string
  course_group: string
  all_enrollments: Enrollment[]
  enrollments?: SectionEnrollment[]
  group_data: SectionGroup
  tae_draft_section: SimpleTAEDraftSection | null
  has_tae_conflicts: boolean
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  is_mentoring?: boolean | null
  must_be_consecutive?: boolean
  hours: number | null
  /** attendances + grades */
  co_teacher_1_perm_to_manage_lesson?: boolean
  /** attendances + grades */
  co_teacher_2_perm_to_manage_lesson?: boolean
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
  quarter: number
  /** @format uuid */
  course: string
  group: number
  /**
   * Main teacher
   * @format uuid
   */
  teacher: string
  tae_enrolment: number | null
  teachers?: string[]
  preferred_rooms: number[]
}

export type SectionEnrollment = {
  id: number
  /** @format uuid */
  student: string
  section?: number
  /**
   * @min 1
   * @max 25
   */
  number_of_lessons: number
}

export type SectionEnrollmentRequest = {
  /** @format uuid */
  student: string
  section?: number
  /**
   * @min 1
   * @max 25
   */
  number_of_lessons: number
}

export type SectionGroup = {
  id: number
  /** @default 0 */
  group_number: number
  /** @format uuid */
  course: string
  school_year: number
  q1_section: number | null
  q2_section: number | null
  q3_section: number | null
  q4_section: number | null
}

export type SectionGroupPlanApproveRequest = {
  /**
   * Optional comment written by the HOD when approving the plan.
   * @maxLength 1024
   */
  approve_comment?: string | null
}

export type SectionGroupPlanAssignments = {
  /** @format uuid */
  id: string
  status: SectionGroupPlanStatusEnum
  section_group_id: number
  school_year: SimpleSchoolYear
  q1_section_plan: SectionPlanAssignments
  q2_section_plan: SectionPlanAssignments
  q3_section_plan: SectionPlanAssignments
  q4_section_plan: SectionPlanAssignments
  course_name: string
  course_levels: CourseLevel[]
  available_hours: number
  was_rejected: boolean
}

export type SectionGroupPlanAvailableItems = {
  items: GlobalStandardItem[]
  deleted_items: GlobalStandardItem[]
}

/** Collects and raises validation errors in bulk */
export type SectionGroupPlanConflictItem = {
  /** @format uuid */
  id?: string
  /** @format uuid */
  domain_id: string
  domain_name: string
  /** @format uuid */
  topic_id: string
  topic_name: string
  /** @format uuid */
  subtopic_id: string
  subtopic_name: string
}

export type SectionGroupPlanConflicts = {
  conflicted_items: SectionGroupPlanConflictItem[]
}

export type SectionGroupPlanCreate = {
  /** @format uuid */
  id: string
  status: SectionGroupPlanStatusEnum
  section_group_id: number
  school_year: SimpleSchoolYear
  section_plans: SectionPlan[]
  course_name: string
  course_levels: CourseLevel[]
  available_hours: number
  q1_section_plan: SectionPlan
  q2_section_plan: SectionPlan
  q3_section_plan: SectionPlan
  q4_section_plan: SectionPlan
}

export type SectionGroupPlanCreateRequest = {
  section_group_id: number
}

export type SectionGroupPlanRejectRequest = {
  /**
   * * `hours_below_range` - Planned hours do not meet course range
   * * `improve_subtopics_order` - Order of subtopics realization to improve
   * * `other` - Other
   */
  reason: Reason837Enum
  /** @maxLength 500 */
  feedback?: string
}

export type SectionGroupPlanRejection = {
  /** @format uuid */
  id: string
  /**
   * * `hours_below_range` - Planned hours do not meet course range
   * * `improve_subtopics_order` - Order of subtopics realization to improve
   * * `other` - Other
   */
  reason: Reason837Enum
  created_by: SimpleUser
  /** @format date-time */
  created: string
  /** @maxLength 500 */
  feedback?: string
}

export type SectionGroupPlanSendForApprovalRequest = {
  section_plan_ids: string[]
}

/**
 * * `to_do` - To do
 * * `in_planning` - In planning
 * * `ready_for_review` - Ready for review
 * * `approved` - Approved
 * * `rejected` - Rejected
 * * `partially_rejected` - Partially rejected
 */
export type SectionGroupPlanStatusEnum =
  | 'to_do'
  | 'in_planning'
  | 'ready_for_review'
  | 'approved'
  | 'rejected'
  | 'partially_rejected'

export type SectionGroupPlanTimeline = {
  /** @format uuid */
  id: string
  status: SectionGroupPlanStatusEnum
  section_group_id: number
  school_year: SimpleSchoolYear
  q1_section_plan: SectionPlan | null
  q2_section_plan: SectionPlan | null
  q3_section_plan: SectionPlan | null
  q4_section_plan: SectionPlan | null
  course_name: string
  course_levels: CourseLevel[]
  available_hours: number
  was_rejected: boolean
}

export type SectionGroupPlanUpdateAssignmentsRequest = {
  q1_section_plan: SectionPlanAssignmentsUpdateRequest
  q2_section_plan: SectionPlanAssignmentsUpdateRequest
  q3_section_plan: SectionPlanAssignmentsUpdateRequest
  q4_section_plan: SectionPlanAssignmentsUpdateRequest
}

export type SectionGroupPlanUpdateTimeline = {
  /** @format uuid */
  id: string
  status: SectionGroupPlanStatusEnum
  section_group_id: number
  school_year: SimpleSchoolYear
  course_name: string
  course_levels: CourseLevel[]
  available_hours: number
}

export type SectionGroupPlanUpdateTimelineRequest = {
  section_group_id: number
  q1_section_plan: SectionPlanRequest
  q2_section_plan: SectionPlanRequest
  q3_section_plan: SectionPlanRequest
  q4_section_plan: SectionPlanRequest
}

export type SectionList = {
  id: number
  course_name: string
  course_code: string
  course_group: string
  /** @format uuid */
  course_id: string
  /**
   * Main teacher
   * @format uuid
   */
  teacher_id: string | null
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1_id: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2_id: string | null
  /** attendances + grades */
  co_teacher_1_perm_to_manage_lesson?: boolean
  /** attendances + grades */
  co_teacher_2_perm_to_manage_lesson?: boolean
  group_data: SectionGroup
  tae_enrolment: number | null
  tae_draft_section: SimpleTAEDraftSection | null
  has_tae_conflicts: boolean
}

export type SectionPlan = {
  /** @format uuid */
  id: string
  section: SectionQuarter
  /**
   * * `to_do` - To do
   * * `in_planning` - In planning
   * * `ready_for_review` - Ready for review
   * * `approved` - Approved
   * * `rejected` - Rejected
   */
  status?: SectionPlanStatusEnum
  items: SectionPlanItem[]
  is_editable: boolean
  is_my_section: boolean
  available_hours: number
}

export type SectionPlanAssignments = {
  /** @format uuid */
  id: string
  section: SectionQuarter
  /**
   * * `to_do` - To do
   * * `in_planning` - In planning
   * * `ready_for_review` - Ready for review
   * * `approved` - Approved
   * * `rejected` - Rejected
   */
  status?: SectionPlanStatusEnum
  items: SectionPlanItemAssignments[]
  is_editable: boolean
  is_my_section: boolean
  available_hours: number
}

export type SectionPlanAssignmentsUpdateRequest = {
  items: SectionPlanItemAssignmentsUpdateRequest[]
}

export type SectionPlanItem = {
  /**
   * Section plan item ID.
   * @format uuid
   */
  id?: string
  item: GlobalStandardItem
  /**
   * Number of lessons which should overwrite subtopic's duration if not null
   * @min 0
   * @max 2147483647
   */
  custom_duration?: number | null
  /**
   * Indicates the order of items in the section plan
   * @min 0
   * @max 2147483647
   */
  ordering?: number
  /**
   * Bool indicates that SectionPlan has at least one relation to
   * CockpitDraftAssignment. Prefetched previously in view.
   */
  has_assignments: boolean
  /**
   * Bool indicates that SectionPlan has relations to only fully fulfilled
   * CockpitDraftAssignments. Prefetched previously in view.
   */
  has_all_valid_assignments: boolean
  is_merged: boolean
  has_parent: boolean
  has_children: boolean
  is_revision: boolean
}

export type SectionPlanItemAssignments = {
  /**
   * Section plan item ID.
   * @format uuid
   */
  id?: string
  item: GlobalStandardItem
  /**
   * Indicates the order of items in the section plan
   * @min 0
   * @max 2147483647
   */
  ordering?: number
  /**
   * Number of lessons which should overwrite subtopic's duration if not null
   * @min 0
   * @max 2147483647
   */
  custom_duration?: number | null
  assignments: CockpitDraftAssignment[]
  parent: SectionPlanItemRelatedItem | null
  children: SectionPlanItemRelatedItem[]
  is_revision: boolean
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type SectionPlanItemAssignmentsUpdateRequest = {
  /** @format uuid */
  id: string
  assignments: CockpitDraftAssignmentUpdateRequest[]
  /** @format uuid */
  parent_id: string | null
}

export type SectionPlanItemList = {
  /** @format uuid */
  id: string
  subtopic_name: string
  duration: number
  /**
   * * `to_do` - To do
   * * `planned` - Planned
   * * `in_progress` - In progress
   * * `completed` - Completed
   */
  status: LessonSectionPlanItemStatusEnum
  realized_lessons: number
  artisan: SimpleUser
  students: StudentFullName[]
}

export type SectionPlanItemRelatedItem = {
  /**
   * Section plan item ID.
   * @format uuid
   */
  id?: string
  item: GlobalStandardItem
}

export type SectionPlanItemRequest = {
  /**
   * Section plan item ID.
   * @format uuid
   */
  id?: string
  /**
   * Related global standard item ID.
   * @format uuid
   */
  item_id: string
  /**
   * Number of lessons which should overwrite subtopic's duration if not null
   * @min 0
   * @max 2147483647
   */
  custom_duration?: number | null
  /**
   * Indicates the order of items in the section plan
   * @min 0
   * @max 2147483647
   */
  ordering?: number
}

export type SectionPlanItemsProgressAvailableFiltersResponse = {
  artisan: AvailableStringFilter
  students: AvailableStringFilter
  status: AvailableStringFilter
}

export type SectionPlanRequest = {
  /** @format uuid */
  id: string
  /**
   * * `to_do` - To do
   * * `in_planning` - In planning
   * * `ready_for_review` - Ready for review
   * * `approved` - Approved
   * * `rejected` - Rejected
   */
  status?: SectionPlanStatusEnum
  items: SectionPlanItemRequest[]
}

/**
 * * `to_do` - To do
 * * `in_planning` - In planning
 * * `ready_for_review` - Ready for review
 * * `approved` - Approved
 * * `rejected` - Rejected
 */
export type SectionPlanStatusEnum =
  | 'to_do'
  | 'in_planning'
  | 'ready_for_review'
  | 'approved'
  | 'rejected'

export type SectionQuarter = {
  id: number
  quarter: SimpleQuarter
  teacher: SimpleUser
}

/** Adds nested create feature */
export type SectionRequest = {
  enrollments?: SectionEnrollmentRequest[]
  create_for_quarters?: number[]
  is_mentoring?: boolean | null
  must_be_consecutive?: boolean
  /** attendances + grades */
  co_teacher_1_perm_to_manage_lesson?: boolean
  /** attendances + grades */
  co_teacher_2_perm_to_manage_lesson?: boolean
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
  quarter: number
  /** @format uuid */
  course: string
  /**
   * Main teacher
   * @format uuid
   */
  teacher: string
  teachers?: string[]
}

export type SectionToEnroll = {
  id: number
  /**
   * Main teacher
   * @format uuid
   */
  teacher?: string | null
  teacher_str: string | null
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  co_teacher_1_str: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
  co_teacher_2_str: string | null
  /** attendances + grades */
  co_teacher_1_perm_to_manage_lesson?: boolean
  /** attendances + grades */
  co_teacher_2_perm_to_manage_lesson?: boolean
  all_enrollments: Enrollment[]
  score: string
  group_data: SectionGroup
  is_suggested: boolean
}

export type SectionUsagesResponse = {
  id: number
  idp_enrollments: IdpEnrollmentUsageInSection[]
  section_in_live_timetable: boolean
}

export type SectionsAvailableIntegerFilter = {
  query_param: string
  label: string
  choices: SectionsIntegerOption[]
}

export type SectionsIntegerOption = {
  label: number
  value: number
  details: AvailableSectionDetails
}

/** Collects and raises validation errors in bulk */
export type SecurityTeamProfile = {
  id: string
  /** @format uuid */
  user?: string | null
}

/** Collects and raises validation errors in bulk */
export type SecurityTeamProfileRequest = {
  /** @format uuid */
  user?: string | null
}

export type Semester = {
  id: number
  name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  short_name: ShortNameD69Enum
  school_year: number
}

export type SetGradeAim = {
  id: number
  /** @maxLength 64 */
  name: string
  set_grade: boolean
}

export type ShortNameD69Enum = 'S1' | 'S2'

export type ShuffleCourseMetadata = {
  /** @format uuid */
  id: string
  code: string
  name: string
}

export type ShuffleSection = {
  id: string
  section_id: number
  /** @format uuid */
  course_id: string
  course_code: string
  course_name: string
  hours: number | null
  students_count: number
  students: ShuffleSectionStudent[]
  grade_levels: string[]
  is_private: boolean
  is_divided: boolean
  can_shuffle: boolean
  invalid_related_section: boolean
}

export type ShuffleSectionGroup = {
  id: string
  /**
   * * `course` - course
   * * `related_courses` - related_courses
   */
  type: ShuffleSectionGroupTypeEnum
  exclusion_enforced: boolean
  courses: ShuffleCourseMetadata[]
  section_ids: string[]
  items: ShuffleSectionGroupItem[]
}

export type ShuffleSectionGroupItem = {
  id: string
  section_ids: string[]
  students_count: number
  students: ShuffleSectionStudent[]
  grade_levels: string[]
  sections: ShuffleSectionGroupSection[]
  exclusion_reason_codes: ExclusionReasonCodesEnum[]
}

export type ShuffleSectionGroupSection = {
  id: string
  section_id: number
  /** @format uuid */
  course_id: string
  course_code: string
  course_name: string
  hours: number | null
  students_count: number
  students: ShuffleSectionStudent[]
  grade_levels: string[]
}

/**
 * * `course` - course
 * * `related_courses` - related_courses
 */
export type ShuffleSectionGroupTypeEnum = 'course' | 'related_courses'

export type ShuffleSectionGroupsOptions = {
  previous_user_excluded_section_ids: string[]
  groups: ShuffleSectionGroup[]
}

export type ShuffleSectionStudent = {
  id: string
  name: string
}

export type ShuffleSectionsOptions = {
  default_sections_excluded_from_shuffle: string[]
  previous_sections_excluded_from_shuffle: string[]
  sections_with_shuffle_metadata: ShuffleSection[]
}

export type SickReport = {
  id: string
  /**
   * * `regular` - Regular
   * * `med_only` - Med-only
   */
  type?: SickReportTypeEnum
  type_display: string
  /** @format uuid */
  student: string
  first_contact: FirstContact
  student_id: string
  generate_absences_on_create?: boolean
  /** @format uuid */
  requested_by: string | null
  student_str: string
  status_display: string
  status: string
  close_reason?: CloseReasonEnum | BlankEnum
  close_reason_display: string
  /** @format date-time */
  closed_at?: string | null
  /** @format date-time */
  created: string
  has_comments: string
  requested_by_str: string
  closed_by_str: string
  opened_by_str: string
  created_by?: string
  last_modified_by?: string
  absence_dates: SickReportAbsenceDates[]
  current_location: string
  pending_closure_request_id: number | null
  pending_closure_request_reason: string
}

export type SickReportAbsenceDates = {
  /** @format uuid */
  id: string
  created_by?: string
  last_modified_by?: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  sick_report: string
  /** @format date-time */
  absent_from?: string | null
  /** @format date-time */
  absent_to?: string | null
  /**
   * * `regular` - Regular
   * * `sport` - Sport
   */
  class_type: ClassTypeEnum
  omit_campus_checks?: boolean
}

export type SickReportAbsenceDatesRequest = {
  created_by?: string
  last_modified_by?: string
  /** @minLength 1 */
  sick_report: string
  /** @format date-time */
  absent_from?: string | null
  /** @format date-time */
  absent_to?: string | null
  /**
   * * `regular` - Regular
   * * `sport` - Sport
   */
  class_type: ClassTypeEnum
  omit_campus_checks?: boolean
}

export type SickReportAvailableFilters = {
  status: AvailableStringFilter
  type: AvailableStringFilter
  student: AvailableStringFilter
  house: AvailableStringFilter
}

export type SickReportClosureRequest = {
  /**
   * * `mistake` - Sick report made by mistake
   * * `student_not_sick` - Student not sick
   * * `student_recovered` - Student recovered
   */
  close_reason: CloseReasonEnum
}

export type SickReportClosureRequestRequest = {
  /**
   * * `mistake` - Sick report made by mistake
   * * `student_not_sick` - Student not sick
   * * `student_recovered` - Student recovered
   */
  close_reason: CloseReasonEnum
}

export type SickReportComment = {
  /** @format uuid */
  id: string
  author?: string
  author_str: string
  content: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
}

export type SickReportCommentRequest = {
  author?: string
  /** @minLength 1 */
  content: string
}

export type SickReportHasActiveMedicineInPlan = {
  has_active_medicine_in_plan: boolean
}

export type SickReportInHealthCheck = {
  id: string
  /** @format date-time */
  created: string
  symptoms: SymptomsFf5Enum[]
  status: string
}

export type SickReportList = {
  id: string
  /** @format uuid */
  student: string
  current_location: string
  student_id: string
  /** @format uuid */
  requested_by: string | null
  student_str: string
  /**
   * * `regular` - Regular
   * * `med_only` - Med-only
   */
  type?: SickReportTypeEnum
  type_display: string
  status: string
  status_display: string
  close_reason_display: string
  /** @format date-time */
  created: string
  absent_now: string
  /** Return shortened grade in number */
  student_grade: number
  has_comments: string
  pending_closure_request_id: number | null
  pending_closure_request_reason: string
  created_by?: string
  symptoms_display: string[]
}

export type SickReportRequest = {
  /**
   * * `regular` - Regular
   * * `med_only` - Med-only
   */
  type?: SickReportTypeEnum
  /** @format uuid */
  student: string
  generate_absences_on_create?: boolean
  close_reason?: CloseReasonEnum | BlankEnum
  /** @format date-time */
  closed_at?: string | null
  created_by?: string
  last_modified_by?: string
}

/**
 * * `regular` - Regular
 * * `med_only` - Med-only
 */
export type SickReportTypeEnum = 'regular' | 'med_only'

export type SimpleAim = {
  id: number
  /** @maxLength 64 */
  name: string
}

export type SimpleAimRequest = {
  /**
   * @minLength 1
   * @maxLength 64
   */
  name: string
}

export type SimpleAssignmentType = {
  id: number
  /** @maxLength 64 */
  name: string
}

export type SimpleCampusAbsenceStudent = {
  /** @format uuid */
  id: string
  student_data: StudentSimple
}

export type SimpleCandidateContact = {
  /**
   * Student
   * @format uuid
   */
  pk: string
  /**
   * @min 0
   * @max 32767
   */
  status?: CandidateProfileStatusEnum
}

export type SimpleCandidateContactRequest = {
  /**
   * Student
   * @format uuid
   */
  pk: string
  /**
   * @min 0
   * @max 32767
   */
  status?: CandidateProfileStatusEnum
}

export type SimpleCourse = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  name: string
}

export type SimpleCourseRequest = {
  /**
   * @minLength 1
   * @maxLength 128
   */
  name: string
}

export type SimpleCourseWithGroup = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  name: string
  group_id: string | null
}

export type SimpleDepartment = {
  id: number
  /** @maxLength 32 */
  name: string
  /** @maxLength 32 */
  label?: string
}

export type SimpleDepartmentRequest = {
  /**
   * @minLength 1
   * @maxLength 32
   */
  name: string
  /** @maxLength 32 */
  label?: string
}

export type SimpleDomain = {
  /** @format uuid */
  id: string
  /** @maxLength 255 */
  name: string
}

export type SimpleDomainRequest = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name: string
}

export type SimpleDraftActivity = {
  /** @format uuid */
  id: string
  name: string
  timetable_id: number
}

export type SimpleEnrolledStudent = {
  /** @format uuid */
  id: string
  full_name: string
}

export type SimpleGradeLevel = {
  id: number
  /** @maxLength 64 */
  name: string
}

export type SimpleGradeLevelRequest = {
  /**
   * @minLength 1
   * @maxLength 64
   */
  name: string
}

export type SimpleHouse = {
  id: number
  /** @maxLength 128 */
  name: string
}

export type SimpleParentContact = {
  /** @format uuid */
  id: string
  full_name: string
  /**
   * Email address
   * @format email
   * @maxLength 254
   */
  email: string
  /** @maxLength 128 */
  phone_number?: string
}

export type SimpleParentContactRequest = {
  /**
   * Email address
   * @format email
   * @minLength 1
   * @maxLength 254
   */
  email: string
  /** @maxLength 128 */
  phone_number?: string
}

export type SimpleQuarter = {
  id: number
  short_name: QuarterShortNameEnum
}

export type SimpleRoom = {
  id: number
  /** @maxLength 128 */
  name: string
}

export type SimpleRoomRequest = {
  /**
   * @minLength 1
   * @maxLength 128
   */
  name: string
}

export type SimpleSchoolYear = {
  id: number
  /** @maxLength 32 */
  name: string
}

export type SimpleSchoolYearRequest = {
  /**
   * @minLength 1
   * @maxLength 32
   */
  name: string
}

/** Sections without enrollments */
export type SimpleSection = {
  id: number
  quarter: number
  /** @format uuid */
  course: string
  /**
   * Main teacher
   * @format uuid
   */
  teacher?: string | null
  teacher_str: string
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  co_teacher_1_str: string
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
  co_teacher_2_str: string
  /** attendances + grades */
  co_teacher_1_perm_to_manage_lesson?: boolean
  /** attendances + grades */
  co_teacher_2_perm_to_manage_lesson?: boolean
  group_data: SectionGroup
}

export type SimpleSickReport = {
  id: string
}

export type SimpleStudentCampusAbsence = {
  /** @format uuid */
  id: string
  campus_absence_subtype: CampusAbsenceSubTypeEnum
  campus_absence_subtype_display: string
  /** @format date-time */
  date_from: string
  /** @format date-time */
  date_to: string
}

export type SimpleStudentContact = {
  /** @format uuid */
  id: string
  full_name: string
  /**
   * Email address
   * @format email
   */
  email: string
  phone_number: string
  year_of_rosenberg_entry: number | null
  /** Account created for student application process */
  is_draft_candidate: boolean
  /**
   * Active
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active: boolean
  candidate_profile: SimpleCandidateContact | null
  academic_status: AcademicStatusEnum | NullEnum | null
}

export type SimpleStudentWithGradeLevel = {
  /** @format uuid */
  id: string
  full_name: string
  current_grade_level: SimpleGradeLevel
}

export type SimpleSubtopic = {
  /** @format uuid */
  id: string
  /** @maxLength 255 */
  name: string
  /**
   * Planned number of lessons for completing the subtopic
   * @min 0
   * @max 2147483647
   */
  duration: number
  /** @maxLength 20 */
  code?: string | null
  /**
   * Indicates the order of subtopics in the form
   * @min 0
   * @max 2147483647
   */
  ordering?: number
  /** @default false */
  is_used_in_section_plan: boolean
}

export type SimpleTAEDraftSection = {
  id: number
  /** @format uuid */
  teacher: string
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
  course_enrolment: number
}

export type SimpleTAEDraftSectionRequest = {
  /** @format uuid */
  teacher: string
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
  course_enrolment: number
}

export type SimpleTeam = {
  /** @format uuid */
  id: string
  name: string
}

export type SimpleTeamMember = {
  /** @format uuid */
  id: string
  first_name: string
  last_name: string
  name: string
  full_name: string
}

export type SimpleTeamWithMembers = {
  /** @format uuid */
  id: string
  name: string
  members: SimpleTeamMember[]
}

export type SimpleTopic = {
  /** @format uuid */
  id: string
  /** @maxLength 255 */
  name: string
  description?: string | null
  subtopics: SimpleSubtopic[]
}

export type SimpleUser = {
  /** @format uuid */
  id: string
  full_name: string
}

export type SimpleUserOptimized = {
  /** @format uuid */
  id: string
  full_name: string
}

export type SimpleUserWithAvatar = {
  /** @format uuid */
  id: string
  full_name: string
  /** @format uri */
  avatar: string | null
}

export type SiteSettings = {
  id: number
  /** Allow teachers to set target grades */
  target_grades_set_allowed?: boolean
  /**
   * Setting target grades starts at
   * @format date-time
   */
  target_grades_start?: string | null
  /**
   * Setting target grades ends at
   * @format date-time
   */
  target_grades_end?: string | null
  /** Edit values, or set from pending only */
  target_grades_set_all?: boolean
  /**
   * Disable generating tasks from datetime
   * @format date-time
   */
  disable_generating_tasks_from?: string | null
  /**
   * Disable generating tasks to datetime
   * @format date-time
   */
  disable_generating_tasks_to?: string | null
  /** Disable generating take student to destination task */
  disable_take_student_to_destination_task?: boolean
}

/**
 * * `S` - Small
 * * `L` - Large
 */
export type SizeEnum = 'S' | 'L'

export type Skill = {
  id: number
  /** @maxLength 256 */
  name: string
}

export type SkillRequest = {
  /**
   * @minLength 1
   * @maxLength 256
   */
  name: string
}

/**
 * * `parents` - Parents (e-mail)
 * * `medical` - Medical Liaison Team
 */
export type SkipReasonEnum = 'parents' | 'medical'

/**
 * * `manual` - Manual
 * * `check_out` - Check out
 * * `sick_report` - Sick report
 * * `doctor_appointment` - Doctor appointment
 * * `hospital_appointment` - Hospital appointment
 * * `campus_absence` - Campus absence
 * * `exam` - Exam session
 * * `programme_enrolment` - School Programme Enrolment
 */
export type SourceAaeEnum =
  | 'manual'
  | 'check_out'
  | 'sick_report'
  | 'doctor_appointment'
  | 'hospital_appointment'
  | 'campus_absence'
  | 'exam'
  | 'programme_enrolment'

export type SourceIDPPlan = {
  id: number
  idp_plan?: string
  serialized_data: any
}

/**
 * * `hf_member` - Rosenberg Health & Fitness Center Membership
 * * `none` - No membership
 */
export type SportMembershipOriginalChoiceEnum = 'hf_member' | 'none'

export type StaffMember = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  first_name?: string
  /** @maxLength 128 */
  last_name?: string
  teams: Record<
    string,
    {
      name?: string
      roles?: {
        value?: string
        label?: string
      }[]
      team_role?: {
        value?: string
        label?: string
      }
    }
  >
}

export type StaffTimetablePage = {
  count: number
  page_size: number
  current: number
  next: string | null
  previous: string | null
  results: StaffTimetableRow[]
}

export type StaffTimetableRow = {
  /** @format uuid */
  staff_id: string
  kind: KindEnum
  id: string
  /** @format date */
  date: string
  /** @format date-time */
  start: string | null
  /** @format date-time */
  end: string | null
  title: string
  /** @format uuid */
  course_id: string | null
  roles: Role[]
  status: StaffTimetableRowStatusEnum
  location: string | null
  people: People
  house: IdName | null
  team: IdName | null
  rota_type: Role | null
  team_role: string | null
}

/**
 * * `working` - Working
 * * `covered` - Covered
 * * `time_off` - Time off
 */
export type StaffTimetableRowStatusEnum = 'working' | 'covered' | 'time_off'

/**
 * * `academic` - Academic
 * * `boarding` - Boarding
 * * `tae` - TAE
 * * `other` - Other
 */
export type StaffTypeEnum = 'academic' | 'boarding' | 'tae' | 'other'

/**
 * * `0` - Pre-School
 * * `1` - Primary School
 * * `2` - Middle School
 * * `3` - High School
 */
export type StageEnum = 0 | 1 | 2 | 3

/**
 * * `PENDING` - PENDING
 * * `RECEIVED` - RECEIVED
 * * `STARTED` - STARTED
 * * `PROGRESS` - PROGRESS
 * * `FINALIZE` - FINALIZE
 * * `SUCCESS` - SUCCESS
 * * `FAILURE` - FAILURE
 * * `REVOKED` - REVOKED
 * * `REJECTED` - REJECTED
 * * `RETRY` - RETRY
 * * `IGNORED` - IGNORED
 */
export type StateEnum =
  | 'PENDING'
  | 'RECEIVED'
  | 'STARTED'
  | 'PROGRESS'
  | 'FINALIZE'
  | 'SUCCESS'
  | 'FAILURE'
  | 'REVOKED'
  | 'REJECTED'
  | 'RETRY'
  | 'IGNORED'

/**
 * * `draft` - Draft
 * * `planned` - Planned
 * * `in_progress` - In progress
 * * `ended` - Ended
 */
export type Status128Enum = 'draft' | 'planned' | 'in_progress' | 'ended'

/**
 * * `new` - New
 * * `in_progress` - In progress
 * * `failed` - Generation has failed
 * * `success` - Success
 */
export type Status241Enum = 'new' | 'in_progress' | 'failed' | 'success'

/**
 * * `live` - Live
 * * `active_in_past` - Active in past
 * * `generation_in_progress` - Generation in progress
 * * `draft` - Draft
 * * `infeasible` - Infeasible data. Please increase time, or verify sections
 * * `failed` - Generation process failed
 */
export type Status2A1Enum =
  | 'live'
  | 'active_in_past'
  | 'generation_in_progress'
  | 'draft'
  | 'infeasible'
  | 'failed'

/**
 * * `pending` - Pending
 * * `running` - Running
 * * `succeeded` - Succeeded
 * * `failed` - Failed
 * * `cancelled` - Cancelled
 * * `completed` - Completed
 */
export type Status7B2Enum =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'completed'

/**
 * * `DRAFT` - Draft
 * * `SCHEDULED` - Scheduled
 * * `OPEN` - Open
 * * `CLOSED` - Closed
 * * `ARCHIVED` - Archived
 */
export type Status8FbEnum =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'OPEN'
  | 'CLOSED'
  | 'ARCHIVED'

/**
 * * `available` - Available
 * * `last_seats` - Last seats
 */
export type Status95FEnum = 'available' | 'last_seats'

/**
 * * `generated` - Generated
 * * `applied` - Applied
 * * `stale` - Stale
 */
export type StatusE12Enum = 'generated' | 'applied' | 'stale'

/**
 * * `100` - Pending
 * * `200` - Not a Duplicate
 * * `300` - Merged
 */
export type StatusEd6Enum = 100 | 200 | 300

export type StatusResponse = {
  /** Boolean for contacts, integer for students/candidates */
  value: string
  label: string
}

export type StringOption = {
  label: string
  value: string
}

export type Student = {
  first_name: string
  middle_name: string
  last_name: string
  /** @format date */
  birthdate: string
  birthplace: string
  gender: string
  address: string
  address_city: string
  address_zipcode: string
  address_country: string
  billing_address: string
  billing_address_city: string
  billing_address_zipcode: string
  billing_address_country: string
  is_billing_address_same: boolean
  native_language?: string
  nationalities?: NationalityEnum[]
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type StudentAcademicAttendance = {
  /** @format uuid */
  id: string
  /** @format date */
  date: string
  mark_label: string
}

export type StudentAccommodation = {
  id: number
  house: number
  room?: number | null
  house_str: string
  /** @format date */
  start_date: string
  room_str: string
  room_code: string
  room_name: string
  is_room_only_for_student?: boolean
}

export type StudentAccommodationRequest = {
  house: number
  room?: number | null
  /** @format date */
  start_date: string
  is_room_only_for_student?: boolean
}

export type StudentAchievement = {
  id: number
  achievement: number
  achievement_str: string
  /** @format date */
  date: string
}

export type StudentAchievementRequest = {
  achievement: number
  /** @format date */
  date: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentAdditionalAusgang = {
  id: string
  status: MeritRewardStatusEnum
  /** @format uuid */
  student_id: string
  /** @format date-time */
  created: string
  /** @format date-time */
  date_from: string
  /** @format date-time */
  date_to: string
  leave_details: string
  custom_name: string
  decision_comment: string
  campus_absence_students: StudentMeritRewardParticipant[]
  my_participation: StudentMeritRewardParticipant
  /** @format uuid */
  campus_absence: string | null
  notifications_mark_as_read: number | null
  student_saw_changes: boolean
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentAdditionalAusgangRequest = {
  student?: string
  /** @format date-time */
  date_from: string
  /** @format date-time */
  date_to: string
  /** @minLength 1 */
  leave_details: string
  /** @minLength 1 */
  custom_name: string
  campus_absence_students: StudentMeritRewardParticipantRequest[]
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type StudentAddress = {
  id: number
  /** @format uuid */
  user: string
  label?: string
  name: string
  /**
   * @min 0
   * @max 32767
   */
  address_type: AddressTypeEnum
  address: string
  address_city: string
  address_zipcode: string
  address_country: string
  order: number
}

export type StudentAddressRequest = {
  label?: string
  /** @minLength 1 */
  name: string
  /**
   * @min 0
   * @max 32767
   */
  address_type: AddressTypeEnum
  /** @minLength 1 */
  address: string
  /** @minLength 1 */
  address_city: string
  /** @minLength 1 */
  address_zipcode: string
  /** @minLength 1 */
  address_country: string
}

export type StudentAimForCourse = {
  id: number
  /** @format uuid */
  student: string
  /** @format uuid */
  course: string
  aim?: number | null
}

export type StudentAimForCourseRequest = {
  /** @format uuid */
  student: string
  /** @format uuid */
  course: string
  aim?: number | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentAllergy = {
  id: number
  /** @format uuid */
  student: string
  allergen_kind: string
  allergen_subtypes?: string[]
  reactions?: StudentAllergyReaction[]
  medicines?: string[]
  is_emergency_medicine?: boolean
  medicine_location?: MedicineLocationEnum | BlankEnum
  immediate_response?: StudentAllergyImmediateResponseEnum | BlankEnum
  medicine_location_display: string
  immediate_response_display: string
}

/**
 * * `medicine_and_emergency` - Apply Medicine + Contact Emergency Services (Dial 122)
 * * `emergency` - Contact Emergency Services (Dial 122)
 * * `report_to_health_coordinator` - Immediate Report to Health Coordinator + BDT
 */
export type StudentAllergyImmediateResponseEnum =
  | 'medicine_and_emergency'
  | 'emergency'
  | 'report_to_health_coordinator'

export type StudentAllergyReaction = {
  id: number
  reaction: string
  severe_reactions?: string[]
  mild_reactions?: string[]
}

export type StudentAllergyReactionRequest = {
  /** @minLength 1 */
  reaction: string
  severe_reactions?: string[]
  mild_reactions?: string[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentAllergyRequest = {
  /** @format uuid */
  student: string
  /** @minLength 1 */
  allergen_kind: string
  allergen_subtypes?: string[]
  reactions?: StudentAllergyReactionRequest[]
  medicines?: string[]
  is_emergency_medicine?: boolean
  medicine_location?: MedicineLocationEnum | BlankEnum
  immediate_response?: StudentAllergyImmediateResponseEnum | BlankEnum
}

export type StudentAnnouncement = {
  id: string
  emojis?: string[]
  /** @maxLength 100 */
  text: string
  /** @maxLength 7 */
  bg_color?: string
  student: string
}

export type StudentApplication = {
  external_id: string
  status: string
  /** @format date-time */
  paid_on?: string | null
  student: Student
  applicant_relation: string
  applicant_first_name: string
  applicant_last_name: string
  /** @format email */
  applicant_email: string
  applicant_salutation?: string | null
  applicant_salutation_other?: string | null
  applicant_occupation?: string | null
  /** @format email */
  applicant_secondary_email?: string | null
  applicant_phone?: string | null
  parent_1_first_name: string
  parent_1_last_name: string
  parent_1_salutation: string
  parent_1_salutation_other?: string
  parent_1_occupation: string
  /** @format email */
  parent_1_email: string
  /** @format email */
  parent_1_secondary_email?: string
  parent_1_phone: string
  parent_2_first_name?: string
  parent_2_last_name?: string
  parent_2_salutation?: string
  parent_2_salutation_other?: string
  parent_2_occupation?: string
  /** @format email */
  parent_2_email?: string
  /** @format email */
  parent_2_secondary_email?: string
  parent_2_phone?: string
  legal_guardian_first_name?: string
  legal_guardian_last_name?: string
  legal_guardian_salutation?: string
  legal_guardian_salutation_other?: string
  legal_guardian_occupation?: string
  /** @format email */
  legal_guardian_email?: string
  /** @format email */
  legal_guardian_secondary_email?: string
  legal_guardian_phone?: string
  /**
   * * `first_parent` - First Parent
   * * `second_parent` - Second Parent
   * * `both_parents` - Both Parents
   * * `relative` - Relative
   * * `other` - Other
   */
  legal_guardian_relation: LegalGuardianRelationEnum
  contact_person?: ContactPersonEnum | BlankEnum
  contact_first_name?: string
  contact_last_name?: string
  contact_occupation?: string
  /** @format email */
  contact_email?: string
  /** @format email */
  contact_secondary_email?: string
  contact_phone?: string
  contact_child_relation?: string
  contact_child_relation_other?: string
  is_contact_allowed_to_visit?: boolean
  date_of_entry_to_rosenberg_year?: number
  career_aim?: string
  mission_control_degree_1_aim?: string
  mission_control_degree_2_aim?: string
  mission_control_degree_1_destination?: string
  mission_control_degree_2_destination?: string
  last_schools?: any[]
  studied_languages?: any[]
  has_other_language_qualifications?: boolean
  other_language_qualifications?: string
  rosenberg_legacies?: any[]
  current_hobbies?: string
  current_interests?: string
  would_have_to_lunch_with?: string
  awards?: any[]
  has_any_least_favourite_subjects?: boolean
  least_favourite_subjects?: any[]
  favourite_subjects?: any[]
  last_book_read?: string
  does_student_play_any_musical_instruments?: boolean
  musical_instruments?: any[]
  has_any_allergies: boolean
  allergies?: any[]
  has_any_chronic_conditions: boolean
  chronic_conditions?: any[]
  has_any_dietary_restrictions: boolean
  dietary_restrictions?: string
  has_any_regular_medication: boolean
  medications?: StudentApplicationMedication[]
  has_any_mental_health_issues: boolean
  mental_health_issues?: any[]
  has_regular_psychological_care: boolean
  medical_reports?: string[]
  /** @format double */
  student_height?: number
  /** @format double */
  student_weight?: number
  student_fitness_for_sports: string
  student_blood_type?: string
  is_student_swimmer: boolean
  is_more_medical_information_available: boolean
  more_medical_information?: string
  has_student_contact_with_drugs: boolean
  has_been_student_expelled: boolean
  report_cards_from_last_three_years?: string[]
  personal_statement?: string[]
  /** @format uri */
  student_photo_in_passport_format: string
  student_passport_photo: string[]
  legal_guardian_passport_photo: string[]
  vaccination_record?: string[]
  other_document?: string[]
  decision_comment: string
  focus_courses?: ApplicationSummerSubjectAssign[]
  private_lessons?: ApplicationSummerSubjectAssign[]
  package?: string
  duration?: number | null
  additional_comments?: string
  /** @format date */
  arrival?: string | null
  airport_service?: boolean
  airport_origin?: string
  airport_contact_person?: string
  airport_contact_first_name?: string
  airport_contact_last_name?: string
  airport_contact_occupation?: string
  /** @format email */
  airport_contact_email?: string
  airport_contact_secondary_email?: string
  airport_contact_phone?: string
  airport_contact_salutation?: string
  airport_contact_salutation_other?: string
  flight_number?: string
  train_number?: string
  /** @format date-time */
  requested_arrival?: string | null
  has_tetanus_vaccination?: boolean
  /** @format date */
  tetanus_vaccination_date?: string | null
}

export type StudentApplicationMedication = {
  medicine_name: string
  dosage: string
  reason_and_diagnosis: string
}

export type StudentApplicationMedicationRequest = {
  /** @minLength 1 */
  medicine_name: string
  /** @minLength 1 */
  dosage: string
  /** @minLength 1 */
  reason_and_diagnosis: string
}

export type StudentApplicationRequest = {
  /** @minLength 1 */
  external_id: string
  /** @minLength 1 */
  status: string
  /** @format date-time */
  paid_on?: string | null
  student: StudentRequest
  /** @minLength 1 */
  applicant_relation: string
  /** @minLength 1 */
  applicant_first_name: string
  /** @minLength 1 */
  applicant_last_name: string
  /**
   * @format email
   * @minLength 1
   */
  applicant_email: string
  applicant_salutation?: string | null
  applicant_salutation_other?: string | null
  applicant_occupation?: string | null
  /** @format email */
  applicant_secondary_email?: string | null
  applicant_phone?: string | null
  /** @minLength 1 */
  parent_1_first_name: string
  /** @minLength 1 */
  parent_1_last_name: string
  /** @minLength 1 */
  parent_1_salutation: string
  parent_1_salutation_other?: string
  /** @minLength 1 */
  parent_1_occupation: string
  /**
   * @format email
   * @minLength 1
   */
  parent_1_email: string
  /** @format email */
  parent_1_secondary_email?: string
  /** @minLength 1 */
  parent_1_phone: string
  parent_2_first_name?: string
  parent_2_last_name?: string
  parent_2_salutation?: string
  parent_2_salutation_other?: string
  parent_2_occupation?: string
  /** @format email */
  parent_2_email?: string
  /** @format email */
  parent_2_secondary_email?: string
  parent_2_phone?: string
  legal_guardian_first_name?: string
  legal_guardian_last_name?: string
  legal_guardian_salutation?: string
  legal_guardian_salutation_other?: string
  legal_guardian_occupation?: string
  /** @format email */
  legal_guardian_email?: string
  /** @format email */
  legal_guardian_secondary_email?: string
  legal_guardian_phone?: string
  /**
   * * `first_parent` - First Parent
   * * `second_parent` - Second Parent
   * * `both_parents` - Both Parents
   * * `relative` - Relative
   * * `other` - Other
   */
  legal_guardian_relation: LegalGuardianRelationEnum
  contact_person?: ContactPersonEnum | BlankEnum
  contact_first_name?: string
  contact_last_name?: string
  contact_occupation?: string
  /** @format email */
  contact_email?: string
  /** @format email */
  contact_secondary_email?: string
  contact_phone?: string
  contact_child_relation?: string
  contact_child_relation_other?: string
  is_contact_allowed_to_visit?: boolean
  date_of_entry_to_rosenberg_year?: number
  /** @minLength 1 */
  career_aim?: string
  mission_control_degree_1_aim?: string
  mission_control_degree_2_aim?: string
  mission_control_degree_1_destination?: string
  mission_control_degree_2_destination?: string
  last_schools?: any[]
  studied_languages?: any[]
  has_other_language_qualifications?: boolean
  other_language_qualifications?: string
  rosenberg_legacies?: any[]
  /** @minLength 1 */
  current_hobbies?: string
  /** @minLength 1 */
  current_interests?: string
  /** @minLength 1 */
  would_have_to_lunch_with?: string
  awards?: any[]
  has_any_least_favourite_subjects?: boolean
  least_favourite_subjects?: any[]
  favourite_subjects?: any[]
  /** @minLength 1 */
  last_book_read?: string
  does_student_play_any_musical_instruments?: boolean
  musical_instruments?: any[]
  has_any_allergies: boolean
  allergies?: any[]
  has_any_chronic_conditions: boolean
  chronic_conditions?: any[]
  has_any_dietary_restrictions: boolean
  dietary_restrictions?: string
  has_any_regular_medication: boolean
  medications?: StudentApplicationMedicationRequest[]
  has_any_mental_health_issues: boolean
  mental_health_issues?: any[]
  has_regular_psychological_care: boolean
  medical_reports?: string[]
  /** @format double */
  student_height?: number
  /** @format double */
  student_weight?: number
  /** @minLength 1 */
  student_fitness_for_sports: string
  student_blood_type?: string
  is_student_swimmer: boolean
  is_more_medical_information_available: boolean
  more_medical_information?: string
  has_student_contact_with_drugs: boolean
  has_been_student_expelled: boolean
  report_cards_from_last_three_years?: string[]
  personal_statement?: string[]
  /**
   * @format uri
   * @minLength 1
   */
  student_photo_in_passport_format: string
  student_passport_photo: string[]
  legal_guardian_passport_photo: string[]
  vaccination_record?: string[]
  other_document?: string[]
  decision_comment: string
  focus_courses?: ApplicationSummerSubjectAssignRequest[]
  private_lessons?: ApplicationSummerSubjectAssignRequest[]
  /** @minLength 1 */
  package?: string
  duration?: number | null
  additional_comments?: string
  /** @format date */
  arrival?: string | null
  airport_service?: boolean
  airport_origin?: string
  airport_contact_person?: string
  airport_contact_first_name?: string
  airport_contact_last_name?: string
  airport_contact_occupation?: string
  /** @format email */
  airport_contact_email?: string
  airport_contact_secondary_email?: string
  airport_contact_phone?: string
  airport_contact_salutation?: string
  airport_contact_salutation_other?: string
  flight_number?: string
  train_number?: string
  /** @format date-time */
  requested_arrival?: string | null
  has_tetanus_vaccination?: boolean
  /** @format date */
  tetanus_vaccination_date?: string | null
}

export type StudentAssignmentGrade = {
  percents: number
  grade: string
  assignment_type: CourseAssignmentType
}

export type StudentAssignmentItem = {
  /** @format uuid */
  id: string
  grade_in_percent: number | null
  /**
   * Custom grades are eligible only for assignments related with CCU courses
   *
   * * `SC` - Successfully Completed
   * * `CA` - Successfully Completed & Credit Awarded
   */
  custom_grade?: TECustomGradeEnum | BlankEnum
  average_grade: number | null
  /** @maxLength 32 */
  grade_in_ib?: string
  is_submitted?: boolean
  is_incomplete?: boolean
  is_late?: boolean
  is_exempt?: boolean
  /** @format uuid */
  student: string
  student_full_name: string
  /** @format date-time */
  grade_modified_at?: string | null
  /**
   * Moderated grade in percents.
   * @min 0
   * @max 100
   */
  moderated_grade?: number | null
  moderated_grade_status: ModeratedGradeStatusEnum | NullEnum | null
}

export type StudentAssignmentItemRequest = {
  /**
   * Custom grades are eligible only for assignments related with CCU courses
   *
   * * `SC` - Successfully Completed
   * * `CA` - Successfully Completed & Credit Awarded
   */
  custom_grade?: TECustomGradeEnum | BlankEnum
  /** @maxLength 32 */
  grade_in_ib?: string
  is_submitted?: boolean
  is_incomplete?: boolean
  is_late?: boolean
  is_exempt?: boolean
  /** @format uuid */
  student: string
  /** @format date-time */
  grade_modified_at?: string | null
  /**
   * Moderated grade in percents.
   * @min 0
   * @max 100
   */
  moderated_grade?: number | null
}

export type StudentAssignmentItemWithGradeLevel = {
  /** @format uuid */
  id: string
  grade_in_percent: number | null
  /**
   * Custom grades are eligible only for assignments related with CCU courses
   *
   * * `SC` - Successfully Completed
   * * `CA` - Successfully Completed & Credit Awarded
   */
  custom_grade?: TECustomGradeEnum | BlankEnum
  average_grade: number | null
  /** @maxLength 32 */
  grade_in_ib?: string
  is_submitted?: boolean
  is_incomplete?: boolean
  is_late?: boolean
  is_exempt?: boolean
  /** @format uuid */
  student: string
  student_full_name: string
  /** @format date-time */
  grade_modified_at?: string | null
  /**
   * Moderated grade in percents.
   * @min 0
   * @max 100
   */
  moderated_grade?: number | null
  moderated_grade_status: ModeratedGradeStatusEnum | NullEnum | null
  /** Student's grade level in assignment's school year. Returns student's current grade level as a fallback. */
  student_grade_level_id: number | null
}

export type StudentAssignmentScore = {
  /** @format uuid */
  id?: string
  /** @format uuid */
  student_id: string
  /** @format double */
  achieved_points?: number | null
  student_full_name: string
}

export type StudentAssignmentScoreRequest = {
  /** @format uuid */
  id?: string
  /** @format uuid */
  student_id: string
  /** @format double */
  achieved_points?: number | null
}

export type StudentBasic = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  first_name?: string
  /** @maxLength 128 */
  last_name?: string
  full_name: string
  /**
   * mainly student's app usage
   * -> we're considering only first name given
   */
  reduced_full_name: string
}

export type StudentBasicInfo = {
  /** @format uuid */
  id: string
  full_name: string
  has_allergy: boolean
}

export type StudentBillingDetails = {
  abacus_id: string
  /** @format email */
  billing_email_address: string
  /**
   * * `5` - Settled
   * * `10` - First reminder
   * * `15` - Second reminder
   * * `17` - Unsettled
   * * `20` - App locked
   */
  saldo: SaldoEnum
  main_invoice_recipient: BillingRecipient | null
  invoice_recipient: BillingRecipient | null
  invoice_copy_recipients: BillingRecipient[]
}

export type StudentBillingDetailsRequest = {
  /**
   * * `5` - Settled
   * * `10` - First reminder
   * * `15` - Second reminder
   * * `17` - Unsettled
   * * `20` - App locked
   */
  saldo: SaldoEnum
  main_invoice_recipient: BillingRecipientRequest | null
  invoice_recipient: BillingRecipientRequest | null
  invoice_copy_recipients: BillingRecipientRequest[]
}

export type StudentBirthdayValidateRequest = {
  /** @format date */
  date_of_birth: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentBookTheCafe = {
  id: string
  status: MeritRewardStatusEnum
  /** @format uuid */
  student_id: string
  /** @format date-time */
  created: string
  /** @format date */
  date: string
  decision_comment: string
  /** @maxLength 128 */
  movie: string
  campus_absence_students: StudentMeritRewardParticipant[]
  my_participation: StudentMeritRewardParticipant
  /** @format uuid */
  campus_absence: string | null
  notifications_mark_as_read: number | null
  student_saw_changes: boolean
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentBookTheCafeRequest = {
  student?: string
  /** @format date */
  date: string
  /**
   * @minLength 1
   * @maxLength 128
   */
  movie: string
  campus_absence_students: StudentMeritRewardParticipantRequest[]
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentCampusAbsence = {
  /** @format uuid */
  id: string
  created_by?: string
  campus_absence_type: CampusAbsenceTypeEnum
  campus_absence_type_label: string
  /**
   * * `afternoon_ausgang` - Afternoon Ausgang
   * * `evening_ausgang` - Evening Ausgang
   * * `evening_campus_ausgang` - Evening - Campus Ausgang
   * * `birthday_ausgang` - Birthday Ausgang
   * * `family_visit` - Family visit
   * * `on_board` - On board Ausgang
   * * `prolonged_ausgang` - Prolonged Ausgang
   * * `campus` - Campus
   * * `offsite` - Offsite
   * * `offsite_overnight` - Offsite, Overnight
   * * `official_appointment` - Official appointment
   * * `specialist_doctor_appointment` - Specialist Doctor Appointment
   * * `programme` - Programme
   * * `house_evening` - House evening
   * * `term_break` - Term Break
   * * `school_trip` - School Trip
   * * `weekend_programme` - Weekend Programme
   * * `other` - Other
   * * `book_the_cafe` - Book the Cafe
   * * `city_brunch_zurich` - City Brunch Zurich
   * * `additional_ausgang` - Additional Ausgang
   * * `on_campus_ausgang` - On Campus Ausgang
   */
  campus_absence_subtype: CampusAbsenceSubTypeEnum
  custom_name?: string
  campus_absence_subtype_label: string
  status: CampusAbsenceStatusEnum
  status_label: string
  leave_details?: string
  /** @maxLength 256 */
  contact_person_name?: string
  /** @maxLength 128 */
  contact_person_phone?: string
  /** @format date-time */
  date_from: string
  /** @format date-time */
  date_to: string
  can_cancel: boolean
  campus_absence_students?: StudentCampusAbsenceStudent[]
  can_edit: boolean
  student_saw_changes: boolean
  object_type: ObjectTypeEnum
  notifications_mark_as_read: number | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentCampusAbsenceRequest = {
  created_by?: string
  /**
   * * `afternoon_ausgang` - Afternoon Ausgang
   * * `evening_ausgang` - Evening Ausgang
   * * `evening_campus_ausgang` - Evening - Campus Ausgang
   * * `birthday_ausgang` - Birthday Ausgang
   * * `family_visit` - Family visit
   * * `on_board` - On board Ausgang
   * * `prolonged_ausgang` - Prolonged Ausgang
   * * `campus` - Campus
   * * `offsite` - Offsite
   * * `offsite_overnight` - Offsite, Overnight
   * * `official_appointment` - Official appointment
   * * `specialist_doctor_appointment` - Specialist Doctor Appointment
   * * `programme` - Programme
   * * `house_evening` - House evening
   * * `term_break` - Term Break
   * * `school_trip` - School Trip
   * * `weekend_programme` - Weekend Programme
   * * `other` - Other
   * * `book_the_cafe` - Book the Cafe
   * * `city_brunch_zurich` - City Brunch Zurich
   * * `additional_ausgang` - Additional Ausgang
   * * `on_campus_ausgang` - On Campus Ausgang
   */
  campus_absence_subtype: CampusAbsenceSubTypeEnum
  custom_name?: string
  leave_details?: string
  /** @maxLength 256 */
  contact_person_name?: string
  /** @maxLength 128 */
  contact_person_phone?: string
  /** @format date-time */
  date_from: string
  /** @format date-time */
  date_to: string
  campus_absence_students?: StudentCampusAbsenceStudentRequest[]
}

export type StudentCampusAbsenceStatus = {
  label: string
  /**
   * * `requested` - Pending
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `canceled_by_student` - Canceled by student
   * * `done` - Done
   * * `cancelled` - Cancelled
   */
  value: CampusAbsenceStatusEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentCampusAbsenceStudent = {
  /** @format uuid */
  id: string
  /** @format uuid */
  student: string
  student_data: StudentData
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentCampusAbsenceStudentRequest = {
  /** @format uuid */
  student: string
}

export type StudentCampusAbsenceSubtype = {
  label: string
  /**
   * * `afternoon_ausgang` - Afternoon Ausgang
   * * `evening_ausgang` - Evening Ausgang
   * * `evening_campus_ausgang` - Evening - Campus Ausgang
   * * `birthday_ausgang` - Birthday Ausgang
   * * `family_visit` - Family visit
   * * `on_board` - On board Ausgang
   * * `prolonged_ausgang` - Prolonged Ausgang
   * * `campus` - Campus
   * * `offsite` - Offsite
   * * `offsite_overnight` - Offsite, Overnight
   * * `official_appointment` - Official appointment
   * * `specialist_doctor_appointment` - Specialist Doctor Appointment
   * * `programme` - Programme
   * * `house_evening` - House evening
   * * `term_break` - Term Break
   * * `school_trip` - School Trip
   * * `weekend_programme` - Weekend Programme
   * * `other` - Other
   * * `book_the_cafe` - Book the Cafe
   * * `city_brunch_zurich` - City Brunch Zurich
   * * `additional_ausgang` - Additional Ausgang
   * * `on_campus_ausgang` - On Campus Ausgang
   */
  value: CampusAbsenceSubTypeEnum
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type StudentCampusAbsenceType = {
  label: string
  /**
   * * `ausgang` - Ausgang
   * * `external_exam` - External exam
   * * `official` - Official
   * * `school_programme` - School Programme
   * * `extra_ausgang` - Extra Ausgang
   */
  value: CampusAbsenceTypeEnum
  subtypes: StudentCampusAbsenceSubtype[]
}

export type StudentCandidateProfileDetail = {
  /** @format uuid */
  id: string
  /** @format date-time */
  last_update: string
  last_updated_by: SimpleUser
  full_name: string
  year_of_rosenberg_entry: number | null
  current_grade_level?: number | null
  /** @format date */
  date_of_entry?: string | null
  is_resident_entrepreneur?: boolean | null
  academic_status: AcademicStatusEnum | NullEnum | null
}

export type StudentCandidateProfileDetailRequest = {
  year_of_rosenberg_entry: number | null
  current_grade_level?: number | null
  /** @format date */
  date_of_entry?: string | null
  is_resident_entrepreneur?: boolean | null
}

export type StudentCandidateProfileList = {
  /** @format uuid */
  id: string
  /** @format date-time */
  date_joined: string
  /** @format date-time */
  last_update: string
  last_updated_by: SimpleUser
  full_name: string
  /** @format date */
  date_of_birth: string | null
  nationalities: NationalityEnum[]
  year_of_rosenberg_entry: number | null
  /** @format date */
  date_of_entry: string | null
  is_resident_entrepreneur: boolean | null
  gender: ForGenderEnum
  academic_status: AcademicStatusEnum | NullEnum | null
}

export type StudentCasualAnyday = {
  id: string
  status: MeritRewardStatusEnum
  /** @format date-time */
  created: string
  /** @format date */
  date: string
  decision_comment: string
  notifications_mark_as_read: number | null
  student_saw_changes: boolean
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type StudentCasualAnydayRequest = {
  student?: string
  /** @format date */
  date: string
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

/**
 * * `4` - S
 * * `3` - E
 * * `2` - A
 * * `1` - C
 * * `0` - not assigned
 */
export type StudentCategoryEnum = 4 | 3 | 2 | 1 | 0

export type StudentCategoryItem = {
  /** @format date-time */
  downgrade_due_date: string | null
  is_changed_by_downgrade: boolean
  /** @format uuid */
  id: string
  /** @format uuid */
  student: string
  student_str: string
  /**
   * @min -2147483648
   * @max 2147483647
   */
  assigned_category?: AssignedCategory92AEnum
  is_completed?: boolean
  week_ago_category: WeekAgoCategoryEnum
  two_weeks_ago_category: TwoWeeksAgoCategoryEnum
  /** @format double */
  weekly_merit_count: number
  pending_merit_count: number
}

export type StudentCategoryItemList = {
  house: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  results: StudentCategoryItem[]
}

export type StudentCategoryItemRequest = {
  /** @format uuid */
  student: string
  /**
   * @min -2147483648
   * @max 2147483647
   */
  assigned_category?: AssignedCategory92AEnum
  is_completed?: boolean
}

export type StudentCategoryReportList = {
  /** @format uuid */
  id: string
  /** @format date-time */
  created: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  house_str: string
  status_display: string
  /**
   * * `new` - New
   * * `incomplete` - Incomplete
   * * `finished` - Finished
   */
  status?: BoardingAttendanceReportStatusEnum
}

export type StudentCategoryTransition = {
  /**
   * * `S` - S
   * * `E` - E
   * * `A` - A
   * * `C` - C
   * * `not assigned` - not assigned
   */
  assigned_category: StudentCategoryTransitionAssignedCategoryEnum
  background_color: string
  text: string
  text_color: string
  assignment_color: string
  upgrade_text: string
}

/**
 * * `S` - S
 * * `E` - E
 * * `A` - A
 * * `C` - C
 * * `not assigned` - not assigned
 */
export type StudentCategoryTransitionAssignedCategoryEnum =
  | 'S'
  | 'E'
  | 'A'
  | 'C'
  | 'not assigned'

export type StudentCategoryUpgrade = {
  id: string
  status: MeritRewardStatusEnum
  /** @format date-time */
  created: string
  decision_comment: string
  notifications_mark_as_read: number | null
  student_saw_changes: boolean
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type StudentCategoryUpgradeRequest = {
  student?: string
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type StudentChangePhoto = {
  /**
   * @format uri
   * @pattern (?:png|jpg|jpeg)$
   */
  photo: string
  photo_display_name: string
}

export type StudentChoiceFreeSpots = {
  course_enrollment_id: number
  free_spots: number
}

export type StudentChronicCondition = {
  id: number
  /** @format uuid */
  student: string
  condition_type: string
  condition_subtype?: string[]
  other_condition_type?: string
  impact?: ImpactEnum[]
  watch_out_for?: WatchOutForEnum[]
  emergency_medicines?: string[]
  emergency_medicine_applications?: EmergencyMedicineApplicationsEnum[]
  emergency_medicine_location?: EmergencyMedicineLocationEnum[]
  iep_contact?: IepContactEnum[]
  immediate_response?: StudentChronicConditionImmediateResponseEnum | BlankEnum
  comment?: string
  has_medicine_plan?: boolean
  has_emergency_medicine?: boolean
  has_iep?: boolean
  impact_display: string
  watch_out_for_display: string
  emergency_medicine_applications_display: string
  emergency_medicine_location_display: string
  iep_contact_display: string
  immediate_response_display: string
}

/**
 * * `medicine_and_emergency` - Apply Medicine + Contact Emergency Services (Dial 144)
 * * `emergency` - Contact Emergency Services (Dial 144)
 * * `report_to_health_coordinator` - Immediate Report to Health Coordinator + BDT
 */
export type StudentChronicConditionImmediateResponseEnum =
  | 'medicine_and_emergency'
  | 'emergency'
  | 'report_to_health_coordinator'

export type StudentChronicConditionRequest = {
  /** @format uuid */
  student: string
  /** @minLength 1 */
  condition_type: string
  condition_subtype?: string[]
  other_condition_type?: string
  impact?: ImpactEnum[]
  watch_out_for?: WatchOutForEnum[]
  emergency_medicines?: string[]
  emergency_medicine_applications?: EmergencyMedicineApplicationsEnum[]
  emergency_medicine_location?: EmergencyMedicineLocationEnum[]
  iep_contact?: IepContactEnum[]
  immediate_response?: StudentChronicConditionImmediateResponseEnum | BlankEnum
  comment?: string
  has_medicine_plan?: boolean
  has_emergency_medicine?: boolean
  has_iep?: boolean
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentCityBrunchZurich = {
  id: string
  status: MeritRewardStatusEnum
  /** @format uuid */
  student_id: string
  /** @format date-time */
  created: string
  /** @format date */
  date: string
  decision_comment: string
  campus_absence_students: StudentMeritRewardParticipant[]
  my_participation: StudentMeritRewardParticipant
  /** @format uuid */
  campus_absence: string | null
  notifications_mark_as_read: number | null
  student_saw_changes: boolean
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentCityBrunchZurichRequest = {
  student?: string
  /** @format date */
  date: string
  campus_absence_students: StudentMeritRewardParticipantRequest[]
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type StudentClashes = {
  lessons: LessonClashes[]
  exams: ExamClashes[]
  programmes: ProgrammeClashes[]
  leave_requests: LeaveRequestClashes[]
}

export type StudentClashesNumber = {
  clashes_number: number
}

export type StudentClashesNumberRequest = {
  clashes_number: number
}

export type StudentConnection = {
  id: number
  /** @format uuid */
  parent: string
  /** @format uuid */
  student: string
  /** Parent details */
  contact_data: SimpleParentContact
  /** Parent roles for student */
  roles: ContactTypeEnum[]
  has_app_access: boolean
}

export type StudentContent =
  | AppMysteryLink
  | AppStudentAnnouncement
  | AppStudentPic

export type StudentContentRequest =
  | AppMysteryLinkRequest
  | AppStudentAnnouncementRequest
  | AppStudentPicRequest

/**
 * * `StudentMysteryLink` - StudentMysteryLink
 * * `StudentAnnouncement` - StudentAnnouncement
 * * `StudentPic` - StudentPic
 */
export type StudentContentResourcetypeEnum =
  | 'StudentMysteryLink'
  | 'StudentAnnouncement'
  | 'StudentPic'

/**
 * * `pending` - Pending
 * * `approved` - Approved
 * * `rejected` - Rejected
 * * `deleted` - Deleted
 * * `archived` - Archived
 */
export type StudentContentStatusEnum =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'deleted'
  | 'archived'

export type StudentCourseCatalogue = {
  id: number
  name: string
  status: Status95FEnum
  private_package: boolean
  is_private: boolean
}

export type StudentCourseCatalogueError = {
  /**
   * * `no_active_enrolment` - no_active_enrolment
   * * `deadline_passed` - deadline_passed
   */
  code: StudentCourseCatalogueErrorCodeEnum
  detail: string
}

/**
 * * `no_active_enrolment` - no_active_enrolment
 * * `deadline_passed` - deadline_passed
 */
export type StudentCourseCatalogueErrorCodeEnum =
  | 'no_active_enrolment'
  | 'deadline_passed'

export type StudentCourseGradesPeriods = {
  quarter: string
  semester: string
  potential: string
}

export type StudentCourseRequirement = {
  id: number
  course: string
  min_grade: string
  current_grade: string
  potential_grade: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /**
   * @min 0
   * @max 100
   */
  min_percents?: number | null
  /**
   * @min 0
   * @max 100
   */
  current_percents?: number | null
  /**
   * @min 0
   * @max 100
   */
  potential_percents?: number | null
  aim?: number | null
  year: number
}

export type StudentCourseRequirementRequest = {
  /** @minLength 1 */
  min_grade: string
  /** @minLength 1 */
  current_grade: string
  /** @minLength 1 */
  potential_grade: string
  /**
   * @min 0
   * @max 100
   */
  min_percents?: number | null
  /**
   * @min 0
   * @max 100
   */
  current_percents?: number | null
  /**
   * @min 0
   * @max 100
   */
  potential_percents?: number | null
  aim?: number | null
  year: number
}

export type StudentCourseYearResponse = {
  id: number
  name: string
  is_active: boolean
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type StudentCourses = {
  id: string
  name: string
  teacher: UserInCourse
  co_teacher_1?: UserInCourse
  co_teacher_2?: UserInCourse
  grades: StudentGrade[]
  current_grade: StudentGrade
  notifications_mark_as_read: number | null
  homework_count: number
}

export type StudentCreateRequest = {
  /** Child is set instead of add in m2m save, handle blank save_kwargs */
  user_details: StudentDetailRequest
  arrival_info?: ArrivalInfoRequest | null
  courses_and_lessons?: SummerCampStudentAssignsRequest | null
  rosenberg_legacy?: StudentLegaciesWithSummerCampsRequest | null
}

export type StudentData = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  first_name?: string
  /** @maxLength 128 */
  last_name?: string
  full_name: string
  /**
   * mainly student's app usage
   * -> we're considering only first name given
   */
  reduced_full_name: string
  /** @format uri */
  avatar?: string | null
}

export type StudentDataRequest = {
  /** @maxLength 128 */
  first_name?: string
  /** @maxLength 128 */
  last_name?: string
  /** @format binary */
  avatar?: File | null
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type StudentDayMenu = {
  id: number
  /** @format date */
  date: string
  is_skipped: boolean
  lunch_starter: string
  lunch_soup: string
  lunch_main_course: string
  lunch_dessert: string
  lunch_starter_vege: string
  lunch_soup_vege: string
  lunch_main_course_vege: string
  lunch_dessert_vege: string
  dinner_starter: string
  dinner_soup: string
  dinner_main_course: string
  dinner_dessert: string
  dinner_starter_vege: string
  dinner_soup_vege: string
  dinner_main_course_vege: string
  dinner_dessert_vege: string
  breakfast_special: string
  breakfast_special_vege: string
  is_room_service: boolean
  student_skipped_meals: MealTypeEnum[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentDetail = {
  /** @format uuid */
  id: string
  /** @format date-time */
  date_joined: string
  /** @format date-time */
  last_update: string
  /** Required. 150 characters or fewer. Letters, digits and @/./+/-/_ only. */
  username: string
  /** @maxLength 256 */
  nickname?: string
  /** @maxLength 128 */
  first_name: string
  /** @maxLength 128 */
  last_name: string
  full_name: string
  /** @maxLength 256 */
  name?: string
  /** @maxLength 256 */
  title?: string
  current_grade_level?: number | null
  current_grade_level_str: string | null
  /**
   * Email address
   * @format email
   * @maxLength 254
   */
  email: string
  billing_email_address: string
  billing_additional_email_id?: number | null
  /** @maxLength 128 */
  phone_number?: string
  /** @maxItems 250 */
  nationalities?: (NationalityEnum | BlankEnum)[]
  native_language?: NativeLanguageEnum | BlankEnum
  education_details?: string
  /** @format date */
  date_of_birth: string
  birthplace?: string
  age: string
  /**
   * * `male` - Male
   * * `female` - Female
   */
  gender: ForGenderEnum
  is_at_home?: boolean
  has_allergy: boolean
  has_chronic_condition: boolean
  accommodation: StudentAccommodation
  category: string
  /**
   * Active
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active: boolean
  saa_student?: boolean
  is_statemented?: boolean
  /**
   * @min -2147483648
   * @max 2147483647
   */
  exam_extra_time?: ExamExtraTimeEnum | NullEnum | null
  access_arrangements?: AccessArrangementsEnum[]
  is_draft?: boolean
  has_pending_draft_medications: boolean
  contacts: StudentConnection[]
  external_id_in_invoice_system?: string
  meal_preference: StudentMealPreference
  blood_type: BloodTypeEnum | BlankEnum | null
  /**
   * @min 0
   * @max 2147483647
   */
  saldo_status?: SaldoStatusEnum
  has_any_dietary_restrictions?: boolean
  dietary_restrictions?: string
  is_more_medical_information_available: boolean | null
  more_medical_information: string | null
  is_student_swimmer: boolean | null
  has_student_contact_with_drugs: boolean | null
  student_fitness_for_sports: StudentFitnessForSportsEnum | BlankEnum | null
  summer_legacies: StudentLegacy[]
  /** @format date */
  date_of_entry?: string | null
  entry_quarter: string
  /** @format date */
  date_of_depart?: string | null
  /** @format date */
  diploma_date?: string | null
  diploma_type?: DiplomaTypeEnum | BlankEnum | NullEnum | null
  year_of_rosenberg_entry?: number | null
  is_resident_entrepreneur?: boolean | null
  /** This field is needed for the summer camps instance */
  ever_expelled_from_school?: boolean
  week_of_camps: string[]
  /**
   * @min 0
   * @max 32767
   */
  academic_status?: AcademicStatusEnum | NullEnum | null
  member_of_alumni_association?: boolean
  collabs_with_alumni_app?: boolean
  zemis_no?: string
  /** @format date */
  residence_permit_valid_thru?: string | null
  /** @format date */
  passport_valid_thru?: string | null
  /** @format uri */
  photo: string
  photo_display_name: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentDetailRequest = {
  /** @maxLength 256 */
  nickname?: string
  /**
   * @minLength 1
   * @maxLength 128
   */
  first_name: string
  /**
   * @minLength 1
   * @maxLength 128
   */
  last_name: string
  /** @maxLength 256 */
  name?: string
  /** @maxLength 256 */
  title?: string
  current_grade_level?: number | null
  /**
   * Email address
   * @format email
   * @minLength 1
   * @maxLength 254
   */
  email: string
  billing_additional_email_id?: number | null
  /** @maxLength 128 */
  phone_number?: string
  /** @maxItems 250 */
  nationalities?: (NationalityEnum | BlankEnum)[]
  native_language?: NativeLanguageEnum | BlankEnum
  education_details?: string
  /** @format date */
  date_of_birth: string
  birthplace?: string
  /**
   * * `male` - Male
   * * `female` - Female
   */
  gender: ForGenderEnum
  is_at_home?: boolean
  saa_student?: boolean
  is_statemented?: boolean
  /**
   * @min -2147483648
   * @max 2147483647
   */
  exam_extra_time?: ExamExtraTimeEnum | NullEnum | null
  access_arrangements?: AccessArrangementsEnum[]
  is_draft?: boolean
  external_id_in_invoice_system?: string
  /**
   * @min 0
   * @max 2147483647
   */
  saldo_status?: SaldoStatusEnum
  has_any_dietary_restrictions?: boolean
  dietary_restrictions?: string
  /** @format date */
  date_of_entry?: string | null
  /** @format date */
  date_of_depart?: string | null
  /** @format date */
  diploma_date?: string | null
  diploma_type?: DiplomaTypeEnum | BlankEnum | NullEnum | null
  year_of_rosenberg_entry?: number | null
  is_resident_entrepreneur?: boolean | null
  /** This field is needed for the summer camps instance */
  ever_expelled_from_school?: boolean
  /**
   * @min 0
   * @max 32767
   */
  academic_status?: AcademicStatusEnum | NullEnum | null
  member_of_alumni_association?: boolean
  collabs_with_alumni_app?: boolean
  zemis_no?: string
  /** @format date */
  residence_permit_valid_thru?: string | null
  /** @format date */
  passport_valid_thru?: string | null
}

export type StudentDietaryRestrictions = {
  has_any_dietary_restrictions?: boolean
  dietary_restrictions?: string
}

export type StudentDietaryRestrictionsRequest = {
  has_any_dietary_restrictions?: boolean
  dietary_restrictions?: string
}

export type StudentDocument = {
  id: number
  /** @format uuid */
  student: string
  /** @format uri */
  file: string | null
  content_type: string | null
  /** @format uri */
  preview_url: string | null
  computed_file_name: string
  /** @format uri */
  source_url: string | null
  is_trello_attachment: boolean
  is_imported_from_bsi: boolean
  /**
   * @min 0
   * @max 32767
   */
  document_type: DocumentTypeEnum
  /** @maxLength 256 */
  document_name: string
  document_name_str: string
  access_profiles_override?: string[] | null
  effective_access_profiles: StudentDocumentAccessProfile[]
  is_access_overridden: boolean
  has_access: boolean
  order: number
  added_by_str: string | null
  last_modified_by_str: string | null
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
}

export type StudentDocumentAccessProfile = {
  value: string
  label: string
}

export type StudentDocumentAvailableFilters = {
  document_type: AvailableIntegerFilter
  added_by: AvailableStringFilter
  school_year: AvailableIntegerFilter
}

export type StudentDocumentRequest = {
  /** @format uuid */
  student: string
  /** @format binary */
  file: File
  /**
   * @min 0
   * @max 32767
   */
  document_type: DocumentTypeEnum
  /**
   * @minLength 1
   * @maxLength 256
   */
  document_name: string
  access_profiles_override?: string[] | null
  added_by?: string
  last_modified_by?: string
}

export type StudentDocumentTypeAccess = {
  value: number
  label: string
  can_select: boolean
  default_access_profiles: StudentDocumentAccessProfile[]
}

export type StudentDocumentTypeAccessMetadata = {
  document_types: StudentDocumentTypeAccess[]
  profile_options: StudentDocumentAccessProfile[]
}

export type StudentEnrollOnCoursesSuccess = {
  courses_capacity_or_max_sections_increased: CoursesCapacityOrMaxSectionsIncreased[]
}

export type StudentEnrolmentChoiceUpdateRequest = {
  id: number
  /**
   * * `FIRST_CHOICE` - First choice
   * * `SECOND_CHOICE` - Second choice
   * * `EXTRA_CHOICES` - Extra choices
   * * `ADMIN_CHOICE` - Admin choice
   */
  choice_type: TAEManageSignupChoicesChoiceTypeEnum
  /**
   * @min 0
   * @max 32767
   */
  number_of_lessons?: number
}

export type StudentEnrolmentChoiceWrite = {
  id: number
  /**
   * * `FIRST_CHOICE` - FIRST_CHOICE
   * * `SECOND_CHOICE` - SECOND_CHOICE
   * * `EXTRA_CHOICES` - EXTRA_CHOICES
   */
  choice_type: StudentEnrolmentChoiceWriteChoiceTypeEnum
  /**
   * @min 0
   * @max 32767
   * @default 1
   */
  number_of_lessons?: number
}

/**
 * * `FIRST_CHOICE` - FIRST_CHOICE
 * * `SECOND_CHOICE` - SECOND_CHOICE
 * * `EXTRA_CHOICES` - EXTRA_CHOICES
 */
export type StudentEnrolmentChoiceWriteChoiceTypeEnum =
  | 'FIRST_CHOICE'
  | 'SECOND_CHOICE'
  | 'EXTRA_CHOICES'

export type StudentEnrolmentChoiceWriteRequest = {
  id: number
  /**
   * * `FIRST_CHOICE` - FIRST_CHOICE
   * * `SECOND_CHOICE` - SECOND_CHOICE
   * * `EXTRA_CHOICES` - EXTRA_CHOICES
   */
  choice_type: StudentEnrolmentChoiceWriteChoiceTypeEnum
  /**
   * @min 0
   * @max 32767
   * @default 1
   */
  number_of_lessons?: number
}

export type StudentEnrolmentCreate = {
  sports?: StudentEnrolmentChoiceWrite[]
  co_curriculars?: StudentEnrolmentChoiceWrite[]
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_package?: SportMembershipOriginalChoiceEnum
}

export type StudentEnrolmentCreateRequest = {
  sports?: StudentEnrolmentChoiceWriteRequest[]
  co_curriculars?: StudentEnrolmentChoiceWriteRequest[]
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_package?: SportMembershipOriginalChoiceEnum
}

export type StudentEnrolmentUpdateRequest = {
  sports?: StudentEnrolmentChoiceUpdateRequest[]
  co_curriculars?: StudentEnrolmentChoiceUpdateRequest[]
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_package?: SportMembershipOriginalChoiceEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentExamUpdate = {
  external_exams: StudentExternalExam[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentExamUpdateRequest = {
  external_exams: StudentExternalExamUpdateRequest[]
}

export type StudentExternalCredits = {
  /**
   * @format double
   * @min -10000
   * @exclusiveMin true
   * @max 10000
   * @exclusiveMax true
   */
  external_credits: number
  include_external_credits_in_transcript?: boolean
}

export type StudentExternalCreditsRequest = {
  /**
   * @format double
   * @min -10000
   * @exclusiveMin true
   * @max 10000
   * @exclusiveMax true
   */
  external_credits: number
  include_external_credits_in_transcript?: boolean
}

export type StudentExternalExam = {
  id: number
  /**
   * * `ACT` - ACT
   * * `A_LEVEL` - A Level (GCE), iGCSE
   * * `AP` - Advanced Placement
   * * `CAMBRIDGE` - CAMBRIDGE
   * * `DELE` - DELE
   * * `DELF` - DELF
   * * `GCSE` - GCSE
   * * `GMAT` - GMAT
   * * `GOETHE` - GOETHE
   * * `IAL` - International A Level
   * * `IB` - International Baccalaureate
   * * `IELTS` - IELTS
   * * `LAMDA` - LAMDA
   * * `MAP` - MAP
   * * `SAT_ENG` - SAT ENG
   * * `SAT_MAT` - SAT MAT
   * * `WIDA` - WIDA
   */
  type: ExternalExamTypeEnum
  /** @format date */
  date?: string | null
  /**
   * Field allows to set only calendar year instead of full date
   * @maxLength 4
   */
  calendar_year?: string | null
  /** @format double */
  min_score?: number | null
  /** @format double */
  score?: number | null
  /** @maxLength 16 */
  subtype?: string
  name: string
  certificate: Attachment
  /**
   * The external course name provided by user
   * @maxLength 255
   */
  external_course_name?: string | null
}

export type StudentExternalExamRequest = {
  /**
   * * `ACT` - ACT
   * * `A_LEVEL` - A Level (GCE), iGCSE
   * * `AP` - Advanced Placement
   * * `CAMBRIDGE` - CAMBRIDGE
   * * `DELE` - DELE
   * * `DELF` - DELF
   * * `GCSE` - GCSE
   * * `GMAT` - GMAT
   * * `GOETHE` - GOETHE
   * * `IAL` - International A Level
   * * `IB` - International Baccalaureate
   * * `IELTS` - IELTS
   * * `LAMDA` - LAMDA
   * * `MAP` - MAP
   * * `SAT_ENG` - SAT ENG
   * * `SAT_MAT` - SAT MAT
   * * `WIDA` - WIDA
   */
  type: ExternalExamTypeEnum
  /** @format date */
  date?: string | null
  /**
   * Field allows to set only calendar year instead of full date
   * @maxLength 4
   */
  calendar_year?: string | null
  /** @format double */
  min_score?: number | null
  /** @format double */
  score?: number | null
  /** @maxLength 16 */
  subtype?: string
  /**
   * The external course name provided by user
   * @maxLength 255
   */
  external_course_name?: string | null
}

export type StudentExternalExamUpdateRequest = {
  id?: number
  /**
   * * `ACT` - ACT
   * * `A_LEVEL` - A Level (GCE), iGCSE
   * * `AP` - Advanced Placement
   * * `CAMBRIDGE` - CAMBRIDGE
   * * `DELE` - DELE
   * * `DELF` - DELF
   * * `GCSE` - GCSE
   * * `GMAT` - GMAT
   * * `GOETHE` - GOETHE
   * * `IAL` - International A Level
   * * `IB` - International Baccalaureate
   * * `IELTS` - IELTS
   * * `LAMDA` - LAMDA
   * * `MAP` - MAP
   * * `SAT_ENG` - SAT ENG
   * * `SAT_MAT` - SAT MAT
   * * `WIDA` - WIDA
   */
  type: ExternalExamTypeEnum
  /** @format date */
  date?: string | null
  /**
   * Field allows to set only calendar year instead of full date
   * @maxLength 4
   */
  calendar_year?: string | null
  /** @format double */
  min_score?: number | null
  /** @format double */
  score?: number | null
  /** @maxLength 16 */
  subtype?: string
  /**
   * The external course name provided by user
   * @maxLength 255
   */
  external_course_name?: string | null
}

/**
 * * `moderate` - Moderate
 * * `good` - Good
 * * `very_good` - Very good
 * * `` - n/a
 */
export type StudentFitnessForSportsEnum = 'moderate' | 'good' | 'very_good'

export type StudentForExam = {
  /** @format uuid */
  student: string
  student_str: string
  grade_level: number
  saa_student?: boolean
  is_statemented?: boolean
  access_arrangements: string
  /**
   * @min -2147483648
   * @max 2147483647
   */
  exam_extra_time?: ExamExtraTimeEnum | NullEnum | null
  course: string
}

export type StudentFullName = {
  /** @format uuid */
  id: string
  full_name: string
}

export type StudentGrade = {
  period_short_name: string
  grade: string
  weighted_percents: number | null
  target_grade: string | null
  target_percents: number | null
  /**
   * * `pending` - Pending
   * * `much_above` - Much Above
   * * `above` - Above
   * * `below` - Below
   * * `much_below` - Much Below
   */
  target_grade_status?: TargetGradeStatusEnum
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type StudentHomework = {
  /** @format uuid */
  id: string
  grade_in_percent: number | null
  grade_in_ib: string
  is_submitted: boolean
  is_incomplete: boolean
  is_late: boolean
  is_exempt: boolean
  assignment_name: string
  assignment_description: string
  /** @format date */
  assignment_due_date: string
  assignment_type_name: string
  /** @format uuid */
  course_id: string
  course_name: string
  student_saw_changes: boolean
  status: string
  notifications_mark_as_read?: number | null
  is_homework: boolean
  quarter_name: string
  /** @format date-time */
  created: string
}

export type StudentHomeworkCurrentlyLate = {
  currently_late: number
}

export type StudentHomeworkNewlyReviewed = {
  newly_reviewed: number
}

export type StudentHomeworkToDo = {
  to_do: number
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentIdpPlan = {
  id: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  baskets?: Basket[]
  /** @format uuid */
  student: string
  status: string
  status_display: string
  note?: string
  grade_level: number
  aim: string
  year: number
  draft_payload?: any
  exams?: ExamScore[]
  warnings?: ParentWarning[]
  /**
   * @min 0
   * @max 2147483647
   */
  version: number
  student_str: string
  reject_details: any
  /** @format double */
  credit_count?: number | null
  /** @format uuid */
  admin_approve_author: string | null
  admin_approve_author_str: string
  /** @format date-time */
  admin_approved_at: string | null
  /** @format date */
  parent_approval_deadline: string | null
  parent_message?: string
  parent_message_author_str: string
  /** @format date-time */
  parent_message_created_at: string | null
  parent_approve_comment: string
  /** @format uuid */
  parent_approve_author: string | null
  parent_approve_author_str: string
  /** @format date-time */
  parent_approved_at: string | null
  send_to_parents_as_update: boolean
}

export type StudentIdpPlanAvailableFilters = {
  aim: AvailableIntegerFilter
  grade_level: AvailableIntegerFilter
  year: AvailableIntegerFilter
  status: AvailableStringFilter
  student: AvailableStringFilter
}

export type StudentIdpPlanList = {
  id: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  student: IdpPlanStudent
  status: string
  status_display: string
  note?: string
  grade_level: number
  aim: string
  year: number
  /**
   * @min 0
   * @max 2147483647
   */
  version: number
  /** Last approved version */
  is_active?: boolean
  student_str: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentIdpPlanRequest = {
  baskets?: BasketRequest[]
  /** @format uuid */
  student: string
  note?: string
  grade_level: number
  /** @minLength 1 */
  aim: string
  year: number
  draft_payload?: any
  exams?: ExamScoreRequest[]
  warnings?: ParentWarningRequest[]
  /** @default true */
  commit?: boolean
  /** @format double */
  credit_count?: number | null
  courses_mission_control_departments?: CourseMissionControlDepartmentRequest[]
  parent_message?: string
}

export type StudentIdpViewGrades = {
  student: SimpleUser
  aim: SimpleAim
  artisan: SimpleUser | null
  grades: IdpViewGrades
  is_currently_enrolled: boolean
}

export type StudentIdsListRequest = {
  students: string[]
}

export type StudentInAttendance = {
  /** @format uuid */
  id: string
  full_name: string
  is_at_home?: boolean
}

export type StudentInAttendanceRequest = {
  is_at_home?: boolean
}

export type StudentInternship = {
  id: number
  internship_str: string
  internship: number
  document?: number | null
  document_data: InternshipAttachment | null
  /** @maxLength 256 */
  organisation: string
  /** @format date */
  date: string
  /** @format date */
  date_to: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentInternshipUpdate = {
  id?: number
  internship: number
  document?: number | null
  /** @maxLength 256 */
  organisation: string
  /** @format date */
  date: string
  /** @format date */
  date_to: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentInternshipUpdateRequest = {
  id?: number
  internship: number
  document?: number | null
  /**
   * @minLength 1
   * @maxLength 256
   */
  organisation: string
  /** @format date */
  date: string
  /** @format date */
  date_to: string
}

/** Validate ownership before nested replacement deletes existing records. */
export type StudentInternshipsUpdate = {
  internships: StudentInternshipUpdate[]
}

/** Validate ownership before nested replacement deletes existing records. */
export type StudentInternshipsUpdateRequest = {
  internships: StudentInternshipUpdateRequest[]
}

export type StudentLaundryOrder = {
  id: number
  qr_code: string | null
  status: StudentLaundryOrderStatusEnum
  laundry_type: LaundryTypeEnum
  /** @format date */
  delivery_date: string | null
  house_name: string | null
  room_name: string | null
  /** @deprecated */
  is_claim_submitted: boolean
  claim_submitted_by: LaundryOrderClaimSubmittedByEnum | NullEnum | null
  can_submit_claim: boolean
  order_items: StudentLaundryOrderItem[]
}

export type StudentLaundryOrderClaimCreate = {
  /** @maxLength 500 */
  claim_details: string
  claimed_items: StudentLaundryOrderClaimedItem[]
}

export type StudentLaundryOrderClaimCreateRequest = {
  /**
   * @minLength 1
   * @maxLength 500
   */
  claim_details: string
  claimed_items: StudentLaundryOrderClaimedItemRequest[]
}

export type StudentLaundryOrderClaimedItem = {
  /** @min 1 */
  catalog_item_id?: number | null
  /** @min 1 */
  version_item_id?: number | null
  /** @min 1 */
  quantity: number
}

export type StudentLaundryOrderClaimedItemRequest = {
  /** @min 1 */
  catalog_item_id?: number | null
  /** @min 1 */
  version_item_id?: number | null
  /** @min 1 */
  quantity: number
}

export type StudentLaundryOrderCreateRequest = {
  order_items: LaundryOrderVersionItemCreateRequest[]
  qr_code_id: number
  /**
   * * `normal` - Normal
   * * `delicate` - Delicate
   * * `dry` - Dry
   */
  laundry_type: LaundryTypeEnum
}

export type StudentLaundryOrderDetail = {
  id: number
  qr_code: string | null
  status: StudentLaundryOrderStatusEnum
  laundry_type: LaundryTypeEnum
  /** @format date */
  delivery_date: string | null
  house_name: string | null
  room_name: string | null
  /** @deprecated */
  is_claim_submitted: boolean
  claim_submitted_by: LaundryOrderClaimSubmittedByEnum | NullEnum | null
  can_submit_claim: boolean
  order_items: StudentLaundryOrderItem[]
  can_edit_laundry: boolean
  /** @format date-time */
  pickup_date: string
  /** @maxLength 500 */
  rejection_reason?: string
  claim_details: string | null
  hotel_services_note: string | null
  /**
   * @format double
   * @min -100000000
   * @exclusiveMin true
   * @max 100000000
   * @exclusiveMax true
   */
  total_price: number
  number_of_items: number
  /** @format double */
  old_total_price: number | null
  old_number_of_items: number | null
}

export type StudentLaundryOrderItem = {
  id: number
  catalog_item_id: number | null
  version_item_id: number | null
  name: string
  /**
   * @format double
   * @min -10000
   * @exclusiveMin true
   * @max 10000
   * @exclusiveMax true
   */
  unit_price: number
  quantity: number
  old_quantity: number | null
  is_new: boolean | null
  is_removed: boolean | null
  is_edited: boolean | null
  is_claimed: boolean
  claimed_quantity: number | null
}

export type StudentLaundryOrderList = {
  id: number
  /**
   * * `pickup` - Pickup
   * * `in_laundry` - In laundry
   * * `completed` - Completed
   * * `rejected` - Rejected
   * * `claim` - Claim
   * * `closed` - Closed
   * * `claim_closed` - Claim closed
   */
  status?: StudentLaundryOrderStatusEnum
  /** @format date-time */
  created: string
  /** @format date */
  delivery_date: string | null
  /** @format date-time */
  closed_at?: string | null
  number_of_items: number
  /**
   * * `normal` - Normal
   * * `delicate` - Delicate
   * * `dry` - Dry
   */
  laundry_type?: LaundryTypeEnum
  is_laundry_edited: boolean
  /** @deprecated */
  is_claim_submitted: boolean
  claim_submitted_by: LaundryOrderClaimSubmittedByEnum | NullEnum | null
  can_submit_claim: boolean
  /** @format double */
  total_price: number
}

export type StudentLaundryOrderQRCodeLookup = {
  /**
   * * `order_found` - order_found
   * * `not_your_order` - not_your_order
   * * `no_order_assigned` - no_order_assigned
   * * `wrong_house_or_room` - wrong_house_or_room
   */
  result_type: ResultTypeEnum
  order_id: number | null
  qr_code_id: number | null
}

/**
 * * `pickup` - Pickup
 * * `in_laundry` - In laundry
 * * `completed` - Completed
 * * `rejected` - Rejected
 * * `claim` - Claim
 * * `closed` - Closed
 * * `claim_closed` - Claim closed
 */
export type StudentLaundryOrderStatusEnum =
  | 'pickup'
  | 'in_laundry'
  | 'completed'
  | 'rejected'
  | 'claim'
  | 'closed'
  | 'claim_closed'

export type StudentLaundryOrderVersionItemUpdateRequest = {
  id?: number | null
  catalog_item_id: number | null
  /**
   * @minLength 1
   * @maxLength 128
   */
  name?: string
  /**
   * @min 1
   * @max 2147483647
   */
  quantity: number
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentLaundryOrderVersionUpdateRequest = {
  /**
   * * `normal` - Normal
   * * `delicate` - Delicate
   * * `dry` - Dry
   */
  laundry_type?: LaundryTypeEnum
  order_items: StudentLaundryOrderVersionItemUpdateRequest[]
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type StudentLeaveCheck = {
  /** @format uuid */
  id: string
  /** @maxLength 1024 */
  destination: string
  /** @maxLength 256 */
  train?: string
  /** @maxLength 256 */
  plane?: string
  /** @maxLength 256 */
  pick_up_location?: string
  /** @format time */
  pick_up_time?: string | null
  comment?: string
  /** @maxLength 64 */
  contact_person: string
  /** @maxLength 128 */
  contact_person_phone: string
  is_driver_needed?: boolean
  /** @format date-time */
  date: string
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type StudentLeaveCheckRequest = {
  /**
   * @minLength 1
   * @maxLength 1024
   */
  destination: string
  /** @maxLength 256 */
  train?: string
  /** @maxLength 256 */
  plane?: string
  /** @maxLength 256 */
  pick_up_location?: string
  /** @format time */
  pick_up_time?: string | null
  comment?: string
  /**
   * @minLength 1
   * @maxLength 64
   */
  contact_person: string
  /**
   * @minLength 1
   * @maxLength 128
   */
  contact_person_phone: string
  is_driver_needed?: boolean
  /** @format date-time */
  date: string
  /** @format date-time */
  original_date?: string | null
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type StudentLeaveRequest = {
  /** @format uuid */
  id: string
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  check_out: StudentLeaveCheck
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  check_in: StudentLeaveCheck
  /**
   * @min -2147483648
   * @max 2147483647
   */
  leave_type: LeaveRequestTypeEnum
  leave_type_label: string
  request_type: RequestTypeEnum
  status: LeaveRequestStatusEnum
  status_label: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /** @format date-time */
  order_date: string
  can_edit: boolean
  can_cancel: boolean
  can_delete: boolean
  object_type: ObjectTypeEnum
  student_message?: string
  student_saw_changes: boolean
  notifications_mark_as_read: number | null
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type StudentLeaveRequestRequest = {
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  check_out: StudentLeaveCheckRequest
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  check_in: StudentLeaveCheckRequest
  /**
   * @min -2147483648
   * @max 2147483647
   */
  leave_type: LeaveRequestTypeEnum
  student_message?: string
}

export type StudentLeaveRequestStatus = {
  label: string
  /**
   * * `tentative` - Pending guardian
   * * `confirmed_contact_person` - Pending academic
   * * `confirmed_academic` - Pending boarding
   * * `changed` - Changed
   * * `confirmed` - Approved
   * * `declined` - Rejected
   * * `declined_contact_person` - Rejected by guardian
   * * `declined_academic` - Rejected by academic
   * * `canceled` - Canceled by student
   * * `expired` - Expired
   */
  value: LeaveRequestStatusEnum
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type StudentLeaveRequestSwagger = {
  /** @format uuid */
  id: string
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  check_out?: StudentLeaveCheck
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  check_in?: StudentLeaveCheck
  /**
   * @min -2147483648
   * @max 2147483647
   */
  leave_type: LeaveRequestTypeEnum
  leave_type_label: string
  request_type: RequestTypeEnum
  status: LeaveRequestStatusEnum
  status_label: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /** @format date-time */
  order_date: string
  can_edit: boolean
  can_cancel: boolean
  can_delete: boolean
  object_type: ObjectTypeEnum
  student_message?: string
  student_saw_changes: boolean
  notifications_mark_as_read: number | null
}

export type StudentLeaveRequestType = {
  label: string
  /**
   * * `0` - Weekend
   * * `1` - Holiday
   * * `2` - Other
   * * `3` - Arrival
   */
  value: LeaveRequestTypeEnum
}

export type StudentLeavesSummarySwagger = {
  /** @format uuid */
  id: string
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  check_in?: StudentLeaveCheck
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  check_out?: StudentLeaveCheck
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /** @format date-time */
  order_date: string
  can_edit: boolean
  leave_type: LeaveRequestTypeEnum
  /**
   * * `manual` - Manual
   * * `from idp` - From IDP
   * * `by_student` - Requested by student
   */
  request_type: RequestTypeEnum
  /**
   * * `tentative` - Pending guardian
   * * `confirmed_contact_person` - Pending academic
   * * `confirmed_academic` - Pending boarding
   * * `changed` - Changed
   * * `confirmed` - Approved
   * * `declined` - Rejected
   * * `declined_contact_person` - Rejected by guardian
   * * `declined_academic` - Rejected by academic
   * * `canceled` - Canceled by student
   * * `expired` - Expired
   * * `requested` - Pending
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `canceled_by_student` - Canceled by student
   * * `done` - Done
   * * `cancelled` - Cancelled
   */
  status: StudentLeavesSummarySwaggerStatusEnum
  /** @format uuid */
  created_by: string
  campus_absence_students?: StudentCampusAbsenceStudent[]
  /**
   * * `afternoon_ausgang` - Afternoon Ausgang
   * * `evening_ausgang` - Evening Ausgang
   * * `evening_campus_ausgang` - Evening - Campus Ausgang
   * * `birthday_ausgang` - Birthday Ausgang
   * * `family_visit` - Family visit
   * * `on_board` - On board Ausgang
   * * `prolonged_ausgang` - Prolonged Ausgang
   * * `campus` - Campus
   * * `offsite` - Offsite
   * * `offsite_overnight` - Offsite, Overnight
   * * `official_appointment` - Official appointment
   * * `specialist_doctor_appointment` - Specialist Doctor Appointment
   * * `programme` - Programme
   * * `house_evening` - House evening
   * * `term_break` - Term Break
   * * `school_trip` - School Trip
   * * `weekend_programme` - Weekend Programme
   * * `other` - Other
   * * `book_the_cafe` - Book the Cafe
   * * `city_brunch_zurich` - City Brunch Zurich
   * * `additional_ausgang` - Additional Ausgang
   * * `on_campus_ausgang` - On Campus Ausgang
   */
  campus_absence_subtype: CampusAbsenceSubTypeEnum
  campus_absence_subtype_label: string
  leave_details?: string
  contact_person_name?: string
  contact_person_phone?: string
  /** @format date-time */
  date_from: string
  /** @format date-time */
  date_to: string
  object_type: ObjectTypeEnum
  leave_type_label: string
  status_label: string
  student_saw_changes: boolean
  custom_name?: string
}

export type StudentLeavesSummarySwaggerList = {
  results: StudentLeavesSummarySwagger[]
}

/**
 * * `tentative` - Pending guardian
 * * `confirmed_contact_person` - Pending academic
 * * `confirmed_academic` - Pending boarding
 * * `changed` - Changed
 * * `confirmed` - Approved
 * * `declined` - Rejected
 * * `declined_contact_person` - Rejected by guardian
 * * `declined_academic` - Rejected by academic
 * * `canceled` - Canceled by student
 * * `expired` - Expired
 * * `requested` - Pending
 * * `approved` - Approved
 * * `rejected` - Rejected
 * * `canceled_by_student` - Canceled by student
 * * `done` - Done
 * * `cancelled` - Cancelled
 */
export type StudentLeavesSummarySwaggerStatusEnum =
  | 'tentative'
  | 'confirmed_contact_person'
  | 'confirmed_academic'
  | 'changed'
  | 'confirmed'
  | 'declined'
  | 'declined_contact_person'
  | 'declined_academic'
  | 'canceled'
  | 'expired'
  | 'requested'
  | 'approved'
  | 'rejected'
  | 'canceled_by_student'
  | 'done'
  | 'cancelled'

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentLegaciesWithSummerCamps = {
  student_legacies: StudentLegacy[]
  summer_camp?: SummerCampLegacy
  /** Used only for summer camps instances. */
  year_of_rosenberg_entry?: number | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentLegaciesWithSummerCampsRequest = {
  student_legacies: StudentLegacyRequest[]
  summer_camp?: SummerCampLegacyRequest
  /** Used only for summer camps instances. */
  year_of_rosenberg_entry?: number | null
}

export type StudentLegacy = {
  id: number
  /** @format uuid */
  student: string
  /**
   * * `family_member_attended` - A family member attended Rosenberg.
   * * `friend_attended` - My friends attended Rosenberg and recommended me.
   * * `family_friend_told_me` - A family friend told me about Rosenberg.
   * * `educational_consultant` - Educational consultant.
   * * `other` - Other
   */
  legacy_type: LegacyTypeEnum
  legacy_type_str: string
  first_name?: string
  last_name?: string
  other_comment?: string
  /**
   * @min 0
   * @max 2147483647
   */
  order?: number
}

export type StudentLegacyRequest = {
  /**
   * * `family_member_attended` - A family member attended Rosenberg.
   * * `friend_attended` - My friends attended Rosenberg and recommended me.
   * * `family_friend_told_me` - A family friend told me about Rosenberg.
   * * `educational_consultant` - Educational consultant.
   * * `other` - Other
   */
  legacy_type: LegacyTypeEnum
  first_name?: string
  last_name?: string
  other_comment?: string
  /**
   * @min 0
   * @max 2147483647
   */
  order?: number
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type StudentList = {
  /** @format uuid */
  id: string
  /** Required. 150 characters or fewer. Letters, digits and @/./+/-/_ only. */
  username: string
  /** @maxLength 256 */
  nickname?: string
  /** @maxLength 128 */
  first_name: string
  /** @maxLength 128 */
  last_name: string
  first_and_last_name: string
  full_name: string
  /** @maxLength 256 */
  name?: string
  current_grade_level: number | null
  current_grade_level_str: string | null
  /**
   * Email address
   * @format email
   * @maxLength 254
   */
  email: string
  /** @maxLength 128 */
  phone_number?: string
  /** @maxItems 250 */
  nationalities: (NationalityEnum | BlankEnum)[]
  native_language?: NativeLanguageEnum | BlankEnum
  education_details?: string
  /** @format date */
  date_of_birth?: string | null
  two_factor_auth?: boolean
  gender?: ForGenderEnum | BlankEnum
  is_at_home?: boolean
  has_allergy: boolean
  has_chronic_condition: boolean
  is_swimmer: boolean | null
  is_athlete: boolean | null
  is_resident_entrepreneur?: boolean | null
  homework_status: string
  accommodation: StudentAccommodation
  category: string
  week_ago_category: string
  /**
   * Active
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  disable_access_to_idp?: boolean
  saa_student?: boolean
  is_statemented?: boolean
  /**
   * @min -2147483648
   * @max 2147483647
   */
  exam_extra_time?: ExamExtraTimeEnum | NullEnum | null
  access_arrangements?: AccessArrangementsEnum[]
  is_draft?: boolean
  external_id_in_invoice_system?: string
  searchable_content?: string
  /** @format date-time */
  report_card_sent_at: string | null
  /**
   * @min 0
   * @max 2147483647
   */
  saldo_status?: SaldoStatusEnum
  boarding_comments: BoardingCommentSimple
  age: string
  number_of_weeks_in_camps: number | null
  week_of_camps: string[]
  summer_certificate_rules_met: boolean
  is_rosenberg_student: boolean
  year_of_rosenberg_entry: number
  panel_student: PanelStudentEnum
  package_option: SummerPackageTypeEnum | NullEnum | null
  is_new: boolean
  /** @format date */
  date_of_entry?: string | null
  entry_quarter: string
  is_entry_quarter_current_or_upcoming: boolean
  /** @format date-time */
  date_joined?: string
  /**
   * @min 0
   * @max 32767
   */
  academic_status?: AcademicStatusEnum | NullEnum | null
  /** @format date */
  diploma_date?: string | null
  diploma_type?: DiplomaTypeEnum | BlankEnum | NullEnum | null
}

export type StudentMakeAnImpact = {
  id: string
  status: MeritRewardStatusEnum
  /** @format date-time */
  created: string
  decision_comment: string
  notifications_mark_as_read: number | null
  student_saw_changes: boolean
  /**
   * @min 1
   * @max 2147483647
   */
  points_donated: number
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type StudentMakeAnImpactRequest = {
  student?: string
  /**
   * @min 1
   * @max 2147483647
   */
  points_donated: number
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type StudentMealAttendanceFormStatus = {
  /**
   * * `closed` - closed
   * * `open` - open
   */
  status: StudentMealAttendanceFormStatusStatusEnum
  /** @format date-time */
  deadline?: string
  available_meals?: StudentMealSkip[]
  /**
   * * `date` - date
   * * `category` - category
   * * `day_student` - day_student
   * * `no_available_meals` - no_available_meals
   * * `already_sent` - already_sent
   * * `disabled` - disabled
   */
  closed_reason?: ClosedReasonEnum
}

/**
 * * `closed` - closed
 * * `open` - open
 */
export type StudentMealAttendanceFormStatusStatusEnum = 'closed' | 'open'

export type StudentMealPreference = {
  /**
   * * `l1` - L1, Regular and vegetarian. Students can choose themselves.
   * * `l2` - L2, Specific, individual requirements. Kitchen adapts the existing menu to specific needs.
   * * `l3` - L3, Specific, very individual requirements. Kitchen creates new menu to specific needs, almost every meal has to be cooked individually.
   * * `l4` - L4, Highly specific. Severe allergic reaction to traces of certain food. Meals need to be ordered from outside supplier.
   */
  diet_type?: DietTypeEnum
  other_requirements?: string
}

export type StudentMealPreferenceHistory = {
  id: number
  /** @format date-time */
  created: string
  /**
   * * `l1` - L1, Regular and vegetarian. Students can choose themselves.
   * * `l2` - L2, Specific, individual requirements. Kitchen adapts the existing menu to specific needs.
   * * `l3` - L3, Specific, very individual requirements. Kitchen creates new menu to specific needs, almost every meal has to be cooked individually.
   * * `l4` - L4, Highly specific. Severe allergic reaction to traces of certain food. Meals need to be ordered from outside supplier.
   */
  diet_type?: DietTypeEnum
  diet_type_display: string
  other_requirements?: string
}

export type StudentMealPreferenceRequest = {
  /** @format uuid */
  student: string
  /**
   * * `l1` - L1, Regular and vegetarian. Students can choose themselves.
   * * `l2` - L2, Specific, individual requirements. Kitchen adapts the existing menu to specific needs.
   * * `l3` - L3, Specific, very individual requirements. Kitchen creates new menu to specific needs, almost every meal has to be cooked individually.
   * * `l4` - L4, Highly specific. Severe allergic reaction to traces of certain food. Meals need to be ordered from outside supplier.
   */
  diet_type?: DietTypeEnum
  other_requirements?: string
}

export type StudentMealSkip = {
  id: number
  /** @format date-time */
  date: string
  /**
   * * `breakfast` - Brunch
   * * `dinner` - Dinner
   */
  meal: MealTypeEnum
  meal_label: MealLabelEnum
}

export type StudentMealSkipCreateRequest = {
  meals_to_skip: StudentMealSkipRequest[]
}

export type StudentMealSkipRequest = {
  /** @format date-time */
  date: string
  /**
   * * `breakfast` - Brunch
   * * `dinner` - Dinner
   */
  meal: MealTypeEnum
}

export type StudentMedicalInfo = {
  /** @format double */
  height?: number | null
  /** @format double */
  weight?: number | null
  blood_type?: BloodTypeEnum | BlankEnum
  is_student_swimmer?: boolean | null
  is_student_athlete?: boolean | null
  student_fitness_for_sports?: StudentFitnessForSportsEnum | BlankEnum
  insurance_type?: InsuranceTypeEnum | BlankEnum
  /** @maxLength 64 */
  insurance_number?: string
  /** @maxLength 64 */
  insurance_client_number?: string
  /** @maxLength 32 */
  ahv_number?: string
  has_student_contact_with_drugs?: boolean
  is_more_medical_information_available?: boolean
  more_medical_information?: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
}

export type StudentMedicalInfoRequest = {
  /** @format double */
  height?: number | null
  /** @format double */
  weight?: number | null
  blood_type?: BloodTypeEnum | BlankEnum
  is_student_swimmer?: boolean | null
  is_student_athlete?: boolean | null
  student_fitness_for_sports?: StudentFitnessForSportsEnum | BlankEnum
  insurance_type?: InsuranceTypeEnum | BlankEnum
  /** @maxLength 64 */
  insurance_number?: string
  /** @maxLength 64 */
  insurance_client_number?: string
  /** @maxLength 32 */
  ahv_number?: string
  has_student_contact_with_drugs?: boolean
  is_more_medical_information_available?: boolean
  more_medical_information?: string
}

export type StudentMeritCount = {
  merits_count: number
  demerits_count: number
}

export type StudentMeritItemDetails = {
  id: number
  name: string
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type StudentMeritList = {
  id: string
  /** @format double */
  value: number
  merit_type: MeritTypeEnum
  /** @format date-time */
  created: string
  /** @format date-time */
  reviewed_at: string | null
  escalated_merit_item_str: string
  escalated_sanction_str: string
  escalated_sanction: NextLastSanctionEnum
  merit_item_details: StudentMeritItemDetails
  student_saw_changes: boolean
  status: string
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type StudentMeritRetrieve = {
  id: string
  /** @format double */
  value: number
  merit_type: MeritTypeEnum
  /** @format date-time */
  created: string
  escalated_merit_item_str: string
  escalated_sanction_str: string
  escalated_sanction: NextLastSanctionEnum
  merit_item_details: StudentMeritItemDetails
  student_saw_changes: boolean
  status: string
  course_str: string
  note: string
  author_str: string
  next_sanction_str: string
  escalation_expire_after_str: string
  notifications_mark_as_read?: number | null
  /** @format date */
  suspension_date_from: string | null
  /** @format date */
  suspension_date_to: string | null
  sanction_message: string
  /** @format date-time */
  reviewed_at: string | null
}

export type StudentMeritReward =
  | StudentCasualAnyday
  | StudentRoomService
  | StudentBookTheCafe
  | StudentCityBrunchZurich
  | StudentAdditionalAusgang
  | StudentCategoryUpgrade
  | StudentStopTheClock
  | StudentMakeAnImpact
  | StudentOnCampusAusgang

export type StudentMeritRewardParticipant = {
  id: number
  /** @format uuid */
  student: string
  student_data: ParticipantStudent
  status: StudentMeritRewardParticipantStatusEnum
  student_saw_changes: boolean
}

export type StudentMeritRewardParticipantRequest = {
  /** @format uuid */
  student: string
}

/**
 * * `pending` - Pending
 * * `accepted` - Accepted
 * * `rejected` - Rejected
 */
export type StudentMeritRewardParticipantStatusEnum =
  | 'pending'
  | 'accepted'
  | 'rejected'

export type StudentMeritRewardRequest =
  | StudentCasualAnydayRequest
  | StudentRoomServiceRequest
  | StudentBookTheCafeRequest
  | StudentCityBrunchZurichRequest
  | StudentAdditionalAusgangRequest
  | StudentCategoryUpgradeRequest
  | StudentStopTheClockRequest
  | StudentMakeAnImpactRequest
  | StudentOnCampusAusgangRequest

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type StudentMessage = {
  id: number
  content: string
  /** @format date-time */
  created: string
  /**
   * * `alert` - Alert
   * * `notice` - Notice
   */
  type: MessageTypeEnum
  /** via annotate (feed) */
  reaction_count: number
  /** via annotate (feed) */
  reacted_by_user: boolean
  /** @default "message" */
  content_type: ContentTypeF8CEnum
}

export type StudentMove = {
  id: number
  /** @format uuid */
  student: string
  student_name: string
  source_section: number
  source_section_id: number
  source_section_name: string
  source_course_code: string
  source_course_name: string
  target_section: number
  target_section_id: number
  target_section_name: string
  target_course_code: string
  target_course_name: string
  /** @format date-time */
  applied_at?: string | null
  /** @format uuid */
  applied_by?: string | null
  applied_by_name: string
  /** @format date-time */
  reverted_at?: string | null
  /** @format uuid */
  reverted_by?: string | null
  reverted_by_name: string
  can_apply: boolean
  can_revert: boolean
  apply_summary: StudentMoveApplySummary | null
}

export type StudentMoveApplySummary = {
  student_section_assignment_quarters: string[]
}

export type StudentMoveSection = {
  section_id: number
  course_name: string
  course_code: string
  teacher_name: string | null
  students: StudentMoveSectionStudent[]
}

export type StudentMoveSectionStudent = {
  /** @format uuid */
  student_id: string
  student_name: string
  /**
   * * `unchanged` - unchanged
   * * `removed` - removed
   * * `added` - added
   */
  status: StudentMoveSectionStudentStatusEnum
  move_id: number | null
  /** @format date-time */
  applied_at: string | null
  applied_by_name: string | null
  /** @format date-time */
  reverted_at: string | null
  reverted_by_name: string | null
  can_apply: boolean
  can_revert: boolean
}

/**
 * * `unchanged` - unchanged
 * * `removed` - removed
 * * `added` - added
 */
export type StudentMoveSectionStudentStatusEnum =
  | 'unchanged'
  | 'removed'
  | 'added'

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentOnCampusAusgang = {
  id: string
  status: MeritRewardStatusEnum
  /** @format uuid */
  student_id: string
  /** @format date-time */
  created: string
  /** @format date-time */
  date_from: string
  /** @format date-time */
  date_to: string
  leave_details: string
  custom_name: string
  decision_comment: string
  campus_absence_students: StudentMeritRewardParticipant[]
  my_participation: StudentMeritRewardParticipant
  /** @format uuid */
  campus_absence: string | null
  notifications_mark_as_read: number | null
  student_saw_changes: boolean
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentOnCampusAusgangRequest = {
  student?: string
  /** @format date-time */
  date_from: string
  /** @format date-time */
  date_to: string
  /** @minLength 1 */
  leave_details: string
  /** @minLength 1 */
  custom_name: string
  campus_absence_students: StudentMeritRewardParticipantRequest[]
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type StudentPeriodGPASummary = Record<
  string,
  {
    school_year_id: number
    school_year_name: string
    gpas: PeriodGPA[]
  }[]
>

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type StudentPic = {
  id: string
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  image: PublicImage
  /** @maxLength 50 */
  title?: string
  student_str: string
}

export type StudentPotentialGradesView = {
  student: StudentWithGradeLevel
  aim: SimpleAim
  course_aim: SetGradeAim
  artisan: SimpleUser | null
  grades: PotentailGrades
  latest_grades: StudentAssignmentGrade[]
}

export type StudentProfile = {
  /** @format uuid */
  id: string
  first_name: string
  last_name: string
  name: string
  full_name: string
  /**
   * mainly student's app usage
   * -> we're considering only first name given
   */
  reduced_full_name: string
  /**
   * Email address
   * @format email
   */
  email: string
  nationalities: NationalityEnum[]
  /**
   * This field is deprecated and will be removed in a future release.
   *
   * * `AF` - Afghanistan
   * * `AX` - Åland Islands
   * * `AL` - Albania
   * * `DZ` - Algeria
   * * `AS` - American Samoa
   * * `AD` - Andorra
   * * `AO` - Angola
   * * `AI` - Anguilla
   * * `AQ` - Antarctica
   * * `AG` - Antigua and Barbuda
   * * `AR` - Argentina
   * * `AM` - Armenia
   * * `AW` - Aruba
   * * `AU` - Australia
   * * `AT` - Austria
   * * `AZ` - Azerbaijan
   * * `BS` - Bahamas
   * * `BH` - Bahrain
   * * `BD` - Bangladesh
   * * `BB` - Barbados
   * * `BY` - Belarus
   * * `BE` - Belgium
   * * `BZ` - Belize
   * * `BJ` - Benin
   * * `BM` - Bermuda
   * * `BT` - Bhutan
   * * `BO` - Bolivia
   * * `BQ` - Bonaire, Sint Eustatius and Saba
   * * `BA` - Bosnia and Herzegovina
   * * `BW` - Botswana
   * * `BV` - Bouvet Island
   * * `BR` - Brazil
   * * `IO` - British Indian Ocean Territory
   * * `BN` - Brunei
   * * `BG` - Bulgaria
   * * `BF` - Burkina Faso
   * * `BI` - Burundi
   * * `CV` - Cabo Verde
   * * `KH` - Cambodia
   * * `CM` - Cameroon
   * * `CA` - Canada
   * * `KY` - Cayman Islands
   * * `CF` - Central African Republic
   * * `TD` - Chad
   * * `CL` - Chile
   * * `CN` - China
   * * `CX` - Christmas Island
   * * `CC` - Cocos (Keeling) Islands
   * * `CO` - Colombia
   * * `KM` - Comoros
   * * `CG` - Congo
   * * `CD` - Congo (the Democratic Republic of the)
   * * `CK` - Cook Islands
   * * `CR` - Costa Rica
   * * `CI` - Côte d'Ivoire
   * * `HR` - Croatia
   * * `CU` - Cuba
   * * `CW` - Curaçao
   * * `CY` - Cyprus
   * * `CZ` - Czechia
   * * `DK` - Denmark
   * * `DJ` - Djibouti
   * * `DM` - Dominica
   * * `DO` - Dominican Republic
   * * `EC` - Ecuador
   * * `EG` - Egypt
   * * `SV` - El Salvador
   * * `GQ` - Equatorial Guinea
   * * `ER` - Eritrea
   * * `EE` - Estonia
   * * `SZ` - Eswatini
   * * `ET` - Ethiopia
   * * `FK` - Falkland Islands (Malvinas)
   * * `FO` - Faroe Islands
   * * `FJ` - Fiji
   * * `FI` - Finland
   * * `FR` - France
   * * `GF` - French Guiana
   * * `PF` - French Polynesia
   * * `TF` - French Southern Territories
   * * `GA` - Gabon
   * * `GM` - Gambia
   * * `GE` - Georgia
   * * `DE` - Germany
   * * `GH` - Ghana
   * * `GI` - Gibraltar
   * * `GR` - Greece
   * * `GL` - Greenland
   * * `GD` - Grenada
   * * `GP` - Guadeloupe
   * * `GU` - Guam
   * * `GT` - Guatemala
   * * `GG` - Guernsey
   * * `GN` - Guinea
   * * `GW` - Guinea-Bissau
   * * `GY` - Guyana
   * * `HT` - Haiti
   * * `HM` - Heard Island and McDonald Islands
   * * `VA` - Holy See
   * * `HN` - Honduras
   * * `HK` - Hong Kong
   * * `HU` - Hungary
   * * `IS` - Iceland
   * * `IN` - India
   * * `ID` - Indonesia
   * * `IR` - Iran
   * * `IQ` - Iraq
   * * `IE` - Ireland
   * * `IM` - Isle of Man
   * * `IL` - Israel
   * * `IT` - Italy
   * * `JM` - Jamaica
   * * `JP` - Japan
   * * `JE` - Jersey
   * * `JO` - Jordan
   * * `KZ` - Kazakhstan
   * * `KE` - Kenya
   * * `KI` - Kiribati
   * * `XK` - Kosovo
   * * `KW` - Kuwait
   * * `KG` - Kyrgyzstan
   * * `LA` - Laos
   * * `LV` - Latvia
   * * `LB` - Lebanon
   * * `LS` - Lesotho
   * * `LR` - Liberia
   * * `LY` - Libya
   * * `LI` - Liechtenstein
   * * `LT` - Lithuania
   * * `LU` - Luxembourg
   * * `MO` - Macao
   * * `MG` - Madagascar
   * * `MW` - Malawi
   * * `MY` - Malaysia
   * * `MV` - Maldives
   * * `ML` - Mali
   * * `MT` - Malta
   * * `MH` - Marshall Islands
   * * `MQ` - Martinique
   * * `MR` - Mauritania
   * * `MU` - Mauritius
   * * `YT` - Mayotte
   * * `MX` - Mexico
   * * `FM` - Micronesia
   * * `MD` - Moldova
   * * `MC` - Monaco
   * * `MN` - Mongolia
   * * `ME` - Montenegro
   * * `MS` - Montserrat
   * * `MA` - Morocco
   * * `MZ` - Mozambique
   * * `MM` - Myanmar
   * * `NA` - Namibia
   * * `NR` - Nauru
   * * `NP` - Nepal
   * * `NL` - Netherlands
   * * `NC` - New Caledonia
   * * `NZ` - New Zealand
   * * `NI` - Nicaragua
   * * `NE` - Niger
   * * `NG` - Nigeria
   * * `NU` - Niue
   * * `NF` - Norfolk Island
   * * `KP` - North Korea
   * * `MK` - North Macedonia
   * * `MP` - Northern Mariana Islands
   * * `NO` - Norway
   * * `OM` - Oman
   * * `PK` - Pakistan
   * * `PW` - Palau
   * * `PS` - Palestine, State of
   * * `PA` - Panama
   * * `PG` - Papua New Guinea
   * * `PY` - Paraguay
   * * `PE` - Peru
   * * `PH` - Philippines
   * * `PN` - Pitcairn
   * * `PL` - Poland
   * * `PT` - Portugal
   * * `PR` - Puerto Rico
   * * `QA` - Qatar
   * * `RE` - Réunion
   * * `RO` - Romania
   * * `RU` - Russia
   * * `RW` - Rwanda
   * * `BL` - Saint Barthélemy
   * * `SH` - Saint Helena, Ascension and Tristan da Cunha
   * * `KN` - Saint Kitts and Nevis
   * * `LC` - Saint Lucia
   * * `MF` - Saint Martin (French part)
   * * `PM` - Saint Pierre and Miquelon
   * * `VC` - Saint Vincent and the Grenadines
   * * `WS` - Samoa
   * * `SM` - San Marino
   * * `ST` - Sao Tome and Principe
   * * `SA` - Saudi Arabia
   * * `SN` - Senegal
   * * `RS` - Serbia
   * * `SC` - Seychelles
   * * `SL` - Sierra Leone
   * * `SG` - Singapore
   * * `SX` - Sint Maarten (Dutch part)
   * * `SK` - Slovakia
   * * `SI` - Slovenia
   * * `SB` - Solomon Islands
   * * `SO` - Somalia
   * * `ZA` - South Africa
   * * `GS` - South Georgia and the South Sandwich Islands
   * * `KR` - South Korea
   * * `SS` - South Sudan
   * * `ES` - Spain
   * * `LK` - Sri Lanka
   * * `SD` - Sudan
   * * `SR` - Suriname
   * * `SJ` - Svalbard and Jan Mayen
   * * `SE` - Sweden
   * * `CH` - Switzerland
   * * `SY` - Syria
   * * `TW` - Taiwan
   * * `TJ` - Tajikistan
   * * `TZ` - Tanzania
   * * `TH` - Thailand
   * * `TL` - Timor-Leste
   * * `TG` - Togo
   * * `TK` - Tokelau
   * * `TO` - Tonga
   * * `TT` - Trinidad and Tobago
   * * `TN` - Tunisia
   * * `TR` - Türkiye
   * * `TM` - Turkmenistan
   * * `TC` - Turks and Caicos Islands
   * * `TV` - Tuvalu
   * * `UG` - Uganda
   * * `UA` - Ukraine
   * * `AE` - United Arab Emirates
   * * `GB` - United Kingdom
   * * `UM` - United States Minor Outlying Islands
   * * `US` - United States of America
   * * `UY` - Uruguay
   * * `UZ` - Uzbekistan
   * * `VU` - Vanuatu
   * * `VE` - Venezuela
   * * `VN` - Vietnam
   * * `VG` - Virgin Islands (British)
   * * `VI` - Virgin Islands (U.S.)
   * * `WF` - Wallis and Futuna
   * * `EH` - Western Sahara
   * * `YE` - Yemen
   * * `ZM` - Zambia
   * * `ZW` - Zimbabwe
   */
  nationality: NationalityEnum
  nickname: string
  /** @format date */
  date_of_birth: string | null
  current_grade_level: number
  current_grade_level_str: string | null
  grade_level_in_next_year: number
  house_id: number | null
  /** @format uri */
  avatar: string | null
  /** @format double */
  merit_points: number
  /** @format double */
  pending_merit_points: number
  is_student_new: boolean
  allow_push_notifications?: boolean
  allow_email_notifications?: boolean
  favourites?: string[]
}

export type StudentProfileRequest = {
  allow_push_notifications?: boolean
  allow_email_notifications?: boolean
  favourites?: string[]
}

export type StudentProgrammeEnrollmentStatusSwagger = {
  label: string
  /**
   * * `pending` - Pending
   * * `requested` - Requested
   * * `rejected` - Rejected
   * * `approved` - Approved
   * * `leave_absent` - Leave - Absent
   */
  value: EnrolmentStatusEnum
}

export type StudentProgressAverageGrade = {
  percents: number
  grade: string
  /**
   * * `pending` - Pending
   * * `much_above` - Much Above
   * * `above` - Above
   * * `below` - Below
   * * `much_below` - Much Below
   */
  grade_change: GradeChangeEnum
}

export type StudentProgressGrade = {
  grade_id: number
  quarter_id: number | null
  semester_id: number | null
  grade: string
}

export type StudentProgressGrades = {
  Q1?: StudentProgressGrade | null
  Q2?: StudentProgressGrade | null
  Q3?: StudentProgressGrade | null
  Q4?: StudentProgressGrade | null
  S1?: StudentProgressGrade | null
  S2?: StudentProgressGrade | null
  average_grade?: StudentProgressAverageGrade | null
}

export type StudentProgressResult = {
  course: EnrolledCourse
  school_year: SimpleSchoolYear
  aim: SimpleAim
  artisan: SimpleUser | null
  grades: StudentProgressGrades
}

export type StudentProgressViewResponseData = {
  progress_results: StudentProgressResult[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentPsychologicalCare = {
  has_any_mental_health_issues?: boolean
  has_regular_psychological_care?: boolean
  summer_mental_health_issues: SummerMentalHealthIssue[]
  medical_reports: MedicalReportAttachment[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type StudentPsychologicalCareRequest = {
  has_any_mental_health_issues?: boolean
  has_regular_psychological_care?: boolean
  summer_mental_health_issues: SummerMentalHealthIssueRequest[]
  medical_reports: MedicalReportAttachmentRequest[]
}

export type StudentQuarterViewGrades = {
  student: StudentWithGradeLevel
  aim: SimpleAim
  course_aim: SetGradeAim
  artisan: SimpleUser | null
  grades: QuarterViewGrades
  latest_grades: StudentAssignmentGrade[]
}

export type StudentRecurringProgramme = {
  id: number
  can_enrol: boolean
  /** @format date-time */
  enrolment_deadline: string
  title: string
  description: string
  is_compulsory: boolean
  /**
   * * `academics` - Academics
   * * `house_evening` - House Evening
   * * `term_break` - Term Break
   * * `school_trip` - School Trip
   * * `weekend_programme` - Weekend Programme
   * * `exam_preparation` - Exam Preparation
   * * `other` - Other
   * * `absence_recovery` - Absence Recovery
   * * `lunchtime_recovery` - Lunchtime Recovery
   */
  type: SchoolProgrammeTypeEnum
  type_label: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  students_limit: number
  number_of_sessions: number
  /** @format date-time */
  next_session_date: string | null
  recurring_type: RecurringTypeEnum
  /**
   * * `requested` - Requested
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `cancelled` - Cancelled
   */
  status: SchoolProgrammeStatusEnum
  status_label: string
}

export type StudentRecurringProgrammeRequest = {
  can_enrol: boolean
  /** @format date-time */
  enrolment_deadline: string
  /** @minLength 1 */
  title: string
  /** @minLength 1 */
  description: string
  is_compulsory: boolean
  /**
   * * `academics` - Academics
   * * `house_evening` - House Evening
   * * `term_break` - Term Break
   * * `school_trip` - School Trip
   * * `weekend_programme` - Weekend Programme
   * * `exam_preparation` - Exam Preparation
   * * `other` - Other
   * * `absence_recovery` - Absence Recovery
   * * `lunchtime_recovery` - Lunchtime Recovery
   */
  type: SchoolProgrammeTypeEnum
  /** @minLength 1 */
  type_label: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  students_limit: number
  number_of_sessions: number
  /** @format date-time */
  next_session_date: string | null
  /**
   * * `requested` - Requested
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `cancelled` - Cancelled
   */
  status: SchoolProgrammeStatusEnum
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type StudentRecurringProgrammeSession = {
  id: number
  can_enrol: boolean
  /** @format date-time */
  enrolment_deadline: string | null
  /**
   * * `pending` - Pending
   * * `requested` - Requested
   * * `rejected` - Rejected
   * * `approved` - Approved
   * * `leave_absent` - Leave - Absent
   */
  enrolment_status: EnrolmentStatusEnum
  enrolment_status_label: string
  enrolment_rejection_comment: string
  enrolment_rejected_by_full_name: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  room: SimpleRoom
  leaders: SimpleUserWithAvatar[]
  met_requirements: boolean
  /** @deprecated */
  has_required_category: boolean
  status: SchoolProgrammeStatusEnum
  status_label: string
}

export type StudentRequest = {
  /** @minLength 1 */
  first_name: string
  middle_name: string
  /** @minLength 1 */
  last_name: string
  /** @format date */
  birthdate: string
  /** @minLength 1 */
  birthplace: string
  /** @minLength 1 */
  gender: string
  /** @minLength 1 */
  address: string
  /** @minLength 1 */
  address_city: string
  /** @minLength 1 */
  address_zipcode: string
  /** @minLength 1 */
  address_country: string
  billing_address: string
  billing_address_city: string
  billing_address_zipcode: string
  billing_address_country: string
  is_billing_address_same: boolean
  native_language?: string
  nationalities?: NationalityEnum[]
}

export type StudentRoomService = {
  id: string
  status: MeritRewardStatusEnum
  /** @format date-time */
  created: string
  /** @format date */
  date: string
  decision_comment: string
  notifications_mark_as_read: number | null
  student_saw_changes: boolean
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type StudentRoomServiceRequest = {
  student?: string
  /** @format date */
  date: string
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type StudentSaldoStatus = {
  /**
   * @min 0
   * @max 2147483647
   */
  saldo_status: SaldoStatusEnum
}

export type StudentSchoolHistory = {
  id: number
  /** @format uuid */
  student: string
  school_name: string
  /**
   * @min 1
   * @max 12
   */
  school_from_month: SchoolFromMonthEnum
  /**
   * @min 0
   * @max 2147483647
   */
  school_from_year: number
  /**
   * @min 1
   * @max 12
   */
  school_until_month: SchoolUntilMonthEnum
  /**
   * @min 0
   * @max 2147483647
   */
  school_until_year: number
  order: number
}

export type StudentSchoolHistoryRequest = {
  /** @format uuid */
  student: string
  /** @minLength 1 */
  school_name: string
  /**
   * @min 1
   * @max 12
   */
  school_from_month: SchoolFromMonthEnum
  /**
   * @min 0
   * @max 2147483647
   */
  school_from_year: number
  /**
   * @min 1
   * @max 12
   */
  school_until_month: SchoolUntilMonthEnum
  /**
   * @min 0
   * @max 2147483647
   */
  school_until_year: number
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type StudentSchoolProgramme = {
  id: number
  can_enrol: string
  /** @format date-time */
  enrolment_deadline: string | null
  enrolment_status: EnrolmentStatusEnum
  enrolment_status_label: string
  enrolment_rejection_comment: string
  enrolment_rejected_by_full_name: string
  leaders: SimpleUserWithAvatar[]
  title: string
  description: string
  is_compulsory: boolean
  status: SchoolProgrammeStatusEnum
  status_label: string
  type: SchoolProgrammeTypeEnum
  type_label: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  students_limit: number
  room: SimpleRoom
  notifications_mark_as_read: number | null
  met_requirements: boolean
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type StudentSchoolProgrammeList = {
  id: number
  can_enrol: boolean
  /** @format date-time */
  enrolment_deadline: string | null
  enrolment_status: EnrolmentStatusEnum
  enrolment_status_label: string
  enrolment_rejection_comment: string
  enrolment_rejected_by_full_name: string
  student_saw_changes: boolean
  title: string
  is_compulsory: boolean
  status: SchoolProgrammeStatusEnum
  status_label: string
  type: SchoolProgrammeTypeEnum
  type_label: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  number_of_sessions: number | null
  /** @format date-time */
  next_session_date: string | null
  recurring_id: number | null
  recurring_type: RecurringTypeEnum | NullEnum | null
  met_requirements: boolean
  /** @deprecated */
  has_required_category: boolean
}

export type StudentSchoolProgrammeStatusSwagger = {
  label: string
  /**
   * * `requested` - Requested
   * * `approved` - Approved
   * * `rejected` - Rejected
   * * `cancelled` - Cancelled
   */
  value: SchoolProgrammeStatusEnum
}

export type StudentSchoolProgrammeTypeSwagger = {
  label: string
  /**
   * * `academics` - Academics
   * * `house_evening` - House Evening
   * * `term_break` - Term Break
   * * `school_trip` - School Trip
   * * `weekend_programme` - Weekend Programme
   * * `exam_preparation` - Exam Preparation
   * * `other` - Other
   * * `absence_recovery` - Absence Recovery
   * * `lunchtime_recovery` - Lunchtime Recovery
   */
  value: SchoolProgrammeTypeEnum
}

export type StudentSchoolProgrammeUpcomingSession = {
  id: number
  title: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  recurring_id: number | null
}

export type StudentSimple = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  first_name?: string
  /** @maxLength 128 */
  last_name?: string
  full_name: string
  /** @maxLength 256 */
  name?: string
  /**
   * mainly student's app usage
   * -> we're considering only first name given
   */
  reduced_full_name: string
}

export type StudentSimpleRequest = {
  /** @maxLength 128 */
  first_name?: string
  /** @maxLength 128 */
  last_name?: string
  /** @maxLength 256 */
  name?: string
}

export type StudentStopTheClock = {
  id: string
  status: MeritRewardStatusEnum
  /** @format date-time */
  created: string
  decision_comment: string
  /** @format uuid */
  campus_absence: string
  notifications_mark_as_read: number | null
  student_saw_changes: boolean
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type StudentStopTheClockRequest = {
  student?: string
  /** @format uuid */
  campus_absence: string
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

export type StudentStudiedLanguage = {
  id: number
  /** @format uuid */
  student: string
  /**
   * * `german` - German
   * * `french` - French
   * * `english` - English
   * * `latin` - Latin
   * * `italian` - Italian
   * * `spanish` - Spanish
   * * `other` - Other
   */
  language_name: LanguageNameEnum
  language_name_other?: string
  /**
   * * `a1` - A1
   * * `a2` - A2
   * * `b1` - B1
   * * `b2` - B2
   * * `c1` - C1
   * * `c2` - C2
   * * `other` - Other
   */
  language_level: LanguageLevelEnum
  language_level_other?: string
  /**
   * @min 0
   * @max 2147483647
   */
  language_year: number
  order: number
}

export type StudentStudiedLanguageRequest = {
  /** @format uuid */
  student: string
  /**
   * * `german` - German
   * * `french` - French
   * * `english` - English
   * * `latin` - Latin
   * * `italian` - Italian
   * * `spanish` - Spanish
   * * `other` - Other
   */
  language_name: LanguageNameEnum
  language_name_other?: string
  /**
   * * `a1` - A1
   * * `a2` - A2
   * * `b1` - B1
   * * `b2` - B2
   * * `c1` - C1
   * * `c2` - C2
   * * `other` - Other
   */
  language_level: LanguageLevelEnum
  language_level_other?: string
  /**
   * @min 0
   * @max 2147483647
   */
  language_year: number
}

export type StudentSummerCourseSummary = {
  course_id: string
  course_name: string
  rules_met: boolean
  number_of_positive_days: number
}

export type StudentSummerSummary = {
  focus_courses_data: StudentSummerCourseSummary[]
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
}

export type StudentSurveyResponse = {
  answers: any
}

export type StudentSurveyResponseRequest = {
  answers: any
}

export type StudentTAECourseEnrolment = {
  id: number
  name: string
  /**
   * * `available` - Available
   * * `last_seats` - Last seats
   */
  status: Status95FEnum
  private_package: boolean
  is_private: boolean
}

export type StudentTAEEnrolment = {
  id: number
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_package: SportMembershipOriginalChoiceEnum
  sports: StudentTAEEnrolmentChoice[]
  co_curriculars: StudentTAEEnrolmentChoice[]
}

export type StudentTAEEnrolmentChoice = {
  id: number
  name: string
  course_enrolment_id: number
  choice_type: TAEManageSignupChoicesChoiceTypeEnum
  private_status: PrivateStatusEnum | NullEnum | null
  number_of_lessons: number | null
  is_private: boolean
  should_select_replacement: boolean
}

export type StudentTAEEnrolmentChoiceCreate = {
  id: number
  /**
   * * `FIRST_CHOICE` - First choice
   * * `SECOND_CHOICE` - Second choice
   * * `EXTRA_CHOICES` - Extra choices
   * * `ADMIN_CHOICE` - Admin choice
   */
  choice_type: TAEManageSignupChoicesChoiceTypeEnum
  /** @default 1 */
  number_of_lessons?: number
}

export type StudentTAEEnrolmentChoiceCreateRequest = {
  id: number
  /**
   * * `FIRST_CHOICE` - First choice
   * * `SECOND_CHOICE` - Second choice
   * * `EXTRA_CHOICES` - Extra choices
   * * `ADMIN_CHOICE` - Admin choice
   */
  choice_type: TAEManageSignupChoicesChoiceTypeEnum
  /** @default 1 */
  number_of_lessons?: number
}

export type StudentTAEEnrolmentChoiceUpdate = {
  id: number
  /**
   * * `FIRST_CHOICE` - First choice
   * * `SECOND_CHOICE` - Second choice
   * * `EXTRA_CHOICES` - Extra choices
   * * `ADMIN_CHOICE` - Admin choice
   */
  choice_type: TAEManageSignupChoicesChoiceTypeEnum
  /** @default 1 */
  number_of_lessons?: number
}

export type StudentTAEEnrolmentChoiceUpdateRequest = {
  id: number
  /**
   * * `FIRST_CHOICE` - First choice
   * * `SECOND_CHOICE` - Second choice
   * * `EXTRA_CHOICES` - Extra choices
   * * `ADMIN_CHOICE` - Admin choice
   */
  choice_type: TAEManageSignupChoicesChoiceTypeEnum
  /** @default 1 */
  number_of_lessons?: number
}

export type StudentTAEEnrolmentChoiceV2 = {
  id: number
  name: string
  course_enrolment_id: number
  choice_type: TAEManageSignupChoicesChoiceTypeEnum
  private_status: PrivateStatusEnum | NullEnum | null
  number_of_lessons: number | null
  is_private: boolean
  should_select_replacement: boolean
}

export type StudentTAEEnrolmentCreate = {
  sports?: StudentTAEEnrolmentChoiceCreate[]
  co_curriculars?: StudentTAEEnrolmentChoiceCreate[]
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_package?: SportMembershipOriginalChoiceEnum
}

export type StudentTAEEnrolmentCreateRequest = {
  sports?: StudentTAEEnrolmentChoiceCreateRequest[]
  co_curriculars?: StudentTAEEnrolmentChoiceCreateRequest[]
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_package?: SportMembershipOriginalChoiceEnum
}

export type StudentTAEEnrolmentStatus = {
  /**
   * * `open` - Open
   * * `in_review` - In review
   * * `reviewed` - Reviewed
   * * `closed` - Closed
   * * `reopened` - Reopened
   */
  status: StudentTAEEnrolmentStatusEnum
  /** @format date */
  deadline: string | null
  open_state: OpenStateEnum | NullEnum | null
  semester: StudentTAEEnrolmentStatusSemester | null
  student_enrolment_id: number | null
  /** @format date-time */
  status_modified: string | null
  /** @format uri */
  offer_url: string | null
  reopened_co_curriculars: boolean
  reopened_sports: boolean
  scope: ScopeEnum[] | null
}

export type StudentTAEEnrolmentStatusEnrolment = {
  id: number
  name: string
  school_year: SimpleSchoolYear
}

/**
 * * `open` - Open
 * * `in_review` - In review
 * * `reviewed` - Reviewed
 * * `closed` - Closed
 * * `reopened` - Reopened
 */
export type StudentTAEEnrolmentStatusEnum =
  | 'open'
  | 'in_review'
  | 'reviewed'
  | 'closed'
  | 'reopened'

export type StudentTAEEnrolmentStatusScope = {
  /**
   * * `CCU` - CCU
   * * `ATH` - ATH
   */
  course_group: CourseGroupEnum
  quarters: SimpleQuarter[]
}

export type StudentTAEEnrolmentStatusSemester = {
  id: number
  name: string
  short_name: string
}

export type StudentTAEEnrolmentStatusV2 = {
  /**
   * * `open` - Open
   * * `in_review` - In review
   * * `reviewed` - Reviewed
   * * `closed` - Closed
   * * `reopened` - Reopened
   */
  status: StudentTAEEnrolmentStatusEnum
  /** @format date-time */
  deadline: string | null
  student_enrolment_id: number | null
  /** @format date-time */
  status_modified: string | null
  open_state: OpenStateEnum | NullEnum | null
  /** @format uri */
  offer_url: string | null
  reopened_co_curriculars: boolean
  reopened_sports: boolean
  enrolment: StudentTAEEnrolmentStatusEnrolment | null
  scope: StudentTAEEnrolmentStatusScope[] | null
}

export type StudentTAEEnrolmentUpdate = {
  sports?: StudentTAEEnrolmentChoiceUpdate[]
  co_curriculars?: StudentTAEEnrolmentChoiceUpdate[]
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_package?: SportMembershipOriginalChoiceEnum
}

export type StudentTAEEnrolmentUpdateRequest = {
  sports?: StudentTAEEnrolmentChoiceUpdateRequest[]
  co_curriculars?: StudentTAEEnrolmentChoiceUpdateRequest[]
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_package?: SportMembershipOriginalChoiceEnum
}

export type StudentTAEExperience = {
  id: number
  experience_str: string
  experience: number
  /** @format date */
  date: string
}

export type StudentTAEExperienceRequest = {
  experience: number
  /** @format date */
  date: string
}

/** Validate ownership before nested replacement deletes existing records. */
export type StudentTAEExperienceUpdate = {
  tae_experiences: StudentTAEExperience[]
}

/** Validate ownership before nested replacement deletes existing records. */
export type StudentTAEExperienceUpdateRequest = {
  tae_experiences: StudentTAEExperienceRequest[]
}

export type StudentTAEProject = {
  id: number
  /** @maxLength 256 */
  name: string
  /** @format date */
  expected_completion_date: string
  /** @format date */
  completion_date?: string | null
}

export type StudentTAEProjectRequest = {
  /**
   * @minLength 1
   * @maxLength 256
   */
  name: string
  /** @format date */
  expected_completion_date: string
  /** @format date */
  completion_date?: string | null
}

/** Validate ownership before nested replacement deletes existing records. */
export type StudentTAEProjectUpdate = {
  tae_projects: StudentTAEProject[]
}

/** Validate ownership before nested replacement deletes existing records. */
export type StudentTAEProjectUpdateRequest = {
  tae_projects: StudentTAEProjectRequest[]
}

export type StudentTAESportPackage = {
  id: number
  /**
   * * `hf_member` - H&F Membership
   * * `sport_package` - Sports Package ++
   * * `sport_package_plus` - Sports Package Plus ++
   */
  package: StudentTAESportPackagePackageEnum
  quarter: number
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  /** Enrolment the membership was generated from. Reports the `source_student_enrolment` data whenever one is stored, so clients written against the legacy field keep working. */
  tae_student_enrollment: PackageEnrolmentSource | null
  source_student_enrolment: PackageEnrolmentSource | null
  /** Number of the student's H&F memberships, regardless of source. The per-source counts only cover memberships backed by that source. */
  student_hf_membership_count: number | null
}

/**
 * * `hf_member` - H&F Membership
 * * `sport_package` - Sports Package ++
 * * `sport_package_plus` - Sports Package Plus ++
 */
export type StudentTAESportPackagePackageEnum =
  | 'hf_member'
  | 'sport_package'
  | 'sport_package_plus'

export type StudentTAESportPackagePutResponse = {
  tae_sport_packages: StudentTAESportPackage[]
}

export type StudentTAESportPackageRequest = {
  /** Order will be overridden by backend based on order in payload. */
  order?: number
  /**
   * * `hf_member` - H&F Membership
   * * `sport_package` - Sports Package ++
   * * `sport_package_plus` - Sports Package Plus ++
   */
  package: StudentTAESportPackagePackageEnum
  quarter: number
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
}

export type StudentTAEStudentEnrolmentV2 = {
  id: number
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_package: SportMembershipOriginalChoiceEnum
  sports: StudentTAEEnrolmentChoiceV2[]
  co_curriculars: StudentTAEEnrolmentChoiceV2[]
}

export type StudentTargetProgressComment = {
  /** @format uuid */
  id: string
  comments?: string[]
}

export type StudentTargetProgressCommentRequest = {
  comments?: string[]
}

export type StudentToEnroll = {
  /** @format uuid */
  id: string
  full_name: string
  current_grade_level: string
  current_grade_level_id: number
  is_student_new: boolean
  /** @format date */
  date_of_entry?: string | null
  entry_quarter: string
}

export type StudentTranscript = {
  id: number
  grade_level: number
  /**
   * @min -2147483648
   * @max 2147483647
   */
  semester: BoardingCommentSemesterEnum
  /** @maxLength 256 */
  school?: string
  certificate: Attachment
  year: string
  status?: StudentTranscriptStatusEnum | BlankEnum
}

export type StudentTranscriptRequest = {
  grade_level: number
  /**
   * @min -2147483648
   * @max 2147483647
   */
  semester: BoardingCommentSemesterEnum
  /** @maxLength 256 */
  school?: string
  status?: StudentTranscriptStatusEnum | BlankEnum
}

/**
 * * `RECEIVED` - Received
 * * `MISSING` - Missing
 */
export type StudentTranscriptStatusEnum = 'RECEIVED' | 'MISSING'

/** Validate ownership before nested replacement deletes existing records. */
export type StudentTranscriptUpdate = {
  transcripts: StudentTranscript[]
}

export type StudentTranscriptUpdateItemRequest = {
  id?: number
  grade_level: number
  /**
   * @min -2147483648
   * @max 2147483647
   */
  semester: BoardingCommentSemesterEnum
  /** @maxLength 256 */
  school?: string
  status?: StudentTranscriptStatusEnum | BlankEnum
}

/** Validate ownership before nested replacement deletes existing records. */
export type StudentTranscriptUpdateRequest = {
  transcripts: StudentTranscriptUpdateItemRequest[]
}

/** mainly student's app usage */
export type StudentWithAvatar = {
  /** @format uuid */
  id: string
  full_name: string
  /** @format uri */
  avatar: string | null
  /**
   * mainly student's app usage
   * -> we're considering only first name given
   */
  reduced_full_name: string
}

export type StudentWithGradeLevel = {
  /** @format uuid */
  id: string
  full_name: string
  grade_level_id: number | null
}

export type StudentWithNoData = {
  /** @format uuid */
  id: string
  name: string
}

export type StudentsAvailableFilters = {
  id: AvailableStringFilter
  house: AvailableStringFilter
  is_accommodated: AvailableIntegerFilter
  room: AvailableStringFilter
  room_code: AvailableStringFilter
  current_grade_level: AvailableIntegerFilter
  activity__quarter: AvailableIntegerFilter
  activity__course: AvailableStringFilter
  homework_status: AvailableStringFilter
  is_at_home: AvailableBooleanFilter
  no_lesson_at_the_moment: AvailableBooleanFilter
  category: AvailableIntegerFilter
  current_location: AvailableStringFilter
  campus_status: AvailableStringFilter
  campus_status_reason: AvailableStringFilter
  campus_location: AvailableStringFilter
  gender: AvailableStringFilter
  has_not_downloaded_certificate: AvailableBooleanFilter
  is_on_senior_house: AvailableBooleanFilter
  birthday_today: AvailableBooleanFilter
  birthday_in_week: AvailableBooleanFilter
  allergen_kinds: AvailableStringFilter
  chronic_condition_types: AvailableStringFilter
  meal_preference: AvailableStringFilter
  is_sick_now: AvailableBooleanFilter
  is_new: AvailableBooleanFilter
  has_dietary_restrictions: AvailableBooleanFilter
  fitness_for_sports: AvailableStringFilter
  insurance_type: AvailableStringFilter
  is_swimmer: AvailableBooleanFilter
  is_athlete: AvailableBooleanFilter
  is_resident_entrepreneur: AvailableBooleanFilter
  week_of_camps: AvailableStringFilter
  number_of_weeks: AvailableIntegerFilter
  focus_courses: AvailableStringFilter
  nationalities: AvailableStringFilter
  age: AvailableIntegerFilter
  panel_students: AvailableStringFilter
  degree_destination: AvailableStringFilter
  career_goal: AvailableIntegerFilter
  program_type: AvailableStringFilter
  academic_status: AvailableStringFilter
  saldo_status: AvailableIntegerFilter
  show_inactive: AvailableBooleanFilter
}

export type StudentsClashesForDateParamsRequest = {
  student_ids: string[]
  /** @format date-time */
  date_from: string
  /** @format date-time */
  date_to: string
  /** @minLength 1 */
  exclude_lesson_id?: string | null
  /** @minLength 1 */
  exclude_exam_id?: string | null
  /** @minLength 1 */
  exclude_programme_id?: string | null
  /** @minLength 1 */
  exclude_leave_request_id?: string | null
}

export type StudentsEnrollOnCourseSuccess = {
  new_capacity_per_section: number | null
  new_max_sections: number | null
  students_count: number
}

export type StudentsFile = {
  id: number
  name: string
  /** @format uri */
  file: string | null
  filename: string
  /** @format uri */
  link: string
}

/**
 * * `fine-arts` - Fine Arts
 * * `performing-arts` - Performing Arts
 * * `leadership` - Diplomacy & Activism
 * * `robotics` - Robotics & Engineering
 * * `environmental-science` - Environmental Science & Sustainability
 * * `fashion` - Fashion & Textile Design
 * * `informatics` - Informatics & Coding
 * * `intensive-english` - Intensive English
 * * `intensive-german` - Intensive German
 * * `succession-management` - Succession Management Essentials
 * * `wealth-and-investment` - Wealth Creation & Investment Essentials
 * * `ai-agentic` - AI Agentic Enterprise Essentials
 * * `rosenberg-x-crimson-university-admission` - Rosenberg x Crimson University Admission Focus Course
 * * `english` - English
 * * `german` - German
 * * `tennis` - Tennis
 * * `personal-training` - Personal Training
 * * `horse-riding` - Horse Riding
 * * `golf` - Golf
 */
export type SubjectNameEnum =
  | 'fine-arts'
  | 'performing-arts'
  | 'leadership'
  | 'robotics'
  | 'environmental-science'
  | 'fashion'
  | 'informatics'
  | 'intensive-english'
  | 'intensive-german'
  | 'succession-management'
  | 'wealth-and-investment'
  | 'ai-agentic'
  | 'rosenberg-x-crimson-university-admission'
  | 'english'
  | 'german'
  | 'tennis'
  | 'personal-training'
  | 'horse-riding'
  | 'golf'

/**
 * * `lesson` - Lesson
 * * `training` - Training
 * * `focus_course` - Focus Course
 */
export type SubjectTypeEnum = 'lesson' | 'training' | 'focus_course'

export type Subtask = {
  id: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  title: string
  is_done: boolean
}

export type SubtaskRequest = {
  /** @minLength 1 */
  title: string
}

export type Subtopic = {
  /** @format uuid */
  id?: string
  /** @maxLength 255 */
  name: string
  /**
   * Planned number of lessons for completing the subtopic
   * @min 0
   * @max 2147483647
   */
  duration: number
  /** @maxLength 20 */
  code?: string | null
  /**
   * Indicates the order of subtopics in the form
   * @min 0
   * @max 2147483647
   */
  ordering?: number
  /** @default false */
  is_used_in_global_standard: boolean
}

export type SubtopicForGlobalStandard = {
  /** @format uuid */
  id: string
  /** @maxLength 255 */
  name: string
  /** @maxLength 20 */
  code?: string | null
  /**
   * Indicates the order of subtopics in the form
   * @min 0
   * @max 2147483647
   */
  ordering?: number
  /**
   * Planned number of lessons for completing the subtopic
   * @min 0
   * @max 2147483647
   */
  duration: number
}

export type SubtopicRequest = {
  /** @format uuid */
  id?: string
  /**
   * @minLength 1
   * @maxLength 255
   */
  name: string
  /**
   * Planned number of lessons for completing the subtopic
   * @min 0
   * @max 2147483647
   */
  duration: number
  /** @maxLength 20 */
  code?: string | null
  /**
   * Indicates the order of subtopics in the form
   * @min 0
   * @max 2147483647
   */
  ordering?: number
}

/**
 * * `attitude` - Attitude
 * * `kindness` - Kindness
 * * `service` - Service
 * * `language` - Language
 * * `electronics` - Electronics
 * * `dress` - Dress
 * * `late` - Late
 * * `night` - Night
 * * `room` - Room
 * * `absence` - Absence
 * * `leave_request` - Leave request
 * * `ausgang` - Ausgang
 * * `deception` - Deception
 * * `transport` - Transport
 * * `private_home` - Private Home
 * * `deliveries` - Deliveries
 * * `safety` - Safety
 * * `facilities` - Facilities
 * * `aggression` - Aggression
 * * `recklessness` - Recklessness
 * * `covid` - Covid
 * * `smoking` - Smoking
 * * `alcohol` - Alcohol
 * * `drugs` - Drugs
 * * `possession` - Possession
 * * `reputation` - Reputation
 * * `refusal` - Refusal
 * * `abusive_behaviour` - Abusive Behaviour
 */
export type SubtypeEnum =
  | 'attitude'
  | 'kindness'
  | 'service'
  | 'language'
  | 'electronics'
  | 'dress'
  | 'late'
  | 'night'
  | 'room'
  | 'absence'
  | 'leave_request'
  | 'ausgang'
  | 'deception'
  | 'transport'
  | 'private_home'
  | 'deliveries'
  | 'safety'
  | 'facilities'
  | 'aggression'
  | 'recklessness'
  | 'covid'
  | 'smoking'
  | 'alcohol'
  | 'drugs'
  | 'possession'
  | 'reputation'
  | 'refusal'
  | 'abusive_behaviour'

export type SuggestedRoomsResponse = {
  suggested: RoomForExam[]
  rooms_with_seats: RoomForExam[]
  rooms_with_no_seats: RoomForExam[]
  rooms_occupied_by_lessons: RoomForExam[]
  auto_assigned_room: number | null
}

export type SuggestedUser = {
  /** @format uuid */
  id: string
  full_name: string
  salutation: string
  user_type: UserTypeResponse
}

export type SuggesterExternalEvent = {
  event_data: Record<string, any>
  /**
   * * `draft_activity_suggester_task_succeeded` - DRAFT_ACTIVITY_SUGGESTER_TASK_SUCCEEDED
   * * `draft_activity_suggester_task_failed` - DRAFT_ACTIVITY_SUGGESTER_TASK_FAILED
   */
  event_type: SuggesterExternalEventEventTypeEnum
}

/**
 * * `draft_activity_suggester_task_succeeded` - DRAFT_ACTIVITY_SUGGESTER_TASK_SUCCEEDED
 * * `draft_activity_suggester_task_failed` - DRAFT_ACTIVITY_SUGGESTER_TASK_FAILED
 */
export type SuggesterExternalEventEventTypeEnum =
  | 'draft_activity_suggester_task_succeeded'
  | 'draft_activity_suggester_task_failed'

export type SuggesterExternalEventRequest = {
  event_data: Record<string, any>
  /**
   * * `draft_activity_suggester_task_succeeded` - DRAFT_ACTIVITY_SUGGESTER_TASK_SUCCEEDED
   * * `draft_activity_suggester_task_failed` - DRAFT_ACTIVITY_SUGGESTER_TASK_FAILED
   */
  event_type: SuggesterExternalEventEventTypeEnum
}

export type SuggesterTaskConfig = {
  suggester_process_timeout: number
}

export type Suggestions = {
  school_year: number
  /** @format uuid */
  student: string
  courses_in_baskets?: Record<string, any>
}

export type SuggestionsRequest = {
  school_year: number
  /** @format uuid */
  student: string
  courses_in_baskets?: Record<string, any>
}

export type SuggestionsRequestRequest = {
  rota_timetable_id: number
  room_id?: number
  users: string[]
  /**
   * @min 0
   * @max 6
   */
  day?: number
  /** @format time */
  start_hour?: string
  /** @format time */
  end_hour?: string
}

export type SuggestionsResponse = {
  day: number
  /** @format time */
  start_hour: string
  /** @format time */
  end_hour: string
}

export type SummerAcademicComment = {
  id: number
  /** @format uuid */
  author_id: string | null
  author?: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 2000 */
  content: string
  week: WeekEnum
  /** @format uuid */
  course: string
}

export type SummerAcademicCommentCreate = {
  id: number
  /** @format uuid */
  author_id: string | null
  author?: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 2000 */
  content: string
  /**
   * * `W1` - Week 1 2024
   * * `W2` - Week 2 2024
   * * `W3` - Week 3 2024
   * * `W4` - Week 4 2024
   * * `W1_2025` - Week 1 2025
   * * `W2_2025` - Week 2 2025
   * * `W3_2025` - Week 3 2025
   * * `W4_2025` - Week 4 2025
   * * `W1_2026` - Week 1 2026
   * * `W2_2026` - Week 2 2026
   * * `W3_2026` - Week 3 2026
   * * `W4_2026` - Week 4 2026
   * * `W1_2027` - Week 1 2027
   * * `W2_2027` - Week 2 2027
   * * `W3_2027` - Week 3 2027
   * * `W4_2027` - Week 4 2027
   */
  week: WeekEnum
  /** @format uuid */
  course: string
}

export type SummerAcademicCommentCreateRequest = {
  author?: string
  /**
   * @minLength 1
   * @maxLength 2000
   */
  content: string
  /**
   * * `W1` - Week 1 2024
   * * `W2` - Week 2 2024
   * * `W3` - Week 3 2024
   * * `W4` - Week 4 2024
   * * `W1_2025` - Week 1 2025
   * * `W2_2025` - Week 2 2025
   * * `W3_2025` - Week 3 2025
   * * `W4_2025` - Week 4 2025
   * * `W1_2026` - Week 1 2026
   * * `W2_2026` - Week 2 2026
   * * `W3_2026` - Week 3 2026
   * * `W4_2026` - Week 4 2026
   * * `W1_2027` - Week 1 2027
   * * `W2_2027` - Week 2 2027
   * * `W3_2027` - Week 3 2027
   * * `W4_2027` - Week 4 2027
   */
  week: WeekEnum
  /** @format uuid */
  course: string
}

export type SummerAcademicCommentList = {
  id: number
  /** @format uuid */
  student_id: string
  /** @format uuid */
  course_id: string
}

export type SummerAcademicCommentRequest = {
  author?: string
  /**
   * @minLength 1
   * @maxLength 2000
   */
  content: string
}

export type SummerAssignOptionsResponse = {
  levels: StringOption[]
  types: StringOption[]
}

export type SummerBoardingComment = {
  id: number
  /** @format uuid */
  author_id: string | null
  author?: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 2000 */
  content: string
  week: WeekEnum
}

export type SummerBoardingCommentCreate = {
  id: number
  /** @format uuid */
  author_id: string | null
  author?: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 2000 */
  content: string
  /**
   * * `W1` - Week 1 2024
   * * `W2` - Week 2 2024
   * * `W3` - Week 3 2024
   * * `W4` - Week 4 2024
   * * `W1_2025` - Week 1 2025
   * * `W2_2025` - Week 2 2025
   * * `W3_2025` - Week 3 2025
   * * `W4_2025` - Week 4 2025
   * * `W1_2026` - Week 1 2026
   * * `W2_2026` - Week 2 2026
   * * `W3_2026` - Week 3 2026
   * * `W4_2026` - Week 4 2026
   * * `W1_2027` - Week 1 2027
   * * `W2_2027` - Week 2 2027
   * * `W3_2027` - Week 3 2027
   * * `W4_2027` - Week 4 2027
   */
  week: WeekEnum
}

export type SummerBoardingCommentCreateRequest = {
  author?: string
  /**
   * @minLength 1
   * @maxLength 2000
   */
  content: string
  /**
   * * `W1` - Week 1 2024
   * * `W2` - Week 2 2024
   * * `W3` - Week 3 2024
   * * `W4` - Week 4 2024
   * * `W1_2025` - Week 1 2025
   * * `W2_2025` - Week 2 2025
   * * `W3_2025` - Week 3 2025
   * * `W4_2025` - Week 4 2025
   * * `W1_2026` - Week 1 2026
   * * `W2_2026` - Week 2 2026
   * * `W3_2026` - Week 3 2026
   * * `W4_2026` - Week 4 2026
   * * `W1_2027` - Week 1 2027
   * * `W2_2027` - Week 2 2027
   * * `W3_2027` - Week 3 2027
   * * `W4_2027` - Week 4 2027
   */
  week: WeekEnum
}

export type SummerBoardingCommentRequest = {
  author?: string
  /**
   * @minLength 1
   * @maxLength 2000
   */
  content: string
}

export type SummerCampLegacy = {
  attended_in_past?: boolean
  is_rosenberg_student?: boolean
}

export type SummerCampLegacyRequest = {
  attended_in_past?: boolean
  is_rosenberg_student?: boolean
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type SummerCampStudent = {
  /**
   * * `standard` - Standard
   * * `special` - Special
   */
  package?: SummerPackageTypeEnum
  package_str: string
  duration: number | null
  /** @format uuid */
  student_id: string
  current_aim_str: string
  level_str: string
  age: string
  /** @format date */
  date_of_birth: string | null
  has_tetanus_vaccination?: boolean
  /** @format date */
  tetanus_vaccination_date?: string | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type SummerCampStudentAssigns = {
  /** Course booking additional comment */
  additional_comments?: string
  summer_assigns: SummerSubjectAssign[]
  /**
   * * `standard` - Standard
   * * `special` - Special
   */
  package: SummerPackageTypeEnum
  /**
   * @min 1
   * @max 4
   */
  duration: number | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type SummerCampStudentAssignsRequest = {
  /** Course booking additional comment */
  additional_comments?: string
  summer_assigns: SummerSubjectAssignRequest[]
  /**
   * * `standard` - Standard
   * * `special` - Special
   */
  package: SummerPackageTypeEnum
  /**
   * @min 1
   * @max 4
   */
  duration: number | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type SummerCampStudentLegacies = {
  summer_legacies: StudentLegacy[]
  attended_in_past?: boolean
  is_rosenberg_student?: boolean
  year_of_rosenberg_entry?: number | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type SummerCampStudentLegaciesRequest = {
  summer_legacies: StudentLegacyRequest[]
  attended_in_past?: boolean
  is_rosenberg_student?: boolean
  year_of_rosenberg_entry?: number | null
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type SummerCampStudentRequest = {
  /**
   * * `standard` - Standard
   * * `special` - Special
   */
  package?: SummerPackageTypeEnum
  has_tetanus_vaccination?: boolean
  /** @format date */
  tetanus_vaccination_date?: string | null
}

export type SummerCertificateAttachment = {
  /** @format uri */
  file: string
}

export type SummerCertificateCeremonyStudentSummary = {
  /** @format uuid */
  id: string
  full_name: string
}

export type SummerCertificateCeremonySummary = {
  /** @format date */
  week_end_date: string
  /** @format date */
  week_ceremony_date: string
  students_without_broken_rules: SummerCertificateCeremonyStudentSummary[]
  students_with_broken_rules: SummerCertificateCeremonyStudentSummary[]
}

export type SummerCertificateDraft = {
  /** It's a helper field for storing data only for frontend purposes. */
  draft?: any
}

export type SummerCertificateDraftRequest = {
  /** It's a helper field for storing data only for frontend purposes. */
  draft?: any
}

export type SummerCommentsExport = {
  id: string
  author: string
  author_first_name: string
  author_last_name: string
  author_profiles: string[]
  /** @format uuid */
  student_id: string
  student_first_name: string
  student_last_name: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  comment_type: string
  week: string
  related_course: string
  course_name: string
  content: string
}

export type SummerCommentsExportFilters = {
  author: AvailableStringFilter
}

export type SummerMentalHealthIssue = {
  id: string
  issue_name: string
}

export type SummerMentalHealthIssueRequest = {
  /** @minLength 1 */
  issue_name: string
}

/**
 * * `standard` - Standard
 * * `special` - Special
 */
export type SummerPackageTypeEnum = 'standard' | 'special'

export type SummerSubjectAssign = {
  id: string
  /** for frontend purposes */
  key: string
  /**
   * * `fine-arts` - Fine Arts
   * * `performing-arts` - Performing Arts
   * * `leadership` - Diplomacy & Activism
   * * `robotics` - Robotics & Engineering
   * * `environmental-science` - Environmental Science & Sustainability
   * * `fashion` - Fashion & Textile Design
   * * `informatics` - Informatics & Coding
   * * `intensive-english` - Intensive English
   * * `intensive-german` - Intensive German
   * * `succession-management` - Succession Management Essentials
   * * `wealth-and-investment` - Wealth Creation & Investment Essentials
   * * `ai-agentic` - AI Agentic Enterprise Essentials
   * * `rosenberg-x-crimson-university-admission` - Rosenberg x Crimson University Admission Focus Course
   * * `english` - English
   * * `german` - German
   * * `tennis` - Tennis
   * * `personal-training` - Personal Training
   * * `horse-riding` - Horse Riding
   * * `golf` - Golf
   */
  subject_name: SubjectNameEnum
  /**
   * * `lesson` - Lesson
   * * `training` - Training
   * * `focus_course` - Focus Course
   */
  subject_type: SubjectTypeEnum
  subject_type_str: string
  level?: LevelEnum | BlankEnum
  level_str: string
  number_of_weeks: number | null
  lessons_per_week: number | null
  /**
   * @min 0
   * @max 2147483647
   */
  order?: number
}

export type SummerSubjectAssignRequest = {
  /**
   * * `fine-arts` - Fine Arts
   * * `performing-arts` - Performing Arts
   * * `leadership` - Diplomacy & Activism
   * * `robotics` - Robotics & Engineering
   * * `environmental-science` - Environmental Science & Sustainability
   * * `fashion` - Fashion & Textile Design
   * * `informatics` - Informatics & Coding
   * * `intensive-english` - Intensive English
   * * `intensive-german` - Intensive German
   * * `succession-management` - Succession Management Essentials
   * * `wealth-and-investment` - Wealth Creation & Investment Essentials
   * * `ai-agentic` - AI Agentic Enterprise Essentials
   * * `rosenberg-x-crimson-university-admission` - Rosenberg x Crimson University Admission Focus Course
   * * `english` - English
   * * `german` - German
   * * `tennis` - Tennis
   * * `personal-training` - Personal Training
   * * `horse-riding` - Horse Riding
   * * `golf` - Golf
   */
  subject_name: SubjectNameEnum
  /**
   * * `lesson` - Lesson
   * * `training` - Training
   * * `focus_course` - Focus Course
   */
  subject_type: SubjectTypeEnum
  level?: LevelEnum | BlankEnum
  number_of_weeks: number | null
  lessons_per_week: number | null
  /**
   * @min 0
   * @max 2147483647
   */
  order?: number
}

/**
 * * `cold` - Cold
 * * `flu` - Flu
 * * `stomach` - Stomach
 * * `allergy` - Allergy
 * * `teeth` - Teeth
 * * `headache` - Headache
 * * `fatigue` - Fatigue
 * * `nausea` - Nausea
 * * `menstruation_pain` - Menstruation pain
 * * `fever` - Fever
 * * `covid_isolation` - Covid - isolation
 * * `covid_quarantine` - Covid - quarantine
 * * `other` - Other
 */
export type SymptomsFf5Enum =
  | 'cold'
  | 'flu'
  | 'stomach'
  | 'allergy'
  | 'teeth'
  | 'headache'
  | 'fatigue'
  | 'nausea'
  | 'menstruation_pain'
  | 'fever'
  | 'covid_isolation'
  | 'covid_quarantine'
  | 'other'

export type TAEAvailableCourse = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  code: string
  /** @maxLength 128 */
  name: string
  version: number
  group: string
  is_selected: boolean
  applied_in: string[]
}

export type TAECopyDraftSectionsRequest = {
  source_quarter: number
  destination_quarter: number
}

export type TAECourse = {
  /** @format uuid */
  id: string
  name: string
  group: string | null
  code: string
  number_of_lessons: number
  is_tae_private: boolean
}

/**
 * * `choice_number_of_lessons` - Choice number of lessons
 * * `total_course_capacity` - Total course capacity
 * * `capacity_per_section` - Capacity per section
 * * `max_sections` - Max sections
 */
export type TAECourseCapacityErrorTypeEnum =
  | 'choice_number_of_lessons'
  | 'total_course_capacity'
  | 'capacity_per_section'
  | 'max_sections'

export type TAECourseEnrollment = {
  id: number
  course: TAECourse
  grades: SimpleGradeLevel[]
  /**
   * @min 0
   * @max 32767
   */
  minimum_participants: number
  /**
   * @min 0
   * @max 32767
   */
  capacity_per_section: number
  /**
   * @min 0
   * @max 32767
   */
  max_sections: number
  enrolled_students_count: number
  approved_students_count: number
  first_choice_count: number
  first_choice_approved_count: number
  second_choice_count: number
  second_choice_approved_count: number
  extra_choices_count: number
  extra_choices_approved_count: number
  admin_choice_count: number
  admin_choice_approved_count: number
  is_closed?: boolean
}

export type TAECourseEnrollmentManageSignupList = {
  id: number
  student: TAEStudent
  /** @format date-time */
  timestamp: string
  order: number
  has_parent_comments: boolean
  choices: TAECourseManageSignupChoices[]
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_membership?: SportMembershipOriginalChoiceEnum
  sport_membership_original_choice:
    | SportMembershipOriginalChoiceEnum
    | NullEnum
    | null
}

export type TAECourseEnrolment = {
  id: number
  course: TAECourseEnrolmentCourse
  grades: number[]
  /**
   * @min 0
   * @max 32767
   */
  minimum_participants?: number | null
  /**
   * @min 0
   * @max 32767
   */
  capacity_per_section?: number | null
  /**
   * @min 0
   * @max 32767
   */
  max_sections?: number | null
  is_closed: boolean
  is_validated: boolean
  can_delete: boolean
  enrolled_students_count: number
  approved_students_count: number
  first_choice_count: number
  first_choice_approved_count: number
  second_choice_count: number
  second_choice_approved_count: number
  extra_choices_count: number
  extra_choices_approved_count: number
  admin_choice_count: number
  admin_choice_approved_count: number
}

export type TAECourseEnrolmentAvailableFilters = {
  course: AvailableStringFilter
  group: AvailableStringFilter
  grades: AvailableIntegerFilter
  free_spots: AvailableStringFilter
  sign_up: AvailableStringFilter
}

export type TAECourseEnrolmentBulkValidationResult = {
  /** @min 0 */
  unvalidated_changes_count: number
  unvalidated_course_ids: number[]
  first_unvalidated_pages: TAECourseEnrolmentFirstUnvalidatedPages
}

export type TAECourseEnrolmentCapacityIncreased = {
  id: number
  course_id: string
  course_name: string
  /**
   * @min 0
   * @max 32767
   */
  capacity_per_section?: number | null
  /**
   * @min 0
   * @max 32767
   */
  max_sections?: number | null
}

export type TAECourseEnrolmentCapacityReached = {
  id: number
  course_id: string
  course_name: string
  total_capacity: number
  /**
   * @min 0
   * @max 32767
   */
  capacity_per_section?: number | null
  /**
   * @min 0
   * @max 32767
   */
  max_sections?: number | null
}

export type TAECourseEnrolmentCourse = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  code: string
  /** @maxLength 128 */
  name: string
  version: number
  group: string
  number_of_lessons: number
}

export type TAECourseEnrolmentCourseRequest = {
  /**
   * @minLength 1
   * @maxLength 128
   */
  code: string
  /**
   * @minLength 1
   * @maxLength 128
   */
  name: string
}

export type TAECourseEnrolmentDraftSection = {
  id: number
  quarter: SimpleQuarter
  /** @format uuid */
  teacher: string
  teacher_data: SimpleUser
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1: string | null
  co_teacher_1_data: SimpleUser
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2: string | null
  co_teacher_2_data: SimpleUser
  course_enrolment: number
  enrolments: TAEEnrolmentV2DraftSectionEnrolment[]
  section: number | null
  /** This boolean flag helps to keep fallback error about no reason on recalculation run. Please be aware that this flag will be restarted if we update instance */
  is_infeasible_without_reason: boolean
  number_of_conflicts: number
}

export type TAECourseEnrolmentDraftSectionsAvailableFilters = {
  course_group: AvailableStringFilter
  course: AvailableStringFilter
  tae_enrolment: AvailableIntegerFilter
  has_conflicts: AvailableBooleanFilter
  grades: AvailableIntegerFilter
}

export type TAECourseEnrolmentEnrollStudentsRequest = {
  students: string[]
  increase?: IncreaseEnum | NullEnum | null
}

export type TAECourseEnrolmentEnrollStudentsSuccess = {
  new_capacity_per_section: number | null
  new_max_sections: number | null
  students_count: number
}

export type TAECourseEnrolmentFirstUnvalidatedPages = {
  /** @min 1 */
  ccu: number | null
  /** @min 1 */
  ath: number | null
}

export type TAECourseEnrolmentInDraftSections = {
  id: number
  course: TAECourseEnrolmentCourse
  tae_enrolment: TAEEnrolmentInDraftSections
  grades: SimpleGradeLevel[]
  capacity_per_section: number | null
  max_sections: number | null
  draft_sections_count: number
  has_conflicts: boolean
  approved_students_count: number
  unassigned_students_count: number
}

export type TAECourseEnrolmentManageSignupChoice = {
  id: number
  course_enrolment_id: number
  course: TAECourseEnrolmentManageSignupCourse
  is_approved?: boolean
  /**
   * * `FIRST_CHOICE` - First choice
   * * `SECOND_CHOICE` - Second choice
   * * `EXTRA_CHOICES` - Extra choices
   * * `ADMIN_CHOICE` - Admin choice
   */
  choice_type: TAEManageSignupChoicesChoiceTypeEnum
  /**
   * @min 0
   * @max 32767
   */
  number_of_lessons?: number | null
  number_of_lessons_original_choice: number | null
  has_tae_draft_section: boolean
}

export type TAECourseEnrolmentManageSignupCourse = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  code: string
  /** @maxLength 128 */
  name: string
  version: number
  group: string
  number_of_lessons: number
  is_tae_private?: boolean
}

export type TAECourseEnrolmentManageSignupList = {
  id: number
  student: TAEEnrolmentV2ManageSignupStudent
  /** @format date-time */
  timestamp: string
  order: number
  has_parent_comments: boolean
  choices: TAECourseEnrolmentManageSignupChoice[]
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_membership?: SportMembershipOriginalChoiceEnum
  sport_membership_original_choice:
    | SportMembershipOriginalChoiceEnum
    | NullEnum
    | null
}

export type TAECourseEnrolmentPublicationResult = {
  /** @min 0 */
  unvalidated_changes_count: number
  unvalidated_course_ids: number[]
  first_unvalidated_pages: TAECourseEnrolmentFirstUnvalidatedPages
  /** @min 0 */
  affected_students?: number
}

export type TAECourseEnrolmentPublishErrorV2 =
  | TAECourseEnrolmentBulkValidationResult
  | TAECourseEnrolmentPublishStatusError
  | TAECourseEnrolmentPublishFilterError

export type TAECourseEnrolmentPublishFilterError = {
  course?: string[]
  group?: string[]
  grades?: string[]
  free_spots?: string[]
  sign_up?: string[]
  force?: string[]
}

export type TAECourseEnrolmentPublishStatusError = {
  status: string
}

export type TAECourseEnrolmentRequest = {
  grades: number[]
  /**
   * @min 0
   * @max 32767
   */
  minimum_participants?: number | null
  /**
   * @min 0
   * @max 32767
   */
  capacity_per_section?: number | null
  /**
   * @min 0
   * @max 32767
   */
  max_sections?: number | null
}

export type TAECourseEnrolmentSummary = {
  /** @min 0 */
  missing_data_courses_count: number
  /** @min 0 */
  total_ccu_capacity: number
  /** @min 0 */
  total_ath_capacity: number
  /** @min 0 */
  unvalidated_changes_count: number
  has_validated_course: boolean
  all_courses_validated: boolean
}

export type TAECourseGradeConflictDetail = {
  course: string
  grades: string[]
}

export type TAECourseGradeConflictResponse = {
  ccu: TAECourseGradeConflictDetail[]
  ath: TAECourseGradeConflictDetail[]
}

export type TAECourseManageSignupChoices = {
  id: number
  course_enrolment_id: number
  course: TAECourse
  is_approved?: boolean
  /**
   * * `FIRST_CHOICE` - First choice
   * * `SECOND_CHOICE` - Second choice
   * * `EXTRA_CHOICES` - Extra choices
   * * `ADMIN_CHOICE` - Admin choice
   */
  choice_type: TAEManageSignupChoicesChoiceTypeEnum
  /**
   * @min 0
   * @max 32767
   */
  number_of_lessons?: number | null
  number_of_lessons_original_choice: number | null
  has_tae_draft_section: boolean
}

export type TAECoursePublishGradeConflictDetail = {
  student_name: string
  grade: string
  course_name: string
}

export type TAECoursePublishGradeConflictResponse = {
  ccu: TAECoursePublishGradeConflictDetail[]
  ath: TAECoursePublishGradeConflictDetail[]
}

export type TAECourseSelectionRequest = {
  courses: string[]
}

export type TAECourseStudentsToEnrollAvailableFilters = {
  grades: AvailableIntegerFilter
}

export type TAECourseToEnrolOn = {
  course_enrolment_id: number
  enrolment_id: number
  enrolment_name: string
  course: TAECourseEnrolmentCourse
  grades: SimpleGradeLevel[]
  maximum_participants: number | null
  free_spots: number | null
}

export type TAECoursesDraftSectionsAvailableFilters = {
  group: AvailableStringFilter
  quarter: AvailableIntegerFilter
  grades: AvailableIntegerFilter
}

export type TAECoursesDraftSectionsList = {
  course_enrollment_id: number
  course: TAECourse
  draft_sections_count: number
  has_conflicts: boolean
  capacity_per_section: number
  max_sections: number
  approved_students_count: number
  grades: SimpleGradeLevel[]
}

export type TAEDraftActivity = {
  /** @format uuid */
  id: string
  course: SimpleCourse
  students: SimpleStudentWithGradeLevel[]
  teacher: SimpleUser
  co_teacher_1: SimpleUser | null
  co_teacher_2: SimpleUser | null
  room: SimpleRoom | null
  /**
   * @min 0
   * @max 2147483647
   */
  day: RotaDayEnum
  start_hour: string
  end_hour: string
}

export type TAEDraftActivityAvailableFilters = {
  students: AvailableStringFilter
  course: AvailableStringFilter
  room: AvailableStringFilter
  teacher: AvailableStringFilter
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TAEDraftSection = {
  id: number
  /** @format uuid */
  teacher?: string
  teacher_data: SimpleUser
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  co_teacher_1_data: SimpleUser
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
  co_teacher_2_data: SimpleUser
  course_enrolment: number
  quarter: number
  enrolments: TAEDraftSectionEnrolment[]
  section: number | null
  /** This boolean flag helps to keep fallback error about no reason on recalculation run. Please be aware that this flag will be restarted if we update instance */
  is_infeasible_without_reason: boolean
  number_of_conflicts: number
}

export type TAEDraftSectionAvailableStudent = {
  /** @format uuid */
  id: string
  full_name: string
  current_grade_level: string
  current_grade_level_id: number
  is_approved: boolean
  number_of_lessons: number | null
  number_of_lessons_assigned: number
}

export type TAEDraftSectionCapacityConflict = {
  message: string
  error_type: TAECourseCapacityErrorTypeEnum
}

export type TAEDraftSectionCapacityConflictsResponse = {
  conflicts: TAEDraftSectionCapacityConflict[]
  query_param_to_force_increase: string
}

export type TAEDraftSectionCourseCapacity = {
  /**
   * @min 0
   * @max 32767
   */
  capacity_per_section: number
}

export type TAEDraftSectionCourseCapacityResponse = {
  capacity_per_section: number
}

export type TAEDraftSectionCreateRequest = {
  /** @format uuid */
  teacher?: string
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
  course_enrolment: number
  quarter: number
}

export type TAEDraftSectionEnrolment = {
  id: number
  /** @format uuid */
  student: string
  /**
   * @min 1
   * @max 25
   */
  number_of_lessons: number
  number_of_lessons_approved: number
  student_data: TAEStudent
  /** @format date-time */
  created: string
}

export type TAEDraftSectionEnrolmentCreateRequest = {
  /** @format uuid */
  student: string
  /**
   * @min 1
   * @max 25
   */
  number_of_lessons: number
}

export type TAEDraftSectionEnrolmentDeleteConflictResponse = {
  message: string
  query_param_to_force: string
}

export type TAEDraftSectionEnrolmentRequest = {
  /** @format uuid */
  student: string
  /**
   * @min 1
   * @max 25
   */
  number_of_lessons: number
}

export type TAEDraftSectionEnrolmentUpdateRequest = {
  /**
   * @min 1
   * @max 25
   */
  number_of_lessons: number
}

export type TAEDraftSectionPushActionState = {
  is_synced: boolean
  sections_changed: boolean
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TAEDraftSectionRequest = {
  /** @format uuid */
  teacher?: string
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
  course_enrolment: number
  quarter: number
  enrolments: TAEDraftSectionEnrolmentRequest[]
}

export type TAEDraftSectionStatus = {
  capacity_reached: boolean
}

export type TAEDraftSectionStudent = {
  /** @format uuid */
  id: string
  full_name: string
  current_grade_level: string
  current_grade_level_id: number
}

export type TAEDraftSectionUpdateRequest = {
  /** @format uuid */
  teacher?: string
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
}

export type TAEDraftSectionsRegenerationConflict =
  | MessageResponseSchema
  | CopySectionsDifferencesResponse
  | CopySectionsConflictResponse

export type TAEDraftSectionsRegenerationValidationError =
  | DraftSectionsValidationError
  | NoEligibleEnrolmentsError
  | NoDraftSectionsForQuarterError
  | NoCommonCoursesForCopyError

export type TAEDraftSectionsTask = {
  id: number
  actor: SimpleUser
  tae_enrolment: number
  /** @format date-time */
  started_at?: string | null
  /**
   * * `pending` - Pending
   * * `running` - Running
   * * `calculating_conflicts` - Calculating conflicts
   * * `succeeded` - Succeeded
   * * `failed` - Failed
   * * `cancelled` - Cancelled
   */
  status?: TAEDraftSectionsTaskStatusEnum
}

/**
 * * `pending` - Pending
 * * `running` - Running
 * * `calculating_conflicts` - Calculating conflicts
 * * `succeeded` - Succeeded
 * * `failed` - Failed
 * * `cancelled` - Cancelled
 */
export type TAEDraftSectionsTaskStatusEnum =
  | 'pending'
  | 'running'
  | 'calculating_conflicts'
  | 'succeeded'
  | 'failed'
  | 'cancelled'

export type TAEEnrollmentStudentChoice = {
  /**
   * @min 0
   * @max 32767
   */
  number_of_lessons?: number | null
}

export type TAEEnrollmentStudentChoiceRequest = {
  /**
   * @min 0
   * @max 32767
   */
  number_of_lessons?: number | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TAEEnrolment = {
  id: number
  /** @format date */
  start: string | null
  semester: number | null
  /** @format date-time */
  deadline_recurring: string
  /** @format date-time */
  deadline_new: string
  offer_attachment: TAEOfferAttachment
  courses: TAEEnrolmentCourses[]
}

export type TAEEnrolmentActivationConflict = {
  detail: string
  /** @min 0 */
  unvalidated_courses_count?: number
  /** @min 1 */
  existing_enrolment_id?: number
  /** @min 1 */
  existing_plan_id?: number
  /**
   * * `scheduled` - scheduled
   * * `open` - open
   */
  existing_enrolment_status?: ExistingEnrolmentStatusEnum
}

export type TAEEnrolmentActivationError = {
  detail?: string
  force?: string[]
}

export type TAEEnrolmentAvailableFilters = {
  semester: AvailableStringFilter
  school_year: AvailableStringFilter
  status: AvailableStringFilter
}

export type TAEEnrolmentConfigOptions = {
  quarters: TAEEnrolmentIntegerOption[]
  grade_levels: TAEEnrolmentIntegerOption[]
  course_groups: TAEEnrolmentStringOption[]
  disabled_date_ranges: TAEEnrolmentDisabledDateRanges
}

export type TAEEnrolmentCourses = {
  id?: number
  /** @format uuid */
  course: string
  grades: number[]
  /**
   * @min 0
   * @max 32767
   */
  minimum_participants: number
  /**
   * @min 0
   * @max 32767
   */
  capacity_per_section: number
  /**
   * @min 0
   * @max 32767
   */
  max_sections: number
}

export type TAEEnrolmentCoursesAvailableFilters = {
  group: AvailableStringFilter
  free_spots: AvailableStringFilter
  grades: AvailableStringFilter
}

export type TAEEnrolmentCoursesRequest = {
  id?: number
  /** Order will be overridden by backend based on order in payload. */
  order?: number
  /** @format uuid */
  course: string
  grades: number[]
  /**
   * @min 0
   * @max 32767
   */
  minimum_participants: number
  /**
   * @min 0
   * @max 32767
   */
  capacity_per_section: number
  /**
   * @min 0
   * @max 32767
   */
  max_sections: number
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TAEEnrolmentDetail = {
  id: number
  /** @maxLength 255 */
  name: string
  /** @format date */
  start: string | null
  /** @format date-time */
  deadline_recurring: string | null
  /** @format date-time */
  deadline_new: string | null
  status: Status8FbEnum
  includes_ccu: boolean
  includes_ath: boolean
  offer_attachment: TAEEnrolmentV2OfferAttachment
  scopes: TAEEnrolmentScope[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TAEEnrolmentDetailRequest = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name: string
  /** @format date */
  start: string | null
  /** @format date-time */
  deadline_recurring: string | null
  /** @format date-time */
  deadline_new: string | null
  offer_attachment: TAEEnrolmentV2OfferAttachmentRequest
  scopes: TAEEnrolmentScopeRequest[]
}

export type TAEEnrolmentDisabledDateRange = {
  enrolment_id: number
  /** @format date */
  date_from: string
  /** @format date */
  date_to: string
}

export type TAEEnrolmentDisabledDateRanges = {
  start: TAEEnrolmentDisabledDateRange[]
  deadline_recurring: TAEEnrolmentDisabledDateRange[]
  deadline_new: TAEEnrolmentDisabledDateRange[]
}

export type TAEEnrolmentInDraftSections = {
  id: number
  name: string
  status: Status8FbEnum
}

export type TAEEnrolmentIntegerOption = {
  id: number
  name: string
}

export type TAEEnrolmentList = {
  id: number
  /** @format date */
  start: string
  /** @format date-time */
  deadline_recurring: string
  /** @format date-time */
  deadline_new: string
  /**
   * * `DRAFT` - Draft
   * * `VALIDATED` - Validated
   * * `SCHEDULED` - Scheduled
   * * `OPEN` - Open
   * * `CLOSED` - Closed
   * * `ARCHIVED` - Archived
   */
  status?: TAEEnrolmentStatusEnum
  status_display: string
  semester: string
  year: string
  draft_data: TAEEnrolment | null
  enrolled_students_count: number
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TAEEnrolmentRequest = {
  /** @format date */
  start: string | null
  semester: number | null
  /** @format date-time */
  deadline_recurring: string
  /** @format date-time */
  deadline_new: string
  offer_attachment: TAEOfferAttachmentRequest
  courses: TAEEnrolmentCoursesRequest[]
}

export type TAEEnrolmentRetrieve = {
  id: number
  /** @format date */
  start: string
  /** @format date-time */
  deadline_recurring: string
  /** @format date-time */
  deadline_new: string
  /**
   * * `DRAFT` - Draft
   * * `VALIDATED` - Validated
   * * `SCHEDULED` - Scheduled
   * * `OPEN` - Open
   * * `CLOSED` - Closed
   * * `ARCHIVED` - Archived
   */
  status: TAEEnrolmentStatusEnum
  status_display: string
  semester: Semester
  draft_data: TAEEnrolment | null
  offer_attachment: TAEOfferAttachment
  year: string
  enrolled_students_count: number
  reenrolling_students_count: number
  students_count: number
  students_past_deadline_count: number
  courses_count: number
  ath_courses_count: number
  ccu_courses_count: number
  overbooked_ath_courses_count: number
  overbooked_ccu_courses_count: number
  total_ath_capacity: number
  total_ccu_capacity: number
}

export type TAEEnrolmentSaveDraftResponseSchema = {
  id: number
  draft_data: TAEEnrolment
}

export type TAEEnrolmentScope = {
  /**
   * * `CCU` - CCU
   * * `ATH` - ATH
   */
  course_group: CourseGroupEnum
  quarter: number
  grade_levels: number[]
}

export type TAEEnrolmentScopeRequest = {
  /**
   * * `CCU` - CCU
   * * `ATH` - ATH
   */
  course_group: CourseGroupEnum
  quarter: number
  grade_levels: number[]
}

export type TAEEnrolmentStatus = {
  id: number
  /**
   * * `DRAFT` - Draft
   * * `VALIDATED` - Validated
   * * `SCHEDULED` - Scheduled
   * * `OPEN` - Open
   * * `CLOSED` - Closed
   * * `ARCHIVED` - Archived
   */
  status?: TAEEnrolmentStatusEnum
  status_display: string
}

/**
 * * `DRAFT` - Draft
 * * `VALIDATED` - Validated
 * * `SCHEDULED` - Scheduled
 * * `OPEN` - Open
 * * `CLOSED` - Closed
 * * `ARCHIVED` - Archived
 */
export type TAEEnrolmentStatusEnum =
  | 'DRAFT'
  | 'VALIDATED'
  | 'SCHEDULED'
  | 'OPEN'
  | 'CLOSED'
  | 'ARCHIVED'

export type TAEEnrolmentStringOption = {
  id: string
  name: string
}

export type TAEEnrolmentStudent = {
  /** @format uuid */
  id: string
  full_name: string
  current_grade_level: string
  current_grade_level_id: number
  is_new: boolean
  has_parent_comments: boolean
}

export type TAEEnrolmentStudents = {
  enrolled_students: TAEStudentEnrollmentList[]
  not_enrolled_students: TAEStudent[]
}

export type TAEEnrolmentStudentsAvailableFilters = {
  student: AvailableStringFilter
  enrolment_state: AvailableStringFilter
  has_parent_comment: AvailableBooleanFilter
  sport_membership: AvailableStringFilter
  is_student_new: AvailableBooleanFilter
  grades: AvailableIntegerFilter
}

export type TAEEnrolmentStudentsSummary = {
  students_count: number
  enrolled_students_count: number
  reenrolling_students_count: number
  has_validated_course: boolean
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TAEEnrolmentUpdateCheckConflictsRequest = {
  courses: TAEEnrolmentCoursesRequest[]
}

export type TAEEnrolmentUpdateConflictDetail = {
  student_name: string
  year: string
  course_name: string
}

export type TAEEnrolmentUpdateConflictResponse = {
  ccu: TAEEnrolmentUpdateConflictDetail[]
  ath: TAEEnrolmentUpdateConflictDetail[]
}

export type TAEEnrolmentV2AvailableFilters = {
  status: AvailableStringFilter
}

export type TAEEnrolmentV2DraftSectionEnrolment = {
  id: number
  /** @format uuid */
  student: string
  number_of_lessons: number
  number_of_lessons_approved: number
  student_data: TAEDraftSectionStudent
  /** @format date-time */
  created: string
}

export type TAEEnrolmentV2DraftSectionsCreateValidationError =
  | DraftSectionsValidationError
  | NoEligibleEnrolmentsError

export type TAEEnrolmentV2DraftSectionsTask = {
  id: number
  plan: number
  quarter: number
  source_quarter: number | null
  actor: SimpleUserOptimized
  /** @format date-time */
  started_at: string | null
  status: TAEEnrolmentV2DraftSectionsTaskStatusEnum
  operation: TAEEnrolmentV2DraftSectionsTaskOperationEnum
  operation_result: CopySectionsOperationResult | null
  sections_summary: CopySectionsSummary | null
}

/**
 * * `GENERATE` - Generate
 * * `REGENERATE_ALL` - Regenerate all
 * * `ADD_UNASSIGNED` - Add unassigned students
 * * `COPY_SECTIONS` - Copy sections
 */
export type TAEEnrolmentV2DraftSectionsTaskOperationEnum =
  | 'GENERATE'
  | 'REGENERATE_ALL'
  | 'ADD_UNASSIGNED'
  | 'COPY_SECTIONS'

/**
 * * `PENDING` - Pending
 * * `RUNNING` - Running
 * * `SUCCEEDED` - Succeeded
 * * `FAILED` - Failed
 * * `CANCELLED` - Cancelled
 */
export type TAEEnrolmentV2DraftSectionsTaskStatusEnum =
  | 'PENDING'
  | 'RUNNING'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'CANCELLED'

export type TAEEnrolmentV2List = {
  id: number
  /** @maxLength 255 */
  name: string
  /** @format date */
  start?: string | null
  /** @format date-time */
  deadline_recurring?: string | null
  /** @format date-time */
  deadline_new?: string | null
  /**
   * * `DRAFT` - Draft
   * * `SCHEDULED` - Scheduled
   * * `OPEN` - Open
   * * `CLOSED` - Closed
   * * `ARCHIVED` - Archived
   */
  status?: Status8FbEnum
  status_display: string
  enrolled_students_count: number
  students_capacity: number
}

export type TAEEnrolmentV2ManageSignupStudent = {
  /** @format uuid */
  id: string
  full_name: string
  current_grade_level: string
  current_grade_level_id: number
  is_new: boolean
}

export type TAEEnrolmentV2OfferAttachment = {
  id: number
  /** @format uri */
  file: string
  /** @format uuid */
  user: string | null
  file_name: string
}

export type TAEEnrolmentV2OfferAttachmentRequest = {
  id: number
}

export type TAEEnrolmentV2StudentsAvailableFilters = {
  student: AvailableStringFilter
  enrolment_state: AvailableStringFilter
  has_parent_comment: AvailableBooleanFilter
  sport_membership: AvailableStringFilter
  is_student_new: AvailableBooleanFilter
  grades: AvailableIntegerFilter
}

export type TAEExperience = {
  id: number
  /** @maxLength 256 */
  name: string
}

export type TAEGeneratorCreateTaskRequest = {
  /** @default false */
  update_only?: boolean
}

export type TAEGeneratorDraftActivityUpdate = {
  change_id: number
  old_state?: GeneratorUpdateDraftActivity | null
  new_state?: GeneratorUpdateNewDraftActivity | null
  update_type: TAEGeneratorDraftActivityUpdateUpdateTypeEnum
}

/**
 * * `unchanged` - Unchanged
 * * `modified` - Modified
 * * `new` - New
 * * `deleted` - Deleted
 */
export type TAEGeneratorDraftActivityUpdateUpdateTypeEnum =
  | 'unchanged'
  | 'modified'
  | 'new'
  | 'deleted'

export type TAEGeneratorTask = {
  id: number
  actor: SimpleUser
  timetable: number
  /** @format date-time */
  started_at?: string | null
  /**
   * * `pending` - Pending
   * * `running` - Running
   * * `succeeded` - Succeeded
   * * `failed` - Failed
   * * `cancelled` - Cancelled
   * * `discarded` - Discarded
   * * `discarded_after_fail` - Discarded after fail
   * * `applying_in_progress` - Applying in progress
   * * `applied` - Applied
   */
  status?: TAEGeneratorTaskStatusEnum
  infeasible_section_ids: number[] | null
  activities_changed_count: number
  update_only?: boolean
}

/**
 * * `pending` - Pending
 * * `running` - Running
 * * `succeeded` - Succeeded
 * * `failed` - Failed
 * * `cancelled` - Cancelled
 * * `discarded` - Discarded
 * * `discarded_after_fail` - Discarded after fail
 * * `applying_in_progress` - Applying in progress
 * * `applied` - Applied
 */
export type TAEGeneratorTaskStatusEnum =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'discarded'
  | 'discarded_after_fail'
  | 'applying_in_progress'
  | 'applied'

/**
 * * `FIRST_CHOICE` - First choice
 * * `SECOND_CHOICE` - Second choice
 * * `EXTRA_CHOICES` - Extra choices
 * * `ADMIN_CHOICE` - Admin choice
 */
export type TAEManageSignupChoicesChoiceTypeEnum =
  | 'FIRST_CHOICE'
  | 'SECOND_CHOICE'
  | 'EXTRA_CHOICES'
  | 'ADMIN_CHOICE'

export type TAEManageSignupStudentEnrolment = {
  id: number
  student: TAEEnrolmentV2ManageSignupStudent
  has_parent_comments: boolean
  approved_course_enrolments_count: number
}

export type TAENotAssignedStudent = {
  /** @format uuid */
  id: string
  full_name: string
  current_grade_level: string
  current_grade_level_id: number
  /** Flag depending on course enrolment id */
  is_approved: boolean
  /** Number of lessons from student choice */
  number_of_lessons: number
}

export type TAENumberOfConflictsResponse = {
  tae_enrolment_id: number
  quarter_id: number
  no_of_conflicts: number
  timetable_id: number | null
}

export type TAEOfferAttachment = {
  id: number
  /** @format uri */
  file: string
  /** @format uuid */
  user: string | null
  file_name: string
}

export type TAEOfferAttachmentRequest = {
  id: number
}

export type TAEOfferUpload = {
  id: string
  /** @format uri */
  file: string
  user?: string
  file_name: string
}

export type TAEOfferUploadRequest = {
  /** @format binary */
  file: File
  user?: string
}

export type TAEParentComment = {
  id: number
  /** @format uuid */
  student: string
  tae_enrolment: number
  content: string
  /** @format date-time */
  created: string
  /** @format uuid */
  author: string
  author_str: string
}

export type TAEParentCommentV2 = {
  id: number
  /** @format uuid */
  student: string
  tae_enrolment: number
  content: string
  /** @format date-time */
  created: string
  /** @format uuid */
  author: string
  author_str: string
}

export type TAEPlan = {
  id: number
  school_year: number
  courses_count: number
  enrolments_count: number
  sections_count: number
}

export type TAEPlanAvailableFilters = {
  school_year: AvailableIntegerFilter
}

export type TAEPlanRequest = {
  school_year: number
}

export type TAEPlanStudentToReview = {
  /** @format uuid */
  id: string
  full_name: string
  current_grade_level_name: string
  current_grade_level_id: number
  is_ccu_over_limit: boolean
  is_ath_over_limit: boolean
  /** @default 4 */
  ccu_limit: number
  /** @default 5 */
  ath_limit: number
  course_enrollments: TAEPlanStudentsToReviewCourseEnrolment[]
}

export type TAEPlanStudentsToReviewCountResponse = {
  students_to_review_count: number
}

export type TAEPlanStudentsToReviewCourseEnrolment = {
  id: number
  course_name: string
  course_code: string
  is_enrolled_in_section: boolean
  group: Group702Enum
}

export type TAESectionConflict = {
  tae_draft_section: number
  course_name: string
  conflicts: string[]
}

export type TAESectionStudentToReview = {
  /** @format uuid */
  id: string
  full_name: string
  current_grade_level_name: string
  current_grade_level_id: number
  course_enrollments: TAEStudentCourseChoices[]
  is_ccu_over_limit: boolean
  is_ath_over_limit: boolean
  /** @default 4 */
  ccu_limit: number
  /** @default 5 */
  ath_limit: number
}

export type TAEStudent = {
  /** @format uuid */
  id: string
  full_name: string
  current_grade_level: string
  current_grade_level_id: number
  is_new: boolean
  has_parent_comments: boolean
}

export type TAEStudentChoiceApproveRequest = {
  increase?: IncreaseEnum | NullEnum | null
}

export type TAEStudentChoiceFreeSpots = {
  course_enrollment_id: number
  free_spots: number
}

/**
 * Admin edit of the lessons selected for a student's course choice.
 *
 * `number_of_lessons_original_choice` is deliberately absent: it records what
 * the student requested and stays the "Requested" select option forever.
 */
export type TAEStudentChoiceNumberOfLessons = {
  /**
   * @min 1
   * @max 25
   */
  number_of_lessons: number
}

/**
 * Admin edit of the lessons selected for a student's course choice.
 *
 * `number_of_lessons_original_choice` is deliberately absent: it records what
 * the student requested and stays the "Requested" select option forever.
 */
export type TAEStudentChoiceNumberOfLessonsRequest = {
  /**
   * @min 1
   * @max 25
   */
  number_of_lessons: number
}

export type TAEStudentChoiceRejectResponse = {
  course_enrollment_id: number
  free_spots: number | null
  /** @min 0 */
  deleted_tae_draft_section_enrolments_count: number
}

export type TAEStudentChoiceStatusError = {
  status: string[]
}

export type TAEStudentCourseChoices = {
  id: number
  course_name: string
  course_code: string
  is_enrolled_in_section: boolean
}

export type TAEStudentEnrolOnCoursesConflict = {
  enrolment_id: number
  enrolment_name: string
  enrolments_capacity_reached: TAECourseEnrolmentCapacityReached[]
}

export type TAEStudentEnrolOnCoursesRequest = {
  course_enrolments: number[]
  increase?: IncreaseEnum | NullEnum | null
}

export type TAEStudentEnrolOnCoursesSuccess = {
  enrolment_id: number
  enrolment_name: string
  courses_capacity_or_max_sections_increased: TAECourseEnrolmentCapacityIncreased[]
}

export type TAEStudentEnrollment = {
  id: number
  student: TAEStudent
  has_parent_comments: boolean
  approved_course_enrolments_count: number
}

export type TAEStudentEnrollmentList = {
  student_enrolment_id: number
  /** @format date-time */
  created: string
  student: TAEStudent
  has_parent_comments: boolean
  course_enrolments_count: number
  approved_course_enrolments_count: number
  enrolled_by: EnrolledByEnum | null
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_membership?: SportMembershipOriginalChoiceEnum
  sport_membership_original_choice:
    | SportMembershipOriginalChoiceEnum
    | NullEnum
    | null
  /**
   * * `met` - Met
   * * `over_limit` - Over limit
   * * `not_enough` - Not enough
   */
  ccu_rules: CcuRulesEnum
  /**
   * * `met` - Met
   * * `over_limit` - Over limit
   * * `not_enough` - Not enough
   */
  ath_rules: AthRulesEnum
  reopened_ccu?: boolean
  reopened_ath?: boolean
}

export type TAEStudentEnrollmentManageSignupList = {
  course_enrolment_id: number
  maximum_participants: number
  free_spots: number
  course: TAECourse
  choices: TAEStudentManageSignupChoices[]
}

export type TAEStudentEnrollmentReopen = {
  reopened_ccu: boolean
  reopened_ath: boolean
}

export type TAEStudentEnrollmentReopenRequest = {
  reopened_ccu: boolean
  reopened_ath: boolean
}

export type TAEStudentEnrollmentUpdate = {
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_membership?: SportMembershipOriginalChoiceEnum
}

export type TAEStudentEnrollmentUpdateRequest = {
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_membership?: SportMembershipOriginalChoiceEnum
}

export type TAEStudentEnrolmentList = {
  student_enrolment_id: number
  /** @format date-time */
  created: string
  student: TAEEnrolmentStudent
  has_parent_comments: boolean
  course_enrolments_count: number
  approved_course_enrolments_count: number
  enrolled_by: EnrolledByEnum | null
  sport_membership: SportMembershipOriginalChoiceEnum
  sport_membership_original_choice:
    | SportMembershipOriginalChoiceEnum
    | NullEnum
    | null
  is_sport_membership_applicable: boolean
  ccu_rules: CcuRulesEnum
  ath_rules: AthRulesEnum
  reopened_ccu: boolean
  reopened_ath: boolean
}

export type TAEStudentEnrolmentManageSignupChoice = {
  id: number
  student_enrolment: TAEManageSignupStudentEnrolment
  is_approved?: boolean
  /**
   * * `FIRST_CHOICE` - First choice
   * * `SECOND_CHOICE` - Second choice
   * * `EXTRA_CHOICES` - Extra choices
   * * `ADMIN_CHOICE` - Admin choice
   */
  choice_type: TAEManageSignupChoicesChoiceTypeEnum
  /**
   * @min 0
   * @max 32767
   */
  number_of_lessons?: number | null
  number_of_lessons_original_choice: number | null
  has_tae_draft_section: boolean
  /** @format date-time */
  timestamp: string
  order: number
}

export type TAEStudentEnrolmentManageSignupList = {
  course_enrolment_id: number
  maximum_participants: number | null
  free_spots: number | null
  course: TAECourseEnrolmentManageSignupCourse
  choices: TAEStudentEnrolmentManageSignupChoice[]
}

export type TAEStudentEnrolmentReopenSignup = {
  reopened_ccu: boolean
  reopened_ath: boolean
}

export type TAEStudentEnrolmentReopenSignupRequest = {
  reopened_ccu: boolean
  reopened_ath: boolean
}

/** Which course groups the admin may reopen for one student, plus current state. */
export type TAEStudentEnrolmentSignupGroups = {
  course_groups: CourseGroupsEnum[]
  reopened_ccu: boolean
  reopened_ath: boolean
}

export type TAEStudentEnrolmentSportMembership = {
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_membership: SportMembershipOriginalChoiceEnum
}

export type TAEStudentEnrolmentSportMembershipRequest = {
  /**
   * * `hf_member` - Rosenberg Health & Fitness Center Membership
   * * `none` - No membership
   */
  sport_membership: SportMembershipOriginalChoiceEnum
}

export type TAEStudentManageSignupChoices = {
  id: number
  student_enrolment: TAEStudentEnrollment
  is_approved?: boolean
  /**
   * * `FIRST_CHOICE` - First choice
   * * `SECOND_CHOICE` - Second choice
   * * `EXTRA_CHOICES` - Extra choices
   * * `ADMIN_CHOICE` - Admin choice
   */
  choice_type: TAEManageSignupChoicesChoiceTypeEnum
  /**
   * @min 0
   * @max 32767
   */
  number_of_lessons?: number | null
  number_of_lessons_original_choice: number | null
  has_tae_draft_section: boolean
  /** @format date-time */
  timestamp: string
  order: number
}

export type TAEStudentsToReviewCount = {
  students_to_review_count: number
}

export type TAETaskConflictCalculator = {
  id: number
  tae_enrolment: number
  tae_course_enrolment: number
  quarter: number
  timetable?: number | null
  status: TAETaskConflictCalculatorStatusEnum
}

export type TAETaskConflictCalculatorRequest = {
  tae_enrolment: number
  tae_course_enrolment: number
  quarter: number
  timetable?: number | null
}

/**
 * * `pending` - Pending
 * * `running` - Running
 * * `failed` - Failed
 * * `finished` - Finished
 */
export type TAETaskConflictCalculatorStatusEnum =
  | 'pending'
  | 'running'
  | 'failed'
  | 'finished'

/**
 * * `` -
 * * `SC` - Successfully Completed
 * * `CA` - Successfully Completed & Credit Awarded
 */
export type TECustomGradeEnum = 'SC' | 'CA'

export type TalentEnrichmentChoice = {
  id: number
  course_enrolment_id: number
  /** @format uuid */
  course_id: string
  course_name: string
  course_group: string | null
  choice_type: TAEManageSignupChoicesChoiceTypeEnum
  number_of_lessons: number | null
  is_approved: boolean
}

export type TalentEnrichmentEnrolment = {
  id: number
  name: string
  plan_id: number
  school_year_id: number
  school_year_name: string
  status: Status8FbEnum
  /** @format date */
  start: string | null
  /** @format date-time */
  deadline_recurring: string | null
  /** @format date-time */
  deadline_new: string | null
  includes_ccu: boolean
  includes_ath: boolean
  scopes: TalentEnrichmentScope[]
}

export type TalentEnrichmentOffering = {
  id: number
  enrolment_id: number
  /** @format uuid */
  course_id: string
  course_name: string
  course_code: string
  course_group: string | null
  grade_level_ids: number[]
  minimum_participants: number | null
  capacity_per_section: number | null
  max_sections: number | null
  maximum_participants: number | null
  enrolled_students_count: number
  approved_students_count: number
  free_spots: number | null
  is_closed: boolean
  is_validated: boolean
  has_draft_changes: boolean
}

export type TalentEnrichmentScope = {
  course_group: CourseGroupEnum
  quarter_id: number
  quarter_name: string
  grade_level_ids: number[]
}

export type TalentEnrichmentSignup = {
  id: number
  enrolment_id: number
  /** @format uuid */
  student_id: string
  student_name: string
  grade_level_id: number | null
  status: TalentEnrichmentSignupStatusEnum
  sport_membership: SportMembershipOriginalChoiceEnum
  reopened_ccu: boolean
  reopened_ath: boolean
  choices: TalentEnrichmentChoice[]
}

/**
 * * `ENROLLED` - Enrolled
 * * `PENDING` - Pending
 * * `APPROVED` - Approved
 */
export type TalentEnrichmentSignupStatusEnum =
  | 'ENROLLED'
  | 'PENDING'
  | 'APPROVED'

export type TargetGrade = {
  id: number
  target_grade: string
}

export type TargetGradeRequest = {
  /** @minLength 1 */
  target_grade: string
}

/**
 * * `pending` - Pending
 * * `much_above` - Much Above
 * * `above` - Above
 * * `below` - Below
 * * `much_below` - Much Below
 */
export type TargetGradeStatusEnum =
  | 'pending'
  | 'much_above'
  | 'above'
  | 'below'
  | 'much_below'

export type TargetGradeStatusResponse = {
  is_open: boolean
}

/**
 * * `student` - Student
 * * `parent` - Parent
 * * `infoboards` - Infoboards
 */
export type TargetsEnum = 'student' | 'parent' | 'infoboards'

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type Task = {
  id: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  title: string
  description?: string
  /**
   * @format uri
   * @maxLength 200
   */
  link?: string
  /** @format date-time */
  start: string | null
  /** @format date-time */
  deadline?: string | null
  is_done: boolean
  created_by?: string
  created_by_str: string
  /** @format uuid */
  assignee?: string | null
  assignee_str: string
  /** @format uuid */
  team?: string | null
  team_str: string
  is_recurring: boolean
  subtasks?: Subtask[]
  status: string
  is_marked_done_manually: boolean
  done_comment?: string
  allow_to_close_subtask: boolean
  complete_automatically: boolean
  is_editable: string
  recurring?: Recurring | null
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TaskRequest = {
  /** @minLength 1 */
  title: string
  description?: string
  /**
   * @format uri
   * @maxLength 200
   */
  link?: string
  /** @format date-time */
  deadline?: string | null
  created_by?: string
  /** @format uuid */
  assignee?: string | null
  /** @format uuid */
  team?: string | null
  subtasks?: SubtaskRequest[]
  done_comment?: string
  recurring?: RecurringRequest | null
  is_cloned?: boolean
  /** @minLength 1 */
  cloned_from?: string | null
}

export type TaskToggleIsDone = {
  done_comment?: string
}

export type TaskToggleIsDoneRequest = {
  done_comment?: string
}

export type TaskToggleSubtaskIsDone = {
  subtask: string
}

export type TaskToggleSubtaskIsDoneRequest = {
  /** @minLength 1 */
  subtask: string
}

export type TeacherAcademicActivitiesReportPreview = {
  artisan: string
  hours: number
  courses_no: number
  courses: string
}

export type TeacherBoardingActivitiesReportPreview = {
  artisan: string
  duty_hours: string
  night_presence_hours: string
  leaders_meeting_hours: string
  ski_trips_hours: string
  total_hours: string
  breaks: string
}

export type TeacherFromCourse = {
  /** @format uuid */
  teacher_id: string
  teacher_full_name: string
  /** @format uuid */
  course_id: string
}

export type TeacherStudentItem = {
  /** @format uuid */
  id: string
  /**
   * @min 0
   * @max 100
   */
  grade_in_percent?: number | null
}

export type TeacherStudentWithAssignment = {
  /** @format uuid */
  id: string
  full_name: string
  assignments: TeacherStudentItem[]
}

export type TeacherTimetableCourse = {
  /** @format uuid */
  id: string
  /** @maxLength 128 */
  name: string
  types: string[]
  levels: string[]
  /** @format double */
  credits: number | null
  departments: string[]
  /** @format uri */
  outline_file?: string | null
  outline_file_name: string | null
}

export type TeacherTimetableLesson = {
  /** @format uuid */
  id: string
  /** @format date */
  date: string
  start: string
  /**
   * @min 0
   * @max 15
   */
  hour?: HourEnum | NullEnum | null
  /** @format double */
  duration?: number
  day: RotaDayEnum
  end: string
  display_hour: string
  course: TeacherTimetableCourse
  quarter: number
  room?: number | null
  students: SimpleUser[]
  teacher: SimpleUser
  is_archived?: boolean
  teacher_cover?: SimpleUser | null
  co_teacher_1: SimpleUser | null
  co_teacher_2: SimpleUser | null
  section: number | null
  /** attendances */
  co_teacher_1_perm_to_manage_lesson?: boolean
  /** attendances */
  co_teacher_2_perm_to_manage_lesson?: boolean
  section_plan_item: LessonSectionPlanItem | null
  /** @format uuid */
  first_unplanned_lesson: string | null
}

export type TeacherTimetableListLesson = {
  /** @format uuid */
  id: string
  /** @format date */
  date: string
  start: string
  end: string
  display_hour: string
  course: SimpleCourse
  room: TeacherTimetableRoom
  quarter: number
  /** @format uuid */
  teacher: string
  /** @format uuid */
  teacher_cover?: string | null
  /**
   * Co-artisan 1
   * @format uuid
   */
  co_teacher_1?: string | null
  /**
   * Co-artisan 2
   * @format uuid
   */
  co_teacher_2?: string | null
}

export type TeacherTimetableRoom = {
  id: number
  /** @maxLength 128 */
  name: string
  /**
   * @min 0
   * @max 2147483647
   */
  capacity?: number | null
}

/**
 * * `selected-suggested-subtopic` - Selected Suggested Subtopic
 * * `changed-subtopic` - Changed Subtopic
 */
export type TeacherTimetableUpdateLessonActionTypeEnum =
  | 'selected-suggested-subtopic'
  | 'changed-subtopic'

export type TeachersPerGrade = {
  id: number
  teachers: string[]
  grade_level: number
  mentoring?: number
}

export type TeachersPerGradeRequest = {
  teachers: string[]
  grade_level: number
  mentoring?: number
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type Team = {
  /** @format uuid */
  id: string
  name: string
  house?: number | null
  house_name: string
  members: TeamMember[]
  is_predefined: boolean
  include_in_boarding_rota?: boolean
  allow_manual_boarding_rota_assignment?: boolean
}

export type TeamMember = {
  /** @format uuid */
  id: string
  /** @format uuid */
  user: string
  /**
   * @min 0
   * @max 32767
   */
  role: TeamMemberRoleEnum
  role_display: string
  user_first_name: string
  user_last_name: string
  rota_roles?: BoardingArtisanRoleEnum[]
}

export type TeamMemberRequest = {
  /** @format uuid */
  user: string
  /**
   * @min 0
   * @max 32767
   */
  role: TeamMemberRoleEnum
  rota_roles?: BoardingArtisanRoleEnum[]
}

/**
 * * `1` - Member
 * * `2` - Leader
 */
export type TeamMemberRoleEnum = 1 | 2

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TeamRequest = {
  /** @minLength 1 */
  name: string
  house?: number | null
  members: TeamMemberRequest[]
  include_in_boarding_rota?: boolean
  allow_manual_boarding_rota_assignment?: boolean
}

export type TemperatureCheck = {
  id: number
  /** @format uuid */
  health_check: string
  /** @format double */
  temperature: number
}

export type TemperatureCheckRequest = {
  /** @format uuid */
  health_check: string
  /** @format double */
  temperature: number
}

/**
 * * `isolation` - Isolation
 * * `quarantine` - Quarantine
 * * `sleepover` - Sleepover
 */
export type TemporaryMovementTypeEnum = 'isolation' | 'quarantine' | 'sleepover'

/**
 * * `quarter` - Quarter
 * * `school_year` - School Year
 */
export type TimeframeTypeEnum = 'quarter' | 'school_year'

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type Timetable = {
  id: number
  /** @format date-time */
  created: string
  total_gaps: number | null
  /**
   * @format decimal
   * @pattern ^-?\d{0,2}(?:\.\d{0,2})?$
   */
  gaps_per_students?: string | null
  quarter: number
  total_activities: number
  /**
   * * `live` - Live
   * * `active_in_past` - Active in past
   * * `generation_in_progress` - Generation in progress
   * * `draft` - Draft
   * * `infeasible` - Infeasible data. Please increase time, or verify sections
   * * `failed` - Generation process failed
   */
  status?: Status2A1Enum
  status_display: string
  generation_task: number
  quarter_str: string
  quarter_short_name: QuarterShortNameEnum
  idp_plans: number[]
  /** Enabled preview for Artisans */
  enabled_preview_for_teachers?: boolean
  selected_rota_timetable: string
  total_tae_activities: number
  has_tae_sections_changed?: boolean
}

export type TimetableAutoImportConfig = {
  is_enabled: boolean
  is_daily: boolean
  days: RotaDayEnum[]
  /** @format time */
  time: string
  /** @format date-time */
  next_autoimport_date: string | null
}

export type TimetableAutoImportConfigUpdate = {
  is_enabled: boolean
  is_daily: boolean
  days: RotaDayEnum[]
  /** @format time */
  time: string
  /** @format date-time */
  next_autoimport_date: string | null
  disabled_autoimport: DisabledAutoimportTimetable | null
}

export type TimetableCampusAbsence = {
  /** @format uuid */
  id: string
  /** @format date-time */
  start: string
  /** @format date-time */
  end: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /**
   *  type
   * @default "campus_absence"
   */
  _type: TimetableCampusAbsenceTypeEnum
  campus_absence_type: CampusAbsenceTypeEnum
  campus_absence_type_label: string
  campus_absence_subtype: CampusAbsenceSubTypeEnum
  campus_absence_subtype_label: string
  students: StudentWithAvatar[]
  leave_details: string
  can_cancel: boolean
  can_edit: boolean
  created_by?: string
  merit_reward: string
  resourcetype: MeritRewardResourcetypeEnum | null
  custom_name: string
}

/** * `campus_absence` - campus_absence */
export type TimetableCampusAbsenceTypeEnum = 'campus_absence'

export type TimetableDoctorAppointment = {
  id: number
  /** @format date-time */
  start: string
  /** @format date-time */
  end: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /**
   *  type
   * @default "doctor_appointment"
   */
  _type: TimetableDoctorAppointmentTypeEnum
  student_id: string
  doctor_name: string
}

/** * `doctor_appointment` - doctor_appointment */
export type TimetableDoctorAppointmentTypeEnum = 'doctor_appointment'

export type TimetableExam = {
  id: string
  start: string
  end: string
  start_date: string
  end_date: string
  /**
   *  type
   * @default "exam"
   */
  _type: Type4CaEnum
  /** @format date */
  date: string | null
  /** @format uuid */
  course: string
  course_str: string
  official_name: string
  users: string
  location_str: string
  test_invigilators: SimpleUserWithAvatar[]
  saa_invigilators: SimpleUserWithAvatar[]
  teachers: SimpleUserWithAvatar[]
  exam_session_id: string
  is_allowed: string
  /** via annotate */
  school_year_id: number
  /**
   * * `Exam` - Exam
   * * `Lesson` - Lesson
   * * `SchoolProgramme` - SchoolProgramme
   * * `Rota` - Rota
   */
  resourcetype: AdminTimetableResourcetypeEnum
}

export type TimetableLeaveCheck = {
  /** @format uuid */
  id: string
  /** @maxLength 1024 */
  destination: string
  /** @maxLength 256 */
  train?: string
  /** @maxLength 256 */
  plane?: string
  /** @maxLength 256 */
  pick_up_location?: string
  /** @format time */
  pick_up_time?: string | null
  comment?: string
  /** @maxLength 64 */
  contact_person: string
  /** @maxLength 128 */
  contact_person_phone: string
  is_completed?: boolean
  is_driver_needed?: boolean
}

export type TimetableLeaveRequest = {
  /** @format uuid */
  id: string
  /** @format date-time */
  start: string
  /** @format date-time */
  end: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /**
   *  type
   * @default "leave_request"
   */
  _type: TimetableLeaveTypeEnum
  /** @format uuid */
  student_id: string
  leave_type: LeaveRequestTypeEnum
  leave_type_label: string
  check_in: TimetableLeaveCheck
  check_out: TimetableLeaveCheck
  can_edit: boolean
  can_cancel: boolean
}

/** * `leave_request` - leave_request */
export type TimetableLeaveTypeEnum = 'leave_request'

export type TimetableLesson = {
  /** @format uuid */
  id: string
  start: string
  end: string
  start_date: string
  end_date: string
  /**
   *  type
   * @default "lesson"
   */
  _type: TimetableLessonTypeEnum
  /** @format date */
  date: string
  /** @format uuid */
  course: string
  course_str: string
  users: SimpleUserWithAvatar[]
  room_str: string
  teacher: SimpleUserWithAvatar
  teacher_cover: SimpleUserWithAvatar | null
  co_teacher_1: SimpleUserWithAvatar | null
  co_teacher_2: SimpleUserWithAvatar | null
  is_allowed: boolean
  homework_count: number
  /** via annotate */
  school_year_id: number
  /**
   * * `Exam` - Exam
   * * `Lesson` - Lesson
   * * `SchoolProgramme` - SchoolProgramme
   * * `Rota` - Rota
   */
  resourcetype: AdminTimetableResourcetypeEnum
}

/** * `lesson` - lesson */
export type TimetableLessonTypeEnum = 'lesson'

export type TimetableLog = {
  id: number
  /** @format date-time */
  created: string
  message: string
  changed_fields: string
  timetable: number
  /**
   * * `create` - Create
   * * `delete` - Delete
   * * `update` - Update
   */
  log_type: TimetableLogLogTypeEnum
}

/**
 * * `create` - Create
 * * `delete` - Delete
 * * `update` - Update
 */
export type TimetableLogLogTypeEnum = 'create' | 'delete' | 'update'

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TimetableMentoring = {
  id: number
  ignored_students?: string[]
  timetable: number
  grades_to_assign: number[]
  /**
   * @min 0
   * @max 2147483647
   */
  students_in_section?: number
  is_applied: boolean
  copy_from_previous_quarter?: boolean
  teachers_per_grade: TeachersPerGrade[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TimetableMentoringRequest = {
  ignored_students?: string[]
  timetable: number
  grades_to_assign: number[]
  /**
   * @min 0
   * @max 2147483647
   */
  students_in_section?: number
  copy_from_previous_quarter?: boolean
  teachers_per_grade: TeachersPerGradeRequest[]
}

export type TimetableMeritReward = {
  id: string
  /** @format date */
  start: string
  /** @format date */
  end: string
  start_date: string
  end_date: string
  /**
   *  type
   * @default "merit_reward"
   */
  _type: TimetableMeritRewardTypeEnum
  /**
   * * `CasualAnyday` - CasualAnyday
   * * `RoomService` - RoomService
   * * `BookTheCafe` - BookTheCafe
   * * `CityBrunchZurich` - CityBrunchZurich
   * * `AdditionalAusgang` - AdditionalAusgang
   * * `CategoryUpgrade` - CategoryUpgrade
   * * `StopTheClock` - StopTheClock
   * * `MakeAnImpact` - MakeAnImpact
   * * `OnCampusAusgang` - OnCampusAusgang
   */
  resourcetype: MeritRewardResourcetypeEnum
}

/** * `merit_reward` - merit_reward */
export type TimetableMeritRewardTypeEnum = 'merit_reward'

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type TimetableRequest = {
  /**
   * @format decimal
   * @pattern ^-?\d{0,2}(?:\.\d{0,2})?$
   */
  gaps_per_students?: string | null
  /**
   * * `live` - Live
   * * `active_in_past` - Active in past
   * * `generation_in_progress` - Generation in progress
   * * `draft` - Draft
   * * `infeasible` - Infeasible data. Please increase time, or verify sections
   * * `failed` - Generation process failed
   */
  status?: Status2A1Enum
  /** Enabled preview for Artisans */
  enabled_preview_for_teachers?: boolean
  has_tae_sections_changed?: boolean
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type TimetableRetrieve = {
  id: number
  /** @format date-time */
  created: string
  total_gaps: number | null
  /**
   * @format decimal
   * @pattern ^-?\d{0,2}(?:\.\d{0,2})?$
   */
  gaps_per_students?: string | null
  quarter: number
  total_activities: number
  /**
   * * `live` - Live
   * * `active_in_past` - Active in past
   * * `generation_in_progress` - Generation in progress
   * * `draft` - Draft
   * * `infeasible` - Infeasible data. Please increase time, or verify sections
   * * `failed` - Generation process failed
   */
  status?: Status2A1Enum
  status_display: string
  generation_task: number
  quarter_str: string
  quarter_short_name: QuarterShortNameEnum
  idp_plans: number[]
  /** Enabled preview for Artisans */
  enabled_preview_for_teachers?: boolean
  selected_rota_timetable: string
  total_tae_activities: number
  has_tae_sections_changed?: boolean
  number_of_changes: number
  number_of_rota_changes: number
  autoimport_config: TimetableAutoImportConfig
  blocking_tae_generator_task: TAEGeneratorTask | null
  active_tae_enrolment: number | null
  shuffle_students_in_sections: boolean
}

export type TimetableRotaActivity = {
  id: string
  /** @format date-time */
  start: string
  /** @format date-time */
  end: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /**
   *  type
   * @default "rota"
   */
  _type: TimetableRotaActivityTypeEnum
  role: string
  role_str: string
  /** @default "" */
  house_name: string
  user: string
  user_str: string
  rota_type: RotaTypeEnum
  /** @format uuid */
  team: string | null
  team_str: string
  date: string
  covered_by: SimpleUser[] | null
  covering: SimpleUser[]
  users_str: string
  /**
   * * `Exam` - Exam
   * * `Lesson` - Lesson
   * * `SchoolProgramme` - SchoolProgramme
   * * `Rota` - Rota
   */
  resourcetype: AdminTimetableResourcetypeEnum
}

/** * `rota` - rota */
export type TimetableRotaActivityTypeEnum = 'rota'

export type TimetableSchoolBreak = {
  id: number
  /** @format date */
  start: string
  /** @format date */
  end: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
  /**
   *  type
   * @default "school_break"
   */
  _type: TimetableSchoolBreakTypeEnum
  name: string
  related_notification_id: string
}

/** * `school_break` - school_break */
export type TimetableSchoolBreakTypeEnum = 'school_break'

export type TimetableSchoolProgramme = {
  id: string
  /** @format date-time */
  start: string
  /** @format date-time */
  end: string
  /** @format date-time */
  start_date: string
  /** @format date-time */
  end_date: string
  /**
   *  type
   * @default "school_programme"
   */
  _type: TimetableSchoolProgrammeTypeEnum
  title: string
  is_allowed: boolean
  leaders_data: SimpleUser[]
  type: SchoolProgrammeTypeEnum
  room_str: string
  is_recovery_type: boolean
  recovery_location: string | null
  recovery_id: string | null
  recurring_id: number | null
  /**
   * * `Exam` - Exam
   * * `Lesson` - Lesson
   * * `SchoolProgramme` - SchoolProgramme
   * * `Rota` - Rota
   */
  resourcetype: AdminTimetableResourcetypeEnum
}

/** * `school_programme` - school_programme */
export type TimetableSchoolProgrammeTypeEnum = 'school_programme'

export type TimetableStats = {
  id: number
  /** @format date-time */
  created: string
  total_gaps: number | null
  /**
   * @format decimal
   * @pattern ^-?\d{0,2}(?:\.\d{0,2})?$
   */
  gaps_per_students?: string | null
  total_activities: number
  /**
   * Mixin for parsing restql query from request.
   *
   * NOTE: We are using `request.GET` instead of
   * `request.query_params` because this might be
   * called before DRF request is created(i.e from dispatch).
   * This means `request.query_params` might not be available
   * when this mixin is used.
   */
  generation_task: GenerationTask
  late_activities: string
  students_with_most_hours: string
  teachers_with_most_hours: string
  generation_time: string
}

export type TimetableWithRota = {
  id: number
  rota_timetable_id: number
}

export type ToggleReactionRequest = {
  /**
   * * `widget` - Widget
   * * `student_content` - Student Content
   * * `message` - Message
   * * `celebration_item` - Celebration Item
   * * `congratulations_item` - Result
   * * `student_art` - Student Art
   * * `variant_manual_congratulations` - Congratulations
   */
  content_type: FeedContentTypeEnum
  /** @minLength 1 */
  object_id: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type Topic = {
  /** @format uuid */
  id: string
  /** @maxLength 255 */
  name: string
  description?: string | null
  domain: SimpleDomain
  departments: SimpleDepartment[]
  grade_levels: SimpleGradeLevel[]
  subtopics: Subtopic[]
  aims: SimpleAim[]
}

export type TopicForGlobalStandard = {
  /** @format uuid */
  id: string
  /** @maxLength 255 */
  name: string
  domain: SimpleDomain
  subtopics: SubtopicForGlobalStandard[]
}

export type TopicList = {
  /** @format uuid */
  id: string
  /** @maxLength 255 */
  name: string
  domain: SimpleDomain
  subtopics_count: number
  /** @default false */
  is_used_in_global_standard: boolean
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TopicRequest = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name: string
  description?: string | null
  /** @format uuid */
  domain_id: string
  department_ids: number[]
  grade_levels_ids: number[]
  subtopics: SubtopicRequest[]
  aim_ids: number[]
}

export type TopicsForGlobalStandardAvailableFiltersResponse = {
  domain: AvailableStringFilter
  departments: AvailableIntegerFilter
  grade_levels: AvailableGradeLevelsChoices
  aims: AvailableIntegerFilter
  subtopics__code: AvailableStringFilter
}

export type TranscriptOfGradesCourseList = {
  /** @format uuid */
  id: string
  name: string
  aim: number
  /** @format double */
  credits: number | null
  average: number | null
  quarter_averages: QuarterAverage[]
  grade: string | null
  difficulty: string
  is_dropped: boolean
  /**
   * * `CCU` - CCU
   * * `SPORTS` - Sports
   * * `ACADEMIC` - Academic
   */
  group: TranscriptOfGradesItemGroupEnum
  year: number
  year_str: string
  related_courses_ids: string[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TranscriptOfGradesCreate = {
  /** @format uuid */
  id: string
  /** @format uuid */
  student: string
  /** @maxLength 255 */
  name?: string
  /** @default "active" */
  status: TranscriptOfGradesStatusEnum
  /**
   * @format double
   * @min -10000
   * @exclusiveMin true
   * @max 10000
   * @exclusiveMax true
   */
  internal_credits: number
  /**
   * @format double
   * @min -10000
   * @exclusiveMin true
   * @max 10000
   * @exclusiveMax true
   */
  total_credits: number
  school_year: SimpleSchoolYear
  grade_level: SimpleGradeLevel
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  items: TranscriptOfGradesItem[]
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TranscriptOfGradesCreateRequest = {
  /** @format uuid */
  student: string
  /**
   * @minLength 1
   * @maxLength 255
   */
  name?: string
  grade_level_id: number
  school_year_id: number
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TranscriptOfGradesItem = {
  /** @format uuid */
  id?: string
  /**
   * Indicates the order of items in the form and file
   * @min -2147483648
   * @max 2147483647
   */
  ordering: number
  /**
   * Course official name
   * @maxLength 150
   */
  official_name?: string | null
  courses: TranscriptOfGradesItemCourse[]
  /**
   * Student results average percentage
   * @min 0
   * @max 2147483647
   */
  average?: number | null
  /**
   * @format double
   * @min -10000
   * @exclusiveMin true
   * @max 10000
   * @exclusiveMax true
   */
  credits: number
  /** @maxLength 32 */
  grade?: string | null
  /**
   * Course difficulty
   * @maxLength 32
   */
  level?: string | null
  /**
   * * `CCU` - CCU
   * * `SPORTS` - Sports
   * * `ACADEMIC` - Academic
   */
  group: TranscriptOfGradesItemGroupEnum
  is_deletable: boolean
}

export type TranscriptOfGradesItemCourse = {
  id: number
  /** @format uuid */
  course_id: string
  year: number
  year_str: string
}

export type TranscriptOfGradesItemCourseRequest = {
  /** @format uuid */
  course_id: string
  year: number
}

/**
 * * `CCU` - CCU
 * * `SPORTS` - Sports
 * * `ACADEMIC` - Academic
 */
export type TranscriptOfGradesItemGroupEnum = 'CCU' | 'SPORTS' | 'ACADEMIC'

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TranscriptOfGradesItemRequest = {
  /** @format uuid */
  id?: string
  /**
   * Indicates the order of items in the form and file
   * @min -2147483648
   * @max 2147483647
   */
  ordering: number
  /**
   * Course official name
   * @maxLength 150
   */
  official_name?: string | null
  courses: TranscriptOfGradesItemCourseRequest[]
  /**
   * Student results average percentage
   * @min 0
   * @max 2147483647
   */
  average?: number | null
  /**
   * @format double
   * @min -10000
   * @exclusiveMin true
   * @max 10000
   * @exclusiveMax true
   */
  credits: number
  /** @maxLength 32 */
  grade?: string | null
  /**
   * Course difficulty
   * @maxLength 32
   */
  level?: string | null
  /**
   * * `CCU` - CCU
   * * `SPORTS` - Sports
   * * `ACADEMIC` - Academic
   */
  group: TranscriptOfGradesItemGroupEnum
}

export type TranscriptOfGradesList = {
  grouped_years: TranscriptOfGradesListYear[]
}

export type TranscriptOfGradesListItem = {
  /** @format uuid */
  id: string
  /** @maxLength 255 */
  name?: string
  /**
   * @format double
   * @min -10000
   * @exclusiveMin true
   * @max 10000
   * @exclusiveMax true
   */
  internal_credits: number
  /** @format date-time */
  modified: string
  /** @format date-time */
  created: string
  /**
   * * `active` - Active
   * * `complete` - Complete
   * * `official` - Official
   */
  status?: TranscriptOfGradesStatusEnum
  allow_use_as_draft: boolean
}

export type TranscriptOfGradesListYear = {
  grade_level: SimpleGradeLevel
  school_year: SimpleSchoolYear
  items: TranscriptOfGradesListItem[]
  active_or_complete_exists: boolean
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TranscriptOfGradesRestore = {
  /** @format uuid */
  id: string
  /** @format uuid */
  student: string
  name: string
  /** @default "active" */
  status: TranscriptOfGradesStatusEnum
  /**
   * @format double
   * @min -10000
   * @exclusiveMin true
   * @max 10000
   * @exclusiveMax true
   */
  internal_credits: number
  /**
   * @format double
   * @min -10000
   * @exclusiveMin true
   * @max 10000
   * @exclusiveMax true
   */
  total_credits: number
  school_year: SimpleSchoolYear
  grade_level: SimpleGradeLevel
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  items: TranscriptOfGradesItem[]
  allow_use_as_draft: boolean
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TranscriptOfGradesRetrieve = {
  /** @format uuid */
  id: string
  /** @format uuid */
  student: string
  /** @maxLength 255 */
  name?: string
  /** @default "active" */
  status: TranscriptOfGradesStatusEnum
  /**
   * @format double
   * @min -10000
   * @exclusiveMin true
   * @max 10000
   * @exclusiveMax true
   */
  internal_credits: number
  /**
   * @format double
   * @min -10000
   * @exclusiveMin true
   * @max 10000
   * @exclusiveMax true
   */
  total_credits: number
  school_year: SimpleSchoolYear
  grade_level: SimpleGradeLevel
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  items: TranscriptOfGradesItem[]
  allow_use_as_draft: boolean
}

export type TranscriptOfGradesSignature = {
  /** @format uuid */
  id: string
  name: string
}

/**
 * * `active` - Active
 * * `complete` - Complete
 * * `official` - Official
 */
export type TranscriptOfGradesStatusEnum = 'active' | 'complete' | 'official'

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TranscriptOfGradesUpdate = {
  /** @format uuid */
  id: string
  /** @format uuid */
  student: string
  /** @maxLength 255 */
  name?: string
  /**
   * * `active` - Active
   * * `complete` - Complete
   * * `official` - Official
   */
  status: TranscriptOfGradesStatusEnum
  /**
   * @format double
   * @min -10000
   * @exclusiveMin true
   * @max 10000
   * @exclusiveMax true
   */
  internal_credits: number
  /**
   * @format double
   * @min -10000
   * @exclusiveMin true
   * @max 10000
   * @exclusiveMax true
   */
  total_credits: number
  school_year: SimpleSchoolYear
  grade_level: SimpleGradeLevel
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  items: TranscriptOfGradesItem[]
  allow_use_as_draft: boolean
}

export type TranscriptOfGradesUpdateGradesRequestRequest = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name?: string
}

/** Child is set instead of add in m2m save, handle blank save_kwargs */
export type TranscriptOfGradesUpdateRequest = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name?: string
  /**
   * * `active` - Active
   * * `complete` - Complete
   * * `official` - Official
   */
  status: TranscriptOfGradesStatusEnum
  items: TranscriptOfGradesItemRequest[]
}

export type TreatmentHistoryResponse = {
  id: string
  symptoms: TreatmentHistoryResponseSymptomsEnum[] | null
  /**
   * * `opened` - Open
   * * `closed` - Closed
   * * `requested` - Requested
   * * `rejected` - Rejected
   */
  status: TreatmentHistoryResponseStatusEnum
  /** @format date */
  created: string
  /** @format date-time */
  closed_at: string
  absent_now: boolean
}

/**
 * * `opened` - Open
 * * `closed` - Closed
 * * `requested` - Requested
 * * `rejected` - Rejected
 */
export type TreatmentHistoryResponseStatusEnum =
  | 'opened'
  | 'closed'
  | 'requested'
  | 'rejected'

/**
 * * `Cold` - Cold
 * * `Flu` - Flu
 * * `Stomach` - Stomach
 * * `Allergy` - Allergy
 * * `Teeth` - Teeth
 * * `Headache` - Headache
 * * `Fatigue` - Fatigue
 * * `Nausea` - Nausea
 * * `Menstruation pain` - Menstruation pain
 * * `Fever` - Fever
 * * `Covid - isolation` - Covid - isolation
 * * `Covid - quarantine` - Covid - quarantine
 * * `Other` - Other
 */
export type TreatmentHistoryResponseSymptomsEnum =
  | 'Cold'
  | 'Flu'
  | 'Stomach'
  | 'Allergy'
  | 'Teeth'
  | 'Headache'
  | 'Fatigue'
  | 'Nausea'
  | 'Menstruation pain'
  | 'Fever'
  | 'Covid - isolation'
  | 'Covid - quarantine'
  | 'Other'

export type Tutor = {
  /** @format uuid */
  id: string
  full_name: string
  profiles: string[]
  /**
   * Active
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
}

export type TwoFactorLogin =
  | TwoFactorLoginResponse
  | TwoFactorLoginCodeResponse
  | TwoFactorLoginUserResponse

export type TwoFactorLoginCodeResponse = {
  code: number
  /** @format uuid */
  challenge_id: string
  user: LoginUserResponse
}

export type TwoFactorLoginResponse = {
  token: string
  expiry: string
  user: LoginUserResponse
}

export type TwoFactorLoginUserResponse = {
  /** @format uuid */
  challenge_id: string
  user: LoginUserResponse
}

/**
 * * `4` - 4
 * * `3` - 3
 * * `2` - 2
 * * `1` - 1
 * * `0` - 0
 */
export type TwoWeeksAgoCategoryEnum = 4 | 3 | 2 | 1 | 0

/**
 * * `daily` - Daily
 * * `every_weekday` - Every weekday
 * * `weekly` - Weekly
 * * `monthly` - Monthly
 */
export type Type061Enum = 'daily' | 'every_weekday' | 'weekly' | 'monthly'

export type UnpublishBoardRequest = {
  /** Unique destinations to remove visibility and feed pins from. Other destinations retain their visibility and publication status. */
  targets: TargetsEnum[]
}

export type UpdateActivitiesRooms = {
  /**
   * @format uri
   * @pattern (?:csv)$
   */
  file: string
}

export type UpdateActivitiesRoomsRequest = {
  /**
   * @format binary
   * @pattern (?:csv)$
   */
  file: File
}

/** Collects and raises validation errors in bulk */
export type UserAcademicAdminProfile = {
  id: string
  /** @format uuid */
  user?: string | null
}

/** Collects and raises validation errors in bulk */
export type UserAcademicAdminProfileRequest = {
  /** @format uuid */
  user?: string | null
}

/** Collects and raises validation errors in bulk */
export type UserAcademicHODProfile = {
  id: string
  /** @format uuid */
  user?: string | null
}

/** Collects and raises validation errors in bulk */
export type UserAcademicHODProfileRequest = {
  /** @format uuid */
  user?: string | null
}

export type UserAdditionalEmail = {
  id: number
  /** @format uuid */
  user: string
  /**
   * @format email
   * @maxLength 254
   */
  email: string
  is_billing_email: boolean
  order: number
}

export type UserAdditionalEmailRequest = {
  /**
   * @format email
   * @minLength 1
   * @maxLength 254
   */
  email: string
}

export type UserAdditionalPhoneNumber = {
  id: number
  /** @format uuid */
  user: string
  /** @maxLength 128 */
  phone_number: string
  order: number
}

export type UserAdditionalPhoneNumberRequest = {
  /**
   * @minLength 1
   * @maxLength 128
   */
  phone_number: string
}

export type UserAddress = {
  id: number
  /** @format uuid */
  user: string
  label?: string
  name?: string
  /**
   * @min 0
   * @max 32767
   */
  address_type: AddressTypeEnum
  address?: string
  address_city?: string
  address_zipcode?: string
  address_country?: string
  order: number
}

export type UserAddressRequest = {
  label?: string
  name?: string
  /**
   * @min 0
   * @max 32767
   */
  address_type: AddressTypeEnum
  address?: string
  address_city?: string
  address_zipcode?: string
  address_country?: string
}

/** Collects and raises validation errors in bulk */
export type UserAdminProfile = {
  id: string
  /** @format uuid */
  user?: string | null
}

/** Collects and raises validation errors in bulk */
export type UserAdminProfileRequest = {
  /** @format uuid */
  user?: string | null
}

/** Collects and raises validation errors in bulk */
export type UserBoardingDirectorProfile = {
  id: string
  /** @format uuid */
  user?: string | null
}

/** Collects and raises validation errors in bulk */
export type UserBoardingDirectorProfileRequest = {
  /** @format uuid */
  user?: string | null
}

/**
 * Mixin for parsing restql query from request.
 *
 * NOTE: We are using `request.GET` instead of
 * `request.query_params` because this might be
 * called before DRF request is created(i.e from dispatch).
 * This means `request.query_params` might not be available
 * when this mixin is used.
 */
export type UserContacts = {
  /** @format uuid */
  id: string
  full_name: string
  salutation: string | null
  title: string | null
  nationalities: string[]
  phone_number: string
  /**
   * Email address
   * @format email
   */
  email: string
  /** @format date-time */
  date_joined: string
  /** @format date-time */
  last_update: string
  students: SimpleUser[]
  candidates: SimpleUser[]
  user_type: UserTypeResponse
  year_of_rosenberg_entry: number | null
  app_access: AppAccessEnum | NullEnum | null
  saldo_status: SaldoStatusEnum | NullEnum | null
  current_company: Company | null
  current_company_str: string | null
  status: StatusResponse | null
  academic_details: ParentAcademicDetails | null
  inactive_note: UserNote | null
}

export type UserContactsAvailableFilters = {
  id: AvailableStringFilter
  user_type: AvailableIntegerFilter
  nationalities: AvailableStringFilter
  student: AvailableStringFilter
  has_active_students: AvailableBooleanFilter
  candidate: AvailableStringFilter
  roles: AvailableStringFilter
  current_company: AvailableStringFilter
  year_of_rosenberg_entry: AvailableIntegerFilter
  app_access: AvailableStringFilter
  saldo_status: AvailableIntegerFilter
  status: AvailableIntegerFilter
  former_student_status: AvailableStringFilter
  is_on_senior_house: AvailableBooleanFilter
  is_new_student: AvailableBooleanFilter
  career_goal: AvailableStringFilter
  gender: AvailableStringFilter
  program_type: AvailableStringFilter
  current_grade_level: AvailableIntegerFilter
  target_grade_level: AvailableIntegerFilter
}

export type UserContactsSimpleList = {
  /** @format uuid */
  id: string
  full_name: string
  contact_user_type: ContactUserTypeEnum
}

export type UserExams = {
  user: SimpleUser
  exams: ClashWithExamEC[]
}

/** Collects and raises validation errors in bulk */
export type UserHealthCoordinatorProfile = {
  id: string
  /** @format uuid */
  user?: string | null
  excluded_from_worse_condition_email?: boolean
}

/** Collects and raises validation errors in bulk */
export type UserHealthCoordinatorProfileRequest = {
  /** @format uuid */
  user?: string | null
  excluded_from_worse_condition_email?: boolean
}

/** Collects and raises validation errors in bulk */
export type UserIdpAdminProfile = {
  id: string
  /** @format uuid */
  user?: string | null
}

/** Collects and raises validation errors in bulk */
export type UserIdpAdminProfileRequest = {
  /** @format uuid */
  user?: string | null
}

export type UserInCourse = {
  /** @format uuid */
  id: string
  name: string
  /** @format uri */
  avatar?: string | null
}

export type UserLessons = {
  user: SimpleUser
  lessons: LessonClash[]
}

export type UserNote = {
  id: number
  /** @format uuid */
  user: string
  /** @maxLength 255 */
  title?: string
  /** @maxLength 2000 */
  note: string
  /**
   * * `academic` - Academic
   * * `administrative` - Administrative
   * * `admission` - Admission
   * * `archived` - Archived
   * * `boarding` - Boarding
   * * `medical` - Medical
   * * `other` - Other
   */
  note_type: NoteTypeEnum
  access_profiles_override?: string[] | null
  effective_access_profiles: UserNoteAccessProfile[]
  is_access_overridden: boolean
  has_access: boolean
  is_type_locked?: boolean
  is_trello_comment: boolean
  is_trello_closed: boolean
  order: number
  added_by_str: string
  last_modified_by_str: string | null
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
}

export type UserNoteAccessProfile = {
  value: string
  label: string
}

export type UserNoteAvailableFilters = {
  note_type: AvailableStringFilter
  added_by: AvailableStringFilter
  school_year: AvailableIntegerFilter
  is_trello_comment: AvailableBooleanFilter
}

export type UserNoteRequest = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  title: string
  /**
   * @minLength 1
   * @maxLength 2000
   */
  note: string
  /**
   * * `academic` - Academic
   * * `administrative` - Administrative
   * * `admission` - Admission
   * * `archived` - Archived
   * * `boarding` - Boarding
   * * `medical` - Medical
   * * `other` - Other
   */
  note_type: NoteTypeEnum
  access_profiles_override?: string[] | null
  is_type_locked?: boolean
  added_by?: string
  last_modified_by?: string
}

export type UserNoteTypeAccess = {
  value: string
  label: string
  can_select: boolean
  default_access_profiles: UserNoteAccessProfile[]
}

export type UserNoteTypeAccessMetadata = {
  note_types: UserNoteTypeAccess[]
  profile_options: UserNoteAccessProfile[]
}

export type UserPhoneNumberRequest = {
  /** @minLength 1 */
  phone_number: string
  /** @format date */
  date_of_birth: string
}

export type UserRelationCreateRequest = {
  /** @format uuid */
  user: string
  /** @format uuid */
  related_user: string
  /**
   * @min 0
   * @max 32767
   */
  relation_type: RelationTypeEnum
  /** Roles for this relation (synced with ParentStudentConnection) */
  roles?: ContactTypeEnum[] | null
  has_app_access?: boolean
}

export type UserRelationList = {
  id: number
  contact_data: ContactData
  /** Roles for this relation (synced with ParentStudentConnection) */
  roles: ContactTypeEnum[] | null
  relation_type: RelationTypeEnum | NullEnum | null
}

export type UserRelationUpdate = {
  id: number
  /**
   * @min 0
   * @max 32767
   */
  relation_type: RelationTypeEnum
  /** Roles for this relation (synced with ParentStudentConnection) */
  roles?: ContactTypeEnum[] | null
  has_app_access?: boolean
}

export type UserRelationUpdateRequest = {
  /**
   * @min 0
   * @max 32767
   */
  relation_type: RelationTypeEnum
  /** Roles for this relation (synced with ParentStudentConnection) */
  roles?: ContactTypeEnum[] | null
  has_app_access?: boolean
}

export type UserSchoolProgrammes = {
  user: SimpleUser
  school_programmes: SchoolProgrammeClash[]
}

export type UserTaskTitle = {
  id: string
  title: string
  added_by?: string
  last_modified_by?: string
}

export type UserTaskTitleRequest = {
  /** @minLength 1 */
  title: string
  added_by?: string
  last_modified_by?: string
}

/** Collects and raises validation errors in bulk */
export type UserTeacherProfile = {
  id: string
  /** @maxLength 16 */
  acronym: string
  departments?: number[]
  preferred_room?: number | null
  preferred_rooms_str: string[]
  departments_str: string[]
  /** Teacher hidden in timetable generator */
  is_hidden_in_generator?: boolean
  preferred_rooms?: number[]
  custom_schedule?: any
  is_mentor?: boolean
  mentor_for_grades?: number[]
  mentor_for_grades_str: string[]
  /** @format uuid */
  user?: string | null
}

/** Collects and raises validation errors in bulk */
export type UserTeacherProfileRequest = {
  /**
   * @minLength 1
   * @maxLength 16
   */
  acronym: string
  departments?: number[]
  preferred_room?: number | null
  /** Teacher hidden in timetable generator */
  is_hidden_in_generator?: boolean
  preferred_rooms?: number[]
  custom_schedule?: any
  is_mentor?: boolean
  mentor_for_grades?: number[]
  /** @format uuid */
  user?: string | null
}

export type UserTimetable =
  | TimetableCampusAbsence
  | TimetableDoctorAppointment
  | TimetableExam
  | TimetableLeaveRequest
  | TimetableLesson
  | TimetableSchoolBreak
  | TimetableSchoolProgramme
  | TimetableRotaActivity
  | TimetableMeritReward

export type UserTypeEnum = 1 | 2 | 3

export type UserTypeResponse = {
  /**
   * * `1` - Contact
   * * `2` - Candidate
   * * `3` - Student
   */
  value: UserTypeResponseValueEnum
  label: string
}

/**
 * * `1` - Contact
 * * `2` - Candidate
 * * `3` - Student
 */
export type UserTypeResponseValueEnum = 1 | 2 | 3

export type UsersClashesForDateParamsRequest = {
  user_ids: string[]
  /** @format date-time */
  date_from: string
  /** @format date-time */
  date_to: string
  /** @minLength 1 */
  exclude_lesson_id?: string | null
  /** @minLength 1 */
  exclude_exam_id?: string | null
  /** @minLength 1 */
  exclude_programme_id?: string | null
}

export type ValidateCodeRequest = {
  code: number
}

export type ValidateCodeV2Request = {
  /** @format uuid */
  challenge_id: string
  code: number
}

export type ValidateLoginLinkRequest = {
  /** @minLength 1 */
  token: string
}

export type ValidateLoginLinkV2Request = {
  /** @format uuid */
  challenge_id: string
  /** @minLength 1 */
  token: string
}

/**
 * * `green_paper` - Green paper
 * * `pink_paper` - Pink paper
 * * `breaks` - Breaks
 * * `prompt` - Prompt
 * * `use_laptop` - Use of laptop
 * * `reader_pen` - Reader
 * * `own_room` - Own room
 * * `scribe` - Scribe
 */
export type Value6A8Enum =
  | 'green_paper'
  | 'pink_paper'
  | 'breaks'
  | 'prompt'
  | 'use_laptop'
  | 'reader_pen'
  | 'own_room'
  | 'scribe'

/**
 * * `wreath` - Wreath
 * * `cap` - Cap
 */
export type VariantEnum = 'wreath' | 'cap'

export type Video = {
  id: string
  original_filename: string
  status: VideoStatusEnum
  status_version: number
  error_code: VideoErrorCodeEnum | NullEnum | null
  file_url: string | null
  /** @format double */
  duration: number | null
  width: number | null
  height: number | null
  file_size: number | null
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @format date-time */
  processing_started_at: string | null
  /** @format date-time */
  processing_finished_at: string | null
}

/**
 * * `invalid_media` - Invalid or corrupt video
 * * `source_limit_exceeded` - Video exceeds source limits
 * * `unsupported_color` - Unsupported video color format
 * * `conversion_failed` - Video conversion failed
 * * `processing_timeout` - Video processing timed out
 * * `infrastructure_error` - Video processing unavailable
 */
export type VideoErrorCodeEnum =
  | 'invalid_media'
  | 'source_limit_exceeded'
  | 'unsupported_color'
  | 'conversion_failed'
  | 'processing_timeout'
  | 'infrastructure_error'

/**
 * * `pending` - Pending
 * * `processing` - Processing
 * * `ready` - Ready
 * * `failed` - Failed
 */
export type VideoStatusEnum = 'pending' | 'processing' | 'ready' | 'failed'

export type VideoUploadRequest = {
  /**
   * @format binary
   * @pattern (?:mp4)$
   */
  file: File
}

/** * `new_laundry_form` - new_laundry_form */
export type ViewTypeEnum = 'new_laundry_form'

/** Collects and raises validation errors in bulk */
export type ViewerProfile = {
  id: string
  /** @format uuid */
  user?: string | null
}

/** Collects and raises validation errors in bulk */
export type ViewerProfileRequest = {
  /** @format uuid */
  user?: string | null
}

/**
 * * `shortness of breath` - Shortness of Breath
 * * `wheezing` - Wheezing
 * * `anxiety` - Anxiety
 * * `confusion` - Confusion
 * * `sudden or severe tiredness` - Sudden or Severe Tiredness
 * * `exhaustion` - Exhaustion
 * * `pale skin` - Pale Skin
 * * `skin rash` - Skin Rash
 * * `skin hive` - Skin Hive
 * * `nausea` - Nausea
 * * `faintness` - Faintness
 * * `inability to concentrate` - Inability to Concentrate
 * * `heart palpitations` - Heart Palpitations
 * * `seizure` - Seizure
 * * `unusual behaviour` - Unusual Behaviour
 * * `sensations` - Sensations
 * * `compulsive bahviour` - Compulsive Behaviour
 * * `obsessive behaviour` - Obsessive Behaviour
 * * `headaches` - Headaches
 * * `infection` - Infection
 * * `body ache or pain` - Body Ache or Pain
 * * `sadness` - Sadness
 * * `depression` - Depression
 */
export type WatchOutForEnum =
  | 'shortness of breath'
  | 'wheezing'
  | 'anxiety'
  | 'confusion'
  | 'sudden or severe tiredness'
  | 'exhaustion'
  | 'pale skin'
  | 'skin rash'
  | 'skin hive'
  | 'nausea'
  | 'faintness'
  | 'inability to concentrate'
  | 'heart palpitations'
  | 'seizure'
  | 'unusual behaviour'
  | 'sensations'
  | 'compulsive bahviour'
  | 'obsessive behaviour'
  | 'headaches'
  | 'infection'
  | 'body ache or pain'
  | 'sadness'
  | 'depression'

export type WebsocketAuthTokenResponse = {
  token: string
}

export type Week = {
  name: string
  short_name: string
  /** @format date */
  start_date: string
  /** @format date */
  end_date: string
}

/**
 * * `4` - 4
 * * `3` - 3
 * * `2` - 2
 * * `1` - 1
 * * `0` - 0
 */
export type WeekAgoCategoryEnum = 4 | 3 | 2 | 1 | 0

export type WeekDay = {
  /**
   * * `0` - 0
   * * `1` - 1
   * * `2` - 2
   * * `3` - 3
   * * `4` - 4
   * * `5` - 5
   * * `6` - 6
   */
  value: WeekDayValueEnum
  /**
   * * `Monday` - Monday
   * * `Tuesday` - Tuesday
   * * `Wednesday` - Wednesday
   * * `Thursday` - Thursday
   * * `Friday` - Friday
   * * `Saturday` - Saturday
   * * `Sunday` - Sunday
   */
  label: WeekDayLabelEnum
}

/**
 * * `0` - Monday
 * * `1` - Tuesday
 * * `2` - Wednesday
 * * `3` - Thursday
 * * `4` - Friday
 */
export type WeekDayEnum = 0 | 1 | 2 | 3 | 4

/**
 * * `Monday` - Monday
 * * `Tuesday` - Tuesday
 * * `Wednesday` - Wednesday
 * * `Thursday` - Thursday
 * * `Friday` - Friday
 * * `Saturday` - Saturday
 * * `Sunday` - Sunday
 */
export type WeekDayLabelEnum =
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday'
  | 'Sunday'

/**
 * * `0` - 0
 * * `1` - 1
 * * `2` - 2
 * * `3` - 3
 * * `4` - 4
 * * `5` - 5
 * * `6` - 6
 */
export type WeekDayValueEnum = 0 | 1 | 2 | 3 | 4 | 5 | 6

/**
 * * `W1` - Week 1 2024
 * * `W2` - Week 2 2024
 * * `W3` - Week 3 2024
 * * `W4` - Week 4 2024
 * * `W1_2025` - Week 1 2025
 * * `W2_2025` - Week 2 2025
 * * `W3_2025` - Week 3 2025
 * * `W4_2025` - Week 4 2025
 * * `W1_2026` - Week 1 2026
 * * `W2_2026` - Week 2 2026
 * * `W3_2026` - Week 3 2026
 * * `W4_2026` - Week 4 2026
 * * `W1_2027` - Week 1 2027
 * * `W2_2027` - Week 2 2027
 * * `W3_2027` - Week 3 2027
 * * `W4_2027` - Week 4 2027
 */
export type WeekEnum =
  | 'W1'
  | 'W2'
  | 'W3'
  | 'W4'
  | 'W1_2025'
  | 'W2_2025'
  | 'W3_2025'
  | 'W4_2025'
  | 'W1_2026'
  | 'W2_2026'
  | 'W3_2026'
  | 'W4_2026'
  | 'W1_2027'
  | 'W2_2027'
  | 'W3_2027'
  | 'W4_2027'

export type WeekResults = {
  results: Week[]
  current_period?: Week | null
}

export type WeeklyComment = {
  id: number
  author: string
  last_modified_by?: string
  /** @format date-time */
  created: string
  /** @format date-time */
  modified: string
  /** @maxLength 2000 */
  content: string
  /** @format uuid */
  course: string
  /** @format uuid */
  student: string
}

export type WeeklyCommentRequest = {
  last_modified_by?: string
  /**
   * @minLength 1
   * @maxLength 2000
   */
  content: string
  /** @format uuid */
  course: string
  /** @format uuid */
  student: string
}

/**
 * * `BoardingAnnouncementWidget` - BoardingAnnouncementWidget
 * * `CelebrationWidget` - CelebrationWidget
 * * `EventAgendaWidget` - EventAgendaWidget
 * * `CongratulationsWidget` - CongratulationsWidget
 * * `PosterWidget` - PosterWidget
 * * `StudentArtGalleryWidget` - StudentArtGalleryWidget
 * * `EventInfoWidget` - EventInfoWidget
 * * `EventInfoWithPhotoWidget` - EventInfoWithPhotoWidget
 * * `InfoWithVideoWidget` - InfoWithVideoWidget
 * * `JournalWidget` - JournalWidget
 * * `MysteryLinkWidget` - MysteryLinkWidget
 * * `MenuWidget` - MenuWidget
 * * `StudentsAnnouncementWidget` - StudentsAnnouncementWidget
 * * `StudentsPicWidget` - StudentsPicWidget
 * * `VariantManualCongratulationsWidget` - VariantManualCongratulationsWidget
 * * `ClockWidget` - ClockWidget
 */
export type WidgetResourcetypeEnum =
  | 'BoardingAnnouncementWidget'
  | 'CelebrationWidget'
  | 'EventAgendaWidget'
  | 'CongratulationsWidget'
  | 'PosterWidget'
  | 'StudentArtGalleryWidget'
  | 'EventInfoWidget'
  | 'EventInfoWithPhotoWidget'
  | 'InfoWithVideoWidget'
  | 'JournalWidget'
  | 'MysteryLinkWidget'
  | 'MenuWidget'
  | 'StudentsAnnouncementWidget'
  | 'StudentsPicWidget'
  | 'VariantManualCongratulationsWidget'
  | 'ClockWidget'

/**
 * * `unpublished` - Unpublished
 * * `published` - Published
 * * `scheduled` - Scheduled
 * * `draft` - Draft
 */
export type WidgetStatusEnum =
  | 'unpublished'
  | 'published'
  | 'scheduled'
  | 'draft'

/** * `exam` - exam */
export type Type4CaEnum = 'exam'

import type { AxiosInstance, AxiosRequestConfig, ResponseType } from 'axios'
import axios from 'axios'

export type QueryParamsType = Record<string | number, any>

export interface FullRequestParams
  extends Omit<AxiosRequestConfig, 'data' | 'params' | 'url' | 'responseType'> {
  /** set parameter to `true` for call `securityWorker` for this request */
  secure?: boolean
  /** request path */
  path: string
  /** content type of request body */
  type?: ContentType
  /** query params */
  query?: QueryParamsType
  /** format of response (i.e. response.json() -> format: "json") */
  format?: ResponseType
  /** request body */
  body?: unknown
}

export type RequestParams = Omit<
  FullRequestParams,
  'body' | 'method' | 'query' | 'path'
>

export interface ApiConfig<SecurityDataType = unknown>
  extends Omit<AxiosRequestConfig, 'data' | 'cancelToken'> {
  securityWorker?: (
    securityData: SecurityDataType | null
  ) => Promise<AxiosRequestConfig | void> | AxiosRequestConfig | void
  secure?: boolean
  format?: ResponseType
}

export enum ContentType {
  Json = 'application/json',
  FormData = 'multipart/form-data',
  UrlEncoded = 'application/x-www-form-urlencoded',
  Text = 'text/plain'
}

export class HttpClient<SecurityDataType = unknown> {
  public instance: AxiosInstance
  private securityData: SecurityDataType | null = null
  private securityWorker?: ApiConfig<SecurityDataType>['securityWorker']
  private secure?: boolean
  private format?: ResponseType

  constructor({
    securityWorker,
    secure,
    format,
    ...axiosConfig
  }: ApiConfig<SecurityDataType> = {}) {
    this.instance = axios.create({
      ...axiosConfig,
      baseURL: axiosConfig.baseURL || ''
    })
    this.secure = secure
    this.format = format
    this.securityWorker = securityWorker
  }

  public setSecurityData = (data: SecurityDataType | null) => {
    this.securityData = data
  }

  protected mergeRequestParams(
    params1: AxiosRequestConfig,
    params2?: AxiosRequestConfig
  ): AxiosRequestConfig {
    return {
      ...this.instance.defaults,
      ...params1,
      ...(params2 || {}),
      headers: {
        ...(params1.headers || {}),
        ...((params2 && params2.headers) || {})
      }
    }
  }

  protected stringifyFormItem(formItem: unknown) {
    if (typeof formItem === 'object' && formItem !== null) {
      return JSON.stringify(formItem)
    } else {
      return `${formItem}`
    }
  }

  protected createFormData(input: Record<string, unknown>): FormData {
    return Object.keys(input || {}).reduce((formData, key) => {
      const property = input[key]
      const propertyContent: any[] =
        property instanceof Array ? property : [property]

      for (const formItem of propertyContent) {
        const isFileType = formItem instanceof Blob || formItem instanceof File
        formData.append(
          key,
          isFileType ? formItem : this.stringifyFormItem(formItem)
        )
      }

      return formData
    }, new FormData())
  }

  public request = async <T = any, _E = any>({
    secure,
    path,
    type,
    query,
    format,
    body,
    ...params
  }: FullRequestParams): Promise<T> => {
    const secureParams =
      ((typeof secure === 'boolean' ? secure : this.secure) &&
        this.securityWorker &&
        (await this.securityWorker(this.securityData))) ||
      {}
    const requestParams = this.mergeRequestParams(params, secureParams)
    const responseFormat = format || this.format || undefined

    if (
      type === ContentType.FormData &&
      body &&
      body !== null &&
      typeof body === 'object'
    ) {
      body = this.createFormData(body as Record<string, unknown>)
    }

    if (
      type === ContentType.Text &&
      body &&
      body !== null &&
      typeof body !== 'string'
    ) {
      body = JSON.stringify(body)
    }

    return this.instance
      .request({
        ...requestParams,
        headers: {
          ...(requestParams.headers || {}),
          ...(type && type !== ContentType.FormData
            ? { 'Content-Type': type }
            : {})
        },
        params: query,
        responseType: responseFormat,
        data: body,
        url: path
      })
      .then(response => response.data)
  }
}

type Flatten<T> = T extends any[] ? T[number] : T
type ChangeTypeOfKeys<T extends object, Keys extends keyof T, NewType> = {
  [key in keyof T]: key extends Keys ? NewType : T[key]
}
type MapObjectKeysToTrue<ObjectType extends Record<PropertyKey, any>> = {
  [key in keyof ObjectType]?: ObjectType[key] extends Record<PropertyKey, any>
    ? true | MapObjectKeysToTrue<Flatten<ObjectType[key]>>
    : true
}
type FetchKeysType = { [key in PropertyKey]: true | FetchKeysType }
type FetchKeys<ResponseModel> = ResponseModel extends {
  count?: number
  results?: Array<infer DataModel>
}
  ? DataModel extends Record<PropertyKey, any>
    ? MapObjectKeysToTrue<DataModel>
    : DataModel
  : ResponseModel extends Record<PropertyKey, any>
  ? MapObjectKeysToTrue<Flatten<ResponseModel>>
  : ResponseModel

type NestedSafePick<ResponseModel, FetchKeysType> = ResponseModel extends Array<
  infer Model
>
  ? NestedSafePick<Model, FetchKeysType>[]
  : {
      [key in keyof FetchKeysType]: FetchKeysType[key] extends Record<
        PropertyKey,
        any
      >
        ? key extends keyof ResponseModel
          ? ResponseModel[key] extends Array<infer DataModel>
            ? NestedSafePick<DataModel, FetchKeysType[key]>[]
            : NestedSafePick<ResponseModel[key], FetchKeysType[key]>
          : never
        : key extends keyof ResponseModel
        ? ResponseModel[key] extends Array<infer DataModel>
          ? DataModel[]
          : ResponseModel[key]
        : never
    }

type PickKeys<ResponseModel, FetchKeysType> = {} extends FetchKeysType
  ? ResponseModel
  : ResponseModel extends {
      count?: number
      results?: Array<infer DataModel>
    }
  ? ChangeTypeOfKeys<
      ResponseModel,
      'results',
      NestedSafePick<DataModel, FetchKeysType>[]
    >
  : ResponseModel extends Array<infer DataModel>
  ? NestedSafePick<DataModel, FetchKeysType>[]
  : NestedSafePick<ResponseModel, FetchKeysType>

function postprocessQuery(query?: any) {
  if (!query) return query

  const parsedQuery = query
  if (query.fetchKeys) {
    function getStringifiedQueryKeys(keys: FetchKeysType) {
      let query = '{'
      Object.entries(keys).forEach(([key, value], index) => {
        if (typeof value === 'object') {
          const keyToAdd = `${key}${getStringifiedQueryKeys(value)}`
          query += index > 0 ? `,${keyToAdd}` : keyToAdd
        } else {
          query += index > 0 ? `,${key}` : key
        }
      })
      return query + '}'
    }

    parsedQuery.query = getStringifiedQueryKeys(query.fetchKeys)
  }

  delete parsedQuery.fetchKeys
  return parsedQuery
}

/**
 * @title API* @version 1.0.0 (v1)*/
export class Api<
  SecurityDataType extends unknown
> extends HttpClient<SecurityDataType> {
  achievements = {
    /**
     * No description
     *
     * @tags achievements
     * @name AchievementsList
     * @request GET:/achievements/
     * @secure
     */
    achievementsList: <T extends FetchKeys<PaginatedAchievementList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAchievementList, T>, any>({
        path: `/achievements/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags achievements
     * @name AchievementsRetrieve
     * @request GET:/achievements/{id}/
     * @secure
     */,
    achievementsRetrieve: <T extends FetchKeys<Achievement>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Achievement, T>, any>({
        path: `/achievements/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags achievements
     * @name AchievementsStudentsList
     * @request GET:/achievements/students/
     * @secure
     */,
    achievementsStudentsList: <
      T extends FetchKeys<PaginatedAchievementHolderList>
    >(
      query?: {
        achievement?: number
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAchievementHolderList, T>, any>({
        path: `/achievements/students/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags achievements
     * @name AchievementsStudentsList2
     * @request GET:/achievements/students/{studentId}/
     * @secure
     */,
    achievementsStudentsList2: <
      T extends FetchKeys<PaginatedStudentAchievementList>
    >(
      studentId: string,
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentAchievementList, T>, any>({
        path: `/achievements/students/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags achievements
     * @name AchievementsStudentsUpdate
     * @request PUT:/achievements/students/{studentId}/
     * @secure
     */,
    achievementsStudentsUpdate: <T extends FetchKeys<StudentAchievement>>(
      studentId: string,
      data: StudentAchievementRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentAchievement, T>, any>({
        path: `/achievements/students/${studentId}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags achievements
     * @name AchievementsStudentsPartialUpdate
     * @request PATCH:/achievements/students/{studentId}/
     * @secure
     */,
    achievementsStudentsPartialUpdate: <
      T extends FetchKeys<StudentAchievement>
    >(
      studentId: string,
      data: PatchedStudentAchievementRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentAchievement, T>, any>({
        path: `/achievements/students/${studentId}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
  }
  activities = {
    /**
     * @description Returns a CSV file with enrollments data.
     *
     * @tags activities
     * @name ActivitiesEnrollmentsRetrieve
     * @request GET:/activities/enrollments/
     * @secure
     */
    activitiesEnrollmentsRetrieve: <T extends FetchKeys<string>>(
      query: {
        /** @format date */
        from_date: string
        /** @format date */
        to_date: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<string, T>, any>({
        path: `/activities/enrollments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
  }
  adminTimetable = {
    /**
     * @description [WARNING] AdminTimetableViewSet inherits from this!
     *
     * @tags admin-timetable
     * @name AdminTimetableList
     * @request GET:/admin-timetable/
     * @secure
     */
    adminTimetableList: <T extends FetchKeys<AdminTimetable[]>>(
      query?: {
        /** @format date */
        date?: string
        students?: string[]
        teachers?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminTimetable[], T>, any>({
        path: `/admin-timetable/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description [WARNING] AdminTimetableViewSet inherits from this!
     *
     * @tags admin-timetable
     * @name AdminTimetablePdfRetrieve
     * @request GET:/admin-timetable/pdf/
     * @secure
     */,
    adminTimetablePdfRetrieve: <T extends FetchKeys<File>>(
      query: {
        /** @format date */
        date_after: string
        /** @format date */
        date_before: string
        /** @format uuid */
        students?: string
        teachers?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<File, T>, any>({
        path: `/admin-timetable/pdf/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
  }
  admin = {
    /**
     * No description
     *
     * @tags admin
     * @name AdminActivitiesLessonList
     * @request GET:/admin/activities/lesson/
     * @secure
     */
    adminActivitiesLessonList: <
      T extends FetchKeys<PaginatedAdminTimetableLessonList>
    >(
      query?: {
        /** @format uuid */
        activity__course?: string
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        during_exam_sessions?: string[]
        during_exams?: string[]
        generation_id?: string
        hide_past_lessons?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** @format uuid */
        student?: string
        students?: string[]
        /** @format uuid */
        teacher?: string
        /** @format uuid */
        teacher_cover?: string
        teacher_cover__isnull?: boolean
        /** @format uuid */
        teacher_covered?: string
        teachers?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAdminTimetableLessonList, T>, any>({
        path: `/admin/activities/lesson/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminActivitiesLessonRetrieve
     * @request GET:/admin/activities/lesson/{id}/
     * @secure
     */,
    adminActivitiesLessonRetrieve: <T extends FetchKeys<AdminTimetableLesson>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminTimetableLesson, T>, any>({
        path: `/admin/activities/lesson/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminActivitiesLessonUpdate
     * @request PUT:/admin/activities/lesson/{id}/
     * @secure
     */,
    adminActivitiesLessonUpdate: <
      T extends FetchKeys<AdminTimetableLessonUpdate>
    >(
      id: string,
      data: AdminTimetableLessonUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminTimetableLessonUpdate, T>, any>({
        path: `/admin/activities/lesson/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminActivitiesLessonPartialUpdate
     * @request PATCH:/admin/activities/lesson/{id}/
     * @secure
     */,
    adminActivitiesLessonPartialUpdate: <
      T extends FetchKeys<AdminTimetableLessonUpdate>
    >(
      id: string,
      data: PatchedAdminTimetableLessonUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminTimetableLessonUpdate, T>, any>({
        path: `/admin/activities/lesson/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminActivitiesLessonAssignRoomUpdate
     * @request PUT:/admin/activities/lesson/{id}/assign-room/
     * @secure
     */,
    adminActivitiesLessonAssignRoomUpdate: <
      T extends FetchKeys<AdminTimetableLessonRoomAssignment>
    >(
      id: string,
      data: AdminTimetableLessonRoomAssignmentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminTimetableLessonRoomAssignment, T>, any>({
        path: `/admin/activities/lesson/${id}/assign-room/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminActivitiesLessonSplitLessonCreate
     * @request POST:/admin/activities/lesson/{id}/split-lesson/
     * @secure
     */,
    adminActivitiesLessonSplitLessonCreate: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/admin/activities/lesson/${id}/split-lesson/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags admin
     * @name AdminAttendancesAbsencesList
     * @request GET:/admin/attendances/absences/
     * @secure
     */,
    adminAttendancesAbsencesList: <T extends FetchKeys<PaginatedAbsenceList>>(
      query?: {
        /**
         * * `late` - Late
         * * `absent_excused` - Absent excused
         * * `absent_unexcused` - Absent unexcused
         * * `sick` - Sick
         */
        absence_type?: 'absent_excused' | 'absent_unexcused' | 'late' | 'sick'
        /** @format date */
        end_date?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        school_year?: string
        /** @format date */
        start_date?: string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAbsenceList, T>, any>({
        path: `/admin/attendances/absences/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags admin
     * @name AdminAttendancesAbsencesCreate
     * @request POST:/admin/attendances/absences/
     * @secure
     */,
    adminAttendancesAbsencesCreate: <T extends FetchKeys<Absence>>(
      data: AbsenceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Absence, T>, any>({
        path: `/admin/attendances/absences/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags admin
     * @name AdminAttendancesAbsencesRetrieve
     * @request GET:/admin/attendances/absences/{id}/
     * @secure
     */,
    adminAttendancesAbsencesRetrieve: <T extends FetchKeys<Absence>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Absence, T>, any>({
        path: `/admin/attendances/absences/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags admin
     * @name AdminAttendancesAbsencesUpdate
     * @request PUT:/admin/attendances/absences/{id}/
     * @secure
     */,
    adminAttendancesAbsencesUpdate: <T extends FetchKeys<AbsenceUpdate>>(
      id: string,
      data: AbsenceUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AbsenceUpdate, T>, any>({
        path: `/admin/attendances/absences/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags admin
     * @name AdminAttendancesAbsencesPartialUpdate
     * @request PATCH:/admin/attendances/absences/{id}/
     * @secure
     */,
    adminAttendancesAbsencesPartialUpdate: <T extends FetchKeys<AbsenceUpdate>>(
      id: string,
      data: PatchedAbsenceUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AbsenceUpdate, T>, any>({
        path: `/admin/attendances/absences/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags admin
     * @name AdminAttendancesAbsencesDestroy
     * @request DELETE:/admin/attendances/absences/{id}/
     * @secure
     */,
    adminAttendancesAbsencesDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/admin/attendances/absences/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags admin
     * @name AdminAttendancesAbsencesAvailableFiltersRetrieve
     * @request GET:/admin/attendances/absences/available-filters/
     * @secure
     */,
    adminAttendancesAbsencesAvailableFiltersRetrieve: <
      T extends FetchKeys<AbsenceAvailableFiltersResponse>
    >(
      query?: {
        /**
         * * `late` - Late
         * * `absent_excused` - Absent excused
         * * `absent_unexcused` - Absent unexcused
         * * `sick` - Sick
         */
        absence_type?: 'absent_excused' | 'absent_unexcused' | 'late' | 'sick'
        /** @format date */
        end_date?: string
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        /** Which field to use when ordering the results. */
        ordering?: string
        school_year?: string
        /** @format date */
        start_date?: string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AbsenceAvailableFiltersResponse, T>, any>({
        path: `/admin/attendances/absences/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags admin
     * @name AdminAttendancesAlertsList
     * @request GET:/admin/attendances/alerts/
     * @secure
     */,
    adminAttendancesAlertsList: <
      T extends FetchKeys<PaginatedAcademicAttendanceAlertList>
    >(
      query?: {
        /** @format uuid */
        course?: string
        is_notification_sent?: boolean
        is_read?: boolean
        /** @format date */
        lesson_date?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /**
         * * `unresolved` - Unresolved
         * * `resolved` - Resolved
         */
        status?: 'resolved' | 'unresolved'
        /** @format uuid */
        teacher?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAcademicAttendanceAlertList, T>, any>({
        path: `/admin/attendances/alerts/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags admin
     * @name AdminAttendancesAlertsRetrieve
     * @request GET:/admin/attendances/alerts/{id}/
     * @secure
     */,
    adminAttendancesAlertsRetrieve: <
      T extends FetchKeys<AcademicAttendanceAlert>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AcademicAttendanceAlert, T>, any>({
        path: `/admin/attendances/alerts/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags admin
     * @name AdminAttendancesAlertsMarkAsReadCreate
     * @request POST:/admin/attendances/alerts/{id}/mark-as-read/
     * @secure
     */,
    adminAttendancesAlertsMarkAsReadCreate: <
      T extends FetchKeys<AcademicAttendanceAlert>
    >(
      id: string,
      data: AcademicAttendanceAlertRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AcademicAttendanceAlert, T>, any>({
        path: `/admin/attendances/alerts/${id}/mark-as-read/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags admin
     * @name AdminAttendancesAlertsSendNotificationCreate
     * @request POST:/admin/attendances/alerts/{id}/send-notification/
     * @secure
     */,
    adminAttendancesAlertsSendNotificationCreate: <
      T extends FetchKeys<AcademicAttendanceAlert>
    >(
      id: string,
      data: AcademicAttendanceAlertRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AcademicAttendanceAlert, T>, any>({
        path: `/admin/attendances/alerts/${id}/send-notification/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags admin
     * @name AdminAttendancesAlertsAvailableFiltersRetrieve
     * @request GET:/admin/attendances/alerts/available-filters/
     * @secure
     */,
    adminAttendancesAlertsAvailableFiltersRetrieve: <
      T extends FetchKeys<AttendanceAlertsAvailableFiltersResponse>
    >(
      query?: {
        /** @format uuid */
        course?: string
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        is_notification_sent?: boolean
        is_read?: boolean
        /** @format date */
        lesson_date?: string
        /**
         * * `unresolved` - Unresolved
         * * `resolved` - Resolved
         */
        status?: 'resolved' | 'unresolved'
        /** @format uuid */
        teacher?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AttendanceAlertsAvailableFiltersResponse, T>, any>({
        path: `/admin/attendances/alerts/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminCoreCalendarList
     * @request GET:/admin/core/calendar/
     * @secure
     */,
    adminCoreCalendarList: <T extends FetchKeys<PaginatedCalendarEventList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCalendarEventList, T>, any>({
        path: `/admin/core/calendar/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminCoreCalendarCreate
     * @request POST:/admin/core/calendar/
     * @secure
     */,
    adminCoreCalendarCreate: <T extends FetchKeys<CalendarEvent>>(
      data: CalendarEventRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CalendarEvent, T>, any>({
        path: `/admin/core/calendar/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminCoreCalendarRetrieve
     * @request GET:/admin/core/calendar/{id}/
     * @secure
     */,
    adminCoreCalendarRetrieve: <T extends FetchKeys<CalendarEvent>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CalendarEvent, T>, any>({
        path: `/admin/core/calendar/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminCoreCalendarUpdate
     * @request PUT:/admin/core/calendar/{id}/
     * @secure
     */,
    adminCoreCalendarUpdate: <T extends FetchKeys<CalendarEvent>>(
      id: number,
      data: CalendarEventRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CalendarEvent, T>, any>({
        path: `/admin/core/calendar/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminCoreCalendarPartialUpdate
     * @request PATCH:/admin/core/calendar/{id}/
     * @secure
     */,
    adminCoreCalendarPartialUpdate: <T extends FetchKeys<CalendarEvent>>(
      id: number,
      data: PatchedCalendarEventRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CalendarEvent, T>, any>({
        path: `/admin/core/calendar/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminCoreCalendarDestroy
     * @request DELETE:/admin/core/calendar/{id}/
     * @secure
     */,
    adminCoreCalendarDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/admin/core/calendar/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminCoreCalendarTypesRetrieve
     * @request GET:/admin/core/calendar/types/
     * @secure
     */,
    adminCoreCalendarTypesRetrieve: <T extends FetchKeys<CalendarEvent>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CalendarEvent, T>, any>({
        path: `/admin/core/calendar/types/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminCoreSettingsRetrieve
     * @request GET:/admin/core/settings/
     * @secure
     */,
    adminCoreSettingsRetrieve: <T extends FetchKeys<AdminSiteSettings>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminSiteSettings, T>, any>({
        path: `/admin/core/settings/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminCoreSettingsUpdate
     * @request PUT:/admin/core/settings/
     * @secure
     */,
    adminCoreSettingsUpdate: <T extends FetchKeys<AdminSiteSettings>>(
      data: AdminSiteSettingsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminSiteSettings, T>, any>({
        path: `/admin/core/settings/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminCoreSettingsPartialUpdate
     * @request PATCH:/admin/core/settings/
     * @secure
     */,
    adminCoreSettingsPartialUpdate: <T extends FetchKeys<AdminSiteSettings>>(
      data: PatchedAdminSiteSettingsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminSiteSettings, T>, any>({
        path: `/admin/core/settings/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminDashboardContentCountersRetrieve
     * @request GET:/admin/dashboard/content-counters/
     * @secure
     */,
    adminDashboardContentCountersRetrieve: <T extends FetchKeys<void>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/admin/dashboard/content-counters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminDashboardUserRetrieve
     * @request GET:/admin/dashboard/user/
     * @secure
     */,
    adminDashboardUserRetrieve: <T extends FetchKeys<AdminDashboardUser>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminDashboardUser, T>, any>({
        path: `/admin/dashboard/user/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminImportingCeleryTaskStatusList
     * @request GET:/admin/importing/celery-task-status/
     * @secure
     */,
    adminImportingCeleryTaskStatusList: <
      T extends FetchKeys<PaginatedAdminCeleryTaskStatusListList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /**
         * * `PENDING` - PENDING
         * * `RECEIVED` - RECEIVED
         * * `STARTED` - STARTED
         * * `PROGRESS` - PROGRESS
         * * `FINALIZE` - FINALIZE
         * * `SUCCESS` - SUCCESS
         * * `FAILURE` - FAILURE
         * * `REVOKED` - REVOKED
         * * `REJECTED` - REJECTED
         * * `RETRY` - RETRY
         * * `IGNORED` - IGNORED
         */
        state?:
          | 'FAILURE'
          | 'FINALIZE'
          | 'IGNORED'
          | 'PENDING'
          | 'PROGRESS'
          | 'RECEIVED'
          | 'REJECTED'
          | 'RETRY'
          | 'REVOKED'
          | 'STARTED'
          | 'SUCCESS'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAdminCeleryTaskStatusListList, T>, any>({
        path: `/admin/importing/celery-task-status/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminImportingCeleryTaskStatusRetrieve
     * @request GET:/admin/importing/celery-task-status/{id}/
     * @secure
     */,
    adminImportingCeleryTaskStatusRetrieve: <
      T extends FetchKeys<AdminCeleryTaskStatus>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminCeleryTaskStatus, T>, any>({
        path: `/admin/importing/celery-task-status/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminImportingCeleryTaskStatusRevokeTaskCreate
     * @request POST:/admin/importing/celery-task-status/{id}/revoke-task/
     * @secure
     */,
    adminImportingCeleryTaskStatusRevokeTaskCreate: <
      T extends FetchKeys<AdminCeleryTaskStatus>
    >(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminCeleryTaskStatus, T>, any>({
        path: `/admin/importing/celery-task-status/${id}/revoke-task/`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminImportingImportStatusRetrieve
     * @request GET:/admin/importing/import-status/
     * @secure
     */,
    adminImportingImportStatusRetrieve: <
      T extends FetchKeys<ImportStatusCheckResponse>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ImportStatusCheckResponse, T>, any>({
        path: `/admin/importing/import-status/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminImportingImportTimetableCreate
     * @request POST:/admin/importing/import-timetable/
     * @secure
     */,
    adminImportingImportTimetableCreate: <
      T extends FetchKeys<AdminImportResponse>
    >(
      data: AdminImportRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminImportResponse, T>, any>({
        path: `/admin/importing/import-timetable/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminPeriodsYearsList
     * @request GET:/admin/periods/years/
     * @secure
     */,
    adminPeriodsYearsList: <T extends FetchKeys<PaginatedAdminSchoolYearList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAdminSchoolYearList, T>, any>({
        path: `/admin/periods/years/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminPeriodsYearsCreate
     * @request POST:/admin/periods/years/
     * @secure
     */,
    adminPeriodsYearsCreate: <T extends FetchKeys<AdminSchoolYear>>(
      data: AdminSchoolYearRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminSchoolYear, T>, any>({
        path: `/admin/periods/years/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminPeriodsYearsRetrieve
     * @request GET:/admin/periods/years/{id}/
     * @secure
     */,
    adminPeriodsYearsRetrieve: <T extends FetchKeys<AdminSchoolYear>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminSchoolYear, T>, any>({
        path: `/admin/periods/years/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminPeriodsYearsUpdate
     * @request PUT:/admin/periods/years/{id}/
     * @secure
     */,
    adminPeriodsYearsUpdate: <T extends FetchKeys<AdminSchoolYear>>(
      id: number,
      data: AdminSchoolYearRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminSchoolYear, T>, any>({
        path: `/admin/periods/years/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminPeriodsYearsPartialUpdate
     * @request PATCH:/admin/periods/years/{id}/
     * @secure
     */,
    adminPeriodsYearsPartialUpdate: <T extends FetchKeys<AdminSchoolYear>>(
      id: number,
      data: PatchedAdminSchoolYearRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminSchoolYear, T>, any>({
        path: `/admin/periods/years/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminPeriodsYearsDestroy
     * @request DELETE:/admin/periods/years/{id}/
     * @secure
     */,
    adminPeriodsYearsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/admin/periods/years/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminStudentsClashesCreate
     * @request POST:/admin/students/clashes/
     * @secure
     */,
    adminStudentsClashesCreate: <T extends FetchKeys<ManyStudentsClashes>>(
      data: StudentsClashesForDateParamsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ManyStudentsClashes, T>, any>({
        path: `/admin/students/clashes/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminStudentsClashesHaveStudentsClashesCreate
     * @request POST:/admin/students/clashes/have-students-clashes/
     * @secure
     */,
    adminStudentsClashesHaveStudentsClashesCreate: <
      T extends FetchKeys<HaveStudentsClashes>
    >(
      data: StudentsClashesForDateParamsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<HaveStudentsClashes, T>, any>({
        path: `/admin/students/clashes/have-students-clashes/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags admin
     * @name AdminTeachersList
     * @request GET:/admin/teachers/
     * @secure
     */,
    adminTeachersList: <T extends FetchKeys<PaginatedAdminTeacherList>>(
      query?: {
        /** Filter teachers available in gap, format: date,hour,duration */
        available_in_gap?: string
        departments?: number[]
        has_activities_in_current_quarter?: boolean
        is_hidden_in_generator?: boolean
        order_by_department?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter?: string
        /** A search term. */
        search?: string
        show_inactive?: boolean
        show_placeholders?: boolean
        /**
         * * `academic` - Academic
         * * `boarding` - Boarding
         * * `tae` - TAE
         * * `other` - Other
         */
        user__staff_type?: 'academic' | 'boarding' | 'other' | 'tae'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAdminTeacherList, T>, any>({
        path: `/admin/teachers/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags admin
     * @name AdminTeachersCreate
     * @request POST:/admin/teachers/
     * @secure
     */,
    adminTeachersCreate: <T extends FetchKeys<AdminTeacher>>(
      data: AdminTeacherRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminTeacher, T>, any>({
        path: `/admin/teachers/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags admin
     * @name AdminTeachersRetrieve
     * @request GET:/admin/teachers/{user}/
     * @secure
     */,
    adminTeachersRetrieve: <T extends FetchKeys<AdminTeacher>>(
      user: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminTeacher, T>, any>({
        path: `/admin/teachers/${user}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags admin
     * @name AdminTeachersUpdate
     * @request PUT:/admin/teachers/{user}/
     * @secure
     */,
    adminTeachersUpdate: <T extends FetchKeys<AdminTeacher>>(
      user: string,
      data: AdminTeacherRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminTeacher, T>, any>({
        path: `/admin/teachers/${user}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags admin
     * @name AdminTeachersPartialUpdate
     * @request PATCH:/admin/teachers/{user}/
     * @secure
     */,
    adminTeachersPartialUpdate: <T extends FetchKeys<AdminTeacher>>(
      user: string,
      data: PatchedAdminTeacherRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminTeacher, T>, any>({
        path: `/admin/teachers/${user}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags admin
     * @name AdminTeachersDestroy
     * @request DELETE:/admin/teachers/{user}/
     * @secure
     */,
    adminTeachersDestroy: <T extends FetchKeys<void>>(
      user: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/admin/teachers/${user}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags admin
     * @name AdminTeachersCountedLessonsRetrieve
     * @request GET:/admin/teachers/counted-lessons/
     * @secure
     */,
    adminTeachersCountedLessonsRetrieve: <
      T extends FetchKeys<Record<string, number>>
    >(
      query: {
        /** Multiple values may be separated by commas. */
        available_in_gap?: string[]
        /** @format date */
        date_from: string
        /** @format date */
        date_to: string
        departments?: number[]
        has_activities_in_current_quarter?: boolean
        is_hidden_in_generator?: boolean
        order_by_department?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        quarter?: string
        /** A search term. */
        search?: string
        /**
         * * `academic` - Academic
         * * `boarding` - Boarding
         * * `tae` - TAE
         * * `other` - Other
         */
        user__staff_type?: 'academic' | 'boarding' | 'other' | 'tae'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Record<string, number>, T>, any>({
        path: `/admin/teachers/counted-lessons/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Returns a CSV file with new artisans email addresses.
     *
     * @tags admin
     * @name AdminTeachersReportsEmailAddressesRetrieve
     * @request GET:/admin/teachers/reports/email-addresses/
     * @secure
     */,
    adminTeachersReportsEmailAddressesRetrieve: <T extends FetchKeys<string>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<string, T>, any>({
        path: `/admin/teachers/reports/email-addresses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags admin
     * @name AdminUsersList
     * @request GET:/admin/users/
     * @secure
     */,
    adminUsersList: <T extends FetchKeys<PaginatedAdminUserListList>>(
      query?: {
        groups?: number[]
        is_active?: boolean
        is_staff?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        profile?: string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAdminUserListList, T>, any>({
        path: `/admin/users/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags admin
     * @name AdminUsersCreate
     * @request POST:/admin/users/
     * @secure
     */,
    adminUsersCreate: <T extends FetchKeys<AdminUser>>(
      data: AdminUserChangeRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminUser, T>, any>({
        path: `/admin/users/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags admin
     * @name AdminUsersRetrieve
     * @request GET:/admin/users/{id}/
     * @secure
     */,
    adminUsersRetrieve: <T extends FetchKeys<AdminUser>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminUser, T>, any>({
        path: `/admin/users/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags admin
     * @name AdminUsersUpdate
     * @request PUT:/admin/users/{id}/
     * @secure
     */,
    adminUsersUpdate: <T extends FetchKeys<AdminUser>>(
      id: string,
      data: AdminUserChangeRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminUser, T>, any>({
        path: `/admin/users/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags admin
     * @name AdminUsersPartialUpdate
     * @request PATCH:/admin/users/{id}/
     * @secure
     */,
    adminUsersPartialUpdate: <T extends FetchKeys<AdminUser>>(
      id: string,
      data: PatchedAdminUserRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminUser, T>, any>({
        path: `/admin/users/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags admin
     * @name AdminUsersDestroy
     * @request DELETE:/admin/users/{id}/
     * @secure
     */,
    adminUsersDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/admin/users/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags admin
     * @name AdminUsersSetPasswordCreate
     * @request POST:/admin/users/{id}/set-password/
     * @secure
     */,
    adminUsersSetPasswordCreate: <T extends FetchKeys<AdminUserPasswordChange>>(
      id: string,
      data: AdminUserPasswordChangeRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminUserPasswordChange, T>, any>({
        path: `/admin/users/${id}/set-password/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags admin
     * @name AdminUsersToggleActiveCreate
     * @request POST:/admin/users/{id}/toggle-active/
     * @secure
     */,
    adminUsersToggleActiveCreate: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/admin/users/${id}/toggle-active/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminUsersAvailabilityRetrieve
     * @request GET:/admin/users/{userId}/availability/
     * @secure
     */,
    adminUsersAvailabilityRetrieve: <
      T extends FetchKeys<AdminUserAvailability>
    >(
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminUserAvailability, T>, any>({
        path: `/admin/users/${userId}/availability/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminUsersAvailabilityCreate
     * @request POST:/admin/users/{userId}/availability/
     * @secure
     */,
    adminUsersAvailabilityCreate: <T extends FetchKeys<void>>(
      userId: string,
      data: AdminUserAvailabilityUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/admin/users/${userId}/availability/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminUsersAvailabilityUpdate
     * @request PUT:/admin/users/{userId}/availability/
     * @secure
     */,
    adminUsersAvailabilityUpdate: <T extends FetchKeys<void>>(
      userId: string,
      data: AdminUserAvailabilityUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/admin/users/${userId}/availability/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminUsersAvailabilityDestroy
     * @request DELETE:/admin/users/{userId}/availability/
     * @secure
     */,
    adminUsersAvailabilityDestroy: <T extends FetchKeys<void>>(
      userId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/admin/users/${userId}/availability/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminUsersClashesCreate
     * @request POST:/admin/users/clashes/
     * @secure
     */,
    adminUsersClashesCreate: <T extends FetchKeys<ManyStaffUsersClashes>>(
      data: UsersClashesForDateParamsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ManyStaffUsersClashes, T>, any>({
        path: `/admin/users/clashes/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminUsersClashesHaveStaffUsersClashesCreate
     * @request POST:/admin/users/clashes/have-staff-users-clashes/
     * @secure
     */,
    adminUsersClashesHaveStaffUsersClashesCreate: <
      T extends FetchKeys<HaveStaffUsersClashes>
    >(
      data: UsersClashesForDateParamsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<HaveStaffUsersClashes, T>, any>({
        path: `/admin/users/clashes/have-staff-users-clashes/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminUsersDuplicatesList
     * @request GET:/admin/users/duplicates/
     * @secure
     */,
    adminUsersDuplicatesList: <
      T extends FetchKeys<PaginatedDuplicateRecordListList>
    >(
      query?: {
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        /**
         * Type of users involved in this duplicate pair
         *
         * * `10` - Student - Student
         * * `20` - Parent - Parent
         * * `30` - Student - Candidate
         * * `40` - Candidate - Candidate
         */
        duplicate_type?: (10 | 20 | 30 | 40)[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /**
         * * `100` - Pending
         * * `200` - Not a Duplicate
         * * `300` - Merged
         */
        status?: (100 | 200 | 300)[]
        /** @format uuid */
        user?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedDuplicateRecordListList, T>, any>({
        path: `/admin/users/duplicates/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminUsersDuplicatesRetrieve
     * @request GET:/admin/users/duplicates/{id}/
     * @secure
     */,
    adminUsersDuplicatesRetrieve: <T extends FetchKeys<DuplicateRecord>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DuplicateRecord, T>, any>({
        path: `/admin/users/duplicates/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminUsersDuplicatesMergeCreate
     * @request POST:/admin/users/duplicates/{id}/merge/
     * @secure
     */,
    adminUsersDuplicatesMergeCreate: <T extends FetchKeys<MergeResponse>>(
      id: number,
      data: MergeRequestRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MergeResponse, T>, MergeErrorResponse>({
        path: `/admin/users/duplicates/${id}/merge/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminUsersDuplicatesNotDuplicateCreate
     * @request POST:/admin/users/duplicates/{id}/not-duplicate/
     * @secure
     */,
    adminUsersDuplicatesNotDuplicateCreate: <
      T extends FetchKeys<MergeResponse>
    >(
      id: number,
      data: MergeRequestRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MergeResponse, T>, MergeErrorResponse>({
        path: `/admin/users/duplicates/${id}/not-duplicate/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags admin
     * @name AdminUsersGendersList
     * @request GET:/admin/users/genders/
     * @secure
     */,
    adminUsersGendersList: <T extends FetchKeys<StringOption[]>>(
      query?: {
        groups?: number[]
        is_active?: boolean
        is_staff?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        profile?: string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/admin/users/genders/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags admin
     * @name AdminUsersProfilesList
     * @request GET:/admin/users/profiles/
     * @secure
     */,
    adminUsersProfilesList: <T extends FetchKeys<StringOption[]>>(
      query?: {
        groups?: number[]
        is_active?: boolean
        is_staff?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        profile?: string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/admin/users/profiles/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminUsersSalutationsList
     * @request GET:/admin/users/salutations/
     * @secure
     */,
    adminUsersSalutationsList: <T extends FetchKeys<Salutation[]>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Salutation[], T>, any>({
        path: `/admin/users/salutations/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags admin
     * @name AdminUsersSalutationsCreate
     * @request POST:/admin/users/salutations/
     * @secure
     */,
    adminUsersSalutationsCreate: <T extends FetchKeys<Salutation>>(
      data: SalutationRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Salutation, T>, any>({
        path: `/admin/users/salutations/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags admin
     * @name AdminUsersTeachersAndTutorsList
     * @request GET:/admin/users/teachers-and-tutors/
     * @secure
     */,
    adminUsersTeachersAndTutorsList: <T extends FetchKeys<SimpleUser[]>>(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SimpleUser[], T>, any>({
        path: `/admin/users/teachers-and-tutors/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags admin
     * @name AdminUsersTypesList
     * @request GET:/admin/users/types/
     * @secure
     */,
    adminUsersTypesList: <T extends FetchKeys<StringOption[]>>(
      query?: {
        groups?: number[]
        is_active?: boolean
        is_staff?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        profile?: string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/admin/users/types/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  ai = {
    /**
     * @description Lists assignment items visible to the authenticated user, including grades, assignment details, and score definitions. It does not modify assignment items.
     *
     * @tags ai
     * @name AiAssistantAssignmentItemsList
     * @request GET:/ai/assistant/assignments/items/
     * @secure
     */
    aiAssistantAssignmentItemsList: <
      T extends FetchKeys<PaginatedAIAssistantAssignmentItemList>
    >(
      query?: {
        course?: string[]
        id?: string[]
        /**
         * * `awaiting_approval` - Awaiting approval
         * * `approved` - Approved
         * * `rejected` - Rejected
         */
        moderated_grade_status?: (
          | 'approved'
          | 'awaiting_approval'
          | 'rejected'
          | null
        )[]
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number
        quarter?: number[]
        student?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAIAssistantAssignmentItemList, T>, any>({
        path: `/ai/assistant/assignments/items/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Lists compact assessment results visible to the authenticated user for current students, with the row student's points per score definition. Every supplied filter intersects. quarter matches the assignment quarter only. course_dropped filters each result row's drop state; omitted includes both states. It does not modify marks.
     *
     * @tags ai
     * @name AiAssistantAssessmentResultsList
     * @request GET:/ai/assistant/assignments/results/
     * @secure
     */,
    aiAssistantAssessmentResultsList: <
      T extends FetchKeys<PaginatedAIAssistantAssessmentResultList>
    >(
      query?: {
        course?: string[]
        course_dropped?: boolean
        current_grade_level?: number[]
        has_recorded_mark?: boolean
        is_exempt?: boolean
        is_incomplete?: boolean
        is_late?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number
        quarter?: number[]
        student?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAIAssistantAssessmentResultList, T>, any>({
        path: `/ai/assistant/assignments/results/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Lists academic attendance one row per student per lesson between two lesson dates, ordered by lesson start, then student name. Cancelled lessons and lessons of deleted courses are excluded. status follows the lesson register: present, late, absent_excused, absent_unexcused or sick, and unmarked when nothing is marked. start and end are local times. lesson.teacher is the scheduled teacher, not a cover teacher. As in the lesson register, teachers without a staff profile see only lessons they teach, cover or co-teach, including rows of students who later dropped the course; other users with the permission see every lesson. Rows of draft students and CRM candidates are excluded. It does not return attendance totals or boarding roll-calls.
     *
     * @tags ai
     * @name AiAssistantAttendancesRecordsList
     * @request GET:/ai/assistant/attendances/records/
     * @secure
     */,
    aiAssistantAttendancesRecordsList: <
      T extends FetchKeys<PaginatedAIAssistantAttendanceRecordList>
    >(
      query: {
        course?: string[]
        /** The student's current grade level. */
        current_grade_level?: number[]
        /** Students with a current or future accommodation in the house. */
        house?: number[]
        /**
         * Inclusive.
         * @format date
         */
        lesson_date_after: string
        /**
         * Inclusive.
         * @format date
         */
        lesson_date_before: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number
        /**
         * * `present` - present
         * * `late` - late
         * * `absent_excused` - absent_excused
         * * `absent_unexcused` - absent_unexcused
         * * `sick` - sick
         * * `unmarked` - unmarked
         */
        status?: (
          | 'absent_excused'
          | 'absent_unexcused'
          | 'late'
          | 'present'
          | 'sick'
          | 'unmarked'
        )[]
        /** At most 100 values. */
        student?: string[]
        /** The lesson's teacher, cover teacher or co-teacher. */
        teacher?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAIAssistantAttendanceRecordList, T>, any>({
        path: `/ai/assistant/attendances/records/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Lists academic attendance totals between two lesson dates, one row per student, as on the student's Academic Attendances tab: lessons that are not cancelled, of courses that are not deleted, including dropped courses. Counters are the numbers of lessons marked with each status; marked_attendances_count is their sum and unmarked lessons count nowhere. Missed means absent excused, absent unexcused or sick; late is attended. missed_lessons_percentage is missed over marked, rounded to 2 decimals, and null when nothing is marked. missed_minutes is the real length of the missed lessons, in whole minutes. Students with nothing marked are listed with zero counts. The _min bounds are inclusive. Teachers without a staff profile count only lessons they teach, cover or co-teach. Draft students and CRM candidates are excluded; without academic_status, students of every status are listed. unavailable_students lists requested students that are unknown or not visible. courses is null unless include=courses. It does not return which lessons were missed.
     *
     * @tags ai
     * @name AiAssistantAttendancesSummaryList
     * @request GET:/ai/assistant/attendances/summary/
     * @secure
     */,
    aiAssistantAttendancesSummaryList: <
      T extends FetchKeys<PaginatedAIAssistantAttendanceSummaryList>
    >(
      query: {
        /**
         * * `15` - Enrolled
         * * `30` - Active
         * * `45` - Left
         * * `60` - Alumni
         */
        academic_status?: ('15' | '30' | '45' | '60' | null)[]
        /** Students enrolled in any of the courses in the current quarter, or the next one between quarters. Counts only those courses' lessons. */
        course?: string[]
        /** The student's current grade level. */
        current_grade_level?: number[]
        /** Students with a current or future accommodation in the house. */
        house?: number[]
        /** courses: per-course rows. */
        include?: 'courses'[]
        /**
         * Inclusive.
         * @format date
         */
        lesson_date_after: string
        /**
         * Inclusive.
         * @format date
         */
        lesson_date_before: string
        /** @min 0 */
        missed_lessons_count_min?: number
        /**
         * @format double
         * @min 0
         * @max 100
         */
        missed_lessons_percentage_min?: number
        /** @min 0 */
        missed_minutes_min?: number
        /**
         * Ties and students with no percentage sort by name, then id.
         *
         * * `full_name` - full_name
         * * `-missed_lessons_percentage` - -missed_lessons_percentage
         * * `-missed_lessons_count` - -missed_lessons_count
         * * `-missed_minutes` - -missed_minutes
         * * `-absent_unexcused` - -absent_unexcused
         * * `-late` - -late
         * @minLength 1
         * @default "full_name"
         */
        ordering?:
          | 'full_name'
          | '-missed_lessons_percentage'
          | '-missed_lessons_count'
          | '-missed_minutes'
          | '-absent_unexcused'
          | '-late'
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number
        /** At most 100 values. */
        student?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAIAssistantAttendanceSummaryList, T>, any>(
        {
          path: `/ai/assistant/attendances/summary/`,
          method: 'GET',
          query: postprocessQuery(query),
          secure: true,
          format: 'json',
          ...params
        }
      )
    /**
     * @description Lists Cockpit course plans, one row per class, including classes with no plan yet (to_do). A class is one course revision's group in one school year, in any year, with Q1-Q4 quarter sections. It has a Cockpit plan and at least one section, or an active-IDP enrollment of any student, so classes of former students and past years stay. Classes of deleted courses, classes whose course departments are all excluded from Cockpit, and classes with neither a plan with sections nor such an enrollment are left out. Every course revision counts: course is one exact revision id, and a class stays on the revision it was created on. Without school_year, only the current school year (otherwise the latest ended one) is listed. A Teacher sees the classes they teach or co-teach; an Academic HOD sees all. artisans are the sections' main teachers and co_artisans their co-artisans, each by name. plan_status is the year plan's approval workflow status and says nothing about delivery. quarters lists the class's quarter sections in date order. Per quarter, the subtopic counts, planned_lessons and realized_lessons are over that quarter plan's subtopics, as the subtopics list returns them; a subtopic's realized lessons can come from another quarter's lessons. taught_lessons, unlinked_lessons, last_linked_lesson_on and first_unlinked_lesson_on are over the quarter section's own taught lessons (not cancelled, ended), in lessons, and describe how teaching is recorded against subtopics, not plan coverage. A taught lesson without a subtopic is unrecorded, not untaught. A quarter without a plan is to_do with zero subtopics and null unlinked fields. It does not say whether delivery is behind schedule.
     *
     * @tags ai
     * @name AiAssistantCockpitCoursePlansList
     * @request GET:/ai/assistant/cockpit/course-plans/
     * @secure
     */,
    aiAssistantCockpitCoursePlansList: <
      T extends FetchKeys<PaginatedAIAssistantCockpitCoursePlanList>
    >(
      query?: {
        /** Teacher user ids. Classes with a section the teacher teaches or co-teaches. */
        artisan?: string[]
        course?: string[]
        /** Departments of the class's own course revision. */
        department?: number[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number
        /**
         * Year plan status; a class without a plan is to_do.
         *
         * * `to_do` - To do
         * * `in_planning` - In planning
         * * `ready_for_review` - Ready for review
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `partially_rejected` - Partially rejected
         */
        plan_status?: (
          | 'approved'
          | 'in_planning'
          | 'partially_rejected'
          | 'ready_for_review'
          | 'rejected'
          | 'to_do'
        )[]
        /** Defaults to the current school year, otherwise the latest ended one. */
        school_year?: number[]
        /** Classes with a section the student is enrolled in, including former students. */
        student?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAIAssistantCockpitCoursePlanList, T>, any>(
        {
          path: `/ai/assistant/cockpit/course-plans/`,
          method: 'GET',
          query: postprocessQuery(query),
          secure: true,
          format: 'json',
          ...params
        }
      )
    /**
     * @description Lists the planned subtopics of classes' Cockpit quarter plans, one row per subtopic of a quarter plan, from plans in every workflow status, merged subtopics as their own rows (merged_into_id). A class is one course revision's group in one school year, in any year, with Q1-Q4 quarter sections. It has a Cockpit plan and at least one section, or an active-IDP enrollment of any student, so classes of former students and past years stay. Classes of deleted courses, classes whose course departments are all excluded from Cockpit, and classes with neither a plan with sections nor such an enrollment are left out. Every course revision counts: course is one exact revision id, and a class stays on the revision it was created on. At least one of course, course_plan, artisan or student is required. Without school_year, course_plan or quarter, only the current school year (otherwise the latest ended one) is listed. Approved quarter plans are readable on every course; plans in other statuses only on classes the caller teaches or co-teaches (an AcademicHOD reads every class). A lesson records what was taught by linking to one subtopic; there are no lesson notes. planned_lessons is the subtopic's planned length, scheduled_lessons all linked lessons, past and future, realized_lessons the linked lessons taught so far (not cancelled, ended), whichever quarter they fall in. status is completed when finished early or realized reaches planned, in_progress with started lessons, planned with only future lessons, to_do with none. taught_on lists the distinct dates of taught lessons, ascending; taught filters on them. is_custom marks a subtopic the Artisan created inside this plan. No student lists.
     *
     * @tags ai
     * @name AiAssistantCockpitSubtopicsList
     * @request GET:/ai/assistant/cockpit/subtopics/
     * @secure
     */,
    aiAssistantCockpitSubtopicsList: <
      T extends FetchKeys<PaginatedAIAssistantCockpitSubtopicList>
    >(
      query?: {
        /** Teacher user ids. Subtopics whose quarter section the teacher teaches or co-teaches. */
        artisan?: string[]
        course?: string[]
        /** Class ids, the course plan row's id. */
        course_plan?: number[]
        /** Case-insensitive substring of the domain, topic or subtopic name. */
        name?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number
        /**
         * Quarter plan status.
         *
         * * `to_do` - To do
         * * `in_planning` - In planning
         * * `ready_for_review` - Ready for review
         * * `approved` - Approved
         * * `rejected` - Rejected
         */
        plan_status?: (
          | 'approved'
          | 'in_planning'
          | 'ready_for_review'
          | 'rejected'
          | 'to_do'
        )[]
        quarter?: number[]
        /** Defaults to the current school year, otherwise the latest ended one, unless course_plan or quarter is given. */
        school_year?: number[]
        /**
         * * `to_do` - To do
         * * `planned` - Planned
         * * `in_progress` - In progress
         * * `completed` - Completed
         */
        status?: ('completed' | 'in_progress' | 'planned' | 'to_do')[]
        /** Subtopics whose quarter section the student is enrolled in, including former students. */
        student?: string[]
        /**
         * Inclusive. Subtopics with at least one taught lesson dated within the range.
         * @format date
         */
        taught_after?: string
        /**
         * Inclusive. Subtopics with at least one taught lesson dated within the range.
         * @format date
         */
        taught_before?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAIAssistantCockpitSubtopicList, T>, any>({
        path: `/ai/assistant/cockpit/subtopics/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Reads canonical Credit History for one visible student: imported rows and selected active-approved/last-approved high-school IDP plans. Summary totals cover every selected row, independently of pagination. credits_achieved includes plan credits through the current school year, including current-year planned credits; without a current school year it includes all selected years. It is not a verified earned-so-far total. credits_target includes EVERY selected year including future years, not a graduation requirement. credit_count is the nullable stored year/plan total, distinct from department totals. Units are school credits. Imported rows remain alongside same-year IDPs. Results order by year start date, source (imports first), then stable source ID. No course-level history, transcript-derived total, graduation inference or writes. Serves only current (Enrolled or Active) students; a Left or Alumni student returns 404.
     *
     * @tags ai
     * @name AiAssistantStudentCreditHistoryRetrieve
     * @request GET:/ai/assistant/credit-history/students/{studentId}/
     * @secure
     */,
    aiAssistantStudentCreditHistoryRetrieve: <
      T extends FetchKeys<AIAssistantStudentCreditHistory>
    >(
      studentId: string,
      query?: {
        /** 1-based page of compact year rows. */
        page?: number
        /** Defaults to 50; maximum 100. */
        page_size?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AIAssistantStudentCreditHistory, T>, any>({
        path: `/ai/assistant/credit-history/students/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Lists the weekly discipline categories (S, E, A, C or not assigned) from the boarding houses' weekly category reports, one row per student and week, for active students the user can see (drafts, draft candidates, reviewer and duplicate-deleted accounts excluded). Ordered by week, then student name. A week matches a date range when it overlaps it. Each row carries the report's house and the whole house's report status; is_completed belongs to the row's student only. is_changed_by_downgrade means a demerit sanction set the category; is_upgraded_by_reward means the student exchanged merits for a one-step upgrade; both can be set. Categories are the current weekly values, not a change log: revoking a sanction rewrites earlier weeks. It does not return when or why a category changed beyond these flags.
     *
     * @tags ai
     * @name AiAssistantDisciplineWeeklyCategoriesList
     * @request GET:/ai/assistant/discipline/weekly-categories/
     * @secure
     */,
    aiAssistantDisciplineWeeklyCategoriesList: <
      T extends FetchKeys<PaginatedAIAssistantWeeklyCategoryList>
    >(
      query?: {
        /**
         * * `4` - S
         * * `3` - E
         * * `2` - A
         * * `1` - C
         * * `0` - not assigned
         */
        assigned_category?: (0 | 1 | 2 | 3 | 4)[]
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /** The house the weekly report was generated for, so a student who moved house matches their earlier house for earlier weeks. */
        house?: number[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number
        student?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAIAssistantWeeklyCategoryList, T>, any>({
        path: `/ai/assistant/discipline/weekly-categories/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Lists students' actual Exam Planner enrolments. As in the Exam Planner, House Tutors, Heads of House, Boarding Directors, Health Coordinators and Campus Head Team see only published exams. Excludes inactive, draft, draft-candidate, reviewer and duplicate-deleted students, and deleted exams, sessions and courses. Dates and times belong to the sitting; session exam_type identifies Quarter exams, Academic or External exams. Standalone external exams have no real Exam Planner session, so their exam_session_id and exam_session_name are null. Publication, scheduling and status are independent. This does not establish an external awarding body's registration.
     *
     * @tags ai
     * @name AiAssistantExamSittingsList
     * @request GET:/ai/assistant/exams/sittings/
     * @secure
     */,
    aiAssistantExamSittingsList: <
      T extends FetchKeys<PaginatedAIAssistantExamSittingList>
    >(
      query?: {
        course?: string[]
        current_grade_level?: number[]
        /**
         * Inclusive exam date bounds as date_after and date_before.
         * @format date
         */
        date_after?: string
        /**
         * Inclusive exam date bounds as date_after and date_before.
         * @format date
         */
        date_before?: string
        exam?: string[]
        exam_session?: string[]
        /**
         * * `quarter` - Quarter exams
         * * `academic` - Academic
         * * `external` - External exams
         */
        exam_type?: ('academic' | 'external' | 'quarter')[]
        is_published?: boolean
        is_scheduled?: boolean
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number
        school_year?: number[]
        student?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAIAssistantExamSittingList, T>, any>({
        path: `/ai/assistant/exams/sittings/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Lists recorded Estimated GPA, one row per student, including students with nothing recorded. periods holds the recorded quarter, semester and annual GPA of the selected school_year_id (default: current school year in the school timezone), normalized, with weighted and unweighted values kept separate. high_school_gpa is the recorded cumulative high-school GPA, independent of the selected year. school_year is the resolved year, returned once beside the page metadata; with no current school year it is null and periods are empty. Missing records are not zero; stored zero is valid. recorded_modified_at is row modification time. Draft students and CRM candidates are excluded; without academic_status, students of every status are listed. Teachers see taught students and house tutors see students in their houses; combined profiles use the union, while unrestricted staff see all students. No recalculation, per-course contributions, GPA value filters or writes.
     *
     * @tags ai
     * @name AiAssistantGpaList
     * @request GET:/ai/assistant/gpa/
     * @secure
     */,
    aiAssistantGpaList: <
      T extends FetchKeys<PaginatedAIAssistantStudentGPAList>
    >(
      query?: {
        /**
         * * `15` - Enrolled
         * * `30` - Active
         * * `45` - Left
         * * `60` - Alumni
         */
        academic_status?: ('15' | '30' | '45' | '60' | null)[]
        course?: string[]
        current_grade_level?: number[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number
        school_year_id?: number
        /** A search term. */
        search?: string
        student?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAIAssistantStudentGPAList, T>, any>({
        path: `/ai/assistant/gpa/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Lists the school's grade levels in configured order.
     *
     * @tags ai
     * @name AiAssistantGradeLevels
     * @request GET:/ai/assistant/grade-levels/
     * @secure
     */,
    aiAssistantGradeLevels: <T extends FetchKeys<AIAssistantGradeLevel[]>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AIAssistantGradeLevel[], T>, any>({
        path: `/ai/assistant/grade-levels/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Lists ACC grades visible to the authenticated user and supports finding them by student, course, grade level, period, and target status. quarter, semester and year each match only grades stored for that period, with no related-period expansion. It does not modify grades.
     *
     * @tags ai
     * @name AiAssistantAccGradesList
     * @request GET:/ai/assistant/grades/acc/
     * @secure
     */,
    aiAssistantAccGradesList: <
      T extends FetchKeys<PaginatedAIAssistantAccGradeList>
    >(
      query?: {
        below_target?: boolean
        course?: string[]
        course_in_active_idp_plan?: boolean
        /** @format date-time */
        created?: string
        current_grade_level?: number[]
        dropped_grades?: boolean
        id?: number[]
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number
        quarter?: number[]
        semester?: number[]
        student?: string[]
        /**
         * * `pending` - Pending
         * * `much_above` - Much Above
         * * `above` - Above
         * * `below` - Below
         * * `much_below` - Much Below
         */
        target_grade_status?: (
          | 'above'
          | 'below'
          | 'much_above'
          | 'much_below'
          | 'pending'
        )[]
        teacher_id?: string[]
        year?: number[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAIAssistantAccGradeList, T>, any>({
        path: `/ai/assistant/grades/acc/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Returns one ACC grade visible to the authenticated user. It does not modify the grade.
     *
     * @tags ai
     * @name AiAssistantAccGradesRetrieve
     * @request GET:/ai/assistant/grades/acc/{id}/
     * @secure
     */,
    aiAssistantAccGradesRetrieve: <T extends FetchKeys<AIAssistantAccGrade>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AIAssistantAccGrade, T>, any>({
        path: `/ai/assistant/grades/acc/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Describes the signed-in user: their name, the Departments they teach in or head, and the houses whose team they are on (heads of house included), ordered by name. roles: the names of the permission roles assigned to the user (Super Admin for superusers); active_roles: the roles active for this request. Both are sorted alphabetically. Role names are editable, so match on them only for display. house_team is not filtered by student access and is not a house catalog. It describes only this user and returns no permissions.
     *
     * @tags ai
     * @name AiAssistantMe
     * @request GET:/ai/assistant/me/
     * @secure
     */,
    aiAssistantMe: <T extends FetchKeys<AIAssistantMe>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AIAssistantMe, T>, any>({
        path: `/ai/assistant/me/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Lists every degree aim a student can choose, including ones no student has chosen. It does not return which students chose them.
     *
     * @tags ai
     * @name AiAssistantMissionControlDegreeAimsList
     * @request GET:/ai/assistant/mission-control/degree-aims/
     * @secure
     */,
    aiAssistantMissionControlDegreeAimsList: <
      T extends FetchKeys<PaginatedAIAssistantDegreeAimList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAIAssistantDegreeAimList, T>, any>({
        path: `/ai/assistant/mission-control/degree-aims/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Lists one row per student, including students without arrangements, with the support they get when sitting exams: SAA student, statemented, extra time and access arrangements. Draft students and CRM candidates are excluded. Teachers see taught students and house tutors see students in their houses; combined profiles use the union, while unrestricted staff see all students. It does not return the underlying learning need.
     *
     * @tags ai
     * @name AiAssistantMissionControlExamAccessArrangementsList
     * @request GET:/ai/assistant/mission-control/exam-access-arrangements/
     * @secure
     */,
    aiAssistantMissionControlExamAccessArrangementsList: <
      T extends FetchKeys<PaginatedAIAssistantExamAccessArrangementsList>
    >(
      query?: {
        /**
         * * `15` - Enrolled
         * * `30` - Active
         * * `45` - Left
         * * `60` - Alumni
         */
        academic_status?: ('15' | '30' | '45' | '60' | null)[]
        /**
         * Students with any of the arrangements.
         *
         * * `green_paper` - Green paper
         * * `pink_paper` - Pink paper
         * * `breaks` - Breaks
         * * `prompt` - Prompt
         * * `use_laptop` - Use of laptop
         * * `reader_pen` - Reader
         * * `own_room` - Own room
         * * `scribe` - Scribe
         */
        access_arrangement?: (
          | 'breaks'
          | 'green_paper'
          | 'own_room'
          | 'pink_paper'
          | 'prompt'
          | 'reader_pen'
          | 'scribe'
          | 'use_laptop'
        )[]
        course?: string[]
        current_grade_level?: number[]
        /**
         * * `25` - 25% extra time
         * * `50` - 50% extra time
         * * `100` - 100% extra time
         */
        exam_extra_time?: ('100' | '25' | '50' | null)[]
        /** SAA, statemented, extra time or any access arrangement. */
        has_any_arrangement?: boolean
        has_extra_time?: boolean
        is_statemented?: boolean
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number
        saa_student?: boolean
        /** A search term. */
        search?: string
        student?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<PaginatedAIAssistantExamAccessArrangementsList, T>,
        any
      >({
        path: `/ai/assistant/mission-control/exam-access-arrangements/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Lists external exams (set by outside boards, such as SAT, IELTS or AP), one row per exam, ordered by date. Exams of draft students and CRM candidates are excluded. External exam access is not limited to students the user teaches or tutors. Exams recorded with only a year are ordered as January 1 of that year. status is taken when a score is recorded or the date or year has passed, upcoming otherwise, and unknown when no date, year or score is recorded. It does not return school exams.
     *
     * @tags ai
     * @name AiAssistantMissionControlExternalExamsList
     * @request GET:/ai/assistant/mission-control/external-exams/
     * @secure
     */,
    aiAssistantMissionControlExternalExamsList: <
      T extends FetchKeys<PaginatedAIAssistantExternalExamList>
    >(
      query?: {
        /**
         * * `15` - Enrolled
         * * `30` - Active
         * * `45` - Left
         * * `60` - Alumni
         */
        academic_status?: (15 | 30 | 45 | 60)[]
        /** Exams with both a score and a minimum score; true when the score is below the minimum. */
        below_minimum?: boolean
        course?: string[]
        current_grade_level?: number[]
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number
        /**
         * * `upcoming` - Upcoming
         * * `taken` - Taken
         */
        status?: ('taken' | 'upcoming')[]
        student?: string[]
        /**
         * * `ACT` - ACT
         * * `A_LEVEL` - A Level (GCE), iGCSE
         * * `AP` - Advanced Placement
         * * `CAMBRIDGE` - CAMBRIDGE
         * * `DELE` - DELE
         * * `DELF` - DELF
         * * `GCSE` - GCSE
         * * `GMAT` - GMAT
         * * `GOETHE` - GOETHE
         * * `IAL` - International A Level
         * * `IB` - International Baccalaureate
         * * `IELTS` - IELTS
         * * `LAMDA` - LAMDA
         * * `MAP` - MAP
         * * `SAT_ENG` - SAT ENG
         * * `SAT_MAT` - SAT MAT
         * * `WIDA` - WIDA
         */
        type?: (
          | 'ACT'
          | 'AP'
          | 'A_LEVEL'
          | 'CAMBRIDGE'
          | 'DELE'
          | 'DELF'
          | 'GCSE'
          | 'GMAT'
          | 'GOETHE'
          | 'IAL'
          | 'IB'
          | 'IELTS'
          | 'LAMDA'
          | 'MAP'
          | 'SAT_ENG'
          | 'SAT_MAT'
          | 'WIDA'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAIAssistantExternalExamList, T>, any>({
        path: `/ai/assistant/mission-control/external-exams/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Lists one row per student, including students with nothing recorded, with their current aim for the school year, up to two degree goals (degree aim and destination country), career goal and estimated programme completion. Draft students and CRM candidates are excluded. Teachers see taught students and house tutors see students in their houses; combined profiles use the union, while unrestricted staff see all students. No university is recorded. It does not return interests, skills or recommendations.
     *
     * @tags ai
     * @name AiAssistantMissionControlGoalsList
     * @request GET:/ai/assistant/mission-control/goals/
     * @secure
     */,
    aiAssistantMissionControlGoalsList: <
      T extends FetchKeys<PaginatedAIAssistantStudentGoalsList>
    >(
      query?: {
        /**
         * * `15` - Enrolled
         * * `30` - Active
         * * `45` - Left
         * * `60` - Alumni
         */
        academic_status?: ('15' | '30' | '45' | '60' | null)[]
        course?: string[]
        /**
         * The aim for the current school year; students without one have the school default.
         *
         * * `RIC` - RIC
         * * `RIC+AP` - RIC+AP
         * * `A-level` - A-level
         * * `IB` - IB
         */
        current_aim?: ('A-level' | 'IB' | 'RIC' | 'RIC+AP')[]
        current_grade_level?: number[]
        degree_aim?: number[]
        /**
         * Matches the first or second degree destination.
         *
         * * `AF` - Afghanistan
         * * `AX` - Åland Islands
         * * `AL` - Albania
         * * `DZ` - Algeria
         * * `AS` - American Samoa
         * * `AD` - Andorra
         * * `AO` - Angola
         * * `AI` - Anguilla
         * * `AQ` - Antarctica
         * * `AG` - Antigua and Barbuda
         * * `AR` - Argentina
         * * `AM` - Armenia
         * * `AW` - Aruba
         * * `AU` - Australia
         * * `AT` - Austria
         * * `AZ` - Azerbaijan
         * * `BS` - Bahamas
         * * `BH` - Bahrain
         * * `BD` - Bangladesh
         * * `BB` - Barbados
         * * `BY` - Belarus
         * * `BE` - Belgium
         * * `BZ` - Belize
         * * `BJ` - Benin
         * * `BM` - Bermuda
         * * `BT` - Bhutan
         * * `BO` - Bolivia
         * * `BQ` - Bonaire, Sint Eustatius and Saba
         * * `BA` - Bosnia and Herzegovina
         * * `BW` - Botswana
         * * `BV` - Bouvet Island
         * * `BR` - Brazil
         * * `IO` - British Indian Ocean Territory
         * * `BN` - Brunei
         * * `BG` - Bulgaria
         * * `BF` - Burkina Faso
         * * `BI` - Burundi
         * * `CV` - Cabo Verde
         * * `KH` - Cambodia
         * * `CM` - Cameroon
         * * `CA` - Canada
         * * `KY` - Cayman Islands
         * * `CF` - Central African Republic
         * * `TD` - Chad
         * * `CL` - Chile
         * * `CN` - China
         * * `CX` - Christmas Island
         * * `CC` - Cocos (Keeling) Islands
         * * `CO` - Colombia
         * * `KM` - Comoros
         * * `CG` - Congo
         * * `CD` - Congo (the Democratic Republic of the)
         * * `CK` - Cook Islands
         * * `CR` - Costa Rica
         * * `CI` - Côte d'Ivoire
         * * `HR` - Croatia
         * * `CU` - Cuba
         * * `CW` - Curaçao
         * * `CY` - Cyprus
         * * `CZ` - Czechia
         * * `DK` - Denmark
         * * `DJ` - Djibouti
         * * `DM` - Dominica
         * * `DO` - Dominican Republic
         * * `EC` - Ecuador
         * * `EG` - Egypt
         * * `SV` - El Salvador
         * * `GQ` - Equatorial Guinea
         * * `ER` - Eritrea
         * * `EE` - Estonia
         * * `SZ` - Eswatini
         * * `ET` - Ethiopia
         * * `FK` - Falkland Islands (Malvinas)
         * * `FO` - Faroe Islands
         * * `FJ` - Fiji
         * * `FI` - Finland
         * * `FR` - France
         * * `GF` - French Guiana
         * * `PF` - French Polynesia
         * * `TF` - French Southern Territories
         * * `GA` - Gabon
         * * `GM` - Gambia
         * * `GE` - Georgia
         * * `DE` - Germany
         * * `GH` - Ghana
         * * `GI` - Gibraltar
         * * `GR` - Greece
         * * `GL` - Greenland
         * * `GD` - Grenada
         * * `GP` - Guadeloupe
         * * `GU` - Guam
         * * `GT` - Guatemala
         * * `GG` - Guernsey
         * * `GN` - Guinea
         * * `GW` - Guinea-Bissau
         * * `GY` - Guyana
         * * `HT` - Haiti
         * * `HM` - Heard Island and McDonald Islands
         * * `VA` - Holy See
         * * `HN` - Honduras
         * * `HK` - Hong Kong
         * * `HU` - Hungary
         * * `IS` - Iceland
         * * `IN` - India
         * * `ID` - Indonesia
         * * `IR` - Iran
         * * `IQ` - Iraq
         * * `IE` - Ireland
         * * `IM` - Isle of Man
         * * `IL` - Israel
         * * `IT` - Italy
         * * `JM` - Jamaica
         * * `JP` - Japan
         * * `JE` - Jersey
         * * `JO` - Jordan
         * * `KZ` - Kazakhstan
         * * `KE` - Kenya
         * * `KI` - Kiribati
         * * `XK` - Kosovo
         * * `KW` - Kuwait
         * * `KG` - Kyrgyzstan
         * * `LA` - Laos
         * * `LV` - Latvia
         * * `LB` - Lebanon
         * * `LS` - Lesotho
         * * `LR` - Liberia
         * * `LY` - Libya
         * * `LI` - Liechtenstein
         * * `LT` - Lithuania
         * * `LU` - Luxembourg
         * * `MO` - Macao
         * * `MG` - Madagascar
         * * `MW` - Malawi
         * * `MY` - Malaysia
         * * `MV` - Maldives
         * * `ML` - Mali
         * * `MT` - Malta
         * * `MH` - Marshall Islands
         * * `MQ` - Martinique
         * * `MR` - Mauritania
         * * `MU` - Mauritius
         * * `YT` - Mayotte
         * * `MX` - Mexico
         * * `FM` - Micronesia
         * * `MD` - Moldova
         * * `MC` - Monaco
         * * `MN` - Mongolia
         * * `ME` - Montenegro
         * * `MS` - Montserrat
         * * `MA` - Morocco
         * * `MZ` - Mozambique
         * * `MM` - Myanmar
         * * `NA` - Namibia
         * * `NR` - Nauru
         * * `NP` - Nepal
         * * `NL` - Netherlands
         * * `NC` - New Caledonia
         * * `NZ` - New Zealand
         * * `NI` - Nicaragua
         * * `NE` - Niger
         * * `NG` - Nigeria
         * * `NU` - Niue
         * * `NF` - Norfolk Island
         * * `KP` - North Korea
         * * `MK` - North Macedonia
         * * `MP` - Northern Mariana Islands
         * * `NO` - Norway
         * * `OM` - Oman
         * * `PK` - Pakistan
         * * `PW` - Palau
         * * `PS` - Palestine, State of
         * * `PA` - Panama
         * * `PG` - Papua New Guinea
         * * `PY` - Paraguay
         * * `PE` - Peru
         * * `PH` - Philippines
         * * `PN` - Pitcairn
         * * `PL` - Poland
         * * `PT` - Portugal
         * * `PR` - Puerto Rico
         * * `QA` - Qatar
         * * `RE` - Réunion
         * * `RO` - Romania
         * * `RU` - Russia
         * * `RW` - Rwanda
         * * `BL` - Saint Barthélemy
         * * `SH` - Saint Helena, Ascension and Tristan da Cunha
         * * `KN` - Saint Kitts and Nevis
         * * `LC` - Saint Lucia
         * * `MF` - Saint Martin (French part)
         * * `PM` - Saint Pierre and Miquelon
         * * `VC` - Saint Vincent and the Grenadines
         * * `WS` - Samoa
         * * `SM` - San Marino
         * * `ST` - Sao Tome and Principe
         * * `SA` - Saudi Arabia
         * * `SN` - Senegal
         * * `RS` - Serbia
         * * `SC` - Seychelles
         * * `SL` - Sierra Leone
         * * `SG` - Singapore
         * * `SX` - Sint Maarten (Dutch part)
         * * `SK` - Slovakia
         * * `SI` - Slovenia
         * * `SB` - Solomon Islands
         * * `SO` - Somalia
         * * `ZA` - South Africa
         * * `GS` - South Georgia and the South Sandwich Islands
         * * `KR` - South Korea
         * * `SS` - South Sudan
         * * `ES` - Spain
         * * `LK` - Sri Lanka
         * * `SD` - Sudan
         * * `SR` - Suriname
         * * `SJ` - Svalbard and Jan Mayen
         * * `SE` - Sweden
         * * `CH` - Switzerland
         * * `SY` - Syria
         * * `TW` - Taiwan
         * * `TJ` - Tajikistan
         * * `TZ` - Tanzania
         * * `TH` - Thailand
         * * `TL` - Timor-Leste
         * * `TG` - Togo
         * * `TK` - Tokelau
         * * `TO` - Tonga
         * * `TT` - Trinidad and Tobago
         * * `TN` - Tunisia
         * * `TR` - Türkiye
         * * `TM` - Turkmenistan
         * * `TC` - Turks and Caicos Islands
         * * `TV` - Tuvalu
         * * `UG` - Uganda
         * * `UA` - Ukraine
         * * `AE` - United Arab Emirates
         * * `GB` - United Kingdom
         * * `UM` - United States Minor Outlying Islands
         * * `US` - United States of America
         * * `UY` - Uruguay
         * * `UZ` - Uzbekistan
         * * `VU` - Vanuatu
         * * `VE` - Venezuela
         * * `VN` - Vietnam
         * * `VG` - Virgin Islands (British)
         * * `VI` - Virgin Islands (U.S.)
         * * `WF` - Wallis and Futuna
         * * `EH` - Western Sahara
         * * `YE` - Yemen
         * * `ZM` - Zambia
         * * `ZW` - Zimbabwe
         */
        degree_destination?: (
          | 'AD'
          | 'AE'
          | 'AF'
          | 'AG'
          | 'AI'
          | 'AL'
          | 'AM'
          | 'AO'
          | 'AQ'
          | 'AR'
          | 'AS'
          | 'AT'
          | 'AU'
          | 'AW'
          | 'AX'
          | 'AZ'
          | 'BA'
          | 'BB'
          | 'BD'
          | 'BE'
          | 'BF'
          | 'BG'
          | 'BH'
          | 'BI'
          | 'BJ'
          | 'BL'
          | 'BM'
          | 'BN'
          | 'BO'
          | 'BQ'
          | 'BR'
          | 'BS'
          | 'BT'
          | 'BV'
          | 'BW'
          | 'BY'
          | 'BZ'
          | 'CA'
          | 'CC'
          | 'CD'
          | 'CF'
          | 'CG'
          | 'CH'
          | 'CI'
          | 'CK'
          | 'CL'
          | 'CM'
          | 'CN'
          | 'CO'
          | 'CR'
          | 'CU'
          | 'CV'
          | 'CW'
          | 'CX'
          | 'CY'
          | 'CZ'
          | 'DE'
          | 'DJ'
          | 'DK'
          | 'DM'
          | 'DO'
          | 'DZ'
          | 'EC'
          | 'EE'
          | 'EG'
          | 'EH'
          | 'ER'
          | 'ES'
          | 'ET'
          | 'FI'
          | 'FJ'
          | 'FK'
          | 'FM'
          | 'FO'
          | 'FR'
          | 'GA'
          | 'GB'
          | 'GD'
          | 'GE'
          | 'GF'
          | 'GG'
          | 'GH'
          | 'GI'
          | 'GL'
          | 'GM'
          | 'GN'
          | 'GP'
          | 'GQ'
          | 'GR'
          | 'GS'
          | 'GT'
          | 'GU'
          | 'GW'
          | 'GY'
          | 'HK'
          | 'HM'
          | 'HN'
          | 'HR'
          | 'HT'
          | 'HU'
          | 'ID'
          | 'IE'
          | 'IL'
          | 'IM'
          | 'IN'
          | 'IO'
          | 'IQ'
          | 'IR'
          | 'IS'
          | 'IT'
          | 'JE'
          | 'JM'
          | 'JO'
          | 'JP'
          | 'KE'
          | 'KG'
          | 'KH'
          | 'KI'
          | 'KM'
          | 'KN'
          | 'KP'
          | 'KR'
          | 'KW'
          | 'KY'
          | 'KZ'
          | 'LA'
          | 'LB'
          | 'LC'
          | 'LI'
          | 'LK'
          | 'LR'
          | 'LS'
          | 'LT'
          | 'LU'
          | 'LV'
          | 'LY'
          | 'MA'
          | 'MC'
          | 'MD'
          | 'ME'
          | 'MF'
          | 'MG'
          | 'MH'
          | 'MK'
          | 'ML'
          | 'MM'
          | 'MN'
          | 'MO'
          | 'MP'
          | 'MQ'
          | 'MR'
          | 'MS'
          | 'MT'
          | 'MU'
          | 'MV'
          | 'MW'
          | 'MX'
          | 'MY'
          | 'MZ'
          | 'NA'
          | 'NC'
          | 'NE'
          | 'NF'
          | 'NG'
          | 'NI'
          | 'NL'
          | 'NO'
          | 'NP'
          | 'NR'
          | 'NU'
          | 'NZ'
          | 'OM'
          | 'PA'
          | 'PE'
          | 'PF'
          | 'PG'
          | 'PH'
          | 'PK'
          | 'PL'
          | 'PM'
          | 'PN'
          | 'PR'
          | 'PS'
          | 'PT'
          | 'PW'
          | 'PY'
          | 'QA'
          | 'RE'
          | 'RO'
          | 'RS'
          | 'RU'
          | 'RW'
          | 'SA'
          | 'SB'
          | 'SC'
          | 'SD'
          | 'SE'
          | 'SG'
          | 'SH'
          | 'SI'
          | 'SJ'
          | 'SK'
          | 'SL'
          | 'SM'
          | 'SN'
          | 'SO'
          | 'SR'
          | 'SS'
          | 'ST'
          | 'SV'
          | 'SX'
          | 'SY'
          | 'SZ'
          | 'TC'
          | 'TD'
          | 'TF'
          | 'TG'
          | 'TH'
          | 'TJ'
          | 'TK'
          | 'TL'
          | 'TM'
          | 'TN'
          | 'TO'
          | 'TR'
          | 'TT'
          | 'TV'
          | 'TW'
          | 'TZ'
          | 'UA'
          | 'UG'
          | 'UM'
          | 'US'
          | 'UY'
          | 'UZ'
          | 'VA'
          | 'VC'
          | 'VE'
          | 'VG'
          | 'VI'
          | 'VN'
          | 'VU'
          | 'WF'
          | 'WS'
          | 'XK'
          | 'YE'
          | 'YT'
          | 'ZA'
          | 'ZM'
          | 'ZW'
        )[]
        /** @format date */
        estimated_completion_after?: string
        /** @format date */
        estimated_completion_before?: string
        /** Whether any degree aim or destination is recorded. */
        has_degree_goal?: boolean
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number
        /** A search term. */
        search?: string
        student?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAIAssistantStudentGoalsList, T>, any>({
        path: `/ai/assistant/mission-control/goals/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Returns every school year with its semesters and quarters, in date order, and the ids of the current year, semester and quarter. Between periods the current one is the most recently ended, as in the panel. Each level is resolved on its own, so a current quarter that starts before its semester sits beside the previous semester. It does not include lessons or teaching days.
     *
     * @tags ai
     * @name AiAssistantPeriods
     * @request GET:/ai/assistant/periods/
     * @secure
     */,
    aiAssistantPeriods: <T extends FetchKeys<AIAssistantPeriodCatalog>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AIAssistantPeriodCatalog, T>, any>({
        path: `/ai/assistant/periods/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description OpenApi3 schema for this API. Format can be selected via content negotiation. - YAML: application/vnd.oai.openapi - JSON: application/vnd.oai.openapi+json
     *
     * @tags ai
     * @name AiAssistantSchemaRetrieve
     * @request GET:/ai/assistant/schema/
     * @secure
     */,
    aiAssistantSchemaRetrieve: <T extends FetchKeys<Record<string, any>>>(
      query?: {
        format?: 'json' | 'yaml'
        lang?: 'en'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Record<string, any>, T>, any>({
        path: `/ai/assistant/schema/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Returns lessons, exams, programmes and duties for up to 5 staff users across an inclusive range of at most 31 days. Each row is one staff member on one source object, with their roles and whether the row means they are working, covered, or on time off. Night presence ends at the real end of the shift. An exam with no times has a date and null start and end. It does not search the school for who is free to cover a slot.
     *
     * @tags ai
     * @name ListStaffTimetable
     * @request GET:/ai/assistant/staff-timetable/
     * @secure
     */,
    listStaffTimetable: <T extends FetchKeys<StaffTimetablePage>>(
      query: {
        /** Inclusive range start, YYYY-MM-DD. */
        date_after: string
        /** Inclusive range end, YYYY-MM-DD. */
        date_before: string
        /** Repeat to limit kinds. Omit it to return all four. */
        kind?: ('lesson' | 'exam' | 'programme' | 'duty')[]
        /**
         * Staff user ids. Repeat for more than one person.
         * @maxItems 5
         */
        staff: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StaffTimetablePage, T>, any>({
        path: `/ai/assistant/staff-timetable/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Lists students visible to the authenticated user for ID discovery. Use fuzzy_search instead of search for approximate full-name or nickname matches. Matches are ranked by similarity unless ordering is supplied. It does not return student details.
     *
     * @tags ai
     * @name AiAssistantStudentsList
     * @request GET:/ai/assistant/students/
     * @secure
     */,
    aiAssistantStudentsList: <
      T extends FetchKeys<PaginatedAIAssistantStudentList>
    >(
      query?: {
        /**
         * * `15` - Enrolled
         * * `30` - Active
         * * `45` - Left
         * * `60` - Alumni
         */
        academic_status?: ('15' | '30' | '45' | '60' | null)[]
        current_grade_level?: number[]
        /** @format date */
        diploma_date_after?: string
        /** @format date */
        diploma_date_before?: string
        /**
         * * `abitur` - Abitur
         * * `aufnahmeprufung_uni_bern` - Aufnahmeprüfung Uni Bern
         * * `british_gce_a_levels` - British GCE A Levels
         * * `diploma` - Diploma
         * * `esame_di_stato` - Esame di Stato
         * * `esame_die_stato_linguistico` - Esame die Stato Linguistico
         * * `esame_die_stato_scientifico` - Esame die Stato Scientifico
         * * `graduation` - Graduation
         * * `handelsdiplom` - Handelsdiplom
         * * `high_school_certificate` - High School Certificate
         * * `high_school_diploma` - High School Diploma
         * * `high_school_diploma_ap` - High School Diploma & AP
         * * `international_bacalaureate_ib` - International Bacalaureate (IB)
         * * `matura` - Matura
         * * `zurcher_matura` - Zürcher Matura
         */
        diploma_type?: (
          | 'abitur'
          | 'aufnahmeprufung_uni_bern'
          | 'british_gce_a_levels'
          | 'diploma'
          | 'esame_di_stato'
          | 'esame_die_stato_linguistico'
          | 'esame_die_stato_scientifico'
          | 'graduation'
          | 'handelsdiplom'
          | 'high_school_certificate'
          | 'high_school_diploma'
          | 'high_school_diploma_ap'
          | 'international_bacalaureate_ib'
          | 'matura'
          | 'zurcher_matura'
          | null
        )[]
        /** Name lookup: 2 characters match a complete name word or nickname; 3 or more use approximate matching. Results default to closest match first; use instead of search for typo recovery. */
        fuzzy_search?: string
        house?: string[]
        id?: string[]
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAIAssistantStudentList, T>, any>({
        path: `/ai/assistant/students/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Returns basic identity, demographic, and care details for a student visible to the authenticated user. It does not return contact details or detailed medical information.
     *
     * @tags ai
     * @name AiAssistantStudentsRetrieve
     * @request GET:/ai/assistant/students/{id}/
     * @secure
     */,
    aiAssistantStudentsRetrieve: <
      T extends FetchKeys<AIAssistantStudentDetail>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AIAssistantStudentDetail, T>, any>({
        path: `/ai/assistant/students/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Returns details for the requested students visible to the authenticated user. Unknown or inaccessible students are omitted.
     *
     * @tags ai
     * @name AiAssistantStudentsDetails
     * @request GET:/ai/assistant/students/details/
     * @secure
     */,
    aiAssistantStudentsDetails: <
      T extends FetchKeys<PaginatedAIAssistantStudentDetailList>
    >(
      query: {
        id: string[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAIAssistantStudentDetailList, T>, any>({
        path: `/ai/assistant/students/details/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Lists where students are right now, as the panel's Students list shows it: campus_status with campus_status_reason, campus_location, and boarding_status. The data is live, can be about a minute old, and is not history. An at-home student is never sick in campus_status_reason; sick_at_home marks an at-home student with an active sick absence. A student at a doctor's visit has boarding_status appointment. house is the house of the current accommodation, null without one. counts covers every student matching the filters, not only the page. Only students with an active academic status are listed; draft students and CRM candidates are excluded. Teachers see taught students and house tutors see students in their houses; heads of house and unrestricted staff see all students; other users see none. covers_all_students says whether the user sees every active student.
     *
     * @tags ai
     * @name AiAssistantStudentsWhereaboutsList
     * @request GET:/ai/assistant/students/whereabouts/
     * @secure
     */,
    aiAssistantStudentsWhereaboutsList: <
      T extends FetchKeys<PaginatedAIAssistantStudentWhereaboutsList>
    >(
      query?: {
        /**
         * * `at home` - at home
         * * `regular on board` - regular on board
         * * `temporary movement` - temporary movement
         * * `day` - day
         * * `campus absence` - campus absence
         * * `appointment` - appointment
         */
        boarding_status?: (
          | 'appointment'
          | 'at home'
          | 'campus absence'
          | 'day'
          | 'regular on board'
          | 'temporary movement'
        )[]
        /**
         * * `on campus` - on campus
         * * `off campus` - off campus
         */
        campus_status?: ('off campus' | 'on campus')[]
        /**
         * * `leave request` - leave request
         * * `school programme` - school programme
         * * `campus absence` - campus absence
         * * `absent excused` - absent excused
         * * `absent unexcused` - absent unexcused
         * * `sick` - sick
         */
        campus_status_reason?: (
          | 'absent excused'
          | 'absent unexcused'
          | 'campus absence'
          | 'leave request'
          | 'school programme'
          | 'sick'
        )[]
        current_grade_level?: number[]
        /** House of the current accommodation. */
        house?: string[]
        id?: string[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number
        /** A search term. */
        search?: string
        /** true keeps at-home students with an active sick absence; false excludes them. */
        sick_at_home?: boolean

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<PaginatedAIAssistantStudentWhereaboutsList, T>,
        any
      >({
        path: `/ai/assistant/students/whereabouts/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Lists Talent & Enrichment (T&E) enrolments, the signup rounds of a school year's T&E plan, newest school year first. Each has a status, a start date, a signup deadline for returning students (deadline_recurring) and one for new students (deadline_new), and scopes: the quarters and grade levels each course group (CCU, ATH) runs for. Pass the id to the offerings and signups endpoints.
     *
     * @tags ai
     * @name AiAssistantTalentEnrichmentEnrolmentsList
     * @request GET:/ai/assistant/talent-enrichment/enrolments/
     * @secure
     */,
    aiAssistantTalentEnrichmentEnrolmentsList: <
      T extends FetchKeys<PaginatedTalentEnrichmentEnrolmentList>
    >(
      query?: {
        id?: number[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number
        /** Enrolments with a scope in this quarter. */
        quarter?: number[]
        school_year?: number[]
        /**
         * Inclusive.
         * @format date
         */
        start_after?: string
        /**
         * Inclusive.
         * @format date
         */
        start_before?: string
        /**
         * * `DRAFT` - Draft
         * * `SCHEDULED` - Scheduled
         * * `OPEN` - Open
         * * `CLOSED` - Closed
         * * `ARCHIVED` - Archived
         */
        status?: ('ARCHIVED' | 'CLOSED' | 'DRAFT' | 'OPEN' | 'SCHEDULED')[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTalentEnrichmentEnrolmentList, T>, any>({
        path: `/ai/assistant/talent-enrichment/enrolments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Lists the courses T&E enrolments offer, ordered by course name, with their published grade levels and capacity. Courses added as a draft and deleted courses are excluded. has_draft_changes means staff edited the offering without publishing; the values returned are the published ones, and is_validated is false while an open enrolment has such edits. maximum_participants is capacity_per_section times max_sections. enrolled_students_count counts the students who chose the course, approved_students_count the approved ones, and free_spots is maximum_participants minus approved students. maximum_participants and free_spots are null when capacity is not set.
     *
     * @tags ai
     * @name AiAssistantTalentEnrichmentOfferingsList
     * @request GET:/ai/assistant/talent-enrichment/offerings/
     * @secure
     */,
    aiAssistantTalentEnrichmentOfferingsList: <
      T extends FetchKeys<PaginatedTalentEnrichmentOfferingList>
    >(
      query: {
        course?: string[]
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        course_group?: ('ATH' | 'CCU' | null)[]
        enrolment: number[]
        grade_level?: number[]
        is_closed?: boolean
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTalentEnrichmentOfferingList, T>, any>({
        path: `/ai/assistant/talent-enrichment/offerings/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Lists T&E signups, one row per student and enrolment, for active students (drafts, draft candidates, reviewer and duplicate-deleted accounts excluded), ordered by student name, with the courses each student chose in the order they chose them. Choices replaced by another course and choices of deleted courses are excluded. grade_level_id is the student's grade in the enrolment's school year, or their current grade when none is recorded for that year. status starts as ENROLLED when the student signs up; once staff approve, reject or add a choice it is PENDING while any choice is unapproved and APPROVED when all are. reopened_ccu and reopened_ath mean staff reopened that course group for the student to change their choices; they stay set until staff turn them off. sport_membership is the student's Health & Fitness choice, not an issued membership.
     *
     * @tags ai
     * @name AiAssistantTalentEnrichmentSignupsList
     * @request GET:/ai/assistant/talent-enrichment/signups/
     * @secure
     */,
    aiAssistantTalentEnrichmentSignupsList: <
      T extends FetchKeys<PaginatedTalentEnrichmentSignupList>
    >(
      query: {
        enrolment: number[]
        /** The student's grade in the enrolment's school year. */
        grade_level?: string[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number
        /** CCU or ATH signup reopened. */
        reopened?: boolean
        /** A search term. */
        search?: string
        /**
         * * `ENROLLED` - Enrolled
         * * `PENDING` - Pending
         * * `APPROVED` - Approved
         */
        status?: ('APPROVED' | 'ENROLLED' | 'PENDING')[]
        student?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTalentEnrichmentSignupList, T>, any>({
        path: `/ai/assistant/talent-enrichment/signups/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Lists teachers for ID discovery, with their email and departments. Inactive users, accounts merged away as duplicates and placeholder teachers are excluded; staff without a teacher profile, such as house tutors, are not listed. Not limited to teachers the user works with. search matches first name, last name, display name or email, ignoring accents and case. Ordered by last name, then first name. The id is the teacher's user ID.
     *
     * @tags ai
     * @name AiAssistantTeachersList
     * @request GET:/ai/assistant/teachers/
     * @secure
     */,
    aiAssistantTeachersList: <
      T extends FetchKeys<PaginatedAIAssistantTeacherList>
    >(
      query?: {
        department?: number[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAIAssistantTeacherList, T>, any>({
        path: `/ai/assistant/teachers/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Returns when lessons were actually taught in the current (or most recently ended) school year and the years before and after it, or in the requested years: a teaching summary for each year, semester and quarter by id, and the school breaks overlapping those years. Names and dates of the periods come from the periods endpoint. A teaching day has at least 10 lessons that are not cancelled and is not inside a school break. Each period counts the teaching days inside its own dates, so a semester can count fewer than its quarters when a quarter starts before it. first_lesson_date may be in the future when lessons are scheduled but not yet taught; latest_lesson_date and teaching_days_so_far are capped at today. It does not list individual lessons.
     *
     * @tags ai
     * @name AiAssistantTeachingCalendar
     * @request GET:/ai/assistant/teaching-calendar/
     * @secure
     */,
    aiAssistantTeachingCalendar: <
      T extends FetchKeys<AIAssistantTeachingCalendar>
    >(
      query?: {
        /**
         * School year ids to return instead of the default window.
         * @maxItems 5
         */
        year?: number[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AIAssistantTeachingCalendar, T>, any>({
        path: `/ai/assistant/teaching-calendar/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags ai
     * @name AiCommentsGenerateCreate
     * @request POST:/ai/comments/generate/
     * @secure
     */,
    aiCommentsGenerateCreate: <T extends FetchKeys<CommentGenerationResponse>>(
      data: CommentGenerationRequest,
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<CommentGenerationResponse, T>,
        MessageResponseSchema
      >({
        path: `/ai/comments/generate/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
  }
  aims = {
    /**
     * No description
     *
     * @tags aims
     * @name AimsList
     * @request GET:/aims/
     * @secure
     */
    aimsList: <T extends FetchKeys<PaginatedAimList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAimList, T>, any>({
        path: `/aims/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags aims
     * @name AimsAimForCourseRetrieve
     * @request GET:/aims/aim-for-course/
     * @secure
     */,
    aimsAimForCourseRetrieve: <T extends FetchKeys<StudentAimForCourse>>(
      query: {
        /** @format uuid */
        course: string
        /** @format uuid */
        student: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentAimForCourse, T>, any>({
        path: `/aims/aim-for-course/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags aims
     * @name AimsAimForCourseUpdate
     * @request PUT:/aims/aim-for-course/
     * @secure
     */,
    aimsAimForCourseUpdate: <T extends FetchKeys<StudentAimForCourse>>(
      query: {
        /** @format uuid */
        course: string
        /** @format uuid */
        student: string
      },
      data: StudentAimForCourseRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentAimForCourse, T>, any>({
        path: `/aims/aim-for-course/`,
        method: 'PUT',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags aims
     * @name AimsAimForCoursePartialUpdate
     * @request PATCH:/aims/aim-for-course/
     * @secure
     */,
    aimsAimForCoursePartialUpdate: <T extends FetchKeys<StudentAimForCourse>>(
      query: {
        /** @format uuid */
        course: string
        /** @format uuid */
        student: string
      },
      data: PatchedStudentAimForCourseRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentAimForCourse, T>, any>({
        path: `/aims/aim-for-course/`,
        method: 'PATCH',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags aims
     * @name AimsCalcGradeCreate
     * @request POST:/aims/calc-grade/
     * @secure
     */,
    aimsCalcGradeCreate: <T extends FetchKeys<void>>(
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/aims/calc-grade/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags aims
     * @name AimsForStudentList
     * @request GET:/aims/for-student/
     * @secure
     */,
    aimsForStudentList: <T extends FetchKeys<PaginatedAimList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAimList, T>, any>({
        path: `/aims/for-student/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags aims
     * @name AimsForStudentRetrieve
     * @request GET:/aims/for-student/{id}/
     * @secure
     */,
    aimsForStudentRetrieve: <T extends FetchKeys<Aim>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Aim, T>, any>({
        path: `/aims/for-student/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags aims
     * @name AimsScoringsRetrieve
     * @request GET:/aims/scorings/
     * @secure
     */,
    aimsScoringsRetrieve: <T extends FetchKeys<Scorings>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Scorings, T>, any>({
        path: `/aims/scorings/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  allergies = {
    /**
     * No description
     *
     * @tags allergies
     * @name AllergiesList
     * @request GET:/allergies/
     * @secure
     */
    allergiesList: <T extends FetchKeys<PaginatedAllergyList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAllergyList, T>, any>({
        path: `/allergies/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags allergies
     * @name AllergiesCreate
     * @request POST:/allergies/
     * @secure
     */,
    allergiesCreate: <T extends FetchKeys<Allergy>>(
      data: AllergyRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Allergy, T>, any>({
        path: `/allergies/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags allergies
     * @name AllergiesRetrieve
     * @request GET:/allergies/{id}/
     * @secure
     */,
    allergiesRetrieve: <T extends FetchKeys<Allergy>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Allergy, T>, any>({
        path: `/allergies/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags allergies
     * @name AllergiesUpdate
     * @request PUT:/allergies/{id}/
     * @secure
     */,
    allergiesUpdate: <T extends FetchKeys<Allergy>>(
      id: number,
      data: AllergyRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Allergy, T>, any>({
        path: `/allergies/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags allergies
     * @name AllergiesPartialUpdate
     * @request PATCH:/allergies/{id}/
     * @secure
     */,
    allergiesPartialUpdate: <T extends FetchKeys<Allergy>>(
      id: number,
      data: PatchedAllergyRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Allergy, T>, any>({
        path: `/allergies/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags allergies
     * @name AllergiesStudentAllergiesList
     * @request GET:/allergies/student-allergies/
     * @secure
     */,
    allergiesStudentAllergiesList: <T extends FetchKeys<StudentAllergy[]>>(
      query?: {
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentAllergy[], T>, any>({
        path: `/allergies/student-allergies/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags allergies
     * @name AllergiesStudentAllergiesCreate
     * @request POST:/allergies/student-allergies/
     * @secure
     */,
    allergiesStudentAllergiesCreate: <T extends FetchKeys<StudentAllergy>>(
      data: StudentAllergyRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentAllergy, T>, any>({
        path: `/allergies/student-allergies/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags allergies
     * @name AllergiesStudentAllergiesRetrieve
     * @request GET:/allergies/student-allergies/{id}/
     * @secure
     */,
    allergiesStudentAllergiesRetrieve: <T extends FetchKeys<StudentAllergy>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentAllergy, T>, any>({
        path: `/allergies/student-allergies/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags allergies
     * @name AllergiesStudentAllergiesUpdate
     * @request PUT:/allergies/student-allergies/{id}/
     * @secure
     */,
    allergiesStudentAllergiesUpdate: <T extends FetchKeys<StudentAllergy>>(
      id: number,
      data: StudentAllergyRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentAllergy, T>, any>({
        path: `/allergies/student-allergies/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags allergies
     * @name AllergiesStudentAllergiesPartialUpdate
     * @request PATCH:/allergies/student-allergies/{id}/
     * @secure
     */,
    allergiesStudentAllergiesPartialUpdate: <
      T extends FetchKeys<StudentAllergy>
    >(
      id: number,
      data: PatchedStudentAllergyRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentAllergy, T>, any>({
        path: `/allergies/student-allergies/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags allergies
     * @name AllergiesStudentAllergiesDestroy
     * @request DELETE:/allergies/student-allergies/{id}/
     * @secure
     */,
    allergiesStudentAllergiesDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/allergies/student-allergies/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags allergies
     * @name AllergiesStudentAllergiesChoicesRetrieve
     * @request GET:/allergies/student-allergies/choices/
     * @secure
     */,
    allergiesStudentAllergiesChoicesRetrieve: <
      T extends FetchKeys<AllergyChoicesResponse>
    >(
      query?: {
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AllergyChoicesResponse, T>, any>({
        path: `/allergies/student-allergies/choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  assignments = {
    /**
     * No description
     *
     * @tags assignments
     * @name AssignmentsList
     * @request GET:/assignments/
     * @secure
     */
    assignmentsList: <
      T extends FetchKeys<PaginatedAssignmentWithSubtopicNamesListList>
    >(
      query?: {
        /** @format uuid */
        course?: string
        /** @format date-time */
        created?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter?: number
        quarter__semester?: number
        students?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<PaginatedAssignmentWithSubtopicNamesListList, T>,
        any
      >({
        path: `/assignments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags assignments
     * @name AssignmentsCreate
     * @request POST:/assignments/
     * @secure
     */,
    assignmentsCreate: <T extends FetchKeys<Assignment>>(
      data: AssignmentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Assignment, T>, any>({
        path: `/assignments/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags assignments
     * @name AssignmentsRetrieve
     * @request GET:/assignments/{id}/
     * @secure
     */,
    assignmentsRetrieve: <T extends FetchKeys<AssignmentRetrieve>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AssignmentRetrieve, T>, any>({
        path: `/assignments/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags assignments
     * @name AssignmentsUpdate
     * @request PUT:/assignments/{id}/
     * @secure
     */,
    assignmentsUpdate: <T extends FetchKeys<AssignmentUpdate>>(
      id: string,
      data: AssignmentUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AssignmentUpdate, T>, any>({
        path: `/assignments/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags assignments
     * @name AssignmentsPartialUpdate
     * @request PATCH:/assignments/{id}/
     * @secure
     */,
    assignmentsPartialUpdate: <T extends FetchKeys<AssignmentUpdate>>(
      id: string,
      data: PatchedAssignmentUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AssignmentUpdate, T>, any>({
        path: `/assignments/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags assignments
     * @name AssignmentsDestroy
     * @request DELETE:/assignments/{id}/
     * @secure
     */,
    assignmentsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/assignments/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags assignments
     * @name AssignmentsItemsList
     * @request GET:/assignments/items/
     * @secure
     */,
    assignmentsItemsList: <T extends FetchKeys<PaginatedAssignmentGradeList>>(
      query?: {
        /** @format uuid */
        assignment__course?: string
        assignment__quarter?: number
        /**
         * * `awaiting_approval` - Awaiting approval
         * * `approved` - Approved
         * * `rejected` - Rejected
         */
        moderated_grade_status?:
          | 'approved'
          | 'awaiting_approval'
          | 'rejected'
          | null
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAssignmentGradeList, T>, any>({
        path: `/assignments/items/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags assignments
     * @name AssignmentsItemsCreate
     * @request POST:/assignments/items/
     * @secure
     */,
    assignmentsItemsCreate: <T extends FetchKeys<AssignmentGradeCreate>>(
      data: AssignmentGradeCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AssignmentGradeCreate, T>, any>({
        path: `/assignments/items/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags assignments
     * @name AssignmentsItemsRetrieve
     * @request GET:/assignments/items/{id}/
     * @secure
     */,
    assignmentsItemsRetrieve: <T extends FetchKeys<AssignmentGrade>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AssignmentGrade, T>, any>({
        path: `/assignments/items/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags assignments
     * @name AssignmentsItemsUpdate
     * @request PUT:/assignments/items/{id}/
     * @secure
     */,
    assignmentsItemsUpdate: <T extends FetchKeys<AssignmentGrade>>(
      id: string,
      data: AssignmentGradeRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AssignmentGrade, T>, any>({
        path: `/assignments/items/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags assignments
     * @name AssignmentsItemsPartialUpdate
     * @request PATCH:/assignments/items/{id}/
     * @secure
     */,
    assignmentsItemsPartialUpdate: <T extends FetchKeys<AssignmentGrade>>(
      id: string,
      data: PatchedAssignmentGradeRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AssignmentGrade, T>, any>({
        path: `/assignments/items/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags assignments
     * @name AssignmentsItemsDestroy
     * @request DELETE:/assignments/items/{id}/
     * @secure
     */,
    assignmentsItemsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/assignments/items/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags assignments
     * @name AssignmentsItemsModeratedApproveCreate
     * @request POST:/assignments/items/{id}/moderated/approve/
     * @secure
     */,
    assignmentsItemsModeratedApproveCreate: <T extends FetchKeys<void>>(
      id: string,
      data: AssignmentModeratedGradeApproveRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, DetailResponseSchema>({
        path: `/assignments/items/${id}/moderated/approve/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags assignments
     * @name AssignmentsItemsModeratedFeedbackRetrieve
     * @request GET:/assignments/items/{id}/moderated/feedback/
     * @secure
     */,
    assignmentsItemsModeratedFeedbackRetrieve: <
      T extends FetchKeys<ModeratedGradeFeedback>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ModeratedGradeFeedback, T>, any>({
        path: `/assignments/items/${id}/moderated/feedback/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags assignments
     * @name AssignmentsItemsModeratedRejectCreate
     * @request POST:/assignments/items/{id}/moderated/reject/
     * @secure
     */,
    assignmentsItemsModeratedRejectCreate: <T extends FetchKeys<void>>(
      id: string,
      data: AssignmentModeratedGradeRejectRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, DetailResponseSchema>({
        path: `/assignments/items/${id}/moderated/reject/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags assignments
     * @name AssignmentsItemsModeratedList
     * @request GET:/assignments/items/moderated/
     * @secure
     */,
    assignmentsItemsModeratedList: <
      T extends FetchKeys<PaginatedAssignmentModeratedGradeListList>
    >(
      query?: {
        /** @format uuid */
        assignment__course?: string
        assignment__quarter?: number
        /**
         * * `awaiting_approval` - Awaiting approval
         * * `approved` - Approved
         * * `rejected` - Rejected
         */
        moderated_grade_status?:
          | 'approved'
          | 'awaiting_approval'
          | 'rejected'
          | null
        /** Sorting results. Available options: 'student__name' | '-student__name', 'artisan__name' | '-artisan__name', 'grade_moderated_at' | '-grade_moderated_at', 'moderated_grade_status_ordering' |  '-moderated_grade_status_ordering' */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAssignmentModeratedGradeListList, T>, any>(
        {
          path: `/assignments/items/moderated/`,
          method: 'GET',
          query: postprocessQuery(query),
          secure: true,
          format: 'json',
          ...params
        }
      )
    /**
     * No description
     *
     * @tags assignments
     * @name AssignmentsItemsModeratedRejectionReasonsList
     * @request GET:/assignments/items/moderated/rejection-reasons/
     * @secure
     */,
    assignmentsItemsModeratedRejectionReasonsList: <
      T extends FetchKeys<StringOption[]>
    >(
      query?: {
        /** @format uuid */
        assignment__course?: string
        assignment__quarter?: number
        /**
         * * `awaiting_approval` - Awaiting approval
         * * `approved` - Approved
         * * `rejected` - Rejected
         */
        moderated_grade_status?:
          | 'approved'
          | 'awaiting_approval'
          | 'rejected'
          | null
        /** Which field to use when ordering the results. */
        ordering?: string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/assignments/items/moderated/rejection-reasons/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Returns a CSV file with students assignements data.
     *
     * @tags assignments
     * @name AssignmentsReportsRetrieve
     * @request GET:/assignments/reports/
     * @secure
     */,
    assignmentsReportsRetrieve: <T extends FetchKeys<string>>(
      query: {
        /** @format date */
        from_date: string
        /** @format date */
        to_date: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<string, T>, any>({
        path: `/assignments/reports/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags assignments
     * @name AssignmentsTypesList
     * @request GET:/assignments/types/
     * @secure
     */,
    assignmentsTypesList: <T extends FetchKeys<AssignmentType[]>>(
      query?: {
        /** @format uuid */
        course?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AssignmentType[], T>, any>({
        path: `/assignments/types/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  attachments = {
    /**
     * No description
     *
     * @tags attachments
     * @name AttachmentsList
     * @request GET:/attachments/{attachmentType}/
     * @secure
     */
    attachmentsList: <T extends FetchKeys<PaginatedAttachmentList>>(
      attachmentType: string,
      query?: {
        /** Multiple values may be separated by commas. */
        id__in?: number[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAttachmentList, T>, any>({
        path: `/attachments/${attachmentType}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags attachments
     * @name AttachmentsCreate
     * @request POST:/attachments/{attachmentType}/
     * @secure
     */,
    attachmentsCreate: <T extends FetchKeys<Attachment>>(
      attachmentType: string,
      data: AttachmentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Attachment, T>, any>({
        path: `/attachments/${attachmentType}/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.UrlEncoded,
        format: 'json',
        ...params
      })
  }
  attendances = {
    /**
     * No description
     *
     * @tags attendances
     * @name AttendancesList
     * @request GET:/attendances/
     * @secure
     */
    attendancesList: <T extends FetchKeys<PaginatedAcademicAttendanceList>>(
      query?: {
        /** @format uuid */
        lesson?: string
        /** @format uuid */
        lesson__activity__course?: string
        /** @format date */
        lesson__date_after?: string
        /** @format date */
        lesson__date_before?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAcademicAttendanceList, T>, any>({
        path: `/attendances/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags attendances
     * @name AttendancesUpdate
     * @request PUT:/attendances/{id}/
     * @secure
     */,
    attendancesUpdate: <T extends FetchKeys<AcademicAttendance>>(
      id: string,
      data: AcademicAttendanceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AcademicAttendance, T>, any>({
        path: `/attendances/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags attendances
     * @name AttendancesPartialUpdate
     * @request PATCH:/attendances/{id}/
     * @secure
     */,
    attendancesPartialUpdate: <T extends FetchKeys<AcademicAttendance>>(
      id: string,
      data: PatchedAcademicAttendanceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AcademicAttendance, T>, any>({
        path: `/attendances/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Return attendances in cumulative format. Requires student in query params. :return response [ { "course": course_name, "attendances_count": 5, "is_present": 2, "is_late": 2, "is_absent_excused": 0, "is_absent_unexcused": 0, "is_sick": 1, "missed_lessons_count": 1, "missed_lessons_percentage": 20.0 } ]
     *
     * @tags attendances
     * @name AttendancesCumulativeRetrieve
     * @request GET:/attendances/cumulative/
     * @secure
     */,
    attendancesCumulativeRetrieve: <T extends FetchKeys<CumulativeResponse>>(
      query?: {
        /** @format uuid */
        lesson?: string
        /** @format uuid */
        lesson__activity__course?: string
        /** @format date */
        lesson__date_after?: string
        /** @format date */
        lesson__date_before?: string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CumulativeResponse, T>, any>({
        path: `/attendances/cumulative/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Returns a CSV file with attendances data.
     *
     * @tags attendances
     * @name AttendancesReportsRetrieve
     * @request GET:/attendances/reports/
     * @secure
     */,
    attendancesReportsRetrieve: <T extends FetchKeys<string>>(
      query: {
        /** @format date */
        from_date: string
        max_grade?: number
        min_grade?: number
        /** @format date */
        to_date: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<string, T>, any>({
        path: `/attendances/reports/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
  }
  billings = {
    /**
     * No description
     *
     * @tags billings
     * @name BillingsAbacusProductsList
     * @request GET:/billings/abacus-products/
     * @secure
     */
    billingsAbacusProductsList: <
      T extends FetchKeys<PaginatedAbacusInvoiceProductList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAbacusInvoiceProductList, T>, any>({
        path: `/billings/abacus-products/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags billings
     * @name BillingsAbacusProductsCreate
     * @request POST:/billings/abacus-products/
     * @secure
     */,
    billingsAbacusProductsCreate: <T extends FetchKeys<AbacusInvoiceProduct>>(
      data: AbacusInvoiceProductRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AbacusInvoiceProduct, T>, any>({
        path: `/billings/abacus-products/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags billings
     * @name BillingsAbacusProductsRetrieve
     * @request GET:/billings/abacus-products/{id}/
     * @secure
     */,
    billingsAbacusProductsRetrieve: <T extends FetchKeys<AbacusInvoiceProduct>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AbacusInvoiceProduct, T>, any>({
        path: `/billings/abacus-products/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags billings
     * @name BillingsAbacusProductsUpdate
     * @request PUT:/billings/abacus-products/{id}/
     * @secure
     */,
    billingsAbacusProductsUpdate: <T extends FetchKeys<AbacusInvoiceProduct>>(
      id: number,
      data: AbacusInvoiceProductRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AbacusInvoiceProduct, T>, any>({
        path: `/billings/abacus-products/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags billings
     * @name BillingsAbacusProductsPartialUpdate
     * @request PATCH:/billings/abacus-products/{id}/
     * @secure
     */,
    billingsAbacusProductsPartialUpdate: <
      T extends FetchKeys<AbacusInvoiceProduct>
    >(
      id: number,
      data: PatchedAbacusInvoiceProductRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AbacusInvoiceProduct, T>, any>({
        path: `/billings/abacus-products/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags billings
     * @name BillingsAbacusProductsDestroy
     * @request DELETE:/billings/abacus-products/{id}/
     * @secure
     */,
    billingsAbacusProductsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/billings/abacus-products/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags billings
     * @name BillingsReportsList
     * @request GET:/billings/reports/
     * @secure
     */,
    billingsReportsList: <T extends FetchKeys<PaginatedBillingReportList>>(
      query?: {
        /** @format uuid */
        author?: string
        /** @format date */
        date?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter?: number
        school_year?: number
        /** A search term. */
        search?: string
        /**
         * * `quarter` - Quarter
         * * `school_year` - School Year
         */
        timeframe_type?: 'quarter' | 'school_year'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedBillingReportList, T>, any>({
        path: `/billings/reports/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags billings
     * @name BillingsReportsCreate
     * @request POST:/billings/reports/
     * @secure
     */,
    billingsReportsCreate: <T extends FetchKeys<BillingReportCreate>>(
      data: BillingReportCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BillingReportCreate, T>, any>({
        path: `/billings/reports/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags billings
     * @name BillingsReportsRetrieve
     * @request GET:/billings/reports/{id}/
     * @secure
     */,
    billingsReportsRetrieve: <T extends FetchKeys<BillingReport>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BillingReport, T>, any>({
        path: `/billings/reports/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags billings
     * @name BillingsReportsUpdate
     * @request PUT:/billings/reports/{id}/
     * @secure
     */,
    billingsReportsUpdate: <T extends FetchKeys<BillingReport>>(
      id: string,
      data: BillingReportRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BillingReport, T>, any>({
        path: `/billings/reports/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags billings
     * @name BillingsReportsPartialUpdate
     * @request PATCH:/billings/reports/{id}/
     * @secure
     */,
    billingsReportsPartialUpdate: <T extends FetchKeys<BillingReport>>(
      id: string,
      data: PatchedBillingReportRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BillingReport, T>, any>({
        path: `/billings/reports/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags billings
     * @name BillingsReportsDestroy
     * @request DELETE:/billings/reports/{id}/
     * @secure
     */,
    billingsReportsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/billings/reports/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags billings
     * @name BillingsReportsDownloadRetrieve
     * @request GET:/billings/reports/{id}/download/
     * @secure
     */,
    billingsReportsDownloadRetrieve: <T extends FetchKeys<File>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<File, T>, any>({
        path: `/billings/reports/${id}/download/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags billings
     * @name BillingsReportsItemsRetrieve
     * @request GET:/billings/reports/{id}/items/
     * @secure
     */,
    billingsReportsItemsRetrieve: <T extends FetchKeys<BillingReportsItems>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BillingReportsItems, T>, any>({
        path: `/billings/reports/${id}/items/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags billings
     * @name BillingsReportsAuthorsList
     * @request GET:/billings/reports/authors/
     * @secure
     */,
    billingsReportsAuthorsList: <T extends FetchKeys<StringOption[]>>(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/billings/reports/authors/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Handler method for HTTP 'OPTIONS' request.
     *
     * @tags billings
     * @name BillingsReportsOptionsRetrieve
     * @request GET:/billings/reports/options/
     * @secure
     */,
    billingsReportsOptionsRetrieve: <
      T extends FetchKeys<BillingReportsOptions>
    >(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BillingReportsOptions, T>, any>({
        path: `/billings/reports/options/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags billings
     * @name BillingsReportsPreviewCreate
     * @request POST:/billings/reports/preview/
     * @secure
     */,
    billingsReportsPreviewCreate: <T extends FetchKeys<BillingReportsPreview>>(
      data: BillingReportCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BillingReportsPreview, T>, any>({
        path: `/billings/reports/preview/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
  }
  boarding = {
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingAccommodationsList
     * @request GET:/boarding/accommodations/
     * @secure
     */
    boardingAccommodationsList: <
      T extends FetchKeys<PaginatedAccommodationList>
    >(
      query?: {
        current_or_future?: boolean
        house?: number
        is_temporary_movement?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        room?: number
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAccommodationList, T>, any>({
        path: `/boarding/accommodations/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingAccommodationsCreate
     * @request POST:/boarding/accommodations/
     * @secure
     */,
    boardingAccommodationsCreate: <T extends FetchKeys<Accommodation>>(
      data: AccommodationRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Accommodation, T>, any>({
        path: `/boarding/accommodations/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingAccommodationsRetrieve
     * @request GET:/boarding/accommodations/{id}/
     * @secure
     */,
    boardingAccommodationsRetrieve: <T extends FetchKeys<Accommodation>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Accommodation, T>, any>({
        path: `/boarding/accommodations/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingAccommodationsUpdate
     * @request PUT:/boarding/accommodations/{id}/
     * @secure
     */,
    boardingAccommodationsUpdate: <T extends FetchKeys<Accommodation>>(
      id: number,
      data: AccommodationRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Accommodation, T>, any>({
        path: `/boarding/accommodations/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingAccommodationsPartialUpdate
     * @request PATCH:/boarding/accommodations/{id}/
     * @secure
     */,
    boardingAccommodationsPartialUpdate: <T extends FetchKeys<Accommodation>>(
      id: number,
      data: PatchedAccommodationRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Accommodation, T>, any>({
        path: `/boarding/accommodations/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingAccommodationsDestroy
     * @request DELETE:/boarding/accommodations/{id}/
     * @secure
     */,
    boardingAccommodationsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/boarding/accommodations/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingAttendancesList
     * @request GET:/boarding/attendances/
     * @secure
     */,
    boardingAttendancesList: <T extends FetchKeys<BoardingAttendance[]>>(
      query?: {
        /**
         * * `present` - Present
         * * `late` - Late
         * * `sick` - Sick
         * * `absent_excused` - Absent exc.
         * * `absent_unexcused` - Absent unexc.
         */
        mark?:
          | 'absent_excused'
          | 'absent_unexcused'
          | 'late'
          | 'present'
          | 'sick'
        /** Which field to use when ordering the results. */
        ordering?: string
        report?: number
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BoardingAttendance[], T>, any>({
        path: `/boarding/attendances/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingAttendancesCreate
     * @request POST:/boarding/attendances/
     * @secure
     */,
    boardingAttendancesCreate: <T extends FetchKeys<BoardingAttendance>>(
      data: BoardingAttendanceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BoardingAttendance, T>, any>({
        path: `/boarding/attendances/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingAttendancesRetrieve
     * @request GET:/boarding/attendances/{id}/
     * @secure
     */,
    boardingAttendancesRetrieve: <T extends FetchKeys<BoardingAttendance>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BoardingAttendance, T>, any>({
        path: `/boarding/attendances/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingAttendancesUpdate
     * @request PUT:/boarding/attendances/{id}/
     * @secure
     */,
    boardingAttendancesUpdate: <T extends FetchKeys<BoardingAttendance>>(
      id: string,
      data: BoardingAttendanceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BoardingAttendance, T>, any>({
        path: `/boarding/attendances/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingAttendancesPartialUpdate
     * @request PATCH:/boarding/attendances/{id}/
     * @secure
     */,
    boardingAttendancesPartialUpdate: <T extends FetchKeys<BoardingAttendance>>(
      id: string,
      data: PatchedBoardingAttendanceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BoardingAttendance, T>, any>({
        path: `/boarding/attendances/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingAttendancesDestroy
     * @request DELETE:/boarding/attendances/{id}/
     * @secure
     */,
    boardingAttendancesDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/boarding/attendances/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingAttendancesMarksList
     * @request GET:/boarding/attendances/marks/
     * @secure
     */,
    boardingAttendancesMarksList: <T extends FetchKeys<StringOption[]>>(
      query?: {
        /**
         * * `present` - Present
         * * `late` - Late
         * * `sick` - Sick
         * * `absent_excused` - Absent exc.
         * * `absent_unexcused` - Absent unexc.
         */
        mark?:
          | 'absent_excused'
          | 'absent_unexcused'
          | 'late'
          | 'present'
          | 'sick'
        /** Which field to use when ordering the results. */
        ordering?: string
        report?: number
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/boarding/attendances/marks/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags boarding
     * @name BoardingAttendancesReportsList
     * @request GET:/boarding/attendances/reports/
     * @secure
     */,
    boardingAttendancesReportsList: <
      T extends FetchKeys<PaginatedBoardingAttendanceReportListList>
    >(
      query?: {
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        for_senior_campus?: boolean
        /**
         * * `male` - Male
         * * `female` - Female
         */
        for_senior_campus_type?: 'female' | 'male' | null
        house?: number
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /**
         * * `new` - New
         * * `incomplete` - Incomplete
         * * `finished` - Finished
         */
        status?: 'finished' | 'incomplete' | 'new'
        /** @format uuid */
        tutor?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedBoardingAttendanceReportListList, T>, any>(
        {
          path: `/boarding/attendances/reports/`,
          method: 'GET',
          query: postprocessQuery(query),
          secure: true,
          format: 'json',
          ...params
        }
      )
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags boarding
     * @name BoardingAttendancesReportsCreate
     * @request POST:/boarding/attendances/reports/
     * @secure
     */,
    boardingAttendancesReportsCreate: <
      T extends FetchKeys<BoardingAttendanceReport>
    >(
      data: BoardingAttendanceReportRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BoardingAttendanceReport, T>, any>({
        path: `/boarding/attendances/reports/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags boarding
     * @name BoardingAttendancesReportsRetrieve
     * @request GET:/boarding/attendances/reports/{id}/
     * @secure
     */,
    boardingAttendancesReportsRetrieve: <
      T extends FetchKeys<BoardingAttendanceReport>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BoardingAttendanceReport, T>, any>({
        path: `/boarding/attendances/reports/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags boarding
     * @name BoardingAttendancesReportsUpdate
     * @request PUT:/boarding/attendances/reports/{id}/
     * @secure
     */,
    boardingAttendancesReportsUpdate: <
      T extends FetchKeys<BoardingAttendanceReport>
    >(
      id: number,
      data: BoardingAttendanceReportRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BoardingAttendanceReport, T>, any>({
        path: `/boarding/attendances/reports/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags boarding
     * @name BoardingAttendancesReportsPartialUpdate
     * @request PATCH:/boarding/attendances/reports/{id}/
     * @secure
     */,
    boardingAttendancesReportsPartialUpdate: <
      T extends FetchKeys<BoardingAttendanceReport>
    >(
      id: number,
      data: PatchedBoardingAttendanceReportRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BoardingAttendanceReport, T>, any>({
        path: `/boarding/attendances/reports/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags boarding
     * @name BoardingAttendancesReportsDestroy
     * @request DELETE:/boarding/attendances/reports/{id}/
     * @secure
     */,
    boardingAttendancesReportsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/boarding/attendances/reports/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags boarding
     * @name BoardingAttendancesReportsAvailableFiltersRetrieve
     * @request GET:/boarding/attendances/reports/available-filters/
     * @secure
     */,
    boardingAttendancesReportsAvailableFiltersRetrieve: <
      T extends FetchKeys<BoardingAttendanceReportAvailableFilters>
    >(
      query?: {
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        for_senior_campus?: boolean
        /**
         * * `male` - Male
         * * `female` - Female
         */
        for_senior_campus_type?: 'female' | 'male' | null
        house?: number
        /** Which field to use when ordering the results. */
        ordering?: string
        /**
         * * `new` - New
         * * `incomplete` - Incomplete
         * * `finished` - Finished
         */
        status?: 'finished' | 'incomplete' | 'new'
        /** @format uuid */
        tutor?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BoardingAttendanceReportAvailableFilters, T>, any>({
        path: `/boarding/attendances/reports/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags boarding
     * @name BoardingAttendancesReportsStatusesList
     * @request GET:/boarding/attendances/reports/statuses/
     * @secure
     */,
    boardingAttendancesReportsStatusesList: <
      T extends FetchKeys<StringOption[]>
    >(
      query?: {
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        for_senior_campus?: boolean
        /**
         * * `male` - Male
         * * `female` - Female
         */
        for_senior_campus_type?: 'female' | 'male' | null
        house?: number
        /** Which field to use when ordering the results. */
        ordering?: string
        /**
         * * `new` - New
         * * `incomplete` - Incomplete
         * * `finished` - Finished
         */
        status?: 'finished' | 'incomplete' | 'new'
        /** @format uuid */
        tutor?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/boarding/attendances/reports/statuses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingBoardingCommentsList
     * @request GET:/boarding/boarding-comments/
     * @secure
     */,
    boardingBoardingCommentsList: <
      T extends FetchKeys<PaginatedBoardingCommentList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        school_year?: number
        /** @format uuid */
        student?: string
        weekly?: boolean

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedBoardingCommentList, T>, any>({
        path: `/boarding/boarding-comments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingBoardingCommentsCreate
     * @request POST:/boarding/boarding-comments/
     * @secure
     */,
    boardingBoardingCommentsCreate: <T extends FetchKeys<BoardingComment>>(
      data: BoardingCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BoardingComment, T>, any>({
        path: `/boarding/boarding-comments/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingBoardingCommentsRetrieve
     * @request GET:/boarding/boarding-comments/{id}/
     * @secure
     */,
    boardingBoardingCommentsRetrieve: <T extends FetchKeys<BoardingComment>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BoardingComment, T>, any>({
        path: `/boarding/boarding-comments/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingBoardingCommentsUpdate
     * @request PUT:/boarding/boarding-comments/{id}/
     * @secure
     */,
    boardingBoardingCommentsUpdate: <T extends FetchKeys<BoardingComment>>(
      id: string,
      data: BoardingCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BoardingComment, T>, any>({
        path: `/boarding/boarding-comments/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingBoardingCommentsPartialUpdate
     * @request PATCH:/boarding/boarding-comments/{id}/
     * @secure
     */,
    boardingBoardingCommentsPartialUpdate: <
      T extends FetchKeys<BoardingComment>
    >(
      id: string,
      data: PatchedBoardingCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BoardingComment, T>, any>({
        path: `/boarding/boarding-comments/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingBoardingCommentsDestroy
     * @request DELETE:/boarding/boarding-comments/{id}/
     * @secure
     */,
    boardingBoardingCommentsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/boarding/boarding-comments/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingHousesList
     * @request GET:/boarding/houses/
     * @secure
     */,
    boardingHousesList: <T extends FetchKeys<PaginatedHouseListList>>(
      query?: {
        /**
         * * `male` - Male
         * * `female` - Female
         */
        gender?: 'female' | 'male'
        generate_night_attendance_reports?: boolean
        is_female_campus?: boolean
        is_male_campus?: boolean
        is_senior_campus?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedHouseListList, T>, any>({
        path: `/boarding/houses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingHousesCreate
     * @request POST:/boarding/houses/
     * @secure
     */,
    boardingHousesCreate: <T extends FetchKeys<House>>(
      data: HouseRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<House, T>, any>({
        path: `/boarding/houses/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingHousesRetrieve
     * @request GET:/boarding/houses/{id}/
     * @secure
     */,
    boardingHousesRetrieve: <T extends FetchKeys<House>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<House, T>, any>({
        path: `/boarding/houses/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingHousesUpdate
     * @request PUT:/boarding/houses/{id}/
     * @secure
     */,
    boardingHousesUpdate: <T extends FetchKeys<House>>(
      id: number,
      data: HouseRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<House, T>, any>({
        path: `/boarding/houses/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingHousesPartialUpdate
     * @request PATCH:/boarding/houses/{id}/
     * @secure
     */,
    boardingHousesPartialUpdate: <T extends FetchKeys<House>>(
      id: number,
      data: PatchedHouseRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<House, T>, any>({
        path: `/boarding/houses/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags boarding
     * @name BoardingHousesRoomsList
     * @request GET:/boarding/houses/rooms/
     * @secure
     */,
    boardingHousesRoomsList: <T extends FetchKeys<PaginatedHouseRoomListList>>(
      query?: {
        booked_for?: string
        code?: string
        house?: number
        id?: number
        name?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        status?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedHouseRoomListList, T>, any>({
        path: `/boarding/houses/rooms/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags boarding
     * @name BoardingHousesRoomsRetrieve
     * @request GET:/boarding/houses/rooms/{id}/
     * @secure
     */,
    boardingHousesRoomsRetrieve: <T extends FetchKeys<HouseRoom>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<HouseRoom, T>, any>({
        path: `/boarding/houses/rooms/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags boarding
     * @name BoardingHousesRoomsUpdate
     * @request PUT:/boarding/houses/rooms/{id}/
     * @secure
     */,
    boardingHousesRoomsUpdate: <T extends FetchKeys<HouseRoom>>(
      id: number,
      data: HouseRoomRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<HouseRoom, T>, any>({
        path: `/boarding/houses/rooms/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags boarding
     * @name BoardingHousesRoomsPartialUpdate
     * @request PATCH:/boarding/houses/rooms/{id}/
     * @secure
     */,
    boardingHousesRoomsPartialUpdate: <T extends FetchKeys<HouseRoom>>(
      id: number,
      data: PatchedHouseRoomRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<HouseRoom, T>, any>({
        path: `/boarding/houses/rooms/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description cancel (remove) all bookings/accommodations made for specific room, having both -> cancel only bookings
     *
     * @tags boarding
     * @name BoardingHousesRoomsCancelBookingCreate
     * @request POST:/boarding/houses/rooms/{id}/cancel-booking/
     * @secure
     */,
    boardingHousesRoomsCancelBookingCreate: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/boarding/houses/rooms/${id}/cancel-booking/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags boarding
     * @name BoardingHousesRoomsAvailableFiltersRetrieve
     * @request GET:/boarding/houses/rooms/available-filters/
     * @secure
     */,
    boardingHousesRoomsAvailableFiltersRetrieve: <
      T extends FetchKeys<HouseRoomAvailableFilters>
    >(
      query?: {
        booked_for?: string
        code?: string
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        house?: number
        id?: number
        name?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        status?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<HouseRoomAvailableFilters, T>, any>({
        path: `/boarding/houses/rooms/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags boarding
     * @name BoardingHousesRoomsCodesList
     * @request GET:/boarding/houses/rooms/codes/
     * @secure
     */,
    boardingHousesRoomsCodesList: <
      T extends FetchKeys<PaginatedStringOptionList>
    >(
      query?: {
        booked_for?: string
        code?: string
        house?: number
        id?: number
        name?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        status?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStringOptionList, T>, any>({
        path: `/boarding/houses/rooms/codes/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingHousesTutorsList
     * @request GET:/boarding/houses/tutors/
     * @secure
     */,
    boardingHousesTutorsList: <T extends FetchKeys<PaginatedTutorList>>(
      query?: {
        is_active?: boolean
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTutorList, T>, any>({
        path: `/boarding/houses/tutors/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingLocationsList
     * @request GET:/boarding/locations/
     * @secure
     */,
    boardingLocationsList: <T extends FetchKeys<PaginatedLocationList>>(
      query?: {
        house?: number
        is_academic_location?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedLocationList, T>, any>({
        path: `/boarding/locations/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesList
     * @request GET:/boarding/night-attendances/
     * @secure
     */,
    boardingNightAttendancesList: <T extends FetchKeys<NightAttendance[]>>(
      query?: {
        house?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        report?: number
        /**
         * * `male` - Male
         * * `female` - Female
         */
        student__gender?: 'female' | 'male'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NightAttendance[], T>, any>({
        path: `/boarding/night-attendances/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesCommentsList
     * @request GET:/boarding/night-attendances/{attendanceId}/comments/
     * @secure
     */,
    boardingNightAttendancesCommentsList: <
      T extends FetchKeys<NightAttendanceComment[]>
    >(
      attendanceId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NightAttendanceComment[], T>, any>({
        path: `/boarding/night-attendances/${attendanceId}/comments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesCommentsCreate
     * @request POST:/boarding/night-attendances/{attendanceId}/comments/
     * @secure
     */,
    boardingNightAttendancesCommentsCreate: <
      T extends FetchKeys<NightAttendanceComment>
    >(
      attendanceId: string,
      data: NightAttendanceCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NightAttendanceComment, T>, any>({
        path: `/boarding/night-attendances/${attendanceId}/comments/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesCommentsRetrieve
     * @request GET:/boarding/night-attendances/{attendanceId}/comments/{id}/
     * @secure
     */,
    boardingNightAttendancesCommentsRetrieve: <
      T extends FetchKeys<NightAttendanceComment>
    >(
      attendanceId: string,
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NightAttendanceComment, T>, any>({
        path: `/boarding/night-attendances/${attendanceId}/comments/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesCommentsUpdate
     * @request PUT:/boarding/night-attendances/{attendanceId}/comments/{id}/
     * @secure
     */,
    boardingNightAttendancesCommentsUpdate: <
      T extends FetchKeys<NightAttendanceComment>
    >(
      attendanceId: string,
      id: string,
      data: NightAttendanceCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NightAttendanceComment, T>, any>({
        path: `/boarding/night-attendances/${attendanceId}/comments/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesCommentsPartialUpdate
     * @request PATCH:/boarding/night-attendances/{attendanceId}/comments/{id}/
     * @secure
     */,
    boardingNightAttendancesCommentsPartialUpdate: <
      T extends FetchKeys<NightAttendanceComment>
    >(
      attendanceId: string,
      id: string,
      data: PatchedNightAttendanceCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NightAttendanceComment, T>, any>({
        path: `/boarding/night-attendances/${attendanceId}/comments/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesCommentsDestroy
     * @request DELETE:/boarding/night-attendances/{attendanceId}/comments/{id}/
     * @secure
     */,
    boardingNightAttendancesCommentsDestroy: <T extends FetchKeys<void>>(
      attendanceId: string,
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/boarding/night-attendances/${attendanceId}/comments/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesCommentsVersionsRetrieve
     * @request GET:/boarding/night-attendances/{attendanceId}/comments/{id}/versions/
     * @secure
     */,
    boardingNightAttendancesCommentsVersionsRetrieve: <
      T extends FetchKeys<NightAttendanceComment>
    >(
      attendanceId: string,
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NightAttendanceComment, T>, any>({
        path: `/boarding/night-attendances/${attendanceId}/comments/${id}/versions/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesUpdate
     * @request PUT:/boarding/night-attendances/{id}/
     * @secure
     */,
    boardingNightAttendancesUpdate: <T extends FetchKeys<NightAttendance>>(
      id: string,
      data: NightAttendanceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NightAttendance, T>, any>({
        path: `/boarding/night-attendances/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesPartialUpdate
     * @request PATCH:/boarding/night-attendances/{id}/
     * @secure
     */,
    boardingNightAttendancesPartialUpdate: <
      T extends FetchKeys<NightAttendance>
    >(
      id: string,
      data: PatchedNightAttendanceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NightAttendance, T>, any>({
        path: `/boarding/night-attendances/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesMarksList
     * @request GET:/boarding/night-attendances/marks/
     * @secure
     */,
    boardingNightAttendancesMarksList: <T extends FetchKeys<StringOption[]>>(
      query?: {
        house?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        report?: number
        /**
         * * `male` - Male
         * * `female` - Female
         */
        student__gender?: 'female' | 'male'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/boarding/night-attendances/marks/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesReportRetrieve
     * @request GET:/boarding/night-attendances/report/
     * @secure
     */,
    boardingNightAttendancesReportRetrieve: <T extends FetchKeys<void>>(
      query?: {
        house?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        report?: number
        /**
         * * `male` - Male
         * * `female` - Female
         */
        student__gender?: 'female' | 'male'
        week?: number
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/boarding/night-attendances/report/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesReportsList
     * @request GET:/boarding/night-attendances/reports/
     * @secure
     */,
    boardingNightAttendancesReportsList: <
      T extends FetchKeys<PaginatedNightAttendanceReportListList>
    >(
      query?: {
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        /**
         * * `male` - Male
         * * `female` - Female
         */
        for_gender?: 'female' | 'male'
        has_disruption?: boolean
        /** @format uuid */
        night_guard?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /**
         * * `new` - New
         * * `incomplete` - Incomplete
         * * `finished` - Finished
         */
        status?: 'finished' | 'incomplete' | 'new'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedNightAttendanceReportListList, T>, any>({
        path: `/boarding/night-attendances/reports/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesReportsCreate
     * @request POST:/boarding/night-attendances/reports/
     * @secure
     */,
    boardingNightAttendancesReportsCreate: <
      T extends FetchKeys<NightAttendanceReport>
    >(
      data: NightAttendanceReportRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NightAttendanceReport, T>, any>({
        path: `/boarding/night-attendances/reports/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesReportsRetrieve
     * @request GET:/boarding/night-attendances/reports/{id}/
     * @secure
     */,
    boardingNightAttendancesReportsRetrieve: <
      T extends FetchKeys<NightAttendanceReport>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NightAttendanceReport, T>, any>({
        path: `/boarding/night-attendances/reports/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesReportsUpdate
     * @request PUT:/boarding/night-attendances/reports/{id}/
     * @secure
     */,
    boardingNightAttendancesReportsUpdate: <
      T extends FetchKeys<NightAttendanceReport>
    >(
      id: number,
      data: NightAttendanceReportRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NightAttendanceReport, T>, any>({
        path: `/boarding/night-attendances/reports/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesReportsPartialUpdate
     * @request PATCH:/boarding/night-attendances/reports/{id}/
     * @secure
     */,
    boardingNightAttendancesReportsPartialUpdate: <
      T extends FetchKeys<NightAttendanceReport>
    >(
      id: number,
      data: PatchedNightAttendanceReportRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NightAttendanceReport, T>, any>({
        path: `/boarding/night-attendances/reports/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesReportsDestroy
     * @request DELETE:/boarding/night-attendances/reports/{id}/
     * @secure
     */,
    boardingNightAttendancesReportsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/boarding/night-attendances/reports/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesReportsCreatorsList
     * @request GET:/boarding/night-attendances/reports/creators/
     * @secure
     */,
    boardingNightAttendancesReportsCreatorsList: <
      T extends FetchKeys<StringOption[]>
    >(
      query?: {
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        /**
         * * `male` - Male
         * * `female` - Female
         */
        for_gender?: 'female' | 'male'
        has_disruption?: boolean
        /** @format uuid */
        night_guard?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /**
         * * `new` - New
         * * `incomplete` - Incomplete
         * * `finished` - Finished
         */
        status?: 'finished' | 'incomplete' | 'new'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/boarding/night-attendances/reports/creators/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingNightAttendancesReportsStatusesList
     * @request GET:/boarding/night-attendances/reports/statuses/
     * @secure
     */,
    boardingNightAttendancesReportsStatusesList: <
      T extends FetchKeys<StringOption[]>
    >(
      query?: {
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        /**
         * * `male` - Male
         * * `female` - Female
         */
        for_gender?: 'female' | 'male'
        has_disruption?: boolean
        /** @format uuid */
        night_guard?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /**
         * * `new` - New
         * * `incomplete` - Incomplete
         * * `finished` - Finished
         */
        status?: 'finished' | 'incomplete' | 'new'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/boarding/night-attendances/reports/statuses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaList
     * @request GET:/boarding/rota/
     * @secure
     */,
    boardingRotaList: <T extends FetchKeys<RotaTimetable[]>>(
      query?: {
        timetable?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RotaTimetable[], T>, any>({
        path: `/boarding/rota/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaActivitiesList
     * @request GET:/boarding/rota-activities/
     * @secure
     */,
    boardingRotaActivitiesList: <
      T extends FetchKeys<PaginatedDraftRotaActivityResponseList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        rota_timetable?: number
        timetable__enabled_preview_for_teachers?: boolean
        user?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedDraftRotaActivityResponseList, T>, any>({
        path: `/boarding/rota-activities/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaActivitiesCreate
     * @request POST:/boarding/rota-activities/
     * @secure
     */,
    boardingRotaActivitiesCreate: <T extends FetchKeys<DraftRotaActivity>>(
      data: DraftRotaActivityRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DraftRotaActivity, T>, any>({
        path: `/boarding/rota-activities/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaActivitiesRetrieve
     * @request GET:/boarding/rota-activities/{id}/
     * @secure
     */,
    boardingRotaActivitiesRetrieve: <
      T extends FetchKeys<DraftRotaActivityResponse>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DraftRotaActivityResponse, T>, any>({
        path: `/boarding/rota-activities/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaActivitiesUpdate
     * @request PUT:/boarding/rota-activities/{id}/
     * @secure
     */,
    boardingRotaActivitiesUpdate: <T extends FetchKeys<DraftRotaActivity>>(
      id: string,
      data: DraftRotaActivityRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DraftRotaActivity, T>, any>({
        path: `/boarding/rota-activities/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaActivitiesPartialUpdate
     * @request PATCH:/boarding/rota-activities/{id}/
     * @secure
     */,
    boardingRotaActivitiesPartialUpdate: <
      T extends FetchKeys<DraftRotaActivity>
    >(
      id: string,
      data: PatchedDraftRotaActivityRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DraftRotaActivity, T>, any>({
        path: `/boarding/rota-activities/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaActivitiesDestroy
     * @request DELETE:/boarding/rota-activities/{id}/
     * @secure
     */,
    boardingRotaActivitiesDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/boarding/rota-activities/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaActivitiesCheckConflictsUpdate
     * @request PUT:/boarding/rota-activities/{id}/check-conflicts/
     * @secure
     */,
    boardingRotaActivitiesCheckConflictsUpdate: <
      T extends FetchKeys<DraftRotaActivityConflictCheck>
    >(
      id: string,
      data: DraftRotaActivityConflictCheckRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DraftRotaActivityConflictCheck, T>, any>({
        path: `/boarding/rota-activities/${id}/check-conflicts/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaActivitiesCheckConflictsPartialUpdate
     * @request PATCH:/boarding/rota-activities/{id}/check-conflicts/
     * @secure
     */,
    boardingRotaActivitiesCheckConflictsPartialUpdate: <
      T extends FetchKeys<DraftRotaActivityConflictCheck>
    >(
      id: string,
      data: PatchedDraftRotaActivityConflictCheckRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DraftRotaActivityConflictCheck, T>, any>({
        path: `/boarding/rota-activities/${id}/check-conflicts/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaActivitiesCheckConstraintsUpdate
     * @request PUT:/boarding/rota-activities/{id}/check-constraints/
     * @secure
     */,
    boardingRotaActivitiesCheckConstraintsUpdate: <
      T extends FetchKeys<DraftRotaActivityConstraintCheck>
    >(
      id: string,
      data: DraftRotaActivityConstraintCheckRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DraftRotaActivityConstraintCheck, T>, any>({
        path: `/boarding/rota-activities/${id}/check-constraints/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaActivitiesCheckConstraintsDestroy
     * @request DELETE:/boarding/rota-activities/{id}/check-constraints/
     * @secure
     */,
    boardingRotaActivitiesCheckConstraintsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/boarding/rota-activities/${id}/check-constraints/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaActivitiesCheckCoverConflictsUpdate
     * @request PUT:/boarding/rota-activities/{id}/check-cover-conflicts/
     * @secure
     */,
    boardingRotaActivitiesCheckCoverConflictsUpdate: <
      T extends FetchKeys<void>
    >(
      id: string,
      data: CoverConflictRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/boarding/rota-activities/${id}/check-cover-conflicts/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaActivitiesCheckCoverConflictsPartialUpdate
     * @request PATCH:/boarding/rota-activities/{id}/check-cover-conflicts/
     * @secure
     */,
    boardingRotaActivitiesCheckCoverConflictsPartialUpdate: <
      T extends FetchKeys<void>
    >(
      id: string,
      data: PatchedCoverConflictRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/boarding/rota-activities/${id}/check-cover-conflicts/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaActivitiesCheckConflictsCreate
     * @request POST:/boarding/rota-activities/check-conflicts/
     * @secure
     */,
    boardingRotaActivitiesCheckConflictsCreate: <
      T extends FetchKeys<DraftRotaActivityConflictCheck>
    >(
      data: DraftRotaActivityConflictCheckRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DraftRotaActivityConflictCheck, T>, any>({
        path: `/boarding/rota-activities/check-conflicts/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaActivitiesCheckConstraintsCreate
     * @request POST:/boarding/rota-activities/check-constraints/
     * @secure
     */,
    boardingRotaActivitiesCheckConstraintsCreate: <
      T extends FetchKeys<DraftRotaActivityConstraintCheck>
    >(
      data: DraftRotaActivityConstraintCheckRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DraftRotaActivityConstraintCheck, T>, any>({
        path: `/boarding/rota-activities/check-constraints/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaActivitiesSuggestionsCreate
     * @request POST:/boarding/rota-activities/suggestions/
     * @secure
     */,
    boardingRotaActivitiesSuggestionsCreate: <
      T extends FetchKeys<SuggestionsResponse[]>
    >(
      data: SuggestionsRequestRequest,
      query?: {
        rota_timetable?: number
        timetable__enabled_preview_for_teachers?: boolean
        user?: string[]
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SuggestionsResponse[], T>, any>({
        path: `/boarding/rota-activities/suggestions/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaRetrieve
     * @request GET:/boarding/rota/{id}/
     * @secure
     */,
    boardingRotaRetrieve: <T extends FetchKeys<RotaTimetable>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RotaTimetable, T>, any>({
        path: `/boarding/rota/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaBusyUsersAndRoomsCreate
     * @request POST:/boarding/rota/{id}/busy-users-and-rooms/
     * @secure
     */,
    boardingRotaBusyUsersAndRoomsCreate: <
      T extends FetchKeys<BusyUsersResponse>
    >(
      id: number,
      data: BusyUsersRequestRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BusyUsersResponse, T>, any>({
        path: `/boarding/rota/${id}/busy-users-and-rooms/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingRotaChangesList
     * @request GET:/boarding/rota/{id}/changes/
     * @secure
     */,
    boardingRotaChangesList: <T extends FetchKeys<PaginatedLogEntryList>>(
      id: number,
      query?: {
        is_imported?: boolean
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedLogEntryList, T>, any>({
        path: `/boarding/rota/${id}/changes/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaMarkAsDraftCreate
     * @request POST:/boarding/rota/{id}/mark-as-draft/
     * @secure
     */,
    boardingRotaMarkAsDraftCreate: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/boarding/rota/${id}/mark-as-draft/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaMarkAsSelectedForImportCreate
     * @request POST:/boarding/rota/{id}/mark-as-selected-for-import/
     * @secure
     */,
    boardingRotaMarkAsSelectedForImportCreate: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/boarding/rota/${id}/mark-as-selected-for-import/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingRotaStaffTeamsList
     * @request GET:/boarding/rota/{rotaTimetableId}/staff/teams/
     * @secure
     */,
    boardingRotaStaffTeamsList: <T extends FetchKeys<SimpleTeam[]>>(
      rotaTimetableId: string,
      query?: {
        /** Return only users or teams referenced by a DraftRotaActivity in this rota timetable. */
        only_with_draft_rota_activities?: boolean
        /**
         * * `boarding_director` - Boarding Director
         * * `campus_head` - Campus Head
         * * `deputy_campus_head_main` - Deputy Campus Head (Main)
         * * `deputy_campus_head_senior` - Deputy Campus Head (Sen)
         * * `head_of_house` - Head of House
         * * `house_tutor` - House Tutor
         */
        roles?: (
          | 'boarding_director'
          | 'campus_head'
          | 'deputy_campus_head_main'
          | 'deputy_campus_head_senior'
          | 'head_of_house'
          | 'house_tutor'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SimpleTeam[], T>, any>({
        path: `/boarding/rota/${rotaTimetableId}/staff/teams/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingRotaStaffUsersList
     * @request GET:/boarding/rota/{rotaTimetableId}/staff/users/
     * @secure
     */,
    boardingRotaStaffUsersList: <T extends FetchKeys<StaffMember[]>>(
      rotaTimetableId: string,
      query?: {
        /** Return only users or teams referenced by a DraftRotaActivity in this rota timetable. */
        only_with_draft_rota_activities?: boolean
        /**
         * * `boarding_director` - Boarding Director
         * * `campus_head` - Campus Head
         * * `deputy_campus_head_main` - Deputy Campus Head (Main)
         * * `deputy_campus_head_senior` - Deputy Campus Head (Sen)
         * * `head_of_house` - Head of House
         * * `house_tutor` - House Tutor
         */
        roles?: (
          | 'boarding_director'
          | 'campus_head'
          | 'deputy_campus_head_main'
          | 'deputy_campus_head_senior'
          | 'head_of_house'
          | 'house_tutor'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StaffMember[], T>, any>({
        path: `/boarding/rota/${rotaTimetableId}/staff/users/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaGenerationList
     * @request GET:/boarding/rota/generation/
     * @secure
     */,
    boardingRotaGenerationList: <
      T extends FetchKeys<PaginatedRotaGenerationListList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /**
         * * `new` - New
         * * `in_progress` - In progress
         * * `failed` - Generation has failed
         * * `success` - Success
         */
        status?: 'failed' | 'in_progress' | 'new' | 'success'
        timetable?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedRotaGenerationListList, T>, any>({
        path: `/boarding/rota/generation/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaGenerationCreate
     * @request POST:/boarding/rota/generation/
     * @secure
     */,
    boardingRotaGenerationCreate: <T extends FetchKeys<RotaGenerationTask>>(
      data: RotaGenerationTaskRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RotaGenerationTask, T>, any>({
        path: `/boarding/rota/generation/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags boarding
     * @name BoardingRotaGenerationCallbackCreate
     * @request POST:/boarding/rota/generation-callback/
     * @secure
     */,
    boardingRotaGenerationCallbackCreate: <
      T extends FetchKeys<RotaGenerationCallback>
    >(
      data: RotaGenerationCallbackRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RotaGenerationCallback, T>, any>({
        path: `/boarding/rota/generation-callback/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaGenerationRetrieve
     * @request GET:/boarding/rota/generation/{id}/
     * @secure
     */,
    boardingRotaGenerationRetrieve: <
      T extends FetchKeys<RotaGenerationTaskRetrieve>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RotaGenerationTaskRetrieve, T>, any>({
        path: `/boarding/rota/generation/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaGenerationActiveList
     * @request GET:/boarding/rota/generation/active/
     * @secure
     */,
    boardingRotaGenerationActiveList: <
      T extends FetchKeys<RotaGenerationTask[]>
    >(
      query?: {
        /**
         * * `new` - New
         * * `in_progress` - In progress
         * * `failed` - Generation has failed
         * * `success` - Success
         */
        status?: 'failed' | 'in_progress' | 'new' | 'success'
        timetable?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RotaGenerationTask[], T>, any>({
        path: `/boarding/rota/generation/active/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags boarding
     * @name BoardingRotaGenerationCopyRotaCreate
     * @request POST:/boarding/rota/generation/copy-rota/
     * @secure
     */,
    boardingRotaGenerationCopyRotaCreate: <
      T extends FetchKeys<RotaGenerationTask>
    >(
      data: CopyRotaGenerationRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RotaGenerationTask, T>, any>({
        path: `/boarding/rota/generation/copy-rota/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
  }
  campusAbsences = {
    /**
     * No description
     *
     * @tags campus-absences
     * @name CampusAbsencesCommentsList
     * @request GET:/campus-absences/{campusAbsenceId}/comments/
     * @secure
     */
    campusAbsencesCommentsList: <T extends FetchKeys<CampusAbsenceComment[]>>(
      campusAbsenceId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CampusAbsenceComment[], T>, any>({
        path: `/campus-absences/${campusAbsenceId}/comments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags campus-absences
     * @name CampusAbsencesCommentsCreate
     * @request POST:/campus-absences/{campusAbsenceId}/comments/
     * @secure
     */,
    campusAbsencesCommentsCreate: <T extends FetchKeys<CampusAbsenceComment>>(
      campusAbsenceId: string,
      data: CampusAbsenceCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CampusAbsenceComment, T>, any>({
        path: `/campus-absences/${campusAbsenceId}/comments/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags campus-absences
     * @name CampusAbsencesCommentsRetrieve
     * @request GET:/campus-absences/{campusAbsenceId}/comments/{id}/
     * @secure
     */,
    campusAbsencesCommentsRetrieve: <T extends FetchKeys<CampusAbsenceComment>>(
      campusAbsenceId: string,
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CampusAbsenceComment, T>, any>({
        path: `/campus-absences/${campusAbsenceId}/comments/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags campus-absences
     * @name CampusAbsencesCommentsUpdate
     * @request PUT:/campus-absences/{campusAbsenceId}/comments/{id}/
     * @secure
     */,
    campusAbsencesCommentsUpdate: <T extends FetchKeys<CampusAbsenceComment>>(
      campusAbsenceId: string,
      id: string,
      data: CampusAbsenceCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CampusAbsenceComment, T>, any>({
        path: `/campus-absences/${campusAbsenceId}/comments/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags campus-absences
     * @name CampusAbsencesCommentsPartialUpdate
     * @request PATCH:/campus-absences/{campusAbsenceId}/comments/{id}/
     * @secure
     */,
    campusAbsencesCommentsPartialUpdate: <
      T extends FetchKeys<CampusAbsenceComment>
    >(
      campusAbsenceId: string,
      id: string,
      data: PatchedCampusAbsenceCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CampusAbsenceComment, T>, any>({
        path: `/campus-absences/${campusAbsenceId}/comments/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags campus-absences
     * @name CampusAbsencesCommentsDestroy
     * @request DELETE:/campus-absences/{campusAbsenceId}/comments/{id}/
     * @secure
     */,
    campusAbsencesCommentsDestroy: <T extends FetchKeys<void>>(
      campusAbsenceId: string,
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/campus-absences/${campusAbsenceId}/comments/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags campus-absences
     * @name CampusAbsencesCommentsVersionsRetrieve
     * @request GET:/campus-absences/{campusAbsenceId}/comments/{id}/versions/
     * @secure
     */,
    campusAbsencesCommentsVersionsRetrieve: <
      T extends FetchKeys<CampusAbsenceComment>
    >(
      campusAbsenceId: string,
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CampusAbsenceComment, T>, any>({
        path: `/campus-absences/${campusAbsenceId}/comments/${id}/versions/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags campus-absences
     * @name CampusAbsencesAusgangsPerWeekRetrieve
     * @request GET:/campus-absences/ausgangs-per-week/
     * @secure
     */,
    campusAbsencesAusgangsPerWeekRetrieve: <
      T extends FetchKeys<AusgangsPerWeekResponse>
    >(
      query?: {
        from_current_week?: boolean

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AusgangsPerWeekResponse, T>, any>({
        path: `/campus-absences/ausgangs-per-week/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags campus-absences
     * @name CampusAbsencesCampusAbsencesList
     * @request GET:/campus-absences/campus-absences/
     * @secure
     */,
    campusAbsencesCampusAbsencesList: <
      T extends FetchKeys<PaginatedCampusAbsenceListList>
    >(
      query?: {
        /**
         * * `ausgang` - Ausgang
         * * `external_exam` - External exam
         * * `official` - Official
         * * `school_programme` - School Programme
         * * `extra_ausgang` - Extra Ausgang
         */
        campus_absence_type?:
          | 'ausgang'
          | 'external_exam'
          | 'extra_ausgang'
          | 'official'
          | 'school_programme'
        check_in?: boolean
        check_out?: boolean
        /** @format date-time */
        date_from_after?: string
        /** @format date-time */
        date_to_before?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /**
         * * `requested` - Pending
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `canceled_by_student` - Canceled by student
         * * `done` - Done
         * * `cancelled` - Cancelled
         */
        status?:
          | 'approved'
          | 'canceled_by_student'
          | 'cancelled'
          | 'done'
          | 'rejected'
          | 'requested'
        students?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCampusAbsenceListList, T>, any>({
        path: `/campus-absences/campus-absences/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags campus-absences
     * @name CampusAbsencesCampusAbsencesCreate
     * @request POST:/campus-absences/campus-absences/
     * @secure
     */,
    campusAbsencesCampusAbsencesCreate: <T extends FetchKeys<CampusAbsence>>(
      data: CampusAbsenceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CampusAbsence, T>, any>({
        path: `/campus-absences/campus-absences/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags campus-absences
     * @name CampusAbsencesCampusAbsencesRetrieve
     * @request GET:/campus-absences/campus-absences/{id}/
     * @secure
     */,
    campusAbsencesCampusAbsencesRetrieve: <T extends FetchKeys<CampusAbsence>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CampusAbsence, T>, any>({
        path: `/campus-absences/campus-absences/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags campus-absences
     * @name CampusAbsencesCampusAbsencesUpdate
     * @request PUT:/campus-absences/campus-absences/{id}/
     * @secure
     */,
    campusAbsencesCampusAbsencesUpdate: <T extends FetchKeys<CampusAbsence>>(
      id: string,
      data: CampusAbsenceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CampusAbsence, T>, any>({
        path: `/campus-absences/campus-absences/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags campus-absences
     * @name CampusAbsencesCampusAbsencesPartialUpdate
     * @request PATCH:/campus-absences/campus-absences/{id}/
     * @secure
     */,
    campusAbsencesCampusAbsencesPartialUpdate: <
      T extends FetchKeys<CampusAbsence>
    >(
      id: string,
      data: PatchedCampusAbsenceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CampusAbsence, T>, any>({
        path: `/campus-absences/campus-absences/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags campus-absences
     * @name CampusAbsencesCampusAbsencesChangeCheckInOutUpdate
     * @request PUT:/campus-absences/campus-absences/{id}/change-check-in-out/
     * @secure
     */,
    campusAbsencesCampusAbsencesChangeCheckInOutUpdate: <
      T extends FetchKeys<CampusAbsenceCheckInOut>
    >(
      id: string,
      data: CampusAbsenceCheckInOutRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CampusAbsenceCheckInOut, T>, any>({
        path: `/campus-absences/campus-absences/${id}/change-check-in-out/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags campus-absences
     * @name CampusAbsencesCampusAbsencesChangeStatusUpdate
     * @request PUT:/campus-absences/campus-absences/{id}/change-status/
     * @secure
     */,
    campusAbsencesCampusAbsencesChangeStatusUpdate: <
      T extends FetchKeys<CampusAbsenceChangeStatus>
    >(
      id: string,
      data: CampusAbsenceChangeStatusRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CampusAbsenceChangeStatus, T>, any>({
        path: `/campus-absences/campus-absences/${id}/change-status/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags campus-absences
     * @name CampusAbsencesCampusAbsencesHistoryList
     * @request GET:/campus-absences/campus-absences/{id}/history/
     * @secure
     */,
    campusAbsencesCampusAbsencesHistoryList: <
      T extends FetchKeys<CampusAbsenceLog[]>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CampusAbsenceLog[], T>, any>({
        path: `/campus-absences/campus-absences/${id}/history/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags campus-absences
     * @name CampusAbsencesCampusAbsencesChoicesRetrieve
     * @request GET:/campus-absences/campus-absences/choices/
     * @secure
     */,
    campusAbsencesCampusAbsencesChoicesRetrieve: <
      T extends FetchKeys<CampusAbsencesChoices>
    >(
      query?: {
        /**
         * * `ausgang` - Ausgang
         * * `external_exam` - External exam
         * * `official` - Official
         * * `school_programme` - School Programme
         * * `extra_ausgang` - Extra Ausgang
         */
        campus_absence_type?:
          | 'ausgang'
          | 'external_exam'
          | 'extra_ausgang'
          | 'official'
          | 'school_programme'
        check_in?: boolean
        check_out?: boolean
        /** @format date-time */
        date_from_after?: string
        /** @format date-time */
        date_to_before?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /**
         * * `requested` - Pending
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `canceled_by_student` - Canceled by student
         * * `done` - Done
         * * `cancelled` - Cancelled
         */
        status?:
          | 'approved'
          | 'canceled_by_student'
          | 'cancelled'
          | 'done'
          | 'rejected'
          | 'requested'
        students?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CampusAbsencesChoices, T>, any>({
        path: `/campus-absences/campus-absences/choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags campus-absences
     * @name CampusAbsencesCampusAbsencesCollisionsCreate
     * @request POST:/campus-absences/campus-absences/collisions/
     * @secure
     */,
    campusAbsencesCampusAbsencesCollisionsCreate: <
      T extends FetchKeys<CampusAbsenceCollisionsResponse>
    >(
      data: CampusAbsenceWarningRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CampusAbsenceCollisionsResponse, T>, any>({
        path: `/campus-absences/campus-absences/collisions/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
  }
  chronicConditions = {
    /**
     * No description
     *
     * @tags chronic-conditions
     * @name ChronicConditionsList
     * @request GET:/chronic-conditions/
     * @secure
     */
    chronicConditionsList: <T extends FetchKeys<PaginatedChronicConditionList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedChronicConditionList, T>, any>({
        path: `/chronic-conditions/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags chronic-conditions
     * @name ChronicConditionsCreate
     * @request POST:/chronic-conditions/
     * @secure
     */,
    chronicConditionsCreate: <T extends FetchKeys<ChronicCondition>>(
      data: ChronicConditionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ChronicCondition, T>, any>({
        path: `/chronic-conditions/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags chronic-conditions
     * @name ChronicConditionsRetrieve
     * @request GET:/chronic-conditions/{id}/
     * @secure
     */,
    chronicConditionsRetrieve: <T extends FetchKeys<ChronicCondition>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ChronicCondition, T>, any>({
        path: `/chronic-conditions/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags chronic-conditions
     * @name ChronicConditionsUpdate
     * @request PUT:/chronic-conditions/{id}/
     * @secure
     */,
    chronicConditionsUpdate: <T extends FetchKeys<ChronicCondition>>(
      id: number,
      data: ChronicConditionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ChronicCondition, T>, any>({
        path: `/chronic-conditions/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags chronic-conditions
     * @name ChronicConditionsPartialUpdate
     * @request PATCH:/chronic-conditions/{id}/
     * @secure
     */,
    chronicConditionsPartialUpdate: <T extends FetchKeys<ChronicCondition>>(
      id: number,
      data: PatchedChronicConditionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ChronicCondition, T>, any>({
        path: `/chronic-conditions/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags chronic-conditions
     * @name ChronicConditionsStudentChronicConditionsList
     * @request GET:/chronic-conditions/student-chronic-conditions/
     * @secure
     */,
    chronicConditionsStudentChronicConditionsList: <
      T extends FetchKeys<StudentChronicCondition[]>
    >(
      query?: {
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentChronicCondition[], T>, any>({
        path: `/chronic-conditions/student-chronic-conditions/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags chronic-conditions
     * @name ChronicConditionsStudentChronicConditionsCreate
     * @request POST:/chronic-conditions/student-chronic-conditions/
     * @secure
     */,
    chronicConditionsStudentChronicConditionsCreate: <
      T extends FetchKeys<StudentChronicCondition>
    >(
      data: StudentChronicConditionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentChronicCondition, T>, any>({
        path: `/chronic-conditions/student-chronic-conditions/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags chronic-conditions
     * @name ChronicConditionsStudentChronicConditionsRetrieve
     * @request GET:/chronic-conditions/student-chronic-conditions/{id}/
     * @secure
     */,
    chronicConditionsStudentChronicConditionsRetrieve: <
      T extends FetchKeys<StudentChronicCondition>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentChronicCondition, T>, any>({
        path: `/chronic-conditions/student-chronic-conditions/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags chronic-conditions
     * @name ChronicConditionsStudentChronicConditionsUpdate
     * @request PUT:/chronic-conditions/student-chronic-conditions/{id}/
     * @secure
     */,
    chronicConditionsStudentChronicConditionsUpdate: <
      T extends FetchKeys<StudentChronicCondition>
    >(
      id: number,
      data: StudentChronicConditionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentChronicCondition, T>, any>({
        path: `/chronic-conditions/student-chronic-conditions/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags chronic-conditions
     * @name ChronicConditionsStudentChronicConditionsPartialUpdate
     * @request PATCH:/chronic-conditions/student-chronic-conditions/{id}/
     * @secure
     */,
    chronicConditionsStudentChronicConditionsPartialUpdate: <
      T extends FetchKeys<StudentChronicCondition>
    >(
      id: number,
      data: PatchedStudentChronicConditionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentChronicCondition, T>, any>({
        path: `/chronic-conditions/student-chronic-conditions/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags chronic-conditions
     * @name ChronicConditionsStudentChronicConditionsDestroy
     * @request DELETE:/chronic-conditions/student-chronic-conditions/{id}/
     * @secure
     */,
    chronicConditionsStudentChronicConditionsDestroy: <
      T extends FetchKeys<void>
    >(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/chronic-conditions/student-chronic-conditions/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags chronic-conditions
     * @name ChronicConditionsStudentChronicConditionsChoicesRetrieve
     * @request GET:/chronic-conditions/student-chronic-conditions/choices/
     * @secure
     */,
    chronicConditionsStudentChronicConditionsChoicesRetrieve: <
      T extends FetchKeys<ChronicConditionChoicesResponse>
    >(
      query?: {
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ChronicConditionChoicesResponse, T>, any>({
        path: `/chronic-conditions/student-chronic-conditions/choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  cockpit = {
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseBuilderGlobalStandardList
     * @request GET:/cockpit/course-builder/global-standard/
     * @secure
     */
    cockpitCourseBuilderGlobalStandardList: <
      T extends FetchKeys<PaginatedGlobalStandardListList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedGlobalStandardListList, T>, any>({
        path: `/cockpit/course-builder/global-standard/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseBuilderGlobalStandardCreate
     * @request POST:/cockpit/course-builder/global-standard/
     * @secure
     */,
    cockpitCourseBuilderGlobalStandardCreate: <
      T extends FetchKeys<GlobalStandard>
    >(
      data: GlobalStandardRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<GlobalStandard, T>, any>({
        path: `/cockpit/course-builder/global-standard/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseBuilderGlobalStandardRetrieve
     * @request GET:/cockpit/course-builder/global-standard/{id}/
     * @secure
     */,
    cockpitCourseBuilderGlobalStandardRetrieve: <
      T extends FetchKeys<GlobalStandardRetrieve>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<GlobalStandardRetrieve, T>, any>({
        path: `/cockpit/course-builder/global-standard/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseBuilderGlobalStandardUpdate
     * @request PUT:/cockpit/course-builder/global-standard/{id}/
     * @secure
     */,
    cockpitCourseBuilderGlobalStandardUpdate: <
      T extends FetchKeys<GlobalStandardUpdate>
    >(
      id: string,
      data: GlobalStandardUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<GlobalStandardUpdate, T>, any>({
        path: `/cockpit/course-builder/global-standard/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Workaround suggested by the authors of drf-writable-nested for supporting PATCH request method.
     *
     * @tags cockpit
     * @name CockpitCourseBuilderGlobalStandardPartialUpdate
     * @request PATCH:/cockpit/course-builder/global-standard/{id}/
     * @secure
     */,
    cockpitCourseBuilderGlobalStandardPartialUpdate: <
      T extends FetchKeys<GlobalStandardUpdate>
    >(
      id: string,
      data: PatchedGlobalStandardUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<GlobalStandardUpdate, T>, any>({
        path: `/cockpit/course-builder/global-standard/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseBuilderGlobalStandardDestroy
     * @request DELETE:/cockpit/course-builder/global-standard/{id}/
     * @secure
     */,
    cockpitCourseBuilderGlobalStandardDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/cockpit/course-builder/global-standard/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerQuarterWeeksList
     * @request GET:/cockpit/course-scheduler/quarter-weeks/{quarterId}
     * @secure
     */,
    cockpitCourseSchedulerQuarterWeeksList: <
      T extends FetchKeys<StringOption[]>
    >(
      quarterId: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/cockpit/course-scheduler/quarter-weeks/${quarterId}`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupPlanCreate
     * @request POST:/cockpit/course-scheduler/section-group-plan/
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupPlanCreate: <
      T extends FetchKeys<SectionGroupPlanCreate>
    >(
      data: SectionGroupPlanCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SectionGroupPlanCreate, T>, any>({
        path: `/cockpit/course-scheduler/section-group-plan/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Approve SectionGroupPlan endpoint by HOD. Make in transaction: 1. Change status of SectionGroupPlan and its related SectionPlans from "Ready for review" to "Approved" 2. Update optional approve_comment field if given 3. Create Assignments & StudentAssignmentItem from CockpitDraftAssignments
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupPlanApproveCreate
     * @request POST:/cockpit/course-scheduler/section-group-plan/{id}/approve/
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupPlanApproveCreate: <
      T extends FetchKeys<void>
    >(
      id: string,
      data: SectionGroupPlanApproveRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, DetailResponseSchema>({
        path: `/cockpit/course-scheduler/section-group-plan/${id}/approve/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupPlanAssignmentsRetrieve
     * @request GET:/cockpit/course-scheduler/section-group-plan/{id}/assignments
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupPlanAssignmentsRetrieve: <
      T extends FetchKeys<SectionGroupPlanAssignments>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SectionGroupPlanAssignments, T>, any>({
        path: `/cockpit/course-scheduler/section-group-plan/${id}/assignments`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupPlanAssignmentsUpdate
     * @request PUT:/cockpit/course-scheduler/section-group-plan/{id}/assignments
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupPlanAssignmentsUpdate: <
      T extends FetchKeys<SectionGroupPlanAssignments>
    >(
      id: string,
      data: SectionGroupPlanUpdateAssignmentsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SectionGroupPlanAssignments, T>, any>({
        path: `/cockpit/course-scheduler/section-group-plan/${id}/assignments`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupPlanAssignmentsPartialUpdate
     * @request PATCH:/cockpit/course-scheduler/section-group-plan/{id}/assignments
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupPlanAssignmentsPartialUpdate: <
      T extends FetchKeys<SectionGroupPlanAssignments>
    >(
      id: string,
      data: PatchedSectionGroupPlanUpdateAssignmentsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SectionGroupPlanAssignments, T>, any>({
        path: `/cockpit/course-scheduler/section-group-plan/${id}/assignments`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Returns list of global standard items which are available to choose in section group plan.
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupPlanAvailableItemsRetrieve
     * @request GET:/cockpit/course-scheduler/section-group-plan/{id}/available-items/
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupPlanAvailableItemsRetrieve: <
      T extends FetchKeys<SectionGroupPlanAvailableItems>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SectionGroupPlanAvailableItems, T>, any>({
        path: `/cockpit/course-scheduler/section-group-plan/${id}/available-items/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Creates custom subtopic and related global standard item available only within given section group plan. Returns related global standard item data.
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupPlanCustomSubtopicsCreate
     * @request POST:/cockpit/course-scheduler/section-group-plan/{id}/custom-subtopics
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupPlanCustomSubtopicsCreate: <
      T extends FetchKeys<GlobalStandardItem>
    >(
      id: string,
      data: CustomSubtopicCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<GlobalStandardItem, T>, DetailResponseSchema>({
        path: `/cockpit/course-scheduler/section-group-plan/${id}/custom-subtopics`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupPlanEditCreate
     * @request POST:/cockpit/course-scheduler/section-group-plan/{id}/edit/
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupPlanEditCreate: <
      T extends FetchKeys<void>
    >(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/cockpit/course-scheduler/section-group-plan/${id}/edit/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupPlanLatestRejectionRetrieve
     * @request GET:/cockpit/course-scheduler/section-group-plan/{id}/latest-rejection/
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupPlanLatestRejectionRetrieve: <
      T extends FetchKeys<SectionGroupPlanRejection>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SectionGroupPlanRejection, T>, any>({
        path: `/cockpit/course-scheduler/section-group-plan/${id}/latest-rejection/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupPlanRejectCreate
     * @request POST:/cockpit/course-scheduler/section-group-plan/{id}/reject/
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupPlanRejectCreate: <
      T extends FetchKeys<void>
    >(
      id: string,
      data: SectionGroupPlanRejectRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, DetailResponseSchema>({
        path: `/cockpit/course-scheduler/section-group-plan/${id}/reject/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupPlanRejectionHistoryList
     * @request GET:/cockpit/course-scheduler/section-group-plan/{id}/rejection-history/
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupPlanRejectionHistoryList: <
      T extends FetchKeys<SectionGroupPlanRejection[]>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SectionGroupPlanRejection[], T>, any>({
        path: `/cockpit/course-scheduler/section-group-plan/${id}/rejection-history/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupPlanSendForApprovalCreate
     * @request POST:/cockpit/course-scheduler/section-group-plan/{id}/send-for-approval/
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupPlanSendForApprovalCreate: <
      T extends FetchKeys<void>
    >(
      id: string,
      data: SectionGroupPlanSendForApprovalRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, DetailResponseSchema>({
        path: `/cockpit/course-scheduler/section-group-plan/${id}/send-for-approval/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupPlanTimelineRetrieve
     * @request GET:/cockpit/course-scheduler/section-group-plan/{id}/timeline
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupPlanTimelineRetrieve: <
      T extends FetchKeys<SectionGroupPlanTimeline>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SectionGroupPlanTimeline, T>, any>({
        path: `/cockpit/course-scheduler/section-group-plan/${id}/timeline`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupPlanTimelineUpdate
     * @request PUT:/cockpit/course-scheduler/section-group-plan/{id}/timeline
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupPlanTimelineUpdate: <
      T extends FetchKeys<SectionGroupPlanUpdateTimeline>
    >(
      id: string,
      data: SectionGroupPlanUpdateTimelineRequest,
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<SectionGroupPlanUpdateTimeline, T>,
        SectionGroupPlanConflicts
      >({
        path: `/cockpit/course-scheduler/section-group-plan/${id}/timeline`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupPlanTimelinePartialUpdate
     * @request PATCH:/cockpit/course-scheduler/section-group-plan/{id}/timeline
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupPlanTimelinePartialUpdate: <
      T extends FetchKeys<SectionGroupPlanUpdateTimeline>
    >(
      id: string,
      data: PatchedSectionGroupPlanUpdateTimelineRequest,
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<SectionGroupPlanUpdateTimeline, T>,
        SectionGroupPlanConflicts
      >({
        path: `/cockpit/course-scheduler/section-group-plan/${id}/timeline`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupPlanRejectionReasonsList
     * @request GET:/cockpit/course-scheduler/section-group-plan/rejection-reasons/
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupPlanRejectionReasonsList: <
      T extends FetchKeys<StringOption[]>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/cockpit/course-scheduler/section-group-plan/rejection-reasons/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupsList
     * @request GET:/cockpit/course-scheduler/section-groups/
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupsList: <
      T extends FetchKeys<PaginatedCockpitSectionGroupList>
    >(
      query?: {
        /** @format uuid */
        course?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        school_year?: number
        /**
         * * `to_do` - To do
         * * `in_planning` - In planning
         * * `ready_for_review` - Ready for review
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `partially_rejected` - Partially rejected
         */
        section_group_plan_status?: (
          | 'approved'
          | 'in_planning'
          | 'partially_rejected'
          | 'ready_for_review'
          | 'rejected'
          | 'to_do'
        )[]
        teachers?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCockpitSectionGroupList, T>, any>({
        path: `/cockpit/course-scheduler/section-groups/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupsAvailableFiltersRetrieve
     * @request GET:/cockpit/course-scheduler/section-groups/available-filters/
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupsAvailableFiltersRetrieve: <
      T extends FetchKeys<CockpitSectionGroupsAvailableFiltersResponse>
    >(
      query?: {
        /** @format uuid */
        course?: string
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        /** Which field to use when ordering the results. */
        ordering?: string
        school_year?: number
        /**
         * * `to_do` - To do
         * * `in_planning` - In planning
         * * `ready_for_review` - Ready for review
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `partially_rejected` - Partially rejected
         */
        section_group_plan_status?: (
          | 'approved'
          | 'in_planning'
          | 'partially_rejected'
          | 'ready_for_review'
          | 'rejected'
          | 'to_do'
        )[]
        teachers?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<CockpitSectionGroupsAvailableFiltersResponse, T>,
        any
      >({
        path: `/cockpit/course-scheduler/section-groups/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Statistics differs for both HOD and Teacher.
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionGroupsStatsRetrieve
     * @request GET:/cockpit/course-scheduler/section-groups/stats/
     * @secure
     */,
    cockpitCourseSchedulerSectionGroupsStatsRetrieve: <
      T extends FetchKeys<CockpitSectionGroupsStats>
    >(
      query?: {
        /** @format uuid */
        course?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        school_year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CockpitSectionGroupsStats, T>, any>({
        path: `/cockpit/course-scheduler/section-groups/stats/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionPlanItemsProgressList
     * @request GET:/cockpit/course-scheduler/section-plan-items-progress/
     * @secure
     */,
    cockpitCourseSchedulerSectionPlanItemsProgressList: <
      T extends FetchKeys<SectionPlanItemList[]>
    >(
      query: {
        artisan?: string[]
        /** @format uuid */
        course: string
        /** Which field to use when ordering the results. */
        ordering?: string
        quarter: number
        /**
         * * `to_do` - To do
         * * `planned` - Planned
         * * `in_progress` - In progress
         * * `completed` - Completed
         */
        status?: ('completed' | 'in_progress' | 'planned' | 'to_do')[]
        students?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SectionPlanItemList[], T>, any>({
        path: `/cockpit/course-scheduler/section-plan-items-progress/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags cockpit
     * @name CockpitCourseSchedulerSectionPlanItemsProgressAvailableFiltersRetrieve
     * @request GET:/cockpit/course-scheduler/section-plan-items-progress/available-filters/
     * @secure
     */,
    cockpitCourseSchedulerSectionPlanItemsProgressAvailableFiltersRetrieve: <
      T extends FetchKeys<SectionPlanItemsProgressAvailableFiltersResponse>
    >(
      query: {
        artisan?: string[]
        /** @format uuid */
        course: string
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        /** Which field to use when ordering the results. */
        ordering?: string
        quarter: number
        /**
         * * `to_do` - To do
         * * `planned` - Planned
         * * `in_progress` - In progress
         * * `completed` - Completed
         */
        status?: ('completed' | 'in_progress' | 'planned' | 'to_do')[]
        students?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<SectionPlanItemsProgressAvailableFiltersResponse, T>,
        any
      >({
        path: `/cockpit/course-scheduler/section-plan-items-progress/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitEditingPeriodsCreate
     * @request POST:/cockpit/editing-periods/
     * @secure
     */,
    cockpitEditingPeriodsCreate: <T extends FetchKeys<CockpitEditingPeriod>>(
      data: CockpitEditingPeriodRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CockpitEditingPeriod, T>, any>({
        path: `/cockpit/editing-periods/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitEditingPeriodsUpdate
     * @request PUT:/cockpit/editing-periods/{id}/
     * @secure
     */,
    cockpitEditingPeriodsUpdate: <T extends FetchKeys<CockpitEditingPeriod>>(
      id: string,
      data: CockpitEditingPeriodRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CockpitEditingPeriod, T>, any>({
        path: `/cockpit/editing-periods/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitEditingPeriodsCurrentRetrieve
     * @request GET:/cockpit/editing-periods/current/
     * @secure
     */,
    cockpitEditingPeriodsCurrentRetrieve: <
      T extends FetchKeys<CockpitEditingPeriod>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CockpitEditingPeriod, T>, any>({
        path: `/cockpit/editing-periods/current/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitRepositoryDomainsList
     * @request GET:/cockpit/repository/domains/
     * @secure
     */,
    cockpitRepositoryDomainsList: <T extends FetchKeys<PaginatedDomainList>>(
      query?: {
        aims?: number[]
        departments?: number[]
        grade_levels?: number[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedDomainList, T>, any>({
        path: `/cockpit/repository/domains/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitRepositoryDomainsCreate
     * @request POST:/cockpit/repository/domains/
     * @secure
     */,
    cockpitRepositoryDomainsCreate: <T extends FetchKeys<DomainDetails>>(
      data: DomainCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DomainDetails, T>, any>({
        path: `/cockpit/repository/domains/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitRepositoryDomainsRetrieve
     * @request GET:/cockpit/repository/domains/{id}/
     * @secure
     */,
    cockpitRepositoryDomainsRetrieve: <T extends FetchKeys<DomainDetails>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DomainDetails, T>, any>({
        path: `/cockpit/repository/domains/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags cockpit
     * @name CockpitRepositoryDomainsDestroy
     * @request DELETE:/cockpit/repository/domains/{id}/
     * @secure
     */,
    cockpitRepositoryDomainsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/cockpit/repository/domains/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags cockpit
     * @name CockpitRepositoryTopicsList
     * @request GET:/cockpit/repository/topics/
     * @secure
     */,
    cockpitRepositoryTopicsList: <T extends FetchKeys<PaginatedTopicListList>>(
      query?: {
        aims?: number[]
        departments?: number[]
        domain?: string[]
        grade_levels?: number[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /** Multiple values may be separated by commas. */
        subtopics__code?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTopicListList, T>, any>({
        path: `/cockpit/repository/topics/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags cockpit
     * @name CockpitRepositoryTopicsCreate
     * @request POST:/cockpit/repository/topics/
     * @secure
     */,
    cockpitRepositoryTopicsCreate: <T extends FetchKeys<Topic>>(
      data: TopicRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Topic, T>, any>({
        path: `/cockpit/repository/topics/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags cockpit
     * @name CockpitRepositoryTopicsRetrieve
     * @request GET:/cockpit/repository/topics/{id}/
     * @secure
     */,
    cockpitRepositoryTopicsRetrieve: <T extends FetchKeys<Topic>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Topic, T>, any>({
        path: `/cockpit/repository/topics/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags cockpit
     * @name CockpitRepositoryTopicsUpdate
     * @request PUT:/cockpit/repository/topics/{id}/
     * @secure
     */,
    cockpitRepositoryTopicsUpdate: <T extends FetchKeys<Topic>>(
      id: string,
      data: TopicRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Topic, T>, any>({
        path: `/cockpit/repository/topics/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags cockpit
     * @name CockpitRepositoryTopicsPartialUpdate
     * @request PATCH:/cockpit/repository/topics/{id}/
     * @secure
     */,
    cockpitRepositoryTopicsPartialUpdate: <T extends FetchKeys<Topic>>(
      id: string,
      data: PatchedTopicRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Topic, T>, any>({
        path: `/cockpit/repository/topics/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags cockpit
     * @name CockpitRepositoryTopicsDestroy
     * @request DELETE:/cockpit/repository/topics/{id}/
     * @secure
     */,
    cockpitRepositoryTopicsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/cockpit/repository/topics/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags cockpit
     * @name CockpitRepositoryTopicsAvailableFiltersRetrieve
     * @request GET:/cockpit/repository/topics/available-filters/
     * @secure
     */,
    cockpitRepositoryTopicsAvailableFiltersRetrieve: <
      T extends FetchKeys<Topic>
    >(
      query?: {
        aims?: number[]
        departments?: number[]
        domain?: string[]
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        grade_levels?: number[]
        /** A search term. */
        search?: string
        /** Multiple values may be separated by commas. */
        subtopics__code?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Topic, T>, any>({
        path: `/cockpit/repository/topics/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags cockpit
     * @name CockpitRepositoryTopicsGlobalStandardViewList
     * @request GET:/cockpit/repository/topics/global-standard-view/
     * @secure
     */,
    cockpitRepositoryTopicsGlobalStandardViewList: <
      T extends FetchKeys<PaginatedTopicForGlobalStandardList>
    >(
      query?: {
        aims?: number[]
        departments?: number[]
        domain?: string[]
        grade_levels?: number[]
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /** A search by choice. */
        search_by?: 'topic' | 'subtopic'
        /** Multiple values may be separated by commas. */
        subtopics__code?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTopicForGlobalStandardList, T>, any>({
        path: `/cockpit/repository/topics/global-standard-view/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags cockpit
     * @name CockpitRepositoryTopicsGlobalStandardViewAvailableFiltersRetrieve
     * @request GET:/cockpit/repository/topics/global-standard-view/available-filters/
     * @secure
     */,
    cockpitRepositoryTopicsGlobalStandardViewAvailableFiltersRetrieve: <
      T extends FetchKeys<TopicsForGlobalStandardAvailableFiltersResponse>
    >(
      query?: {
        aims?: number[]
        departments?: number[]
        domain?: string[]
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        grade_levels?: number[]
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string
        /** A search by choice. */
        search_by?: 'topic' | 'subtopic'
        /** Multiple values may be separated by commas. */
        subtopics__code?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TopicsForGlobalStandardAvailableFiltersResponse, T>,
        any
      >({
        path: `/cockpit/repository/topics/global-standard-view/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  companies = {
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags companies
     * @name CompaniesList
     * @request GET:/companies/
     * @secure
     */
    companiesList: <T extends FetchKeys<PaginatedCompanyListList>>(
      query?: {
        /** @format uuid */
        candidate?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** @format uuid */
        parent?: string
        /** A search term. */
        search?: string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCompanyListList, T>, any>({
        path: `/companies/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags companies
     * @name CompaniesCreate
     * @request POST:/companies/
     * @secure
     */,
    companiesCreate: <T extends FetchKeys<CompanyCreate>>(
      data: CompanyCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CompanyCreate, T>, any>({
        path: `/companies/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags companies
     * @name CompaniesRetrieve
     * @request GET:/companies/{id}/
     * @secure
     */,
    companiesRetrieve: <T extends FetchKeys<CompanyDetail>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CompanyDetail, T>, any>({
        path: `/companies/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags companies
     * @name CompaniesUpdate
     * @request PUT:/companies/{id}/
     * @secure
     */,
    companiesUpdate: <T extends FetchKeys<CompanyUpdate>>(
      id: string,
      data: CompanyUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CompanyUpdate, T>, any>({
        path: `/companies/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags companies
     * @name CompaniesPartialUpdate
     * @request PATCH:/companies/{id}/
     * @secure
     */,
    companiesPartialUpdate: <T extends FetchKeys<CompanyUpdate>>(
      id: string,
      data: PatchedCompanyUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CompanyUpdate, T>, any>({
        path: `/companies/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags companies
     * @name CompaniesAvailableFiltersRetrieve
     * @request GET:/companies/available-filters/
     * @secure
     */,
    companiesAvailableFiltersRetrieve: <
      T extends FetchKeys<CompanyAvailableFilters>
    >(
      query?: {
        /** @format uuid */
        candidate?: string
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        /** Which field to use when ordering the results. */
        ordering?: string
        /** @format uuid */
        parent?: string
        /** A search term. */
        search?: string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CompanyAvailableFilters, T>, any>({
        path: `/companies/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  core = {
    /**
     * No description
     *
     * @tags core
     * @name CoreAppStatusRetrieve
     * @request GET:/core/app-status/
     * @secure
     */
    coreAppStatusRetrieve: <T extends FetchKeys<AppStatusVersion>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AppStatusVersion, T>, any>({
        path: `/core/app-status/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags core
     * @name CoreCountriesList
     * @request GET:/core/countries/
     * @secure
     */,
    coreCountriesList: <T extends FetchKeys<Country[]>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Country[], T>, any>({
        path: `/core/countries/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags core
     * @name CoreCurrencyChoicesRetrieve
     * @request GET:/core/currency-choices/
     * @secure
     */,
    coreCurrencyChoicesRetrieve: <T extends FetchKeys<void>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/core/currency-choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags core
     * @name CoreSettingsRetrieve
     * @request GET:/core/settings/
     * @secure
     */,
    coreSettingsRetrieve: <T extends FetchKeys<SiteSettings>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SiteSettings, T>, any>({
        path: `/core/settings/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  courses = {
    /**
     * No description
     *
     * @tags courses
     * @name CoursesList
     * @request GET:/courses/
     * @secure
     */
    coursesList: <T extends FetchKeys<PaginatedCourseListList>>(
      query?: {
        is_latest_version?: boolean
        name?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter?: string
        /** A search term. */
        search?: string
        types__name?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCourseListList, T>, any>({
        path: `/courses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesRetrieve
     * @request GET:/courses/{id}/
     * @secure
     */,
    coursesRetrieve: <T extends FetchKeys<CourseDetail>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseDetail, T>, any>({
        path: `/courses/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesGlobalStandardRetrieve
     * @request GET:/courses/{id}/global-standard/
     * @secure
     */,
    coursesGlobalStandardRetrieve: <T extends FetchKeys<GlobalStandardId>>(
      id: string,
      query: {
        school_year: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<GlobalStandardId, T>, any>({
        path: `/courses/${id}/global-standard/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesGradesIdpViewRetrieve
     * @request GET:/courses/{id}/grades/idp-view/
     * @secure
     */,
    coursesGradesIdpViewRetrieve: <
      T extends FetchKeys<CourseGradesIdpViewResponse>
    >(
      id: string,
      query: {
        assignment_type?: number
        year: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseGradesIdpViewResponse, T>, any>({
        path: `/courses/${id}/grades/idp-view/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesGradesPotentialViewRetrieve
     * @request GET:/courses/{id}/grades/potential-view/
     * @secure
     */,
    coursesGradesPotentialViewRetrieve: <
      T extends FetchKeys<CoursePotentialGradesViewResponse>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CoursePotentialGradesViewResponse, T>, any>({
        path: `/courses/${id}/grades/potential-view/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesGradesQuarterViewRetrieve
     * @request GET:/courses/{id}/grades/quarter-view/
     * @secure
     */,
    coursesGradesQuarterViewRetrieve: <
      T extends FetchKeys<CourseGradesQuarterViewResponse>
    >(
      id: string,
      query: {
        assignment_type?: number
        quarter: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseGradesQuarterViewResponse, T>, any>({
        path: `/courses/${id}/grades/quarter-view/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesUpdateDescriptionPartialUpdate
     * @request PATCH:/courses/{id}/update-description/
     * @secure
     */,
    coursesUpdateDescriptionPartialUpdate: <
      T extends FetchKeys<CourseDescription>
    >(
      id: string,
      data: PatchedCourseDescriptionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseDescription, T>, any>({
        path: `/courses/${id}/update-description/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesEnrollmentsList
     * @request GET:/courses/enrollments/
     * @secure
     */,
    coursesEnrollmentsList: <T extends FetchKeys<CourseEnrollment[]>>(
      query?: {
        /** @format uuid */
        course?: string
        dropped_enrollments?: boolean
        id?: number[]
        quarter?: number
        quarter__semester?: number
        quarter__semester__year?: number
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseEnrollment[], T>, any>({
        path: `/courses/enrollments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesOverviewList
     * @request GET:/courses/overview/
     * @secure
     */,
    coursesOverviewList: <T extends FetchKeys<PaginatedCourseOverviewList>>(
      query?: {
        department?: number[]
        is_latest_version?: boolean
        name?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter?: number
        /** A search term. */
        search?: string
        types__name?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCourseOverviewList, T>, any>({
        path: `/courses/overview/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesPlannerList
     * @request GET:/courses/planner/
     * @secure
     */,
    coursesPlannerList: <T extends FetchKeys<PaginatedCoursePlannerListList>>(
      query?: {
        activities__quarter?: number
        code?: string
        custom_constraints?: boolean
        department?: string[]
        group?: string[]
        is_included_in_sport_package?: boolean
        is_latest_version?: boolean
        is_tae_private?: boolean
        name?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter?: string
        /** A search term. */
        search?: string
        types__name?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCoursePlannerListList, T>, any>({
        path: `/courses/planner/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesPlannerCreate
     * @request POST:/courses/planner/
     * @secure
     */,
    coursesPlannerCreate: <T extends FetchKeys<CoursePlanner>>(
      data: CoursePlannerRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CoursePlanner, T>, any>({
        path: `/courses/planner/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesPlannerRetrieve
     * @request GET:/courses/planner/{id}/
     * @secure
     */,
    coursesPlannerRetrieve: <T extends FetchKeys<CoursePlannerResponse>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CoursePlannerResponse, T>, any>({
        path: `/courses/planner/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesPlannerUpdate
     * @request PUT:/courses/planner/{id}/
     * @secure
     */,
    coursesPlannerUpdate: <T extends FetchKeys<CoursePlannerResponse>>(
      id: string,
      data: CoursePlannerRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CoursePlannerResponse, T>, any>({
        path: `/courses/planner/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesPlannerPartialUpdate
     * @request PATCH:/courses/planner/{id}/
     * @secure
     */,
    coursesPlannerPartialUpdate: <T extends FetchKeys<CoursePlanner>>(
      id: string,
      data: PatchedCoursePlannerRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CoursePlanner, T>, any>({
        path: `/courses/planner/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesPlannerDestroy
     * @request DELETE:/courses/planner/{id}/
     * @secure
     */,
    coursesPlannerDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/courses/planner/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesPlannerToggleLatestVersionCreate
     * @request POST:/courses/planner/{id}/toggle-latest-version/
     * @secure
     */,
    coursesPlannerToggleLatestVersionCreate: <
      T extends FetchKeys<CourseLatestVersionResponse>
    >(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseLatestVersionResponse, T>, any>({
        path: `/courses/planner/${id}/toggle-latest-version/`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesPlannerDefaultCustomConstraintsRetrieve
     * @request GET:/courses/planner/default-custom-constraints/
     * @secure
     */,
    coursesPlannerDefaultCustomConstraintsRetrieve: <
      T extends FetchKeys<DefaultCustomConstraints>
    >(
      query?: {
        activities__quarter?: number
        code?: string
        course_group?: string
        course_levels?: string[]
        custom_constraints?: boolean
        department?: string[]
        group?: string[]
        is_included_in_sport_package?: boolean
        is_latest_version?: boolean
        is_tae_private?: boolean
        name?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        quarter?: string
        /** A search term. */
        search?: string
        types__name?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DefaultCustomConstraints, T>, any>({
        path: `/courses/planner/default-custom-constraints/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesPlannerSetQuarterCreate
     * @request POST:/courses/planner/set-quarter/
     * @secure
     */,
    coursesPlannerSetQuarterCreate: <T extends FetchKeys<QuarterInCourses>>(
      data: QuarterInCoursesRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<QuarterInCourses, T>, any>({
        path: `/courses/planner/set-quarter/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesWeeklyCommentsList
     * @request GET:/courses/weekly-comments/
     * @secure
     */,
    coursesWeeklyCommentsList: <
      T extends FetchKeys<PaginatedWeeklyCommentList>
    >(
      query?: {
        /** @format uuid */
        course?: string
        /** @format date */
        from_date?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** @format uuid */
        student?: string
        /** @format date */
        to_date?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedWeeklyCommentList, T>, any>({
        path: `/courses/weekly-comments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesWeeklyCommentsCreate
     * @request POST:/courses/weekly-comments/
     * @secure
     */,
    coursesWeeklyCommentsCreate: <T extends FetchKeys<WeeklyComment>>(
      data: WeeklyCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<WeeklyComment, T>, any>({
        path: `/courses/weekly-comments/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesWeeklyCommentsRetrieve
     * @request GET:/courses/weekly-comments/{id}/
     * @secure
     */,
    coursesWeeklyCommentsRetrieve: <T extends FetchKeys<WeeklyComment>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<WeeklyComment, T>, any>({
        path: `/courses/weekly-comments/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesWeeklyCommentsUpdate
     * @request PUT:/courses/weekly-comments/{id}/
     * @secure
     */,
    coursesWeeklyCommentsUpdate: <T extends FetchKeys<WeeklyComment>>(
      id: number,
      data: WeeklyCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<WeeklyComment, T>, any>({
        path: `/courses/weekly-comments/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesWeeklyCommentsPartialUpdate
     * @request PATCH:/courses/weekly-comments/{id}/
     * @secure
     */,
    coursesWeeklyCommentsPartialUpdate: <T extends FetchKeys<WeeklyComment>>(
      id: number,
      data: PatchedWeeklyCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<WeeklyComment, T>, any>({
        path: `/courses/weekly-comments/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesWeeklyCommentsDestroy
     * @request DELETE:/courses/weekly-comments/{id}/
     * @secure
     */,
    coursesWeeklyCommentsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/courses/weekly-comments/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesWeeklyCommentsCsvRetrieve
     * @request GET:/courses/weekly-comments/csv/
     * @secure
     */,
    coursesWeeklyCommentsCsvRetrieve: <T extends FetchKeys<File>>(
      query?: {
        /** @format uuid */
        course?: string
        /** @format date */
        from_date?: string
        /** @format uuid */
        student?: string
        /** @format date */
        to_date?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<File, T>, any>({
        path: `/courses/weekly-comments/csv/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesWeeklyCommentsDownloadRetrieve
     * @request GET:/courses/weekly-comments/download/
     * @secure
     */,
    coursesWeeklyCommentsDownloadRetrieve: <T extends FetchKeys<File>>(
      query?: {
        /** @format uuid */
        course?: string
        /** File format (PDF or CSV) */
        file_format?: string
        /** @format date */
        from_date?: string
        /** @format uuid */
        student?: string
        /** @format date */
        to_date?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<File, T>, any>({
        path: `/courses/weekly-comments/download/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags courses
     * @name CoursesWeeklyCommentsPdfRetrieve
     * @request GET:/courses/weekly-comments/pdf/
     * @secure
     */,
    coursesWeeklyCommentsPdfRetrieve: <T extends FetchKeys<File>>(
      query?: {
        /** @format uuid */
        course?: string
        /** @format date */
        from_date?: string
        /** @format uuid */
        student?: string
        /** @format date */
        to_date?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<File, T>, any>({
        path: `/courses/weekly-comments/pdf/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
  }
  coursesMeta = {
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaFamiliesList
     * @request GET:/courses-meta/families/
     * @secure
     */
    coursesMetaFamiliesList: <T extends FetchKeys<PaginatedCourseFamilyList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCourseFamilyList, T>, any>({
        path: `/courses-meta/families/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaFamiliesCreate
     * @request POST:/courses-meta/families/
     * @secure
     */,
    coursesMetaFamiliesCreate: <T extends FetchKeys<CourseFamily>>(
      data: CourseFamilyRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseFamily, T>, any>({
        path: `/courses-meta/families/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaFamiliesRetrieve
     * @request GET:/courses-meta/families/{id}/
     * @secure
     */,
    coursesMetaFamiliesRetrieve: <T extends FetchKeys<CourseFamily>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseFamily, T>, any>({
        path: `/courses-meta/families/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaFamiliesUpdate
     * @request PUT:/courses-meta/families/{id}/
     * @secure
     */,
    coursesMetaFamiliesUpdate: <T extends FetchKeys<CourseFamily>>(
      id: string,
      data: CourseFamilyRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseFamily, T>, any>({
        path: `/courses-meta/families/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaFamiliesPartialUpdate
     * @request PATCH:/courses-meta/families/{id}/
     * @secure
     */,
    coursesMetaFamiliesPartialUpdate: <T extends FetchKeys<CourseFamily>>(
      id: string,
      data: PatchedCourseFamilyRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseFamily, T>, any>({
        path: `/courses-meta/families/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaFamiliesDestroy
     * @request DELETE:/courses-meta/families/{id}/
     * @secure
     */,
    coursesMetaFamiliesDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/courses-meta/families/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaGroupsList
     * @request GET:/courses-meta/groups/
     * @secure
     */,
    coursesMetaGroupsList: <T extends FetchKeys<PaginatedCourseGroupList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCourseGroupList, T>, any>({
        path: `/courses-meta/groups/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaGroupsCreate
     * @request POST:/courses-meta/groups/
     * @secure
     */,
    coursesMetaGroupsCreate: <T extends FetchKeys<CourseGroup>>(
      data: CourseGroupRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseGroup, T>, any>({
        path: `/courses-meta/groups/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaGroupsRetrieve
     * @request GET:/courses-meta/groups/{id}/
     * @secure
     */,
    coursesMetaGroupsRetrieve: <T extends FetchKeys<CourseGroup>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseGroup, T>, any>({
        path: `/courses-meta/groups/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaGroupsUpdate
     * @request PUT:/courses-meta/groups/{id}/
     * @secure
     */,
    coursesMetaGroupsUpdate: <T extends FetchKeys<CourseGroup>>(
      id: string,
      data: CourseGroupRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseGroup, T>, any>({
        path: `/courses-meta/groups/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaGroupsPartialUpdate
     * @request PATCH:/courses-meta/groups/{id}/
     * @secure
     */,
    coursesMetaGroupsPartialUpdate: <T extends FetchKeys<CourseGroup>>(
      id: string,
      data: PatchedCourseGroupRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseGroup, T>, any>({
        path: `/courses-meta/groups/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaGroupsDestroy
     * @request DELETE:/courses-meta/groups/{id}/
     * @secure
     */,
    coursesMetaGroupsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/courses-meta/groups/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaIntelligencesList
     * @request GET:/courses-meta/intelligences/
     * @secure
     */,
    coursesMetaIntelligencesList: <
      T extends FetchKeys<PaginatedCourseIntelligenceList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCourseIntelligenceList, T>, any>({
        path: `/courses-meta/intelligences/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaIntelligencesCreate
     * @request POST:/courses-meta/intelligences/
     * @secure
     */,
    coursesMetaIntelligencesCreate: <T extends FetchKeys<CourseIntelligence>>(
      data: CourseIntelligenceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseIntelligence, T>, any>({
        path: `/courses-meta/intelligences/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaIntelligencesRetrieve
     * @request GET:/courses-meta/intelligences/{id}/
     * @secure
     */,
    coursesMetaIntelligencesRetrieve: <T extends FetchKeys<CourseIntelligence>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseIntelligence, T>, any>({
        path: `/courses-meta/intelligences/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaIntelligencesUpdate
     * @request PUT:/courses-meta/intelligences/{id}/
     * @secure
     */,
    coursesMetaIntelligencesUpdate: <T extends FetchKeys<CourseIntelligence>>(
      id: string,
      data: CourseIntelligenceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseIntelligence, T>, any>({
        path: `/courses-meta/intelligences/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaIntelligencesPartialUpdate
     * @request PATCH:/courses-meta/intelligences/{id}/
     * @secure
     */,
    coursesMetaIntelligencesPartialUpdate: <
      T extends FetchKeys<CourseIntelligence>
    >(
      id: string,
      data: PatchedCourseIntelligenceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseIntelligence, T>, any>({
        path: `/courses-meta/intelligences/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaIntelligencesDestroy
     * @request DELETE:/courses-meta/intelligences/{id}/
     * @secure
     */,
    coursesMetaIntelligencesDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/courses-meta/intelligences/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaLevelsList
     * @request GET:/courses-meta/levels/
     * @secure
     */,
    coursesMetaLevelsList: <T extends FetchKeys<PaginatedCourseLevelList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCourseLevelList, T>, any>({
        path: `/courses-meta/levels/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaLevelsCreate
     * @request POST:/courses-meta/levels/
     * @secure
     */,
    coursesMetaLevelsCreate: <T extends FetchKeys<CourseLevel>>(
      data: CourseLevelRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseLevel, T>, any>({
        path: `/courses-meta/levels/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaLevelsRetrieve
     * @request GET:/courses-meta/levels/{id}/
     * @secure
     */,
    coursesMetaLevelsRetrieve: <T extends FetchKeys<CourseLevel>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseLevel, T>, any>({
        path: `/courses-meta/levels/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaLevelsUpdate
     * @request PUT:/courses-meta/levels/{id}/
     * @secure
     */,
    coursesMetaLevelsUpdate: <T extends FetchKeys<CourseLevel>>(
      id: string,
      data: CourseLevelRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseLevel, T>, any>({
        path: `/courses-meta/levels/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaLevelsPartialUpdate
     * @request PATCH:/courses-meta/levels/{id}/
     * @secure
     */,
    coursesMetaLevelsPartialUpdate: <T extends FetchKeys<CourseLevel>>(
      id: string,
      data: PatchedCourseLevelRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseLevel, T>, any>({
        path: `/courses-meta/levels/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaLevelsDestroy
     * @request DELETE:/courses-meta/levels/{id}/
     * @secure
     */,
    coursesMetaLevelsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/courses-meta/levels/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaSubjectsList
     * @request GET:/courses-meta/subjects/
     * @secure
     */,
    coursesMetaSubjectsList: <T extends FetchKeys<PaginatedCourseSubjectList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCourseSubjectList, T>, any>({
        path: `/courses-meta/subjects/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaSubjectsCreate
     * @request POST:/courses-meta/subjects/
     * @secure
     */,
    coursesMetaSubjectsCreate: <T extends FetchKeys<CourseSubject>>(
      data: CourseSubjectRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseSubject, T>, any>({
        path: `/courses-meta/subjects/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaSubjectsRetrieve
     * @request GET:/courses-meta/subjects/{id}/
     * @secure
     */,
    coursesMetaSubjectsRetrieve: <T extends FetchKeys<CourseSubject>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseSubject, T>, any>({
        path: `/courses-meta/subjects/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaSubjectsUpdate
     * @request PUT:/courses-meta/subjects/{id}/
     * @secure
     */,
    coursesMetaSubjectsUpdate: <T extends FetchKeys<CourseSubject>>(
      id: string,
      data: CourseSubjectRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseSubject, T>, any>({
        path: `/courses-meta/subjects/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaSubjectsPartialUpdate
     * @request PATCH:/courses-meta/subjects/{id}/
     * @secure
     */,
    coursesMetaSubjectsPartialUpdate: <T extends FetchKeys<CourseSubject>>(
      id: string,
      data: PatchedCourseSubjectRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseSubject, T>, any>({
        path: `/courses-meta/subjects/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaSubjectsDestroy
     * @request DELETE:/courses-meta/subjects/{id}/
     * @secure
     */,
    coursesMetaSubjectsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/courses-meta/subjects/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaTypesList
     * @request GET:/courses-meta/types/
     * @secure
     */,
    coursesMetaTypesList: <T extends FetchKeys<PaginatedCourseTypeList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCourseTypeList, T>, any>({
        path: `/courses-meta/types/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaTypesCreate
     * @request POST:/courses-meta/types/
     * @secure
     */,
    coursesMetaTypesCreate: <T extends FetchKeys<CourseType>>(
      data: CourseTypeRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseType, T>, any>({
        path: `/courses-meta/types/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaTypesRetrieve
     * @request GET:/courses-meta/types/{id}/
     * @secure
     */,
    coursesMetaTypesRetrieve: <T extends FetchKeys<CourseType>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseType, T>, any>({
        path: `/courses-meta/types/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaTypesUpdate
     * @request PUT:/courses-meta/types/{id}/
     * @secure
     */,
    coursesMetaTypesUpdate: <T extends FetchKeys<CourseType>>(
      id: string,
      data: CourseTypeRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseType, T>, any>({
        path: `/courses-meta/types/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaTypesPartialUpdate
     * @request PATCH:/courses-meta/types/{id}/
     * @secure
     */,
    coursesMetaTypesPartialUpdate: <T extends FetchKeys<CourseType>>(
      id: string,
      data: PatchedCourseTypeRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseType, T>, any>({
        path: `/courses-meta/types/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags courses-meta
     * @name CoursesMetaTypesDestroy
     * @request DELETE:/courses-meta/types/{id}/
     * @secure
     */,
    coursesMetaTypesDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/courses-meta/types/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
  }
  crm = {
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmCandidateProfilesList
     * @request GET:/crm/candidate-profiles/
     * @secure
     */
    crmCandidateProfilesList: <
      T extends FetchKeys<PaginatedCandidateProfileListList>
    >(
      query?: {
        /**
         * * `5` - Not started
         * * `10` - Invoice sent
         * * `15` - Waitlist sent
         * * `17` - Deposit
         * * `20` - Paid
         * * `22` - Waitlist deposit
         * * `25` - Waitlist paid
         * * `30` - Failed
         */
        academic_offer__invoice_status?: (
          | 10
          | 15
          | 17
          | 20
          | 22
          | 25
          | 30
          | 5
        )[]
        /**
         * * `5` - Not started
         * * `10` - Offered
         * * `15` - Waitlist offered
         * * `20` - Accepted
         * * `25` - Waitlist accepted
         * * `30` - No reply
         * * `35` - Rejected
         * * `40` - Lost
         */
        academic_offer__offer_status?: (10 | 15 | 20 | 25 | 30 | 35 | 40 | 5)[]
        /**
         * * `dayschool` - Day School
         * * `boarding` - Boarding
         * * `external` - External
         */
        academic_offer__program_type?:
          | 'boarding'
          | 'dayschool'
          | 'external'
          | null
        /**
         * * `5` - Not started
         * * `10` - Documents requested
         * * `15` - Assessment scheduled
         * * `20` - Final review
         * * `25` - Accepted
         * * `30` - Waitlist
         * * `35` - On hold
         * * `40` - Rejected
         */
        academic_review_status?: (10 | 15 | 20 | 25 | 30 | 35 | 40 | 5)[]
        /** Multiple values may be separated by commas. */
        age?: number[]
        allergen_kinds?: string[]
        career_goal?: number[]
        chronic_condition_types?: string[]
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        current_grade_level?: number[]
        fitness_for_sports?: string[]
        hide_enrolled?: boolean
        insurance_type?: string[]
        is_athlete?: boolean
        is_resident_entrepreneur?: boolean
        is_swimmer?: string
        /**
         * * `l1` - L1, Regular and vegetarian. Students can choose themselves.
         * * `l2` - L2, Specific, individual requirements. Kitchen adapts the existing menu to specific needs.
         * * `l3` - L3, Specific, very individual requirements. Kitchen creates new menu to specific needs, almost every meal has to be cooked individually.
         * * `l4` - L4, Highly specific. Severe allergic reaction to traces of certain food. Meals need to be ordered from outside supplier.
         */
        meal_preference?: 'l1' | 'l2' | 'l3' | 'l4'
        /**
         * * `AF` - Afghanistan
         * * `AX` - Åland Islands
         * * `AL` - Albania
         * * `DZ` - Algeria
         * * `AS` - American Samoa
         * * `AD` - Andorra
         * * `AO` - Angola
         * * `AI` - Anguilla
         * * `AQ` - Antarctica
         * * `AG` - Antigua and Barbuda
         * * `AR` - Argentina
         * * `AM` - Armenia
         * * `AW` - Aruba
         * * `AU` - Australia
         * * `AT` - Austria
         * * `AZ` - Azerbaijan
         * * `BS` - Bahamas
         * * `BH` - Bahrain
         * * `BD` - Bangladesh
         * * `BB` - Barbados
         * * `BY` - Belarus
         * * `BE` - Belgium
         * * `BZ` - Belize
         * * `BJ` - Benin
         * * `BM` - Bermuda
         * * `BT` - Bhutan
         * * `BO` - Bolivia
         * * `BQ` - Bonaire, Sint Eustatius and Saba
         * * `BA` - Bosnia and Herzegovina
         * * `BW` - Botswana
         * * `BV` - Bouvet Island
         * * `BR` - Brazil
         * * `IO` - British Indian Ocean Territory
         * * `BN` - Brunei
         * * `BG` - Bulgaria
         * * `BF` - Burkina Faso
         * * `BI` - Burundi
         * * `CV` - Cabo Verde
         * * `KH` - Cambodia
         * * `CM` - Cameroon
         * * `CA` - Canada
         * * `KY` - Cayman Islands
         * * `CF` - Central African Republic
         * * `TD` - Chad
         * * `CL` - Chile
         * * `CN` - China
         * * `CX` - Christmas Island
         * * `CC` - Cocos (Keeling) Islands
         * * `CO` - Colombia
         * * `KM` - Comoros
         * * `CG` - Congo
         * * `CD` - Congo (the Democratic Republic of the)
         * * `CK` - Cook Islands
         * * `CR` - Costa Rica
         * * `CI` - Côte d'Ivoire
         * * `HR` - Croatia
         * * `CU` - Cuba
         * * `CW` - Curaçao
         * * `CY` - Cyprus
         * * `CZ` - Czechia
         * * `DK` - Denmark
         * * `DJ` - Djibouti
         * * `DM` - Dominica
         * * `DO` - Dominican Republic
         * * `EC` - Ecuador
         * * `EG` - Egypt
         * * `SV` - El Salvador
         * * `GQ` - Equatorial Guinea
         * * `ER` - Eritrea
         * * `EE` - Estonia
         * * `SZ` - Eswatini
         * * `ET` - Ethiopia
         * * `FK` - Falkland Islands (Malvinas)
         * * `FO` - Faroe Islands
         * * `FJ` - Fiji
         * * `FI` - Finland
         * * `FR` - France
         * * `GF` - French Guiana
         * * `PF` - French Polynesia
         * * `TF` - French Southern Territories
         * * `GA` - Gabon
         * * `GM` - Gambia
         * * `GE` - Georgia
         * * `DE` - Germany
         * * `GH` - Ghana
         * * `GI` - Gibraltar
         * * `GR` - Greece
         * * `GL` - Greenland
         * * `GD` - Grenada
         * * `GP` - Guadeloupe
         * * `GU` - Guam
         * * `GT` - Guatemala
         * * `GG` - Guernsey
         * * `GN` - Guinea
         * * `GW` - Guinea-Bissau
         * * `GY` - Guyana
         * * `HT` - Haiti
         * * `HM` - Heard Island and McDonald Islands
         * * `VA` - Holy See
         * * `HN` - Honduras
         * * `HK` - Hong Kong
         * * `HU` - Hungary
         * * `IS` - Iceland
         * * `IN` - India
         * * `ID` - Indonesia
         * * `IR` - Iran
         * * `IQ` - Iraq
         * * `IE` - Ireland
         * * `IM` - Isle of Man
         * * `IL` - Israel
         * * `IT` - Italy
         * * `JM` - Jamaica
         * * `JP` - Japan
         * * `JE` - Jersey
         * * `JO` - Jordan
         * * `KZ` - Kazakhstan
         * * `KE` - Kenya
         * * `KI` - Kiribati
         * * `XK` - Kosovo
         * * `KW` - Kuwait
         * * `KG` - Kyrgyzstan
         * * `LA` - Laos
         * * `LV` - Latvia
         * * `LB` - Lebanon
         * * `LS` - Lesotho
         * * `LR` - Liberia
         * * `LY` - Libya
         * * `LI` - Liechtenstein
         * * `LT` - Lithuania
         * * `LU` - Luxembourg
         * * `MO` - Macao
         * * `MG` - Madagascar
         * * `MW` - Malawi
         * * `MY` - Malaysia
         * * `MV` - Maldives
         * * `ML` - Mali
         * * `MT` - Malta
         * * `MH` - Marshall Islands
         * * `MQ` - Martinique
         * * `MR` - Mauritania
         * * `MU` - Mauritius
         * * `YT` - Mayotte
         * * `MX` - Mexico
         * * `FM` - Micronesia
         * * `MD` - Moldova
         * * `MC` - Monaco
         * * `MN` - Mongolia
         * * `ME` - Montenegro
         * * `MS` - Montserrat
         * * `MA` - Morocco
         * * `MZ` - Mozambique
         * * `MM` - Myanmar
         * * `NA` - Namibia
         * * `NR` - Nauru
         * * `NP` - Nepal
         * * `NL` - Netherlands
         * * `NC` - New Caledonia
         * * `NZ` - New Zealand
         * * `NI` - Nicaragua
         * * `NE` - Niger
         * * `NG` - Nigeria
         * * `NU` - Niue
         * * `NF` - Norfolk Island
         * * `KP` - North Korea
         * * `MK` - North Macedonia
         * * `MP` - Northern Mariana Islands
         * * `NO` - Norway
         * * `OM` - Oman
         * * `PK` - Pakistan
         * * `PW` - Palau
         * * `PS` - Palestine, State of
         * * `PA` - Panama
         * * `PG` - Papua New Guinea
         * * `PY` - Paraguay
         * * `PE` - Peru
         * * `PH` - Philippines
         * * `PN` - Pitcairn
         * * `PL` - Poland
         * * `PT` - Portugal
         * * `PR` - Puerto Rico
         * * `QA` - Qatar
         * * `RE` - Réunion
         * * `RO` - Romania
         * * `RU` - Russia
         * * `RW` - Rwanda
         * * `BL` - Saint Barthélemy
         * * `SH` - Saint Helena, Ascension and Tristan da Cunha
         * * `KN` - Saint Kitts and Nevis
         * * `LC` - Saint Lucia
         * * `MF` - Saint Martin (French part)
         * * `PM` - Saint Pierre and Miquelon
         * * `VC` - Saint Vincent and the Grenadines
         * * `WS` - Samoa
         * * `SM` - San Marino
         * * `ST` - Sao Tome and Principe
         * * `SA` - Saudi Arabia
         * * `SN` - Senegal
         * * `RS` - Serbia
         * * `SC` - Seychelles
         * * `SL` - Sierra Leone
         * * `SG` - Singapore
         * * `SX` - Sint Maarten (Dutch part)
         * * `SK` - Slovakia
         * * `SI` - Slovenia
         * * `SB` - Solomon Islands
         * * `SO` - Somalia
         * * `ZA` - South Africa
         * * `GS` - South Georgia and the South Sandwich Islands
         * * `KR` - South Korea
         * * `SS` - South Sudan
         * * `ES` - Spain
         * * `LK` - Sri Lanka
         * * `SD` - Sudan
         * * `SR` - Suriname
         * * `SJ` - Svalbard and Jan Mayen
         * * `SE` - Sweden
         * * `CH` - Switzerland
         * * `SY` - Syria
         * * `TW` - Taiwan
         * * `TJ` - Tajikistan
         * * `TZ` - Tanzania
         * * `TH` - Thailand
         * * `TL` - Timor-Leste
         * * `TG` - Togo
         * * `TK` - Tokelau
         * * `TO` - Tonga
         * * `TT` - Trinidad and Tobago
         * * `TN` - Tunisia
         * * `TR` - Türkiye
         * * `TM` - Turkmenistan
         * * `TC` - Turks and Caicos Islands
         * * `TV` - Tuvalu
         * * `UG` - Uganda
         * * `UA` - Ukraine
         * * `AE` - United Arab Emirates
         * * `GB` - United Kingdom
         * * `UM` - United States Minor Outlying Islands
         * * `US` - United States of America
         * * `UY` - Uruguay
         * * `UZ` - Uzbekistan
         * * `VU` - Vanuatu
         * * `VE` - Venezuela
         * * `VN` - Vietnam
         * * `VG` - Virgin Islands (British)
         * * `VI` - Virgin Islands (U.S.)
         * * `WF` - Wallis and Futuna
         * * `EH` - Western Sahara
         * * `YE` - Yemen
         * * `ZM` - Zambia
         * * `ZW` - Zimbabwe
         */
        nationalities?: (
          | 'AD'
          | 'AE'
          | 'AF'
          | 'AG'
          | 'AI'
          | 'AL'
          | 'AM'
          | 'AO'
          | 'AQ'
          | 'AR'
          | 'AS'
          | 'AT'
          | 'AU'
          | 'AW'
          | 'AX'
          | 'AZ'
          | 'BA'
          | 'BB'
          | 'BD'
          | 'BE'
          | 'BF'
          | 'BG'
          | 'BH'
          | 'BI'
          | 'BJ'
          | 'BL'
          | 'BM'
          | 'BN'
          | 'BO'
          | 'BQ'
          | 'BR'
          | 'BS'
          | 'BT'
          | 'BV'
          | 'BW'
          | 'BY'
          | 'BZ'
          | 'CA'
          | 'CC'
          | 'CD'
          | 'CF'
          | 'CG'
          | 'CH'
          | 'CI'
          | 'CK'
          | 'CL'
          | 'CM'
          | 'CN'
          | 'CO'
          | 'CR'
          | 'CU'
          | 'CV'
          | 'CW'
          | 'CX'
          | 'CY'
          | 'CZ'
          | 'DE'
          | 'DJ'
          | 'DK'
          | 'DM'
          | 'DO'
          | 'DZ'
          | 'EC'
          | 'EE'
          | 'EG'
          | 'EH'
          | 'ER'
          | 'ES'
          | 'ET'
          | 'FI'
          | 'FJ'
          | 'FK'
          | 'FM'
          | 'FO'
          | 'FR'
          | 'GA'
          | 'GB'
          | 'GD'
          | 'GE'
          | 'GF'
          | 'GG'
          | 'GH'
          | 'GI'
          | 'GL'
          | 'GM'
          | 'GN'
          | 'GP'
          | 'GQ'
          | 'GR'
          | 'GS'
          | 'GT'
          | 'GU'
          | 'GW'
          | 'GY'
          | 'HK'
          | 'HM'
          | 'HN'
          | 'HR'
          | 'HT'
          | 'HU'
          | 'ID'
          | 'IE'
          | 'IL'
          | 'IM'
          | 'IN'
          | 'IO'
          | 'IQ'
          | 'IR'
          | 'IS'
          | 'IT'
          | 'JE'
          | 'JM'
          | 'JO'
          | 'JP'
          | 'KE'
          | 'KG'
          | 'KH'
          | 'KI'
          | 'KM'
          | 'KN'
          | 'KP'
          | 'KR'
          | 'KW'
          | 'KY'
          | 'KZ'
          | 'LA'
          | 'LB'
          | 'LC'
          | 'LI'
          | 'LK'
          | 'LR'
          | 'LS'
          | 'LT'
          | 'LU'
          | 'LV'
          | 'LY'
          | 'MA'
          | 'MC'
          | 'MD'
          | 'ME'
          | 'MF'
          | 'MG'
          | 'MH'
          | 'MK'
          | 'ML'
          | 'MM'
          | 'MN'
          | 'MO'
          | 'MP'
          | 'MQ'
          | 'MR'
          | 'MS'
          | 'MT'
          | 'MU'
          | 'MV'
          | 'MW'
          | 'MX'
          | 'MY'
          | 'MZ'
          | 'NA'
          | 'NC'
          | 'NE'
          | 'NF'
          | 'NG'
          | 'NI'
          | 'NL'
          | 'NO'
          | 'NP'
          | 'NR'
          | 'NU'
          | 'NZ'
          | 'OM'
          | 'PA'
          | 'PE'
          | 'PF'
          | 'PG'
          | 'PH'
          | 'PK'
          | 'PL'
          | 'PM'
          | 'PN'
          | 'PR'
          | 'PS'
          | 'PT'
          | 'PW'
          | 'PY'
          | 'QA'
          | 'RE'
          | 'RO'
          | 'RS'
          | 'RU'
          | 'RW'
          | 'SA'
          | 'SB'
          | 'SC'
          | 'SD'
          | 'SE'
          | 'SG'
          | 'SH'
          | 'SI'
          | 'SJ'
          | 'SK'
          | 'SL'
          | 'SM'
          | 'SN'
          | 'SO'
          | 'SR'
          | 'SS'
          | 'ST'
          | 'SV'
          | 'SX'
          | 'SY'
          | 'SZ'
          | 'TC'
          | 'TD'
          | 'TF'
          | 'TG'
          | 'TH'
          | 'TJ'
          | 'TK'
          | 'TL'
          | 'TM'
          | 'TN'
          | 'TO'
          | 'TR'
          | 'TT'
          | 'TV'
          | 'TW'
          | 'TZ'
          | 'UA'
          | 'UG'
          | 'UM'
          | 'US'
          | 'UY'
          | 'UZ'
          | 'VA'
          | 'VC'
          | 'VE'
          | 'VG'
          | 'VI'
          | 'VN'
          | 'VU'
          | 'WF'
          | 'WS'
          | 'XK'
          | 'YE'
          | 'YT'
          | 'ZA'
          | 'ZM'
          | 'ZW'
        )[]
        /**
         * Ordering
         *
         * * `full_name` - Full name
         * * `-full_name` - Full name (descending)
         * * `date_of_birth` - Date of birth
         * * `-date_of_birth` - Date of birth (descending)
         * * `date_of_entry` - Date of entry
         * * `-date_of_entry` - Date of entry (descending)
         * * `date_joined` - Date joined
         * * `-date_joined` - Date joined (descending)
         * * `gender` - Gender
         * * `-gender` - Gender (descending)
         * * `year_of_rosenberg_entry` - Year of rosenberg entry
         * * `-year_of_rosenberg_entry` - Year of rosenberg entry (descending)
         * * `age` - Age
         * * `-age` - Age (descending)
         * * `created` - Created
         * * `-created` - Created (descending)
         * * `modified` - Modified
         * * `-modified` - Modified (descending)
         * * `last_update` - Last update
         * * `-last_update` - Last update (descending)
         * * `status` - Status
         * * `-status` - Status (descending)
         * * `current_grade_level` - Current grade level
         * * `-current_grade_level` - Current grade level (descending)
         * * `academic_review_status` - Academic review status
         * * `-academic_review_status` - Academic review status (descending)
         * * `offer_status` - Offer status
         * * `-offer_status` - Offer status (descending)
         * * `invoice_status` - Invoice status
         * * `-invoice_status` - Invoice status (descending)
         */
        ordering?: (
          | '-academic_review_status'
          | '-age'
          | '-created'
          | '-current_grade_level'
          | '-date_joined'
          | '-date_of_birth'
          | '-date_of_entry'
          | '-full_name'
          | '-gender'
          | '-invoice_status'
          | '-last_update'
          | '-modified'
          | '-offer_status'
          | '-status'
          | '-year_of_rosenberg_entry'
          | 'academic_review_status'
          | 'age'
          | 'created'
          | 'current_grade_level'
          | 'date_joined'
          | 'date_of_birth'
          | 'date_of_entry'
          | 'full_name'
          | 'gender'
          | 'invoice_status'
          | 'last_update'
          | 'modified'
          | 'offer_status'
          | 'status'
          | 'year_of_rosenberg_entry'
        )[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /**
         * * `1` - Completed
         * * `2` - In progress
         * * `3` - Waitlist
         * * `4` - Rejected
         * * `5` - Applied
         * * `7` - Lost
         */
        status?: (1 | 2 | 3 | 4 | 5 | 7)[]
        /**
         * * `male` - Male
         * * `female` - Female
         */
        student__gender?: 'female' | 'male'
        student__year_of_rosenberg_entry?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCandidateProfileListList, T>, any>({
        path: `/crm/candidate-profiles/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmCandidateProfilesRetrieve
     * @request GET:/crm/candidate-profiles/{student}/
     * @secure
     */,
    crmCandidateProfilesRetrieve: <T extends FetchKeys<CandidateProfileDetail>>(
      student: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CandidateProfileDetail, T>, any>({
        path: `/crm/candidate-profiles/${student}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmCandidateProfilesUpdate
     * @request PUT:/crm/candidate-profiles/{student}/
     * @secure
     */,
    crmCandidateProfilesUpdate: <T extends FetchKeys<CandidateProfileDetail>>(
      student: string,
      data: CandidateProfileDetailRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CandidateProfileDetail, T>, any>({
        path: `/crm/candidate-profiles/${student}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmCandidateProfilesPartialUpdate
     * @request PATCH:/crm/candidate-profiles/{student}/
     * @secure
     */,
    crmCandidateProfilesPartialUpdate: <
      T extends FetchKeys<CandidateProfileDetail>
    >(
      student: string,
      data: PatchedCandidateProfileDetailRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CandidateProfileDetail, T>, any>({
        path: `/crm/candidate-profiles/${student}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmCandidateProfilesCreateStudentProfileCreate
     * @request POST:/crm/candidate-profiles/{student}/create-student-profile/
     * @secure
     */,
    crmCandidateProfilesCreateStudentProfileCreate: <T extends FetchKeys<void>>(
      student: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, MessageResponseSchema>({
        path: `/crm/candidate-profiles/${student}/create-student-profile/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmCandidateProfilesAvailableFiltersRetrieve
     * @request GET:/crm/candidate-profiles/available-filters/
     * @secure
     */,
    crmCandidateProfilesAvailableFiltersRetrieve: <
      T extends FetchKeys<CandidateProfileAvailableFilters>
    >(
      query?: {
        /**
         * * `5` - Not started
         * * `10` - Invoice sent
         * * `15` - Waitlist sent
         * * `17` - Deposit
         * * `20` - Paid
         * * `22` - Waitlist deposit
         * * `25` - Waitlist paid
         * * `30` - Failed
         */
        academic_offer__invoice_status?: (
          | 10
          | 15
          | 17
          | 20
          | 22
          | 25
          | 30
          | 5
        )[]
        /**
         * * `5` - Not started
         * * `10` - Offered
         * * `15` - Waitlist offered
         * * `20` - Accepted
         * * `25` - Waitlist accepted
         * * `30` - No reply
         * * `35` - Rejected
         * * `40` - Lost
         */
        academic_offer__offer_status?: (10 | 15 | 20 | 25 | 30 | 35 | 40 | 5)[]
        /**
         * * `dayschool` - Day School
         * * `boarding` - Boarding
         * * `external` - External
         */
        academic_offer__program_type?:
          | 'boarding'
          | 'dayschool'
          | 'external'
          | null
        /**
         * * `5` - Not started
         * * `10` - Documents requested
         * * `15` - Assessment scheduled
         * * `20` - Final review
         * * `25` - Accepted
         * * `30` - Waitlist
         * * `35` - On hold
         * * `40` - Rejected
         */
        academic_review_status?: (10 | 15 | 20 | 25 | 30 | 35 | 40 | 5)[]
        /** Multiple values may be separated by commas. */
        age?: number[]
        allergen_kinds?: string[]
        career_goal?: number[]
        chronic_condition_types?: string[]
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        current_grade_level?: number[]
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        fitness_for_sports?: string[]
        hide_enrolled?: boolean
        insurance_type?: string[]
        is_athlete?: boolean
        is_resident_entrepreneur?: boolean
        is_swimmer?: string
        /**
         * * `l1` - L1, Regular and vegetarian. Students can choose themselves.
         * * `l2` - L2, Specific, individual requirements. Kitchen adapts the existing menu to specific needs.
         * * `l3` - L3, Specific, very individual requirements. Kitchen creates new menu to specific needs, almost every meal has to be cooked individually.
         * * `l4` - L4, Highly specific. Severe allergic reaction to traces of certain food. Meals need to be ordered from outside supplier.
         */
        meal_preference?: 'l1' | 'l2' | 'l3' | 'l4'
        /**
         * * `AF` - Afghanistan
         * * `AX` - Åland Islands
         * * `AL` - Albania
         * * `DZ` - Algeria
         * * `AS` - American Samoa
         * * `AD` - Andorra
         * * `AO` - Angola
         * * `AI` - Anguilla
         * * `AQ` - Antarctica
         * * `AG` - Antigua and Barbuda
         * * `AR` - Argentina
         * * `AM` - Armenia
         * * `AW` - Aruba
         * * `AU` - Australia
         * * `AT` - Austria
         * * `AZ` - Azerbaijan
         * * `BS` - Bahamas
         * * `BH` - Bahrain
         * * `BD` - Bangladesh
         * * `BB` - Barbados
         * * `BY` - Belarus
         * * `BE` - Belgium
         * * `BZ` - Belize
         * * `BJ` - Benin
         * * `BM` - Bermuda
         * * `BT` - Bhutan
         * * `BO` - Bolivia
         * * `BQ` - Bonaire, Sint Eustatius and Saba
         * * `BA` - Bosnia and Herzegovina
         * * `BW` - Botswana
         * * `BV` - Bouvet Island
         * * `BR` - Brazil
         * * `IO` - British Indian Ocean Territory
         * * `BN` - Brunei
         * * `BG` - Bulgaria
         * * `BF` - Burkina Faso
         * * `BI` - Burundi
         * * `CV` - Cabo Verde
         * * `KH` - Cambodia
         * * `CM` - Cameroon
         * * `CA` - Canada
         * * `KY` - Cayman Islands
         * * `CF` - Central African Republic
         * * `TD` - Chad
         * * `CL` - Chile
         * * `CN` - China
         * * `CX` - Christmas Island
         * * `CC` - Cocos (Keeling) Islands
         * * `CO` - Colombia
         * * `KM` - Comoros
         * * `CG` - Congo
         * * `CD` - Congo (the Democratic Republic of the)
         * * `CK` - Cook Islands
         * * `CR` - Costa Rica
         * * `CI` - Côte d'Ivoire
         * * `HR` - Croatia
         * * `CU` - Cuba
         * * `CW` - Curaçao
         * * `CY` - Cyprus
         * * `CZ` - Czechia
         * * `DK` - Denmark
         * * `DJ` - Djibouti
         * * `DM` - Dominica
         * * `DO` - Dominican Republic
         * * `EC` - Ecuador
         * * `EG` - Egypt
         * * `SV` - El Salvador
         * * `GQ` - Equatorial Guinea
         * * `ER` - Eritrea
         * * `EE` - Estonia
         * * `SZ` - Eswatini
         * * `ET` - Ethiopia
         * * `FK` - Falkland Islands (Malvinas)
         * * `FO` - Faroe Islands
         * * `FJ` - Fiji
         * * `FI` - Finland
         * * `FR` - France
         * * `GF` - French Guiana
         * * `PF` - French Polynesia
         * * `TF` - French Southern Territories
         * * `GA` - Gabon
         * * `GM` - Gambia
         * * `GE` - Georgia
         * * `DE` - Germany
         * * `GH` - Ghana
         * * `GI` - Gibraltar
         * * `GR` - Greece
         * * `GL` - Greenland
         * * `GD` - Grenada
         * * `GP` - Guadeloupe
         * * `GU` - Guam
         * * `GT` - Guatemala
         * * `GG` - Guernsey
         * * `GN` - Guinea
         * * `GW` - Guinea-Bissau
         * * `GY` - Guyana
         * * `HT` - Haiti
         * * `HM` - Heard Island and McDonald Islands
         * * `VA` - Holy See
         * * `HN` - Honduras
         * * `HK` - Hong Kong
         * * `HU` - Hungary
         * * `IS` - Iceland
         * * `IN` - India
         * * `ID` - Indonesia
         * * `IR` - Iran
         * * `IQ` - Iraq
         * * `IE` - Ireland
         * * `IM` - Isle of Man
         * * `IL` - Israel
         * * `IT` - Italy
         * * `JM` - Jamaica
         * * `JP` - Japan
         * * `JE` - Jersey
         * * `JO` - Jordan
         * * `KZ` - Kazakhstan
         * * `KE` - Kenya
         * * `KI` - Kiribati
         * * `XK` - Kosovo
         * * `KW` - Kuwait
         * * `KG` - Kyrgyzstan
         * * `LA` - Laos
         * * `LV` - Latvia
         * * `LB` - Lebanon
         * * `LS` - Lesotho
         * * `LR` - Liberia
         * * `LY` - Libya
         * * `LI` - Liechtenstein
         * * `LT` - Lithuania
         * * `LU` - Luxembourg
         * * `MO` - Macao
         * * `MG` - Madagascar
         * * `MW` - Malawi
         * * `MY` - Malaysia
         * * `MV` - Maldives
         * * `ML` - Mali
         * * `MT` - Malta
         * * `MH` - Marshall Islands
         * * `MQ` - Martinique
         * * `MR` - Mauritania
         * * `MU` - Mauritius
         * * `YT` - Mayotte
         * * `MX` - Mexico
         * * `FM` - Micronesia
         * * `MD` - Moldova
         * * `MC` - Monaco
         * * `MN` - Mongolia
         * * `ME` - Montenegro
         * * `MS` - Montserrat
         * * `MA` - Morocco
         * * `MZ` - Mozambique
         * * `MM` - Myanmar
         * * `NA` - Namibia
         * * `NR` - Nauru
         * * `NP` - Nepal
         * * `NL` - Netherlands
         * * `NC` - New Caledonia
         * * `NZ` - New Zealand
         * * `NI` - Nicaragua
         * * `NE` - Niger
         * * `NG` - Nigeria
         * * `NU` - Niue
         * * `NF` - Norfolk Island
         * * `KP` - North Korea
         * * `MK` - North Macedonia
         * * `MP` - Northern Mariana Islands
         * * `NO` - Norway
         * * `OM` - Oman
         * * `PK` - Pakistan
         * * `PW` - Palau
         * * `PS` - Palestine, State of
         * * `PA` - Panama
         * * `PG` - Papua New Guinea
         * * `PY` - Paraguay
         * * `PE` - Peru
         * * `PH` - Philippines
         * * `PN` - Pitcairn
         * * `PL` - Poland
         * * `PT` - Portugal
         * * `PR` - Puerto Rico
         * * `QA` - Qatar
         * * `RE` - Réunion
         * * `RO` - Romania
         * * `RU` - Russia
         * * `RW` - Rwanda
         * * `BL` - Saint Barthélemy
         * * `SH` - Saint Helena, Ascension and Tristan da Cunha
         * * `KN` - Saint Kitts and Nevis
         * * `LC` - Saint Lucia
         * * `MF` - Saint Martin (French part)
         * * `PM` - Saint Pierre and Miquelon
         * * `VC` - Saint Vincent and the Grenadines
         * * `WS` - Samoa
         * * `SM` - San Marino
         * * `ST` - Sao Tome and Principe
         * * `SA` - Saudi Arabia
         * * `SN` - Senegal
         * * `RS` - Serbia
         * * `SC` - Seychelles
         * * `SL` - Sierra Leone
         * * `SG` - Singapore
         * * `SX` - Sint Maarten (Dutch part)
         * * `SK` - Slovakia
         * * `SI` - Slovenia
         * * `SB` - Solomon Islands
         * * `SO` - Somalia
         * * `ZA` - South Africa
         * * `GS` - South Georgia and the South Sandwich Islands
         * * `KR` - South Korea
         * * `SS` - South Sudan
         * * `ES` - Spain
         * * `LK` - Sri Lanka
         * * `SD` - Sudan
         * * `SR` - Suriname
         * * `SJ` - Svalbard and Jan Mayen
         * * `SE` - Sweden
         * * `CH` - Switzerland
         * * `SY` - Syria
         * * `TW` - Taiwan
         * * `TJ` - Tajikistan
         * * `TZ` - Tanzania
         * * `TH` - Thailand
         * * `TL` - Timor-Leste
         * * `TG` - Togo
         * * `TK` - Tokelau
         * * `TO` - Tonga
         * * `TT` - Trinidad and Tobago
         * * `TN` - Tunisia
         * * `TR` - Türkiye
         * * `TM` - Turkmenistan
         * * `TC` - Turks and Caicos Islands
         * * `TV` - Tuvalu
         * * `UG` - Uganda
         * * `UA` - Ukraine
         * * `AE` - United Arab Emirates
         * * `GB` - United Kingdom
         * * `UM` - United States Minor Outlying Islands
         * * `US` - United States of America
         * * `UY` - Uruguay
         * * `UZ` - Uzbekistan
         * * `VU` - Vanuatu
         * * `VE` - Venezuela
         * * `VN` - Vietnam
         * * `VG` - Virgin Islands (British)
         * * `VI` - Virgin Islands (U.S.)
         * * `WF` - Wallis and Futuna
         * * `EH` - Western Sahara
         * * `YE` - Yemen
         * * `ZM` - Zambia
         * * `ZW` - Zimbabwe
         */
        nationalities?: (
          | 'AD'
          | 'AE'
          | 'AF'
          | 'AG'
          | 'AI'
          | 'AL'
          | 'AM'
          | 'AO'
          | 'AQ'
          | 'AR'
          | 'AS'
          | 'AT'
          | 'AU'
          | 'AW'
          | 'AX'
          | 'AZ'
          | 'BA'
          | 'BB'
          | 'BD'
          | 'BE'
          | 'BF'
          | 'BG'
          | 'BH'
          | 'BI'
          | 'BJ'
          | 'BL'
          | 'BM'
          | 'BN'
          | 'BO'
          | 'BQ'
          | 'BR'
          | 'BS'
          | 'BT'
          | 'BV'
          | 'BW'
          | 'BY'
          | 'BZ'
          | 'CA'
          | 'CC'
          | 'CD'
          | 'CF'
          | 'CG'
          | 'CH'
          | 'CI'
          | 'CK'
          | 'CL'
          | 'CM'
          | 'CN'
          | 'CO'
          | 'CR'
          | 'CU'
          | 'CV'
          | 'CW'
          | 'CX'
          | 'CY'
          | 'CZ'
          | 'DE'
          | 'DJ'
          | 'DK'
          | 'DM'
          | 'DO'
          | 'DZ'
          | 'EC'
          | 'EE'
          | 'EG'
          | 'EH'
          | 'ER'
          | 'ES'
          | 'ET'
          | 'FI'
          | 'FJ'
          | 'FK'
          | 'FM'
          | 'FO'
          | 'FR'
          | 'GA'
          | 'GB'
          | 'GD'
          | 'GE'
          | 'GF'
          | 'GG'
          | 'GH'
          | 'GI'
          | 'GL'
          | 'GM'
          | 'GN'
          | 'GP'
          | 'GQ'
          | 'GR'
          | 'GS'
          | 'GT'
          | 'GU'
          | 'GW'
          | 'GY'
          | 'HK'
          | 'HM'
          | 'HN'
          | 'HR'
          | 'HT'
          | 'HU'
          | 'ID'
          | 'IE'
          | 'IL'
          | 'IM'
          | 'IN'
          | 'IO'
          | 'IQ'
          | 'IR'
          | 'IS'
          | 'IT'
          | 'JE'
          | 'JM'
          | 'JO'
          | 'JP'
          | 'KE'
          | 'KG'
          | 'KH'
          | 'KI'
          | 'KM'
          | 'KN'
          | 'KP'
          | 'KR'
          | 'KW'
          | 'KY'
          | 'KZ'
          | 'LA'
          | 'LB'
          | 'LC'
          | 'LI'
          | 'LK'
          | 'LR'
          | 'LS'
          | 'LT'
          | 'LU'
          | 'LV'
          | 'LY'
          | 'MA'
          | 'MC'
          | 'MD'
          | 'ME'
          | 'MF'
          | 'MG'
          | 'MH'
          | 'MK'
          | 'ML'
          | 'MM'
          | 'MN'
          | 'MO'
          | 'MP'
          | 'MQ'
          | 'MR'
          | 'MS'
          | 'MT'
          | 'MU'
          | 'MV'
          | 'MW'
          | 'MX'
          | 'MY'
          | 'MZ'
          | 'NA'
          | 'NC'
          | 'NE'
          | 'NF'
          | 'NG'
          | 'NI'
          | 'NL'
          | 'NO'
          | 'NP'
          | 'NR'
          | 'NU'
          | 'NZ'
          | 'OM'
          | 'PA'
          | 'PE'
          | 'PF'
          | 'PG'
          | 'PH'
          | 'PK'
          | 'PL'
          | 'PM'
          | 'PN'
          | 'PR'
          | 'PS'
          | 'PT'
          | 'PW'
          | 'PY'
          | 'QA'
          | 'RE'
          | 'RO'
          | 'RS'
          | 'RU'
          | 'RW'
          | 'SA'
          | 'SB'
          | 'SC'
          | 'SD'
          | 'SE'
          | 'SG'
          | 'SH'
          | 'SI'
          | 'SJ'
          | 'SK'
          | 'SL'
          | 'SM'
          | 'SN'
          | 'SO'
          | 'SR'
          | 'SS'
          | 'ST'
          | 'SV'
          | 'SX'
          | 'SY'
          | 'SZ'
          | 'TC'
          | 'TD'
          | 'TF'
          | 'TG'
          | 'TH'
          | 'TJ'
          | 'TK'
          | 'TL'
          | 'TM'
          | 'TN'
          | 'TO'
          | 'TR'
          | 'TT'
          | 'TV'
          | 'TW'
          | 'TZ'
          | 'UA'
          | 'UG'
          | 'UM'
          | 'US'
          | 'UY'
          | 'UZ'
          | 'VA'
          | 'VC'
          | 'VE'
          | 'VG'
          | 'VI'
          | 'VN'
          | 'VU'
          | 'WF'
          | 'WS'
          | 'XK'
          | 'YE'
          | 'YT'
          | 'ZA'
          | 'ZM'
          | 'ZW'
        )[]
        /**
         * Ordering
         *
         * * `full_name` - Full name
         * * `-full_name` - Full name (descending)
         * * `date_of_birth` - Date of birth
         * * `-date_of_birth` - Date of birth (descending)
         * * `date_of_entry` - Date of entry
         * * `-date_of_entry` - Date of entry (descending)
         * * `date_joined` - Date joined
         * * `-date_joined` - Date joined (descending)
         * * `gender` - Gender
         * * `-gender` - Gender (descending)
         * * `year_of_rosenberg_entry` - Year of rosenberg entry
         * * `-year_of_rosenberg_entry` - Year of rosenberg entry (descending)
         * * `age` - Age
         * * `-age` - Age (descending)
         * * `created` - Created
         * * `-created` - Created (descending)
         * * `modified` - Modified
         * * `-modified` - Modified (descending)
         * * `last_update` - Last update
         * * `-last_update` - Last update (descending)
         * * `status` - Status
         * * `-status` - Status (descending)
         * * `current_grade_level` - Current grade level
         * * `-current_grade_level` - Current grade level (descending)
         * * `academic_review_status` - Academic review status
         * * `-academic_review_status` - Academic review status (descending)
         * * `offer_status` - Offer status
         * * `-offer_status` - Offer status (descending)
         * * `invoice_status` - Invoice status
         * * `-invoice_status` - Invoice status (descending)
         */
        ordering?: (
          | '-academic_review_status'
          | '-age'
          | '-created'
          | '-current_grade_level'
          | '-date_joined'
          | '-date_of_birth'
          | '-date_of_entry'
          | '-full_name'
          | '-gender'
          | '-invoice_status'
          | '-last_update'
          | '-modified'
          | '-offer_status'
          | '-status'
          | '-year_of_rosenberg_entry'
          | 'academic_review_status'
          | 'age'
          | 'created'
          | 'current_grade_level'
          | 'date_joined'
          | 'date_of_birth'
          | 'date_of_entry'
          | 'full_name'
          | 'gender'
          | 'invoice_status'
          | 'last_update'
          | 'modified'
          | 'offer_status'
          | 'status'
          | 'year_of_rosenberg_entry'
        )[]
        /** A search term. */
        search?: string
        /**
         * * `1` - Completed
         * * `2` - In progress
         * * `3` - Waitlist
         * * `4` - Rejected
         * * `5` - Applied
         * * `7` - Lost
         */
        status?: (1 | 2 | 3 | 4 | 5 | 7)[]
        /**
         * * `male` - Male
         * * `female` - Female
         */
        student__gender?: 'female' | 'male'
        student__year_of_rosenberg_entry?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CandidateProfileAvailableFilters, T>, any>({
        path: `/crm/candidate-profiles/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmCandidateProfilesSummaryRetrieve
     * @request GET:/crm/candidate-profiles/summary/
     * @secure
     */,
    crmCandidateProfilesSummaryRetrieve: <
      T extends FetchKeys<CandidateProfileSummaryResponse>
    >(
      query?: {
        academic_review_status?: (5 | 10 | 15 | 20 | 25 | 30 | 35 | 40)[]
        enrolled_status?: ('enrolled' | 'not_enrolled')[]
        invoice_status?: (5 | 10 | 15 | 17 | 20 | 22 | 25 | 30)[]
        offer_status?: (5 | 10 | 15 | 20 | 25 | 30 | 35 | 40)[]
        school_year?: number
        /** A search term. */
        search?: string
        search_in_profile_statuses?: (1 | 2 | 3 | 4 | 5 | 7)[]
        status?: (1 | 2 | 3 | 4 | 5 | 7)[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CandidateProfileSummaryResponse, T>, any>({
        path: `/crm/candidate-profiles/summary/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing company-related data.
     *
     * @tags crm
     * @name CrmContactDetailsCompaniesAddressesList
     * @request GET:/crm/contact-details/companies/{userId}/addresses/
     * @secure
     */,
    crmContactDetailsCompaniesAddressesList: <
      T extends FetchKeys<UserAddress[]>
    >(
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAddress[], T>, any>({
        path: `/crm/contact-details/companies/${userId}/addresses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing company-related data.
     *
     * @tags crm
     * @name CrmContactDetailsCompaniesAddressesCreate
     * @request POST:/crm/contact-details/companies/{userId}/addresses/
     * @secure
     */,
    crmContactDetailsCompaniesAddressesCreate: <
      T extends FetchKeys<UserAddress>
    >(
      userId: string,
      data: UserAddressRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAddress, T>, any>({
        path: `/crm/contact-details/companies/${userId}/addresses/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing company-related data.
     *
     * @tags crm
     * @name CrmContactDetailsCompaniesAddressesRetrieve
     * @request GET:/crm/contact-details/companies/{userId}/addresses/{id}/
     * @secure
     */,
    crmContactDetailsCompaniesAddressesRetrieve: <
      T extends FetchKeys<UserAddress>
    >(
      id: number,
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAddress, T>, any>({
        path: `/crm/contact-details/companies/${userId}/addresses/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing company-related data.
     *
     * @tags crm
     * @name CrmContactDetailsCompaniesAddressesUpdate
     * @request PUT:/crm/contact-details/companies/{userId}/addresses/{id}/
     * @secure
     */,
    crmContactDetailsCompaniesAddressesUpdate: <
      T extends FetchKeys<UserAddress>
    >(
      id: number,
      userId: string,
      data: UserAddressRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAddress, T>, any>({
        path: `/crm/contact-details/companies/${userId}/addresses/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing company-related data.
     *
     * @tags crm
     * @name CrmContactDetailsCompaniesAddressesPartialUpdate
     * @request PATCH:/crm/contact-details/companies/{userId}/addresses/{id}/
     * @secure
     */,
    crmContactDetailsCompaniesAddressesPartialUpdate: <
      T extends FetchKeys<UserAddress>
    >(
      id: number,
      userId: string,
      data: PatchedUserAddressRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAddress, T>, any>({
        path: `/crm/contact-details/companies/${userId}/addresses/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing company-related data.
     *
     * @tags crm
     * @name CrmContactDetailsCompaniesAddressesDestroy
     * @request DELETE:/crm/contact-details/companies/{userId}/addresses/{id}/
     * @secure
     */,
    crmContactDetailsCompaniesAddressesDestroy: <T extends FetchKeys<void>>(
      id: number,
      userId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/crm/contact-details/companies/${userId}/addresses/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing company-related data.
     *
     * @tags crm
     * @name CrmContactDetailsCompaniesEmailsList
     * @request GET:/crm/contact-details/companies/{userId}/emails/
     * @secure
     */,
    crmContactDetailsCompaniesEmailsList: <
      T extends FetchKeys<UserAdditionalEmail[]>
    >(
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalEmail[], T>, any>({
        path: `/crm/contact-details/companies/${userId}/emails/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing company-related data.
     *
     * @tags crm
     * @name CrmContactDetailsCompaniesEmailsCreate
     * @request POST:/crm/contact-details/companies/{userId}/emails/
     * @secure
     */,
    crmContactDetailsCompaniesEmailsCreate: <
      T extends FetchKeys<UserAdditionalEmail>
    >(
      userId: string,
      data: UserAdditionalEmailRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalEmail, T>, any>({
        path: `/crm/contact-details/companies/${userId}/emails/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing company-related data.
     *
     * @tags crm
     * @name CrmContactDetailsCompaniesEmailsRetrieve
     * @request GET:/crm/contact-details/companies/{userId}/emails/{id}/
     * @secure
     */,
    crmContactDetailsCompaniesEmailsRetrieve: <
      T extends FetchKeys<UserAdditionalEmail>
    >(
      id: number,
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalEmail, T>, any>({
        path: `/crm/contact-details/companies/${userId}/emails/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing company-related data.
     *
     * @tags crm
     * @name CrmContactDetailsCompaniesEmailsUpdate
     * @request PUT:/crm/contact-details/companies/{userId}/emails/{id}/
     * @secure
     */,
    crmContactDetailsCompaniesEmailsUpdate: <
      T extends FetchKeys<UserAdditionalEmail>
    >(
      id: number,
      userId: string,
      data: UserAdditionalEmailRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalEmail, T>, any>({
        path: `/crm/contact-details/companies/${userId}/emails/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing company-related data.
     *
     * @tags crm
     * @name CrmContactDetailsCompaniesEmailsPartialUpdate
     * @request PATCH:/crm/contact-details/companies/{userId}/emails/{id}/
     * @secure
     */,
    crmContactDetailsCompaniesEmailsPartialUpdate: <
      T extends FetchKeys<UserAdditionalEmail>
    >(
      id: number,
      userId: string,
      data: PatchedUserAdditionalEmailRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalEmail, T>, any>({
        path: `/crm/contact-details/companies/${userId}/emails/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing company-related data.
     *
     * @tags crm
     * @name CrmContactDetailsCompaniesEmailsDestroy
     * @request DELETE:/crm/contact-details/companies/{userId}/emails/{id}/
     * @secure
     */,
    crmContactDetailsCompaniesEmailsDestroy: <T extends FetchKeys<void>>(
      id: number,
      userId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/crm/contact-details/companies/${userId}/emails/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing company-related data.
     *
     * @tags crm
     * @name CrmContactDetailsCompaniesPhoneNumbersList
     * @request GET:/crm/contact-details/companies/{userId}/phone-numbers/
     * @secure
     */,
    crmContactDetailsCompaniesPhoneNumbersList: <
      T extends FetchKeys<UserAdditionalPhoneNumber[]>
    >(
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalPhoneNumber[], T>, any>({
        path: `/crm/contact-details/companies/${userId}/phone-numbers/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing company-related data.
     *
     * @tags crm
     * @name CrmContactDetailsCompaniesPhoneNumbersCreate
     * @request POST:/crm/contact-details/companies/{userId}/phone-numbers/
     * @secure
     */,
    crmContactDetailsCompaniesPhoneNumbersCreate: <
      T extends FetchKeys<UserAdditionalPhoneNumber>
    >(
      userId: string,
      data: UserAdditionalPhoneNumberRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalPhoneNumber, T>, any>({
        path: `/crm/contact-details/companies/${userId}/phone-numbers/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing company-related data.
     *
     * @tags crm
     * @name CrmContactDetailsCompaniesPhoneNumbersRetrieve
     * @request GET:/crm/contact-details/companies/{userId}/phone-numbers/{id}/
     * @secure
     */,
    crmContactDetailsCompaniesPhoneNumbersRetrieve: <
      T extends FetchKeys<UserAdditionalPhoneNumber>
    >(
      id: number,
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalPhoneNumber, T>, any>({
        path: `/crm/contact-details/companies/${userId}/phone-numbers/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing company-related data.
     *
     * @tags crm
     * @name CrmContactDetailsCompaniesPhoneNumbersUpdate
     * @request PUT:/crm/contact-details/companies/{userId}/phone-numbers/{id}/
     * @secure
     */,
    crmContactDetailsCompaniesPhoneNumbersUpdate: <
      T extends FetchKeys<UserAdditionalPhoneNumber>
    >(
      id: number,
      userId: string,
      data: UserAdditionalPhoneNumberRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalPhoneNumber, T>, any>({
        path: `/crm/contact-details/companies/${userId}/phone-numbers/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing company-related data.
     *
     * @tags crm
     * @name CrmContactDetailsCompaniesPhoneNumbersPartialUpdate
     * @request PATCH:/crm/contact-details/companies/{userId}/phone-numbers/{id}/
     * @secure
     */,
    crmContactDetailsCompaniesPhoneNumbersPartialUpdate: <
      T extends FetchKeys<UserAdditionalPhoneNumber>
    >(
      id: number,
      userId: string,
      data: PatchedUserAdditionalPhoneNumberRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalPhoneNumber, T>, any>({
        path: `/crm/contact-details/companies/${userId}/phone-numbers/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing company-related data.
     *
     * @tags crm
     * @name CrmContactDetailsCompaniesPhoneNumbersDestroy
     * @request DELETE:/crm/contact-details/companies/{userId}/phone-numbers/{id}/
     * @secure
     */,
    crmContactDetailsCompaniesPhoneNumbersDestroy: <T extends FetchKeys<void>>(
      id: number,
      userId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/crm/contact-details/companies/${userId}/phone-numbers/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing parent-related (contacts) data.
     *
     * @tags crm
     * @name CrmContactDetailsContactsAddressesList
     * @request GET:/crm/contact-details/contacts/{userId}/addresses/
     * @secure
     */,
    crmContactDetailsContactsAddressesList: <
      T extends FetchKeys<UserAddress[]>
    >(
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAddress[], T>, any>({
        path: `/crm/contact-details/contacts/${userId}/addresses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing parent-related (contacts) data.
     *
     * @tags crm
     * @name CrmContactDetailsContactsAddressesCreate
     * @request POST:/crm/contact-details/contacts/{userId}/addresses/
     * @secure
     */,
    crmContactDetailsContactsAddressesCreate: <
      T extends FetchKeys<UserAddress>
    >(
      userId: string,
      data: UserAddressRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAddress, T>, any>({
        path: `/crm/contact-details/contacts/${userId}/addresses/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing parent-related (contacts) data.
     *
     * @tags crm
     * @name CrmContactDetailsContactsAddressesRetrieve
     * @request GET:/crm/contact-details/contacts/{userId}/addresses/{id}/
     * @secure
     */,
    crmContactDetailsContactsAddressesRetrieve: <
      T extends FetchKeys<UserAddress>
    >(
      id: number,
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAddress, T>, any>({
        path: `/crm/contact-details/contacts/${userId}/addresses/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing parent-related (contacts) data.
     *
     * @tags crm
     * @name CrmContactDetailsContactsAddressesUpdate
     * @request PUT:/crm/contact-details/contacts/{userId}/addresses/{id}/
     * @secure
     */,
    crmContactDetailsContactsAddressesUpdate: <
      T extends FetchKeys<UserAddress>
    >(
      id: number,
      userId: string,
      data: UserAddressRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAddress, T>, any>({
        path: `/crm/contact-details/contacts/${userId}/addresses/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing parent-related (contacts) data.
     *
     * @tags crm
     * @name CrmContactDetailsContactsAddressesPartialUpdate
     * @request PATCH:/crm/contact-details/contacts/{userId}/addresses/{id}/
     * @secure
     */,
    crmContactDetailsContactsAddressesPartialUpdate: <
      T extends FetchKeys<UserAddress>
    >(
      id: number,
      userId: string,
      data: PatchedUserAddressRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAddress, T>, any>({
        path: `/crm/contact-details/contacts/${userId}/addresses/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing parent-related (contacts) data.
     *
     * @tags crm
     * @name CrmContactDetailsContactsAddressesDestroy
     * @request DELETE:/crm/contact-details/contacts/{userId}/addresses/{id}/
     * @secure
     */,
    crmContactDetailsContactsAddressesDestroy: <T extends FetchKeys<void>>(
      id: number,
      userId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/crm/contact-details/contacts/${userId}/addresses/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing parent-related (contacts) data.
     *
     * @tags crm
     * @name CrmContactDetailsContactsEmailsList
     * @request GET:/crm/contact-details/contacts/{userId}/emails/
     * @secure
     */,
    crmContactDetailsContactsEmailsList: <
      T extends FetchKeys<UserAdditionalEmail[]>
    >(
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalEmail[], T>, any>({
        path: `/crm/contact-details/contacts/${userId}/emails/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing parent-related (contacts) data.
     *
     * @tags crm
     * @name CrmContactDetailsContactsEmailsCreate
     * @request POST:/crm/contact-details/contacts/{userId}/emails/
     * @secure
     */,
    crmContactDetailsContactsEmailsCreate: <
      T extends FetchKeys<UserAdditionalEmail>
    >(
      userId: string,
      data: UserAdditionalEmailRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalEmail, T>, any>({
        path: `/crm/contact-details/contacts/${userId}/emails/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing parent-related (contacts) data.
     *
     * @tags crm
     * @name CrmContactDetailsContactsEmailsRetrieve
     * @request GET:/crm/contact-details/contacts/{userId}/emails/{id}/
     * @secure
     */,
    crmContactDetailsContactsEmailsRetrieve: <
      T extends FetchKeys<UserAdditionalEmail>
    >(
      id: number,
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalEmail, T>, any>({
        path: `/crm/contact-details/contacts/${userId}/emails/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing parent-related (contacts) data.
     *
     * @tags crm
     * @name CrmContactDetailsContactsEmailsUpdate
     * @request PUT:/crm/contact-details/contacts/{userId}/emails/{id}/
     * @secure
     */,
    crmContactDetailsContactsEmailsUpdate: <
      T extends FetchKeys<UserAdditionalEmail>
    >(
      id: number,
      userId: string,
      data: UserAdditionalEmailRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalEmail, T>, any>({
        path: `/crm/contact-details/contacts/${userId}/emails/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing parent-related (contacts) data.
     *
     * @tags crm
     * @name CrmContactDetailsContactsEmailsPartialUpdate
     * @request PATCH:/crm/contact-details/contacts/{userId}/emails/{id}/
     * @secure
     */,
    crmContactDetailsContactsEmailsPartialUpdate: <
      T extends FetchKeys<UserAdditionalEmail>
    >(
      id: number,
      userId: string,
      data: PatchedUserAdditionalEmailRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalEmail, T>, any>({
        path: `/crm/contact-details/contacts/${userId}/emails/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing parent-related (contacts) data.
     *
     * @tags crm
     * @name CrmContactDetailsContactsEmailsDestroy
     * @request DELETE:/crm/contact-details/contacts/{userId}/emails/{id}/
     * @secure
     */,
    crmContactDetailsContactsEmailsDestroy: <T extends FetchKeys<void>>(
      id: number,
      userId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/crm/contact-details/contacts/${userId}/emails/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing parent-related (contacts) data.
     *
     * @tags crm
     * @name CrmContactDetailsContactsPhoneNumbersList
     * @request GET:/crm/contact-details/contacts/{userId}/phone-numbers/
     * @secure
     */,
    crmContactDetailsContactsPhoneNumbersList: <
      T extends FetchKeys<UserAdditionalPhoneNumber[]>
    >(
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalPhoneNumber[], T>, any>({
        path: `/crm/contact-details/contacts/${userId}/phone-numbers/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing parent-related (contacts) data.
     *
     * @tags crm
     * @name CrmContactDetailsContactsPhoneNumbersCreate
     * @request POST:/crm/contact-details/contacts/{userId}/phone-numbers/
     * @secure
     */,
    crmContactDetailsContactsPhoneNumbersCreate: <
      T extends FetchKeys<UserAdditionalPhoneNumber>
    >(
      userId: string,
      data: UserAdditionalPhoneNumberRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalPhoneNumber, T>, any>({
        path: `/crm/contact-details/contacts/${userId}/phone-numbers/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing parent-related (contacts) data.
     *
     * @tags crm
     * @name CrmContactDetailsContactsPhoneNumbersRetrieve
     * @request GET:/crm/contact-details/contacts/{userId}/phone-numbers/{id}/
     * @secure
     */,
    crmContactDetailsContactsPhoneNumbersRetrieve: <
      T extends FetchKeys<UserAdditionalPhoneNumber>
    >(
      id: number,
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalPhoneNumber, T>, any>({
        path: `/crm/contact-details/contacts/${userId}/phone-numbers/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing parent-related (contacts) data.
     *
     * @tags crm
     * @name CrmContactDetailsContactsPhoneNumbersUpdate
     * @request PUT:/crm/contact-details/contacts/{userId}/phone-numbers/{id}/
     * @secure
     */,
    crmContactDetailsContactsPhoneNumbersUpdate: <
      T extends FetchKeys<UserAdditionalPhoneNumber>
    >(
      id: number,
      userId: string,
      data: UserAdditionalPhoneNumberRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalPhoneNumber, T>, any>({
        path: `/crm/contact-details/contacts/${userId}/phone-numbers/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing parent-related (contacts) data.
     *
     * @tags crm
     * @name CrmContactDetailsContactsPhoneNumbersPartialUpdate
     * @request PATCH:/crm/contact-details/contacts/{userId}/phone-numbers/{id}/
     * @secure
     */,
    crmContactDetailsContactsPhoneNumbersPartialUpdate: <
      T extends FetchKeys<UserAdditionalPhoneNumber>
    >(
      id: number,
      userId: string,
      data: PatchedUserAdditionalPhoneNumberRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalPhoneNumber, T>, any>({
        path: `/crm/contact-details/contacts/${userId}/phone-numbers/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing parent-related (contacts) data.
     *
     * @tags crm
     * @name CrmContactDetailsContactsPhoneNumbersDestroy
     * @request DELETE:/crm/contact-details/contacts/{userId}/phone-numbers/{id}/
     * @secure
     */,
    crmContactDetailsContactsPhoneNumbersDestroy: <T extends FetchKeys<void>>(
      id: number,
      userId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/crm/contact-details/contacts/${userId}/phone-numbers/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmContactDetailsStudentsAddressesList
     * @request GET:/crm/contact-details/students/{userId}/addresses/
     * @secure
     */,
    crmContactDetailsStudentsAddressesList: <
      T extends FetchKeys<StudentAddress[]>
    >(
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentAddress[], T>, any>({
        path: `/crm/contact-details/students/${userId}/addresses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmContactDetailsStudentsAddressesCreate
     * @request POST:/crm/contact-details/students/{userId}/addresses/
     * @secure
     */,
    crmContactDetailsStudentsAddressesCreate: <
      T extends FetchKeys<StudentAddress>
    >(
      userId: string,
      data: StudentAddressRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentAddress, T>, any>({
        path: `/crm/contact-details/students/${userId}/addresses/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmContactDetailsStudentsAddressesRetrieve
     * @request GET:/crm/contact-details/students/{userId}/addresses/{id}/
     * @secure
     */,
    crmContactDetailsStudentsAddressesRetrieve: <
      T extends FetchKeys<StudentAddress>
    >(
      id: number,
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentAddress, T>, any>({
        path: `/crm/contact-details/students/${userId}/addresses/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmContactDetailsStudentsAddressesUpdate
     * @request PUT:/crm/contact-details/students/{userId}/addresses/{id}/
     * @secure
     */,
    crmContactDetailsStudentsAddressesUpdate: <
      T extends FetchKeys<StudentAddress>
    >(
      id: number,
      userId: string,
      data: StudentAddressRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentAddress, T>, any>({
        path: `/crm/contact-details/students/${userId}/addresses/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmContactDetailsStudentsAddressesPartialUpdate
     * @request PATCH:/crm/contact-details/students/{userId}/addresses/{id}/
     * @secure
     */,
    crmContactDetailsStudentsAddressesPartialUpdate: <
      T extends FetchKeys<StudentAddress>
    >(
      id: number,
      userId: string,
      data: PatchedStudentAddressRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentAddress, T>, any>({
        path: `/crm/contact-details/students/${userId}/addresses/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmContactDetailsStudentsAddressesDestroy
     * @request DELETE:/crm/contact-details/students/{userId}/addresses/{id}/
     * @secure
     */,
    crmContactDetailsStudentsAddressesDestroy: <T extends FetchKeys<void>>(
      id: number,
      userId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/crm/contact-details/students/${userId}/addresses/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing student-related data.
     *
     * @tags crm
     * @name CrmContactDetailsStudentsEmailsList
     * @request GET:/crm/contact-details/students/{userId}/emails/
     * @secure
     */,
    crmContactDetailsStudentsEmailsList: <
      T extends FetchKeys<UserAdditionalEmail[]>
    >(
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalEmail[], T>, any>({
        path: `/crm/contact-details/students/${userId}/emails/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing student-related data.
     *
     * @tags crm
     * @name CrmContactDetailsStudentsEmailsCreate
     * @request POST:/crm/contact-details/students/{userId}/emails/
     * @secure
     */,
    crmContactDetailsStudentsEmailsCreate: <
      T extends FetchKeys<UserAdditionalEmail>
    >(
      userId: string,
      data: UserAdditionalEmailRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalEmail, T>, any>({
        path: `/crm/contact-details/students/${userId}/emails/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing student-related data.
     *
     * @tags crm
     * @name CrmContactDetailsStudentsEmailsRetrieve
     * @request GET:/crm/contact-details/students/{userId}/emails/{id}/
     * @secure
     */,
    crmContactDetailsStudentsEmailsRetrieve: <
      T extends FetchKeys<UserAdditionalEmail>
    >(
      id: number,
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalEmail, T>, any>({
        path: `/crm/contact-details/students/${userId}/emails/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing student-related data.
     *
     * @tags crm
     * @name CrmContactDetailsStudentsEmailsUpdate
     * @request PUT:/crm/contact-details/students/{userId}/emails/{id}/
     * @secure
     */,
    crmContactDetailsStudentsEmailsUpdate: <
      T extends FetchKeys<UserAdditionalEmail>
    >(
      id: number,
      userId: string,
      data: UserAdditionalEmailRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalEmail, T>, any>({
        path: `/crm/contact-details/students/${userId}/emails/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing student-related data.
     *
     * @tags crm
     * @name CrmContactDetailsStudentsEmailsPartialUpdate
     * @request PATCH:/crm/contact-details/students/{userId}/emails/{id}/
     * @secure
     */,
    crmContactDetailsStudentsEmailsPartialUpdate: <
      T extends FetchKeys<UserAdditionalEmail>
    >(
      id: number,
      userId: string,
      data: PatchedUserAdditionalEmailRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalEmail, T>, any>({
        path: `/crm/contact-details/students/${userId}/emails/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing student-related data.
     *
     * @tags crm
     * @name CrmContactDetailsStudentsEmailsDestroy
     * @request DELETE:/crm/contact-details/students/{userId}/emails/{id}/
     * @secure
     */,
    crmContactDetailsStudentsEmailsDestroy: <T extends FetchKeys<void>>(
      id: number,
      userId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/crm/contact-details/students/${userId}/emails/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing student-related data.
     *
     * @tags crm
     * @name CrmContactDetailsStudentsPhoneNumbersList
     * @request GET:/crm/contact-details/students/{userId}/phone-numbers/
     * @secure
     */,
    crmContactDetailsStudentsPhoneNumbersList: <
      T extends FetchKeys<UserAdditionalPhoneNumber[]>
    >(
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalPhoneNumber[], T>, any>({
        path: `/crm/contact-details/students/${userId}/phone-numbers/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing student-related data.
     *
     * @tags crm
     * @name CrmContactDetailsStudentsPhoneNumbersCreate
     * @request POST:/crm/contact-details/students/{userId}/phone-numbers/
     * @secure
     */,
    crmContactDetailsStudentsPhoneNumbersCreate: <
      T extends FetchKeys<UserAdditionalPhoneNumber>
    >(
      userId: string,
      data: UserAdditionalPhoneNumberRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalPhoneNumber, T>, any>({
        path: `/crm/contact-details/students/${userId}/phone-numbers/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing student-related data.
     *
     * @tags crm
     * @name CrmContactDetailsStudentsPhoneNumbersRetrieve
     * @request GET:/crm/contact-details/students/{userId}/phone-numbers/{id}/
     * @secure
     */,
    crmContactDetailsStudentsPhoneNumbersRetrieve: <
      T extends FetchKeys<UserAdditionalPhoneNumber>
    >(
      id: number,
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalPhoneNumber, T>, any>({
        path: `/crm/contact-details/students/${userId}/phone-numbers/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing student-related data.
     *
     * @tags crm
     * @name CrmContactDetailsStudentsPhoneNumbersUpdate
     * @request PUT:/crm/contact-details/students/{userId}/phone-numbers/{id}/
     * @secure
     */,
    crmContactDetailsStudentsPhoneNumbersUpdate: <
      T extends FetchKeys<UserAdditionalPhoneNumber>
    >(
      id: number,
      userId: string,
      data: UserAdditionalPhoneNumberRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalPhoneNumber, T>, any>({
        path: `/crm/contact-details/students/${userId}/phone-numbers/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing student-related data.
     *
     * @tags crm
     * @name CrmContactDetailsStudentsPhoneNumbersPartialUpdate
     * @request PATCH:/crm/contact-details/students/{userId}/phone-numbers/{id}/
     * @secure
     */,
    crmContactDetailsStudentsPhoneNumbersPartialUpdate: <
      T extends FetchKeys<UserAdditionalPhoneNumber>
    >(
      id: number,
      userId: string,
      data: PatchedUserAdditionalPhoneNumberRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserAdditionalPhoneNumber, T>, any>({
        path: `/crm/contact-details/students/${userId}/phone-numbers/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin to set permission rules for changing student-related data.
     *
     * @tags crm
     * @name CrmContactDetailsStudentsPhoneNumbersDestroy
     * @request DELETE:/crm/contact-details/students/{userId}/phone-numbers/{id}/
     * @secure
     */,
    crmContactDetailsStudentsPhoneNumbersDestroy: <T extends FetchKeys<void>>(
      id: number,
      userId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/crm/contact-details/students/${userId}/phone-numbers/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags crm
     * @name CrmStatusSummaryRetrieve
     * @request GET:/crm/status-summary/
     * @secure
     */,
    crmStatusSummaryRetrieve: <T extends FetchKeys<CRMStatusSummary>>(
      query?: {
        date_of_entry_to_rosenberg_year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<CRMStatusSummary, T>,
        {
          message?: string
        }
      >({
        path: `/crm/status-summary/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentDocumentsList
     * @request GET:/crm/student-documents/{studentId}/
     * @secure
     */,
    crmStudentDocumentsList: <
      T extends FetchKeys<PaginatedStudentDocumentList>
    >(
      studentId: string,
      query?: {
        added_by?: string
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        /**
         * * `1` - Draft
         * * `5` - Academic
         * * `7` - Admission
         * * `10` - Administrative
         * * `13` - Boarding
         * * `15` - Medical
         * * `20` - Other
         * * `25` - Archived
         */
        document_type?: 1 | 10 | 13 | 15 | 20 | 25 | 5 | 7
        /** @format date */
        modified_after?: string
        /** @format date */
        modified_before?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        school_year?: string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentDocumentList, T>, any>({
        path: `/crm/student-documents/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentDocumentsCreate
     * @request POST:/crm/student-documents/{studentId}/
     * @secure
     */,
    crmStudentDocumentsCreate: <T extends FetchKeys<StudentDocument>>(
      studentId: string,
      data: StudentDocumentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentDocument, T>, any>({
        path: `/crm/student-documents/${studentId}/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentDocumentsRetrieve
     * @request GET:/crm/student-documents/{studentId}/{id}/
     * @secure
     */,
    crmStudentDocumentsRetrieve: <T extends FetchKeys<StudentDocument>>(
      id: number,
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentDocument, T>, any>({
        path: `/crm/student-documents/${studentId}/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentDocumentsUpdate
     * @request PUT:/crm/student-documents/{studentId}/{id}/
     * @secure
     */,
    crmStudentDocumentsUpdate: <T extends FetchKeys<StudentDocument>>(
      id: number,
      studentId: string,
      data: StudentDocumentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentDocument, T>, any>({
        path: `/crm/student-documents/${studentId}/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentDocumentsPartialUpdate
     * @request PATCH:/crm/student-documents/{studentId}/{id}/
     * @secure
     */,
    crmStudentDocumentsPartialUpdate: <T extends FetchKeys<StudentDocument>>(
      id: number,
      studentId: string,
      data: PatchedStudentDocumentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentDocument, T>, any>({
        path: `/crm/student-documents/${studentId}/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentDocumentsDestroy
     * @request DELETE:/crm/student-documents/{studentId}/{id}/
     * @secure
     */,
    crmStudentDocumentsDestroy: <T extends FetchKeys<void>>(
      id: number,
      studentId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/crm/student-documents/${studentId}/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags crm
     * @name CrmStudentDocumentsDownloadRetrieve
     * @request GET:/crm/student-documents/{studentId}/{id}/download/
     * @secure
     */,
    crmStudentDocumentsDownloadRetrieve: <T extends FetchKeys<File>>(
      id: string,
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<File, T>, any>({
        path: `/crm/student-documents/${studentId}/${id}/download/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags crm
     * @name CrmStudentDocumentsPreviewRetrieve
     * @request GET:/crm/student-documents/{studentId}/{id}/preview/
     * @secure
     */,
    crmStudentDocumentsPreviewRetrieve: <T extends FetchKeys<File>>(
      id: string,
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<File, T>, any>({
        path: `/crm/student-documents/${studentId}/${id}/preview/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentDocumentsAvailableFiltersRetrieve
     * @request GET:/crm/student-documents/{studentId}/available-filters/
     * @secure
     */,
    crmStudentDocumentsAvailableFiltersRetrieve: <
      T extends FetchKeys<StudentDocumentAvailableFilters>
    >(
      studentId: string,
      query?: {
        added_by?: string
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        /**
         * * `1` - Draft
         * * `5` - Academic
         * * `7` - Admission
         * * `10` - Administrative
         * * `13` - Boarding
         * * `15` - Medical
         * * `20` - Other
         * * `25` - Archived
         */
        document_type?: 1 | 10 | 13 | 15 | 20 | 25 | 5 | 7
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        /** @format date */
        modified_after?: string
        /** @format date */
        modified_before?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        school_year?: string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentDocumentAvailableFilters, T>, any>({
        path: `/crm/student-documents/${studentId}/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags crm
     * @name CrmStudentDocumentsDocumentTypeAccessRetrieve
     * @request GET:/crm/student-documents/document-type-access/
     * @secure
     */,
    crmStudentDocumentsDocumentTypeAccessRetrieve: <
      T extends FetchKeys<StudentDocumentTypeAccessMetadata>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentDocumentTypeAccessMetadata, T>, any>({
        path: `/crm/student-documents/document-type-access/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags crm
     * @name CrmStudentLegaciesRetrieve
     * @request GET:/crm/student-legacies/{studentId}/
     * @secure
     */,
    crmStudentLegaciesRetrieve: <
      T extends FetchKeys<StudentLegaciesWithSummerCamps>
    >(
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentLegaciesWithSummerCamps, T>, any>({
        path: `/crm/student-legacies/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags crm
     * @name CrmStudentLegaciesUpdate
     * @request PUT:/crm/student-legacies/{studentId}/
     * @secure
     */,
    crmStudentLegaciesUpdate: <
      T extends FetchKeys<StudentLegaciesWithSummerCamps>
    >(
      studentId: string,
      data: StudentLegaciesWithSummerCampsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentLegaciesWithSummerCamps, T>, any>({
        path: `/crm/student-legacies/${studentId}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentMedicalInfoRetrieve
     * @request GET:/crm/student-medical-info/{studentId}/
     * @secure
     */,
    crmStudentMedicalInfoRetrieve: <T extends FetchKeys<StudentMedicalInfo>>(
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentMedicalInfo, T>, any>({
        path: `/crm/student-medical-info/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentMedicalInfoUpdate
     * @request PUT:/crm/student-medical-info/{studentId}/
     * @secure
     */,
    crmStudentMedicalInfoUpdate: <T extends FetchKeys<StudentMedicalInfo>>(
      studentId: string,
      data: StudentMedicalInfoRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentMedicalInfo, T>, any>({
        path: `/crm/student-medical-info/${studentId}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentMedicalInfoPartialUpdate
     * @request PATCH:/crm/student-medical-info/{studentId}/
     * @secure
     */,
    crmStudentMedicalInfoPartialUpdate: <
      T extends FetchKeys<StudentMedicalInfo>
    >(
      studentId: string,
      data: PatchedStudentMedicalInfoRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentMedicalInfo, T>, any>({
        path: `/crm/student-medical-info/${studentId}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentSchoolsList
     * @request GET:/crm/student-schools/{studentId}/
     * @secure
     */,
    crmStudentSchoolsList: <T extends FetchKeys<StudentSchoolHistory[]>>(
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentSchoolHistory[], T>, any>({
        path: `/crm/student-schools/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentSchoolsCreate
     * @request POST:/crm/student-schools/{studentId}/
     * @secure
     */,
    crmStudentSchoolsCreate: <T extends FetchKeys<StudentSchoolHistory>>(
      studentId: string,
      data: StudentSchoolHistoryRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentSchoolHistory, T>, any>({
        path: `/crm/student-schools/${studentId}/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentSchoolsRetrieve
     * @request GET:/crm/student-schools/{studentId}/{id}/
     * @secure
     */,
    crmStudentSchoolsRetrieve: <T extends FetchKeys<StudentSchoolHistory>>(
      id: number,
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentSchoolHistory, T>, any>({
        path: `/crm/student-schools/${studentId}/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentSchoolsUpdate
     * @request PUT:/crm/student-schools/{studentId}/{id}/
     * @secure
     */,
    crmStudentSchoolsUpdate: <T extends FetchKeys<StudentSchoolHistory>>(
      id: number,
      studentId: string,
      data: StudentSchoolHistoryRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentSchoolHistory, T>, any>({
        path: `/crm/student-schools/${studentId}/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentSchoolsPartialUpdate
     * @request PATCH:/crm/student-schools/{studentId}/{id}/
     * @secure
     */,
    crmStudentSchoolsPartialUpdate: <T extends FetchKeys<StudentSchoolHistory>>(
      id: number,
      studentId: string,
      data: PatchedStudentSchoolHistoryRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentSchoolHistory, T>, any>({
        path: `/crm/student-schools/${studentId}/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentSchoolsDestroy
     * @request DELETE:/crm/student-schools/{studentId}/{id}/
     * @secure
     */,
    crmStudentSchoolsDestroy: <T extends FetchKeys<void>>(
      id: number,
      studentId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/crm/student-schools/${studentId}/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentStudiedLanguagesList
     * @request GET:/crm/student-studied-languages/{studentId}/
     * @secure
     */,
    crmStudentStudiedLanguagesList: <
      T extends FetchKeys<StudentStudiedLanguage[]>
    >(
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentStudiedLanguage[], T>, any>({
        path: `/crm/student-studied-languages/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentStudiedLanguagesCreate
     * @request POST:/crm/student-studied-languages/{studentId}/
     * @secure
     */,
    crmStudentStudiedLanguagesCreate: <
      T extends FetchKeys<StudentStudiedLanguage>
    >(
      studentId: string,
      data: StudentStudiedLanguageRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentStudiedLanguage, T>, any>({
        path: `/crm/student-studied-languages/${studentId}/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentStudiedLanguagesRetrieve
     * @request GET:/crm/student-studied-languages/{studentId}/{id}/
     * @secure
     */,
    crmStudentStudiedLanguagesRetrieve: <
      T extends FetchKeys<StudentStudiedLanguage>
    >(
      id: number,
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentStudiedLanguage, T>, any>({
        path: `/crm/student-studied-languages/${studentId}/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentStudiedLanguagesUpdate
     * @request PUT:/crm/student-studied-languages/{studentId}/{id}/
     * @secure
     */,
    crmStudentStudiedLanguagesUpdate: <
      T extends FetchKeys<StudentStudiedLanguage>
    >(
      id: number,
      studentId: string,
      data: StudentStudiedLanguageRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentStudiedLanguage, T>, any>({
        path: `/crm/student-studied-languages/${studentId}/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentStudiedLanguagesPartialUpdate
     * @request PATCH:/crm/student-studied-languages/{studentId}/{id}/
     * @secure
     */,
    crmStudentStudiedLanguagesPartialUpdate: <
      T extends FetchKeys<StudentStudiedLanguage>
    >(
      id: number,
      studentId: string,
      data: PatchedStudentStudiedLanguageRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentStudiedLanguage, T>, any>({
        path: `/crm/student-studied-languages/${studentId}/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmStudentStudiedLanguagesDestroy
     * @request DELETE:/crm/student-studied-languages/{studentId}/{id}/
     * @secure
     */,
    crmStudentStudiedLanguagesDestroy: <T extends FetchKeys<void>>(
      id: number,
      studentId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/crm/student-studied-languages/${studentId}/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags crm
     * @name CrmTrelloWebhookBoardCreate
     * @request POST:/crm/trello/webhook/board/
     * @secure
     */,
    crmTrelloWebhookBoardCreate: <T extends FetchKeys<void>>(
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/crm/trello/webhook/board/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags crm
     * @name CrmUserContactsList
     * @request GET:/crm/user-contacts/
     * @secure
     */,
    crmUserContactsList: <T extends FetchKeys<PaginatedUserContactsList>>(
      query?: {
        /**
         * * `rosenberg_parent` - Rosenberg Parent
         * * `my_rosenberg_idp` - My Rosenberg IDP
         */
        app_access?: 'my_rosenberg_idp' | 'rosenberg_parent'
        /** @format uuid */
        candidate?: string
        career_goal?: string[]
        /** @format uuid */
        current_company?: string
        current_grade_level?: number
        /**
         * * `no` - No
         * * `left` - Left
         * * `alumni` - Alumni
         */
        former_student_status?: 'alumni' | 'left' | 'no'
        /**
         * * `male` - Male
         * * `female` - Female
         */
        gender?: 'female' | 'male'
        has_active_students?: boolean
        /** @format uuid */
        id?: string
        is_new_student?: boolean
        is_on_senior_house?: boolean
        /**
         * * `AF` - Afghanistan
         * * `AX` - Åland Islands
         * * `AL` - Albania
         * * `DZ` - Algeria
         * * `AS` - American Samoa
         * * `AD` - Andorra
         * * `AO` - Angola
         * * `AI` - Anguilla
         * * `AQ` - Antarctica
         * * `AG` - Antigua and Barbuda
         * * `AR` - Argentina
         * * `AM` - Armenia
         * * `AW` - Aruba
         * * `AU` - Australia
         * * `AT` - Austria
         * * `AZ` - Azerbaijan
         * * `BS` - Bahamas
         * * `BH` - Bahrain
         * * `BD` - Bangladesh
         * * `BB` - Barbados
         * * `BY` - Belarus
         * * `BE` - Belgium
         * * `BZ` - Belize
         * * `BJ` - Benin
         * * `BM` - Bermuda
         * * `BT` - Bhutan
         * * `BO` - Bolivia
         * * `BQ` - Bonaire, Sint Eustatius and Saba
         * * `BA` - Bosnia and Herzegovina
         * * `BW` - Botswana
         * * `BV` - Bouvet Island
         * * `BR` - Brazil
         * * `IO` - British Indian Ocean Territory
         * * `BN` - Brunei
         * * `BG` - Bulgaria
         * * `BF` - Burkina Faso
         * * `BI` - Burundi
         * * `CV` - Cabo Verde
         * * `KH` - Cambodia
         * * `CM` - Cameroon
         * * `CA` - Canada
         * * `KY` - Cayman Islands
         * * `CF` - Central African Republic
         * * `TD` - Chad
         * * `CL` - Chile
         * * `CN` - China
         * * `CX` - Christmas Island
         * * `CC` - Cocos (Keeling) Islands
         * * `CO` - Colombia
         * * `KM` - Comoros
         * * `CG` - Congo
         * * `CD` - Congo (the Democratic Republic of the)
         * * `CK` - Cook Islands
         * * `CR` - Costa Rica
         * * `CI` - Côte d'Ivoire
         * * `HR` - Croatia
         * * `CU` - Cuba
         * * `CW` - Curaçao
         * * `CY` - Cyprus
         * * `CZ` - Czechia
         * * `DK` - Denmark
         * * `DJ` - Djibouti
         * * `DM` - Dominica
         * * `DO` - Dominican Republic
         * * `EC` - Ecuador
         * * `EG` - Egypt
         * * `SV` - El Salvador
         * * `GQ` - Equatorial Guinea
         * * `ER` - Eritrea
         * * `EE` - Estonia
         * * `SZ` - Eswatini
         * * `ET` - Ethiopia
         * * `FK` - Falkland Islands (Malvinas)
         * * `FO` - Faroe Islands
         * * `FJ` - Fiji
         * * `FI` - Finland
         * * `FR` - France
         * * `GF` - French Guiana
         * * `PF` - French Polynesia
         * * `TF` - French Southern Territories
         * * `GA` - Gabon
         * * `GM` - Gambia
         * * `GE` - Georgia
         * * `DE` - Germany
         * * `GH` - Ghana
         * * `GI` - Gibraltar
         * * `GR` - Greece
         * * `GL` - Greenland
         * * `GD` - Grenada
         * * `GP` - Guadeloupe
         * * `GU` - Guam
         * * `GT` - Guatemala
         * * `GG` - Guernsey
         * * `GN` - Guinea
         * * `GW` - Guinea-Bissau
         * * `GY` - Guyana
         * * `HT` - Haiti
         * * `HM` - Heard Island and McDonald Islands
         * * `VA` - Holy See
         * * `HN` - Honduras
         * * `HK` - Hong Kong
         * * `HU` - Hungary
         * * `IS` - Iceland
         * * `IN` - India
         * * `ID` - Indonesia
         * * `IR` - Iran
         * * `IQ` - Iraq
         * * `IE` - Ireland
         * * `IM` - Isle of Man
         * * `IL` - Israel
         * * `IT` - Italy
         * * `JM` - Jamaica
         * * `JP` - Japan
         * * `JE` - Jersey
         * * `JO` - Jordan
         * * `KZ` - Kazakhstan
         * * `KE` - Kenya
         * * `KI` - Kiribati
         * * `XK` - Kosovo
         * * `KW` - Kuwait
         * * `KG` - Kyrgyzstan
         * * `LA` - Laos
         * * `LV` - Latvia
         * * `LB` - Lebanon
         * * `LS` - Lesotho
         * * `LR` - Liberia
         * * `LY` - Libya
         * * `LI` - Liechtenstein
         * * `LT` - Lithuania
         * * `LU` - Luxembourg
         * * `MO` - Macao
         * * `MG` - Madagascar
         * * `MW` - Malawi
         * * `MY` - Malaysia
         * * `MV` - Maldives
         * * `ML` - Mali
         * * `MT` - Malta
         * * `MH` - Marshall Islands
         * * `MQ` - Martinique
         * * `MR` - Mauritania
         * * `MU` - Mauritius
         * * `YT` - Mayotte
         * * `MX` - Mexico
         * * `FM` - Micronesia
         * * `MD` - Moldova
         * * `MC` - Monaco
         * * `MN` - Mongolia
         * * `ME` - Montenegro
         * * `MS` - Montserrat
         * * `MA` - Morocco
         * * `MZ` - Mozambique
         * * `MM` - Myanmar
         * * `NA` - Namibia
         * * `NR` - Nauru
         * * `NP` - Nepal
         * * `NL` - Netherlands
         * * `NC` - New Caledonia
         * * `NZ` - New Zealand
         * * `NI` - Nicaragua
         * * `NE` - Niger
         * * `NG` - Nigeria
         * * `NU` - Niue
         * * `NF` - Norfolk Island
         * * `KP` - North Korea
         * * `MK` - North Macedonia
         * * `MP` - Northern Mariana Islands
         * * `NO` - Norway
         * * `OM` - Oman
         * * `PK` - Pakistan
         * * `PW` - Palau
         * * `PS` - Palestine, State of
         * * `PA` - Panama
         * * `PG` - Papua New Guinea
         * * `PY` - Paraguay
         * * `PE` - Peru
         * * `PH` - Philippines
         * * `PN` - Pitcairn
         * * `PL` - Poland
         * * `PT` - Portugal
         * * `PR` - Puerto Rico
         * * `QA` - Qatar
         * * `RE` - Réunion
         * * `RO` - Romania
         * * `RU` - Russia
         * * `RW` - Rwanda
         * * `BL` - Saint Barthélemy
         * * `SH` - Saint Helena, Ascension and Tristan da Cunha
         * * `KN` - Saint Kitts and Nevis
         * * `LC` - Saint Lucia
         * * `MF` - Saint Martin (French part)
         * * `PM` - Saint Pierre and Miquelon
         * * `VC` - Saint Vincent and the Grenadines
         * * `WS` - Samoa
         * * `SM` - San Marino
         * * `ST` - Sao Tome and Principe
         * * `SA` - Saudi Arabia
         * * `SN` - Senegal
         * * `RS` - Serbia
         * * `SC` - Seychelles
         * * `SL` - Sierra Leone
         * * `SG` - Singapore
         * * `SX` - Sint Maarten (Dutch part)
         * * `SK` - Slovakia
         * * `SI` - Slovenia
         * * `SB` - Solomon Islands
         * * `SO` - Somalia
         * * `ZA` - South Africa
         * * `GS` - South Georgia and the South Sandwich Islands
         * * `KR` - South Korea
         * * `SS` - South Sudan
         * * `ES` - Spain
         * * `LK` - Sri Lanka
         * * `SD` - Sudan
         * * `SR` - Suriname
         * * `SJ` - Svalbard and Jan Mayen
         * * `SE` - Sweden
         * * `CH` - Switzerland
         * * `SY` - Syria
         * * `TW` - Taiwan
         * * `TJ` - Tajikistan
         * * `TZ` - Tanzania
         * * `TH` - Thailand
         * * `TL` - Timor-Leste
         * * `TG` - Togo
         * * `TK` - Tokelau
         * * `TO` - Tonga
         * * `TT` - Trinidad and Tobago
         * * `TN` - Tunisia
         * * `TR` - Türkiye
         * * `TM` - Turkmenistan
         * * `TC` - Turks and Caicos Islands
         * * `TV` - Tuvalu
         * * `UG` - Uganda
         * * `UA` - Ukraine
         * * `AE` - United Arab Emirates
         * * `GB` - United Kingdom
         * * `UM` - United States Minor Outlying Islands
         * * `US` - United States of America
         * * `UY` - Uruguay
         * * `UZ` - Uzbekistan
         * * `VU` - Vanuatu
         * * `VE` - Venezuela
         * * `VN` - Vietnam
         * * `VG` - Virgin Islands (British)
         * * `VI` - Virgin Islands (U.S.)
         * * `WF` - Wallis and Futuna
         * * `EH` - Western Sahara
         * * `YE` - Yemen
         * * `ZM` - Zambia
         * * `ZW` - Zimbabwe
         */
        nationalities?: (
          | 'AD'
          | 'AE'
          | 'AF'
          | 'AG'
          | 'AI'
          | 'AL'
          | 'AM'
          | 'AO'
          | 'AQ'
          | 'AR'
          | 'AS'
          | 'AT'
          | 'AU'
          | 'AW'
          | 'AX'
          | 'AZ'
          | 'BA'
          | 'BB'
          | 'BD'
          | 'BE'
          | 'BF'
          | 'BG'
          | 'BH'
          | 'BI'
          | 'BJ'
          | 'BL'
          | 'BM'
          | 'BN'
          | 'BO'
          | 'BQ'
          | 'BR'
          | 'BS'
          | 'BT'
          | 'BV'
          | 'BW'
          | 'BY'
          | 'BZ'
          | 'CA'
          | 'CC'
          | 'CD'
          | 'CF'
          | 'CG'
          | 'CH'
          | 'CI'
          | 'CK'
          | 'CL'
          | 'CM'
          | 'CN'
          | 'CO'
          | 'CR'
          | 'CU'
          | 'CV'
          | 'CW'
          | 'CX'
          | 'CY'
          | 'CZ'
          | 'DE'
          | 'DJ'
          | 'DK'
          | 'DM'
          | 'DO'
          | 'DZ'
          | 'EC'
          | 'EE'
          | 'EG'
          | 'EH'
          | 'ER'
          | 'ES'
          | 'ET'
          | 'FI'
          | 'FJ'
          | 'FK'
          | 'FM'
          | 'FO'
          | 'FR'
          | 'GA'
          | 'GB'
          | 'GD'
          | 'GE'
          | 'GF'
          | 'GG'
          | 'GH'
          | 'GI'
          | 'GL'
          | 'GM'
          | 'GN'
          | 'GP'
          | 'GQ'
          | 'GR'
          | 'GS'
          | 'GT'
          | 'GU'
          | 'GW'
          | 'GY'
          | 'HK'
          | 'HM'
          | 'HN'
          | 'HR'
          | 'HT'
          | 'HU'
          | 'ID'
          | 'IE'
          | 'IL'
          | 'IM'
          | 'IN'
          | 'IO'
          | 'IQ'
          | 'IR'
          | 'IS'
          | 'IT'
          | 'JE'
          | 'JM'
          | 'JO'
          | 'JP'
          | 'KE'
          | 'KG'
          | 'KH'
          | 'KI'
          | 'KM'
          | 'KN'
          | 'KP'
          | 'KR'
          | 'KW'
          | 'KY'
          | 'KZ'
          | 'LA'
          | 'LB'
          | 'LC'
          | 'LI'
          | 'LK'
          | 'LR'
          | 'LS'
          | 'LT'
          | 'LU'
          | 'LV'
          | 'LY'
          | 'MA'
          | 'MC'
          | 'MD'
          | 'ME'
          | 'MF'
          | 'MG'
          | 'MH'
          | 'MK'
          | 'ML'
          | 'MM'
          | 'MN'
          | 'MO'
          | 'MP'
          | 'MQ'
          | 'MR'
          | 'MS'
          | 'MT'
          | 'MU'
          | 'MV'
          | 'MW'
          | 'MX'
          | 'MY'
          | 'MZ'
          | 'NA'
          | 'NC'
          | 'NE'
          | 'NF'
          | 'NG'
          | 'NI'
          | 'NL'
          | 'NO'
          | 'NP'
          | 'NR'
          | 'NU'
          | 'NZ'
          | 'OM'
          | 'PA'
          | 'PE'
          | 'PF'
          | 'PG'
          | 'PH'
          | 'PK'
          | 'PL'
          | 'PM'
          | 'PN'
          | 'PR'
          | 'PS'
          | 'PT'
          | 'PW'
          | 'PY'
          | 'QA'
          | 'RE'
          | 'RO'
          | 'RS'
          | 'RU'
          | 'RW'
          | 'SA'
          | 'SB'
          | 'SC'
          | 'SD'
          | 'SE'
          | 'SG'
          | 'SH'
          | 'SI'
          | 'SJ'
          | 'SK'
          | 'SL'
          | 'SM'
          | 'SN'
          | 'SO'
          | 'SR'
          | 'SS'
          | 'ST'
          | 'SV'
          | 'SX'
          | 'SY'
          | 'SZ'
          | 'TC'
          | 'TD'
          | 'TF'
          | 'TG'
          | 'TH'
          | 'TJ'
          | 'TK'
          | 'TL'
          | 'TM'
          | 'TN'
          | 'TO'
          | 'TR'
          | 'TT'
          | 'TV'
          | 'TW'
          | 'TZ'
          | 'UA'
          | 'UG'
          | 'UM'
          | 'US'
          | 'UY'
          | 'UZ'
          | 'VA'
          | 'VC'
          | 'VE'
          | 'VG'
          | 'VI'
          | 'VN'
          | 'VU'
          | 'WF'
          | 'WS'
          | 'XK'
          | 'YE'
          | 'YT'
          | 'ZA'
          | 'ZM'
          | 'ZW'
        )[]
        /**
         * Ordering
         *
         * * `first_name` - First name
         * * `-first_name` - First name (descending)
         * * `last_name` - Last name
         * * `-last_name` - Last name (descending)
         * * `user_type` - User type
         * * `-user_type` - User type (descending)
         * * `app_access` - App access
         * * `-app_access` - App access (descending)
         * * `saldo_status` - Saldo status
         * * `-saldo_status` - Saldo status (descending)
         * * `date_joined` - Date joined
         * * `-date_joined` - Date joined (descending)
         * * `last_update` - Last update
         * * `-last_update` - Last update (descending)
         * * `status` - Status
         * * `-status` - Status (descending)
         */
        ordering?: (
          | '-app_access'
          | '-date_joined'
          | '-first_name'
          | '-last_name'
          | '-last_update'
          | '-saldo_status'
          | '-status'
          | '-user_type'
          | 'app_access'
          | 'date_joined'
          | 'first_name'
          | 'last_name'
          | 'last_update'
          | 'saldo_status'
          | 'status'
          | 'user_type'
        )[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /**
         * * `dayschool` - Day School
         * * `boarding` - Boarding
         * * `external` - External
         */
        program_type?: 'boarding' | 'dayschool' | 'external'
        /**
         * * `parent` - Parent
         * * `legal_guardian` - Legal Guardian
         * * `in_legal_guardian_absence` - In Legal Guardian Absence
         * * `arrival_contact` - Arrival Contact
         * * `educational_consultant` - Educational Consultant
         * * `main_invoice_recipient` - Main Invoice Recipient
         * * `invoice_recipient` - Invoice Recipient
         * * `contact_leave` - Contact Leave
         * * `invoice_copy_recipient` - Invoice Copy Recipient
         * * `assistant` - Assistant
         * * `relative` - Relative
         * * `other` - Other
         */
        roles?: (
          | 'arrival_contact'
          | 'assistant'
          | 'contact_leave'
          | 'educational_consultant'
          | 'in_legal_guardian_absence'
          | 'invoice_copy_recipient'
          | 'invoice_recipient'
          | 'legal_guardian'
          | 'main_invoice_recipient'
          | 'other'
          | 'parent'
          | 'relative'
        )[]
        /**
         * * `5` - Settled
         * * `10` - First reminder
         * * `15` - Second reminder
         * * `17` - Unsettled
         * * `20` - App locked
         */
        saldo_status?: (10 | 15 | 17 | 20 | 5)[]
        /** A search term. */
        search?: string
        status?: string[]
        /** @format uuid */
        student?: string
        target_grade_level?: number
        /**
         * * `1` - Contact
         * * `2` - Candidate
         * * `3` - Student
         */
        user_type?: 1 | 2 | 3
        year_of_rosenberg_entry?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedUserContactsList, T>, any>({
        path: `/crm/user-contacts/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags crm
     * @name CrmUserContactsList2
     * @request GET:/crm/user-contacts/{userId}/
     * @secure
     */,
    crmUserContactsList2: <T extends FetchKeys<UserRelationList[]>>(
      userId: string,
      query?: {
        /**
         * 'parent' filters by user_id (returns parent→student relations with real roles). Default (omitted) filters by related_user_id (returns others→user relations).
         *
         * * `parent` - parent
         * @minLength 1
         */
        perspective?: 'parent'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserRelationList[], T>, any>({
        path: `/crm/user-contacts/${userId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags crm
     * @name CrmUserContactsCreate
     * @request POST:/crm/user-contacts/{userId}/
     * @secure
     */,
    crmUserContactsCreate: <T extends FetchKeys<void>>(
      userId: string,
      data: UserRelationCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/crm/user-contacts/${userId}/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags crm
     * @name CrmUserContactsUpdate
     * @request PUT:/crm/user-contacts/{userId}/{id}/
     * @secure
     */,
    crmUserContactsUpdate: <T extends FetchKeys<UserRelationUpdate>>(
      id: number,
      userId: string,
      data: UserRelationUpdateRequest,
      query?: {
        /**
         * 'parent' filters by user_id (returns parent→student relations with real roles). Default (omitted) filters by related_user_id (returns others→user relations).
         *
         * * `parent` - parent
         * @minLength 1
         */
        perspective?: 'parent'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserRelationUpdate, T>, any>({
        path: `/crm/user-contacts/${userId}/${id}/`,
        method: 'PUT',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags crm
     * @name CrmUserContactsPartialUpdate
     * @request PATCH:/crm/user-contacts/{userId}/{id}/
     * @secure
     */,
    crmUserContactsPartialUpdate: <T extends FetchKeys<UserRelationUpdate>>(
      id: number,
      userId: string,
      data: PatchedUserRelationUpdateRequest,
      query?: {
        /**
         * 'parent' filters by user_id (returns parent→student relations with real roles). Default (omitted) filters by related_user_id (returns others→user relations).
         *
         * * `parent` - parent
         * @minLength 1
         */
        perspective?: 'parent'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserRelationUpdate, T>, any>({
        path: `/crm/user-contacts/${userId}/${id}/`,
        method: 'PATCH',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags crm
     * @name CrmUserContactsDestroy
     * @request DELETE:/crm/user-contacts/{userId}/{id}/
     * @secure
     */,
    crmUserContactsDestroy: <T extends FetchKeys<void>>(
      id: number,
      userId: string,
      query?: {
        /**
         * 'parent' filters by user_id (returns parent→student relations with real roles). Default (omitted) filters by related_user_id (returns others→user relations).
         *
         * * `parent` - parent
         * @minLength 1
         */
        perspective?: 'parent'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/crm/user-contacts/${userId}/${id}/`,
        method: 'DELETE',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags crm
     * @name CrmUserContactsSuggestionsList
     * @request GET:/crm/user-contacts/{userId}/suggestions/
     * @secure
     */,
    crmUserContactsSuggestionsList: <
      T extends FetchKeys<RelationSuggestionList[]>
    >(
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RelationSuggestionList[], T>, any>({
        path: `/crm/user-contacts/${userId}/suggestions/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags crm
     * @name CrmUserContactsSuggestionsToggleDismissCreate
     * @request POST:/crm/user-contacts/{userId}/suggestions/{id}/toggle-dismiss/
     * @secure
     */,
    crmUserContactsSuggestionsToggleDismissCreate: <
      T extends FetchKeys<RelationSuggestionToggleDismiss>
    >(
      id: number,
      userId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RelationSuggestionToggleDismiss, T>, any>({
        path: `/crm/user-contacts/${userId}/suggestions/${id}/toggle-dismiss/`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags crm
     * @name CrmUserContactsAvailableFiltersRetrieve
     * @request GET:/crm/user-contacts/available-filters/
     * @secure
     */,
    crmUserContactsAvailableFiltersRetrieve: <
      T extends FetchKeys<UserContactsAvailableFilters>
    >(
      query?: {
        /**
         * * `rosenberg_parent` - Rosenberg Parent
         * * `my_rosenberg_idp` - My Rosenberg IDP
         */
        app_access?: 'my_rosenberg_idp' | 'rosenberg_parent'
        /** @format uuid */
        candidate?: string
        career_goal?: string[]
        /** @format uuid */
        current_company?: string
        current_grade_level?: number
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        /**
         * * `no` - No
         * * `left` - Left
         * * `alumni` - Alumni
         */
        former_student_status?: 'alumni' | 'left' | 'no'
        /**
         * * `male` - Male
         * * `female` - Female
         */
        gender?: 'female' | 'male'
        has_active_students?: boolean
        /** @format uuid */
        id?: string
        is_new_student?: boolean
        is_on_senior_house?: boolean
        /**
         * * `AF` - Afghanistan
         * * `AX` - Åland Islands
         * * `AL` - Albania
         * * `DZ` - Algeria
         * * `AS` - American Samoa
         * * `AD` - Andorra
         * * `AO` - Angola
         * * `AI` - Anguilla
         * * `AQ` - Antarctica
         * * `AG` - Antigua and Barbuda
         * * `AR` - Argentina
         * * `AM` - Armenia
         * * `AW` - Aruba
         * * `AU` - Australia
         * * `AT` - Austria
         * * `AZ` - Azerbaijan
         * * `BS` - Bahamas
         * * `BH` - Bahrain
         * * `BD` - Bangladesh
         * * `BB` - Barbados
         * * `BY` - Belarus
         * * `BE` - Belgium
         * * `BZ` - Belize
         * * `BJ` - Benin
         * * `BM` - Bermuda
         * * `BT` - Bhutan
         * * `BO` - Bolivia
         * * `BQ` - Bonaire, Sint Eustatius and Saba
         * * `BA` - Bosnia and Herzegovina
         * * `BW` - Botswana
         * * `BV` - Bouvet Island
         * * `BR` - Brazil
         * * `IO` - British Indian Ocean Territory
         * * `BN` - Brunei
         * * `BG` - Bulgaria
         * * `BF` - Burkina Faso
         * * `BI` - Burundi
         * * `CV` - Cabo Verde
         * * `KH` - Cambodia
         * * `CM` - Cameroon
         * * `CA` - Canada
         * * `KY` - Cayman Islands
         * * `CF` - Central African Republic
         * * `TD` - Chad
         * * `CL` - Chile
         * * `CN` - China
         * * `CX` - Christmas Island
         * * `CC` - Cocos (Keeling) Islands
         * * `CO` - Colombia
         * * `KM` - Comoros
         * * `CG` - Congo
         * * `CD` - Congo (the Democratic Republic of the)
         * * `CK` - Cook Islands
         * * `CR` - Costa Rica
         * * `CI` - Côte d'Ivoire
         * * `HR` - Croatia
         * * `CU` - Cuba
         * * `CW` - Curaçao
         * * `CY` - Cyprus
         * * `CZ` - Czechia
         * * `DK` - Denmark
         * * `DJ` - Djibouti
         * * `DM` - Dominica
         * * `DO` - Dominican Republic
         * * `EC` - Ecuador
         * * `EG` - Egypt
         * * `SV` - El Salvador
         * * `GQ` - Equatorial Guinea
         * * `ER` - Eritrea
         * * `EE` - Estonia
         * * `SZ` - Eswatini
         * * `ET` - Ethiopia
         * * `FK` - Falkland Islands (Malvinas)
         * * `FO` - Faroe Islands
         * * `FJ` - Fiji
         * * `FI` - Finland
         * * `FR` - France
         * * `GF` - French Guiana
         * * `PF` - French Polynesia
         * * `TF` - French Southern Territories
         * * `GA` - Gabon
         * * `GM` - Gambia
         * * `GE` - Georgia
         * * `DE` - Germany
         * * `GH` - Ghana
         * * `GI` - Gibraltar
         * * `GR` - Greece
         * * `GL` - Greenland
         * * `GD` - Grenada
         * * `GP` - Guadeloupe
         * * `GU` - Guam
         * * `GT` - Guatemala
         * * `GG` - Guernsey
         * * `GN` - Guinea
         * * `GW` - Guinea-Bissau
         * * `GY` - Guyana
         * * `HT` - Haiti
         * * `HM` - Heard Island and McDonald Islands
         * * `VA` - Holy See
         * * `HN` - Honduras
         * * `HK` - Hong Kong
         * * `HU` - Hungary
         * * `IS` - Iceland
         * * `IN` - India
         * * `ID` - Indonesia
         * * `IR` - Iran
         * * `IQ` - Iraq
         * * `IE` - Ireland
         * * `IM` - Isle of Man
         * * `IL` - Israel
         * * `IT` - Italy
         * * `JM` - Jamaica
         * * `JP` - Japan
         * * `JE` - Jersey
         * * `JO` - Jordan
         * * `KZ` - Kazakhstan
         * * `KE` - Kenya
         * * `KI` - Kiribati
         * * `XK` - Kosovo
         * * `KW` - Kuwait
         * * `KG` - Kyrgyzstan
         * * `LA` - Laos
         * * `LV` - Latvia
         * * `LB` - Lebanon
         * * `LS` - Lesotho
         * * `LR` - Liberia
         * * `LY` - Libya
         * * `LI` - Liechtenstein
         * * `LT` - Lithuania
         * * `LU` - Luxembourg
         * * `MO` - Macao
         * * `MG` - Madagascar
         * * `MW` - Malawi
         * * `MY` - Malaysia
         * * `MV` - Maldives
         * * `ML` - Mali
         * * `MT` - Malta
         * * `MH` - Marshall Islands
         * * `MQ` - Martinique
         * * `MR` - Mauritania
         * * `MU` - Mauritius
         * * `YT` - Mayotte
         * * `MX` - Mexico
         * * `FM` - Micronesia
         * * `MD` - Moldova
         * * `MC` - Monaco
         * * `MN` - Mongolia
         * * `ME` - Montenegro
         * * `MS` - Montserrat
         * * `MA` - Morocco
         * * `MZ` - Mozambique
         * * `MM` - Myanmar
         * * `NA` - Namibia
         * * `NR` - Nauru
         * * `NP` - Nepal
         * * `NL` - Netherlands
         * * `NC` - New Caledonia
         * * `NZ` - New Zealand
         * * `NI` - Nicaragua
         * * `NE` - Niger
         * * `NG` - Nigeria
         * * `NU` - Niue
         * * `NF` - Norfolk Island
         * * `KP` - North Korea
         * * `MK` - North Macedonia
         * * `MP` - Northern Mariana Islands
         * * `NO` - Norway
         * * `OM` - Oman
         * * `PK` - Pakistan
         * * `PW` - Palau
         * * `PS` - Palestine, State of
         * * `PA` - Panama
         * * `PG` - Papua New Guinea
         * * `PY` - Paraguay
         * * `PE` - Peru
         * * `PH` - Philippines
         * * `PN` - Pitcairn
         * * `PL` - Poland
         * * `PT` - Portugal
         * * `PR` - Puerto Rico
         * * `QA` - Qatar
         * * `RE` - Réunion
         * * `RO` - Romania
         * * `RU` - Russia
         * * `RW` - Rwanda
         * * `BL` - Saint Barthélemy
         * * `SH` - Saint Helena, Ascension and Tristan da Cunha
         * * `KN` - Saint Kitts and Nevis
         * * `LC` - Saint Lucia
         * * `MF` - Saint Martin (French part)
         * * `PM` - Saint Pierre and Miquelon
         * * `VC` - Saint Vincent and the Grenadines
         * * `WS` - Samoa
         * * `SM` - San Marino
         * * `ST` - Sao Tome and Principe
         * * `SA` - Saudi Arabia
         * * `SN` - Senegal
         * * `RS` - Serbia
         * * `SC` - Seychelles
         * * `SL` - Sierra Leone
         * * `SG` - Singapore
         * * `SX` - Sint Maarten (Dutch part)
         * * `SK` - Slovakia
         * * `SI` - Slovenia
         * * `SB` - Solomon Islands
         * * `SO` - Somalia
         * * `ZA` - South Africa
         * * `GS` - South Georgia and the South Sandwich Islands
         * * `KR` - South Korea
         * * `SS` - South Sudan
         * * `ES` - Spain
         * * `LK` - Sri Lanka
         * * `SD` - Sudan
         * * `SR` - Suriname
         * * `SJ` - Svalbard and Jan Mayen
         * * `SE` - Sweden
         * * `CH` - Switzerland
         * * `SY` - Syria
         * * `TW` - Taiwan
         * * `TJ` - Tajikistan
         * * `TZ` - Tanzania
         * * `TH` - Thailand
         * * `TL` - Timor-Leste
         * * `TG` - Togo
         * * `TK` - Tokelau
         * * `TO` - Tonga
         * * `TT` - Trinidad and Tobago
         * * `TN` - Tunisia
         * * `TR` - Türkiye
         * * `TM` - Turkmenistan
         * * `TC` - Turks and Caicos Islands
         * * `TV` - Tuvalu
         * * `UG` - Uganda
         * * `UA` - Ukraine
         * * `AE` - United Arab Emirates
         * * `GB` - United Kingdom
         * * `UM` - United States Minor Outlying Islands
         * * `US` - United States of America
         * * `UY` - Uruguay
         * * `UZ` - Uzbekistan
         * * `VU` - Vanuatu
         * * `VE` - Venezuela
         * * `VN` - Vietnam
         * * `VG` - Virgin Islands (British)
         * * `VI` - Virgin Islands (U.S.)
         * * `WF` - Wallis and Futuna
         * * `EH` - Western Sahara
         * * `YE` - Yemen
         * * `ZM` - Zambia
         * * `ZW` - Zimbabwe
         */
        nationalities?: (
          | 'AD'
          | 'AE'
          | 'AF'
          | 'AG'
          | 'AI'
          | 'AL'
          | 'AM'
          | 'AO'
          | 'AQ'
          | 'AR'
          | 'AS'
          | 'AT'
          | 'AU'
          | 'AW'
          | 'AX'
          | 'AZ'
          | 'BA'
          | 'BB'
          | 'BD'
          | 'BE'
          | 'BF'
          | 'BG'
          | 'BH'
          | 'BI'
          | 'BJ'
          | 'BL'
          | 'BM'
          | 'BN'
          | 'BO'
          | 'BQ'
          | 'BR'
          | 'BS'
          | 'BT'
          | 'BV'
          | 'BW'
          | 'BY'
          | 'BZ'
          | 'CA'
          | 'CC'
          | 'CD'
          | 'CF'
          | 'CG'
          | 'CH'
          | 'CI'
          | 'CK'
          | 'CL'
          | 'CM'
          | 'CN'
          | 'CO'
          | 'CR'
          | 'CU'
          | 'CV'
          | 'CW'
          | 'CX'
          | 'CY'
          | 'CZ'
          | 'DE'
          | 'DJ'
          | 'DK'
          | 'DM'
          | 'DO'
          | 'DZ'
          | 'EC'
          | 'EE'
          | 'EG'
          | 'EH'
          | 'ER'
          | 'ES'
          | 'ET'
          | 'FI'
          | 'FJ'
          | 'FK'
          | 'FM'
          | 'FO'
          | 'FR'
          | 'GA'
          | 'GB'
          | 'GD'
          | 'GE'
          | 'GF'
          | 'GG'
          | 'GH'
          | 'GI'
          | 'GL'
          | 'GM'
          | 'GN'
          | 'GP'
          | 'GQ'
          | 'GR'
          | 'GS'
          | 'GT'
          | 'GU'
          | 'GW'
          | 'GY'
          | 'HK'
          | 'HM'
          | 'HN'
          | 'HR'
          | 'HT'
          | 'HU'
          | 'ID'
          | 'IE'
          | 'IL'
          | 'IM'
          | 'IN'
          | 'IO'
          | 'IQ'
          | 'IR'
          | 'IS'
          | 'IT'
          | 'JE'
          | 'JM'
          | 'JO'
          | 'JP'
          | 'KE'
          | 'KG'
          | 'KH'
          | 'KI'
          | 'KM'
          | 'KN'
          | 'KP'
          | 'KR'
          | 'KW'
          | 'KY'
          | 'KZ'
          | 'LA'
          | 'LB'
          | 'LC'
          | 'LI'
          | 'LK'
          | 'LR'
          | 'LS'
          | 'LT'
          | 'LU'
          | 'LV'
          | 'LY'
          | 'MA'
          | 'MC'
          | 'MD'
          | 'ME'
          | 'MF'
          | 'MG'
          | 'MH'
          | 'MK'
          | 'ML'
          | 'MM'
          | 'MN'
          | 'MO'
          | 'MP'
          | 'MQ'
          | 'MR'
          | 'MS'
          | 'MT'
          | 'MU'
          | 'MV'
          | 'MW'
          | 'MX'
          | 'MY'
          | 'MZ'
          | 'NA'
          | 'NC'
          | 'NE'
          | 'NF'
          | 'NG'
          | 'NI'
          | 'NL'
          | 'NO'
          | 'NP'
          | 'NR'
          | 'NU'
          | 'NZ'
          | 'OM'
          | 'PA'
          | 'PE'
          | 'PF'
          | 'PG'
          | 'PH'
          | 'PK'
          | 'PL'
          | 'PM'
          | 'PN'
          | 'PR'
          | 'PS'
          | 'PT'
          | 'PW'
          | 'PY'
          | 'QA'
          | 'RE'
          | 'RO'
          | 'RS'
          | 'RU'
          | 'RW'
          | 'SA'
          | 'SB'
          | 'SC'
          | 'SD'
          | 'SE'
          | 'SG'
          | 'SH'
          | 'SI'
          | 'SJ'
          | 'SK'
          | 'SL'
          | 'SM'
          | 'SN'
          | 'SO'
          | 'SR'
          | 'SS'
          | 'ST'
          | 'SV'
          | 'SX'
          | 'SY'
          | 'SZ'
          | 'TC'
          | 'TD'
          | 'TF'
          | 'TG'
          | 'TH'
          | 'TJ'
          | 'TK'
          | 'TL'
          | 'TM'
          | 'TN'
          | 'TO'
          | 'TR'
          | 'TT'
          | 'TV'
          | 'TW'
          | 'TZ'
          | 'UA'
          | 'UG'
          | 'UM'
          | 'US'
          | 'UY'
          | 'UZ'
          | 'VA'
          | 'VC'
          | 'VE'
          | 'VG'
          | 'VI'
          | 'VN'
          | 'VU'
          | 'WF'
          | 'WS'
          | 'XK'
          | 'YE'
          | 'YT'
          | 'ZA'
          | 'ZM'
          | 'ZW'
        )[]
        /**
         * Ordering
         *
         * * `first_name` - First name
         * * `-first_name` - First name (descending)
         * * `last_name` - Last name
         * * `-last_name` - Last name (descending)
         * * `user_type` - User type
         * * `-user_type` - User type (descending)
         * * `app_access` - App access
         * * `-app_access` - App access (descending)
         * * `saldo_status` - Saldo status
         * * `-saldo_status` - Saldo status (descending)
         * * `date_joined` - Date joined
         * * `-date_joined` - Date joined (descending)
         * * `last_update` - Last update
         * * `-last_update` - Last update (descending)
         * * `status` - Status
         * * `-status` - Status (descending)
         */
        ordering?: (
          | '-app_access'
          | '-date_joined'
          | '-first_name'
          | '-last_name'
          | '-last_update'
          | '-saldo_status'
          | '-status'
          | '-user_type'
          | 'app_access'
          | 'date_joined'
          | 'first_name'
          | 'last_name'
          | 'last_update'
          | 'saldo_status'
          | 'status'
          | 'user_type'
        )[]
        /**
         * * `dayschool` - Day School
         * * `boarding` - Boarding
         * * `external` - External
         */
        program_type?: 'boarding' | 'dayschool' | 'external'
        /**
         * * `parent` - Parent
         * * `legal_guardian` - Legal Guardian
         * * `in_legal_guardian_absence` - In Legal Guardian Absence
         * * `arrival_contact` - Arrival Contact
         * * `educational_consultant` - Educational Consultant
         * * `main_invoice_recipient` - Main Invoice Recipient
         * * `invoice_recipient` - Invoice Recipient
         * * `contact_leave` - Contact Leave
         * * `invoice_copy_recipient` - Invoice Copy Recipient
         * * `assistant` - Assistant
         * * `relative` - Relative
         * * `other` - Other
         */
        roles?: (
          | 'arrival_contact'
          | 'assistant'
          | 'contact_leave'
          | 'educational_consultant'
          | 'in_legal_guardian_absence'
          | 'invoice_copy_recipient'
          | 'invoice_recipient'
          | 'legal_guardian'
          | 'main_invoice_recipient'
          | 'other'
          | 'parent'
          | 'relative'
        )[]
        /**
         * * `5` - Settled
         * * `10` - First reminder
         * * `15` - Second reminder
         * * `17` - Unsettled
         * * `20` - App locked
         */
        saldo_status?: (10 | 15 | 17 | 20 | 5)[]
        /** A search term. */
        search?: string
        status?: string[]
        /** @format uuid */
        student?: string
        target_grade_level?: number
        /**
         * * `1` - Contact
         * * `2` - Candidate
         * * `3` - Student
         */
        user_type?: 1 | 2 | 3
        year_of_rosenberg_entry?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserContactsAvailableFilters, T>, any>({
        path: `/crm/user-contacts/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags crm
     * @name CrmUserContactsSimpleListList
     * @request GET:/crm/user-contacts/simple-list/
     * @secure
     */,
    crmUserContactsSimpleListList: <
      T extends FetchKeys<PaginatedUserContactsSimpleListList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedUserContactsSimpleListList, T>, any>({
        path: `/crm/user-contacts/simple-list/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmUserNotesList
     * @request GET:/crm/user-notes/{userId}/
     * @secure
     */,
    crmUserNotesList: <T extends FetchKeys<PaginatedUserNoteList>>(
      userId: string,
      query?: {
        /** @format uuid */
        added_by?: string
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        is_trello_comment?: boolean
        /** @format date */
        modified_after?: string
        /** @format date */
        modified_before?: string
        /**
         * * `academic` - Academic
         * * `administrative` - Administrative
         * * `admission` - Admission
         * * `archived` - Archived
         * * `boarding` - Boarding
         * * `medical` - Medical
         * * `other` - Other
         */
        note_type?:
          | 'academic'
          | 'administrative'
          | 'admission'
          | 'archived'
          | 'boarding'
          | 'medical'
          | 'other'
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        school_year?: string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedUserNoteList, T>, any>({
        path: `/crm/user-notes/${userId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmUserNotesCreate
     * @request POST:/crm/user-notes/{userId}/
     * @secure
     */,
    crmUserNotesCreate: <T extends FetchKeys<UserNote>>(
      userId: string,
      data: UserNoteRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserNote, T>, any>({
        path: `/crm/user-notes/${userId}/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmUserNotesRetrieve
     * @request GET:/crm/user-notes/{userId}/{id}/
     * @secure
     */,
    crmUserNotesRetrieve: <T extends FetchKeys<UserNote>>(
      id: number,
      userId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserNote, T>, any>({
        path: `/crm/user-notes/${userId}/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmUserNotesUpdate
     * @request PUT:/crm/user-notes/{userId}/{id}/
     * @secure
     */,
    crmUserNotesUpdate: <T extends FetchKeys<UserNote>>(
      id: number,
      userId: string,
      data: UserNoteRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserNote, T>, any>({
        path: `/crm/user-notes/${userId}/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmUserNotesPartialUpdate
     * @request PATCH:/crm/user-notes/{userId}/{id}/
     * @secure
     */,
    crmUserNotesPartialUpdate: <T extends FetchKeys<UserNote>>(
      id: number,
      userId: string,
      data: PatchedUserNoteRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserNote, T>, any>({
        path: `/crm/user-notes/${userId}/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmUserNotesDestroy
     * @request DELETE:/crm/user-notes/{userId}/{id}/
     * @secure
     */,
    crmUserNotesDestroy: <T extends FetchKeys<void>>(
      id: number,
      userId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/crm/user-notes/${userId}/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags crm
     * @name CrmUserNotesAvailableFiltersRetrieve
     * @request GET:/crm/user-notes/{userId}/available-filters/
     * @secure
     */,
    crmUserNotesAvailableFiltersRetrieve: <
      T extends FetchKeys<UserNoteAvailableFilters>
    >(
      userId: string,
      query?: {
        /** @format uuid */
        added_by?: string
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        is_trello_comment?: boolean
        /** @format date */
        modified_after?: string
        /** @format date */
        modified_before?: string
        /**
         * * `academic` - Academic
         * * `administrative` - Administrative
         * * `admission` - Admission
         * * `archived` - Archived
         * * `boarding` - Boarding
         * * `medical` - Medical
         * * `other` - Other
         */
        note_type?:
          | 'academic'
          | 'administrative'
          | 'admission'
          | 'archived'
          | 'boarding'
          | 'medical'
          | 'other'
        /** Which field to use when ordering the results. */
        ordering?: string
        school_year?: string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserNoteAvailableFilters, T>, any>({
        path: `/crm/user-notes/${userId}/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags crm
     * @name CrmUserNotesNoteTypeAccessRetrieve
     * @request GET:/crm/user-notes/note-type-access/
     * @secure
     */,
    crmUserNotesNoteTypeAccessRetrieve: <
      T extends FetchKeys<UserNoteTypeAccessMetadata>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserNoteTypeAccessMetadata, T>, any>({
        path: `/crm/user-notes/note-type-access/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  curriculum = {
    /**
     * No description
     *
     * @tags curriculum
     * @name CurriculumGradeLevelsList
     * @request GET:/curriculum/grade-levels/
     * @secure
     */
    curriculumGradeLevelsList: <T extends FetchKeys<PaginatedGradeLevelList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedGradeLevelList, T>, any>({
        path: `/curriculum/grade-levels/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags curriculum
     * @name CurriculumGradeLevelsRetrieve
     * @request GET:/curriculum/grade-levels/{id}/
     * @secure
     */,
    curriculumGradeLevelsRetrieve: <T extends FetchKeys<GradeLevel>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<GradeLevel, T>, any>({
        path: `/curriculum/grade-levels/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  departments = {
    /**
     * No description
     *
     * @tags departments
     * @name DepartmentsList
     * @request GET:/departments/
     * @secure
     */
    departmentsList: <T extends FetchKeys<PaginatedDepartmentList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedDepartmentList, T>, any>({
        path: `/departments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags departments
     * @name DepartmentsCreate
     * @request POST:/departments/
     * @secure
     */,
    departmentsCreate: <T extends FetchKeys<Department>>(
      data: DepartmentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Department, T>, any>({
        path: `/departments/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags departments
     * @name DepartmentsRetrieve
     * @request GET:/departments/{id}/
     * @secure
     */,
    departmentsRetrieve: <T extends FetchKeys<Department>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Department, T>, any>({
        path: `/departments/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags departments
     * @name DepartmentsUpdate
     * @request PUT:/departments/{id}/
     * @secure
     */,
    departmentsUpdate: <T extends FetchKeys<Department>>(
      id: number,
      data: DepartmentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Department, T>, any>({
        path: `/departments/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags departments
     * @name DepartmentsPartialUpdate
     * @request PATCH:/departments/{id}/
     * @secure
     */,
    departmentsPartialUpdate: <T extends FetchKeys<Department>>(
      id: number,
      data: PatchedDepartmentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Department, T>, any>({
        path: `/departments/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags departments
     * @name DepartmentsDestroy
     * @request DELETE:/departments/{id}/
     * @secure
     */,
    departmentsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/departments/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
  }
  discipline = {
    /**
     * No description
     *
     * @tags discipline
     * @name DisciplineStudentCategoryReportsList
     * @request GET:/discipline/student-category-reports/
     * @secure
     */
    disciplineStudentCategoryReportsList: <
      T extends FetchKeys<PaginatedStudentCategoryReportListList>
    >(
      query?: {
        /** @format date */
        end_date__lte?: string
        house?: number
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** @format date */
        start_date__gte?: string
        /**
         * * `new` - New
         * * `incomplete` - Incomplete
         * * `finished` - Finished
         */
        status?: 'finished' | 'incomplete' | 'new'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentCategoryReportListList, T>, any>({
        path: `/discipline/student-category-reports/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags discipline
     * @name DisciplineStudentCategoryReportsRetrieve
     * @request GET:/discipline/student-category-reports/{reportId}/
     * @secure
     */,
    disciplineStudentCategoryReportsRetrieve: <
      T extends FetchKeys<StudentCategoryItemList>
    >(
      reportId: string,
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentCategoryItemList, T>, any>({
        path: `/discipline/student-category-reports/${reportId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags discipline
     * @name DisciplineStudentCategoryReportsRetrieve2
     * @request GET:/discipline/student-category-reports/{reportId}/{categoryItemId}/
     * @secure
     */,
    disciplineStudentCategoryReportsRetrieve2: <
      T extends FetchKeys<StudentCategoryItemList>
    >(
      categoryItemId: string,
      reportId: string,
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentCategoryItemList, T>, any>({
        path: `/discipline/student-category-reports/${reportId}/${categoryItemId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags discipline
     * @name DisciplineStudentCategoryReportsUpdate
     * @request PUT:/discipline/student-category-reports/{reportId}/{categoryItemId}/
     * @secure
     */,
    disciplineStudentCategoryReportsUpdate: <
      T extends FetchKeys<StudentCategoryItem>
    >(
      categoryItemId: string,
      reportId: string,
      data: StudentCategoryItemRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentCategoryItem, T>, any>({
        path: `/discipline/student-category-reports/${reportId}/${categoryItemId}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags discipline
     * @name DisciplineStudentCategoryReportsPartialUpdate
     * @request PATCH:/discipline/student-category-reports/{reportId}/{categoryItemId}/
     * @secure
     */,
    disciplineStudentCategoryReportsPartialUpdate: <
      T extends FetchKeys<StudentCategoryItem>
    >(
      categoryItemId: string,
      reportId: string,
      data: PatchedStudentCategoryItemRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentCategoryItem, T>, any>({
        path: `/discipline/student-category-reports/${reportId}/${categoryItemId}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
  }
  doctors = {
    /**
     * No description
     *
     * @tags doctors
     * @name DoctorsList
     * @request GET:/doctors/
     * @secure
     */
    doctorsList: <T extends FetchKeys<PaginatedDoctorList>>(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedDoctorList, T>, any>({
        path: `/doctors/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags doctors
     * @name DoctorsCreate
     * @request POST:/doctors/
     * @secure
     */,
    doctorsCreate: <T extends FetchKeys<Doctor>>(
      data: DoctorRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Doctor, T>, any>({
        path: `/doctors/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags doctors
     * @name DoctorsRetrieve
     * @request GET:/doctors/{id}/
     * @secure
     */,
    doctorsRetrieve: <T extends FetchKeys<Doctor>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Doctor, T>, any>({
        path: `/doctors/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags doctors
     * @name DoctorsUpdate
     * @request PUT:/doctors/{id}/
     * @secure
     */,
    doctorsUpdate: <T extends FetchKeys<Doctor>>(
      id: number,
      data: DoctorRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Doctor, T>, any>({
        path: `/doctors/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags doctors
     * @name DoctorsPartialUpdate
     * @request PATCH:/doctors/{id}/
     * @secure
     */,
    doctorsPartialUpdate: <T extends FetchKeys<Doctor>>(
      id: number,
      data: PatchedDoctorRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Doctor, T>, any>({
        path: `/doctors/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags doctors
     * @name DoctorsDestroy
     * @request DELETE:/doctors/{id}/
     * @secure
     */,
    doctorsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/doctors/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
  }
  exams = {
    /**
     * No description
     *
     * @tags exams
     * @name ExamsList
     * @request GET:/exams/
     * @secure
     */
    examsList: <T extends FetchKeys<PaginatedStudentExternalExamList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentExternalExamList, T>, any>({
        path: `/exams/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsCreate
     * @request POST:/exams/
     * @secure
     */,
    examsCreate: <T extends FetchKeys<StudentExternalExam>>(
      data: StudentExternalExamRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentExternalExam, T>, any>({
        path: `/exams/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsRetrieve
     * @request GET:/exams/{id}/
     * @secure
     */,
    examsRetrieve: <T extends FetchKeys<StudentExternalExam>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentExternalExam, T>, any>({
        path: `/exams/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsUpdate
     * @request PUT:/exams/{id}/
     * @secure
     */,
    examsUpdate: <T extends FetchKeys<StudentExternalExam>>(
      id: number,
      data: StudentExternalExamRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentExternalExam, T>, any>({
        path: `/exams/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsPartialUpdate
     * @request PATCH:/exams/{id}/
     * @secure
     */,
    examsPartialUpdate: <T extends FetchKeys<StudentExternalExam>>(
      id: number,
      data: PatchedStudentExternalExamRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentExternalExam, T>, any>({
        path: `/exams/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsDestroy
     * @request DELETE:/exams/{id}/
     * @secure
     */,
    examsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/exams/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsUploadCertificateCreate
     * @request POST:/exams/{id}/upload-certificate/
     * @secure
     */,
    examsUploadCertificateCreate: <T extends FetchKeys<StudentExternalExam>>(
      id: number,
      data: StudentExternalExamRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentExternalExam, T>, any>({
        path: `/exams/${id}/upload-certificate/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.UrlEncoded,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamClashesList
     * @request GET:/exams/exam-planner/exam-clashes/
     * @secure
     */,
    examsExamPlannerExamClashesList: <T extends FetchKeys<ExamClash[]>>(
      query?: {
        exams?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamClash[], T>, any>({
        path: `/exams/exam-planner/exam-clashes/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamClashesExamClashPreviewCreate
     * @request POST:/exams/exam-planner/exam-clashes/exam-clash-preview/
     * @secure
     */,
    examsExamPlannerExamClashesExamClashPreviewCreate: <
      T extends FetchKeys<ExamClash[]>
    >(
      data: ExamClashPreviewRequest,
      query?: {
        exams?: string[]
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamClash[], T>, any>({
        path: `/exams/exam-planner/exam-clashes/exam-clash-preview/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamEnrolledStudentList
     * @request GET:/exams/exam-planner/exam-enrolled-student/
     * @secure
     */,
    examsExamPlannerExamEnrolledStudentList: <
      T extends FetchKeys<PaginatedExamEnrolledStudentList>
    >(
      query?: {
        exam?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedExamEnrolledStudentList, T>, any>({
        path: `/exams/exam-planner/exam-enrolled-student/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamEnrolledStudentRetrieve
     * @request GET:/exams/exam-planner/exam-enrolled-student/{id}/
     * @secure
     */,
    examsExamPlannerExamEnrolledStudentRetrieve: <
      T extends FetchKeys<ExamEnrolledStudent>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamEnrolledStudent, T>, any>({
        path: `/exams/exam-planner/exam-enrolled-student/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamEnrolledStudentUpdate
     * @request PUT:/exams/exam-planner/exam-enrolled-student/{id}/
     * @secure
     */,
    examsExamPlannerExamEnrolledStudentUpdate: <
      T extends FetchKeys<ExamEnrolledStudent>
    >(
      id: number,
      data: ExamEnrolledStudentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamEnrolledStudent, T>, any>({
        path: `/exams/exam-planner/exam-enrolled-student/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamEnrolledStudentPartialUpdate
     * @request PATCH:/exams/exam-planner/exam-enrolled-student/{id}/
     * @secure
     */,
    examsExamPlannerExamEnrolledStudentPartialUpdate: <
      T extends FetchKeys<ExamEnrolledStudent>
    >(
      id: number,
      data: PatchedExamEnrolledStudentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamEnrolledStudent, T>, any>({
        path: `/exams/exam-planner/exam-enrolled-student/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionList
     * @request GET:/exams/exam-planner/exam-session/
     * @secure
     */,
    examsExamPlannerExamSessionList: <T extends FetchKeys<ExamSessionList[]>>(
      query?: {
        /**
         * * `quarter` - Quarter exams
         * * `academic` - Academic
         * * `external` - External exams
         */
        exam_type?: 'academic' | 'external' | 'quarter'
        quarter?: number
        school_year?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamSessionList[], T>, any>({
        path: `/exams/exam-planner/exam-session/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionCreate
     * @request POST:/exams/exam-planner/exam-session/
     * @secure
     */,
    examsExamPlannerExamSessionCreate: <T extends FetchKeys<ExamSessionCreate>>(
      data: ExamSessionCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamSessionCreate, T>, any>({
        path: `/exams/exam-planner/exam-session/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionExamsList
     * @request GET:/exams/exam-planner/exam-session/{examSessionId}/exams/
     * @secure
     */,
    examsExamPlannerExamSessionExamsList: <
      T extends FetchKeys<PaginatedExamList>
    >(
      examSessionId: string,
      query?: {
        all_invigilators?: string[]
        course?: string[]
        /** @format date */
        date?: string
        departments?: string[]
        end_time__isnull?: boolean
        grade_levels?: number[]
        has_clashes?: boolean
        has_saa_students?: boolean
        is_published?: boolean
        is_scheduled?: boolean
        location?: number[]
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        saa_invigilators?: string[]
        start_time__isnull?: boolean
        /**
         * * `draft` - Draft
         * * `planned` - Planned
         * * `in_progress` - In progress
         * * `ended` - Ended
         */
        status?: ('draft' | 'ended' | 'in_progress' | 'planned')[]
        students?: number[]
        teachers?: string[]
        test_invigilators?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedExamList, T>, any>({
        path: `/exams/exam-planner/exam-session/${examSessionId}/exams/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionExamsCreate
     * @request POST:/exams/exam-planner/exam-session/{examSessionId}/exams/
     * @secure
     */,
    examsExamPlannerExamSessionExamsCreate: <T extends FetchKeys<ExamForm>>(
      examSessionId: string,
      data: ExamFormRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamForm, T>, any>({
        path: `/exams/exam-planner/exam-session/${examSessionId}/exams/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionExamsRetrieve
     * @request GET:/exams/exam-planner/exam-session/{examSessionId}/exams/{id}/
     * @secure
     */,
    examsExamPlannerExamSessionExamsRetrieve: <T extends FetchKeys<Exam>>(
      examSessionId: string,
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Exam, T>, any>({
        path: `/exams/exam-planner/exam-session/${examSessionId}/exams/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionExamsUpdate
     * @request PUT:/exams/exam-planner/exam-session/{examSessionId}/exams/{id}/
     * @secure
     */,
    examsExamPlannerExamSessionExamsUpdate: <T extends FetchKeys<Exam>>(
      examSessionId: string,
      id: string,
      data: ExamFormRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Exam, T>, any>({
        path: `/exams/exam-planner/exam-session/${examSessionId}/exams/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionExamsPartialUpdate
     * @request PATCH:/exams/exam-planner/exam-session/{examSessionId}/exams/{id}/
     * @secure
     */,
    examsExamPlannerExamSessionExamsPartialUpdate: <T extends FetchKeys<Exam>>(
      examSessionId: string,
      id: string,
      data: PatchedExamRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Exam, T>, any>({
        path: `/exams/exam-planner/exam-session/${examSessionId}/exams/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionExamsDestroy
     * @request DELETE:/exams/exam-planner/exam-session/{examSessionId}/exams/{id}/
     * @secure
     */,
    examsExamPlannerExamSessionExamsDestroy: <T extends FetchKeys<void>>(
      examSessionId: string,
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/exams/exam-planner/exam-session/${examSessionId}/exams/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionExamsAllIdsRetrieve
     * @request GET:/exams/exam-planner/exam-session/{examSessionId}/exams/all-ids/
     * @secure
     */,
    examsExamPlannerExamSessionExamsAllIdsRetrieve: <
      T extends FetchKeys<string[]>
    >(
      examSessionId: string,
      query?: {
        all_invigilators?: string[]
        course?: string[]
        /** @format date */
        date?: string
        departments?: string[]
        end_time__isnull?: boolean
        grade_levels?: number[]
        has_clashes?: boolean
        has_saa_students?: boolean
        is_published?: boolean
        is_scheduled?: boolean
        location?: number[]
        /** Which field to use when ordering the results. */
        ordering?: string
        saa_invigilators?: string[]
        start_time__isnull?: boolean
        /**
         * * `draft` - Draft
         * * `planned` - Planned
         * * `in_progress` - In progress
         * * `ended` - Ended
         */
        status?: ('draft' | 'ended' | 'in_progress' | 'planned')[]
        students?: number[]
        teachers?: string[]
        test_invigilators?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<string[], T>, any>({
        path: `/exams/exam-planner/exam-session/${examSessionId}/exams/all-ids/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionExamsAvailableFiltersRetrieve
     * @request GET:/exams/exam-planner/exam-session/{examSessionId}/exams/available-filters/
     * @secure
     */,
    examsExamPlannerExamSessionExamsAvailableFiltersRetrieve: <
      T extends FetchKeys<ExamAvailableFilters>
    >(
      examSessionId: string,
      query?: {
        all_invigilators?: string[]
        course?: string[]
        /** @format date */
        date?: string
        departments?: string[]
        end_time__isnull?: boolean
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        grade_levels?: number[]
        has_clashes?: boolean
        has_saa_students?: boolean
        is_published?: boolean
        is_scheduled?: boolean
        location?: number[]
        /** Which field to use when ordering the results. */
        ordering?: string
        saa_invigilators?: string[]
        start_time__isnull?: boolean
        /**
         * * `draft` - Draft
         * * `planned` - Planned
         * * `in_progress` - In progress
         * * `ended` - Ended
         */
        status?: ('draft' | 'ended' | 'in_progress' | 'planned')[]
        students?: number[]
        teachers?: string[]
        test_invigilators?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamAvailableFilters, T>, any>({
        path: `/exams/exam-planner/exam-session/${examSessionId}/exams/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionExamsDatesRetrieve
     * @request GET:/exams/exam-planner/exam-session/{examSessionId}/exams/dates/
     * @secure
     */,
    examsExamPlannerExamSessionExamsDatesRetrieve: <
      T extends FetchKeys<string[]>
    >(
      examSessionId: string,
      query?: {
        all_invigilators?: string[]
        course?: string[]
        /** @format date */
        date?: string
        departments?: string[]
        end_time__isnull?: boolean
        grade_levels?: number[]
        has_clashes?: boolean
        has_saa_students?: boolean
        is_published?: boolean
        is_scheduled?: boolean
        location?: number[]
        /** Which field to use when ordering the results. */
        ordering?: string
        saa_invigilators?: string[]
        start_time__isnull?: boolean
        /**
         * * `draft` - Draft
         * * `planned` - Planned
         * * `in_progress` - In progress
         * * `ended` - Ended
         */
        status?: ('draft' | 'ended' | 'in_progress' | 'planned')[]
        students?: number[]
        teachers?: string[]
        test_invigilators?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<string[], T>, any>({
        path: `/exams/exam-planner/exam-session/${examSessionId}/exams/dates/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionExamsRemoveMultipleCreate
     * @request POST:/exams/exam-planner/exam-session/{examSessionId}/exams/remove-multiple/
     * @secure
     */,
    examsExamPlannerExamSessionExamsRemoveMultipleCreate: <
      T extends FetchKeys<MultipleExam>
    >(
      examSessionId: string,
      data: MultipleExamRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MultipleExam, T>, any>({
        path: `/exams/exam-planner/exam-session/${examSessionId}/exams/remove-multiple/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionRetrieve
     * @request GET:/exams/exam-planner/exam-session/{id}/
     * @secure
     */,
    examsExamPlannerExamSessionRetrieve: <
      T extends FetchKeys<ExamSessionRetrieve>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamSessionRetrieve, T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionUpdate
     * @request PUT:/exams/exam-planner/exam-session/{id}/
     * @secure
     */,
    examsExamPlannerExamSessionUpdate: <T extends FetchKeys<ExamSession>>(
      id: string,
      data: ExamSessionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamSession, T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionDestroy
     * @request DELETE:/exams/exam-planner/exam-session/{id}/
     * @secure
     */,
    examsExamPlannerExamSessionDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionApplyCreate
     * @request POST:/exams/exam-planner/exam-session/{id}/apply/
     * @secure
     */,
    examsExamPlannerExamSessionApplyCreate: <T extends FetchKeys<void>>(
      id: string,
      data: ExamGenerationApplyRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/apply/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionAvailableExamCoursesList
     * @request GET:/exams/exam-planner/exam-session/{id}/available-exam-courses/
     * @secure
     */,
    examsExamPlannerExamSessionAvailableExamCoursesList: <
      T extends FetchKeys<AvailableExam[]>
    >(
      id: string,
      query?: {
        /**
         * * `quarter` - Quarter exams
         * * `academic` - Academic
         * * `external` - External exams
         */
        exam_type?: 'academic' | 'external' | 'quarter'
        only_empty?: boolean
        only_id?: boolean
        /** @minLength 1 */
        pattern_exam_id?: string
        quarter?: number
        school_year?: string
        show_courses?: boolean

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AvailableExam[], T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/available-exam-courses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionChangesPreviewCreate
     * @request POST:/exams/exam-planner/exam-session/{id}/changes-preview/
     * @secure
     */,
    examsExamPlannerExamSessionChangesPreviewCreate: <
      T extends FetchKeys<ExamSessionChangesPreviewResponse>
    >(
      id: string,
      data: ExamSessionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamSessionChangesPreviewResponse, T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/changes-preview/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionCoursesInExamSessionRetrieve
     * @request GET:/exams/exam-planner/exam-session/{id}/courses-in-exam-session/
     * @secure
     */,
    examsExamPlannerExamSessionCoursesInExamSessionRetrieve: <
      T extends FetchKeys<CourseInExamSession>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseInExamSession, T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/courses-in-exam-session/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionDiscardCreate
     * @request POST:/exams/exam-planner/exam-session/{id}/discard/
     * @secure
     */,
    examsExamPlannerExamSessionDiscardCreate: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/discard/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionExamsReadyToPublishRetrieve
     * @request GET:/exams/exam-planner/exam-session/{id}/exams-ready-to-publish/
     * @secure
     */,
    examsExamPlannerExamSessionExamsReadyToPublishRetrieve: <
      T extends FetchKeys<ExamSession>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamSession, T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/exams-ready-to-publish/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionGenerateCreate
     * @request POST:/exams/exam-planner/exam-session/{id}/generate/
     * @secure
     */,
    examsExamPlannerExamSessionGenerateCreate: <
      T extends FetchKeys<ExamGenerationStarted>
    >(
      id: string,
      data: ExamSessionGenerateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamGenerationStarted, T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/generate/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionPublishExamsCreate
     * @request POST:/exams/exam-planner/exam-session/{id}/publish-exams/
     * @secure
     */,
    examsExamPlannerExamSessionPublishExamsCreate: <T extends FetchKeys<void>>(
      id: string,
      data: PublishExamRequestRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/publish-exams/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionPublishReadyExamsCreate
     * @request POST:/exams/exam-planner/exam-session/{id}/publish-ready-exams/
     * @secure
     */,
    examsExamPlannerExamSessionPublishReadyExamsCreate: <
      T extends FetchKeys<ExamSession>
    >(
      id: string,
      data: ExamSessionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamSession, T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/publish-ready-exams/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionRegenerateCreate
     * @request POST:/exams/exam-planner/exam-session/{id}/regenerate/
     * @secure
     */,
    examsExamPlannerExamSessionRegenerateCreate: <
      T extends FetchKeys<ExamGenerationStarted>
    >(
      id: string,
      data: ExamSessionGenerateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamGenerationStarted, T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/regenerate/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionScheduledUnpublishedExamsCountRetrieve
     * @request GET:/exams/exam-planner/exam-session/{id}/scheduled-unpublished-exams-count/
     * @secure
     */,
    examsExamPlannerExamSessionScheduledUnpublishedExamsCountRetrieve: <
      T extends FetchKeys<number>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<number, T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/scheduled-unpublished-exams-count/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionStopCreate
     * @request POST:/exams/exam-planner/exam-session/{id}/stop/
     * @secure
     */,
    examsExamPlannerExamSessionStopCreate: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/stop/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionStudentsForExamRetrieve
     * @request GET:/exams/exam-planner/exam-session/{id}/students-for-exam/
     * @deprecated
     * @secure
     */,
    examsExamPlannerExamSessionStudentsForExamRetrieve: <
      T extends FetchKeys<ExamSession>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamSession, T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/students-for-exam/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionStudentsFromCoursesList
     * @request GET:/exams/exam-planner/exam-session/{id}/students-from-courses/
     * @secure
     */,
    examsExamPlannerExamSessionStudentsFromCoursesList: <
      T extends FetchKeys<StudentForExam[]>
    >(
      id: string,
      query: {
        courses: string[]
        /**
         * * `quarter` - Quarter exams
         * * `academic` - Academic
         * * `external` - External exams
         */
        exam_type?: 'academic' | 'external' | 'quarter'
        grade_level_ids?: number[]
        quarter?: number
        school_year?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentForExam[], T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/students-from-courses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionTeachersFromCoursesList
     * @request GET:/exams/exam-planner/exam-session/{id}/teachers-from-courses/
     * @secure
     */,
    examsExamPlannerExamSessionTeachersFromCoursesList: <
      T extends FetchKeys<TeacherFromCourse[]>
    >(
      id: string,
      query: {
        courses: string[]
        /**
         * * `quarter` - Quarter exams
         * * `academic` - Academic
         * * `external` - External exams
         */
        exam_type?: 'academic' | 'external' | 'quarter'
        quarter?: number
        school_year?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TeacherFromCourse[], T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/teachers-from-courses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionXlsxRetrieve
     * @request GET:/exams/exam-planner/exam-session/{id}/xlsx/
     * @secure
     */,
    examsExamPlannerExamSessionXlsxRetrieve: <T extends FetchKeys<ExamSession>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamSession, T>, any>({
        path: `/exams/exam-planner/exam-session/${id}/xlsx/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionAvailableUsersRetrieve
     * @request GET:/exams/exam-planner/exam-session/available-users/
     * @secure
     */,
    examsExamPlannerExamSessionAvailableUsersRetrieve: <
      T extends FetchKeys<AvailableUsersResponse>
    >(
      query: {
        /** @format date */
        date?: string
        /** @format time */
        end_time?: string
        /** @minLength 1 */
        exam_id?: string
        excluded_exam_ids: string[]
        only_teachers?: boolean
        /** @format time */
        start_time?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AvailableUsersResponse, T>, any>({
        path: `/exams/exam-planner/exam-session/available-users/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionHasExamsInDateRangeRetrieve
     * @request GET:/exams/exam-planner/exam-session/has-exams-in-date-range/
     * @secure
     */,
    examsExamPlannerExamSessionHasExamsInDateRangeRetrieve: <
      T extends FetchKeys<string[]>
    >(
      query: {
        /** @format date */
        end_date: string
        /** @format date */
        start_date: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<string[], T>, any>({
        path: `/exams/exam-planner/exam-session/has-exams-in-date-range/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionInvigilatorsFromRoomRetrieve
     * @request GET:/exams/exam-planner/exam-session/invigilators-from-room/
     * @secure
     */,
    examsExamPlannerExamSessionInvigilatorsFromRoomRetrieve: <
      T extends FetchKeys<InvigilatorsFromRoomResponse>
    >(
      query: {
        /** @format date */
        date: string
        /** @format time */
        end_time: string
        exam_location: number
        /**
         * * `quarter` - Quarter exams
         * * `academic` - Academic
         * * `external` - External exams
         */
        exam_type?: 'academic' | 'external' | 'quarter'
        quarter?: number
        school_year?: string
        /** @format time */
        start_time: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<InvigilatorsFromRoomResponse, T>, any>({
        path: `/exams/exam-planner/exam-session/invigilators-from-room/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionSimpleListList
     * @request GET:/exams/exam-planner/exam-session/simple-list/
     * @secure
     */,
    examsExamPlannerExamSessionSimpleListList: <
      T extends FetchKeys<ExamSessionSimpleList[]>
    >(
      query?: {
        /**
         * * `quarter` - Quarter exams
         * * `academic` - Academic
         * * `external` - External exams
         */
        exam_type?: 'academic' | 'external' | 'quarter'
        quarter?: number
        school_year?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamSessionSimpleList[], T>, any>({
        path: `/exams/exam-planner/exam-session/simple-list/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionStudentsList
     * @request GET:/exams/exam-planner/exam-session/students/
     * @secure
     */,
    examsExamPlannerExamSessionStudentsList: <
      T extends FetchKeys<ExamStudent[]>
    >(
      query?: {
        exams?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamStudent[], T>, any>({
        path: `/exams/exam-planner/exam-session/students/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionSuggestedRoomsRetrieve
     * @request GET:/exams/exam-planner/exam-session/suggested-rooms/
     * @secure
     */,
    examsExamPlannerExamSessionSuggestedRoomsRetrieve: <
      T extends FetchKeys<SuggestedRoomsResponse>
    >(
      query: {
        /** @format date */
        date?: string
        /** @format time */
        end_time?: string
        excluded_exam_ids: string[]
        invigilator_ids?: string[]
        /** @default false */
        saa_students_only?: boolean
        /** @format time */
        start_time?: string
        students_number: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SuggestedRoomsResponse, T>, any>({
        path: `/exams/exam-planner/exam-session/suggested-rooms/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExamSessionSummaryCreate
     * @request POST:/exams/exam-planner/exam-session/summary/
     * @secure
     */,
    examsExamPlannerExamSessionSummaryCreate: <
      T extends FetchKeys<ExamSessionSummary>
    >(
      data: ExamSessionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamSessionSummary, T>, any>({
        path: `/exams/exam-planner/exam-session/summary/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags exams
     * @name ExamsExamPlannerExamTimetableList
     * @request GET:/exams/exam-planner/exam-timetable/
     * @secure
     */,
    examsExamPlannerExamTimetableList: <
      T extends FetchKeys<ExamPlannerTimetableExam[]>
    >(
      query?: {
        all_invigilators?: string[]
        course?: string[]
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        departments?: string[]
        end_time__isnull?: boolean
        /** @format short-uuid */
        exam_session?: string
        /**
         * @minLength 1
         * @maxLength 64
         */
        generation_id?: string
        grade_levels?: number[]
        has_clashes?: boolean
        has_saa_students?: boolean
        is_published?: boolean
        is_scheduled?: boolean
        location?: number[]
        saa_invigilators?: string[]
        start_time__isnull?: boolean
        /**
         * * `draft` - Draft
         * * `planned` - Planned
         * * `in_progress` - In progress
         * * `ended` - Ended
         */
        status?: ('draft' | 'ended' | 'in_progress' | 'planned')[]
        students?: string[]
        teachers?: string[]
        test_invigilators?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamPlannerTimetableExam[], T>, any>({
        path: `/exams/exam-planner/exam-timetable/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags exams
     * @name ExamsExamPlannerExamTimetableAvailableFiltersRetrieve
     * @request GET:/exams/exam-planner/exam-timetable/available-filters/
     * @secure
     */,
    examsExamPlannerExamTimetableAvailableFiltersRetrieve: <
      T extends FetchKeys<ExamTimetableAvailableFilters>
    >(
      query?: {
        all_invigilators?: string[]
        course?: string[]
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        departments?: string[]
        end_time__isnull?: boolean
        /** @format short-uuid */
        exam_session?: string
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        /**
         * @minLength 1
         * @maxLength 64
         */
        generation_id?: string
        grade_levels?: number[]
        has_clashes?: boolean
        has_saa_students?: boolean
        is_published?: boolean
        is_scheduled?: boolean
        location?: number[]
        saa_invigilators?: string[]
        start_time__isnull?: boolean
        /**
         * * `draft` - Draft
         * * `planned` - Planned
         * * `in_progress` - In progress
         * * `ended` - Ended
         */
        status?: ('draft' | 'ended' | 'in_progress' | 'planned')[]
        students?: string[]
        teachers?: string[]
        test_invigilators?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamTimetableAvailableFilters, T>, any>({
        path: `/exams/exam-planner/exam-timetable/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags exams
     * @name ExamsExamPlannerExamsList
     * @request GET:/exams/exam-planner/exams/
     * @secure
     */,
    examsExamPlannerExamsList: <T extends FetchKeys<PaginatedExamList>>(
      query?: {
        all_invigilators?: string[]
        course?: string[]
        /** @format date */
        date?: string
        departments?: string[]
        end_time__isnull?: boolean
        exam_session_id?: string
        /**
         * * `quarter` - Quarter exams
         * * `academic` - Academic
         * * `external` - External exams
         */
        exam_type?: ('academic' | 'external' | 'quarter')[]
        grade_levels?: number[]
        has_clashes?: boolean
        has_saa_students?: boolean
        is_published?: boolean
        is_scheduled?: boolean
        location?: number[]
        name?: string[]
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter?: number
        saa_invigilators?: string[]
        school_year?: number
        start_time__isnull?: boolean
        /**
         * * `draft` - Draft
         * * `planned` - Planned
         * * `in_progress` - In progress
         * * `ended` - Ended
         */
        status?: ('draft' | 'ended' | 'in_progress' | 'planned')[]
        students?: number[]
        teachers?: string[]
        test_invigilators?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedExamList, T>, any>({
        path: `/exams/exam-planner/exams/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags exams
     * @name ExamsExamPlannerExamsAvailableFiltersRetrieve
     * @request GET:/exams/exam-planner/exams/available-filters/
     * @secure
     */,
    examsExamPlannerExamsAvailableFiltersRetrieve: <T extends FetchKeys<Exam>>(
      query?: {
        all_invigilators?: string[]
        course?: string[]
        /** @format date */
        date?: string
        departments?: string[]
        end_time__isnull?: boolean
        exam_session_id?: string
        /**
         * * `quarter` - Quarter exams
         * * `academic` - Academic
         * * `external` - External exams
         */
        exam_type?: ('academic' | 'external' | 'quarter')[]
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        grade_levels?: number[]
        has_clashes?: boolean
        has_saa_students?: boolean
        is_published?: boolean
        is_scheduled?: boolean
        location?: number[]
        name?: string[]
        /** Which field to use when ordering the results. */
        ordering?: string
        quarter?: number
        saa_invigilators?: string[]
        school_year?: number
        start_time__isnull?: boolean
        /**
         * * `draft` - Draft
         * * `planned` - Planned
         * * `in_progress` - In progress
         * * `ended` - Ended
         */
        status?: ('draft' | 'ended' | 'in_progress' | 'planned')[]
        students?: number[]
        teachers?: string[]
        test_invigilators?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Exam, T>, any>({
        path: `/exams/exam-planner/exams/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags exams
     * @name ExamsExamPlannerExamsGroupedList
     * @request GET:/exams/exam-planner/exams/grouped/
     * @secure
     */,
    examsExamPlannerExamsGroupedList: <
      T extends FetchKeys<PaginatedGroupedExamListList>
    >(
      query?: {
        all_invigilators?: string[]
        course?: string[]
        /** @format date */
        date?: string
        departments?: string[]
        end_time__isnull?: boolean
        exam_session_id?: string
        /**
         * * `quarter` - Quarter exams
         * * `academic` - Academic
         * * `external` - External exams
         */
        exam_type?: ('academic' | 'external' | 'quarter')[]
        grade_levels?: number[]
        has_clashes?: boolean
        has_saa_students?: boolean
        is_published?: boolean
        is_scheduled?: boolean
        location?: number[]
        name?: string[]
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter?: number
        saa_invigilators?: string[]
        school_year?: number
        start_time__isnull?: boolean
        /**
         * * `draft` - Draft
         * * `planned` - Planned
         * * `in_progress` - In progress
         * * `ended` - Ended
         */
        status?: ('draft' | 'ended' | 'in_progress' | 'planned')[]
        students?: number[]
        teachers?: string[]
        test_invigilators?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedGroupedExamListList, T>, any>({
        path: `/exams/exam-planner/exams/grouped/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExternalExamsCreate
     * @request POST:/exams/exam-planner/external-exams/
     * @secure
     */,
    examsExamPlannerExternalExamsCreate: <
      T extends FetchKeys<ExternalExamForm>
    >(
      data: ExternalExamFormRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExternalExamForm, T>, any>({
        path: `/exams/exam-planner/external-exams/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerExternalExamsAvailableExamCoursesList
     * @request GET:/exams/exam-planner/external-exams/available-exam-courses/
     * @secure
     */,
    examsExamPlannerExternalExamsAvailableExamCoursesList: <
      T extends FetchKeys<AvailableExternalExamCourses[]>
    >(
      query: {
        /**
         * * `quarter` - Quarter exams
         * * `academic` - Academic
         * * `external` - External exams
         * @minLength 1
         */
        exam_type: 'quarter' | 'academic' | 'external'
        quarter: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AvailableExternalExamCourses[], T>, any>({
        path: `/exams/exam-planner/external-exams/available-exam-courses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerProcessStateRetrieve
     * @request GET:/exams/exam-planner/process-state/
     * @secure
     */,
    examsExamPlannerProcessStateRetrieve: <
      T extends FetchKeys<ExamGenerationProcessState>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExamGenerationProcessState, T>, any>({
        path: `/exams/exam-planner/process-state/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerRevisionWeekAutofillPlansList
     * @request GET:/exams/exam-planner/revision-week-autofill-plans/
     * @secure
     */,
    examsExamPlannerRevisionWeekAutofillPlansList: <
      T extends FetchKeys<RevisionWeekAutofillPlanList[]>
    >(
      query?: {
        exam_session_id?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RevisionWeekAutofillPlanList[], T>, any>({
        path: `/exams/exam-planner/revision-week-autofill-plans/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerRevisionWeekAutofillPlansCreate
     * @request POST:/exams/exam-planner/revision-week-autofill-plans/
     * @secure
     */,
    examsExamPlannerRevisionWeekAutofillPlansCreate: <
      T extends FetchKeys<RevisionWeekAutofillPlanDetail>
    >(
      data: RevisionWeekAutofillPlanCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RevisionWeekAutofillPlanDetail, T>, any>({
        path: `/exams/exam-planner/revision-week-autofill-plans/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerRevisionWeekAutofillPlansRetrieve
     * @request GET:/exams/exam-planner/revision-week-autofill-plans/{id}/
     * @secure
     */,
    examsExamPlannerRevisionWeekAutofillPlansRetrieve: <
      T extends FetchKeys<RevisionWeekAutofillPlanDetail>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RevisionWeekAutofillPlanDetail, T>, any>({
        path: `/exams/exam-planner/revision-week-autofill-plans/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExamPlannerRevisionWeekAutofillPlansApplyCreate
     * @request POST:/exams/exam-planner/revision-week-autofill-plans/{id}/apply/
     * @secure
     */,
    examsExamPlannerRevisionWeekAutofillPlansApplyCreate: <
      T extends FetchKeys<RevisionWeekAutofillPlanDetail>
    >(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RevisionWeekAutofillPlanDetail, T>, any>({
        path: `/exams/exam-planner/revision-week-autofill-plans/${id}/apply/`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsExternalTypesList
     * @request GET:/exams/external-types/
     * @secure
     */,
    examsExternalTypesList: <T extends FetchKeys<ExternalExamType[]>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExternalExamType[], T>, any>({
        path: `/exams/external-types/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsStudentsList
     * @request GET:/exams/students/{studentId}/
     * @secure
     */,
    examsStudentsList: <T extends FetchKeys<PaginatedStudentExternalExamList>>(
      studentId: string,
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentExternalExamList, T>, any>({
        path: `/exams/students/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsStudentsUpdate
     * @request PUT:/exams/students/{studentId}/
     * @secure
     */,
    examsStudentsUpdate: <T extends FetchKeys<StudentExamUpdate>>(
      studentId: string,
      data: StudentExamUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentExamUpdate, T>, any>({
        path: `/exams/students/${studentId}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsStudentsPartialUpdate
     * @request PATCH:/exams/students/{studentId}/
     * @secure
     */,
    examsStudentsPartialUpdate: <T extends FetchKeys<StudentExternalExam>>(
      studentId: string,
      data: PatchedStudentExternalExamRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentExternalExam, T>, any>({
        path: `/exams/students/${studentId}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsTranscriptsList
     * @request GET:/exams/transcripts/
     * @secure
     */,
    examsTranscriptsList: <T extends FetchKeys<PaginatedStudentTranscriptList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentTranscriptList, T>, any>({
        path: `/exams/transcripts/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsTranscriptsCreate
     * @request POST:/exams/transcripts/
     * @secure
     */,
    examsTranscriptsCreate: <T extends FetchKeys<StudentTranscript>>(
      data: StudentTranscriptRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTranscript, T>, any>({
        path: `/exams/transcripts/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsTranscriptsRetrieve
     * @request GET:/exams/transcripts/{id}/
     * @secure
     */,
    examsTranscriptsRetrieve: <T extends FetchKeys<StudentTranscript>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTranscript, T>, any>({
        path: `/exams/transcripts/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsTranscriptsUpdate
     * @request PUT:/exams/transcripts/{id}/
     * @secure
     */,
    examsTranscriptsUpdate: <T extends FetchKeys<StudentTranscript>>(
      id: number,
      data: StudentTranscriptRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTranscript, T>, any>({
        path: `/exams/transcripts/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsTranscriptsPartialUpdate
     * @request PATCH:/exams/transcripts/{id}/
     * @secure
     */,
    examsTranscriptsPartialUpdate: <T extends FetchKeys<StudentTranscript>>(
      id: number,
      data: PatchedStudentTranscriptRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTranscript, T>, any>({
        path: `/exams/transcripts/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsTranscriptsDestroy
     * @request DELETE:/exams/transcripts/{id}/
     * @secure
     */,
    examsTranscriptsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/exams/transcripts/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsTranscriptsUploadCertificateCreate
     * @request POST:/exams/transcripts/{id}/upload-certificate/
     * @secure
     */,
    examsTranscriptsUploadCertificateCreate: <
      T extends FetchKeys<StudentTranscript>
    >(
      id: number,
      data: StudentTranscriptRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTranscript, T>, any>({
        path: `/exams/transcripts/${id}/upload-certificate/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.UrlEncoded,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsTranscriptsStudentsList
     * @request GET:/exams/transcripts/students/{studentId}/
     * @secure
     */,
    examsTranscriptsStudentsList: <
      T extends FetchKeys<PaginatedStudentTranscriptList>
    >(
      studentId: string,
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentTranscriptList, T>, any>({
        path: `/exams/transcripts/students/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsTranscriptsStudentsUpdate
     * @request PUT:/exams/transcripts/students/{studentId}/
     * @secure
     */,
    examsTranscriptsStudentsUpdate: <
      T extends FetchKeys<StudentTranscriptUpdate>
    >(
      studentId: string,
      data: StudentTranscriptUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTranscriptUpdate, T>, any>({
        path: `/exams/transcripts/students/${studentId}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags exams
     * @name ExamsTranscriptsStudentsPartialUpdate
     * @request PATCH:/exams/transcripts/students/{studentId}/
     * @secure
     */,
    examsTranscriptsStudentsPartialUpdate: <
      T extends FetchKeys<StudentTranscriptUpdate>
    >(
      studentId: string,
      data: PatchedStudentTranscriptUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTranscriptUpdate, T>, any>({
        path: `/exams/transcripts/students/${studentId}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
  }
  favouriteWidgets = {
    /**
     * No description
     *
     * @tags favourite-widgets
     * @name FavouriteWidgetsList
     * @request GET:/favourite-widgets/
     * @secure
     */
    favouriteWidgetsList: <T extends FetchKeys<PaginatedFavouriteWidgetList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedFavouriteWidgetList, T>, any>({
        path: `/favourite-widgets/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  filesForStudents = {
    /**
     * No description
     *
     * @tags files-for-students
     * @name FilesForStudentsList
     * @request GET:/files-for-students/
     * @secure
     */
    filesForStudentsList: <T extends FetchKeys<PaginatedFileForStudentList>>(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedFileForStudentList, T>, any>({
        path: `/files-for-students/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags files-for-students
     * @name FilesForStudentsCreate
     * @request POST:/files-for-students/
     * @secure
     */,
    filesForStudentsCreate: <T extends FetchKeys<FileForStudent>>(
      data: FileForStudentSwaggerRequest[],
      query?: {
        replace_duplicated?: boolean
        save_duplicated?: boolean
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FileForStudent, T>, any>({
        path: `/files-for-students/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags files-for-students
     * @name FilesForStudentsRetrieve
     * @request GET:/files-for-students/{id}/
     * @secure
     */,
    filesForStudentsRetrieve: <T extends FetchKeys<FileForStudent>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FileForStudent, T>, any>({
        path: `/files-for-students/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags files-for-students
     * @name FilesForStudentsUpdate
     * @request PUT:/files-for-students/{id}/
     * @secure
     */,
    filesForStudentsUpdate: <T extends FetchKeys<FileForStudent>>(
      id: number,
      data: FileForStudentSwaggerRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FileForStudent, T>, any>({
        path: `/files-for-students/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags files-for-students
     * @name FilesForStudentsPartialUpdate
     * @request PATCH:/files-for-students/{id}/
     * @secure
     */,
    filesForStudentsPartialUpdate: <T extends FetchKeys<FileForStudent>>(
      id: number,
      data: PatchedFileForStudentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FileForStudent, T>, any>({
        path: `/files-for-students/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags files-for-students
     * @name FilesForStudentsDestroy
     * @request DELETE:/files-for-students/{id}/
     * @secure
     */,
    filesForStudentsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/files-for-students/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags files-for-students
     * @name FilesForStudentsChoicesRetrieve
     * @request GET:/files-for-students/choices/
     * @secure
     */,
    filesForStudentsChoicesRetrieve: <
      T extends FetchKeys<FileForStudentChoices>
    >(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FileForStudentChoices, T>, any>({
        path: `/files-for-students/choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags files-for-students
     * @name FilesForStudentsSaveOrderCreate
     * @request POST:/files-for-students/save-order/
     * @secure
     */,
    filesForStudentsSaveOrderCreate: <
      T extends FetchKeys<SaveStudentsFilesOrder>
    >(
      data: SaveStudentsFilesOrderRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SaveStudentsFilesOrder, T>, any>({
        path: `/files-for-students/save-order/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
  }
  grades = {
    /**
     * No description
     *
     * @tags grades
     * @name GradesAccList
     * @request GET:/grades/acc/
     * @secure
     */
    gradesAccList: <T extends FetchKeys<PaginatedAccGradeList>>(
      query?: {
        below_target?: boolean
        /** @format uuid */
        course?: string
        course_in_active_idp_plan?: boolean
        /** @format date-time */
        created?: string
        dropped_grades?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter?: number
        semester?: number
        /** @format uuid */
        student?: string
        /**
         * * `pending` - Pending
         * * `much_above` - Much Above
         * * `above` - Above
         * * `below` - Below
         * * `much_below` - Much Below
         */
        target_grade_status?:
          | 'above'
          | 'below'
          | 'much_above'
          | 'much_below'
          | 'pending'
        /** @format uuid */
        teacher_id?: string
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAccGradeList, T>, any>({
        path: `/grades/acc/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags grades
     * @name GradesAccCreate
     * @request POST:/grades/acc/
     * @secure
     */,
    gradesAccCreate: <T extends FetchKeys<AccGrade>>(
      data: AccGradeRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AccGrade, T>, any>({
        path: `/grades/acc/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags grades
     * @name GradesAccRetrieve
     * @request GET:/grades/acc/{id}/
     * @secure
     */,
    gradesAccRetrieve: <T extends FetchKeys<AccGrade>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AccGrade, T>, any>({
        path: `/grades/acc/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags grades
     * @name GradesAccUpdate
     * @request PUT:/grades/acc/{id}/
     * @secure
     */,
    gradesAccUpdate: <T extends FetchKeys<AccGrade>>(
      id: number,
      data: AccGradeRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AccGrade, T>, any>({
        path: `/grades/acc/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags grades
     * @name GradesAccPartialUpdate
     * @request PATCH:/grades/acc/{id}/
     * @secure
     */,
    gradesAccPartialUpdate: <T extends FetchKeys<AccGrade>>(
      id: number,
      data: PatchedAccGradeRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AccGrade, T>, any>({
        path: `/grades/acc/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags grades
     * @name GradesAccDestroy
     * @request DELETE:/grades/acc/{id}/
     * @secure
     */,
    gradesAccDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/grades/acc/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags grades
     * @name GradesAccSetTargetCreate
     * @request POST:/grades/acc/{id}/set-target/
     * @secure
     */,
    gradesAccSetTargetCreate: <T extends FetchKeys<TargetGrade>>(
      id: number,
      data: TargetGradeRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TargetGrade, T>, any>({
        path: `/grades/acc/${id}/set-target/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags grades
     * @name GradesAccAllGradesRetrieve
     * @request GET:/grades/acc/all-grades/
     * @secure
     */,
    gradesAccAllGradesRetrieve: <T extends FetchKeys<Record<string, string[]>>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Record<string, string[]>, T>, any>({
        path: `/grades/acc/all-grades/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags grades
     * @name GradesAccCsvRetrieve
     * @request GET:/grades/acc/csv/
     * @secure
     */,
    gradesAccCsvRetrieve: <T extends FetchKeys<AccGrade>>(
      query?: {
        below_target?: boolean
        /** @format uuid */
        course?: string
        course_in_active_idp_plan?: boolean
        /** @format date-time */
        created?: string
        dropped_grades?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        quarter?: number
        semester?: number
        /** @format uuid */
        student?: string
        /**
         * * `pending` - Pending
         * * `much_above` - Much Above
         * * `above` - Above
         * * `below` - Below
         * * `much_below` - Much Below
         */
        target_grade_status?:
          | 'above'
          | 'below'
          | 'much_above'
          | 'much_below'
          | 'pending'
        /** @format uuid */
        teacher_id?: string
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AccGrade, T>, any>({
        path: `/grades/acc/csv/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags grades
     * @name GradesAccCsvForCurrentQuarterRetrieve
     * @request GET:/grades/acc/csv-for-current-quarter/
     * @secure
     */,
    gradesAccCsvForCurrentQuarterRetrieve: <T extends FetchKeys<AccGrade>>(
      query?: {
        below_target?: boolean
        /** @format uuid */
        course?: string
        course_in_active_idp_plan?: boolean
        /** @format date-time */
        created?: string
        dropped_grades?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        quarter?: number
        semester?: number
        /** @format uuid */
        student?: string
        /**
         * * `pending` - Pending
         * * `much_above` - Much Above
         * * `above` - Above
         * * `below` - Below
         * * `much_below` - Much Below
         */
        target_grade_status?:
          | 'above'
          | 'below'
          | 'much_above'
          | 'much_below'
          | 'pending'
        /** @format uuid */
        teacher_id?: string
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AccGrade, T>, any>({
        path: `/grades/acc/csv-for-current-quarter/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Return grades in IDP format (columns with Q1, S1 etc.) :return response [ { student: 1, courses: [ { "course": course_id, "grades": { "Q1": {"id": 1, "grade": "A+", "comment": "X"}, "Q2": {"id": 1, "grade": "A+", "comment": "X"}, "S1": {"id": 1, "grade": "A+", "comment": "X"}, } } ] } ]
     *
     * @tags grades
     * @name GradesAccIdpTableList
     * @request GET:/grades/acc/idp-table/
     * @deprecated
     * @secure
     */,
    gradesAccIdpTableList: <
      T extends FetchKeys<IdpTableStudentCoursesGrades[]>
    >(
      query?: {
        below_target?: boolean
        /** @format uuid */
        course?: string
        course_in_active_idp_plan?: boolean
        /** @format date-time */
        created?: string
        dropped_grades?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        quarter?: number
        semester?: number
        /** @format uuid */
        student?: string
        /**
         * * `pending` - Pending
         * * `much_above` - Much Above
         * * `above` - Above
         * * `below` - Below
         * * `much_below` - Much Below
         */
        target_grade_status?:
          | 'above'
          | 'below'
          | 'much_above'
          | 'much_below'
          | 'pending'
        /** @format uuid */
        teacher_id?: string
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<IdpTableStudentCoursesGrades[], T>, any>({
        path: `/grades/acc/idp-table/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags grades
     * @name GradesCustomGradesList
     * @request GET:/grades/custom-grades/
     * @secure
     */,
    gradesCustomGradesList: <T extends FetchKeys<CustomGradeType[]>>(
      query?: {
        /** @format uuid */
        course?: string
        grade_level?: number
        quarter?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CustomGradeType[], T>, any>({
        path: `/grades/custom-grades/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags grades
     * @name GradesHighSchoolGpaRetrieve
     * @request GET:/grades/high-school-gpa/{studentId}/
     * @secure
     */,
    gradesHighSchoolGpaRetrieve: <T extends FetchKeys<HighSchoolGPA>>(
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<HighSchoolGPA, T>, any>({
        path: `/grades/high-school-gpa/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags grades
     * @name GradesPeriodGpasRetrieve
     * @request GET:/grades/period-gpas/{studentId}/
     * @secure
     */,
    gradesPeriodGpasRetrieve: <T extends FetchKeys<StudentPeriodGPASummary>>(
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentPeriodGPASummary, T>, any>({
        path: `/grades/period-gpas/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags grades
     * @name GradesTargetGradeStatusRetrieve
     * @request GET:/grades/target-grade-status/
     * @secure
     */,
    gradesTargetGradeStatusRetrieve: <
      T extends FetchKeys<TargetGradeStatusResponse>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TargetGradeStatusResponse, T>, any>({
        path: `/grades/target-grade-status/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  health = {
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags health
     * @name HealthChecksList
     * @request GET:/health/checks/
     * @secure
     */
    healthChecksList: <T extends FetchKeys<PaginatedHealthCheckListList>>(
      query?: {
        /**
         * * `room` - room check
         * * `campus` - campus check
         */
        check_type?: 'campus' | 'room'
        /** @format date */
        date?: string
        house?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        sick_report?: string
        /** @format time */
        start_time?: string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedHealthCheckListList, T>, any>({
        path: `/health/checks/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags health
     * @name HealthChecksRetrieve
     * @request GET:/health/checks/{id}/
     * @secure
     */,
    healthChecksRetrieve: <T extends FetchKeys<HealthCheck>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<HealthCheck, T>, any>({
        path: `/health/checks/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags health
     * @name HealthChecksMedicinesList
     * @request GET:/health/checks/{id}/medicines/
     * @secure
     */,
    healthChecksMedicinesList: <T extends FetchKeys<MedicinesInHealthCheck[]>>(
      id: string,
      query?: {
        /**
         * * `room` - room check
         * * `campus` - campus check
         */
        check_type?: 'campus' | 'room'
        /** @format date */
        date?: string
        house?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string
        sick_report?: string
        /** @format time */
        start_time?: string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MedicinesInHealthCheck[], T>, any>({
        path: `/health/checks/${id}/medicines/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags health
     * @name HealthChecksOptionalMedicinesList
     * @request GET:/health/checks/{id}/optional-medicines/
     * @secure
     */,
    healthChecksOptionalMedicinesList: <
      T extends FetchKeys<OptionalMedicinesInHealthCheck[]>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<OptionalMedicinesInHealthCheck[], T>, any>({
        path: `/health/checks/${id}/optional-medicines/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags health
     * @name HealthChecksAvailableFiltersRetrieve
     * @request GET:/health/checks/available-filters/
     * @secure
     */,
    healthChecksAvailableFiltersRetrieve: <
      T extends FetchKeys<HealthCheckAvailableFilters>
    >(
      query?: {
        /**
         * * `room` - room check
         * * `campus` - campus check
         */
        check_type?: 'campus' | 'room'
        /** @format date */
        date?: string
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        house?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string
        sick_report?: string
        /** @format time */
        start_time?: string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<HealthCheckAvailableFilters, T>, any>({
        path: `/health/checks/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksCommentsList
     * @request GET:/health/checks/comments/
     * @secure
     */,
    healthChecksCommentsList: <T extends FetchKeys<HealthCheckComment[]>>(
      query?: {
        /** @format uuid */
        health_check?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<HealthCheckComment[], T>, any>({
        path: `/health/checks/comments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksCommentsCreate
     * @request POST:/health/checks/comments/
     * @secure
     */,
    healthChecksCommentsCreate: <T extends FetchKeys<HealthCheckComment>>(
      data: HealthCheckCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<HealthCheckComment, T>, any>({
        path: `/health/checks/comments/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksCommentsRetrieve
     * @request GET:/health/checks/comments/{healthCheckPk}/
     * @secure
     */,
    healthChecksCommentsRetrieve: <T extends FetchKeys<HealthCheckComment>>(
      healthCheckPk: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<HealthCheckComment, T>, any>({
        path: `/health/checks/comments/${healthCheckPk}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksCommentsUpdate
     * @request PUT:/health/checks/comments/{healthCheckPk}/
     * @secure
     */,
    healthChecksCommentsUpdate: <T extends FetchKeys<HealthCheckComment>>(
      healthCheckPk: string,
      data: HealthCheckCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<HealthCheckComment, T>, any>({
        path: `/health/checks/comments/${healthCheckPk}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksCommentsPartialUpdate
     * @request PATCH:/health/checks/comments/{healthCheckPk}/
     * @secure
     */,
    healthChecksCommentsPartialUpdate: <
      T extends FetchKeys<HealthCheckComment>
    >(
      healthCheckPk: string,
      data: PatchedHealthCheckCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<HealthCheckComment, T>, any>({
        path: `/health/checks/comments/${healthCheckPk}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksCommentsDestroy
     * @request DELETE:/health/checks/comments/{healthCheckPk}/
     * @secure
     */,
    healthChecksCommentsDestroy: <T extends FetchKeys<void>>(
      healthCheckPk: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/health/checks/comments/${healthCheckPk}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksConditionsList
     * @request GET:/health/checks/conditions/
     * @secure
     */,
    healthChecksConditionsList: <T extends FetchKeys<Condition[]>>(
      query?: {
        /** @format uuid */
        health_check?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Condition[], T>, any>({
        path: `/health/checks/conditions/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksConditionsCreate
     * @request POST:/health/checks/conditions/
     * @secure
     */,
    healthChecksConditionsCreate: <T extends FetchKeys<Condition>>(
      data: ConditionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Condition, T>, any>({
        path: `/health/checks/conditions/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksConditionsRetrieve
     * @request GET:/health/checks/conditions/{healthCheckPk}/
     * @secure
     */,
    healthChecksConditionsRetrieve: <T extends FetchKeys<Condition>>(
      healthCheckPk: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Condition, T>, any>({
        path: `/health/checks/conditions/${healthCheckPk}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksConditionsUpdate
     * @request PUT:/health/checks/conditions/{healthCheckPk}/
     * @secure
     */,
    healthChecksConditionsUpdate: <T extends FetchKeys<Condition>>(
      healthCheckPk: string,
      data: ConditionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Condition, T>, any>({
        path: `/health/checks/conditions/${healthCheckPk}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksConditionsPartialUpdate
     * @request PATCH:/health/checks/conditions/{healthCheckPk}/
     * @secure
     */,
    healthChecksConditionsPartialUpdate: <T extends FetchKeys<Condition>>(
      healthCheckPk: string,
      data: PatchedConditionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Condition, T>, any>({
        path: `/health/checks/conditions/${healthCheckPk}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksConditionsDestroy
     * @request DELETE:/health/checks/conditions/{healthCheckPk}/
     * @secure
     */,
    healthChecksConditionsDestroy: <T extends FetchKeys<void>>(
      healthCheckPk: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/health/checks/conditions/${healthCheckPk}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksDrinkGiveList
     * @request GET:/health/checks/drink-give/
     * @secure
     */,
    healthChecksDrinkGiveList: <T extends FetchKeys<DrinkGive[]>>(
      query?: {
        /** @format date-time */
        created?: string
        /** @format uuid */
        health_check?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DrinkGive[], T>, any>({
        path: `/health/checks/drink-give/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksDrinkGiveCreate
     * @request POST:/health/checks/drink-give/
     * @secure
     */,
    healthChecksDrinkGiveCreate: <T extends FetchKeys<DrinkGive>>(
      data: DrinkGiveRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DrinkGive, T>, any>({
        path: `/health/checks/drink-give/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksDrinkGiveRetrieve
     * @request GET:/health/checks/drink-give/{healthCheckPk}/
     * @secure
     */,
    healthChecksDrinkGiveRetrieve: <T extends FetchKeys<DrinkGive>>(
      healthCheckPk: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DrinkGive, T>, any>({
        path: `/health/checks/drink-give/${healthCheckPk}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksDrinkGiveUpdate
     * @request PUT:/health/checks/drink-give/{healthCheckPk}/
     * @secure
     */,
    healthChecksDrinkGiveUpdate: <T extends FetchKeys<DrinkGive>>(
      healthCheckPk: string,
      data: DrinkGiveRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DrinkGive, T>, any>({
        path: `/health/checks/drink-give/${healthCheckPk}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksDrinkGivePartialUpdate
     * @request PATCH:/health/checks/drink-give/{healthCheckPk}/
     * @secure
     */,
    healthChecksDrinkGivePartialUpdate: <T extends FetchKeys<DrinkGive>>(
      healthCheckPk: string,
      data: PatchedDrinkGiveRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DrinkGive, T>, any>({
        path: `/health/checks/drink-give/${healthCheckPk}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksDrinkGiveDestroy
     * @request DELETE:/health/checks/drink-give/{healthCheckPk}/
     * @secure
     */,
    healthChecksDrinkGiveDestroy: <T extends FetchKeys<void>>(
      healthCheckPk: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/health/checks/drink-give/${healthCheckPk}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksFoodGiveList
     * @request GET:/health/checks/food-give/
     * @secure
     */,
    healthChecksFoodGiveList: <T extends FetchKeys<FoodGive[]>>(
      query?: {
        /** @format date-time */
        created?: string
        /** @format uuid */
        health_check?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FoodGive[], T>, any>({
        path: `/health/checks/food-give/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksFoodGiveCreate
     * @request POST:/health/checks/food-give/
     * @secure
     */,
    healthChecksFoodGiveCreate: <T extends FetchKeys<FoodGive>>(
      data: FoodGiveRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FoodGive, T>, any>({
        path: `/health/checks/food-give/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksFoodGiveRetrieve
     * @request GET:/health/checks/food-give/{healthCheckPk}/
     * @secure
     */,
    healthChecksFoodGiveRetrieve: <T extends FetchKeys<FoodGive>>(
      healthCheckPk: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FoodGive, T>, any>({
        path: `/health/checks/food-give/${healthCheckPk}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksFoodGiveUpdate
     * @request PUT:/health/checks/food-give/{healthCheckPk}/
     * @secure
     */,
    healthChecksFoodGiveUpdate: <T extends FetchKeys<FoodGive>>(
      healthCheckPk: string,
      data: FoodGiveRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FoodGive, T>, any>({
        path: `/health/checks/food-give/${healthCheckPk}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksFoodGivePartialUpdate
     * @request PATCH:/health/checks/food-give/{healthCheckPk}/
     * @secure
     */,
    healthChecksFoodGivePartialUpdate: <T extends FetchKeys<FoodGive>>(
      healthCheckPk: string,
      data: PatchedFoodGiveRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FoodGive, T>, any>({
        path: `/health/checks/food-give/${healthCheckPk}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksFoodGiveDestroy
     * @request DELETE:/health/checks/food-give/{healthCheckPk}/
     * @secure
     */,
    healthChecksFoodGiveDestroy: <T extends FetchKeys<void>>(
      healthCheckPk: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/health/checks/food-give/${healthCheckPk}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksLogsList
     * @request GET:/health/checks/logs/
     * @secure
     */,
    healthChecksLogsList: <T extends FetchKeys<HealthCheckLog[]>>(
      query?: {
        /** @format date */
        date?: string
        health_check__sick_report?: string
        /** @format uuid */
        health_check__student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<HealthCheckLog[], T>, any>({
        path: `/health/checks/logs/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksLogsRetrieve
     * @request GET:/health/checks/logs/{id}/
     * @secure
     */,
    healthChecksLogsRetrieve: <T extends FetchKeys<HealthCheckLog>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<HealthCheckLog, T>, any>({
        path: `/health/checks/logs/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksMedicineGivenList
     * @request GET:/health/checks/medicine-given/
     * @secure
     */,
    healthChecksMedicineGivenList: <T extends FetchKeys<MedicineGiven[]>>(
      query?: {
        /** @format uuid */
        health_check?: string
        is_regular_medicine?: boolean

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MedicineGiven[], T>, any>({
        path: `/health/checks/medicine-given/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksMedicineGivenCreate
     * @request POST:/health/checks/medicine-given/
     * @secure
     */,
    healthChecksMedicineGivenCreate: <T extends FetchKeys<MedicineGiven>>(
      data: MedicineGivenRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MedicineGiven, T>, any>({
        path: `/health/checks/medicine-given/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksMedicineGivenRetrieve
     * @request GET:/health/checks/medicine-given/{id}/
     * @secure
     */,
    healthChecksMedicineGivenRetrieve: <T extends FetchKeys<MedicineGiven>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MedicineGiven, T>, any>({
        path: `/health/checks/medicine-given/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksMedicineGivenUpdate
     * @request PUT:/health/checks/medicine-given/{id}/
     * @secure
     */,
    healthChecksMedicineGivenUpdate: <T extends FetchKeys<MedicineGiven>>(
      id: string,
      data: MedicineGivenRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MedicineGiven, T>, any>({
        path: `/health/checks/medicine-given/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksMedicineGivenPartialUpdate
     * @request PATCH:/health/checks/medicine-given/{id}/
     * @secure
     */,
    healthChecksMedicineGivenPartialUpdate: <
      T extends FetchKeys<MedicineGiven>
    >(
      id: string,
      data: PatchedMedicineGivenRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MedicineGiven, T>, any>({
        path: `/health/checks/medicine-given/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksMedicineGivenDestroy
     * @request DELETE:/health/checks/medicine-given/{id}/
     * @secure
     */,
    healthChecksMedicineGivenDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/health/checks/medicine-given/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags health
     * @name HealthChecksMinAndMaxDateRetrieve
     * @request GET:/health/checks/min-and-max-date/
     * @secure
     */,
    healthChecksMinAndMaxDateRetrieve: <
      T extends FetchKeys<HealthCheckMinAndMaxDate>
    >(
      query?: {
        /**
         * * `room` - room check
         * * `campus` - campus check
         */
        check_type?: 'campus' | 'room'
        /** @format date */
        date?: string
        house?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string
        sick_report?: string
        /** @format time */
        start_time?: string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<HealthCheckMinAndMaxDate, T>, any>({
        path: `/health/checks/min-and-max-date/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags health
     * @name HealthChecksSlotsList
     * @request GET:/health/checks/slots/
     * @secure
     */,
    healthChecksSlotsList: <T extends FetchKeys<HealthCheckSlot[]>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<HealthCheckSlot[], T>, any>({
        path: `/health/checks/slots/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksTemperatureList
     * @request GET:/health/checks/temperature/
     * @secure
     */,
    healthChecksTemperatureList: <T extends FetchKeys<TemperatureCheck[]>>(
      query?: {
        /** @format uuid */
        health_check?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TemperatureCheck[], T>, any>({
        path: `/health/checks/temperature/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksTemperatureCreate
     * @request POST:/health/checks/temperature/
     * @secure
     */,
    healthChecksTemperatureCreate: <T extends FetchKeys<TemperatureCheck>>(
      data: TemperatureCheckRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TemperatureCheck, T>, any>({
        path: `/health/checks/temperature/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksTemperatureRetrieve
     * @request GET:/health/checks/temperature/{healthCheckPk}/
     * @secure
     */,
    healthChecksTemperatureRetrieve: <T extends FetchKeys<TemperatureCheck>>(
      healthCheckPk: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TemperatureCheck, T>, any>({
        path: `/health/checks/temperature/${healthCheckPk}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksTemperatureUpdate
     * @request PUT:/health/checks/temperature/{healthCheckPk}/
     * @secure
     */,
    healthChecksTemperatureUpdate: <T extends FetchKeys<TemperatureCheck>>(
      healthCheckPk: string,
      data: TemperatureCheckRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TemperatureCheck, T>, any>({
        path: `/health/checks/temperature/${healthCheckPk}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksTemperaturePartialUpdate
     * @request PATCH:/health/checks/temperature/{healthCheckPk}/
     * @secure
     */,
    healthChecksTemperaturePartialUpdate: <
      T extends FetchKeys<TemperatureCheck>
    >(
      healthCheckPk: string,
      data: PatchedTemperatureCheckRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TemperatureCheck, T>, any>({
        path: `/health/checks/temperature/${healthCheckPk}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthChecksTemperatureDestroy
     * @request DELETE:/health/checks/temperature/{healthCheckPk}/
     * @secure
     */,
    healthChecksTemperatureDestroy: <T extends FetchKeys<void>>(
      healthCheckPk: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/health/checks/temperature/${healthCheckPk}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthHospitalsList
     * @request GET:/health/hospitals/
     * @secure
     */,
    healthHospitalsList: <T extends FetchKeys<PaginatedHospitalList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedHospitalList, T>, any>({
        path: `/health/hospitals/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthHospitalsCreate
     * @request POST:/health/hospitals/
     * @secure
     */,
    healthHospitalsCreate: <T extends FetchKeys<Hospital>>(
      data: HospitalRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Hospital, T>, any>({
        path: `/health/hospitals/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthHospitalsRetrieve
     * @request GET:/health/hospitals/{id}/
     * @secure
     */,
    healthHospitalsRetrieve: <T extends FetchKeys<Hospital>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Hospital, T>, any>({
        path: `/health/hospitals/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthHospitalsUpdate
     * @request PUT:/health/hospitals/{id}/
     * @secure
     */,
    healthHospitalsUpdate: <T extends FetchKeys<Hospital>>(
      id: number,
      data: HospitalRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Hospital, T>, any>({
        path: `/health/hospitals/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthHospitalsPartialUpdate
     * @request PATCH:/health/hospitals/{id}/
     * @secure
     */,
    healthHospitalsPartialUpdate: <T extends FetchKeys<Hospital>>(
      id: number,
      data: PatchedHospitalRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Hospital, T>, any>({
        path: `/health/hospitals/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthHospitalsDestroy
     * @request DELETE:/health/hospitals/{id}/
     * @secure
     */,
    healthHospitalsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/health/hospitals/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthMedicineList
     * @request GET:/health/medicine/
     * @secure
     */,
    healthMedicineList: <T extends FetchKeys<PaginatedMedicineList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedMedicineList, T>, any>({
        path: `/health/medicine/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthMedicineCreate
     * @request POST:/health/medicine/
     * @secure
     */,
    healthMedicineCreate: <T extends FetchKeys<Medicine>>(
      data: MedicineRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Medicine, T>, any>({
        path: `/health/medicine/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthMedicinePlanMedicineInPlanList
     * @request GET:/health/medicine-plan/medicine-in-plan/
     * @secure
     */,
    healthMedicinePlanMedicineInPlanList: <
      T extends FetchKeys<PaginatedMedicineInPlanList>
    >(
      query?: {
        /** @format date */
        end_date_before?: string
        house?: string
        is_archived?: boolean
        is_temporary?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** @format date */
        scheduled_medicine_for?: string
        /** A search term. */
        search?: string
        /** @format date */
        start_date_after?: string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedMedicineInPlanList, T>, any>({
        path: `/health/medicine-plan/medicine-in-plan/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthMedicinePlanMedicineInPlanCreate
     * @request POST:/health/medicine-plan/medicine-in-plan/
     * @secure
     */,
    healthMedicinePlanMedicineInPlanCreate: <
      T extends FetchKeys<MedicineInPlan>
    >(
      data: MedicineInPlanRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MedicineInPlan, T>, any>({
        path: `/health/medicine-plan/medicine-in-plan/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthMedicinePlanMedicineInPlanUpdate
     * @request PUT:/health/medicine-plan/medicine-in-plan/{id}/
     * @secure
     */,
    healthMedicinePlanMedicineInPlanUpdate: <
      T extends FetchKeys<MedicineInPlanUpdate>
    >(
      id: string,
      data: MedicineInPlanUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MedicineInPlanUpdate, T>, any>({
        path: `/health/medicine-plan/medicine-in-plan/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthMedicinePlanMedicineInPlanPartialUpdate
     * @request PATCH:/health/medicine-plan/medicine-in-plan/{id}/
     * @secure
     */,
    healthMedicinePlanMedicineInPlanPartialUpdate: <
      T extends FetchKeys<MedicineInPlan>
    >(
      id: string,
      data: PatchedMedicineInPlanRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MedicineInPlan, T>, any>({
        path: `/health/medicine-plan/medicine-in-plan/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthMedicinePlanMedicineInPlanChangeEndDateUpdate
     * @request PUT:/health/medicine-plan/medicine-in-plan/{id}/change-end-date/
     * @secure
     */,
    healthMedicinePlanMedicineInPlanChangeEndDateUpdate: <
      T extends FetchKeys<ChangeEndDateMedicineInPlan>
    >(
      id: string,
      data: ChangeEndDateMedicineInPlanRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ChangeEndDateMedicineInPlan, T>, any>({
        path: `/health/medicine-plan/medicine-in-plan/${id}/change-end-date/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthMedicinePlanMedicineInPlanGetWarningsChangeEndDateCreate
     * @request POST:/health/medicine-plan/medicine-in-plan/{id}/get-warnings-change-end-date/
     * @secure
     */,
    healthMedicinePlanMedicineInPlanGetWarningsChangeEndDateCreate: <
      T extends FetchKeys<ChangeEndDateMedicineInPlan>
    >(
      id: string,
      data: ChangeEndDateMedicineInPlanRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ChangeEndDateMedicineInPlan, T>, any>({
        path: `/health/medicine-plan/medicine-in-plan/${id}/get-warnings-change-end-date/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthMedicinePlanMedicineInPlanAvailableFiltersRetrieve
     * @request GET:/health/medicine-plan/medicine-in-plan/available-filters/
     * @secure
     */,
    healthMedicinePlanMedicineInPlanAvailableFiltersRetrieve: <
      T extends FetchKeys<MedicineInPlanAvailableFilters>
    >(
      query?: {
        /** @format date */
        end_date_before?: string
        house?: string
        is_archived?: boolean
        is_temporary?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** @format date */
        scheduled_medicine_for?: string
        /** A search term. */
        search?: string
        /** @format date */
        start_date_after?: string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MedicineInPlanAvailableFilters, T>, any>({
        path: `/health/medicine-plan/medicine-in-plan/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthMedicinePlanMedicinePlanList
     * @request GET:/health/medicine-plan/medicine-plan/
     * @secure
     */,
    healthMedicinePlanMedicinePlanList: <
      T extends FetchKeys<PaginatedMedicinePlanList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        sick_report?: string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedMedicinePlanList, T>, any>({
        path: `/health/medicine-plan/medicine-plan/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthMedicinePlanMedicinePlanCreate
     * @request POST:/health/medicine-plan/medicine-plan/
     * @secure
     */,
    healthMedicinePlanMedicinePlanCreate: <T extends FetchKeys<MedicinePlan>>(
      data: MedicinePlanRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MedicinePlan, T>, any>({
        path: `/health/medicine-plan/medicine-plan/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthMedicineRetrieve
     * @request GET:/health/medicine/{id}/
     * @secure
     */,
    healthMedicineRetrieve: <T extends FetchKeys<Medicine>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Medicine, T>, any>({
        path: `/health/medicine/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthScheduledMedicineList
     * @request GET:/health/scheduled-medicine/
     * @secure
     */,
    healthScheduledMedicineList: <T extends FetchKeys<ScheduledMedicine[]>>(
      query?: {
        /** @format date */
        date?: string
        is_deleted?: boolean
        medicine_in_plan?: string
        /** @format uuid */
        medicine_in_plan__student?: string
        /** @format date-time */
        should_be_given_at_after?: string
        /** @format date-time */
        should_be_given_at_before?: string
        was_given?: boolean

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ScheduledMedicine[], T>, any>({
        path: `/health/scheduled-medicine/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags health
     * @name HealthScheduledMedicineToggleSkipCreate
     * @request POST:/health/scheduled-medicine/{id}/toggle-skip/
     * @secure
     */,
    healthScheduledMedicineToggleSkipCreate: <T extends FetchKeys<void>>(
      id: string,
      query?: {
        /**
         * * `parents` - Parents (e-mail)
         * * `medical` - Medical Liaison Team
         * @minLength 1
         */
        skip_reason?: 'parents' | 'medical'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, MessageResponseSchema>({
        path: `/health/scheduled-medicine/${id}/toggle-skip/`,
        method: 'POST',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
  }
  images = {
    /**
     * @description Use with ModelViewSet, or trigger invalidate_cache method manually
     *
     * @tags images
     * @name ImagesList
     * @request GET:/images/
     * @secure
     */
    imagesList: <T extends FetchKeys<PaginatedImageList>>(
      query?: {
        catalog?: number
        id?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedImageList, T>, any>({
        path: `/images/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with ModelViewSet, or trigger invalidate_cache method manually
     *
     * @tags images
     * @name ImagesCreate
     * @request POST:/images/
     * @secure
     */,
    imagesCreate: <T extends FetchKeys<Image>>(
      data: ImageRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Image, T>, any>({
        path: `/images/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with ModelViewSet, or trigger invalidate_cache method manually
     *
     * @tags images
     * @name ImagesRetrieve
     * @request GET:/images/{id}/
     * @secure
     */,
    imagesRetrieve: <T extends FetchKeys<Image>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Image, T>, any>({
        path: `/images/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with ModelViewSet, or trigger invalidate_cache method manually
     *
     * @tags images
     * @name ImagesUpdate
     * @request PUT:/images/{id}/
     * @secure
     */,
    imagesUpdate: <T extends FetchKeys<Image>>(
      id: string,
      data: ImageRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Image, T>, any>({
        path: `/images/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with ModelViewSet, or trigger invalidate_cache method manually
     *
     * @tags images
     * @name ImagesPartialUpdate
     * @request PATCH:/images/{id}/
     * @secure
     */,
    imagesPartialUpdate: <T extends FetchKeys<Image>>(
      id: string,
      data: PatchedImageRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Image, T>, any>({
        path: `/images/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with ModelViewSet, or trigger invalidate_cache method manually
     *
     * @tags images
     * @name ImagesDestroy
     * @request DELETE:/images/{id}/
     * @secure
     */,
    imagesDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/images/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags images
     * @name ImagesCatalogsList
     * @request GET:/images/catalogs/
     * @secure
     */,
    imagesCatalogsList: <T extends FetchKeys<PaginatedCatalogList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCatalogList, T>, any>({
        path: `/images/catalogs/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags images
     * @name ImagesCatalogsCreate
     * @request POST:/images/catalogs/
     * @secure
     */,
    imagesCatalogsCreate: <T extends FetchKeys<Catalog>>(
      data: CatalogRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Catalog, T>, any>({
        path: `/images/catalogs/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags images
     * @name ImagesCatalogsRetrieve
     * @request GET:/images/catalogs/{id}/
     * @secure
     */,
    imagesCatalogsRetrieve: <T extends FetchKeys<Catalog>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Catalog, T>, any>({
        path: `/images/catalogs/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags images
     * @name ImagesCatalogsUpdate
     * @request PUT:/images/catalogs/{id}/
     * @secure
     */,
    imagesCatalogsUpdate: <T extends FetchKeys<Catalog>>(
      id: number,
      data: CatalogRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Catalog, T>, any>({
        path: `/images/catalogs/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags images
     * @name ImagesCatalogsPartialUpdate
     * @request PATCH:/images/catalogs/{id}/
     * @secure
     */,
    imagesCatalogsPartialUpdate: <T extends FetchKeys<Catalog>>(
      id: number,
      data: PatchedCatalogRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Catalog, T>, any>({
        path: `/images/catalogs/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags images
     * @name ImagesCatalogsDestroy
     * @request DELETE:/images/catalogs/{id}/
     * @secure
     */,
    imagesCatalogsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/images/catalogs/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
  }
  importer = {
    /**
     * No description
     *
     * @tags importer
     * @name ImporterCourseConstraintsRetrieve
     * @request GET:/importer/course-constraints/
     * @secure
     */
    importerCourseConstraintsRetrieve: <T extends FetchKeys<string>>(
      query: {
        /**
         * * `ccu_ath` - ccu_ath
         * * `acd_add` - acd_add
         * @minLength 1
         */
        export_type: 'ccu_ath' | 'acd_add'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<string, T>, any>({
        path: `/importer/course-constraints/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags importer
     * @name ImporterCourseConstraintsCreate
     * @request POST:/importer/course-constraints/
     * @secure
     */,
    importerCourseConstraintsCreate: <
      T extends FetchKeys<ComparisonTableRow[]>
    >(
      data: BaseCSVFileRequest,
      query?: {
        /** @default true */
        return_only_changed?: boolean
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ComparisonTableRow[], T>, any>({
        path: `/importer/course-constraints/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags importer
     * @name ImporterCourseConstraintsApplyCreate
     * @request POST:/importer/course-constraints/apply/
     * @secure
     */,
    importerCourseConstraintsApplyCreate: <T extends FetchKeys<ApplyChanges>>(
      data: ComparisonTableRowRequest[],
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ApplyChanges, T>, any>({
        path: `/importer/course-constraints/apply/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
  }
  infoboards = {
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsAnalyticsFeedClickCreate
     * @request POST:/infoboards/analytics/feed-click/
     * @secure
     */
    infoboardsAnalyticsFeedClickCreate: <T extends FetchKeys<FeedClick>>(
      data: FeedClickRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FeedClick, T>, any>({
        path: `/infoboards/analytics/feed-click/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsAnalyticsFeedSurfTimeCreate
     * @request POST:/infoboards/analytics/feed-surf-time/
     * @secure
     */,
    infoboardsAnalyticsFeedSurfTimeCreate: <T extends FetchKeys<FeedSurfTime>>(
      data: FeedSurfTimeRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FeedSurfTime, T>, any>({
        path: `/infoboards/analytics/feed-surf-time/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags infoboards
     * @name InfoboardsBoardsList
     * @request GET:/infoboards/boards/
     * @secure
     */,
    infoboardsBoardsList: <T extends FetchKeys<PaginatedAdminBoardList>>(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /**
         * * `unpublished` - Unpublished
         * * `published` - Published
         * * `scheduled` - Scheduled
         * * `draft` - Draft
         */
        status?: 'draft' | 'published' | 'scheduled' | 'unpublished'
        widget_type?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAdminBoardList, T>, any>({
        path: `/infoboards/boards/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags infoboards
     * @name InfoboardsBoardsCreate
     * @request POST:/infoboards/boards/
     * @secure
     */,
    infoboardsBoardsCreate: <T extends FetchKeys<AdminBoard>>(
      data: AdminBoardRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminBoard, T>, any>({
        path: `/infoboards/boards/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags infoboards
     * @name InfoboardsBoardsRetrieve
     * @request GET:/infoboards/boards/{id}/
     * @secure
     */,
    infoboardsBoardsRetrieve: <T extends FetchKeys<AdminBoard>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminBoard, T>, any>({
        path: `/infoboards/boards/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags infoboards
     * @name InfoboardsBoardsUpdate
     * @request PUT:/infoboards/boards/{id}/
     * @secure
     */,
    infoboardsBoardsUpdate: <T extends FetchKeys<AdminBoard>>(
      id: string,
      data: AdminBoardRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminBoard, T>, any>({
        path: `/infoboards/boards/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags infoboards
     * @name InfoboardsBoardsPartialUpdate
     * @request PATCH:/infoboards/boards/{id}/
     * @secure
     */,
    infoboardsBoardsPartialUpdate: <T extends FetchKeys<AdminBoard>>(
      id: string,
      data: PatchedAdminBoardRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminBoard, T>, any>({
        path: `/infoboards/boards/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags infoboards
     * @name InfoboardsBoardsDestroy
     * @request DELETE:/infoboards/boards/{id}/
     * @secure
     */,
    infoboardsBoardsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/infoboards/boards/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags infoboards
     * @name InfoboardsBoardsPublishUpdate
     * @request PUT:/infoboards/boards/{id}/publish/
     * @secure
     */,
    infoboardsBoardsPublishUpdate: <T extends FetchKeys<PublishBoard>>(
      id: string,
      data: PublishBoardRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublishBoard, T>, any>({
        path: `/infoboards/boards/${id}/publish/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags infoboards
     * @name InfoboardsBoardsPublishPartialUpdate
     * @request PATCH:/infoboards/boards/{id}/publish/
     * @secure
     */,
    infoboardsBoardsPublishPartialUpdate: <T extends FetchKeys<PublishBoard>>(
      id: string,
      data: PatchedPublishBoardRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublishBoard, T>, any>({
        path: `/infoboards/boards/${id}/publish/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Remove the selected student, parent or infoboards destinations. Other destinations keep their publication state. Repeated removal succeeds.
     *
     * @tags infoboards
     * @name InfoboardsBoardsUnpublishCreate
     * @request POST:/infoboards/boards/{id}/unpublish/
     * @secure
     */,
    infoboardsBoardsUnpublishCreate: <T extends FetchKeys<void>>(
      id: string,
      data: UnpublishBoardRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, void>({
        path: `/infoboards/boards/${id}/unpublish/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags infoboards
     * @name InfoboardsBoardsAutomatedWidgetsStatusList
     * @request GET:/infoboards/boards/automated-widgets-status/
     * @secure
     */,
    infoboardsBoardsAutomatedWidgetsStatusList: <
      T extends FetchKeys<AutomatedWidgetsStatus[]>
    >(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string
        /**
         * * `unpublished` - Unpublished
         * * `published` - Published
         * * `scheduled` - Scheduled
         * * `draft` - Draft
         */
        status?: 'draft' | 'published' | 'scheduled' | 'unpublished'
        widget_type?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AutomatedWidgetsStatus[], T>, any>({
        path: `/infoboards/boards/automated-widgets-status/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags infoboards
     * @name InfoboardsBoardsChangeOrderCreate
     * @request POST:/infoboards/boards/change-order/
     * @secure
     */,
    infoboardsBoardsChangeOrderCreate: <T extends FetchKeys<void>>(
      data: ChangeOrderRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/infoboards/boards/change-order/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags infoboards
     * @name InfoboardsBoardsCountRetrieve
     * @request GET:/infoboards/boards/count/
     * @secure
     */,
    infoboardsBoardsCountRetrieve: <T extends FetchKeys<AdminBoard>>(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string
        /**
         * * `unpublished` - Unpublished
         * * `published` - Published
         * * `scheduled` - Scheduled
         * * `draft` - Draft
         */
        status?: 'draft' | 'published' | 'scheduled' | 'unpublished'
        widget_type?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminBoard, T>, any>({
        path: `/infoboards/boards/count/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags infoboards
     * @name InfoboardsBoardsDraftsList
     * @request GET:/infoboards/boards/drafts/
     * @secure
     */,
    infoboardsBoardsDraftsList: <
      T extends FetchKeys<PaginatedAdminBoardDraftListList>
    >(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAdminBoardDraftListList, T>, any>({
        path: `/infoboards/boards/drafts/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags infoboards
     * @name InfoboardsBoardsDraftsCreate
     * @request POST:/infoboards/boards/drafts/
     * @secure
     */,
    infoboardsBoardsDraftsCreate: <T extends FetchKeys<AdminBoardDraft>>(
      data: AdminBoardDraftRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminBoardDraft, T>, any>({
        path: `/infoboards/boards/drafts/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags infoboards
     * @name InfoboardsBoardsDraftsRetrieve
     * @request GET:/infoboards/boards/drafts/{id}/
     * @secure
     */,
    infoboardsBoardsDraftsRetrieve: <T extends FetchKeys<AdminBoardDraft>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminBoardDraft, T>, any>({
        path: `/infoboards/boards/drafts/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags infoboards
     * @name InfoboardsBoardsDraftsUpdate
     * @request PUT:/infoboards/boards/drafts/{id}/
     * @secure
     */,
    infoboardsBoardsDraftsUpdate: <T extends FetchKeys<AdminBoardDraft>>(
      id: string,
      data: AdminBoardDraftRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminBoardDraft, T>, any>({
        path: `/infoboards/boards/drafts/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags infoboards
     * @name InfoboardsBoardsDraftsPartialUpdate
     * @request PATCH:/infoboards/boards/drafts/{id}/
     * @secure
     */,
    infoboardsBoardsDraftsPartialUpdate: <T extends FetchKeys<AdminBoardDraft>>(
      id: string,
      data: PatchedAdminBoardDraftRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminBoardDraft, T>, any>({
        path: `/infoboards/boards/drafts/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags infoboards
     * @name InfoboardsBoardsDraftsDestroy
     * @request DELETE:/infoboards/boards/drafts/{id}/
     * @secure
     */,
    infoboardsBoardsDraftsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/infoboards/boards/drafts/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsExternalJournalUpdatedCreate
     * @request POST:/infoboards/external-journal-updated/
     * @secure
     */,
    infoboardsExternalJournalUpdatedCreate: <
      T extends FetchKeys<ExternalJournal>
    >(
      data: ExternalJournalRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ExternalJournal, T>, any>({
        path: `/infoboards/external-journal-updated/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsFeedList
     * @request GET:/infoboards/feed/
     * @secure
     */,
    infoboardsFeedList: <T extends FetchKeys<PaginatedAdminFeedList>>(
      query?: {
        name?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /**
         * * `student` - Student
         * * `parent` - Parent
         */
        show_in_app?: 'parent' | 'student'
        status?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAdminFeedList, T>, any>({
        path: `/infoboards/feed/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsFeedDestroy
     * @request DELETE:/infoboards/feed/{id}/
     * @secure
     */,
    infoboardsFeedDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/infoboards/feed/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsFeedChangeOrderCreate
     * @request POST:/infoboards/feed/change-order/
     * @secure
     */,
    infoboardsFeedChangeOrderCreate: <T extends FetchKeys<void>>(
      data: ChangeOrderRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/infoboards/feed/change-order/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * @description Handler method for HTTP 'OPTIONS' request.
     *
     * @tags infoboards
     * @name InfoboardsFeedOptionsRetrieve
     * @request GET:/infoboards/feed/options/
     * @secure
     */,
    infoboardsFeedOptionsRetrieve: <T extends FetchKeys<AdminFeedOptions>>(
      query?: {
        name?: string
        /** A search term. */
        search?: string
        /**
         * * `student` - Student
         * * `parent` - Parent
         */
        show_in_app?: 'parent' | 'student'
        status?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminFeedOptions, T>, any>({
        path: `/infoboards/feed/options/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags infoboards
     * @name InfoboardsMessagesList
     * @request GET:/infoboards/messages/
     * @secure
     */,
    infoboardsMessagesList: <T extends FetchKeys<PaginatedAdminMessageList>>(
      query?: {
        /** @format date */
        created?: string
        is_created_manually?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /**
         * * `alert` - Alert
         * * `notice` - Notice
         */
        type?: 'alert' | 'notice'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAdminMessageList, T>, any>({
        path: `/infoboards/messages/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags infoboards
     * @name InfoboardsMessagesCreate
     * @request POST:/infoboards/messages/
     * @secure
     */,
    infoboardsMessagesCreate: <T extends FetchKeys<AdminMessage>>(
      data: AdminMessageRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminMessage, T>, any>({
        path: `/infoboards/messages/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags infoboards
     * @name InfoboardsMessagesRetrieve
     * @request GET:/infoboards/messages/{id}/
     * @secure
     */,
    infoboardsMessagesRetrieve: <T extends FetchKeys<AdminMessage>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminMessage, T>, any>({
        path: `/infoboards/messages/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags infoboards
     * @name InfoboardsMessagesUpdate
     * @request PUT:/infoboards/messages/{id}/
     * @secure
     */,
    infoboardsMessagesUpdate: <T extends FetchKeys<AdminMessage>>(
      id: number,
      data: AdminMessageRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminMessage, T>, any>({
        path: `/infoboards/messages/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags infoboards
     * @name InfoboardsMessagesPartialUpdate
     * @request PATCH:/infoboards/messages/{id}/
     * @secure
     */,
    infoboardsMessagesPartialUpdate: <T extends FetchKeys<AdminMessage>>(
      id: number,
      data: PatchedAdminMessageRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminMessage, T>, any>({
        path: `/infoboards/messages/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags infoboards
     * @name InfoboardsMessagesDestroy
     * @request DELETE:/infoboards/messages/{id}/
     * @secure
     */,
    infoboardsMessagesDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/infoboards/messages/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags infoboards
     * @name InfoboardsPublicBoardsList
     * @request GET:/infoboards/public/boards/
     * @secure
     */,
    infoboardsPublicBoardsList: <T extends FetchKeys<PublicBoard[]>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicBoard[], T>, any>({
        path: `/infoboards/public/boards/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags infoboards
     * @name InfoboardsPublicBoardsRetrieve
     * @request GET:/infoboards/public/boards/{id}/
     * @secure
     */,
    infoboardsPublicBoardsRetrieve: <T extends FetchKeys<PublicBoard>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicBoard, T>, any>({
        path: `/infoboards/public/boards/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsPublicJournalArticlesList
     * @request GET:/infoboards/public/journal-articles/
     * @secure
     */,
    infoboardsPublicJournalArticlesList: <
      T extends FetchKeys<PublicRecentJournalArticle[]>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicRecentJournalArticle[], T>, any>({
        path: `/infoboards/public/journal-articles/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags infoboards
     * @name InfoboardsPublicMessagesList
     * @request GET:/infoboards/public/messages/
     * @secure
     */,
    infoboardsPublicMessagesList: <T extends FetchKeys<PublicMessage[]>>(
      query?: {
        /**
         * * `alert` - Alert
         * * `notice` - Notice
         */
        type?: 'alert' | 'notice'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicMessage[], T>, any>({
        path: `/infoboards/public/messages/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags infoboards
     * @name InfoboardsPublicMessagesRetrieve
     * @request GET:/infoboards/public/messages/{id}/
     * @secure
     */,
    infoboardsPublicMessagesRetrieve: <T extends FetchKeys<PublicMessage>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicMessage, T>, any>({
        path: `/infoboards/public/messages/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsPublicNextLessonsList
     * @request GET:/infoboards/public/next-lessons/
     * @secure
     */,
    infoboardsPublicNextLessonsList: <
      T extends FetchKeys<PaginatedPublicNextLessonList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedPublicNextLessonList, T>, any>({
        path: `/infoboards/public/next-lessons/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsPublicUpdateStatusRetrieve
     * @request GET:/infoboards/public/update-status/
     * @secure
     */,
    infoboardsPublicUpdateStatusRetrieve: <
      T extends FetchKeys<PublicUpdateStatus>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicUpdateStatus, T>, any>({
        path: `/infoboards/public/update-status/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsReactionsToggleCreate
     * @request POST:/infoboards/reactions/toggle/
     * @secure
     */,
    infoboardsReactionsToggleCreate: <T extends FetchKeys<void>>(
      data: ToggleReactionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/infoboards/reactions/toggle/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsSettingsRetrieve
     * @request GET:/infoboards/settings/
     * @secure
     */,
    infoboardsSettingsRetrieve: <T extends FetchKeys<AdminInfoboardsSettings>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminInfoboardsSettings, T>, any>({
        path: `/infoboards/settings/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsSettingsUpdate
     * @request PUT:/infoboards/settings/
     * @secure
     */,
    infoboardsSettingsUpdate: <T extends FetchKeys<AdminInfoboardsSettings>>(
      data: AdminInfoboardsSettingsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminInfoboardsSettings, T>, any>({
        path: `/infoboards/settings/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsSettingsPartialUpdate
     * @request PATCH:/infoboards/settings/
     * @secure
     */,
    infoboardsSettingsPartialUpdate: <
      T extends FetchKeys<AdminInfoboardsSettings>
    >(
      data: PatchedAdminInfoboardsSettingsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminInfoboardsSettings, T>, any>({
        path: `/infoboards/settings/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags infoboards
     * @name InfoboardsStudentContentList
     * @request GET:/infoboards/student-content/
     * @secure
     */,
    infoboardsStudentContentList: <
      T extends FetchKeys<PaginatedAdminStudentContentList>
    >(
      query?: {
        /**
         * Ordering
         *
         * * `created` - Created
         * * `-created` - Created (descending)
         * * `modified` - Modified
         * * `-modified` - Modified (descending)
         * * `published_at` - Published at
         * * `-published_at` - Published at (descending)
         * * `display_to` - Display to
         * * `-display_to` - Display to (descending)
         * * `status` - Status
         * * `-status` - Status (descending)
         */
        ordering?: (
          | '-created'
          | '-display_to'
          | '-modified'
          | '-published_at'
          | '-status'
          | 'created'
          | 'display_to'
          | 'modified'
          | 'published_at'
          | 'status'
        )[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /**
         * * `pending` - Pending
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `deleted` - Deleted
         * * `archived` - Archived
         */
        status?: 'approved' | 'archived' | 'deleted' | 'pending' | 'rejected'
        /**
         * * `StudentMysteryLink` - None
         * * `StudentAnnouncement` - None
         * * `StudentPic` - None
         */
        student_content_type?:
          | 'StudentAnnouncement'
          | 'StudentMysteryLink'
          | 'StudentPic'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAdminStudentContentList, T>, any>({
        path: `/infoboards/student-content/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags infoboards
     * @name InfoboardsStudentContentRetrieve
     * @request GET:/infoboards/student-content/{id}/
     * @secure
     */,
    infoboardsStudentContentRetrieve: <
      T extends FetchKeys<AdminStudentContent>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminStudentContent, T>, any>({
        path: `/infoboards/student-content/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags infoboards
     * @name InfoboardsStudentContentDestroy
     * @request DELETE:/infoboards/student-content/{id}/
     * @secure
     */,
    infoboardsStudentContentDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/infoboards/student-content/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags infoboards
     * @name InfoboardsStudentContentApprovePartialUpdate
     * @request PATCH:/infoboards/student-content/{id}/approve/
     * @secure
     */,
    infoboardsStudentContentApprovePartialUpdate: <
      T extends FetchKeys<ApproveStudentContent>
    >(
      id: string,
      data: PatchedApproveStudentContentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ApproveStudentContent, T>, any>({
        path: `/infoboards/student-content/${id}/approve/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags infoboards
     * @name InfoboardsStudentContentRejectPartialUpdate
     * @request PATCH:/infoboards/student-content/{id}/reject/
     * @secure
     */,
    infoboardsStudentContentRejectPartialUpdate: <
      T extends FetchKeys<AdminStudentContent>
    >(
      id: string,
      data: PatchedAdminStudentContentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminStudentContent, T>, any>({
        path: `/infoboards/student-content/${id}/reject/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags infoboards
     * @name InfoboardsStudentContentPendingItemsRetrieve
     * @request GET:/infoboards/student-content/pending-items/
     * @secure
     */,
    infoboardsStudentContentPendingItemsRetrieve: <
      T extends FetchKeys<PendingStudentContentItems>
    >(
      query?: {
        /**
         * Ordering
         *
         * * `created` - Created
         * * `-created` - Created (descending)
         * * `modified` - Modified
         * * `-modified` - Modified (descending)
         * * `published_at` - Published at
         * * `-published_at` - Published at (descending)
         * * `display_to` - Display to
         * * `-display_to` - Display to (descending)
         * * `status` - Status
         * * `-status` - Status (descending)
         */
        ordering?: (
          | '-created'
          | '-display_to'
          | '-modified'
          | '-published_at'
          | '-status'
          | 'created'
          | 'display_to'
          | 'modified'
          | 'published_at'
          | 'status'
        )[]
        /** A search term. */
        search?: string
        /**
         * * `pending` - Pending
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `deleted` - Deleted
         * * `archived` - Archived
         */
        status?: 'approved' | 'archived' | 'deleted' | 'pending' | 'rejected'
        /**
         * * `StudentMysteryLink` - None
         * * `StudentAnnouncement` - None
         * * `StudentPic` - None
         */
        student_content_type?:
          | 'StudentAnnouncement'
          | 'StudentMysteryLink'
          | 'StudentPic'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PendingStudentContentItems, T>, any>({
        path: `/infoboards/student-content/pending-items/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsWidgetsList
     * @request GET:/infoboards/widgets/
     * @secure
     */,
    infoboardsWidgetsList: <T extends FetchKeys<PaginatedAdminWidgetList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        polymorphic_ctype?: number
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAdminWidgetList, T>, any>({
        path: `/infoboards/widgets/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsWidgetsCreate
     * @request POST:/infoboards/widgets/
     * @secure
     */,
    infoboardsWidgetsCreate: <T extends FetchKeys<AdminWidget>>(
      data: AdminWidgetRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminWidget, T>, any>({
        path: `/infoboards/widgets/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsWidgetsRetrieve
     * @request GET:/infoboards/widgets/{id}/
     * @secure
     */,
    infoboardsWidgetsRetrieve: <T extends FetchKeys<AdminWidget>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminWidget, T>, any>({
        path: `/infoboards/widgets/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsWidgetsUpdate
     * @request PUT:/infoboards/widgets/{id}/
     * @secure
     */,
    infoboardsWidgetsUpdate: <T extends FetchKeys<AdminWidget>>(
      id: string,
      data: AdminWidgetRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminWidget, T>, any>({
        path: `/infoboards/widgets/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsWidgetsPartialUpdate
     * @request PATCH:/infoboards/widgets/{id}/
     * @secure
     */,
    infoboardsWidgetsPartialUpdate: <T extends FetchKeys<AdminWidget>>(
      id: string,
      data: PatchedAdminWidgetRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminWidget, T>, any>({
        path: `/infoboards/widgets/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsWidgetsDestroy
     * @request DELETE:/infoboards/widgets/{id}/
     * @secure
     */,
    infoboardsWidgetsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/infoboards/widgets/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsWidgetsAvailableRetrieve
     * @request GET:/infoboards/widgets/available/
     * @secure
     */,
    infoboardsWidgetsAvailableRetrieve: <T extends FetchKeys<AdminWidget>>(
      query?: {
        polymorphic_ctype?: number
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminWidget, T>, any>({
        path: `/infoboards/widgets/available/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags infoboards
     * @name InfoboardsWidgetsTypesList
     * @request GET:/infoboards/widgets/types/
     * @secure
     */,
    infoboardsWidgetsTypesList: <T extends FetchKeys<AdminTypes[]>>(
      query?: {
        polymorphic_ctype?: number
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminTypes[], T>, any>({
        path: `/infoboards/widgets/types/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  integrations = {
    /**
     * No description
     *
     * @tags integrations
     * @name IntegrationsDeadlineChaserStudentsList
     * @request GET:/integrations/deadline-chaser/students/
     * @secure
     */
    integrationsDeadlineChaserStudentsList: <
      T extends FetchKeys<DeadlineChaserStudent[]>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DeadlineChaserStudent[], T>, any>({
        path: `/integrations/deadline-chaser/students/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags integrations
     * @name IntegrationsDeadlineChaserTokenCreate
     * @request POST:/integrations/deadline-chaser/token/
     * @secure
     */,
    integrationsDeadlineChaserTokenCreate: <
      T extends FetchKeys<DeadlineChaserToken>
    >(
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DeadlineChaserToken, T>, any>({
        path: `/integrations/deadline-chaser/token/`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params
      })
  }
  internships = {
    /**
     * No description
     *
     * @tags internships
     * @name InternshipsList
     * @request GET:/internships/
     * @secure
     */
    internshipsList: <T extends FetchKeys<PaginatedInternshipList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedInternshipList, T>, any>({
        path: `/internships/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags internships
     * @name InternshipsRetrieve
     * @request GET:/internships/{id}/
     * @secure
     */,
    internshipsRetrieve: <T extends FetchKeys<Internship>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Internship, T>, any>({
        path: `/internships/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags internships
     * @name InternshipsStudentsList
     * @request GET:/internships/students/{studentId}/
     * @secure
     */,
    internshipsStudentsList: <
      T extends FetchKeys<PaginatedStudentInternshipList>
    >(
      studentId: string,
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentInternshipList, T>, any>({
        path: `/internships/students/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags internships
     * @name InternshipsStudentsUpdate
     * @request PUT:/internships/students/{studentId}/
     * @secure
     */,
    internshipsStudentsUpdate: <T extends FetchKeys<StudentInternshipsUpdate>>(
      studentId: string,
      data: StudentInternshipsUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentInternshipsUpdate, T>, any>({
        path: `/internships/students/${studentId}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags internships
     * @name InternshipsStudentsPartialUpdate
     * @request PATCH:/internships/students/{studentId}/
     * @secure
     */,
    internshipsStudentsPartialUpdate: <
      T extends FetchKeys<StudentInternshipsUpdate>
    >(
      studentId: string,
      data: PatchedStudentInternshipsUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentInternshipsUpdate, T>, any>({
        path: `/internships/students/${studentId}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
  }
  kitchen = {
    /**
     * No description
     *
     * @tags kitchen
     * @name KitchenMealAttendanceRetrieve
     * @request GET:/kitchen/meal-attendance/
     * @secure
     */
    kitchenMealAttendanceRetrieve: <
      T extends FetchKeys<MealAttendanceResponse>
    >(
      query?: {
        /** @format date */
        date?: string
        diet_type?: 'l1' | 'l2' | 'l3' | 'l4'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MealAttendanceResponse, T>, any>({
        path: `/kitchen/meal-attendance/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags kitchen
     * @name KitchenMealPreferencesStudentsCreate
     * @request POST:/kitchen/meal-preferences/students/
     * @secure
     */,
    kitchenMealPreferencesStudentsCreate: <
      T extends FetchKeys<StudentMealPreference>
    >(
      data: StudentMealPreferenceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentMealPreference, T>, any>({
        path: `/kitchen/meal-preferences/students/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags kitchen
     * @name KitchenMealPreferencesStudentsHistoryList
     * @request GET:/kitchen/meal-preferences/students/{studentId}/history
     * @secure
     */,
    kitchenMealPreferencesStudentsHistoryList: <
      T extends FetchKeys<StudentMealPreferenceHistory[]>
    >(
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentMealPreferenceHistory[], T>, any>({
        path: `/kitchen/meal-preferences/students/${studentId}/history`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags kitchen
     * @name KitchenMealPreferencesStudentsChoicesList
     * @request GET:/kitchen/meal-preferences/students/choices/
     * @secure
     */,
    kitchenMealPreferencesStudentsChoicesList: <
      T extends FetchKeys<MealPreferenceChoice[]>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MealPreferenceChoice[], T>, any>({
        path: `/kitchen/meal-preferences/students/choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags kitchen
     * @name KitchenMenuList
     * @request GET:/kitchen/menu/
     * @secure
     */,
    kitchenMenuList: <T extends FetchKeys<PaginatedDayMenuList>>(
      query?: {
        /** @format date */
        date?: string
        is_filled?: boolean
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        week?: number
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedDayMenuList, T>, any>({
        path: `/kitchen/menu/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags kitchen
     * @name KitchenMenuCreate
     * @request POST:/kitchen/menu/
     * @secure
     */,
    kitchenMenuCreate: <T extends FetchKeys<DayMenu>>(
      data: DayMenuRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DayMenu, T>, any>({
        path: `/kitchen/menu/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags kitchen
     * @name KitchenMenuRetrieve
     * @request GET:/kitchen/menu/{id}/
     * @secure
     */,
    kitchenMenuRetrieve: <T extends FetchKeys<DayMenu>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DayMenu, T>, any>({
        path: `/kitchen/menu/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags kitchen
     * @name KitchenMenuUpdate
     * @request PUT:/kitchen/menu/{id}/
     * @secure
     */,
    kitchenMenuUpdate: <T extends FetchKeys<DayMenu>>(
      id: number,
      data: DayMenuRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DayMenu, T>, any>({
        path: `/kitchen/menu/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags kitchen
     * @name KitchenMenuPartialUpdate
     * @request PATCH:/kitchen/menu/{id}/
     * @secure
     */,
    kitchenMenuPartialUpdate: <T extends FetchKeys<DayMenu>>(
      id: number,
      data: PatchedDayMenuRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DayMenu, T>, any>({
        path: `/kitchen/menu/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags kitchen
     * @name KitchenMenuIsPublishedRetrieve
     * @request GET:/kitchen/menu/is-published/{week}/{year}/
     * @secure
     */,
    kitchenMenuIsPublishedRetrieve: <T extends FetchKeys<DayMenuIsPublished>>(
      week: string,
      year: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DayMenuIsPublished, T>, any>({
        path: `/kitchen/menu/is-published/${week}/${year}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags kitchen
     * @name KitchenMenuPublishCreate
     * @request POST:/kitchen/menu/publish/{week}/{year}/
     * @secure
     */,
    kitchenMenuPublishCreate: <T extends FetchKeys<void>>(
      week: string,
      year: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/kitchen/menu/publish/${week}/${year}/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags kitchen
     * @name KitchenMenuUnpublishCreate
     * @request POST:/kitchen/menu/unpublish/{week}/{year}/
     * @secure
     */,
    kitchenMenuUnpublishCreate: <T extends FetchKeys<void>>(
      week: string,
      year: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/kitchen/menu/unpublish/${week}/${year}/`,
        method: 'POST',
        secure: true,
        ...params
      })
  }
  laundry = {
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryCatalogRetrieve
     * @request GET:/laundry/catalog/
     * @secure
     */
    laundryCatalogRetrieve: <T extends FetchKeys<LaundryCatalog>>(
      query?: {
        /**
         * * `normal` - Normal
         * * `delicate` - Delicate
         * * `dry` - Dry
         */
        laundry_type?: 'delicate' | 'dry' | 'normal'
        order_id?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LaundryCatalog, T>, any>({
        path: `/laundry/catalog/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryCatalogPricingList
     * @request GET:/laundry/catalog/pricing/
     * @secure
     */,
    laundryCatalogPricingList: <T extends FetchKeys<LaundryCatalogPricing>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LaundryCatalogPricing, T>, any>({
        path: `/laundry/catalog/pricing/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryCatalogPricingUpdate
     * @request PUT:/laundry/catalog/pricing/
     * @secure
     */,
    laundryCatalogPricingUpdate: <
      T extends FetchKeys<LaundryCatalogPricingCategory[]>
    >(
      data: LaundryCatalogPricingCategoryRequest[],
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LaundryCatalogPricingCategory[], T>, any>({
        path: `/laundry/catalog/pricing/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryHousesList
     * @request GET:/laundry/houses/
     * @secure
     */,
    laundryHousesList: <T extends FetchKeys<LaundryHouse[]>>(
      query?: {
        /** Filter houses by related laundry QR code ID. */
        qr_code_id?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LaundryHouse[], T>, any>({
        path: `/laundry/houses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryLaundryStaffCatalogRetrieve
     * @request GET:/laundry/laundry-staff/catalog/
     * @secure
     */,
    laundryLaundryStaffCatalogRetrieve: <T extends FetchKeys<LaundryCatalog>>(
      query?: {
        /**
         * * `normal` - Normal
         * * `delicate` - Delicate
         * * `dry` - Dry
         */
        laundry_type?: 'delicate' | 'dry' | 'normal'
        order_id?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LaundryCatalog, T>, any>({
        path: `/laundry/laundry-staff/catalog/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryLaundryStaffOrdersRetrieve
     * @request GET:/laundry/laundry-staff/orders/{id}/
     * @secure
     */,
    laundryLaundryStaffOrdersRetrieve: <
      T extends FetchKeys<LaundryStaffOrderDetail>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LaundryStaffOrderDetail, T>, any>({
        path: `/laundry/laundry-staff/orders/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryLaundryStaffOrdersApproveCreate
     * @request POST:/laundry/laundry-staff/orders/{id}/approve/
     * @secure
     */,
    laundryLaundryStaffOrdersApproveCreate: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/laundry/laundry-staff/orders/${id}/approve/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryLaundryStaffOrdersFinishCreate
     * @request POST:/laundry/laundry-staff/orders/{id}/finish/
     * @secure
     */,
    laundryLaundryStaffOrdersFinishCreate: <
      T extends FetchKeys<LaundryStaffOrderFinish>
    >(
      id: number,
      data: LaundryStaffOrderFinishRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LaundryStaffOrderFinish, T>, any>({
        path: `/laundry/laundry-staff/orders/${id}/finish/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryLaundryStaffOrdersRejectCreate
     * @request POST:/laundry/laundry-staff/orders/{id}/reject/
     * @secure
     */,
    laundryLaundryStaffOrdersRejectCreate: <
      T extends FetchKeys<LaundryStaffOrderReject>
    >(
      id: number,
      data: LaundryStaffOrderRejectRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LaundryStaffOrderReject, T>, any>({
        path: `/laundry/laundry-staff/orders/${id}/reject/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryLaundryStaffOrdersStaffVersionCreate
     * @request POST:/laundry/laundry-staff/orders/{id}/staff-version/
     * @secure
     */,
    laundryLaundryStaffOrdersStaffVersionCreate: <
      T extends FetchKeys<LaundryStaffOrderVersionCreate>
    >(
      id: number,
      data: LaundryStaffOrderVersionCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LaundryStaffOrderVersionCreate, T>, any>({
        path: `/laundry/laundry-staff/orders/${id}/staff-version/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryLaundryStaffOrdersStaffVersionUpdate
     * @request PUT:/laundry/laundry-staff/orders/{id}/staff-version/
     * @secure
     */,
    laundryLaundryStaffOrdersStaffVersionUpdate: <
      T extends FetchKeys<LaundryStaffOrderVersionUpdate>
    >(
      id: number,
      data: LaundryStaffOrderVersionUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LaundryStaffOrderVersionUpdate, T>, any>({
        path: `/laundry/laundry-staff/orders/${id}/staff-version/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags laundry
     * @name LaundryOrdersList
     * @request GET:/laundry/orders/
     * @secure
     */,
    laundryOrdersList: <T extends FetchKeys<PaginatedLaundryOrderListList>>(
      query?: {
        house?: number
        is_claim_submitted?: boolean
        /** When `true`, return current orders (pickup, in_laundry, claim, completed, rejected). When `false`, return archived orders (closed, claim_closed, rejected marked as closed). */
        is_current?: boolean
        is_laundry_edited?: boolean
        /**
         * Ordering
         *
         * * `id` - Id
         * * `-id` - Id (descending)
         * * `student_name` - Student name
         * * `-student_name` - Student name (descending)
         * * `house_name` - House name
         * * `-house_name` - House name (descending)
         * * `pickup_date` - Pickup date
         * * `-pickup_date` - Pickup date (descending)
         * * `delivery_date` - Delivery date
         * * `-delivery_date` - Delivery date (descending)
         * * `status` - Status
         * * `-status` - Status (descending)
         * * `is_laundry_edited` - Is laundry edited
         * * `-is_laundry_edited` - Is laundry edited (descending)
         * * `is_claim_submitted` - Is claim submitted
         * * `-is_claim_submitted` - Is claim submitted (descending)
         * * `created` - Created
         * * `-created` - Created (descending)
         * * `closed_at` - Closed at
         * * `-closed_at` - Closed at (descending)
         */
        ordering?: (
          | '-closed_at'
          | '-created'
          | '-delivery_date'
          | '-house_name'
          | '-id'
          | '-is_claim_submitted'
          | '-is_laundry_edited'
          | '-pickup_date'
          | '-status'
          | '-student_name'
          | 'closed_at'
          | 'created'
          | 'delivery_date'
          | 'house_name'
          | 'id'
          | 'is_claim_submitted'
          | 'is_laundry_edited'
          | 'pickup_date'
          | 'status'
          | 'student_name'
        )[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        search?: string
        /**
         * * `pickup` - Pickup
         * * `in_laundry` - In laundry
         * * `completed` - Completed
         * * `rejected` - Rejected
         * * `claim` - Claim
         * * `closed` - Closed
         * * `claim_closed` - Claim closed
         */
        status?:
          | 'claim'
          | 'claim_closed'
          | 'closed'
          | 'completed'
          | 'in_laundry'
          | 'pickup'
          | 'rejected'
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedLaundryOrderListList, T>, any>({
        path: `/laundry/orders/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags laundry
     * @name LaundryOrdersCreate
     * @request POST:/laundry/orders/
     * @secure
     */,
    laundryOrdersCreate: <T extends FetchKeys<LaundryOrderDetail>>(
      data: LaundryOrderCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LaundryOrderDetail, T>, any>({
        path: `/laundry/orders/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryOrdersClaimCreate
     * @request POST:/laundry/orders/{orderId}/claim/
     * @secure
     */,
    laundryOrdersClaimCreate: <T extends FetchKeys<void>>(
      orderId: number,
      data: LaundryOrderClaimRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/laundry/orders/${orderId}/claim/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryOrdersClaimApproveCreate
     * @request POST:/laundry/orders/{orderId}/claim/approve/
     * @secure
     */,
    laundryOrdersClaimApproveCreate: <T extends FetchKeys<void>>(
      orderId: number,
      data: LaundryOrderClaimRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/laundry/orders/${orderId}/claim/approve/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryOrdersClaimCloseCreate
     * @request POST:/laundry/orders/{orderId}/claim/close/
     * @secure
     */,
    laundryOrdersClaimCloseCreate: <T extends FetchKeys<void>>(
      orderId: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/laundry/orders/${orderId}/claim/close/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryOrdersClaimRejectCreate
     * @request POST:/laundry/orders/{orderId}/claim/reject/
     * @secure
     */,
    laundryOrdersClaimRejectCreate: <T extends FetchKeys<void>>(
      orderId: number,
      data: LaundryOrderClaimRejectRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/laundry/orders/${orderId}/claim/reject/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * @description Revoke an accepted claim on a laundry order in CLAIM status, changing order status back to COMPLETED.
     *
     * @tags laundry
     * @name LaundryOrdersClaimRevokeCreate
     * @request POST:/laundry/orders/{orderId}/claim/revoke/
     * @secure
     */,
    laundryOrdersClaimRevokeCreate: <T extends FetchKeys<void>>(
      orderId: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/laundry/orders/${orderId}/claim/revoke/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags laundry
     * @name LaundryOrdersRetrieve
     * @request GET:/laundry/orders/{id}/
     * @secure
     */,
    laundryOrdersRetrieve: <T extends FetchKeys<LaundryOrderDetail>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LaundryOrderDetail, T>, any>({
        path: `/laundry/orders/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags laundry
     * @name LaundryOrdersAvailableFiltersRetrieve
     * @request GET:/laundry/orders/available-filters/
     * @secure
     */,
    laundryOrdersAvailableFiltersRetrieve: <
      T extends FetchKeys<LaundryOrderAvailableFilters>
    >(
      query?: {
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        house?: number
        is_claim_submitted?: boolean
        /** When `true`, return current orders (pickup, in_laundry, claim, completed, rejected). When `false`, return archived orders (closed, claim_closed, rejected marked as closed). */
        is_current?: boolean
        is_laundry_edited?: boolean
        /**
         * Ordering
         *
         * * `id` - Id
         * * `-id` - Id (descending)
         * * `student_name` - Student name
         * * `-student_name` - Student name (descending)
         * * `house_name` - House name
         * * `-house_name` - House name (descending)
         * * `pickup_date` - Pickup date
         * * `-pickup_date` - Pickup date (descending)
         * * `delivery_date` - Delivery date
         * * `-delivery_date` - Delivery date (descending)
         * * `status` - Status
         * * `-status` - Status (descending)
         * * `is_laundry_edited` - Is laundry edited
         * * `-is_laundry_edited` - Is laundry edited (descending)
         * * `is_claim_submitted` - Is claim submitted
         * * `-is_claim_submitted` - Is claim submitted (descending)
         * * `created` - Created
         * * `-created` - Created (descending)
         * * `closed_at` - Closed at
         * * `-closed_at` - Closed at (descending)
         */
        ordering?: (
          | '-closed_at'
          | '-created'
          | '-delivery_date'
          | '-house_name'
          | '-id'
          | '-is_claim_submitted'
          | '-is_laundry_edited'
          | '-pickup_date'
          | '-status'
          | '-student_name'
          | 'closed_at'
          | 'created'
          | 'delivery_date'
          | 'house_name'
          | 'id'
          | 'is_claim_submitted'
          | 'is_laundry_edited'
          | 'pickup_date'
          | 'status'
          | 'student_name'
        )[]
        search?: string
        /**
         * * `pickup` - Pickup
         * * `in_laundry` - In laundry
         * * `completed` - Completed
         * * `rejected` - Rejected
         * * `claim` - Claim
         * * `closed` - Closed
         * * `claim_closed` - Claim closed
         */
        status?:
          | 'claim'
          | 'claim_closed'
          | 'closed'
          | 'completed'
          | 'in_laundry'
          | 'pickup'
          | 'rejected'
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LaundryOrderAvailableFilters, T>, any>({
        path: `/laundry/orders/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags laundry
     * @name LaundryOrdersCounterRetrieve
     * @request GET:/laundry/orders/counter/
     * @secure
     */,
    laundryOrdersCounterRetrieve: <T extends FetchKeys<LaundryOrderCounter>>(
      query?: {
        house?: number
        is_claim_submitted?: boolean
        /** When `true`, return current orders (pickup, in_laundry, claim, completed, rejected). When `false`, return archived orders (closed, claim_closed, rejected marked as closed). */
        is_current?: boolean
        is_laundry_edited?: boolean
        /**
         * Ordering
         *
         * * `id` - Id
         * * `-id` - Id (descending)
         * * `student_name` - Student name
         * * `-student_name` - Student name (descending)
         * * `house_name` - House name
         * * `-house_name` - House name (descending)
         * * `pickup_date` - Pickup date
         * * `-pickup_date` - Pickup date (descending)
         * * `delivery_date` - Delivery date
         * * `-delivery_date` - Delivery date (descending)
         * * `status` - Status
         * * `-status` - Status (descending)
         * * `is_laundry_edited` - Is laundry edited
         * * `-is_laundry_edited` - Is laundry edited (descending)
         * * `is_claim_submitted` - Is claim submitted
         * * `-is_claim_submitted` - Is claim submitted (descending)
         * * `created` - Created
         * * `-created` - Created (descending)
         * * `closed_at` - Closed at
         * * `-closed_at` - Closed at (descending)
         */
        ordering?: (
          | '-closed_at'
          | '-created'
          | '-delivery_date'
          | '-house_name'
          | '-id'
          | '-is_claim_submitted'
          | '-is_laundry_edited'
          | '-pickup_date'
          | '-status'
          | '-student_name'
          | 'closed_at'
          | 'created'
          | 'delivery_date'
          | 'house_name'
          | 'id'
          | 'is_claim_submitted'
          | 'is_laundry_edited'
          | 'pickup_date'
          | 'status'
          | 'student_name'
        )[]
        search?: string
        /**
         * * `pickup` - Pickup
         * * `in_laundry` - In laundry
         * * `completed` - Completed
         * * `rejected` - Rejected
         * * `claim` - Claim
         * * `closed` - Closed
         * * `claim_closed` - Claim closed
         */
        status?:
          | 'claim'
          | 'claim_closed'
          | 'closed'
          | 'completed'
          | 'in_laundry'
          | 'pickup'
          | 'rejected'
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LaundryOrderCounter, T>, any>({
        path: `/laundry/orders/counter/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags laundry
     * @name LaundryQrCodesList
     * @request GET:/laundry/qr-codes/
     * @secure
     */,
    laundryQrCodesList: <T extends FetchKeys<PaginatedLaundryQRCodeListList>>(
      query?: {
        /**
         * * `free` - Free
         * * `in_use` - In use
         */
        availability?: ('free' | 'in_use')[]
        house?: number[]
        /**
         * Ordering
         *
         * * `id` - Id
         * * `-id` - Id (descending)
         * * `house` - House
         * * `-house` - House (descending)
         * * `room` - Room
         * * `-room` - Room (descending)
         * * `availability` - Availability
         * * `-availability` - Availability (descending)
         * * `created` - Created
         * * `-created` - Created (descending)
         * * `order_id` - Order id
         * * `-order_id` - Order id (descending)
         */
        ordering?: (
          | '-availability'
          | '-created'
          | '-house'
          | '-id'
          | '-order_id'
          | '-room'
          | 'availability'
          | 'created'
          | 'house'
          | 'id'
          | 'order_id'
          | 'room'
        )[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        room?: number[]
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedLaundryQRCodeListList, T>, any>({
        path: `/laundry/qr-codes/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags laundry
     * @name LaundryQrCodesCreate
     * @request POST:/laundry/qr-codes/
     * @secure
     */,
    laundryQrCodesCreate: <T extends FetchKeys<void>>(
      data: LaundryQRCodeBulkCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/laundry/qr-codes/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryQrCodesActiveOrderRetrieve
     * @request GET:/laundry/qr-codes/{code}/active-order/
     * @secure
     */,
    laundryQrCodesActiveOrderRetrieve: <
      T extends FetchKeys<LaundryQRCodeActiveOrderLookupResponse>
    >(
      code: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LaundryQRCodeActiveOrderLookupResponse, T>, void>({
        path: `/laundry/qr-codes/${code}/active-order/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags laundry
     * @name LaundryQrCodesValidateOrderCreate
     * @request POST:/laundry/qr-codes/{id}/validate-order/
     * @secure
     */,
    laundryQrCodesValidateOrderCreate: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, void>({
        path: `/laundry/qr-codes/${id}/validate-order/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags laundry
     * @name LaundryQrCodesAvailableFiltersRetrieve
     * @request GET:/laundry/qr-codes/available-filters/
     * @secure
     */,
    laundryQrCodesAvailableFiltersRetrieve: <
      T extends FetchKeys<LaundryQRCodeAvailableFilters>
    >(
      query?: {
        /**
         * * `free` - Free
         * * `in_use` - In use
         */
        availability?: ('free' | 'in_use')[]
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        house?: number[]
        /**
         * Ordering
         *
         * * `id` - Id
         * * `-id` - Id (descending)
         * * `house` - House
         * * `-house` - House (descending)
         * * `room` - Room
         * * `-room` - Room (descending)
         * * `availability` - Availability
         * * `-availability` - Availability (descending)
         * * `created` - Created
         * * `-created` - Created (descending)
         * * `order_id` - Order id
         * * `-order_id` - Order id (descending)
         */
        ordering?: (
          | '-availability'
          | '-created'
          | '-house'
          | '-id'
          | '-order_id'
          | '-room'
          | 'availability'
          | 'created'
          | 'house'
          | 'id'
          | 'order_id'
          | 'room'
        )[]
        room?: number[]
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LaundryQRCodeAvailableFilters, T>, any>({
        path: `/laundry/qr-codes/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags laundry
     * @name LaundryQrCodesPrintPreviewRetrieve
     * @request GET:/laundry/qr-codes/print-preview/
     * @secure
     */,
    laundryQrCodesPrintPreviewRetrieve: <T extends FetchKeys<any>>(
      query: {
        /** @maxItems 100 */
        id: number[]
        /** @default false */
        inline?: boolean

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<any, T>, any>({
        path: `/laundry/qr-codes/print-preview/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryStudentCatalogRetrieve
     * @request GET:/laundry/student/catalog/
     * @secure
     */,
    laundryStudentCatalogRetrieve: <T extends FetchKeys<LaundryCatalog>>(
      query?: {
        /**
         * * `normal` - Normal
         * * `delicate` - Delicate
         * * `dry` - Dry
         */
        laundry_type?: 'delicate' | 'dry' | 'normal'
        order_id?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LaundryCatalog, T>, any>({
        path: `/laundry/student/catalog/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryStudentQrCodesOrderLookupRetrieve
     * @request GET:/laundry/student/qr-codes/{code}/order-lookup/
     * @secure
     */,
    laundryStudentQrCodesOrderLookupRetrieve: <
      T extends FetchKeys<StudentLaundryOrderQRCodeLookup>
    >(
      code: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentLaundryOrderQRCodeLookup, T>, void>({
        path: `/laundry/student/qr-codes/${code}/order-lookup/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryStudentStudentLaundryOrdersList
     * @request GET:/laundry/student/student-laundry-orders/
     * @secure
     */,
    laundryStudentStudentLaundryOrdersList: <
      T extends FetchKeys<PaginatedStudentLaundryOrderListList>
    >(
      query?: {
        /** When `true`, return current orders (pickup, in_laundry, claim, completed, rejected). When `false`, return archived orders (closed, claim_closed, rejected marked as closed). */
        is_current?: boolean
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentLaundryOrderListList, T>, any>({
        path: `/laundry/student/student-laundry-orders/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryStudentStudentLaundryOrdersCreate
     * @request POST:/laundry/student/student-laundry-orders/
     * @secure
     */,
    laundryStudentStudentLaundryOrdersCreate: <
      T extends FetchKeys<StudentLaundryOrder>
    >(
      data: StudentLaundryOrderCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentLaundryOrder, T>, any>({
        path: `/laundry/student/student-laundry-orders/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryStudentStudentLaundryOrdersRetrieve
     * @request GET:/laundry/student/student-laundry-orders/{id}/
     * @secure
     */,
    laundryStudentStudentLaundryOrdersRetrieve: <
      T extends FetchKeys<StudentLaundryOrderDetail>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentLaundryOrderDetail, T>, any>({
        path: `/laundry/student/student-laundry-orders/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryStudentStudentLaundryOrdersUpdate
     * @request PUT:/laundry/student/student-laundry-orders/{id}/
     * @secure
     */,
    laundryStudentStudentLaundryOrdersUpdate: <
      T extends FetchKeys<StudentLaundryOrderDetail>
    >(
      id: number,
      data: StudentLaundryOrderVersionUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentLaundryOrderDetail, T>, any>({
        path: `/laundry/student/student-laundry-orders/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags laundry
     * @name LaundryStudentStudentLaundryOrdersSubmitClaimCreate
     * @request POST:/laundry/student/student-laundry-orders/{id}/submit-claim/
     * @secure
     */,
    laundryStudentStudentLaundryOrdersSubmitClaimCreate: <
      T extends FetchKeys<StudentLaundryOrderClaimCreate>
    >(
      id: number,
      data: StudentLaundryOrderClaimCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentLaundryOrderClaimCreate, T>, any>({
        path: `/laundry/student/student-laundry-orders/${id}/submit-claim/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
  }
  leaves = {
    /**
     * No description
     *
     * @tags leaves
     * @name LeavesChecksCreate
     * @request POST:/leaves/checks/
     * @secure
     */
    leavesChecksCreate: <T extends FetchKeys<LeaveCheck>>(
      data: LeaveCheckRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LeaveCheck, T>, any>({
        path: `/leaves/checks/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags leaves
     * @name LeavesChecksRetrieve
     * @request GET:/leaves/checks/{id}/
     * @secure
     */,
    leavesChecksRetrieve: <T extends FetchKeys<LeaveCheck>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LeaveCheck, T>, any>({
        path: `/leaves/checks/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags leaves
     * @name LeavesChecksUpdate
     * @request PUT:/leaves/checks/{id}/
     * @secure
     */,
    leavesChecksUpdate: <T extends FetchKeys<LeaveCheck>>(
      id: string,
      data: LeaveCheckRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LeaveCheck, T>, any>({
        path: `/leaves/checks/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags leaves
     * @name LeavesChecksPartialUpdate
     * @request PATCH:/leaves/checks/{id}/
     * @secure
     */,
    leavesChecksPartialUpdate: <T extends FetchKeys<LeaveCheck>>(
      id: string,
      data: PatchedLeaveCheckRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LeaveCheck, T>, any>({
        path: `/leaves/checks/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags leaves
     * @name LeavesChecksCompleteCreate
     * @request POST:/leaves/checks/{id}/complete/
     * @secure
     */,
    leavesChecksCompleteCreate: <T extends FetchKeys<LeaveCheckComplete>>(
      id: string,
      data: LeaveCheckCompleteRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LeaveCheckComplete, T>, any>({
        path: `/leaves/checks/${id}/complete/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags leaves
     * @name LeavesRequestsList
     * @request GET:/leaves/requests/
     * @secure
     */,
    leavesRequestsList: <T extends FetchKeys<PaginatedLeaveRequestListList>>(
      query?: {
        current_year_only?: boolean
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        house?: string
        is_completed?: string
        is_driver_needed?: boolean
        ordering?:
          | '-check_in_date'
          | '-check_out_date'
          | '-created'
          | '-student__first_name'
          | 'check_in_date'
          | 'check_out_date'
          | 'created'
          | 'student__first_name'
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        school_year?: string
        show_todays?: boolean
        /**
         * * `tentative` - Pending guardian
         * * `confirmed_contact_person` - Pending academic
         * * `confirmed_academic` - Pending boarding
         * * `changed` - Changed
         * * `confirmed` - Approved
         * * `declined` - Rejected
         * * `declined_contact_person` - Rejected by guardian
         * * `declined_academic` - Rejected by academic
         * * `canceled` - Canceled by student
         * * `expired` - Expired
         */
        status?:
          | 'canceled'
          | 'changed'
          | 'confirmed'
          | 'confirmed_academic'
          | 'confirmed_contact_person'
          | 'declined'
          | 'declined_academic'
          | 'declined_contact_person'
          | 'expired'
          | 'tentative'
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedLeaveRequestListList, T>, any>({
        path: `/leaves/requests/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags leaves
     * @name LeavesRequestsCreate
     * @request POST:/leaves/requests/
     * @secure
     */,
    leavesRequestsCreate: <T extends FetchKeys<LeaveRequest>>(
      data: LeaveRequestRequest,
      query?: {
        ordering?:
          | '-check_in_date'
          | '-check_out_date'
          | '-created'
          | '-student__first_name'
          | 'check_in_date'
          | 'check_out_date'
          | 'created'
          | 'student__first_name'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LeaveRequest, T>, any>({
        path: `/leaves/requests/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags leaves
     * @name LeavesRequestsRetrieve
     * @request GET:/leaves/requests/{id}/
     * @secure
     */,
    leavesRequestsRetrieve: <T extends FetchKeys<LeaveRequest>>(
      id: string,
      query?: {
        ordering?:
          | '-check_in_date'
          | '-check_out_date'
          | '-created'
          | '-student__first_name'
          | 'check_in_date'
          | 'check_out_date'
          | 'created'
          | 'student__first_name'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LeaveRequest, T>, any>({
        path: `/leaves/requests/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags leaves
     * @name LeavesRequestsUpdate
     * @request PUT:/leaves/requests/{id}/
     * @secure
     */,
    leavesRequestsUpdate: <T extends FetchKeys<LeaveRequest>>(
      id: string,
      data: LeaveRequestRequest,
      query?: {
        ordering?:
          | '-check_in_date'
          | '-check_out_date'
          | '-created'
          | '-student__first_name'
          | 'check_in_date'
          | 'check_out_date'
          | 'created'
          | 'student__first_name'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LeaveRequest, T>, any>({
        path: `/leaves/requests/${id}/`,
        method: 'PUT',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags leaves
     * @name LeavesRequestsPartialUpdate
     * @request PATCH:/leaves/requests/{id}/
     * @secure
     */,
    leavesRequestsPartialUpdate: <T extends FetchKeys<LeaveRequest>>(
      id: string,
      data: PatchedLeaveRequestRequest,
      query?: {
        ordering?:
          | '-check_in_date'
          | '-check_out_date'
          | '-created'
          | '-student__first_name'
          | 'check_in_date'
          | 'check_out_date'
          | 'created'
          | 'student__first_name'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LeaveRequest, T>, any>({
        path: `/leaves/requests/${id}/`,
        method: 'PATCH',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description This endpoint is deprecated. Please use /admin/students/clashes/
     *
     * @tags leaves
     * @name LeavesRequestsClashesRetrieve
     * @request GET:/leaves/requests/{id}/clashes/
     * @deprecated
     * @secure
     */,
    leavesRequestsClashesRetrieve: <T extends FetchKeys<StudentClashes>>(
      id: string,
      query?: {
        ordering?:
          | '-check_in_date'
          | '-check_out_date'
          | '-created'
          | '-student__first_name'
          | 'check_in_date'
          | 'check_out_date'
          | 'created'
          | 'student__first_name'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentClashes, T>, any>({
        path: `/leaves/requests/${id}/clashes/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags leaves
     * @name LeavesRequestsReviewAsParentCreate
     * @request POST:/leaves/requests/{id}/review-as-parent/
     * @secure
     */,
    leavesRequestsReviewAsParentCreate: <
      T extends FetchKeys<ReviewLeaveRequestAsParent>
    >(
      id: string,
      data: ReviewLeaveRequestAsParentRequest,
      query?: {
        ordering?:
          | '-check_in_date'
          | '-check_out_date'
          | '-created'
          | '-student__first_name'
          | 'check_in_date'
          | 'check_out_date'
          | 'created'
          | 'student__first_name'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ReviewLeaveRequestAsParent, T>, any>({
        path: `/leaves/requests/${id}/review-as-parent/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags leaves
     * @name LeavesRequestsReviewByAcademicCreate
     * @request POST:/leaves/requests/{id}/review-by-academic/
     * @secure
     */,
    leavesRequestsReviewByAcademicCreate: <
      T extends FetchKeys<ReviewLeaveRequest>
    >(
      id: string,
      data: ReviewLeaveRequestRequest,
      query?: {
        ordering?:
          | '-check_in_date'
          | '-check_out_date'
          | '-created'
          | '-student__first_name'
          | 'check_in_date'
          | 'check_out_date'
          | 'created'
          | 'student__first_name'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ReviewLeaveRequest, T>, any>({
        path: `/leaves/requests/${id}/review-by-academic/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags leaves
     * @name LeavesRequestsReviewByBoardingCreate
     * @request POST:/leaves/requests/{id}/review-by-boarding/
     * @secure
     */,
    leavesRequestsReviewByBoardingCreate: <
      T extends FetchKeys<ReviewLeaveRequest>
    >(
      id: string,
      data: ReviewLeaveRequestRequest,
      query?: {
        ordering?:
          | '-check_in_date'
          | '-check_out_date'
          | '-created'
          | '-student__first_name'
          | 'check_in_date'
          | 'check_out_date'
          | 'created'
          | 'student__first_name'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ReviewLeaveRequest, T>, any>({
        path: `/leaves/requests/${id}/review-by-boarding/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags leaves
     * @name LeavesRequestsAvailableFiltersRetrieve
     * @request GET:/leaves/requests/available-filters/
     * @secure
     */,
    leavesRequestsAvailableFiltersRetrieve: <
      T extends FetchKeys<LeaveRequestAvailableFilters>
    >(
      query?: {
        current_year_only?: boolean
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        house?: string
        is_completed?: string
        is_driver_needed?: boolean
        ordering?:
          | '-check_in_date'
          | '-check_out_date'
          | '-created'
          | '-student__first_name'
          | 'check_in_date'
          | 'check_out_date'
          | 'created'
          | 'student__first_name'
        school_year?: string
        show_todays?: boolean
        /**
         * * `tentative` - Pending guardian
         * * `confirmed_contact_person` - Pending academic
         * * `confirmed_academic` - Pending boarding
         * * `changed` - Changed
         * * `confirmed` - Approved
         * * `declined` - Rejected
         * * `declined_contact_person` - Rejected by guardian
         * * `declined_academic` - Rejected by academic
         * * `canceled` - Canceled by student
         * * `expired` - Expired
         */
        status?:
          | 'canceled'
          | 'changed'
          | 'confirmed'
          | 'confirmed_academic'
          | 'confirmed_contact_person'
          | 'declined'
          | 'declined_academic'
          | 'declined_contact_person'
          | 'expired'
          | 'tentative'
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LeaveRequestAvailableFilters, T>, any>({
        path: `/leaves/requests/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags leaves
     * @name LeavesRequestsStatusesList
     * @request GET:/leaves/requests/statuses/
     * @secure
     */,
    leavesRequestsStatusesList: <T extends FetchKeys<StringOption[]>>(
      query?: {
        current_year_only?: boolean
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        house?: string
        is_completed?: string
        is_driver_needed?: boolean
        ordering?:
          | '-check_in_date'
          | '-check_out_date'
          | '-created'
          | '-student__first_name'
          | 'check_in_date'
          | 'check_out_date'
          | 'created'
          | 'student__first_name'
        school_year?: string
        show_todays?: boolean
        /**
         * * `tentative` - Pending guardian
         * * `confirmed_contact_person` - Pending academic
         * * `confirmed_academic` - Pending boarding
         * * `changed` - Changed
         * * `confirmed` - Approved
         * * `declined` - Rejected
         * * `declined_contact_person` - Rejected by guardian
         * * `declined_academic` - Rejected by academic
         * * `canceled` - Canceled by student
         * * `expired` - Expired
         */
        status?:
          | 'canceled'
          | 'changed'
          | 'confirmed'
          | 'confirmed_academic'
          | 'confirmed_contact_person'
          | 'declined'
          | 'declined_academic'
          | 'declined_contact_person'
          | 'expired'
          | 'tentative'
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/leaves/requests/statuses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  lessonCancellation = {
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags lesson-cancellation
     * @name LessonCancellationCancelLessonList
     * @request GET:/lesson-cancellation/cancel-lesson/
     * @secure
     */
    lessonCancellationCancelLessonList: <
      T extends FetchKeys<PaginatedCancelLessonList>
    >(
      query?: {
        /**
         * * `TEACHER_HAS_EXAM` - Artisan has exam
         * * `COVER_TEACHER_HAS_EXAM` - Cover artisan has exam
         * * `TEACHER_HAS_SCHOOL_PROGRAMME` - Artisan has school programme
         * * `COVER_TEACHER_HAS_SCHOOL_PROGRAMME` - Cover artisan has school programme
         * * `ALL_STUDENTS_CANCELLED` - All students have cancelled lesson
         * * `SCHOOL_BREAK_PERIOD` - Lesson is cancelled due to school's break
         */
        cancellation_reason?:
          | 'ALL_STUDENTS_CANCELLED'
          | 'COVER_TEACHER_HAS_EXAM'
          | 'COVER_TEACHER_HAS_SCHOOL_PROGRAMME'
          | 'SCHOOL_BREAK_PERIOD'
          | 'TEACHER_HAS_EXAM'
          | 'TEACHER_HAS_SCHOOL_PROGRAMME'
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter?: number
        /** A search term. */
        search?: string
        teacher?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCancelLessonList, T>, any>({
        path: `/lesson-cancellation/cancel-lesson/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags lesson-cancellation
     * @name LessonCancellationCancelLessonAvailableFiltersRetrieve
     * @request GET:/lesson-cancellation/cancel-lesson/available-filters/
     * @secure
     */,
    lessonCancellationCancelLessonAvailableFiltersRetrieve: <
      T extends FetchKeys<CancelLessonsAvailableFiltersResponse>
    >(
      query?: {
        /**
         * * `TEACHER_HAS_EXAM` - Artisan has exam
         * * `COVER_TEACHER_HAS_EXAM` - Cover artisan has exam
         * * `TEACHER_HAS_SCHOOL_PROGRAMME` - Artisan has school programme
         * * `COVER_TEACHER_HAS_SCHOOL_PROGRAMME` - Cover artisan has school programme
         * * `ALL_STUDENTS_CANCELLED` - All students have cancelled lesson
         * * `SCHOOL_BREAK_PERIOD` - Lesson is cancelled due to school's break
         */
        cancellation_reason?:
          | 'ALL_STUDENTS_CANCELLED'
          | 'COVER_TEACHER_HAS_EXAM'
          | 'COVER_TEACHER_HAS_SCHOOL_PROGRAMME'
          | 'SCHOOL_BREAK_PERIOD'
          | 'TEACHER_HAS_EXAM'
          | 'TEACHER_HAS_SCHOOL_PROGRAMME'
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        quarter?: number
        /** A search term. */
        search?: string
        teacher?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CancelLessonsAvailableFiltersResponse, T>, any>({
        path: `/lesson-cancellation/cancel-lesson/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  logs = {
    /**
     * No description
     *
     * @tags logs
     * @name LogsList
     * @request GET:/logs/
     * @secure
     */
    logsList: <T extends FetchKeys<PaginatedLogEntryList>>(
      query?: {
        /**
         * * `0` - create
         * * `1` - update
         * * `2` - delete
         */
        action?: '0' | '1' | '2' | null
        /** @format uuid */
        actor?: string
        content_type?: number
        /**
         * * `auth.permission` - permission
         * * `auth.group` - group
         * * `contenttypes.contenttype` - contenttype
         * * `flatpages.flatpage` - flatpage
         * * `sessions.session` - session
         * * `sites.site` - site
         * * `authtoken.token` - token
         * * `axes.accessattempt` - accessattempt
         * * `axes.accesslog` - accesslog
         * * `gdpr_logs.log` - log
         * * `users.user` - user
         * * `emails.emailmessagestored` - emailmessagestored
         * * `core.sitesettings` - sitesettings
         * * `images.image` - image
         * * `periods.semester` - semester
         * * `aims.aim` - aim
         * * `curriculum.gradelevel` - gradelevel
         * * `departments.department` - department
         * * `rooms.room` - room
         * * `courses.courselevel` - courselevel
         * * `courses.coursetype` - coursetype
         * * `courses.course` - course
         * * `students.student` - student
         * * `teachers.teacher` - teacher
         * * `sections.section` - section
         * * `sections.studentsectionenrollmentlog` - studentsectionenrollmentlog
         * * `activities.activity` - activity
         * * `activities.lesson` - lesson
         * * `assignments.assignment` - assignment
         * * `assignments.assignmenttype` - assignmenttype
         * * `assignments.studentassignmentitem` - studentassignmentitem
         * * `surveys.surveyresponse` - surveyresponse
         * * `webhooks.webhooklog` - webhooklog
         * * `importing.celerytaskstatus` - celerytaskstatus
         * * `aims.studentaimforcourse` - studentaimforcourse
         * * `importing.importsettings` - importsettings
         * * `aims.commenthistory` - commenthistory
         * * `periods.schoolyear` - schoolyear
         * * `periods.quarter` - quarter
         * * `importing.importedtimetable` - importedtimetable
         * * `two_factor.onetimecode` - onetimecode
         * * `merits.merit` - merit
         * * `courses.weeklycomment` - weeklycomment
         * * `grades.accgrade` - accgrade
         * * `grades.accgradecommenthistory` - accgradecommenthistory
         * * `courses_meta.coursefamily` - coursefamily
         * * `courses_meta.coursegroup` - coursegroup
         * * `courses_meta.courseintelligence` - courseintelligence
         * * `courses_meta.courselevel` - courselevel
         * * `courses_meta.coursesubject` - coursesubject
         * * `courses_meta.coursetype` - coursetype
         * * `attendances.absence` - absence
         * * `aims.studentdefaultaim` - studentdefaultaim
         * * `authtoken.tokenproxy` - tokenproxy
         * * `courses.previousnextcourse` - previousnextcourse
         * * `teachers.academichod` - academichod
         * * `timetable.timetable` - timetable
         * * `timetable.sourceidpplan` - sourceidpplan
         * * `permissions.custompermissions` - custompermissions
         * * `remote_generator.remoteserver` - remoteserver
         * * `remote_generator.generationtask` - generationtask
         * * `draft_activities.draftactivity` - draftactivity
         * * `potential_optimizations.potentialoptimization` - potentialoptimization
         * * `sections.sectionenrollment` - sectionenrollment
         * * `logs.timetablelog` - timetablelog
         * * `students_idp.basket` - basket
         * * `students_idp.courseinbasket` - courseinbasket
         * * `students_idp.requestedcourse` - requestedcourse
         * * `students_idp.studentidpplan` - studentidpplan
         * * `students_idp.parentwarning` - parentwarning
         * * `students_idp.examscore` - examscore
         * * `students_idp.idpsectionenrollment` - idpsectionenrollment
         * * `idp_admin.idpadmin` - idpadmin
         * * `admin_profile.admin` - admin
         * * `courses.courseenrollment` - courseenrollment
         * * `students_idp.idpplanpayloadhistory` - idpplanpayloadhistory
         * * `students_idp.idpplanstatushistory` - idpplanstatushistory
         * * `students_idp.idpsectionenrollmenthistory` - idpsectionenrollmenthistory
         * * `teachers.academicadmin` - academicadmin
         * * `profiles.boardingdirector` - boardingdirector
         * * `profiles.healthcoordinator` - healthcoordinator
         * * `reversion.revision` - revision
         * * `reversion.version` - version
         * * `students.studentgradelevelchange` - studentgradelevelchange
         * * `mission_control.competency` - competency
         * * `mission_control.degreeaim` - degreeaim
         * * `mission_control.interest` - interest
         * * `mission_control.skill` - skill
         * * `mission_control.missioncontrol` - missioncontrol
         * * `mission_control.taeexperience` - taeexperience
         * * `mission_control.taeproject` - taeproject
         * * `mission_control.studenttaeexperience` - studenttaeexperience
         * * `achievements.achievement` - achievement
         * * `achievements.studentachievement` - studentachievement
         * * `internships.internship` - internship
         * * `internships.studentinternship` - studentinternship
         * * `attachments.certificateattachment` - certificateattachment
         * * `attachments.examcertificateattachment` - examcertificateattachment
         * * `exams.exam` - exam
         * * `exams.transcript` - transcript
         * * `attendances.academicattendance` - academicattendance
         * * `attendances.academicattendancealert` - academicattendancealert
         * * `requirements.importedstudentcreditshistory` - importedstudentcreditshistory
         * * `requirements.importedcredit` - importedcredit
         * * `requirements.coursegraderequirement` - coursegraderequirement
         * * `leaves.leaverequest` - leaverequest
         * * `leaves.leaverequesthistoryitem` - leaverequesthistoryitem
         * * `leaves.leavecheck` - leavecheck
         * * `boarding_attendances.boardingattendance` - boardingattendance
         * * `boarding_attendances.boardingattendancereport` - boardingattendancereport
         * * `houses.house` - house
         * * `houses.studenthouse` - studenthouse
         * * `houses.houseroom` - houseroom
         * * `boarding_night_attendances.nightguard` - nightguard
         * * `boarding_night_attendances.nightattendancereport` - nightattendancereport
         * * `boarding_night_attendances.nightattendance` - nightattendance
         * * `sick_reports.firstcontact` - firstcontact
         * * `sick_reports.medicineplan` - medicineplan
         * * `sick_reports.sickstudentcheck` - sickstudentcheck
         * * `sick_reports.sickreport` - sickreport
         * * `doctors.doctor` - doctor
         * * `allergies.studentallergy` - studentallergy
         * * `boarding_night_attendances.nightattendancecomment` - nightattendancecomment
         * * `discipline.studentcategoryreport` - studentcategoryreport
         * * `discipline.studentcategoryitem` - studentcategoryitem
         * * `sick_reports.sickreportcomment` - sickreportcomment
         * * `medicine.medicine` - medicine
         * * `medicine_plan.medicineinplan` - medicineinplan
         * * `medicine_plan.medicineplan` - medicineplan
         * * `medicine_plan.medicineintakeplan` - medicineintakeplan
         * * `managers.housetutor` - housetutor
         * * `chronic_conditions.studentchroniccondition` - studentchroniccondition
         * * `sick_reports.appointment` - appointment
         * * `draft_activities.talentenrichmentcopy` - talentenrichmentcopy
         * * `mentorings.mentoringsections` - mentoringsections
         * * `mentorings.timetablementoring` - timetablementoring
         * * `mentorings.teacherspergrade` - teacherspergrade
         * * `scheduled_medicine.scheduledmedicine` - scheduledmedicine
         * * `health_checks.healthcheck` - healthcheck
         * * `health_checks.temperaturecheck` - temperaturecheck
         * * `health_checks.optionalmedicinegiven` - optionalmedicinegiven
         * * `health_checks.healthchecklog` - healthchecklog
         * * `health_checks.healthcheckcomment` - healthcheckcomment
         * * `health_checks.foodgive` - foodgive
         * * `health_checks.drinkgive` - drinkgive
         * * `health_checks.condition` - condition
         * * `health_checks.medicinegiven` - medicinegiven
         * * `sick_reports.sickreportabsencedates` - sickreportabsencedates
         * * `boarding_accommodations.accommodation` - accommodation
         * * `boarding_comments.boardingcomment` - boardingcomment
         * * `core.calendarevent` - calendarevent
         * * `attendances.academicattendancehistory` - academicattendancehistory
         * * `teams.team` - team
         * * `teams.teammember` - teammember
         * * `profiles.campusheadteam` - campusheadteam
         * * `billings.billingreport` - billingreport
         * * `billings.billingreportitem` - billingreportitem
         * * `campus_absences.campusabsence` - campusabsence
         * * `campus_absences.campusabsencestudent` - campusabsencestudent
         * * `campus_absences.campusabsencecomment` - campusabsencecomment
         * * `progress_comments.achievementandeffortprogresscomment` - achievementandeffortprogresscomment
         * * `progress_comments.behaviourandattitudeprogresscomment` - behaviourandattitudeprogresscomment
         * * `progress_comments.interventionprogresscomment` - interventionprogresscomment
         * * `progress_comments.studenttargetprogresscomment` - studenttargetprogresscomment
         * * `progress_comments.progresscomment` - progresscomment
         * * `courses.groupedcourses` - groupedcourses
         * * `hospitals.hospital` - hospital
         * * `merits.merititem` - merititem
         * * `merits.deescalatedsanction` - deescalatedsanction
         * * `merits.demeritemailnotification` - demeritemailnotification
         * * `profiles.headofhouse` - headofhouse
         * * `tasks_management.recurring` - recurring
         * * `tasks_management.task` - task
         * * `tasks_management.usertasktitle` - usertasktitle
         * * `tasks_management.tasklog` - tasklog
         * * `tasks_management.subtask` - subtask
         * * `profiles.viewer` - viewer
         * * `files_for_students.fileforstudents` - fileforstudents
         * * `allergies.studentallergyreaction` - studentallergyreaction
         * * `attachments.privateattachment` - privateattachment
         * * `student_applications.studentapplication` - studentapplication
         * * `student_applications.studentapplicationmedication` - studentapplicationmedication
         * * `knox.authtoken` - authtoken
         * * `menu.daymenu` - daymenu
         * * `favourite_widgets.favouritewidget` - favouritewidget
         * * `students.studentcontactperson` - studentcontactperson
         * * `notifications.notification` - notification
         * * `push.registereddevice` - registereddevice
         * * `push.pushnotification` - pushnotification
         * * `email.emailnotification` - emailnotification
         * * `db.testmodel` - testmodel
         * * `exams.studentexternalexam` - studentexternalexam
         * * `school_breaks.schoolbreak` - schoolbreak
         * * `exam_planner.examsession` - examsession
         * * `exam_planner.examsessioncancellationdate` - examsessioncancellationdate
         * * `exam_planner.exam` - exam
         * * `exam_planner.examstudentnote` - examstudentnote
         * * `exam_planner.examenrolledstudent` - examenrolledstudent
         * * `exam_planner.examattendancehistory` - examattendancehistory
         * * `exam_clashes.examclash` - examclash
         * * `lesson_cancellation.cancellesson` - cancellesson
         * * `lesson_cancellation.cancelstudentinlesson` - cancelstudentinlesson
         * * `social_django.association` - association
         * * `social_django.code` - code
         * * `social_django.nonce` - nonce
         * * `social_django.usersocialauth` - usersocialauth
         * * `social_django.partial` - partial
         * * `core.appstatusversion` - appstatusversion
         * * `sick_reports.sickreportclosurerequest` - sickreportclosurerequest
         * * `auditlog.logentry` - logentry
         * * `profiles.propertymanager` - propertymanager
         * * `billings.abacusinvoiceproduct` - abacusinvoiceproduct
         * * `attachments.schoolprogrammeattachment` - schoolprogrammeattachment
         * * `school_programmes.programmeenrolment` - programmeenrolment
         * * `school_programmes.schoolprogramme` - schoolprogramme
         * * `programme_attendances.programmeattendancereport` - programmeattendancereport
         * * `programme_attendances.programmestudentattendance` - programmestudentattendance
         * * `programme_attendances.programmeattendancehistory` - programmeattendancehistory
         * * `meal_preferences.studentmealpreference` - studentmealpreference
         * * `meal_attendance.mealattendancelastupdate` - mealattendancelastupdate
         * * `profiles.infoboardeditor` - infoboardeditor
         * * `images.catalog` - catalog
         * * `infoboards.infoboardssettings` - infoboardssettings
         * * `infoboards.board` - board
         * * `infoboards.studentcontent` - studentcontent
         * * `infoboards.message` - message
         * * `infoboards.recurring` - recurring
         * * `infoboards.boardschedule` - boardschedule
         * * `infoboards.updatestatus` - updatestatus
         * * `infoboards_links.linkwithqrcode` - linkwithqrcode
         * * `widgets_base.widget` - widget
         * * `widgets_base.widgetmetadata` - widgetmetadata
         * * `boarding_announcement_widget.boardingannouncementwidget` - boardingannouncementwidget
         * * `celebration_widget.celebrationwidget` - celebrationwidget
         * * `event_agenda_widget.eventagendawidget` - eventagendawidget
         * * `event_agenda_widget.agendaitem` - agendaitem
         * * `congratulations_widget.congratulationswidget` - congratulationswidget
         * * `poster_widget.posterwidget` - posterwidget
         * * `student_art_gallery_widget.studentartgallerywidget` - studentartgallerywidget
         * * `student_art_gallery_widget.studentart` - studentart
         * * `event_info_widget.eventinfowidget` - eventinfowidget
         * * `event_info_with_photo_widget.eventinfowithphotowidget` - eventinfowithphotowidget
         * * `journal_widget.journalwidget` - journalwidget
         * * `mystery_link_widget.studentmysterylink` - studentmysterylink
         * * `mystery_link_widget.mysterylinkwidget` - mysterylinkwidget
         * * `menu_widget.menuwidget` - menuwidget
         * * `students_announcement_widget.studentannouncement` - studentannouncement
         * * `students_announcement_widget.studentsannouncementwidget` - studentsannouncementwidget
         * * `students_pic_widget.studentpic` - studentpic
         * * `students_pic_widget.studentspicwidget` - studentspicwidget
         * * `remote_generator.remotegenerationprogress` - remotegenerationprogress
         * * `remote_generator.remotegenerationprocess` - remotegenerationprocess
         * * `remote_generator.remotetimetablepayload` - remotetimetablepayload
         * * `variant_manual_congratulations_widget.variantmanualcongratulations` - variantmanualcongratulations
         * * `variant_manual_congratulations_widget.variantmanualcongratulationswidget` - variantmanualcongratulationswidget
         * * `allergies.allergy` - allergy
         * * `chronic_conditions.chroniccondition` - chroniccondition
         * * `courses.courseenrollmenthistory` - courseenrollmenthistory
         * * `journal_widget.journalitem` - journalitem
         * * `importing.celerytaskstatushistory` - celerytaskstatushistory
         * * `room_bookings.roombooking` - roombooking
         * * `exam_planner.activestudentexamenrollment` - activestudentexamenrollment
         * * `remote_generator.bestsolutionfound` - bestsolutionfound
         * * `school_programmes.programmeretrievalbystudent` - programmeretrievalbystudent
         * * `recovery.recovery` - recovery
         * * `recovery.studentrecovery` - studentrecovery
         * * `recovery.studentrecoveryreason` - studentrecoveryreason
         * * `recovery.absencerecovery` - absencerecovery
         * * `recovery.lunchtimerecovery` - lunchtimerecovery
         * * `profiles.behaviourmanagement` - behaviourmanagement
         * * `grades.periodgpa` - periodgpa
         * * `grades.highschoolgpa` - highschoolgpa
         * * `grades.gpaforcourse` - gpaforcourse
         * * `rota.requiredshifts` - requiredshifts
         * * `rota.breakrequirements` - breakrequirements
         * * `rota.rotateammember` - rotateammember
         * * `rota.rotateam` - rotateam
         * * `rota.meetingrequirement` - meetingrequirement
         * * `rota.rotageneration` - rotageneration
         * * `reports.reportcard` - reportcard
         * * `parents.parent` - parent
         * * `axes.accessfailurelog` - accessfailurelog
         * * `attachments.internshipattachment` - internshipattachment
         * * `rota.enforcedshifts` - enforcedshifts
         * * `rota.rotatimetable` - rotatimetable
         * * `rota_activities.draftrotaactivity` - draftrotaactivity
         * * `rota_activities.rotaactivity` - rotaactivity
         * * `rota_activities.rota` - rota
         * * `merits_exchange.meritpoints` - meritpoints
         * * `merits_exchange.meritrewardparticipant` - meritrewardparticipant
         * * `merits_exchange.meritreward` - meritreward
         * * `merits_exchange.casualanyday` - casualanyday
         * * `merits_exchange.roomservice` - roomservice
         * * `merits_exchange.bookthecafe` - bookthecafe
         * * `merits_exchange.citybrunchzurich` - citybrunchzurich
         * * `merits_exchange.additionalausgang` - additionalausgang
         * * `merits_exchange.categoryupgrade` - categoryupgrade
         * * `merits_exchange.stoptheclock` - stoptheclock
         * * `contact.contactmessage` - contactmessage
         * * `rota.criticalduty` - criticalduty
         * * `rota.preferredtimeforbreak` - preferredtimeforbreak
         * * `rota.usertimeofday` - usertimeofday
         * * `rota.specialdaysoffrule` - specialdaysoffrule
         * * `sections.sectiongroup` - sectiongroup
         * * `clock_widget.clockwidget` - clockwidget
         * * `parents.parentstudentconnection` - parentstudentconnection
         * * `course_drops.coursedrop` - coursedrop
         * * `discipline.studentcategoryitemdowngradehistory` - studentcategoryitemdowngradehistory
         * * `rota.avoiduserchangeon` - avoiduserchangeon
         * * `rota.skitrip` - skitrip
         * * `rota.teambackup` - teambackup
         * * `profiles.securityteam` - securityteam
         * * `web_sockets.websocketauthtoken` - websocketauthtoken
         * * `rota.rotagenerationissue` - rotagenerationissue
         * * `congratulations_widget.congratulationsitem` - congratulationsitem
         * * `infoboards.reaction` - reaction
         * * `analytics.feedsurftime` - feedsurftime
         * * `analytics.feedday` - feedday
         * * `analytics.feedclick` - feedclick
         * * `celebration_widget.celebrationitem` - celebrationitem
         * * `summer_camps.summersubjectassign` - summersubjectassign
         * * `summer_camps.summerrosenberglegacy` - summerrosenberglegacy
         * * `summer_camps.summermentalhealthissue` - summermentalhealthissue
         * * `summer_camps.summercampstudent` - summercampstudent
         * * `web_sockets.websocketnotification` - websocketnotification
         * * `mission_control.taesportpackageitem` - taesportpackageitem
         * * `suggester.draftactivitysuggestertask` - draftactivitysuggestertask
         * * `suggester.draftactivitysuggestion` - draftactivitysuggestion
         * * `suggester.draftactivitysuggestionmove` - draftactivitysuggestionmove
         * * `rota.blockedslots` - blockedslots
         * * `rota.enforcedworkingdays` - enforcedworkingdays
         * * `rota.dislikedtimeforbreak` - dislikedtimeforbreak
         * * `summer_certificates.summercertificate` - summercertificate
         * * `summer_certificates.summercertificatecourseinfo` - summercertificatecourseinfo
         * * `summer_certificates.summercertificateattachment` - summercertificateattachment
         * * `summer_certificates.summercertificateszipped` - summercertificateszipped
         * * `auditlog.archivedauditlogentry` - archivedauditlogentry
         * * `talent_and_enrichment.taegeneratortask` - taegeneratortask
         * * `talent_and_enrichment.taedraftactivity` - taedraftactivity
         * * `courses.courseallowedslot` - courseallowedslot
         * * `sections.sectionallowedslot` - sectionallowedslot
         * * `transcripts_of_grades.transcriptofgrades` - transcriptofgrades
         * * `transcripts_of_grades.transcriptofgradesitem` - transcriptofgradesitem
         * * `transcripts_of_grades.transcriptofgradesitemcourse` - transcriptofgradesitemcourse
         * * `departments.departmenthead` - departmenthead
         * * `tae_enrolment.taeenrolment` - taeenrolment
         * * `tae_enrolment.taestudentenrollment` - taestudentenrollment
         * * `rooms.roomallowedslot` - roomallowedslot
         * * `rota.enforceddaysoff` - enforceddaysoff
         * * `tae_enrolment.taecourseenrollment` - taecourseenrollment
         * * `tae_enrolment.taeenrollmentstudentchoice` - taeenrollmentstudentchoice
         * * `tae_enrolment.taeofferattachment` - taeofferattachment
         * * `tae_draft_sections.taedraftsectionstask` - taedraftsectionstask
         * * `tae_draft_sections.taedraftsection` - taedraftsection
         * * `tae_draft_sections.taedraftsectionenrolment` - taedraftsectionenrolment
         * * `tae_conflicts.taesectionconflictdetail` - taesectionconflictdetail
         * * `tae_conflicts.taesectionconflict` - taesectionconflict
         * * `tae_conflicts.taetaskconflictcalculatorstate` - taetaskconflictcalculatorstate
         * * `medicine_plan.medicineinplanchangelog` - medicineinplanchangelog
         * * `emails.emailsettings` - emailsettings
         * * `tae_enrolment.taeparentcomment` - taeparentcomment
         * * `tae_draft_sections.taedraftsectionpushaction` - taedraftsectionpushaction
         * * `cockpit.domain` - domain
         * * `cockpit.topic` - topic
         * * `cockpit.subtopic` - subtopic
         * * `course_builder.globalstandard` - globalstandard
         * * `course_builder.globalstandarditem` - globalstandarditem
         * * `repository.domain` - domain
         * * `repository.topic` - topic
         * * `repository.subtopic` - subtopic
         * * `course_scheduler.coursesectiongroupschedule` - coursesectiongroupschedule
         * * `summer_comments.summeracademiccomment` - summeracademiccomment
         * * `summer_comments.summerboardingcomment` - summerboardingcomment
         * * `course_scheduler.sectiongroupplan` - sectiongroupplan
         * * `course_scheduler.sectionplan` - sectionplan
         * * `course_scheduler.sectionplanitem` - sectionplanitem
         * * `talent_and_enrichment.taegeneratordraftactivityupdate` - taegeneratordraftactivityupdate
         * * `course_scheduler.cockpitdraftassignment` - cockpitdraftassignment
         * * `course_scheduler.sectiongroupplanrejection` - sectiongroupplanrejection
         * * `users.useravailability` - useravailability
         * * `users.useravailabilityday` - useravailabilityday
         * * `users.useravailabilityslot` - useravailabilityslot
         * * `ai.aiagentslog` - aiagentslog
         * * `merits_exchange.makeanimpact` - makeanimpact
         * * `merits_exchange.oncampusausgang` - oncampusausgang
         * * `meal_attendance.studentmealskip` - studentmealskip
         * * `meal_attendance.studentmealskipformsubmission` - studentmealskipformsubmission
         * * `school_programmes.recurringprogramme` - recurringprogramme
         * * `user_preferences.favouriteviewsetting` - favouriteviewsetting
         * * `assignments.assignmentscoredefinition` - assignmentscoredefinition
         * * `assignments.studentassignmentscore` - studentassignmentscore
         * * `assignments.moderatedgradefeedback` - moderatedgradefeedback
         * * `profiles.crmmanager` - crmmanager
         * * `crm.candidateprofile` - candidateprofile
         * * `student_schools.studentschoolhistory` - studentschoolhistory
         * * `student_studied_languages.studentstudiedlanguage` - studentstudiedlanguage
         * * `student_documents.studentdocument` - studentdocument
         * * `student_addresses.studentaddress` - studentaddress
         * * `student_legacies.studentlegacy` - studentlegacy
         * * `crm.notusedcandidateprofiledata` - notusedcandidateprofiledata
         * * `student_documents.studentdocumentname` - studentdocumentname
         * * `transcripts_of_grades.transcriptofgradessignature` - transcriptofgradessignature
         * * `user_contact_details.useraddress` - useraddress
         * * `user_contact_details.useradditionalemail` - useradditionalemail
         * * `user_contact_details.useradditionalphonenumber` - useradditionalphonenumber
         * * `user_notes.usernote` - usernote
         * * `django_celery_beat.crontabschedule` - crontabschedule
         * * `django_celery_beat.intervalschedule` - intervalschedule
         * * `django_celery_beat.periodictask` - periodictask
         * * `django_celery_beat.periodictasks` - periodictasks
         * * `django_celery_beat.solarschedule` - solarschedule
         * * `django_celery_beat.clockedschedule` - clockedschedule
         * * `crm.candidateacademicoffer` - candidateacademicoffer
         * * `student_medical_info.studentmedicalinfo` - studentmedicalinfo
         * * `salutations.salutation` - salutation
         * * `user_contacts.userrelation` - userrelation
         * * `duplicates.duplicaterecord` - duplicaterecord
         * * `cockpit.cockpiteditingperiod` - cockpiteditingperiod
         * * `user_contacts.relationsuggestion` - relationsuggestion
         * * `crm_trello.admissionsteplist` - admissionsteplist
         * * `crm_trello.cardtemplate` - cardtemplate
         * * `revision_week_autofill.revisionweekautofillplan` - revisionweekautofillplan
         * * `revision_week_autofill.revisionweekautofillplanitem` - revisionweekautofillplanitem
         * * `revision_week_autofill.revisionweekautofillplaniteminvigilator` - revisionweekautofillplaniteminvigilator
         * * `revision_week_autofill.revisionweekautofillplanitemstudent` - revisionweekautofillplanitemstudent
         * * `revision_week_autofill.revisionweekautofillplanitemteacher` - revisionweekautofillplanitemteacher
         * * `locations.location` - location
         * * `users.userlifecyclestate` - userlifecyclestate
         * * `crm.legacycrmidmapping` - legacycrmidmapping
         * * `crm.legacydocumentmapping` - legacydocumentmapping
         * * `crm_legacy.bsicustomer` - bsicustomer
         * * `crm_legacy.bsidefaultaddresscalc` - bsidefaultaddresscalc
         * * `crm_legacy.bsidocument` - bsidocument
         * * `crm_legacy.bsiaddress` - bsiaddress
         * * `crm_legacy.bsibusiness` - bsibusiness
         * * `crm_legacy.bsibusinessproduct` - bsibusinessproduct
         * * `crm_legacy.bsicustomercustomer` - bsicustomercustomer
         * * `crm_legacy.bsicustomercustomerrole` - bsicustomercustomerrole
         * * `crm_legacy.bsibusinessrole` - bsibusinessrole
         * * `crm_legacy.bsicustomertrarelation` - bsicustomertrarelation
         * * `crm_legacy.bsitransitiverelation` - bsitransitiverelation
         * * `crm_legacy.bsiuser` - bsiuser
         * * `crm_legacy.bsicustomerlist` - bsicustomerlist
         * * `crm_legacy.bsiuccountry` - bsiuccountry
         * * `crm_legacy.bsicity` - bsicity
         * * `crm_legacy.bsiuctext` - bsiuctext
         * * `laundry.laundryqrcode` - laundryqrcode
         * * `laundry.studentlaundryorder` - studentlaundryorder
         * * `parents.company` - company
         * * `crm_abacus.abacusrequestlog` - abacusrequestlog
         * * `laundry.laundrycatalogitem` - laundrycatalogitem
         * * `laundry.laundryorderversion` - laundryorderversion
         * * `laundry.laundryorderversionitem` - laundryorderversionitem
         * * `tae_enrolment_v2.taeplan` - taeplan
         * * `tae_enrolment_v2.taeenrolment` - taeenrolment
         * * `tae_enrolment_v2.taeofferattachment` - taeofferattachment
         * * `tae_enrolment_v2.taeenrolmentscope` - taeenrolmentscope
         * * `tae_enrolment_v2.taecourseenrolment` - taecourseenrolment
         * * `laundry.laundryorderclaim` - laundryorderclaim
         * * `companies.company` - company
         * * `user_contacts.studentbillingrecipientassignment` - studentbillingrecipientassignment
         * * `tae_enrolment_v2.taestudentenrolment` - taestudentenrolment
         * * `tae_enrolment_v2.taeenrolmentstudentchoice` - taeenrolmentstudentchoice
         * * `tae_enrolment_v2.taeparentcomment` - taeparentcomment
         * * `timetable.studentmove` - studentmove
         * * `tae_draft_sections_v2.taedraftsection` - taedraftsection
         * * `tae_draft_sections_v2.taedraftsectionenrolment` - taedraftsectionenrolment
         * * `tae_draft_sections_v2.taedraftsectionstask` - taedraftsectionstask
         * * `draft_sections_generator.taedraftsectionstask` - taedraftsectionstask
         * * `laundry.laundryorderclaimitem` - laundryorderclaimitem
         * * `laundry.historicallaundrycatalogitem` - historicallaundrycatalogitem
         * * `recovery.highschoollunchtimerecovery` - highschoollunchtimerecovery
         * * `exam_planner.examgenerationprocess` - examgenerationprocess
         * * `tae_conflicts_v2.taesectionconflictdetail` - taesectionconflictdetail
         * * `tae_conflicts_v2.taetaskconflictcalculatorstate` - taetaskconflictcalculatorstate
         * * `tae_conflicts_v2.taesectionconflict` - taesectionconflict
         * * `videos.video` - video
         * * `info_with_video_widget.infowithvideowidget` - infowithvideowidget
         */
        content_type__in?: (
          | 'achievements.achievement'
          | 'achievements.studentachievement'
          | 'activities.activity'
          | 'activities.lesson'
          | 'admin_profile.admin'
          | 'ai.aiagentslog'
          | 'aims.aim'
          | 'aims.commenthistory'
          | 'aims.studentaimforcourse'
          | 'aims.studentdefaultaim'
          | 'allergies.allergy'
          | 'allergies.studentallergy'
          | 'allergies.studentallergyreaction'
          | 'analytics.feedclick'
          | 'analytics.feedday'
          | 'analytics.feedsurftime'
          | 'assignments.assignment'
          | 'assignments.assignmentscoredefinition'
          | 'assignments.assignmenttype'
          | 'assignments.moderatedgradefeedback'
          | 'assignments.studentassignmentitem'
          | 'assignments.studentassignmentscore'
          | 'attachments.certificateattachment'
          | 'attachments.examcertificateattachment'
          | 'attachments.internshipattachment'
          | 'attachments.privateattachment'
          | 'attachments.schoolprogrammeattachment'
          | 'attendances.absence'
          | 'attendances.academicattendance'
          | 'attendances.academicattendancealert'
          | 'attendances.academicattendancehistory'
          | 'auditlog.archivedauditlogentry'
          | 'auditlog.logentry'
          | 'auth.group'
          | 'auth.permission'
          | 'authtoken.token'
          | 'authtoken.tokenproxy'
          | 'axes.accessattempt'
          | 'axes.accessfailurelog'
          | 'axes.accesslog'
          | 'billings.abacusinvoiceproduct'
          | 'billings.billingreport'
          | 'billings.billingreportitem'
          | 'boarding_accommodations.accommodation'
          | 'boarding_announcement_widget.boardingannouncementwidget'
          | 'boarding_attendances.boardingattendance'
          | 'boarding_attendances.boardingattendancereport'
          | 'boarding_comments.boardingcomment'
          | 'boarding_night_attendances.nightattendance'
          | 'boarding_night_attendances.nightattendancecomment'
          | 'boarding_night_attendances.nightattendancereport'
          | 'boarding_night_attendances.nightguard'
          | 'campus_absences.campusabsence'
          | 'campus_absences.campusabsencecomment'
          | 'campus_absences.campusabsencestudent'
          | 'celebration_widget.celebrationitem'
          | 'celebration_widget.celebrationwidget'
          | 'chronic_conditions.chroniccondition'
          | 'chronic_conditions.studentchroniccondition'
          | 'clock_widget.clockwidget'
          | 'cockpit.cockpiteditingperiod'
          | 'cockpit.domain'
          | 'cockpit.subtopic'
          | 'cockpit.topic'
          | 'companies.company'
          | 'congratulations_widget.congratulationsitem'
          | 'congratulations_widget.congratulationswidget'
          | 'contact.contactmessage'
          | 'contenttypes.contenttype'
          | 'core.appstatusversion'
          | 'core.calendarevent'
          | 'core.sitesettings'
          | 'course_builder.globalstandard'
          | 'course_builder.globalstandarditem'
          | 'course_drops.coursedrop'
          | 'course_scheduler.cockpitdraftassignment'
          | 'course_scheduler.coursesectiongroupschedule'
          | 'course_scheduler.sectiongroupplan'
          | 'course_scheduler.sectiongroupplanrejection'
          | 'course_scheduler.sectionplan'
          | 'course_scheduler.sectionplanitem'
          | 'courses.course'
          | 'courses.courseallowedslot'
          | 'courses.courseenrollment'
          | 'courses.courseenrollmenthistory'
          | 'courses.courselevel'
          | 'courses.coursetype'
          | 'courses.groupedcourses'
          | 'courses.previousnextcourse'
          | 'courses.weeklycomment'
          | 'courses_meta.coursefamily'
          | 'courses_meta.coursegroup'
          | 'courses_meta.courseintelligence'
          | 'courses_meta.courselevel'
          | 'courses_meta.coursesubject'
          | 'courses_meta.coursetype'
          | 'crm.candidateacademicoffer'
          | 'crm.candidateprofile'
          | 'crm.legacycrmidmapping'
          | 'crm.legacydocumentmapping'
          | 'crm.notusedcandidateprofiledata'
          | 'crm_abacus.abacusrequestlog'
          | 'crm_legacy.bsiaddress'
          | 'crm_legacy.bsibusiness'
          | 'crm_legacy.bsibusinessproduct'
          | 'crm_legacy.bsibusinessrole'
          | 'crm_legacy.bsicity'
          | 'crm_legacy.bsicustomer'
          | 'crm_legacy.bsicustomercustomer'
          | 'crm_legacy.bsicustomercustomerrole'
          | 'crm_legacy.bsicustomerlist'
          | 'crm_legacy.bsicustomertrarelation'
          | 'crm_legacy.bsidefaultaddresscalc'
          | 'crm_legacy.bsidocument'
          | 'crm_legacy.bsitransitiverelation'
          | 'crm_legacy.bsiuccountry'
          | 'crm_legacy.bsiuctext'
          | 'crm_legacy.bsiuser'
          | 'crm_trello.admissionsteplist'
          | 'crm_trello.cardtemplate'
          | 'curriculum.gradelevel'
          | 'db.testmodel'
          | 'departments.department'
          | 'departments.departmenthead'
          | 'discipline.studentcategoryitem'
          | 'discipline.studentcategoryitemdowngradehistory'
          | 'discipline.studentcategoryreport'
          | 'django_celery_beat.clockedschedule'
          | 'django_celery_beat.crontabschedule'
          | 'django_celery_beat.intervalschedule'
          | 'django_celery_beat.periodictask'
          | 'django_celery_beat.periodictasks'
          | 'django_celery_beat.solarschedule'
          | 'doctors.doctor'
          | 'draft_activities.draftactivity'
          | 'draft_activities.talentenrichmentcopy'
          | 'draft_sections_generator.taedraftsectionstask'
          | 'duplicates.duplicaterecord'
          | 'email.emailnotification'
          | 'emails.emailmessagestored'
          | 'emails.emailsettings'
          | 'event_agenda_widget.agendaitem'
          | 'event_agenda_widget.eventagendawidget'
          | 'event_info_widget.eventinfowidget'
          | 'event_info_with_photo_widget.eventinfowithphotowidget'
          | 'exam_clashes.examclash'
          | 'exam_planner.activestudentexamenrollment'
          | 'exam_planner.exam'
          | 'exam_planner.examattendancehistory'
          | 'exam_planner.examenrolledstudent'
          | 'exam_planner.examgenerationprocess'
          | 'exam_planner.examsession'
          | 'exam_planner.examsessioncancellationdate'
          | 'exam_planner.examstudentnote'
          | 'exams.exam'
          | 'exams.studentexternalexam'
          | 'exams.transcript'
          | 'favourite_widgets.favouritewidget'
          | 'files_for_students.fileforstudents'
          | 'flatpages.flatpage'
          | 'gdpr_logs.log'
          | 'grades.accgrade'
          | 'grades.accgradecommenthistory'
          | 'grades.gpaforcourse'
          | 'grades.highschoolgpa'
          | 'grades.periodgpa'
          | 'health_checks.condition'
          | 'health_checks.drinkgive'
          | 'health_checks.foodgive'
          | 'health_checks.healthcheck'
          | 'health_checks.healthcheckcomment'
          | 'health_checks.healthchecklog'
          | 'health_checks.medicinegiven'
          | 'health_checks.optionalmedicinegiven'
          | 'health_checks.temperaturecheck'
          | 'hospitals.hospital'
          | 'houses.house'
          | 'houses.houseroom'
          | 'houses.studenthouse'
          | 'idp_admin.idpadmin'
          | 'images.catalog'
          | 'images.image'
          | 'importing.celerytaskstatus'
          | 'importing.celerytaskstatushistory'
          | 'importing.importedtimetable'
          | 'importing.importsettings'
          | 'info_with_video_widget.infowithvideowidget'
          | 'infoboards.board'
          | 'infoboards.boardschedule'
          | 'infoboards.infoboardssettings'
          | 'infoboards.message'
          | 'infoboards.reaction'
          | 'infoboards.recurring'
          | 'infoboards.studentcontent'
          | 'infoboards.updatestatus'
          | 'infoboards_links.linkwithqrcode'
          | 'internships.internship'
          | 'internships.studentinternship'
          | 'journal_widget.journalitem'
          | 'journal_widget.journalwidget'
          | 'knox.authtoken'
          | 'laundry.historicallaundrycatalogitem'
          | 'laundry.laundrycatalogitem'
          | 'laundry.laundryorderclaim'
          | 'laundry.laundryorderclaimitem'
          | 'laundry.laundryorderversion'
          | 'laundry.laundryorderversionitem'
          | 'laundry.laundryqrcode'
          | 'laundry.studentlaundryorder'
          | 'leaves.leavecheck'
          | 'leaves.leaverequest'
          | 'leaves.leaverequesthistoryitem'
          | 'lesson_cancellation.cancellesson'
          | 'lesson_cancellation.cancelstudentinlesson'
          | 'locations.location'
          | 'logs.timetablelog'
          | 'managers.housetutor'
          | 'meal_attendance.mealattendancelastupdate'
          | 'meal_attendance.studentmealskip'
          | 'meal_attendance.studentmealskipformsubmission'
          | 'meal_preferences.studentmealpreference'
          | 'medicine.medicine'
          | 'medicine_plan.medicineinplan'
          | 'medicine_plan.medicineinplanchangelog'
          | 'medicine_plan.medicineintakeplan'
          | 'medicine_plan.medicineplan'
          | 'mentorings.mentoringsections'
          | 'mentorings.teacherspergrade'
          | 'mentorings.timetablementoring'
          | 'menu.daymenu'
          | 'menu_widget.menuwidget'
          | 'merits.deescalatedsanction'
          | 'merits.demeritemailnotification'
          | 'merits.merit'
          | 'merits.merititem'
          | 'merits_exchange.additionalausgang'
          | 'merits_exchange.bookthecafe'
          | 'merits_exchange.casualanyday'
          | 'merits_exchange.categoryupgrade'
          | 'merits_exchange.citybrunchzurich'
          | 'merits_exchange.makeanimpact'
          | 'merits_exchange.meritpoints'
          | 'merits_exchange.meritreward'
          | 'merits_exchange.meritrewardparticipant'
          | 'merits_exchange.oncampusausgang'
          | 'merits_exchange.roomservice'
          | 'merits_exchange.stoptheclock'
          | 'mission_control.competency'
          | 'mission_control.degreeaim'
          | 'mission_control.interest'
          | 'mission_control.missioncontrol'
          | 'mission_control.skill'
          | 'mission_control.studenttaeexperience'
          | 'mission_control.taeexperience'
          | 'mission_control.taeproject'
          | 'mission_control.taesportpackageitem'
          | 'mystery_link_widget.mysterylinkwidget'
          | 'mystery_link_widget.studentmysterylink'
          | 'notifications.notification'
          | 'parents.company'
          | 'parents.parent'
          | 'parents.parentstudentconnection'
          | 'periods.quarter'
          | 'periods.schoolyear'
          | 'periods.semester'
          | 'permissions.custompermissions'
          | 'poster_widget.posterwidget'
          | 'potential_optimizations.potentialoptimization'
          | 'profiles.behaviourmanagement'
          | 'profiles.boardingdirector'
          | 'profiles.campusheadteam'
          | 'profiles.crmmanager'
          | 'profiles.headofhouse'
          | 'profiles.healthcoordinator'
          | 'profiles.infoboardeditor'
          | 'profiles.propertymanager'
          | 'profiles.securityteam'
          | 'profiles.viewer'
          | 'programme_attendances.programmeattendancehistory'
          | 'programme_attendances.programmeattendancereport'
          | 'programme_attendances.programmestudentattendance'
          | 'progress_comments.achievementandeffortprogresscomment'
          | 'progress_comments.behaviourandattitudeprogresscomment'
          | 'progress_comments.interventionprogresscomment'
          | 'progress_comments.progresscomment'
          | 'progress_comments.studenttargetprogresscomment'
          | 'push.pushnotification'
          | 'push.registereddevice'
          | 'recovery.absencerecovery'
          | 'recovery.highschoollunchtimerecovery'
          | 'recovery.lunchtimerecovery'
          | 'recovery.recovery'
          | 'recovery.studentrecovery'
          | 'recovery.studentrecoveryreason'
          | 'remote_generator.bestsolutionfound'
          | 'remote_generator.generationtask'
          | 'remote_generator.remotegenerationprocess'
          | 'remote_generator.remotegenerationprogress'
          | 'remote_generator.remoteserver'
          | 'remote_generator.remotetimetablepayload'
          | 'reports.reportcard'
          | 'repository.domain'
          | 'repository.subtopic'
          | 'repository.topic'
          | 'requirements.coursegraderequirement'
          | 'requirements.importedcredit'
          | 'requirements.importedstudentcreditshistory'
          | 'reversion.revision'
          | 'reversion.version'
          | 'revision_week_autofill.revisionweekautofillplan'
          | 'revision_week_autofill.revisionweekautofillplanitem'
          | 'revision_week_autofill.revisionweekautofillplaniteminvigilator'
          | 'revision_week_autofill.revisionweekautofillplanitemstudent'
          | 'revision_week_autofill.revisionweekautofillplanitemteacher'
          | 'room_bookings.roombooking'
          | 'rooms.room'
          | 'rooms.roomallowedslot'
          | 'rota.avoiduserchangeon'
          | 'rota.blockedslots'
          | 'rota.breakrequirements'
          | 'rota.criticalduty'
          | 'rota.dislikedtimeforbreak'
          | 'rota.enforceddaysoff'
          | 'rota.enforcedshifts'
          | 'rota.enforcedworkingdays'
          | 'rota.meetingrequirement'
          | 'rota.preferredtimeforbreak'
          | 'rota.requiredshifts'
          | 'rota.rotageneration'
          | 'rota.rotagenerationissue'
          | 'rota.rotateam'
          | 'rota.rotateammember'
          | 'rota.rotatimetable'
          | 'rota.skitrip'
          | 'rota.specialdaysoffrule'
          | 'rota.teambackup'
          | 'rota.usertimeofday'
          | 'rota_activities.draftrotaactivity'
          | 'rota_activities.rota'
          | 'rota_activities.rotaactivity'
          | 'salutations.salutation'
          | 'scheduled_medicine.scheduledmedicine'
          | 'school_breaks.schoolbreak'
          | 'school_programmes.programmeenrolment'
          | 'school_programmes.programmeretrievalbystudent'
          | 'school_programmes.recurringprogramme'
          | 'school_programmes.schoolprogramme'
          | 'sections.section'
          | 'sections.sectionallowedslot'
          | 'sections.sectionenrollment'
          | 'sections.sectiongroup'
          | 'sections.studentsectionenrollmentlog'
          | 'sessions.session'
          | 'sick_reports.appointment'
          | 'sick_reports.firstcontact'
          | 'sick_reports.medicineplan'
          | 'sick_reports.sickreport'
          | 'sick_reports.sickreportabsencedates'
          | 'sick_reports.sickreportclosurerequest'
          | 'sick_reports.sickreportcomment'
          | 'sick_reports.sickstudentcheck'
          | 'sites.site'
          | 'social_django.association'
          | 'social_django.code'
          | 'social_django.nonce'
          | 'social_django.partial'
          | 'social_django.usersocialauth'
          | 'student_addresses.studentaddress'
          | 'student_applications.studentapplication'
          | 'student_applications.studentapplicationmedication'
          | 'student_art_gallery_widget.studentart'
          | 'student_art_gallery_widget.studentartgallerywidget'
          | 'student_documents.studentdocument'
          | 'student_documents.studentdocumentname'
          | 'student_legacies.studentlegacy'
          | 'student_medical_info.studentmedicalinfo'
          | 'student_schools.studentschoolhistory'
          | 'student_studied_languages.studentstudiedlanguage'
          | 'students.student'
          | 'students.studentcontactperson'
          | 'students.studentgradelevelchange'
          | 'students_announcement_widget.studentannouncement'
          | 'students_announcement_widget.studentsannouncementwidget'
          | 'students_idp.basket'
          | 'students_idp.courseinbasket'
          | 'students_idp.examscore'
          | 'students_idp.idpplanpayloadhistory'
          | 'students_idp.idpplanstatushistory'
          | 'students_idp.idpsectionenrollment'
          | 'students_idp.idpsectionenrollmenthistory'
          | 'students_idp.parentwarning'
          | 'students_idp.requestedcourse'
          | 'students_idp.studentidpplan'
          | 'students_pic_widget.studentpic'
          | 'students_pic_widget.studentspicwidget'
          | 'suggester.draftactivitysuggestertask'
          | 'suggester.draftactivitysuggestion'
          | 'suggester.draftactivitysuggestionmove'
          | 'summer_camps.summercampstudent'
          | 'summer_camps.summermentalhealthissue'
          | 'summer_camps.summerrosenberglegacy'
          | 'summer_camps.summersubjectassign'
          | 'summer_certificates.summercertificate'
          | 'summer_certificates.summercertificateattachment'
          | 'summer_certificates.summercertificatecourseinfo'
          | 'summer_certificates.summercertificateszipped'
          | 'summer_comments.summeracademiccomment'
          | 'summer_comments.summerboardingcomment'
          | 'surveys.surveyresponse'
          | 'tae_conflicts.taesectionconflict'
          | 'tae_conflicts.taesectionconflictdetail'
          | 'tae_conflicts.taetaskconflictcalculatorstate'
          | 'tae_conflicts_v2.taesectionconflict'
          | 'tae_conflicts_v2.taesectionconflictdetail'
          | 'tae_conflicts_v2.taetaskconflictcalculatorstate'
          | 'tae_draft_sections.taedraftsection'
          | 'tae_draft_sections.taedraftsectionenrolment'
          | 'tae_draft_sections.taedraftsectionpushaction'
          | 'tae_draft_sections.taedraftsectionstask'
          | 'tae_draft_sections_v2.taedraftsection'
          | 'tae_draft_sections_v2.taedraftsectionenrolment'
          | 'tae_draft_sections_v2.taedraftsectionstask'
          | 'tae_enrolment.taecourseenrollment'
          | 'tae_enrolment.taeenrollmentstudentchoice'
          | 'tae_enrolment.taeenrolment'
          | 'tae_enrolment.taeofferattachment'
          | 'tae_enrolment.taeparentcomment'
          | 'tae_enrolment.taestudentenrollment'
          | 'tae_enrolment_v2.taecourseenrolment'
          | 'tae_enrolment_v2.taeenrolment'
          | 'tae_enrolment_v2.taeenrolmentscope'
          | 'tae_enrolment_v2.taeenrolmentstudentchoice'
          | 'tae_enrolment_v2.taeofferattachment'
          | 'tae_enrolment_v2.taeparentcomment'
          | 'tae_enrolment_v2.taeplan'
          | 'tae_enrolment_v2.taestudentenrolment'
          | 'talent_and_enrichment.taedraftactivity'
          | 'talent_and_enrichment.taegeneratordraftactivityupdate'
          | 'talent_and_enrichment.taegeneratortask'
          | 'tasks_management.recurring'
          | 'tasks_management.subtask'
          | 'tasks_management.task'
          | 'tasks_management.tasklog'
          | 'tasks_management.usertasktitle'
          | 'teachers.academicadmin'
          | 'teachers.academichod'
          | 'teachers.teacher'
          | 'teams.team'
          | 'teams.teammember'
          | 'timetable.sourceidpplan'
          | 'timetable.studentmove'
          | 'timetable.timetable'
          | 'transcripts_of_grades.transcriptofgrades'
          | 'transcripts_of_grades.transcriptofgradesitem'
          | 'transcripts_of_grades.transcriptofgradesitemcourse'
          | 'transcripts_of_grades.transcriptofgradessignature'
          | 'two_factor.onetimecode'
          | 'user_contact_details.useradditionalemail'
          | 'user_contact_details.useradditionalphonenumber'
          | 'user_contact_details.useraddress'
          | 'user_contacts.relationsuggestion'
          | 'user_contacts.studentbillingrecipientassignment'
          | 'user_contacts.userrelation'
          | 'user_notes.usernote'
          | 'user_preferences.favouriteviewsetting'
          | 'users.user'
          | 'users.useravailability'
          | 'users.useravailabilityday'
          | 'users.useravailabilityslot'
          | 'users.userlifecyclestate'
          | 'variant_manual_congratulations_widget.variantmanualcongratulations'
          | 'variant_manual_congratulations_widget.variantmanualcongratulationswidget'
          | 'videos.video'
          | 'web_sockets.websocketauthtoken'
          | 'web_sockets.websocketnotification'
          | 'webhooks.webhooklog'
          | 'widgets_base.widget'
          | 'widgets_base.widgetmetadata'
        )[]
        object?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /** @format date-time */
        timestamp_after?: string
        /** @format date-time */
        timestamp_before?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedLogEntryList, T>, any>({
        path: `/logs/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags logs
     * @name LogsRetrieve
     * @request GET:/logs/{id}/
     * @secure
     */,
    logsRetrieve: <T extends FetchKeys<LogEntry>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LogEntry, T>, any>({
        path: `/logs/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  merits = {
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits
     * @name MeritsList
     * @request GET:/merits/
     * @secure
     */
    meritsList: <T extends FetchKeys<PaginatedMeritList>>(
      query?: {
        academic_only?: boolean
        /** @format date */
        added_on?: string
        /** @format uuid */
        author?: string
        /** @format uuid */
        course?: string
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /** @format date-time */
        datetime_after?: string
        /** @format date-time */
        datetime_before?: string
        /** @format date-time */
        escalate_until_after?: string
        /** @format date-time */
        escalate_until_before?: string
        /**
         * * `merit` - Merit
         * * `verbal_commendation` - Verbal commendation
         * * `written_commendation` - Written commendation
         * * `certificate` - Certificate
         * * `verbal_warning` - Verbal warning
         * * `written_warning` - Written warning
         * * `ultimatum` - Ultimatum
         * * `suspension` - Suspension
         * * `exclusion` - Exclusion
         */
        escalated_sanction?:
          | 'certificate'
          | 'exclusion'
          | 'merit'
          | 'suspension'
          | 'ultimatum'
          | 'verbal_commendation'
          | 'verbal_warning'
          | 'written_commendation'
          | 'written_warning'
        house?: number
        merit_iso_year_and_week?: string
        merit_item?: number
        /**
         * * `merit` - Merit
         * * `demerit` - Demerit
         */
        merit_type?: 'demerit' | 'merit'
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        show_not_reviewed?: boolean
        /**
         * * `pending` - Pending
         * * `rejected` - Rejected
         * * `confirmed` - Confirmed
         * * `deescalated` - De-escalated
         */
        status?: 'confirmed' | 'deescalated' | 'pending' | 'rejected'
        /** @format uuid */
        student?: string
        /** @format float */
        value?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedMeritList, T>, any>({
        path: `/merits/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits
     * @name MeritsCreate
     * @request POST:/merits/
     * @secure
     */,
    meritsCreate: <T extends FetchKeys<Merit>>(
      data: MeritRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Merit, T>, any>({
        path: `/merits/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits
     * @name MeritsRetrieve
     * @request GET:/merits/{id}/
     * @secure
     */,
    meritsRetrieve: <T extends FetchKeys<Merit>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Merit, T>, any>({
        path: `/merits/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits
     * @name MeritsUpdate
     * @request PUT:/merits/{id}/
     * @secure
     */,
    meritsUpdate: <T extends FetchKeys<Merit>>(
      id: number,
      data: MeritRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Merit, T>, any>({
        path: `/merits/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits
     * @name MeritsPartialUpdate
     * @request PATCH:/merits/{id}/
     * @secure
     */,
    meritsPartialUpdate: <T extends FetchKeys<Merit>>(
      id: number,
      data: PatchedMeritRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Merit, T>, any>({
        path: `/merits/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits
     * @name MeritsDestroy
     * @request DELETE:/merits/{id}/
     * @secure
     */,
    meritsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/merits/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits
     * @name MeritsConfirmUpdate
     * @request PUT:/merits/{id}/confirm/
     * @secure
     */,
    meritsConfirmUpdate: <T extends FetchKeys<MeritConfirm>>(
      id: number,
      data: MeritConfirmRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritConfirm, T>, any>({
        path: `/merits/${id}/confirm/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits
     * @name MeritsDeescalateCreate
     * @request POST:/merits/{id}/deescalate/
     * @secure
     */,
    meritsDeescalateCreate: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/merits/${id}/deescalate/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits
     * @name MeritsPreviewSanctionRetrieve
     * @request GET:/merits/{id}/preview-sanction/
     * @secure
     */,
    meritsPreviewSanctionRetrieve: <T extends FetchKeys<MeritSanctionPreview>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritSanctionPreview, T>, PendingMeritsPreview>({
        path: `/merits/${id}/preview-sanction/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits
     * @name MeritsRejectUpdate
     * @request PUT:/merits/{id}/reject/
     * @secure
     */,
    meritsRejectUpdate: <T extends FetchKeys<MeritReject>>(
      id: number,
      data: MeritRejectRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritReject, T>, any>({
        path: `/merits/${id}/reject/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits
     * @name MeritsAvailableFiltersRetrieve
     * @request GET:/merits/available-filters/
     * @secure
     */,
    meritsAvailableFiltersRetrieve: <
      T extends FetchKeys<MeritAvailableFilters>
    >(
      query?: {
        academic_only?: boolean
        /** @format date */
        added_on?: string
        /** @format uuid */
        author?: string
        /** @format uuid */
        course?: string
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /** @format date-time */
        datetime_after?: string
        /** @format date-time */
        datetime_before?: string
        /** @format date-time */
        escalate_until_after?: string
        /** @format date-time */
        escalate_until_before?: string
        /**
         * * `merit` - Merit
         * * `verbal_commendation` - Verbal commendation
         * * `written_commendation` - Written commendation
         * * `certificate` - Certificate
         * * `verbal_warning` - Verbal warning
         * * `written_warning` - Written warning
         * * `ultimatum` - Ultimatum
         * * `suspension` - Suspension
         * * `exclusion` - Exclusion
         */
        escalated_sanction?:
          | 'certificate'
          | 'exclusion'
          | 'merit'
          | 'suspension'
          | 'ultimatum'
          | 'verbal_commendation'
          | 'verbal_warning'
          | 'written_commendation'
          | 'written_warning'
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        house?: number
        merit_iso_year_and_week?: string
        merit_item?: number
        /**
         * * `merit` - Merit
         * * `demerit` - Demerit
         */
        merit_type?: 'demerit' | 'merit'
        /** Which field to use when ordering the results. */
        ordering?: string
        show_not_reviewed?: boolean
        /**
         * * `pending` - Pending
         * * `rejected` - Rejected
         * * `confirmed` - Confirmed
         * * `deescalated` - De-escalated
         */
        status?: 'confirmed' | 'deescalated' | 'pending' | 'rejected'
        /** @format uuid */
        student?: string
        /** @format float */
        value?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritAvailableFilters, T>, any>({
        path: `/merits/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits
     * @name MeritsBehavioursList
     * @request GET:/merits/behaviours/
     * @secure
     */,
    meritsBehavioursList: <T extends FetchKeys<MeritItemBehaviour[]>>(
      query?: {
        academic_only?: boolean
        /** @format date */
        added_on?: string
        /** @format uuid */
        author?: string
        /** @format uuid */
        course?: string
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /** @format date-time */
        datetime_after?: string
        /** @format date-time */
        datetime_before?: string
        /** @format date-time */
        escalate_until_after?: string
        /** @format date-time */
        escalate_until_before?: string
        /**
         * * `merit` - Merit
         * * `verbal_commendation` - Verbal commendation
         * * `written_commendation` - Written commendation
         * * `certificate` - Certificate
         * * `verbal_warning` - Verbal warning
         * * `written_warning` - Written warning
         * * `ultimatum` - Ultimatum
         * * `suspension` - Suspension
         * * `exclusion` - Exclusion
         */
        escalated_sanction?:
          | 'certificate'
          | 'exclusion'
          | 'merit'
          | 'suspension'
          | 'ultimatum'
          | 'verbal_commendation'
          | 'verbal_warning'
          | 'written_commendation'
          | 'written_warning'
        house?: number
        merit_iso_year_and_week?: string
        merit_item?: number
        /**
         * * `merit` - Merit
         * * `demerit` - Demerit
         */
        merit_type?: 'demerit' | 'merit'
        /** Which field to use when ordering the results. */
        ordering?: string
        show_not_reviewed?: boolean
        /**
         * * `pending` - Pending
         * * `rejected` - Rejected
         * * `confirmed` - Confirmed
         * * `deescalated` - De-escalated
         */
        status?: 'confirmed' | 'deescalated' | 'pending' | 'rejected'
        /** @format uuid */
        student?: string
        /** @format float */
        value?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritItemBehaviour[], T>, any>({
        path: `/merits/behaviours/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags merits
     * @name MeritsItemsList
     * @request GET:/merits/items/
     * @secure
     */,
    meritsItemsList: <T extends FetchKeys<PaginatedMeritItemList>>(
      query?: {
        allow_to_select?: boolean
        /**
         * * `week` - Weekly
         * * `quarter` - Quarterly
         * * `semester` - Semesterly
         * * `year` - Yearly
         */
        escalation_expire_after?: 'quarter' | 'semester' | 'week' | 'year'
        name?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /**
         * * `merit` - Merit
         * * `verbal_commendation` - Verbal commendation
         * * `written_commendation` - Written commendation
         * * `certificate` - Certificate
         * * `verbal_warning` - Verbal warning
         * * `written_warning` - Written warning
         * * `ultimatum` - Ultimatum
         * * `suspension` - Suspension
         * * `exclusion` - Exclusion
         */
        sanction?:
          | 'certificate'
          | 'exclusion'
          | 'merit'
          | 'suspension'
          | 'ultimatum'
          | 'verbal_commendation'
          | 'verbal_warning'
          | 'written_commendation'
          | 'written_warning'
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedMeritItemList, T>, any>({
        path: `/merits/items/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags merits
     * @name MeritsItemsCreate
     * @request POST:/merits/items/
     * @secure
     */,
    meritsItemsCreate: <T extends FetchKeys<MeritItem>>(
      data: MeritItemRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritItem, T>, any>({
        path: `/merits/items/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags merits
     * @name MeritsItemsRetrieve
     * @request GET:/merits/items/{id}/
     * @secure
     */,
    meritsItemsRetrieve: <T extends FetchKeys<MeritItem>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritItem, T>, any>({
        path: `/merits/items/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags merits
     * @name MeritsItemsUpdate
     * @request PUT:/merits/items/{id}/
     * @secure
     */,
    meritsItemsUpdate: <T extends FetchKeys<MeritItem>>(
      id: number,
      data: MeritItemRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritItem, T>, any>({
        path: `/merits/items/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags merits
     * @name MeritsItemsPartialUpdate
     * @request PATCH:/merits/items/{id}/
     * @secure
     */,
    meritsItemsPartialUpdate: <T extends FetchKeys<MeritItem>>(
      id: number,
      data: PatchedMeritItemRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritItem, T>, any>({
        path: `/merits/items/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags merits
     * @name MeritsItemsAvailableFiltersRetrieve
     * @request GET:/merits/items/available-filters/
     * @secure
     */,
    meritsItemsAvailableFiltersRetrieve: <
      T extends FetchKeys<MeritItemAvailableFilters>
    >(
      query?: {
        allow_to_select?: boolean
        /**
         * * `week` - Weekly
         * * `quarter` - Quarterly
         * * `semester` - Semesterly
         * * `year` - Yearly
         */
        escalation_expire_after?: 'quarter' | 'semester' | 'week' | 'year'
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        name?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /**
         * * `merit` - Merit
         * * `verbal_commendation` - Verbal commendation
         * * `written_commendation` - Written commendation
         * * `certificate` - Certificate
         * * `verbal_warning` - Verbal warning
         * * `written_warning` - Written warning
         * * `ultimatum` - Ultimatum
         * * `suspension` - Suspension
         * * `exclusion` - Exclusion
         */
        sanction?:
          | 'certificate'
          | 'exclusion'
          | 'merit'
          | 'suspension'
          | 'ultimatum'
          | 'verbal_commendation'
          | 'verbal_warning'
          | 'written_commendation'
          | 'written_warning'
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritItemAvailableFilters, T>, any>({
        path: `/merits/items/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Handler method for HTTP 'OPTIONS' request.
     *
     * @tags merits
     * @name MeritsItemsOptionsRetrieve
     * @request GET:/merits/items/options/
     * @secure
     */,
    meritsItemsOptionsRetrieve: <T extends FetchKeys<MeritItemOptions>>(
      query?: {
        allow_to_select?: boolean
        /**
         * * `week` - Weekly
         * * `quarter` - Quarterly
         * * `semester` - Semesterly
         * * `year` - Yearly
         */
        escalation_expire_after?: 'quarter' | 'semester' | 'week' | 'year'
        name?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /**
         * * `merit` - Merit
         * * `verbal_commendation` - Verbal commendation
         * * `written_commendation` - Written commendation
         * * `certificate` - Certificate
         * * `verbal_warning` - Verbal warning
         * * `written_warning` - Written warning
         * * `ultimatum` - Ultimatum
         * * `suspension` - Suspension
         * * `exclusion` - Exclusion
         */
        sanction?:
          | 'certificate'
          | 'exclusion'
          | 'merit'
          | 'suspension'
          | 'ultimatum'
          | 'verbal_commendation'
          | 'verbal_warning'
          | 'written_commendation'
          | 'written_warning'
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritItemOptions, T>, any>({
        path: `/merits/items/options/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits
     * @name MeritsStatisticsRetrieve
     * @request GET:/merits/statistics/
     * @secure
     */,
    meritsStatisticsRetrieve: <T extends FetchKeys<MeritStatistics>>(
      query?: {
        academic_only?: boolean
        /** @format date */
        added_on?: string
        /** @format uuid */
        author?: string
        /** @format uuid */
        course?: string
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /** @format date-time */
        datetime_after?: string
        /** @format date-time */
        datetime_before?: string
        /** @format date-time */
        escalate_until_after?: string
        /** @format date-time */
        escalate_until_before?: string
        /**
         * * `merit` - Merit
         * * `verbal_commendation` - Verbal commendation
         * * `written_commendation` - Written commendation
         * * `certificate` - Certificate
         * * `verbal_warning` - Verbal warning
         * * `written_warning` - Written warning
         * * `ultimatum` - Ultimatum
         * * `suspension` - Suspension
         * * `exclusion` - Exclusion
         */
        escalated_sanction?:
          | 'certificate'
          | 'exclusion'
          | 'merit'
          | 'suspension'
          | 'ultimatum'
          | 'verbal_commendation'
          | 'verbal_warning'
          | 'written_commendation'
          | 'written_warning'
        house?: number
        merit_iso_year_and_week?: string
        merit_item?: number
        /**
         * * `merit` - Merit
         * * `demerit` - Demerit
         */
        merit_type?: 'demerit' | 'merit'
        /** Which field to use when ordering the results. */
        ordering?: string
        show_not_reviewed?: boolean
        /**
         * * `pending` - Pending
         * * `rejected` - Rejected
         * * `confirmed` - Confirmed
         * * `deescalated` - De-escalated
         */
        status?: 'confirmed' | 'deescalated' | 'pending' | 'rejected'
        /** @format uuid */
        student?: string
        /** @format float */
        value?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritStatistics, T>, any>({
        path: `/merits/statistics/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  meritsExchange = {
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits-exchange
     * @name MeritsExchangeList
     * @request GET:/merits-exchange/
     * @secure
     */
    meritsExchangeList: <T extends FetchKeys<PaginatedAdminMeritRewardList>>(
      query?: {
        house?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter?: string
        resourcetype?: string
        school_year?: string
        /**
         * * `pending` - Pending
         * * `pending_attendees` - Pending attendees
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `done` - Done
         * * `cancelled` - Cancelled
         * * `expired` - Expired
         */
        status?:
          | 'approved'
          | 'cancelled'
          | 'done'
          | 'expired'
          | 'pending'
          | 'pending_attendees'
          | 'rejected'
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAdminMeritRewardList, T>, any>({
        path: `/merits-exchange/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits-exchange
     * @name MeritsExchangeRetrieve
     * @request GET:/merits-exchange/{id}/
     * @secure
     */,
    meritsExchangeRetrieve: <T extends FetchKeys<AdminMeritReward>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminMeritReward, T>, any>({
        path: `/merits-exchange/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits-exchange
     * @name MeritsExchangeApproveCreate
     * @request POST:/merits-exchange/{id}/approve/
     * @secure
     */,
    meritsExchangeApproveCreate: <T extends FetchKeys<MeritRewardApprove>>(
      id: string,
      data: MeritRewardApproveRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritRewardApprove, T>, any>({
        path: `/merits-exchange/${id}/approve/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits-exchange
     * @name MeritsExchangeAvailableStudentsList
     * @request GET:/merits-exchange/{id}/available-students/
     * @secure
     */,
    meritsExchangeAvailableStudentsList: <
      T extends FetchKeys<MeritsExchangeAvailableStudents[]>
    >(
      id: string,
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritsExchangeAvailableStudents[], T>, any>({
        path: `/merits-exchange/${id}/available-students/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits-exchange
     * @name MeritsExchangeCancelCreate
     * @request POST:/merits-exchange/{id}/cancel/
     * @secure
     */,
    meritsExchangeCancelCreate: <T extends FetchKeys<MeritRewardReject>>(
      id: string,
      data: MeritRewardRejectRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritRewardReject, T>, any>({
        path: `/merits-exchange/${id}/cancel/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits-exchange
     * @name MeritsExchangeRejectCreate
     * @request POST:/merits-exchange/{id}/reject/
     * @secure
     */,
    meritsExchangeRejectCreate: <T extends FetchKeys<MeritRewardReject>>(
      id: string,
      data: MeritRewardRejectRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritRewardReject, T>, any>({
        path: `/merits-exchange/${id}/reject/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits-exchange
     * @name MeritsExchangeAvailableFiltersRetrieve
     * @request GET:/merits-exchange/available-filters/
     * @secure
     */,
    meritsExchangeAvailableFiltersRetrieve: <
      T extends FetchKeys<FilterRepresentationResponseSchema>
    >(
      query?: {
        house?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        quarter?: string
        resourcetype?: string
        school_year?: string
        /**
         * * `pending` - Pending
         * * `pending_attendees` - Pending attendees
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `done` - Done
         * * `cancelled` - Cancelled
         * * `expired` - Expired
         */
        status?:
          | 'approved'
          | 'cancelled'
          | 'done'
          | 'expired'
          | 'pending'
          | 'pending_attendees'
          | 'rejected'
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FilterRepresentationResponseSchema, T>, any>({
        path: `/merits-exchange/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags merits-exchange
     * @name MeritsExchangeDonationSummaryRetrieve
     * @request GET:/merits-exchange/donation-summary/
     * @secure
     */,
    meritsExchangeDonationSummaryRetrieve: <
      T extends FetchKeys<MeritsRewardMakeAnImpactSummary>
    >(
      query?: {
        house?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        quarter?: string
        resourcetype?: string
        school_year?: string
        /**
         * * `pending` - Pending
         * * `pending_attendees` - Pending attendees
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `done` - Done
         * * `cancelled` - Cancelled
         * * `expired` - Expired
         */
        status?:
          | 'approved'
          | 'cancelled'
          | 'done'
          | 'expired'
          | 'pending'
          | 'pending_attendees'
          | 'rejected'
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritsRewardMakeAnImpactSummary, T>, any>({
        path: `/merits-exchange/donation-summary/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  missionControl = {
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags mission-control
     * @name MissionControlList
     * @request GET:/mission-control/
     * @secure
     */
    missionControlList: <T extends FetchKeys<PaginatedMissionControlList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedMissionControlList, T>, any>({
        path: `/mission-control/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags mission-control
     * @name MissionControlCreate
     * @request POST:/mission-control/
     * @secure
     */,
    missionControlCreate: <T extends FetchKeys<MissionControl>>(
      data: MissionControlRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MissionControl, T>, any>({
        path: `/mission-control/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlLogsRetrieve
     * @request GET:/mission-control/{studentId}/logs/
     * @secure
     */,
    missionControlLogsRetrieve: <T extends FetchKeys<MissionControl>>(
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MissionControl, T>, any>({
        path: `/mission-control/${studentId}/logs/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags mission-control
     * @name MissionControlRetrieve
     * @request GET:/mission-control/{student}/
     * @secure
     */,
    missionControlRetrieve: <T extends FetchKeys<MissionControl>>(
      student: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MissionControl, T>, any>({
        path: `/mission-control/${student}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags mission-control
     * @name MissionControlUpdate
     * @request PUT:/mission-control/{student}/
     * @secure
     */,
    missionControlUpdate: <T extends FetchKeys<MissionControl>>(
      student: string,
      data: MissionControlRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MissionControl, T>, any>({
        path: `/mission-control/${student}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags mission-control
     * @name MissionControlPartialUpdate
     * @request PATCH:/mission-control/{student}/
     * @secure
     */,
    missionControlPartialUpdate: <T extends FetchKeys<MissionControl>>(
      student: string,
      data: PatchedMissionControlRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MissionControl, T>, any>({
        path: `/mission-control/${student}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin that automatically updates user's last_update timestamp on CRUD operations. By default, expects the model to have a 'user' field. Override user_field_name or get_users_from_instance() for different configurations. For models where the instance IS a User (e.g., Parent, Student), you don't need this mixin as last_update will update automatically via auto_now=True.
     *
     * @tags mission-control
     * @name MissionControlDestroy
     * @request DELETE:/mission-control/{student}/
     * @secure
     */,
    missionControlDestroy: <T extends FetchKeys<void>>(
      student: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/mission-control/${student}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlCompetenciesList
     * @request GET:/mission-control/competencies/
     * @secure
     */,
    missionControlCompetenciesList: <
      T extends FetchKeys<PaginatedCompetencyList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCompetencyList, T>, any>({
        path: `/mission-control/competencies/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlCompetenciesRetrieve
     * @request GET:/mission-control/competencies/{id}/
     * @secure
     */,
    missionControlCompetenciesRetrieve: <T extends FetchKeys<Competency>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Competency, T>, any>({
        path: `/mission-control/competencies/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlDegreeAimsList
     * @request GET:/mission-control/degree-aims/
     * @secure
     */,
    missionControlDegreeAimsList: <T extends FetchKeys<PaginatedDegreeAimList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedDegreeAimList, T>, any>({
        path: `/mission-control/degree-aims/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlDegreeAimsCreate
     * @request POST:/mission-control/degree-aims/
     * @secure
     */,
    missionControlDegreeAimsCreate: <T extends FetchKeys<DegreeAim>>(
      data: DegreeAimRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DegreeAim, T>, any>({
        path: `/mission-control/degree-aims/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlDegreeAimsRetrieve
     * @request GET:/mission-control/degree-aims/{id}/
     * @secure
     */,
    missionControlDegreeAimsRetrieve: <T extends FetchKeys<DegreeAim>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DegreeAim, T>, any>({
        path: `/mission-control/degree-aims/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlDegreeAimsUpdate
     * @request PUT:/mission-control/degree-aims/{id}/
     * @secure
     */,
    missionControlDegreeAimsUpdate: <T extends FetchKeys<DegreeAim>>(
      id: number,
      data: DegreeAimRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DegreeAim, T>, any>({
        path: `/mission-control/degree-aims/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlDegreeAimsPartialUpdate
     * @request PATCH:/mission-control/degree-aims/{id}/
     * @secure
     */,
    missionControlDegreeAimsPartialUpdate: <T extends FetchKeys<DegreeAim>>(
      id: number,
      data: PatchedDegreeAimRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DegreeAim, T>, any>({
        path: `/mission-control/degree-aims/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlDegreeAimsDestroy
     * @request DELETE:/mission-control/degree-aims/{id}/
     * @secure
     */,
    missionControlDegreeAimsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/mission-control/degree-aims/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlDegreeAimsStudentsRetrieve
     * @request GET:/mission-control/degree-aims/{id}/students/
     * @secure
     */,
    missionControlDegreeAimsStudentsRetrieve: <
      T extends FetchKeys<DegreeAimStudents>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DegreeAimStudents, T>, any>({
        path: `/mission-control/degree-aims/${id}/students/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlInterestsList
     * @request GET:/mission-control/interests/
     * @secure
     */,
    missionControlInterestsList: <T extends FetchKeys<PaginatedInterestList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedInterestList, T>, any>({
        path: `/mission-control/interests/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlInterestsRetrieve
     * @request GET:/mission-control/interests/{id}/
     * @secure
     */,
    missionControlInterestsRetrieve: <T extends FetchKeys<Interest>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Interest, T>, any>({
        path: `/mission-control/interests/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlLogChoicesRetrieve
     * @request GET:/mission-control/log-choices/
     * @secure
     */,
    missionControlLogChoicesRetrieve: <T extends FetchKeys<void>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/mission-control/log-choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlRequirementsCreditsImportedCreditsHistoryDestroy
     * @request DELETE:/mission-control/requirements/credits/imported-credits-history/{id}/
     * @secure
     */,
    missionControlRequirementsCreditsImportedCreditsHistoryDestroy: <
      T extends FetchKeys<void>
    >(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/mission-control/requirements/credits/imported-credits-history/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlRequirementsCreditsStudentsRetrieve
     * @request GET:/mission-control/requirements/credits/students/{studentId}/
     * @secure
     */,
    missionControlRequirementsCreditsStudentsRetrieve: <
      T extends FetchKeys<void>
    >(
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/mission-control/requirements/credits/students/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlRequirementsCreditsStudentsImportHistoryCreate
     * @request POST:/mission-control/requirements/credits/students/{studentId}/import-history/
     * @secure
     */,
    missionControlRequirementsCreditsStudentsImportHistoryCreate: <
      T extends FetchKeys<void>
    >(
      studentId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/mission-control/requirements/credits/students/${studentId}/import-history/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlRequirementsStudentsList
     * @request GET:/mission-control/requirements/students/{studentId}/
     * @secure
     */,
    missionControlRequirementsStudentsList: <
      T extends FetchKeys<PaginatedStudentCourseRequirementList>
    >(
      studentId: string,
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentCourseRequirementList, T>, any>({
        path: `/mission-control/requirements/students/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlRequirementsStudentsUpdate
     * @request PUT:/mission-control/requirements/students/{studentId}/
     * @secure
     */,
    missionControlRequirementsStudentsUpdate: <
      T extends FetchKeys<StudentCourseRequirement>
    >(
      studentId: string,
      data: StudentCourseRequirementRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentCourseRequirement, T>, any>({
        path: `/mission-control/requirements/students/${studentId}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlRequirementsStudentsPartialUpdate
     * @request PATCH:/mission-control/requirements/students/{studentId}/
     * @secure
     */,
    missionControlRequirementsStudentsPartialUpdate: <
      T extends FetchKeys<StudentCourseRequirement>
    >(
      studentId: string,
      data: PatchedStudentCourseRequirementRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentCourseRequirement, T>, any>({
        path: `/mission-control/requirements/students/${studentId}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlSkillsList
     * @request GET:/mission-control/skills/
     * @secure
     */,
    missionControlSkillsList: <T extends FetchKeys<PaginatedSkillList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedSkillList, T>, any>({
        path: `/mission-control/skills/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlSkillsRetrieve
     * @request GET:/mission-control/skills/{id}/
     * @secure
     */,
    missionControlSkillsRetrieve: <T extends FetchKeys<Skill>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Skill, T>, any>({
        path: `/mission-control/skills/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlTaeExperiencesList
     * @request GET:/mission-control/tae/experiences/
     * @secure
     */,
    missionControlTaeExperiencesList: <
      T extends FetchKeys<PaginatedTAEExperienceList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTAEExperienceList, T>, any>({
        path: `/mission-control/tae/experiences/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlTaeExperiencesRetrieve
     * @request GET:/mission-control/tae/experiences/{id}/
     * @secure
     */,
    missionControlTaeExperiencesRetrieve: <T extends FetchKeys<TAEExperience>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEExperience, T>, any>({
        path: `/mission-control/tae/experiences/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlTaeExperiencesStudentsList
     * @request GET:/mission-control/tae/experiences/students/{studentId}/
     * @secure
     */,
    missionControlTaeExperiencesStudentsList: <
      T extends FetchKeys<PaginatedStudentTAEExperienceList>
    >(
      studentId: string,
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentTAEExperienceList, T>, any>({
        path: `/mission-control/tae/experiences/students/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlTaeExperiencesStudentsUpdate
     * @request PUT:/mission-control/tae/experiences/students/{studentId}/
     * @secure
     */,
    missionControlTaeExperiencesStudentsUpdate: <
      T extends FetchKeys<StudentTAEExperienceUpdate>
    >(
      studentId: string,
      data: StudentTAEExperienceUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTAEExperienceUpdate, T>, any>({
        path: `/mission-control/tae/experiences/students/${studentId}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlTaeExperiencesStudentsPartialUpdate
     * @request PATCH:/mission-control/tae/experiences/students/{studentId}/
     * @secure
     */,
    missionControlTaeExperiencesStudentsPartialUpdate: <
      T extends FetchKeys<StudentTAEExperienceUpdate>
    >(
      studentId: string,
      data: PatchedStudentTAEExperienceUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTAEExperienceUpdate, T>, any>({
        path: `/mission-control/tae/experiences/students/${studentId}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlTaeProjectsStudentsList
     * @request GET:/mission-control/tae/projects/students/{studentId}/
     * @secure
     */,
    missionControlTaeProjectsStudentsList: <
      T extends FetchKeys<PaginatedStudentTAEProjectList>
    >(
      studentId: string,
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentTAEProjectList, T>, any>({
        path: `/mission-control/tae/projects/students/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlTaeProjectsStudentsUpdate
     * @request PUT:/mission-control/tae/projects/students/{studentId}/
     * @secure
     */,
    missionControlTaeProjectsStudentsUpdate: <
      T extends FetchKeys<StudentTAEProjectUpdate>
    >(
      studentId: string,
      data: StudentTAEProjectUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTAEProjectUpdate, T>, any>({
        path: `/mission-control/tae/projects/students/${studentId}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlTaeProjectsStudentsPartialUpdate
     * @request PATCH:/mission-control/tae/projects/students/{studentId}/
     * @secure
     */,
    missionControlTaeProjectsStudentsPartialUpdate: <
      T extends FetchKeys<StudentTAEProjectUpdate>
    >(
      studentId: string,
      data: PatchedStudentTAEProjectUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTAEProjectUpdate, T>, any>({
        path: `/mission-control/tae/projects/students/${studentId}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlTaeSportPackagesList
     * @request GET:/mission-control/tae/sport-packages/{studentId}/
     * @secure
     */,
    missionControlTaeSportPackagesList: <
      T extends FetchKeys<StudentTAESportPackage[]>
    >(
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTAESportPackage[], T>, any>({
        path: `/mission-control/tae/sport-packages/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags mission-control
     * @name MissionControlTaeSportPackagesUpdate
     * @request PUT:/mission-control/tae/sport-packages/{studentId}/
     * @secure
     */,
    missionControlTaeSportPackagesUpdate: <
      T extends FetchKeys<StudentTAESportPackagePutResponse>
    >(
      studentId: string,
      data: StudentTAESportPackageRequest[],
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTAESportPackagePutResponse, T>, any>({
        path: `/mission-control/tae/sport-packages/${studentId}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
  }
  notifications = {
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags notifications
     * @name NotificationsList
     * @request GET:/notifications/
     * @secure
     */
    notificationsList: <T extends FetchKeys<PaginatedNotificationList>>(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        was_read?: boolean

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedNotificationList, T>, any>({
        path: `/notifications/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags notifications
     * @name NotificationsRetrieve
     * @request GET:/notifications/{id}/
     * @secure
     */,
    notificationsRetrieve: <T extends FetchKeys<Notification>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Notification, T>, any>({
        path: `/notifications/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags notifications
     * @name NotificationsMarkAsReadCreate
     * @request POST:/notifications/{id}/mark-as-read/
     * @secure
     */,
    notificationsMarkAsReadCreate: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/notifications/${id}/mark-as-read/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags notifications
     * @name NotificationsMarkAllAsReadCreate
     * @request POST:/notifications/mark-all-as-read/
     * @secure
     */,
    notificationsMarkAllAsReadCreate: <T extends FetchKeys<void>>(
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/notifications/mark-all-as-read/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags notifications
     * @name NotificationsMarkAsReadByClassNameCreate
     * @request POST:/notifications/mark-as-read-by-class-name/
     * @secure
     */,
    notificationsMarkAsReadByClassNameCreate: <
      T extends FetchKeys<NotificationClassName>
    >(
      data: NotificationClassNameRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NotificationClassName, T>, any>({
        path: `/notifications/mark-as-read-by-class-name/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags notifications
     * @name NotificationsPushRegisterDeviceCreate
     * @request POST:/notifications/push/register-device/
     * @secure
     */,
    notificationsPushRegisterDeviceCreate: <
      T extends FetchKeys<RegisterDevice>
    >(
      data: RegisterDeviceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RegisterDevice, T>, any>({
        path: `/notifications/push/register-device/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags notifications
     * @name NotificationsUnreadRetrieve
     * @request GET:/notifications/unread/
     * @secure
     */,
    notificationsUnreadRetrieve: <T extends FetchKeys<number>>(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        was_read?: boolean

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<number, T>, any>({
        path: `/notifications/unread/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  oauth = {
    /**
     * @description View will login or signin (create) the user from social oauth2.0 provider. **Input** (default serializer_class_in): { "provider": "facebook", "code": "AQBPBBTjbdnehj51" } + optional "redirect_uri": "/relative/or/absolute/redirect/uri" **Output**: user data in serializer_class format
     *
     * @tags oauth
     * @name OauthLoginSocialTokenCreate
     * @request POST:/oauth/login/social/token/{provider}/
     * @secure
     */
    oauthLoginSocialTokenCreate: <T extends FetchKeys<Knox>>(
      provider: string,
      data: IdTokenOAuth2InputRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Knox, T>, any>({
        path: `/oauth/login/social/token/${provider}/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
  }
  parent = {
    /**
     * No description
     *
     * @tags parent
     * @name ParentContactMessagesCreate
     * @request POST:/parent/contact/messages/
     * @secure
     */
    parentContactMessagesCreate: <T extends FetchKeys<ParentContactMessage>>(
      data: ParentContactMessageRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentContactMessage, T>, any>({
        path: `/parent/contact/messages/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentContactMessagesSubjectsList
     * @request GET:/parent/contact/messages/subjects/
     * @secure
     */,
    parentContactMessagesSubjectsList: <
      T extends FetchKeys<ParentContactMessageSubjects[]>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentContactMessageSubjects[], T>, any>({
        path: `/parent/contact/messages/subjects/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags parent
     * @name ParentCoursesList
     * @request GET:/parent/courses/
     * @secure
     */,
    parentCoursesList: <T extends FetchKeys<PaginatedParentCoursesList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /** @format uuid */
        student?: string
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedParentCoursesList, T>, any>({
        path: `/parent/courses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags parent
     * @name ParentCoursesRetrieve
     * @request GET:/parent/courses/{courseId}/
     * @secure
     */,
    parentCoursesRetrieve: <T extends FetchKeys<ParentCourses>>(
      courseId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentCourses, T>, any>({
        path: `/parent/courses/${courseId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags parent
     * @name ParentCoursesYearsList
     * @request GET:/parent/courses/years/
     * @secure
     */,
    parentCoursesYearsList: <T extends FetchKeys<StudentCourseYearResponse[]>>(
      query?: {
        /** A search term. */
        search?: string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentCourseYearResponse[], T>, any>({
        path: `/parent/courses/years/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentFavouritesList
     * @request GET:/parent/favourites/
     * @secure
     */,
    parentFavouritesList: <T extends FetchKeys<FavouriteWidget[]>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FavouriteWidget[], T>, any>({
        path: `/parent/favourites/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags parent
     * @name ParentFilesForParentsList
     * @request GET:/parent/files-for-parents/
     * @secure
     */,
    parentFilesForParentsList: <T extends FetchKeys<PaginatedParentsFileList>>(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedParentsFileList, T>, any>({
        path: `/parent/files-for-parents/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentInfoboardsFeedList
     * @request GET:/parent/infoboards/feed/
     * @secure
     */,
    parentInfoboardsFeedList: <T extends FetchKeys<PaginatedFeedList>>(
      query?: {
        include_widgets?: (
          | 'BoardingAnnouncementWidget'
          | 'CelebrationWidget'
          | 'CongratulationsWidget'
          | 'EventAgendaWidget'
          | 'EventInfoWidget'
          | 'EventInfoWithPhotoWidget'
          | 'InfoWithVideoWidget'
          | 'JournalWidget'
          | 'MenuWidget'
          | 'PosterWidget'
          | 'StudentArtGalleryWidget'
          | 'VariantManualCongratulationsWidget'
          | 'all'
        )[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedFeedList, T>, any>({
        path: `/parent/infoboards/feed/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags parent
     * @name ParentInfoboardsMessagesList
     * @request GET:/parent/infoboards/messages/
     * @secure
     */,
    parentInfoboardsMessagesList: <
      T extends FetchKeys<PaginatedParentMessageList>
    >(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedParentMessageList, T>, any>({
        path: `/parent/infoboards/messages/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags parent
     * @name ParentInfoboardsMessagesRetrieve
     * @request GET:/parent/infoboards/messages/{id}/
     * @secure
     */,
    parentInfoboardsMessagesRetrieve: <T extends FetchKeys<ParentMessage>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentMessage, T>, any>({
        path: `/parent/infoboards/messages/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags parent
     * @name ParentLeavesList
     * @request GET:/parent/leaves/
     * @secure
     */,
    parentLeavesList: <T extends FetchKeys<PaginatedParentLeaveRequestList>>(
      query?: {
        /** @format date-time */
        date_after?: string
        /** @format date-time */
        date_before?: string
        /**
         * * `0` - Weekend
         * * `1` - Holiday
         * * `2` - Other
         * * `3` - Arrival
         */
        leave_type?: (0 | 1 | 2 | 3)[]
        /**
         * Ordering
         *
         * * `order_date` - Order date
         * * `-order_date` - Order date (descending)
         */
        ordering?: ('-order_date' | 'order_date')[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /**
         * * `tentative` - Pending guardian
         * * `confirmed_contact_person` - Pending academic
         * * `confirmed_academic` - Pending boarding
         * * `changed` - Changed
         * * `confirmed` - Approved
         * * `declined` - Rejected
         * * `declined_contact_person` - Rejected by guardian
         * * `declined_academic` - Rejected by academic
         * * `canceled` - Canceled by student
         * * `expired` - Expired
         */
        status?: (
          | 'canceled'
          | 'changed'
          | 'confirmed'
          | 'confirmed_academic'
          | 'confirmed_contact_person'
          | 'declined'
          | 'declined_academic'
          | 'declined_contact_person'
          | 'expired'
          | 'tentative'
        )[]
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedParentLeaveRequestList, T>, any>({
        path: `/parent/leaves/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags parent
     * @name ParentLeavesRetrieve
     * @request GET:/parent/leaves/{id}/
     * @secure
     */,
    parentLeavesRetrieve: <T extends FetchKeys<ParentLeaveRequest>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentLeaveRequest, T>, any>({
        path: `/parent/leaves/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags parent
     * @name ParentLeavesApproveCreate
     * @request POST:/parent/leaves/{id}/approve/
     * @secure
     */,
    parentLeavesApproveCreate: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/parent/leaves/${id}/approve/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags parent
     * @name ParentLeavesRejectCreate
     * @request POST:/parent/leaves/{id}/reject/
     * @secure
     */,
    parentLeavesRejectCreate: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/parent/leaves/${id}/reject/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags parent
     * @name ParentLeavesStatusesList
     * @request GET:/parent/leaves/statuses/
     * @secure
     */,
    parentLeavesStatusesList: <T extends FetchKeys<ParentLeaveRequestStatus[]>>(
      query?: {
        /** @format date-time */
        date_after?: string
        /** @format date-time */
        date_before?: string
        /**
         * * `0` - Weekend
         * * `1` - Holiday
         * * `2` - Other
         * * `3` - Arrival
         */
        leave_type?: (0 | 1 | 2 | 3)[]
        /**
         * Ordering
         *
         * * `order_date` - Order date
         * * `-order_date` - Order date (descending)
         */
        ordering?: ('-order_date' | 'order_date')[]
        /** A search term. */
        search?: string
        /**
         * * `tentative` - Pending guardian
         * * `confirmed_contact_person` - Pending academic
         * * `confirmed_academic` - Pending boarding
         * * `changed` - Changed
         * * `confirmed` - Approved
         * * `declined` - Rejected
         * * `declined_contact_person` - Rejected by guardian
         * * `declined_academic` - Rejected by academic
         * * `canceled` - Canceled by student
         * * `expired` - Expired
         */
        status?: (
          | 'canceled'
          | 'changed'
          | 'confirmed'
          | 'confirmed_academic'
          | 'confirmed_contact_person'
          | 'declined'
          | 'declined_academic'
          | 'declined_contact_person'
          | 'expired'
          | 'tentative'
        )[]
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentLeaveRequestStatus[], T>, any>({
        path: `/parent/leaves/statuses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags parent
     * @name ParentLeavesTypeChoicesList
     * @request GET:/parent/leaves/type-choices/
     * @secure
     */,
    parentLeavesTypeChoicesList: <
      T extends FetchKeys<ParentLeaveRequestType[]>
    >(
      query?: {
        /** @format date-time */
        date_after?: string
        /** @format date-time */
        date_before?: string
        /**
         * * `0` - Weekend
         * * `1` - Holiday
         * * `2` - Other
         * * `3` - Arrival
         */
        leave_type?: (0 | 1 | 2 | 3)[]
        /**
         * Ordering
         *
         * * `order_date` - Order date
         * * `-order_date` - Order date (descending)
         */
        ordering?: ('-order_date' | 'order_date')[]
        /** A search term. */
        search?: string
        /**
         * * `tentative` - Pending guardian
         * * `confirmed_contact_person` - Pending academic
         * * `confirmed_academic` - Pending boarding
         * * `changed` - Changed
         * * `confirmed` - Approved
         * * `declined` - Rejected
         * * `declined_contact_person` - Rejected by guardian
         * * `declined_academic` - Rejected by academic
         * * `canceled` - Canceled by student
         * * `expired` - Expired
         */
        status?: (
          | 'canceled'
          | 'changed'
          | 'confirmed'
          | 'confirmed_academic'
          | 'confirmed_contact_person'
          | 'declined'
          | 'declined_academic'
          | 'declined_contact_person'
          | 'expired'
          | 'tentative'
        )[]
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentLeaveRequestType[], T>, any>({
        path: `/parent/leaves/type-choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags parent
     * @name ParentMissionControlRetrieve
     * @request GET:/parent/mission-control/
     * @secure
     */,
    parentMissionControlRetrieve: <
      T extends FetchKeys<ParentMissionControlResponse>
    >(
      query?: {
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentMissionControlResponse, T>, any>({
        path: `/parent/mission-control/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentParentsMeRetrieve
     * @request GET:/parent/parents/me/
     * @secure
     */,
    parentParentsMeRetrieve: <T extends FetchKeys<ParentProfile>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentProfile, T>, any>({
        path: `/parent/parents/me/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentParentsMeUpdate
     * @request PUT:/parent/parents/me/
     * @secure
     */,
    parentParentsMeUpdate: <T extends FetchKeys<ParentProfile>>(
      data: ParentProfileRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentProfile, T>, any>({
        path: `/parent/parents/me/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentParentsMePartialUpdate
     * @request PATCH:/parent/parents/me/
     * @secure
     */,
    parentParentsMePartialUpdate: <T extends FetchKeys<ParentProfile>>(
      data: PatchedParentProfileRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentProfile, T>, any>({
        path: `/parent/parents/me/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentParentsStudentBirthdayValidateCreate
     * @request POST:/parent/parents/student-birthday-validate/
     * @secure
     */,
    parentParentsStudentBirthdayValidateCreate: <T extends FetchKeys<boolean>>(
      data: StudentBirthdayValidateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<boolean, T>, any>({
        path: `/parent/parents/student-birthday-validate/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags parent
     * @name ParentReportsReportCardsList
     * @request GET:/parent/reports/report-cards/
     * @secure
     */,
    parentReportsReportCardsList: <
      T extends FetchKeys<PaginatedParentReportCardList>
    >(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedParentReportCardList, T>, any>({
        path: `/parent/reports/report-cards/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags parent
     * @name ParentReportsReportCardsRetrieve
     * @request GET:/parent/reports/report-cards/{id}/
     * @secure
     */,
    parentReportsReportCardsRetrieve: <T extends FetchKeys<ParentReportCard>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentReportCard, T>, any>({
        path: `/parent/reports/report-cards/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags parent
     * @name ParentReportsReportCardsMarkAsReadCreate
     * @request POST:/parent/reports/report-cards/{id}/mark-as-read/
     * @secure
     */,
    parentReportsReportCardsMarkAsReadCreate: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/parent/reports/report-cards/${id}/mark-as-read/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags parent
     * @name ParentStudentsList
     * @request GET:/parent/students/
     * @secure
     */,
    parentStudentsList: <T extends FetchKeys<ParentStudent[]>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentStudent[], T>, any>({
        path: `/parent/students/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentStudentsIdpRetrieve
     * @request GET:/parent/students-idp/{id}/
     * @secure
     */,
    parentStudentsIdpRetrieve: <T extends FetchKeys<ParentIdpPlan>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentIdpPlan, T>, any>({
        path: `/parent/students-idp/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentStudentsIdpApproveCreate
     * @request POST:/parent/students-idp/{id}/approve/
     * @secure
     */,
    parentStudentsIdpApproveCreate: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/parent/students-idp/${id}/approve/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentStudentsIdpChangesList
     * @request GET:/parent/students-idp/{id}/changes/
     * @secure
     */,
    parentStudentsIdpChangesList: <T extends FetchKeys<ParentIdpPlanChanges[]>>(
      id: string,
      query?: {
        /** @format uuid */
        student?: string
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentIdpPlanChanges[], T>, any>({
        path: `/parent/students-idp/${id}/changes/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentStudentsIdpDiscussCreate
     * @request POST:/parent/students-idp/{id}/discuss/
     * @secure
     */,
    parentStudentsIdpDiscussCreate: <T extends FetchKeys<void>>(
      id: string,
      data: ParentDiscussIdpPlanRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/parent/students-idp/${id}/discuss/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentStudentsIdpActiveRetrieve
     * @request GET:/parent/students-idp/active/
     * @secure
     */,
    parentStudentsIdpActiveRetrieve: <T extends FetchKeys<ParentIdpPlan>>(
      query?: {
        /** @format uuid */
        student?: string
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentIdpPlan, T>, any>({
        path: `/parent/students-idp/active/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentStudentsIdpNewRetrieve
     * @request GET:/parent/students-idp/new/
     * @secure
     */,
    parentStudentsIdpNewRetrieve: <T extends FetchKeys<ParentIdpPlan>>(
      query?: {
        /** @format uuid */
        student?: string
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentIdpPlan, T>, any>({
        path: `/parent/students-idp/new/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentStudentsIdpStatusHistoryList
     * @request GET:/parent/students-idp/status-history/
     * @secure
     */,
    parentStudentsIdpStatusHistoryList: <
      T extends FetchKeys<PaginatedParentIdpPlanStatusHistoryList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** @format uuid */
        student?: string
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedParentIdpPlanStatusHistoryList, T>, any>({
        path: `/parent/students-idp/status-history/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentStudentsIdpYearsList
     * @request GET:/parent/students-idp/years/
     * @secure
     */,
    parentStudentsIdpYearsList: <T extends FetchKeys<ParentIdpPlanYears[]>>(
      query?: {
        /** @format uuid */
        student?: string
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentIdpPlanYears[], T>, any>({
        path: `/parent/students-idp/years/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags parent
     * @name ParentStudentsRetrieve
     * @request GET:/parent/students/{id}/
     * @secure
     */,
    parentStudentsRetrieve: <T extends FetchKeys<ParentStudent>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentStudent, T>, any>({
        path: `/parent/students/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentTaeV2Retrieve
     * @request GET:/parent/tae-v2/{id}/
     * @secure
     */,
    parentTaeV2Retrieve: <T extends FetchKeys<ParentTAEStudentEnrolmentV2>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentTAEStudentEnrolmentV2, T>, any>({
        path: `/parent/tae-v2/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentTaeV2CommentsList
     * @request GET:/parent/tae-v2/comments/
     * @secure
     */,
    parentTaeV2CommentsList: <
      T extends FetchKeys<PaginatedParentTAEParentCommentV2List>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedParentTAEParentCommentV2List, T>, any>({
        path: `/parent/tae-v2/comments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentTaeV2CommentsCreate
     * @request POST:/parent/tae-v2/comments/
     * @secure
     */,
    parentTaeV2CommentsCreate: <
      T extends FetchKeys<ParentTAEParentCommentCreateV2>
    >(
      data: ParentTAEParentCommentCreateV2Request,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentTAEParentCommentCreateV2, T>, any>({
        path: `/parent/tae-v2/comments/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentTaeV2StatusRetrieve
     * @request GET:/parent/tae-v2/status
     * @secure
     */,
    parentTaeV2StatusRetrieve: <
      T extends FetchKeys<ParentTAEEnrolmentStatusV2>
    >(
      query: {
        /** @format uuid */
        student: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentTAEEnrolmentStatusV2, T>, any>({
        path: `/parent/tae-v2/status`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentTaeRetrieve
     * @request GET:/parent/tae/{id}/
     * @secure
     */,
    parentTaeRetrieve: <T extends FetchKeys<ParentTAEStudentEnrolment>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentTAEStudentEnrolment, T>, any>({
        path: `/parent/tae/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentTaeUpdate
     * @request PUT:/parent/tae/{id}/
     * @secure
     */,
    parentTaeUpdate: <T extends FetchKeys<ParentTAEStudentEnrolmentUpdate>>(
      id: number,
      data: ParentTAEStudentEnrolmentUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentTAEStudentEnrolmentUpdate, T>, any>({
        path: `/parent/tae/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentTaePartialUpdate
     * @request PATCH:/parent/tae/{id}/
     * @secure
     */,
    parentTaePartialUpdate: <
      T extends FetchKeys<ParentTAEStudentEnrolmentUpdate>
    >(
      id: number,
      data: PatchedParentTAEStudentEnrolmentUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentTAEStudentEnrolmentUpdate, T>, any>({
        path: `/parent/tae/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentTaeCommentsList
     * @request GET:/parent/tae/comments/
     * @secure
     */,
    parentTaeCommentsList: <
      T extends FetchKeys<PaginatedParentTAEParentCommentList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedParentTAEParentCommentList, T>, any>({
        path: `/parent/tae/comments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentTaeCommentsCreate
     * @request POST:/parent/tae/comments/
     * @secure
     */,
    parentTaeCommentsCreate: <
      T extends FetchKeys<ParentTAEParentCommentCreate>
    >(
      data: ParentTAEParentCommentCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentTAEParentCommentCreate, T>, any>({
        path: `/parent/tae/comments/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags parent
     * @name ParentTaeStatusRetrieve
     * @request GET:/parent/tae/status
     * @secure
     */,
    parentTaeStatusRetrieve: <T extends FetchKeys<ParentTAEEnrolmentStatus>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentTAEEnrolmentStatus, T>, any>({
        path: `/parent/tae/status`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  parents = {
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags parents
     * @name ParentsList
     * @request GET:/parents/
     * @secure
     */
    parentsList: <T extends FetchKeys<PaginatedParentList>>(
      query?: {
        /** @format uuid */
        candidate?: string
        /** @format uuid */
        current_company?: string
        /**
         * * `no` - No
         * * `left` - Left
         * * `alumni` - Alumni
         */
        former_student_status?: 'alumni' | 'left' | 'no' | null
        has_active_students?: boolean
        has_app_access?: boolean
        /** @format uuid */
        id?: string
        /**
         * * `AF` - Afghanistan
         * * `AX` - Åland Islands
         * * `AL` - Albania
         * * `DZ` - Algeria
         * * `AS` - American Samoa
         * * `AD` - Andorra
         * * `AO` - Angola
         * * `AI` - Anguilla
         * * `AQ` - Antarctica
         * * `AG` - Antigua and Barbuda
         * * `AR` - Argentina
         * * `AM` - Armenia
         * * `AW` - Aruba
         * * `AU` - Australia
         * * `AT` - Austria
         * * `AZ` - Azerbaijan
         * * `BS` - Bahamas
         * * `BH` - Bahrain
         * * `BD` - Bangladesh
         * * `BB` - Barbados
         * * `BY` - Belarus
         * * `BE` - Belgium
         * * `BZ` - Belize
         * * `BJ` - Benin
         * * `BM` - Bermuda
         * * `BT` - Bhutan
         * * `BO` - Bolivia
         * * `BQ` - Bonaire, Sint Eustatius and Saba
         * * `BA` - Bosnia and Herzegovina
         * * `BW` - Botswana
         * * `BV` - Bouvet Island
         * * `BR` - Brazil
         * * `IO` - British Indian Ocean Territory
         * * `BN` - Brunei
         * * `BG` - Bulgaria
         * * `BF` - Burkina Faso
         * * `BI` - Burundi
         * * `CV` - Cabo Verde
         * * `KH` - Cambodia
         * * `CM` - Cameroon
         * * `CA` - Canada
         * * `KY` - Cayman Islands
         * * `CF` - Central African Republic
         * * `TD` - Chad
         * * `CL` - Chile
         * * `CN` - China
         * * `CX` - Christmas Island
         * * `CC` - Cocos (Keeling) Islands
         * * `CO` - Colombia
         * * `KM` - Comoros
         * * `CG` - Congo
         * * `CD` - Congo (the Democratic Republic of the)
         * * `CK` - Cook Islands
         * * `CR` - Costa Rica
         * * `CI` - Côte d'Ivoire
         * * `HR` - Croatia
         * * `CU` - Cuba
         * * `CW` - Curaçao
         * * `CY` - Cyprus
         * * `CZ` - Czechia
         * * `DK` - Denmark
         * * `DJ` - Djibouti
         * * `DM` - Dominica
         * * `DO` - Dominican Republic
         * * `EC` - Ecuador
         * * `EG` - Egypt
         * * `SV` - El Salvador
         * * `GQ` - Equatorial Guinea
         * * `ER` - Eritrea
         * * `EE` - Estonia
         * * `SZ` - Eswatini
         * * `ET` - Ethiopia
         * * `FK` - Falkland Islands (Malvinas)
         * * `FO` - Faroe Islands
         * * `FJ` - Fiji
         * * `FI` - Finland
         * * `FR` - France
         * * `GF` - French Guiana
         * * `PF` - French Polynesia
         * * `TF` - French Southern Territories
         * * `GA` - Gabon
         * * `GM` - Gambia
         * * `GE` - Georgia
         * * `DE` - Germany
         * * `GH` - Ghana
         * * `GI` - Gibraltar
         * * `GR` - Greece
         * * `GL` - Greenland
         * * `GD` - Grenada
         * * `GP` - Guadeloupe
         * * `GU` - Guam
         * * `GT` - Guatemala
         * * `GG` - Guernsey
         * * `GN` - Guinea
         * * `GW` - Guinea-Bissau
         * * `GY` - Guyana
         * * `HT` - Haiti
         * * `HM` - Heard Island and McDonald Islands
         * * `VA` - Holy See
         * * `HN` - Honduras
         * * `HK` - Hong Kong
         * * `HU` - Hungary
         * * `IS` - Iceland
         * * `IN` - India
         * * `ID` - Indonesia
         * * `IR` - Iran
         * * `IQ` - Iraq
         * * `IE` - Ireland
         * * `IM` - Isle of Man
         * * `IL` - Israel
         * * `IT` - Italy
         * * `JM` - Jamaica
         * * `JP` - Japan
         * * `JE` - Jersey
         * * `JO` - Jordan
         * * `KZ` - Kazakhstan
         * * `KE` - Kenya
         * * `KI` - Kiribati
         * * `XK` - Kosovo
         * * `KW` - Kuwait
         * * `KG` - Kyrgyzstan
         * * `LA` - Laos
         * * `LV` - Latvia
         * * `LB` - Lebanon
         * * `LS` - Lesotho
         * * `LR` - Liberia
         * * `LY` - Libya
         * * `LI` - Liechtenstein
         * * `LT` - Lithuania
         * * `LU` - Luxembourg
         * * `MO` - Macao
         * * `MG` - Madagascar
         * * `MW` - Malawi
         * * `MY` - Malaysia
         * * `MV` - Maldives
         * * `ML` - Mali
         * * `MT` - Malta
         * * `MH` - Marshall Islands
         * * `MQ` - Martinique
         * * `MR` - Mauritania
         * * `MU` - Mauritius
         * * `YT` - Mayotte
         * * `MX` - Mexico
         * * `FM` - Micronesia
         * * `MD` - Moldova
         * * `MC` - Monaco
         * * `MN` - Mongolia
         * * `ME` - Montenegro
         * * `MS` - Montserrat
         * * `MA` - Morocco
         * * `MZ` - Mozambique
         * * `MM` - Myanmar
         * * `NA` - Namibia
         * * `NR` - Nauru
         * * `NP` - Nepal
         * * `NL` - Netherlands
         * * `NC` - New Caledonia
         * * `NZ` - New Zealand
         * * `NI` - Nicaragua
         * * `NE` - Niger
         * * `NG` - Nigeria
         * * `NU` - Niue
         * * `NF` - Norfolk Island
         * * `KP` - North Korea
         * * `MK` - North Macedonia
         * * `MP` - Northern Mariana Islands
         * * `NO` - Norway
         * * `OM` - Oman
         * * `PK` - Pakistan
         * * `PW` - Palau
         * * `PS` - Palestine, State of
         * * `PA` - Panama
         * * `PG` - Papua New Guinea
         * * `PY` - Paraguay
         * * `PE` - Peru
         * * `PH` - Philippines
         * * `PN` - Pitcairn
         * * `PL` - Poland
         * * `PT` - Portugal
         * * `PR` - Puerto Rico
         * * `QA` - Qatar
         * * `RE` - Réunion
         * * `RO` - Romania
         * * `RU` - Russia
         * * `RW` - Rwanda
         * * `BL` - Saint Barthélemy
         * * `SH` - Saint Helena, Ascension and Tristan da Cunha
         * * `KN` - Saint Kitts and Nevis
         * * `LC` - Saint Lucia
         * * `MF` - Saint Martin (French part)
         * * `PM` - Saint Pierre and Miquelon
         * * `VC` - Saint Vincent and the Grenadines
         * * `WS` - Samoa
         * * `SM` - San Marino
         * * `ST` - Sao Tome and Principe
         * * `SA` - Saudi Arabia
         * * `SN` - Senegal
         * * `RS` - Serbia
         * * `SC` - Seychelles
         * * `SL` - Sierra Leone
         * * `SG` - Singapore
         * * `SX` - Sint Maarten (Dutch part)
         * * `SK` - Slovakia
         * * `SI` - Slovenia
         * * `SB` - Solomon Islands
         * * `SO` - Somalia
         * * `ZA` - South Africa
         * * `GS` - South Georgia and the South Sandwich Islands
         * * `KR` - South Korea
         * * `SS` - South Sudan
         * * `ES` - Spain
         * * `LK` - Sri Lanka
         * * `SD` - Sudan
         * * `SR` - Suriname
         * * `SJ` - Svalbard and Jan Mayen
         * * `SE` - Sweden
         * * `CH` - Switzerland
         * * `SY` - Syria
         * * `TW` - Taiwan
         * * `TJ` - Tajikistan
         * * `TZ` - Tanzania
         * * `TH` - Thailand
         * * `TL` - Timor-Leste
         * * `TG` - Togo
         * * `TK` - Tokelau
         * * `TO` - Tonga
         * * `TT` - Trinidad and Tobago
         * * `TN` - Tunisia
         * * `TR` - Türkiye
         * * `TM` - Turkmenistan
         * * `TC` - Turks and Caicos Islands
         * * `TV` - Tuvalu
         * * `UG` - Uganda
         * * `UA` - Ukraine
         * * `AE` - United Arab Emirates
         * * `GB` - United Kingdom
         * * `UM` - United States Minor Outlying Islands
         * * `US` - United States of America
         * * `UY` - Uruguay
         * * `UZ` - Uzbekistan
         * * `VU` - Vanuatu
         * * `VE` - Venezuela
         * * `VN` - Vietnam
         * * `VG` - Virgin Islands (British)
         * * `VI` - Virgin Islands (U.S.)
         * * `WF` - Wallis and Futuna
         * * `EH` - Western Sahara
         * * `YE` - Yemen
         * * `ZM` - Zambia
         * * `ZW` - Zimbabwe
         */
        nationalities?: (
          | 'AD'
          | 'AE'
          | 'AF'
          | 'AG'
          | 'AI'
          | 'AL'
          | 'AM'
          | 'AO'
          | 'AQ'
          | 'AR'
          | 'AS'
          | 'AT'
          | 'AU'
          | 'AW'
          | 'AX'
          | 'AZ'
          | 'BA'
          | 'BB'
          | 'BD'
          | 'BE'
          | 'BF'
          | 'BG'
          | 'BH'
          | 'BI'
          | 'BJ'
          | 'BL'
          | 'BM'
          | 'BN'
          | 'BO'
          | 'BQ'
          | 'BR'
          | 'BS'
          | 'BT'
          | 'BV'
          | 'BW'
          | 'BY'
          | 'BZ'
          | 'CA'
          | 'CC'
          | 'CD'
          | 'CF'
          | 'CG'
          | 'CH'
          | 'CI'
          | 'CK'
          | 'CL'
          | 'CM'
          | 'CN'
          | 'CO'
          | 'CR'
          | 'CU'
          | 'CV'
          | 'CW'
          | 'CX'
          | 'CY'
          | 'CZ'
          | 'DE'
          | 'DJ'
          | 'DK'
          | 'DM'
          | 'DO'
          | 'DZ'
          | 'EC'
          | 'EE'
          | 'EG'
          | 'EH'
          | 'ER'
          | 'ES'
          | 'ET'
          | 'FI'
          | 'FJ'
          | 'FK'
          | 'FM'
          | 'FO'
          | 'FR'
          | 'GA'
          | 'GB'
          | 'GD'
          | 'GE'
          | 'GF'
          | 'GG'
          | 'GH'
          | 'GI'
          | 'GL'
          | 'GM'
          | 'GN'
          | 'GP'
          | 'GQ'
          | 'GR'
          | 'GS'
          | 'GT'
          | 'GU'
          | 'GW'
          | 'GY'
          | 'HK'
          | 'HM'
          | 'HN'
          | 'HR'
          | 'HT'
          | 'HU'
          | 'ID'
          | 'IE'
          | 'IL'
          | 'IM'
          | 'IN'
          | 'IO'
          | 'IQ'
          | 'IR'
          | 'IS'
          | 'IT'
          | 'JE'
          | 'JM'
          | 'JO'
          | 'JP'
          | 'KE'
          | 'KG'
          | 'KH'
          | 'KI'
          | 'KM'
          | 'KN'
          | 'KP'
          | 'KR'
          | 'KW'
          | 'KY'
          | 'KZ'
          | 'LA'
          | 'LB'
          | 'LC'
          | 'LI'
          | 'LK'
          | 'LR'
          | 'LS'
          | 'LT'
          | 'LU'
          | 'LV'
          | 'LY'
          | 'MA'
          | 'MC'
          | 'MD'
          | 'ME'
          | 'MF'
          | 'MG'
          | 'MH'
          | 'MK'
          | 'ML'
          | 'MM'
          | 'MN'
          | 'MO'
          | 'MP'
          | 'MQ'
          | 'MR'
          | 'MS'
          | 'MT'
          | 'MU'
          | 'MV'
          | 'MW'
          | 'MX'
          | 'MY'
          | 'MZ'
          | 'NA'
          | 'NC'
          | 'NE'
          | 'NF'
          | 'NG'
          | 'NI'
          | 'NL'
          | 'NO'
          | 'NP'
          | 'NR'
          | 'NU'
          | 'NZ'
          | 'OM'
          | 'PA'
          | 'PE'
          | 'PF'
          | 'PG'
          | 'PH'
          | 'PK'
          | 'PL'
          | 'PM'
          | 'PN'
          | 'PR'
          | 'PS'
          | 'PT'
          | 'PW'
          | 'PY'
          | 'QA'
          | 'RE'
          | 'RO'
          | 'RS'
          | 'RU'
          | 'RW'
          | 'SA'
          | 'SB'
          | 'SC'
          | 'SD'
          | 'SE'
          | 'SG'
          | 'SH'
          | 'SI'
          | 'SJ'
          | 'SK'
          | 'SL'
          | 'SM'
          | 'SN'
          | 'SO'
          | 'SR'
          | 'SS'
          | 'ST'
          | 'SV'
          | 'SX'
          | 'SY'
          | 'SZ'
          | 'TC'
          | 'TD'
          | 'TF'
          | 'TG'
          | 'TH'
          | 'TJ'
          | 'TK'
          | 'TL'
          | 'TM'
          | 'TN'
          | 'TO'
          | 'TR'
          | 'TT'
          | 'TV'
          | 'TW'
          | 'TZ'
          | 'UA'
          | 'UG'
          | 'UM'
          | 'US'
          | 'UY'
          | 'UZ'
          | 'VA'
          | 'VC'
          | 'VE'
          | 'VG'
          | 'VI'
          | 'VN'
          | 'VU'
          | 'WF'
          | 'WS'
          | 'XK'
          | 'YE'
          | 'YT'
          | 'ZA'
          | 'ZM'
          | 'ZW'
        )[]
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /**
         * * `parent` - Parent
         * * `legal_guardian` - Legal Guardian
         * * `in_legal_guardian_absence` - In Legal Guardian Absence
         * * `arrival_contact` - Arrival Contact
         * * `educational_consultant` - Educational Consultant
         * * `main_invoice_recipient` - Main Invoice Recipient
         * * `invoice_recipient` - Invoice Recipient
         * * `contact_leave` - Contact Leave
         * * `invoice_copy_recipient` - Invoice Copy Recipient
         * * `assistant` - Assistant
         * * `relative` - Relative
         * * `other` - Other
         */
        roles?: (
          | 'arrival_contact'
          | 'assistant'
          | 'contact_leave'
          | 'educational_consultant'
          | 'in_legal_guardian_absence'
          | 'invoice_copy_recipient'
          | 'invoice_recipient'
          | 'legal_guardian'
          | 'main_invoice_recipient'
          | 'other'
          | 'parent'
          | 'relative'
        )[]
        /** A search term. */
        search?: string
        show_inactive?: boolean
        /** @format uuid */
        student?: string
        year_of_rosenberg_entry?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedParentList, T>, any>({
        path: `/parents/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags parents
     * @name ParentsCreate
     * @request POST:/parents/
     * @secure
     */,
    parentsCreate: <T extends FetchKeys<ParentCreateResponse>>(
      data: ParentCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentCreateResponse, T>, any>({
        path: `/parents/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags parents
     * @name ParentsRetrieve
     * @request GET:/parents/{id}/
     * @secure
     */,
    parentsRetrieve: <T extends FetchKeys<ParentDetail>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentDetail, T>, any>({
        path: `/parents/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags parents
     * @name ParentsUpdate
     * @request PUT:/parents/{id}/
     * @secure
     */,
    parentsUpdate: <T extends FetchKeys<ParentDetail>>(
      id: string,
      data: ParentDetailRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentDetail, T>, any>({
        path: `/parents/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags parents
     * @name ParentsPartialUpdate
     * @request PATCH:/parents/{id}/
     * @secure
     */,
    parentsPartialUpdate: <T extends FetchKeys<ParentDetail>>(
      id: string,
      data: PatchedParentDetailRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentDetail, T>, any>({
        path: `/parents/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags parents
     * @name ParentsDestroy
     * @request DELETE:/parents/{id}/
     * @secure
     */,
    parentsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, MessageResponseSchema>({
        path: `/parents/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags parents
     * @name ParentsChangeConnectionPartialUpdate
     * @request PATCH:/parents/{id}/change-connection/{studentId}/
     * @secure
     */,
    parentsChangeConnectionPartialUpdate: <T extends FetchKeys<void>>(
      id: string,
      studentId: string,
      data: PatchedParentStudentContactUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, void>({
        path: `/parents/${id}/change-connection/${studentId}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags parents
     * @name ParentsChangesList
     * @request GET:/parents/{id}/changes/
     * @secure
     */,
    parentsChangesList: <T extends FetchKeys<PaginatedLogEntryHistoryList>>(
      id: string,
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /** Filter history by tabs (OR). Repeat the parameter for multiple tabs, e.g. tabs=basic_information&tabs=notes. Omit the parameter or send an empty selection to include all available tabs. Entries are matched by content type, regardless of changed fields or action. Matching entries retain all their changes. Available tabs: basic_information (Basic Information), notes (Notes). */
        tabs?: ('basic_information' | 'notes')[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedLogEntryHistoryList, T>, any>({
        path: `/parents/${id}/changes/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags parents
     * @name ParentsCreateConnectionPartialUpdate
     * @request PATCH:/parents/{id}/create-connection/
     * @secure
     */,
    parentsCreateConnectionPartialUpdate: <T extends FetchKeys<void>>(
      id: string,
      data: PatchedParentStudentContactCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/parents/${id}/create-connection/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags parents
     * @name ParentsRemoveConnectionPartialUpdate
     * @request PATCH:/parents/{id}/remove-connection/
     * @secure
     */,
    parentsRemoveConnectionPartialUpdate: <T extends FetchKeys<void>>(
      id: string,
      data: PatchedDisconnectParentStudentContactRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, void>({
        path: `/parents/${id}/remove-connection/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags parents
     * @name ParentsAppLoginReportRetrieve
     * @request GET:/parents/app-login-report/
     * @secure
     */,
    parentsAppLoginReportRetrieve: <T extends FetchKeys<File>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<File, T>, any>({
        path: `/parents/app-login-report/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags parents
     * @name ParentsAvailableFiltersRetrieve
     * @request GET:/parents/available-filters/
     * @secure
     */,
    parentsAvailableFiltersRetrieve: <
      T extends FetchKeys<ParentAvailableFilters>
    >(
      query?: {
        /** @format uuid */
        candidate?: string
        /** @format uuid */
        current_company?: string
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        /**
         * * `no` - No
         * * `left` - Left
         * * `alumni` - Alumni
         */
        former_student_status?: 'alumni' | 'left' | 'no' | null
        has_active_students?: boolean
        has_app_access?: boolean
        /** @format uuid */
        id?: string
        /**
         * * `AF` - Afghanistan
         * * `AX` - Åland Islands
         * * `AL` - Albania
         * * `DZ` - Algeria
         * * `AS` - American Samoa
         * * `AD` - Andorra
         * * `AO` - Angola
         * * `AI` - Anguilla
         * * `AQ` - Antarctica
         * * `AG` - Antigua and Barbuda
         * * `AR` - Argentina
         * * `AM` - Armenia
         * * `AW` - Aruba
         * * `AU` - Australia
         * * `AT` - Austria
         * * `AZ` - Azerbaijan
         * * `BS` - Bahamas
         * * `BH` - Bahrain
         * * `BD` - Bangladesh
         * * `BB` - Barbados
         * * `BY` - Belarus
         * * `BE` - Belgium
         * * `BZ` - Belize
         * * `BJ` - Benin
         * * `BM` - Bermuda
         * * `BT` - Bhutan
         * * `BO` - Bolivia
         * * `BQ` - Bonaire, Sint Eustatius and Saba
         * * `BA` - Bosnia and Herzegovina
         * * `BW` - Botswana
         * * `BV` - Bouvet Island
         * * `BR` - Brazil
         * * `IO` - British Indian Ocean Territory
         * * `BN` - Brunei
         * * `BG` - Bulgaria
         * * `BF` - Burkina Faso
         * * `BI` - Burundi
         * * `CV` - Cabo Verde
         * * `KH` - Cambodia
         * * `CM` - Cameroon
         * * `CA` - Canada
         * * `KY` - Cayman Islands
         * * `CF` - Central African Republic
         * * `TD` - Chad
         * * `CL` - Chile
         * * `CN` - China
         * * `CX` - Christmas Island
         * * `CC` - Cocos (Keeling) Islands
         * * `CO` - Colombia
         * * `KM` - Comoros
         * * `CG` - Congo
         * * `CD` - Congo (the Democratic Republic of the)
         * * `CK` - Cook Islands
         * * `CR` - Costa Rica
         * * `CI` - Côte d'Ivoire
         * * `HR` - Croatia
         * * `CU` - Cuba
         * * `CW` - Curaçao
         * * `CY` - Cyprus
         * * `CZ` - Czechia
         * * `DK` - Denmark
         * * `DJ` - Djibouti
         * * `DM` - Dominica
         * * `DO` - Dominican Republic
         * * `EC` - Ecuador
         * * `EG` - Egypt
         * * `SV` - El Salvador
         * * `GQ` - Equatorial Guinea
         * * `ER` - Eritrea
         * * `EE` - Estonia
         * * `SZ` - Eswatini
         * * `ET` - Ethiopia
         * * `FK` - Falkland Islands (Malvinas)
         * * `FO` - Faroe Islands
         * * `FJ` - Fiji
         * * `FI` - Finland
         * * `FR` - France
         * * `GF` - French Guiana
         * * `PF` - French Polynesia
         * * `TF` - French Southern Territories
         * * `GA` - Gabon
         * * `GM` - Gambia
         * * `GE` - Georgia
         * * `DE` - Germany
         * * `GH` - Ghana
         * * `GI` - Gibraltar
         * * `GR` - Greece
         * * `GL` - Greenland
         * * `GD` - Grenada
         * * `GP` - Guadeloupe
         * * `GU` - Guam
         * * `GT` - Guatemala
         * * `GG` - Guernsey
         * * `GN` - Guinea
         * * `GW` - Guinea-Bissau
         * * `GY` - Guyana
         * * `HT` - Haiti
         * * `HM` - Heard Island and McDonald Islands
         * * `VA` - Holy See
         * * `HN` - Honduras
         * * `HK` - Hong Kong
         * * `HU` - Hungary
         * * `IS` - Iceland
         * * `IN` - India
         * * `ID` - Indonesia
         * * `IR` - Iran
         * * `IQ` - Iraq
         * * `IE` - Ireland
         * * `IM` - Isle of Man
         * * `IL` - Israel
         * * `IT` - Italy
         * * `JM` - Jamaica
         * * `JP` - Japan
         * * `JE` - Jersey
         * * `JO` - Jordan
         * * `KZ` - Kazakhstan
         * * `KE` - Kenya
         * * `KI` - Kiribati
         * * `XK` - Kosovo
         * * `KW` - Kuwait
         * * `KG` - Kyrgyzstan
         * * `LA` - Laos
         * * `LV` - Latvia
         * * `LB` - Lebanon
         * * `LS` - Lesotho
         * * `LR` - Liberia
         * * `LY` - Libya
         * * `LI` - Liechtenstein
         * * `LT` - Lithuania
         * * `LU` - Luxembourg
         * * `MO` - Macao
         * * `MG` - Madagascar
         * * `MW` - Malawi
         * * `MY` - Malaysia
         * * `MV` - Maldives
         * * `ML` - Mali
         * * `MT` - Malta
         * * `MH` - Marshall Islands
         * * `MQ` - Martinique
         * * `MR` - Mauritania
         * * `MU` - Mauritius
         * * `YT` - Mayotte
         * * `MX` - Mexico
         * * `FM` - Micronesia
         * * `MD` - Moldova
         * * `MC` - Monaco
         * * `MN` - Mongolia
         * * `ME` - Montenegro
         * * `MS` - Montserrat
         * * `MA` - Morocco
         * * `MZ` - Mozambique
         * * `MM` - Myanmar
         * * `NA` - Namibia
         * * `NR` - Nauru
         * * `NP` - Nepal
         * * `NL` - Netherlands
         * * `NC` - New Caledonia
         * * `NZ` - New Zealand
         * * `NI` - Nicaragua
         * * `NE` - Niger
         * * `NG` - Nigeria
         * * `NU` - Niue
         * * `NF` - Norfolk Island
         * * `KP` - North Korea
         * * `MK` - North Macedonia
         * * `MP` - Northern Mariana Islands
         * * `NO` - Norway
         * * `OM` - Oman
         * * `PK` - Pakistan
         * * `PW` - Palau
         * * `PS` - Palestine, State of
         * * `PA` - Panama
         * * `PG` - Papua New Guinea
         * * `PY` - Paraguay
         * * `PE` - Peru
         * * `PH` - Philippines
         * * `PN` - Pitcairn
         * * `PL` - Poland
         * * `PT` - Portugal
         * * `PR` - Puerto Rico
         * * `QA` - Qatar
         * * `RE` - Réunion
         * * `RO` - Romania
         * * `RU` - Russia
         * * `RW` - Rwanda
         * * `BL` - Saint Barthélemy
         * * `SH` - Saint Helena, Ascension and Tristan da Cunha
         * * `KN` - Saint Kitts and Nevis
         * * `LC` - Saint Lucia
         * * `MF` - Saint Martin (French part)
         * * `PM` - Saint Pierre and Miquelon
         * * `VC` - Saint Vincent and the Grenadines
         * * `WS` - Samoa
         * * `SM` - San Marino
         * * `ST` - Sao Tome and Principe
         * * `SA` - Saudi Arabia
         * * `SN` - Senegal
         * * `RS` - Serbia
         * * `SC` - Seychelles
         * * `SL` - Sierra Leone
         * * `SG` - Singapore
         * * `SX` - Sint Maarten (Dutch part)
         * * `SK` - Slovakia
         * * `SI` - Slovenia
         * * `SB` - Solomon Islands
         * * `SO` - Somalia
         * * `ZA` - South Africa
         * * `GS` - South Georgia and the South Sandwich Islands
         * * `KR` - South Korea
         * * `SS` - South Sudan
         * * `ES` - Spain
         * * `LK` - Sri Lanka
         * * `SD` - Sudan
         * * `SR` - Suriname
         * * `SJ` - Svalbard and Jan Mayen
         * * `SE` - Sweden
         * * `CH` - Switzerland
         * * `SY` - Syria
         * * `TW` - Taiwan
         * * `TJ` - Tajikistan
         * * `TZ` - Tanzania
         * * `TH` - Thailand
         * * `TL` - Timor-Leste
         * * `TG` - Togo
         * * `TK` - Tokelau
         * * `TO` - Tonga
         * * `TT` - Trinidad and Tobago
         * * `TN` - Tunisia
         * * `TR` - Türkiye
         * * `TM` - Turkmenistan
         * * `TC` - Turks and Caicos Islands
         * * `TV` - Tuvalu
         * * `UG` - Uganda
         * * `UA` - Ukraine
         * * `AE` - United Arab Emirates
         * * `GB` - United Kingdom
         * * `UM` - United States Minor Outlying Islands
         * * `US` - United States of America
         * * `UY` - Uruguay
         * * `UZ` - Uzbekistan
         * * `VU` - Vanuatu
         * * `VE` - Venezuela
         * * `VN` - Vietnam
         * * `VG` - Virgin Islands (British)
         * * `VI` - Virgin Islands (U.S.)
         * * `WF` - Wallis and Futuna
         * * `EH` - Western Sahara
         * * `YE` - Yemen
         * * `ZM` - Zambia
         * * `ZW` - Zimbabwe
         */
        nationalities?: (
          | 'AD'
          | 'AE'
          | 'AF'
          | 'AG'
          | 'AI'
          | 'AL'
          | 'AM'
          | 'AO'
          | 'AQ'
          | 'AR'
          | 'AS'
          | 'AT'
          | 'AU'
          | 'AW'
          | 'AX'
          | 'AZ'
          | 'BA'
          | 'BB'
          | 'BD'
          | 'BE'
          | 'BF'
          | 'BG'
          | 'BH'
          | 'BI'
          | 'BJ'
          | 'BL'
          | 'BM'
          | 'BN'
          | 'BO'
          | 'BQ'
          | 'BR'
          | 'BS'
          | 'BT'
          | 'BV'
          | 'BW'
          | 'BY'
          | 'BZ'
          | 'CA'
          | 'CC'
          | 'CD'
          | 'CF'
          | 'CG'
          | 'CH'
          | 'CI'
          | 'CK'
          | 'CL'
          | 'CM'
          | 'CN'
          | 'CO'
          | 'CR'
          | 'CU'
          | 'CV'
          | 'CW'
          | 'CX'
          | 'CY'
          | 'CZ'
          | 'DE'
          | 'DJ'
          | 'DK'
          | 'DM'
          | 'DO'
          | 'DZ'
          | 'EC'
          | 'EE'
          | 'EG'
          | 'EH'
          | 'ER'
          | 'ES'
          | 'ET'
          | 'FI'
          | 'FJ'
          | 'FK'
          | 'FM'
          | 'FO'
          | 'FR'
          | 'GA'
          | 'GB'
          | 'GD'
          | 'GE'
          | 'GF'
          | 'GG'
          | 'GH'
          | 'GI'
          | 'GL'
          | 'GM'
          | 'GN'
          | 'GP'
          | 'GQ'
          | 'GR'
          | 'GS'
          | 'GT'
          | 'GU'
          | 'GW'
          | 'GY'
          | 'HK'
          | 'HM'
          | 'HN'
          | 'HR'
          | 'HT'
          | 'HU'
          | 'ID'
          | 'IE'
          | 'IL'
          | 'IM'
          | 'IN'
          | 'IO'
          | 'IQ'
          | 'IR'
          | 'IS'
          | 'IT'
          | 'JE'
          | 'JM'
          | 'JO'
          | 'JP'
          | 'KE'
          | 'KG'
          | 'KH'
          | 'KI'
          | 'KM'
          | 'KN'
          | 'KP'
          | 'KR'
          | 'KW'
          | 'KY'
          | 'KZ'
          | 'LA'
          | 'LB'
          | 'LC'
          | 'LI'
          | 'LK'
          | 'LR'
          | 'LS'
          | 'LT'
          | 'LU'
          | 'LV'
          | 'LY'
          | 'MA'
          | 'MC'
          | 'MD'
          | 'ME'
          | 'MF'
          | 'MG'
          | 'MH'
          | 'MK'
          | 'ML'
          | 'MM'
          | 'MN'
          | 'MO'
          | 'MP'
          | 'MQ'
          | 'MR'
          | 'MS'
          | 'MT'
          | 'MU'
          | 'MV'
          | 'MW'
          | 'MX'
          | 'MY'
          | 'MZ'
          | 'NA'
          | 'NC'
          | 'NE'
          | 'NF'
          | 'NG'
          | 'NI'
          | 'NL'
          | 'NO'
          | 'NP'
          | 'NR'
          | 'NU'
          | 'NZ'
          | 'OM'
          | 'PA'
          | 'PE'
          | 'PF'
          | 'PG'
          | 'PH'
          | 'PK'
          | 'PL'
          | 'PM'
          | 'PN'
          | 'PR'
          | 'PS'
          | 'PT'
          | 'PW'
          | 'PY'
          | 'QA'
          | 'RE'
          | 'RO'
          | 'RS'
          | 'RU'
          | 'RW'
          | 'SA'
          | 'SB'
          | 'SC'
          | 'SD'
          | 'SE'
          | 'SG'
          | 'SH'
          | 'SI'
          | 'SJ'
          | 'SK'
          | 'SL'
          | 'SM'
          | 'SN'
          | 'SO'
          | 'SR'
          | 'SS'
          | 'ST'
          | 'SV'
          | 'SX'
          | 'SY'
          | 'SZ'
          | 'TC'
          | 'TD'
          | 'TF'
          | 'TG'
          | 'TH'
          | 'TJ'
          | 'TK'
          | 'TL'
          | 'TM'
          | 'TN'
          | 'TO'
          | 'TR'
          | 'TT'
          | 'TV'
          | 'TW'
          | 'TZ'
          | 'UA'
          | 'UG'
          | 'UM'
          | 'US'
          | 'UY'
          | 'UZ'
          | 'VA'
          | 'VC'
          | 'VE'
          | 'VG'
          | 'VI'
          | 'VN'
          | 'VU'
          | 'WF'
          | 'WS'
          | 'XK'
          | 'YE'
          | 'YT'
          | 'ZA'
          | 'ZM'
          | 'ZW'
        )[]
        /** Which field to use when ordering the results. */
        ordering?: string
        /**
         * * `parent` - Parent
         * * `legal_guardian` - Legal Guardian
         * * `in_legal_guardian_absence` - In Legal Guardian Absence
         * * `arrival_contact` - Arrival Contact
         * * `educational_consultant` - Educational Consultant
         * * `main_invoice_recipient` - Main Invoice Recipient
         * * `invoice_recipient` - Invoice Recipient
         * * `contact_leave` - Contact Leave
         * * `invoice_copy_recipient` - Invoice Copy Recipient
         * * `assistant` - Assistant
         * * `relative` - Relative
         * * `other` - Other
         */
        roles?: (
          | 'arrival_contact'
          | 'assistant'
          | 'contact_leave'
          | 'educational_consultant'
          | 'in_legal_guardian_absence'
          | 'invoice_copy_recipient'
          | 'invoice_recipient'
          | 'legal_guardian'
          | 'main_invoice_recipient'
          | 'other'
          | 'parent'
          | 'relative'
        )[]
        /** A search term. */
        search?: string
        /** @format uuid */
        student?: string
        year_of_rosenberg_entry?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ParentAvailableFilters, T>, any>({
        path: `/parents/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags parents
     * @name ParentsCompanySuggestionsList
     * @request GET:/parents/company-suggestions/
     * @secure
     */,
    parentsCompanySuggestionsList: <T extends FetchKeys<PaginatedCompanyList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCompanyList, T>, any>({
        path: `/parents/company-suggestions/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  periods = {
    /**
     * No description
     *
     * @tags periods
     * @name PeriodsQuartersList
     * @request GET:/periods/quarters/
     * @secure
     */
    periodsQuartersList: <T extends FetchKeys<PaginatedQuarterList>>(
      query?: {
        is_from_active_year?: boolean
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedQuarterList, T>, any>({
        path: `/periods/quarters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags periods
     * @name PeriodsSemestersList
     * @request GET:/periods/semesters/
     * @secure
     */,
    periodsSemestersList: <T extends FetchKeys<PaginatedSemesterList>>(
      query?: {
        is_from_active_year?: boolean
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedSemesterList, T>, any>({
        path: `/periods/semesters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags periods
     * @name PeriodsYearsList
     * @request GET:/periods/years/
     * @secure
     */,
    periodsYearsList: <T extends FetchKeys<PaginatedSchoolYearList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedSchoolYearList, T>, any>({
        path: `/periods/years/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  planner = {
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerActivitiesList
     * @request GET:/planner/activities/
     * @secure
     */
    plannerActivitiesList: <
      T extends FetchKeys<PaginatedDraftActivityListList>
    >(
      query?: {
        complete_task?: boolean
        /** @format uuid */
        course?: string
        exclude_regular_activities?: boolean
        exclude_tae_activities?: boolean
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        room?: string
        students?: string[]
        /** @format uuid */
        teacher?: string
        timetable?: number
        timetable__enabled_preview_for_teachers?: boolean

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedDraftActivityListList, T>, any>({
        path: `/planner/activities/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerActivitiesCreate
     * @request POST:/planner/activities/
     * @secure
     */,
    plannerActivitiesCreate: <T extends FetchKeys<DraftActivityCreate>>(
      data: DraftActivityCreateRequest,
      query?: {
        edit_session_id?: string
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DraftActivityCreate, T>, any>({
        path: `/planner/activities/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerActivitiesRetrieve
     * @request GET:/planner/activities/{id}/
     * @secure
     */,
    plannerActivitiesRetrieve: <T extends FetchKeys<DraftActivity>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DraftActivity, T>, any>({
        path: `/planner/activities/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerActivitiesPartialUpdate
     * @request PATCH:/planner/activities/{id}/
     * @secure
     */,
    plannerActivitiesPartialUpdate: <T extends FetchKeys<DraftActivity>>(
      id: string,
      data: PatchedDraftActivityRequest,
      query?: {
        edit_session_id?: string
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DraftActivity, T>, any>({
        path: `/planner/activities/${id}/`,
        method: 'PATCH',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerActivitiesDestroy
     * @request DELETE:/planner/activities/{id}/
     * @secure
     */,
    plannerActivitiesDestroy: <T extends FetchKeys<void>>(
      id: string,
      query?: {
        edit_session_id?: string
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/activities/${id}/`,
        method: 'DELETE',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerActivitiesEditSessionsCreate
     * @request POST:/planner/activities/{id}/edit-sessions/
     * @secure
     */,
    plannerActivitiesEditSessionsCreate: <
      T extends FetchKeys<EditSessionCreatedResponse>
    >(
      id: string,
      data: CreateEditSessionRequest,
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<EditSessionCreatedResponse, T>,
        EditSessionConflictResponse
      >({
        path: `/planner/activities/${id}/edit-sessions/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerActivitiesRoomsPreferencesRetrieve
     * @request GET:/planner/activities/{id}/rooms-preferences/
     * @secure
     */,
    plannerActivitiesRoomsPreferencesRetrieve: <
      T extends FetchKeys<DraftActivity>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DraftActivity, T>, any>({
        path: `/planner/activities/${id}/rooms-preferences/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerActivitiesAvailableFiltersRetrieve
     * @request GET:/planner/activities/available-filters/
     * @secure
     */,
    plannerActivitiesAvailableFiltersRetrieve: <
      T extends FetchKeys<DraftActivityAvailableFilters>
    >(
      query?: {
        /** @format uuid */
        course?: string
        exclude_regular_activities?: boolean
        exclude_tae_activities?: boolean
        room?: string
        students?: string[]
        /** @format uuid */
        teacher?: string
        timetable?: number
        timetable__enabled_preview_for_teachers?: boolean

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DraftActivityAvailableFilters, T>, any>({
        path: `/planner/activities/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerActivitiesCountRetrieve
     * @request GET:/planner/activities/count/
     * @secure
     */,
    plannerActivitiesCountRetrieve: <T extends FetchKeys<number>>(
      query?: {
        /** @format uuid */
        course?: string
        exclude_regular_activities?: boolean
        exclude_tae_activities?: boolean
        room?: number
        students?: string[]
        /** @format uuid */
        teacher?: string
        timetable?: number
        timetable__enabled_preview_for_teachers?: boolean

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<number, T>, any>({
        path: `/planner/activities/count/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerActivitiesDaysList
     * @request GET:/planner/activities/days/
     * @secure
     */,
    plannerActivitiesDaysList: <T extends FetchKeys<WeekDay[]>>(
      query?: {
        /** @format uuid */
        course?: string
        exclude_regular_activities?: boolean
        exclude_tae_activities?: boolean
        room?: number
        students?: string[]
        /** @format uuid */
        teacher?: string
        timetable?: number
        timetable__enabled_preview_for_teachers?: boolean

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<WeekDay[], T>, any>({
        path: `/planner/activities/days/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Pass timetable and hour in query param
     *
     * @tags planner
     * @name PlannerActivitiesExportActivitiesAfterHourRetrieve
     * @request GET:/planner/activities/export-activities-after-hour/
     * @secure
     */,
    plannerActivitiesExportActivitiesAfterHourRetrieve: <
      T extends FetchKeys<string>
    >(
      query?: {
        /**
         * * `0` - 0
         * * `1` - 1
         * * `2` - 2
         * * `3` - 3
         * * `4` - 4
         * * `5` - 5
         * * `6` - 6
         * * `7` - 7
         * * `8` - 8
         * * `9` - 9
         * * `10` - 10
         * * `11` - 11
         * * `12` - 12
         * * `13` - 13
         * * `14` - 14
         * * `15` - 15
         */
        hour?:
          | 0
          | 1
          | 2
          | 3
          | 4
          | 5
          | 6
          | 7
          | 8
          | 9
          | 10
          | 11
          | 12
          | 13
          | 14
          | 15
        timetable?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<string, T>, any>({
        path: `/planner/activities/export-activities-after-hour/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerActivitiesExportActivitiesPerSlotRetrieve
     * @request GET:/planner/activities/export-activities-per-slot/
     * @secure
     */,
    plannerActivitiesExportActivitiesPerSlotRetrieve: <
      T extends FetchKeys<string>
    >(
      query?: {
        /** Timetable ID or live timetable of the current quarter, falling back to the previous quarter when no quarter is current. */
        timetable?: number | 'live'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<string, T>, any>({
        path: `/planner/activities/export-activities-per-slot/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerActivitiesExportStudentActivitiesRetrieve
     * @request GET:/planner/activities/export-student-activities/
     * @secure
     */,
    plannerActivitiesExportStudentActivitiesRetrieve: <
      T extends FetchKeys<string>
    >(
      query?: {
        timetable?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<string, T>, any>({
        path: `/planner/activities/export-student-activities/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerActivitiesExportTeacherActivitiesRetrieve
     * @request GET:/planner/activities/export-teacher-activities/
     * @secure
     */,
    plannerActivitiesExportTeacherActivitiesRetrieve: <
      T extends FetchKeys<File>
    >(
      query?: {
        timetable?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<File, T>, any>({
        path: `/planner/activities/export-teacher-activities/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerActivitiesHoursList
     * @request GET:/planner/activities/hours/
     * @secure
     */,
    plannerActivitiesHoursList: <T extends FetchKeys<DayHour[]>>(
      query?: {
        /** @format uuid */
        course?: string
        exclude_regular_activities?: boolean
        exclude_tae_activities?: boolean
        room?: number
        students?: string[]
        /** @format uuid */
        teacher?: string
        timetable?: number
        timetable__enabled_preview_for_teachers?: boolean

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DayHour[], T>, any>({
        path: `/planner/activities/hours/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerActivitiesTeacherActivitiesReportPreviewAcademicActivitiesList
     * @request GET:/planner/activities/teacher-activities-report-preview/academic-activities/
     * @secure
     */,
    plannerActivitiesTeacherActivitiesReportPreviewAcademicActivitiesList: <
      T extends FetchKeys<PaginatedTeacherAcademicActivitiesReportPreviewList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        timetable?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<PaginatedTeacherAcademicActivitiesReportPreviewList, T>,
        any
      >({
        path: `/planner/activities/teacher-activities-report-preview/academic-activities/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerActivitiesTeacherActivitiesReportPreviewBoardingActivitiesList
     * @request GET:/planner/activities/teacher-activities-report-preview/boarding-activities/
     * @secure
     */,
    plannerActivitiesTeacherActivitiesReportPreviewBoardingActivitiesList: <
      T extends FetchKeys<PaginatedTeacherBoardingActivitiesReportPreviewList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        timetable?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<PaginatedTeacherBoardingActivitiesReportPreviewList, T>,
        any
      >({
        path: `/planner/activities/teacher-activities-report-preview/boarding-activities/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerActivitiesUpdateActivitiesRoomsCreate
     * @request POST:/planner/activities/update-activities-rooms/
     * @secure
     */,
    plannerActivitiesUpdateActivitiesRoomsCreate: <
      T extends FetchKeys<UpdateActivitiesRooms>
    >(
      data: UpdateActivitiesRoomsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UpdateActivitiesRooms, T>, any>({
        path: `/planner/activities/update-activities-rooms/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Provide the ability to retrieve a single object for further manipulation.
     *
     * @tags planner
     * @name PlannerConflictsRetrieve
     * @request GET:/planner/conflicts/{id}/{day}/
     * @secure
     */,
    plannerConflictsRetrieve: <T extends FetchKeys<void>>(
      day: string,
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/conflicts/${id}/${day}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerGeneratorCallbackCreate
     * @request POST:/planner/generator/callback
     * @secure
     */,
    plannerGeneratorCallbackCreate: <T extends FetchKeys<Callback>>(
      data: CallbackRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Callback, T>, any>({
        path: `/planner/generator/callback`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerGeneratorStatusesRetrieve
     * @request GET:/planner/generator/statuses
     * @secure
     */,
    plannerGeneratorStatusesRetrieve: <T extends FetchKeys<void>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/generator/statuses`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags planner
     * @name PlannerGeneratorTasksList
     * @request GET:/planner/generator/tasks/
     * @secure
     */,
    plannerGeneratorTasksList: <
      T extends FetchKeys<PaginatedGenerationTaskList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedGenerationTaskList, T>, any>({
        path: `/planner/generator/tasks/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags planner
     * @name PlannerGeneratorTasksCreate
     * @request POST:/planner/generator/tasks/
     * @secure
     */,
    plannerGeneratorTasksCreate: <T extends FetchKeys<GenerationTask>>(
      data: GenerationTaskRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<GenerationTask, T>, any>({
        path: `/planner/generator/tasks/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags planner
     * @name PlannerGeneratorTasksRetrieve
     * @request GET:/planner/generator/tasks/{id}/
     * @secure
     */,
    plannerGeneratorTasksRetrieve: <T extends FetchKeys<GenerationTask>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<GenerationTask, T>, any>({
        path: `/planner/generator/tasks/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags planner
     * @name PlannerGeneratorTasksStatsRetrieve
     * @request GET:/planner/generator/tasks/{id}/stats/
     * @secure
     */,
    plannerGeneratorTasksStatsRetrieve: <
      T extends FetchKeys<
        Record<
          string,
          {
            /** @format float */
            score?: number
            /** @format float */
            objective?: number
            /** @format date-time */
            timestamp?: string
            late_activities?: number
          }[]
        >
      >
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<
          Record<
            string,
            {
              /** @format float */
              score?: number
              /** @format float */
              objective?: number
              /** @format date-time */
              timestamp?: string
              late_activities?: number
            }[]
          >,
          T
        >,
        any
      >({
        path: `/planner/generator/tasks/${id}/stats/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags planner
     * @name PlannerGeneratorTasksStopCreate
     * @request POST:/planner/generator/tasks/{id}/stop/
     * @secure
     */,
    plannerGeneratorTasksStopCreate: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/generator/tasks/${id}/stop/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags planner
     * @name PlannerGeneratorTasksCostsRetrieve
     * @request GET:/planner/generator/tasks/costs/
     * @secure
     */,
    plannerGeneratorTasksCostsRetrieve: <
      T extends FetchKeys<GeneratorTaskCosts>
    >(
      query?: {
        quarter?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<GeneratorTaskCosts, T>, any>({
        path: `/planner/generator/tasks/costs/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags planner
     * @name PlannerGeneratorTasksSectionsToShuffleRetrieve
     * @request GET:/planner/generator/tasks/sections-to-shuffle/
     * @secure
     */,
    plannerGeneratorTasksSectionsToShuffleRetrieve: <
      T extends FetchKeys<ShuffleSectionsOptions>
    >(
      query: {
        quarter: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ShuffleSectionsOptions, T>, any>({
        path: `/planner/generator/tasks/sections-to-shuffle/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags planner
     * @name PlannerGeneratorTasksShuffleSectionGroupsRetrieve
     * @request GET:/planner/generator/tasks/shuffle-section-groups/
     * @secure
     */,
    plannerGeneratorTasksShuffleSectionGroupsRetrieve: <
      T extends FetchKeys<ShuffleSectionGroupsOptions>
    >(
      query: {
        quarter: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ShuffleSectionGroupsOptions, T>, any>({
        path: `/planner/generator/tasks/shuffle-section-groups/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerLogsList
     * @request GET:/planner/logs/
     * @secure
     */,
    plannerLogsList: <T extends FetchKeys<PaginatedTimetableLogList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        timetable?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTimetableLogList, T>, any>({
        path: `/planner/logs/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerMentoringsList
     * @request GET:/planner/mentorings/
     * @deprecated
     * @secure
     */,
    plannerMentoringsList: <
      T extends FetchKeys<PaginatedTimetableMentoringList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTimetableMentoringList, T>, any>({
        path: `/planner/mentorings/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerMentoringsCreate
     * @request POST:/planner/mentorings/
     * @deprecated
     * @secure
     */,
    plannerMentoringsCreate: <T extends FetchKeys<TimetableMentoring>>(
      data: TimetableMentoringRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TimetableMentoring, T>, any>({
        path: `/planner/mentorings/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerMentoringsRetrieve
     * @request GET:/planner/mentorings/{id}/
     * @deprecated
     * @secure
     */,
    plannerMentoringsRetrieve: <T extends FetchKeys<TimetableMentoring>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TimetableMentoring, T>, any>({
        path: `/planner/mentorings/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerMentoringsUpdate
     * @request PUT:/planner/mentorings/{id}/
     * @deprecated
     * @secure
     */,
    plannerMentoringsUpdate: <T extends FetchKeys<TimetableMentoring>>(
      id: number,
      data: TimetableMentoringRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TimetableMentoring, T>, any>({
        path: `/planner/mentorings/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerMentoringsPartialUpdate
     * @request PATCH:/planner/mentorings/{id}/
     * @deprecated
     * @secure
     */,
    plannerMentoringsPartialUpdate: <T extends FetchKeys<TimetableMentoring>>(
      id: number,
      data: PatchedTimetableMentoringRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TimetableMentoring, T>, any>({
        path: `/planner/mentorings/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerMentoringsDestroy
     * @request DELETE:/planner/mentorings/{id}/
     * @deprecated
     * @secure
     */,
    plannerMentoringsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/mentorings/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerMentoringsInitialDataRetrieve
     * @request GET:/planner/mentorings/initial-data/
     * @deprecated
     * @secure
     */,
    plannerMentoringsInitialDataRetrieve: <
      T extends FetchKeys<TimetableMentoring>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TimetableMentoring, T>, any>({
        path: `/planner/mentorings/initial-data/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerPotentialOptimizationsList
     * @request GET:/planner/potential-optimizations/
     * @secure
     */,
    plannerPotentialOptimizationsList: <
      T extends FetchKeys<PaginatedPotentialOptimizationList>
    >(
      query?: {
        /**
         * * `0` - 8:00-8:40
         * * `1` - 8:45-9:25
         * * `2` - 9:30-10:10
         * * `3` - 10:30-11:10
         * * `4` - 11:15-11:55
         * * `5` - 13:00-13:40
         * * `6` - 13:45-14:25
         * * `7` - 14:30-15:10
         * * `8` - 15:15-15:55
         * * `9` - 16:00-16:40
         * * `10` - 16:45-17:25
         * * `11` - 17:30-18:10
         * * `12` - 18:15-18:55
         * * `13` - 19:00-19:40
         * * `14` - 19:40-20:30
         * * `15` - 20:30-21:00
         */
        activity__hour?:
          | '0'
          | '1'
          | '10'
          | '11'
          | '12'
          | '13'
          | '14'
          | '15'
          | '2'
          | '3'
          | '4'
          | '5'
          | '6'
          | '7'
          | '8'
          | '9'
          | null
        activity__is_private?: boolean
        /**
         * * `0` - Monday
         * * `1` - Tuesday
         * * `2` - Wednesday
         * * `3` - Thursday
         * * `4` - Friday
         * * `5` - Saturday
         * * `6` - Sunday
         */
        day?: 0 | 1 | 2 | 3 | 4 | 5 | 6
        /**
         * * `0` - 8:00-8:40
         * * `1` - 8:45-9:25
         * * `2` - 9:30-10:10
         * * `3` - 10:30-11:10
         * * `4` - 11:15-11:55
         * * `5` - 13:00-13:40
         * * `6` - 13:45-14:25
         * * `7` - 14:30-15:10
         * * `8` - 15:15-15:55
         * * `9` - 16:00-16:40
         * * `10` - 16:45-17:25
         * * `11` - 17:30-18:10
         * * `12` - 18:15-18:55
         * * `13` - 19:00-19:40
         * * `14` - 19:40-20:30
         * * `15` - 20:30-21:00
         */
        hour?:
          | '0'
          | '1'
          | '10'
          | '11'
          | '12'
          | '13'
          | '14'
          | '15'
          | '2'
          | '3'
          | '4'
          | '5'
          | '6'
          | '7'
          | '8'
          | '9'
          | null
        no_teacher?: boolean
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        timetable?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedPotentialOptimizationList, T>, any>({
        path: `/planner/potential-optimizations/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerPotentialOptimizationsApplyPotentialOptimizationsUpdate
     * @request PUT:/planner/potential-optimizations/{id}/apply-potential-optimizations/
     * @secure
     */,
    plannerPotentialOptimizationsApplyPotentialOptimizationsUpdate: <
      T extends FetchKeys<void>
    >(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/potential-optimizations/${id}/apply-potential-optimizations/`,
        method: 'PUT',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerPotentialOptimizationsApplyPotentialOptimizationsPartialUpdate
     * @request PATCH:/planner/potential-optimizations/{id}/apply-potential-optimizations/
     * @secure
     */,
    plannerPotentialOptimizationsApplyPotentialOptimizationsPartialUpdate: <
      T extends FetchKeys<void>
    >(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/potential-optimizations/${id}/apply-potential-optimizations/`,
        method: 'PATCH',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerPotentialOptimizationsActivitiesMovesList
     * @request GET:/planner/potential-optimizations/activities/{id}/moves/
     * @secure
     */,
    plannerPotentialOptimizationsActivitiesMovesList: <
      T extends FetchKeys<ActivityMove[]>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ActivityMove[], T>, any>({
        path: `/planner/potential-optimizations/activities/${id}/moves/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Provide the ability to retrieve a single object for further manipulation.
     *
     * @tags planner
     * @name PlannerRoomsAssignerCreate
     * @request POST:/planner/rooms-assigner/{id}/
     * @secure
     */,
    plannerRoomsAssignerCreate: <T extends FetchKeys<void>>(
      id: number,
      data: AssignRoomsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/rooms-assigner/${id}/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerSectionsList
     * @request GET:/planner/sections/
     * @secure
     */,
    plannerSectionsList: <T extends FetchKeys<PaginatedSectionListList>>(
      query?: {
        /** @format uuid */
        course?: string
        excluded_section?: string
        id?: number[]
        in_live_timetable?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter?: number
        /** A search term. */
        search?: string
        /** @format uuid */
        students?: string
        /** @format uuid */
        teacher?: string
        /** @format uuid */
        teachers?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedSectionListList, T>, any>({
        path: `/planner/sections/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerSectionsCreate
     * @request POST:/planner/sections/
     * @secure
     */,
    plannerSectionsCreate: <T extends FetchKeys<Section>>(
      data: SectionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Section, T>, any>({
        path: `/planner/sections/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerSectionsRetrieve
     * @request GET:/planner/sections/{id}/
     * @secure
     */,
    plannerSectionsRetrieve: <T extends FetchKeys<Section>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Section, T>, any>({
        path: `/planner/sections/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerSectionsUpdate
     * @request PUT:/planner/sections/{id}/
     * @secure
     */,
    plannerSectionsUpdate: <T extends FetchKeys<Section>>(
      id: number,
      data: SectionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Section, T>, any>({
        path: `/planner/sections/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerSectionsPartialUpdate
     * @request PATCH:/planner/sections/{id}/
     * @secure
     */,
    plannerSectionsPartialUpdate: <T extends FetchKeys<Section>>(
      id: number,
      data: PatchedSectionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Section, T>, any>({
        path: `/planner/sections/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerSectionsDestroy
     * @request DELETE:/planner/sections/{id}/
     * @secure
     */,
    plannerSectionsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/sections/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerSectionsAllowedStudentsList
     * @request GET:/planner/sections/{id}/allowed-students/
     * @secure
     */,
    plannerSectionsAllowedStudentsList: <T extends FetchKeys<StringOption[]>>(
      id: number,
      query?: {
        /** @format uuid */
        course?: string
        excluded_section?: string
        id?: number[]
        in_live_timetable?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        quarter?: number
        /** A search term. */
        search?: string
        /** @format uuid */
        students?: string
        /** @format uuid */
        teacher?: string
        /** @format uuid */
        teachers?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/planner/sections/${id}/allowed-students/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerSectionsUsagesRetrieve
     * @request GET:/planner/sections/{id}/usages/
     * @secure
     */,
    plannerSectionsUsagesRetrieve: <T extends FetchKeys<SectionUsagesResponse>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SectionUsagesResponse, T>, any>({
        path: `/planner/sections/${id}/usages/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerSectionsChangesInSectionsCreate
     * @request POST:/planner/sections/changes-in-sections/
     * @secure
     */,
    plannerSectionsChangesInSectionsCreate: <
      T extends FetchKeys<Record<string, any>>
    >(
      data: ChangesInSectionsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Record<string, any>, T>, any>({
        path: `/planner/sections/changes-in-sections/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerSectionsExportStudentsEnrollmentsRetrieve
     * @request GET:/planner/sections/export-students-enrollments/
     * @secure
     */,
    plannerSectionsExportStudentsEnrollmentsRetrieve: <
      T extends FetchKeys<string>
    >(
      query?: {
        quarter?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<string, T>, any>({
        path: `/planner/sections/export-students-enrollments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerSectionsExportStudentsInSectionsRetrieve
     * @request GET:/planner/sections/export-students-in-sections/
     * @secure
     */,
    plannerSectionsExportStudentsInSectionsRetrieve: <
      T extends FetchKeys<string>
    >(
      query?: {
        quarter?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<string, T>, any>({
        path: `/planner/sections/export-students-in-sections/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerSectionsExportTeachersEnrolmentsRetrieve
     * @request GET:/planner/sections/export-teachers-enrolments/
     * @secure
     */,
    plannerSectionsExportTeachersEnrolmentsRetrieve: <
      T extends FetchKeys<string>
    >(
      query?: {
        quarter?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<string, T>, any>({
        path: `/planner/sections/export-teachers-enrolments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * @description Sections with students and scorings
     *
     * @tags planner
     * @name PlannerSectionsSectionToEnrollList
     * @request GET:/planner/sections/section-to-enroll/
     * @secure
     */,
    plannerSectionsSectionToEnrollList: <
      T extends FetchKeys<SectionToEnroll[]>
    >(
      query?: {
        /** @format uuid */
        course?: string
        excluded_section?: string
        id?: number[]
        in_live_timetable?: boolean
        quarter?: number
        /** @format uuid */
        students?: string
        /** @format uuid */
        teacher?: string
        /** @format uuid */
        teachers?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SectionToEnroll[], T>, any>({
        path: `/planner/sections/section-to-enroll/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Sections API without enrollments
     *
     * @tags planner
     * @name PlannerSectionsSimpleList
     * @request GET:/planner/sections/simple/
     * @secure
     */,
    plannerSectionsSimpleList: <
      T extends FetchKeys<PaginatedSimpleSectionList>
    >(
      query?: {
        /** @format uuid */
        course?: string
        /** multi value filter: id1,id2,id3 */
        id?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedSimpleSectionList, T>, any>({
        path: `/planner/sections/simple/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Sections API without enrollments
     *
     * @tags planner
     * @name PlannerSectionsSimpleRetrieve
     * @request GET:/planner/sections/simple/{id}/
     * @secure
     */,
    plannerSectionsSimpleRetrieve: <T extends FetchKeys<SimpleSection>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SimpleSection, T>, any>({
        path: `/planner/sections/simple/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerSectionsTotalDurationRetrieve
     * @request GET:/planner/sections/total-duration/
     * @secure
     */,
    plannerSectionsTotalDurationRetrieve: <T extends FetchKeys<number>>(
      query?: {
        /** @format uuid */
        course?: string
        excluded_section?: string
        id?: number[]
        in_live_timetable?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        quarter?: number
        /** A search term. */
        search?: string
        /** @format uuid */
        students?: string
        /** @format uuid */
        teacher?: string
        /** @format uuid */
        teachers?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<number, T>, any>({
        path: `/planner/sections/total-duration/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpList
     * @request GET:/planner/students-idp/
     * @secure
     */,
    plannerStudentsIdpList: <
      T extends FetchKeys<PaginatedStudentIdpPlanListList>
    >(
      query?: {
        aim?: number
        grade_level?: number
        is_active?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /**
         * * `new` - New
         * * `draft` - Draft
         * * `rejected` - Rejected
         * * `waits_for_admin_approval` - In admin approval
         * * `in_parental_approval` - In parental approval
         * * `is_discussion` - In discussion
         * * `approved` - Approved
         * * `automatically_approved` - Automatically approved
         * * `updated` - Updated
         * * `archived` - Archived
         */
        status?:
          | 'approved'
          | 'archived'
          | 'automatically_approved'
          | 'draft'
          | 'in_parental_approval'
          | 'is_discussion'
          | 'new'
          | 'rejected'
          | 'updated'
          | 'waits_for_admin_approval'
        /** @format uuid */
        student?: string
        student__is_active?: boolean
        student__is_new?: boolean
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentIdpPlanListList, T>, any>({
        path: `/planner/students-idp/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpCreate
     * @request POST:/planner/students-idp/
     * @secure
     */,
    plannerStudentsIdpCreate: <T extends FetchKeys<StudentIdpPlan>>(
      data: StudentIdpPlanRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentIdpPlan, T>, any>({
        path: `/planner/students-idp/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpRetrieve
     * @request GET:/planner/students-idp/{id}/
     * @secure
     */,
    plannerStudentsIdpRetrieve: <T extends FetchKeys<StudentIdpPlan>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentIdpPlan, T>, any>({
        path: `/planner/students-idp/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpUpdate
     * @request PUT:/planner/students-idp/{id}/
     * @secure
     */,
    plannerStudentsIdpUpdate: <T extends FetchKeys<StudentIdpPlan>>(
      id: string,
      data: StudentIdpPlanRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentIdpPlan, T>, any>({
        path: `/planner/students-idp/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpPartialUpdate
     * @request PATCH:/planner/students-idp/{id}/
     * @secure
     */,
    plannerStudentsIdpPartialUpdate: <T extends FetchKeys<StudentIdpPlan>>(
      id: string,
      data: PatchedStudentIdpPlanRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentIdpPlan, T>, any>({
        path: `/planner/students-idp/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpDestroy
     * @request DELETE:/planner/students-idp/{id}/
     * @secure
     */,
    plannerStudentsIdpDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/students-idp/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpApproveCreate
     * @request POST:/planner/students-idp/{id}/approve/
     * @secure
     */,
    plannerStudentsIdpApproveCreate: <T extends FetchKeys<AdminApproveIdpPlan>>(
      id: string,
      data: AdminApproveIdpPlanRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminApproveIdpPlan, T>, any>({
        path: `/planner/students-idp/${id}/approve/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpAutoAssignSectionsCreate
     * @request POST:/planner/students-idp/{id}/auto-assign-sections/
     * @secure
     */,
    plannerStudentsIdpAutoAssignSectionsCreate: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/students-idp/${id}/auto-assign-sections/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpCancelApprovalRequestUpdate
     * @request PUT:/planner/students-idp/{id}/cancel-approval-request/
     * @secure
     */,
    plannerStudentsIdpCancelApprovalRequestUpdate: <
      T extends FetchKeys<StudentIdpPlan>
    >(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentIdpPlan, T>, any>({
        path: `/planner/students-idp/${id}/cancel-approval-request/`,
        method: 'PUT',
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpParentApproveCreate
     * @request POST:/planner/students-idp/{id}/parent-approve/
     * @secure
     */,
    plannerStudentsIdpParentApproveCreate: <T extends FetchKeys<void>>(
      id: string,
      data: AdminParentApproveIdpPlanRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/students-idp/${id}/parent-approve/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpPdfRetrieve
     * @request GET:/planner/students-idp/{id}/pdf/
     * @secure
     */,
    plannerStudentsIdpPdfRetrieve: <T extends FetchKeys<StudentIdpPlan>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentIdpPlan, T>, any>({
        path: `/planner/students-idp/${id}/pdf/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpRejectCreate
     * @request POST:/planner/students-idp/{id}/reject/
     * @secure
     */,
    plannerStudentsIdpRejectCreate: <T extends FetchKeys<RejectIdpPlan>>(
      id: string,
      data: RejectIdpPlanRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RejectIdpPlan, T>, any>({
        path: `/planner/students-idp/${id}/reject/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpUpdateDraftUpdate
     * @request PUT:/planner/students-idp/{id}/update-draft/
     * @secure
     */,
    plannerStudentsIdpUpdateDraftUpdate: <T extends FetchKeys<StudentIdpPlan>>(
      id: string,
      data: StudentIdpPlanRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentIdpPlan, T>, any>({
        path: `/planner/students-idp/${id}/update-draft/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpValidateApproveCreate
     * @request POST:/planner/students-idp/{id}/validate-approve/
     * @secure
     */,
    plannerStudentsIdpValidateApproveCreate: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/students-idp/${id}/validate-approve/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpAvailableFiltersRetrieve
     * @request GET:/planner/students-idp/available-filters/
     * @secure
     */,
    plannerStudentsIdpAvailableFiltersRetrieve: <
      T extends FetchKeys<StudentIdpPlanAvailableFilters>
    >(
      query?: {
        aim?: number
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        grade_level?: number
        is_active?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string
        /**
         * * `new` - New
         * * `draft` - Draft
         * * `rejected` - Rejected
         * * `waits_for_admin_approval` - In admin approval
         * * `in_parental_approval` - In parental approval
         * * `is_discussion` - In discussion
         * * `approved` - Approved
         * * `automatically_approved` - Automatically approved
         * * `updated` - Updated
         * * `archived` - Archived
         */
        status?:
          | 'approved'
          | 'archived'
          | 'automatically_approved'
          | 'draft'
          | 'in_parental_approval'
          | 'is_discussion'
          | 'new'
          | 'rejected'
          | 'updated'
          | 'waits_for_admin_approval'
        /** @format uuid */
        student?: string
        student__is_active?: boolean
        student__is_new?: string
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentIdpPlanAvailableFilters, T>, any>({
        path: `/planner/students-idp/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpCalculateCreditsCreate
     * @request POST:/planner/students-idp/calculate-credits/
     * @secure
     */,
    plannerStudentsIdpCalculateCreditsCreate: <
      T extends FetchKeys<CalculateCreditsResponse>
    >(
      data: StudentIdpPlanRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CalculateCreditsResponse, T>, any>({
        path: `/planner/students-idp/calculate-credits/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerStudentsIdpCourseInBasketsRetrieve
     * @request GET:/planner/students-idp/course-in-baskets/{id}/
     * @secure
     */,
    plannerStudentsIdpCourseInBasketsRetrieve: <
      T extends FetchKeys<CourseInBasketUpdateDepartment>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseInBasketUpdateDepartment, T>, any>({
        path: `/planner/students-idp/course-in-baskets/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerStudentsIdpCourseInBasketsUpdate
     * @request PUT:/planner/students-idp/course-in-baskets/{id}/
     * @secure
     */,
    plannerStudentsIdpCourseInBasketsUpdate: <
      T extends FetchKeys<CourseInBasketUpdateDepartment>
    >(
      id: number,
      data: CourseInBasketUpdateDepartmentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseInBasketUpdateDepartment, T>, any>({
        path: `/planner/students-idp/course-in-baskets/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerStudentsIdpCourseInBasketsPartialUpdate
     * @request PATCH:/planner/students-idp/course-in-baskets/{id}/
     * @secure
     */,
    plannerStudentsIdpCourseInBasketsPartialUpdate: <
      T extends FetchKeys<CourseInBasketUpdateDepartment>
    >(
      id: number,
      data: PatchedCourseInBasketUpdateDepartmentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseInBasketUpdateDepartment, T>, any>({
        path: `/planner/students-idp/course-in-baskets/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpCoursesList
     * @request GET:/planner/students-idp/courses/
     * @secure
     */,
    plannerStudentsIdpCoursesList: <T extends FetchKeys<CourseInIdp[]>>(
      query?: {
        aim?: number
        grade_level?: number
        is_active?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string
        /**
         * * `new` - New
         * * `draft` - Draft
         * * `rejected` - Rejected
         * * `waits_for_admin_approval` - In admin approval
         * * `in_parental_approval` - In parental approval
         * * `is_discussion` - In discussion
         * * `approved` - Approved
         * * `automatically_approved` - Automatically approved
         * * `updated` - Updated
         * * `archived` - Archived
         */
        status?:
          | 'approved'
          | 'archived'
          | 'automatically_approved'
          | 'draft'
          | 'in_parental_approval'
          | 'is_discussion'
          | 'new'
          | 'rejected'
          | 'updated'
          | 'waits_for_admin_approval'
        /** @format uuid */
        student?: string
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseInIdp[], T>, any>({
        path: `/planner/students-idp/courses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerStudentsIdpEnrollToSectionRetrieve
     * @request GET:/planner/students-idp/enroll-to-section/{id}/
     * @secure
     */,
    plannerStudentsIdpEnrollToSectionRetrieve: <
      T extends FetchKeys<IdpSectionEnroll>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<IdpSectionEnroll, T>, any>({
        path: `/planner/students-idp/enroll-to-section/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerStudentsIdpEnrollToSectionUpdate
     * @request PUT:/planner/students-idp/enroll-to-section/{id}/
     * @secure
     */,
    plannerStudentsIdpEnrollToSectionUpdate: <
      T extends FetchKeys<IdpSectionEnroll>
    >(
      id: number,
      data: IdpSectionEnrollRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<IdpSectionEnroll, T>, any>({
        path: `/planner/students-idp/enroll-to-section/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerStudentsIdpEnrollToSectionPartialUpdate
     * @request PATCH:/planner/students-idp/enroll-to-section/{id}/
     * @secure
     */,
    plannerStudentsIdpEnrollToSectionPartialUpdate: <
      T extends FetchKeys<IdpSectionEnroll>
    >(
      id: number,
      data: PatchedIdpSectionEnrollRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<IdpSectionEnroll, T>, any>({
        path: `/planner/students-idp/enroll-to-section/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpExamsList
     * @request GET:/planner/students-idp/exams/
     * @secure
     */,
    plannerStudentsIdpExamsList: <T extends FetchKeys<StringOption[]>>(
      query?: {
        aim?: number
        grade_level?: number
        is_active?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string
        /**
         * * `new` - New
         * * `draft` - Draft
         * * `rejected` - Rejected
         * * `waits_for_admin_approval` - In admin approval
         * * `in_parental_approval` - In parental approval
         * * `is_discussion` - In discussion
         * * `approved` - Approved
         * * `automatically_approved` - Automatically approved
         * * `updated` - Updated
         * * `archived` - Archived
         */
        status?:
          | 'approved'
          | 'archived'
          | 'automatically_approved'
          | 'draft'
          | 'in_parental_approval'
          | 'is_discussion'
          | 'new'
          | 'rejected'
          | 'updated'
          | 'waits_for_admin_approval'
        /** @format uuid */
        student?: string
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/planner/students-idp/exams/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpLastApprovedRetrieve
     * @request GET:/planner/students-idp/last-approved/
     * @secure
     */,
    plannerStudentsIdpLastApprovedRetrieve: <
      T extends FetchKeys<StudentIdpPlan>
    >(
      query?: {
        aim?: number
        grade_level?: number
        is_active?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string
        /**
         * * `new` - New
         * * `draft` - Draft
         * * `rejected` - Rejected
         * * `waits_for_admin_approval` - In admin approval
         * * `in_parental_approval` - In parental approval
         * * `is_discussion` - In discussion
         * * `approved` - Approved
         * * `automatically_approved` - Automatically approved
         * * `updated` - Updated
         * * `archived` - Archived
         */
        status?:
          | 'approved'
          | 'archived'
          | 'automatically_approved'
          | 'draft'
          | 'in_parental_approval'
          | 'is_discussion'
          | 'new'
          | 'rejected'
          | 'updated'
          | 'waits_for_admin_approval'
        /** @format uuid */
        student?: string
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentIdpPlan, T>, any>({
        path: `/planner/students-idp/last-approved/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerStudentsIdpRequestedCoursesList
     * @request GET:/planner/students-idp/requested-courses/
     * @secure
     */,
    plannerStudentsIdpRequestedCoursesList: <
      T extends FetchKeys<PaginatedRequestedCourseList>
    >(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedRequestedCourseList, T>, any>({
        path: `/planner/students-idp/requested-courses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerStudentsIdpRequestedCoursesRetrieve
     * @request GET:/planner/students-idp/requested-courses/{id}/
     * @secure
     */,
    plannerStudentsIdpRequestedCoursesRetrieve: <
      T extends FetchKeys<RequestedCourse>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RequestedCourse, T>, any>({
        path: `/planner/students-idp/requested-courses/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpSaveDraftCreate
     * @request POST:/planner/students-idp/save-draft/
     * @secure
     */,
    plannerStudentsIdpSaveDraftCreate: <T extends FetchKeys<StudentIdpPlan>>(
      data: StudentIdpPlanRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentIdpPlan, T>, any>({
        path: `/planner/students-idp/save-draft/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpStatusesList
     * @request GET:/planner/students-idp/statuses/
     * @secure
     */,
    plannerStudentsIdpStatusesList: <T extends FetchKeys<StringOption[]>>(
      query?: {
        aim?: number
        grade_level?: number
        is_active?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string
        /**
         * * `new` - New
         * * `draft` - Draft
         * * `rejected` - Rejected
         * * `waits_for_admin_approval` - In admin approval
         * * `in_parental_approval` - In parental approval
         * * `is_discussion` - In discussion
         * * `approved` - Approved
         * * `automatically_approved` - Automatically approved
         * * `updated` - Updated
         * * `archived` - Archived
         */
        status?:
          | 'approved'
          | 'archived'
          | 'automatically_approved'
          | 'draft'
          | 'in_parental_approval'
          | 'is_discussion'
          | 'new'
          | 'rejected'
          | 'updated'
          | 'waits_for_admin_approval'
        /** @format uuid */
        student?: string
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/planner/students-idp/statuses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerStudentsIdpSuggestionsCreate
     * @request POST:/planner/students-idp/suggestions/
     * @secure
     */,
    plannerStudentsIdpSuggestionsCreate: <T extends FetchKeys<Suggestions>>(
      data: SuggestionsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Suggestions, T>, any>({
        path: `/planner/students-idp/suggestions/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeTaeDraftActivitiesList
     * @request GET:/planner/tae/{taskId}/tae-draft-activities/
     * @secure
     */,
    plannerTaeTaeDraftActivitiesList: <
      T extends FetchKeys<PaginatedTAEGeneratorDraftActivityUpdateList>
    >(
      taskId: string,
      query?: {
        course?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        room?: string
        /** A search term. */
        search?: string
        students?: string[]
        teacher?: string
        /**
         * * `unchanged` - Unchanged
         * * `modified` - Modified
         * * `new` - New
         * * `deleted` - Deleted
         */
        update_type?: 'deleted' | 'modified' | 'new' | 'unchanged'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<PaginatedTAEGeneratorDraftActivityUpdateList, T>,
        any
      >({
        path: `/planner/tae/${taskId}/tae-draft-activities/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeTaeDraftActivitiesRetrieve
     * @request GET:/planner/tae/{taskId}/tae-draft-activities/{id}/
     * @secure
     */,
    plannerTaeTaeDraftActivitiesRetrieve: <
      T extends FetchKeys<TAEDraftActivity>
    >(
      id: string,
      taskId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEDraftActivity, T>, any>({
        path: `/planner/tae/${taskId}/tae-draft-activities/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeTaeDraftActivitiesAvailableFiltersRetrieve
     * @request GET:/planner/tae/{taskId}/tae-draft-activities/available-filters/
     * @secure
     */,
    plannerTaeTaeDraftActivitiesAvailableFiltersRetrieve: <
      T extends FetchKeys<TAEDraftActivityAvailableFilters>
    >(
      taskId: string,
      query?: {
        course?: string
        room?: string
        /** A search term. */
        search?: string
        students?: string[]
        teacher?: string
        /**
         * * `unchanged` - Unchanged
         * * `modified` - Modified
         * * `new` - New
         * * `deleted` - Deleted
         */
        update_type?: 'deleted' | 'modified' | 'new' | 'unchanged'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEDraftActivityAvailableFilters, T>, any>({
        path: `/planner/tae/${taskId}/tae-draft-activities/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeCreate
     * @request POST:/planner/tae/{timetableId}/
     * @secure
     */,
    plannerTaeCreate: <T extends FetchKeys<TAEGeneratorTask>>(
      timetableId: string,
      data: TAEGeneratorCreateTaskRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEGeneratorTask, T>, MessageResponseSchema>({
        path: `/planner/tae/${timetableId}/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeRetrieve
     * @request GET:/planner/tae/{timetableId}/{taskId}/
     * @secure
     */,
    plannerTaeRetrieve: <T extends FetchKeys<TAEGeneratorTask>>(
      taskId: number,
      timetableId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEGeneratorTask, T>, any>({
        path: `/planner/tae/${timetableId}/${taskId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeApplyCreate
     * @request POST:/planner/tae/{timetableId}/{taskId}/apply/
     * @secure
     */,
    plannerTaeApplyCreate: <T extends FetchKeys<void>>(
      taskId: number,
      timetableId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/tae/${timetableId}/${taskId}/apply/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeCancelCreate
     * @request POST:/planner/tae/{timetableId}/{taskId}/cancel/
     * @secure
     */,
    plannerTaeCancelCreate: <T extends FetchKeys<void>>(
      taskId: number,
      timetableId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/tae/${timetableId}/${taskId}/cancel/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description It sets to: - "discard" status if previous status is "succeeded" - "discard_and_failed" status if previous status is "failed"
     *
     * @tags planner
     * @name PlannerTaeDiscardCreate
     * @request POST:/planner/tae/{timetableId}/{taskId}/discard/
     * @secure
     */,
    plannerTaeDiscardCreate: <T extends FetchKeys<void>>(
      taskId: number,
      timetableId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/tae/${timetableId}/${taskId}/discard/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeRegenerateCreate
     * @request POST:/planner/tae/{timetableId}/{taskId}/regenerate/
     * @secure
     */,
    plannerTaeRegenerateCreate: <T extends FetchKeys<TAEGeneratorTask>>(
      taskId: number,
      timetableId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEGeneratorTask, T>, MessageResponseSchema>({
        path: `/planner/tae/${timetableId}/${taskId}/regenerate/`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeProcessStateRetrieve
     * @request GET:/planner/tae/{timetableId}/process-state/
     * @secure
     */,
    plannerTaeProcessStateRetrieve: <T extends FetchKeys<TAEGeneratorTask>>(
      timetableId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEGeneratorTask, T>, any>({
        path: `/planner/tae/${timetableId}/process-state/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeConflictsList
     * @request GET:/planner/tae/conflicts/{taeCourseEnrolmentId}/
     * @secure
     */,
    plannerTaeConflictsList: <T extends FetchKeys<TAESectionConflict[]>>(
      taeCourseEnrolmentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAESectionConflict[], T>, any>({
        path: `/planner/tae/conflicts/${taeCourseEnrolmentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeConflictsCalculateConflictsCreate
     * @request POST:/planner/tae/conflicts/calculate-conflicts/
     * @secure
     */,
    plannerTaeConflictsCalculateConflictsCreate: <
      T extends FetchKeys<TAETaskConflictCalculator>
    >(
      data: TAETaskConflictCalculatorRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAETaskConflictCalculator, T>, any>({
        path: `/planner/tae/conflicts/calculate-conflicts/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeConflictsCalculateConflictsNumberOfConflictsRetrieve
     * @request GET:/planner/tae/conflicts/calculate-conflicts/number-of-conflicts/{taeEnrolmentId}/{quarterId}/
     * @secure
     */,
    plannerTaeConflictsCalculateConflictsNumberOfConflictsRetrieve: <
      T extends FetchKeys<TAENumberOfConflictsResponse>
    >(
      quarterId: string,
      taeEnrolmentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAENumberOfConflictsResponse, T>, any>({
        path: `/planner/tae/conflicts/calculate-conflicts/number-of-conflicts/${taeEnrolmentId}/${quarterId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeConflictsCalculateConflictsProcessesStatesList
     * @request GET:/planner/tae/conflicts/calculate-conflicts/processes-states/{taeEnrolmentId}/
     * @secure
     */,
    plannerTaeConflictsCalculateConflictsProcessesStatesList: <
      T extends FetchKeys<TAETaskConflictCalculator[]>
    >(
      taeEnrolmentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAETaskConflictCalculator[], T>, any>({
        path: `/planner/tae/conflicts/calculate-conflicts/processes-states/${taeEnrolmentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentList
     * @request GET:/planner/tae/enrolment/
     * @secure
     */,
    plannerTaeEnrolmentList: <
      T extends FetchKeys<PaginatedTAEEnrolmentListList>
    >(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        school_year?: string
        /** A search term. */
        search?: string
        semester?: 'S1' | 'S2'
        /**
         * * `DRAFT` - Draft
         * * `VALIDATED` - Validated
         * * `SCHEDULED` - Scheduled
         * * `OPEN` - Open
         * * `CLOSED` - Closed
         * * `ARCHIVED` - Archived
         */
        status?:
          | 'ARCHIVED'
          | 'CLOSED'
          | 'DRAFT'
          | 'OPEN'
          | 'SCHEDULED'
          | 'VALIDATED'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTAEEnrolmentListList, T>, any>({
        path: `/planner/tae/enrolment/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentCreate
     * @request POST:/planner/tae/enrolment/
     * @secure
     */,
    plannerTaeEnrolmentCreate: <
      T extends FetchKeys<TAEEnrolmentSaveDraftResponseSchema>
    >(
      data: TAEEnrolmentRequest,
      query?: {
        save_draft?: boolean
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentSaveDraftResponseSchema, T>, any>({
        path: `/planner/tae/enrolment/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansList
     * @request GET:/planner/tae/enrolment-v2/plans/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansList: <T extends FetchKeys<PaginatedTAEPlanList>>(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        school_year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTAEPlanList, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansCreate
     * @request POST:/planner/tae/enrolment-v2/plans/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansCreate: <T extends FetchKeys<TAEPlan>>(
      data: TAEPlanRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEPlan, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansRetrieve
     * @request GET:/planner/tae/enrolment-v2/plans/{id}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansRetrieve: <T extends FetchKeys<TAEPlan>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEPlan, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansUpdate
     * @request PUT:/planner/tae/enrolment-v2/plans/{id}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansUpdate: <T extends FetchKeys<TAEPlan>>(
      id: number,
      data: TAEPlanRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEPlan, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansPartialUpdate
     * @request PATCH:/planner/tae/enrolment-v2/plans/{id}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansPartialUpdate: <T extends FetchKeys<TAEPlan>>(
      id: number,
      data: PatchedTAEPlanRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEPlan, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsEnrolmentsCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/{draftSectionId}/enrolments/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsEnrolmentsCreate: <
      T extends FetchKeys<TAEEnrolmentV2DraftSectionEnrolment>
    >(
      draftSectionId: string,
      planId: string,
      data: TAEDraftSectionEnrolmentCreateRequest,
      query?: {
        /** @default false */
        force_increase?: boolean
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TAEEnrolmentV2DraftSectionEnrolment, T>,
        TAEDraftSectionCapacityConflictsResponse
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/${draftSectionId}/enrolments/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsEnrolmentsUpdate
     * @request PUT:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/{draftSectionId}/enrolments/{enrolmentId}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsEnrolmentsUpdate: <
      T extends FetchKeys<TAEEnrolmentV2DraftSectionEnrolment>
    >(
      draftSectionId: string,
      enrolmentId: number,
      planId: string,
      data: TAEDraftSectionEnrolmentUpdateRequest,
      query?: {
        /** @default false */
        force_increase?: boolean
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TAEEnrolmentV2DraftSectionEnrolment, T>,
        TAEDraftSectionCapacityConflictsResponse
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/${draftSectionId}/enrolments/${enrolmentId}/`,
        method: 'PUT',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsEnrolmentsPartialUpdate
     * @request PATCH:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/{draftSectionId}/enrolments/{enrolmentId}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsEnrolmentsPartialUpdate: <
      T extends FetchKeys<TAEEnrolmentV2DraftSectionEnrolment>
    >(
      draftSectionId: string,
      enrolmentId: number,
      planId: string,
      data: PatchedTAEDraftSectionEnrolmentUpdateRequest,
      query?: {
        /** @default false */
        force_increase?: boolean
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TAEEnrolmentV2DraftSectionEnrolment, T>,
        TAEDraftSectionCapacityConflictsResponse
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/${draftSectionId}/enrolments/${enrolmentId}/`,
        method: 'PATCH',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsEnrolmentsDestroy
     * @request DELETE:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/{draftSectionId}/enrolments/{enrolmentId}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsEnrolmentsDestroy: <
      T extends FetchKeys<void>
    >(
      draftSectionId: string,
      enrolmentId: number,
      planId: string,
      query?: {
        /** @default false */
        force_withdraw_course_approval?: boolean
        /** @default false */
        withdraw_course_approval?: boolean
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<void, T>,
        TAEDraftSectionEnrolmentDeleteConflictResponse
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/${draftSectionId}/enrolments/${enrolmentId}/`,
        method: 'DELETE',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsCoursesList
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/courses/{courseEnrolmentId}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsCoursesList: <
      T extends FetchKeys<TAECourseEnrolmentDraftSection[]>
    >(
      courseEnrolmentId: string,
      planId: string,
      query: {
        quarter: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAECourseEnrolmentDraftSection[], T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/courses/${courseEnrolmentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsCoursesCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/courses/{courseEnrolmentId}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsCoursesCreate: <
      T extends FetchKeys<TAECourseEnrolmentDraftSection>
    >(
      courseEnrolmentId: string,
      planId: string,
      query: {
        /** @default false */
        force_increase?: boolean
        quarter: number
      },
      data: TAEDraftSectionCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TAECourseEnrolmentDraftSection, T>,
        TAEDraftSectionCapacityConflictsResponse
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/courses/${courseEnrolmentId}/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsCoursesUpdate
     * @request PUT:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/courses/{courseEnrolmentId}/{id}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsCoursesUpdate: <
      T extends FetchKeys<TAECourseEnrolmentDraftSection>
    >(
      courseEnrolmentId: string,
      id: number,
      planId: string,
      query: {
        quarter: number
      },
      data: TAEDraftSectionUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAECourseEnrolmentDraftSection, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/courses/${courseEnrolmentId}/${id}/`,
        method: 'PUT',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsCoursesPartialUpdate
     * @request PATCH:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/courses/{courseEnrolmentId}/{id}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsCoursesPartialUpdate: <
      T extends FetchKeys<TAECourseEnrolmentDraftSection>
    >(
      courseEnrolmentId: string,
      id: number,
      planId: string,
      query: {
        quarter: number
      },
      data: PatchedTAEDraftSectionUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAECourseEnrolmentDraftSection, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/courses/${courseEnrolmentId}/${id}/`,
        method: 'PATCH',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsCoursesDestroy
     * @request DELETE:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/courses/{courseEnrolmentId}/{id}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsCoursesDestroy: <
      T extends FetchKeys<void>
    >(
      courseEnrolmentId: string,
      id: number,
      planId: string,
      query: {
        quarter: number
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/courses/${courseEnrolmentId}/${id}/`,
        method: 'DELETE',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsCoursesIncreaseCapacityCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/courses/{courseEnrolmentId}/{id}/increase-capacity/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsCoursesIncreaseCapacityCreate: <
      T extends FetchKeys<TAEDraftSectionCourseCapacityResponse>
    >(
      courseEnrolmentId: string,
      id: number,
      planId: string,
      query: {
        quarter: number
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEDraftSectionCourseCapacityResponse, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/courses/${courseEnrolmentId}/${id}/increase-capacity/`,
        method: 'POST',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsCoursesNotAssignedStudentsList
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/courses/{courseEnrolmentId}/not-assigned-students/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsCoursesNotAssignedStudentsList: <
      T extends FetchKeys<TAEDraftSectionAvailableStudent[]>
    >(
      courseEnrolmentId: string,
      planId: string,
      query: {
        quarter: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEDraftSectionAvailableStudent[], T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/courses/${courseEnrolmentId}/not-assigned-students/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsCoursesAthList
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/courses/ath/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsCoursesAthList: <
      T extends FetchKeys<PaginatedTAECourseEnrolmentInDraftSectionsList>
    >(
      planId: string,
      query: {
        /** @format uuid */
        course?: string
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        course_group?: 'ATH' | 'CCU' | null
        grades?: number[]
        has_conflicts?: boolean
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter: number
        /** A search term. */
        search?: string
        tae_enrolment?: number[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<PaginatedTAECourseEnrolmentInDraftSectionsList, T>,
        any
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/courses/ath/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsCoursesAvailableFiltersRetrieve
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/courses/available-filters/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsCoursesAvailableFiltersRetrieve: <
      T extends FetchKeys<TAECourseEnrolmentDraftSectionsAvailableFilters>
    >(
      planId: string,
      query: {
        /** @format uuid */
        course?: string
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         * @minLength 1
         */
        course_group?: 'CCU' | 'ATH'
        grades?: number[]
        has_conflicts?: boolean
        quarter: number
        /** A search term. */
        search?: string
        tae_enrolment?: number[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TAECourseEnrolmentDraftSectionsAvailableFilters, T>,
        any
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/courses/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsCoursesCcuList
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/courses/ccu/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsCoursesCcuList: <
      T extends FetchKeys<PaginatedTAECourseEnrolmentInDraftSectionsList>
    >(
      planId: string,
      query: {
        /** @format uuid */
        course?: string
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        course_group?: 'ATH' | 'CCU' | null
        grades?: number[]
        has_conflicts?: boolean
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter: number
        /** A search term. */
        search?: string
        tae_enrolment?: number[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<PaginatedTAECourseEnrolmentInDraftSectionsList, T>,
        any
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/courses/ccu/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsStudentsToReviewList
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/students-to-review/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsStudentsToReviewList: <
      T extends FetchKeys<PaginatedTAEPlanStudentToReviewList>
    >(
      planId: number,
      query: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter: number
        review_cause?: ('no_enrolment' | 'no_section' | 'over_limit')[]
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTAEPlanStudentToReviewList, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/students-to-review/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsStudentsToReviewCountRetrieve
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/students-to-review-count/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsStudentsToReviewCountRetrieve: <
      T extends FetchKeys<TAEPlanStudentsToReviewCountResponse>
    >(
      planId: number,
      query: {
        quarter: number
        review_cause?: ('no_enrolment' | 'no_section' | 'over_limit')[]
        /** @default false */
        skip_new_student_filter?: boolean

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEPlanStudentsToReviewCountResponse, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/students-to-review-count/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsTasksCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/tasks/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsTasksCreate: <
      T extends FetchKeys<TAEEnrolmentV2DraftSectionsTask>
    >(
      planId: string,
      query: {
        quarter: number
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TAEEnrolmentV2DraftSectionsTask, T>,
        TAEEnrolmentV2DraftSectionsCreateValidationError | MessageResponseSchema
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/tasks/`,
        method: 'POST',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsTasksRetrieve
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/tasks/{taskId}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsTasksRetrieve: <
      T extends FetchKeys<TAEEnrolmentV2DraftSectionsTask>
    >(
      planId: string,
      taskId: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentV2DraftSectionsTask, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/tasks/${taskId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsTasksCancelCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/tasks/{taskId}/cancel/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsTasksCancelCreate: <
      T extends FetchKeys<void>
    >(
      planId: string,
      taskId: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/tasks/${taskId}/cancel/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsTasksCanGenerateRetrieve
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/tasks/can-generate/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsTasksCanGenerateRetrieve: <
      T extends FetchKeys<DraftSectionsGenerationAvailability>
    >(
      planId: string,
      query: {
        quarter: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DraftSectionsGenerationAvailability, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/tasks/can-generate/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsTasksProcessStateRetrieve
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/tasks/process-state/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsTasksProcessStateRetrieve: <
      T extends FetchKeys<TAEEnrolmentV2DraftSectionsTask>
    >(
      planId: string,
      query: {
        quarter: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentV2DraftSectionsTask, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/tasks/process-state/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansDraftSectionsTasksRegenerateCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/draft-sections/tasks/regenerate/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansDraftSectionsTasksRegenerateCreate: <
      T extends FetchKeys<TAEEnrolmentV2DraftSectionsTask>
    >(
      planId: string,
      query: {
        /** @default false */
        force_copy?: boolean
        quarter: number
      },
      data: RegenerateSectionsRequestRequest,
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TAEEnrolmentV2DraftSectionsTask, T>,
        | TAEDraftSectionsRegenerationValidationError
        | TAEDraftSectionsRegenerationConflict
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/draft-sections/tasks/regenerate/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsList
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsList: <
      T extends FetchKeys<PaginatedTAEEnrolmentV2ListList>
    >(
      planId: string,
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /** @format date */
        start_after?: string
        /** @format date */
        start_before?: string
        /**
         * * `DRAFT` - Draft
         * * `SCHEDULED` - Scheduled
         * * `OPEN` - Open
         * * `CLOSED` - Closed
         * * `ARCHIVED` - Archived
         */
        status?: 'ARCHIVED' | 'CLOSED' | 'DRAFT' | 'OPEN' | 'SCHEDULED'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTAEEnrolmentV2ListList, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/enrolments/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCreate: <
      T extends FetchKeys<TAEEnrolmentDetail>
    >(
      planId: string,
      data: TAEEnrolmentDetailRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentDetail, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsRetrieve
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsRetrieve: <
      T extends FetchKeys<TAEEnrolmentDetail>
    >(
      id: number,
      planId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentDetail, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsUpdate
     * @request PUT:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsUpdate: <
      T extends FetchKeys<TAEEnrolmentDetail>
    >(
      id: number,
      planId: string,
      data: TAEEnrolmentDetailRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentDetail, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsPartialUpdate
     * @request PATCH:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsPartialUpdate: <
      T extends FetchKeys<TAEEnrolmentV2List>
    >(
      id: number,
      planId: string,
      data: PatchedTAEEnrolmentV2ListRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentV2List, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsParentCommentsList
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/{studentId}/parent-comments/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsParentCommentsList: <
      T extends FetchKeys<TAEParentCommentV2[]>
    >(
      id: string,
      planId: string,
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEParentCommentV2[], T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/${studentId}/parent-comments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsArchiveCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/archive/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsArchiveCreate: <
      T extends FetchKeys<void>
    >(
      id: number,
      planId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, TAEEnrolmentActivationError>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/archive/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsAvailableCoursesList
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/available-courses/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsAvailableCoursesList: <
      T extends FetchKeys<PaginatedTAEAvailableCourseList>
    >(
      id: number,
      planId: number,
      query?: {
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        group?: 'ATH' | 'CCU' | null
        /**
         * * `true` - true
         * * `false` - false
         */
        is_selected?: 'false' | 'true'
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTAEAvailableCourseList, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/available-courses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCloseCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/close/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCloseCreate: <
      T extends FetchKeys<void>
    >(
      id: number,
      planId: string,
      data: CloseEnrolmentChoiceV2Request,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, MessageResponseSchema>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/close/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCoursesCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/courses/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCoursesCreate: <
      T extends FetchKeys<TAECourseEnrolment[]>
    >(
      id: string,
      planId: string,
      data: TAECourseSelectionRequest,
      query?: {
        /** @format uuid */
        course?: string
        /**
         * * `no_free_spots` - No free spots
         * * `available` - Available
         * * `below_min_participants` - Below min participants
         */
        free_spots?: (
          | 'available'
          | 'below_min_participants'
          | 'no_free_spots'
        )[]
        grades?: number[]
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        group?: 'ATH' | 'CCU' | null
        /** A search term. */
        search?: string
        /**
         * * `open` - Open
         * * `closed` - Closed
         */
        sign_up?: 'closed' | 'open'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAECourseEnrolment[], T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/courses/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCoursesUpdate
     * @request PUT:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/courses/{courseEnrolmentId}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCoursesUpdate: <
      T extends FetchKeys<TAECourseEnrolment>
    >(
      courseEnrolmentId: number,
      id: string,
      planId: string,
      data: TAECourseEnrolmentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAECourseEnrolment, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/courses/${courseEnrolmentId}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCoursesPartialUpdate
     * @request PATCH:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/courses/{courseEnrolmentId}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCoursesPartialUpdate: <
      T extends FetchKeys<TAECourseEnrolment>
    >(
      courseEnrolmentId: number,
      id: string,
      planId: string,
      data: PatchedTAECourseEnrolmentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAECourseEnrolment, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/courses/${courseEnrolmentId}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCoursesDestroy
     * @request DELETE:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/courses/{courseEnrolmentId}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCoursesDestroy: <
      T extends FetchKeys<void>
    >(
      courseEnrolmentId: number,
      id: string,
      planId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/courses/${courseEnrolmentId}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCoursesCloseSignupCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/courses/{courseEnrolmentId}/close-signup/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCoursesCloseSignupCreate: <
      T extends FetchKeys<void>
    >(
      courseEnrolmentId: number,
      id: string,
      planId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, DetailResponseSchema>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/courses/${courseEnrolmentId}/close-signup/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCoursesEnrollCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/courses/{courseEnrolmentId}/enroll/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCoursesEnrollCreate: <
      T extends FetchKeys<TAECourseEnrolmentEnrollStudentsSuccess>
    >(
      courseEnrolmentId: number,
      id: string,
      planId: string,
      data: TAECourseEnrolmentEnrollStudentsRequest,
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TAECourseEnrolmentEnrollStudentsSuccess, T>,
        TAECourseEnrolmentCapacityReached
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/courses/${courseEnrolmentId}/enroll/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCoursesManageSignupList
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/courses/{courseEnrolmentId}/manage-signup/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCoursesManageSignupList: <
      T extends FetchKeys<TAECourseEnrolmentManageSignupList[]>
    >(
      courseEnrolmentId: number,
      id: string,
      planId: string,
      query?: {
        /** @format uuid */
        course?: string
        /**
         * * `no_free_spots` - No free spots
         * * `available` - Available
         * * `below_min_participants` - Below min participants
         */
        free_spots?: (
          | 'available'
          | 'below_min_participants'
          | 'no_free_spots'
        )[]
        grades?: number[]
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        group?: 'ATH' | 'CCU' | null
        /** A search term. */
        search?: string
        /**
         * * `open` - Open
         * * `closed` - Closed
         */
        sign_up?: 'closed' | 'open'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAECourseEnrolmentManageSignupList[], T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/courses/${courseEnrolmentId}/manage-signup/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCoursesReopenSignupCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/courses/{courseEnrolmentId}/reopen-signup/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCoursesReopenSignupCreate: <
      T extends FetchKeys<void>
    >(
      courseEnrolmentId: number,
      id: string,
      planId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, DetailResponseSchema>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/courses/${courseEnrolmentId}/reopen-signup/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCoursesStudentsToEnrollList
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/courses/{courseEnrolmentId}/students-to-enroll/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCoursesStudentsToEnrollList: <
      T extends FetchKeys<TAEEnrolmentStudent[]>
    >(
      courseEnrolmentId: string,
      id: string,
      planId: string,
      query?: {
        grades?: string[]
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentStudent[], T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/courses/${courseEnrolmentId}/students-to-enroll/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCoursesStudentsToEnrollAvailableFiltersRetrieve
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/courses/{courseEnrolmentId}/students-to-enroll/available-filters/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCoursesStudentsToEnrollAvailableFiltersRetrieve:
      <T extends FetchKeys<TAECourseStudentsToEnrollAvailableFilters>>(
        courseEnrolmentId: string,
        id: string,
        planId: string,
        query?: {
          /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
          fields?: string[]
          grades?: string[]
          /** A search term. */
          search?: string

          fetchKeys?: T
        },
        params: RequestParams = {}
      ) =>
        this.request<
          PickKeys<TAECourseStudentsToEnrollAvailableFilters, T>,
          any
        >({
          path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/courses/${courseEnrolmentId}/students-to-enroll/available-filters/`,
          method: 'GET',
          query: postprocessQuery(query),
          secure: true,
          format: 'json',
          ...params
        })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCoursesAthList
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/courses/ath/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCoursesAthList: <
      T extends FetchKeys<PaginatedTAECourseEnrolmentList>
    >(
      id: string,
      planId: string,
      query?: {
        /** @format uuid */
        course?: string
        /**
         * * `no_free_spots` - No free spots
         * * `available` - Available
         * * `below_min_participants` - Below min participants
         */
        free_spots?: (
          | 'available'
          | 'below_min_participants'
          | 'no_free_spots'
        )[]
        grades?: number[]
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        group?: 'ATH' | 'CCU' | null
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /**
         * * `open` - Open
         * * `closed` - Closed
         */
        sign_up?: 'closed' | 'open'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTAECourseEnrolmentList, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/courses/ath/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCoursesAvailableFiltersRetrieve
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/courses/available-filters/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCoursesAvailableFiltersRetrieve: <
      T extends FetchKeys<TAECourseEnrolmentAvailableFilters>
    >(
      id: string,
      planId: string,
      query?: {
        /** @format uuid */
        course?: string
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        /**
         * * `no_free_spots` - No free spots
         * * `available` - Available
         * * `below_min_participants` - Below min participants
         */
        free_spots?: (
          | 'available'
          | 'below_min_participants'
          | 'no_free_spots'
        )[]
        grades?: number[]
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        group?: 'ATH' | 'CCU' | null
        /** A search term. */
        search?: string
        /**
         * * `open` - Open
         * * `closed` - Closed
         */
        sign_up?: 'closed' | 'open'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAECourseEnrolmentAvailableFilters, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/courses/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCoursesCcuList
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/courses/ccu/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCoursesCcuList: <
      T extends FetchKeys<PaginatedTAECourseEnrolmentList>
    >(
      id: string,
      planId: string,
      query?: {
        /** @format uuid */
        course?: string
        /**
         * * `no_free_spots` - No free spots
         * * `available` - Available
         * * `below_min_participants` - Below min participants
         */
        free_spots?: (
          | 'available'
          | 'below_min_participants'
          | 'no_free_spots'
        )[]
        grades?: number[]
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        group?: 'ATH' | 'CCU' | null
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /**
         * * `open` - Open
         * * `closed` - Closed
         */
        sign_up?: 'closed' | 'open'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTAECourseEnrolmentList, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/courses/ccu/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCoursesGradeConflictsRetrieve
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/courses/grade-conflicts/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCoursesGradeConflictsRetrieve: <
      T extends FetchKeys<TAECourseGradeConflictResponse>
    >(
      id: string,
      planId: string,
      query?: {
        /** @format uuid */
        course?: string
        /**
         * * `no_free_spots` - No free spots
         * * `available` - Available
         * * `below_min_participants` - Below min participants
         */
        free_spots?: (
          | 'available'
          | 'below_min_participants'
          | 'no_free_spots'
        )[]
        grades?: number[]
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        group?: 'ATH' | 'CCU' | null
        /** A search term. */
        search?: string
        /**
         * * `open` - Open
         * * `closed` - Closed
         */
        sign_up?: 'closed' | 'open'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAECourseGradeConflictResponse, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/courses/grade-conflicts/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCoursesPublishCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/courses/publish/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCoursesPublishCreate: <
      T extends FetchKeys<TAECourseEnrolmentPublicationResult>
    >(
      id: string,
      planId: string,
      query?: {
        ath_page?: number
        ath_page_size?: string
        ccu_page?: number
        ccu_page_size?: string
        /** @format uuid */
        course?: string
        /** @default false */
        force?: boolean
        /**
         * * `no_free_spots` - No free spots
         * * `available` - Available
         * * `below_min_participants` - Below min participants
         */
        free_spots?: (
          | 'available'
          | 'below_min_participants'
          | 'no_free_spots'
        )[]
        grades?: number[]
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        group?: 'ATH' | 'CCU' | null
        /** A search term. */
        search?: string
        /**
         * * `open` - Open
         * * `closed` - Closed
         */
        sign_up?: 'closed' | 'open'
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TAECourseEnrolmentPublicationResult, T>,
        TAECourseEnrolmentPublishErrorV2 | TAECoursePublishGradeConflictResponse
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/courses/publish/`,
        method: 'POST',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCoursesSummaryRetrieve
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/courses/summary/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCoursesSummaryRetrieve: <
      T extends FetchKeys<TAECourseEnrolmentSummary>
    >(
      id: string,
      planId: string,
      query?: {
        /** @format uuid */
        course?: string
        /**
         * * `no_free_spots` - No free spots
         * * `available` - Available
         * * `below_min_participants` - Below min participants
         */
        free_spots?: (
          | 'available'
          | 'below_min_participants'
          | 'no_free_spots'
        )[]
        grades?: number[]
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        group?: 'ATH' | 'CCU' | null
        /** A search term. */
        search?: string
        /**
         * * `open` - Open
         * * `closed` - Closed
         */
        sign_up?: 'closed' | 'open'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAECourseEnrolmentSummary, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/courses/summary/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsCoursesValidateCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/courses/validate/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsCoursesValidateCreate: <
      T extends FetchKeys<TAECourseEnrolmentBulkValidationResult>
    >(
      id: string,
      planId: string,
      query?: {
        ath_page?: number
        ath_page_size?: string
        ccu_page?: number
        ccu_page_size?: string
        /** @format uuid */
        course?: string
        /**
         * * `no_free_spots` - No free spots
         * * `available` - Available
         * * `below_min_participants` - Below min participants
         */
        free_spots?: (
          | 'available'
          | 'below_min_participants'
          | 'no_free_spots'
        )[]
        grades?: number[]
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        group?: 'ATH' | 'CCU' | null
        /** A search term. */
        search?: string
        /**
         * * `open` - Open
         * * `closed` - Closed
         */
        sign_up?: 'closed' | 'open'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAECourseEnrolmentBulkValidationResult, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/courses/validate/`,
        method: 'POST',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsDiscardScheduleCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/discard-schedule/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsDiscardScheduleCreate: <
      T extends FetchKeys<TAEEnrolmentDetail>
    >(
      id: number,
      planId: string,
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TAEEnrolmentDetail, T>,
        TAEEnrolmentActivationError
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/discard-schedule/`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsOpenCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/open/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsOpenCreate: <
      T extends FetchKeys<TAEEnrolmentDetail>
    >(
      id: number,
      planId: string,
      query?: {
        /** @default false */
        force?: boolean
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TAEEnrolmentDetail, T>,
        TAEEnrolmentActivationError | TAEEnrolmentActivationConflict
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/open/`,
        method: 'POST',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsScheduleCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/schedule/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsScheduleCreate: <
      T extends FetchKeys<TAEEnrolmentDetail>
    >(
      id: number,
      planId: string,
      query?: {
        /** @default false */
        force?: boolean
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TAEEnrolmentDetail, T>,
        TAEEnrolmentActivationError | TAEEnrolmentActivationConflict
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/schedule/`,
        method: 'POST',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsStudentChoicesUpdate
     * @request PUT:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/student-choices/{choiceId}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsStudentChoicesUpdate: <
      T extends FetchKeys<TAEStudentChoiceNumberOfLessons>
    >(
      choiceId: number,
      id: string,
      planId: string,
      data: TAEStudentChoiceNumberOfLessonsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEStudentChoiceNumberOfLessons, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/student-choices/${choiceId}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsStudentChoicesPartialUpdate
     * @request PATCH:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/student-choices/{choiceId}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsStudentChoicesPartialUpdate: <
      T extends FetchKeys<TAEStudentChoiceNumberOfLessons>
    >(
      choiceId: number,
      id: string,
      planId: string,
      data: PatchedTAEStudentChoiceNumberOfLessonsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEStudentChoiceNumberOfLessons, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/student-choices/${choiceId}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsStudentChoicesApproveCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/student-choices/{choiceId}/approve/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsStudentChoicesApproveCreate: <
      T extends FetchKeys<TAEStudentChoiceFreeSpots>
    >(
      choiceId: number,
      id: string,
      planId: string,
      data: TAEStudentChoiceApproveRequest,
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TAEStudentChoiceFreeSpots, T>,
        TAECourseEnrolmentCapacityReached
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/student-choices/${choiceId}/approve/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsStudentChoicesRejectCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/student-choices/{choiceId}/reject/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsStudentChoicesRejectCreate: <
      T extends FetchKeys<TAEStudentChoiceRejectResponse>
    >(
      choiceId: number,
      id: string,
      planId: string,
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TAEStudentChoiceRejectResponse, T>,
        TAEStudentChoiceStatusError | MessageResponseSchema
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/student-choices/${choiceId}/reject/`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsStudentsUpdate
     * @request PUT:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/students/{studentEnrolmentId}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsStudentsUpdate: <
      T extends FetchKeys<TAEStudentEnrolmentSportMembership>
    >(
      id: string,
      planId: string,
      studentEnrolmentId: string,
      data: TAEStudentEnrolmentSportMembershipRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEStudentEnrolmentSportMembership, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/students/${studentEnrolmentId}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsStudentsPartialUpdate
     * @request PATCH:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/students/{studentEnrolmentId}/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsStudentsPartialUpdate: <
      T extends FetchKeys<TAEStudentEnrolmentSportMembership>
    >(
      id: string,
      planId: string,
      studentEnrolmentId: string,
      data: PatchedTAEStudentEnrolmentSportMembershipRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEStudentEnrolmentSportMembership, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/students/${studentEnrolmentId}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsStudentsManageSignupList
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/students/{studentEnrolmentId}/manage-signup/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsStudentsManageSignupList: <
      T extends FetchKeys<TAEStudentEnrolmentManageSignupList[]>
    >(
      id: string,
      planId: string,
      studentEnrolmentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEStudentEnrolmentManageSignupList[], T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/students/${studentEnrolmentId}/manage-signup/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsStudentsSignupGroupsRetrieve
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/students/{studentEnrolmentId}/signup-groups/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsStudentsSignupGroupsRetrieve: <
      T extends FetchKeys<TAEStudentEnrolmentSignupGroups>
    >(
      id: string,
      planId: string,
      studentEnrolmentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEStudentEnrolmentSignupGroups, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/students/${studentEnrolmentId}/signup-groups/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsStudentsToggleReopenCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/students/{studentEnrolmentId}/toggle-reopen/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsStudentsToggleReopenCreate: <
      T extends FetchKeys<TAEStudentEnrolmentReopenSignup>
    >(
      id: string,
      planId: string,
      studentEnrolmentId: string,
      data: TAEStudentEnrolmentReopenSignupRequest,
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TAEStudentEnrolmentReopenSignup, T>,
        DetailResponseSchema
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/students/${studentEnrolmentId}/toggle-reopen/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsStudentsAvailableFiltersRetrieve
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/students/available-filters/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsStudentsAvailableFiltersRetrieve: <
      T extends FetchKeys<TAEEnrolmentV2StudentsAvailableFilters>
    >(
      id: string,
      planId: string,
      query?: {
        /**
         * * `enrolled` - Enrolled
         * * `re_enrolling` - Re-enrolling
         * * `not_enrolled` - Not enrolled
         */
        enrolment_state?: 'enrolled' | 'not_enrolled' | 're_enrolling'
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        grades?: string[]
        has_parent_comment?: boolean
        is_student_new?: string
        /** A search term. */
        search?: string
        /**
         * * `hf_member` - Rosenberg Health & Fitness Center Membership
         * * `none` - No membership
         */
        sport_membership?: 'hf_member' | 'none'
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentV2StudentsAvailableFilters, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/students/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsStudentsEnrolledList
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/students/enrolled/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsStudentsEnrolledList: <
      T extends FetchKeys<PaginatedTAEStudentEnrolmentListList>
    >(
      id: string,
      planId: string,
      query?: {
        /**
         * * `enrolled` - Enrolled
         * * `re_enrolling` - Re-enrolling
         * * `not_enrolled` - Not enrolled
         */
        enrolment_state?: 'enrolled' | 'not_enrolled' | 're_enrolling'
        grades?: string[]
        has_parent_comment?: boolean
        is_student_new?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /**
         * * `hf_member` - Rosenberg Health & Fitness Center Membership
         * * `none` - No membership
         */
        sport_membership?: 'hf_member' | 'none'
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTAEStudentEnrolmentListList, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/students/enrolled/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsStudentsNotEnrolledList
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/students/not-enrolled/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsStudentsNotEnrolledList: <
      T extends FetchKeys<PaginatedTAEEnrolmentStudentList>
    >(
      id: string,
      planId: string,
      query?: {
        /**
         * * `enrolled` - Enrolled
         * * `re_enrolling` - Re-enrolling
         * * `not_enrolled` - Not enrolled
         */
        enrolment_state?: 'enrolled' | 'not_enrolled' | 're_enrolling'
        grades?: string[]
        has_parent_comment?: boolean
        is_student_new?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /**
         * * `hf_member` - Rosenberg Health & Fitness Center Membership
         * * `none` - No membership
         */
        sport_membership?: 'hf_member' | 'none'
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTAEEnrolmentStudentList, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/students/not-enrolled/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsStudentsSummaryRetrieve
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/{id}/students/summary/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsStudentsSummaryRetrieve: <
      T extends FetchKeys<TAEEnrolmentStudentsSummary>
    >(
      id: string,
      planId: string,
      query?: {
        /**
         * * `enrolled` - Enrolled
         * * `re_enrolling` - Re-enrolling
         * * `not_enrolled` - Not enrolled
         */
        enrolment_state?: 'enrolled' | 'not_enrolled' | 're_enrolling'
        grades?: string[]
        has_parent_comment?: boolean
        is_student_new?: string
        /** A search term. */
        search?: string
        /**
         * * `hf_member` - Rosenberg Health & Fitness Center Membership
         * * `none` - No membership
         */
        sport_membership?: 'hf_member' | 'none'
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentStudentsSummary, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/${id}/students/summary/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsAvailableFiltersRetrieve
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/available-filters/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsAvailableFiltersRetrieve: <
      T extends FetchKeys<TAEEnrolmentV2AvailableFilters>
    >(
      planId: string,
      query?: {
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        /** A search term. */
        search?: string
        /** @format date */
        start_after?: string
        /** @format date */
        start_before?: string
        /**
         * * `DRAFT` - Draft
         * * `SCHEDULED` - Scheduled
         * * `OPEN` - Open
         * * `CLOSED` - Closed
         * * `ARCHIVED` - Archived
         */
        status?: 'ARCHIVED' | 'CLOSED' | 'DRAFT' | 'OPEN' | 'SCHEDULED'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentV2AvailableFilters, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsConfigOptionsRetrieve
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/enrolments/config-options/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsConfigOptionsRetrieve: <
      T extends FetchKeys<TAEEnrolmentConfigOptions>
    >(
      planId: string,
      query?: {
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentConfigOptions, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/config-options/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansEnrolmentsUploadOfferCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/enrolments/upload-offer/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansEnrolmentsUploadOfferCreate: <
      T extends FetchKeys<TAEOfferUpload>
    >(
      planId: string,
      data: TAEOfferUploadRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEOfferUpload, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/enrolments/upload-offer/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.UrlEncoded,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansStudentsCoursesToEnrollList
     * @request GET:/planner/tae/enrolment-v2/plans/{planId}/students/{studentId}/courses-to-enroll/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansStudentsCoursesToEnrollList: <
      T extends FetchKeys<TAECourseToEnrolOn[]>
    >(
      planId: string,
      studentId: string,
      query?: {
        enrolment_ids?: number[]
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAECourseToEnrolOn[], T>, any>({
        path: `/planner/tae/enrolment-v2/plans/${planId}/students/${studentId}/courses-to-enroll/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Enroll a student on courses from one or more enrolments of this plan. Enrolments must be OPEN or CLOSED; CLOSED requires frozen roster membership and current course eligibility. The request is atomic. Both 200 and 409 responses are lists, including for a single enrolment. 200 includes every requested enrolment; 409 includes only capacity conflicts and means all enrolments and capacity increases were rolled back. Each result includes enrolment_id and enrolment_name. Results are ordered by enrolment ID.
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansStudentsEnrollCreate
     * @request POST:/planner/tae/enrolment-v2/plans/{planId}/students/{studentId}/enroll/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansStudentsEnrollCreate: <
      T extends FetchKeys<TAEStudentEnrolOnCoursesSuccess[]>
    >(
      planId: string,
      studentId: string,
      data: TAEStudentEnrolOnCoursesRequest,
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TAEStudentEnrolOnCoursesSuccess[], T>,
        TAEStudentEnrolOnCoursesConflict[]
      >({
        path: `/planner/tae/enrolment-v2/plans/${planId}/students/${studentId}/enroll/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansAvailableFiltersRetrieve
     * @request GET:/planner/tae/enrolment-v2/plans/available-filters/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansAvailableFiltersRetrieve: <
      T extends FetchKeys<TAEPlanAvailableFilters>
    >(
      query?: {
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        /** Which field to use when ordering the results. */
        ordering?: string
        school_year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEPlanAvailableFilters, T>, any>({
        path: `/planner/tae/enrolment-v2/plans/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentV2PlansAvailableSchoolYearsList
     * @request GET:/planner/tae/enrolment-v2/plans/available-school-years/
     * @secure
     */,
    plannerTaeEnrolmentV2PlansAvailableSchoolYearsList: <
      T extends FetchKeys<IntegerOption[]>
    >(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        school_year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<IntegerOption[], T>, any>({
        path: `/planner/tae/enrolment-v2/plans/available-school-years/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentRetrieve
     * @request GET:/planner/tae/enrolment/{id}/
     * @secure
     */,
    plannerTaeEnrolmentRetrieve: <T extends FetchKeys<TAEEnrolmentRetrieve>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentRetrieve, T>, any>({
        path: `/planner/tae/enrolment/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentUpdate
     * @request PUT:/planner/tae/enrolment/{id}/
     * @secure
     */,
    plannerTaeEnrolmentUpdate: <T extends FetchKeys<TAEEnrolmentStatus>>(
      id: number,
      data: TAEEnrolmentRequest,
      query?: {
        force_update?: boolean
        save_draft?: boolean
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TAEEnrolmentStatus, T>,
        TAEEnrolmentUpdateConflictResponse
      >({
        path: `/planner/tae/enrolment/${id}/`,
        method: 'PUT',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentArchiveCreate
     * @request POST:/planner/tae/enrolment/{id}/archive/
     * @secure
     */,
    plannerTaeEnrolmentArchiveCreate: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/tae/enrolment/${id}/archive/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentCheckConflictsCreate
     * @request POST:/planner/tae/enrolment/{id}/check-conflicts/
     * @secure
     */,
    plannerTaeEnrolmentCheckConflictsCreate: <
      T extends FetchKeys<TAEEnrolmentUpdateConflictResponse>
    >(
      id: number,
      data: TAEEnrolmentUpdateCheckConflictsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentUpdateConflictResponse, T>, any>({
        path: `/planner/tae/enrolment/${id}/check-conflicts/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentCloseCreate
     * @request POST:/planner/tae/enrolment/{id}/close/
     * @secure
     */,
    plannerTaeEnrolmentCloseCreate: <T extends FetchKeys<void>>(
      id: number,
      data: CloseEnrolmentChoiceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, MessageResponseSchema>({
        path: `/planner/tae/enrolment/${id}/close/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDiscardScheduleCreate
     * @request POST:/planner/tae/enrolment/{id}/discard-schedule/
     * @secure
     */,
    plannerTaeEnrolmentDiscardScheduleCreate: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, MessageResponseSchema>({
        path: `/planner/tae/enrolment/${id}/discard-schedule/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentForceOpenCreate
     * @request POST:/planner/tae/enrolment/{id}/force-open/
     * @secure
     */,
    plannerTaeEnrolmentForceOpenCreate: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, MessageResponseSchema>({
        path: `/planner/tae/enrolment/${id}/force-open/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentPushDraftSectionsCreate
     * @request POST:/planner/tae/enrolment/{id}/push-draft-sections/
     * @secure
     */,
    plannerTaeEnrolmentPushDraftSectionsCreate: <T extends FetchKeys<void>>(
      id: number,
      data: PushDraftSectionsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/tae/enrolment/${id}/push-draft-sections/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentScheduleCreate
     * @request POST:/planner/tae/enrolment/{id}/schedule/
     * @secure
     */,
    plannerTaeEnrolmentScheduleCreate: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, MessageResponseSchema>({
        path: `/planner/tae/enrolment/${id}/schedule/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentStudentsToEnrollList
     * @request GET:/planner/tae/enrolment/{taeCourseEnrollmentId}/students-to-enroll/
     * @secure
     */,
    plannerTaeEnrolmentStudentsToEnrollList: <
      T extends FetchKeys<StudentToEnroll[]>
    >(
      taeCourseEnrollmentId: string,
      query?: {
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentToEnroll[], T>, any>({
        path: `/planner/tae/enrolment/${taeCourseEnrollmentId}/students-to-enroll/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentCoursesAvailableToEnrollOnList
     * @request GET:/planner/tae/enrolment/{taeEnrolmentId}/{studentId}/courses-available-to-enroll-on/
     * @secure
     */,
    plannerTaeEnrolmentCoursesAvailableToEnrollOnList: <
      T extends FetchKeys<CourseAvailableToEnrollOn[]>
    >(
      studentId: string,
      taeEnrolmentId: string,
      query?: {
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CourseAvailableToEnrollOn[], T>, any>({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/${studentId}/courses-available-to-enroll-on/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentParentCommentsList
     * @request GET:/planner/tae/enrolment/{taeEnrolmentId}/{studentId}/parent-comments/
     * @secure
     */,
    plannerTaeEnrolmentParentCommentsList: <
      T extends FetchKeys<TAEParentComment[]>
    >(
      studentId: string,
      taeEnrolmentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEParentComment[], T>, any>({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/${studentId}/parent-comments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentCoursesList
     * @request GET:/planner/tae/enrolment/{taeEnrolmentId}/courses/
     * @secure
     */,
    plannerTaeEnrolmentCoursesList: <
      T extends FetchKeys<TAECourseEnrollment[]>
    >(
      taeEnrolmentId: string,
      query?: {
        /**
         * * `no_free_spots` - No free spots
         * * `available` - Available
         * * `below_min_participants` - Below min participants
         */
        free_spots?: (
          | 'available'
          | 'below_min_participants'
          | 'no_free_spots'
        )[]
        grades?: number[]
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        group?: 'ATH' | 'CCU'
        /** A search term. */
        search?: string
        /**
         * * `hf_member` - Rosenberg Health & Fitness Center Membership
         * * `none` - No membership
         */
        sport_membership?: 'hf_member' | 'none'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAECourseEnrollment[], T>, any>({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/courses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentCoursesCloseSignupCreate
     * @request POST:/planner/tae/enrolment/{taeEnrolmentId}/courses/{id}/close-signup/
     * @secure
     */,
    plannerTaeEnrolmentCoursesCloseSignupCreate: <T extends FetchKeys<void>>(
      id: number,
      taeEnrolmentId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/courses/${id}/close-signup/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentCoursesEnrollCreate
     * @request POST:/planner/tae/enrolment/{taeEnrolmentId}/courses/{id}/enroll/
     * @secure
     */,
    plannerTaeEnrolmentCoursesEnrollCreate: <
      T extends FetchKeys<StudentsEnrollOnCourseSuccess>
    >(
      id: number,
      taeEnrolmentId: string,
      data: EnrollStudentsRequest,
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<StudentsEnrollOnCourseSuccess, T>,
        EnrollmentCapacityReached
      >({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/courses/${id}/enroll/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentCoursesManageSignupList
     * @request GET:/planner/tae/enrolment/{taeEnrolmentId}/courses/{id}/manage-signup/
     * @secure
     */,
    plannerTaeEnrolmentCoursesManageSignupList: <
      T extends FetchKeys<TAECourseEnrollmentManageSignupList[]>
    >(
      id: number,
      taeEnrolmentId: string,
      query?: {
        /**
         * * `no_free_spots` - No free spots
         * * `available` - Available
         * * `below_min_participants` - Below min participants
         */
        free_spots?: (
          | 'available'
          | 'below_min_participants'
          | 'no_free_spots'
        )[]
        grades?: number[]
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        group?: 'ATH' | 'CCU'
        /** A search term. */
        search?: string
        /**
         * * `hf_member` - Rosenberg Health & Fitness Center Membership
         * * `none` - No membership
         */
        sport_membership?: 'hf_member' | 'none'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAECourseEnrollmentManageSignupList[], T>, any>({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/courses/${id}/manage-signup/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentCoursesReopenSignupCreate
     * @request POST:/planner/tae/enrolment/{taeEnrolmentId}/courses/{id}/reopen-signup/
     * @secure
     */,
    plannerTaeEnrolmentCoursesReopenSignupCreate: <T extends FetchKeys<void>>(
      id: number,
      taeEnrolmentId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/courses/${id}/reopen-signup/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentCoursesAvailableFiltersRetrieve
     * @request GET:/planner/tae/enrolment/{taeEnrolmentId}/courses/available-filters/
     * @secure
     */,
    plannerTaeEnrolmentCoursesAvailableFiltersRetrieve: <
      T extends FetchKeys<TAEEnrolmentCoursesAvailableFilters>
    >(
      taeEnrolmentId: string,
      query?: {
        /**
         * * `no_free_spots` - No free spots
         * * `available` - Available
         * * `below_min_participants` - Below min participants
         */
        free_spots?: (
          | 'available'
          | 'below_min_participants'
          | 'no_free_spots'
        )[]
        grades?: number[]
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        group?: 'ATH' | 'CCU'
        /** A search term. */
        search?: string
        /**
         * * `hf_member` - Rosenberg Health & Fitness Center Membership
         * * `none` - No membership
         */
        sport_membership?: 'hf_member' | 'none'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentCoursesAvailableFilters, T>, any>({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/courses/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentCoursesFreeSpotsList
     * @request GET:/planner/tae/enrolment/{taeEnrolmentId}/courses/free-spots/
     * @secure
     */,
    plannerTaeEnrolmentCoursesFreeSpotsList: <
      T extends FetchKeys<FreeSpotsList[]>
    >(
      taeEnrolmentId: string,
      query?: {
        /**
         * * `no_free_spots` - No free spots
         * * `available` - Available
         * * `below_min_participants` - Below min participants
         */
        free_spots?: (
          | 'available'
          | 'below_min_participants'
          | 'no_free_spots'
        )[]
        grades?: number[]
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        group?: 'ATH' | 'CCU'
        /** A search term. */
        search?: string
        /**
         * * `hf_member` - Rosenberg Health & Fitness Center Membership
         * * `none` - No membership
         */
        sport_membership?: 'hf_member' | 'none'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FreeSpotsList[], T>, any>({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/courses/free-spots/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentStudentChoicesUpdate
     * @request PUT:/planner/tae/enrolment/{taeEnrolmentId}/student-choices/{id}/
     * @secure
     */,
    plannerTaeEnrolmentStudentChoicesUpdate: <
      T extends FetchKeys<TAEEnrollmentStudentChoice>
    >(
      id: number,
      taeEnrolmentId: string,
      data: TAEEnrollmentStudentChoiceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrollmentStudentChoice, T>, any>({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/student-choices/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentStudentChoicesPartialUpdate
     * @request PATCH:/planner/tae/enrolment/{taeEnrolmentId}/student-choices/{id}/
     * @secure
     */,
    plannerTaeEnrolmentStudentChoicesPartialUpdate: <
      T extends FetchKeys<TAEEnrollmentStudentChoice>
    >(
      id: number,
      taeEnrolmentId: string,
      data: PatchedTAEEnrollmentStudentChoiceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrollmentStudentChoice, T>, any>({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/student-choices/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentStudentChoicesApproveCreate
     * @request POST:/planner/tae/enrolment/{taeEnrolmentId}/student-choices/{id}/approve/
     * @secure
     */,
    plannerTaeEnrolmentStudentChoicesApproveCreate: <
      T extends FetchKeys<StudentChoiceFreeSpots>
    >(
      id: number,
      taeEnrolmentId: string,
      data: ApproveStudentRequest,
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<StudentChoiceFreeSpots, T>,
        EnrollmentCapacityReached
      >({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/student-choices/${id}/approve/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentStudentChoicesRejectCreate
     * @request POST:/planner/tae/enrolment/{taeEnrolmentId}/student-choices/{id}/reject/
     * @secure
     */,
    plannerTaeEnrolmentStudentChoicesRejectCreate: <
      T extends FetchKeys<StudentChoiceFreeSpots>
    >(
      id: number,
      taeEnrolmentId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentChoiceFreeSpots, T>, MessageResponseSchema>({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/student-choices/${id}/reject/`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentStudentsList
     * @request GET:/planner/tae/enrolment/{taeEnrolmentId}/students/
     * @secure
     */,
    plannerTaeEnrolmentStudentsList: <
      T extends FetchKeys<TAEEnrolmentStudents[]>
    >(
      taeEnrolmentId: string,
      query?: {
        /**
         * * `enrolled` - Enrolled
         * * `re_enrolling` - Re-enrolling
         * * `not_enrolled` - Not enrolled
         */
        enrolment_state?: 'enrolled' | 'not_enrolled' | 're_enrolling'
        grades?: string[]
        has_parent_comment?: boolean
        is_student_new?: boolean
        /** A search term. */
        search?: string
        /**
         * * `hf_member` - Rosenberg Health & Fitness Center Membership
         * * `none` - No membership
         */
        sport_membership?: 'hf_member' | 'none'
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentStudents[], T>, any>({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/students/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentStudentsUpdate
     * @request PUT:/planner/tae/enrolment/{taeEnrolmentId}/students/{id}/
     * @secure
     */,
    plannerTaeEnrolmentStudentsUpdate: <
      T extends FetchKeys<TAEStudentEnrollmentUpdate>
    >(
      id: number,
      taeEnrolmentId: string,
      data: TAEStudentEnrollmentUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEStudentEnrollmentUpdate, T>, any>({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/students/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentStudentsPartialUpdate
     * @request PATCH:/planner/tae/enrolment/{taeEnrolmentId}/students/{id}/
     * @secure
     */,
    plannerTaeEnrolmentStudentsPartialUpdate: <
      T extends FetchKeys<TAEStudentEnrollmentUpdate>
    >(
      id: number,
      taeEnrolmentId: string,
      data: PatchedTAEStudentEnrollmentUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEStudentEnrollmentUpdate, T>, any>({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/students/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentStudentsManageSignupList
     * @request GET:/planner/tae/enrolment/{taeEnrolmentId}/students/{id}/manage-signup/
     * @secure
     */,
    plannerTaeEnrolmentStudentsManageSignupList: <
      T extends FetchKeys<TAEStudentEnrollmentManageSignupList[]>
    >(
      id: number,
      taeEnrolmentId: string,
      query?: {
        /**
         * * `enrolled` - Enrolled
         * * `re_enrolling` - Re-enrolling
         * * `not_enrolled` - Not enrolled
         */
        enrolment_state?: 'enrolled' | 'not_enrolled' | 're_enrolling'
        grades?: string[]
        has_parent_comment?: boolean
        is_student_new?: boolean
        /** A search term. */
        search?: string
        /**
         * * `hf_member` - Rosenberg Health & Fitness Center Membership
         * * `none` - No membership
         */
        sport_membership?: 'hf_member' | 'none'
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEStudentEnrollmentManageSignupList[], T>, any>({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/students/${id}/manage-signup/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentStudentsToggleReopenCreate
     * @request POST:/planner/tae/enrolment/{taeEnrolmentId}/students/{id}/toggle-reopen/
     * @secure
     */,
    plannerTaeEnrolmentStudentsToggleReopenCreate: <
      T extends FetchKeys<TAEStudentEnrollmentReopen>
    >(
      id: number,
      taeEnrolmentId: string,
      data: TAEStudentEnrollmentReopenRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEStudentEnrollmentReopen, T>, any>({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/students/${id}/toggle-reopen/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentStudentsEnrollCreate
     * @request POST:/planner/tae/enrolment/{taeEnrolmentId}/students/{studentId}/enroll/
     * @secure
     */,
    plannerTaeEnrolmentStudentsEnrollCreate: <
      T extends FetchKeys<StudentEnrollOnCoursesSuccess>
    >(
      studentId: string,
      taeEnrolmentId: string,
      data: EnrollOnCoursesRequest,
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<StudentEnrollOnCoursesSuccess, T>,
        EnrollOnCoursesConflict
      >({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/students/${studentId}/enroll/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentStudentsAvailableFiltersRetrieve
     * @request GET:/planner/tae/enrolment/{taeEnrolmentId}/students/available-filters/
     * @secure
     */,
    plannerTaeEnrolmentStudentsAvailableFiltersRetrieve: <
      T extends FetchKeys<TAEEnrolmentStudentsAvailableFilters>
    >(
      taeEnrolmentId: string,
      query?: {
        /**
         * * `enrolled` - Enrolled
         * * `re_enrolling` - Re-enrolling
         * * `not_enrolled` - Not enrolled
         */
        enrolment_state?: 'enrolled' | 'not_enrolled' | 're_enrolling'
        grades?: string[]
        has_parent_comment?: boolean
        is_student_new?: boolean
        /** A search term. */
        search?: string
        /**
         * * `hf_member` - Rosenberg Health & Fitness Center Membership
         * * `none` - No membership
         */
        sport_membership?: 'hf_member' | 'none'
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentStudentsAvailableFilters, T>, any>({
        path: `/planner/tae/enrolment/${taeEnrolmentId}/students/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentAvailableFiltersRetrieve
     * @request GET:/planner/tae/enrolment/available-filters/
     * @secure
     */,
    plannerTaeEnrolmentAvailableFiltersRetrieve: <
      T extends FetchKeys<TAEEnrolmentAvailableFilters>
    >(
      query?: {
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        /** Which field to use when ordering the results. */
        ordering?: string
        school_year?: string
        /** A search term. */
        search?: string
        semester?: 'S1' | 'S2'
        /**
         * * `DRAFT` - Draft
         * * `VALIDATED` - Validated
         * * `SCHEDULED` - Scheduled
         * * `OPEN` - Open
         * * `CLOSED` - Closed
         * * `ARCHIVED` - Archived
         */
        status?:
          | 'ARCHIVED'
          | 'CLOSED'
          | 'DRAFT'
          | 'OPEN'
          | 'SCHEDULED'
          | 'VALIDATED'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentAvailableFilters, T>, any>({
        path: `/planner/tae/enrolment/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentBlockingEnrolmentRetrieve
     * @request GET:/planner/tae/enrolment/blocking-enrolment/
     * @secure
     */,
    plannerTaeEnrolmentBlockingEnrolmentRetrieve: <
      T extends FetchKeys<TAEEnrolmentStatus>
    >(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEEnrolmentStatus, T>, any>({
        path: `/planner/tae/enrolment/blocking-enrolment/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsList
     * @request GET:/planner/tae/enrolment/draft-sections/{taeEnrolmentId}/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsList: <
      T extends FetchKeys<TAECoursesDraftSectionsList[]>
    >(
      taeEnrolmentId: string,
      query: {
        grades?: number[]
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        group?: 'ATH' | 'CCU'
        has_conflicts?: boolean
        quarter: number
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAECoursesDraftSectionsList[], T>, any>({
        path: `/planner/tae/enrolment/draft-sections/${taeEnrolmentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsAvailableFiltersRetrieve
     * @request GET:/planner/tae/enrolment/draft-sections/{taeEnrolmentId}/available-filters/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsAvailableFiltersRetrieve: <
      T extends FetchKeys<TAECoursesDraftSectionsAvailableFilters>
    >(
      taeEnrolmentId: string,
      query?: {
        grades?: number[]
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        group?: 'ATH' | 'CCU'
        has_conflicts?: boolean
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAECoursesDraftSectionsAvailableFilters, T>, any>({
        path: `/planner/tae/enrolment/draft-sections/${taeEnrolmentId}/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsGroupStudentsToReviewList
     * @request GET:/planner/tae/enrolment/draft-sections/{taeEnrolmentId}/group/{courseGroupId}/students-to-review/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsGroupStudentsToReviewList: <
      T extends FetchKeys<TAESectionStudentToReview[]>
    >(
      courseGroupId: string,
      taeEnrolmentId: string,
      query: {
        quarter: number
        /**
         * * `no_enrolment` - No enrolment
         * * `no_section` - No section
         * * `over_limit` - Over limit
         */
        review_cause?: ('no_enrolment' | 'no_section' | 'over_limit')[]
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAESectionStudentToReview[], T>, any>({
        path: `/planner/tae/enrolment/draft-sections/${taeEnrolmentId}/group/${courseGroupId}/students-to-review/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsStudentsToReviewCountRetrieve
     * @request GET:/planner/tae/enrolment/draft-sections/{taeEnrolmentId}/students-to-review-count/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsStudentsToReviewCountRetrieve: <
      T extends FetchKeys<TAEStudentsToReviewCount>
    >(
      taeEnrolmentId: number,
      query?: {
        quarter?: number
        review_cause?: ('no_enrolment' | 'no_section' | 'over_limit')[]
        /** @default false */
        skip_new_student_filter?: boolean

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEStudentsToReviewCount, T>, any>({
        path: `/planner/tae/enrolment/draft-sections/${taeEnrolmentId}/students-to-review-count/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsDraftSectionsList
     * @request GET:/planner/tae/enrolment/draft-sections/draft-sections/{courseEnrolmentId}/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsDraftSectionsList: <
      T extends FetchKeys<TAEDraftSection[]>
    >(
      courseEnrolmentId: string,
      query: {
        quarter: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEDraftSection[], T>, any>({
        path: `/planner/tae/enrolment/draft-sections/draft-sections/${courseEnrolmentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsDraftSectionsCreate
     * @request POST:/planner/tae/enrolment/draft-sections/draft-sections/{courseEnrolmentId}/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsDraftSectionsCreate: <
      T extends FetchKeys<TAEDraftSection>
    >(
      courseEnrolmentId: string,
      data: TAEDraftSectionRequest,
      query?: {
        /**
         * Force increase missing resources if needed.
         * @default false
         */
        force_increase?: boolean
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEDraftSection, T>, MessageResponseSchema>({
        path: `/planner/tae/enrolment/draft-sections/draft-sections/${courseEnrolmentId}/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsDraftSectionsUpdate
     * @request PUT:/planner/tae/enrolment/draft-sections/draft-sections/{courseEnrolmentId}/{id}/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsDraftSectionsUpdate: <
      T extends FetchKeys<TAEDraftSection>
    >(
      courseEnrolmentId: string,
      id: number,
      data: TAEDraftSectionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEDraftSection, T>, any>({
        path: `/planner/tae/enrolment/draft-sections/draft-sections/${courseEnrolmentId}/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsDraftSectionsPartialUpdate
     * @request PATCH:/planner/tae/enrolment/draft-sections/draft-sections/{courseEnrolmentId}/{id}/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsDraftSectionsPartialUpdate: <
      T extends FetchKeys<TAEDraftSection>
    >(
      courseEnrolmentId: string,
      id: number,
      data: PatchedTAEDraftSectionRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEDraftSection, T>, any>({
        path: `/planner/tae/enrolment/draft-sections/draft-sections/${courseEnrolmentId}/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsDraftSectionsDestroy
     * @request DELETE:/planner/tae/enrolment/draft-sections/draft-sections/{courseEnrolmentId}/{id}/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsDraftSectionsDestroy: <
      T extends FetchKeys<void>
    >(
      courseEnrolmentId: string,
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/tae/enrolment/draft-sections/draft-sections/${courseEnrolmentId}/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsDraftSectionsIncreaseCapacityCreate
     * @request POST:/planner/tae/enrolment/draft-sections/draft-sections/{courseEnrolmentId}/{id}/increase-capacity/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsDraftSectionsIncreaseCapacityCreate: <
      T extends FetchKeys<TAEDraftSectionCourseCapacity>
    >(
      courseEnrolmentId: string,
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEDraftSectionCourseCapacity, T>, any>({
        path: `/planner/tae/enrolment/draft-sections/draft-sections/${courseEnrolmentId}/${id}/increase-capacity/`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsDraftSectionsRemoveDraftEnrolmentPartialUpdate
     * @request PATCH:/planner/tae/enrolment/draft-sections/draft-sections/{courseEnrolmentId}/{id}/remove-draft-enrolment/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsDraftSectionsRemoveDraftEnrolmentPartialUpdate:
      <T extends FetchKeys<void>>(
        courseEnrolmentId: string,
        id: number,
        data: PatchedTAERemoveDraftEnrolmentRequest,
        params: RequestParams = {}
      ) =>
        this.request<PickKeys<void, T>, any>({
          path: `/planner/tae/enrolment/draft-sections/draft-sections/${courseEnrolmentId}/${id}/remove-draft-enrolment/`,
          method: 'PATCH',
          body: data,
          secure: true,
          type: ContentType.Json,
          ...params
        })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsDraftSectionsStatusRetrieve
     * @request GET:/planner/tae/enrolment/draft-sections/draft-sections/{courseEnrolmentId}/{id}/status/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsDraftSectionsStatusRetrieve: <
      T extends FetchKeys<TAEDraftSectionStatus>
    >(
      courseEnrolmentId: string,
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEDraftSectionStatus, T>, any>({
        path: `/planner/tae/enrolment/draft-sections/draft-sections/${courseEnrolmentId}/${id}/status/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsDraftSectionsNotAssignedStudentsList
     * @request GET:/planner/tae/enrolment/draft-sections/draft-sections/{courseEnrolmentId}/not-assigned-students/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsDraftSectionsNotAssignedStudentsList: <
      T extends FetchKeys<TAENotAssignedStudent[]>
    >(
      courseEnrolmentId: string,
      query: {
        quarter: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAENotAssignedStudent[], T>, any>({
        path: `/planner/tae/enrolment/draft-sections/draft-sections/${courseEnrolmentId}/not-assigned-students/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsPushStateRetrieve
     * @request GET:/planner/tae/enrolment/draft-sections/push-state/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsPushStateRetrieve: <
      T extends FetchKeys<TAEDraftSectionPushActionState>
    >(
      query: {
        timetable: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEDraftSectionPushActionState, T>, any>({
        path: `/planner/tae/enrolment/draft-sections/push-state/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsPushStateDismissCreate
     * @request POST:/planner/tae/enrolment/draft-sections/push-state/dismiss/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsPushStateDismissCreate: <
      T extends FetchKeys<void>
    >(
      query: {
        timetable: number
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/tae/enrolment/draft-sections/push-state/dismiss/`,
        method: 'POST',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsTaskCreate
     * @request POST:/planner/tae/enrolment/draft-sections/task/{taeEnrolmentId}/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsTaskCreate: <
      T extends FetchKeys<TAEDraftSectionsTask>
    >(
      taeEnrolmentId: string,
      query?: {
        quarter?: number | null
        update_only?: boolean
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TAEDraftSectionsTask, T>,
        MissingNumberOfLessonsResponseSchema | MessageResponseSchema
      >({
        path: `/planner/tae/enrolment/draft-sections/task/${taeEnrolmentId}/`,
        method: 'POST',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsTaskRetrieve
     * @request GET:/planner/tae/enrolment/draft-sections/task/{taeEnrolmentId}/{taskId}/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsTaskRetrieve: <
      T extends FetchKeys<TAEDraftSectionsTask>
    >(
      taeEnrolmentId: string,
      taskId: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEDraftSectionsTask, T>, any>({
        path: `/planner/tae/enrolment/draft-sections/task/${taeEnrolmentId}/${taskId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsTaskCancelCreate
     * @request POST:/planner/tae/enrolment/draft-sections/task/{taeEnrolmentId}/{taskId}/cancel/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsTaskCancelCreate: <
      T extends FetchKeys<void>
    >(
      taeEnrolmentId: string,
      taskId: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/planner/tae/enrolment/draft-sections/task/${taeEnrolmentId}/${taskId}/cancel/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsTaskCopySectionsCreate
     * @request POST:/planner/tae/enrolment/draft-sections/task/{taeEnrolmentId}/copy-sections/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsTaskCopySectionsCreate: <
      T extends FetchKeys<TAEDraftSectionsTask>
    >(
      taeEnrolmentId: string,
      data: TAECopyDraftSectionsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEDraftSectionsTask, T>, MessageResponseSchema>({
        path: `/planner/tae/enrolment/draft-sections/task/${taeEnrolmentId}/copy-sections/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags planner
     * @name PlannerTaeEnrolmentDraftSectionsTaskProcessStateRetrieve
     * @request GET:/planner/tae/enrolment/draft-sections/task/{taeEnrolmentId}/process-state/
     * @secure
     */,
    plannerTaeEnrolmentDraftSectionsTaskProcessStateRetrieve: <
      T extends FetchKeys<TAEDraftSectionsTask>
    >(
      taeEnrolmentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TAEDraftSectionsTask, T>, any>({
        path: `/planner/tae/enrolment/draft-sections/task/${taeEnrolmentId}/process-state/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags planner
     * @name PlannerTaeEnrolmentUploadOfferCreate
     * @request POST:/planner/tae/enrolment/upload-offer/
     * @secure
     */,
    plannerTaeEnrolmentUploadOfferCreate: <T extends FetchKeys<Attachment>>(
      data: AttachmentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Attachment, T>, any>({
        path: `/planner/tae/enrolment/upload-offer/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.UrlEncoded,
        format: 'json',
        ...params
      })
  }
  privateMedia = {
    /**
     * @description Overwrite get method to provide rest's exception
     *
     * @tags private-media
     * @name PrivateMediaRetrieve
     * @request GET:/private-media/{path}
     * @secure
     */
    privateMediaRetrieve: <T extends FetchKeys<void>>(
      path: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/private-media/${path}`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
  }
  progressComments = {
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags progress-comments
     * @name ProgressCommentsList
     * @request GET:/progress-comments/
     * @secure
     */
    progressCommentsList: <T extends FetchKeys<PaginatedProgressCommentList>>(
      query?: {
        /** @format uuid */
        course?: string
        /** @format date */
        end_date?: string
        fixed_target_grade_status?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        potential_grade?: string
        quarter?: number
        quarter_grade?: string
        /**
         * * `comment_not_filled_in` - None
         * * `more_than_30_percentage` - None
         * * `more_than_3_b_plus` - None
         * * `only_filled_comments` - None
         * * `teacher_has_more_than_30_percentage_under_performing_students` - None
         * * `students_with_higher_grade_than_potential` - None
         */
        quick_filters?:
          | 'comment_not_filled_in'
          | 'more_than_30_percentage'
          | 'more_than_3_b_plus'
          | 'only_filled_comments'
          | 'students_with_higher_grade_than_potential'
          | 'teacher_has_more_than_30_percentage_under_performing_students'
        semester?: number
        /** @format uuid */
        student?: string
        student_grade_level?: number[]
        teacher?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedProgressCommentList, T>, any>({
        path: `/progress-comments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags progress-comments
     * @name ProgressCommentsCreate
     * @request POST:/progress-comments/
     * @secure
     */,
    progressCommentsCreate: <T extends FetchKeys<ProgressComment>>(
      data: ProgressCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgressComment, T>, any>({
        path: `/progress-comments/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags progress-comments
     * @name ProgressCommentsRetrieve
     * @request GET:/progress-comments/{id}/
     * @secure
     */,
    progressCommentsRetrieve: <T extends FetchKeys<ProgressComment>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgressComment, T>, any>({
        path: `/progress-comments/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags progress-comments
     * @name ProgressCommentsUpdate
     * @request PUT:/progress-comments/{id}/
     * @secure
     */,
    progressCommentsUpdate: <T extends FetchKeys<ProgressComment>>(
      id: string,
      data: ProgressCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgressComment, T>, any>({
        path: `/progress-comments/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags progress-comments
     * @name ProgressCommentsPartialUpdate
     * @request PATCH:/progress-comments/{id}/
     * @secure
     */,
    progressCommentsPartialUpdate: <T extends FetchKeys<ProgressComment>>(
      id: string,
      data: PatchedProgressCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgressComment, T>, any>({
        path: `/progress-comments/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags progress-comments
     * @name ProgressCommentsDestroy
     * @request DELETE:/progress-comments/{id}/
     * @secure
     */,
    progressCommentsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/progress-comments/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags progress-comments
     * @name ProgressCommentsChangeCommentDeadlinePartialUpdate
     * @request PATCH:/progress-comments/{id}/change-comment-deadline/
     * @secure
     */,
    progressCommentsChangeCommentDeadlinePartialUpdate: <
      T extends FetchKeys<ProgressCommentDeadline>
    >(
      id: string,
      data: PatchedProgressCommentDeadlineRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgressCommentDeadline, T>, any>({
        path: `/progress-comments/${id}/change-comment-deadline/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags progress-comments
     * @name ProgressCommentsEvaluatePartialUpdate
     * @request PATCH:/progress-comments/{id}/evaluate/
     * @secure
     */,
    progressCommentsEvaluatePartialUpdate: <
      T extends FetchKeys<ProgressCommentEvaluate>
    >(
      id: string,
      data: PatchedProgressCommentEvaluateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgressCommentEvaluate, T>, any>({
        path: `/progress-comments/${id}/evaluate/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags progress-comments
     * @name ProgressCommentsSaveDraftPartialUpdate
     * @request PATCH:/progress-comments/{id}/save-draft/
     * @secure
     */,
    progressCommentsSaveDraftPartialUpdate: <
      T extends FetchKeys<ProgressCommentDraft>
    >(
      id: string,
      data: PatchedProgressCommentDraftRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgressCommentDraft, T>, any>({
        path: `/progress-comments/${id}/save-draft/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags progress-comments
     * @name ProgressCommentsAvailableFiltersRetrieve
     * @request GET:/progress-comments/available-filters/
     * @secure
     */,
    progressCommentsAvailableFiltersRetrieve: <
      T extends FetchKeys<ProgressComment>
    >(
      query?: {
        /** @format uuid */
        course?: string
        /** @format date */
        end_date?: string
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        fixed_target_grade_status?: string
        potential_grade?: string
        quarter?: number
        quarter_grade?: string
        /**
         * * `comment_not_filled_in` - None
         * * `more_than_30_percentage` - None
         * * `more_than_3_b_plus` - None
         * * `only_filled_comments` - None
         * * `teacher_has_more_than_30_percentage_under_performing_students` - None
         * * `students_with_higher_grade_than_potential` - None
         */
        quick_filters?:
          | 'comment_not_filled_in'
          | 'more_than_30_percentage'
          | 'more_than_3_b_plus'
          | 'only_filled_comments'
          | 'students_with_higher_grade_than_potential'
          | 'teacher_has_more_than_30_percentage_under_performing_students'
        semester?: number
        /** @format uuid */
        student?: string
        student_grade_level?: number[]
        teacher?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgressComment, T>, any>({
        path: `/progress-comments/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags progress-comments
     * @name ProgressCommentsChoicesRetrieve
     * @request GET:/progress-comments/choices/
     * @secure
     */,
    progressCommentsChoicesRetrieve: <T extends FetchKeys<ProgressComment>>(
      query?: {
        /** @format uuid */
        course?: string
        /** @format date */
        end_date?: string
        fixed_target_grade_status?: string
        potential_grade?: string
        quarter?: number
        quarter_grade?: string
        /**
         * * `comment_not_filled_in` - None
         * * `more_than_30_percentage` - None
         * * `more_than_3_b_plus` - None
         * * `only_filled_comments` - None
         * * `teacher_has_more_than_30_percentage_under_performing_students` - None
         * * `students_with_higher_grade_than_potential` - None
         */
        quick_filters?:
          | 'comment_not_filled_in'
          | 'more_than_30_percentage'
          | 'more_than_3_b_plus'
          | 'only_filled_comments'
          | 'students_with_higher_grade_than_potential'
          | 'teacher_has_more_than_30_percentage_under_performing_students'
        semester?: number
        /** @format uuid */
        student?: string
        student_grade_level?: number[]
        teacher?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgressComment, T>, any>({
        path: `/progress-comments/choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags progress-comments
     * @name ProgressCommentsCsvRetrieve
     * @request GET:/progress-comments/csv/
     * @secure
     */,
    progressCommentsCsvRetrieve: <T extends FetchKeys<ProgressComment>>(
      query?: {
        /** @format uuid */
        course?: string
        /** @format date */
        end_date?: string
        fixed_target_grade_status?: string
        potential_grade?: string
        quarter?: number
        quarter_grade?: string
        /**
         * * `comment_not_filled_in` - None
         * * `more_than_30_percentage` - None
         * * `more_than_3_b_plus` - None
         * * `only_filled_comments` - None
         * * `teacher_has_more_than_30_percentage_under_performing_students` - None
         * * `students_with_higher_grade_than_potential` - None
         */
        quick_filters?:
          | 'comment_not_filled_in'
          | 'more_than_30_percentage'
          | 'more_than_3_b_plus'
          | 'only_filled_comments'
          | 'students_with_higher_grade_than_potential'
          | 'teacher_has_more_than_30_percentage_under_performing_students'
        semester?: number
        /** @format uuid */
        student?: string
        student_grade_level?: number[]
        teacher?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgressComment, T>, any>({
        path: `/progress-comments/csv/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags progress-comments
     * @name ProgressCommentsDatesForNewProgressCommentRetrieve
     * @request GET:/progress-comments/dates-for-new-progress-comment/
     * @secure
     */,
    progressCommentsDatesForNewProgressCommentRetrieve: <
      T extends FetchKeys<ProgressComment>
    >(
      query?: {
        /** @format uuid */
        course?: string
        /** @format date */
        end_date?: string
        fixed_target_grade_status?: string
        potential_grade?: string
        quarter?: number
        quarter_grade?: string
        /**
         * * `comment_not_filled_in` - None
         * * `more_than_30_percentage` - None
         * * `more_than_3_b_plus` - None
         * * `only_filled_comments` - None
         * * `teacher_has_more_than_30_percentage_under_performing_students` - None
         * * `students_with_higher_grade_than_potential` - None
         */
        quick_filters?:
          | 'comment_not_filled_in'
          | 'more_than_30_percentage'
          | 'more_than_3_b_plus'
          | 'only_filled_comments'
          | 'students_with_higher_grade_than_potential'
          | 'teacher_has_more_than_30_percentage_under_performing_students'
        semester?: number
        /** @format uuid */
        student?: string
        student_grade_level?: number[]
        teacher?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgressComment, T>, any>({
        path: `/progress-comments/dates-for-new-progress-comment/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags progress-comments
     * @name ProgressCommentsGeneralStatisticsRetrieve
     * @request GET:/progress-comments/general-statistics/
     * @secure
     */,
    progressCommentsGeneralStatisticsRetrieve: <
      T extends FetchKeys<ProgressComment>
    >(
      query?: {
        /** @format uuid */
        course?: string
        /** @format date */
        end_date?: string
        fixed_target_grade_status?: string
        potential_grade?: string
        quarter?: number
        quarter_grade?: string
        /**
         * * `comment_not_filled_in` - None
         * * `more_than_30_percentage` - None
         * * `more_than_3_b_plus` - None
         * * `only_filled_comments` - None
         * * `teacher_has_more_than_30_percentage_under_performing_students` - None
         * * `students_with_higher_grade_than_potential` - None
         */
        quick_filters?:
          | 'comment_not_filled_in'
          | 'more_than_30_percentage'
          | 'more_than_3_b_plus'
          | 'only_filled_comments'
          | 'students_with_higher_grade_than_potential'
          | 'teacher_has_more_than_30_percentage_under_performing_students'
        semester?: number
        /** @format uuid */
        student?: string
        student_grade_level?: number[]
        teacher?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgressComment, T>, any>({
        path: `/progress-comments/general-statistics/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags progress-comments
     * @name ProgressCommentsHasInstanceForCurrentWeekRetrieve
     * @request GET:/progress-comments/has-instance-for-current-week/
     * @secure
     */,
    progressCommentsHasInstanceForCurrentWeekRetrieve: <
      T extends FetchKeys<ProgressComment>
    >(
      query?: {
        /** @format uuid */
        course?: string
        /** @format date */
        end_date?: string
        fixed_target_grade_status?: string
        potential_grade?: string
        quarter?: number
        quarter_grade?: string
        /**
         * * `comment_not_filled_in` - None
         * * `more_than_30_percentage` - None
         * * `more_than_3_b_plus` - None
         * * `only_filled_comments` - None
         * * `teacher_has_more_than_30_percentage_under_performing_students` - None
         * * `students_with_higher_grade_than_potential` - None
         */
        quick_filters?:
          | 'comment_not_filled_in'
          | 'more_than_30_percentage'
          | 'more_than_3_b_plus'
          | 'only_filled_comments'
          | 'students_with_higher_grade_than_potential'
          | 'teacher_has_more_than_30_percentage_under_performing_students'
        semester?: number
        /** @format uuid */
        student?: string
        student_grade_level?: number[]
        teacher?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgressComment, T>, any>({
        path: `/progress-comments/has-instance-for-current-week/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  public = {
    /**
     * No description
     *
     * @tags public
     * @name PublicLaundryQrCodesResolveRetrieve
     * @request GET:/public/laundry/qr-codes/{code}/resolve/
     * @secure
     */
    publicLaundryQrCodesResolveRetrieve: <
      T extends FetchKeys<NewLaundryFormQRCodeResolveResponse>
    >(
      code: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NewLaundryFormQRCodeResolveResponse, T>, void>({
        path: `/public/laundry/qr-codes/${code}/resolve/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicLeavesRetrieve
     * @request GET:/public/leaves/{id}/
     * @secure
     */,
    publicLeavesRetrieve: <T extends FetchKeys<PublicLeaveRequest>>(
      id: string,
      query: {
        state: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicLeaveRequest, T>, any>({
        path: `/public/leaves/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicLeavesApproveCreate
     * @request POST:/public/leaves/{id}/approve/
     * @secure
     */,
    publicLeavesApproveCreate: <T extends FetchKeys<PublicLeaveRequest>>(
      id: string,
      query: {
        state: string
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicLeaveRequest, T>, any>({
        path: `/public/leaves/${id}/approve/`,
        method: 'POST',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicLeavesRejectCreate
     * @request POST:/public/leaves/{id}/reject/
     * @secure
     */,
    publicLeavesRejectCreate: <T extends FetchKeys<PublicLeaveRequest>>(
      id: string,
      query: {
        state: string
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicLeaveRequest, T>, any>({
        path: `/public/leaves/${id}/reject/`,
        method: 'POST',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  recovery = {
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags recovery
     * @name RecoveryList
     * @request GET:/recovery/
     * @secure
     */
    recoveryList: <T extends FetchKeys<PaginatedAdminRecoveryList>>(
      query?: {
        /** @format uuid */
        assigned_to?: string
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /**
         * * `academics` - Academics
         * * `house_evening` - House Evening
         * * `term_break` - Term Break
         * * `school_trip` - School Trip
         * * `weekend_programme` - Weekend Programme
         * * `exam_preparation` - Exam Preparation
         * * `other` - Other
         * * `absence_recovery` - Absence Recovery
         * * `lunchtime_recovery` - Lunchtime Recovery
         */
        school_programme__type?:
          | 'absence_recovery'
          | 'academics'
          | 'exam_preparation'
          | 'house_evening'
          | 'lunchtime_recovery'
          | 'other'
          | 'school_trip'
          | 'term_break'
          | 'weekend_programme'
        /** A search term. */
        search?: string
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAdminRecoveryList, T>, any>({
        path: `/recovery/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags recovery
     * @name RecoveryRetrieve
     * @request GET:/recovery/{id}/
     * @secure
     */,
    recoveryRetrieve: <T extends FetchKeys<AdminRecovery>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminRecovery, T>, any>({
        path: `/recovery/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags recovery
     * @name RecoveryUpdate
     * @request PUT:/recovery/{id}/
     * @secure
     */,
    recoveryUpdate: <T extends FetchKeys<AdminRecovery>>(
      id: string,
      data: AdminRecoveryRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminRecovery, T>, any>({
        path: `/recovery/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags recovery
     * @name RecoveryPartialUpdate
     * @request PATCH:/recovery/{id}/
     * @secure
     */,
    recoveryPartialUpdate: <T extends FetchKeys<AdminRecovery>>(
      id: string,
      data: PatchedAdminRecoveryRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminRecovery, T>, any>({
        path: `/recovery/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags recovery
     * @name RecoveryAvailableFiltersRetrieve
     * @request GET:/recovery/available-filters/
     * @secure
     */,
    recoveryAvailableFiltersRetrieve: <
      T extends FetchKeys<RecoveryAvailableFilters>
    >(
      query?: {
        /** @format uuid */
        assigned_to?: string
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        /** Which field to use when ordering the results. */
        ordering?: string
        /**
         * * `academics` - Academics
         * * `house_evening` - House Evening
         * * `term_break` - Term Break
         * * `school_trip` - School Trip
         * * `weekend_programme` - Weekend Programme
         * * `exam_preparation` - Exam Preparation
         * * `other` - Other
         * * `absence_recovery` - Absence Recovery
         * * `lunchtime_recovery` - Lunchtime Recovery
         */
        school_programme__type?:
          | 'absence_recovery'
          | 'academics'
          | 'exam_preparation'
          | 'house_evening'
          | 'lunchtime_recovery'
          | 'other'
          | 'school_trip'
          | 'term_break'
          | 'weekend_programme'
        /** A search term. */
        search?: string
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RecoveryAvailableFilters, T>, any>({
        path: `/recovery/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description return cached staff users map
     *
     * @tags recovery
     * @name RecoveryAvailableStaffUsersList
     * @request GET:/recovery/available-staff-users/
     * @secure
     */,
    recoveryAvailableStaffUsersList: <T extends FetchKeys<StringOption[]>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/recovery/available-staff-users/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  reports = {
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags reports
     * @name ReportsReportCardsList
     * @request GET:/reports/report-cards/
     * @secure
     */
    reportsReportCardsList: <T extends FetchKeys<PaginatedReportCardList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter?: number
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedReportCardList, T>, any>({
        path: `/reports/report-cards/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags reports
     * @name ReportsReportCardsRetrieve
     * @request GET:/reports/report-cards/{id}/
     * @secure
     */,
    reportsReportCardsRetrieve: <T extends FetchKeys<ReportCard>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ReportCard, T>, any>({
        path: `/reports/report-cards/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags reports
     * @name ReportsReportCardsSendToParentsCreate
     * @request POST:/reports/report-cards/send-to-parents/
     * @secure
     */,
    reportsReportCardsSendToParentsCreate: <T extends FetchKeys<void>>(
      data: ParentSendReportCardRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/reports/report-cards/send-to-parents/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
  }
  roomBookings = {
    /**
     * No description
     *
     * @tags room-bookings
     * @name RoomBookingsCreate
     * @request POST:/room-bookings/
     * @secure
     */
    roomBookingsCreate: <T extends FetchKeys<RoomBookingCreate>>(
      data: RoomBookingCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RoomBookingCreate, T>, any>({
        path: `/room-bookings/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags room-bookings
     * @name RoomBookingsUpdate
     * @request PUT:/room-bookings/{id}/
     * @secure
     */,
    roomBookingsUpdate: <T extends FetchKeys<RoomBookingUpdate>>(
      id: number,
      data: RoomBookingUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RoomBookingUpdate, T>, any>({
        path: `/room-bookings/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags room-bookings
     * @name RoomBookingsPartialUpdate
     * @request PATCH:/room-bookings/{id}/
     * @secure
     */,
    roomBookingsPartialUpdate: <T extends FetchKeys<RoomBookingUpdate>>(
      id: number,
      data: PatchedRoomBookingUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RoomBookingUpdate, T>, any>({
        path: `/room-bookings/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags room-bookings
     * @name RoomBookingsDestroy
     * @request DELETE:/room-bookings/{id}/
     * @secure
     */,
    roomBookingsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/room-bookings/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags room-bookings
     * @name RoomBookingsBookForOptionsList
     * @request GET:/room-bookings/book-for-options/
     * @secure
     */,
    roomBookingsBookForOptionsList: <
      T extends FetchKeys<RoomBookingBookForOptions[]>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RoomBookingBookForOptions[], T>, any>({
        path: `/room-bookings/book-for-options/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  rooms = {
    /**
     * No description
     *
     * @tags rooms
     * @name RoomsList
     * @request GET:/rooms/
     * @secure
     */
    roomsList: <T extends FetchKeys<PaginatedRoomList>>(
      query?: {
        available_for_exam?: boolean
        department?: number
        is_private?: boolean
        msl_room?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        pri_room?: boolean
        schedule_automatically?: boolean
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedRoomList, T>, any>({
        path: `/rooms/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags rooms
     * @name RoomsCreate
     * @request POST:/rooms/
     * @secure
     */,
    roomsCreate: <T extends FetchKeys<Room>>(
      data: RoomRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Room, T>, any>({
        path: `/rooms/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags rooms
     * @name RoomsRetrieve
     * @request GET:/rooms/{id}/
     * @secure
     */,
    roomsRetrieve: <T extends FetchKeys<RoomDetail>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RoomDetail, T>, any>({
        path: `/rooms/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags rooms
     * @name RoomsUpdate
     * @request PUT:/rooms/{id}/
     * @secure
     */,
    roomsUpdate: <T extends FetchKeys<Room>>(
      id: number,
      data: RoomRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Room, T>, any>({
        path: `/rooms/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags rooms
     * @name RoomsPartialUpdate
     * @request PATCH:/rooms/{id}/
     * @secure
     */,
    roomsPartialUpdate: <T extends FetchKeys<Room>>(
      id: number,
      data: PatchedRoomRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Room, T>, any>({
        path: `/rooms/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags rooms
     * @name RoomsDestroy
     * @request DELETE:/rooms/{id}/
     * @secure
     */,
    roomsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/rooms/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags rooms
     * @name RoomsAvailableRetrieve
     * @request GET:/rooms/available/
     * @secure
     */,
    roomsAvailableRetrieve: <T extends FetchKeys<Room>>(
      query: {
        available_for_exam?: boolean
        /** @format uuid */
        course: string
        day?: number
        department?: number
        /** @format double */
        duration?: number
        hour?: number
        is_private?: boolean
        msl_room?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        pri_room?: boolean
        schedule_automatically?: boolean
        /** A search term. */
        search?: string
        section: number
        students: string[]
        /** @format uuid */
        teacher: string
        timetable?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Room, T>, any>({
        path: `/rooms/available/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description [WARNING] AdminTimetableViewSet inherits from this!
     *
     * @tags rooms
     * @name RoomsRoomTimetableFreeRoomsList
     * @request GET:/rooms/room-timetable/free-rooms/
     * @secure
     */,
    roomsRoomTimetableFreeRoomsList: <
      T extends FetchKeys<RoomTimetableFreeRoom[]>
    >(
      query: {
        /**
         * * `1-4` - 1-4
         * * `5-7` - 5-7
         * * `8-9` - 8-9
         * * `10-14` - 10-14
         * * `15-24` - 15-24
         * * `25-99` - 25-99
         * * `100` - +100
         * @minLength 1
         */
        capacity?: '1-4' | '5-7' | '8-9' | '10-14' | '15-24' | '25-99' | '100'
        /** @format date */
        end_date: string
        location?: number[]
        /** @format date */
        start_date: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RoomTimetableFreeRoom[], T>, any>({
        path: `/rooms/room-timetable/free-rooms/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description [WARNING] AdminTimetableViewSet inherits from this!
     *
     * @tags rooms
     * @name RoomsRoomTimetableFreeRoomsAvailableFiltersRetrieve
     * @request GET:/rooms/room-timetable/free-rooms/available-filters/
     * @secure
     */,
    roomsRoomTimetableFreeRoomsAvailableFiltersRetrieve: <
      T extends FetchKeys<RoomTimetableFreeRoomsAvailableFilters>
    >(
      query: {
        /**
         * * `1-4` - 1-4
         * * `5-7` - 5-7
         * * `8-9` - 8-9
         * * `10-14` - 10-14
         * * `15-24` - 15-24
         * * `25-99` - 25-99
         * * `100` - +100
         * @minLength 1
         */
        capacity?: '1-4' | '5-7' | '8-9' | '10-14' | '15-24' | '25-99' | '100'
        /** @format date */
        end_date: string
        location?: number[]
        /** @format date */
        start_date: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RoomTimetableFreeRoomsAvailableFilters, T>, any>({
        path: `/rooms/room-timetable/free-rooms/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags rooms
     * @name RoomsRoomTimetableScheduledActivitiesList
     * @request GET:/rooms/room-timetable/scheduled-activities/
     * @secure
     */,
    roomsRoomTimetableScheduledActivitiesList: <
      T extends FetchKeys<RoomTTScheduledActivities[]>
    >(
      query: {
        /**
         * * `1-4` - 1-4
         * * `5-7` - 5-7
         * * `8-9` - 8-9
         * * `10-14` - 10-14
         * * `15-24` - 15-24
         * * `25-99` - 25-99
         * * `100` - +100
         * @minLength 1
         */
        capacity?: '1-4' | '5-7' | '8-9' | '10-14' | '15-24' | '25-99' | '100'
        /** @format date */
        end_date: string
        location?: number[]
        room?: number
        /** @format date */
        start_date: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RoomTTScheduledActivities[], T>, any>({
        path: `/rooms/room-timetable/scheduled-activities/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags rooms
     * @name RoomsRoomTimetableScheduledActivitiesAvailableFiltersRetrieve
     * @request GET:/rooms/room-timetable/scheduled-activities/available-filters/
     * @secure
     */,
    roomsRoomTimetableScheduledActivitiesAvailableFiltersRetrieve: <
      T extends FetchKeys<RoomTimetableAvailableFilters>
    >(
      query: {
        /**
         * * `1-4` - 1-4
         * * `5-7` - 5-7
         * * `8-9` - 8-9
         * * `10-14` - 10-14
         * * `15-24` - 15-24
         * * `25-99` - 25-99
         * * `100` - +100
         * @minLength 1
         */
        capacity?: '1-4' | '5-7' | '8-9' | '10-14' | '15-24' | '25-99' | '100'
        /** @format date */
        end_date: string
        location?: number[]
        room?: number
        /** @format date */
        start_date: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RoomTimetableAvailableFilters, T>, any>({
        path: `/rooms/room-timetable/scheduled-activities/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags rooms
     * @name RoomsRoomsAvailabilityCreate
     * @request POST:/rooms/rooms-availability/
     * @secure
     */,
    roomsRoomsAvailabilityCreate: <
      T extends FetchKeys<RoomAvailabilityResponse[]>
    >(
      data: RoomAvailabilityRequestRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RoomAvailabilityResponse[], T>, any>({
        path: `/rooms/rooms-availability/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description list rooms that include is_occupied regards chosen lesson event
     *
     * @tags rooms
     * @name RoomsWithOccupationList
     * @request GET:/rooms/with-occupation/
     * @secure
     */,
    roomsWithOccupationList: <T extends FetchKeys<RoomWithOccupation[]>>(
      query: {
        available_for_exam?: boolean
        department?: number
        is_private?: boolean
        /** @format uuid */
        lesson_id: string
        msl_room?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        pri_room?: boolean
        schedule_automatically?: boolean
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RoomWithOccupation[], T>, any>({
        path: `/rooms/with-occupation/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  schema91231Cc23Cxz123FUioa812Ax12183 = {
    /**
     * @description OpenApi3 schema for this API. Format can be selected via content negotiation. - YAML: application/vnd.oai.openapi - JSON: application/vnd.oai.openapi+json
     *
     * @tags schema-91231cc23Cxz123fUioa812ax12183
     * @name Schema91231Cc23Cxz123FUioa812Ax12183Retrieve
     * @request GET:/schema-91231cc23Cxz123fUioa812ax12183/
     * @secure
     */
    schema91231Cc23Cxz123FUioa812Ax12183Retrieve: <
      T extends FetchKeys<Record<string, any>>
    >(
      query?: {
        format?: 'json' | 'yaml'
        lang?: 'en'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Record<string, any>, T>, any>({
        path: `/schema-91231cc23Cxz123fUioa812ax12183/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description OpenApi3 schema for this API. Format can be selected via content negotiation. - YAML: application/vnd.oai.openapi - JSON: application/vnd.oai.openapi+json
     *
     * @tags schema-91231cc23Cxz123fUioa812ax12183
     * @name Schema91231Cc23Cxz123FUioa812Ax12183V1Retrieve
     * @request GET:/schema-91231cc23Cxz123fUioa812ax12183/v1/
     * @secure
     */,
    schema91231Cc23Cxz123FUioa812Ax12183V1Retrieve: <
      T extends FetchKeys<Record<string, any>>
    >(
      query?: {
        format?: 'json' | 'yaml'
        lang?: 'en'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Record<string, any>, T>, any>({
        path: `/schema-91231cc23Cxz123fUioa812ax12183/v1/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description OpenApi3 schema for this API. Format can be selected via content negotiation. - YAML: application/vnd.oai.openapi - JSON: application/vnd.oai.openapi+json
     *
     * @tags schema-91231cc23Cxz123fUioa812ax12183
     * @name Schema91231Cc23Cxz123FUioa812Ax12183V2Retrieve
     * @request GET:/schema-91231cc23Cxz123fUioa812ax12183/v2/
     * @secure
     */,
    schema91231Cc23Cxz123FUioa812Ax12183V2Retrieve: <
      T extends FetchKeys<Record<string, any>>
    >(
      query?: {
        format?: 'json' | 'yaml'
        lang?: 'en'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Record<string, any>, T>, any>({
        path: `/schema-91231cc23Cxz123fUioa812ax12183/v2/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  schoolBreaks = {
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags school-breaks
     * @name SchoolBreaksList
     * @request GET:/school-breaks/
     * @secure
     */
    schoolBreaksList: <T extends FetchKeys<PaginatedSchoolBreakList>>(
      query?: {
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        disable_lunchtime_recovery_in_the_preceding_week?: boolean
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        school_year?: string[]
        /** A search term. */
        search?: string
        /**
         * * `past` - Past
         * * `active` - Active
         * * `future` - Future
         */
        status?: 'active' | 'future' | 'past'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedSchoolBreakList, T>, any>({
        path: `/school-breaks/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags school-breaks
     * @name SchoolBreaksCreate
     * @request POST:/school-breaks/
     * @secure
     */,
    schoolBreaksCreate: <T extends FetchKeys<SchoolBreak>>(
      data: SchoolBreakRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SchoolBreak, T>, any>({
        path: `/school-breaks/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags school-breaks
     * @name SchoolBreaksRetrieve
     * @request GET:/school-breaks/{id}/
     * @secure
     */,
    schoolBreaksRetrieve: <T extends FetchKeys<SchoolBreak>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SchoolBreak, T>, any>({
        path: `/school-breaks/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags school-breaks
     * @name SchoolBreaksUpdate
     * @request PUT:/school-breaks/{id}/
     * @secure
     */,
    schoolBreaksUpdate: <T extends FetchKeys<SchoolBreak>>(
      id: number,
      data: SchoolBreakRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SchoolBreak, T>, any>({
        path: `/school-breaks/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags school-breaks
     * @name SchoolBreaksPartialUpdate
     * @request PATCH:/school-breaks/{id}/
     * @secure
     */,
    schoolBreaksPartialUpdate: <T extends FetchKeys<SchoolBreak>>(
      id: number,
      data: PatchedSchoolBreakRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SchoolBreak, T>, any>({
        path: `/school-breaks/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags school-breaks
     * @name SchoolBreaksDestroy
     * @request DELETE:/school-breaks/{id}/
     * @secure
     */,
    schoolBreaksDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/school-breaks/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
  }
  schoolProgrammes = {
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags school-programmes
     * @name SchoolProgrammesList
     * @request GET:/school-programmes/
     * @secure
     */
    schoolProgrammesList: <
      T extends FetchKeys<PaginatedSchoolProgrammeListList>
    >(
      query?: {
        /** @format uuid */
        added_by?: string
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /**
         * * `one_time` - One-time
         * * `recurring` - Recurring
         */
        frequency?: 'one_time' | 'recurring'
        is_compulsory?: boolean
        is_off_campus?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        participant?: string
        recurring?: number
        /** A search term. */
        search?: string
        /**
         * * `requested` - Requested
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `cancelled` - Cancelled
         */
        status?: 'approved' | 'cancelled' | 'rejected' | 'requested'
        student?: string
        /**
         * * `academics` - Academics
         * * `house_evening` - House Evening
         * * `term_break` - Term Break
         * * `school_trip` - School Trip
         * * `weekend_programme` - Weekend Programme
         * * `exam_preparation` - Exam Preparation
         * * `other` - Other
         * * `absence_recovery` - Absence Recovery
         * * `lunchtime_recovery` - Lunchtime Recovery
         */
        type?:
          | 'absence_recovery'
          | 'academics'
          | 'exam_preparation'
          | 'house_evening'
          | 'lunchtime_recovery'
          | 'other'
          | 'school_trip'
          | 'term_break'
          | 'weekend_programme'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedSchoolProgrammeListList, T>, any>({
        path: `/school-programmes/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags school-programmes
     * @name SchoolProgrammesCreate
     * @request POST:/school-programmes/
     * @secure
     */,
    schoolProgrammesCreate: <T extends FetchKeys<SchoolProgramme>>(
      data: SchoolProgrammeRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SchoolProgramme, T>, any>({
        path: `/school-programmes/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags school-programmes
     * @name SchoolProgrammesRetrieve
     * @request GET:/school-programmes/{id}/
     * @secure
     */,
    schoolProgrammesRetrieve: <T extends FetchKeys<SchoolProgramme>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SchoolProgramme, T>, any>({
        path: `/school-programmes/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags school-programmes
     * @name SchoolProgrammesUpdate
     * @request PUT:/school-programmes/{id}/
     * @secure
     */,
    schoolProgrammesUpdate: <T extends FetchKeys<SchoolProgramme>>(
      id: number,
      data: SchoolProgrammeRequest,
      query?: {
        /**
         * * `current` - This session
         * * `current_and_future` - This and following sessions
         * * `all` - All sessions
         * @minLength 1
         */
        recurring_scope?: 'current' | 'current_and_future' | 'all'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SchoolProgramme, T>, any>({
        path: `/school-programmes/${id}/`,
        method: 'PUT',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags school-programmes
     * @name SchoolProgrammesPartialUpdate
     * @request PATCH:/school-programmes/{id}/
     * @secure
     */,
    schoolProgrammesPartialUpdate: <T extends FetchKeys<SchoolProgramme>>(
      id: number,
      data: PatchedSchoolProgrammeRequest,
      query?: {
        /**
         * * `current` - This session
         * * `current_and_future` - This and following sessions
         * * `all` - All sessions
         * @minLength 1
         */
        recurring_scope?: 'current' | 'current_and_future' | 'all'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SchoolProgramme, T>, any>({
        path: `/school-programmes/${id}/`,
        method: 'PATCH',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags school-programmes
     * @name SchoolProgrammesDestroy
     * @request DELETE:/school-programmes/{id}/
     * @secure
     */,
    schoolProgrammesDestroy: <T extends FetchKeys<void>>(
      id: number,
      query?: {
        /**
         * * `current` - This session
         * * `current_and_future` - This and following sessions
         * * `all` - All sessions
         * @minLength 1
         */
        recurring_scope?: 'current' | 'current_and_future' | 'all'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/school-programmes/${id}/`,
        method: 'DELETE',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags school-programmes
     * @name SchoolProgrammesApproveProgrammeCreate
     * @request POST:/school-programmes/{id}/approve-programme/
     * @secure
     */,
    schoolProgrammesApproveProgrammeCreate: <
      T extends FetchKeys<SchoolProgrammeChangeStatusResponse>
    >(
      id: number,
      query?: {
        /**
         * * `current` - This session
         * * `current_and_future` - This and following sessions
         * * `all` - All sessions
         * @minLength 1
         */
        recurring_scope?: 'current' | 'current_and_future' | 'all'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SchoolProgrammeChangeStatusResponse, T>, any>({
        path: `/school-programmes/${id}/approve-programme/`,
        method: 'POST',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags school-programmes
     * @name SchoolProgrammesBulkStatusChangeClashesRetrieve
     * @request GET:/school-programmes/{id}/bulk-status-change-clashes/
     * @secure
     */,
    schoolProgrammesBulkStatusChangeClashesRetrieve: <
      T extends FetchKeys<SchoolProgrammeBulkStatusChangeClashes>
    >(
      id: number,
      query?: {
        /**
         * * `current` - This session
         * * `current_and_future` - This and following sessions
         * * `all` - All sessions
         * @minLength 1
         */
        recurring_scope?: 'current' | 'current_and_future' | 'all'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SchoolProgrammeBulkStatusChangeClashes, T>, any>({
        path: `/school-programmes/${id}/bulk-status-change-clashes/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags school-programmes
     * @name SchoolProgrammesCancelProgrammeCreate
     * @request POST:/school-programmes/{id}/cancel-programme/
     * @secure
     */,
    schoolProgrammesCancelProgrammeCreate: <
      T extends FetchKeys<SchoolProgrammeChangeStatusResponse>
    >(
      id: number,
      data: SchoolProgrammeCancelRequest,
      query?: {
        /**
         * * `current` - This session
         * * `current_and_future` - This and following sessions
         * * `all` - All sessions
         * @minLength 1
         */
        recurring_scope?: 'current' | 'current_and_future' | 'all'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SchoolProgrammeChangeStatusResponse, T>, any>({
        path: `/school-programmes/${id}/cancel-programme/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags school-programmes
     * @name SchoolProgrammesChangesList
     * @request GET:/school-programmes/{id}/changes/
     * @secure
     */,
    schoolProgrammesChangesList: <
      T extends FetchKeys<PaginatedLogEntryHistoryList>
    >(
      id: number,
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedLogEntryHistoryList, T>, any>({
        path: `/school-programmes/${id}/changes/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags school-programmes
     * @name SchoolProgrammesRejectProgrammeCreate
     * @request POST:/school-programmes/{id}/reject-programme/
     * @secure
     */,
    schoolProgrammesRejectProgrammeCreate: <
      T extends FetchKeys<SchoolProgrammeChangeStatusResponse>
    >(
      id: number,
      data: SchoolProgrammeRejectRequest,
      query?: {
        /**
         * * `current` - This session
         * * `current_and_future` - This and following sessions
         * * `all` - All sessions
         * @minLength 1
         */
        recurring_scope?: 'current' | 'current_and_future' | 'all'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SchoolProgrammeChangeStatusResponse, T>, any>({
        path: `/school-programmes/${id}/reject-programme/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags school-programmes
     * @name SchoolProgrammesRequestProgrammeCreate
     * @request POST:/school-programmes/{id}/request-programme/
     * @secure
     */,
    schoolProgrammesRequestProgrammeCreate: <
      T extends FetchKeys<SchoolProgrammeChangeStatusResponse>
    >(
      id: number,
      query?: {
        /**
         * * `current` - This session
         * * `current_and_future` - This and following sessions
         * * `all` - All sessions
         * @minLength 1
         */
        recurring_scope?: 'current' | 'current_and_future' | 'all'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SchoolProgrammeChangeStatusResponse, T>, any>({
        path: `/school-programmes/${id}/request-programme/`,
        method: 'POST',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags school-programmes
     * @name SchoolProgrammesAttendanceReportsList
     * @request GET:/school-programmes/{programmeId}/attendance-reports/
     * @secure
     */,
    schoolProgrammesAttendanceReportsList: <
      T extends FetchKeys<PaginatedProgrammeAttendanceReportList>
    >(
      programmeId: string,
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedProgrammeAttendanceReportList, T>, any>({
        path: `/school-programmes/${programmeId}/attendance-reports/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags school-programmes
     * @name SchoolProgrammesAttendanceReportsCreate
     * @request POST:/school-programmes/{programmeId}/attendance-reports/
     * @secure
     */,
    schoolProgrammesAttendanceReportsCreate: <
      T extends FetchKeys<ProgrammeAttendanceReport>
    >(
      programmeId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgrammeAttendanceReport, T>, any>({
        path: `/school-programmes/${programmeId}/attendance-reports/`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags school-programmes
     * @name SchoolProgrammesAttendanceReportsRetrieve
     * @request GET:/school-programmes/{programmeId}/attendance-reports/{id}/
     * @secure
     */,
    schoolProgrammesAttendanceReportsRetrieve: <
      T extends FetchKeys<ProgrammeAttendanceReport>
    >(
      id: number,
      programmeId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgrammeAttendanceReport, T>, any>({
        path: `/school-programmes/${programmeId}/attendance-reports/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags school-programmes
     * @name SchoolProgrammesAttendanceReportsUpdate
     * @request PUT:/school-programmes/{programmeId}/attendance-reports/{id}/
     * @secure
     */,
    schoolProgrammesAttendanceReportsUpdate: <
      T extends FetchKeys<ProgrammeAttendanceReportUpdate>
    >(
      id: number,
      programmeId: string,
      data: ProgrammeAttendanceReportUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgrammeAttendanceReportUpdate, T>, any>({
        path: `/school-programmes/${programmeId}/attendance-reports/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags school-programmes
     * @name SchoolProgrammesAttendanceReportsPartialUpdate
     * @request PATCH:/school-programmes/{programmeId}/attendance-reports/{id}/
     * @secure
     */,
    schoolProgrammesAttendanceReportsPartialUpdate: <
      T extends FetchKeys<ProgrammeAttendanceReport>
    >(
      id: number,
      programmeId: string,
      data: PatchedProgrammeAttendanceReportRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgrammeAttendanceReport, T>, any>({
        path: `/school-programmes/${programmeId}/attendance-reports/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags school-programmes
     * @name SchoolProgrammesAttendanceReportsStudentsList
     * @request GET:/school-programmes/{programmeId}/attendance-reports/{reportId}/students/
     * @secure
     */,
    schoolProgrammesAttendanceReportsStudentsList: <
      T extends FetchKeys<PaginatedProgrammeStudentAttendanceList>
    >(
      programmeId: string,
      reportId: string,
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedProgrammeStudentAttendanceList, T>, any>({
        path: `/school-programmes/${programmeId}/attendance-reports/${reportId}/students/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags school-programmes
     * @name SchoolProgrammesAttendanceReportsStudentsRetrieve
     * @request GET:/school-programmes/{programmeId}/attendance-reports/{reportId}/students/{id}/
     * @secure
     */,
    schoolProgrammesAttendanceReportsStudentsRetrieve: <
      T extends FetchKeys<ProgrammeStudentAttendance>
    >(
      id: number,
      programmeId: string,
      reportId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgrammeStudentAttendance, T>, any>({
        path: `/school-programmes/${programmeId}/attendance-reports/${reportId}/students/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags school-programmes
     * @name SchoolProgrammesAttendanceReportsStudentsUpdate
     * @request PUT:/school-programmes/{programmeId}/attendance-reports/{reportId}/students/{id}/
     * @secure
     */,
    schoolProgrammesAttendanceReportsStudentsUpdate: <
      T extends FetchKeys<ProgrammeStudentAttendanceUpdate>
    >(
      id: number,
      programmeId: string,
      reportId: string,
      data: ProgrammeStudentAttendanceUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgrammeStudentAttendanceUpdate, T>, any>({
        path: `/school-programmes/${programmeId}/attendance-reports/${reportId}/students/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags school-programmes
     * @name SchoolProgrammesAttendanceReportsStudentsPartialUpdate
     * @request PATCH:/school-programmes/{programmeId}/attendance-reports/{reportId}/students/{id}/
     * @secure
     */,
    schoolProgrammesAttendanceReportsStudentsPartialUpdate: <
      T extends FetchKeys<ProgrammeStudentAttendance>
    >(
      id: number,
      programmeId: string,
      reportId: string,
      data: PatchedProgrammeStudentAttendanceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgrammeStudentAttendance, T>, any>({
        path: `/school-programmes/${programmeId}/attendance-reports/${reportId}/students/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags school-programmes
     * @name SchoolProgrammesAttendanceReportsStudentsEnrolmentHouseNameRetrieve
     * @request GET:/school-programmes/{programmeId}/attendance-reports/{reportId}/students/{id}/enrolment-house-name/
     * @secure
     */,
    schoolProgrammesAttendanceReportsStudentsEnrolmentHouseNameRetrieve: <
      T extends FetchKeys<ProgrammeEnrolmentHouseName>
    >(
      id: number,
      programmeId: string,
      reportId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ProgrammeEnrolmentHouseName, T>, any>({
        path: `/school-programmes/${programmeId}/attendance-reports/${reportId}/students/${id}/enrolment-house-name/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags school-programmes
     * @name SchoolProgrammesEnrolmentsList
     * @request GET:/school-programmes/{programmeId}/enrolments/
     * @secure
     */,
    schoolProgrammesEnrolmentsList: <
      T extends FetchKeys<PaginatedProgrammeEnrolmentList>
    >(
      programmeId: string,
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /**
         * * `pending` - Pending
         * * `requested` - Requested
         * * `rejected` - Rejected
         * * `approved` - Approved
         * * `leave_absent` - Leave - Absent
         */
        status?:
          | 'approved'
          | 'leave_absent'
          | 'pending'
          | 'rejected'
          | 'requested'
        student?: string[]
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedProgrammeEnrolmentList, T>, any>({
        path: `/school-programmes/${programmeId}/enrolments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags school-programmes
     * @name SchoolProgrammesEnrolmentsAvailableFiltersRetrieve
     * @request GET:/school-programmes/{programmeId}/enrolments/available-filters/
     * @secure
     */,
    schoolProgrammesEnrolmentsAvailableFiltersRetrieve: <
      T extends FetchKeys<SchoolProgrammesEnrolmentsAvailableFilters>
    >(
      programmeId: string,
      query?: {
        /**
         * * `pending` - Pending
         * * `requested` - Requested
         * * `rejected` - Rejected
         * * `approved` - Approved
         * * `leave_absent` - Leave - Absent
         */
        status?:
          | 'approved'
          | 'leave_absent'
          | 'pending'
          | 'rejected'
          | 'requested'
        student?: string[]
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<SchoolProgrammesEnrolmentsAvailableFilters, T>,
        any
      >({
        path: `/school-programmes/${programmeId}/enrolments/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags school-programmes
     * @name SchoolProgrammesAvailableFiltersRetrieve
     * @request GET:/school-programmes/available-filters/
     * @secure
     */,
    schoolProgrammesAvailableFiltersRetrieve: <
      T extends FetchKeys<SchoolProgrammeAvailableFilters>
    >(
      query?: {
        /** @format uuid */
        added_by?: string
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        /**
         * * `one_time` - One-time
         * * `recurring` - Recurring
         */
        frequency?: 'one_time' | 'recurring'
        is_compulsory?: boolean
        is_off_campus?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        participant?: string
        recurring?: number
        /** A search term. */
        search?: string
        /**
         * * `requested` - Requested
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `cancelled` - Cancelled
         */
        status?: 'approved' | 'cancelled' | 'rejected' | 'requested'
        student?: string
        /**
         * * `academics` - Academics
         * * `house_evening` - House Evening
         * * `term_break` - Term Break
         * * `school_trip` - School Trip
         * * `weekend_programme` - Weekend Programme
         * * `exam_preparation` - Exam Preparation
         * * `other` - Other
         * * `absence_recovery` - Absence Recovery
         * * `lunchtime_recovery` - Lunchtime Recovery
         */
        type?:
          | 'absence_recovery'
          | 'academics'
          | 'exam_preparation'
          | 'house_evening'
          | 'lunchtime_recovery'
          | 'other'
          | 'school_trip'
          | 'term_break'
          | 'weekend_programme'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SchoolProgrammeAvailableFilters, T>, any>({
        path: `/school-programmes/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags school-programmes
     * @name SchoolProgrammesAvailableStaffUsersList
     * @request GET:/school-programmes/available-staff-users/
     * @secure
     */,
    schoolProgrammesAvailableStaffUsersList: <
      T extends FetchKeys<SchoolProgrammeStaffOption[]>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SchoolProgrammeStaffOption[], T>, any>({
        path: `/school-programmes/available-staff-users/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags school-programmes
     * @name SchoolProgrammesAvailableStudentsList
     * @request GET:/school-programmes/available-students/
     * @secure
     */,
    schoolProgrammesAvailableStudentsList: <
      T extends FetchKeys<PaginatedAvailableStudentList>
    >(
      query?: {
        ages?: string[]
        /**
         * * `4` - S
         * * `3` - E
         * * `2` - A
         * * `1` - C
         * * `0` - not assigned
         */
        categories?: (0 | 1 | 2 | 3 | 4)[]
        grade_levels?: string[]
        houses?: number[]
        /** @format uuid */
        id?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        sections?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedAvailableStudentList, T>, any>({
        path: `/school-programmes/available-students/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags school-programmes
     * @name SchoolProgrammesAvailableStudentsAvailableFiltersRetrieve
     * @request GET:/school-programmes/available-students/available-filters/
     * @secure
     */,
    schoolProgrammesAvailableStudentsAvailableFiltersRetrieve: <
      T extends FetchKeys<AvailableStudentForSchoolProgrammeAvailableFilters>
    >(
      query?: {
        ages?: string[]
        /**
         * * `4` - S
         * * `3` - E
         * * `2` - A
         * * `1` - C
         * * `0` - not assigned
         */
        categories?: (0 | 1 | 2 | 3 | 4)[]
        grade_levels?: string[]
        houses?: number[]
        /** @format uuid */
        id?: string
        sections?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<AvailableStudentForSchoolProgrammeAvailableFilters, T>,
        any
      >({
        path: `/school-programmes/available-students/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags school-programmes
     * @name SchoolProgrammesEnrolmentsSetAsApprovedCreate
     * @request POST:/school-programmes/enrolments/{id}/set-as-approved/
     * @secure
     */,
    schoolProgrammesEnrolmentsSetAsApprovedCreate: <T extends FetchKeys<void>>(
      id: number,
      query?: {
        /**
         * * `current` - This session
         * * `current_and_future` - This and following sessions
         * * `all` - All sessions
         * @minLength 1
         */
        recurring_scope?: 'current' | 'current_and_future' | 'all'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/school-programmes/enrolments/${id}/set-as-approved/`,
        method: 'POST',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags school-programmes
     * @name SchoolProgrammesEnrolmentsSetAsRejectedCreate
     * @request POST:/school-programmes/enrolments/{id}/set-as-rejected/
     * @secure
     */,
    schoolProgrammesEnrolmentsSetAsRejectedCreate: <T extends FetchKeys<void>>(
      id: number,
      data: RejectEnrolmentRequest,
      query?: {
        /**
         * * `current` - This session
         * * `current_and_future` - This and following sessions
         * * `all` - All sessions
         * @minLength 1
         */
        recurring_scope?: 'current' | 'current_and_future' | 'all'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/school-programmes/enrolments/${id}/set-as-rejected/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags school-programmes
     * @name SchoolProgrammesEnrolmentsSetAsRequestedCreate
     * @request POST:/school-programmes/enrolments/{id}/set-as-requested/
     * @secure
     */,
    schoolProgrammesEnrolmentsSetAsRequestedCreate: <T extends FetchKeys<void>>(
      id: number,
      query?: {
        /**
         * * `current` - This session
         * * `current_and_future` - This and following sessions
         * * `all` - All sessions
         * @minLength 1
         */
        recurring_scope?: 'current' | 'current_and_future' | 'all'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/school-programmes/enrolments/${id}/set-as-requested/`,
        method: 'POST',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags school-programmes
     * @name SchoolProgrammesEnrolmentsSetManyAsApprovedCreate
     * @request POST:/school-programmes/enrolments/set-many-as-approved/
     * @secure
     */,
    schoolProgrammesEnrolmentsSetManyAsApprovedCreate: <
      T extends FetchKeys<void>
    >(
      data: ManyEnrolmentsToApprovedRequest,
      query?: {
        /**
         * * `current` - This session
         * * `current_and_future` - This and following sessions
         * * `all` - All sessions
         * @minLength 1
         */
        recurring_scope?: 'current' | 'current_and_future' | 'all'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/school-programmes/enrolments/set-many-as-approved/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags school-programmes
     * @name SchoolProgrammesEnrolmentsSetManyAsRejectedCreate
     * @request POST:/school-programmes/enrolments/set-many-as-rejected/
     * @secure
     */,
    schoolProgrammesEnrolmentsSetManyAsRejectedCreate: <
      T extends FetchKeys<void>
    >(
      data: ManyEnrolmentsToRejectedRequest,
      query?: {
        /**
         * * `current` - This session
         * * `current_and_future` - This and following sessions
         * * `all` - All sessions
         * @minLength 1
         */
        recurring_scope?: 'current' | 'current_and_future' | 'all'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/school-programmes/enrolments/set-many-as-rejected/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags school-programmes
     * @name SchoolProgrammesEnrolmentsSetManyAsRequestedCreate
     * @request POST:/school-programmes/enrolments/set-many-as-requested/
     * @secure
     */,
    schoolProgrammesEnrolmentsSetManyAsRequestedCreate: <
      T extends FetchKeys<void>
    >(
      data: ManyEnrolmentsToRequestedRequest,
      query?: {
        /**
         * * `current` - This session
         * * `current_and_future` - This and following sessions
         * * `all` - All sessions
         * @minLength 1
         */
        recurring_scope?: 'current' | 'current_and_future' | 'all'
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/school-programmes/enrolments/set-many-as-requested/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
  }
  sickReports = {
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsList
     * @request GET:/sick-reports/
     * @secure
     */
    sickReportsList: <T extends FetchKeys<PaginatedSickReportListList>>(
      query?: {
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        house?: number
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        status?: string
        /** @format uuid */
        student?: string
        /**
         * * `regular` - Regular
         * * `med_only` - Med-only
         */
        type?: 'med_only' | 'regular'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedSickReportListList, T>, any>({
        path: `/sick-reports/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsCreate
     * @request POST:/sick-reports/
     * @secure
     */,
    sickReportsCreate: <T extends FetchKeys<SickReport>>(
      data: SickReportRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SickReport, T>, any>({
        path: `/sick-reports/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsRetrieve
     * @request GET:/sick-reports/{id}/
     * @secure
     */,
    sickReportsRetrieve: <T extends FetchKeys<SickReport>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SickReport, T>, any>({
        path: `/sick-reports/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsUpdate
     * @request PUT:/sick-reports/{id}/
     * @secure
     */,
    sickReportsUpdate: <T extends FetchKeys<SickReport>>(
      id: string,
      data: SickReportRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SickReport, T>, any>({
        path: `/sick-reports/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsPartialUpdate
     * @request PATCH:/sick-reports/{id}/
     * @secure
     */,
    sickReportsPartialUpdate: <T extends FetchKeys<SickReport>>(
      id: string,
      data: PatchedSickReportRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SickReport, T>, any>({
        path: `/sick-reports/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsDestroy
     * @request DELETE:/sick-reports/{id}/
     * @secure
     */,
    sickReportsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/sick-reports/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsApproveClosureCreate
     * @request POST:/sick-reports/{id}/approve-closure/
     * @secure
     */,
    sickReportsApproveClosureCreate: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/sick-reports/${id}/approve-closure/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsCloseCreate
     * @request POST:/sick-reports/{id}/close/
     * @secure
     */,
    sickReportsCloseCreate: <T extends FetchKeys<CloseSickReport>>(
      id: string,
      data: CloseSickReportRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CloseSickReport, T>, any>({
        path: `/sick-reports/${id}/close/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsHasActiveMedicineInPlanRetrieve
     * @request GET:/sick-reports/{id}/has-active-medicine-in-plan/
     * @secure
     */,
    sickReportsHasActiveMedicineInPlanRetrieve: <
      T extends FetchKeys<SickReportHasActiveMedicineInPlan>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SickReportHasActiveMedicineInPlan, T>, any>({
        path: `/sick-reports/${id}/has-active-medicine-in-plan/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsOpenCreate
     * @request POST:/sick-reports/{id}/open/
     * @secure
     */,
    sickReportsOpenCreate: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/sick-reports/${id}/open/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsRejectCreate
     * @request POST:/sick-reports/{id}/reject/
     * @secure
     */,
    sickReportsRejectCreate: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/sick-reports/${id}/reject/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsRejectClosureCreate
     * @request POST:/sick-reports/{id}/reject-closure/
     * @secure
     */,
    sickReportsRejectClosureCreate: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/sick-reports/${id}/reject-closure/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsRequestClosureCreate
     * @request POST:/sick-reports/{id}/request-closure/
     * @secure
     */,
    sickReportsRequestClosureCreate: <
      T extends FetchKeys<SickReportClosureRequest>
    >(
      id: string,
      data: SickReportClosureRequestRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SickReportClosureRequest, T>, any>({
        path: `/sick-reports/${id}/request-closure/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsCommentsList
     * @request GET:/sick-reports/{sickReportId}/comments/
     * @secure
     */,
    sickReportsCommentsList: <T extends FetchKeys<SickReportComment[]>>(
      sickReportId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SickReportComment[], T>, any>({
        path: `/sick-reports/${sickReportId}/comments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsCommentsCreate
     * @request POST:/sick-reports/{sickReportId}/comments/
     * @secure
     */,
    sickReportsCommentsCreate: <T extends FetchKeys<SickReportComment>>(
      sickReportId: string,
      data: SickReportCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SickReportComment, T>, any>({
        path: `/sick-reports/${sickReportId}/comments/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsCommentsRetrieve
     * @request GET:/sick-reports/{sickReportId}/comments/{id}/
     * @secure
     */,
    sickReportsCommentsRetrieve: <T extends FetchKeys<SickReportComment>>(
      id: string,
      sickReportId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SickReportComment, T>, any>({
        path: `/sick-reports/${sickReportId}/comments/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsCommentsUpdate
     * @request PUT:/sick-reports/{sickReportId}/comments/{id}/
     * @secure
     */,
    sickReportsCommentsUpdate: <T extends FetchKeys<SickReportComment>>(
      id: string,
      sickReportId: string,
      data: SickReportCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SickReportComment, T>, any>({
        path: `/sick-reports/${sickReportId}/comments/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsCommentsPartialUpdate
     * @request PATCH:/sick-reports/{sickReportId}/comments/{id}/
     * @secure
     */,
    sickReportsCommentsPartialUpdate: <T extends FetchKeys<SickReportComment>>(
      id: string,
      sickReportId: string,
      data: PatchedSickReportCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SickReportComment, T>, any>({
        path: `/sick-reports/${sickReportId}/comments/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsCommentsDestroy
     * @request DELETE:/sick-reports/{sickReportId}/comments/{id}/
     * @secure
     */,
    sickReportsCommentsDestroy: <T extends FetchKeys<void>>(
      id: string,
      sickReportId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/sick-reports/${sickReportId}/comments/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsAppointmentsList
     * @request GET:/sick-reports/appointments/
     * @secure
     */,
    sickReportsAppointmentsList: <T extends FetchKeys<Appointment[]>>(
      query?: {
        report?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Appointment[], T>, any>({
        path: `/sick-reports/appointments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsAppointmentsCreate
     * @request POST:/sick-reports/appointments/
     * @secure
     */,
    sickReportsAppointmentsCreate: <T extends FetchKeys<Appointment>>(
      data: AppointmentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Appointment, T>, any>({
        path: `/sick-reports/appointments/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsAppointmentsRetrieve
     * @request GET:/sick-reports/appointments/{id}/
     * @secure
     */,
    sickReportsAppointmentsRetrieve: <T extends FetchKeys<Appointment>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Appointment, T>, any>({
        path: `/sick-reports/appointments/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsAppointmentsUpdate
     * @request PUT:/sick-reports/appointments/{id}/
     * @secure
     */,
    sickReportsAppointmentsUpdate: <T extends FetchKeys<Appointment>>(
      id: number,
      data: AppointmentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Appointment, T>, any>({
        path: `/sick-reports/appointments/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsAppointmentsPartialUpdate
     * @request PATCH:/sick-reports/appointments/{id}/
     * @secure
     */,
    sickReportsAppointmentsPartialUpdate: <T extends FetchKeys<Appointment>>(
      id: number,
      data: PatchedAppointmentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Appointment, T>, any>({
        path: `/sick-reports/appointments/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsAppointmentsDestroy
     * @request DELETE:/sick-reports/appointments/{id}/
     * @secure
     */,
    sickReportsAppointmentsDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/sick-reports/appointments/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsAppointmentsCancelUpdate
     * @request PUT:/sick-reports/appointments/{id}/cancel/
     * @secure
     */,
    sickReportsAppointmentsCancelUpdate: <
      T extends FetchKeys<AppointmentCancellation>
    >(
      id: number,
      data: AppointmentCancellationRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AppointmentCancellation, T>, any>({
        path: `/sick-reports/appointments/${id}/cancel/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsAppointmentsCancelPartialUpdate
     * @request PATCH:/sick-reports/appointments/{id}/cancel/
     * @secure
     */,
    sickReportsAppointmentsCancelPartialUpdate: <
      T extends FetchKeys<AppointmentCancellation>
    >(
      id: number,
      data: PatchedAppointmentCancellationRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AppointmentCancellation, T>, any>({
        path: `/sick-reports/appointments/${id}/cancel/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsAppointmentsGetDiagnosisRetrieve
     * @request GET:/sick-reports/appointments/{id}/get-diagnosis/
     * @secure
     */,
    sickReportsAppointmentsGetDiagnosisRetrieve: <
      T extends FetchKeys<DoctorDiagnosis>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DoctorDiagnosis, T>, any>({
        path: `/sick-reports/appointments/${id}/get-diagnosis/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsAppointmentsSetDiagnosisUpdate
     * @request PUT:/sick-reports/appointments/{id}/set-diagnosis/
     * @secure
     */,
    sickReportsAppointmentsSetDiagnosisUpdate: <
      T extends FetchKeys<DoctorDiagnosis>
    >(
      id: number,
      data: DoctorDiagnosisRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DoctorDiagnosis, T>, any>({
        path: `/sick-reports/appointments/${id}/set-diagnosis/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsAppointmentsSetDiagnosisPartialUpdate
     * @request PATCH:/sick-reports/appointments/{id}/set-diagnosis/
     * @secure
     */,
    sickReportsAppointmentsSetDiagnosisPartialUpdate: <
      T extends FetchKeys<DoctorDiagnosis>
    >(
      id: number,
      data: PatchedDoctorDiagnosisRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DoctorDiagnosis, T>, any>({
        path: `/sick-reports/appointments/${id}/set-diagnosis/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsAvailableFiltersRetrieve
     * @request GET:/sick-reports/available-filters/
     * @secure
     */,
    sickReportsAvailableFiltersRetrieve: <
      T extends FetchKeys<SickReportAvailableFilters>
    >(
      query?: {
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        house?: number
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string
        status?: string
        /** @format uuid */
        student?: string
        /**
         * * `regular` - Regular
         * * `med_only` - Med-only
         */
        type?: 'med_only' | 'regular'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SickReportAvailableFilters, T>, any>({
        path: `/sick-reports/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsCloseReasonsList
     * @request GET:/sick-reports/close-reasons/
     * @secure
     */,
    sickReportsCloseReasonsList: <T extends FetchKeys<StringOption[]>>(
      query?: {
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        house?: number
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string
        status?: string
        /** @format uuid */
        student?: string
        /**
         * * `regular` - Regular
         * * `med_only` - Med-only
         */
        type?: 'med_only' | 'regular'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/sick-reports/close-reasons/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsFirstContactRetrieve
     * @request GET:/sick-reports/first-contact/{reportId}/
     * @secure
     */,
    sickReportsFirstContactRetrieve: <T extends FetchKeys<FirstContact>>(
      reportId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FirstContact, T>, any>({
        path: `/sick-reports/first-contact/${reportId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsFirstContactCreate
     * @request POST:/sick-reports/first-contact/{reportId}/
     * @secure
     */,
    sickReportsFirstContactCreate: <T extends FetchKeys<FirstContact>>(
      reportId: string,
      data: FirstContactRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FirstContact, T>, any>({
        path: `/sick-reports/first-contact/${reportId}/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsFirstContactUpdate
     * @request PUT:/sick-reports/first-contact/{reportId}/
     * @secure
     */,
    sickReportsFirstContactUpdate: <T extends FetchKeys<FirstContact>>(
      reportId: string,
      data: FirstContactRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FirstContact, T>, any>({
        path: `/sick-reports/first-contact/${reportId}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsFirstContactPartialUpdate
     * @request PATCH:/sick-reports/first-contact/{reportId}/
     * @secure
     */,
    sickReportsFirstContactPartialUpdate: <T extends FetchKeys<FirstContact>>(
      reportId: string,
      data: PatchedFirstContactRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FirstContact, T>, any>({
        path: `/sick-reports/first-contact/${reportId}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsOpenedCovidReportsRetrieve
     * @request GET:/sick-reports/opened-covid-reports/
     * @secure
     */,
    sickReportsOpenedCovidReportsRetrieve: <
      T extends FetchKeys<SimpleSickReport>
    >(
      query?: {
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        house?: number
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string
        status?: string
        /** @format uuid */
        student?: string
        /**
         * * `regular` - Regular
         * * `med_only` - Med-only
         */
        type?: 'med_only' | 'regular'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SimpleSickReport, T>, any>({
        path: `/sick-reports/opened-covid-reports/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsSickReportAbsenceDatesCreate
     * @request POST:/sick-reports/sick-report-absence-dates/
     * @secure
     */,
    sickReportsSickReportAbsenceDatesCreate: <
      T extends FetchKeys<SickReportAbsenceDates>
    >(
      data: SickReportAbsenceDatesRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SickReportAbsenceDates, T>, any>({
        path: `/sick-reports/sick-report-absence-dates/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsSickReportAbsenceDatesUpdate
     * @request PUT:/sick-reports/sick-report-absence-dates/{id}/
     * @secure
     */,
    sickReportsSickReportAbsenceDatesUpdate: <
      T extends FetchKeys<SickReportAbsenceDates>
    >(
      id: string,
      data: SickReportAbsenceDatesRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SickReportAbsenceDates, T>, any>({
        path: `/sick-reports/sick-report-absence-dates/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsSickReportAbsenceDatesPartialUpdate
     * @request PATCH:/sick-reports/sick-report-absence-dates/{id}/
     * @secure
     */,
    sickReportsSickReportAbsenceDatesPartialUpdate: <
      T extends FetchKeys<SickReportAbsenceDates>
    >(
      id: string,
      data: PatchedSickReportAbsenceDatesRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SickReportAbsenceDates, T>, any>({
        path: `/sick-reports/sick-report-absence-dates/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsSymptomsList
     * @request GET:/sick-reports/symptoms/
     * @secure
     */,
    sickReportsSymptomsList: <T extends FetchKeys<StringOption[]>>(
      query?: {
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        house?: number
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A search term. */
        search?: string
        status?: string
        /** @format uuid */
        student?: string
        /**
         * * `regular` - Regular
         * * `med_only` - Med-only
         */
        type?: 'med_only' | 'regular'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/sick-reports/symptoms/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags sick-reports
     * @name SickReportsTreatmentHistoryList
     * @request GET:/sick-reports/treatment-history/{studentId}/
     * @secure
     */,
    sickReportsTreatmentHistoryList: <
      T extends FetchKeys<TreatmentHistoryResponse[]>
    >(
      studentId: string,
      query?: {
        /** @format date */
        history_created_after?: string
        /** @format date */
        history_created_before?: string
        /** Which field to use when ordering the results. */
        treatment_history_ordering?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TreatmentHistoryResponse[], T>, any>({
        path: `/sick-reports/treatment-history/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  studentApplications = {
    /**
     * No description
     *
     * @tags student-applications
     * @name StudentApplicationsCreate
     * @request POST:/student-applications/
     * @secure
     */
    studentApplicationsCreate: <T extends FetchKeys<StudentApplication>>(
      data: StudentApplicationRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentApplication, T>, any>({
        path: `/student-applications/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student-applications
     * @name StudentApplicationsChoicesRetrieve
     * @request GET:/student-applications/choices/
     * @secure
     */,
    studentApplicationsChoicesRetrieve: <
      T extends FetchKeys<StudentApplication>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentApplication, T>, any>({
        path: `/student-applications/choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student-applications
     * @name StudentApplicationsMedicationsList
     * @request GET:/student-applications/medications/
     * @secure
     */,
    studentApplicationsMedicationsList: <
      T extends FetchKeys<AdminStudentApplicationMedication[]>
    >(
      query?: {
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminStudentApplicationMedication[], T>, any>({
        path: `/student-applications/medications/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student-applications
     * @name StudentApplicationsMedicationsRetrieve
     * @request GET:/student-applications/medications/{id}/
     * @secure
     */,
    studentApplicationsMedicationsRetrieve: <
      T extends FetchKeys<AdminStudentApplicationMedication>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminStudentApplicationMedication, T>, any>({
        path: `/student-applications/medications/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student-applications
     * @name StudentApplicationsMedicationsIgnoreCreate
     * @request POST:/student-applications/medications/{id}/ignore/
     * @secure
     */,
    studentApplicationsMedicationsIgnoreCreate: <
      T extends FetchKeys<AdminStudentApplicationMedication>
    >(
      id: number,
      data: AdminStudentApplicationMedicationRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AdminStudentApplicationMedication, T>, any>({
        path: `/student-applications/medications/${id}/ignore/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
  }
  student = {
    /**
     * No description
     *
     * @tags student
     * @name StudentAssignmentsHomeworksList
     * @request GET:/student/assignments/homeworks/
     * @secure
     */
    studentAssignmentsHomeworksList: <
      T extends FetchKeys<PaginatedStudentHomeworkList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentHomeworkList, T>, any>({
        path: `/student/assignments/homeworks/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentAssignmentsHomeworksRetrieve
     * @request GET:/student/assignments/homeworks/{id}/
     * @secure
     */,
    studentAssignmentsHomeworksRetrieve: <T extends FetchKeys<StudentHomework>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentHomework, T>, any>({
        path: `/student/assignments/homeworks/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentAssignmentsHomeworksMarkAsSentByStudentUpdate
     * @request PUT:/student/assignments/homeworks/{id}/mark-as-sent-by-student/
     * @secure
     */,
    studentAssignmentsHomeworksMarkAsSentByStudentUpdate: <
      T extends FetchKeys<MarkHomeworkAsSentStudent>
    >(
      id: string,
      data: MarkHomeworkAsSentStudentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MarkHomeworkAsSentStudent, T>, any>({
        path: `/student/assignments/homeworks/${id}/mark-as-sent-by-student/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentAssignmentsHomeworksMarkAsSentByStudentPartialUpdate
     * @request PATCH:/student/assignments/homeworks/{id}/mark-as-sent-by-student/
     * @secure
     */,
    studentAssignmentsHomeworksMarkAsSentByStudentPartialUpdate: <
      T extends FetchKeys<MarkHomeworkAsSentStudent>
    >(
      id: string,
      data: PatchedMarkHomeworkAsSentStudentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MarkHomeworkAsSentStudent, T>, any>({
        path: `/student/assignments/homeworks/${id}/mark-as-sent-by-student/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentAssignmentsHomeworksCurrentlyLateRetrieve
     * @request GET:/student/assignments/homeworks/currently-late/
     * @secure
     */,
    studentAssignmentsHomeworksCurrentlyLateRetrieve: <
      T extends FetchKeys<StudentHomeworkCurrentlyLate>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentHomeworkCurrentlyLate, T>, any>({
        path: `/student/assignments/homeworks/currently-late/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentAssignmentsHomeworksNewlyReviewedRetrieve
     * @request GET:/student/assignments/homeworks/newly-reviewed/
     * @secure
     */,
    studentAssignmentsHomeworksNewlyReviewedRetrieve: <
      T extends FetchKeys<StudentHomeworkNewlyReviewed>
    >(
      query?: {
        course?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentHomeworkNewlyReviewed, T>, any>({
        path: `/student/assignments/homeworks/newly-reviewed/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentAssignmentsHomeworksToDoRetrieve
     * @request GET:/student/assignments/homeworks/to-do/
     * @secure
     */,
    studentAssignmentsHomeworksToDoRetrieve: <
      T extends FetchKeys<StudentHomeworkToDo>
    >(
      query?: {
        course?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentHomeworkToDo, T>, any>({
        path: `/student/assignments/homeworks/to-do/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentAttendancesAbsencesList
     * @request GET:/student/attendances/absences/
     * @secure
     */,
    studentAttendancesAbsencesList: <
      T extends FetchKeys<PaginatedStudentAcademicAttendanceList>
    >(
      query?: {
        /** @format uuid */
        course?: string
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentAcademicAttendanceList, T>, any>({
        path: `/student/attendances/absences/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentCampusAbsencesList
     * @request GET:/student/campus-absences/
     * @secure
     */,
    studentCampusAbsencesList: <
      T extends FetchKeys<PaginatedStudentCampusAbsenceList>
    >(
      query?: {
        /**
         * * `afternoon_ausgang` - Afternoon Ausgang
         * * `evening_ausgang` - Evening Ausgang
         * * `evening_campus_ausgang` - Evening - Campus Ausgang
         * * `birthday_ausgang` - Birthday Ausgang
         * * `family_visit` - Family visit
         * * `on_board` - On board Ausgang
         * * `prolonged_ausgang` - Prolonged Ausgang
         * * `campus` - Campus
         * * `offsite` - Offsite
         * * `offsite_overnight` - Offsite, Overnight
         * * `official_appointment` - Official appointment
         * * `specialist_doctor_appointment` - Specialist Doctor Appointment
         * * `programme` - Programme
         * * `house_evening` - House evening
         * * `term_break` - Term Break
         * * `school_trip` - School Trip
         * * `weekend_programme` - Weekend Programme
         * * `other` - Other
         * * `book_the_cafe` - Book the Cafe
         * * `city_brunch_zurich` - City Brunch Zurich
         * * `additional_ausgang` - Additional Ausgang
         * * `on_campus_ausgang` - On Campus Ausgang
         */
        campus_absence_subtype?: (
          | 'additional_ausgang'
          | 'afternoon_ausgang'
          | 'birthday_ausgang'
          | 'book_the_cafe'
          | 'campus'
          | 'city_brunch_zurich'
          | 'evening_ausgang'
          | 'evening_campus_ausgang'
          | 'family_visit'
          | 'house_evening'
          | 'official_appointment'
          | 'offsite'
          | 'offsite_overnight'
          | 'on_board'
          | 'on_campus_ausgang'
          | 'other'
          | 'programme'
          | 'prolonged_ausgang'
          | 'school_trip'
          | 'specialist_doctor_appointment'
          | 'term_break'
          | 'weekend_programme'
        )[]
        /**
         * * `ausgang` - Ausgang
         * * `external_exam` - External exam
         * * `official` - Official
         * * `school_programme` - School Programme
         * * `extra_ausgang` - Extra Ausgang
         */
        campus_absence_type?: (
          | 'ausgang'
          | 'external_exam'
          | 'extra_ausgang'
          | 'official'
          | 'school_programme'
        )[]
        /** @format date-time */
        date_after?: string
        /** @format date-time */
        date_before?: string
        /**
         * Ordering
         *
         * * `date_from` - Date from
         * * `-date_from` - Date from (descending)
         */
        ordering?: ('-date_from' | 'date_from')[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /**
         * * `requested` - Pending
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `canceled_by_student` - Canceled by student
         * * `done` - Done
         * * `cancelled` - Cancelled
         */
        status?: (
          | 'approved'
          | 'canceled_by_student'
          | 'cancelled'
          | 'done'
          | 'rejected'
          | 'requested'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentCampusAbsenceList, T>, any>({
        path: `/student/campus-absences/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentCampusAbsencesCreate
     * @request POST:/student/campus-absences/
     * @secure
     */,
    studentCampusAbsencesCreate: <T extends FetchKeys<StudentCampusAbsence>>(
      data: StudentCampusAbsenceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentCampusAbsence, T>, any>({
        path: `/student/campus-absences/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentCampusAbsencesRetrieve
     * @request GET:/student/campus-absences/{id}/
     * @secure
     */,
    studentCampusAbsencesRetrieve: <T extends FetchKeys<StudentCampusAbsence>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentCampusAbsence, T>, any>({
        path: `/student/campus-absences/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentCampusAbsencesUpdate
     * @request PUT:/student/campus-absences/{id}/
     * @secure
     */,
    studentCampusAbsencesUpdate: <T extends FetchKeys<StudentCampusAbsence>>(
      id: string,
      data: StudentCampusAbsenceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentCampusAbsence, T>, any>({
        path: `/student/campus-absences/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentCampusAbsencesPartialUpdate
     * @request PATCH:/student/campus-absences/{id}/
     * @secure
     */,
    studentCampusAbsencesPartialUpdate: <
      T extends FetchKeys<StudentCampusAbsence>
    >(
      id: string,
      data: PatchedStudentCampusAbsenceRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentCampusAbsence, T>, any>({
        path: `/student/campus-absences/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentCampusAbsencesDestroy
     * @request DELETE:/student/campus-absences/{id}/
     * @secure
     */,
    studentCampusAbsencesDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/student/campus-absences/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentCampusAbsencesStatusesList
     * @request GET:/student/campus-absences/statuses/
     * @secure
     */,
    studentCampusAbsencesStatusesList: <
      T extends FetchKeys<StudentCampusAbsenceStatus[]>
    >(
      query?: {
        /**
         * * `afternoon_ausgang` - Afternoon Ausgang
         * * `evening_ausgang` - Evening Ausgang
         * * `evening_campus_ausgang` - Evening - Campus Ausgang
         * * `birthday_ausgang` - Birthday Ausgang
         * * `family_visit` - Family visit
         * * `on_board` - On board Ausgang
         * * `prolonged_ausgang` - Prolonged Ausgang
         * * `campus` - Campus
         * * `offsite` - Offsite
         * * `offsite_overnight` - Offsite, Overnight
         * * `official_appointment` - Official appointment
         * * `specialist_doctor_appointment` - Specialist Doctor Appointment
         * * `programme` - Programme
         * * `house_evening` - House evening
         * * `term_break` - Term Break
         * * `school_trip` - School Trip
         * * `weekend_programme` - Weekend Programme
         * * `other` - Other
         * * `book_the_cafe` - Book the Cafe
         * * `city_brunch_zurich` - City Brunch Zurich
         * * `additional_ausgang` - Additional Ausgang
         * * `on_campus_ausgang` - On Campus Ausgang
         */
        campus_absence_subtype?: (
          | 'additional_ausgang'
          | 'afternoon_ausgang'
          | 'birthday_ausgang'
          | 'book_the_cafe'
          | 'campus'
          | 'city_brunch_zurich'
          | 'evening_ausgang'
          | 'evening_campus_ausgang'
          | 'family_visit'
          | 'house_evening'
          | 'official_appointment'
          | 'offsite'
          | 'offsite_overnight'
          | 'on_board'
          | 'on_campus_ausgang'
          | 'other'
          | 'programme'
          | 'prolonged_ausgang'
          | 'school_trip'
          | 'specialist_doctor_appointment'
          | 'term_break'
          | 'weekend_programme'
        )[]
        /**
         * * `ausgang` - Ausgang
         * * `external_exam` - External exam
         * * `official` - Official
         * * `school_programme` - School Programme
         * * `extra_ausgang` - Extra Ausgang
         */
        campus_absence_type?: (
          | 'ausgang'
          | 'external_exam'
          | 'extra_ausgang'
          | 'official'
          | 'school_programme'
        )[]
        /** @format date-time */
        date_after?: string
        /** @format date-time */
        date_before?: string
        /**
         * Ordering
         *
         * * `date_from` - Date from
         * * `-date_from` - Date from (descending)
         */
        ordering?: ('-date_from' | 'date_from')[]
        /** A search term. */
        search?: string
        /**
         * * `requested` - Pending
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `canceled_by_student` - Canceled by student
         * * `done` - Done
         * * `cancelled` - Cancelled
         */
        status?: (
          | 'approved'
          | 'canceled_by_student'
          | 'cancelled'
          | 'done'
          | 'rejected'
          | 'requested'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentCampusAbsenceStatus[], T>, any>({
        path: `/student/campus-absences/statuses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentCampusAbsencesSubtypeChoicesRetrieve
     * @request GET:/student/campus-absences/subtype-choices/
     * @secure
     */,
    studentCampusAbsencesSubtypeChoicesRetrieve: <
      T extends FetchKeys<StudentCampusAbsenceSubtype>
    >(
      query?: {
        /**
         * * `afternoon_ausgang` - Afternoon Ausgang
         * * `evening_ausgang` - Evening Ausgang
         * * `evening_campus_ausgang` - Evening - Campus Ausgang
         * * `birthday_ausgang` - Birthday Ausgang
         * * `family_visit` - Family visit
         * * `on_board` - On board Ausgang
         * * `prolonged_ausgang` - Prolonged Ausgang
         * * `campus` - Campus
         * * `offsite` - Offsite
         * * `offsite_overnight` - Offsite, Overnight
         * * `official_appointment` - Official appointment
         * * `specialist_doctor_appointment` - Specialist Doctor Appointment
         * * `programme` - Programme
         * * `house_evening` - House evening
         * * `term_break` - Term Break
         * * `school_trip` - School Trip
         * * `weekend_programme` - Weekend Programme
         * * `other` - Other
         * * `book_the_cafe` - Book the Cafe
         * * `city_brunch_zurich` - City Brunch Zurich
         * * `additional_ausgang` - Additional Ausgang
         * * `on_campus_ausgang` - On Campus Ausgang
         */
        campus_absence_subtype?: (
          | 'additional_ausgang'
          | 'afternoon_ausgang'
          | 'birthday_ausgang'
          | 'book_the_cafe'
          | 'campus'
          | 'city_brunch_zurich'
          | 'evening_ausgang'
          | 'evening_campus_ausgang'
          | 'family_visit'
          | 'house_evening'
          | 'official_appointment'
          | 'offsite'
          | 'offsite_overnight'
          | 'on_board'
          | 'on_campus_ausgang'
          | 'other'
          | 'programme'
          | 'prolonged_ausgang'
          | 'school_trip'
          | 'specialist_doctor_appointment'
          | 'term_break'
          | 'weekend_programme'
        )[]
        /**
         * * `ausgang` - Ausgang
         * * `external_exam` - External exam
         * * `official` - Official
         * * `school_programme` - School Programme
         * * `extra_ausgang` - Extra Ausgang
         */
        campus_absence_type?: (
          | 'ausgang'
          | 'external_exam'
          | 'extra_ausgang'
          | 'official'
          | 'school_programme'
        )[]
        /** @format date-time */
        date_after?: string
        /** @format date-time */
        date_before?: string
        /**
         * Ordering
         *
         * * `date_from` - Date from
         * * `-date_from` - Date from (descending)
         */
        ordering?: ('-date_from' | 'date_from')[]
        /** A search term. */
        search?: string
        /**
         * * `requested` - Pending
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `canceled_by_student` - Canceled by student
         * * `done` - Done
         * * `cancelled` - Cancelled
         */
        status?: (
          | 'approved'
          | 'canceled_by_student'
          | 'cancelled'
          | 'done'
          | 'rejected'
          | 'requested'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentCampusAbsenceSubtype, T>, any>({
        path: `/student/campus-absences/subtype-choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentCampusAbsencesTypeChoicesList
     * @request GET:/student/campus-absences/type-choices/
     * @secure
     */,
    studentCampusAbsencesTypeChoicesList: <
      T extends FetchKeys<StudentCampusAbsenceType[]>
    >(
      query?: {
        /**
         * * `afternoon_ausgang` - Afternoon Ausgang
         * * `evening_ausgang` - Evening Ausgang
         * * `evening_campus_ausgang` - Evening - Campus Ausgang
         * * `birthday_ausgang` - Birthday Ausgang
         * * `family_visit` - Family visit
         * * `on_board` - On board Ausgang
         * * `prolonged_ausgang` - Prolonged Ausgang
         * * `campus` - Campus
         * * `offsite` - Offsite
         * * `offsite_overnight` - Offsite, Overnight
         * * `official_appointment` - Official appointment
         * * `specialist_doctor_appointment` - Specialist Doctor Appointment
         * * `programme` - Programme
         * * `house_evening` - House evening
         * * `term_break` - Term Break
         * * `school_trip` - School Trip
         * * `weekend_programme` - Weekend Programme
         * * `other` - Other
         * * `book_the_cafe` - Book the Cafe
         * * `city_brunch_zurich` - City Brunch Zurich
         * * `additional_ausgang` - Additional Ausgang
         * * `on_campus_ausgang` - On Campus Ausgang
         */
        campus_absence_subtype?: (
          | 'additional_ausgang'
          | 'afternoon_ausgang'
          | 'birthday_ausgang'
          | 'book_the_cafe'
          | 'campus'
          | 'city_brunch_zurich'
          | 'evening_ausgang'
          | 'evening_campus_ausgang'
          | 'family_visit'
          | 'house_evening'
          | 'official_appointment'
          | 'offsite'
          | 'offsite_overnight'
          | 'on_board'
          | 'on_campus_ausgang'
          | 'other'
          | 'programme'
          | 'prolonged_ausgang'
          | 'school_trip'
          | 'specialist_doctor_appointment'
          | 'term_break'
          | 'weekend_programme'
        )[]
        /**
         * * `ausgang` - Ausgang
         * * `external_exam` - External exam
         * * `official` - Official
         * * `school_programme` - School Programme
         * * `extra_ausgang` - Extra Ausgang
         */
        campus_absence_type?: (
          | 'ausgang'
          | 'external_exam'
          | 'extra_ausgang'
          | 'official'
          | 'school_programme'
        )[]
        /** @format date-time */
        date_after?: string
        /** @format date-time */
        date_before?: string
        /**
         * Ordering
         *
         * * `date_from` - Date from
         * * `-date_from` - Date from (descending)
         */
        ordering?: ('-date_from' | 'date_from')[]
        /** A search term. */
        search?: string
        /**
         * * `requested` - Pending
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `canceled_by_student` - Canceled by student
         * * `done` - Done
         * * `cancelled` - Cancelled
         */
        status?: (
          | 'approved'
          | 'canceled_by_student'
          | 'cancelled'
          | 'done'
          | 'rejected'
          | 'requested'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentCampusAbsenceType[], T>, any>({
        path: `/student/campus-absences/type-choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentCategoryRetrieve
     * @request GET:/student/category/
     * @secure
     */,
    studentCategoryRetrieve: <T extends FetchKeys<StudentCategoryTransition>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentCategoryTransition, T>, any>({
        path: `/student/category/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentCoursesList
     * @request GET:/student/courses/
     * @secure
     */,
    studentCoursesList: <T extends FetchKeys<PaginatedStudentCoursesList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentCoursesList, T>, any>({
        path: `/student/courses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentCoursesRetrieve
     * @request GET:/student/courses/{courseId}/
     * @secure
     */,
    studentCoursesRetrieve: <T extends FetchKeys<StudentCourses>>(
      courseId: string,
      query?: {
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentCourses, T>, any>({
        path: `/student/courses/${courseId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentCoursesYearsList
     * @request GET:/student/courses/years/
     * @secure
     */,
    studentCoursesYearsList: <T extends FetchKeys<StudentCourseYearResponse[]>>(
      query?: {
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentCourseYearResponse[], T>, any>({
        path: `/student/courses/years/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentFavouritesList
     * @request GET:/student/favourites/
     * @secure
     */,
    studentFavouritesList: <T extends FetchKeys<FavouriteWidget[]>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FavouriteWidget[], T>, any>({
        path: `/student/favourites/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentFilesForStudentsList
     * @request GET:/student/files-for-students/
     * @secure
     */,
    studentFilesForStudentsList: <
      T extends FetchKeys<PaginatedStudentsFileList>
    >(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentsFileList, T>, any>({
        path: `/student/files-for-students/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentImagesCreate
     * @request POST:/student/images/
     * @secure
     */,
    studentImagesCreate: <T extends FetchKeys<Image>>(
      data: ImageRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Image, T>, any>({
        path: `/student/images/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.FormData,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentInfoboardsFeedList
     * @request GET:/student/infoboards/feed/
     * @secure
     */,
    studentInfoboardsFeedList: <T extends FetchKeys<PaginatedFeedList>>(
      query?: {
        include_widgets?: (
          | 'BoardingAnnouncementWidget'
          | 'CelebrationWidget'
          | 'CongratulationsWidget'
          | 'EventAgendaWidget'
          | 'EventInfoWidget'
          | 'EventInfoWithPhotoWidget'
          | 'InfoWithVideoWidget'
          | 'JournalWidget'
          | 'MenuWidget'
          | 'PosterWidget'
          | 'StudentArtGalleryWidget'
          | 'VariantManualCongratulationsWidget'
          | 'all'
        )[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedFeedList, T>, any>({
        path: `/student/infoboards/feed/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentInfoboardsMessagesList
     * @request GET:/student/infoboards/messages/
     * @secure
     */,
    studentInfoboardsMessagesList: <
      T extends FetchKeys<PaginatedStudentMessageList>
    >(
      query?: {
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentMessageList, T>, any>({
        path: `/student/infoboards/messages/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentInfoboardsMessagesRetrieve
     * @request GET:/student/infoboards/messages/{id}/
     * @secure
     */,
    studentInfoboardsMessagesRetrieve: <T extends FetchKeys<StudentMessage>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentMessage, T>, any>({
        path: `/student/infoboards/messages/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentInfoboardsPostsList
     * @request GET:/student/infoboards/posts/
     * @secure
     */,
    studentInfoboardsPostsList: <
      T extends FetchKeys<PaginatedStudentContentList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentContentList, T>, any>({
        path: `/student/infoboards/posts/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentInfoboardsPostsCreate
     * @request POST:/student/infoboards/posts/
     * @secure
     */,
    studentInfoboardsPostsCreate: <T extends FetchKeys<StudentContent>>(
      data: StudentContentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentContent, T>, any>({
        path: `/student/infoboards/posts/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentInfoboardsPostsRetrieve
     * @request GET:/student/infoboards/posts/{id}/
     * @secure
     */,
    studentInfoboardsPostsRetrieve: <T extends FetchKeys<StudentContent>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentContent, T>, any>({
        path: `/student/infoboards/posts/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentInfoboardsPostsUpdate
     * @request PUT:/student/infoboards/posts/{id}/
     * @secure
     */,
    studentInfoboardsPostsUpdate: <T extends FetchKeys<StudentContent>>(
      id: string,
      data: StudentContentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentContent, T>, any>({
        path: `/student/infoboards/posts/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentInfoboardsPostsPartialUpdate
     * @request PATCH:/student/infoboards/posts/{id}/
     * @secure
     */,
    studentInfoboardsPostsPartialUpdate: <T extends FetchKeys<StudentContent>>(
      id: string,
      data: PatchedStudentContentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentContent, T>, any>({
        path: `/student/infoboards/posts/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentInfoboardsPostsDestroy
     * @request DELETE:/student/infoboards/posts/{id}/
     * @secure
     */,
    studentInfoboardsPostsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/student/infoboards/posts/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentInfoboardsPostsPostsInfoRetrieve
     * @request GET:/student/infoboards/posts/posts-info/
     * @secure
     */,
    studentInfoboardsPostsPostsInfoRetrieve: <T extends FetchKeys<PostsInfo>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PostsInfo, T>, any>({
        path: `/student/infoboards/posts/posts-info/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentLeavesList
     * @request GET:/student/leaves/
     * @secure
     */,
    studentLeavesList: <
      T extends FetchKeys<PaginatedStudentLeaveRequestSwaggerList>
    >(
      query?: {
        /** @format date-time */
        date_after?: string
        /** @format date-time */
        date_before?: string
        /**
         * * `0` - Weekend
         * * `1` - Holiday
         * * `2` - Other
         * * `3` - Arrival
         */
        leave_type?: (0 | 1 | 2 | 3)[]
        /**
         * Ordering
         *
         * * `order_date` - Order date
         * * `-order_date` - Order date (descending)
         */
        ordering?: ('-order_date' | 'order_date')[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /**
         * * `tentative` - Pending guardian
         * * `confirmed_contact_person` - Pending academic
         * * `confirmed_academic` - Pending boarding
         * * `changed` - Changed
         * * `confirmed` - Approved
         * * `declined` - Rejected
         * * `declined_contact_person` - Rejected by guardian
         * * `declined_academic` - Rejected by academic
         * * `canceled` - Canceled by student
         * * `expired` - Expired
         */
        status?: (
          | 'canceled'
          | 'changed'
          | 'confirmed'
          | 'confirmed_academic'
          | 'confirmed_contact_person'
          | 'declined'
          | 'declined_academic'
          | 'declined_contact_person'
          | 'expired'
          | 'tentative'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentLeaveRequestSwaggerList, T>, any>({
        path: `/student/leaves/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentLeavesCreate
     * @request POST:/student/leaves/
     * @secure
     */,
    studentLeavesCreate: <T extends FetchKeys<StudentLeaveRequest>>(
      data: StudentLeaveRequestRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentLeaveRequest, T>, any>({
        path: `/student/leaves/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentLeavesRetrieve
     * @request GET:/student/leaves/{id}/
     * @secure
     */,
    studentLeavesRetrieve: <T extends FetchKeys<StudentLeaveRequestSwagger>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentLeaveRequestSwagger, T>, any>({
        path: `/student/leaves/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentLeavesUpdate
     * @request PUT:/student/leaves/{id}/
     * @secure
     */,
    studentLeavesUpdate: <T extends FetchKeys<StudentLeaveRequest>>(
      id: string,
      data: StudentLeaveRequestRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentLeaveRequest, T>, any>({
        path: `/student/leaves/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentLeavesPartialUpdate
     * @request PATCH:/student/leaves/{id}/
     * @secure
     */,
    studentLeavesPartialUpdate: <T extends FetchKeys<StudentLeaveRequest>>(
      id: string,
      data: PatchedStudentLeaveRequestRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentLeaveRequest, T>, any>({
        path: `/student/leaves/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentLeavesDestroy
     * @request DELETE:/student/leaves/{id}/
     * @secure
     */,
    studentLeavesDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/student/leaves/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description This method is deprecated. Please use /student/students/clashes-number/
     *
     * @tags student
     * @name StudentLeavesCheckClashesCreate
     * @request POST:/student/leaves/check-clashes/
     * @deprecated
     * @secure
     */,
    studentLeavesCheckClashesCreate: <
      T extends FetchKeys<StudentClashesNumber>
    >(
      data: StudentClashesNumberRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentClashesNumber, T>, any>({
        path: `/student/leaves/check-clashes/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentLeavesLeavesSummaryRetrieve
     * @request GET:/student/leaves/leaves-summary/
     * @secure
     */,
    studentLeavesLeavesSummaryRetrieve: <
      T extends FetchKeys<StudentLeavesSummarySwaggerList>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentLeavesSummarySwaggerList, T>, any>({
        path: `/student/leaves/leaves-summary/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentLeavesStatusesList
     * @request GET:/student/leaves/statuses/
     * @secure
     */,
    studentLeavesStatusesList: <
      T extends FetchKeys<StudentLeaveRequestStatus[]>
    >(
      query?: {
        /** @format date-time */
        date_after?: string
        /** @format date-time */
        date_before?: string
        /**
         * * `0` - Weekend
         * * `1` - Holiday
         * * `2` - Other
         * * `3` - Arrival
         */
        leave_type?: (0 | 1 | 2 | 3)[]
        /**
         * Ordering
         *
         * * `order_date` - Order date
         * * `-order_date` - Order date (descending)
         */
        ordering?: ('-order_date' | 'order_date')[]
        /** A search term. */
        search?: string
        /**
         * * `tentative` - Pending guardian
         * * `confirmed_contact_person` - Pending academic
         * * `confirmed_academic` - Pending boarding
         * * `changed` - Changed
         * * `confirmed` - Approved
         * * `declined` - Rejected
         * * `declined_contact_person` - Rejected by guardian
         * * `declined_academic` - Rejected by academic
         * * `canceled` - Canceled by student
         * * `expired` - Expired
         */
        status?: (
          | 'canceled'
          | 'changed'
          | 'confirmed'
          | 'confirmed_academic'
          | 'confirmed_contact_person'
          | 'declined'
          | 'declined_academic'
          | 'declined_contact_person'
          | 'expired'
          | 'tentative'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentLeaveRequestStatus[], T>, any>({
        path: `/student/leaves/statuses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentLeavesTypeChoicesList
     * @request GET:/student/leaves/type-choices/
     * @secure
     */,
    studentLeavesTypeChoicesList: <
      T extends FetchKeys<StudentLeaveRequestType[]>
    >(
      query?: {
        /** @format date-time */
        date_after?: string
        /** @format date-time */
        date_before?: string
        /**
         * * `0` - Weekend
         * * `1` - Holiday
         * * `2` - Other
         * * `3` - Arrival
         */
        leave_type?: (0 | 1 | 2 | 3)[]
        /**
         * Ordering
         *
         * * `order_date` - Order date
         * * `-order_date` - Order date (descending)
         */
        ordering?: ('-order_date' | 'order_date')[]
        /** A search term. */
        search?: string
        /**
         * * `tentative` - Pending guardian
         * * `confirmed_contact_person` - Pending academic
         * * `confirmed_academic` - Pending boarding
         * * `changed` - Changed
         * * `confirmed` - Approved
         * * `declined` - Rejected
         * * `declined_contact_person` - Rejected by guardian
         * * `declined_academic` - Rejected by academic
         * * `canceled` - Canceled by student
         * * `expired` - Expired
         */
        status?: (
          | 'canceled'
          | 'changed'
          | 'confirmed'
          | 'confirmed_academic'
          | 'confirmed_contact_person'
          | 'declined'
          | 'declined_academic'
          | 'declined_contact_person'
          | 'expired'
          | 'tentative'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentLeaveRequestType[], T>, any>({
        path: `/student/leaves/type-choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentMealAttendanceCreate
     * @request POST:/student/meal-attendance/
     * @secure
     */,
    studentMealAttendanceCreate: <T extends FetchKeys<void>>(
      data: StudentMealSkipCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/student/meal-attendance/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentMealAttendanceStatusRetrieve
     * @request GET:/student/meal-attendance/status/
     * @secure
     */,
    studentMealAttendanceStatusRetrieve: <
      T extends FetchKeys<StudentMealAttendanceFormStatus>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentMealAttendanceFormStatus, T>, any>({
        path: `/student/meal-attendance/status/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentMenuList
     * @request GET:/student/menu/
     * @secure
     */,
    studentMenuList: <T extends FetchKeys<PaginatedStudentDayMenuList>>(
      query?: {
        /** @format date */
        date?: string
        is_filled?: boolean
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        week?: number
        year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentDayMenuList, T>, any>({
        path: `/student/menu/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentMeritsList
     * @request GET:/student/merits/
     * @secure
     */,
    studentMeritsList: <T extends FetchKeys<PaginatedStudentMeritListList>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter?: string
        /** A search term. */
        search?: string
        semester?: string
        /**
         * * `merit` - Merit
         * * `demerit` - Demerit
         */
        type?: 'demerit' | 'merit'
        week?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentMeritListList, T>, any>({
        path: `/student/merits/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentMeritsExchangeList
     * @request GET:/student/merits-exchange/
     * @secure
     */,
    studentMeritsExchangeList: <
      T extends FetchKeys<PaginatedStudentMeritRewardList>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /**
         * * `pending` - Pending
         * * `pending_attendees` - Pending attendees
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `done` - Done
         * * `cancelled` - Cancelled
         * * `expired` - Expired
         */
        status?:
          | 'approved'
          | 'cancelled'
          | 'done'
          | 'expired'
          | 'pending'
          | 'pending_attendees'
          | 'rejected'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentMeritRewardList, T>, any>({
        path: `/student/merits-exchange/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentMeritsExchangeCreate
     * @request POST:/student/merits-exchange/
     * @secure
     */,
    studentMeritsExchangeCreate: <T extends FetchKeys<StudentMeritReward>>(
      data: StudentMeritRewardRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentMeritReward, T>, any>({
        path: `/student/merits-exchange/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentMeritsExchangeRetrieve
     * @request GET:/student/merits-exchange/{id}/
     * @secure
     */,
    studentMeritsExchangeRetrieve: <T extends FetchKeys<StudentMeritReward>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentMeritReward, T>, any>({
        path: `/student/merits-exchange/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentMeritsExchangeDestroy
     * @request DELETE:/student/merits-exchange/{id}/
     * @secure
     */,
    studentMeritsExchangeDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/student/merits-exchange/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentMeritsExchangeAvailableAusgangsList
     * @request GET:/student/merits-exchange/available-ausgangs/
     * @secure
     */,
    studentMeritsExchangeAvailableAusgangsList: <
      T extends FetchKeys<SimpleStudentCampusAbsence[]>
    >(
      query?: {
        /**
         * * `pending` - Pending
         * * `pending_attendees` - Pending attendees
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `done` - Done
         * * `cancelled` - Cancelled
         * * `expired` - Expired
         */
        status?:
          | 'approved'
          | 'cancelled'
          | 'done'
          | 'expired'
          | 'pending'
          | 'pending_attendees'
          | 'rejected'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SimpleStudentCampusAbsence[], T>, any>({
        path: `/student/merits-exchange/available-ausgangs/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentMeritsExchangeAvailableStudentsList
     * @request GET:/student/merits-exchange/available-students/
     * @secure
     */,
    studentMeritsExchangeAvailableStudentsList: <
      T extends FetchKeys<AvailableStudents[]>
    >(
      query: {
        resourcetype: string
        /**
         * * `pending` - Pending
         * * `pending_attendees` - Pending attendees
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `done` - Done
         * * `cancelled` - Cancelled
         * * `expired` - Expired
         */
        status?:
          | 'approved'
          | 'cancelled'
          | 'done'
          | 'expired'
          | 'pending'
          | 'pending_attendees'
          | 'rejected'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AvailableStudents[], T>, any>({
        path: `/student/merits-exchange/available-students/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentMeritsExchangeParticipantsUpdate
     * @request PUT:/student/merits-exchange/participants/{id}/
     * @secure
     */,
    studentMeritsExchangeParticipantsUpdate: <
      T extends FetchKeys<MeritRewardParticipantUpdate>
    >(
      id: number,
      data: MeritRewardParticipantUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritRewardParticipantUpdate, T>, any>({
        path: `/student/merits-exchange/participants/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentMeritsExchangeParticipantsPartialUpdate
     * @request PATCH:/student/merits-exchange/participants/{id}/
     * @secure
     */,
    studentMeritsExchangeParticipantsPartialUpdate: <
      T extends FetchKeys<MeritRewardParticipantUpdate>
    >(
      id: number,
      data: PatchedMeritRewardParticipantUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritRewardParticipantUpdate, T>, any>({
        path: `/student/merits-exchange/participants/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentMeritsExchangeRewardTypesList
     * @request GET:/student/merits-exchange/reward-types/
     * @secure
     */,
    studentMeritsExchangeRewardTypesList: <T extends FetchKeys<RewardType[]>>(
      query?: {
        /**
         * * `pending` - Pending
         * * `pending_attendees` - Pending attendees
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `done` - Done
         * * `cancelled` - Cancelled
         * * `expired` - Expired
         */
        status?:
          | 'approved'
          | 'cancelled'
          | 'done'
          | 'expired'
          | 'pending'
          | 'pending_attendees'
          | 'rejected'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RewardType[], T>, any>({
        path: `/student/merits-exchange/reward-types/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentMeritsExchangeStatusCountsRetrieve
     * @request GET:/student/merits-exchange/status-counts/
     * @secure
     */,
    studentMeritsExchangeStatusCountsRetrieve: <
      T extends FetchKeys<MeritRewardStatusCounts>
    >(
      query?: {
        /**
         * * `pending` - Pending
         * * `pending_attendees` - Pending attendees
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `done` - Done
         * * `cancelled` - Cancelled
         * * `expired` - Expired
         */
        status?:
          | 'approved'
          | 'cancelled'
          | 'done'
          | 'expired'
          | 'pending'
          | 'pending_attendees'
          | 'rejected'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<MeritRewardStatusCounts, T>, any>({
        path: `/student/merits-exchange/status-counts/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentMeritsRetrieve
     * @request GET:/student/merits/{id}/
     * @secure
     */,
    studentMeritsRetrieve: <T extends FetchKeys<StudentMeritRetrieve>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentMeritRetrieve, T>, any>({
        path: `/student/merits/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentMeritsCountRetrieve
     * @request GET:/student/merits/count/
     * @secure
     */,
    studentMeritsCountRetrieve: <T extends FetchKeys<StudentMeritCount>>(
      query?: {
        quarter?: string
        /** A search term. */
        search?: string
        semester?: string
        /**
         * * `merit` - Merit
         * * `demerit` - Demerit
         */
        type?: 'demerit' | 'merit'
        week?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentMeritCount, T>, any>({
        path: `/student/merits/count/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentSchoolProgrammesList
     * @request GET:/student/school-programmes/
     * @secure
     */,
    studentSchoolProgrammesList: <
      T extends FetchKeys<PaginatedStudentSchoolProgrammeListList>
    >(
      query?: {
        can_enrol?: boolean
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /**
         * * `pending` - Pending
         * * `requested` - Requested
         * * `rejected` - Rejected
         * * `approved` - Approved
         * * `leave_absent` - Leave - Absent
         */
        enrolment_status?: (
          | 'approved'
          | 'leave_absent'
          | 'pending'
          | 'rejected'
          | 'requested'
        )[]
        my_programmes?: boolean
        /** @format date */
        next_session_date_after?: string
        /** @format date */
        next_session_date_before?: string
        /**
         * Ordering
         *
         * * `start_date` - Start date
         * * `-start_date` - Start date (descending)
         * * `next_session_date` - Next session date
         * * `-next_session_date` - Next session date (descending)
         */
        ordering?: (
          | '-next_session_date'
          | '-start_date'
          | 'next_session_date'
          | 'start_date'
        )[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /**
         * * `requested` - Requested
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `cancelled` - Cancelled
         */
        programme_status?: (
          | 'approved'
          | 'cancelled'
          | 'rejected'
          | 'requested'
        )[]
        /** A search term. */
        search?: string
        /**
         * * `academics` - Academics
         * * `house_evening` - House Evening
         * * `term_break` - Term Break
         * * `school_trip` - School Trip
         * * `weekend_programme` - Weekend Programme
         * * `exam_preparation` - Exam Preparation
         * * `other` - Other
         * * `absence_recovery` - Absence Recovery
         * * `lunchtime_recovery` - Lunchtime Recovery
         */
        type?: (
          | 'absence_recovery'
          | 'academics'
          | 'exam_preparation'
          | 'house_evening'
          | 'lunchtime_recovery'
          | 'other'
          | 'school_trip'
          | 'term_break'
          | 'weekend_programme'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentSchoolProgrammeListList, T>, any>({
        path: `/student/school-programmes/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentSchoolProgrammesRetrieve
     * @request GET:/student/school-programmes/{id}/
     * @secure
     */,
    studentSchoolProgrammesRetrieve: <
      T extends FetchKeys<StudentSchoolProgramme>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentSchoolProgramme, T>, any>({
        path: `/student/school-programmes/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentSchoolProgrammesEnrolInCreate
     * @request POST:/student/school-programmes/{id}/enrol-in/
     * @secure
     */,
    studentSchoolProgrammesEnrolInCreate: <
      T extends FetchKeys<StudentSchoolProgrammeList>
    >(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentSchoolProgrammeList, T>, any>({
        path: `/student/school-programmes/${id}/enrol-in/`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentSchoolProgrammesEnrolmentStatusChoicesList
     * @request GET:/student/school-programmes/enrolment-status-choices/
     * @secure
     */,
    studentSchoolProgrammesEnrolmentStatusChoicesList: <
      T extends FetchKeys<StudentProgrammeEnrollmentStatusSwagger[]>
    >(
      query?: {
        can_enrol?: boolean
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /**
         * * `pending` - Pending
         * * `requested` - Requested
         * * `rejected` - Rejected
         * * `approved` - Approved
         * * `leave_absent` - Leave - Absent
         */
        enrolment_status?: (
          | 'approved'
          | 'leave_absent'
          | 'pending'
          | 'rejected'
          | 'requested'
        )[]
        my_programmes?: boolean
        /** @format date */
        next_session_date_after?: string
        /** @format date */
        next_session_date_before?: string
        /**
         * Ordering
         *
         * * `start_date` - Start date
         * * `-start_date` - Start date (descending)
         * * `next_session_date` - Next session date
         * * `-next_session_date` - Next session date (descending)
         */
        ordering?: (
          | '-next_session_date'
          | '-start_date'
          | 'next_session_date'
          | 'start_date'
        )[]
        /**
         * * `requested` - Requested
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `cancelled` - Cancelled
         */
        programme_status?: (
          | 'approved'
          | 'cancelled'
          | 'rejected'
          | 'requested'
        )[]
        /** A search term. */
        search?: string
        /**
         * * `academics` - Academics
         * * `house_evening` - House Evening
         * * `term_break` - Term Break
         * * `school_trip` - School Trip
         * * `weekend_programme` - Weekend Programme
         * * `exam_preparation` - Exam Preparation
         * * `other` - Other
         * * `absence_recovery` - Absence Recovery
         * * `lunchtime_recovery` - Lunchtime Recovery
         */
        type?: (
          | 'absence_recovery'
          | 'academics'
          | 'exam_preparation'
          | 'house_evening'
          | 'lunchtime_recovery'
          | 'other'
          | 'school_trip'
          | 'term_break'
          | 'weekend_programme'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentProgrammeEnrollmentStatusSwagger[], T>, any>(
        {
          path: `/student/school-programmes/enrolment-status-choices/`,
          method: 'GET',
          query: postprocessQuery(query),
          secure: true,
          format: 'json',
          ...params
        }
      )
    /**
     * No description
     *
     * @tags student
     * @name StudentSchoolProgrammesRecurringRetrieve
     * @request GET:/student/school-programmes/recurring/{id}/
     * @secure
     */,
    studentSchoolProgrammesRecurringRetrieve: <
      T extends FetchKeys<StudentRecurringProgramme>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentRecurringProgramme, T>, any>({
        path: `/student/school-programmes/recurring/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentSchoolProgrammesRecurringEnrollCreate
     * @request POST:/student/school-programmes/recurring/{id}/enroll/
     * @secure
     */,
    studentSchoolProgrammesRecurringEnrollCreate: <
      T extends FetchKeys<StudentRecurringProgramme>
    >(
      id: number,
      data: StudentRecurringProgrammeRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentRecurringProgramme, T>, any>({
        path: `/student/school-programmes/recurring/${id}/enroll/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentSchoolProgrammesRecurringSessionsList
     * @request GET:/student/school-programmes/recurring/{recurringId}/sessions/
     * @secure
     */,
    studentSchoolProgrammesRecurringSessionsList: <
      T extends FetchKeys<PaginatedStudentRecurringProgrammeSessionList>
    >(
      recurringId: string,
      query?: {
        /**
         * * `upcoming` - Upcoming
         * * `past` - Past
         */
        date_status?: 'past' | 'upcoming'
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<PaginatedStudentRecurringProgrammeSessionList, T>,
        any
      >({
        path: `/student/school-programmes/recurring/${recurringId}/sessions/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentSchoolProgrammesStatusChoicesRetrieve
     * @request GET:/student/school-programmes/status-choices/
     * @secure
     */,
    studentSchoolProgrammesStatusChoicesRetrieve: <
      T extends FetchKeys<StudentSchoolProgrammeStatusSwagger>
    >(
      query?: {
        can_enrol?: boolean
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /**
         * * `pending` - Pending
         * * `requested` - Requested
         * * `rejected` - Rejected
         * * `approved` - Approved
         * * `leave_absent` - Leave - Absent
         */
        enrolment_status?: (
          | 'approved'
          | 'leave_absent'
          | 'pending'
          | 'rejected'
          | 'requested'
        )[]
        my_programmes?: boolean
        /** @format date */
        next_session_date_after?: string
        /** @format date */
        next_session_date_before?: string
        /**
         * Ordering
         *
         * * `start_date` - Start date
         * * `-start_date` - Start date (descending)
         * * `next_session_date` - Next session date
         * * `-next_session_date` - Next session date (descending)
         */
        ordering?: (
          | '-next_session_date'
          | '-start_date'
          | 'next_session_date'
          | 'start_date'
        )[]
        /**
         * * `requested` - Requested
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `cancelled` - Cancelled
         */
        programme_status?: (
          | 'approved'
          | 'cancelled'
          | 'rejected'
          | 'requested'
        )[]
        /** A search term. */
        search?: string
        /**
         * * `academics` - Academics
         * * `house_evening` - House Evening
         * * `term_break` - Term Break
         * * `school_trip` - School Trip
         * * `weekend_programme` - Weekend Programme
         * * `exam_preparation` - Exam Preparation
         * * `other` - Other
         * * `absence_recovery` - Absence Recovery
         * * `lunchtime_recovery` - Lunchtime Recovery
         */
        type?: (
          | 'absence_recovery'
          | 'academics'
          | 'exam_preparation'
          | 'house_evening'
          | 'lunchtime_recovery'
          | 'other'
          | 'school_trip'
          | 'term_break'
          | 'weekend_programme'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentSchoolProgrammeStatusSwagger, T>, any>({
        path: `/student/school-programmes/status-choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentSchoolProgrammesTypeChoicesList
     * @request GET:/student/school-programmes/type-choices/
     * @secure
     */,
    studentSchoolProgrammesTypeChoicesList: <
      T extends FetchKeys<StudentSchoolProgrammeTypeSwagger[]>
    >(
      query?: {
        can_enrol?: boolean
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /**
         * * `pending` - Pending
         * * `requested` - Requested
         * * `rejected` - Rejected
         * * `approved` - Approved
         * * `leave_absent` - Leave - Absent
         */
        enrolment_status?: (
          | 'approved'
          | 'leave_absent'
          | 'pending'
          | 'rejected'
          | 'requested'
        )[]
        my_programmes?: boolean
        /** @format date */
        next_session_date_after?: string
        /** @format date */
        next_session_date_before?: string
        /**
         * Ordering
         *
         * * `start_date` - Start date
         * * `-start_date` - Start date (descending)
         * * `next_session_date` - Next session date
         * * `-next_session_date` - Next session date (descending)
         */
        ordering?: (
          | '-next_session_date'
          | '-start_date'
          | 'next_session_date'
          | 'start_date'
        )[]
        /**
         * * `requested` - Requested
         * * `approved` - Approved
         * * `rejected` - Rejected
         * * `cancelled` - Cancelled
         */
        programme_status?: (
          | 'approved'
          | 'cancelled'
          | 'rejected'
          | 'requested'
        )[]
        /** A search term. */
        search?: string
        /**
         * * `academics` - Academics
         * * `house_evening` - House Evening
         * * `term_break` - Term Break
         * * `school_trip` - School Trip
         * * `weekend_programme` - Weekend Programme
         * * `exam_preparation` - Exam Preparation
         * * `other` - Other
         * * `absence_recovery` - Absence Recovery
         * * `lunchtime_recovery` - Lunchtime Recovery
         */
        type?: (
          | 'absence_recovery'
          | 'academics'
          | 'exam_preparation'
          | 'house_evening'
          | 'lunchtime_recovery'
          | 'other'
          | 'school_trip'
          | 'term_break'
          | 'weekend_programme'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentSchoolProgrammeTypeSwagger[], T>, any>({
        path: `/student/school-programmes/type-choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags student
     * @name StudentSchoolProgrammesUpcomingSessionRetrieve
     * @request GET:/student/school-programmes/upcoming-session/
     * @secure
     */,
    studentSchoolProgrammesUpcomingSessionRetrieve: <
      T extends FetchKeys<StudentSchoolProgrammeUpcomingSession>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentSchoolProgrammeUpcomingSession, T>, any>({
        path: `/student/school-programmes/upcoming-session/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentStudentsList
     * @request GET:/student/students/
     * @secure
     */,
    studentStudentsList: <T extends FetchKeys<StudentBasic[]>>(
      query?: {
        hide_logged_student?: boolean

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentBasic[], T>, any>({
        path: `/student/students/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentStudentsRetrieve
     * @request GET:/student/students/{id}/
     * @secure
     */,
    studentStudentsRetrieve: <T extends FetchKeys<StudentBasic>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentBasic, T>, any>({
        path: `/student/students/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentStudentsClashesNumberCreate
     * @request POST:/student/students/clashes-number/
     * @secure
     */,
    studentStudentsClashesNumberCreate: <
      T extends FetchKeys<StudentClashesNumber>
    >(
      data: CheckClashesRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentClashesNumber, T>, any>({
        path: `/student/students/clashes-number/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentSurveysResponseCreate
     * @request POST:/student/surveys/response/
     * @secure
     */,
    studentSurveysResponseCreate: <T extends FetchKeys<StudentSurveyResponse>>(
      data: StudentSurveyResponseRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentSurveyResponse, T>, any>({
        path: `/student/surveys/response/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentTaeCreate
     * @request POST:/student/tae/
     * @secure
     */,
    studentTaeCreate: <T extends FetchKeys<StudentTAEEnrolmentCreate>>(
      data: StudentTAEEnrolmentCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTAEEnrolmentCreate, T>, any>({
        path: `/student/tae/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentTaeV2Create
     * @request POST:/student/tae-v2/
     * @secure
     */,
    studentTaeV2Create: <T extends FetchKeys<StudentEnrolmentCreate>>(
      data: StudentEnrolmentCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentEnrolmentCreate, T>, any>({
        path: `/student/tae-v2/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentTaeV2Retrieve
     * @request GET:/student/tae-v2/{id}/
     * @secure
     */,
    studentTaeV2Retrieve: <T extends FetchKeys<StudentTAEStudentEnrolmentV2>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTAEStudentEnrolmentV2, T>, any>({
        path: `/student/tae-v2/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentTaeV2Update
     * @request PUT:/student/tae-v2/{id}/
     * @secure
     */,
    studentTaeV2Update: <T extends FetchKeys<void>>(
      id: number,
      data: StudentEnrolmentUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/student/tae-v2/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentTaeV2CoursesList
     * @request GET:/student/tae-v2/courses/
     * @secure
     */,
    studentTaeV2CoursesList: <T extends FetchKeys<StudentCourseCatalogue[]>>(
      query?: {
        /**
         * * `CCU` - CCU
         * * `ATH` - ATH
         */
        category?: 'ATH' | 'CCU' | null
        /**
         * Ordering
         *
         * * `name` - Name
         * * `-name` - Name (descending)
         */
        ordering?: ('-name' | 'name')[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<StudentCourseCatalogue[], T>,
        StudentCourseCatalogueError
      >({
        path: `/student/tae-v2/courses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentTaeV2StatusRetrieve
     * @request GET:/student/tae-v2/status
     * @secure
     */,
    studentTaeV2StatusRetrieve: <
      T extends FetchKeys<StudentTAEEnrolmentStatusV2>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTAEEnrolmentStatusV2, T>, any>({
        path: `/student/tae-v2/status`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentTaeRetrieve
     * @request GET:/student/tae/{id}/
     * @secure
     */,
    studentTaeRetrieve: <T extends FetchKeys<StudentTAEEnrolment>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTAEEnrolment, T>, any>({
        path: `/student/tae/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentTaeUpdate
     * @request PUT:/student/tae/{id}/
     * @secure
     */,
    studentTaeUpdate: <T extends FetchKeys<StudentTAEEnrolmentUpdate>>(
      id: number,
      data: StudentTAEEnrolmentUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTAEEnrolmentUpdate, T>, any>({
        path: `/student/tae/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentTaeCoursesList
     * @request GET:/student/tae/courses/
     * @secure
     */,
    studentTaeCoursesList: <T extends FetchKeys<StudentTAECourseEnrolment[]>>(
      query?: {
        /**
         * * `ATH` - Sports
         * * `CCU` - Co-curriculars
         */
        category?: 'ATH' | 'CCU' | null
        /** Which field to use when ordering the results. */
        ordering?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTAECourseEnrolment[], T>, any>({
        path: `/student/tae/courses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags student
     * @name StudentTaeStatusRetrieve
     * @request GET:/student/tae/status
     * @secure
     */,
    studentTaeStatusRetrieve: <T extends FetchKeys<StudentTAEEnrolmentStatus>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentTAEEnrolmentStatus, T>, any>({
        path: `/student/tae/status`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  students = {
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsList
     * @request GET:/students/
     * @secure
     */
    studentsList: <T extends FetchKeys<PaginatedStudentListList>>(
      query?: {
        /**
         * * `15` - Enrolled
         * * `30` - Active
         * * `45` - Left
         * * `60` - Alumni
         */
        academic_status?: ('15' | '30' | '45' | '60' | null)[]
        activity__course?: string
        activity__quarter?: number
        activity__school_year?: number
        /** Multiple values may be separated by commas. */
        age?: number[]
        allergen_kinds?: string[]
        birthday_in_week?: boolean
        birthday_today?: boolean
        boarding_status?: string
        campus_location?: string
        campus_status?: string
        campus_status_reason?: string
        career_goal?: string[]
        category?: number
        chronic_condition_types?: string[]
        current_grade_level?: number
        current_location?: string
        /**
         * * `AF` - Afghanistan
         * * `AX` - Åland Islands
         * * `AL` - Albania
         * * `DZ` - Algeria
         * * `AS` - American Samoa
         * * `AD` - Andorra
         * * `AO` - Angola
         * * `AI` - Anguilla
         * * `AQ` - Antarctica
         * * `AG` - Antigua and Barbuda
         * * `AR` - Argentina
         * * `AM` - Armenia
         * * `AW` - Aruba
         * * `AU` - Australia
         * * `AT` - Austria
         * * `AZ` - Azerbaijan
         * * `BS` - Bahamas
         * * `BH` - Bahrain
         * * `BD` - Bangladesh
         * * `BB` - Barbados
         * * `BY` - Belarus
         * * `BE` - Belgium
         * * `BZ` - Belize
         * * `BJ` - Benin
         * * `BM` - Bermuda
         * * `BT` - Bhutan
         * * `BO` - Bolivia
         * * `BQ` - Bonaire, Sint Eustatius and Saba
         * * `BA` - Bosnia and Herzegovina
         * * `BW` - Botswana
         * * `BV` - Bouvet Island
         * * `BR` - Brazil
         * * `IO` - British Indian Ocean Territory
         * * `BN` - Brunei
         * * `BG` - Bulgaria
         * * `BF` - Burkina Faso
         * * `BI` - Burundi
         * * `CV` - Cabo Verde
         * * `KH` - Cambodia
         * * `CM` - Cameroon
         * * `CA` - Canada
         * * `KY` - Cayman Islands
         * * `CF` - Central African Republic
         * * `TD` - Chad
         * * `CL` - Chile
         * * `CN` - China
         * * `CX` - Christmas Island
         * * `CC` - Cocos (Keeling) Islands
         * * `CO` - Colombia
         * * `KM` - Comoros
         * * `CG` - Congo
         * * `CD` - Congo (the Democratic Republic of the)
         * * `CK` - Cook Islands
         * * `CR` - Costa Rica
         * * `CI` - Côte d'Ivoire
         * * `HR` - Croatia
         * * `CU` - Cuba
         * * `CW` - Curaçao
         * * `CY` - Cyprus
         * * `CZ` - Czechia
         * * `DK` - Denmark
         * * `DJ` - Djibouti
         * * `DM` - Dominica
         * * `DO` - Dominican Republic
         * * `EC` - Ecuador
         * * `EG` - Egypt
         * * `SV` - El Salvador
         * * `GQ` - Equatorial Guinea
         * * `ER` - Eritrea
         * * `EE` - Estonia
         * * `SZ` - Eswatini
         * * `ET` - Ethiopia
         * * `FK` - Falkland Islands (Malvinas)
         * * `FO` - Faroe Islands
         * * `FJ` - Fiji
         * * `FI` - Finland
         * * `FR` - France
         * * `GF` - French Guiana
         * * `PF` - French Polynesia
         * * `TF` - French Southern Territories
         * * `GA` - Gabon
         * * `GM` - Gambia
         * * `GE` - Georgia
         * * `DE` - Germany
         * * `GH` - Ghana
         * * `GI` - Gibraltar
         * * `GR` - Greece
         * * `GL` - Greenland
         * * `GD` - Grenada
         * * `GP` - Guadeloupe
         * * `GU` - Guam
         * * `GT` - Guatemala
         * * `GG` - Guernsey
         * * `GN` - Guinea
         * * `GW` - Guinea-Bissau
         * * `GY` - Guyana
         * * `HT` - Haiti
         * * `HM` - Heard Island and McDonald Islands
         * * `VA` - Holy See
         * * `HN` - Honduras
         * * `HK` - Hong Kong
         * * `HU` - Hungary
         * * `IS` - Iceland
         * * `IN` - India
         * * `ID` - Indonesia
         * * `IR` - Iran
         * * `IQ` - Iraq
         * * `IE` - Ireland
         * * `IM` - Isle of Man
         * * `IL` - Israel
         * * `IT` - Italy
         * * `JM` - Jamaica
         * * `JP` - Japan
         * * `JE` - Jersey
         * * `JO` - Jordan
         * * `KZ` - Kazakhstan
         * * `KE` - Kenya
         * * `KI` - Kiribati
         * * `XK` - Kosovo
         * * `KW` - Kuwait
         * * `KG` - Kyrgyzstan
         * * `LA` - Laos
         * * `LV` - Latvia
         * * `LB` - Lebanon
         * * `LS` - Lesotho
         * * `LR` - Liberia
         * * `LY` - Libya
         * * `LI` - Liechtenstein
         * * `LT` - Lithuania
         * * `LU` - Luxembourg
         * * `MO` - Macao
         * * `MG` - Madagascar
         * * `MW` - Malawi
         * * `MY` - Malaysia
         * * `MV` - Maldives
         * * `ML` - Mali
         * * `MT` - Malta
         * * `MH` - Marshall Islands
         * * `MQ` - Martinique
         * * `MR` - Mauritania
         * * `MU` - Mauritius
         * * `YT` - Mayotte
         * * `MX` - Mexico
         * * `FM` - Micronesia
         * * `MD` - Moldova
         * * `MC` - Monaco
         * * `MN` - Mongolia
         * * `ME` - Montenegro
         * * `MS` - Montserrat
         * * `MA` - Morocco
         * * `MZ` - Mozambique
         * * `MM` - Myanmar
         * * `NA` - Namibia
         * * `NR` - Nauru
         * * `NP` - Nepal
         * * `NL` - Netherlands
         * * `NC` - New Caledonia
         * * `NZ` - New Zealand
         * * `NI` - Nicaragua
         * * `NE` - Niger
         * * `NG` - Nigeria
         * * `NU` - Niue
         * * `NF` - Norfolk Island
         * * `KP` - North Korea
         * * `MK` - North Macedonia
         * * `MP` - Northern Mariana Islands
         * * `NO` - Norway
         * * `OM` - Oman
         * * `PK` - Pakistan
         * * `PW` - Palau
         * * `PS` - Palestine, State of
         * * `PA` - Panama
         * * `PG` - Papua New Guinea
         * * `PY` - Paraguay
         * * `PE` - Peru
         * * `PH` - Philippines
         * * `PN` - Pitcairn
         * * `PL` - Poland
         * * `PT` - Portugal
         * * `PR` - Puerto Rico
         * * `QA` - Qatar
         * * `RE` - Réunion
         * * `RO` - Romania
         * * `RU` - Russia
         * * `RW` - Rwanda
         * * `BL` - Saint Barthélemy
         * * `SH` - Saint Helena, Ascension and Tristan da Cunha
         * * `KN` - Saint Kitts and Nevis
         * * `LC` - Saint Lucia
         * * `MF` - Saint Martin (French part)
         * * `PM` - Saint Pierre and Miquelon
         * * `VC` - Saint Vincent and the Grenadines
         * * `WS` - Samoa
         * * `SM` - San Marino
         * * `ST` - Sao Tome and Principe
         * * `SA` - Saudi Arabia
         * * `SN` - Senegal
         * * `RS` - Serbia
         * * `SC` - Seychelles
         * * `SL` - Sierra Leone
         * * `SG` - Singapore
         * * `SX` - Sint Maarten (Dutch part)
         * * `SK` - Slovakia
         * * `SI` - Slovenia
         * * `SB` - Solomon Islands
         * * `SO` - Somalia
         * * `ZA` - South Africa
         * * `GS` - South Georgia and the South Sandwich Islands
         * * `KR` - South Korea
         * * `SS` - South Sudan
         * * `ES` - Spain
         * * `LK` - Sri Lanka
         * * `SD` - Sudan
         * * `SR` - Suriname
         * * `SJ` - Svalbard and Jan Mayen
         * * `SE` - Sweden
         * * `CH` - Switzerland
         * * `SY` - Syria
         * * `TW` - Taiwan
         * * `TJ` - Tajikistan
         * * `TZ` - Tanzania
         * * `TH` - Thailand
         * * `TL` - Timor-Leste
         * * `TG` - Togo
         * * `TK` - Tokelau
         * * `TO` - Tonga
         * * `TT` - Trinidad and Tobago
         * * `TN` - Tunisia
         * * `TR` - Türkiye
         * * `TM` - Turkmenistan
         * * `TC` - Turks and Caicos Islands
         * * `TV` - Tuvalu
         * * `UG` - Uganda
         * * `UA` - Ukraine
         * * `AE` - United Arab Emirates
         * * `GB` - United Kingdom
         * * `UM` - United States Minor Outlying Islands
         * * `US` - United States of America
         * * `UY` - Uruguay
         * * `UZ` - Uzbekistan
         * * `VU` - Vanuatu
         * * `VE` - Venezuela
         * * `VN` - Vietnam
         * * `VG` - Virgin Islands (British)
         * * `VI` - Virgin Islands (U.S.)
         * * `WF` - Wallis and Futuna
         * * `EH` - Western Sahara
         * * `YE` - Yemen
         * * `ZM` - Zambia
         * * `ZW` - Zimbabwe
         */
        degree_destination?: (
          | 'AD'
          | 'AE'
          | 'AF'
          | 'AG'
          | 'AI'
          | 'AL'
          | 'AM'
          | 'AO'
          | 'AQ'
          | 'AR'
          | 'AS'
          | 'AT'
          | 'AU'
          | 'AW'
          | 'AX'
          | 'AZ'
          | 'BA'
          | 'BB'
          | 'BD'
          | 'BE'
          | 'BF'
          | 'BG'
          | 'BH'
          | 'BI'
          | 'BJ'
          | 'BL'
          | 'BM'
          | 'BN'
          | 'BO'
          | 'BQ'
          | 'BR'
          | 'BS'
          | 'BT'
          | 'BV'
          | 'BW'
          | 'BY'
          | 'BZ'
          | 'CA'
          | 'CC'
          | 'CD'
          | 'CF'
          | 'CG'
          | 'CH'
          | 'CI'
          | 'CK'
          | 'CL'
          | 'CM'
          | 'CN'
          | 'CO'
          | 'CR'
          | 'CU'
          | 'CV'
          | 'CW'
          | 'CX'
          | 'CY'
          | 'CZ'
          | 'DE'
          | 'DJ'
          | 'DK'
          | 'DM'
          | 'DO'
          | 'DZ'
          | 'EC'
          | 'EE'
          | 'EG'
          | 'EH'
          | 'ER'
          | 'ES'
          | 'ET'
          | 'FI'
          | 'FJ'
          | 'FK'
          | 'FM'
          | 'FO'
          | 'FR'
          | 'GA'
          | 'GB'
          | 'GD'
          | 'GE'
          | 'GF'
          | 'GG'
          | 'GH'
          | 'GI'
          | 'GL'
          | 'GM'
          | 'GN'
          | 'GP'
          | 'GQ'
          | 'GR'
          | 'GS'
          | 'GT'
          | 'GU'
          | 'GW'
          | 'GY'
          | 'HK'
          | 'HM'
          | 'HN'
          | 'HR'
          | 'HT'
          | 'HU'
          | 'ID'
          | 'IE'
          | 'IL'
          | 'IM'
          | 'IN'
          | 'IO'
          | 'IQ'
          | 'IR'
          | 'IS'
          | 'IT'
          | 'JE'
          | 'JM'
          | 'JO'
          | 'JP'
          | 'KE'
          | 'KG'
          | 'KH'
          | 'KI'
          | 'KM'
          | 'KN'
          | 'KP'
          | 'KR'
          | 'KW'
          | 'KY'
          | 'KZ'
          | 'LA'
          | 'LB'
          | 'LC'
          | 'LI'
          | 'LK'
          | 'LR'
          | 'LS'
          | 'LT'
          | 'LU'
          | 'LV'
          | 'LY'
          | 'MA'
          | 'MC'
          | 'MD'
          | 'ME'
          | 'MF'
          | 'MG'
          | 'MH'
          | 'MK'
          | 'ML'
          | 'MM'
          | 'MN'
          | 'MO'
          | 'MP'
          | 'MQ'
          | 'MR'
          | 'MS'
          | 'MT'
          | 'MU'
          | 'MV'
          | 'MW'
          | 'MX'
          | 'MY'
          | 'MZ'
          | 'NA'
          | 'NC'
          | 'NE'
          | 'NF'
          | 'NG'
          | 'NI'
          | 'NL'
          | 'NO'
          | 'NP'
          | 'NR'
          | 'NU'
          | 'NZ'
          | 'OM'
          | 'PA'
          | 'PE'
          | 'PF'
          | 'PG'
          | 'PH'
          | 'PK'
          | 'PL'
          | 'PM'
          | 'PN'
          | 'PR'
          | 'PS'
          | 'PT'
          | 'PW'
          | 'PY'
          | 'QA'
          | 'RE'
          | 'RO'
          | 'RS'
          | 'RU'
          | 'RW'
          | 'SA'
          | 'SB'
          | 'SC'
          | 'SD'
          | 'SE'
          | 'SG'
          | 'SH'
          | 'SI'
          | 'SJ'
          | 'SK'
          | 'SL'
          | 'SM'
          | 'SN'
          | 'SO'
          | 'SR'
          | 'SS'
          | 'ST'
          | 'SV'
          | 'SX'
          | 'SY'
          | 'SZ'
          | 'TC'
          | 'TD'
          | 'TF'
          | 'TG'
          | 'TH'
          | 'TJ'
          | 'TK'
          | 'TL'
          | 'TM'
          | 'TN'
          | 'TO'
          | 'TR'
          | 'TT'
          | 'TV'
          | 'TW'
          | 'TZ'
          | 'UA'
          | 'UG'
          | 'UM'
          | 'US'
          | 'UY'
          | 'UZ'
          | 'VA'
          | 'VC'
          | 'VE'
          | 'VG'
          | 'VI'
          | 'VN'
          | 'VU'
          | 'WF'
          | 'WS'
          | 'XK'
          | 'YE'
          | 'YT'
          | 'ZA'
          | 'ZM'
          | 'ZW'
        )[]
        /** @format date */
        diploma_date_after?: string
        /** @format date */
        diploma_date_before?: string
        /**
         * * `abitur` - Abitur
         * * `aufnahmeprufung_uni_bern` - Aufnahmeprüfung Uni Bern
         * * `british_gce_a_levels` - British GCE A Levels
         * * `diploma` - Diploma
         * * `esame_di_stato` - Esame di Stato
         * * `esame_die_stato_linguistico` - Esame die Stato Linguistico
         * * `esame_die_stato_scientifico` - Esame die Stato Scientifico
         * * `graduation` - Graduation
         * * `handelsdiplom` - Handelsdiplom
         * * `high_school_certificate` - High School Certificate
         * * `high_school_diploma` - High School Diploma
         * * `high_school_diploma_ap` - High School Diploma & AP
         * * `international_bacalaureate_ib` - International Bacalaureate (IB)
         * * `matura` - Matura
         * * `zurcher_matura` - Zürcher Matura
         */
        diploma_type?: (
          | 'abitur'
          | 'aufnahmeprufung_uni_bern'
          | 'british_gce_a_levels'
          | 'diploma'
          | 'esame_di_stato'
          | 'esame_die_stato_linguistico'
          | 'esame_die_stato_scientifico'
          | 'graduation'
          | 'handelsdiplom'
          | 'high_school_certificate'
          | 'high_school_diploma'
          | 'high_school_diploma_ap'
          | 'international_bacalaureate_ib'
          | 'matura'
          | 'zurcher_matura'
          | null
        )[]
        fitness_for_sports?: string[]
        /**
         * * `fine-arts` - Fine Arts
         * * `performing-arts` - Performing Arts
         * * `leadership` - Diplomacy & Activism
         * * `robotics` - Robotics & Engineering
         * * `environmental-science` - Environmental Science & Sustainability
         * * `fashion` - Fashion & Textile Design
         * * `informatics` - Informatics & Coding
         * * `intensive-english` - Intensive English
         * * `intensive-german` - Intensive German
         * * `succession-management` - Succession Management Essentials
         * * `wealth-and-investment` - Wealth Creation & Investment Essentials
         * * `ai-agentic` - AI Agentic Enterprise Essentials
         * * `rosenberg-x-crimson-university-admission` - Rosenberg x Crimson University Admission Focus Course
         */
        focus_courses?: (
          | 'ai-agentic'
          | 'environmental-science'
          | 'fashion'
          | 'fine-arts'
          | 'informatics'
          | 'intensive-english'
          | 'intensive-german'
          | 'leadership'
          | 'performing-arts'
          | 'robotics'
          | 'rosenberg-x-crimson-university-admission'
          | 'succession-management'
          | 'wealth-and-investment'
        )[]
        /**
         * * `male` - Male
         * * `female` - Female
         */
        gender?: 'female' | 'male'
        has_dietary_restrictions?: boolean
        has_not_downloaded_certificate?: boolean
        homework_status?: string
        house?: string
        /** @format uuid */
        id?: string
        insurance_type?: string[]
        is_accommodated?: boolean
        is_at_home?: boolean
        is_athlete?: boolean
        is_new?: string
        is_on_senior_house?: boolean
        is_resident_entrepreneur?: boolean
        is_sick_now?: boolean
        is_swimmer?: string
        /**
         * * `l1` - L1, Regular and vegetarian. Students can choose themselves.
         * * `l2` - L2, Specific, individual requirements. Kitchen adapts the existing menu to specific needs.
         * * `l3` - L3, Specific, very individual requirements. Kitchen creates new menu to specific needs, almost every meal has to be cooked individually.
         * * `l4` - L4, Highly specific. Severe allergic reaction to traces of certain food. Meals need to be ordered from outside supplier.
         */
        meal_preference?: 'l1' | 'l2' | 'l3' | 'l4'
        /**
         * * `AF` - Afghanistan
         * * `AX` - Åland Islands
         * * `AL` - Albania
         * * `DZ` - Algeria
         * * `AS` - American Samoa
         * * `AD` - Andorra
         * * `AO` - Angola
         * * `AI` - Anguilla
         * * `AQ` - Antarctica
         * * `AG` - Antigua and Barbuda
         * * `AR` - Argentina
         * * `AM` - Armenia
         * * `AW` - Aruba
         * * `AU` - Australia
         * * `AT` - Austria
         * * `AZ` - Azerbaijan
         * * `BS` - Bahamas
         * * `BH` - Bahrain
         * * `BD` - Bangladesh
         * * `BB` - Barbados
         * * `BY` - Belarus
         * * `BE` - Belgium
         * * `BZ` - Belize
         * * `BJ` - Benin
         * * `BM` - Bermuda
         * * `BT` - Bhutan
         * * `BO` - Bolivia
         * * `BQ` - Bonaire, Sint Eustatius and Saba
         * * `BA` - Bosnia and Herzegovina
         * * `BW` - Botswana
         * * `BV` - Bouvet Island
         * * `BR` - Brazil
         * * `IO` - British Indian Ocean Territory
         * * `BN` - Brunei
         * * `BG` - Bulgaria
         * * `BF` - Burkina Faso
         * * `BI` - Burundi
         * * `CV` - Cabo Verde
         * * `KH` - Cambodia
         * * `CM` - Cameroon
         * * `CA` - Canada
         * * `KY` - Cayman Islands
         * * `CF` - Central African Republic
         * * `TD` - Chad
         * * `CL` - Chile
         * * `CN` - China
         * * `CX` - Christmas Island
         * * `CC` - Cocos (Keeling) Islands
         * * `CO` - Colombia
         * * `KM` - Comoros
         * * `CG` - Congo
         * * `CD` - Congo (the Democratic Republic of the)
         * * `CK` - Cook Islands
         * * `CR` - Costa Rica
         * * `CI` - Côte d'Ivoire
         * * `HR` - Croatia
         * * `CU` - Cuba
         * * `CW` - Curaçao
         * * `CY` - Cyprus
         * * `CZ` - Czechia
         * * `DK` - Denmark
         * * `DJ` - Djibouti
         * * `DM` - Dominica
         * * `DO` - Dominican Republic
         * * `EC` - Ecuador
         * * `EG` - Egypt
         * * `SV` - El Salvador
         * * `GQ` - Equatorial Guinea
         * * `ER` - Eritrea
         * * `EE` - Estonia
         * * `SZ` - Eswatini
         * * `ET` - Ethiopia
         * * `FK` - Falkland Islands (Malvinas)
         * * `FO` - Faroe Islands
         * * `FJ` - Fiji
         * * `FI` - Finland
         * * `FR` - France
         * * `GF` - French Guiana
         * * `PF` - French Polynesia
         * * `TF` - French Southern Territories
         * * `GA` - Gabon
         * * `GM` - Gambia
         * * `GE` - Georgia
         * * `DE` - Germany
         * * `GH` - Ghana
         * * `GI` - Gibraltar
         * * `GR` - Greece
         * * `GL` - Greenland
         * * `GD` - Grenada
         * * `GP` - Guadeloupe
         * * `GU` - Guam
         * * `GT` - Guatemala
         * * `GG` - Guernsey
         * * `GN` - Guinea
         * * `GW` - Guinea-Bissau
         * * `GY` - Guyana
         * * `HT` - Haiti
         * * `HM` - Heard Island and McDonald Islands
         * * `VA` - Holy See
         * * `HN` - Honduras
         * * `HK` - Hong Kong
         * * `HU` - Hungary
         * * `IS` - Iceland
         * * `IN` - India
         * * `ID` - Indonesia
         * * `IR` - Iran
         * * `IQ` - Iraq
         * * `IE` - Ireland
         * * `IM` - Isle of Man
         * * `IL` - Israel
         * * `IT` - Italy
         * * `JM` - Jamaica
         * * `JP` - Japan
         * * `JE` - Jersey
         * * `JO` - Jordan
         * * `KZ` - Kazakhstan
         * * `KE` - Kenya
         * * `KI` - Kiribati
         * * `XK` - Kosovo
         * * `KW` - Kuwait
         * * `KG` - Kyrgyzstan
         * * `LA` - Laos
         * * `LV` - Latvia
         * * `LB` - Lebanon
         * * `LS` - Lesotho
         * * `LR` - Liberia
         * * `LY` - Libya
         * * `LI` - Liechtenstein
         * * `LT` - Lithuania
         * * `LU` - Luxembourg
         * * `MO` - Macao
         * * `MG` - Madagascar
         * * `MW` - Malawi
         * * `MY` - Malaysia
         * * `MV` - Maldives
         * * `ML` - Mali
         * * `MT` - Malta
         * * `MH` - Marshall Islands
         * * `MQ` - Martinique
         * * `MR` - Mauritania
         * * `MU` - Mauritius
         * * `YT` - Mayotte
         * * `MX` - Mexico
         * * `FM` - Micronesia
         * * `MD` - Moldova
         * * `MC` - Monaco
         * * `MN` - Mongolia
         * * `ME` - Montenegro
         * * `MS` - Montserrat
         * * `MA` - Morocco
         * * `MZ` - Mozambique
         * * `MM` - Myanmar
         * * `NA` - Namibia
         * * `NR` - Nauru
         * * `NP` - Nepal
         * * `NL` - Netherlands
         * * `NC` - New Caledonia
         * * `NZ` - New Zealand
         * * `NI` - Nicaragua
         * * `NE` - Niger
         * * `NG` - Nigeria
         * * `NU` - Niue
         * * `NF` - Norfolk Island
         * * `KP` - North Korea
         * * `MK` - North Macedonia
         * * `MP` - Northern Mariana Islands
         * * `NO` - Norway
         * * `OM` - Oman
         * * `PK` - Pakistan
         * * `PW` - Palau
         * * `PS` - Palestine, State of
         * * `PA` - Panama
         * * `PG` - Papua New Guinea
         * * `PY` - Paraguay
         * * `PE` - Peru
         * * `PH` - Philippines
         * * `PN` - Pitcairn
         * * `PL` - Poland
         * * `PT` - Portugal
         * * `PR` - Puerto Rico
         * * `QA` - Qatar
         * * `RE` - Réunion
         * * `RO` - Romania
         * * `RU` - Russia
         * * `RW` - Rwanda
         * * `BL` - Saint Barthélemy
         * * `SH` - Saint Helena, Ascension and Tristan da Cunha
         * * `KN` - Saint Kitts and Nevis
         * * `LC` - Saint Lucia
         * * `MF` - Saint Martin (French part)
         * * `PM` - Saint Pierre and Miquelon
         * * `VC` - Saint Vincent and the Grenadines
         * * `WS` - Samoa
         * * `SM` - San Marino
         * * `ST` - Sao Tome and Principe
         * * `SA` - Saudi Arabia
         * * `SN` - Senegal
         * * `RS` - Serbia
         * * `SC` - Seychelles
         * * `SL` - Sierra Leone
         * * `SG` - Singapore
         * * `SX` - Sint Maarten (Dutch part)
         * * `SK` - Slovakia
         * * `SI` - Slovenia
         * * `SB` - Solomon Islands
         * * `SO` - Somalia
         * * `ZA` - South Africa
         * * `GS` - South Georgia and the South Sandwich Islands
         * * `KR` - South Korea
         * * `SS` - South Sudan
         * * `ES` - Spain
         * * `LK` - Sri Lanka
         * * `SD` - Sudan
         * * `SR` - Suriname
         * * `SJ` - Svalbard and Jan Mayen
         * * `SE` - Sweden
         * * `CH` - Switzerland
         * * `SY` - Syria
         * * `TW` - Taiwan
         * * `TJ` - Tajikistan
         * * `TZ` - Tanzania
         * * `TH` - Thailand
         * * `TL` - Timor-Leste
         * * `TG` - Togo
         * * `TK` - Tokelau
         * * `TO` - Tonga
         * * `TT` - Trinidad and Tobago
         * * `TN` - Tunisia
         * * `TR` - Türkiye
         * * `TM` - Turkmenistan
         * * `TC` - Turks and Caicos Islands
         * * `TV` - Tuvalu
         * * `UG` - Uganda
         * * `UA` - Ukraine
         * * `AE` - United Arab Emirates
         * * `GB` - United Kingdom
         * * `UM` - United States Minor Outlying Islands
         * * `US` - United States of America
         * * `UY` - Uruguay
         * * `UZ` - Uzbekistan
         * * `VU` - Vanuatu
         * * `VE` - Venezuela
         * * `VN` - Vietnam
         * * `VG` - Virgin Islands (British)
         * * `VI` - Virgin Islands (U.S.)
         * * `WF` - Wallis and Futuna
         * * `EH` - Western Sahara
         * * `YE` - Yemen
         * * `ZM` - Zambia
         * * `ZW` - Zimbabwe
         */
        nationalities?: (
          | 'AD'
          | 'AE'
          | 'AF'
          | 'AG'
          | 'AI'
          | 'AL'
          | 'AM'
          | 'AO'
          | 'AQ'
          | 'AR'
          | 'AS'
          | 'AT'
          | 'AU'
          | 'AW'
          | 'AX'
          | 'AZ'
          | 'BA'
          | 'BB'
          | 'BD'
          | 'BE'
          | 'BF'
          | 'BG'
          | 'BH'
          | 'BI'
          | 'BJ'
          | 'BL'
          | 'BM'
          | 'BN'
          | 'BO'
          | 'BQ'
          | 'BR'
          | 'BS'
          | 'BT'
          | 'BV'
          | 'BW'
          | 'BY'
          | 'BZ'
          | 'CA'
          | 'CC'
          | 'CD'
          | 'CF'
          | 'CG'
          | 'CH'
          | 'CI'
          | 'CK'
          | 'CL'
          | 'CM'
          | 'CN'
          | 'CO'
          | 'CR'
          | 'CU'
          | 'CV'
          | 'CW'
          | 'CX'
          | 'CY'
          | 'CZ'
          | 'DE'
          | 'DJ'
          | 'DK'
          | 'DM'
          | 'DO'
          | 'DZ'
          | 'EC'
          | 'EE'
          | 'EG'
          | 'EH'
          | 'ER'
          | 'ES'
          | 'ET'
          | 'FI'
          | 'FJ'
          | 'FK'
          | 'FM'
          | 'FO'
          | 'FR'
          | 'GA'
          | 'GB'
          | 'GD'
          | 'GE'
          | 'GF'
          | 'GG'
          | 'GH'
          | 'GI'
          | 'GL'
          | 'GM'
          | 'GN'
          | 'GP'
          | 'GQ'
          | 'GR'
          | 'GS'
          | 'GT'
          | 'GU'
          | 'GW'
          | 'GY'
          | 'HK'
          | 'HM'
          | 'HN'
          | 'HR'
          | 'HT'
          | 'HU'
          | 'ID'
          | 'IE'
          | 'IL'
          | 'IM'
          | 'IN'
          | 'IO'
          | 'IQ'
          | 'IR'
          | 'IS'
          | 'IT'
          | 'JE'
          | 'JM'
          | 'JO'
          | 'JP'
          | 'KE'
          | 'KG'
          | 'KH'
          | 'KI'
          | 'KM'
          | 'KN'
          | 'KP'
          | 'KR'
          | 'KW'
          | 'KY'
          | 'KZ'
          | 'LA'
          | 'LB'
          | 'LC'
          | 'LI'
          | 'LK'
          | 'LR'
          | 'LS'
          | 'LT'
          | 'LU'
          | 'LV'
          | 'LY'
          | 'MA'
          | 'MC'
          | 'MD'
          | 'ME'
          | 'MF'
          | 'MG'
          | 'MH'
          | 'MK'
          | 'ML'
          | 'MM'
          | 'MN'
          | 'MO'
          | 'MP'
          | 'MQ'
          | 'MR'
          | 'MS'
          | 'MT'
          | 'MU'
          | 'MV'
          | 'MW'
          | 'MX'
          | 'MY'
          | 'MZ'
          | 'NA'
          | 'NC'
          | 'NE'
          | 'NF'
          | 'NG'
          | 'NI'
          | 'NL'
          | 'NO'
          | 'NP'
          | 'NR'
          | 'NU'
          | 'NZ'
          | 'OM'
          | 'PA'
          | 'PE'
          | 'PF'
          | 'PG'
          | 'PH'
          | 'PK'
          | 'PL'
          | 'PM'
          | 'PN'
          | 'PR'
          | 'PS'
          | 'PT'
          | 'PW'
          | 'PY'
          | 'QA'
          | 'RE'
          | 'RO'
          | 'RS'
          | 'RU'
          | 'RW'
          | 'SA'
          | 'SB'
          | 'SC'
          | 'SD'
          | 'SE'
          | 'SG'
          | 'SH'
          | 'SI'
          | 'SJ'
          | 'SK'
          | 'SL'
          | 'SM'
          | 'SN'
          | 'SO'
          | 'SR'
          | 'SS'
          | 'ST'
          | 'SV'
          | 'SX'
          | 'SY'
          | 'SZ'
          | 'TC'
          | 'TD'
          | 'TF'
          | 'TG'
          | 'TH'
          | 'TJ'
          | 'TK'
          | 'TL'
          | 'TM'
          | 'TN'
          | 'TO'
          | 'TR'
          | 'TT'
          | 'TV'
          | 'TW'
          | 'TZ'
          | 'UA'
          | 'UG'
          | 'UM'
          | 'US'
          | 'UY'
          | 'UZ'
          | 'VA'
          | 'VC'
          | 'VE'
          | 'VG'
          | 'VI'
          | 'VN'
          | 'VU'
          | 'WF'
          | 'WS'
          | 'XK'
          | 'YE'
          | 'YT'
          | 'ZA'
          | 'ZM'
          | 'ZW'
        )[]
        no_lesson_at_the_moment?: boolean
        /** Multiple values may be separated by commas. */
        number_of_weeks?: number[]
        /**
         * Ordering
         *
         * * `nickname` - Nickname
         * * `-nickname` - Nickname (descending)
         * * `username` - Username
         * * `-username` - Username (descending)
         * * `first_name` - First name
         * * `-first_name` - First name (descending)
         * * `last_name` - Last name
         * * `-last_name` - Last name (descending)
         * * `full_name` - Full name
         * * `-full_name` - Full name (descending)
         * * `grade_level_ordering` - Grade level ordering
         * * `-grade_level_ordering` - Grade level ordering (descending)
         * * `date_of_birth` - Date of birth
         * * `-date_of_birth` - Date of birth (descending)
         * * `age` - Age
         * * `-age` - Age (descending)
         * * `gender` - Gender
         * * `-gender` - Gender (descending)
         * * `category` - Category
         * * `-category` - Category (descending)
         * * `package_option` - Package option
         * * `-package_option` - Package option (descending)
         * * `number_of_weeks_in_camps` - Number of weeks in camps
         * * `-number_of_weeks_in_camps` - Number of weeks in camps (descending)
         * * `nationalities` - Nationalities
         * * `-nationalities` - Nationalities (descending)
         * * `certificate` - Certificate
         * * `-certificate` - Certificate (descending)
         * * `panel_students` - Panel students
         * * `-panel_students` - Panel students (descending)
         * * `week_of_camps` - Week of camps
         * * `-week_of_camps` - Week of camps (descending)
         * * `date_joined` - Date joined
         * * `-date_joined` - Date joined (descending)
         * * `date_of_entry` - Date of entry
         * * `-date_of_entry` - Date of entry (descending)
         * * `saldo_status` - Saldo status
         * * `-saldo_status` - Saldo status (descending)
         * * `academic_status` - Academic status
         * * `-academic_status` - Academic status (descending)
         * * `house` - House
         * * `-house` - House (descending)
         */
        ordering?: (
          | '-academic_status'
          | '-age'
          | '-category'
          | '-certificate'
          | '-date_joined'
          | '-date_of_birth'
          | '-date_of_entry'
          | '-first_name'
          | '-full_name'
          | '-gender'
          | '-grade_level_ordering'
          | '-house'
          | '-last_name'
          | '-nationalities'
          | '-nickname'
          | '-number_of_weeks_in_camps'
          | '-package_option'
          | '-panel_students'
          | '-saldo_status'
          | '-username'
          | '-week_of_camps'
          | 'academic_status'
          | 'age'
          | 'category'
          | 'certificate'
          | 'date_joined'
          | 'date_of_birth'
          | 'date_of_entry'
          | 'first_name'
          | 'full_name'
          | 'gender'
          | 'grade_level_ordering'
          | 'house'
          | 'last_name'
          | 'nationalities'
          | 'nickname'
          | 'number_of_weeks_in_camps'
          | 'package_option'
          | 'panel_students'
          | 'saldo_status'
          | 'username'
          | 'week_of_camps'
        )[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /**
         * * `no` - No
         * * `current` - Current
         * * `future` - Future
         */
        panel_students?: ('current' | 'future' | 'no')[]
        /**
         * * `dayschool` - Day School
         * * `boarding` - Boarding
         * * `external` - External
         */
        program_type?: 'boarding' | 'dayschool' | 'external'
        room?: string
        room_code?: string
        /**
         * * `5` - Settled
         * * `10` - First reminder
         * * `15` - Second reminder
         * * `17` - Unsettled
         * * `20` - App locked
         */
        saldo_status?: (10 | 15 | 17 | 20 | 5)[]
        /** A search term. */
        search?: string
        section?: number
        show_draft?: boolean
        show_inactive?: boolean
        /**
         * * `W1` - Week 1 2024
         * * `W2` - Week 2 2024
         * * `W3` - Week 3 2024
         * * `W4` - Week 4 2024
         * * `W1_2025` - Week 1 2025
         * * `W2_2025` - Week 2 2025
         * * `W3_2025` - Week 3 2025
         * * `W4_2025` - Week 4 2025
         * * `W1_2026` - Week 1 2026
         * * `W2_2026` - Week 2 2026
         * * `W3_2026` - Week 3 2026
         * * `W4_2026` - Week 4 2026
         * * `W1_2027` - Week 1 2027
         * * `W2_2027` - Week 2 2027
         * * `W3_2027` - Week 3 2027
         * * `W4_2027` - Week 4 2027
         */
        week_of_camps?: (
          | 'W1'
          | 'W1_2025'
          | 'W1_2026'
          | 'W1_2027'
          | 'W2'
          | 'W2_2025'
          | 'W2_2026'
          | 'W2_2027'
          | 'W3'
          | 'W3_2025'
          | 'W3_2026'
          | 'W3_2027'
          | 'W4'
          | 'W4_2025'
          | 'W4_2026'
          | 'W4_2027'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedStudentListList, T>, any>({
        path: `/students/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsCreate
     * @request POST:/students/
     * @secure
     */,
    studentsCreate: <T extends FetchKeys<StudentDetail>>(
      data: StudentCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentDetail, T>, any>({
        path: `/students/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsRetrieve
     * @request GET:/students/{id}/
     * @secure
     */,
    studentsRetrieve: <T extends FetchKeys<StudentDetail>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentDetail, T>, any>({
        path: `/students/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsUpdate
     * @request PUT:/students/{id}/
     * @secure
     */,
    studentsUpdate: <T extends FetchKeys<StudentDetail>>(
      id: string,
      data: StudentDetailRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentDetail, T>, any>({
        path: `/students/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsPartialUpdate
     * @request PATCH:/students/{id}/
     * @secure
     */,
    studentsPartialUpdate: <T extends FetchKeys<StudentDetail>>(
      id: string,
      data: PatchedStudentDetailRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentDetail, T>, any>({
        path: `/students/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsDestroy
     * @request DELETE:/students/{id}/
     * @secure
     */,
    studentsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/students/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsBasicInfoRetrieve
     * @request GET:/students/{id}/basic-info/
     * @secure
     */,
    studentsBasicInfoRetrieve: <T extends FetchKeys<StudentBasicInfo>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentBasicInfo, T>, any>({
        path: `/students/${id}/basic-info/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags students
     * @name StudentsBillingDetailsRetrieve
     * @request GET:/students/{id}/billing-details/
     * @secure
     */,
    studentsBillingDetailsRetrieve: <
      T extends FetchKeys<StudentBillingDetails>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentBillingDetails, T>, any>({
        path: `/students/${id}/billing-details/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags students
     * @name StudentsBillingDetailsUpdate
     * @request PUT:/students/{id}/billing-details/
     * @secure
     */,
    studentsBillingDetailsUpdate: <T extends FetchKeys<StudentBillingDetails>>(
      id: string,
      data: StudentBillingDetailsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentBillingDetails, T>, any>({
        path: `/students/${id}/billing-details/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsChangePhotoPartialUpdate
     * @request PATCH:/students/{id}/change-photo/
     * @secure
     */,
    studentsChangePhotoPartialUpdate: <T extends FetchKeys<StudentChangePhoto>>(
      id: string,
      data: PatchedStudentChangePhotoRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentChangePhoto, T>, any>({
        path: `/students/${id}/change-photo/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags students
     * @name StudentsChangesList
     * @request GET:/students/{id}/changes/
     * @secure
     */,
    studentsChangesList: <T extends FetchKeys<PaginatedLogEntryHistoryList>>(
      id: string,
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string
        /** Filter history by tabs (OR). Repeat the parameter for multiple tabs, e.g. tabs=mission_control&tabs=health_card. Omit the parameter or send an empty selection to include all available tabs. Student entries always belong to basic_information. Ordinary updates (without a custom action) also match mission_control or health_card by changed fields. Health fields include dietary restrictions, psychological care, legacy medical fields and summer tetanus vaccination. On Summer Camps instances, SummerCampStudent ordinary updates match tabs by fields; other actions belong to basic_information. Updates containing only medical reports or vaccination fields belong only to health_card. Purely technical or unclassified summer updates are excluded. CourseInBasket and T&E student enrolments are included only for department or sport membership updates. Empty selections return the union of all configured tabs. Matching entries retain all their changes. Available tabs: mission_control (Mission Control), health_card (Health card), basic_information (Basic information), documents_and_notes (Documents & Notes). */
        tabs?: (
          | 'mission_control'
          | 'health_card'
          | 'basic_information'
          | 'documents_and_notes'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedLogEntryHistoryList, T>, any>({
        path: `/students/${id}/changes/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsDeletePhotoDestroy
     * @request DELETE:/students/{id}/delete-photo/
     * @secure
     */,
    studentsDeletePhotoDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/students/${id}/delete-photo/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsDietaryRestrictionsUpdate
     * @request PUT:/students/{id}/dietary-restrictions/
     * @secure
     */,
    studentsDietaryRestrictionsUpdate: <
      T extends FetchKeys<StudentDietaryRestrictions>
    >(
      id: string,
      data: StudentDietaryRestrictionsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentDietaryRestrictions, T>, any>({
        path: `/students/${id}/dietary-restrictions/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsDietaryRestrictionsPartialUpdate
     * @request PATCH:/students/{id}/dietary-restrictions/
     * @secure
     */,
    studentsDietaryRestrictionsPartialUpdate: <
      T extends FetchKeys<StudentDietaryRestrictions>
    >(
      id: string,
      data: PatchedStudentDietaryRestrictionsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentDietaryRestrictions, T>, any>({
        path: `/students/${id}/dietary-restrictions/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsExamRecommendationsPartialUpdate
     * @request PATCH:/students/{id}/exam-recommendations/
     * @secure
     */,
    studentsExamRecommendationsPartialUpdate: <
      T extends FetchKeys<StudentDetail>
    >(
      id: string,
      data: PatchedStudentDetailRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentDetail, T>, any>({
        path: `/students/${id}/exam-recommendations/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsExternalCreditsRetrieve
     * @request GET:/students/{id}/external-credits/
     * @secure
     */,
    studentsExternalCreditsRetrieve: <
      T extends FetchKeys<StudentExternalCredits>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentExternalCredits, T>, any>({
        path: `/students/${id}/external-credits/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsExternalCreditsUpdate
     * @request PUT:/students/{id}/external-credits/
     * @secure
     */,
    studentsExternalCreditsUpdate: <
      T extends FetchKeys<StudentExternalCredits>
    >(
      id: string,
      data: StudentExternalCreditsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentExternalCredits, T>, any>({
        path: `/students/${id}/external-credits/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsExternalCreditsPartialUpdate
     * @request PATCH:/students/{id}/external-credits/
     * @secure
     */,
    studentsExternalCreditsPartialUpdate: <
      T extends FetchKeys<StudentExternalCredits>
    >(
      id: string,
      data: PatchedStudentExternalCreditsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentExternalCredits, T>, any>({
        path: `/students/${id}/external-credits/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Get student's grades for courses in quarter
     *
     * @tags students
     * @name StudentsGradesIdpViewRetrieve
     * @request GET:/students/{id}/grades/idp_view/
     * @secure
     */,
    studentsGradesIdpViewRetrieve: <T extends FetchKeys<IdpViewStudentGrades>>(
      id: string,
      query: {
        assignment_type?: number
        year: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<IdpViewStudentGrades, T>, void>({
        path: `/students/${id}/grades/idp_view/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Get student's grades for courses in quarter
     *
     * @tags students
     * @name StudentsGradesQuarterViewRetrieve
     * @request GET:/students/{id}/grades/quarter_view/
     * @secure
     */,
    studentsGradesQuarterViewRetrieve: <
      T extends FetchKeys<QuarterViewStudentGrades>
    >(
      id: string,
      query: {
        assignment_type?: number
        quarter: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<QuarterViewStudentGrades, T>, void>({
        path: `/students/${id}/grades/quarter_view/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsPotentialCategoryRetrieve
     * @request GET:/students/{id}/potential-category/
     * @secure
     */,
    studentsPotentialCategoryRetrieve: <T extends FetchKeys<StudentDetail>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentDetail, T>, any>({
        path: `/students/${id}/potential-category/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Get student's grades for courses in quarter
     *
     * @tags students
     * @name StudentsProgressRetrieve
     * @request GET:/students/{id}/progress/
     * @secure
     */,
    studentsProgressRetrieve: <
      T extends FetchKeys<StudentProgressViewResponseData>
    >(
      id: string,
      query: {
        /** @format uuid */
        course: string
        /**
         * * `all` - All preceding courses
         * * `prerequisite` - Prerequisite courses
         * * `lower_difficulty` - Lower difficulty courses
         * @minLength 1
         * @default "all"
         */
        preceding_courses?: 'all' | 'prerequisite' | 'lower_difficulty'
        year: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentProgressViewResponseData, T>, void>({
        path: `/students/${id}/progress/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags students
     * @name StudentsPsychologicalCareRetrieve
     * @request GET:/students/{id}/psychological-care/
     * @secure
     */,
    studentsPsychologicalCareRetrieve: <
      T extends FetchKeys<StudentPsychologicalCare>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentPsychologicalCare, T>, any>({
        path: `/students/${id}/psychological-care/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags students
     * @name StudentsPsychologicalCareUpdate
     * @request PUT:/students/{id}/psychological-care/
     * @secure
     */,
    studentsPsychologicalCareUpdate: <
      T extends FetchKeys<StudentPsychologicalCare>
    >(
      id: string,
      data: StudentPsychologicalCareRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentPsychologicalCare, T>, any>({
        path: `/students/${id}/psychological-care/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags students
     * @name StudentsPsychologicalCarePartialUpdate
     * @request PATCH:/students/{id}/psychological-care/
     * @secure
     */,
    studentsPsychologicalCarePartialUpdate: <
      T extends FetchKeys<StudentPsychologicalCare>
    >(
      id: string,
      data: PatchedStudentPsychologicalCareRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentPsychologicalCare, T>, any>({
        path: `/students/${id}/psychological-care/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsReportCardRetrieve
     * @request GET:/students/{id}/report-card/
     * @secure
     */,
    studentsReportCardRetrieve: <T extends FetchKeys<StudentDetail>>(
      id: string,
      query?: {
        quarter?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentDetail, T>, any>({
        path: `/students/${id}/report-card/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsSaldoStatusPartialUpdate
     * @request PATCH:/students/{id}/saldo-status/
     * @secure
     */,
    studentsSaldoStatusPartialUpdate: <T extends FetchKeys<StudentSaldoStatus>>(
      id: string,
      data: PatchedStudentSaldoStatusRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentSaldoStatus, T>, any>({
        path: `/students/${id}/saldo-status/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsSetPasswordCreate
     * @request POST:/students/{id}/set-password/
     * @secure
     */,
    studentsSetPasswordCreate: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/students/${id}/set-password/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsAccessArrangementsChoicesList
     * @request GET:/students/access-arrangements-choices/
     * @secure
     */,
    studentsAccessArrangementsChoicesList: <
      T extends FetchKeys<StringOption[]>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/students/access-arrangements-choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Use with Generic DRF views, or trigger update_search_index method manually
     *
     * @tags students
     * @name StudentsAvailableFiltersRetrieve
     * @request GET:/students/available-filters/
     * @secure
     */,
    studentsAvailableFiltersRetrieve: <
      T extends FetchKeys<StudentsAvailableFilters>
    >(
      query?: {
        /**
         * * `15` - Enrolled
         * * `30` - Active
         * * `45` - Left
         * * `60` - Alumni
         */
        academic_status?: ('15' | '30' | '45' | '60' | null)[]
        activity__course?: string
        activity__quarter?: number
        activity__school_year?: number
        /** Multiple values may be separated by commas. */
        age?: number[]
        allergen_kinds?: string[]
        birthday_in_week?: boolean
        birthday_today?: boolean
        boarding_status?: string
        campus_location?: string
        campus_status?: string
        campus_status_reason?: string
        career_goal?: string[]
        category?: number
        chronic_condition_types?: string[]
        current_grade_level?: number
        current_location?: string
        /**
         * * `AF` - Afghanistan
         * * `AX` - Åland Islands
         * * `AL` - Albania
         * * `DZ` - Algeria
         * * `AS` - American Samoa
         * * `AD` - Andorra
         * * `AO` - Angola
         * * `AI` - Anguilla
         * * `AQ` - Antarctica
         * * `AG` - Antigua and Barbuda
         * * `AR` - Argentina
         * * `AM` - Armenia
         * * `AW` - Aruba
         * * `AU` - Australia
         * * `AT` - Austria
         * * `AZ` - Azerbaijan
         * * `BS` - Bahamas
         * * `BH` - Bahrain
         * * `BD` - Bangladesh
         * * `BB` - Barbados
         * * `BY` - Belarus
         * * `BE` - Belgium
         * * `BZ` - Belize
         * * `BJ` - Benin
         * * `BM` - Bermuda
         * * `BT` - Bhutan
         * * `BO` - Bolivia
         * * `BQ` - Bonaire, Sint Eustatius and Saba
         * * `BA` - Bosnia and Herzegovina
         * * `BW` - Botswana
         * * `BV` - Bouvet Island
         * * `BR` - Brazil
         * * `IO` - British Indian Ocean Territory
         * * `BN` - Brunei
         * * `BG` - Bulgaria
         * * `BF` - Burkina Faso
         * * `BI` - Burundi
         * * `CV` - Cabo Verde
         * * `KH` - Cambodia
         * * `CM` - Cameroon
         * * `CA` - Canada
         * * `KY` - Cayman Islands
         * * `CF` - Central African Republic
         * * `TD` - Chad
         * * `CL` - Chile
         * * `CN` - China
         * * `CX` - Christmas Island
         * * `CC` - Cocos (Keeling) Islands
         * * `CO` - Colombia
         * * `KM` - Comoros
         * * `CG` - Congo
         * * `CD` - Congo (the Democratic Republic of the)
         * * `CK` - Cook Islands
         * * `CR` - Costa Rica
         * * `CI` - Côte d'Ivoire
         * * `HR` - Croatia
         * * `CU` - Cuba
         * * `CW` - Curaçao
         * * `CY` - Cyprus
         * * `CZ` - Czechia
         * * `DK` - Denmark
         * * `DJ` - Djibouti
         * * `DM` - Dominica
         * * `DO` - Dominican Republic
         * * `EC` - Ecuador
         * * `EG` - Egypt
         * * `SV` - El Salvador
         * * `GQ` - Equatorial Guinea
         * * `ER` - Eritrea
         * * `EE` - Estonia
         * * `SZ` - Eswatini
         * * `ET` - Ethiopia
         * * `FK` - Falkland Islands (Malvinas)
         * * `FO` - Faroe Islands
         * * `FJ` - Fiji
         * * `FI` - Finland
         * * `FR` - France
         * * `GF` - French Guiana
         * * `PF` - French Polynesia
         * * `TF` - French Southern Territories
         * * `GA` - Gabon
         * * `GM` - Gambia
         * * `GE` - Georgia
         * * `DE` - Germany
         * * `GH` - Ghana
         * * `GI` - Gibraltar
         * * `GR` - Greece
         * * `GL` - Greenland
         * * `GD` - Grenada
         * * `GP` - Guadeloupe
         * * `GU` - Guam
         * * `GT` - Guatemala
         * * `GG` - Guernsey
         * * `GN` - Guinea
         * * `GW` - Guinea-Bissau
         * * `GY` - Guyana
         * * `HT` - Haiti
         * * `HM` - Heard Island and McDonald Islands
         * * `VA` - Holy See
         * * `HN` - Honduras
         * * `HK` - Hong Kong
         * * `HU` - Hungary
         * * `IS` - Iceland
         * * `IN` - India
         * * `ID` - Indonesia
         * * `IR` - Iran
         * * `IQ` - Iraq
         * * `IE` - Ireland
         * * `IM` - Isle of Man
         * * `IL` - Israel
         * * `IT` - Italy
         * * `JM` - Jamaica
         * * `JP` - Japan
         * * `JE` - Jersey
         * * `JO` - Jordan
         * * `KZ` - Kazakhstan
         * * `KE` - Kenya
         * * `KI` - Kiribati
         * * `XK` - Kosovo
         * * `KW` - Kuwait
         * * `KG` - Kyrgyzstan
         * * `LA` - Laos
         * * `LV` - Latvia
         * * `LB` - Lebanon
         * * `LS` - Lesotho
         * * `LR` - Liberia
         * * `LY` - Libya
         * * `LI` - Liechtenstein
         * * `LT` - Lithuania
         * * `LU` - Luxembourg
         * * `MO` - Macao
         * * `MG` - Madagascar
         * * `MW` - Malawi
         * * `MY` - Malaysia
         * * `MV` - Maldives
         * * `ML` - Mali
         * * `MT` - Malta
         * * `MH` - Marshall Islands
         * * `MQ` - Martinique
         * * `MR` - Mauritania
         * * `MU` - Mauritius
         * * `YT` - Mayotte
         * * `MX` - Mexico
         * * `FM` - Micronesia
         * * `MD` - Moldova
         * * `MC` - Monaco
         * * `MN` - Mongolia
         * * `ME` - Montenegro
         * * `MS` - Montserrat
         * * `MA` - Morocco
         * * `MZ` - Mozambique
         * * `MM` - Myanmar
         * * `NA` - Namibia
         * * `NR` - Nauru
         * * `NP` - Nepal
         * * `NL` - Netherlands
         * * `NC` - New Caledonia
         * * `NZ` - New Zealand
         * * `NI` - Nicaragua
         * * `NE` - Niger
         * * `NG` - Nigeria
         * * `NU` - Niue
         * * `NF` - Norfolk Island
         * * `KP` - North Korea
         * * `MK` - North Macedonia
         * * `MP` - Northern Mariana Islands
         * * `NO` - Norway
         * * `OM` - Oman
         * * `PK` - Pakistan
         * * `PW` - Palau
         * * `PS` - Palestine, State of
         * * `PA` - Panama
         * * `PG` - Papua New Guinea
         * * `PY` - Paraguay
         * * `PE` - Peru
         * * `PH` - Philippines
         * * `PN` - Pitcairn
         * * `PL` - Poland
         * * `PT` - Portugal
         * * `PR` - Puerto Rico
         * * `QA` - Qatar
         * * `RE` - Réunion
         * * `RO` - Romania
         * * `RU` - Russia
         * * `RW` - Rwanda
         * * `BL` - Saint Barthélemy
         * * `SH` - Saint Helena, Ascension and Tristan da Cunha
         * * `KN` - Saint Kitts and Nevis
         * * `LC` - Saint Lucia
         * * `MF` - Saint Martin (French part)
         * * `PM` - Saint Pierre and Miquelon
         * * `VC` - Saint Vincent and the Grenadines
         * * `WS` - Samoa
         * * `SM` - San Marino
         * * `ST` - Sao Tome and Principe
         * * `SA` - Saudi Arabia
         * * `SN` - Senegal
         * * `RS` - Serbia
         * * `SC` - Seychelles
         * * `SL` - Sierra Leone
         * * `SG` - Singapore
         * * `SX` - Sint Maarten (Dutch part)
         * * `SK` - Slovakia
         * * `SI` - Slovenia
         * * `SB` - Solomon Islands
         * * `SO` - Somalia
         * * `ZA` - South Africa
         * * `GS` - South Georgia and the South Sandwich Islands
         * * `KR` - South Korea
         * * `SS` - South Sudan
         * * `ES` - Spain
         * * `LK` - Sri Lanka
         * * `SD` - Sudan
         * * `SR` - Suriname
         * * `SJ` - Svalbard and Jan Mayen
         * * `SE` - Sweden
         * * `CH` - Switzerland
         * * `SY` - Syria
         * * `TW` - Taiwan
         * * `TJ` - Tajikistan
         * * `TZ` - Tanzania
         * * `TH` - Thailand
         * * `TL` - Timor-Leste
         * * `TG` - Togo
         * * `TK` - Tokelau
         * * `TO` - Tonga
         * * `TT` - Trinidad and Tobago
         * * `TN` - Tunisia
         * * `TR` - Türkiye
         * * `TM` - Turkmenistan
         * * `TC` - Turks and Caicos Islands
         * * `TV` - Tuvalu
         * * `UG` - Uganda
         * * `UA` - Ukraine
         * * `AE` - United Arab Emirates
         * * `GB` - United Kingdom
         * * `UM` - United States Minor Outlying Islands
         * * `US` - United States of America
         * * `UY` - Uruguay
         * * `UZ` - Uzbekistan
         * * `VU` - Vanuatu
         * * `VE` - Venezuela
         * * `VN` - Vietnam
         * * `VG` - Virgin Islands (British)
         * * `VI` - Virgin Islands (U.S.)
         * * `WF` - Wallis and Futuna
         * * `EH` - Western Sahara
         * * `YE` - Yemen
         * * `ZM` - Zambia
         * * `ZW` - Zimbabwe
         */
        degree_destination?: (
          | 'AD'
          | 'AE'
          | 'AF'
          | 'AG'
          | 'AI'
          | 'AL'
          | 'AM'
          | 'AO'
          | 'AQ'
          | 'AR'
          | 'AS'
          | 'AT'
          | 'AU'
          | 'AW'
          | 'AX'
          | 'AZ'
          | 'BA'
          | 'BB'
          | 'BD'
          | 'BE'
          | 'BF'
          | 'BG'
          | 'BH'
          | 'BI'
          | 'BJ'
          | 'BL'
          | 'BM'
          | 'BN'
          | 'BO'
          | 'BQ'
          | 'BR'
          | 'BS'
          | 'BT'
          | 'BV'
          | 'BW'
          | 'BY'
          | 'BZ'
          | 'CA'
          | 'CC'
          | 'CD'
          | 'CF'
          | 'CG'
          | 'CH'
          | 'CI'
          | 'CK'
          | 'CL'
          | 'CM'
          | 'CN'
          | 'CO'
          | 'CR'
          | 'CU'
          | 'CV'
          | 'CW'
          | 'CX'
          | 'CY'
          | 'CZ'
          | 'DE'
          | 'DJ'
          | 'DK'
          | 'DM'
          | 'DO'
          | 'DZ'
          | 'EC'
          | 'EE'
          | 'EG'
          | 'EH'
          | 'ER'
          | 'ES'
          | 'ET'
          | 'FI'
          | 'FJ'
          | 'FK'
          | 'FM'
          | 'FO'
          | 'FR'
          | 'GA'
          | 'GB'
          | 'GD'
          | 'GE'
          | 'GF'
          | 'GG'
          | 'GH'
          | 'GI'
          | 'GL'
          | 'GM'
          | 'GN'
          | 'GP'
          | 'GQ'
          | 'GR'
          | 'GS'
          | 'GT'
          | 'GU'
          | 'GW'
          | 'GY'
          | 'HK'
          | 'HM'
          | 'HN'
          | 'HR'
          | 'HT'
          | 'HU'
          | 'ID'
          | 'IE'
          | 'IL'
          | 'IM'
          | 'IN'
          | 'IO'
          | 'IQ'
          | 'IR'
          | 'IS'
          | 'IT'
          | 'JE'
          | 'JM'
          | 'JO'
          | 'JP'
          | 'KE'
          | 'KG'
          | 'KH'
          | 'KI'
          | 'KM'
          | 'KN'
          | 'KP'
          | 'KR'
          | 'KW'
          | 'KY'
          | 'KZ'
          | 'LA'
          | 'LB'
          | 'LC'
          | 'LI'
          | 'LK'
          | 'LR'
          | 'LS'
          | 'LT'
          | 'LU'
          | 'LV'
          | 'LY'
          | 'MA'
          | 'MC'
          | 'MD'
          | 'ME'
          | 'MF'
          | 'MG'
          | 'MH'
          | 'MK'
          | 'ML'
          | 'MM'
          | 'MN'
          | 'MO'
          | 'MP'
          | 'MQ'
          | 'MR'
          | 'MS'
          | 'MT'
          | 'MU'
          | 'MV'
          | 'MW'
          | 'MX'
          | 'MY'
          | 'MZ'
          | 'NA'
          | 'NC'
          | 'NE'
          | 'NF'
          | 'NG'
          | 'NI'
          | 'NL'
          | 'NO'
          | 'NP'
          | 'NR'
          | 'NU'
          | 'NZ'
          | 'OM'
          | 'PA'
          | 'PE'
          | 'PF'
          | 'PG'
          | 'PH'
          | 'PK'
          | 'PL'
          | 'PM'
          | 'PN'
          | 'PR'
          | 'PS'
          | 'PT'
          | 'PW'
          | 'PY'
          | 'QA'
          | 'RE'
          | 'RO'
          | 'RS'
          | 'RU'
          | 'RW'
          | 'SA'
          | 'SB'
          | 'SC'
          | 'SD'
          | 'SE'
          | 'SG'
          | 'SH'
          | 'SI'
          | 'SJ'
          | 'SK'
          | 'SL'
          | 'SM'
          | 'SN'
          | 'SO'
          | 'SR'
          | 'SS'
          | 'ST'
          | 'SV'
          | 'SX'
          | 'SY'
          | 'SZ'
          | 'TC'
          | 'TD'
          | 'TF'
          | 'TG'
          | 'TH'
          | 'TJ'
          | 'TK'
          | 'TL'
          | 'TM'
          | 'TN'
          | 'TO'
          | 'TR'
          | 'TT'
          | 'TV'
          | 'TW'
          | 'TZ'
          | 'UA'
          | 'UG'
          | 'UM'
          | 'US'
          | 'UY'
          | 'UZ'
          | 'VA'
          | 'VC'
          | 'VE'
          | 'VG'
          | 'VI'
          | 'VN'
          | 'VU'
          | 'WF'
          | 'WS'
          | 'XK'
          | 'YE'
          | 'YT'
          | 'ZA'
          | 'ZM'
          | 'ZW'
        )[]
        /** @format date */
        diploma_date_after?: string
        /** @format date */
        diploma_date_before?: string
        /**
         * * `abitur` - Abitur
         * * `aufnahmeprufung_uni_bern` - Aufnahmeprüfung Uni Bern
         * * `british_gce_a_levels` - British GCE A Levels
         * * `diploma` - Diploma
         * * `esame_di_stato` - Esame di Stato
         * * `esame_die_stato_linguistico` - Esame die Stato Linguistico
         * * `esame_die_stato_scientifico` - Esame die Stato Scientifico
         * * `graduation` - Graduation
         * * `handelsdiplom` - Handelsdiplom
         * * `high_school_certificate` - High School Certificate
         * * `high_school_diploma` - High School Diploma
         * * `high_school_diploma_ap` - High School Diploma & AP
         * * `international_bacalaureate_ib` - International Bacalaureate (IB)
         * * `matura` - Matura
         * * `zurcher_matura` - Zürcher Matura
         */
        diploma_type?: (
          | 'abitur'
          | 'aufnahmeprufung_uni_bern'
          | 'british_gce_a_levels'
          | 'diploma'
          | 'esame_di_stato'
          | 'esame_die_stato_linguistico'
          | 'esame_die_stato_scientifico'
          | 'graduation'
          | 'handelsdiplom'
          | 'high_school_certificate'
          | 'high_school_diploma'
          | 'high_school_diploma_ap'
          | 'international_bacalaureate_ib'
          | 'matura'
          | 'zurcher_matura'
          | null
        )[]
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        fitness_for_sports?: string[]
        /**
         * * `fine-arts` - Fine Arts
         * * `performing-arts` - Performing Arts
         * * `leadership` - Diplomacy & Activism
         * * `robotics` - Robotics & Engineering
         * * `environmental-science` - Environmental Science & Sustainability
         * * `fashion` - Fashion & Textile Design
         * * `informatics` - Informatics & Coding
         * * `intensive-english` - Intensive English
         * * `intensive-german` - Intensive German
         * * `succession-management` - Succession Management Essentials
         * * `wealth-and-investment` - Wealth Creation & Investment Essentials
         * * `ai-agentic` - AI Agentic Enterprise Essentials
         * * `rosenberg-x-crimson-university-admission` - Rosenberg x Crimson University Admission Focus Course
         */
        focus_courses?: (
          | 'ai-agentic'
          | 'environmental-science'
          | 'fashion'
          | 'fine-arts'
          | 'informatics'
          | 'intensive-english'
          | 'intensive-german'
          | 'leadership'
          | 'performing-arts'
          | 'robotics'
          | 'rosenberg-x-crimson-university-admission'
          | 'succession-management'
          | 'wealth-and-investment'
        )[]
        /**
         * * `male` - Male
         * * `female` - Female
         */
        gender?: 'female' | 'male'
        has_dietary_restrictions?: boolean
        has_not_downloaded_certificate?: boolean
        homework_status?: string
        house?: string
        /** @format uuid */
        id?: string
        insurance_type?: string[]
        is_accommodated?: boolean
        is_at_home?: boolean
        is_athlete?: boolean
        is_new?: string
        is_on_senior_house?: boolean
        is_resident_entrepreneur?: boolean
        is_sick_now?: boolean
        is_swimmer?: string
        /**
         * * `l1` - L1, Regular and vegetarian. Students can choose themselves.
         * * `l2` - L2, Specific, individual requirements. Kitchen adapts the existing menu to specific needs.
         * * `l3` - L3, Specific, very individual requirements. Kitchen creates new menu to specific needs, almost every meal has to be cooked individually.
         * * `l4` - L4, Highly specific. Severe allergic reaction to traces of certain food. Meals need to be ordered from outside supplier.
         */
        meal_preference?: 'l1' | 'l2' | 'l3' | 'l4'
        /**
         * * `AF` - Afghanistan
         * * `AX` - Åland Islands
         * * `AL` - Albania
         * * `DZ` - Algeria
         * * `AS` - American Samoa
         * * `AD` - Andorra
         * * `AO` - Angola
         * * `AI` - Anguilla
         * * `AQ` - Antarctica
         * * `AG` - Antigua and Barbuda
         * * `AR` - Argentina
         * * `AM` - Armenia
         * * `AW` - Aruba
         * * `AU` - Australia
         * * `AT` - Austria
         * * `AZ` - Azerbaijan
         * * `BS` - Bahamas
         * * `BH` - Bahrain
         * * `BD` - Bangladesh
         * * `BB` - Barbados
         * * `BY` - Belarus
         * * `BE` - Belgium
         * * `BZ` - Belize
         * * `BJ` - Benin
         * * `BM` - Bermuda
         * * `BT` - Bhutan
         * * `BO` - Bolivia
         * * `BQ` - Bonaire, Sint Eustatius and Saba
         * * `BA` - Bosnia and Herzegovina
         * * `BW` - Botswana
         * * `BV` - Bouvet Island
         * * `BR` - Brazil
         * * `IO` - British Indian Ocean Territory
         * * `BN` - Brunei
         * * `BG` - Bulgaria
         * * `BF` - Burkina Faso
         * * `BI` - Burundi
         * * `CV` - Cabo Verde
         * * `KH` - Cambodia
         * * `CM` - Cameroon
         * * `CA` - Canada
         * * `KY` - Cayman Islands
         * * `CF` - Central African Republic
         * * `TD` - Chad
         * * `CL` - Chile
         * * `CN` - China
         * * `CX` - Christmas Island
         * * `CC` - Cocos (Keeling) Islands
         * * `CO` - Colombia
         * * `KM` - Comoros
         * * `CG` - Congo
         * * `CD` - Congo (the Democratic Republic of the)
         * * `CK` - Cook Islands
         * * `CR` - Costa Rica
         * * `CI` - Côte d'Ivoire
         * * `HR` - Croatia
         * * `CU` - Cuba
         * * `CW` - Curaçao
         * * `CY` - Cyprus
         * * `CZ` - Czechia
         * * `DK` - Denmark
         * * `DJ` - Djibouti
         * * `DM` - Dominica
         * * `DO` - Dominican Republic
         * * `EC` - Ecuador
         * * `EG` - Egypt
         * * `SV` - El Salvador
         * * `GQ` - Equatorial Guinea
         * * `ER` - Eritrea
         * * `EE` - Estonia
         * * `SZ` - Eswatini
         * * `ET` - Ethiopia
         * * `FK` - Falkland Islands (Malvinas)
         * * `FO` - Faroe Islands
         * * `FJ` - Fiji
         * * `FI` - Finland
         * * `FR` - France
         * * `GF` - French Guiana
         * * `PF` - French Polynesia
         * * `TF` - French Southern Territories
         * * `GA` - Gabon
         * * `GM` - Gambia
         * * `GE` - Georgia
         * * `DE` - Germany
         * * `GH` - Ghana
         * * `GI` - Gibraltar
         * * `GR` - Greece
         * * `GL` - Greenland
         * * `GD` - Grenada
         * * `GP` - Guadeloupe
         * * `GU` - Guam
         * * `GT` - Guatemala
         * * `GG` - Guernsey
         * * `GN` - Guinea
         * * `GW` - Guinea-Bissau
         * * `GY` - Guyana
         * * `HT` - Haiti
         * * `HM` - Heard Island and McDonald Islands
         * * `VA` - Holy See
         * * `HN` - Honduras
         * * `HK` - Hong Kong
         * * `HU` - Hungary
         * * `IS` - Iceland
         * * `IN` - India
         * * `ID` - Indonesia
         * * `IR` - Iran
         * * `IQ` - Iraq
         * * `IE` - Ireland
         * * `IM` - Isle of Man
         * * `IL` - Israel
         * * `IT` - Italy
         * * `JM` - Jamaica
         * * `JP` - Japan
         * * `JE` - Jersey
         * * `JO` - Jordan
         * * `KZ` - Kazakhstan
         * * `KE` - Kenya
         * * `KI` - Kiribati
         * * `XK` - Kosovo
         * * `KW` - Kuwait
         * * `KG` - Kyrgyzstan
         * * `LA` - Laos
         * * `LV` - Latvia
         * * `LB` - Lebanon
         * * `LS` - Lesotho
         * * `LR` - Liberia
         * * `LY` - Libya
         * * `LI` - Liechtenstein
         * * `LT` - Lithuania
         * * `LU` - Luxembourg
         * * `MO` - Macao
         * * `MG` - Madagascar
         * * `MW` - Malawi
         * * `MY` - Malaysia
         * * `MV` - Maldives
         * * `ML` - Mali
         * * `MT` - Malta
         * * `MH` - Marshall Islands
         * * `MQ` - Martinique
         * * `MR` - Mauritania
         * * `MU` - Mauritius
         * * `YT` - Mayotte
         * * `MX` - Mexico
         * * `FM` - Micronesia
         * * `MD` - Moldova
         * * `MC` - Monaco
         * * `MN` - Mongolia
         * * `ME` - Montenegro
         * * `MS` - Montserrat
         * * `MA` - Morocco
         * * `MZ` - Mozambique
         * * `MM` - Myanmar
         * * `NA` - Namibia
         * * `NR` - Nauru
         * * `NP` - Nepal
         * * `NL` - Netherlands
         * * `NC` - New Caledonia
         * * `NZ` - New Zealand
         * * `NI` - Nicaragua
         * * `NE` - Niger
         * * `NG` - Nigeria
         * * `NU` - Niue
         * * `NF` - Norfolk Island
         * * `KP` - North Korea
         * * `MK` - North Macedonia
         * * `MP` - Northern Mariana Islands
         * * `NO` - Norway
         * * `OM` - Oman
         * * `PK` - Pakistan
         * * `PW` - Palau
         * * `PS` - Palestine, State of
         * * `PA` - Panama
         * * `PG` - Papua New Guinea
         * * `PY` - Paraguay
         * * `PE` - Peru
         * * `PH` - Philippines
         * * `PN` - Pitcairn
         * * `PL` - Poland
         * * `PT` - Portugal
         * * `PR` - Puerto Rico
         * * `QA` - Qatar
         * * `RE` - Réunion
         * * `RO` - Romania
         * * `RU` - Russia
         * * `RW` - Rwanda
         * * `BL` - Saint Barthélemy
         * * `SH` - Saint Helena, Ascension and Tristan da Cunha
         * * `KN` - Saint Kitts and Nevis
         * * `LC` - Saint Lucia
         * * `MF` - Saint Martin (French part)
         * * `PM` - Saint Pierre and Miquelon
         * * `VC` - Saint Vincent and the Grenadines
         * * `WS` - Samoa
         * * `SM` - San Marino
         * * `ST` - Sao Tome and Principe
         * * `SA` - Saudi Arabia
         * * `SN` - Senegal
         * * `RS` - Serbia
         * * `SC` - Seychelles
         * * `SL` - Sierra Leone
         * * `SG` - Singapore
         * * `SX` - Sint Maarten (Dutch part)
         * * `SK` - Slovakia
         * * `SI` - Slovenia
         * * `SB` - Solomon Islands
         * * `SO` - Somalia
         * * `ZA` - South Africa
         * * `GS` - South Georgia and the South Sandwich Islands
         * * `KR` - South Korea
         * * `SS` - South Sudan
         * * `ES` - Spain
         * * `LK` - Sri Lanka
         * * `SD` - Sudan
         * * `SR` - Suriname
         * * `SJ` - Svalbard and Jan Mayen
         * * `SE` - Sweden
         * * `CH` - Switzerland
         * * `SY` - Syria
         * * `TW` - Taiwan
         * * `TJ` - Tajikistan
         * * `TZ` - Tanzania
         * * `TH` - Thailand
         * * `TL` - Timor-Leste
         * * `TG` - Togo
         * * `TK` - Tokelau
         * * `TO` - Tonga
         * * `TT` - Trinidad and Tobago
         * * `TN` - Tunisia
         * * `TR` - Türkiye
         * * `TM` - Turkmenistan
         * * `TC` - Turks and Caicos Islands
         * * `TV` - Tuvalu
         * * `UG` - Uganda
         * * `UA` - Ukraine
         * * `AE` - United Arab Emirates
         * * `GB` - United Kingdom
         * * `UM` - United States Minor Outlying Islands
         * * `US` - United States of America
         * * `UY` - Uruguay
         * * `UZ` - Uzbekistan
         * * `VU` - Vanuatu
         * * `VE` - Venezuela
         * * `VN` - Vietnam
         * * `VG` - Virgin Islands (British)
         * * `VI` - Virgin Islands (U.S.)
         * * `WF` - Wallis and Futuna
         * * `EH` - Western Sahara
         * * `YE` - Yemen
         * * `ZM` - Zambia
         * * `ZW` - Zimbabwe
         */
        nationalities?: (
          | 'AD'
          | 'AE'
          | 'AF'
          | 'AG'
          | 'AI'
          | 'AL'
          | 'AM'
          | 'AO'
          | 'AQ'
          | 'AR'
          | 'AS'
          | 'AT'
          | 'AU'
          | 'AW'
          | 'AX'
          | 'AZ'
          | 'BA'
          | 'BB'
          | 'BD'
          | 'BE'
          | 'BF'
          | 'BG'
          | 'BH'
          | 'BI'
          | 'BJ'
          | 'BL'
          | 'BM'
          | 'BN'
          | 'BO'
          | 'BQ'
          | 'BR'
          | 'BS'
          | 'BT'
          | 'BV'
          | 'BW'
          | 'BY'
          | 'BZ'
          | 'CA'
          | 'CC'
          | 'CD'
          | 'CF'
          | 'CG'
          | 'CH'
          | 'CI'
          | 'CK'
          | 'CL'
          | 'CM'
          | 'CN'
          | 'CO'
          | 'CR'
          | 'CU'
          | 'CV'
          | 'CW'
          | 'CX'
          | 'CY'
          | 'CZ'
          | 'DE'
          | 'DJ'
          | 'DK'
          | 'DM'
          | 'DO'
          | 'DZ'
          | 'EC'
          | 'EE'
          | 'EG'
          | 'EH'
          | 'ER'
          | 'ES'
          | 'ET'
          | 'FI'
          | 'FJ'
          | 'FK'
          | 'FM'
          | 'FO'
          | 'FR'
          | 'GA'
          | 'GB'
          | 'GD'
          | 'GE'
          | 'GF'
          | 'GG'
          | 'GH'
          | 'GI'
          | 'GL'
          | 'GM'
          | 'GN'
          | 'GP'
          | 'GQ'
          | 'GR'
          | 'GS'
          | 'GT'
          | 'GU'
          | 'GW'
          | 'GY'
          | 'HK'
          | 'HM'
          | 'HN'
          | 'HR'
          | 'HT'
          | 'HU'
          | 'ID'
          | 'IE'
          | 'IL'
          | 'IM'
          | 'IN'
          | 'IO'
          | 'IQ'
          | 'IR'
          | 'IS'
          | 'IT'
          | 'JE'
          | 'JM'
          | 'JO'
          | 'JP'
          | 'KE'
          | 'KG'
          | 'KH'
          | 'KI'
          | 'KM'
          | 'KN'
          | 'KP'
          | 'KR'
          | 'KW'
          | 'KY'
          | 'KZ'
          | 'LA'
          | 'LB'
          | 'LC'
          | 'LI'
          | 'LK'
          | 'LR'
          | 'LS'
          | 'LT'
          | 'LU'
          | 'LV'
          | 'LY'
          | 'MA'
          | 'MC'
          | 'MD'
          | 'ME'
          | 'MF'
          | 'MG'
          | 'MH'
          | 'MK'
          | 'ML'
          | 'MM'
          | 'MN'
          | 'MO'
          | 'MP'
          | 'MQ'
          | 'MR'
          | 'MS'
          | 'MT'
          | 'MU'
          | 'MV'
          | 'MW'
          | 'MX'
          | 'MY'
          | 'MZ'
          | 'NA'
          | 'NC'
          | 'NE'
          | 'NF'
          | 'NG'
          | 'NI'
          | 'NL'
          | 'NO'
          | 'NP'
          | 'NR'
          | 'NU'
          | 'NZ'
          | 'OM'
          | 'PA'
          | 'PE'
          | 'PF'
          | 'PG'
          | 'PH'
          | 'PK'
          | 'PL'
          | 'PM'
          | 'PN'
          | 'PR'
          | 'PS'
          | 'PT'
          | 'PW'
          | 'PY'
          | 'QA'
          | 'RE'
          | 'RO'
          | 'RS'
          | 'RU'
          | 'RW'
          | 'SA'
          | 'SB'
          | 'SC'
          | 'SD'
          | 'SE'
          | 'SG'
          | 'SH'
          | 'SI'
          | 'SJ'
          | 'SK'
          | 'SL'
          | 'SM'
          | 'SN'
          | 'SO'
          | 'SR'
          | 'SS'
          | 'ST'
          | 'SV'
          | 'SX'
          | 'SY'
          | 'SZ'
          | 'TC'
          | 'TD'
          | 'TF'
          | 'TG'
          | 'TH'
          | 'TJ'
          | 'TK'
          | 'TL'
          | 'TM'
          | 'TN'
          | 'TO'
          | 'TR'
          | 'TT'
          | 'TV'
          | 'TW'
          | 'TZ'
          | 'UA'
          | 'UG'
          | 'UM'
          | 'US'
          | 'UY'
          | 'UZ'
          | 'VA'
          | 'VC'
          | 'VE'
          | 'VG'
          | 'VI'
          | 'VN'
          | 'VU'
          | 'WF'
          | 'WS'
          | 'XK'
          | 'YE'
          | 'YT'
          | 'ZA'
          | 'ZM'
          | 'ZW'
        )[]
        no_lesson_at_the_moment?: boolean
        /** Multiple values may be separated by commas. */
        number_of_weeks?: number[]
        /**
         * Ordering
         *
         * * `nickname` - Nickname
         * * `-nickname` - Nickname (descending)
         * * `username` - Username
         * * `-username` - Username (descending)
         * * `first_name` - First name
         * * `-first_name` - First name (descending)
         * * `last_name` - Last name
         * * `-last_name` - Last name (descending)
         * * `full_name` - Full name
         * * `-full_name` - Full name (descending)
         * * `grade_level_ordering` - Grade level ordering
         * * `-grade_level_ordering` - Grade level ordering (descending)
         * * `date_of_birth` - Date of birth
         * * `-date_of_birth` - Date of birth (descending)
         * * `age` - Age
         * * `-age` - Age (descending)
         * * `gender` - Gender
         * * `-gender` - Gender (descending)
         * * `category` - Category
         * * `-category` - Category (descending)
         * * `package_option` - Package option
         * * `-package_option` - Package option (descending)
         * * `number_of_weeks_in_camps` - Number of weeks in camps
         * * `-number_of_weeks_in_camps` - Number of weeks in camps (descending)
         * * `nationalities` - Nationalities
         * * `-nationalities` - Nationalities (descending)
         * * `certificate` - Certificate
         * * `-certificate` - Certificate (descending)
         * * `panel_students` - Panel students
         * * `-panel_students` - Panel students (descending)
         * * `week_of_camps` - Week of camps
         * * `-week_of_camps` - Week of camps (descending)
         * * `date_joined` - Date joined
         * * `-date_joined` - Date joined (descending)
         * * `date_of_entry` - Date of entry
         * * `-date_of_entry` - Date of entry (descending)
         * * `saldo_status` - Saldo status
         * * `-saldo_status` - Saldo status (descending)
         * * `academic_status` - Academic status
         * * `-academic_status` - Academic status (descending)
         * * `house` - House
         * * `-house` - House (descending)
         */
        ordering?: (
          | '-academic_status'
          | '-age'
          | '-category'
          | '-certificate'
          | '-date_joined'
          | '-date_of_birth'
          | '-date_of_entry'
          | '-first_name'
          | '-full_name'
          | '-gender'
          | '-grade_level_ordering'
          | '-house'
          | '-last_name'
          | '-nationalities'
          | '-nickname'
          | '-number_of_weeks_in_camps'
          | '-package_option'
          | '-panel_students'
          | '-saldo_status'
          | '-username'
          | '-week_of_camps'
          | 'academic_status'
          | 'age'
          | 'category'
          | 'certificate'
          | 'date_joined'
          | 'date_of_birth'
          | 'date_of_entry'
          | 'first_name'
          | 'full_name'
          | 'gender'
          | 'grade_level_ordering'
          | 'house'
          | 'last_name'
          | 'nationalities'
          | 'nickname'
          | 'number_of_weeks_in_camps'
          | 'package_option'
          | 'panel_students'
          | 'saldo_status'
          | 'username'
          | 'week_of_camps'
        )[]
        /**
         * * `no` - No
         * * `current` - Current
         * * `future` - Future
         */
        panel_students?: ('current' | 'future' | 'no')[]
        /**
         * * `dayschool` - Day School
         * * `boarding` - Boarding
         * * `external` - External
         */
        program_type?: 'boarding' | 'dayschool' | 'external'
        room?: string
        room_code?: string
        /**
         * * `5` - Settled
         * * `10` - First reminder
         * * `15` - Second reminder
         * * `17` - Unsettled
         * * `20` - App locked
         */
        saldo_status?: (10 | 15 | 17 | 20 | 5)[]
        /** A search term. */
        search?: string
        section?: number
        show_inactive?: boolean
        /**
         * * `W1` - Week 1 2024
         * * `W2` - Week 2 2024
         * * `W3` - Week 3 2024
         * * `W4` - Week 4 2024
         * * `W1_2025` - Week 1 2025
         * * `W2_2025` - Week 2 2025
         * * `W3_2025` - Week 3 2025
         * * `W4_2025` - Week 4 2025
         * * `W1_2026` - Week 1 2026
         * * `W2_2026` - Week 2 2026
         * * `W3_2026` - Week 3 2026
         * * `W4_2026` - Week 4 2026
         * * `W1_2027` - Week 1 2027
         * * `W2_2027` - Week 2 2027
         * * `W3_2027` - Week 3 2027
         * * `W4_2027` - Week 4 2027
         */
        week_of_camps?: (
          | 'W1'
          | 'W1_2025'
          | 'W1_2026'
          | 'W1_2027'
          | 'W2'
          | 'W2_2025'
          | 'W2_2026'
          | 'W2_2027'
          | 'W3'
          | 'W3_2025'
          | 'W3_2026'
          | 'W3_2027'
          | 'W4'
          | 'W4_2025'
          | 'W4_2026'
          | 'W4_2027'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentsAvailableFilters, T>, any>({
        path: `/students/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags students
     * @name StudentsCommentsList
     * @request GET:/students/comments/
     * @secure
     */,
    studentsCommentsList: <T extends FetchKeys<PaginatedCommentList>>(
      query?: {
        /**
         * * `academic` - academic
         * * `boarding` - boarding
         * * `progress` - progress
         */
        comment_type?: ('academic' | 'boarding' | 'progress')[]
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** @format uuid */
        related_author?: string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedCommentList, T>, any>({
        path: `/students/comments/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags students
     * @name StudentsCommentsAddedByChoicesList
     * @request GET:/students/comments/added-by-choices/
     * @secure
     */,
    studentsCommentsAddedByChoicesList: <T extends FetchKeys<StringOption[]>>(
      query?: {
        /**
         * * `academic` - academic
         * * `boarding` - boarding
         * * `progress` - progress
         */
        comment_type?: ('academic' | 'boarding' | 'progress')[]
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        /** @format uuid */
        related_author?: string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/students/comments/added-by-choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Endpoint dedicated to summer camps, in the future may be used in regular panel.
     *
     * @tags students
     * @name StudentsCommentsPdfRetrieve
     * @request GET:/students/comments/pdf/
     * @secure
     */,
    studentsCommentsPdfRetrieve: <T extends FetchKeys<Comment>>(
      query?: {
        /**
         * * `academic` - academic
         * * `boarding` - boarding
         * * `progress` - progress
         */
        comment_type?: ('academic' | 'boarding' | 'progress')[]
        /** @format date */
        created_after?: string
        /** @format date */
        created_before?: string
        /** @format uuid */
        related_author?: string
        /** @format uuid */
        student?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Comment, T>, any>({
        path: `/students/comments/pdf/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags students
     * @name StudentsCurrentLocationsList
     * @request GET:/students/current-locations/
     * @secure
     */,
    studentsCurrentLocationsList: <T extends FetchKeys<CurrentLocation[]>>(
      query?: {
        boarding_status?: string
        current_location?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CurrentLocation[], T>, any>({
        path: `/students/current-locations/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags students
     * @name StudentsMeRetrieve
     * @request GET:/students/me/
     * @secure
     */,
    studentsMeRetrieve: <T extends FetchKeys<StudentProfile>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentProfile, T>, any>({
        path: `/students/me/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags students
     * @name StudentsMeUpdate
     * @request PUT:/students/me/
     * @secure
     */,
    studentsMeUpdate: <T extends FetchKeys<StudentProfile>>(
      data: StudentProfileRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentProfile, T>, any>({
        path: `/students/me/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags students
     * @name StudentsMePartialUpdate
     * @request PATCH:/students/me/
     * @secure
     */,
    studentsMePartialUpdate: <T extends FetchKeys<StudentProfile>>(
      data: PatchedStudentProfileRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentProfile, T>, any>({
        path: `/students/me/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Get current state of user new students banner.
     *
     * @tags students
     * @name StudentsNewBannerList
     * @request GET:/students/new/banner/
     * @secure
     */,
    studentsNewBannerList: <T extends FetchKeys<NewStudentsBannerResponse[]>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NewStudentsBannerResponse[], T>, void>({
        path: `/students/new/banner/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Remove specified user new students from banner.
     *
     * @tags students
     * @name StudentsNewBannerCreate
     * @request POST:/students/new/banner/
     * @secure
     */,
    studentsNewBannerCreate: <T extends FetchKeys<void>>(
      data: StudentIdsListRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, void>({
        path: `/students/new/banner/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params
      })
    /**
     * @description Get current state of user new students counter.
     *
     * @tags students
     * @name StudentsNewCounterRetrieve
     * @request GET:/students/new/counter/
     * @secure
     */,
    studentsNewCounterRetrieve: <T extends FetchKeys<NewStudentsCountResponse>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NewStudentsCountResponse, T>, void>({
        path: `/students/new/counter/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Reset user new students counter.
     *
     * @tags students
     * @name StudentsNewCounterUpdate
     * @request PUT:/students/new/counter/
     * @secure
     */,
    studentsNewCounterUpdate: <T extends FetchKeys<void>>(
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, void>({
        path: `/students/new/counter/`,
        method: 'PUT',
        secure: true,
        ...params
      })
    /**
     * @description Returns a CSV file with students email addresses.
     *
     * @tags students
     * @name StudentsReportsEmailAddressesRetrieve
     * @request GET:/students/reports/email-addresses/
     * @secure
     */,
    studentsReportsEmailAddressesRetrieve: <T extends FetchKeys<string>>(
      query?: {
        academic_status?: (15 | 30 | 45 | 60)[]
        /** @default false */
        is_student_new?: boolean

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<string, T>, any>({
        path: `/students/reports/email-addresses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * @description Returns a CSV file with students insurance numbers.
     *
     * @tags students
     * @name StudentsReportsInsuranceNumbersRetrieve
     * @request GET:/students/reports/insurance-numbers/
     * @secure
     */,
    studentsReportsInsuranceNumbersRetrieve: <T extends FetchKeys<string>>(
      query?: {
        /** Academic status filter. Can be provided multiple times. */
        academic_status?: number[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<string, T>, any>({
        path: `/students/reports/insurance-numbers/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
  }
  suggester = {
    /**
     * No description
     *
     * @tags suggester
     * @name SuggesterDraftactivitySuggestionsApplyCreate
     * @request POST:/suggester/draftactivity/suggestions/{id}/apply/
     * @secure
     */
    suggesterDraftactivitySuggestionsApplyCreate: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, MessageResponseSchema>({
        path: `/suggester/draftactivity/suggestions/${id}/apply/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags suggester
     * @name SuggesterDraftactivityTasksCreate
     * @request POST:/suggester/draftactivity/tasks/
     * @secure
     */,
    suggesterDraftactivityTasksCreate: <
      T extends FetchKeys<CreateSuggesterTaskResponse>
    >(
      data: CreateDraftActivitySuggesterTaskRequest,
      query?: {
        ignore_conflicts?: boolean
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<CreateSuggesterTaskResponse, T>,
        MessageResponseSchema
      >({
        path: `/suggester/draftactivity/tasks/`,
        method: 'POST',
        query: postprocessQuery(query),
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags suggester
     * @name SuggesterDraftactivityTasksRetrieve
     * @request GET:/suggester/draftactivity/tasks/{id}/
     * @secure
     */,
    suggesterDraftactivityTasksRetrieve: <
      T extends FetchKeys<RetrieveDraftActivitySuggesterTask>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RetrieveDraftActivitySuggesterTask, T>, any>({
        path: `/suggester/draftactivity/tasks/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags suggester
     * @name SuggesterDraftactivityTasksConfigRetrieve
     * @request GET:/suggester/draftactivity/tasks/config/
     * @secure
     */,
    suggesterDraftactivityTasksConfigRetrieve: <
      T extends FetchKeys<SuggesterTaskConfig>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SuggesterTaskConfig, T>, any>({
        path: `/suggester/draftactivity/tasks/config/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags suggester
     * @name SuggesterDraftactivityTasksProcessStateRetrieve
     * @request GET:/suggester/draftactivity/tasks/process_state/
     * @secure
     */,
    suggesterDraftactivityTasksProcessStateRetrieve: <
      T extends FetchKeys<DraftActivityInProgressSuggesterTask>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DraftActivityInProgressSuggesterTask, T>, any>({
        path: `/suggester/draftactivity/tasks/process_state/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags suggester
     * @name SuggesterIntegrationWebhookCreate
     * @request POST:/suggester/integration/webhook
     * @secure
     */,
    suggesterIntegrationWebhookCreate: <
      T extends FetchKeys<SuggesterExternalEvent>
    >(
      data: SuggesterExternalEventRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SuggesterExternalEvent, T>, any>({
        path: `/suggester/integration/webhook`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags suggester
     * @name SuggesterSuggestionsPreviewList
     * @request GET:/suggester/suggestions/{id}/preview
     * @secure
     */,
    suggesterSuggestionsPreviewList: <
      T extends FetchKeys<PaginatedDraftActivitySuggestionPreviewList>
    >(
      id: number,
      query?: {
        /** @format uuid */
        course?: string
        exclude_regular_activities?: boolean
        exclude_tae_activities?: boolean
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        room?: string
        students?: string[]
        /** @format uuid */
        teacher?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<PaginatedDraftActivitySuggestionPreviewList, T>,
        any
      >({
        path: `/suggester/suggestions/${id}/preview`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags suggester
     * @name SuggesterSuggestionsPreviewAvailableFiltersRetrieve
     * @request GET:/suggester/suggestions/{id}/preview/available-filters
     * @secure
     */,
    suggesterSuggestionsPreviewAvailableFiltersRetrieve: <
      T extends FetchKeys<DraftActivityAvailableFilters>
    >(
      id: number,
      query?: {
        /** @format uuid */
        course?: string
        exclude_regular_activities?: boolean
        exclude_tae_activities?: boolean
        room?: string
        students?: string[]
        /** @format uuid */
        teacher?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DraftActivityAvailableFilters, T>, any>({
        path: `/suggester/suggestions/${id}/preview/available-filters`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  summerCamps = {
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsRetrieve
     * @request GET:/summer-camps/{studentId}/
     * @secure
     */
    summerCampsRetrieve: <T extends FetchKeys<SummerCampStudent>>(
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerCampStudent, T>, any>({
        path: `/summer-camps/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsUpdate
     * @request PUT:/summer-camps/{studentId}/
     * @secure
     */,
    summerCampsUpdate: <T extends FetchKeys<SummerCampStudent>>(
      studentId: string,
      data: SummerCampStudentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerCampStudent, T>, any>({
        path: `/summer-camps/${studentId}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsPartialUpdate
     * @request PATCH:/summer-camps/{studentId}/
     * @secure
     */,
    summerCampsPartialUpdate: <T extends FetchKeys<SummerCampStudent>>(
      studentId: string,
      data: PatchedSummerCampStudentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerCampStudent, T>, any>({
        path: `/summer-camps/${studentId}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsArrivalInfoRetrieve
     * @request GET:/summer-camps/{studentId}/arrival-info/
     * @secure
     */,
    summerCampsArrivalInfoRetrieve: <T extends FetchKeys<ArrivalInfo>>(
      studentId: string,
      query?: {
        response_format?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ArrivalInfo, T>, any>({
        path: `/summer-camps/${studentId}/arrival-info/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsArrivalInfoUpdate
     * @request PUT:/summer-camps/{studentId}/arrival-info/
     * @secure
     */,
    summerCampsArrivalInfoUpdate: <T extends FetchKeys<ArrivalInfo>>(
      studentId: string,
      data: ArrivalInfoRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ArrivalInfo, T>, any>({
        path: `/summer-camps/${studentId}/arrival-info/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description DEPRECATED: This endpoint is deprecated. Use student legacies endpoint instead. This endpoint will be removed in a future version.
     *
     * @tags summer-camps
     * @name SummerCampsLegaciesRetrieve
     * @request GET:/summer-camps/{studentId}/legacies/
     * @deprecated
     * @secure
     */,
    summerCampsLegaciesRetrieve: <
      T extends FetchKeys<SummerCampStudentLegacies>
    >(
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerCampStudentLegacies, T>, any>({
        path: `/summer-camps/${studentId}/legacies/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description DEPRECATED: This endpoint is deprecated. Use student legacies endpoint instead. This endpoint will be removed in a future version.
     *
     * @tags summer-camps
     * @name SummerCampsLegaciesUpdate
     * @request PUT:/summer-camps/{studentId}/legacies/
     * @deprecated
     * @secure
     */,
    summerCampsLegaciesUpdate: <T extends FetchKeys<SummerCampStudentLegacies>>(
      studentId: string,
      data: SummerCampStudentLegaciesRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerCampStudentLegacies, T>, any>({
        path: `/summer-camps/${studentId}/legacies/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsAssignsRetrieve
     * @request GET:/summer-camps/assigns/{student}/
     * @secure
     */,
    summerCampsAssignsRetrieve: <T extends FetchKeys<SummerCampStudentAssigns>>(
      student: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerCampStudentAssigns, T>, any>({
        path: `/summer-camps/assigns/${student}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsAssignsUpdate
     * @request PUT:/summer-camps/assigns/{student}/
     * @secure
     */,
    summerCampsAssignsUpdate: <T extends FetchKeys<SummerCampStudentAssigns>>(
      student: string,
      data: SummerCampStudentAssignsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerCampStudentAssigns, T>, any>({
        path: `/summer-camps/assigns/${student}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsAssignsPartialUpdate
     * @request PATCH:/summer-camps/assigns/{student}/
     * @secure
     */,
    summerCampsAssignsPartialUpdate: <
      T extends FetchKeys<SummerCampStudentAssigns>
    >(
      student: string,
      data: PatchedSummerCampStudentAssignsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerCampStudentAssigns, T>, any>({
        path: `/summer-camps/assigns/${student}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsAssignsChoicesRetrieve
     * @request GET:/summer-camps/assigns/choices/
     * @secure
     */,
    summerCampsAssignsChoicesRetrieve: <
      T extends FetchKeys<SummerAssignOptionsResponse>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerAssignOptionsResponse, T>, any>({
        path: `/summer-camps/assigns/choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCertificateRetrieve
     * @request GET:/summer-camps/certificate/{id}/
     * @secure
     */,
    summerCampsCertificateRetrieve: <
      T extends FetchKeys<SummerCertificateAttachment>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerCertificateAttachment, T>, any>({
        path: `/summer-camps/certificate/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCertificateCreate
     * @request POST:/summer-camps/certificate/{id}/
     * @secure
     */,
    summerCampsCertificateCreate: <
      T extends FetchKeys<SummerCertificateAttachment>
    >(
      id: string,
      data: GenerateSummerCertificateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerCertificateAttachment, T>, any>({
        path: `/summer-camps/certificate/${id}/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCertificateCeremonySummaryList
     * @request GET:/summer-camps/certificate/ceremony-summary/
     * @secure
     */,
    summerCampsCertificateCeremonySummaryList: <
      T extends FetchKeys<SummerCertificateCeremonySummary[]>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerCertificateCeremonySummary[], T>, any>({
        path: `/summer-camps/certificate/ceremony-summary/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCertificateCoursesSummaryRetrieve
     * @request GET:/summer-camps/certificate/courses-summary/{student}/
     * @secure
     */,
    summerCampsCertificateCoursesSummaryRetrieve: <
      T extends FetchKeys<StudentSummerSummary>
    >(
      student: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentSummerSummary, T>, any>({
        path: `/summer-camps/certificate/courses-summary/${student}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCertificateDraftRetrieve
     * @request GET:/summer-camps/certificate/draft/{id}/
     * @secure
     */,
    summerCampsCertificateDraftRetrieve: <
      T extends FetchKeys<SummerCertificateDraft>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerCertificateDraft, T>, any>({
        path: `/summer-camps/certificate/draft/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCertificateDraftUpdate
     * @request PUT:/summer-camps/certificate/draft/{id}/
     * @secure
     */,
    summerCampsCertificateDraftUpdate: <
      T extends FetchKeys<SummerCertificateDraft>
    >(
      id: string,
      data: SummerCertificateDraftRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerCertificateDraft, T>, any>({
        path: `/summer-camps/certificate/draft/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCertificateDraftPartialUpdate
     * @request PATCH:/summer-camps/certificate/draft/{id}/
     * @secure
     */,
    summerCampsCertificateDraftPartialUpdate: <
      T extends FetchKeys<SummerCertificateDraft>
    >(
      id: string,
      data: PatchedSummerCertificateDraftRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerCertificateDraft, T>, any>({
        path: `/summer-camps/certificate/draft/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCertificateGenerateZippedCertificatesCreate
     * @request POST:/summer-camps/certificate/generate-zipped-certificates/
     * @secure
     */,
    summerCampsCertificateGenerateZippedCertificatesCreate: <
      T extends FetchKeys<SummerCertificateAttachment>
    >(
      data: GenerateZippedCertificatesRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerCertificateAttachment, T>, any>({
        path: `/summer-camps/certificate/generate-zipped-certificates/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCommentsAcademicList2
     * @request GET:/summer-camps/comments/{studentId}/academic/
     * @secure
     */,
    summerCampsCommentsAcademicList2: <
      T extends FetchKeys<SummerAcademicComment[]>
    >(
      studentId: string,
      query?: {
        /** @format uuid */
        course?: string
        /** @format date */
        from_date?: string
        /** @format date */
        to_date?: string
        /**
         * * `W1` - Week 1 2024
         * * `W2` - Week 2 2024
         * * `W3` - Week 3 2024
         * * `W4` - Week 4 2024
         * * `W1_2025` - Week 1 2025
         * * `W2_2025` - Week 2 2025
         * * `W3_2025` - Week 3 2025
         * * `W4_2025` - Week 4 2025
         * * `W1_2026` - Week 1 2026
         * * `W2_2026` - Week 2 2026
         * * `W3_2026` - Week 3 2026
         * * `W4_2026` - Week 4 2026
         * * `W1_2027` - Week 1 2027
         * * `W2_2027` - Week 2 2027
         * * `W3_2027` - Week 3 2027
         * * `W4_2027` - Week 4 2027
         */
        week?:
          | 'W1'
          | 'W1_2025'
          | 'W1_2026'
          | 'W1_2027'
          | 'W2'
          | 'W2_2025'
          | 'W2_2026'
          | 'W2_2027'
          | 'W3'
          | 'W3_2025'
          | 'W3_2026'
          | 'W3_2027'
          | 'W4'
          | 'W4_2025'
          | 'W4_2026'
          | 'W4_2027'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerAcademicComment[], T>, any>({
        path: `/summer-camps/comments/${studentId}/academic/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCommentsAcademicCreate
     * @request POST:/summer-camps/comments/{studentId}/academic/
     * @secure
     */,
    summerCampsCommentsAcademicCreate: <
      T extends FetchKeys<SummerAcademicCommentCreate>
    >(
      studentId: string,
      data: SummerAcademicCommentCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerAcademicCommentCreate, T>, any>({
        path: `/summer-camps/comments/${studentId}/academic/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCommentsAcademicRetrieve
     * @request GET:/summer-camps/comments/{studentId}/academic/{id}/
     * @secure
     */,
    summerCampsCommentsAcademicRetrieve: <
      T extends FetchKeys<SummerAcademicComment>
    >(
      id: number,
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerAcademicComment, T>, any>({
        path: `/summer-camps/comments/${studentId}/academic/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCommentsAcademicUpdate
     * @request PUT:/summer-camps/comments/{studentId}/academic/{id}/
     * @secure
     */,
    summerCampsCommentsAcademicUpdate: <
      T extends FetchKeys<SummerAcademicComment>
    >(
      id: number,
      studentId: string,
      data: SummerAcademicCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerAcademicComment, T>, any>({
        path: `/summer-camps/comments/${studentId}/academic/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCommentsAcademicPartialUpdate
     * @request PATCH:/summer-camps/comments/{studentId}/academic/{id}/
     * @secure
     */,
    summerCampsCommentsAcademicPartialUpdate: <
      T extends FetchKeys<SummerAcademicComment>
    >(
      id: number,
      studentId: string,
      data: PatchedSummerAcademicCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerAcademicComment, T>, any>({
        path: `/summer-camps/comments/${studentId}/academic/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCommentsAcademicDestroy
     * @request DELETE:/summer-camps/comments/{studentId}/academic/{id}/
     * @secure
     */,
    summerCampsCommentsAcademicDestroy: <T extends FetchKeys<void>>(
      id: number,
      studentId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/summer-camps/comments/${studentId}/academic/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCommentsBoardingList
     * @request GET:/summer-camps/comments/{studentId}/boarding/
     * @secure
     */,
    summerCampsCommentsBoardingList: <
      T extends FetchKeys<SummerBoardingComment[]>
    >(
      studentId: string,
      query?: {
        /** @format date */
        from_date?: string
        /** @format date */
        to_date?: string
        /**
         * * `W1` - Week 1 2024
         * * `W2` - Week 2 2024
         * * `W3` - Week 3 2024
         * * `W4` - Week 4 2024
         * * `W1_2025` - Week 1 2025
         * * `W2_2025` - Week 2 2025
         * * `W3_2025` - Week 3 2025
         * * `W4_2025` - Week 4 2025
         * * `W1_2026` - Week 1 2026
         * * `W2_2026` - Week 2 2026
         * * `W3_2026` - Week 3 2026
         * * `W4_2026` - Week 4 2026
         * * `W1_2027` - Week 1 2027
         * * `W2_2027` - Week 2 2027
         * * `W3_2027` - Week 3 2027
         * * `W4_2027` - Week 4 2027
         */
        week?:
          | 'W1'
          | 'W1_2025'
          | 'W1_2026'
          | 'W1_2027'
          | 'W2'
          | 'W2_2025'
          | 'W2_2026'
          | 'W2_2027'
          | 'W3'
          | 'W3_2025'
          | 'W3_2026'
          | 'W3_2027'
          | 'W4'
          | 'W4_2025'
          | 'W4_2026'
          | 'W4_2027'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerBoardingComment[], T>, any>({
        path: `/summer-camps/comments/${studentId}/boarding/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCommentsBoardingCreate
     * @request POST:/summer-camps/comments/{studentId}/boarding/
     * @secure
     */,
    summerCampsCommentsBoardingCreate: <
      T extends FetchKeys<SummerBoardingCommentCreate>
    >(
      studentId: string,
      data: SummerBoardingCommentCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerBoardingCommentCreate, T>, any>({
        path: `/summer-camps/comments/${studentId}/boarding/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCommentsBoardingRetrieve
     * @request GET:/summer-camps/comments/{studentId}/boarding/{id}/
     * @secure
     */,
    summerCampsCommentsBoardingRetrieve: <
      T extends FetchKeys<SummerBoardingComment>
    >(
      id: number,
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerBoardingComment, T>, any>({
        path: `/summer-camps/comments/${studentId}/boarding/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCommentsBoardingUpdate
     * @request PUT:/summer-camps/comments/{studentId}/boarding/{id}/
     * @secure
     */,
    summerCampsCommentsBoardingUpdate: <
      T extends FetchKeys<SummerBoardingComment>
    >(
      id: number,
      studentId: string,
      data: SummerBoardingCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerBoardingComment, T>, any>({
        path: `/summer-camps/comments/${studentId}/boarding/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCommentsBoardingPartialUpdate
     * @request PATCH:/summer-camps/comments/{studentId}/boarding/{id}/
     * @secure
     */,
    summerCampsCommentsBoardingPartialUpdate: <
      T extends FetchKeys<SummerBoardingComment>
    >(
      id: number,
      studentId: string,
      data: PatchedSummerBoardingCommentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerBoardingComment, T>, any>({
        path: `/summer-camps/comments/${studentId}/boarding/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCommentsBoardingDestroy
     * @request DELETE:/summer-camps/comments/{studentId}/boarding/{id}/
     * @secure
     */,
    summerCampsCommentsBoardingDestroy: <T extends FetchKeys<void>>(
      id: number,
      studentId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/summer-camps/comments/${studentId}/boarding/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCommentsAcademicList
     * @request GET:/summer-camps/comments/academic/
     * @secure
     */,
    summerCampsCommentsAcademicList: <
      T extends FetchKeys<SummerAcademicCommentList[]>
    >(
      query?: {
        course?: string[]
        student?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerAcademicCommentList[], T>, any>({
        path: `/summer-camps/comments/academic/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCommentsExportList
     * @request GET:/summer-camps/comments/export/
     * @secure
     */,
    summerCampsCommentsExportList: <
      T extends FetchKeys<SummerCommentsExport[]>
    >(
      query?: {
        /** @format uuid */
        author?: string
        /**
         * * `academic` - Academic
         * * `boarding` - Boarding
         */
        comment_type?: ('academic' | 'boarding')[]
        /** @format uuid */
        student?: string
        /**
         * * `W1` - Week 1 2024
         * * `W2` - Week 2 2024
         * * `W3` - Week 3 2024
         * * `W4` - Week 4 2024
         * * `W1_2025` - Week 1 2025
         * * `W2_2025` - Week 2 2025
         * * `W3_2025` - Week 3 2025
         * * `W4_2025` - Week 4 2025
         * * `W1_2026` - Week 1 2026
         * * `W2_2026` - Week 2 2026
         * * `W3_2026` - Week 3 2026
         * * `W4_2026` - Week 4 2026
         * * `W1_2027` - Week 1 2027
         * * `W2_2027` - Week 2 2027
         * * `W3_2027` - Week 3 2027
         * * `W4_2027` - Week 4 2027
         */
        week?: (
          | 'W1'
          | 'W1_2025'
          | 'W1_2026'
          | 'W1_2027'
          | 'W2'
          | 'W2_2025'
          | 'W2_2026'
          | 'W2_2027'
          | 'W3'
          | 'W3_2025'
          | 'W3_2026'
          | 'W3_2027'
          | 'W4'
          | 'W4_2025'
          | 'W4_2026'
          | 'W4_2027'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerCommentsExport[], T>, any>({
        path: `/summer-camps/comments/export/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCommentsExportAvailableFiltersRetrieve
     * @request GET:/summer-camps/comments/export/available-filters/
     * @secure
     */,
    summerCampsCommentsExportAvailableFiltersRetrieve: <
      T extends FetchKeys<SummerCommentsExportFilters>
    >(
      query?: {
        /** @format uuid */
        author?: string
        /**
         * * `academic` - Academic
         * * `boarding` - Boarding
         */
        comment_type?: ('academic' | 'boarding')[]
        /** @format uuid */
        student?: string
        /**
         * * `W1` - Week 1 2024
         * * `W2` - Week 2 2024
         * * `W3` - Week 3 2024
         * * `W4` - Week 4 2024
         * * `W1_2025` - Week 1 2025
         * * `W2_2025` - Week 2 2025
         * * `W3_2025` - Week 3 2025
         * * `W4_2025` - Week 4 2025
         * * `W1_2026` - Week 1 2026
         * * `W2_2026` - Week 2 2026
         * * `W3_2026` - Week 3 2026
         * * `W4_2026` - Week 4 2026
         * * `W1_2027` - Week 1 2027
         * * `W2_2027` - Week 2 2027
         * * `W3_2027` - Week 3 2027
         * * `W4_2027` - Week 4 2027
         */
        week?: (
          | 'W1'
          | 'W1_2025'
          | 'W1_2026'
          | 'W1_2027'
          | 'W2'
          | 'W2_2025'
          | 'W2_2026'
          | 'W2_2027'
          | 'W3'
          | 'W3_2025'
          | 'W3_2026'
          | 'W3_2027'
          | 'W4'
          | 'W4_2025'
          | 'W4_2026'
          | 'W4_2027'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerCommentsExportFilters, T>, any>({
        path: `/summer-camps/comments/export/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsCommentsExportPdfRetrieve
     * @request GET:/summer-camps/comments/export/pdf/
     * @secure
     */,
    summerCampsCommentsExportPdfRetrieve: <
      T extends FetchKeys<SummerCommentsExport>
    >(
      query?: {
        /** @format uuid */
        author?: string
        /**
         * * `academic` - Academic
         * * `boarding` - Boarding
         */
        comment_type?: ('academic' | 'boarding')[]
        /** @format uuid */
        student?: string
        /**
         * * `W1` - Week 1 2024
         * * `W2` - Week 2 2024
         * * `W3` - Week 3 2024
         * * `W4` - Week 4 2024
         * * `W1_2025` - Week 1 2025
         * * `W2_2025` - Week 2 2025
         * * `W3_2025` - Week 3 2025
         * * `W4_2025` - Week 4 2025
         * * `W1_2026` - Week 1 2026
         * * `W2_2026` - Week 2 2026
         * * `W3_2026` - Week 3 2026
         * * `W4_2026` - Week 4 2026
         * * `W1_2027` - Week 1 2027
         * * `W2_2027` - Week 2 2027
         * * `W3_2027` - Week 3 2027
         * * `W4_2027` - Week 4 2027
         */
        week?: (
          | 'W1'
          | 'W1_2025'
          | 'W1_2026'
          | 'W1_2027'
          | 'W2'
          | 'W2_2025'
          | 'W2_2026'
          | 'W2_2027'
          | 'W3'
          | 'W3_2025'
          | 'W3_2026'
          | 'W3_2027'
          | 'W4'
          | 'W4_2025'
          | 'W4_2026'
          | 'W4_2027'
        )[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SummerCommentsExport, T>, any>({
        path: `/summer-camps/comments/export/pdf/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags summer-camps
     * @name SummerCampsWeeksRetrieve
     * @request GET:/summer-camps/weeks/
     * @secure
     */,
    summerCampsWeeksRetrieve: <T extends FetchKeys<WeekResults>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<WeekResults, T>, any>({
        path: `/summer-camps/weeks/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  tasksManagement = {
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags tasks-management
     * @name TasksManagementTasksList
     * @request GET:/tasks-management/tasks/
     * @secure
     */
    tasksManagementTasksList: <T extends FetchKeys<PaginatedTaskList>>(
      query?: {
        assignee?: string[]
        created_by?: string[]
        /** @format date-time */
        date_after?: string
        /** @format date-time */
        date_before?: string
        has_subtasks?: boolean
        is_cloned?: boolean
        is_recurring?: boolean
        /**
         * Ordering
         *
         * * `is_done` - Is done
         * * `-is_done` - Is done (descending)
         * * `deadline` - Deadline
         * * `-deadline` - Deadline (descending)
         * * `assignee` - Assignee
         * * `-assignee` - Assignee (descending)
         * * `team` - Team
         * * `-team` - Team (descending)
         */
        ordering?: (
          | '-assignee'
          | '-deadline'
          | '-is_done'
          | '-team'
          | 'assignee'
          | 'deadline'
          | 'is_done'
          | 'team'
        )[]
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /**
         * * `admin` - Admin
         * * `teacher` - Artisan
         * * `academichod` - Academic HOD
         * * `academicadmin` - Academic Admin
         * * `behaviourmanagement` - Behaviour Management
         * * `boardingdirector` - Boarding Director
         * * `campusheadteam` - Campus Head Team
         * * `headofhouse` - Head of House
         * * `healthcoordinator` - Health Coordinator
         * * `infoboardeditor` - Infoboard Editor
         * * `propertymanager` - Property Manager
         * * `securityteam` - Security & Safety Team
         * * `viewer` - View Only
         * * `housetutor` - House Tutor
         * * `nightguard` - Night Guard
         * * `idpadmin` - IDP Admin
         */
        role?: (
          | 'academicadmin'
          | 'academichod'
          | 'admin'
          | 'behaviourmanagement'
          | 'boardingdirector'
          | 'campusheadteam'
          | 'headofhouse'
          | 'healthcoordinator'
          | 'housetutor'
          | 'idpadmin'
          | 'infoboardeditor'
          | 'nightguard'
          | 'propertymanager'
          | 'securityteam'
          | 'teacher'
          | 'viewer'
        )[]
        /**
         * * `done` - Done
         * * `done_manually` - Done manually
         * * `overdue` - Overdue
         * * `close_deadline` - Close deadline
         * * `unscheduled` - Unscheduled
         * * `expired` - Expired
         */
        status?: (
          | 'close_deadline'
          | 'done'
          | 'done_manually'
          | 'expired'
          | 'overdue'
          | 'unscheduled'
        )[]
        /** @format uuid */
        team?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTaskList, T>, any>({
        path: `/tasks-management/tasks/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags tasks-management
     * @name TasksManagementTasksCreate
     * @request POST:/tasks-management/tasks/
     * @secure
     */,
    tasksManagementTasksCreate: <T extends FetchKeys<Task>>(
      data: TaskRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Task, T>, any>({
        path: `/tasks-management/tasks/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags tasks-management
     * @name TasksManagementTasksRetrieve
     * @request GET:/tasks-management/tasks/{id}/
     * @secure
     */,
    tasksManagementTasksRetrieve: <T extends FetchKeys<Task>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Task, T>, any>({
        path: `/tasks-management/tasks/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags tasks-management
     * @name TasksManagementTasksUpdate
     * @request PUT:/tasks-management/tasks/{id}/
     * @secure
     */,
    tasksManagementTasksUpdate: <T extends FetchKeys<Task>>(
      id: string,
      data: TaskRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Task, T>, any>({
        path: `/tasks-management/tasks/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags tasks-management
     * @name TasksManagementTasksPartialUpdate
     * @request PATCH:/tasks-management/tasks/{id}/
     * @secure
     */,
    tasksManagementTasksPartialUpdate: <T extends FetchKeys<Task>>(
      id: string,
      data: PatchedTaskRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Task, T>, any>({
        path: `/tasks-management/tasks/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags tasks-management
     * @name TasksManagementTasksDestroy
     * @request DELETE:/tasks-management/tasks/{id}/
     * @secure
     */,
    tasksManagementTasksDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/tasks-management/tasks/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags tasks-management
     * @name TasksManagementTasksToggleIsDoneCreate
     * @request POST:/tasks-management/tasks/{id}/toggle-is-done/
     * @secure
     */,
    tasksManagementTasksToggleIsDoneCreate: <
      T extends FetchKeys<TaskToggleIsDone>
    >(
      id: string,
      data: TaskToggleIsDoneRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TaskToggleIsDone, T>, any>({
        path: `/tasks-management/tasks/${id}/toggle-is-done/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags tasks-management
     * @name TasksManagementTasksToggleSubtaskIsDoneCreate
     * @request POST:/tasks-management/tasks/{id}/toggle-subtask-is-done/
     * @secure
     */,
    tasksManagementTasksToggleSubtaskIsDoneCreate: <
      T extends FetchKeys<TaskToggleSubtaskIsDone>
    >(
      id: string,
      data: TaskToggleSubtaskIsDoneRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TaskToggleSubtaskIsDone, T>, any>({
        path: `/tasks-management/tasks/${id}/toggle-subtask-is-done/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags tasks-management
     * @name TasksManagementTasksAvailableFiltersRetrieve
     * @request GET:/tasks-management/tasks/available-filters/
     * @secure
     */,
    tasksManagementTasksAvailableFiltersRetrieve: <T extends FetchKeys<Task>>(
      query?: {
        assignee?: string[]
        created_by?: string[]
        /** @format date-time */
        date_after?: string
        /** @format date-time */
        date_before?: string
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        has_subtasks?: boolean
        is_cloned?: boolean
        is_recurring?: boolean
        /**
         * Ordering
         *
         * * `is_done` - Is done
         * * `-is_done` - Is done (descending)
         * * `deadline` - Deadline
         * * `-deadline` - Deadline (descending)
         * * `assignee` - Assignee
         * * `-assignee` - Assignee (descending)
         * * `team` - Team
         * * `-team` - Team (descending)
         */
        ordering?: (
          | '-assignee'
          | '-deadline'
          | '-is_done'
          | '-team'
          | 'assignee'
          | 'deadline'
          | 'is_done'
          | 'team'
        )[]
        /**
         * * `admin` - Admin
         * * `teacher` - Artisan
         * * `academichod` - Academic HOD
         * * `academicadmin` - Academic Admin
         * * `behaviourmanagement` - Behaviour Management
         * * `boardingdirector` - Boarding Director
         * * `campusheadteam` - Campus Head Team
         * * `headofhouse` - Head of House
         * * `healthcoordinator` - Health Coordinator
         * * `infoboardeditor` - Infoboard Editor
         * * `propertymanager` - Property Manager
         * * `securityteam` - Security & Safety Team
         * * `viewer` - View Only
         * * `housetutor` - House Tutor
         * * `nightguard` - Night Guard
         * * `idpadmin` - IDP Admin
         */
        role?: (
          | 'academicadmin'
          | 'academichod'
          | 'admin'
          | 'behaviourmanagement'
          | 'boardingdirector'
          | 'campusheadteam'
          | 'headofhouse'
          | 'healthcoordinator'
          | 'housetutor'
          | 'idpadmin'
          | 'infoboardeditor'
          | 'nightguard'
          | 'propertymanager'
          | 'securityteam'
          | 'teacher'
          | 'viewer'
        )[]
        /**
         * * `done` - Done
         * * `done_manually` - Done manually
         * * `overdue` - Overdue
         * * `close_deadline` - Close deadline
         * * `unscheduled` - Unscheduled
         * * `expired` - Expired
         */
        status?: (
          | 'close_deadline'
          | 'done'
          | 'done_manually'
          | 'expired'
          | 'overdue'
          | 'unscheduled'
        )[]
        /** @format uuid */
        team?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Task, T>, any>({
        path: `/tasks-management/tasks/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Returns incomplete tasks with today deadline for authenticated user
     *
     * @tags tasks-management
     * @name TasksManagementTasksCountDailyIncompleteRetrieve
     * @request GET:/tasks-management/tasks/count-daily-incomplete/
     * @secure
     */,
    tasksManagementTasksCountDailyIncompleteRetrieve: <
      T extends FetchKeys<IncompleteDailyTaskCount>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<IncompleteDailyTaskCount, T>, any>({
        path: `/tasks-management/tasks/count-daily-incomplete/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags tasks-management
     * @name TasksManagementTasksCountIncompleteRetrieve
     * @request GET:/tasks-management/tasks/count-incomplete/
     * @secure
     */,
    tasksManagementTasksCountIncompleteRetrieve: <
      T extends FetchKeys<IncompleteTaskCount>
    >(
      query?: {
        assignee?: string[]
        created_by?: string[]
        /** @format date-time */
        date_after?: string
        /** @format date-time */
        date_before?: string
        has_subtasks?: boolean
        is_cloned?: boolean
        is_recurring?: boolean
        /**
         * Ordering
         *
         * * `is_done` - Is done
         * * `-is_done` - Is done (descending)
         * * `deadline` - Deadline
         * * `-deadline` - Deadline (descending)
         * * `assignee` - Assignee
         * * `-assignee` - Assignee (descending)
         * * `team` - Team
         * * `-team` - Team (descending)
         */
        ordering?: (
          | '-assignee'
          | '-deadline'
          | '-is_done'
          | '-team'
          | 'assignee'
          | 'deadline'
          | 'is_done'
          | 'team'
        )[]
        /**
         * * `admin` - Admin
         * * `teacher` - Artisan
         * * `academichod` - Academic HOD
         * * `academicadmin` - Academic Admin
         * * `behaviourmanagement` - Behaviour Management
         * * `boardingdirector` - Boarding Director
         * * `campusheadteam` - Campus Head Team
         * * `headofhouse` - Head of House
         * * `healthcoordinator` - Health Coordinator
         * * `infoboardeditor` - Infoboard Editor
         * * `propertymanager` - Property Manager
         * * `securityteam` - Security & Safety Team
         * * `viewer` - View Only
         * * `housetutor` - House Tutor
         * * `nightguard` - Night Guard
         * * `idpadmin` - IDP Admin
         */
        role?: (
          | 'academicadmin'
          | 'academichod'
          | 'admin'
          | 'behaviourmanagement'
          | 'boardingdirector'
          | 'campusheadteam'
          | 'headofhouse'
          | 'healthcoordinator'
          | 'housetutor'
          | 'idpadmin'
          | 'infoboardeditor'
          | 'nightguard'
          | 'propertymanager'
          | 'securityteam'
          | 'teacher'
          | 'viewer'
        )[]
        /**
         * * `done` - Done
         * * `done_manually` - Done manually
         * * `overdue` - Overdue
         * * `close_deadline` - Close deadline
         * * `unscheduled` - Unscheduled
         * * `expired` - Expired
         */
        status?: (
          | 'close_deadline'
          | 'done'
          | 'done_manually'
          | 'expired'
          | 'overdue'
          | 'unscheduled'
        )[]
        /** @format uuid */
        team?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<IncompleteTaskCount, T>, any>({
        path: `/tasks-management/tasks/count-incomplete/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags tasks-management
     * @name TasksManagementTasksOverdueCountRetrieve
     * @request GET:/tasks-management/tasks/overdue-count/
     * @secure
     */,
    tasksManagementTasksOverdueCountRetrieve: <T extends FetchKeys<Task>>(
      query?: {
        assignee?: string[]
        created_by?: string[]
        /** @format date-time */
        date_after?: string
        /** @format date-time */
        date_before?: string
        has_subtasks?: boolean
        is_cloned?: boolean
        is_recurring?: boolean
        /**
         * Ordering
         *
         * * `is_done` - Is done
         * * `-is_done` - Is done (descending)
         * * `deadline` - Deadline
         * * `-deadline` - Deadline (descending)
         * * `assignee` - Assignee
         * * `-assignee` - Assignee (descending)
         * * `team` - Team
         * * `-team` - Team (descending)
         */
        ordering?: (
          | '-assignee'
          | '-deadline'
          | '-is_done'
          | '-team'
          | 'assignee'
          | 'deadline'
          | 'is_done'
          | 'team'
        )[]
        /**
         * * `admin` - Admin
         * * `teacher` - Artisan
         * * `academichod` - Academic HOD
         * * `academicadmin` - Academic Admin
         * * `behaviourmanagement` - Behaviour Management
         * * `boardingdirector` - Boarding Director
         * * `campusheadteam` - Campus Head Team
         * * `headofhouse` - Head of House
         * * `healthcoordinator` - Health Coordinator
         * * `infoboardeditor` - Infoboard Editor
         * * `propertymanager` - Property Manager
         * * `securityteam` - Security & Safety Team
         * * `viewer` - View Only
         * * `housetutor` - House Tutor
         * * `nightguard` - Night Guard
         * * `idpadmin` - IDP Admin
         */
        role?: (
          | 'academicadmin'
          | 'academichod'
          | 'admin'
          | 'behaviourmanagement'
          | 'boardingdirector'
          | 'campusheadteam'
          | 'headofhouse'
          | 'healthcoordinator'
          | 'housetutor'
          | 'idpadmin'
          | 'infoboardeditor'
          | 'nightguard'
          | 'propertymanager'
          | 'securityteam'
          | 'teacher'
          | 'viewer'
        )[]
        /**
         * * `done` - Done
         * * `done_manually` - Done manually
         * * `overdue` - Overdue
         * * `close_deadline` - Close deadline
         * * `unscheduled` - Unscheduled
         * * `expired` - Expired
         */
        status?: (
          | 'close_deadline'
          | 'done'
          | 'done_manually'
          | 'expired'
          | 'overdue'
          | 'unscheduled'
        )[]
        /** @format uuid */
        team?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Task, T>, any>({
        path: `/tasks-management/tasks/overdue-count/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags tasks-management
     * @name TasksManagementUserTaskTitlesList
     * @request GET:/tasks-management/user-task-titles/
     * @secure
     */,
    tasksManagementUserTaskTitlesList: <T extends FetchKeys<UserTaskTitle[]>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserTaskTitle[], T>, any>({
        path: `/tasks-management/user-task-titles/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags tasks-management
     * @name TasksManagementUserTaskTitlesCreate
     * @request POST:/tasks-management/user-task-titles/
     * @secure
     */,
    tasksManagementUserTaskTitlesCreate: <T extends FetchKeys<UserTaskTitle>>(
      data: UserTaskTitleRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserTaskTitle, T>, any>({
        path: `/tasks-management/user-task-titles/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags tasks-management
     * @name TasksManagementUserTaskTitlesRetrieve
     * @request GET:/tasks-management/user-task-titles/{id}/
     * @secure
     */,
    tasksManagementUserTaskTitlesRetrieve: <T extends FetchKeys<UserTaskTitle>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserTaskTitle, T>, any>({
        path: `/tasks-management/user-task-titles/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags tasks-management
     * @name TasksManagementUserTaskTitlesUpdate
     * @request PUT:/tasks-management/user-task-titles/{id}/
     * @secure
     */,
    tasksManagementUserTaskTitlesUpdate: <T extends FetchKeys<UserTaskTitle>>(
      id: string,
      data: UserTaskTitleRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserTaskTitle, T>, any>({
        path: `/tasks-management/user-task-titles/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags tasks-management
     * @name TasksManagementUserTaskTitlesPartialUpdate
     * @request PATCH:/tasks-management/user-task-titles/{id}/
     * @secure
     */,
    tasksManagementUserTaskTitlesPartialUpdate: <
      T extends FetchKeys<UserTaskTitle>
    >(
      id: string,
      data: PatchedUserTaskTitleRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserTaskTitle, T>, any>({
        path: `/tasks-management/user-task-titles/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags tasks-management
     * @name TasksManagementUserTaskTitlesDestroy
     * @request DELETE:/tasks-management/user-task-titles/{id}/
     * @secure
     */,
    tasksManagementUserTaskTitlesDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/tasks-management/user-task-titles/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
  }
  teacher = {
    /**
     * No description
     *
     * @tags teacher
     * @name TeacherActivitiesLessonList
     * @request GET:/teacher/activities/lesson/
     * @secure
     */
    teacherActivitiesLessonList: <
      T extends FetchKeys<PaginatedTeacherTimetableListLessonList>
    >(
      query?: {
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** @format uuid */
        teacher?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTeacherTimetableListLessonList, T>, any>({
        path: `/teacher/activities/lesson/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags teacher
     * @name TeacherActivitiesLessonRetrieve
     * @request GET:/teacher/activities/lesson/{id}/
     * @secure
     */,
    teacherActivitiesLessonRetrieve: <
      T extends FetchKeys<TeacherTimetableLesson>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TeacherTimetableLesson, T>, any>({
        path: `/teacher/activities/lesson/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags teacher
     * @name TeacherActivitiesLessonPartialUpdate
     * @request PATCH:/teacher/activities/lesson/{id}/
     * @secure
     */,
    teacherActivitiesLessonPartialUpdate: <
      T extends FetchKeys<TeacherTimetableLesson>
    >(
      id: string,
      data: PatchedTeacherTimetableUpdateLessonRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TeacherTimetableLesson, T>, any>({
        path: `/teacher/activities/lesson/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags teacher
     * @name TeacherActivitiesLessonAvailableSectionPlanItemsList
     * @request GET:/teacher/activities/lesson/{id}/available-section-plan-items/
     * @secure
     */,
    teacherActivitiesLessonAvailableSectionPlanItemsList: <
      T extends FetchKeys<LessonAvailableSectionPlanItemsQuarter[]>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LessonAvailableSectionPlanItemsQuarter[], T>, any>({
        path: `/teacher/activities/lesson/${id}/available-section-plan-items/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags teacher
     * @name TeacherActivitiesLessonSuggestedSectionPlanItemsRetrieve
     * @request GET:/teacher/activities/lesson/{id}/suggested-section-plan-items/
     * @secure
     */,
    teacherActivitiesLessonSuggestedSectionPlanItemsRetrieve: <
      T extends FetchKeys<LessonSuggestedSectionPlanItemsResponse>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<LessonSuggestedSectionPlanItemsResponse, T>,
        DetailResponseSchema
      >({
        path: `/teacher/activities/lesson/${id}/suggested-section-plan-items/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags teacher
     * @name TeacherAssignmentsStudentsInCourseRetrieve
     * @request GET:/teacher/assignments/students-in-course/{id}/
     * @secure
     */,
    teacherAssignmentsStudentsInCourseRetrieve: <
      T extends FetchKeys<TeacherStudentWithAssignment>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TeacherStudentWithAssignment, T>, any>({
        path: `/teacher/assignments/students-in-course/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  teams = {
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags teams
     * @name TeamsList
     * @request GET:/teams/
     * @secure
     */
    teamsList: <T extends FetchKeys<PaginatedTeamList>>(
      query?: {
        house?: number
        /** @format uuid */
        id?: string
        include_in_boarding_rota?: boolean
        /**
         * * `boarding_director` - Boarding Director
         * * `campus_head` - Campus Head
         * * `deputy_campus_head_main` - Deputy Campus Head (Main)
         * * `deputy_campus_head_senior` - Deputy Campus Head (Sen)
         * * `head_of_house` - Head of House
         * * `house_tutor` - House Tutor
         */
        members__rota_role?:
          | 'boarding_director'
          | 'campus_head'
          | 'deputy_campus_head_main'
          | 'deputy_campus_head_senior'
          | 'head_of_house'
          | 'house_tutor'
        /** @format uuid */
        members__user?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTeamList, T>, any>({
        path: `/teams/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags teams
     * @name TeamsCreate
     * @request POST:/teams/
     * @secure
     */,
    teamsCreate: <T extends FetchKeys<Team>>(
      data: TeamRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Team, T>, any>({
        path: `/teams/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags teams
     * @name TeamsRetrieve
     * @request GET:/teams/{id}/
     * @secure
     */,
    teamsRetrieve: <T extends FetchKeys<Team>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Team, T>, any>({
        path: `/teams/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags teams
     * @name TeamsUpdate
     * @request PUT:/teams/{id}/
     * @secure
     */,
    teamsUpdate: <T extends FetchKeys<Team>>(
      id: string,
      data: TeamRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Team, T>, any>({
        path: `/teams/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags teams
     * @name TeamsPartialUpdate
     * @request PATCH:/teams/{id}/
     * @secure
     */,
    teamsPartialUpdate: <T extends FetchKeys<Team>>(
      id: string,
      data: PatchedTeamRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Team, T>, any>({
        path: `/teams/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags teams
     * @name TeamsDestroy
     * @request DELETE:/teams/{id}/
     * @secure
     */,
    teamsDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/teams/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags teams
     * @name TeamsAvailableFiltersRetrieve
     * @request GET:/teams/available-filters/
     * @secure
     */,
    teamsAvailableFiltersRetrieve: <T extends FetchKeys<Team>>(
      query?: {
        /** Optional list of filter fields to include in the response. The parameter can be provided multiple times, for example: `?fields=homework_status&fields=house&fields=is_at_home`. If omitted - choices for all fields will be returned. */
        fields?: string[]
        house?: number
        /** @format uuid */
        id?: string
        include_in_boarding_rota?: boolean
        /**
         * * `boarding_director` - Boarding Director
         * * `campus_head` - Campus Head
         * * `deputy_campus_head_main` - Deputy Campus Head (Main)
         * * `deputy_campus_head_senior` - Deputy Campus Head (Sen)
         * * `head_of_house` - Head of House
         * * `house_tutor` - House Tutor
         */
        members__rota_role?:
          | 'boarding_director'
          | 'campus_head'
          | 'deputy_campus_head_main'
          | 'deputy_campus_head_senior'
          | 'head_of_house'
          | 'house_tutor'
        /** @format uuid */
        members__user?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Team, T>, any>({
        path: `/teams/available-filters/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags teams
     * @name TeamsChoicesList
     * @request GET:/teams/choices/
     * @secure
     */,
    teamsChoicesList: <T extends FetchKeys<StringOption[]>>(
      query?: {
        house?: number
        /** @format uuid */
        id?: string
        include_in_boarding_rota?: boolean
        /**
         * * `boarding_director` - Boarding Director
         * * `campus_head` - Campus Head
         * * `deputy_campus_head_main` - Deputy Campus Head (Main)
         * * `deputy_campus_head_senior` - Deputy Campus Head (Sen)
         * * `head_of_house` - Head of House
         * * `house_tutor` - House Tutor
         */
        members__rota_role?:
          | 'boarding_director'
          | 'campus_head'
          | 'deputy_campus_head_main'
          | 'deputy_campus_head_senior'
          | 'head_of_house'
          | 'house_tutor'
        /** @format uuid */
        members__user?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StringOption[], T>, any>({
        path: `/teams/choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags teams
     * @name TeamsRotaRolesChoicesList
     * @request GET:/teams/rota-roles-choices/
     * @secure
     */,
    teamsRotaRolesChoicesList: <T extends FetchKeys<RotaRoleChoice[]>>(
      query?: {
        house?: number
        /** @format uuid */
        id?: string
        include_in_boarding_rota?: boolean
        /**
         * * `boarding_director` - Boarding Director
         * * `campus_head` - Campus Head
         * * `deputy_campus_head_main` - Deputy Campus Head (Main)
         * * `deputy_campus_head_senior` - Deputy Campus Head (Sen)
         * * `head_of_house` - Head of House
         * * `house_tutor` - House Tutor
         */
        members__rota_role?:
          | 'boarding_director'
          | 'campus_head'
          | 'deputy_campus_head_main'
          | 'deputy_campus_head_senior'
          | 'head_of_house'
          | 'house_tutor'
        /** @format uuid */
        members__user?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<RotaRoleChoice[], T>, any>({
        path: `/teams/rota-roles-choices/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Add action to ViewSet with available_filters Run get_choice method from filter for every field, passing queryset with excluded filter ex: You have products - TShirt with sizes [S, M, L] and colors [Red ,Blue] - Pants with sizes [32, 34] and colors [Red, Green] After selecting filters [S & Red] in available filters should be sizes: [M, L, 32, 34]
     *
     * @tags teams
     * @name TeamsStaffListList
     * @request GET:/teams/staff-list/
     * @secure
     */,
    teamsStaffListList: <T extends FetchKeys<StaffMember[]>>(
      query?: {
        house?: number
        /** @format uuid */
        id?: string
        include_in_boarding_rota?: boolean
        /**
         * * `boarding_director` - Boarding Director
         * * `campus_head` - Campus Head
         * * `deputy_campus_head_main` - Deputy Campus Head (Main)
         * * `deputy_campus_head_senior` - Deputy Campus Head (Sen)
         * * `head_of_house` - Head of House
         * * `house_tutor` - House Tutor
         */
        members__rota_role?:
          | 'boarding_director'
          | 'campus_head'
          | 'deputy_campus_head_main'
          | 'deputy_campus_head_senior'
          | 'head_of_house'
          | 'house_tutor'
        /** @format uuid */
        members__user?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StaffMember[], T>, any>({
        path: `/teams/staff-list/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  timetable = {
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetableList
     * @request GET:/timetable/
     * @secure
     */
    timetableList: <T extends FetchKeys<PaginatedTimetableList>>(
      query?: {
        /** @format uuid */
        idp_plans__idp_plan__student?: string
        is_hidden?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        quarter?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedTimetableList, T>, any>({
        path: `/timetable/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetableCreate
     * @request POST:/timetable/
     * @secure
     */,
    timetableCreate: <T extends FetchKeys<Timetable>>(
      data: TimetableRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Timetable, T>, any>({
        path: `/timetable/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetableRetrieve
     * @request GET:/timetable/{id}/
     * @secure
     */,
    timetableRetrieve: <T extends FetchKeys<TimetableRetrieve>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TimetableRetrieve, T>, any>({
        path: `/timetable/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetableUpdate
     * @request PUT:/timetable/{id}/
     * @secure
     */,
    timetableUpdate: <T extends FetchKeys<Timetable>>(
      id: number,
      data: TimetableRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Timetable, T>, any>({
        path: `/timetable/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetablePartialUpdate
     * @request PATCH:/timetable/{id}/
     * @secure
     */,
    timetablePartialUpdate: <T extends FetchKeys<Timetable>>(
      id: number,
      data: PatchedTimetableRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Timetable, T>, any>({
        path: `/timetable/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetableDestroy
     * @request DELETE:/timetable/{id}/
     * @secure
     */,
    timetableDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/timetable/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * @description List sections where a generator-selected teacher can be applied.
     *
     * @tags timetable
     * @name TimetableAlternativeTeacherSectionsList
     * @request GET:/timetable/{id}/alternative-teacher-sections/
     * @secure
     */,
    timetableAlternativeTeacherSectionsList: <
      T extends FetchKeys<AlternativeTeacherSection[]>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<AlternativeTeacherSection[], T>, any>({
        path: `/timetable/${id}/alternative-teacher-sections/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Apply the generator-selected teacher and remove alternatives.
     *
     * @tags timetable
     * @name TimetableAlternativeTeacherSectionsApplySelectionCreate
     * @request POST:/timetable/{id}/alternative-teacher-sections/{sectionId}/apply-selection/
     * @secure
     */,
    timetableAlternativeTeacherSectionsApplySelectionCreate: <
      T extends FetchKeys<void>
    >(
      id: number,
      sectionId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/timetable/${id}/alternative-teacher-sections/${sectionId}/apply-selection/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetableAutoimportConfigPartialUpdate
     * @request PATCH:/timetable/{id}/autoimport-config/
     * @secure
     */,
    timetableAutoimportConfigPartialUpdate: <
      T extends FetchKeys<TimetableAutoImportConfigUpdate>
    >(
      id: number,
      data: PatchedTimetableAutoImportConfigUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<TimetableAutoImportConfigUpdate, T>,
        MessageResponseSchema
      >({
        path: `/timetable/${id}/autoimport-config/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetableBusyInSlotCreate
     * @request POST:/timetable/{id}/busy-in-slot/
     * @secure
     */,
    timetableBusyInSlotCreate: <T extends FetchKeys<BusyInSlotResponse>>(
      id: number,
      data: BusyInSlotRequestRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<BusyInSlotResponse, T>, any>({
        path: `/timetable/${id}/busy-in-slot/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags timetable
     * @name TimetableChangesList
     * @request GET:/timetable/{id}/changes/
     * @secure
     */,
    timetableChangesList: <T extends FetchKeys<PaginatedLogEntryList>>(
      id: number,
      query?: {
        is_imported?: boolean
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number | string
        /** A search term. */
        search?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PaginatedLogEntryList, T>, any>({
        path: `/timetable/${id}/changes/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetableCollectPotentialOptimizationsCreate
     * @request POST:/timetable/{id}/collect-potential-optimizations/
     * @secure
     */,
    timetableCollectPotentialOptimizationsCreate: <
      T extends FetchKeys<Timetable>
    >(
      id: number,
      data: TimetableRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Timetable, T>, any>({
        path: `/timetable/${id}/collect-potential-optimizations/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetableConsistencyRetrieve
     * @request GET:/timetable/{id}/consistency/
     * @secure
     */,
    timetableConsistencyRetrieve: <T extends FetchKeys<Timetable>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Timetable, T>, any>({
        path: `/timetable/${id}/consistency/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetableFilterOptionsRetrieve
     * @request GET:/timetable/{id}/filter-options/
     * @secure
     */,
    timetableFilterOptionsRetrieve: <T extends FetchKeys<Timetable>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Timetable, T>, any>({
        path: `/timetable/${id}/filter-options/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetableFreeSlotsRetrieve
     * @request GET:/timetable/{id}/free-slots/
     * @secure
     */,
    timetableFreeSlotsRetrieve: <T extends FetchKeys<FreeSlot>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FreeSlot, T>, any>({
        path: `/timetable/${id}/free-slots/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags timetable
     * @name TimetablePreviewCreate
     * @request POST:/timetable/{id}/preview/
     * @secure
     */,
    timetablePreviewCreate: <T extends FetchKeys<DraftActivityList>>(
      id: number,
      data: DraftActivityListRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<DraftActivityList, T>, any>({
        path: `/timetable/${id}/preview/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetableStatsRetrieve
     * @request GET:/timetable/{id}/stats/
     * @secure
     */,
    timetableStatsRetrieve: <T extends FetchKeys<TimetableStats>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TimetableStats, T>, any>({
        path: `/timetable/${id}/stats/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetableStudentMovesList
     * @request GET:/timetable/{id}/student-moves/
     * @secure
     */,
    timetableStudentMovesList: <T extends FetchKeys<StudentMoveSection[]>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentMoveSection[], T>, any>({
        path: `/timetable/${id}/student-moves/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetableStudentMovesApplyCreate
     * @request POST:/timetable/{id}/student-moves/{studentMoveId}/apply/
     * @secure
     */,
    timetableStudentMovesApplyCreate: <T extends FetchKeys<StudentMove>>(
      id: number,
      studentMoveId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentMove, T>, any>({
        path: `/timetable/${id}/student-moves/${studentMoveId}/apply/`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetableStudentMovesRevertCreate
     * @request POST:/timetable/{id}/student-moves/{studentMoveId}/revert/
     * @secure
     */,
    timetableStudentMovesRevertCreate: <T extends FetchKeys<StudentMove>>(
      id: number,
      studentMoveId: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<StudentMove, T>, any>({
        path: `/timetable/${id}/student-moves/${studentMoveId}/revert/`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetableTogglePreviewForTeachersCreate
     * @request POST:/timetable/{id}/toggle-preview-for-teachers/
     * @secure
     */,
    timetableTogglePreviewForTeachersCreate: <T extends FetchKeys<Timetable>>(
      id: number,
      data: TimetableRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Timetable, T>, any>({
        path: `/timetable/${id}/toggle-preview-for-teachers/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetableCopyTalentEnrichmentCreate
     * @request POST:/timetable/copy-talent-enrichment/
     * @secure
     */,
    timetableCopyTalentEnrichmentCreate: <
      T extends FetchKeys<CopyTalentEnrichment>
    >(
      data: CopyTalentEnrichmentRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CopyTalentEnrichment, T>, any>({
        path: `/timetable/copy-talent-enrichment/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags timetable
     * @name TimetableCurrentLessonsList
     * @request GET:/timetable/current-lessons/
     * @secure
     */,
    timetableCurrentLessonsList: <T extends FetchKeys<CurrentLesson[]>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CurrentLesson[], T>, any>({
        path: `/timetable/current-lessons/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags timetable
     * @name TimetableSourceIdpRetrieve
     * @request GET:/timetable/source-idp/{id}/
     * @secure
     */,
    timetableSourceIdpRetrieve: <T extends FetchKeys<SourceIDPPlan>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SourceIDPPlan, T>, any>({
        path: `/timetable/source-idp/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags timetable
     * @name TimetableSourceIdpRetrieve2
     * @request GET:/timetable/source-idp/{studentId}/{timetableId}/
     * @secure
     */,
    timetableSourceIdpRetrieve2: <T extends FetchKeys<SourceIDPPlan>>(
      studentId: string,
      timetableId: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SourceIDPPlan, T>, any>({
        path: `/timetable/source-idp/${studentId}/${timetableId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description Mixin for parsing restql query from request. NOTE: We are using `request.GET` instead of `request.query_params` because this might be called before DRF request is created(i.e from dispatch). This means `request.query_params` might not be available when this mixin is used.
     *
     * @tags timetable
     * @name TimetableWithRotasList
     * @request GET:/timetable/with-rotas/
     * @secure
     */,
    timetableWithRotasList: <T extends FetchKeys<TimetableWithRota[]>>(
      query?: {
        /** @format uuid */
        idp_plans__idp_plan__student?: string
        is_hidden?: boolean
        /** Which field to use when ordering the results. */
        ordering?: string
        quarter?: number
        /** Filter timetables by school year ID */
        school_year?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TimetableWithRota[], T>, any>({
        path: `/timetable/with-rotas/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  transcriptsOfGrades = {
    /**
     * No description
     *
     * @tags transcripts-of-grades
     * @name TranscriptsOfGradesCreate
     * @request POST:/transcripts-of-grades/
     * @secure
     */
    transcriptsOfGradesCreate: <T extends FetchKeys<TranscriptOfGradesCreate>>(
      data: TranscriptOfGradesCreateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TranscriptOfGradesCreate, T>, any>({
        path: `/transcripts-of-grades/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags transcripts-of-grades
     * @name TranscriptsOfGradesRetrieve
     * @request GET:/transcripts-of-grades/{id}/
     * @secure
     */,
    transcriptsOfGradesRetrieve: <
      T extends FetchKeys<TranscriptOfGradesRetrieve>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TranscriptOfGradesRetrieve, T>, any>({
        path: `/transcripts-of-grades/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags transcripts-of-grades
     * @name TranscriptsOfGradesUpdate
     * @request PUT:/transcripts-of-grades/{id}/
     * @secure
     */,
    transcriptsOfGradesUpdate: <T extends FetchKeys<TranscriptOfGradesUpdate>>(
      id: string,
      data: TranscriptOfGradesUpdateRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TranscriptOfGradesUpdate, T>, any>({
        path: `/transcripts-of-grades/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags transcripts-of-grades
     * @name TranscriptsOfGradesDestroy
     * @request DELETE:/transcripts-of-grades/{id}/
     * @secure
     */,
    transcriptsOfGradesDestroy: <T extends FetchKeys<void>>(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/transcripts-of-grades/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags transcripts-of-grades
     * @name TranscriptsOfGradesCoursesList
     * @request GET:/transcripts-of-grades/{id}/courses/
     * @secure
     */,
    transcriptsOfGradesCoursesList: <
      T extends FetchKeys<TranscriptOfGradesCourseList[]>
    >(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TranscriptOfGradesCourseList[], T>, any>({
        path: `/transcripts-of-grades/${id}/courses/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags transcripts-of-grades
     * @name TranscriptsOfGradesItemDataRetrieve
     * @request GET:/transcripts-of-grades/{id}/item_data/
     * @secure
     */,
    transcriptsOfGradesItemDataRetrieve: <
      T extends FetchKeys<CalculateItemDataResponse>
    >(
      id: string,
      query: {
        courses: string[]
        /**
         * * `CCU` - CCU
         * * `SPORTS` - Sports
         * * `ACADEMIC` - Academic
         * @minLength 1
         */
        group: 'CCU' | 'SPORTS' | 'ACADEMIC'

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CalculateItemDataResponse, T>, any>({
        path: `/transcripts-of-grades/${id}/item_data/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags transcripts-of-grades
     * @name TranscriptsOfGradesRestore
     * @request POST:/transcripts-of-grades/{id}/restore/
     * @secure
     */,
    transcriptsOfGradesRestore: <
      T extends FetchKeys<TranscriptOfGradesRestore>
    >(
      id: string,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TranscriptOfGradesRestore, T>, any>({
        path: `/transcripts-of-grades/${id}/restore/`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags transcripts-of-grades
     * @name TranscriptsOfGradesUpdateGrades
     * @request POST:/transcripts-of-grades/{id}/update-grades/
     * @secure
     */,
    transcriptsOfGradesUpdateGrades: <
      T extends FetchKeys<TranscriptOfGradesRetrieve>
    >(
      id: string,
      data: TranscriptOfGradesUpdateGradesRequestRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TranscriptOfGradesRetrieve, T>, any>({
        path: `/transcripts-of-grades/${id}/update-grades/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags transcripts-of-grades
     * @name TranscriptsOfGradesAvailableGradeLevelsList
     * @request GET:/transcripts-of-grades/available-grade-levels/{studentId}/
     * @secure
     */,
    transcriptsOfGradesAvailableGradeLevelsList: <
      T extends FetchKeys<SimpleGradeLevel[]>
    >(
      studentId: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SimpleGradeLevel[], T>, any>({
        path: `/transcripts-of-grades/available-grade-levels/${studentId}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags transcripts-of-grades
     * @name TranscriptsOfGradesAvailableSignaturesList
     * @request GET:/transcripts-of-grades/available-signatures/
     * @secure
     */,
    transcriptsOfGradesAvailableSignaturesList: <
      T extends FetchKeys<TranscriptOfGradesSignature[]>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TranscriptOfGradesSignature[], T>, any>({
        path: `/transcripts-of-grades/available-signatures/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags transcripts-of-grades
     * @name TranscriptsOfGradesList
     * @request GET:/transcripts-of-grades/grouped-list/
     * @secure
     */,
    transcriptsOfGradesList: <T extends FetchKeys<TranscriptOfGradesList>>(
      query: {
        /**
         * A UUID string identifying the student who transcripts should be returned for.
         * @format uuid
         */
        student: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TranscriptOfGradesList, T>, any>({
        path: `/transcripts-of-grades/grouped-list/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags transcripts-of-grades
     * @name TranscriptsOfGradesPdfRetrieve
     * @request GET:/transcripts-of-grades/pdf/
     * @secure
     */,
    transcriptsOfGradesPdfRetrieve: <T extends FetchKeys<any>>(
      query: {
        /** Boolean value indicating if signature should be added to pdf. */
        add_signature?: boolean
        preview?: boolean
        /** @format uuid */
        signature_id?: string
        /**
         * Student UUID for which the transcript PDF should be returned.
         * @format uuid
         */
        student_id: string
        /** List of transcript UUIDs for which the transcript PDF should be returned. */
        transcripts_ids: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<any, T>, any>({
        path: `/transcripts-of-grades/pdf/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  userPreferences = {
    /**
     * No description
     *
     * @tags user-preferences
     * @name UserPreferencesFavouriteViewSettingList
     * @request GET:/user-preferences/favourite-view-setting/
     * @secure
     */
    userPreferencesFavouriteViewSettingList: <
      T extends FetchKeys<FavouriteViewSetting[]>
    >(
      query?: {
        /**
         * * `1` - students
         * * `2` - crm
         * * `3` - contacts_crm
         * * `4` - contacts_students
         * * `5` - companies
         */
        module?: 1 | 2 | 3 | 4 | 5

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FavouriteViewSetting[], T>, any>({
        path: `/user-preferences/favourite-view-setting/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags user-preferences
     * @name UserPreferencesFavouriteViewSettingCreate
     * @request POST:/user-preferences/favourite-view-setting/
     * @secure
     */,
    userPreferencesFavouriteViewSettingCreate: <
      T extends FetchKeys<FavouriteViewSetting>
    >(
      data: FavouriteViewSettingRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FavouriteViewSetting, T>, any>({
        path: `/user-preferences/favourite-view-setting/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags user-preferences
     * @name UserPreferencesFavouriteViewSettingRetrieve
     * @request GET:/user-preferences/favourite-view-setting/{id}/
     * @secure
     */,
    userPreferencesFavouriteViewSettingRetrieve: <
      T extends FetchKeys<FavouriteViewSetting>
    >(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FavouriteViewSetting, T>, any>({
        path: `/user-preferences/favourite-view-setting/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags user-preferences
     * @name UserPreferencesFavouriteViewSettingUpdate
     * @request PUT:/user-preferences/favourite-view-setting/{id}/
     * @secure
     */,
    userPreferencesFavouriteViewSettingUpdate: <
      T extends FetchKeys<FavouriteViewSetting>
    >(
      id: number,
      data: FavouriteViewSettingRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FavouriteViewSetting, T>, any>({
        path: `/user-preferences/favourite-view-setting/${id}/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags user-preferences
     * @name UserPreferencesFavouriteViewSettingPartialUpdate
     * @request PATCH:/user-preferences/favourite-view-setting/{id}/
     * @secure
     */,
    userPreferencesFavouriteViewSettingPartialUpdate: <
      T extends FetchKeys<FavouriteViewSetting>
    >(
      id: number,
      data: PatchedFavouriteViewSettingRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<FavouriteViewSetting, T>, any>({
        path: `/user-preferences/favourite-view-setting/${id}/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags user-preferences
     * @name UserPreferencesFavouriteViewSettingDestroy
     * @request DELETE:/user-preferences/favourite-view-setting/{id}/
     * @secure
     */,
    userPreferencesFavouriteViewSettingDestroy: <T extends FetchKeys<void>>(
      id: number,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/user-preferences/favourite-view-setting/${id}/`,
        method: 'DELETE',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags user-preferences
     * @name UserPreferencesFavouriteViewSettingShareableTeamsList
     * @request GET:/user-preferences/favourite-view-setting/shareable-teams/
     * @secure
     */,
    userPreferencesFavouriteViewSettingShareableTeamsList: <
      T extends FetchKeys<SimpleTeamWithMembers[]>
    >(
      query: {
        /**
         * * `1` - students
         * * `2` - crm
         * * `3` - contacts_crm
         * * `4` - contacts_students
         * * `5` - companies
         */
        module: 1 | 2 | 3 | 4 | 5

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<SimpleTeamWithMembers[], T>, any>({
        path: `/user-preferences/favourite-view-setting/shareable-teams/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  userTimetable = {
    /**
     * @description [WARNING] AdminTimetableViewSet inherits from this!
     *
     * @tags user-timetable
     * @name UserTimetableList
     * @request GET:/user-timetable/
     * @secure
     */
    userTimetableList: <T extends FetchKeys<UserTimetable[]>>(
      query?: {
        /** @format date */
        date_after?: string
        /** @format date */
        date_before?: string
        include?: (
          | 'campus_absences'
          | 'doctor_appointments'
          | 'exams'
          | 'leave_requests'
          | 'lessons'
          | 'merit_rewards'
          | 'rotas'
          | 'school_breaks'
          | 'school_programmes'
        )[]
        students?: string[]
        teachers?: string[]

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<UserTimetable[], T>, any>({
        path: `/user-timetable/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description [WARNING] AdminTimetableViewSet inherits from this!
     *
     * @tags user-timetable
     * @name UserTimetablePdfRetrieve
     * @request GET:/user-timetable/pdf/
     * @secure
     */,
    userTimetablePdfRetrieve: <T extends FetchKeys<File>>(
      query: {
        /** @format date */
        date_after: string
        /** @format date */
        date_before: string
        /** @format uuid */
        students?: string
        teachers?: string

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<File, T>, any>({
        path: `/user-timetable/pdf/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
  }
  users = {
    /**
     * No description
     *
     * @tags users
     * @name UsersRetrieve
     * @request GET:/users/{id}/
     * @secure
     */
    usersRetrieve: <T extends FetchKeys<CurrentUser>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CurrentUser, T>, any>({
        path: `/users/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags users
     * @name UsersAuthEmailCodeRequestCreate
     * @request POST:/users/auth/email-code-request/
     * @secure
     */,
    usersAuthEmailCodeRequestCreate: <T extends FetchKeys<CodeRequestResponse>>(
      data: UserPhoneNumberRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CodeRequestResponse, T>, any>({
        path: `/users/auth/email-code-request/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags users
     * @name UsersAuthGoogleCreate
     * @request POST:/users/auth/google/
     * @secure
     */,
    usersAuthGoogleCreate: <T extends FetchKeys<GoogleLogin>>(
      data: GoogleLoginRequestRequest,
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<GoogleLogin, T>,
        GoogleLoginServiceUnavailableResponse
      >({
        path: `/users/auth/google/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags users
     * @name UsersAuthLoginCreate
     * @request POST:/users/auth/login/
     * @secure
     */,
    usersAuthLoginCreate: <T extends FetchKeys<TwoFactorLogin>>(
      data: PasswordLoginRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<TwoFactorLogin, T>, any>({
        path: `/users/auth/login/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags users
     * @name UsersAuthLoginV2ValidateCodeCreate
     * @request POST:/users/auth/login/v2/validate-code/
     * @secure
     */,
    usersAuthLoginV2ValidateCodeCreate: <T extends FetchKeys<LoginResponse>>(
      data: ValidateCodeV2Request,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LoginResponse, T>, any>({
        path: `/users/auth/login/v2/validate-code/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags users
     * @name UsersAuthLoginV2ValidateLinkCreate
     * @request POST:/users/auth/login/v2/validate-link/
     * @secure
     */,
    usersAuthLoginV2ValidateLinkCreate: <
      T extends FetchKeys<LoginLinkResponse>
    >(
      data: ValidateLoginLinkV2Request,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LoginLinkResponse, T>, any>({
        path: `/users/auth/login/v2/validate-link/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags users
     * @name UsersAuthLoginValidateCodeCreate
     * @request POST:/users/auth/login/validate-code/
     * @secure
     */,
    usersAuthLoginValidateCodeCreate: <T extends FetchKeys<LoginResponse>>(
      data: ValidateCodeRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LoginResponse, T>, any>({
        path: `/users/auth/login/validate-code/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags users
     * @name UsersAuthLoginValidateLinkCreate
     * @request POST:/users/auth/login/validate-link/
     * @secure
     */,
    usersAuthLoginValidateLinkCreate: <T extends FetchKeys<LoginLinkResponse>>(
      data: ValidateLoginLinkRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<LoginLinkResponse, T>, any>({
        path: `/users/auth/login/validate-link/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags users
     * @name UsersAuthLogoutCreate
     * @request POST:/users/auth/logout/
     * @secure
     */,
    usersAuthLogoutCreate: <T extends FetchKeys<void>>(
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/users/auth/logout/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags users
     * @name UsersAuthSmsCodeRequestCreate
     * @request POST:/users/auth/sms-code-request/
     * @secure
     */,
    usersAuthSmsCodeRequestCreate: <T extends FetchKeys<CodeRequestResponse>>(
      data: UserPhoneNumberRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CodeRequestResponse, T>, any>({
        path: `/users/auth/sms-code-request/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags users
     * @name UsersChangeAvatarRetrieve
     * @request GET:/users/change-avatar/
     * @secure
     */,
    usersChangeAvatarRetrieve: <T extends FetchKeys<ChangeAvatar>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ChangeAvatar, T>, any>({
        path: `/users/change-avatar/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags users
     * @name UsersChangeAvatarUpdate
     * @request PUT:/users/change-avatar/
     * @secure
     */,
    usersChangeAvatarUpdate: <T extends FetchKeys<ChangeAvatar>>(
      data: ChangeAvatarRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ChangeAvatar, T>, any>({
        path: `/users/change-avatar/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags users
     * @name UsersChangeAvatarPartialUpdate
     * @request PATCH:/users/change-avatar/
     * @secure
     */,
    usersChangeAvatarPartialUpdate: <T extends FetchKeys<ChangeAvatar>>(
      data: PatchedChangeAvatarRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ChangeAvatar, T>, any>({
        path: `/users/change-avatar/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags users
     * @name UsersMeRetrieve
     * @request GET:/users/me/
     * @secure
     */,
    usersMeRetrieve: <T extends FetchKeys<CurrentUser>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<CurrentUser, T>, any>({
        path: `/users/me/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags users
     * @name UsersNotificationSettingsRetrieve
     * @request GET:/users/notification-settings/
     * @secure
     */,
    usersNotificationSettingsRetrieve: <
      T extends FetchKeys<NotificationSettings>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NotificationSettings, T>, any>({
        path: `/users/notification-settings/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags users
     * @name UsersNotificationSettingsUpdate
     * @request PUT:/users/notification-settings/
     * @secure
     */,
    usersNotificationSettingsUpdate: <
      T extends FetchKeys<NotificationSettings>
    >(
      data: NotificationSettingsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NotificationSettings, T>, any>({
        path: `/users/notification-settings/`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags users
     * @name UsersNotificationSettingsPartialUpdate
     * @request PATCH:/users/notification-settings/
     * @secure
     */,
    usersNotificationSettingsPartialUpdate: <
      T extends FetchKeys<NotificationSettings>
    >(
      data: PatchedNotificationSettingsRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<NotificationSettings, T>, any>({
        path: `/users/notification-settings/`,
        method: 'PATCH',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags users
     * @name UsersResetPinCodeCreate
     * @request POST:/users/reset-pin-code/
     * @secure
     */,
    usersResetPinCodeCreate: <T extends FetchKeys<ResetMobilePinCode>>(
      data: ResetMobilePinCodeRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ResetMobilePinCode, T>, any>({
        path: `/users/reset-pin-code/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags users
     * @name UsersResetPinCodeConfirmCreate
     * @request POST:/users/reset-pin-code/confirm/
     * @secure
     */,
    usersResetPinCodeConfirmCreate: <
      T extends FetchKeys<ResetMobilePinCodeConfirm>
    >(
      data: ResetMobilePinCodeConfirmRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<ResetMobilePinCodeConfirm, T>, any>({
        path: `/users/reset-pin-code/confirm/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
  }
  videos = {
    /**
     * No description
     *
     * @tags videos
     * @name VideosCreate
     * @request POST:/videos/
     * @secure
     */
    videosCreate: <T extends FetchKeys<Video>>(
      data: VideoUploadRequest,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Video, T>, any>({
        path: `/videos/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.FormData,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags videos
     * @name VideosRetrieve
     * @request GET:/videos/{id}/
     * @secure
     */,
    videosRetrieve: <T extends FetchKeys<Video>>(
      id: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<Video, T>, any>({
        path: `/videos/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
  ws = {
    /**
     * No description
     *
     * @tags ws
     * @name WsAuthTokenRetrieve
     * @request GET:/ws/auth/token/
     * @secure
     */
    wsAuthTokenRetrieve: <T extends FetchKeys<WebsocketAuthTokenResponse>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<WebsocketAuthTokenResponse, T>, any>({
        path: `/ws/auth/token/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
}
